// DispatchDeck - Dispatch Module
// Job scheduling with multi-day support and asset assignment

import {
  db,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  arrayUnion,   // TASK-4792 — jobs.removedDates
  arrayRemove,  // TASK-4792 — jobs.removedDates
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  auth,
  increment
} from '../firebase-config.js?v=1868';

// runTransaction is not re-exported from firebase-config.js — import it directly
// (like ap.js) so this module doesn't force a firebase-config cache-buster rebump
// across the whole app. Used for the zero-floored deployedQty decrement (TASK-1255).
// TASK-9366 — serverTimestamp is likewise not re-exported from firebase-config.js;
// same gstatic URL so it is the same SDK instance. Used ONLY by the auto-close
// toggle, whose enabledAt is the cron's go-forward boundary and must be server time.
// TASK-9645 C (COD Code Reviewer) — getDocsFromServer for the Edit Job invoice
// lock's save-time re-check: plain getDocs may answer from the offline cache.
import { runTransaction, serverTimestamp, getDocsFromServer } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

// TASK-9151 — same reason as runTransaction above: uploadBytesResumable is not
// re-exported from firebase-config.js, and adding it there would force a
// firebase-config ?v= rebump across every importer. The gstatic module URL is
// identical to the one firebase-config.js imports, so the SDK instance (and the
// `storage` handle) is shared — this is an extra named export, not a second copy.
// uploadBytes (non-resumable) has NO progress callback at all; a resumable task
// is the only way the SDK reports bytesTransferred, which is what the permit
// progress bar is driven from.
import { uploadBytesResumable } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-storage.js';

import { processPhoto, generateThumbnail, validateImageFile } from '../utils/imageProcessor.js?v=1955';
import { toDrawablePhoto, photoErrorMessage, createPhotoPrepTracker, detectHeic } from '../utils/heicConvert.js?v=3';
// TASK-4825 — ONE retry loop for every `_counters` allocator in the app. This
// file used to carry its own copy at a 24-attempt budget with no wall clock; see
// allocateJobNumber below for what that measured.
// 🔴 Bump this `?v=` in lockstep with views/billing.js and utils/slipHelpers.js.
import { allocateCounterWithRetry } from '../utils/counterRetry.js?v=1';
// TASK-9585 S7d — which jobs are on "Permit costs waiting for a yes" + which
// permit files a yes covers. dispatch.js is its only importer.
// integ/2120 perf budget (appColdLoadGzip): TASK-9585 S7d's permit-cost waiting helpers load on
// first paint of the waiting strip, not at cold start (TASK-9885 pattern, client side). The three
// names stay module-level so the strip code reads exactly as before; until the chunk lands they are
// null and the strip paints itself again once it does.
let permitWaitingRows = null, permitWaitingTotal = null, parsePermitAmount = null, permitDeclinedRows = null, isPermitCostWaiting = null; // + S7d reinstate helpers
let _permitCostWaitingLoad = null;
function _loadPermitCostWaiting() {
  if (!_permitCostWaitingLoad) {
    _permitCostWaitingLoad = __ddChunkWrap(import('../utils/permitCostWaiting.js?v=10282127')).then((m) => {
      ({ permitWaitingRows, permitWaitingTotal, parsePermitAmount, permitDeclinedRows, isPermitCostWaiting } = m);
      return m;
    }).catch((e) => { _permitCostWaitingLoad = null; throw e; });
  }
  return _permitCostWaitingLoad;
}
// TASK-10112 — the shared send preview / progress / result component. Lazy
// (first Notify Crew open), read through window.SendFeedback only (Pitfall #1:
// hr.js and notes.js import the same file with the SAME ?v=).
let _sendFeedbackLoad = null;
function _loadSendFeedback() {
  if (window.SendFeedback) return Promise.resolve(window.SendFeedback);
  if (!_sendFeedbackLoad) {
    _sendFeedbackLoad = __ddChunkWrap(import('../utils/sendFeedback.js?v=10112001')).then(() => window.SendFeedback)
      .catch((e) => { _sendFeedbackLoad = null; throw e; });
  }
  return _sendFeedbackLoad;
}
// TASK-9541 — permanent job-stop ids. Pure functions, no module state, so a
// second instance on another `?v=` would be harmless — still, bump in lockstep
// with demoSeeder.js.
import { mintStopId, isValidStopId } from '../utils/stopIds.js?v=1';
// TASK-9540 — saved-place categories (shape + color per category, the first
// data layer of the TASK-4789 map builder). Pure, no module state; dispatch.js
// is its only importer.
import { PLACE_SHAPES, PLACE_SHAPE_IDS, PLACE_COLORS, OTHER_CATEGORY_ID, PLACE_CAT_NAME_MAX, isStarterCategoryId, isValidStopCategoryId, validatePlaceCategory, mergePlaceCategories, categoryForPlace, placeCategorySuggestions, filterPlacesByHiddenCategories, countPlacesByCategory, placeShapeSvg } from '../utils/placeCategories.js?v=1';
import { STARTER_LIMITS } from '../config/planLimits.js?v=1763';
// TASK-9958 — file provenance (who/how/when) on job permits + photos; pure helpers.
import { buildTicketDocUploader, stampFileProvenance, fileAddedAuditPayload, fileUploadCustomMetadata, ticketDocUploadedLine } from '../shared/ticket-doc-meta.js?v=9958';
// TASK-9163 — the ONE "job or question?" rule, GENERATED from
// functions/src/shared/jobBuilderIntent.js by scripts/build-shared.cjs. The
// server's parseJobDescription runs the same rule. dispatch.js is its only importer.
import { classifyJobBuilderInput, resolveJobParseIntent } from '../shared/job-builder-intent.js?v=9163';
// TASK-9694 — a driver's job-screen Doc (kind 'doc') rides in job.permits[] but is
// not a permit (generated from functions/src/shared/jobPermitEntry.js; same ?v= as billing.js).
import { isJobDocEntry } from '../shared/job-permit-entry.js?v=10282127';
import { jobPoList, jobPoLabel, jobPoTextMatch } from '../shared/job-po-read.js?v=9811003'; // TASK-9811 S2 main + extra POs (generated read half of shared/jobPoList.js)
// TASK-9844 — the crew-skills rule (`window.CrewSkills`), GENERATED side-effect
// module; roster.js imports the SAME url (keep both `?v=` identical, Pitfall #7).
import '../shared/crew-skills.js?v=9911004';
// TASK-4803 — stops the Smart Job Builder found in past jobs (server-verified).
// dispatch.js is its only importer.
import { planJobBuilderWaypoints, jobBuilderHistoryNoteHtml, jobBuilderCrewSuggestionsHtml } from '../utils/jobBuilderHistory.js?v=10295'; // TASK-4803 S3a usual-crew suggestions
import { claudeJobIdFromFocus, claudeDraftFormFacts, claudeAssumedKey, claudeDraftUnconfirmed, claudeDraftBannerHtml } from '../utils/claudeJobDraft.js?v=4'; // TASK-9170 gauge mark · TASK-9587 Claude job request -> Job Builder review · TASK-9737 start time · TASK-9750 tentative (2089-on-2088.6: both changed it)
// trailguide is lazy-imported from app.js (TASK-345 S3) — use window.Trailguide
// global instead of static-import here so we don't duplicate the module under a
// stale ?v= cache-buster (Pitfall #1 in CLAUDE.md). showAIEmailGate only fires
// in demo mode, by which point app.js has already loaded the trailguide module
// via initTrailguide() in the auth handler. Fallback dynamic-import with retry
// if the user somehow lands on the AI prompt before initTrailguide settles.
// IMPORTANT: keep ?v= synced with app.js _ensureTrailguide() specifier.
async function showAIEmailGate(...args) {
  if (window.Trailguide?.showAIGate) {
    return window.Trailguide.showAIGate(...args);
  }
  try {
    const m = await __ddChunkWrap(import('../trailguide.js?v=10329295')); // 
    return m.showAIEmailGate(...args);
  } catch (err) {
    console.warn('[Dispatch] trailguide import failed, retrying:', err?.message);
    await new Promise(r => setTimeout(r, 1000));
    const m = await __ddChunkWrap(import('../trailguide.js?v=10329295')); // 
    return m.showAIEmailGate(...args);
  }
}

import {
  jobDayVisibleToCurrentDriver as _sharedJobDayVisible,
  buildDriverLoadCounterHTML as _sharedBuildDriverLoadCounterHTML,
  buildCalendarMonthGridHTML as _sharedBuildCalendarMonthGridHTML,
  computeLoadInfo as _sharedComputeLoadInfo,
} from '../utils/driverScheduleHelpers.js?v=6';

// ============================================
// DISPATCH STATE
// ============================================
// Check if in demo mode (anonymous auth) - returns true when in demo
// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

function isDemoMode() {
  return window.AppState?.isDemo === true;
}

// Check if 811 permits feature is enabled in settings
function is811Enabled() {
  return window.AppState?.settings?.enable811 === true || isDemoMode();
}

// TASK-4649 — OS/OW permit tracking (the "Permit Required" checkbox + the
// AR/AP missing-permit flag in billing.js). Tenant-gated, DEFAULT OFF: 85 of
// 86 tenants don't pull oversize permits, so absent === disabled. Key follows
// the sibling settings.enableX pattern above, not a new features.* namespace.
function isPermitTrackingEnabled() {
  return window.AppState?.settings?.enablePermitTracking === true || isDemoMode();
}

// TASK-4688 — Add Job form section visibility (Dispatch Settings).
// POLARITY: these six sections render for EVERY tenant today, so the key is
// hide* and absent === false === shown. That matches hideComplianceWarnings
// (:hideComplianceWarnings below), NOT the enableX opt-ins above — 811 and
// permit tracking are features you switch ON, these are chrome you switch OFF.
// Absent = status quo, so no tenant's form changes without them asking.
// A field that is PRESENT but not the shape we expect counts as data. Hiding a
// value we cannot parse is the fail-CLOSED direction, and that is exactly how
// data goes missing quietly. [[lesson_control_chars_fail_open_silently]]
const _jobHas = (v) => v != null && v !== false && v !== '' && !(Array.isArray(v) && v.length === 0);
const _jobHasWaypoint = (v) => Array.isArray(v) ? v.some(w => w && (w.name || w.url)) : _jobHas(v);
const _jobHasGeofence = (v) => (v && typeof v === 'object') ? v.enabled === true : _jobHas(v);

const JOB_FORM_SECTIONS = [
  { key: 'hideAfeVendor',      el: 'job-afe-vendor-row',      hasData: j => _jobHas(j.vendorNumber) || _jobHas(j.afeNumber) },
  // TASK-9370 — a driver-created job's flags are UNKNOWN (null) until a dispatcher
  // confirms them; the row must show even on a tenant that hides it.
  { key: 'hideDotFlags',       el: 'job-dot-flags-row',       hasData: j => _jobHas(j.interstate) || _jobHas(j.hazmat) || _jobHas(j.liquidTank) || j.needsDispatchReview === true },
  { key: 'hideTicketingMode',  el: 'job-ticketing-section',   hasData: j => j.splitBy != null && j.splitBy !== '' && j.splitBy !== 'combined' },
  { key: 'hideWaypoints',      el: 'job-waypoints-section',   hasData: j => _jobHasWaypoint(j.waypoints) },
  { key: 'hideGeofence',       el: 'job-geofence-section',    hasData: j => _jobHasGeofence(j.geofence) },
  { key: 'hideAttachments',    el: 'job-attachments-section', hasData: j => _jobHas(j.photos) || _jobHas(j.permits) },
];

// Hides the section CHROME with CSS only — every input stays in the DOM, so
// saveJob (which reads #job-vendor-number etc. without optional chaining) keeps
// working and a hidden section can never silently drop stored data.
// A job that ALREADY has data in a hidden section overrides the toggle and
// renders anyway. Same shape as hideComplianceWarnings letting severity:'safety'
// through: an opt-out never hides something that is actually there.
// Called at modal-open time, not template-build time, so a settings change
// takes effect on the next open with no page reload.
function applyJobFormSectionVisibility(job) {
  const s = window.AppState?.settings || {};
  JOB_FORM_SECTIONS.forEach(sec => {
    const el = document.getElementById(sec.el);
    if (!el) return;
    let hide = s[sec.key] === true;
    if (hide && job) {
      // Never let a bad job shape hide data — fail OPEN (visible).
      try { if (sec.hasData(job)) hide = false; } catch (_) { hide = false; }
    }
    el.classList.toggle('hidden', hide);
  });
  _refreshJobFormGroups();
}

// ============================================
// TASK-4695 — Add Job modal: collapsed groups + the Project picker
// ============================================
// The less-used fields live in two <details class="job-acc"> groups. Nothing in
// a closed group may go unseen: the summary row lists every filled value.
// Called from applyJobFormSectionVisibility (every open, every programmatic
// fill via revealPopulatedJobFormSections), from the groups' own input/change
// events, from setJobSplitBy (a hidden input — no event) and the driver trim.
function _jobFormGroupValues(groupId) {
  const v = (id) => String(document.getElementById(id)?.value || '').trim();
  const out = [];
  if (groupId === 'job-acc-site') {
    // TASK-9648 — a GPS pair reads as the place it is (a saved location or stop
    // within a quarter mile) or "GPS pin"; the raw numbers live in the body.
    if (v('job-location')) {
      const place = _jobLocationPlaceInfo();
      out.push(place ? (place.name || 'GPS pin') : v('job-location'));
    }
    if (v('job-well-name')) out.push(v('job-well-name'));
    const ids = _getJobPermitIds();
    if (ids.length && is811Enabled()) { // TASK-9994 — nothing 811 while the company has it off
      const first = (DispatchState.permits || []).find(p => p.id === ids[0]);
      out.push(ids.length === 1
        ? '811: ' + (String(first?.name || '').trim() || '1 ticket')
        : '811: ' + ids.length + ' tickets');
      // TASK-9859 — the closed group still says when a ticket is a problem.
      const w = _p811JobFormTicketWarnings(ids);
      if (w.expired) out.push(w.expired === 1 ? '1 ticket expired' : w.expired + ' tickets expired');
      if (w.late) out.push(w.late === 1 ? '1 ticket expires before scheduled work' : w.late + ' tickets expire before scheduled work');
    }
    if (document.getElementById('job-geofence-enabled')?.checked) {
      const radius = document.getElementById('job-geofence-radius');
      const r = radius && radius.value ? String(radius.selectedOptions?.[0]?.textContent || radius.value).trim() : '';
      out.push('Geofence' + (r ? ' ' + r : ' on'));
    }
  } else if (groupId === 'job-acc-billing') {
    if (v('job-vendor-number')) out.push('Vendor ' + v('job-vendor-number'));
    if (v('job-afe')) out.push('AFE ' + v('job-afe'));
    if (v('job-split-by') === 'vehicle') out.push('Split by vehicle');
    const lock = document.getElementById('job-split-lock');
    if (lock && lock.style.display !== 'none') out.push('Ticketing locked today');
  }
  return out;
}

// A group is shown unless EVERY section inside it is off — by Dispatch Settings
// (.hidden, applyJobFormSectionVisibility) or by the driver trim (inline
// display:none). A settings-hidden section must never come back as an empty
// collapsed header; one that holds data is un-hidden above, so its group shows.
function _refreshJobFormGroups() {
  // TASK-9648 — the Site group's live lines (place under Location, geofence
  // center + radius) repaint on the same beat as the summary.
  try { _renderJobLocationPlace(); refreshGeofenceCenterLine(); } catch (e) { console.warn('[Dispatch] site lines:', e); }
  document.querySelectorAll('#modal-job details.job-acc').forEach(group => {
    const body = group.querySelector('.job-acc-body');
    const kids = body ? Array.from(body.children).filter(c => c.tagName !== 'STYLE') : [];
    const allOff = kids.length > 0 && kids.every(c => c.classList.contains('hidden') || c.style.display === 'none');
    group.classList.toggle('hidden', allOff);
    const slot = group.querySelector('[data-acc-values]');
    if (!slot) return;
    const vals = _jobFormGroupValues(group.id);
    slot.textContent = vals.length ? vals.join(' · ') : (slot.dataset.accEmpty || '');
    slot.classList.toggle('is-empty', vals.length === 0);
    slot.title = vals.join(' · ');
  });
}

function _closeJobFormGroups() {
  document.querySelectorAll('#modal-job details.job-acc').forEach(g => { g.open = false; });
  _p811ClearStartBanner(); // TASK-9859 — a fresh form never carries the last ticket's suggestions
  _p811ResetFormPotential(); // TASK-9887 — nor the last form's potential
  // TASK-9859 — editJob closes the groups AFTER filling the tickets, so a
  // problem ticket re-opens Site here (fresh signature per open).
  const site = document.getElementById('job-acc-site');
  if (site) delete site.dataset.p811WarnOpened;
  try { _p811MaybeOpenSiteGroup(_getJobPermitIds()); } catch (_) { /* advisory only */ }
}

// TASK-9651 — the office job form's Project picker follows the 'projects'
// module (window.canAccessProjects, = firestore.rules projRead). Without it the
// whole Project section is hidden — the list would be empty anyway — and
// _jobFormProjectIdForSave keeps an edited job's stored link, so Add/Edit Job
// still saves for a dispatch-only login. The PIN driver form owns this section
// itself (_driverRefreshProjectOptions), so it is never touched here.
function _jobFormProjectsHidden() {
  return window.AppState?.driverModePin !== true && window.canAccessProjects?.() === false;
}
function _applyJobProjectAccess() {
  if (window.AppState?.driverModePin === true) return;
  const hide = _jobFormProjectsHidden();
  const section = document.getElementById('job-project-section');
  if (section) section.style.display = hide ? 'none' : '';
  if (hide && !document.getElementById('job-id')?.value) {
    const sel = document.getElementById('job-project-id');
    if (sel) sel.value = '';
    const cb = document.getElementById('job-add-to-project');
    if (cb) cb.checked = false;
    _releaseProjectJobDefaultClaims(); // TASK-4696 COD r3
  }
  // COD (UX): an edited job that is in a project keeps its customer for this
  // login (saveJob would refuse the change) — lock the picker and say why up
  // front, instead of a refusal on Save. Undone on the next open.
  const cust = document.getElementById('job-customer');
  if (cust) {
    if (cust.dataset.lockedByProjects === '1') {
      cust.disabled = false; cust.title = ''; delete cust.dataset.lockedByProjects;
    }
    if (hide && document.getElementById('job-id')?.value && _editJobOriginalProjectId) {
      cust.disabled = true; cust.title = 'This job is in a project. Changing its customer needs Projects access.';
      cust.dataset.lockedByProjects = '1';
    }
  }
}

// The picker IS the link: any project picked (or a new one being created) =
// linked; "No project" = not. #job-add-to-project is the hidden mirror every
// existing reader uses (saveJob validation, _jobFormProjectIdForSave, the dirty
// check, the copy carry), so the three open paths (showAddJob, editJob, the
// copy/project carry) and this change handler all keep it in step.
function _syncJobProjectLinkBox() {
  const cb = document.getElementById('job-add-to-project');
  if (!cb) return;
  const creating = document.getElementById('job-create-new-project')?.checked === true;
  cb.checked = creating || !!(document.getElementById('job-project-id')?.value);
}

// "No project" is the old untick, NOT a project with no PO: it leaves the PO box
// exactly as it is (a PO is a money field — never cleared as a side effect of
// unlinking). If that PO is one the project filled in, say so, so a dispatcher
// who meant to drop it can see it before saving. Picking a project runs the
// normal standing-PO fill.
function onJobProjectChange(selectEl) {
  const sel = selectEl || document.getElementById('job-project-id');
  // Wolverine 2026-10-21 — a price book picked from the Project list starts a
  // project of that name with this job. The "pb:" value never stays on the
  // select: it is turned into the inline New project form here.
  if (sel && String(sel.value || '').startsWith(_PB_OPT_PREFIX)) {
    _startProjectFromPriceBook(sel.value.slice(_PB_OPT_PREFIX.length));
    return;
  }
  _syncJobProjectLinkBox();
  try { populatePermitDropdown(); } catch (_) { /* TASK-9767 — repaint the project's 811 rows */ }
  if (sel && sel.value) {
    _renderJobProjectCustomerNote('', null); // a pick supersedes the "removed"/empty line
    // TASK-9606 — customer FIRST (its onCustomerChange may drop a blanket
    // contract PO in), then the project's own PO, as the project New Job path does.
    _fillCustomerFromProject(sel.value);
    applyProjectPo(sel);
    applyProjectJobDefaults(sel.value); // TASK-4696
    _fillJobSiteFromProject(sel.value); // Wolverine 2026-10-21
    _fillJobTitleFromProject(sel); // TASK-10126
    return;
  }
  _releaseProjectJobDefaultClaims(); // TASK-4696 — COD r1: unlinked values are the job's now
  _showUnlinkedProjectPoHint();
}

// TASK-10126 (Guy 10/21) — company setting settings.jobTitleFromProject
// (absent = OFF, only === true turns it on). With it on, picking a project on a
// NEW job (office or driver Add Job — same form) puts the project's name in an
// EMPTY title. A title the user typed is never touched: we only replace a value
// WE filled on this form (dataset.autofilledTitle, stamped with _jobFormGen so
// a previous form's stamp can never claim this one's title — the job-location
// autofilledLoc pattern). Edit Job never changes a saved title. "No project"
// leaves the title as it is.
function _jobTitleFromProjectName(sel) {
  const opt = sel?.selectedOptions?.[0] || null;
  const fromOpt = String(opt?.dataset?.name || '').trim(); // driver list paints data-name
  if (fromOpt) return fromOpt;
  const id = sel?.value || '';
  if (!id) return '';
  const all = (window.ProjectsModule?.getActiveProjects?.() || [])
    .concat(window.ProjectsModule?.getState?.()?.projects || []);
  const p = all.find(x => x && x.id === id);
  return String(p?.name || '').trim();
}
function _fillJobTitleFromProject(sel) {
  if (window.AppState?.settings?.jobTitleFromProject !== true) return false;
  if (document.getElementById('job-id')?.value) return false; // new jobs only
  const title = document.getElementById('job-title');
  if (!title || title.readOnly || !sel?.value) return false;
  const name = _jobTitleFromProjectName(sel).slice(0, 100); // job-title data-maxlen
  if (!name) return false;
  const cur = String(title.value || '').trim();
  const ours = title.dataset.autofilledTitleGen === String(_jobFormGen)
    ? String(title.dataset.autofilledTitle || '') : '';
  if (cur && !(ours && cur === ours)) return false; // typed (or not ours) — never overwrite
  if (cur === name) return false;
  title.value = name;
  title.dataset.autofilledTitle = name;
  title.dataset.autofilledTitleGen = String(_jobFormGen);
  // COD (Code Reviewer): any keystroke in the title — even retyping the same
  // text — makes it the user's. Programmatic .value sets fire no input event.
  if (!title.dataset.autofilledTitleBound) {
    title.dataset.autofilledTitleBound = '1';
    title.addEventListener('input', () => {
      delete title.dataset.autofilledTitle;
      delete title.dataset.autofilledTitleGen;
    });
  }
  return true;
}

function _showUnlinkedProjectPoHint() {
  const hint = document.getElementById('job-project-po-hint');
  if (!hint) return;
  const po = document.getElementById('job-po');
  const val = String(po?.value || '').trim();
  const ours = String(po?.dataset?.autofilledPo || '').trim();
  if (val && ours && val === ours) {
    hint.textContent = `PO ${val} came from the project and is still on this job. Clear the PO Number below if this job bills differently.`;
    hint.style.display = 'block';
  } else {
    hint.textContent = '';
    hint.style.display = 'none';
  }
}

// Closes the inline "New project" form WITHOUT touching the link mirror — the
// open paths set that themselves, from the job, right after.
function _resetJobNewProjectMode() {
  const np = document.getElementById('job-create-new-project');
  if (np) np.checked = false;
  const fields = document.getElementById('job-new-project-fields');
  if (fields) fields.style.display = 'none';
  const sel = document.getElementById('job-project-id');
  if (sel) sel.disabled = false;
  const btn = document.getElementById('btn-job-new-project');
  if (btn) btn.setAttribute('aria-expanded', 'false');
  const head = document.getElementById('job-new-project-heading');
  if (head) head.textContent = 'New project';
  const blank = sel?.querySelector('option[value=""]');
  if (blank) blank.textContent = 'No project'; // _startProjectFromPriceBook relabels it
}

// "+" beside the picker opens the inline New project form; its Cancel closes it.
// Same state the old nested checkbox drove: the create flag on, the picker
// disabled (saveJob's create-new branch wins over a picked project anyway).
function setJobNewProjectMode(on) {
  if (!on) {
    _resetJobNewProjectMode();
    _syncJobProjectLinkBox();
    document.getElementById('job-project-id')?.focus();
    return;
  }
  const np = document.getElementById('job-create-new-project');
  const fields = document.getElementById('job-new-project-fields');
  if (!np || !fields) return;
  np.checked = true;
  fields.style.display = 'block';
  const sel = document.getElementById('job-project-id');
  if (sel) sel.disabled = true;
  document.getElementById('btn-job-new-project')?.setAttribute('aria-expanded', 'true');
  _syncJobProjectLinkBox();
  document.getElementById('job-new-project-name')?.focus();
}

// TASK-4688 — anything that fills the form PROGRAMMATICALLY (Smart Job Builder,
// Copy to New Job) can drop a value into a hidden section the dispatcher cannot
// see. Re-read the shape straight off the DOM and let the has-data rule above
// reveal it. Cheap, and it needs no job doc.
function _readJobFormShape() {
  const v = id => document.getElementById(id)?.value || '';
  const c = id => document.getElementById(id)?.checked === true;
  return {
    vendorNumber: v('job-vendor-number'),
    afeNumber: v('job-afe'),
    interstate: c('job-interstate'),
    hazmat: c('job-hazmat'),
    liquidTank: c('job-liquid-tank'),
    splitBy: v('job-split-by'),
    waypoints: [...document.querySelectorAll('#job-waypoints-list .waypoint-name')]
      .map(i => ({ name: i.value || '' })),
    geofence: { enabled: c('job-geofence-enabled') },
    photos: (DispatchState.pendingJobPhotos || []).concat(DispatchState.existingJobPhotos || []),
    permits: (DispatchState.pendingPermitFiles || []).concat(DispatchState.existingPermitFiles || []),
  };
}

function revealPopulatedJobFormSections() {
  try { applyJobFormSectionVisibility(_readJobFormShape()); } catch (_) { /* never block a parse */ }
}

function isLoadCalcEnabled() {
  return window.AppState?.settings?.enableLoadCalculator === true || isDemoMode();
}

function isRoutePlannerEnabled() {
  return window.AppState?.settings?.enableRoutePlanner === true || isDemoMode();
}

// ============================================
// HARD-STOP COMPLIANCE GATE (TASK-258 v1.15.535)
// ============================================
// Mirrored from public/src/views/compliance.js daysUntilExpiry() — local copy
// because importing across cache busters would create two module instances per
// CLAUDE.md pitfall #1. Returns null for missing/invalid date, otherwise days
// from today (negative = past, 0 = today, >0 = future).
function _hsDaysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  // TASK-4739 — date-only strings anchor NOON LOCAL. A bare YYYY-MM-DD parses as
  // UTC midnight and the setHours(0,0,0,0) below then normalises it to the LOCAL
  // day that instant falls in — the PREVIOUS day in every US timezone, which made
  // every expiry countdown read one day early. Guarded so a caller passing a full
  // ISO timestamp keeps its own offset semantics, mirroring coerceJobTimestamp.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(String(dateStr).trim())
    ? new Date(String(dateStr).trim() + 'T12:00:00')
    : new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

function _hsIsHardStopEnabled() {
  return window.AppState?.tenantData?.compliance?.hardStopExpiredCredentials === true;
}

// Aggregate expired credentials for a (driver, [trucks]) assignment attempt.
// Returns array of { personId, personName, expired:[{kind,label,daysOverdue,detail?}] }
// for any driver+truck combo with at least one expired credential.
function _hsCollectExpiredForAssignment(drivers, trucks) {
  const out = [];
  if (!Array.isArray(drivers) || drivers.length === 0) return out;
  for (const d of drivers) {
    const expired = [];
    // TASK-934 — credential checks respect the canonical driver classification
    // (shared window.DqMatrix — imported once by hr.js; never a second ?v=
    // import per pitfall #1). A non_cdl_driver isn't blocked on a stale CDL
    // date and a non_driver isn't blocked on either. Unclassified people
    // resolve to the FULL checklist (both checks — exactly the old behavior),
    // and if the matrix somehow isn't loaded we fail CLOSED to full checks.
    const dqItems = window.DqMatrix?.getApplicableDqItems(d).items
      || { cdlValid: true, medCard: true };
    const cdlDays = dqItems.cdlValid ? _hsDaysUntilExpiry(d.cdlExpiryDate) : null;
    if (cdlDays !== null && cdlDays < 0) {
      expired.push({ kind: 'cdl', label: 'CDL', daysOverdue: Math.abs(cdlDays) });
    }
    const medDays = dqItems.medCard ? _hsDaysUntilExpiry(d.medCardExpiryDate) : null;
    if (medDays !== null && medDays < 0) {
      expired.push({ kind: 'medcard', label: 'Medical Card', daysOverdue: Math.abs(medDays) });
    }
    // AVI: any truck on this assignment with expired inspectionExpiry expires
    // every driver on the assignment (they share the equipment).
    if (Array.isArray(trucks)) {
      for (const t of trucks) {
        const inspDays = _hsDaysUntilExpiry(t?.inspectionExpiry);
        if (inspDays !== null && inspDays < 0) {
          expired.push({
            kind: 'avi',
            label: 'Annual Vehicle Inspection',
            daysOverdue: Math.abs(inspDays),
            detail: t.unit || t.nickname || t.name || t.id
          });
        }
      }
    }
    if (expired.length > 0) {
      out.push({ personId: d.id, personName: d.name || 'Unknown', expired });
    }
  }
  return out;
}

// TASK-258 — gate the override button on admin / owner / founder. Any of
// these may bypass the hard-stop (with audit trail). Dispatch.js is reached
// only by users with dispatcher+ access — but the override still requires
// admin/owner/founder explicitly (Crown's #1 ask: dispatchers can't roll
// expired credentials on their own).
//
// TASK-932 — the founder arm was a TENANT test (`isFounderAccount()`), so every
// session on that tenant, dispatchers included, got the override — the exact
// thing this gate exists to stop. Members are judged by their own doc. A session
// with NO member doc is the shared owner login — often a yard KIOSK where a
// dispatcher unlocked only the dispatch PIN — so it is NOT owner-level by
// default (COD round 2: hrEffectiveAdmin's `ma == null → true` is the wrong twin
// here). It gets the override only with owner-level kiosk authority:
// canManageAccessPins() = no PINs configured (owner-only tenant) OR the device
// holds the PRIMARY PIN. Fails closed if the helper is missing.
function _hsCanOverrideAssignment() {
  const A = window.AppState || {};
  if (A.isPublicAccess || A.driverMode || A.driverModePin) return false;
  if (A.memberAccessPending) return false;
  if (!A.memberAccess) {
    try {
      return typeof window.canManageAccessPins === 'function' && window.canManageAccessPins() === true;
    } catch (_) { return false; }
  }
  const role = A.memberAccess.role;
  if (role !== 'owner' && role !== 'admin') return false;
  // COD round 3 — a PIN-locked yard tablet runs on the OWNER's login (PIN holders
  // share the owner uid, so memberAccess IS the owner doc). When the owner has
  // deliberately locked this device (TASK-719 isOwnerDeviceLocked), the person at
  // the screen is whoever unlocked a PIN — require the Primary PIN, same as the
  // no-member-doc kiosk above. Unlocked owner/admin devices are unchanged.
  let locked = false;
  try { locked = typeof window.isOwnerDeviceLocked === 'function' && window.isOwnerDeviceLocked() === true; } catch (_) { locked = false; }
  if (!locked) return true;
  try {
    return typeof window.canManageAccessPins === 'function' && window.canManageAccessPins() === true;
  } catch (_) { return false; }
}

// Build & show the assignment hard-stop modal. Returns a promise that resolves
// with { proceeded: bool, reason?: string } so the caller can branch:
// proceeded=false → stay blocked (default Close click).
// proceeded=true  → admin override; caller continues + writes HARD_STOP_OVERRIDE audit.
function _hsShowAssignmentBlockModal(blocked) {
  return new Promise((resolve) => {
    const driversHtml = blocked.map(b => {
      const items = b.expired.map(e => {
        const suffix = e.detail ? ` <span style="color:var(--text-secondary);">(${String(e.detail).replace(/[<>]/g,'')})</span>` : '';
        return `<li style="margin:3px 0;color:var(--text-primary);font-size:14px;">
          <strong>${e.label}</strong> — expired ${e.daysOverdue} day${e.daysOverdue === 1 ? '' : 's'} ago${suffix}
        </li>`;
      }).join('');
      return `<div style="margin:0 0 12px 0;padding:10px;background:var(--bg-input,#F8FAFC);border-left:3px solid #dc2626;border-radius:6px;">
        <div style="font-weight:600;color:var(--text-primary);margin-bottom:4px;">${String(b.personName).replace(/[<>]/g,'')}</div>
        <ul style="margin:4px 0 0 18px;padding:0;">${items}</ul>
      </div>`;
    }).join('');
    const canOverride = _hsCanOverrideAssignment();
    const overrideBtn = canOverride
      ? `<button type="button" class="btn btn-warning" id="hs-assignment-hardstop-override" style="min-height:44px;margin-right:8px;">Admin Override</button>`
      : '';
    const wrap = document.createElement('div');
    wrap.className = 'modal';
    wrap.id = 'hs-assignment-hardstop-modal';
    wrap.style.cssText = 'display:flex;position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:9999;align-items:center;justify-content:center;';
    wrap.innerHTML = `
      <div class="modal-content modal-fullscreen" style="max-width:520px;background:var(--bg-card);border-radius:12px;display:flex;flex-direction:column;">
        <div class="modal-header" style="padding:20px 20px 12px 20px;border-bottom:1px solid var(--border-light);">
          <h3 style="margin:0;color:var(--text-primary);display:flex;align-items:center;gap:10px;font-size:18px;">
            <span class="material-icons" style="color:#dc2626;">block</span>
            Cannot assign driver — credential expired
          </h3>
        </div>
        <div class="modal-body" style="padding:20px;flex:1;overflow-y:auto;min-height:0;">
          <p style="margin:0 0 12px 0;color:var(--text-primary);font-size:14px;">
            This assignment has ${blocked.length} driver${blocked.length === 1 ? '' : 's'} with expired credential${blocked.length === 1 && blocked[0].expired.length === 1 ? '' : 's'}. Update Roster before assigning to a job.
          </p>
          ${driversHtml}
          <p style="margin:8px 0 0 0;color:var(--text-secondary);font-size:13px;">This block is enforced per the tenant's hard-stop policy. Contact your dispatcher or compliance partner to renew.</p>
        </div>
        <div class="modal-footer" style="padding:12px 20px 20px 20px;border-top:1px solid var(--border-light);flex-shrink:0;display:flex;justify-content:flex-end;align-items:center;">
          ${overrideBtn}
          <button type="button" class="btn btn-primary" id="hs-assignment-hardstop-ok" style="min-height:44px;">Close</button>
        </div>
      </div>`;
    document.body.appendChild(wrap);
    const cleanup = (result) => { wrap.remove(); resolve(result); };
    wrap.querySelector('#hs-assignment-hardstop-ok').addEventListener('click', () => cleanup({ proceeded: false }));
    if (canOverride) {
      wrap.querySelector('#hs-assignment-hardstop-override').addEventListener('click', () => {
        const reason = (window.prompt(
          'Admin override — this assignment will be audit-logged. Enter reason (min 8 chars):',
          ''
        ) || '').trim();
        if (!reason || reason.length < 8) {
          window.showToast?.('Override cancelled — reason required (min 8 chars).', 'warning');
          return; // keep modal open
        }
        cleanup({ proceeded: true, reason });
      });
    }
  });
}

// ============================================
// TASK-9227 — THE COMPLIANCE FLAG REACHES THE ASSIGNMENT SURFACE
// ============================================
// A compliance partner flagged a real TITS driver NOT LEGAL on 2026-07-31 and he
// stayed on the `drivers` array of jobDays for six weeks. The flag was in the
// data the whole time; it reached nobody who assigns work. dispatch.js already
// LOADS `people.flagged` — the people subscription spreads whole docs, so every
// DispatchState.people[*] carries it — and before this read it exactly zero
// times (19 `flagged` hits in this file, all of them the English word in a
// comment or the TASK-9275 asset-stock pill).
//
// 🔴 WARN, NOT BLOCK — a decision, not an omission:
//  • jobDays writes are direct client→Firestore. There is no updateJobDay /
//    assignDriver callable to hook, and the jobDays update rule is field-agnostic
//    about flags, so a UI-only "block" reproduces this very defect one layer
//    down: the dispatcher sees a wall, the SDK does not. Reproduced 2026-09-22 on
//    `_e2e_flag9227` — a carrier owner's own ID token PATCHed a flagged person
//    into BOTH `drivers` and `helpers` over REST, HTTP 200 both times.
//  • 4 of the 9 paths that seat crew COPY it from another day with NO HUMAN
//    present (date-range extend, buildInheritedJobDay ×3). A block there blames
//    whoever saved today for a decision someone else made a week ago — and on the
//    date-sync path there is nobody on screen to block.
//  • TASK-428/489 precedent: a tightening fix on this surface stranded two real
//    people clocked in 14+ hours. A dispatcher who cannot crew a truck at 5am is
//    a worse outcome than a visible warning.
//  • The FCRA carve-out is unresolved (Chris-only): if a block traces back to
//    MVR / Clearinghouse / PSP findings, refusing dispatch work can function as
//    an adverse employment action and pull in pre-adverse notice sequencing.
//    `warn` does not raise this.
// So: every path SHOWS the flag and every seating is RECORDED. Nothing is
// refused. `block` needs a rules predicate or a gated callable first.
//
// 🔒 WHAT DISPATCH IS ALLOWED TO SAY: attribution + age ONLY. The free-text
// reason lives in the server-only `tenants/{tid}/driverFlags/{personId}` doc
// (rules: read = tenant admin or managing partner, write = false) and is NEVER
// fetched here. `flagged` / `flaggedByEmail` / `flaggedAt` are already on the
// person doc dispatch loads, so this adds NO new read and needs NO rules change
// — which is also why it cannot leak the reason to a driver-PIN session.
//
// Semantic warning colour, deliberately NOT the brand accent (--brand-blue /
// --accent-teal): a flag is a severity signal, not chrome. Literal hex rather
// than a var() so an undefined custom property can never silently kill the
// property. #B45309 on #FFFBEB is the same amber compliance.js already uses for
// the unflagged state of its own flag button.
const _DF_WARN_FG = '#B45309';
const _DF_WARN_BG = '#FFFBEB';
const _DF_WARN_BORDER = '#FCD34D';

// Whole days since the flag was set. Accepts a Firestore Timestamp, a Date, an
// epoch ms number, a {seconds} shape or an ISO string — the person doc reaches
// dispatch through a snapshot (Timestamp) but through demoSeeder and the search
// cache as other shapes, and a formatter that only knows one of them reports the
// age of every flag as "unknown" without failing.
function _dfAgeDays(flaggedAt) {
  if (!flaggedAt) return null;
  let d = null;
  try {
    if (typeof flaggedAt?.toDate === 'function') d = flaggedAt.toDate();
    else if (flaggedAt instanceof Date) d = flaggedAt;
    else if (typeof flaggedAt === 'number') d = new Date(flaggedAt);
    else if (typeof flaggedAt?.seconds === 'number') d = new Date(flaggedAt.seconds * 1000);
    else d = new Date(flaggedAt);
  } catch (_) { return null; }
  if (!d || isNaN(d.getTime())) return null;
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  return days < 0 ? 0 : days;
}

// CLAUDE.md pitfall #5 — GAS-imported crew arrays hold NAME strings, not ids, so
// an id-only lookup silently does not fire for exactly the legacy population
// most likely to carry a stale record. DispatchState.people is pre-filtered to
// `active !== false`; peopleAll is the unfiltered roster, and a flagged person
// who was also deactivated must still resolve when their id is sitting in a
// jobDay array.
function _dfPersonById(ref) {
  if (!ref) return null;
  const live = DispatchState.people || [];
  const all = DispatchState.peopleAll || [];
  return live.find(p => p.id === ref || p.name === ref)
      || all.find(p => p.id === ref || p.name === ref)
      || null;
}

// refs → [{ personId, personName, by, ageDays }] for the FLAGGED ones only.
// Deduped: the same person can legitimately appear in drivers AND helpers.
function _dfFlaggedEntries(refs) {
  const out = [];
  const seen = new Set();
  for (const ref of (Array.isArray(refs) ? refs : [])) {
    if (!ref || seen.has(ref)) continue;
    seen.add(ref);
    const p = _dfPersonById(ref);
    if (!p || p.flagged !== true) continue;
    if (out.some(e => e.personId === p.id)) continue; // id + name both pointed here
    out.push({
      personId: p.id,
      personName: p.name || 'Unknown',
      by: String(p.flaggedByEmail || '').trim(),
      ageDays: _dfAgeDays(p.flaggedAt),
    });
  }
  return out;
}

// "office@crowncompliance.com, 53 days" — attribution + age, never the reason.
function _dfMeta(entry) {
  const bits = [];
  if (entry?.by) bits.push(entry.by);
  const age = entry?.ageDays;
  if (age !== null && age !== undefined) {
    bits.push(age === 0 ? 'today' : `${age} day${age === 1 ? '' : 's'}`);
  }
  return bits.join(', ');
}

// The in-row badge. THIS is the real signal — a modal at 5am gets reflex-clicked,
// a badge sitting next to the name before anyone picks it does not.
// `flaggedByEmail` is tenant-writable-adjacent free text, so the title attribute
// goes through escAttr (the `onclick="fn('${esc(x)}')"` class of bug does not
// apply here — this is a plain attribute, not a JS string inside one).
function _dfBadgeHtml(person) {
  if (person?.flagged !== true) return '';
  const entry = {
    personName: person.name,
    by: String(person.flaggedByEmail || '').trim(),
    ageDays: _dfAgeDays(person.flaggedAt),
  };
  const meta = _dfMeta(entry);
  const escAttrFn = window.escAttr || escapeHtml;
  const tip = escAttrFn('Flagged for follow-up' + (meta ? ' — ' + meta : '')
    + '. The reason is in Compliance.');
  const age = entry.ageDays;
  const ageTxt = (age === null || age === undefined) ? '' : (age === 0 ? ' today' : ` ${age}d`);
  return `<span class="checkbox-driver-flag" title="${tip}" style="display:inline-flex;align-items:center;gap:3px;margin-left:6px;padding:1px 7px;border:1px solid ${_DF_WARN_BORDER};background:${_DF_WARN_BG};color:${_DF_WARN_FG};border-radius:10px;font-size:0.68rem;font-weight:700;white-space:nowrap;"><span class="material-icons" style="font-size:12px;">flag</span>Flagged${escapeHtml(ageTxt)}</span>`;
}

// One audit shape for every flag event on this surface, so "was anyone warned
// before this person ran?" is answerable from `auditLog` rather than guessed at.
// module 'compliance' → TTL-exempt, same as the TASK-258 hard-stop rows.
function _dfLog(action, entries, ctx) {
  if (!entries || entries.length === 0) return;
  try {
    window.logAudit?.('compliance', action, {
      source: 'dispatch',
      surface: ctx?.surface || 'unknown',
      jobId: ctx?.jobId || null,
      jobDayId: ctx?.jobDayId || null,
      dayCount: ctx?.dayCount || null,
      flaggedCount: entries.length,
      flagged: entries.map(e => ({
        personId: e.personId,
        personName: e.personName,
        flaggedBy: e.by || null,
        ageDays: e.ageDays,
      })),
      at: new Date().toISOString(),
    });
  } catch (err) { console.warn('[Dispatch] TASK-9227 flag audit failed:', err); }
}

// The no-human paths: crew was COPIED onto a day nobody is looking at. Say who,
// say how old, say how many days — then record it. Never blocks, because there
// is nothing here to block.
function _dfNoticeEntries(entries, ctx) {
  if (!entries || entries.length === 0) return;
  const names = entries.map(e => e.personName).join(', ');
  const oldest = entries.reduce((m, e) => (
    (e.ageDays !== null && e.ageDays !== undefined && e.ageDays > m) ? e.ageDays : m
  ), 0);
  const where = (ctx?.dayCount > 1) ? ` onto ${ctx.dayCount} days` : '';
  const age = oldest > 0 ? ` Flagged ${oldest} day${oldest === 1 ? '' : 's'} ago.` : '';
  window.showToast?.(
    `Flagged for follow-up: ${names}${where}.${age} Check Compliance before this crew runs.`,
    'warning', null, 10000
  );
  _dfLog(ctx?.action || 'FLAGGED_CREW_CARRIED', entries, ctx);
}

// TASK-9227 — the crew that gets WRITTEN is re-read after the confirm resolves,
// and the checkboxes stay live while the save awaits the network (toggleJobCheckbox
// never consults `__saveJobInFlight`). So a flagged person ticked during that
// window would be seated having been vetted against a different set — the same
// "the guard checked one thing and the write did another" shape TASK-4655 hit in
// this file. Nothing is refused here: the write is already in flight and there is
// no honest way to block it. Name the person and record it.
// `vettedIds` is the Set the dialog actually showed.
function _dfNoticeUnvetted(refs, vettedIds, ctx) {
  try {
    const late = _dfFlaggedEntries(refs).filter(e => !vettedIds.has(e.personId));
    if (late.length === 0) return;
    _dfNoticeEntries(late, { ...ctx, action: 'FLAGGED_CREW_ASSIGNED_UNVETTED' });
  } catch (e) { console.warn('[Dispatch] TASK-9227 post-confirm crew re-check failed:', e); }
}

// The human paths: ask, then record either answer. Returns { ok, entries }.
//
// 🔴 FAILS OPEN ON PURPOSE. `window.showConfirm` is the RECEIPT, not the gate —
// if it is somehow missing, `(await undefined) === true` would be false and this
// warn-mode feature would become a SILENT BLOCK on every save, which is the
// TASK-428 failure wearing a guard's clothes. Absent helper → surface + record,
// and let the save through.
async function _dfConfirmSeating(refs, ctx) {
  const entries = _dfFlaggedEntries(refs);
  if (entries.length === 0) return { ok: true, entries: [] };
  if (typeof window.showConfirm !== 'function') {
    _dfNoticeEntries(entries, { ...ctx, action: 'FLAGGED_CREW_ASSIGNED' });
    return { ok: true, entries };
  }
  const lines = entries.map(e => {
    const meta = _dfMeta(e);
    return `• ${e.personName} — flagged for follow-up${meta ? ' by ' + meta : ''}`;
  }).join('\n');
  const ok = (await window.showConfirm(
    entries.length === 1 ? 'This person is flagged for follow-up' : `${entries.length} people are flagged for follow-up`,
    `${lines}\n\nSomeone — your compliance partner or your own office — marked ${entries.length === 1 ? 'this person' : 'these people'} as needing attention. The reason is on the Compliance page.\n\nAssign anyway?`,
    { confirmText: 'Assign Anyway', cancelText: 'Go Back', danger: true }
  )) === true;
  _dfLog(ok ? 'FLAGGED_CREW_ASSIGNED' : 'FLAGGED_CREW_ASSIGN_DECLINED', entries, ctx);
  return { ok, entries };
}

// ============================================
// TASK-1615 — MAP COORDINATE VALIDATION
// ============================================
// A stored waypoint coordinate is only usable if it is a finite number inside
// the real lat/lng ranges. Truthiness (`wp.lat && wp.lng`) is NOT enough: TITS
// carries waypoints written as whole-integer garbage (lat 5304 / lng 105 and
// seven more like it, no decimal point anywhere). One of those fed to a shared
// LatLngBounds makes fitBounds honour a box that spans the globe, and the whole
// day's dispatch map lands at zoom 0-1 — which reads as "the map did not load".
//
// DROP the bad point, do NOT clamp it. Clamping lat 5304 to 90 is a confident
// lie: it plants a pin at the North Pole that a dispatcher could route or bill
// against, and the bounds box still stretches pole-to-Williston, so it does not
// even fix the zoom. Omitting the point is honest — and the map header names
// the count, because a map that quietly swallows a stop is its own bug.

// Exact 0,0 is the Gulf of Guinea and the classic "unset" sentinel. Reject the
// PAIR only — lat 0 alongside a real longitude is a legitimate equator point.
const MAP_NULL_ISLAND_EPS = 1e-7;

// Numbers may arrive as numeric strings from older write paths. Empty string,
// null and undefined must NOT coerce to 0 (that is how a missing longitude
// silently becomes a pin in the North Sea), so they are rejected here.
function coordToNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return NaN;
}

// Returns a clean { lat, lng } number pair, or null if the point must not be
// plotted / fed to bounds. Single source of truth for every map coordinate.
function plottableCoord(wp) {
  if (!wp) return null;
  const lat = coordToNumber(wp.lat);
  const lng = coordToNumber(wp.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat > 90 || lat < -90) return null;
  if (lng > 180 || lng < -180) return null;
  if (Math.abs(lat) < MAP_NULL_ISLAND_EPS && Math.abs(lng) < MAP_NULL_ISLAND_EPS) return null;
  return { lat, lng };
}

// True when SOMETHING was written into lat/lng. Separates "this stop was never
// geocoded" (counted as "without coordinates", already surfaced) from "this stop
// has a coordinate and it is garbage" (new, distinct, and fixable).
function coordWasProvided(wp) {
  const filled = v => v !== null && v !== undefined && !(typeof v === 'string' && v.trim() === '');
  return !!wp && (filled(wp.lat) || filled(wp.lng));
}

// True when a waypoint carries a coordinate that we refuse to plot.
function coordIsBad(wp) {
  return coordWasProvided(wp) && !plottableCoord(wp);
}

// TASK-4786 — split a job's waypoints[] into the three things a dispatcher can
// actually act on. Pulled out of renderMapView as a pure function so the map's
// most load-bearing arithmetic is testable without a Google Maps stub
// (tests/dispatch-map-every-stop.unit.cjs).
//
// WHY THIS EXISTS: renderMapView used to take `plottableWps[0]` and pin only
// that, then draw the polyline through EVERY waypoint. Measured on live TITS
// 2026-09-08: 276 of 697 plottable stops (39.6%) were a line vertex with no
// marker on them. Chris: "r/n i see a waypoint and a wird blue line."
//
// The three buckets never overlap and always sum to waypoints.length:
//   plottable — a real coordinate, gets a pin
//   bad       — SOMETHING was stored in lat/lng and it is out of range
//   unlocated — the stop was never geocoded at all (771 of 2,541 TITS
//               waypoints as of 2026-09-08, mostly unresolved short links)
// `unlocated` is deliberately returned rather than dropped: a stop we cannot
// draw is information the dispatcher needs, not an absence. It is what the
// header's "stops not plotted" chip counts.
//
// `stopNumber` is 1-based POSITION IN THE ROUTE, not position among the
// plottable ones — stop 3 stays "3" on the pin even when stop 2 has no
// coordinate, so the pin numbers match the job's stop list.
function classifyJobStops(waypoints) {
  const plottable = [], bad = [], unlocated = [];
  (waypoints || []).forEach((wp, i) => {
    const stopNumber = i + 1;
    const c = plottableCoord(wp);
    if (c) plottable.push({ ...wp, lat: c.lat, lng: c.lng, stopIndex: i, stopNumber });
    else if (coordWasProvided(wp)) bad.push({ ...wp, stopIndex: i, stopNumber });
    else unlocated.push({ ...wp, stopIndex: i, stopNumber });
  });
  return { plottable, bad, unlocated, total: (waypoints || []).length };
}

// TASK-9497 — the server (onJobStopsGeocode / the backfill) applies only
// confident fixes; a loose one is parked on the stop as `geoSuggestion` for a
// person to confirm from the "not plotted" list. Pure: the suggestion if it is
// a usable point, with its reason in plain words, else null. Confirming goes
// through applyStopLocationSuggestion, which re-reads the stored suggestion —
// the coordinate never comes from here.
const STOP_SUGGESTION_WHY = {
  far: 'far from the job’s other destinations',
  'name-only': 'matched on the destination name only',
  'name-match': 'same name as one of your saved locations',
  'partial-match': 'only part of the address matched',
  ambiguous: 'more than one place matched',
  imprecise: 'only an approximate area matched'
};
function stopSuggestionView(wp) {
  const s = wp && wp.geoSuggestion;
  if (!s || typeof s !== 'object' || !plottableCoord(s)) return null;
  return { label: String(s.label || 'Suggested location').slice(0, 160), why: STOP_SUGGESTION_WHY[s.why] || 'needs a check' };
}

// ── TASK-9174 — THE ROAD, NOT A DIAGONAL ACROSS MONTANA ─────────────────────
//
// WHY THIS EXISTS: the map drew `new google.maps.Polyline({ path: stops, geodesic: true })`
// over RAW STOP COORDINATES, so a Bozeman MT → Williston ND job rendered as one
// straight line across the whole of Montana. A dispatcher cannot read miles,
// hours or highway off that — it is decoration that looks like information,
// which is worse than no line at all. Chris: "these straight lines aren't worth
// shit."
//
// The road shape was already being fetched and thrown away: getDirections read
// `routes[0].legs` for distance/duration and dropped
// `routes[0].overview_polyline.points` from the very same response. The server
// half (93d5bb21) now returns it, plus a `routeStatus` discriminator.
//
// 🔴 THE RULE THAT SHAPES ALL OF THIS — RESOLVE EAGERLY ON SAVE, NEVER FROM A
// REPAINT. renderMapView re-runs on every range preset click, every custom
// Apply and every jobs/jobDays snapshot settle. A per-job Directions call from
// there is a request storm against a billed API (44 jobs in TITS's live recent
// window alone). So the geometry is computed ONCE when a job's stops change and
// cached on the job document; the map only ever DECODES what is already there.
//
// 🔴 `routeStatus === 'OK'` IS THE ONLY VALUE MEANING "HERE IS THE ROAD."
// Everything else — NO_GEOMETRY, ZERO_RESULTS, NOT_FOUND, CONFIG_ERROR,
// REQUEST_FAILED, MAX_WAYPOINTS_EXCEEDED — means there is no road answer, and
// the map must show its own VISIBLY DIFFERENT line rather than pretend.
// Do not key off Google's raw status: a degenerate route (origin == destination)
// comes back status OK with a valid ONE-POINT polyline that draws nothing, which
// is exactly why the server gates on drawability and reports NO_GEOMETRY.

// 5 decimals ≈ 1.1 m — finer than any address-level geocode, coarse enough that
// float noise never invalidates a cache.
const MAP_ROUTE_FP_PRECISION = 5;
// A polyline longer than this is not a route anybody can read, and `jobs`
// streams to every session over onSnapshot. Bound the document growth here
// rather than discover the ceiling in production.
const MAP_ROUTE_MAX_POLYLINE_CHARS = 8000;
// ── TASK-9734 — every multi-stop job gets its road route, with no click ──────
// TASK-9174 resolved on SAVE only, so a job written any other way (seed
// script, connector, driver Add Job, anything older than the ship) sat on the
// stops-only fallback until somebody clicked "Draw road routes". show-account
// had 0 of 197 routed.
//
// The cost rule still stands: ONE Directions lookup per job per change of
// stops, never one per repaint. What changed is WHO starts it: the map queues
// the jobs on screen that have no answer for their CURRENT stops and works
// through them in the background (autoWarmMapRoutes), paced well under the
// getDirections per-user limit (30/min, shared with geocoding). Three things
// keep it to one lookup:
//   1. the answer is cached on the job (routeCache, fingerprinted by stops);
//   2. a session-local "already tried this fingerprint" set, so a snapshot
//      that has not caught up yet cannot re-queue the same job;
//   3. a LEASE written into routeCache (routeStatus 'PENDING') inside a
//      transaction before the call, so two dispatchers with the map open (or
//      a save-path kick racing the map) never both pay for the same job.
// Every write stays inside `routeCache`: it is on the server's
// JOB_BOOKKEEPING_FIELDS list, so none of them can text a crew.
const MAP_ROUTE_WARM_BATCH = 8;
const MAP_ROUTE_WARM_CONCURRENCY = 2;
// Pause between background batches: 8 per 30 s = 16/min at most.
const MAP_ROUTE_WARM_PAUSE_MS = 30 * 1000;
// A session that took the lease and died (tab closed mid-call) must not hold
// the job hostage — after this anyone may look it up again.
const MAP_ROUTE_LEASE_MS = 90 * 1000;
// Transient failures retry on their own: 5 min, 10, 20 … capped at 6 h.
const MAP_ROUTE_RETRY_BASE_MS = 5 * 60 * 1000;
const MAP_ROUTE_RETRY_MAX_MS = 6 * 60 * 60 * 1000;
// 🔴 CACHE THE FAILURES TOO, but only the DETERMINISTIC ones. A job whose stops
// have no road between them must not be re-asked forever. A network blip or a
// missing API key is NOT a property of that job — caching those would
// permanently brand a perfectly routable job as unroutable. TASK-9734: they ARE
// written now, but as `transient: true` with a `retryAfterMs`, so the map can
// say "lookup failed, retrying" instead of "no route", and the background warm
// retries them on a backoff instead of never.
const MAP_ROUTE_CACHEABLE_FAILURES = new Set([
  'NO_GEOMETRY', 'ZERO_RESULTS', 'NOT_FOUND', 'MAX_WAYPOINTS_EXCEEDED',
  'INVALID_REQUEST', 'GEOMETRY_TOO_LARGE'
]);

// The stale-cache detector. Rounded lat/lng in ROUTE ORDER, prefixed with the
// count so [A,B] and [A,B,B] can never collide. '' means "fewer than two
// plottable stops" — nothing to route, and nothing to cache.
function routeWaypointsFingerprint(waypoints) {
  const pts = (waypoints || []).map(wp => plottableCoord(wp)).filter(Boolean);
  if (pts.length < 2) return '';
  return pts.length + ':' + pts
    .map(c => c.lat.toFixed(MAP_ROUTE_FP_PRECISION) + ',' + c.lng.toFixed(MAP_ROUTE_FP_PRECISION))
    .join('|');
}

// The cached route for a job, or null when there isn't a usable one. A cache
// whose fingerprint does not match the job's CURRENT stops is STALE and is
// treated as absent — the alternative is drawing last week's road over this
// week's stops, which is the same lie in a different costume.
function jobCachedRoute(job) {
  const rc = job && job.routeCache;
  if (!rc || typeof rc !== 'object') return null;
  const fp = routeWaypointsFingerprint(job.waypoints);
  if (!fp || rc.waypointsFingerprint !== fp) return null;
  // TASK-9734 — a lease or a transient failure is NOT an answer. Reading either
  // as one would draw "no road" over a job that simply has not been answered
  // yet — the exact mislabel this task removes. routeLookupState names them.
  if (rc.routeStatus === 'PENDING' || rc.transient === true) return null;
  const poly = (typeof rc.overviewPolyline === 'string' && rc.overviewPolyline) ? rc.overviewPolyline : null;
  let status = typeof rc.routeStatus === 'string' ? rc.routeStatus : 'UNKNOWN_ERROR';
  // Defence in depth: 'OK' with no shape is not OK. The server already refuses
  // to emit that; this map must not depend on it having done so.
  if (status === 'OK' && !poly) status = 'NO_GEOMETRY';
  return {
    routeStatus: status,
    overviewPolyline: status === 'OK' ? poly : null,
    routeSummary: rc.routeSummary || null
  };
}

// TASK-9734 — what a job's road route IS right now, for the map key, the stop
// info window and the background warm. Pure (`nowMs` injected) so it is unit
// tested as written.
//   'none'     — fewer than two pinned stops: nothing to route
//   'ok'       — a road route for these exact stops is cached
//   'noroad'   — asked and answered: no road route between these stops
//   'looking'  — someone holds a live lease on the lookup right now
//   'failed'   — the last lookup hit a temporary error; `due` once its
//                backoff has passed
//   'unlooked' — never looked up for these stops (no cache, a stale cache, or
//                a lease that lapsed without an answer)
// `due` = the background warm should look this job up now. `wakeAt` (ms) =
// when a not-yet-due job WILL be due (lease lapses / backoff ends).
function routeLookupState(job, nowMs) {
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const fp = routeWaypointsFingerprint(job && job.waypoints);
  if (!fp) return { state: 'none', due: false, fp: '' };
  const rc = job && job.routeCache;
  if (!rc || typeof rc !== 'object' || rc.waypointsFingerprint !== fp) {
    return { state: 'unlooked', due: true, fp };
  }
  if (rc.routeStatus === 'PENDING') {
    const until = Number(rc.leaseUntilMs);
    if (Number.isFinite(until) && until > now) return { state: 'looking', due: false, fp, wakeAt: until };
    return { state: 'unlooked', due: true, fp };
  }
  if (rc.transient === true) {
    const after = Number(rc.retryAfterMs);
    const due = !Number.isFinite(after) || after <= now;
    return { state: 'failed', due, fp, wakeAt: due ? null : after };
  }
  const cached = jobCachedRoute(job);
  if (cached && cached.routeStatus === 'OK') return { state: 'ok', due: false, fp };
  return { state: 'noroad', due: false, fp };
}

// One plain sentence per state, for the stop info window. Never says "route"
// about something that is not a road answer.
function mapRouteStateText(state, cached, permitRequired) {
  const via = cached && cached.routeStatus === 'OK' && typeof cached.routeSummary === 'string' && cached.routeSummary.trim()
    ? ` via ${cached.routeSummary.trim().slice(0, 60)}` : '';
  switch (state) {
    case 'ok': return permitRequired
      ? `Rough road route${via} (dashed). Not the permit route.`
      : `Proposed road route${via} (dotted). Actual route driven is unknown.`;
    case 'noroad': return 'No road route between these destinations. Pins only.';
    case 'failed': return 'Lookup failed. Retrying on its own. Pins only.';
    case 'looking': return 'Looking up the road route…';
    case 'unlooked': return 'Road route not looked up yet. Pins only.';
    default: return '';
  }
}

// Backoff before retrying the Nth consecutive transient failure (1-based).
function routeRetryDelayMs(attempts) {
  const n = Math.max(1, Math.floor(Number(attempts) || 1));
  return Math.min(MAP_ROUTE_RETRY_MAX_MS, MAP_ROUTE_RETRY_BASE_MS * Math.pow(2, Math.min(n - 1, 16)));
}

// Decode to LatLngs, or null. Requires `libraries=geometry` on the Maps loader
// in public/index.html — without it `decodePath` is simply undefined and every
// job falls back to the straight line, VISIBLY, rather than throwing.
function decodeCachedRoutePath(cached) {
  if (!cached || cached.routeStatus !== 'OK' || !cached.overviewPolyline) return null;
  const enc = window.google?.maps?.geometry?.encoding;
  if (!enc || typeof enc.decodePath !== 'function') return null;
  try {
    const pts = enc.decodePath(cached.overviewPolyline);
    return (Array.isArray(pts) && pts.length >= 2) ? pts : null;
  } catch (e) {
    console.warn('[dispatch map] could not decode a cached route polyline:', e?.message || e);
    return null;
  }
}

// One Directions call. Returns the server's shape verbatim, plus a client-side
// status for the failures that never reach it.
async function fetchRouteGeometry(coordWps) {
  try {
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) return { routeStatus: 'NO_AUTH', overviewPolyline: null };
    // TASK-9734 (COD) — bounded well inside the 90 s lease, so a slow call ends
    // as a transient failure instead of outliving the lease it holds.
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    const tmo = ctl ? setTimeout(() => ctl.abort(), 30 * 1000) : null;
    const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/getDirections', {
      signal: ctl ? ctl.signal : undefined,
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ waypoints: coordWps.map(w => ({ lat: w.lat, lng: w.lng })) })
    }).finally(() => { if (tmo) clearTimeout(tmo); });
    // TASK-9734 — our own per-user throttle, not a property of the job: the
    // caller backs off and lets its lease lapse, it never records a failure.
    if (resp.status === 429) return { routeStatus: 'RATE_LIMITED', overviewPolyline: null };
    const data = await resp.json();
    return {
      routeStatus: typeof data?.routeStatus === 'string' ? data.routeStatus : 'UNKNOWN_ERROR',
      overviewPolyline: typeof data?.overviewPolyline === 'string' ? data.overviewPolyline : null,
      routeSummary: data?.routeSummary || null,
      routeBounds: data?.routeBounds || null
    };
  } catch (e) {
    return { routeStatus: 'REQUEST_FAILED', overviewPolyline: null };
  }
}

// Compute + cache a job's road geometry. Returns the written routeCache (an
// answer, or a transient failure carrying its retry time), or null when
// nothing was looked up: already answered, nothing to route, someone else holds
// the lease, signed out, or our own rate limit (the lease just lapses).
//
// TASK-9734 — whether to look up is decided against the job's STORED
// routeCache inside the transaction that takes the lease, never against a
// snapshot the caller happened to hold. `priorCache` only short-circuits the
// obvious "already answered" for the two save paths.
async function resolveJobRouteCache(jobId, waypoints, priorCache) {
  if (!jobId || !DispatchState.tenantId) return null;
  const fp = routeWaypointsFingerprint(waypoints);
  if (!fp) return null;                                                   // < 2 plottable stops
  if (priorCache && priorCache.waypointsFingerprint === fp
      && priorCache.routeStatus !== 'PENDING' && priorCache.transient !== true) return null;  // already answered
  if (!auth.currentUser) return null;

  const tid = DispatchState.tenantId;
  const jobRef = doc(db, 'tenants', tid, 'jobs', jobId);
  let priorAttempts = 0;
  // Who holds the lease. The answer lands only while it is still OURS, so a
  // late reply can never overwrite a newer session's lookup.
  const leaseId = Math.random().toString(36).slice(2) + Date.now().toString(36);
  const leased = await runTransaction(db, async (tx) => {
    const snap = await tx.get(jobRef);
    if (!snap.exists()) return false;
    const cur = snap.data() || {};
    // The stops moved since the caller read them — that newer save kicks its own.
    if (routeWaypointsFingerprint(cur.waypoints) !== fp) return false;
    const st = routeLookupState(cur, Date.now());
    if (!st.due) return false;
    const rc0 = cur.routeCache;
    priorAttempts = (rc0 && rc0.waypointsFingerprint === fp)
      ? Math.max(0, Math.floor(Number(rc0.attempts) || 0)) : 0;
    tx.update(jobRef, { routeCache: {
      overviewPolyline: null,
      routeStatus: 'PENDING',
      waypointsFingerprint: fp,
      leaseUntilMs: Date.now() + MAP_ROUTE_LEASE_MS,
      leaseId,
      attempts: priorAttempts,
      computedAt: Timestamp.now()
    } });
    return true;
  });
  if (!leased) return null;

  const coordWps = (waypoints || []).map(wp => plottableCoord(wp)).filter(Boolean);
  const res = await fetchRouteGeometry(coordWps);

  let status = res.routeStatus;
  // Nothing to do with this job — let the lease lapse and the caller back off.
  // Recording a "failure" here would brand a routable job.
  // RATE_LIMITED throws so the background warm stops its batch and backs off
  // (a null here would let the remaining jobs keep asking). The save-path kick
  // already swallows a throw.
  if (status === 'RATE_LIMITED') { const err = new Error('getDirections rate limited'); err.code = 'resource-exhausted'; throw err; }
  if (status === 'NO_AUTH') return null;
  let poly = res.overviewPolyline || null;
  if (status === 'OK' && !poly) status = 'NO_GEOMETRY';
  if (status === 'OK' && poly.length > MAP_ROUTE_MAX_POLYLINE_CHARS) { status = 'GEOMETRY_TOO_LARGE'; poly = null; }
  const transient = status !== 'OK' && !MAP_ROUTE_CACHEABLE_FAILURES.has(status);

  const routeCache = {
    // null, never '' — an empty string reads as a successful route with no
    // shape, which is the exact ambiguity this task exists to remove.
    overviewPolyline: status === 'OK' ? poly : null,
    routeStatus: status,
    routeSummary: status === 'OK' ? (res.routeSummary || null) : null,
    routeBounds: status === 'OK' ? (res.routeBounds || null) : null,
    waypointsFingerprint: fp,
    computedAt: Timestamp.now()
  };
  if (transient) {
    const attempts = priorAttempts + 1;
    routeCache.transient = true;
    routeCache.attempts = attempts;
    routeCache.retryAfterMs = Date.now() + routeRetryDelayMs(attempts);
  }
  // Land the answer only if the stops did not move while we were asking, and
  // the tenant did not change under us — otherwise an old road would be
  // stamped over a newer fingerprint's lease.
  if (DispatchState.tenantId !== tid) return null;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(jobRef);
    if (!snap.exists()) return;
    const cur = snap.data() || {};
    if (routeWaypointsFingerprint(cur.waypoints) !== fp) return;
    if (!cur.routeCache || cur.routeCache.leaseId !== leaseId) return;   // lease lost — someone else owns this lookup
    tx.update(jobRef, { routeCache });
  });
  return routeCache;
}

// Fire-and-forget wrapper for the save path. A route is a nicety: it must never
// fail a save, slow one down, or put an error over a successful one.
function kickJobRouteCache(jobId, waypoints, priorCache) {
  try {
    resolveJobRouteCache(jobId, waypoints, priorCache)
      .catch(e => console.warn('[dispatch map] route geometry not cached:', e?.message || e));
  } catch (e) {
    console.warn('[dispatch map] route geometry not cached:', e?.message || e);
  }
}

// ── TASK-9194 — THE BOARD HAS TO BE ABLE TO SAY "THIS STOP HAS NO PIN" ──────
// The other half of the timeout fix. A failed resolve saves the waypoint with
// lat:null, and until now the ONLY notice of that was a toast that fades in
// four seconds. So the way a missing pin actually got discovered was a driver
// tapping Navigate and landing nowhere. Two Stellar/Twin Butte jobs are sitting
// in exactly that state on live TITS right now.
//
// This reuses classifyJobStops rather than re-deriving: `unlocated` is a stop
// that was never given a coordinate, `bad` is one whose stored pair is out of
// range. Both are "no usable pin" to a dispatcher, and both break the route.
// An EMPTY row — no name, no url — is not a missing pin, it is nothing.
function unpinnedStopCount(job) {
  const wps = (job && job.waypoints) || [];
  if (!wps.length) return 0;
  const { unlocated, bad } = classifyJobStops(wps);
  return unlocated.filter(w => w && (w.name || w.url)).length + bad.length;
}

// `variant`: 'compact' for the card indicator strip (week view, where 🚛 and 👤
// already live), anything else for the inline location line.
function buildUnpinnedStopBadge(job, variant) {
  const n = unpinnedStopCount(job);
  if (!n) return '';
  const title = `${n} destination${n > 1 ? 's have' : ' has'} no map pin — route mileage is incomplete and the driver gets no navigation for `
    + `${n > 1 ? 'them' : 'it'}. Open the job and re-check the location.`;
  if (variant === 'compact') {
    return `<span class="assign-indicator unpinned-stop" title="${escapeHtml(title)}" style="color:var(--status-warning);">📍</span>`;
  }
  return `<span class="unpinned-stop-badge" title="${escapeHtml(title)}" style="margin-left:6px;font-size:0.7rem;color:var(--status-warning);font-weight:600;">`
    + `${n} no pin</span>`;
}

// Haversine distance between two lat/lng points (returns miles)
function haversineMiles(lat1, lng1, lat2, lng2) {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Calculate total route mileage from waypoints array (returns null if coords missing)
//
// 🔴 THIS IS A BILLING INPUT, NOT JUST A DISPLAY HELPER. billing.js:23998 and
// :24062 call it via window.DispatchModule to auto-fill the quantity on "Per
// Mile" ticket lines, and both sites read it as `if (routeMiles) qty = ...`
// with qty defaulting to 0. So making this stricter does not fail safe — it
// silently bills $0.
//
// TASK-1615 deliberately did NOT put plottableCoord() in here for that reason,
// even though an out-of-range latitude still yields a garbage multi-thousand-
// mile total today. Fixing it needs a matching bad-location warning on the
// BILLING screen (the dispatch map header is the wrong place for a biller to
// find out), which means touching billing.js. Filed, not smuggled in here.
function calcRouteMileage(waypoints) {
  if (!waypoints || waypoints.length < 2) return null;
  let total = 0;
  let hasAll = true;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i], b = waypoints[i + 1];
    if (a.lat && a.lng && b.lat && b.lng) {
      total += haversineMiles(a.lat, a.lng, b.lat, b.lng);
    } else {
      hasAll = false;
    }
  }
  return hasAll && total > 0 ? total : null;
}

// Build route planner HTML for a job's waypoints
function buildRoutePlannerHTML(job) {
  if (!isRoutePlannerEnabled()) return '';
  const wps = job.waypoints || [];
  if (wps.length < 2) return '';

  // Calculate haversine distances as instant fallback
  let totalMiles = 0;
  let hasAllCoords = true;
  const legs = [];
  for (let i = 0; i < wps.length - 1; i++) {
    const from = wps[i];
    const to = wps[i + 1];
    let dist = null;
    // TASK-1615 — validate before any distance math (see plottableCoord).
    const fromC = plottableCoord(from), toC = plottableCoord(to);
    if (fromC && toC) {
      dist = haversineMiles(fromC.lat, fromC.lng, toC.lat, toC.lng);
      totalMiles += dist;
    } else {
      hasAllCoords = false;
    }
    legs.push({ from, to, dist });
  }

  // Build multi-stop Google Maps directions URL
  let mapsUrl = 'https://www.google.com/maps/dir/';
  wps.forEach(wp => {
    // TASK-1615 — an out-of-range pair would build a directions URL that lands
    // the driver nowhere. Fall back to the stop name so the link still works.
    const c = plottableCoord(wp);
    if (c) {
      mapsUrl += `${c.lat},${c.lng}/`;
    } else {
      mapsUrl += `${encodeURIComponent(wp.name || wp.url || '')}/`;
    }
  });

  // Unique ID for async update
  const rpId = `rp-${Date.now()}`;

  // Route step visualization
  const stepsHtml = wps.map((wp, i) => {
    const label = escapeHtml(wp.name || `Destination ${i + 1}`);
    const isLast = i === wps.length - 1;
    const legDist = !isLast && legs[i]?.dist != null ? `<span class="rp-leg-dist" style="font-size:0.7rem;color:var(--text-secondary);margin-left:8px;">~${legs[i].dist.toFixed(1)} mi</span>` : '';
    // TASK-9194 — a stop with no pin used to look EXACTLY like a stop with one
    // here (it just quietly had no leg distance next to it). This is the list a
    // dispatcher reads before sending a driver, so it is the right place to be
    // blunt about which stop the route does not actually cover.
    const noPin = !plottableCoord(wp) && (wp.name || wp.url)
      ? `<span class="rp-no-pin" title="This destination has no map pin — it is not on the route and the driver gets no navigation to it." style="margin-left:8px;font-size:0.65rem;font-weight:700;color:var(--status-warning);">NO PIN</span>`
      : '';

    return `<div style="display:flex;align-items:flex-start;gap:8px;">
      <div style="display:flex;flex-direction:column;align-items:center;min-width:24px;">
        <div style="width:24px;height:24px;border-radius:50%;background:${i === 0 ? 'var(--brand-blue)' : isLast ? '#10B981' : 'var(--text-secondary)'};color:#fff;display:flex;align-items:center;justify-content:center;font-size:0.7rem;font-weight:700;">${i + 1}</div>
        ${!isLast ? `<div style="width:2px;height:20px;background:var(--border-light);margin:2px 0;"></div>` : ''}
      </div>
      <div style="padding-top:2px;">
        <div style="font-size:0.8rem;font-weight:500;color:var(--text-primary);">${label}${legDist}${noPin}</div>
      </div>
    </div>`;
  }).join('');

  // Summary line (haversine estimate, will be updated by directions API)
  let summaryHtml = `${wps.length} destinations`;
  if (hasAllCoords && totalMiles > 0) {
    const estMinutes = Math.round(totalMiles / 45 * 60);
    const hrs = Math.floor(estMinutes / 60);
    const mins = estMinutes % 60;
    const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    summaryHtml += ` &middot; ~${totalMiles.toFixed(1)} mi &middot; ~${timeStr}`;
  }

  // Fetch real driving directions async and update in-place
  if (hasAllCoords && wps.length >= 2) {
    // TASK-1615 — validate before handing coordinates to the Directions API.
    const coordWps = wps.map(w => { const c = plottableCoord(w); return c ? { ...w, ...c } : null; }).filter(Boolean);
    if (coordWps.length >= 2) {
      fetchRouteDirections(rpId, coordWps);
    }
  }

  return `
    <div id="${rpId}" class="quickview-section" style="background:rgba(16,185,129,0.05);border-radius:8px;padding:10px 12px;margin:4px 0;">
      <div class="quickview-section-icon"><span class="material-icons" style="color:#10B981;">route</span></div>
      <div class="quickview-section-content">
        <div class="quickview-section-label" style="display:flex;align-items:center;justify-content:space-between;">
          <span>Route Planner</span>
          <span class="rp-summary" style="font-size:0.7rem;font-weight:400;color:var(--text-secondary);">${summaryHtml}</span>
        </div>
        <div class="rp-steps" style="margin-top:8px;">${stepsHtml}</div>
        <a href="${escapeHtml(mapsUrl)}" target="_blank" class="btn btn-sm btn-primary" style="margin-top:8px;display:inline-flex;align-items:center;gap:4px;font-size:0.8rem;">
          <span class="material-icons" style="font-size:16px;">navigation</span> Navigate Full Route
        </a>
      </div>
    </div>
  `;
}

// Fetch real driving distance/time from Directions API and update route planner in-place
async function fetchRouteDirections(rpId, waypoints) {
  try {
    // Maps endpoints now require a Firebase ID token (safety-audit M3).
    const idToken = await auth.currentUser?.getIdToken();
    if (!idToken) return;
    const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/getDirections', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ waypoints: waypoints.map(w => ({ lat: w.lat, lng: w.lng })) })
    });
    const data = await resp.json();
    if (!data.totalMiles || !data.legs) return;

    const el = document.getElementById(rpId);
    if (!el) return;

    // Update summary
    const summaryEl = el.querySelector('.rp-summary');
    if (summaryEl) {
      const secs = data.totalDurationSeconds || 0;
      const hrs = Math.floor(secs / 3600);
      const mins = Math.round((secs % 3600) / 60);
      const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
      summaryEl.textContent = `${waypoints.length} destinations · ${data.totalMiles.toFixed(1)} mi · ${timeStr}`;
    }

    // Update per-leg distances
    const legEls = el.querySelectorAll('.rp-leg-dist');
    legEls.forEach((legEl, i) => {
      if (data.legs[i]) {
        legEl.textContent = `${data.legs[i].distanceMiles.toFixed(1)} mi · ${data.legs[i].durationText}`;
      }
    });
  } catch { /* silently keep haversine fallback */ }
}

// Build load calculator summary HTML for a jobDay
function buildLoadSummaryHTML(jobDay) {
  if (!isLoadCalcEnabled() || !jobDay) return '';

  const rawAssets = (jobDay.assets || []).map(id => {
    const a = DispatchState.assets.find(x => x.id === id);
    if (!a || !a.unitWeight) return null;
    const qty = jobDay.assetQtys?.[id] || 1;
    return { name: a.name, unitWeight: a.unitWeight, qty, totalWeight: a.unitWeight * qty };
  }).filter(Boolean);

  if (rawAssets.length === 0) return '';

  // Group identical assets (same name + unitWeight) into one line
  const groupMap = new Map();
  for (const a of rawAssets) {
    const key = `${a.name}|${a.unitWeight}`;
    if (groupMap.has(key)) {
      const g = groupMap.get(key);
      g.qty += a.qty;
      g.totalWeight += a.totalWeight;
    } else {
      groupMap.set(key, { ...a });
    }
  }
  const assignedAssets = Array.from(groupMap.values());

  const totalWeight = assignedAssets.reduce((s, a) => s + a.totalWeight, 0);
  if (totalWeight === 0) return '';

  const rawTrucks = (jobDay.trucks || []).map(id => {
    const t = DispatchState.trucks.find(x => x.id === id);
    if (!t || !t.payloadCapacity) return null;
    return { name: window.getUnitDisplay?.(t) || t.unit || t.name, capacity: t.payloadCapacity };
  }).filter(Boolean);

  if (rawTrucks.length === 0) return '';

  // Deduplicate trucks with same name + capacity
  const truckMap = new Map();
  for (const t of rawTrucks) {
    const key = `${t.name}|${t.capacity}`;
    if (truckMap.has(key)) {
      truckMap.get(key).count++;
    } else {
      truckMap.set(key, { ...t, count: 1 });
    }
  }
  const assignedTrucks = Array.from(truckMap.values());

  let truckBreakdown = '';
  const allSameCapacity = assignedTrucks.every(t => t.capacity === assignedTrucks[0].capacity);

  const totalTruckCount = assignedTrucks.reduce((s, t) => s + t.count, 0);
  if (allSameCapacity) {
    const loadsPerTruck = Math.ceil(totalWeight / assignedTrucks[0].capacity);
    truckBreakdown = `<div style="font-weight:600;font-size:0.9rem;color:var(--text-primary);">${loadsPerTruck.toLocaleString()} load${loadsPerTruck !== 1 ? 's' : ''} needed</div>
      <div style="font-size:0.75rem;color:var(--text-secondary);">${totalTruckCount} truck${totalTruckCount !== 1 ? 's' : ''} × ${assignedTrucks[0].capacity.toLocaleString()} lb capacity</div>`;
  } else {
    truckBreakdown = assignedTrucks.map(t => {
      const loads = Math.ceil(totalWeight / t.capacity);
      const label = t.count > 1 ? `${t.count}× ${escapeHtml(t.name)}` : escapeHtml(t.name);
      return `<div style="display:flex;justify-content:space-between;padding:2px 0;font-size:0.8rem;">
        <span>${label}</span>
        <span style="font-weight:600;">${loads} load${loads !== 1 ? 's' : ''} <span style="font-weight:400;color:var(--text-secondary);">(${t.capacity.toLocaleString()} lb)</span></span>
      </div>`;
    }).join('');
  }

  const assetBreakdown = assignedAssets.map(a =>
    `<div style="font-size:0.75rem;color:var(--text-secondary);">${a.qty}× ${escapeHtml(a.name)} @ ${a.unitWeight.toLocaleString()} lbs = ${a.totalWeight.toLocaleString()} lbs</div>`
  ).join('');

  return `
    <div class="quickview-section" style="background:rgba(59,130,246,0.05);border-radius:8px;padding:10px 12px;margin:4px 0;">
      <div class="quickview-section-icon"><span class="material-icons" style="color:var(--brand-blue);">calculate</span></div>
      <div class="quickview-section-content">
        <div class="quickview-section-label">Load Calculator</div>
        <div style="margin-top:4px;">
          <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">${totalWeight.toLocaleString()} lbs total</div>
          ${assetBreakdown}
          <div style="margin-top:8px;padding-top:6px;border-top:1px solid var(--border-light);">
            ${truckBreakdown}
          </div>
        </div>
      </div>
    </div>
  `;
}

// Build load counter HTML for quickview/job cards — shows loads completed vs total
// Build load counter HTML — delegates to shared util so behavior matches
// jobboard.js exactly. Renders rich progress-bar counter when assets+trucks
// have weights/capacity, OR a manual +/- counter when they don't (so drivers
// can still log loads on jobs whose assets lack unitWeight). The fallback
// is the actual fix for Guy's drivers.
function buildLoadCounterHTML(jobDay, jobId) {
  if (!isLoadCalcEnabled()) return '';
  const inner = renderLiveLoadCounterInner(jobDay);
  // Wrapper is keyed by jobDay.id so jobDays.onSnapshot can surgically
  // swap the inner HTML without re-rendering the whole modal — preserves
  // scroll position, text selection, and any future input focus state.
  const id = jobDay?.id || 'noid';
  return `<div class="live-load-counter-wrapper" id="live-load-counter-${id}">${inner}</div>`;
}

function renderLiveLoadCounterInner(jobDay) {
  // Dispatcher surfaces show the "Set expected total" inline-edit; driver
  // surfaces hide it (drivers shouldn't be setting the target).
  const showDispatcherEdit = DispatchState.roleMode !== 'driver';
  return _sharedBuildDriverLoadCounterHTML(
    jobDay,
    window.AppState?.settings,
    DispatchState.assets,
    DispatchState.trucks,
    { recordModule: 'DispatchModule', showDispatcherEdit }
  );
}

// Dispatcher-only: prompt for an expected-loads target on a jobDay, write
// to Firestore. The fallback counter then renders rich-style with this
// target so drivers see "X / N · remaining" even on jobs without weights.
async function promptSetLoadsExpected(jobDayId) {
  const jd = _resolveJobDayAnywhere(jobDayId); // TASK-4535 — search cards too
  if (!jd) return;
  const current = jd.loadsExpected || '';
  const input = window.prompt(
    'How many loads expected for this job day? (Leave blank to clear; whole number)',
    current ? String(current) : ''
  );
  if (input === null) return; // user cancelled
  const trimmed = String(input).trim();
  let nextValue;
  if (trimmed === '') {
    nextValue = null; // clear
  } else {
    const n = parseInt(trimmed, 10);
    if (!Number.isFinite(n) || n < 0) {
      window.showToast?.('Please enter a non-negative whole number, or leave blank to clear', 'error');
      return;
    }
    nextValue = n;
  }
  try {
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), {
      loadsExpected: nextValue,
      updatedAt: Timestamp.now(),
    });
    jd.loadsExpected = nextValue;
    window.showToast?.(nextValue == null ? 'Expected loads cleared' : `Expected loads set to ${nextValue}`, 'success');
    // Refresh whichever quickview is open
    if (DispatchState.roleMode === 'driver') {
      showDriverQuickview(jd.jobId, jobDayId);
    } else {
      showQuickview(jd.jobId, jobDayId);
    }
  } catch (e) {
    console.error('[Dispatch] setLoadsExpected error:', e);
    window.showToast?.('Could not save expected loads', 'error');
  }
}

// Record a load pickup — increment loadsCompleted on jobDay
async function recordLoadPickup(jobDayId, currentCount, totalLoads) {
  const newCount = currentCount + 1;
  try {
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), {
      loadsCompleted: newCount,
      lastLoadAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    window.showToast(totalLoads > 0 ? `Load ${newCount}/${totalLoads} recorded` : `Load ${newCount} recorded`, 'success');
    // Refresh whichever quickview is currently open (dispatcher OR driver)
    const jd = DispatchState.jobDays.find(d => d.id === jobDayId);
    if (jd) {
      jd.loadsCompleted = newCount;
      if (DispatchState.roleMode === 'driver') {
        showDriverQuickview(jd.jobId, jobDayId);
      } else {
        showQuickview(jd.jobId, jobDayId);
      }
    }
  } catch (err) {
    console.error('[Dispatch] Record load error:', err);
    window.showToast('Error recording load', 'error');
  }
}

// Undo last load pickup
async function undoLoadPickup(jobDayId, currentCount) {
  if (currentCount <= 0) return;
  const newCount = currentCount - 1;
  try {
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), {
      loadsCompleted: newCount,
      updatedAt: Timestamp.now()
    });
    window.showToast('Load undone', 'info');
    const jd = DispatchState.jobDays.find(d => d.id === jobDayId);
    if (jd) {
      jd.loadsCompleted = newCount;
      if (DispatchState.roleMode === 'driver') {
        showDriverQuickview(jd.jobId, jobDayId);
      } else {
        showQuickview(jd.jobId, jobDayId);
      }
    }
  } catch (err) {
    console.error('[Dispatch] Undo load error:', err);
    window.showToast('Error undoing load', 'error');
  }
}

// Build compact load progress badge for job cards.
// Delegates to shared computeLoadInfo so weight-derived AND manual-target jobDays
// both get a badge. Falls back to a count-only badge when only loadsCompleted is set.
function buildLoadProgressBadge(jobDay) {
  if (!isLoadCalcEnabled() || !jobDay) return '';

  const settings = window.AppState?.settings || { enableLoadCalculator: true };
  const info = _sharedComputeLoadInfo(jobDay, settings, DispatchState.assets, DispatchState.trucks);

  if (info && info.totalLoads > 0) {
    const color = info.pct >= 100 ? '#10B981' : info.pct >= 50 ? '#F59E0B' : '#3B82F6';
    const title = info.source === 'manual' ? 'Loads (manual target)' : 'Loads completed';
    return `<div style="font-size:0.65rem;font-weight:600;color:${color};margin-top:1px;" title="${title}">📦 ${info.completed}/${info.totalLoads}</div>`;
  }

  // No total available, but driver has logged loads → show running count.
  const completed = jobDay.loadsCompleted || 0;
  if (completed > 0) {
    return `<div style="font-size:0.65rem;font-weight:600;color:#3B82F6;margin-top:1px;" title="Loads logged">📦 ${completed}</div>`;
  }

  return '';
}

// TASK-10013 — a positive number of pounds, or 0. Roster stores parseInt or
// null; anything else (a string, a negative, NaN) counts as "no weight".
function _loadCalcLbs(v) {
  const x = typeof v === 'string' ? Number(v) : v;
  return (typeof x === 'number' && Number.isFinite(x) && x > 0) ? x : 0;
}

// TASK-10013 — PURE. The load calculator's numbers AND what it had to leave
// out. Before this, an asset with no Unit Weight and a truck with no Payload
// Capacity were silently dropped, so Guy's 120 rubber mats read "240,000 lbs"
// and nothing else — "why isn't the load calculator calculating?" Now every
// selected item is either counted or NAMED.
// assets/trucks: the roster docs; qtys: { assetId: qty }; nameOfTruck: fn.
function buildJobLoadCalc(selectedAssetIds, selectedTruckIds, assets, trucks, qtys, nameOfTruck) {
  const weighted = [];
  const noWeight = [];
  for (const id of selectedAssetIds || []) {
    const a = (assets || []).find(x => x && x.id === id);
    if (!a) continue;
    const qRaw = Number((qtys || {})[id]);
    const qty = Number.isFinite(qRaw) && qRaw > 0 ? qRaw : 1;
    const w = _loadCalcLbs(a.unitWeight);
    if (w) weighted.push({ name: a.name || 'Asset', unitWeight: w, qty, totalWeight: w * qty });
    else noWeight.push(a.name || 'Asset');
  }
  const withCap = [];
  const noCapacity = [];
  for (const id of selectedTruckIds || []) {
    const t = (trucks || []).find(x => x && x.id === id);
    if (!t) continue;
    const name = (nameOfTruck && nameOfTruck(t)) || t.unit || t.nickname || t.name || 'Vehicle';
    const c = _loadCalcLbs(t.payloadCapacity);
    if (c) withCap.push({ name, capacity: c });
    else noCapacity.push(name);
  }
  const totalWeight = weighted.reduce((s, a) => s + a.totalWeight, 0);
  const totalCapacity = withCap.reduce((s, t) => s + t.capacity, 0);
  const loadsNeeded = totalWeight > 0 && totalCapacity > 0 ? Math.ceil(totalWeight / totalCapacity) : 0;
  const pct = totalWeight > 0 && totalCapacity > 0 ? Math.round((totalWeight / totalCapacity) * 100) : 0;
  return {
    assets: weighted, noWeight, trucks: withCap, noCapacity,
    truckCount: withCap.length + noCapacity.length,
    totalWeight, totalCapacity, loadsNeeded, pct,
    // Shown when there is anything to count. A job with only unweighted
    // assets and no trucks stays quiet (most jobs on a tenant that never
    // entered weights), exactly as before.
    show: totalWeight > 0 || withCap.length > 0,
  };
}

// TASK-10013 — "A, B and 2 more".
function _loadCalcNameList(names) {
  const list = (names || []).slice(0, 3).join(', ');
  return list + ((names || []).length > 3 ? ` and ${names.length - 3} more` : '');
}

// Live load calculator for the Add/Edit Job modal — updates as user selects assets/trucks
function updateJobLoadCalcLive() {
  const container = document.getElementById('job-load-calculator-live');
  if (!container) return;
  if (!isLoadCalcEnabled()) { container.style.display = 'none'; return; }

  const calc = buildJobLoadCalc(
    getJobCrewSelected('assets'),
    getJobCrewSelected('trucks'),
    DispatchState.assets,
    DispatchState.trucks,
    DispatchState.pendingAssetQtys,
    (t) => window.getUnitDisplay?.(t) || t.unit || t.name
  );
  const assets = calc.assets;
  const trucks = calc.trucks;
  const totalWeight = calc.totalWeight;

  if (!calc.show) {
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';

  let html = '<div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;font-weight:600;font-size:0.9rem;color:var(--text-primary);"><span class="material-icons" style="font-size:18px;color:var(--brand-blue);">scale</span> Load Calculator</div>';

  if (assets.length > 0) {
    html += assets.map(a =>
      `<div style="font-size:0.8rem;color:var(--text-secondary);padding:2px 0;">${a.qty}× ${escapeHtml(a.name)} @ ${a.unitWeight.toLocaleString()} lbs = <strong>${a.totalWeight.toLocaleString()} lbs</strong></div>`
    ).join('');
    html += `<div style="font-weight:700;font-size:0.9rem;margin-top:4px;color:var(--text-primary);">Total: ${totalWeight.toLocaleString()} lbs</div>`;
  }

  if (trucks.length > 0 && totalWeight > 0) {
    const totalCapacity = calc.totalCapacity;
    const loadsNeeded = calc.loadsNeeded;
    const pct = calc.pct;
    const color = pct > 100 ? '#DC2626' : pct > 80 ? '#F59E0B' : '#10B981';
    // TASK-10013 — say the real combined capacity. "2 trucks × <first truck's
    // capacity>" was wrong whenever the trucks differ.
    const capLine = trucks.length === 1
      ? `1 truck × ${trucks[0].capacity.toLocaleString()} lb capacity`
      : `${trucks.length} trucks, ${totalCapacity.toLocaleString()} lb capacity together`;
    html += `<div style="margin-top:8px;padding-top:8px;border-top:1px solid var(--border-light);">`;
    html += `<div style="font-size:0.85rem;color:var(--text-primary);"><span class="material-icons" style="font-size:16px;vertical-align:middle;color:${color};">local_shipping</span> ${capLine}</div>`;
    html += `<div style="font-weight:700;font-size:0.95rem;color:${color};">${loadsNeeded} load${loadsNeeded !== 1 ? 's' : ''} needed (${pct}% capacity)</div>`;
    html += `</div>`;
  } else if (trucks.length > 0) {
    html += `<div style="font-size:0.8rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;">local_shipping</span> ${trucks.length} truck${trucks.length !== 1 ? 's' : ''} selected — add weighted assets to see load calculation</div>`;
  } else if (totalWeight > 0 && calc.truckCount === 0) {
    // TASK-10013 — the total alone read as "not calculating". Say what's missing.
    html += `<div data-load-calc-note="no-truck" style="font-size:0.8rem;color:var(--text-primary);margin-top:6px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">local_shipping</span> Pick a truck to see how many loads that is.</div>`;
  }

  // TASK-10013 — name everything the math had to leave out, and where to fix it.
  const notes = [];
  if (calc.noWeight.length) {
    const one = calc.noWeight.length === 1;
    notes.push(`<div data-load-calc-note="no-weight">${escapeHtml(_loadCalcNameList(calc.noWeight))} ${one ? 'has' : 'have'} no weight, so ${one ? 'it isn’t' : 'they aren’t'} in the total — add Unit Weight in Roster › Assets.</div>`);
  }
  if (calc.noCapacity.length) {
    const one = calc.noCapacity.length === 1;
    notes.push(`<div data-load-calc-note="no-capacity">${escapeHtml(_loadCalcNameList(calc.noCapacity))} ${one ? 'has' : 'have'} no payload capacity, so ${one ? 'it isn’t' : 'they aren’t'} in the load count — add Payload Capacity in Roster › Vehicles.</div>`);
  }
  if (notes.length) {
    html += `<div style="margin-top:8px;padding:8px 10px;border-radius:8px;background:#FFFBEB;border:1px solid #FDE68A;color:#92400E;font-size:0.78rem;display:flex;flex-direction:column;gap:4px;">${notes.join('')}</div>`;
  }

  container.innerHTML = html;
}

// Check if user has ONLY 811 access (no full dispatch)
function is811OnlyMode() {
  if (isDemoMode()) return false;
  // TASK-9779 — a signed-in staff login with '811-permits' and no 'dispatch'.
  if (window.isMember811Only?.() === true) return true;
  const accessPins = (window.accessPinMap?.() || window.AppState?.settings?.accessPins || {});
  if (Object.keys(accessPins).length === 0) return false; // No PINs = full access
  const unlocked = window.getUnlockedModules?.() || [];
  return unlocked.includes('811') && !unlocked.includes('dispatch');
}

const DispatchState = {
  // TASK-348 — transient identity stash for SPA personal-action wrappers.
  // Set by openDriverTimeOffForPerson / openSwitchJobForPerson; consumed by
  // submitDriverTimeOff / submitSwitchJob; cleared on close/submit. Lets the
  // existing legacy handlers (which read AppState.driverPersonId) be reused
  // from SPA context without mutating AppState.
  _personalActionPersonId: null,
  _personalActionPersonName: null,

  // TASK-598 — driver self-service time-off edit state.
  // _driverTimeOffRequests: { [docId]: rawDocData } stash for the "Your Requests"
  //   list, so editDriverTimeOff(id) can prefill without re-querying.
  // _editTimeOffRequestId: when set, submitDriverTimeOff() UPDATEs that pending
  //   request instead of creating a new one.
  _driverTimeOffRequests: {},
  _editTimeOffRequestId: null,
  // TASK-9838 — true once the open modal's person is known to be on a plan
  // (their level exists in the company plan). False = Unpaid only.
  _driverTimeOffHasPlan: false,
  // TASK-9838 COD r1 — true when the person or plan read FAILED (not "no plan"):
  // the list stays Unpaid only but the hint says it could not load.
  _driverTimeOffPlanUnknown: false,
  // TASK-9838 COD r1 — bumped per panel load; a slower earlier load for a
  // closed/reopened modal must not apply its plan to the current one.
  _driverTimeOffLoadGen: 0,

  jobs: [],
  jobDays: [],
  people: [],
  trucks: [],
  trailers: [],
  assets: [],
  customers: [],
  permits: [],
  savedLocations: [], // User-saved locations for quick waypoint entry
  // TASK-9540 — tenants/{tid}/waypointCategories docs (starter overrides +
  // custom categories). Always read through _placeCats().
  waypointCategoryDocs: [],
  // TASK-4783 — "places from past jobs": the coordinate-deduped view over
  // tenants/{tid}/waypoints. Lazy (nothing loads until the picker opens),
  // session-cached, and dropped after a job save because that save just wrote
  // new waypoint rows. Never written back to Firestore.
  _pastPlaceIndex: null,
  _pastPlaceRows: null, // the raw rows the index was built from, so a job save can extend it without re-reading
  _pastPlaceCustomerByJob: null, // jobId -> customerId, resolved once per load
  _pastPlaceTruncated: false,
  // 🔴 The tenant the cache belongs to. DispatchState survives a tenant switch in
  // the same tab, so an unstamped cache would show one tenant's yards to another.
  _pastPlaceTenantId: null,
  _pastPlaceStatus: 'idle', // idle | loading | ready | error
  _pastPlaceScope: 'customer', // customer | all
  _pastPlaceScopeFor: undefined, // the customerId _pastPlaceScope was chosen for; a different one resets to 'customer'
  _pastPlaceVisible: null, // { place: [], link: [] } — what the last render drew, for click-by-index
  _savedLocScope: 'customer', // TASK-9421 — customer | all, for the Saved Locations section
  _savedLocScopeFor: undefined, // TASK-9421 — customerId the scope was chosen for; a different one resets to 'customer'
  _savedLocHiddenSources: [], // TASK-9421 — source chips switched off
  _savedLocFormCustomerIds: [], // TASK-9421 — customer tags being edited in the save/edit form
  safetyHolds: [], // Critical DVIR tasks that put vehicles on hold
  timeOffRequests: [], // Approved time-off requests
  opportunityHolds: [], // CRM opportunity holds for dispatch calendar
  vehicleHolds: [], // Maintenance-issued vehicle holds (from vehicleHolds collection)
  pretripCoverage: {}, // TASK-9886 — window.DvirCoverage.build() map: truck + driver + day
  pretripCoverageFromDay: null, // first YYYY-MM-DD the coverage map can see (null = not loaded / failed)
  jsaJobDayIds: {}, // Lookup: { "jobDayId": true } for JSA status
  jsaIdByJobDay: {}, // TASK-794: { "jobDayId": jsaReportId } — most recent non-dismissed JSA per jobDay
  jsaIdByJob: {}, // TASK-794: { "jobId": jsaReportId } — most recent non-dismissed JSA per job (fallback when no jobDayId match)
  jsaListByJobDay: {}, // TASK-9371: { "jobDayId": [summary…] } — EVERY non-dismissed JSA per jobDay, createdAt desc (card "N JSAs" pill)
  // driverMyJsas removed 2026-05-28 — TASK-361 cleanup (was relocated to
  // Inspections > JSA tab via jsa.js renderMyJsaSection). dispatch.js had a
  // parallel implementation that TASK-361 missed; user reported the JSA
  // section still appearing below daily jobs on the driver view.
  currentDate: new Date(),
  viewMode: 'week', // week, month, list, lanes, search, map
  // TASK-1664 — the view you were on before the magnifier took you to search,
  // so tapping it again puts you back. Null until the first search entry.
  _preSearchViewMode: null,
  // TASK-583 — one-shot trigger: the NEXT week render auto-scrolls today's
  // block to the top (mobile stacked layout only). Set on module entry, on
  // week navigation, and on Today jumps — NOT on layer-chip / data rerenders,
  // so those never yank a scroll position the user set.
  _scrollWeekToToday: true,
  mapInstance: null,  // Google Maps instance (persists across re-renders)
  mapMarkers: [],     // Current map markers
  pingMarkers: [],    // Slip + clock GPS ping markers (Mode A pings layer)
  searchQuery: '',
  searchStatusFilter: 'all', // all, active, finished
  searchResultsLimit: 50,
  roleMode: 'dispatcher', // dispatcher, driver
  driverViewMode: (() => { try { return localStorage.getItem('dd_dispatch_driver_view_mode_v1') === 'calendar' ? 'calendar' : 'day'; } catch (_) { return 'day'; } })(), // 'day' | 'calendar' — only used when roleMode === 'driver'
  driverCalendarMonth: new Date(), // anchor date when driverViewMode === 'calendar'
  // Per-device layer filters for Week + Month calendar. Default all-on.
  // TASK-461: the Maintenance chip was REMOVED — both safety holds AND vehicle
  // holds (maintenance) are now ALWAYS visible. `maintenance` is forced hard-true
  // regardless of any persisted value: a user who previously toggled Maintenance
  // OFF would otherwise have vehicleHolds stuck hidden with no chip to re-enable.
  // TASK-4646: `completed` (finished jobs) is a NEW key on an OLD storage version.
  // It MUST read ON for the millions of devices whose persisted object predates it
  // — `parsed.completed !== false` gives that for free (undefined !== false), which
  // is why the key is NOT versioned up to _v2: a bump would reset jobs/daysoff too.
  calendarFilters: (() => {
    try {
      const raw = localStorage.getItem('dd_dispatch_calendar_filters_v1');
      if (!raw) return { jobs: true, daysoff: true, maintenance: true, completed: true };
      const parsed = JSON.parse(raw);
      return {
        jobs: parsed.jobs !== false,
        daysoff: parsed.daysoff !== false,
        maintenance: true, // forced on — chip removed (TASK-461); ignore persisted false
        completed: parsed.completed !== false, // TASK-4646 — absent key === ON
      };
    } catch (_) {
      return { jobs: true, daysoff: true, maintenance: true, completed: true };
    }
  })(),
  statusFilter: 'all', // all, assigned, unassigned, en-route, completed
  listOffset: 0, // for list view navigation
  unsubscribers: [],
  tenantId: null,
  pendingJobPhotos: [],
  existingJobPhotos: [],
  pendingPermitFiles: [],
  existingPermitFiles: [],
  // Counts as LOADED, so save can detect removals. `existingPermitFiles.length
  // > 0` cannot: removing the last permit yields 0, which is indistinguishable
  // from "this job never had permits" — so the delete was silently dropped.
  loadedPhotoCount: 0,
  loadedPermitCount: 0,
  // Loading flags for async data
  dataLoaded: {
    people: false,
    trucks: false,
    trailers: false,
    assets: false
  },
  // TASK-9212 — the permits (811) collection is not in dataLoaded above because
  // no picker gated on it. The carried-ticket recovery needs it: an empty
  // #job-permit means "nothing has loaded" before this flips and "that ticket was
  // deleted" after, and those call for opposite behaviour at save time.
  permitsLoaded: false,
  // Lanes view state
  lanesStartDate: null,
  lanesPeopleExpanded: false,
  lanesTrucksExpanded: false,
  lanesTrailersExpanded: false,
  lanesAssetsExpanded: false,
  // Line item picker state
  lineItemCache: {},  // { customerId: { contracts: [], items: [], loadedAt: timestamp } }
  pendingLineItemSelections: {},  // Assignment modal: { "truck:abc123": { lineItemId, contractId, rate, ... } }
  pendingJobLineItemSelections: {},  // Job creation modal
  pendingSelectedContractId: null,  // Assignment modal: selected contract ID (Level 1)
  pendingJobSelectedContractId: null,  // Job creation modal: selected contract ID (Level 1)
  pendingAssetQtys: {},  // { assetId: qty } for quantity-tracked assets in assignment
  pendingAssetDirections: {},  // { assetId: 'deploy' | 'return' } for rental assets
  // TASK-1672 — what the Edit Job crew section was SEEDED with, captured on
  // modal open by _buildJobCrewPayload(). saveJob diffs the current selection
  // against it and only rewrites jobDay crew when it actually changed; null on
  // the new-job path (nothing to preserve).
  jobCrewSnapshot: null,
  // The crew editJob MEANT to seed, plus whether the roster snapshot was still
  // loading when it did — refreshJobModalCrewIfOpen re-seeds from these once.
  jobCrewSeedIntent: null,
  jobCrewSeedPending: null,
  // TASK-9258 — JOB TEMPLATES. `jobTemplates` is the tenant's saved equipment
  // kits; `jobTemplatesReady` flips true only after a snapshot actually LANDS,
  // and the whole UI is gated on it (a denied read renders nothing rather than a
  // broken picker — see loadJobTemplates).
  jobTemplates: [],
  jobTemplatesReady: false,
  // What templates have been applied to the CURRENTLY OPEN form.
  //   templateApplied = { applications:[{id,name,appliedAt,appliedBy,crewSlots}],
  //                       seededBy:{ "<section>:<id>": templateId },
  //                       snapshot:{ trucks,trailers,assets,assetQtys } }
  //   templateUnconfirmed = ["<section>:<id>", …] — seeded rows NO HUMAN HAS
  //                       TOUCHED since; shrinks as rows are touched.
  // 🔴 TWO LISTS, NOT ONE, and `seededBy` is NEVER cleared by a touch. Touching
  // a row changes whether it was CONFIRMED; it does not change the fact that a
  // template put it there. Collapsing them made `source:'template'` land on
  // hand-picked assets (COD Code Reviewer + Edge Case Hunter, both lanes).
  // Both are modal-scoped and cleared by showAddJob / editJob, exactly like
  // pendingAssetQtys (TASK-9238's lesson: modal-scoped state that only the save
  // path clears leaks into the next form).
  templateApplied: null,
  templateUnconfirmed: [],
  tickets: [],  // Lightweight ticket data for status dots on calendar
  _jobDaysLoaded: false,
  // 2026-08-23 — `_jobDaysLoaded` alone is NOT enough to answer "is the
  // schedule readable yet". getJobsForDate() walks jobDays and then does
  // `DispatchState.jobs.find(j => j.id === jobDay.jobId)` — so if jobDays has
  // landed but jobs has not, EVERY lookup misses and the function returns an
  // empty array indistinguishable from a genuinely empty day.
  _jobsLoaded: false,
  // `_jobsLoaded` means "something is renderable". `_jobsComplete` means "both
  // initial job snapshots actually landed". A board may use the first; anything
  // irreversible — an SMS to the whole crew — must use the second.
  _jobsComplete: false,
  // TASK-4588 — "we gave up", NOT "the data arrived". Kept separate from the three
  // load flags on purpose: a render may fall through on it, but nothing
  // irreversible may — faking readiness here would re-open the jobless-crew-text
  // hole that the v1.15.1796 Notify Crew guard closed.
  _dispatchLoadFailed: false,
  // TASK-424 #5 (C2): admin jobs/jobDays live window. The non-PIN admin path
  // used to stream the ENTIRE jobs + jobDays collections (unbounded) on every
  // view entry — +140 firestore reqs of nav churn, scales linearly with tenant
  // age. We now window it the SAME way the PIN-driver path is windowed
  // (TASK-345 Slice M) but with a wider default lookback (90d vs the driver's
  // 7d). The window is a `>=` lower bound with NO upper bound, so ALL upcoming/
  // future jobs are always streamed; only jobs/jobDays OLDER than the cutoff are
  // excluded. "Load older" widens this and resubscribes. Search-Past-Jobs view
  // bypasses the window with a one-shot full-collection fetch (see searchJobs).
  adminWindowDaysBack: 90,
  // Full-history snapshot for the Search-Past-Jobs view only — populated by a
  // one-shot getDocs (NOT a live stream) so search still spans ALL history even
  // though the live board is windowed. Falls back to the windowed live arrays
  // if the one-shot fetch hasn't run / errored.
  searchJobs: null,
  searchJobDays: null,
  _searchHistoryLoading: false,
  _searchHistoryLoaded: false,
  // TASK-1616 — map date range. DELIBERATELY separate from `currentDate`:
  // changing the map's span must not drag the Week/Month/List board with it.
  // null on both = "not chosen yet"; the map fills them with today on its
  // first paint (see getMapRange).
  mapDateFrom: null,      // 'YYYY-MM-DD', local calendar day
  mapDateTo: null,        // 'YYYY-MM-DD', local calendar day, inclusive
  // TASK-4786 A2 — the map opens on TODAY (Chris 2026-09-08). Must match the
  // fallback in getMapRange / renderMapView and a branch in mapRangeForPreset.
  mapRangePreset: 'today', // 'today' | 'yesterday' | '3d' | '7d' | 'custom'
  // TASK-9175 — map key layers ({stops,saved,lastDispatched,fleet}; null until
  // first render reads defaults + the viewer's remembered choice), the project
  // filter (null = use the date range) and the key drawer's open state.
  mapLayers: null,
  mapProjectId: null,
  mapKeyOpen: null,
  // TASK-9631 S3 — the saved map open right now ({ id, name, tenantId,
  // excluded: Set of place ids not in it }) and a view to restore on the next
  // map build. Per tenant; cleared on a switch.
  mapViewActive: null,
  _mapViewPending: null,
  // TASK-4800 — Equipment layer caches: the one-shot assetTransactions read
  // (null = not read yet; [] = read, empty), jobs fetched for lots outside the
  // board window ({ id: job | null } — null = the job no longer exists), and
  // the list panel's filters.
  _mapEqTxns: null,
  _mapEqTxnsState: 'idle',   // 'idle' | 'loading' | 'loaded' | 'failed'
  _mapEqJobs: {},
  _mapEqJobsInflight: null,
  _mapEqFilters: { kind: 'all', customer: '', project: '' },
  mapShowCustom: false    // custom from/to pair revealed?
};

// ============================================
// INITIALIZE DISPATCH VIEW
// ============================================
// Driver-link notice — written nightly by the expired-link sweep
// (functions/src/services/driverLinkNotices.js) to tenants/{tid}/settings/
// driverLinkNotice: which driver phones the NEXT sweep removes (same expiry +
// grace rule as the sweep) and which the last one removed. Office view only,
// shown for 36 h, dismissible per notice. Names come from the data, so the
// banner is built with textContent only. There is no "extend link" action in
// the app; the button opens Notify Crew, where Copy Driver Link mints a fresh
// link (an expired one is never reused) and a phone that opens it stays on.
const _DL_NOTICE_MAX_AGE_MS = 36 * 3600 * 1000;
// Short on purpose (COD UX): at most 3 names, the rest as a count, so the
// banner stays small on a phone.
function _dlNoticeWho(w) {
  if (!w) return '';
  const names = Array.isArray(w.names) ? w.names.map((n) => String(n)) : [];
  const shown = names.slice(0, 3);
  const others = (names.length - shown.length) + (Number(w.more) || 0) + (Number(w.unnamed) || 0);
  if (!shown.length) return `${others} driver phone${others === 1 ? '' : 's'}`;
  return others > 0 ? `${shown.join(', ')} and ${others} other${others === 1 ? '' : 's'}` : shown.join(', ');
}
async function _paintDriverLinkNotice(container, tenantId) {
  try {
    if (DispatchState.roleMode === 'driver' || window.AppState?.driverMode || window.AppState?.driverModePin) return;
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'settings', 'driverLinkNotice'));
    if (!snap.exists() || DispatchState.tenantId !== tenantId || !container.isConnected) return;
    const n = snap.data() || {};
    if (!Number.isFinite(n.updatedAtMs) || Date.now() - n.updatedAtMs > _DL_NOTICE_MAX_AGE_MS) return;
    const key = 'dd_dl_notice_dismissed:' + tenantId;
    try { if (localStorage.getItem(key) === String(n.updatedAtMs)) return; } catch (_) { /* storage blocked */ }
    const lines = [];
    for (const g of Array.isArray(n.expiring) ? n.expiring : []) {
      lines.push(`Driver link expired — ${_dlNoticeWho(g)} lose${(g.count || 0) === 1 ? 's' : ''} the schedule in about a day.`);
    }
    if (n.lockedOut && n.lockedOut.count > 0) {
      lines.push(`Lost the schedule last night: ${_dlNoticeWho(n.lockedOut)}.`);
    }
    if (!lines.length) return;
    document.getElementById('dd-driver-link-notice')?.remove();
    const el = document.createElement('div');
    el.id = 'dd-driver-link-notice';
    el.setAttribute('role', 'status');
    el.style.cssText = 'display:flex;gap:10px;align-items:flex-start;flex-wrap:wrap;margin:8px 12px;padding:10px 12px;border-radius:var(--radius-md);background:#FEF3C7;color:#78350F;border:1px solid #F59E0B;font-size:0.9rem;';
    const icon = document.createElement('span');
    icon.className = 'material-icons';
    icon.textContent = 'link_off';
    icon.style.cssText = 'font-size:20px;color:#B45309;';
    const body = document.createElement('div');
    body.style.cssText = 'flex:1;min-width:200px;';
    const title = document.createElement('strong');
    title.textContent = 'Driver links';
    body.appendChild(title);
    for (const l of lines) { const p = document.createElement('div'); p.textContent = l; body.appendChild(p); }
    const hint = document.createElement('div');
    hint.style.cssText = 'margin-top:4px;';
    hint.textContent = 'Send a new driver link to keep them on. The old link cannot be extended.';
    body.appendChild(hint);
    const send = document.createElement('button');
    send.className = 'btn btn-primary btn-sm';
    send.type = 'button';
    send.textContent = 'Send new link';
    send.addEventListener('click', () => { try { showMassText({ forceStandard: true }); } catch (e) { console.warn('[Dispatch] notify crew open failed', e); } });
    const close = document.createElement('button');
    close.className = 'btn btn-secondary btn-sm';
    close.type = 'button';
    close.textContent = 'Dismiss';
    close.addEventListener('click', () => {
      try { localStorage.setItem(key, String(n.updatedAtMs)); } catch (_) { /* storage blocked */ }
      el.remove();
    });
    el.append(icon, body, send, close);
    container.prepend(el);
  } catch (e) {
    console.warn('[Dispatch] driver-link notice skipped:', e && e.message);
  }
}

// P0 1019 (Cassian: TITS driver board listed Split Rock's vehicles) —
// DispatchState is module-scoped and outlives a logout / company switch in the
// same tab. A PIN driver who lands on the Job Board never runs initDispatch, so
// the driver Vehicles list (_resolveDriverFleet) kept serving the PREVIOUS
// company's DispatchState.trucks. Rule: DispatchState.tenantId names the tenant
// every array below belongs to; a different tenant empties them all first, and
// readers outside the mounted board check _dispatchDataIsFor() before trusting them.
const _DISPATCH_TENANT_ARRAYS = [
  'jobs', 'jobDays', 'people', 'trucks', 'trailers', 'assets', 'customers',
  'permits', 'savedLocations', 'waypointCategoryDocs', 'safetyHolds', 'timeOffRequests',
  'opportunityHolds', 'vehicleHolds', 'potentials', 'driverBoardJobs', 'driverBoardJobDays',
];
function resetDispatchTenantData() {
  _DISPATCH_TENANT_ARRAYS.forEach(k => { DispatchState[k] = []; });
  DispatchState.peopleAll = undefined; // unset until a roster read fills it (readers fall back to people)
  DispatchState.pretripCoverage = {};
  DispatchState.pretripCoverageFromDay = null;
  DispatchState._fleetAll = { trucks: null, trailers: null, assets: null };
  DispatchState.dataLoaded = { people: false, trucks: false, trailers: false, assets: false };
  DispatchState.mapProjectId = null;
  DispatchState.mapCustKeys = DispatchState.mapColorMode = null; // TASK-4786 A4
  DispatchState._mapProjectJobs = {};
  DispatchState._externalJobCache = null; // openJobDetailsById — per tenant too
  DispatchState._mapProjectLoading = null;
  DispatchState._mapProjectLoadFailed = null;
  DispatchState._ldClock = {};
  DispatchState.mapLayers = null;
  DispatchState.mapViewActive = null;   // TASK-9631 S3
  DispatchState._mapViewPending = null;
  // TASK-4800 — equipment ledger + fetched jobs are per tenant too.
  DispatchState._mapEqTxns = null;
  DispatchState._mapEqTxnsState = 'idle';
  DispatchState._mapEqJobs = {};
  DispatchState._mapEqJobsInflight = null;
  DispatchState._mapEqFilters = { kind: 'all', customer: '', project: '' };
  DispatchState.tenantId = null;
}
function _syncDispatchTenant(tid) {
  if (DispatchState.tenantId !== tid) resetDispatchTenantData();
  DispatchState.tenantId = tid || null;
}
function _dispatchDataIsFor(tid) {
  return !!tid && DispatchState.tenantId === tid;
}

export function initDispatch(container, tenantId) {
  console.log('[Dispatch] Initializing for tenant:', tenantId);

  // Guard against null tenant
  if (!tenantId) {
    console.error('[Dispatch] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load dispatch. Please try logging out and back in.<br>
          If the issue persists, contact support.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  // TASK-9175 COD r2 — the map's caches are per tenant. A tenant switch must
  // not paint the previous tenant's project jobs, clock punches or layer prefs.
  // P0 1019 — nor its roster: the switch now drops people/trucks/trailers/
  // assets/customers/jobs too (resetDispatchTenantData), not only the map caches.
  _syncDispatchTenant(tenantId);

  // TASK-9219 — a queued crew text's outcome outlives the modal it was sent from
  // AND this view. Re-attach on every mount so navigating away (or reloading) is
  // not what makes a failed blast invisible; evicts a watch from another tenant.
  _resumeMassTextWatch(tenantId);

  // TASK-9151 — permit recovery state is per-FORM, and a tenant switch destroys
  // the form without going through it. showAddJob/editJob already reset this, so
  // this is belt-and-braces — but a stale submit lock would hand the next tenant
  // a Save button that is disabled forever, and that failure is silent.
  resetPermitUploadState();   // clears the tray too — and refuses while in flight

  // Reset view mode to week when re-entering module
  DispatchState.viewMode = 'week';
  DispatchState.listOffset = 0;
  // TASK-583 — first week render after entering the module lands on today
  // (mobile stacked layout auto-scrolls today's block to the top).
  DispatchState._scrollWeekToToday = true;

  // Reset data loaded flags - subscriptions will set them when data arrives
  DispatchState.dataLoaded = {
    people: false,
    trucks: false,
    trailers: false,
    assets: false
  };
  // TASK-9212 — resets WITH dataLoaded, not separately. This flag is the only
  // thing distinguishing "the permits collection has not arrived" from "that
  // ticket was deleted", and leaving it true across a remount (or a tenant
  // switch) while DispatchState.permits is empty makes every carried ticket look
  // deleted. COD Independent, round 5.
  DispatchState.permitsLoaded = false;
  DispatchState.potentials = []; // TASK-9887 — potential jobs/projects (Locates)
  DispatchState._jobDaysLoaded = false;
  DispatchState._jobsLoaded = false;
  DispatchState._jobsComplete = false;
  DispatchState._dispatchLoadFailed = false;

  // Check for driver mode from URL or AppState
  if (window.AppState?.driverMode || window.AppState?.driverModePin) {
    DispatchState.roleMode = 'driver';
    // Always start drivers on TODAY — `DispatchState.currentDate` is
    // module-scoped and persists across mode switches / re-logins on the
    // same page. Without this reset, a driver entering their PIN inherits
    // whatever date the dispatcher (or a prior driver session) had been
    // viewing. Drivers want today by default; not a remembered date.
    DispatchState.currentDate = new Date();
    DispatchState.driverCalendarMonth = new Date();
  }

  // TASK-9370 — a PIN driver who used Add/Edit Job from the Job Board has the
  // job modal mounted on <body> (driverOpenAddJob). getDispatchHTML() paints its
  // own copy, and two #modal-job / #form-job in one document means every
  // getElementById in this file hits whichever came first. Drop the host first.
  document.getElementById('dd-driver-job-modal-host')?.remove();

  // Render the dispatch UI
  container.innerHTML = getDispatchHTML();
  // TASK-9657 — paint the Approvals count (cached; gates itself off drivers/PIN).
  window.ApprovalsBadge?.paint();
  // Driver-link notice (nightly sweep): phones about to lose / that lost access.
  _paintDriverLinkNotice(container, tenantId);

  // Set up event handlers
  setupEventHandlers();
  setupDragHandlers();

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // TASK-4800 S3 — arrived from Roster › Assets › On site › "View on map".
  _consumePendingEquipmentMap();

  // TASK-9587 — arrived from a Claude link (#/dispatch/claude-job/<id>), or
  // from the approval list's "Review in Job Builder". Never in driver / link
  // mode (the parked focus is still consumed, so it cannot fire later).
  if (window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin) {
    window.RecordLinks?.takePendingFocus?.('dispatch');
  } else {
    _consumeClaudeJobLink();
  }
  _drainDispatchOpenHint(); // TASK-9671 S1 — a Claude lookup link (job / board date)

  // TASK-9260 — customer-portal requests awaiting approval (never in driver mode).
  if (!(window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin)) {
    _subscribeCustomerRequests(tenantId);
    _subscribePermitCostsWaiting(tenantId); // TASK-9585 S7d
  }

  // Return cleanup function
  return () => {
    DispatchState.unsubscribers.forEach(unsub => unsub());
    DispatchState.unsubscribers = [];
    try { _mapDrawMod?.resetMapLayers(); } catch (_) { /* TASK-4789 — drawn-layers listener goes with the session */ }
    try { _mapPinsMod?.resetMapPins(); } catch (_) { /* TASK-9631 S2 — a pin mid-drop/drag goes with the session */ }
    try { _mapViewsMod?.resetMapViews(); } catch (_) { /* TASK-9631 S3 — saved-maps control/dialog go with the session */ }
    // TASK-4834 — this list MIRRORS the SA.register() block in subscribeToData.
    // `job-po` and `job-vendor-number` are deliberately NOT Smart Assistant
    // fields (identifiers, see the comment at that block). A teardown list that
    // names a non-SA field is how one gets re-added by reflex;
    // tests/vendor-number-identifier.unit.cjs fails if this list names an id the
    // register block does not register.
    ['job-title','job-well-name','job-location','job-notes']
      .forEach(id => window.SmartAssistant?.unregister(id));
  };
}

// ============================================
// HTML TEMPLATE
// ============================================
function getDispatchHTML() {
  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin;

  // Driver view - simplified jobs list only
  if (isDriverMode) {
    return getDriverBoardHTML();
  }

  // 811-only mode — permits person sees just the upload button
  // TASK-9994: nothing 811 while the company has it off.
  if (is811OnlyMode() && !is811Enabled()) {
    return `
      <div class="dispatch-view" style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:12px;">
        <span class="material-icons" style="font-size:56px;color:var(--text-secondary);" aria-hidden="true">block</span>
        <p style="color:var(--text-primary);text-align:center;max-width:340px;margin:0;">There's nothing here for your login right now. Ask the owner to check what your login can open.</p>
      </div>`;
  }
  if (is811OnlyMode()) {
    return `
      <div class="dispatch-view" style="display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:60vh;gap:16px;">
        <span class="material-icons" style="font-size:64px;color:#F59E0B;">assignment</span>
        <h2 style="margin:0;color:var(--text-primary);">Locates (811)</h2>
        <p style="color:var(--text-secondary);text-align:center;max-width:320px;">${window.isPartner811ReadOnly?.() === true ? "View this company's 811 ticket PDFs." : 'Upload the locate ticket PDFs from the one-call website. We read the ticket #, dates and site for you to check.'}</p>
        <button class="btn btn-tool btn-811-submit" onclick="window.DispatchModule.show811Submission()" style="font-size:1rem;padding:14px 28px;">
          <span class="material-icons">cloud_upload</span>
          <span>Open Locates (811)</span>
        </button>
      </div>
      ${getJobModalsHTML()}
    `;
  }

  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return `
    <div class="dispatch-view">
      <!-- TASK-1077: Consolidated dispatch toolbar — one bordered control card, two rows.
           Row 1: view segmented control (left) + actions cluster (right).
           Row 2: Today (left) + centered date-nav + filter pills (right). -->
      <div class="dispatch-toolbar">
        <div class="dispatch-toolbar-row dispatch-toolbar-row1">
        <!-- LEFT: view segmented control (Week · Month · List · Lanes · Map · Projects) + the ⋮ overflow menu.
             Search stays visible in the actions zone.
             TASK-1263 (Chris): the ⋮ sits to the RIGHT of the segmented bar in its own space,
             not in .header-actions — where it was the last item in a wrapping flex row and so
             dropped ALONE onto a centered line of its own at phone widths. -->
        <div class="dispatch-viewbar">
        <div class="dispatch-view-toggle">
          <div class="view-toggle">
            <button class="view-btn active" data-view="week" title="Week View">
              <span class="material-icons">view_week</span>
              <span class="view-btn-label">Week</span>
            </button>
            <button class="view-btn" data-view="month" title="Month View">
              <span class="material-icons">calendar_view_month</span>
              <span class="view-btn-label">Month</span>
            </button>
            <button class="view-btn" data-view="list" title="List View">
              <span class="material-icons">view_agenda</span>
              <span class="view-btn-label">List</span>
            </button>
            <button class="view-btn" data-view="lanes" title="Lanes View">
              <span class="material-icons">view_timeline</span>
              <span class="view-btn-label">Lanes</span>
            </button>
            <button class="view-btn" data-view="map" title="Maps — job destinations, projects, saved places, last-dispatched trucks and fleet">
              <span class="material-icons">map</span>
              <span class="view-btn-label">Maps</span>
            </button>
            <!-- Chris 2026-09-01: Projects was buried in the ⋮ overflow. It's a VIEW
                 MODE like the other five (renderProjectsView owns the board), not an
                 action, so it belongs in the segmented bar. dispatcher-only stays —
                 a PIN driver must never reach the projects surface.
                 TASK-9651: and only a login that holds the 'projects' module. -->
            ${window.canAccessProjects?.() === false ? '' : `<button class="view-btn dispatcher-only" data-view="projects" title="Projects">
              <span class="material-icons">folder_special</span>
              <span class="view-btn-label">Projects</span>
              <!-- TASK-9392: pending customer requests (painted by _paintCustomerRequestBadges) -->
              <span id="dispatch-projects-cr-badge" class="dd-cr-badge" style="display:none;"></span>
            </button>`}
          </div>
        </div>
          <div class="dispatch-actions-overflow">
            <button class="job-overflow-btn dispatch-more-btn" title="More actions" aria-haspopup="true" aria-label="More dispatch actions">
              <span class="material-icons">more_vert</span>
            </button>
            <div class="overflow-dropdown dispatch-overflow-dropdown">
              ${is811Enabled() ? `<button class="overflow-item dispatcher-only" id="btn-811-submit" onclick="window.DispatchModule.show811Submission()" title="Upload and manage 811 permits">
                <span class="material-icons">assignment</span> Locates (811)
              </button>` : ''}
              <button class="overflow-item dispatcher-only" onclick="window.DispatchModule.showExportJobsModal()" title="Export or print the job schedule for a date range">
                <span class="material-icons">file_download</span> Export / Print Jobs
              </button>
              <button class="overflow-item dispatcher-only" onclick="window.DispatchModule.openJobActivityLog()" title="See who created, edited, or deleted jobs">
                <span class="material-icons">history</span> History
              </button>
              <!-- TASK-10010 — owner only; unhidden each time the menu opens (tenant data can land after this renders). -->
              <button class="overflow-item dispatcher-only" id="btn-recently-deleted" ${_canRestoreDeletedJobs() ? '' : 'hidden'} onclick="window.DispatchModule.openRecentlyDeleted()" title="Bring back a job deleted in the last 30 days">
                <span class="material-icons">restore_from_trash</span> Recently deleted
              </button>
            </div>
          </div>
        </div>
        <!-- RIGHT-secondary: Search (visible) + Add Job (primary) -->
        <div class="header-actions">
          <button class="view-btn dispatch-search-btn dispatcher-only" data-view="search" title="Search Past Jobs">
            <span class="material-icons">search</span>
            <span class="view-btn-label">Search</span>
          </button>
          <button class="btn btn-primary btn-prominent dispatch-addjob-btn dispatcher-only" onclick="window.DispatchModule.showAddJob()">
            <span class="material-icons">add</span> <span class="btn-label">Add Job</span>
          </button>
          <button class="btn btn-secondary btn-tool btn-mass-text dispatch-notify-btn dispatcher-only" onclick="window.DispatchModule.showMassText()" title="Send today's schedule to all assigned drivers via SMS">
            <span class="material-icons">sms</span> <span class="btn-tool-label">Notify Crew</span>
          </button>
          <button class="btn btn-secondary btn-neon-settings btn-module-header dispatcher-only" onclick="window.DispatchModule.openDispatchSettingsModal()" title="Dispatch Settings">
            <span class="material-icons">settings</span>
          </button>
        </div>
        </div>

        <div class="dispatch-toolbar-row dispatch-toolbar-row2">
        <!-- TASK-1077 (Chris review) — no toolbar Today button: the contextual
             floating "Today" chip (TASK-583, week-today-jump) already appears exactly
             when today is off-screen (scrolled away / different week) and auto-scrolls
             back; an always-on toolbar duplicate was redundant. Empty left grid cell
             keeps the date centered. -->
        <div class="dispatch-toolbar-today">
          <!-- TASK-9657 — Claude requests waiting for THIS person's approval. Lives in
               this empty left cell, not row 1: row 1 is already full at laptop widths
               (a 6th action pushed off the right edge). Hidden until window.ApprovalsBadge
               paints a count > 0; tap opens the approval list (#/account?claude=requests).
               dispatcher-only: never on a PIN board. -->
          <button type="button" class="btn btn-secondary dd-approvals-btn dispatcher-only" data-approvals-badge hidden style="display:none;">
            <span class="material-icons">pending_actions</span> <span class="dd-approvals-label">Approvals</span> <span class="dd-approvals-count"></span>
          </button>
        </div>
        <!-- CENTER: date SELECTOR (prev/next control). -->
        <div class="dispatch-datenav-center">
        <div class="date-nav">
          <button class="btn btn-icon-nav" onclick="window.DispatchModule.navigate(-1)" title="Previous">
            <span class="material-icons">chevron_left</span>
          </button>
          <span class="date-display" id="current-date">${dateDisplay}</span>
          <button class="btn btn-icon-nav" onclick="window.DispatchModule.navigate(1)" title="Next">
            <span class="material-icons">chevron_right</span>
          </button>
        </div>
      </div>

      <!-- Layer Filters — only render on Week/Month, controlled by toggleLayerFilter -->
      <div class="dispatch-layer-filters" id="dispatch-layer-filters">
        <!-- TASK-10195: chips live in their own strip so on a phone the strip
             scrolls sideways (nothing wraps) and Load older drops to its own line. -->
        <div class="layer-chip-strip">
        <button class="layer-chip" data-layer="jobs" aria-pressed="true" onclick="event.stopPropagation(); window.DispatchModule.toggleLayerFilter('jobs')" title="Show or hide jobs on the calendar">
          <span class="material-icons">local_shipping</span>
          <span class="layer-chip-label">Jobs</span>
        </button>
        <button class="layer-chip" data-layer="daysoff" aria-pressed="true" onclick="event.stopPropagation(); window.DispatchModule.toggleLayerFilter('daysoff')" title="Show or hide PTO and days-on">
          <span class="material-icons">event_busy</span>
          <span class="layer-chip-label">Days Off</span>
        </button>
        <!-- TASK-4646: Completed. autoCloseJobs flips finished work to status:'finished'
             and the calendar used to drop those rows BEFORE any renderer saw them, so a
             tenant's whole past emptied out overnight with no signal. History now paints
             (dimmed) by default; this chip is the OPT-OUT, and turning it off fires the
             "N items hidden" pill so nobody loses a month silently again.
             dispatcher-only: a PIN driver's board must not sprout months of closed work. -->
        <button class="layer-chip dispatcher-only" data-layer="completed" aria-pressed="true" onclick="event.stopPropagation(); window.DispatchModule.toggleLayerFilter('completed')" title="Show or hide finished jobs on the calendar">
          <span class="material-icons">task_alt</span>
          <span class="layer-chip-label">Completed</span>
        </button>
        <!-- TASK-461: Maintenance chip removed — vehicle holds AND safety holds are
             now always visible (hiding a critical DVIR safety hold = FMCSA §396.9/396.11
             liability). maintenance filter is forced hard-true in calendarFilters init. -->
        <span class="layer-filters-warning" id="layer-filters-warning" style="display:none;"></span>
        </div>
        <!-- TASK-424 #5 (C2): Load older. The board streams the last ~90 days
             (+ all upcoming) by default; this widens the live window further
             back. Hidden in driver mode and once the window is "all". -->
        <button class="dispatch-load-older-link dispatcher-only" id="dispatch-load-older" style="display:none;" onclick="event.stopPropagation(); window.DispatchModule.loadOlderDispatch()" title="Load jobs older than the current window">
          <span class="material-icons">history</span>
          <span class="load-older-label"><span class="load-older-window"></span><span class="load-older-action">Load older</span></span>
        </button>
      </div>
        </div>
      </div>

      <!-- TASK-9260: customer-portal requests waiting for approval. Filled by
           _renderCustomerRequestStrip(); hidden when there are none or the
           viewer cannot read them (rules: dispatcher+ or dispatch module). -->
      <div id="customer-requests-strip" class="dispatcher-only" style="display:none;"></div>

      <!-- TASK-9585 S7d: AI-read permit costs from before S7 that nobody has said
           yes to. Filled by _renderPermitWaitStrip(); hidden when there are none
           or the viewer has no Dispatch module (or is not the owner). -->
      <div id="permit-costs-waiting-strip" class="dispatcher-only" style="display:none;"></div>

      <!-- TASK-10007: starred Roster assets — owned / out / available. Filled by
           renderPinnedAssetStrip(); hidden when nothing is starred, and never
           rendered for a driver session (gated in code, not only this class). -->
      <div id="pinned-assets-strip" class="dispatcher-only" style="display:none;max-width:100%;min-width:0;"></div>

      <!-- Calendar/List Container -->
      <div class="dispatch-content" id="dispatch-content">
        <div class="loading-indicator">
          <div class="loading-spinner"></div>
          <p>Loading dispatch board...</p>
        </div>
      </div>
    </div>

    ${getJobModalsHTML()}
  `;
}

// ============================================
// DRIVER BOARD - Simplified view for drivers
// ============================================

function getDriverBoardHTML() {
  const today = new Date();
  const dateDisplay = today.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  // Check if we're in PIN-based driver mode
  const isPinMode = window.AppState?.driverModePin;
  const driverName = (window.AppState?.driverPersonName || 'Driver').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');

  return `
    <div class="driver-board">
      ${isPinMode ? `
      <!-- PIN Driver Mode Header -->
      <div class="driver-mode-header">
        <span class="driver-name">Viewing as: ${driverName}</span>
        <button class="btn-exit-driver" onclick="window.exitDriverPinMode()">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:6px;">logout</span>
          Exit
        </button>
      </div>
      ` : ''}

      <!-- Driver Header -->
      <div class="driver-board-header">
        <h1 class="driver-board-title">Daily Jobs</h1>
        <span class="driver-board-count" id="driver-job-count">0 Jobs</span>
      </div>

      <!-- TASK-347 Phase 2 advanced 2026-05-22 — .driver-action-bar removed
           per Chris (all 6 buttons redundant with bottom-nav primary tabs +
           Actions sheet tiles). All handlers preserved on window.DispatchModule
           (called from bottom-nav + Actions sheet). -->

      <!-- Tab Bar — TASK-9358: only when the driver can actually USE the
           Timesheet (driverHasClockAccess = PIN session at a clock tenant). A
           read-only ?access= link (or any non-PIN session) never initializes it
           (initDriverTimesheet bails), so the tab opened an empty panel. Whole
           bar gated so no lone "Jobs" tab is left; the Jobs view renders by
           default either way. Mirrors the bottom-nav Clock gate below. -->
      ${(window.driverHasClockAccess && window.driverHasClockAccess()) ? `
      <div id="driver-tab-bar" style="display:flex;gap:0;margin:0 0 12px;border-radius:10px;overflow:hidden;border:1.5px solid var(--border-light,#E2E8F0);background:var(--bg-secondary,#F1F5F9);">
        <button id="driver-tab-jobs" class="driver-tab active" onclick="window.DispatchModule.switchDriverTab('jobs')" style="flex:1;padding:10px;font-size:0.85rem;font-weight:600;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:all 0.2s;background:#fff;color:var(--text-primary,#0F172A);">
          <span class="material-icons" style="font-size:18px;">work</span> Jobs
        </button>
        <button id="driver-tab-timesheet" class="driver-tab" onclick="window.DispatchModule.switchDriverTab('timesheet')" style="flex:1;padding:10px;font-size:0.85rem;font-weight:600;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:6px;transition:all 0.2s;background:transparent;color:var(--text-secondary,#64748B);">
          <span class="material-icons" style="font-size:18px;">date_range</span> Timesheet
        </button>
      </div>
      ` : ''}

      <!-- Jobs View -->
      <div id="driver-view-jobs">
        <!-- Unified date-nav pill (chevrons + tappable date) + calendar toggle -->
        <div class="driver-date-nav">
          <div class="driver-date-pill">
            <button class="driver-pill-chevron" type="button" aria-label="Previous"
              onclick="window.DispatchModule.navigate(-1)">
              <span class="material-icons">chevron_left</span>
            </button>
            <button class="driver-pill-date" type="button"
              onclick="window.DispatchModule.driverJumpToToday()"
              title="Tap to jump to today">
              <span class="driver-pill-date-main" id="current-date">${dateDisplay}</span>
              <span class="driver-pill-date-sub" id="current-date-sub"></span>
            </button>
            <button class="driver-pill-chevron" type="button" aria-label="Next"
              onclick="window.DispatchModule.navigate(1)">
              <span class="material-icons">chevron_right</span>
            </button>
          </div>
          <button id="driver-view-mode-toggle" class="driver-nav-btn" type="button"
            onclick="window.DispatchModule.toggleDriverViewMode()"
            title="Switch between day and calendar view">
            <span class="material-icons" id="driver-view-mode-icon">calendar_month</span>
          </button>
        </div>

        <!-- Jobs List (day mode) OR Calendar Grid (calendar mode) -->
        <div class="driver-jobs-list" id="dispatch-content">
          <div class="loading-indicator">
            <div class="loading-spinner"></div>
            <p>Loading your jobs...</p>
          </div>
        </div>
      </div>

      <!-- Timesheet View (hidden by default) -->
      <div id="driver-view-timesheet" style="display:none;">
      </div>

      <!-- My Timesheet Section (moved into timesheet view) -->
      <div id="driver-timesheet-section" style="display:none;margin-top:0;">
        <div id="pub-timesheet" style="background:#fff;border:1px solid var(--border-light,#E2E8F0);border-radius:12px;padding:0;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,0.08);">
          <div style="display:flex;align-items:center;gap:8px;padding:14px 20px;border-bottom:1px solid var(--border-light,#E2E8F0);cursor:pointer;" onclick="window.DispatchModule.toggleTimesheet()">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue,#3B82F6);">date_range</span>
            <span style="font-weight:600;font-size:0.9rem;color:var(--text-primary,#0F172A);">My Timesheet</span>
            <span class="material-icons" id="ts-chevron" style="margin-left:auto;font-size:20px;color:var(--text-muted,#94A3B8);">expand_more</span>
          </div>
          <div id="pub-ts-summary" style="display:none;padding:12px 20px;border-bottom:1px solid var(--border-light,#E2E8F0);"></div>
          <div id="pub-ts-days" style="display:none;"></div>
        </div>
      </div>

      <!-- TASK-361 (2026-05-26) — "My Recent JSAs" section was relocated to
           Inspections > JSA tab where drivers look for their safety forms.
           dispatch.js parallel implementation removed 2026-05-28 (user
           bug report: section still appearing below daily jobs because
           TASK-361 only cleaned up jobboard.js). See jsa.js
           renderMyJsaSection. -->

      <!-- Refresh Button -->
      <button class="driver-refresh-btn" onclick="window.DispatchModule.refreshDriverBoard()">
        <span class="material-icons">refresh</span>
      </button>

      ${isPinMode ? getDriverBottomNavHTML() : ''}
      ${isPinMode ? getDriverActionsSheetHTML() : ''}
    </div>

    ${getDriverQuickviewModalHTML()}
  `;
}

// TASK-347 Slice 4 — One-time trailguide tip pointing at the new bottom nav.
// Uses localStorage instead of the Trailguide module so PIN-driver sessions
// (which never load trailguide.js — TASK-345 S3 lazy-import) still see it.
// Dismissed via tap-anywhere or tap the X. Stored as
// dd-trailguide-driver-bottomnav-seen=1 in localStorage.
function _maybeShowBottomNavTip() {
  try {
    if (localStorage.getItem('dd-trailguide-driver-bottomnav-seen') === '1') return;
    if (document.getElementById('driver-btmnav-tip')) return;
    // Defer until layout settles so positioning aligns with the rendered nav.
    setTimeout(() => {
      const nav = document.getElementById('driver-bottom-nav');
      if (!nav) return;
      const tip = document.createElement('div');
      tip.id = 'driver-btmnav-tip';
      tip.setAttribute('role', 'status');
      tip.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:96px;background:var(--brand-blue,#3b82f6);color:#fff;padding:10px 14px;border-radius:10px;font-size:0.9rem;font-weight:600;box-shadow:0 4px 12px rgba(15,23,42,0.25);z-index:115;max-width:90vw;display:flex;align-items:center;gap:10px;animation:driver-btmnav-tip-in 0.3s ease-out;';
      tip.innerHTML = '<span>New: quick actions at the bottom</span>'
        + '<span class="material-icons" style="font-size:20px;">arrow_downward</span>'
        + '<button type="button" aria-label="Dismiss" style="background:rgba(255,255,255,0.2);border:none;color:#fff;border-radius:6px;padding:2px 8px;cursor:pointer;font-size:1.1rem;line-height:1;">&times;</button>';
      const dismiss = () => {
        try { localStorage.setItem('dd-trailguide-driver-bottomnav-seen', '1'); } catch (e) {}
        tip.remove();
      };
      tip.querySelector('button').addEventListener('click', dismiss);
      // Auto-dismiss after 8s if untouched.
      setTimeout(() => { if (document.body.contains(tip)) dismiss(); }, 8000);
      document.body.appendChild(tip);
    }, 400);
  } catch (e) { /* localStorage may be blocked — non-fatal */ }
}

// TASK-347 Slice 1 — Driver bottom-nav primitive.
// 4 fixed tabs (Inspections / Clock / Exam Report / Actions) rendered ONLY
// in PIN-driver mode. CSS in styles.css. Phase 2 advanced 2026-05-22 —
// legacy .driver-action-bar removed (was scheduled ~2026-06-04, Chris
// confirmed all 6 redundant with bottom-nav + Actions sheet).
// Exam Report tab keeps a hidden placeholder slot when enableCitations is
// false so the grid does not reflow when feature flags load async (Code
// Reviewer pre-locked mitigation from cod-proposals doc, section 7).
function getDriverBottomNavHTML(opts) {
  const showExam = window.AppState?.settings?.enableCitations !== false;
  // v1.15.923 — accept optional opts.clockOnClick so the calling view can
  // inject its own clock-button handler. JobBoard passes
  // 'window.JobBoardModule.handleBottomNavClock()' because dispatch's
  // openDriverClockIn reads DispatchState.customers which is empty when
  // PIN drivers are on /jobboard (TASK-355 canonical view). Architect COD
  // ⚠️ flagged the cross-module circular dep; config-object injection
  // keeps dispatch.js isolated.
  const clockOnClick = (opts && opts.clockOnClick)
    || 'window.DispatchModule.handleDriverClockButton()';
  // TASK-587 — off-board (shell) variant options, used by app.js
  // _syncDriverPinShellChrome to keep this nav alive on non-board PIN views
  // (Inspections/Maintenance):
  //   opts.boardTab  — prepend a "Job Board" home tab (off-board views have
  //                    no other way back once the generic #mobile-nav hides).
  //                    Routes to jobboard (canonical PIN landing) when the
  //                    driver has clock access, else the dispatch driver board.
  //   opts.activeView — highlight the matching tab ('pretrip' → Inspections).
  //   opts.clockRoutesToBoard — render the Clock tab as a pure ROUTER back to
  //                    the Job Board (neutral "Clock" label, no in/out claim).
  //                    Off the board there is NO live clock-state source
  //                    (DispatchState._clockEntries is empty for PIN drivers,
  //                    JobBoardState's sync only runs while its subscription
  //                    is mounted), so an action-button label could lie about
  //                    clock-in/out direction — a TASK-428-class hazard.
  //                    Routing keeps the tab's muscle-memory position (COD UX
  //                    Lead) while the live state loads safely on the board.
  //                    Hidden entirely when the carrier has no clock access
  //                    (mirrors updateDriverBottomNavClock's display gate).
  // Board call sites pass none of these → board markup unchanged.
  const activeView = (opts && opts.activeView) || '';
  const boardTabHTML = (opts && opts.boardTab) ? `
      <button type="button" class="driver-bottom-nav-item" id="driver-btmnav-board"
        onclick="window.navigateToView(window.driverHasClockAccess&&window.driverHasClockAccess()?'jobboard':'dispatch')" aria-label="Open job board">
        <span class="material-icons">assignment</span>
        <span class="driver-bottom-nav-label">Job Board</span>
      </button>` : '';
  const clockTabHTML = (opts && opts.clockRoutesToBoard)
    ? ((window.driverHasClockAccess && window.driverHasClockAccess()) ? `
      <button type="button" class="driver-bottom-nav-item" id="driver-btmnav-clock"
        onclick="window.navigateToView('jobboard')" aria-label="Open job board to clock in or out">
        <span class="material-icons" id="driver-btmnav-clock-icon">schedule</span>
        <span class="driver-bottom-nav-label" id="driver-btmnav-clock-label">Clock</span>
      </button>` : '')
    : `
      <button type="button" class="driver-bottom-nav-item" id="driver-btmnav-clock"
        onclick="${clockOnClick}" aria-label="Clock in or out">
        <span class="material-icons" id="driver-btmnav-clock-icon">login</span>
        <span class="driver-bottom-nav-label" id="driver-btmnav-clock-label">Clock In</span>
      </button>`;
  // Single-quoted strings everywhere inside template literals here — never
  // use backticks inside HTML comments inside JS template literals. See
  // CLAUDE.md Critical Pitfall and 2026-05-21 prod-breaker incidents.
  //
  // TASK-9373 — each tab is one item so the per-tenant layout
  // (settings.driverApp via window.driverAppLayout, app.js) can hide + reorder
  // INSIDE this one builder — both call sites (jobboard.js board, app.js
  // _syncDriverPinShellChrome off-board shell) get it for free; never filter
  // at a call site. NEVER_HIDE (board, Inspections, Clock, Actions) is
  // enforced by the reader; only Exam Report is hideable here. A default
  // layout returns the items untouched, so the markup is byte-identical to
  // the pre-task nav (tests/driver-app-layout.unit.cjs snapshot).
  const navItems = [
    { id: 'driver-btmnav-board', html: boardTabHTML },
    { id: 'driver-btmnav-inspect', html: `
      <button type="button" class="driver-bottom-nav-item${activeView === 'pretrip' ? ' active' : ''}" id="driver-btmnav-inspect"
        onclick="window.navigateToView('pretrip')" aria-label="Open inspections">
        <span class="material-icons">fact_check</span>
        <span class="driver-bottom-nav-label">Inspections</span>
      </button>` },
    { id: 'driver-btmnav-clock', html: clockTabHTML },
    { id: 'driver-btmnav-exam', html: `
      <button type="button" class="driver-bottom-nav-item ${showExam ? '' : 'driver-bottom-nav-item-hidden'}"
        id="driver-btmnav-exam"
        onclick="window.DispatchModule.openCitationUpload()" aria-label="Submit exam report"
        ${showExam ? '' : 'aria-hidden="true" tabindex="-1"'}>
        <span class="material-icons">gavel</span>
        <span class="driver-bottom-nav-label">Exam Report</span>
      </button>` },
    { id: 'driver-btmnav-actions', html: `
      <button type="button" class="driver-bottom-nav-item" id="driver-btmnav-actions"
        onclick="window.DispatchModule.openDriverActionsSheet()" aria-label="More actions">
        <span class="material-icons">more_horiz</span>
        <span class="driver-bottom-nav-label">Actions</span>
      </button>` },
  ];
  const navLayout = (window.driverAppLayout && window.driverAppApplyOrder) ? window.driverAppLayout() : null;
  const shownNav = navLayout ? window.driverAppApplyOrder('nav', navItems, navLayout) : navItems;
  return `
    <nav id="driver-bottom-nav" class="driver-bottom-nav" role="navigation" aria-label="Driver quick actions">${shownNav.map(i => i.html).join('')}
    </nav>
  `;
}

// TASK-347 Slice 3 — Driver Actions sheet (mirrors .mobile-more-menu pattern).
// Tiles render conditionally on tap (openDriverActionsSheet) so that addon
// flags loaded after the initial render do not produce stale empty tiles.
function getDriverActionsSheetHTML() {
  return `
    <div id="driver-actions-sheet" class="driver-actions-sheet hidden" role="dialog" aria-modal="true" aria-label="Driver actions">
      <div class="driver-actions-backdrop" onclick="window.DispatchModule.closeDriverActionsSheet()"></div>
      <div class="driver-actions-sheet-content">
        <div class="driver-actions-sheet-handle"></div>
        <div class="driver-actions-sheet-title">Actions</div>
        <div class="driver-actions-tiles" id="driver-actions-tiles">
          <!-- Tiles injected by openDriverActionsSheet() based on current addon + supervisor state -->
        </div>
      </div>
    </div>
  `;
}

function getDriverQuickviewModalHTML() {
  return `
    <!-- Driver Quickview Modal -->
    <div class="modal hidden" id="modal-driver-quickview">
      <div class="modal-content driver-quickview-modal">
        <div class="driver-quickview-header" id="driver-qv-header">
          <div class="quickview-header-text">
            <h2 id="driver-qv-customer">Customer Name</h2>
            <p id="driver-qv-title">Job Title</p>
          </div>
          <button class="driver-quickview-close" onclick="window.DispatchModule.closeModal('driver-quickview')">
            <span class="material-icons">close</span>
          </button>
        </div>

        <div class="driver-quickview-body" id="driver-qv-body">
          <!-- Content populated dynamically -->
        </div>

        <div class="driver-quickview-footer" id="driver-qv-footer">
          <button class="btn btn-driver-action btn-directions" id="driver-qv-directions" onclick="window.DispatchModule.openDirections()">
            <span class="material-icons">near_me</span> Directions
          </button>
          <button class="btn btn-driver-action btn-vehicle hidden" id="driver-qv-vehicle" onclick="window.DispatchModule.showDriverTruckDetails()">
            <span class="material-icons">local_shipping</span> Vehicle
          </button>
          <button class="btn btn-driver-action btn-call hidden" id="driver-qv-call" onclick="window.DispatchModule.callPOC()">
            <span class="material-icons">phone</span> Call
          </button>
        </div>
      </div>
    </div>

    <!-- Driver Truck Details Modal -->
    <div class="modal hidden" id="modal-driver-truck">
      <div class="modal-content driver-truck-modal">
        <div class="modal-header">
          <h3 id="driver-truck-title">Vehicle Details</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('driver-truck')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="driver-truck-body">
          <!-- Content populated dynamically -->
        </div>
      </div>
    </div>
  `;
}

// (moved from the markup below — HTML comments ship to every user) Project —
// TASK-4695. An always-visible, optional picker directly under Customer
// (TASK-4688 put it here; Customer, then Project, then Job is the real
// hierarchy). It replaces the "Add to Project" checkbox, which kept the picker
// hidden until ticked and was passed over on almost every job — 844 founder-
// tenant jobs, none linked. "No project" IS the unlinked state. #job-add-to-
// project and #job-create-new-project stay as HIDDEN mirrors, kept in step by
// _syncJobProjectLinkBox, because saveJob, the dirty check,
// _jobFormProjectIdForSave and the copy carry all read them. Solid tint, not
// the Customer gradient: Project is optional, Customer is not.
// (moved from the markup below — HTML comments ship to every user) TASK-4695 —
// the fields most jobs never touch, grouped and collapsed (last 200 founder-
// tenant jobs: well 5%, location 3%, AFE and vendor 0%, per-vehicle tickets
// 0%, geofence 0%). A collapsed group never hides what it holds: its summary
// row names every filled value, refreshed on open, on every programmatic fill
// (applyJobFormSectionVisibility) and on every keystroke. A group whose every
// section is switched off in Dispatch Settings (or trimmed off the driver
// form) disappears with them. Hazmat / Interstate / Liquid Tank are
// deliberately NOT in here: they drive runJobComplianceCheck, and a collapsed
// flag is a hazmat haul created with no compliance check (TASK-731).
// (moved from the markup below — HTML comments ship to every user) The icon is
// "event", NOT "event_upcoming". /fonts/material-icons.woff2 is the classic
// Material Icons set and has no event_upcoming ligature, so it half-resolved
// into two overlapping glyphs. Caught on a Pixel 5 viewport, not by any exit
// code — a missing ligature renders garbage rather than throwing. "event" is
// already used in app.js, so it is known present in this font file. NO
// BACKTICKS IN THIS COMMENT: all of this markup lives inside a JS template
// literal, and a backtick here ends the literal and kills the whole module.
// align-items:flex-start because this copy wraps to three lines on a phone;
// centering would float the icon against the middle line.
// (moved from the markup below — HTML comments ship to every user) TASK-4811 —
// Notes lives on jobs/{id} and NEVER on a jobDay: one note is rendered by
// every day of the job, so editing it from any day rewrites it for all of
// them. A dispatcher hit this live 2026-09-08 (Stellar equipment move): he
// added a second day, typed in this box, and silently replaced the first day's
// note. The text is unrecoverable — jobAuditLog's changedFields never captures
// notes text, so there is no error, no undo and no restore. The top-of-form
// banner already said "notes apply to all N days" and was scrolled past; this
// form is long and the box is ~700px below it. So the scope is stated HERE, at
// the control, before the keystroke. Rewritten per-open by
// _renderJobWideScopeMarkers; hidden on new/single-day jobs.
// (moved from the markup below — HTML comments ship to every user) TASK-1331:
// "Add Day Instead" read as "give up the crew you were about to set" — the
// button now ends in the per-day crew editor, so say that. TASK-4812: this is
// an ADVISORY warning inside the CREATE form, and the primary action of that
// screen is Save Job at the bottom. A 192px blue gradient next to a 69px
// "Dismiss" pushed a dispatcher who came here on purpose to fold their new job
// into an old one, and gave the option they wanted no name at all. Two
// symmetric secondaries; the copy above already carries the recommendation.
// The label is deliberately NOT "Keep as separate job" — dismissing sets no
// bypass, so saveJob's own duplicate gate (~:10087) still asks, and THAT modal
// is where Add Day is legitimately primary. This button states what the
// dispatcher believes, not a promise the app then breaks.
// (moved from the markup below — HTML comments ship to every user) TASK-4792 —
// this modal used to CLIP instead of scrolling on a laptop. (No backticks in
// here: this comment is inside a JS template literal, and one backtick would
// end the string and kill the whole module.) .modal-content is a flex column
// with max-height:90vh and overflow:hidden, and the only rule that ever gave a
// plain form inside one a scrollbar lives in the 768px-and-under block —
// .modal-content form:not(.modal-form-scrollable), styles.css ~:9693. On
// desktop there was NO such rule, so everything past 90vh was simply
// unreachable: no scrollbar, no wheel, nothing. It never showed on a 27-inch
// monitor (90vh is ~1200px, the whole form fits) and it is unmissable on a
// 1080p-or-shorter laptop, where 90vh is ~620px and cuts off around the
// Vehicles section. Mike hit it 2026-09-08 trying to reach Assign on a day.
// Structure copied from #form-job (:1715): scrollable form + inner .modal-
// form-scroll + pinned .modal-form-footer. The form's own padding is zeroed
// because .modal-content form (styles.css :5190) still applies spacing-lg and
// .modal-form-scroll brings its own — without that the content would be
// double-padded.
function getJobModalsHTML() {
  return `
    <!-- Add/Edit Job Modal -->
    <div class="modal hidden" id="modal-job">
      <div class="modal-content modal-large" style="display:flex;flex-direction:column;max-height:95vh;max-height:95dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3 id="modal-job-title">New Job</h3>
          <div class="modal-header-actions">
            <button type="button" id="btn-ai-create" class="btn btn-ai ai-entry" onclick="window.DispatchModule.toggleAiPrompt()" title="Smart Job Builder. Describe the job in plain English and it will populate the fields, match crew &amp; equipment, and flag compliance issues automatically.">
              <span class="icon-dd-brain" aria-hidden="true"></span> Smart Job Builder
            </button>
            <button type="button" id="btn-job-from-doc" class="btn-icon" onclick="window.DispatchModule.openJobFromDocument()" aria-label="Fill from a document" title="Fill from a work order, rate con or BOL">
              <span class="material-icons">note_add</span>
            </button>
            <!-- TASK-9151 — the X is guarded, but the guard is inside closeModal
                 itself: this is not the only door out of the form (duplicate-job,
                 the Settings force-close and add-day all close it too) and a
                 guard on one door is not a guard. -->
            <button class="btn-icon" onclick="window.DispatchModule.closeModal('job')">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>

        <!-- TASK-9598: "copied from J-####" banner. Outside the scroll so it
             stays in view. Filled by _renderJobCopySourceBanner on a Copy to New
             Job or a project prefill that ticked crew/equipment; hidden on every
             plain New Job and every edit. -->
        <div id="job-copy-source-banner" class="hidden" role="status" aria-live="polite" style="flex-shrink:0"></div>

        <!-- AI Prompt Section (hidden by default) -->
        <div class="ai-prompt-section hidden" id="ai-prompt-section" style="flex-shrink:0">
          <div class="ai-prompt-header">
            <span class="material-icons">verified_user</span>
            <span>Describe the job and I'll fill in the form, match crew &amp; equipment, and flag any compliance issues</span>
          </div>
          <textarea id="ai-prompt-input" rows="3" placeholder="e.g. Send Kyle in truck 101 from Williston yard to Odessa TX tomorrow 6am, hauling 90,000 lbs of equipment, PO 45678"></textarea>
          <div class="ai-prompt-disclaimer">
            <span class="material-icons">info</span>
            <span>Smart Tools assist with planning. <strong>You're responsible for verifying permits, real-time road conditions, and final compliance.</strong></span>
          </div>
          <div class="ai-prompt-actions">
            <button type="button" class="btn btn-secondary btn-small" onclick="window.DispatchModule.toggleAiPrompt()">Cancel</button>
            <button type="button" class="btn btn-primary btn-small" onclick="window.DispatchModule.parseWithAi()" id="btn-parse-ai">
              <span class="material-icons">verified_user</span> Dispatch
            </button>
          </div>
        </div>

        <form id="form-job" class="modal-form-scrollable" novalidate style="display:flex;flex-direction:column;flex:1;min-height:0;overflow:hidden" onsubmit="window.DispatchModule.saveJob(event)">
          <div class="modal-form-scroll" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <input type="hidden" id="job-id">
          <!-- TASK-9392: "From a customer request" banner (who asked + their
               verbatim words). Filled by _renderJobCustomerRequestBox; empty and
               hidden on every other open. -->
          <div id="job-customer-request-box" style="display:none;"></div>
          <!-- TASK-9587: "From Claude — not on the board yet" banner + the
               assumed values to confirm. Filled by _renderJobClaudeDraftBox. -->
          <div id="job-claude-draft-box" style="display:none;"></div>

          <!-- Customer Selection - Prominent at top -->
          <div class="form-section-highlight job-pick-card">
            <div class="form-group" style="margin-bottom:0;">
              <label for="job-customer" class="job-pick-label">
                <span class="material-icons" style="font-size:18px;">business</span>
                Customer *
              </label>
              <div class="input-with-action">
                <select id="job-customer" onchange="window.DispatchModule.onCustomerChange()" style="font-size:1rem;padding:12px;">
                  <option value="">Select customer...</option>
                </select>
                <button type="button" class="btn-add-inline" onclick="window.DispatchModule.showEditCustomer()" title="Edit customer">
                  <span class="material-icons">edit</span>
                </button>
                <button type="button" class="btn-add-inline" onclick="window.DispatchModule.showQuickAddCustomer()" title="Add new customer">
                  <span class="material-icons">add</span>
                </button>
              </div>
            </div>
          </div>

          <!-- TASK-9859 — "Start from 811 ticket" suggestions (filled by p811StartFromTicket). -->
          <button type="button" class="btn btn-secondary hidden" id="btn-start-from-811" style="min-height:44px;margin:0 0 var(--spacing-md);" onclick="window.DispatchModule.open811StartPicker()">
            <span class="material-icons" aria-hidden="true">add_task</span> Start from 811 ticket
          </button>
          <div id="job-811-start-banner" class="job-811-start hidden" role="status"></div>
          <div id="job-811-attach" class="p811-attach hidden" role="status"></div>
          <div class="form-section-highlight job-pick-card" id="job-project-section">
            <input type="checkbox" id="job-add-to-project" hidden tabindex="-1" aria-hidden="true">
            <input type="checkbox" id="job-create-new-project" hidden tabindex="-1" aria-hidden="true">
            <div class="form-group" style="margin-bottom:0;">
              <label for="job-project-id" class="job-pick-label">
                <span class="material-icons" style="font-size:18px;">folder_special</span>
                Project <span style="font-weight:400;font-size:0.8rem;color:var(--text-secondary);">optional</span>
              </label>
              <div class="input-with-action">
                <select id="job-project-id" style="font-size:1rem;padding:12px;" onchange="window.DispatchModule.onJobProjectChange(this)">
                  <option value="">No project</option>
                </select>
                <button type="button" class="btn-add-inline" id="btn-job-new-project" onclick="window.DispatchModule.setJobNewProjectMode(true)" title="New project" aria-label="Create a new project for this job" aria-controls="job-new-project-fields" aria-expanded="false">
                  <span class="material-icons">add</span>
                </button>
              </div>
              <div id="job-project-po-hint" style="display:none;margin-top:4px;font-size:0.75rem;color:var(--text-secondary);"></div>
              <div id="job-project-defaults-hint" style="display:none;margin-top:4px;font-size:0.75rem;color:var(--text-secondary);"></div><!-- TASK-4696 -->
              <div id="job-project-customer-note" role="status" style="display:none;margin-top:4px;font-size:0.8rem;color:#92400e;"></div>
              <div id="job-project-docs" class="hidden"></div><!-- TASK-9650 -->
            </div>
            <div id="job-new-project-fields" style="display:none;margin-top:10px;background:var(--bg-card);border-radius:8px;padding:12px;border:1px solid var(--border-light);">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:8px;">
                <strong id="job-new-project-heading" style="font-size:0.9rem;color:var(--text-primary);">New project</strong>
                <button type="button" class="btn btn-secondary btn-small" onclick="window.DispatchModule.setJobNewProjectMode(false)">Cancel</button>
              </div>
              <div class="form-group" style="margin-bottom:8px;">
                <label style="font-size:0.8rem;">Project Name *</label>
                <input type="text" id="job-new-project-name" placeholder="e.g. Chevron AST Site">
              </div>
              <div class="form-row" style="gap:8px;">
                <div class="form-group flex-1" style="margin-bottom:8px;">
                  <label style="font-size:0.8rem;">Start Date</label>
                  <input type="date" id="job-new-project-start">
                </div>
                <div class="form-group flex-1" style="margin-bottom:8px;">
                  <label style="font-size:0.8rem;">End Date</label>
                  <input type="date" id="job-new-project-end">
                </div>
              </div>
              <div class="form-group" style="margin-bottom:8px;">
                <label style="font-size:0.8rem;">Standing PO Number</label>
                <input type="text" id="job-new-project-po" placeholder="e.g. ND26UA2-U0">
                <small style="display:block;margin-top:3px;color:var(--text-secondary);font-size:0.72rem;">
                  Carries to every job on this project that has no PO of its own.
                </small>
              </div>
              <div class="form-group" style="margin-bottom:8px;">
                <label style="font-size:0.8rem;">Location</label>
                <input type="text" id="job-new-project-location" placeholder="Site address or well name">
              </div>
              <div class="form-group" style="margin-bottom:0;">
                <label style="font-size:0.8rem;">Description</label>
                <textarea id="job-new-project-description" rows="2" placeholder="Project scope, notes..."></textarea>
              </div>
            </div>
          </div>

          <!-- Wolverine 2026-10-21 — Site: the customer's saved sites (CRM → Sites).
               Picking one fills Location (in "Site, well & geofence" below). Hidden
               when the customer has none, and on the PIN driver form. -->
          <div class="form-group hidden" id="job-site-group">
            <label for="job-site-select" style="font-weight:600;display:flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;" aria-hidden="true">place</span>
              Site <span style="font-weight:400;font-size:0.8rem;color:var(--text-secondary);">fills Location</span>
            </label>
            <select id="job-site-select" style="font-size:1rem;padding:12px;width:100%;max-width:100%;" onchange="window.DispatchModule.onJobSiteChange(this)">
              <option value="">Pick a site…</option>
            </select>
            <div id="job-site-hint" role="status" style="display:none;margin-top:4px;font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;"></div>
          </div>

          <!-- POC Section (shown when customer selected) -->
          <div id="poc-section" class="hidden">
            <div class="form-group">
              <label for="job-poc-select">Point of Contact <span class="job-scope-chip" data-job-scope style="display:none;"></span></label>
              <select id="job-poc-select" onchange="window.DispatchModule.onPocChange()">
                <option value="">Select POC</option>
              </select>
              <span class="form-hint">Use the Customer module to add contacts</span>
            </div>
            <div id="poc-info-card" class="poc-info-card hidden">
              <!-- Filled dynamically when POC selected -->
            </div>
            <div id="poc-manual-fields" style="display:none;">
              <input type="hidden" id="job-poc-name">
              <input type="hidden" id="job-poc-role">
              <input type="hidden" id="job-poc-phone">
              <input type="hidden" id="job-poc-phone2">
              <input type="hidden" id="job-poc-email">
            </div>
          </div>

          <div class="form-row">
            <div class="form-group flex-1">
              <!-- TASK-4811 — data-job-scope spans are filled per-open by
                   _renderJobWideScopeMarkers on every field that lives on the JOB doc
                   rather than the jobDay. Hidden entirely on new and single-day jobs. -->
              <label for="job-title">Title / Job Name * <span class="job-scope-chip" data-job-scope style="display:none;"></span></label>
              <input type="text" id="job-title" required placeholder="Oil field service - Well #123" data-maxlen="100" onfocusout="window.DispatchModule.checkJobDuplicate();window.DispatchModule.maybeAutoSuggestPermitRequired()" oninput="window.DispatchModule.p811JobFormSiteChanged()">
              <div id="job-duplicate-warning" class="smart-warning smart-warning--stacked" style="display:none;">
                <div class="smart-warning-content">
                  <span class="material-icons smart-warning-icon" style="color:#F59E0B;">warning</span>
                  <div class="smart-warning-text">
                    <strong>Similar job exists</strong>
                    <span id="job-duplicate-detail"></span>
                  </div>
                </div>
                <div class="smart-warning-actions">
                  <button type="button" class="btn btn-small btn-secondary" onclick="window.DispatchModule.addDayInstead()" id="btn-add-day-instead">
                    <span class="material-icons">add_circle</span> Add Day &amp; Assign Crew
                  </button>
                  <!-- TASK-1581 S3 — shown by checkJobDuplicate only when the matched job is in an
                       active project this form could pick and the form has no project yet.
                       It only PICKS the project on the form (customer, PO, defaults fill the
                       same way a pick from the list does); nothing saves until Save Job. -->
                  <button type="button" class="btn btn-small btn-secondary" id="btn-dupe-add-to-project" style="display:none;" onclick="window.DispatchModule.addDupeMatchProject()"></button>
                  <button type="button" class="btn btn-small btn-secondary" id="btn-dupe-keep-separate" onclick="document.getElementById('job-duplicate-warning').style.display='none'">No, this is a different job</button>
                </div>
              </div>
            </div>
            <div class="form-group" id="job-type-group" style="min-width: 160px;">
              <label for="job-type">Type</label>
              <!-- TASK-9390: a tenant-managed list (settings.jobTypes). The id stays
                   job-type (dirty-field list, onJobTypeChange, project carry). Options
                   are filled by _renderJobTypeSelect on every open; an old job's
                   free-text type is offered as its own option so saving never
                   rewrites it. The pencil is shown only to dispatch-module holders. -->
              <div class="input-with-action">
                <select id="job-type" onchange="this.dataset.jdUserPicked='1';window.DispatchModule.onJobTypeChange()">
                  <option value="job">Job</option>
                </select>
                <button type="button" class="btn-add-inline" id="btn-manage-job-types" onclick="window.DispatchModule.showManageJobTypes()" title="Edit the job type list" aria-label="Edit the job type list" style="display:none;">
                  <span class="material-icons">edit</span>
                </button>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="job-start-date">Start Date *</label>
              <input type="date" id="job-start-date" required onchange="window.DispatchModule.onJobStartDateChange(this.value)">
              <!-- TASK-962 (S3): shown when the AI parse returned no date and the form kept its default start date (TASK-9407 setting: today, tomorrow or next business day) -->
              <div id="job-date-assumed-flag" style="display:none;margin-top:4px;padding:4px 8px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:6px;color:#92400E;font-size:0.8rem;align-items:center;gap:5px;">
                <span class="material-icons" style="font-size:16px;">warning_amber</span>
                <span>Date assumed — verify</span>
              </div>
              <!-- TASK-9112: the date is off the visible board. ITS OWN ELEMENT AND ITS OWN
                   STATE, deliberately. setDateAssumedFlag() hides #job-date-assumed-flag AND
                   resets the input's borderColor, and it is called from onJobStartDateChange —
                   the exact moment this hint should appear. Sharing either the element or the
                   border styling would show this hint and hide it in the same tick.
                   BLUE, not amber: an off-board date is usually deliberate (that IS how you
                   book next week). This is "here's where it will land", not "you got it wrong"
                   — and the colour difference keeps the two from reading as one duplicated
                   warning on the rare occasion both are up at once. -->
              <div id="job-date-offscreen-hint" style="display:none;margin-top:4px;padding:6px 8px;background:#EFF6FF;border:1px solid #93C5FD;border-radius:6px;color:#1E40AF;font-size:0.8rem;align-items:flex-start;gap:6px;line-height:1.35;">
                <span class="material-icons" style="font-size:16px;flex:0 0 auto;margin-top:1px;">event</span>
                <span id="job-date-offscreen-hint-text"></span>
              </div>
            </div>
            <div class="form-group" id="job-end-date-wrapper" style="display:none;">
              <label for="job-end-date">End Date *</label>
              <input type="date" id="job-end-date" required onchange="document.getElementById('job-start-date').max = this.value; window.DispatchModule.refreshJobDateOffscreenHint();">
            </div>
            <div class="form-group form-group-checkbox job-check-group">
              <label class="checkbox-label job-check">
                <input type="checkbox" id="job-all-day" checked onchange="window.DispatchModule.toggleAllDay()">
                <span>All Day</span>
              </label>
            </div>
          </div>

          <!-- TASK-273: Multi-day toggle — hidden by default for new jobs; reveals end-date field on check. -->
          <div class="form-row job-check-row">
            <div class="form-group form-group-checkbox job-check-group">
              <label class="checkbox-label job-check">
                <input type="checkbox" id="job-multi-day-toggle" onchange="window.DispatchModule.toggleMultiDay()">
                <span>This job continues to additional days</span>
              </label>
            </div>
          </div>

          <!-- TASK-9750 — NEW jobs only (hidden on edit: there, each day carries its
               own flags in Assign Crew). Stamped on every day this save creates. -->
          <div class="form-row job-check-row" id="job-day-flags-row">
            <div class="form-group form-group-checkbox job-check-group">
              <label class="checkbox-label job-check">
                <input type="checkbox" id="job-tentative">
                <span>Tentative: don't text the crew yet</span>
              </label>
            </div>
            <div class="form-group form-group-checkbox job-check-group">
              <label class="checkbox-label job-check">
                <input type="checkbox" id="job-half-day">
                <span>Half day</span>
              </label>
            </div>
          </div>

          <div class="form-row time-fields" id="time-fields" style="display: none;">
            <div class="form-group">
              <label for="job-start-time">Start Time</label>
              <input type="time" id="job-start-time" value="06:00">
            </div>
            <div class="form-group">
              <label for="job-end-time">End Time</label>
              <input type="time" id="job-end-time" value="18:00">
            </div>
          </div>

          <div class="form-row" id="job-po-row">
            <div class="form-group flex-1">
              <label for="job-po">PO Number <span class="job-scope-chip" data-job-scope style="display:none;"></span></label>
              <!-- TASK-4782 — the PO is a money field: it is how the customer's AP
                   department finds this invoice. Three deliberate properties here.
                   NOTE: no backticks in this comment. It sits inside a JS template
                   literal, where one backtick ends the string and takes the whole
                   modal with it ([[lesson_inline_html_script_outage_apply]]).
                   (1) the list attribute suggests POs already in use for THIS
                   customer, so a second spelling of one PO never gets typed — but a
                   datalist only SUGGESTS, so free text still types straight through.
                   (2) NO maxlength attribute. QuickBooks caps Invoice.PONumber at
                   15, and a customer whose PO is genuinely 20 characters must still
                   be able to record it. We warn under the box; we never cut it off.
                   (3) autocomplete="off" so the browser's own history does not
                   compete with the customer-scoped list. -->
              <input type="text" id="job-po" placeholder="PO-12345"
                     list="job-po-datalist" autocomplete="off"
                     oninput="window.DispatchModule.onJobPoInput()">
              <datalist id="job-po-datalist"></datalist>
              <div id="job-po-hint"></div>
              <div id="job-extra-pos"></div>
              <button type="button" class="btn-add-inline btn-add-inline-text" data-job-extra-po-add="1" onclick="window.DispatchModule.addJobExtraPo()"><span class="material-icons" aria-hidden="true">add</span> Add another PO</button>
            </div>
            <div class="form-group job-check-group job-po-visible-group">
              <label class="checkbox-label job-check">
                <input type="checkbox" id="job-po-visible">
                <span>Include PO in Driver's job view</span>
              </label>
            </div>
          </div>

          <!-- DOT Classification (compliance flagging) -->
          <!-- TASK-4688 — only the three CLASSIFICATION chips are hideable.
               #job-dot-flags-row is display:contents so hiding it removes exactly
               those three and leaves the flex row (and Permit Required) untouched;
               .hidden is display:none !important, which beats display:contents. -->
          <div class="job-flag-chips">
          <!-- TASK-9370 — driver-created job: the three DOT flags are UNKNOWN until a
               dispatcher confirms them. Shown by _renderJobDotReviewChip(). -->
          <div id="job-dot-review-chip" style="display:none;flex-basis:100%;align-items:center;gap:8px;flex-wrap:wrap;padding:8px 12px;border-radius:8px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.85rem;">
            <span class="material-icons" style="font-size:18px;">help</span>
            <span style="flex:1;min-width:180px;"><strong>Driver-created — confirm DOT flags.</strong> Interstate, Hazmat and Liquid Tank were never set. Tick what applies, or confirm none do.</span>
            <button type="button" class="btn btn-secondary btn-small" onclick="window.DispatchModule.confirmJobDotFlags()">None apply — confirm</button>
          </div>
          <div id="job-dot-flags-row" style="display:contents;">
            <label class="checkbox-label job-flag-chip">
              <input type="checkbox" id="job-interstate" onchange="window.DispatchModule.onJobDotFlagChange()">
              <span>Interstate</span>
              <span class="job-flag-chip-hint">· Crosses state lines</span>
            </label>
            <label class="checkbox-label job-flag-chip">
              <input type="checkbox" id="job-hazmat" onchange="window.DispatchModule.onJobDotFlagChange()">
              <span>Hazmat</span>
              <span class="job-flag-chip-hint">· Placarded materials</span>
            </label>
            <label class="checkbox-label job-flag-chip">
              <input type="checkbox" id="job-liquid-tank" onchange="window.DispatchModule.onJobDotFlagChange()">
              <span>Liquid Tank</span>
              <span class="job-flag-chip-hint">· &gt;1,000 gal</span>
            </label>
            </div>
            <!-- Permit Required sits OUTSIDE the gate on purpose: maybeAutoSuggestPermitRequired()
                 auto-ticks it from the job text and saveJob records the value, so hiding it
                 would file a permit decision with its confirming UI invisible. It already has
                 its own tenant gate (enablePermitTracking). -->
            ${isPermitTrackingEnabled() ? `
            <label class="checkbox-label job-flag-chip">
              <input type="checkbox" id="job-permit-required" onchange="window.DispatchModule.onPermitRequiredToggle()">
              <span>Permit Required</span>
              <span class="job-flag-chip-hint">· OS/OW road permit</span>
              <span id="job-permit-required-auto" class="hidden" title="Suggested from the job description — confirm or untick. Nothing is recorded until you save." style="background:#FEF3C7;color:#92400E;border:1px solid #FDE68A;font-size:0.7rem;font-weight:600;padding:1px 6px;border-radius:8px;">auto-suggested</span>
            </label>` : ''}
          </div>


          <div class="form-group">
            <label for="job-notes">Notes <span class="job-scope-chip" data-job-scope style="display:none;"></span></label>
            <div id="job-notes-scope-warning" style="display:none;"></div>
            <textarea id="job-notes" class="textarea-grow" rows="3" placeholder="Special instructions, equipment needed, etc." oninput="window.DispatchModule.syncJobNotesHeight()" onfocusout="window.DispatchModule.maybeAutoSuggestPermitRequired()"></textarea>
            <!-- TASK-9391 — read-only "Previous notes" + Use these (_renderJobPrevNotesBox) -->
            <div id="job-prev-notes-box" style="display:none;"></div>
          </div>

          <!-- Waypoints Section -->
          <div class="form-section" id="job-waypoints-section">
            <div class="form-section-header" style="display:flex;align-items:center;justify-content:space-between;">
              <h4>Destinations <span class="job-scope-chip" data-job-scope style="display:none;"></span></h4>
            </div>
            <div id="job-waypoints-list" class="waypoints-list">
              <!-- Rendered dynamically -->
            </div>
            <div id="waypoint-mileage-estimate" style="display:none;padding:6px 10px;margin-top:4px;background:rgba(16,185,129,0.06);border-radius:6px;font-size:0.8rem;color:var(--text-secondary);align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:16px;color:#10B981;">route</span>
              <span id="waypoint-mileage-text"></span>
            </div>
            <div class="waypoint-actions">
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.addWaypoint()">
                + Add Destination
              </button>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.showSavedLocationsPicker()" style="background:#EFF6FF;border-color:#BFDBFE;color:#1D4ED8;">
                <span class="material-icons" style="font-size:16px;">bookmark</span> Saved Locations
              </button>
              <button type="button" class="btn btn-danger-outline btn-sm" onclick="window.DispatchModule.clearAllWaypoints()">
                Clear All Destinations
              </button>
            </div>
          </div>

          <!-- TASK-4695 — styles for the collapsed groups, scoped to this modal and
               kept in the template rather than styles.css so the markup carries its
               own look (styles.css has a hand-synced buster). NO BACKTICKS in here. -->
          <style>
            #modal-job details.job-acc { border:1px solid var(--border-light); border-radius:var(--radius-md); margin-bottom:var(--spacing-lg); background:var(--bg-card); }
            #modal-job details.job-acc > summary { list-style:none; display:flex; align-items:center; gap:8px; min-height:44px; padding:8px 12px; cursor:pointer; border-radius:var(--radius-md); background:var(--bg-elevated); user-select:none; }
            #modal-job details.job-acc > summary::-webkit-details-marker { display:none; }
            #modal-job details.job-acc > summary:focus-visible { outline:3px solid var(--brand-blue); outline-offset:2px; }
            #modal-job details.job-acc[open] > summary { border-bottom:1px solid var(--border-light); border-radius:var(--radius-md) var(--radius-md) 0 0; }
            #modal-job .job-acc-icon { font-size:18px; color:var(--text-secondary); flex-shrink:0; }
            #modal-job .job-acc-title { font-weight:600; font-size:0.9375rem; color:var(--text-primary); white-space:nowrap; }
            #modal-job .job-acc-values { flex:1; min-width:0; text-align:right; font-size:0.8rem; color:var(--text-primary); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
            #modal-job .job-acc-values.is-empty { color:var(--text-secondary); }
            #modal-job .job-acc-arrow { margin-left:auto; color:var(--text-secondary); transition:transform 0.2s ease; flex-shrink:0; }
            #modal-job details.job-acc[open] .job-acc-arrow { transform:rotate(180deg); }
            #modal-job .job-acc-body { padding:12px; }
            #modal-job .job-acc-body > .form-section:last-child, #modal-job .job-acc-body > .form-group:last-child { margin-bottom:0; }
            @media (max-width: 480px) { #modal-job .job-acc-values.is-empty { display:none; } }
            /* TASK-9648 — Site section. Scoped to #modal-job; text on the light card is
               --text-primary / --text-secondary only, never --text-muted. */
            #modal-job .job-site-place { display:flex; align-items:flex-start; gap:8px; margin-top:8px; font-size:0.875rem; color:var(--text-primary); line-height:1.35; }
            #modal-job .job-site-place .material-icons { font-size:18px; color:var(--brand-blue); flex-shrink:0; margin-top:1px; }
            #modal-job .job-site-place-sub { display:block; font-size:0.8rem; color:var(--text-secondary); }
            #modal-job .job-site-actions { display:flex; flex-wrap:wrap; gap:8px; margin-top:8px; }
            #modal-job .job-site-btn { min-height:44px; display:inline-flex; align-items:center; justify-content:center; gap:6px; padding:8px 14px; font-size:0.875rem; font-weight:600; }
            #modal-job .job-site-btn .material-icons { font-size:18px; }
            #modal-job .job-811-list { display:flex; flex-direction:column; gap:8px; margin-bottom:8px; }
            #modal-job .job-811-list:empty { display:none; }
            #modal-job .job-811-row { display:flex; align-items:center; gap:10px; min-height:52px; padding:6px 6px 6px 12px; border:1px solid #CBD5E1; border-radius:var(--radius-md); background:var(--bg-card); }
            #modal-job .job-811-row .job-811-pdf { color:#B45309; font-size:22px; flex-shrink:0; }
            #modal-job .job-811-main { flex:1; min-width:0; }
            #modal-job .job-811-name { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; font-weight:600; color:var(--text-primary); overflow:hidden; overflow-wrap:anywhere; line-height:1.3; }
            #modal-job .job-811-meta { display:block; font-size:0.8rem; color:var(--text-secondary); }
            #modal-job .job-811-row .job-811-act { min-width:44px; min-height:44px; display:inline-flex; align-items:center; justify-content:center; gap:4px; padding:0 10px; border:1px solid #CBD5E1; border-radius:var(--radius-md); background:var(--bg-card); color:var(--text-primary); font-weight:600; font-size:0.85rem; text-decoration:none; cursor:pointer; }
            #modal-job .job-811-row .job-811-act .material-icons { font-size:18px; }
            #modal-job .job-gf { border:0; border-top:1px solid var(--border-light); border-radius:0; padding:12px 0 0; margin:4px 0 0; background:transparent; }
            #modal-job .job-gf-row { min-height:48px; gap:12px; margin:0; position:relative; }
            /* keep the switch keyboard-reachable: styles.css display:none's the box */
            #modal-job .job-gf-row.checkbox-switch input[type="checkbox"] { display:block !important; visibility:visible !important; position:absolute !important; left:0; top:0; opacity:0 !important; width:1px !important; height:1px !important; min-width:0 !important; min-height:0 !important; margin:0 !important; padding:0 !important; border:0 !important; -webkit-appearance:none !important; appearance:none !important; clip-path:inset(50%); overflow:hidden; pointer-events:none; }
            #modal-job .job-gf-row input:focus-visible + .switch-slider { outline:3px solid var(--brand-blue); outline-offset:2px; }
            #modal-job .job-gf-icon { font-size:22px; color:var(--text-secondary); flex-shrink:0; }
            #modal-job .job-gf-text { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
            #modal-job .job-gf-title { font-weight:600; font-size:0.9375rem; color:var(--text-primary); }
            #modal-job .job-gf-radius-chip { font-weight:600; font-size:0.8rem; color:var(--text-secondary); margin-left:6px; }
            #modal-job .job-gf-help { font-size:0.85rem; font-weight:400; color:var(--text-secondary); line-height:1.35; }
            #modal-job .job-gf-body { margin-top:10px; }
            #modal-job .job-gf-center { display:flex; align-items:flex-start; gap:8px; font-size:0.875rem; color:var(--text-primary); line-height:1.35; margin-bottom:10px; }
            #modal-job .job-gf-center .material-icons { font-size:18px; color:var(--brand-blue); flex-shrink:0; margin-top:1px; }
            #modal-job .job-gf-center.is-warn { color:#92400E; }
            #modal-job .job-gf-center.is-warn .material-icons { color:#B45309; }
            #modal-job .job-gf-fields { display:grid; grid-template-columns:minmax(120px, 180px) 1fr; gap:12px; }
            #modal-job .job-gf-fields .form-group { margin-bottom:0; }
            #modal-job .job-gf-fields select, #modal-job .job-gf-fields input { min-height:44px; }
            #modal-job .job-gf-address-row { display:flex; align-items:center; gap:6px; }
            #modal-job .job-gf-address-row input { flex:1; min-width:0; }
            #modal-job #job-geofence-geocode-status { font-size:18px; color:var(--text-secondary); }
            #modal-job .job-gf-readout { font-size:0.8rem; color:var(--text-secondary); margin-top:4px; }
            #modal-job .job-gf-readout:empty { display:none; }
            @media (max-width: 480px) { #modal-job .job-gf-fields { grid-template-columns:1fr; } #modal-job .job-site-actions .job-site-btn { flex:1 1 0; } #modal-job .job-811-act-label { display:none; } }
          </style>
          <details class="job-acc" id="job-acc-site" oninput="window.DispatchModule.refreshJobFormGroups()" onchange="window.DispatchModule.refreshJobFormGroups()">
            <summary>
              <span class="material-icons job-acc-icon" aria-hidden="true">place</span>
              <span class="job-acc-title">Site, well &amp; geofence</span>
              <span class="job-acc-values is-empty" data-acc-values data-acc-empty="${is811Enabled() ? 'Location, well, 811 ticket, geofence' : 'Location, well, geofence'}">${is811Enabled() ? 'Location, well, 811 ticket, geofence' : 'Location, well, geofence'}</span><!-- TASK-9994 -->
              <span class="material-icons job-acc-arrow" aria-hidden="true">expand_more</span>
            </summary>
            <div class="job-acc-body">
              <!-- TASK-9648 — Site section cleanup (Chris 10/8). Location gets a
                   readable place line + Use my location / Open in Maps; the 811
                   field is a list of linked tickets + an Add button (permitIds);
                   the geofence is one switch row with its radius and center in
                   plain view. Ids are unchanged so every reader keeps working. -->
              <div class="form-group">
                <label for="job-location">Location <span class="job-scope-chip" data-job-scope style="display:none;"></span></label>
                <input type="text" id="job-location" placeholder="Address, legal description, or GPS (lat, long)" data-maxlen="200" oninput="window.DispatchModule.refreshJobLocationPlace()" onchange="window.DispatchModule.refreshJobLocationPlace()">
                <div id="job-location-place" class="job-site-place hidden" aria-live="polite"></div>
                <div class="job-site-actions">
                  <button type="button" class="btn btn-secondary job-site-btn" id="btn-job-location-here" onclick="window.DispatchModule.useMyLocationForJob()">
                    <span class="material-icons" aria-hidden="true">my_location</span> Use my location
                  </button>
                  <button type="button" class="btn btn-secondary job-site-btn" id="btn-job-location-map" onclick="window.DispatchModule.openJobLocationInMaps()">
                    <span class="material-icons" aria-hidden="true">map</span> Open in Maps
                  </button>
                </div>
              </div>

              <div class="form-group">
                <label for="job-well-name">Well name / number</label>
                <input type="text" id="job-well-name" placeholder="Well name and number" data-maxlen="100" onfocusout="window.DispatchModule.checkJobDuplicate();window.DispatchModule.maybeAutoSuggestPermitRequired()">
              </div>

              <div class="form-group ${is811Enabled() ? '' : 'hidden'}" id="job-permit-group">
                <label id="job-permit-label">811 tickets</label>
                <!-- TASK-9648 — #job-permit is now a HIDDEN input holding the linked
                     ticket ids, comma-joined, in order (permitIds; permitId mirrors
                     the first). Kept under the old id so the dirty check, the
                     group summary and the copy carry read one source. -->
                <input type="hidden" id="job-permit" value="">
                <div id="job-permit-chips" class="job-811-list" aria-labelledby="job-permit-label"></div>
                <button type="button" class="btn btn-secondary job-site-btn" id="btn-add-811-ticket" onclick="window.DispatchModule.open811Picker()">
                  <span class="material-icons" aria-hidden="true">add</span> Add 811 ticket
                </button>
              </div>

              <!-- TASK-191 Phase 2 — per-job geofence. ADVISORY ONLY: flags a clock-in
                   or out beyond the radius, never blocks one. -->
              <div class="form-section job-gf" id="job-geofence-section">
                <label class="checkbox-switch job-gf-row" for="job-geofence-enabled">
                  <span class="material-icons job-gf-icon" aria-hidden="true">my_location</span>
                  <span class="job-gf-text">
                    <span class="job-gf-title">Geofence <span class="job-gf-radius-chip" id="job-geofence-radius-chip"></span></span>
                    <span class="job-gf-help">Flags clock-ins outside this radius. Never blocks a driver.</span>
                  </span>
                  <input type="checkbox" id="job-geofence-enabled" onchange="window.DispatchModule.toggleGeofenceSection()">
                  <span class="switch-slider" aria-hidden="true"></span>
                </label>
                <div id="job-geofence-body" class="hidden job-gf-body">
                  <div class="job-gf-center" id="job-geofence-center-line"></div>
                  <div class="job-gf-fields">
                    <div class="form-group job-gf-radius">
                      <label for="job-geofence-radius">Radius</label>
                      <select id="job-geofence-radius" onchange="window.DispatchModule.refreshGeofenceCenterLine()">
                        <option value="250">250 ft</option>
                        <option value="500" selected>500 ft</option>
                        <option value="1000">1,000 ft</option>
                        <option value="2500">2,500 ft</option>
                        <option value="5280">1 mile</option>
                      </select>
                    </div>
                    <div class="form-group job-gf-address">
                      <label for="job-geofence-address">Different center (optional)</label>
                      <div class="job-gf-address-row">
                        <input type="text" id="job-geofence-address" placeholder="Address or place" onblur="window.DispatchModule.geocodeJobGeofence()">
                        <span class="material-icons" id="job-geofence-geocode-status" aria-hidden="true"></span>
                      </div>
                      <div id="job-geofence-coords-readout" class="job-gf-readout" aria-live="polite"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <details class="job-acc" id="job-acc-billing" oninput="window.DispatchModule.refreshJobFormGroups()" onchange="window.DispatchModule.refreshJobFormGroups()">
            <summary>
              <span class="material-icons job-acc-icon" aria-hidden="true">receipt_long</span>
              <span class="job-acc-title">Billing codes &amp; ticketing</span>
              <span class="job-acc-values is-empty" data-acc-values data-acc-empty="Vendor #, AFE, ticket split">Vendor #, AFE, ticket split</span>
              <span class="material-icons job-acc-arrow" aria-hidden="true">expand_more</span>
            </summary>
            <div class="job-acc-body">
              <div class="form-row" id="job-afe-vendor-row">
                <div class="form-group flex-1">
                  <label for="job-vendor-number">Vendor Number</label>
                  <input type="text" id="job-vendor-number" placeholder="Vendor #">
                </div>
                <div class="form-group flex-1">
                  <label for="job-afe">AFE / Cost Center</label>
                  <input type="text" id="job-afe" placeholder="AFE-001">
                </div>
              </div>

              <!-- TASK-1221: Ticketing mode (per-job splitBy). Phase 1 = combined | vehicle. -->
              <div class="form-section" id="job-ticketing-section">
                <label style="display:block;font-weight:600;font-size:0.9rem;color:var(--text-primary);margin-bottom:6px;">Ticketing</label>
                <input type="hidden" id="job-split-by" value="combined">
                <div class="ib-mode-toggle" id="job-split-toggle" style="margin-bottom:8px;">
                  <button type="button" class="ib-mode-btn active" data-split="combined" onclick="window.DispatchModule.setJobSplitBy('combined')">One combined ticket</button>
                  <button type="button" class="ib-mode-btn" data-split="vehicle" onclick="window.DispatchModule.setJobSplitBy('vehicle')">Split by vehicle</button>
                </div>
                <p class="form-hint" id="job-split-hint">All crew and equipment on this job roll onto one ticket per day.</p>
                <div id="job-split-lock" class="smart-warning smart-warning--stacked" style="display:none;">
                  <div class="smart-warning-content">
                    <span class="material-icons smart-warning-icon" style="color:#F59E0B;">lock</span>
                    <div class="smart-warning-text">
                      <strong>Ticketing is locked for today</strong>
                      <span>Tickets already exist for today. Use Combine/Split in Billing to reorganize.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </details>

          <!-- Attachments & Permits Section -->
          <div class="form-section" id="job-attachments-section">
            <div class="form-section-header">
              <h4>Attachments & Load Permits</h4>
            </div>

            <!-- Attachments (Photos) toggle -->
            <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:8px;">
              <input type="checkbox" id="job-attachments-toggle" onchange="window.DispatchModule.toggleAttachmentsSection()">
              <label for="job-attachments-toggle" style="cursor:pointer;margin:0;font-weight:600;">
                <span class="material-icons" style="font-size:18px;vertical-align:middle;">photo_camera</span>
                Add Photos
              </label>
            </div>
            <div id="job-attachments-body" class="hidden">
              <div class="photo-upload-section">
                <div id="job-photo-grid" class="photo-grid"></div>
                <button type="button" class="btn btn-secondary btn-sm photo-upload-btn" onclick="document.getElementById('job-photo-input').click()">
                  <span class="material-icons">add_a_photo</span> Add Photo
                </button>
                <!-- TASK-9546 - .pdf is accepted HERE too. A PDF picked under Add Photo
                     is filed into Load Permit Files below (same path and cap as the
                     permit picker), never through the JPEG + thumbnail pipeline. Mike
                     could not get a permit onto a job because this picker would not
                     even let him select it. No backticks in this comment: it sits
                     inside a JS template literal. -->
                <input type="file" id="job-photo-input" accept="image/*,.heic,.heif,.pdf" style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none"
                       onchange="window.DispatchModule.handleJobPhoto(event)">
                <span class="photo-count" id="job-photo-count">0/${STARTER_LIMITS.jobs.maxPhotos} photos</span>
              </div>
              <div class="form-group" style="margin-top: 8px;">
                <label class="checkbox-label">
                  <input type="checkbox" id="job-photos-visible" onchange="window.DispatchModule.onJobPhotosVisibleChange()">
                  Include photos in Driver's job view
                </label>
                <p class="form-hint" style="margin-left: 24px;">Useful for site maps or instructions drivers need on-site</p>
                <p class="form-hint" id="job-photos-hidden-hint" style="display:none;margin-left:24px;color:var(--warning);font-weight:600;">Drivers can't see this photo — tick Include photos in Driver's job view.</p>
              </div>
            </div>

            <!-- Permits header. TASK-9546 - this used to be an "Attach Load Permit
                 Files" checkbox that hid the whole permit section until ticked, which
                 is how a permit ended up in the photo picker. The section is always
                 open now. The id stays on the header and data-trailguide wires the
                 trailguide.js Permits tip to it (that tip had no click mapping at all
                 before, so it never fired). Nothing reads it as a checkbox. -->
            <div id="job-permits-toggle" data-trailguide="job-permits-toggle" style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:8px;margin-top:12px;">
              <span style="margin:0;font-weight:600;">
                <span class="material-icons" style="font-size:18px;vertical-align:middle;color:var(--accent-teal);">description</span>
                Load Permit Files
              </span>
            </div>
            <div id="job-permits-body">
              <div class="photo-upload-section">
                <div id="job-permit-grid" class="photo-grid"></div>
                <div id="job-permit-provenance" class="hidden" style="display:flex;flex-direction:column;gap:2px;margin:4px 0 6px;"></div>
                <button type="button" class="btn btn-secondary btn-sm photo-upload-btn"
                        onclick="document.getElementById('job-permit-input').click()">
                  <span class="material-icons">upload_file</span> Add File / Photo
                </button>
                <!-- TASK-9151 - multi-select. A dispatcher standing at a truck with a
                     folder of filed permits had to re-open the phone file picker
                     once per file. handlePermitUpload now walks event.target.files.
                     No backticks in this comment: it sits inside a JS template
                     literal and one backtick would end the string. -->
                <input type="file" id="job-permit-input" accept="image/*,.heic,.heif,.pdf" multiple
                       style="position:absolute;width:1px;height:1px;opacity:0;pointer-events:none"
                       onchange="window.DispatchModule.handlePermitUpload(event)">
                <span class="photo-count" id="job-permit-count">0/${STARTER_LIMITS.jobs.maxPermits} files</span>
              </div>

              ${window.hasAiPack?.() ? `
              <div class="permit-billing-options" style="margin-top:10px;padding:10px;background:var(--bg-elevated);border-radius:8px;border:1px solid var(--border-light);">
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:6px;">
                  <input type="checkbox" id="permit-ai-parse" checked>
                  <label for="permit-ai-parse" style="cursor:pointer;margin:0;">
                    <span class="material-icons" style="font-size:16px;vertical-align:middle;color:var(--brand-blue);">auto_awesome</span>
                    Auto-parse for billing
                  </label>
                </div>
                <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                  <input type="checkbox" id="permit-fasttrax">
                  <label for="permit-fasttrax" style="cursor:pointer;margin:0;">
                    <span class="material-icons" style="font-size:16px;vertical-align:middle;color:var(--status-warning);">bolt</span>
                    Used FastTrax
                  </label>
                </div>
              </div>
              ` : ''}
            </div>
            <!-- TASK-9740 - signed documents the crew filed from the phone (Bill of
                 Lading / Disposal Ticket). Read-only: the server writes job.documents[]
                 when it paints the PDF; the office opens them here. Filled by
                 renderJobSignedDocs(); hidden on a new job. No backticks in this
                 comment: it sits inside a JS template literal. -->
            <div id="job-signed-docs" class="hidden" style="margin-top:12px;"></div>
          </div>

          <!-- Crew & Equipment Assignment Section -->
          <div class="form-section" id="job-crew-section">
            <div class="form-section-header">
              <h4>Crew & Equipment</h4>
              <!-- TASK-4614 — rewritten per-open by _renderJobCrewScopeHint: on an
                   EDIT this section writes to the opened day only, not all days. -->
              <span class="form-hint" id="job-crew-scope-hint">Assigned to all days of this job</span>
            </div>

            <!-- TASK-9258 — Job Templates. Filled by renderJobTemplateBar(); stays
                 EMPTY (and the whole feature invisible) until a jobTemplates
                 snapshot actually lands, so a tenant whose rules don't grant the
                 collection sees no half-working picker. -->
            <div id="job-template-bar"></div>
            <div id="job-template-notice"></div>

            <div id="compliance-warnings-job"></div>

            <div id="job-contract-area"></div>
            <!-- TASK-9446: "a Price Book is in Draft" hint. Its own element so renderContractPicker (which empties job-contract-area) never wipes it. -->
            <div id="job-pricebook-hint"></div>

            <div class="collapsible-section" id="job-section-drivers">
              <div class="collapsible-header" role="button" tabindex="0" onclick="this.parentElement.classList.toggle('open')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">People</span>
                <span class="collapsible-count" id="job-count-drivers">0</span>
              </div>
              <div class="collapsible-body" id="job-select-drivers">
                <!-- Filled dynamically -->
              </div>
            </div>

            <div class="collapsible-section hidden" id="job-section-helpers">
              <div class="collapsible-header" role="button" tabindex="0" onclick="this.parentElement.classList.toggle('open')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">Helpers</span>
                <span class="collapsible-count" id="job-count-helpers">0</span>
              </div>
              <div class="collapsible-body" id="job-select-helpers">
                <!-- Filled dynamically -->
              </div>
            </div>

            <div class="collapsible-section" id="job-section-trucks">
              <div class="collapsible-header" role="button" tabindex="0" onclick="this.parentElement.classList.toggle('open')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">Vehicles</span>
                <span class="collapsible-count" id="job-count-trucks">0</span>
              </div>
              <div class="collapsible-body" id="job-select-trucks">
                <!-- Filled dynamically -->
              </div>
            </div>

            <div class="collapsible-section" id="job-section-trailers">
              <div class="collapsible-header" role="button" tabindex="0" onclick="this.parentElement.classList.toggle('open')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">Trailers</span>
                <span class="collapsible-count" id="job-count-trailers">0</span>
              </div>
              <div class="collapsible-body" id="job-select-trailers">
                <!-- Filled dynamically -->
              </div>
            </div>

            <div class="collapsible-section" id="job-section-assets">
              <div class="collapsible-header" role="button" tabindex="0" onclick="this.parentElement.classList.toggle('open')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}">
                <span class="material-icons collapsible-arrow">expand_more</span>
                <span class="collapsible-title">Assets</span>
                <span class="collapsible-count" id="job-count-assets">0</span>
              </div>
              <div class="collapsible-body" id="job-select-assets">
                <!-- Filled dynamically -->
              </div>
            </div>

            <!-- Live Load Calculator (updates as user selects assets/trucks) -->
            <div id="job-load-calculator-live" style="display:none;margin-top:12px;padding:12px;background:linear-gradient(135deg, rgba(59,130,246,0.05), rgba(6,182,212,0.05));border:1px solid var(--border-light);border-radius:var(--radius-md);"></div>

          </div>
          </div><!-- end modal-form-scroll -->

          <!-- TASK-9151 — permit upload progress. It is a sibling of the scroll pane,
               pinned directly ABOVE the footer, because on a phone the permits grid is
               scrolled far off-screen by the time Save is pressed — a progress bar down
               in the permits section would be invisible for the whole upload, which is
               indistinguishable from the frozen page this task exists to fix.
               Inline styles on purpose: styles.css belongs to another lane right now. -->
          <div id="job-permit-progress" class="hidden" role="status" aria-live="polite"
               style="flex-shrink:0;padding:10px 24px 0;">
            <div style="padding:10px 12px;border-radius:8px;background:var(--bg-elevated);border:1px solid var(--border-light);">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:7px;">
                <span class="material-icons" id="job-permit-progress-icon" style="font-size:18px;color:var(--brand-blue);flex-shrink:0;">cloud_upload</span>
                <span id="job-permit-progress-label" style="font-size:0.86rem;font-weight:600;color:var(--text-primary);line-height:1.3;">Uploading permits…</span>
              </div>
              <div id="job-permit-progress-track" style="height:8px;border-radius:4px;background:var(--border-light);overflow:hidden;">
                <div id="job-permit-progress-bar" style="height:100%;width:0%;border-radius:4px;background:var(--brand-blue);transition:width .2s ease;"></div>
              </div>
              <div id="job-permit-progress-sub" style="margin-top:6px;font-size:0.76rem;color:var(--text-secondary);line-height:1.3;"></div>
              <div id="job-permit-progress-actions" class="hidden" style="margin-top:9px;display:flex;gap:8px;flex-wrap:wrap;">
                <button type="button" class="btn btn-primary btn-sm" style="padding:7px 14px;"
                        onclick="window.DispatchModule.retryFailedPermitUploads()">
                  <span class="material-icons" style="font-size:16px;">refresh</span> Retry upload
                </button>
                <button type="button" class="btn btn-secondary btn-sm" style="padding:7px 14px;"
                        onclick="window.DispatchModule.discardFailedPermitUploads()">
                  Discard them
                </button>
              </div>
            </div>
          </div>

          <div class="modal-form-footer modal-footer-job" style="flex-shrink:0;">
            <div class="modal-footer-edit-actions hidden" id="job-edit-actions" style="flex-wrap:wrap;">
              <!-- TASK-4812 — this was btn-primary, the SAME blue gradient as the
                   Save Job submit two buttons away, and measured WIDER than it
                   (166.6px vs 142.7px at 1440). The dispatcher opened Edit Job to
                   change something and save it: Save Job is the one primary this
                   footer gets. Adding a day stays exactly where it is, one class
                   quieter — it is a frequent action, not a hidden one. -->
              <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.addDayToCurrentJob()">
                <span class="material-icons">add_circle</span> Add Another Day
              </button>
              <!-- TASK-9943 — taking a day back off lives next to adding one. Office only (editJob hides it for a PIN driver and view-and-upload members). -->
              <button type="button" class="btn btn-secondary" id="btn-job-remove-day" onclick="window.DispatchModule.openEditJobRemoveDay(this)">
                <span class="material-icons">event_busy</span> Remove Day
              </button>
              <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.duplicateCurrentJob()">
                <span class="material-icons">content_copy</span> Copy to New Job
              </button>
              <button type="button" class="btn btn-danger" id="btn-job-delete" onclick="window.DispatchModule.deleteCurrentJob(this)">
                <span class="material-icons">delete</span> Delete
              </button>
            </div>
            <button type="submit" class="btn btn-primary" id="btn-save-job"><span class="material-icons">save</span> Save Job</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Job Day Assignment Modal -->
    <div class="modal hidden" id="modal-assignment">
      <div class="modal-content modal-medium" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3 id="modal-assignment-title">Assign Resources</h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.cancelAssignment()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-assignment" class="modal-form-scrollable" novalidate style="display:flex;flex-direction:column;flex:1;min-height:0;overflow:hidden;padding:0" onsubmit="window.DispatchModule.saveAssignment(event)">
          <input type="hidden" id="assignment-job-day-id">
          <input type="hidden" id="assignment-job-id">

          <div class="modal-form-scroll" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">

          <div class="assignment-info" id="assignment-info">
            <!-- Filled dynamically: date, schedule -->
          </div>

          <div id="compliance-warnings-assignment"></div>

          <!-- TASK-9750 — per-day flags. Tentative = on the board, nobody texted
               until it is confirmed (unticking it here confirms the day). Half day
               is a label only; it does not change billing. -->
          <div id="assignment-day-flags" style="display:flex;flex-wrap:wrap;gap:4px 16px;margin:8px 0 4px;">
            <label class="checkbox-label" style="min-height:44px;display:flex;align-items:center;cursor:pointer;">
              <input type="checkbox" id="assignment-tentative" style="width:18px;height:18px;margin-right:8px;">
              <span>Tentative: don't text the crew yet</span>
            </label>
            <label class="checkbox-label" style="min-height:44px;display:flex;align-items:center;cursor:pointer;">
              <input type="checkbox" id="assignment-half-day" style="width:18px;height:18px;margin-right:8px;">
              <span>Half day</span>
            </label>
          </div>

          <div id="assignment-contract-area"></div>

          <!-- TASK-1673 — the LINE pick, at the same prominence as the Price Book
               (contract) pick above it. It used to live only as a small <select>
               inside the collapsed Vehicles section, which is why adoption was
               0.9% while 38% of jobDays carried a contract pick. This block is
               never collapsed and lists every assigned truck/trailer/asset with
               the rate that will actually be billed. -->
          <div id="assignment-rate-area"></div>

          <div class="collapsible-section" id="section-drivers">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Drivers</span>
              <span class="collapsible-count" id="count-drivers">0</span>
            </div>
            <div class="collapsible-body" id="select-drivers">
              <!-- Filled dynamically -->
            </div>
          </div>

          <div class="collapsible-section" id="section-helpers">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Helpers</span>
              <span class="collapsible-count" id="count-helpers">0</span>
            </div>
            <div class="collapsible-body" id="select-helpers">
              <!-- Filled dynamically -->
            </div>
          </div>

          <div class="collapsible-section" id="section-trucks">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Vehicles</span>
              <span class="collapsible-count" id="count-trucks">0</span>
            </div>
            <div class="collapsible-body" id="select-trucks">
              <!-- Filled dynamically -->
            </div>
          </div>

          <div class="collapsible-section" id="section-trailers">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Trailers</span>
              <span class="collapsible-count" id="count-trailers">0</span>
            </div>
            <div class="collapsible-body" id="select-trailers">
              <!-- Filled dynamically -->
            </div>
          </div>

          <div class="collapsible-section" id="section-assets">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Assets</span>
              <span class="collapsible-count" id="count-assets">0</span>
            </div>
            <div class="collapsible-body" id="select-assets">
              <!-- Filled dynamically -->
            </div>
          </div>

          <!-- TASK-9763 — "Who's in what": per-unit operator picks for THIS day.
               Rendered only for 2+ people or 2+ trucks (renderAssignmentPairing). -->
          <div id="assignment-pairing-area"></div>

          <div class="collapsible-section" id="section-subcontractors">
            <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">Subcontractors</span>
              <span class="collapsible-count" id="count-subcontractors">0</span>
            </div>
            <div class="collapsible-body" id="select-subcontractors">
              <!-- Filled dynamically -->
            </div>
          </div>

          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer" style="flex-shrink:0">
            <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.cancelAssignment()">Cancel</button>
            <button type="submit" class="btn btn-primary">Save Assignment</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Job Details Modal -->
    <div class="modal hidden" id="modal-job-details">
      <div class="modal-content modal-large" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <div style="min-width:0;flex:1;">
            <h3 id="modal-job-details-title">Job Details</h3>
            <!-- TASK-10009 — the job type, on the line under the title (empty for the default type). -->
            <div id="modal-job-details-type" class="job-details-type" style="display:none;font-size:0.9rem;font-weight:600;color:var(--text-secondary);margin-top:2px;"></div>
          </div>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('job-details')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div id="job-details-content" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <!-- Filled dynamically -->
        </div>
        <div id="job-details-footer" style="flex-shrink:0">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Quick Add Customer Modal -->
    <div class="modal hidden" id="modal-quick-customer">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Add Customer</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('quick-customer')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-quick-customer" novalidate onsubmit="window.DispatchModule.saveQuickCustomer(event)" style="display:flex;flex-direction:column;min-height:0;flex:1;">
          <div style="flex:1;min-height:0;overflow-y:auto;padding:var(--spacing-md);">
          <div class="form-group">
            <label for="quick-customer-name">Customer Name *</label>
            <input type="text" id="quick-customer-name" required placeholder="ABC Oil & Gas" data-maxlen="100">
          </div>

          <div class="form-group">
            <label for="quick-customer-phone">Phone</label>
            <input type="tel" id="quick-customer-phone" placeholder="303-555-1234">
          </div>

          <div class="form-group">
            <label for="quick-customer-email">Email</label>
            <input type="email" id="quick-customer-email" placeholder="contact@company.com">
          </div>

          <div class="form-section">
            <button type="button" class="collapsible-toggle" onclick="this.classList.toggle('open'); this.nextElementSibling.classList.toggle('hidden')">
              <span class="material-icons">expand_more</span> Add Contact (Optional)
            </button>
            <div class="collapsible-content hidden">
            <div class="form-row">
              <div class="form-group flex-1">
                <label for="quick-poc-name">Name</label>
                <input type="text" id="quick-poc-name" placeholder="John Smith" data-maxlen="100">
              </div>
              <div class="form-group flex-1">
                <label for="quick-poc-role">Role</label>
                <input type="text" id="quick-poc-role" placeholder="Site Supervisor">
              </div>
            </div>
            <div class="form-row">
              <div class="form-group flex-1">
                <label for="quick-poc-phone">Phone</label>
                <input type="tel" id="quick-poc-phone" placeholder="303-555-5678">
              </div>
              <div class="form-group flex-1">
                <label for="quick-poc-phone2">Phone 2</label>
                <input type="tel" id="quick-poc-phone2" placeholder="303-555-9999">
              </div>
            </div>
            <div class="form-group">
              <label for="quick-poc-email">Email</label>
              <input type="email" id="quick-poc-email" placeholder="john@company.com">
            </div>
            </div>
          </div>
          </div>

          <div class="modal-footer" style="flex-shrink:0;">
            <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeModal('quick-customer')">Cancel</button>
            <button type="submit" class="btn btn-primary">Add Customer</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Day Details Modal -->
    <div class="modal hidden" id="modal-day-details">
      <div class="modal-content modal-medium">
        <div class="modal-header">
          <h3 id="modal-day-details-title">Day Details</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('day-details')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="day-details-content">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Lanes: one day's jobs, or one person's / vehicle's week -->
    <div class="modal hidden" id="modal-lane-jobs">
      <div class="modal-content modal-medium" role="dialog" aria-modal="true" aria-labelledby="modal-lane-jobs-title">
        <div class="modal-header">
          <h3 id="modal-lane-jobs-title">Jobs</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('lane-jobs')" aria-label="Close">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="lane-jobs-content">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Job Quickview Modal -->
    <div class="modal hidden" id="modal-quickview">
      <div class="modal-content modal-quickview" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="quickview-header" id="quickview-header" style="flex-shrink:0">
          <!-- Filled dynamically -->
        </div>
        <div class="quickview-body" id="quickview-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <!-- Filled dynamically -->
        </div>
        <div class="quickview-footer" id="quickview-footer" style="flex-shrink:0">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Mass Text Modal -->
    <div class="modal hidden" id="modal-mass-text">
      <div class="modal-content modal-medium modal-fullscreen">
        <div class="modal-header">
          <h3>Notify Crew</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('mass-text')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <!-- TASK-10027 muted-tenant UI hints (app.js paintTenantSendNotices) -->
          <div data-tenant-send-notice="crewText" style="display:none;"></div>
          <div class="mass-text-toggle">
            <button id="mass-text-today" class="mass-text-btn active" data-day="today" onclick="window.DispatchModule.setMassTextDay('today')">Today</button>
            <button id="mass-text-tomorrow" class="mass-text-btn" data-day="tomorrow" onclick="window.DispatchModule.setMassTextDay('tomorrow')">Tomorrow</button>
            <button id="mass-text-3days" class="mass-text-btn" data-day="3days" onclick="window.DispatchModule.setMassTextDay('3days')">3 Days</button>
            <button id="mass-text-week" class="mass-text-btn" data-day="week" onclick="window.DispatchModule.setMassTextDay('week')">Week</button>
          </div>
          <div class="mass-text-subheader">
            <span class="mass-text-date" id="mass-text-date"></span>
            <span class="mass-text-job-count" id="mass-text-job-count"></span>
          </div>
          <textarea id="mass-text-content" class="mass-text-textarea" placeholder="Tap here to edit message..." oninput="window.DispatchModule.onMassTextInput(true)"></textarea>
          <div class="mass-text-footer">
            <span id="mass-text-char-count">0 chars</span>
            <span id="mass-text-recipient-count" class="recipient-count"></span>
          </div>
          <!-- TASK-1533 — WHO gets this text, not just how many. A number is
               not reviewable: "13" read as normal on 2026-08-12 because 13 IS
               the crew size, while 12 of them were supposed to be muted. Names
               + the exclusion tally make a wrong send visible BEFORE it leaves.
               Re-rendered live by updateRecipientCount (people onSnapshot). -->
          <div id="mass-text-recipient-panel" style="margin:4px 0 12px;"></div>
          <!-- Full Notes Toggle -->
          <div style="margin-bottom:12px;">
            <label class="checkbox-switch" style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-elevated);border-radius:var(--radius-md);">
              <input type="checkbox" id="mass-text-full-notes" onchange="window.DispatchModule.toggleFullNotes('mass')">
              <span class="switch-slider"></span>
              <span class="switch-label" style="flex:1;">
                <strong>Send Full Notes</strong>
                <span style="display:block;font-size:0.8em;color:var(--text-secondary);">Include complete job notes instead of a short preview</span>
              </span>
            </label>
          </div>
          <!-- TASK-9597 — what this text contains is a tenant setting -->
          <div style="margin:-4px 0 12px;font-size:0.85rem;">
            <a href="#" onclick="event.preventDefault();window.DispatchModule.editCrewTextLayout()" style="color:var(--brand-blue);">Edit default layout</a>
            <span style="color:var(--text-secondary);"> — choose which fields this text contains</span>
          </div>
          <!-- TASK-9842 — a reply is the only proof someone SAW the text. Off by
               default; remembered per device. Appended at send time, so the
               editable preview above stays the dispatcher's own words. -->
          <div style="margin-bottom:12px;">
            <label class="checkbox-switch" style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-elevated);border-radius:var(--radius-md);">
              <input type="checkbox" id="mass-text-confirm-ask" onchange="window.DispatchModule.toggleMassTextConfirmAsk()">
              <span class="switch-slider"></span>
              <span class="switch-label" style="flex:1;">
                <strong>Ask for a reply</strong>
                <span style="display:block;font-size:0.8em;color:var(--text-secondary);">Adds "Reply 1 to confirm you got this." to the end. Replies show under Recent crew texts.</span>
              </span>
            </label>
          </div>
          <!-- Personalized Mode Toggle -->
          <div class="mass-text-mode-toggle" style="margin-bottom:12px;">
            <label class="checkbox-switch" style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-elevated);border-radius:var(--radius-md);">
              <input type="checkbox" id="mass-text-personalized" onchange="window.DispatchModule.togglePersonalizedMode()">
              <span class="switch-slider"></span>
              <span class="switch-label" style="flex:1;">
                <strong>Personalized Messages</strong>
                <span style="display:block;font-size:0.8em;color:var(--text-secondary);">Each driver sees only their own jobs (no URL in message)</span>
              </span>
            </label>
          </div>

          <!-- Personalized Mode Info (shown when personalized) -->
          <div id="mass-text-personalized-info" style="display:none;padding:12px;background:linear-gradient(135deg,#f0f9ff,#e0f2fe);border:1px solid #bae6fd;border-radius:var(--radius-md);margin-bottom:12px;">
            <div style="display:flex;align-items:flex-start;gap:8px;">
              <span class="material-icons" style="font-size:18px;color:#0284c7;">info</span>
              <div style="font-size:0.85rem;">
                <strong style="color:#0369a1;">How it works:</strong>
                <ul style="margin:4px 0 0 0;padding-left:16px;color:#475569;">
                  <li>Each driver gets a personalized message with only THEIR jobs</li>
                  <li>Drivers log in with their PIN</li>
                  <li>No URLs in message = better delivery rates</li>
                </ul>
                <div id="personalized-sms-warning" style="margin-top:8px;padding:6px 8px;background:rgba(234,179,8,0.15);border-radius:4px;color:#92400e;font-size:0.8rem;display:none;">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">sms</span>
                  <strong>Requires SMS Pack</strong> — can't copy/paste individual messages for each person
                </div>
              </div>
            </div>
          </div>
          <!-- SMS Upsell Banner (shown when no SMS pack) -->
          <div id="mass-text-sms-upsell" class="sms-upsell-banner" style="display:none;">
            <div class="sms-upsell-content">
              <span class="material-icons sms-upsell-icon">sms</span>
              <div class="sms-upsell-text">
                <strong>Send texts directly from DispatchDeck</strong>
                <span>Get a dedicated phone number + 1,500 messages for $25</span>
              </div>
            </div>
            <button class="btn btn-primary sms-upsell-btn" onclick="window.showSmsUpgradeModal?.('mass-text')">
              <span class="material-icons">sms</span> Get SMS Pack
            </button>
          </div>
          <div id="mass-text-sms-meter" style="margin-bottom:12px;"></div>
          <!-- Read-only Driver Link share (H3) — view-only job board, PIN still
               required to clock in / build tickets / log expenses. -->
          <div class="driver-link-share" style="margin:4px 0 12px 0;padding:12px;background:var(--bg-elevated);border-radius:var(--radius-md);">
            <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;">
              <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">visibility</span>
              <div style="flex:1;">
                <strong style="font-size:0.9rem;color:var(--text-primary);">Share read-only schedule link</strong>
                <span style="display:block;font-size:0.8rem;color:var(--text-secondary);">Drivers view their job board without a login. To clock in or build a ticket they log in with their PIN.</span>
              </div>
            </div>
            <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <label for="driver-link-expiry" style="font-size:0.85rem;color:var(--text-secondary);">Expires in</label>
              <select id="driver-link-expiry" class="form-select" style="width:auto;min-width:96px;">
                <option value="7" selected>7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
              </select>
              <button class="btn btn-secondary" style="flex:1;min-width:140px;" onclick="window.DispatchModule.copyDriverLink()">
                <span class="material-icons">link</span> Copy Driver Link
              </button>
            </div>
          </div>
          <!-- TASK-10112 — live send status (steps + elapsed + "Sending 4 of 15")
               and the result ("Sent to 14 · 1 failed", who + why, credits used).
               Rendered by window.SendFeedback; textContent-safe markup only. -->
          <div id="mass-text-send-status" style="display:none;margin:0 0 12px;padding:10px 12px;background:var(--bg-card,#fff);border:1px solid var(--border-light,#E2E8F0);border-radius:8px;"></div>
          <div class="mass-text-actions">
            <button class="btn btn-secondary" onclick="window.DispatchModule.copyMassText()">
              <span class="material-icons">content_copy</span> Copy
            </button>
            <button class="btn btn-primary" id="btn-send-mass-text" onclick="window.DispatchModule.sendMassText()">
              <span class="material-icons">send</span> Send
            </button>
          </div>
          <!-- TASK-9842 — RECENT CREW TEXTS: per send, per person — Sent → Delivered
               or Failed, plus Replied (with the text). Subscribes only while open
               (ontoggle), torn down by closeModal('mass-text'). Body is built with
               createElement/textContent — names and reply text come off an SMS. -->
          <details id="mass-text-recent" style="margin-top:14px;border-top:1px solid var(--border-color);padding-top:10px;" ontoggle="window.DispatchModule.onMassTextRecentToggle(this.open)">
            <summary style="cursor:pointer;font-weight:600;color:var(--text-primary);font-size:0.9rem;">Recent crew texts — who got it, who replied</summary>
            <div id="mass-text-recent-body" style="margin-top:8px;"></div>
          </details>
        </div>
      </div>
    </div>

    <!-- Individual Text Modal -->
    <div class="modal hidden" id="modal-individual-text">
      <div class="modal-content modal-medium modal-fullscreen">
        <div class="modal-header">
          <h3>📱 Text Crew</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('individual-text')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div data-tenant-send-notice="crewText" style="display:none;"></div>
          <textarea id="individual-text-content" class="mass-text-textarea" placeholder="Tap here to edit message..." oninput="window.DispatchModule.onIndividualTextInput()"></textarea>
          <div class="mass-text-footer">
            <span id="individual-text-char-count">0 chars</span>
            <span id="individual-text-recipients" class="recipient-count"></span>
          </div>

          <!-- Full Notes Toggle -->
          <div style="margin-bottom:12px;">
            <label class="checkbox-switch" style="display:flex;align-items:center;gap:12px;padding:12px;background:var(--bg-elevated);border-radius:var(--radius-md);">
              <input type="checkbox" id="individual-text-full-notes" onchange="window.DispatchModule.toggleFullNotes('individual')">
              <span class="switch-slider"></span>
              <span class="switch-label" style="flex:1;">
                <strong>Send Full Notes</strong>
                <span style="display:block;font-size:0.8em;color:var(--text-secondary);">Include complete job notes instead of a short preview</span>
              </span>
            </label>
          </div>

          <div id="individual-text-sms-meter" style="margin-bottom:12px;"></div>
          <div class="mass-text-actions">
            <button class="btn btn-secondary" onclick="window.DispatchModule.copyIndividualText()">
              <span class="material-icons">content_copy</span> Copy
            </button>
            <button class="btn btn-primary" id="btn-send-individual-text" onclick="window.DispatchModule.sendIndividualTextSMS()">
              <span class="material-icons">send</span> Send SMS
            </button>
          </div>
          <div id="individual-text-hint" class="text-hint" style="display: none;"></div>
        </div>
      </div>
    </div>

    <!-- Waypoints Modal -->
    <div class="modal hidden" id="modal-waypoints">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Destinations</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('waypoints')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="waypoints-modal-body">
          <!-- Filled dynamically -->
        </div>
      </div>
    </div>

    <!-- Edit Customer Modal -->
    <div class="modal hidden" id="modal-edit-customer">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-edit-customer-title">Edit Customer</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('edit-customer')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-edit-customer" novalidate onsubmit="window.DispatchModule.saveEditCustomer(event)">
          <input type="hidden" id="edit-customer-id">

          <div class="form-group">
            <label for="edit-customer-name">Customer Name *</label>
            <input type="text" id="edit-customer-name" required placeholder="ABC Oil & Gas">
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="edit-customer-phone">Phone</label>
              <input type="tel" id="edit-customer-phone" placeholder="303-555-1234">
            </div>
            <div class="form-group">
              <label for="edit-customer-email">Email</label>
              <input type="email" id="edit-customer-email" placeholder="contact@company.com">
            </div>
          </div>

          <!-- TASK-9588 — per-customer "No ticket emails" (same field as the CRM editor) -->
          <div class="form-group">
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-weight:500;">
              <input type="checkbox" id="edit-customer-ticket-emails-off">
              <span>Don't email tickets to this customer</span>
            </label>
            <p style="font-size:12px;color:var(--text-secondary);margin:2px 0 0 26px;">Hides the 'unsent' count for this customer. You can still email a ticket by hand.</p>
            <p id="edit-customer-ticket-email-note" style="display:none;font-size:12px;color:#92400E;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:4px 8px;margin:4px 0 0 26px;"></p>
          </div>

          <div class="form-section">
            <div class="section-header">
              <h4>Contacts</h4>
              <button type="button" class="btn btn-secondary btn-small" onclick="window.DispatchModule.addContactRow()">
                <span class="material-icons">add</span> Add Contact
              </button>
            </div>
            <div id="customer-contacts-list">
              <!-- Filled dynamically -->
            </div>
          </div>

          <div id="edit-customer-contracts">
            <!-- Filled dynamically for clockbilling subscribers -->
          </div>

          <div class="modal-footer" style="justify-content:space-between;">
            <button type="button" class="btn btn-sm" style="color:#DC2626;border:1px solid #FECACA;background:#FEF2F2;" onclick="window.DispatchModule.deleteCustomer()">
              <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">delete</span> Delete
            </button>
            <div style="display:flex;gap:8px;">
              <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeModal('edit-customer')">Cancel</button>
              <button type="submit" class="btn btn-primary">Save Customer</button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Safety Hold Details Modal -->
    <div class="modal hidden" id="modal-safety-hold-details">
      <div class="modal-content modal-small">
        <div class="modal-header modal-header-danger">
          <h3><span class="material-icons">lock</span> DVIR Safety Hold</h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('safety-hold-details')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="safety-hold-detail-info">
            <div class="detail-row">
              <span class="detail-label" id="hold-detail-unit-label">Unit:</span>
              <span class="detail-value" id="hold-detail-unit"></span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Defect:</span>
              <span class="detail-value" id="hold-detail-defect"></span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Reported by:</span>
              <span class="detail-value" id="hold-detail-reporter"></span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Date:</span>
              <span class="detail-value" id="hold-detail-date"></span>
            </div>
          </div>
          <p class="hold-release-note">
            <span class="material-icons">info</span>
            Go to Maintenance module to release this hold.
          </p>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.DispatchModule.closeModal('safety-hold-details')">Close</button>
        </div>
      </div>
    </div>

    <!-- Truck Info Modal -->
    <div class="modal hidden" id="modal-truck-info">
      <div class="modal-content modal-medium">
        <div class="modal-header">
          <h3><span class="material-icons">local_shipping</span> <span id="truck-info-title">Truck Info</span></h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('truck-info')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="truck-info-body">
          <!-- Filled dynamically -->
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.DispatchModule.closeModal('truck-info')">Close</button>
        </div>
      </div>
    </div>

    <!-- Trailer Info Modal -->
    <div class="modal hidden" id="modal-trailer-info">
      <div class="modal-content modal-medium">
        <div class="modal-header">
          <h3><span class="material-icons">rv_hookup</span> <span id="trailer-info-title">Trailer Info</span></h3>
          <button class="btn-icon" onclick="window.DispatchModule.closeModal('trailer-info')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="trailer-info-body">
          <!-- Filled dynamically -->
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.DispatchModule.closeModal('trailer-info')">Close</button>
        </div>
      </div>
    </div>

    <!-- 811 Permits Modal — TASK-9859: a LIBRARY (every ticket listed, expired on
         top, optional customer filter) with a bulk uploader on top. Customer and
         job are optional per ticket; the job list waits for a customer. -->
    <div class="modal hidden" id="modal-811-submission">
      <style>
        .p811-exp { display:inline-flex; align-items:center; gap:3px; padding:1px 8px; border-radius:var(--radius-sm); border:1px solid #CBD5E1; background:#F1F5F9; color:#475569; font-size:0.75rem; font-weight:600; line-height:1.5; white-space:nowrap; }
        .p811-exp .material-icons { font-size:14px; }
        .p811-exp-soon { background:#FEF3C7; color:#92400E; border-color:#FCD34D; }
        .p811-exp-expired { background:#FEE2E2; color:#991B1B; border-color:#FCA5A5; }
        .job-811-warn { display:flex; align-items:flex-start; gap:4px; margin-top:4px; padding:6px 8px; border-radius:var(--radius-sm); background:#FFFBEB; border:1px solid #FDE68A; color:#92400E; font-size:0.8rem; line-height:1.35; }
        .job-811-warn .material-icons { font-size:16px; flex-shrink:0; }
        .p811-page .permit-upload-area { min-height:96px; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; cursor:pointer; }
        .p811-page .permit-upload-area.is-drag { border-color:var(--brand-blue); background:rgba(59,130,246,0.08); }
        .p811-page .permit-upload-area:focus-visible { outline:3px solid var(--brand-blue); outline-offset:2px; }
        .p811-page .p811-jobhint { margin:0 0 12px; padding:8px 10px; border-radius:var(--radius-md); background:#ECFEFF; border:1px solid #A5F3FC; color:#155E75; font-size:0.875rem; }
        .p811-page .p811-up-row { border:1px solid #CBD5E1; border-radius:var(--radius-md); padding:10px 12px; margin:0 0 10px; background:var(--bg-card); }
        .p811-page .p811-up-row.is-error, .p811-page .p811-up-row.is-invalid { border-color:#FCA5A5; background:#FEF2F2; }
        .p811-page .p811-up-head { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
        .p811-page .p811-up-file { flex:1; min-width:0; font-size:0.875rem; color:var(--text-secondary); overflow-wrap:anywhere; }
        .p811-page .p811-up-head .btn-icon { min-width:44px; min-height:44px; }
        .p811-page .p811-up-row label { display:block; margin:6px 0 2px; font-size:0.875rem; font-weight:600; color:var(--text-primary); }
        .p811-page .p811-up-row input, .p811-page .p811-up-row select { width:100%; min-height:44px; font-size:16px; }
        .p811-page .p811-up-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(180px, 1fr)); gap:0 10px; } /* TASK-9862 rowfix: wraps instead of clipping */
        .p811-page .p811-up-jobq { margin-bottom:6px; }
        .p811-page .p811-up-status { margin-top:6px; font-size:0.85rem; color:var(--text-secondary); min-height:1.2em; }
        .p811-page .p811-up-err { color:#991B1B; display:flex; align-items:center; gap:4px; font-weight:600; }
        .p811-page .p811-up-err .material-icons { font-size:16px; }
        .p811-page .p811-up-bar { height:6px; border-radius:999px; background:var(--bg-elevated); overflow:hidden; margin-bottom:2px; }
        .p811-page .p811-up-bar > span { display:block; height:100%; background:var(--brand-blue); transition:width 0.2s ease; }
        .p811-page .p811-up-actions { display:flex; gap:8px; flex-wrap:wrap; margin:4px 0 16px; }
        .p811-page .p811-up-actions .btn { min-height:48px; }
        .p811-page .permit-list-item.is-expired { border-left:4px solid #DC2626; }
        .p811-page .permit-list-item .btn-icon { min-width:44px; min-height:44px; }
        .p811-page .permit-list-meta .p811-exp { margin-right:6px; }
        .p811-page .p811-edit input, .p811-page .p811-edit select { width:100%; min-height:44px; font-size:16px; }
        .p811-wait { display:inline-flex; align-items:center; padding:1px 8px; margin-right:6px; border-radius:var(--radius-sm); border:1px solid #C4B5FD; background:#F3E8FF; color:#6B21A8; font-size:0.75rem; font-weight:600; white-space:nowrap; }
        .p811-pjn { margin-right:6px; color:var(--text-primary); font-weight:600; }
        .job-811-start { margin:0 0 var(--spacing-md); padding:10px 12px; border-radius:var(--radius-md); background:#ECFEFF; border:1px solid #A5F3FC; color:#155E75; font-size:0.875rem; display:flex; flex-direction:column; gap:6px; }
        .job-811-start-title { display:flex; align-items:center; gap:6px; font-weight:700; }
        .job-811-start-line { display:flex; flex-wrap:wrap; align-items:center; gap:6px; color:var(--text-primary); }
        .job-811-start-line .btn { min-height:44px; }
        .p811-page .p811-startjob { min-height:44px; white-space:nowrap; margin-right:4px; }
        .p811-page .p811-up-hint { display:block; margin-top:2px; font-size:0.8rem; color:#92400E; }
        .p811-page .p811-up-why { color:var(--text-secondary); }
        .p811-page .p811-up-saved { display:block; margin-top:2px; font-size:0.85rem; color:var(--text-primary); }
        .p811-wait.p811-proj-chip { background:#ECFEFF; border-color:#A5F3FC; color:#155E75; }
        .p811-page .p811-name-row { display:flex; gap:8px; align-items:stretch; }
        .p811-page .p811-name-row input { flex:1; min-width:0; }
        .p811-page .p811-gen { min-height:44px; min-width:44px; display:inline-flex; align-items:center; gap:4px; white-space:nowrap; padding:0 12px; }
        .p811-page .p811-gen .material-icons { font-size:18px; }
        .p811-page .p811-lib-tools { display:flex; gap:8px; flex-wrap:wrap; margin:0 0 12px; }
        .p811-page .p811-lib-tools input { flex:1; min-width:160px; min-height:44px; font-size:16px; }
        .p811-page .p811-lib-chip { min-height:44px; padding:0 14px; border-radius:999px; border:1px solid #CBD5E1; background:var(--bg-card); color:var(--text-primary); font-weight:600; cursor:pointer; }
        .p811-page .p811-lib-chip[aria-pressed="true"] { background:#F3E8FF; border-color:#C4B5FD; color:#6B21A8; }
        @media (max-width: 600px) { .p811-page .p811-up-grid { grid-template-columns:1fr; } }
        /* TASK-9862 — Locates page (mockups 2026-10-14). 44-48px targets, one gutter, no side scroll at 390px. */
        .p811-page .modal-header { gap:8px; flex-wrap:wrap; }
        .p811-page .modal-header h3 { flex:1; min-width:0; }
        .p811-page .p811-upload-btn { min-height:48px; color:#fff; }
        .p811-page .p811-upload-btn .material-icons { color:#fff; }
        .p811-page .p811-drop { min-height:56px; flex-direction:row; gap:10px; padding:10px 14px; margin:0 0 12px; font-size:0.9rem; color:var(--text-primary); }
        .p811-page .p811-drop .material-icons { font-size:28px; color:var(--text-secondary); }
        .p811-page .p811-lib-tools select { min-height:44px; font-size:16px; max-width:260px; }
        .p811-chips { display:flex; gap:8px; overflow-x:auto; margin:0 0 12px; padding-bottom:2px; }
        .p811-chips .p811-lib-chip { flex-shrink:0; }
        .p811-page .p811-lib-chip[aria-pressed="true"] { background:var(--brand-navy,#0F172A); border-color:var(--brand-navy,#0F172A); color:#fff; }
        .p811-alarm { background:#991B1B; color:#fff; border-radius:12px; padding:12px 14px; display:flex; gap:12px; align-items:center; margin:0 0 12px; flex-wrap:wrap; }
        .p811-alarm .material-icons { font-size:30px; }
        .p811-alarm-big { font-size:1.1rem; font-weight:800; }
        .p811-alarm-sub { color:rgba(255,255,255,.92); font-size:0.9rem; }
        .p811-alarm .btn { background:#fff; color:#991B1B; border:0; min-height:44px; font-weight:700; }
        .p811-group { background:var(--bg-card); border:1px solid var(--border-light); border-radius:10px; box-shadow:var(--shadow-sm); margin:0 0 8px; overflow:hidden; display:flex; }
        .p811-group-stripe { width:6px; flex-shrink:0; background:var(--brand-blue); }
        .p811-group.is-expired .p811-group-stripe { background:#DC2626; }
        .p811-group.is-soon .p811-group-stripe { background:#D97706; }
        .p811-group.is-renewed .p811-group-stripe { background:#7C3AED; }
        .p811-group-body { flex:1; min-width:0; padding:8px 12px; display:flex; gap:10px; align-items:center; }
        .p811-group-left { flex:1; min-width:220px; }
        .p811-group-cust { font-size:0.72rem; font-weight:700; color:var(--text-secondary); text-transform:uppercase; letter-spacing:.04em; }
        .p811-group-title { font-size:1.05rem; font-weight:700; margin:0; color:var(--text-primary); overflow-wrap:anywhere; display:flex; flex-wrap:wrap; align-items:center; gap:4px 8px; }
        /* TASK-9920 — the site name opens the site; the project chip sits beside it. */
        .p811-title-btn { background:none; border:0; padding:0; margin:0; font:inherit; color:inherit; text-align:left; cursor:pointer; overflow-wrap:anywhere; }
        .p811-title-btn:hover { color:var(--brand-blue); text-decoration:underline; }
        .p811-title-btn:focus-visible { outline:3px solid var(--brand-blue); outline-offset:2px; border-radius:4px; }
        .p811-proj-chip { display:inline-flex; align-items:center; gap:4px; background:#EFF6FF; color:#1E40AF; border:1px solid #BFDBFE; border-radius:999px; padding:1px 10px; font-size:0.85rem; font-weight:700; }
        .p811-proj-chip .material-icons { font-size:15px; }
        .p811-group-title .p811-count { margin-left:0; }
        /* TASK-9920 — site sheet header: what the name is, rename, project beside it. */
        #p811-group-title { display:inline-flex; flex-wrap:wrap; align-items:center; gap:4px 8px; min-width:0; }
        .p811-hdr-kicker { display:block; width:100%; font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:var(--text-secondary); }
        .p811-hdr-name { overflow-wrap:anywhere; }
        #p811-group-title .p811-rename-btn { min-width:44px; min-height:44px; }
        #p811-group-title .p811-count { margin-left:0; }
        .p811-rename { display:flex; flex-wrap:wrap; gap:6px; align-items:center; width:100%; }
        .p811-rename input { flex:1 1 220px; min-width:0; min-height:44px; font-size:16px; box-sizing:border-box; }
        .p811-rename .btn { min-height:44px; }
        .p811-count { display:inline-block; background:var(--bg-elevated); border:1px solid #CBD5E1; border-radius:999px; padding:1px 10px; font-size:0.85rem; font-weight:700; margin-left:6px; vertical-align:middle; }
        .p811-group-meta { color:var(--text-secondary); font-size:0.85rem; margin-top:2px; display:flex; gap:2px 12px; flex-wrap:wrap; align-items:center; }
        .p811-group-meta b { color:var(--text-primary); }
        .p811-group-acts { display:flex; gap:6px; align-items:center; flex-shrink:0; }
        .p811-group-acts .btn { min-height:40px; padding:4px 12px; font-size:0.9rem; white-space:nowrap; }
        .p811-mi { display:inline-flex; align-items:center; gap:4px; }
        .p811-group-acts .p811-cardmenu summary { min-width:40px; min-height:40px; border:1px solid var(--border-light); border-radius:8px; }
        .p811-group-acts .p811-tickmenu-list { min-width:200px; }
        .p811-badge { display:inline-flex; align-items:center; gap:4px; border-radius:6px; padding:2px 8px; font-size:0.8rem; font-weight:700; border:1px solid; white-space:nowrap; }
        .p811-badge .material-icons { font-size:15px; }
        .p811-b-exp { background:#FEE2E2; color:#991B1B; border-color:#FCA5A5; }
        .p811-b-soon { background:#FEF3C7; color:#92400E; border-color:#FCD34D; }
        .p811-b-clear { background:#DCFCE7; color:#166534; border-color:#86EFAC; }
        .p811-b-ren { background:#F3E8FF; color:#6B21A8; border-color:#C4B5FD; }
        .p811-b-none { background:#F3F4F6; color:#374151; border-color:#D1D5DB; }
        /* TASK-9920 — before the legal dig start: never green. */
        .p811-b-wait { background:#FFF7ED; color:#9A3412; border-color:#FDBA74; }
        .p811-group.is-waiting .p811-group-stripe { background:#EA580C; }
        .p811-merge { margin-top:8px; background:#ECFEFF; border:1px solid #A5F3FC; color:#155E75; border-radius:8px; padding:6px 10px; font-size:0.875rem; font-weight:600; display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        .p811-merge .btn { min-height:44px; }
        .p811-unnamed h4 { margin:16px 0 8px; }
        .p811-undated { display:flex; gap:10px; align-items:flex-start; margin:0 0 12px; padding:10px 12px; border-radius:10px; background:#FEF3C7; border:1px solid #F59E0B; color:#78350F; font-size:0.9rem; }
        .p811-undated .material-icons { color:#B45309; flex-shrink:0; }
        .p811-undated b { color:#78350F; }
        .p811-b-undated { background:#FEF3C7; color:#78350F; border-color:#F59E0B; }
        .p811-tns { font-family:'Courier New',Courier,monospace; font-weight:700; font-size:0.85rem; color:var(--text-secondary); margin-top:1px; overflow-wrap:anywhere; } /* TASK-9994 */
        #dispatch-settings-modal.p811-over { z-index:1100; }
        /* TASK-10180 — the filter row stays on top of the list while it scrolls; a PDF dragged over the window outlines it. */
        @media (min-width: 601px) { .p811-page .p811-lib-head { position:sticky; top:calc(-1 * var(--spacing-lg, 16px)); z-index:2; background:var(--bg-card, #fff); padding-top:4px; margin-top:-4px; } }
        .p811-page .p811-lib-tools select { flex:1 1 140px; max-width:none; }
        .p811-page .p811-lib-tools #permit-search { flex:1 1 100%; }
        #permit-upload-area.is-drag { outline:3px dashed var(--brand-blue); outline-offset:-3px; }
        @media (max-width: 600px) {
          .p811-page .p811-lib-tools #permit-search { flex:1 1 100%; }
          #permit-upload-area .p811-lib-tools select { flex:1 1 40%; min-width:0; } /* TASK-10180: two per row on a phone */
          .p811-b-pot { white-space:normal; }
        }
        .p811-mono { font-family:'Courier New',Courier,monospace; font-weight:700; }
        .p811-page .p811-up-row input, .p811-page .p811-up-row select { box-sizing:border-box; max-width:100%; min-width:0; }
        .p811-read-tag { display:block; font-size:0.75rem; font-weight:700; color:#155E75; margin-top:2px; }
        .p811-read-tag.is-file { color:var(--text-secondary); }
        .p811-read { font-size:0.85rem; margin:0 0 4px; }
        .p811-rev-more { margin-top:6px; }
        .p811-rev-more summary { min-height:44px; display:flex; align-items:center; cursor:pointer; font-weight:600; color:var(--text-primary); }
        .p811-facts { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:10px; margin:0 0 14px; }
        .p811-fact { background:var(--bg-elevated); border:1px solid var(--border-light); border-radius:10px; padding:8px 10px; min-width:0; }
        .p811-fact .k { font-size:0.8rem; font-weight:700; color:var(--text-secondary); }
        .p811-fact .v { font-size:1rem; font-weight:700; margin-top:2px; color:var(--text-primary); overflow-wrap:anywhere; }
        .p811-fact.is-red { background:#FEF2F2; border-color:#FECACA; }
        .p811-fact.is-red .v { color:#991B1B; }
        /* TASK-9887 — the tiles are controls: press affordance + a "what it does" line. */
        button.p811-fact.is-action { display:block; width:100%; text-align:left; font:inherit; cursor:pointer; min-height:44px; transition:border-color .15s ease, box-shadow .15s ease, background .15s ease; }
        button.p811-fact.is-action:hover { border-color:var(--brand-blue); box-shadow:var(--shadow-sm); background:var(--bg-card); }
        button.p811-fact.is-action:active { transform:translateY(1px); }
        button.p811-fact.is-action:focus-visible { outline:3px solid var(--brand-blue); outline-offset:2px; }
        button.p811-fact.is-action.is-red:hover { background:#FEF2F2; border-color:#DC2626; }
        .p811-fact .h { display:flex; align-items:center; gap:4px; margin-top:4px; font-size:0.8rem; font-weight:600; color:#1D4ED8; }
        .p811-fact.is-red .h { color:#991B1B; }
        .p811-fact .h .material-icons { font-size:15px; }
        .p811-fact .h.is-ro { color:var(--text-secondary); font-weight:500; }
        .p811-tick { display:grid; grid-template-columns:44px 110px 160px 84px 100px minmax(0,1fr) 44px; gap:8px; align-items:center; padding:8px 10px; border-bottom:1px solid var(--border-light); }
        .p811-tick.head { font-size:0.75rem; font-weight:700; color:var(--text-secondary); text-transform:uppercase; background:var(--bg-elevated); }
        .p811-tick input[type=checkbox] { width:24px; height:24px; }
        .p811-tick .btn-icon { min-width:44px; min-height:44px; }
        .p811-tick .p811-b-wait { white-space:normal; } /* TASK-9920 — wraps inside its column */
        .p811-ticks { border:1px solid var(--border-light); border-radius:12px; overflow:hidden; }
        .p811-renew { margin-top:14px; border:2px solid var(--brand-blue); border-radius:12px; padding:12px; background:#F8FBFF; }
        .p811-renew h5 { margin:0 0 6px; font-size:1.05rem; display:flex; gap:6px; align-items:center; }
        .p811-renew label.p811-opt { display:flex; gap:10px; align-items:flex-start; min-height:44px; padding:6px 0; cursor:pointer; color:var(--text-primary); }
        .p811-renew input[type=radio] { width:22px; height:22px; flex-shrink:0; margin-top:1px; }
        .p811-renew input[type=date] { min-height:44px; font-size:16px; }
        .p811-stop { background:#FEF2F2; border:2px solid #DC2626; border-radius:12px; padding:14px; display:flex; gap:12px; align-items:flex-start; }
        .p811-stop-ic { background:#DC2626; color:#fff; border-radius:999px; width:40px; height:40px; display:grid; place-items:center; flex-shrink:0; }
        .p811-stop h4 { margin:0 0 4px; font-size:1.1rem; color:#991B1B; }
        .p811-stop p { margin:0 0 8px; color:#7F1D1D; }
        .p811-stop-why { margin-top:12px; }
        .p811-stop-why label { display:block; font-weight:700; margin-bottom:4px; color:var(--text-primary); }
        .p811-stop-why textarea { width:100%; min-height:72px; font-size:16px; }
        #modal-811-stop .modal-footer .btn { min-height:48px; }
        .p811-attach { border:2px solid var(--brand-cyan,#06B6D4); background:#ECFEFF; border-radius:12px; padding:12px; display:flex; gap:10px; align-items:center; flex-wrap:wrap; margin:0 0 12px; }
        .p811-attach .t { font-size:1rem; font-weight:700; color:#155E75; }
        .p811-attach .s { font-size:0.9rem; color:#155E75; }
        .p811-attach .btn { min-height:48px; }
        .job-card .p811-card-flag, .dispatch-job-card .p811-card-flag { margin-top:4px; background:#DC2626; color:#fff; border-radius:6px; padding:3px 8px; font-weight:800; font-size:0.75rem; display:inline-flex; gap:4px; align-items:center; letter-spacing:.02em; }
        .p811-card-flag .material-icons { font-size:14px; }
        .job-card .p811-card-icon, .dispatch-job-card .p811-card-icon { color:#DC2626; opacity:1; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; vertical-align:middle; min-width:24px; min-height:24px; }
        .p811-card-icon .material-icons { font-size:18px; }
        .job-card.p811-locate-stop, .dispatch-job-card.p811-locate-stop { border-right:3px solid #DC2626; }
        .p811-card-icon:hover, .p811-card-icon:focus-visible { transform:scale(1.2); }
        .p811-tickmenu { position:relative; }
        .p811-tickmenu summary { list-style:none; display:grid; place-items:center; cursor:pointer; }
        .p811-tickmenu summary::-webkit-details-marker { display:none; }
        .p811-tickmenu-list { position:absolute; right:0; top:46px; z-index:5; background:var(--bg-card); border:1px solid var(--border-light); border-radius:10px; box-shadow:var(--shadow-lg); padding:6px; display:flex; flex-direction:column; gap:6px; min-width:160px; }
        .p811-tickmenu-list .btn { min-height:44px; justify-content:flex-start; }
        /* TASK-9887 — hidden until p811PlaceMenu has put it in the viewport (the
           toggle event is async: one frame at the old clipped spot otherwise). */
        .p811-tickmenu[open] .p811-tickmenu-list:not([data-flip]) { visibility:hidden; }
        /* TASK-9887 — potentials + the directions placeholder. */
        .p811-b-pot { background:#ECFEFF; color:#155E75; border-color:#A5F3FC; }
        .p811-group.p811-pot .p811-group-stripe { background:#0891B2; }
        .p811-group.p811-pot.is-archived, .p811-group.p811-pot.is-wont, .p811-group.p811-pot.is-used { opacity:.85; }
        .p811-group.p811-pot.is-archived .p811-group-stripe, .p811-group.p811-pot.is-wont .p811-group-stripe { background:#94A3B8; }
        .p811-pot-notes { margin-top:4px; font-size:0.875rem; color:var(--text-primary); overflow-wrap:anywhere; }
        .p811-pot-h { display:flex; align-items:center; gap:6px; margin:0 0 8px; font-size:1rem; color:var(--text-primary); }
        .p811-pot-kind { display:flex; gap:16px; margin:0 0 12px; }
        .p811-pot-kind label { display:flex; align-items:center; gap:8px; min-height:44px; font-weight:600; cursor:pointer; color:var(--text-primary); }
        .p811-pot-kind input { width:22px; height:22px; }
        .p811-pot-ticks { max-height:240px; overflow-y:auto; border:1px solid var(--border-light); border-radius:10px; padding:4px 8px; }
        .p811-pot-tick { display:flex; align-items:center; gap:10px; min-height:44px; flex-wrap:wrap; cursor:pointer; color:var(--text-primary); }
        .p811-pot-tick input { width:22px; height:22px; flex-shrink:0; }
        .p811-pot-tick-site { flex:1; min-width:120px; overflow-wrap:anywhere; }
        #modal-811-potential input[type=text], #modal-811-potential select, #modal-811-potential textarea { width:100%; min-height:44px; font-size:16px; box-sizing:border-box; }
        .p811-dirs { margin:0 0 14px; padding:8px 10px; border:1px dashed #CBD5E1; border-radius:10px; background:var(--bg-elevated); }
        .p811-dirs .k { display:flex; align-items:center; gap:6px; font-size:0.8rem; font-weight:700; color:var(--text-secondary); }
        .p811-dirs .k .material-icons { font-size:16px; }
        .p811-dirs .v { margin-top:2px; font-size:0.9rem; color:var(--text-primary); overflow-wrap:anywhere; white-space:pre-line; }
        .p811-empty { color:var(--text-secondary); }
        @media (max-width: 900px) {
          .p811-facts { grid-template-columns:1fr 1fr; }
          .p811-tick { grid-template-columns:44px minmax(0,1fr) minmax(0,auto) 44px; }
          .p811-tick .p811-hm { display:none; }
        }
        @media (max-width: 600px) {
          .p811-group-body { flex-wrap:wrap; }
          .p811-group-acts { width:100%; }
          .p811-group-acts .btn, .p811-group-acts .p811-cardmenu summary { min-height:44px; min-width:44px; }
          .p811-group-acts .p811-primary-act { flex:1 1 auto; justify-content:center; }
          .p811-group-left { min-width:0; }
          .p811-page .p811-upload-label .p811-upload-long { display:none; }
          .p811-page .p811-lib-tools select { max-width:100%; flex:1; }
        }
      </style>
      <!-- TASK-10180 — the drop zone is gone from the top: a PDF dropped anywhere on the window
           still uploads (p811DragOver / p811Drop mark this frame, id kept for them). -->
      <div class="modal-content modal-large p811-page" id="permit-upload-area" style="display:flex;flex-direction:column;max-height:94vh;max-height:94dvh;overflow:hidden"
           ondragover="window.DispatchModule.p811DragOver(event)" ondragleave="window.DispatchModule.p811DragLeave(event)" ondrop="window.DispatchModule.p811Drop(event)">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">assignment</span> Locates (811)</h3>
          <button type="button" class="btn-icon hidden" id="btn-p811-settings" aria-label="811 settings" title="811 settings" onclick="window.DispatchModule.p811OpenSettings()"><span class="material-icons">settings</span></button>
          <button type="button" class="btn btn-primary p811-upload-btn" onclick="document.getElementById('permit-file-input').click()"><span class="material-icons" aria-hidden="true">upload_file</span><span class="p811-upload-label">Upload<span class="p811-upload-long"> ticket PDFs</span></span></button>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.closeModal('811-submission')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <!-- TASK-9805 — a compliance partner sees the tickets, never the upload form. -->
          <p id="permit-readonly-note" class="hidden" style="margin:0 0 12px;color:var(--text-primary);font-size:0.9rem;">You can view this company's 811 tickets. Only their own staff can upload or change them.</p>
          <!-- TASK-10180 (Alyse) — the filter row comes first: Customer, Project, Show, order, then search. -->
          <div class="p811-lib-head">
          <div class="p811-lib-tools">
            <select id="permit-customer-filter" aria-label="Customer" onchange="window.DispatchModule.renderPermitsList()">
              <option value="">All customers</option>
            </select>
            <select id="permit-project-filter" aria-label="Project" title="Tickets on the project, or on a job of the project" onchange="window.DispatchModule.renderPermitsList()">
              <option value="">All projects</option>
            </select>
            <select id="p811-show" aria-label="Show" onchange="window.DispatchModule.p811SetFilter(this.value || 'all')">
              <option value="">Current tickets</option>
              <option value="renewed">Renewed (history)</option>
              <option value="potential">Potential jobs / projects</option>
            </select>
            <!-- Remembered per login on this device. A typed search lists best matches first. -->
            <select id="p811-sort" aria-label="Sort locates" title="Order of the list. A typed search lists best matches first." onchange="window.DispatchModule.p811SetSort(this.value)">
              <option value="new">Newest uploaded</option>
              <option value="soon">Soonest first</option>
              <option value="az">Customer A–Z</option>
            </select>
            <input type="search" id="permit-search" aria-label="Search locates" placeholder="Search ticket #, site, customer, county, job #, date…" title="Finds part of a ticket #, site or lease, customer, county or township, address, project, job #, crew, or dates like &quot;good through next week&quot;, &quot;oct 20&quot;, &quot;expired&quot;. Typos are OK." oninput="window.DispatchModule.renderPermitsList()">
          </div>
          <div class="p811-chips" role="group" aria-label="Filter locates">
            <button type="button" class="p811-lib-chip" data-p811-filter="all" aria-pressed="true" onclick="window.DispatchModule.p811SetFilter('all')">All</button>
            <button type="button" class="p811-lib-chip" data-p811-filter="alert" aria-pressed="false" onclick="window.DispatchModule.p811SetFilter('alert')">Expired / expiring</button>
            <button type="button" class="p811-lib-chip" id="permit-filter-waiting" data-p811-filter="nojob" aria-pressed="false" onclick="window.DispatchModule.p811SetFilter('nojob')">Not on the board</button>
          </div>
          </div>
          <div id="permit-upload-section">
          <p id="permit-job-form-hint" class="p811-jobhint hidden"></p>
          <input type="file" id="permit-file-input" accept=".pdf,application/pdf" multiple style="display:none" onchange="window.DispatchModule.handlePermitFile(event)">
          <div id="p811-alarm" class="p811-alarm hidden" role="alert"></div>
          </div>
          <!-- TASK-9994 — a ticket on a job with no expiration date: the morning email
               cannot warn about it. A critical notice, never behind the warnings switch. -->
          <div id="p811-undated" class="p811-undated hidden" role="status"></div>
          <div id="p811-groups" class="p811-groups"></div>
          <div id="p811-unnamed" class="p811-unnamed hidden">
            <h4 id="p811-unnamed-title">Needs a site name</h4>
            <div id="permits-list" class="permits-list"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- TASK-9862 — bulk upload review: check what we read off the PDFs. -->
    <div class="modal hidden" id="modal-811-review">
      <div class="modal-content modal-large p811-page" style="max-width:1180px;display:flex;flex-direction:column;max-height:94vh;max-height:94dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">fact_check</span> <span id="p811-review-title">Check tickets before saving</span></h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.p811CloseReview()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <p id="p811-review-hint" class="p811-jobhint hidden"></p>
          <!-- TASK-10168 — "Reading 3 of 8" once at the top; she keeps typing. -->
          <p id="p811-review-progress" class="p811-rt-prog hidden" role="status" aria-live="polite"></p>
          <div id="permit-upload-rows"></div>
        </div>
        <div class="modal-footer p811-up-actions hidden" id="permit-upload-actions" style="flex-shrink:0">
          <button type="button" class="btn btn-secondary" id="btn-clear-permit-rows" onclick="window.DispatchModule.clearPermitFile()">Cancel</button>
          <button type="button" class="btn btn-secondary" onclick="document.getElementById('permit-file-input').click()"><span class="material-icons">add</span> Add PDFs</button>
          <button type="button" class="btn btn-primary" onclick="window.DispatchModule.savePermit()" id="btn-save-permit">
            <span class="material-icons">save</span> Save
          </button>
        </div>
      </div>
    </div>

    <!-- TASK-9862 — one site's tickets: facts, tickets, Renew. -->
    <div class="modal hidden" id="modal-811-group">
      <div class="modal-content modal-large p811-page" style="display:flex;flex-direction:column;max-height:94vh;max-height:94dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">place</span> <span id="p811-group-title">Site</span></h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.closeModal('811-group')"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" id="p811-group-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch"></div>
        <div class="modal-footer" id="p811-group-foot" style="flex-shrink:0;display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end"></div>
      </div>
    </div>

    <!-- TASK-9887 — a potential job / project (Locates). Never on the board. -->
    <div class="modal hidden" id="modal-811-potential">
      <div class="modal-content modal-medium p811-page" style="display:flex;flex-direction:column;max-height:94vh;max-height:94dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">lightbulb</span> <span id="p811-pot-title">New potential job or project</span></h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.p811ClosePotential()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <p class="p811-jobhint">Not on the board. When dispatch makes a job or project like this, DispatchDeck suggests it and one tap pulls these tickets in.</p>
          <div class="p811-pot-kind" role="radiogroup" aria-label="Kind">
            <label><input type="radio" name="p811-pot-kind" value="job" checked> Job</label>
            <label><input type="radio" name="p811-pot-kind" value="project"> Project</label>
          </div>
          <div class="form-group"><label for="p811-pot-name">Name</label><input type="text" id="p811-pot-name" maxlength="120" autocomplete="off" placeholder="Lease, pad or project name"></div>
          <div class="form-group"><label for="p811-pot-customer">Customer</label><select id="p811-pot-customer" onchange="window.DispatchModule.p811PotCustomerChanged()"></select></div>
          <div class="form-group"><label for="p811-pot-site">Site / lease (optional)</label><input type="text" id="p811-pot-site" maxlength="120" autocomplete="off"></div>
          <div class="form-group"><label id="p811-pot-tickets-label">811 tickets</label><div id="p811-pot-tickets" class="p811-pot-ticks" role="group" aria-labelledby="p811-pot-tickets-label"></div></div>
          <div class="form-group"><label for="p811-pot-notes">Notes</label><textarea id="p811-pot-notes" maxlength="1000" rows="3" placeholder="Who told you, when it might start, anything dispatch should know"></textarea></div>
        </div>
        <div class="modal-footer" style="flex-shrink:0;display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end">
          <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811ClosePotential()">Close</button>
          <button type="button" class="btn btn-secondary hidden" id="p811-pot-wont" onclick="window.DispatchModule.p811PotentialWontFromSheet()"><span class="material-icons">do_not_disturb_on</span> Won't happen</button>
          <button type="button" class="btn btn-primary" id="p811-pot-save" onclick="window.DispatchModule.p811SavePotential()"><span class="material-icons">save</span> Save</button>
        </div>
      </div>
    </div>

    <!-- TASK-9862 — the expired-locate HARD STOP (job save / crew / add day). -->
    <div class="modal hidden" id="modal-811-stop" role="alertdialog" aria-labelledby="p811-stop-title">
      <div class="modal-content modal-medium" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto">
          <div class="p811-stop">
            <div class="p811-stop-ic"><span class="material-icons" aria-hidden="true">block</span></div>
            <div style="flex:1;min-width:0">
              <h4 id="p811-stop-title">Locate expired</h4>
              <div id="p811-stop-body"></div>
            </div>
          </div>
          <div id="p811-stop-override" class="p811-stop-why hidden">
            <label for="p811-stop-reason">Dispatch anyway: reason (required, saved to job history)</label>
            <textarea id="p811-stop-reason" rows="2" maxlength="300" placeholder="e.g. No digging 10/19, hauling mats only"></textarea>
          </div>
        </div>
        <div class="modal-footer" id="p811-stop-foot" style="flex-shrink:0;display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end"></div>
      </div>
    </div>

    <!-- TASK-9859 — "Start from 811 ticket": tickets waiting for a job. -->
    <div class="modal hidden" id="modal-811-start">
      <style>
        #modal-811-start .p811-start-row { display:flex; align-items:center; gap:12px; width:100%; min-height:56px; padding:8px 12px; margin:0 0 6px; border:1px solid #CBD5E1; border-radius:var(--radius-md); background:var(--bg-card); text-align:left; cursor:pointer; }
        #modal-811-start .p811-start-row:hover { border-color:var(--brand-blue); }
        #modal-811-start .p811-main { flex:1; min-width:0; display:flex; flex-direction:column; gap:2px; }
        #modal-811-start .p811-name { font-weight:600; color:var(--text-primary); overflow-wrap:anywhere; }
        #modal-811-start .p811-meta { font-size:0.8rem; color:var(--text-secondary); }
        #modal-811-start .p811-empty { color:var(--text-secondary); }
      </style>
      <div class="modal-content modal-medium" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">add_task</span> Start from 811 ticket</h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.closeModal('811-start')"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <p style="margin:0 0 10px;color:var(--text-secondary);font-size:0.875rem;">Tickets not on the board yet. Picking one fills the customer, project and job name from the ticket — you can change any of it before saving.</p>
          <div id="p811-start-list"></div>
        </div>
      </div>
    </div>

    <!-- TASK-9648 — "+ Add 811 ticket" picker (job form). Multi-select list like the
         811 window: the job's customer first, unassigned + other customers collapsed. -->
    <div class="modal hidden" id="modal-811-picker">
      <style>
        #modal-811-picker .p811-search { width:100%; min-height:48px; font-size:1rem; margin-bottom:12px; }
        #modal-811-picker .p811-sub { margin:0 0 10px; color:var(--text-secondary); font-size:0.875rem; }
        #modal-811-picker .p811-group { margin-bottom:10px; }
        #modal-811-picker .p811-group > summary { list-style:none; display:flex; align-items:center; gap:8px; min-height:44px; padding:6px 4px; cursor:pointer; font-weight:600; color:var(--text-primary); }
        #modal-811-picker .p811-group > summary::-webkit-details-marker { display:none; }
        #modal-811-picker .p811-group > summary .material-icons { margin-left:auto; color:var(--text-secondary); transition:transform 0.2s ease; }
        #modal-811-picker .p811-group[open] > summary .material-icons { transform:rotate(180deg); }
        #modal-811-picker .p811-count { font-size:0.8rem; font-weight:600; color:var(--text-secondary); background:var(--bg-elevated); border-radius:999px; padding:1px 8px; }
        #modal-811-picker .p811-row { display:flex; align-items:center; gap:12px; min-height:56px; padding:8px 12px; margin:0 0 6px; border:1px solid #CBD5E1; border-radius:var(--radius-md); background:var(--bg-card); cursor:pointer; }
        #modal-811-picker .p811-row.is-on { border-color:var(--brand-blue); background:rgba(59,130,246,0.08); }
        #modal-811-picker .p811-row input[type="checkbox"] { width:22px; height:22px; flex-shrink:0; margin:0; accent-color:var(--brand-blue); }
        #modal-811-picker .p811-pdf { color:#B45309; font-size:22px; flex-shrink:0; }
        #modal-811-picker .p811-main { flex:1; min-width:0; display:flex; flex-direction:column; }
        #modal-811-picker .p811-name { font-weight:600; color:var(--text-primary); overflow-wrap:anywhere; line-height:1.3; }
        #modal-811-picker .p811-meta { font-size:0.8rem; color:var(--text-secondary); }
        #modal-811-picker .p811-empty { color:var(--text-secondary); font-size:0.9rem; margin:8px 0; }
        #modal-811-picker .modal-footer { display:flex; gap:8px; flex-wrap:wrap; }
        #modal-811-picker .modal-footer .btn { min-height:48px; }
        #modal-811-picker .p811-footer-spacer { flex:1; }
      </style>
      <div class="modal-content modal-medium" style="display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;overflow:hidden">
        <div class="modal-header" style="flex-shrink:0">
          <h3><span class="material-icons">assignment</span> Add 811 tickets</h3>
          <button class="btn-icon" aria-label="Close" onclick="window.DispatchModule.closeModal('811-picker')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch">
          <p class="p811-sub" id="p811-subtitle"></p>
          <input type="search" id="p811-search" class="p811-search" aria-label="Search 811 tickets" placeholder="Search tickets" oninput="window.DispatchModule.render811PickerList()">
          <div id="p811-list"></div>
        </div>
        <div class="modal-footer" style="flex-shrink:0">
          <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.upload811FromPicker()">
            <span class="material-icons">upload</span> Upload new ticket
          </button>
          <span class="p811-footer-spacer"></span>
          <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeModal('811-picker')">Cancel</button>
          <button type="button" class="btn btn-primary" id="p811-apply" onclick="window.DispatchModule.apply811Picker()">Done</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// DATA SUBSCRIPTION
// ============================================
// TASK-345 Slice M (2026-05-26): For driver-PIN sessions, date-filter jobs
// + jobDays subscriptions to a 7-day-back window. Legacy multi-week / GAS-
// imported active jobs are picked up via a second query keyed on endDate >=
// 7d-ago (status !== 'finished' filtered client-side to also catch the 88
// TITS jobs where status is undefined). Two queries merged client-side by
// doc ID, fed to DispatchState.jobs. Admin / dispatcher sessions still
// stream ALL jobs unchanged.
//
// Kill switch: flip FAST_PIN_DRIVER_FILTER to false to revert to the legacy
// "stream everything" behavior in one place if a customer reports missing
// jobs on a driver schedule.
//
// FUTURE: Architect COD V2 noted that this filter pattern is duplicated in
// jobboard.js initJobBoard (kept under the JB_FAST_PIN_DRIVER_FILTER flag
// there). Consolidation to a shared util is intentionally deferred — a
// shared data store was Finding #7 in driver-perf-cold-load-2026-05-21.md
// and COD pushed back as premature (Firestore SDK already caches in-memory).
// Revisit if the filter spec drifts between the two files.
const FAST_PIN_DRIVER_FILTER = true;

// TASK-424 #5 (C2): "Load older" — widen the admin live window further back and
// resubscribe. Each tap roughly doubles the lookback (90 → 270 → 630 …), then
// jumps to "all" (full collection, no lower bound). Re-callable subscribeToData
// is the same mechanism the PIN-driver path uses on retry, BUT subscribeToData
// does NOT clear its own unsubscribers (cleanup lives at the caller — see
// initDispatch cleanup at the bottom of this view). So we MUST tear down the
// existing live listeners here before resubscribing, or we leak/duplicate them
// (COD Code Reviewer + Edge Case Hunter). Debounced via _loadOlderBusy so a
// double-tap can't stack two resubscribes.
let _loadOlderBusy = false;
function loadOlderDispatch() {
  if (_loadOlderBusy) return;
  if (!DispatchState.tenantId) return;
  _loadOlderBusy = true;
  const _raw = Number(DispatchState.adminWindowDaysBack);
  const cur = Number.isFinite(_raw) ? _raw : 90; // NOT `|| 90` — 0 = "all" sentinel
  // *3 growth: 90 → 270 → 810 → 2430 → all. Once we'd exceed ~2 years (730d)
  // the next tap goes fully unbounded (0 = "all", no lower bound).
  let next;
  if (cur <= 0) { _loadOlderBusy = false; return; } // already "all"
  else if (cur >= 730) next = 0; // 0 = all (no lower bound)
  else next = cur * 3;
  DispatchState.adminWindowDaysBack = next;
  console.log('[Dispatch] Load older — widening admin window from', cur, 'to', next === 0 ? 'ALL' : next, 'days back');
  // Tear down current live listeners, then resubscribe with the wider window.
  try {
    DispatchState.unsubscribers.forEach(unsub => { try { unsub(); } catch (_) {} });
  } catch (_) {}
  DispatchState.unsubscribers = [];
  subscribeToData(DispatchState.tenantId);
  // Reflect new state in the button label/visibility.
  syncLoadOlderControl();
  // Brief debounce so a frustrated double-tap doesn't stack resubscribes.
  setTimeout(() => { _loadOlderBusy = false; }, 1200);
}

// Show/hide + relabel the "Load older" control based on the current window.
// Hidden ONLY for driver mode. At the "all history" boundary we render a
// terminal DISABLED affordance ("Showing all history") instead of hiding the
// control — a button that silently vanishes after 3 taps reads as broken
// (TASK-1254).
function syncLoadOlderControl() {
  const el = document.getElementById('dispatch-load-older');
  if (!el) return;
  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin;
  const _raw = Number(DispatchState.adminWindowDaysBack);
  const cur = Number.isFinite(_raw) ? _raw : 90; // NOT `|| 90` — 0 = "all"
  // TASK-10195: plain window text + a link-styled action ("Showing last ~3 mo · Load older").
  const winEl = el.querySelector('.load-older-window');
  const actEl = el.querySelector('.load-older-action');
  // Driver mode: the control is admin-only — hide it entirely.
  if (isDriverMode) {
    el.style.display = 'none';
    return;
  }
  el.style.display = '';
  if (cur <= 0) {
    // Terminal state: window fully expanded ("all"). Disabled, non-clickable —
    // the dispatcher sees they've reached the end of history. TASK-1254.
    el.setAttribute('disabled', '');
    el.classList.add('is-terminal');
    if (winEl) winEl.textContent = 'Showing all history';
    if (actEl) actEl.style.display = 'none';
    return;
  }
  // Clickable state: more history available further back.
  el.removeAttribute('disabled');
  el.classList.remove('is-terminal');
  const months = Math.round(cur / 30);
  if (winEl) winEl.textContent = `Showing last ~${months} mo · `;
  if (actEl) actEl.style.display = '';
}

// TASK-424 #5: the Search-Past-Jobs view must search ALL history, not just the
// windowed live board. Do a ONE-SHOT full-collection getDocs (NOT a live
// stream) into separate searchJobs/searchJobDays arrays the moment the
// dispatcher enters the Search view. renderSearchView prefers these when
// present; if the fetch errors it silently falls back to the windowed live
// arrays (search still works, just over the visible window).
async function loadDispatchSearchHistory() {
  if (DispatchState._searchHistoryLoaded || DispatchState._searchHistoryLoading) return;
  if (!DispatchState.tenantId) return;
  // Don't widen driver read scope — search view is dispatcher-only anyway.
  if (window.AppState?.driverModePin || window.AppState?.isPublicAccess) return;
  DispatchState._searchHistoryLoading = true;
  try {
    const tenantId = DispatchState.tenantId;
    const [jobsSnap, jobDaysSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenantId, 'jobs'), orderBy('startDate', 'desc'))),
      getDocs(query(collection(db, 'tenants', tenantId, 'jobDays'), orderBy('date', 'asc'))),
    ]);
    // TASK-4610 — unbounded fetch, so string-dated jobs DO arrive here; normalize
    // so search results land on the right day.
    DispatchState.searchJobs = jobsSnap.docs.map(d => normalizeJobDoc(d.id, d.data()));
    DispatchState.searchJobDays = jobDaysSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    DispatchState._searchHistoryLoaded = true;
    console.log('[Dispatch] Search history loaded (full collection):', DispatchState.searchJobs.length, 'jobs,', DispatchState.searchJobDays.length, 'jobDays');
    // Re-render if the search view is still active so results expand.
    if (DispatchState.viewMode === 'search') renderDispatchBoard();
  } catch (e) {
    console.error('[Dispatch] Search history fetch failed — search will use the windowed live data only:', e);
    // Leave searchJobs null → renderSearchView falls back to live arrays.
  } finally {
    DispatchState._searchHistoryLoading = false;
  }
}

// ─── TASK-4610 — jobs.startDate/endDate MUST be a Firestore Timestamp ────────
// Firestore range filters are TYPE-SCOPED. Verified live 2026-08-25 on a
// throwaway tenant: a job whose startDate is the STRING '2026-08-26' is NOT
// returned by `where('startDate','>=',<Timestamp>)` — which is exactly the
// shape of every windowed board query below. The doc exists, the write throws
// nothing, and the job is invisible to the driver. (Real incident: Ethan could
// not see his 310 mini-ex haul for a day.)
//
// It stayed hidden because the RENDER path already tolerates both types
// (`j.startDate?.toDate?.() || new Date(j.startDate)`) — that tolerance makes
// strings LOOK supported while the query layer silently drops them. The doc
// never arrives; there is nothing to render.
//
// Two halves, and both are needed:
//   1. coerceJobTimestamp() on every write — the app can never author one.
//   2. subscribeStringDatedJobs() on read — the app is NOT the only writer.
//      Seed scripts, GAS imports and Cloud Functions go through the Admin SDK
//      (which bypasses firestore.rules entirely, so a rule cannot stop them),
//      and one bad row is a driver who does not show up for work.
//
// Anchored NOON local, never midnight: `new Date('2026-08-26')` is parsed as
// UTC midnight = 2026-08-25 19:00 CDT and renders on the WRONG DAY. Same
// convention as jobDays.date — see memory/lesson_jobday_date_anchors_to_noon_local.
const _JOB_DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function coerceJobTimestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value?.toDate === 'function') return value;              // already a Timestamp
  let d = null;
  if (value instanceof Date) d = value;
  else if (typeof value === 'number' && Number.isFinite(value)) d = new Date(value);
  else if (typeof value === 'string') {
    const s = value.trim();
    // Date-only → noon LOCAL. Anything with a time component already carries
    // its own offset semantics; do NOT re-anchor it.
    d = _JOB_DATE_ONLY_RE.test(s) ? new Date(s + 'T12:00:00') : new Date(s);
  }
  if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return null;
  return Timestamp.fromDate(d);
}
window.__coerceJobTimestamp = coerceJobTimestamp; // exposed for tests/console probes

// ─── TASK-4781 — human-readable JOB NUMBERS (`J-1042`) ──────────────────────
//
// A job was identified only by an EDITABLE title plus a Firestore id nobody can
// say out loud. 51% of TITS jobs share a normalized title with another job for
// the same customer (24x "stealth"), so "the stealth job" is ambiguous and
// `jobAuditLog` shows JOB_UPDATE x1046 — titles move. Every sibling record
// already has a number (tickets `ticketNo`, invoices `invoiceNo-*`); jobs were
// the hole.
//
// COPIED, NOT INVENTED. This is a faithful mirror of billing.js
// getNextTicketNumber / ticketGateway.js getNextTicketNumberServer, down to the
// retry count and the seed. Read the long header on billing.js
// getNextTicketNumber (~:26800) for the full rationale; the short version and
// the JOB-SPECIFIC deltas are here.
//
// ── WHY A COUNTER DOC AND NOT max+1 ──
// Reading the max and adding one is the bug this repo has already paid for
// twice — once on ticket numbers (TASK-1339/1354) and once on TASK ids
// (scripts/next-task.cjs, where two sessions both picked max+1 and git merged
// both cleanly with NO conflict). Two dispatchers saving a job in the same
// second both read the same max and both write it. So allocation is a
// `runTransaction` on ONE document, `tenants/{tid}/_counters/jobNo`: Firestore
// serializes transactions touching the same doc and re-runs the loser against
// the committed value, so N concurrent callers get N distinct numbers. That
// guarantee is between callers of THIS allocator (client) and its server twin
// (getNextJobNumberServer) — both transact the SAME doc, exactly as ticketNo
// does, because jobs are minted from both sides (the SMS assistant creates
// server-side).
//
// ── PER TENANT, NOT GLOBAL ──
// Deliberate. A global sequence would show a 12-truck carrier "J-48213", which
// tells a customer our volume, not theirs. It would also be a single hot
// document across every tenant — Firestore caps a doc at ~1 sustained write/sec,
// so a global counter is a scaling wall as well as a privacy leak. Every sibling
// counter here (`ticketNo`, `slips`, `invoiceNo-*`) is already per-tenant.
//
// ── FLAT, NOT ENCODED ──
// No year or customer segment. Chris's default (task row): encoded codes rot —
// a job that slips into January, a customer that gets merged. Flat -> encoded is
// additive later; encoded -> flat is a renumber. `jobNumber` is stored as an
// INT and rendered with the prefix by formatJobNumber(); storing "J-1042" would
// make the prefix a migration.
//
// ── SEED 1000 -> the first job on an empty tenant is J-1001 ──
// Same seed as TICKET_NO_SEED so the two sequences read as siblings. They can
// coincide numerically on a brand-new tenant; the `J-` prefix is what keeps
// "job ten-forty-two" and "ticket ten-forty-two" apart when both are said in
// the same sentence, which is why the prefix exists.
//
// ── IMMUTABILITY: SELF-HEAL is the guard shipped here ──
// Stated explicitly because the task row demands the choice be named. Rules do
// NOT pin `jobNumber` today. A rules clause is RECOMMENDED (mirror the
// `createdBy` create-pin + update-immutability pair at rules ~:4513) but rules
// can never be the ONLY guard: the SMS assistant, any backfill and every Cloud
// Function write through the Admin SDK, which bypasses firestore.rules entirely
// ([[lesson_admin_sdk_op_must_mirror_rules_whole_chain]]). So the enforced
// guarantee is the same one ticketNo relies on — the max-probe runs on EVERY
// allocation and re-raises the counter to max(counter, observedMax), so a number
// minted outside the counter can never be re-issued, and a lowered or rolled-back
// counter repairs itself on the next call.
//
// HONEST LIMIT (same as ticketNo): the probe is OUTSIDE the transaction because
// the Web SDK cannot run a query inside one. Self-healing is EVENTUAL. A number
// planted by a NON-allocator writer that commits between our probe and our
// transaction is unseen by THIS allocation and could collide once; the next
// allocation's probe corrects the sequence. Between allocator callers, collision
// is impossible.
//
// ── NOT A JOIN KEY (COD Independent) ──
// `jobNumber` is a HUMAN LABEL. The Firestore document id stays the join key for
// tickets, jobDays, clock entries and invoices. Nothing in this file, or any
// caller, may query or match on `jobNumber`.
const JOB_NO_SEED = 1000; // → the first job on an empty tenant is J-1001

// Display form. Single source of truth for the prefix — keep every surface on
// this, never hand-concatenate 'J-' + n. Returns '' (not 'J-undefined') for the
// ~1,543 pre-existing jobs that carry no number, so an unnumbered job renders
// exactly as it does today.
function formatJobNumber(job) {
  const n = (job && typeof job === 'object') ? job.jobNumber : job;
  return Number.isFinite(n) ? `J-${n}` : '';
}

// ── RETIRED RULE — read before "restoring" it ──
// TASK-4781 (a925d2c6, 2026-09-08) shipped the number BESIDE the title and
// deliberately NOT as a placard token, on Chris's call "Mike gets an
// identifier, Lacey keeps her name", so a tenant could never make the number
// REPLACE the title. TASK-9278 (2026-09-22) REPEALED that, also on Chris's call:
// "i cant predict all things, if this is weirdly what someones wants who am I to
// prevent them from true happiness." Job number is now the 8th placard token
// (PLACARD_TOKEN_DEFS). A tenant may move it, untick it, or leave it as the ONLY
// ticked field, so a card reading just `J-1054` is an ALLOWED arrangement. Do
// not add a "needs one non-number field" save guard or skip it when picking
// the day-details headline; both were considered and rejected in TASK-9278.
//
// This badge is still what the three cards render: on the legacy path (tenant
// never saved a token order) it is prepended exactly as TASK-4781 shipped it,
// and on the token path getPlacardTitleHtml() emits it where the token sits.
//
// 🔴 THREE CARD RENDERERS, NOT ONE. The board has `.job-card` (Week/Month grid —
// the DEFAULT view), `.dispatch-job-card` (List), and a third copy of that markup
// in the Search results. Wiring only the List card shipped a feature that was
// invisible in the view a dispatcher actually opens every morning. Caught by an
// E2E assertion on a REAL card; the earlier version of that test asserted on
// formatJobNumber()'s return value and passed while the board showed nothing.
//
// Inline-styled on purpose — styles.css is held by another lane this session, and
// a class that resolves to nothing renders an unstyled badge in production.
// `var(--text-secondary)` per the contrast rule (never --text-muted on light).
// Returns '' for the ~1,543 legacy jobs with no number, so their cards are
// byte-identical to today.
// `marginCss` exists for TASK-9278: a badge placed mid-title or last by the
// token order needs its gap on the other side. The default keeps the legacy
// output byte-identical.
function jobNumberBadgeHtml(job, marginCss = 'margin-right:6px;') {
  const s = formatJobNumber(job);
  if (!s) return '';
  return `<span class="dispatch-job-number" title="Job number" style="flex-shrink:0;${marginCss}font-size:0.72rem;font-weight:700;color:var(--text-secondary);font-variant-numeric:tabular-nums;">${escapeHtml(s)}</span>`;
}

// orderBy+limit(1) deliberately omits docs with no `jobNumber` — Firestore drops
// them from an ordered query, which is what we want: unnumbered legacy jobs and
// `jobNumberPending` jobs must never anchor the sequence.
async function _probeMaxJobNumber(tenant) {
  const snap = await getDocs(query(
    collection(db, 'tenants', tenant, 'jobs'),
    orderBy('jobNumber', 'desc'),
    limit(1)
  ));
  if (snap.docs.length === 0) return null;
  const n = snap.docs[0].data().jobNumber;
  return Number.isFinite(n) ? n : null;
}

// 🔴 THE RETRY IS NOT OPTIONAL — MEASURED, NOT ASSUMED (TASK-4781).
//
// The counter doc's own firestore.rules guard is MONOTONIC:
//   allow update: ... && request.resource.data.value > resource.data.value
// When two allocations race, the loser's commit is evaluated against the state
// the WINNER just wrote, so `next > current` is false and Firestore returns
// **permission-denied** — which the Web SDK treats as FATAL and does not retry,
// instead of the ABORTED it would normally re-run the transaction on.
//
// Measured on a throwaway tenant, 5 simultaneous allocations, one browser:
//   without this retry → 1 succeeded, 4 permission-denied
// The SHIPPED ticket allocator has the identical shape and was measured in the
// same run: 4 succeeded, 1 permission-denied. So this is a PRE-EXISTING defect in
// `getNextTicketNumber` / `getNextTicketNumberServer`, not something jobs
// introduced — reported separately; a driver clocking out at the same second as
// another driver can lose their ticket number this way.
//
// WHAT THE COUNTER STILL GUARANTEES, precisely: no two allocations ever return
// the SAME number. Zero duplicates in every measured run, with or without the
// retry. What it did NOT guarantee is that every caller gets one at all. This
// loop closes that half.
//
// A retry is SAFE here specifically BECAUSE the counter is monotonic: each
// attempt re-probes and re-reads, then writes current+1. It can never re-issue a
// number, only take the next free one.
//
// 🔴 TASK-4825 — THE BUDGET THIS FILE SHIPPED WAS TOO SMALL, AND THE CLAIM THAT
// IT WAS NOT DID NOT REPRODUCE. The loop here was a private copy with
// `_JOBNO_MAX_ATTEMPTS = 24` and NO wall clock. Its own lane reported "zero
// failures at N=50"; an independent lane re-measured the same curve and got
// **17 of 50 still failing**, because each contended round lets only ~1.4
// callers through, so 24 rounds cannot drain 50 contenders. The sibling ticket
// allocator had already been raised to 64 with a 30s wall-clock bound
// (TASK-4823). Both numbers now come from ONE place — utils/counterRetry.js —
// because the whole reason they disagreed is that they lived in two files.
//
// TWO behavioural changes come with the shared loop, both deliberate:
//   • 24 → 64 attempts, so N=50 actually drains.
//   • a 30s WALL-CLOCK bound is added, which the local copy did not have. An
//     attempt count cannot bound latency (it does not see the transaction
//     round-trip), and this file's old comment priced the worst case at "~24
//     rounds x <=280ms ≈ 4s" — a backoff-only estimate. Measured at N=50 on one
//     counter the slowest caller was 58-73 SECONDS, because the real ceiling is
//     Firestore's ~1 write/sec per document, not the retry curve. So the honest
//     trade is stated rather than hidden: a dispatcher waits at most ~30s of
//     retry-starts and then takes the degraded path below (allocateJobNumberSafe
//     → `jobNumberPending`), instead of being frozen for over a minute.
//
// ⚠️ The bound is a RETRY-START budget, not a hard ceiling: the attempt already
// in flight is never abandoned. Full argument in utils/counterRetry.js.
//
// ⚠️ CORRECTION to the header above: it claims this allocator's guarantee holds
// "between callers of THIS allocator (client) and its server twin
// (getNextJobNumberServer)". There is no such function — `getNextJobNumberServer`
// does not exist anywhere in `functions/`; it is TASK-4781 Slice 2, unshipped.
// Today jobs are minted from the client only, so the guarantee is client-to-
// client. When Slice 2 lands it must transact THIS SAME doc, and it must NOT use
// counterRetry (Admin SDK bypasses rules — see that file).

async function allocateJobNumber(tenant) {
  if (!tenant) throw new Error('allocateJobNumber: no tenant');

  // THE PROBE RUNS ONCE, OUTSIDE THE RETRY LOOP — deliberately.
  // It is a whole query round-trip and it exists to SEED and SELF-HEAL, not to
  // resolve contention: by the time we are retrying, the counter has already
  // moved PAST any max the probe could have seen, so re-probing every round
  // would cost a query per round and change nothing. Keeping it out here is what
  // makes each retry cheap enough for the budget above to actually drain.
  let observedMax = null;
  let probeErr = null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      observedMax = await _probeMaxJobNumber(tenant);
      probeErr = null;
      break;
    } catch (e) {
      probeErr = e;
      if (attempt === 0) await new Promise((r) => setTimeout(r, 400));
    }
  }

  // A genuine authz failure (this user may not write counters at all) looks
  // identical to a lost race from inside the loop. Both bounds mean the honest
  // denial still surfaces, just later — and the caller degrades (see
  // allocateJobNumberSafe) rather than trapping the dispatcher. `lastErr` is
  // re-thrown by the helper, never swallowed.
  return await allocateCounterWithRetry('job number',
    () => _allocateJobNumberTxn(tenant, observedMax, probeErr));
}

async function _allocateJobNumberTxn(tenant, observedMax, probeErr) {
  const counterRef = doc(db, 'tenants', tenant, '_counters', 'jobNo');
  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const current = snap.exists() ? snap.data().value : null;
    const haveCounter = Number.isFinite(current);

    if (!haveCounter && probeErr) {
      // No counter to read AND no max to seed from — nothing safe to return.
      // Plain Error so the SDK treats it as fatal, not a retryable txn error.
      // 🔴 NEVER `return 1` here: that is the exact bug the ticket mirror shipped
      // and paid for — on a tenant at ~100,400 it minted "#1", then "#2" forever.
      throw new Error('Could not allocate a job number: ' + (probeErr?.message || probeErr));
    }

    let base;
    if (haveCounter || observedMax != null) {
      base = Math.max(
        haveCounter ? current : Number.NEGATIVE_INFINITY,
        observedMax != null ? observedMax : Number.NEGATIVE_INFINITY
      );
    } else {
      base = JOB_NO_SEED; // genuinely empty sequence — no counter, no numbered job
    }

    const next = base + 1;
    tx.set(counterRef, {
      value: next,
      updatedAt: Timestamp.now(),
      // seededFrom is forensics: "did this counter start from the live max or
      // from the empty-tenant seed", without re-deriving it later.
      ...(snap.exists() ? {} : { createdAt: Timestamp.now(), seededFrom: base })
    }, { merge: true });
    return next;
  });
}

// DEGRADE, DON'T BLOCK — and the degradation is queryable, not invisible.
//
// The judgement call, stated so it can be argued with: refusing to create a job
// because a counter read blipped is an operational outage on the single most
// core flow in the product. A job that saves without a number is recoverable in
// one backfill pass; a job that would not save at all is a dispatcher standing
// at a keyboard while a crew waits.
//
// The objection to holes is that they are INVISIBLE — a missing number reads as
// a deleted job. So a degraded save is stamped `jobNumberPending: true`, which
// makes the hole a query, not a mystery, and the backfill script picks those up
// on its next run alongside the legacy jobs. Same shape as `ticketNoPending`.
async function allocateJobNumberSafe(tenant) {
  try {
    const n = await allocateJobNumber(tenant);
    return { jobNumber: n, jobNumberPending: false };
  } catch (e) {
    console.error('[Dispatch] TASK-4781: job number allocation failed — saving without a number (jobNumberPending)', e);
    try {
      window.showToast?.('Job saved, but it could not be given a job number yet.', 'info');
    } catch (_) { /* toast is cosmetic; never let it fail the save */ }
    return { jobNumber: null, jobNumberPending: true };
  }
}

// Warn ONCE per job id — a snapshot re-fires on every unrelated field change
// and this must not turn into a console flood.
const _warnedStringDateJobs = new Set();

// Ingest normalizer. Every place this module turns a job snapshot into state
// runs through here, so DispatchState.jobs is guaranteed Timestamp-typed no
// matter what is actually stored. Display-only — it does NOT write Firestore.
// Repairing the stored doc is Chris's call, not a side effect of opening a board.
function normalizeJobDoc(id, data) {
  const s = data.startDate, e = data.endDate;
  const sBad = s !== null && s !== undefined && typeof s.toDate !== 'function';
  const eBad = e !== null && e !== undefined && typeof e.toDate !== 'function';
  if (!sBad && !eBad) return { id, ...data };
  const out = { id, ...data };
  if (sBad) out.startDate = coerceJobTimestamp(s);
  if (eBad) out.endDate = coerceJobTimestamp(e);
  out._dateTypeRepaired = true;
  if (!_warnedStringDateJobs.has(id)) {
    _warnedStringDateJobs.add(id);
    console.warn('[Dispatch] TASK-4610 — job', id, 'stores a NON-Timestamp date. Coerced for display only; the stored doc is still wrong and is invisible to the windowed board query. Run scripts sweep + tell Chris.', { startDate: s, endDate: e });
  }
  return out;
}

// The quarantine read. Strings sort AFTER timestamps in Firestore's value
// order, and a range filter only matches its OWN type — so `startDate >= ''`
// returns EXACTLY the string-typed startDates. Verified live: it matches
// neither numbers, maps, null, nor a missing field. In a healthy tenant that
// is ZERO documents, so the cost is two extra listeners and one billed read
// each on connect — not a doubled board. It NEVER gates the board's publish:
// if it errors or never lands, the board renders exactly as it does today.
//
// BOTH fields are swept, not just startDate (COD Code Reviewer). A doc with a
// Timestamp startDate OLDER than the cutoff and a STRING endDate inside it is
// invisible to all three of Query 1 (start < cutoff), Query 2 (endDate is not
// a Timestamp) and a startDate-only quarantine (startDate is not a string).
// That mixed shape is exactly what a partial hand-repair produces.
//
// Deliberately UNBOUNDED (no upper bound on the range). A bound like
// `< cutoffISO` would only be correct if every bad value were ISO-formatted —
// 'Aug 26 2026' sorts nowhere near it and would be silently missed again,
// which is the exact failure mode being fixed. Unbounded costs one read per
// corrupt job and zero in a healthy tenant, so the price is paid only by a
// tenant that already has a problem. TASK_4610_DIRTY_TENANT_WARN makes that
// loud instead of quietly expensive.
//
// Calls back with a snapshot-shaped `{ docs }` union so the two call sites
// stay identical to a plain onSnapshot handler.
const TASK_4610_DIRTY_TENANT_WARN = 50;
function subscribeStringDatedJobs(tenantId, onSnap) {
  const byField = { startDate: [], endDate: [] };
  const emit = () => {
    const seen = new Map();
    for (const d of byField.startDate.concat(byField.endDate)) if (!seen.has(d.id)) seen.set(d.id, d);
    if (seen.size >= TASK_4610_DIRTY_TENANT_WARN) {
      console.warn(`[Dispatch] TASK-4610 — ${seen.size} jobs in this tenant store a NON-Timestamp date. The board is paying a read for each one on every load. Run the sweep and repair the data.`);
    }
    onSnap({ docs: Array.from(seen.values()) });
  };
  const one = (field) => {
    try {
      return onSnapshot(
        query(collection(db, 'tenants', tenantId, 'jobs'), where(field, '>=', ''), orderBy(field, 'asc')),
        (snap) => { byField[field] = snap.docs; emit(); },
        (error) => console.warn(`[Dispatch] TASK-4610 ${field} string-date sweep unavailable (board unaffected):`, error?.message || error)
      );
    } catch (e) {
      console.warn(`[Dispatch] TASK-4610 ${field} string-date sweep could not subscribe:`, e?.message || e);
      return () => {};
    }
  };
  const unsubStart = one('startDate');
  const unsubEnd = one('endDate');
  return () => { try { unsubStart(); } catch (_) {} try { unsubEnd(); } catch (_) {} };
}

// Apply the SAME window the bad type excluded the doc from — a rescued job
// must not surface anywhere a correctly-typed one wouldn't. Mirrors Query 1
// (startDate >= cutoff, no status filter) and Query 2 (endDate >= cutoff,
// minus 'finished'). This is a repair, not a widening.
// TASK-4646 — `includeFinished` mirrors the same widening applied to the real Query 2
// on the ADMIN arm only. The rescue path has to track whatever the typed queries do or
// a string-dated job would be treated more harshly than a well-formed one purely for
// having been typed wrong by an outside writer. PIN drivers pass false and keep the
// old 7d-window-minus-finished behavior exactly.
function stringDatedJobsInWindow(snapshot, cutoffTs, includeFinished = false) {
  const cutMs = cutoffTs?.toMillis?.() ?? 0;
  const out = new Map();
  snapshot.docs.forEach(d => {
    const job = normalizeJobDoc(d.id, d.data());
    const sMs = job.startDate?.toMillis?.() ?? null;
    const eMs = job.endDate?.toMillis?.() ?? null;
    const inQ1 = sMs !== null && sMs >= cutMs;
    const inQ2 = eMs !== null && eMs >= cutMs && (includeFinished || job.status !== 'finished');
    if (inQ1 || inQ2) out.set(d.id, job);
  });
  return out;
}

function subscribeToData(tenantId) {
  // For access-token sessions, membership doc may not be visible yet — retry once on permission error
  let _jobsRetried = false;
  let _jobDaysRetried = false;
  // Prevent infinite toast spam — onSnapshot error callbacks fire on every Firestore retry
  let _errorToastShown = false;

  const _isPinDriver = FAST_PIN_DRIVER_FILTER && window.AppState?.driverModePin === true;

  // Compute window bounds ONCE per subscribeToData call (not per snapshot).
  // Resubscribe happens on access-token retry / view re-init so window stays fresh.
  // Snap to start-of-day local time (not rolling now - 7*24h) so day-noon-
  // normalized startDate/endDate docs (TITS convention) align consistently
  // with the cutoff regardless of what hour of day the driver loads.
  const _now = new Date();
  const _todayStartLocal = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  const _sevenDaysAgo = new Date(_todayStartLocal.getTime() - 7 * 24 * 60 * 60 * 1000);
  const _sevenDaysAgoTs = Timestamp.fromDate(_sevenDaysAgo);

  // TASK-424 #5 (C2): admin window cutoff. Same machinery as the driver's 7d
  // window but with a configurable, much wider lookback (default 90d, widened
  // by the "Load older" control). adminWindowDaysBack <= 0 means "all" (no
  // lower bound) — used after the dispatcher taps "Load older" enough times.
  // NB: parse with Number.isFinite (NOT `|| 90`) — `0` is a valid sentinel for
  // "all" and `0 || 90` would coerce it back to 90, silently breaking the
  // unbounded branch (COD Code Reviewer blocker).
  // TASK-4567 — that parse + cutoff math now lives in the PURE computeAdminWindow()
  // (identical arithmetic, same _now) so the far-past add-day confirm gates on THIS
  // cutoff instead of a second hardcoded 90 that would drift from "Load older".
  const _adminWindow = computeAdminWindow(DispatchState.adminWindowDaysBack, _now);
  const _adminDaysBack = _adminWindow.daysBack;
  const _adminWindowAll = _adminWindow.all;
  const _adminCutoff = _adminWindow.cutoff;
  const _adminCutoffTs = Timestamp.fromDate(_adminCutoff);

  // Subscribe to jobs
  if (_isPinDriver) {
    console.log('[Dispatch] TASK-345 Slice M: PIN-driver date-filtered jobs subscription (window: 7d back, +active-endDate fallback)');

    // Track per-source results so a partial fail doesn't blow up the merge.
    const _jobsRecent = new Map();
    const _jobsLegacy = new Map();
    const _jobsStringDated = new Map(); // TASK-4610 quarantine — normally empty
    let _gotRecent = false;
    let _gotLegacy = false;

    const _mergeAndPublish = () => {
      if (!_gotRecent || !_gotLegacy) return; // wait for both initial snapshots
      // Union by doc.id, dedupe (recent wins — same data, but explicit).
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      // TASK-4610 — string-dated jobs the two range queries above CANNOT see.
      // Lowest precedence: a correctly-typed copy always wins.
      for (const [id, job] of _jobsStringDated) if (!merged.has(id)) merged.set(id, job);
      DispatchState._jobsLoaded = true;
      DispatchState._jobsComplete = true;
      DispatchState.jobs = Array.from(merged.values());
      console.log('[Dispatch] Jobs updated (PIN-filtered):', DispatchState.jobs.length, '(recent:', _jobsRecent.size, '+ legacy-active:', _jobsLegacy.size, ')');
      renderDispatchBoard();
    };

    // Query 1: recent + future jobs (startDate >= 7 days ago).
    const recentJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('startDate', '>=', _sevenDaysAgoTs),
      orderBy('startDate', 'desc')
    );

    const unsubRecentJobs = onSnapshot(recentJobsQuery, (snapshot) => {
      _jobsRecent.clear();
      snapshot.docs.forEach(d => _jobsRecent.set(d.id, normalizeJobDoc(d.id, d.data())));
      _gotRecent = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[Dispatch] Recent jobs subscription error:', error);
      if (!_jobsRetried && (window.AppState?.isPublicAccess || window.AppState?.driverModePin)) {
        _jobsRetried = true;
        console.log('[Dispatch] Retrying jobs subscription in 2s...');
        setTimeout(() => { if (DispatchState.tenantId === tenantId) subscribeToData(tenantId); }, 2000); // P0 1019 COD — never re-subscribe a company we left
        return;
      }
      _gotRecent = true; // unblock the merge with whatever legacy fetched
      _mergeAndPublish();
      renderDispatchBoard();
      if (DispatchState.jobs.length === 0 && !_errorToastShown) {
        _errorToastShown = true;
        window.showToast?.('Unable to load data - check connection', 'error');
      }
    });
    DispatchState.unsubscribers.push(unsubRecentJobs);

    // Query 2 (legacy multi-week + recently-closed fallback): endDate >= 7d ago.
    // Catches (a) GAS-imported long-running jobs whose startDate is months
    // ago but endDate is today/future, AND (b) jobs that closed within the
    // last 7d but whose startDate is older than the recent window. The
    // jobDays window is also 7d-back, so a job whose endDate is <7d-old has
    // jobDays inside the window, and renderers (getJobsForDate at
    // jobboard.js:1603, similar in dispatch driver path) need the job-doc
    // present to dereference the jobDay. Without this width the renderer
    // silently drops valid scheduled rows.
    //
    // Status filter is CLIENT-SIDE (drop 'finished'): 88 TITS jobs have
    // status===undefined (legacy schema per docs/modules/dispatch.md), so
    // `where('status','==','active')` would silently drop them.
    //
    // TASK-4646 — KEEP THIS DROP. The admin arm's twin was removed so dispatchers get
    // their calendar history back; a PIN driver has no such need and no chip to turn it
    // off with (the Completed chip is .dispatcher-only). Leaving it here is what stops a
    // driver's board from sprouting months of closed work. This asymmetry is deliberate
    // — do not "make the two arms consistent".
    const legacyJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('endDate', '>=', _sevenDaysAgoTs),
      orderBy('endDate', 'asc')
    );

    const unsubLegacyJobs = onSnapshot(legacyJobsQuery, (snapshot) => {
      _jobsLegacy.clear();
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'finished') return; // archived — skip
        _jobsLegacy.set(d.id, normalizeJobDoc(d.id, data));
      });
      _gotLegacy = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[Dispatch] Legacy-active jobs subscription error:', error);
      _gotLegacy = true; // unblock the merge — recent window alone is still useful
      _mergeAndPublish();
    });
    DispatchState.unsubscribers.push(unsubLegacyJobs);

    // Query 3 (TASK-4610 quarantine): jobs whose startDate was stored as a
    // STRING. Queries 1 and 2 are range filters against a Timestamp and are
    // therefore structurally incapable of returning them — this is the ONLY
    // way a driver ever sees a job an outside writer typed wrong. Returns
    // zero docs in a healthy tenant and never gates _mergeAndPublish.
    const unsubStringDatedJobs = subscribeStringDatedJobs(tenantId, (snapshot) => {
      _jobsStringDated.clear();
      for (const [id, job] of stringDatedJobsInWindow(snapshot, _sevenDaysAgoTs)) _jobsStringDated.set(id, job);
      if (_jobsStringDated.size) {
        console.warn('[Dispatch] TASK-4610 — rescued', _jobsStringDated.size, 'in-window job(s) with a non-Timestamp startDate/endDate:', Array.from(_jobsStringDated.keys()));
      }
      _mergeAndPublish();
    });
    DispatchState.unsubscribers.push(unsubStringDatedJobs);

    // UX timeout (Skeptic 🔴 V2 considered — net keep, see comment): if
    // EITHER query lags (slow cell, cold edge), publish whatever we have
    // after 3s rather than stalling the spinner. _mergeAndPublish is
    // idempotent — when the late query DOES land it republishes the
    // union. The Skeptic V2 concern about wiping user interactions
    // doesn't apply because this is a read-only schedule view (no input
    // state to lose); UX Lead explicitly endorsed this for spotty-cell
    // drivers. **HOTFIX 2026-05-28 (Chris bug report: stuck on "Loading
    // dispatch…" spinner):** the original condition required
    // `_gotRecent && !_gotLegacy` — so if BOTH queries hung (rules block,
    // network fail) the timeout did nothing and the spinner stayed
    // forever. Now fires if EITHER snapshot hasn't landed, publishes the
    // union of whatever we got. Empty result is acceptable UX
    // (renderDispatchBoard shows the empty calendar) vs. infinite spinner
    // which strands the driver.
    setTimeout(() => {
      if (_gotRecent && _gotLegacy) return; // both arrived in time — nothing to do
      if (DispatchState.jobs && DispatchState.jobs.length > 0) return; // already populated by something else
      console.warn('[Dispatch] Jobs subscription slow after 3s — publishing partial set so user isn\'t stranded', {
        gotRecent: _gotRecent,
        gotLegacy: _gotLegacy,
        recentSize: _jobsRecent.size,
        legacySize: _jobsLegacy.size,
      });
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      // DELIBERATELY does NOT set _jobsComplete: this is the partial set. The board
      // may render it; Notify Crew must not text it. Do not "fix" this omission.
      DispatchState._jobsLoaded = true;
      DispatchState.jobs = Array.from(merged.values());
      // Mark both as "got" so any late snapshot landing AFTER this still
      // publishes via _mergeAndPublish (idempotent re-render).
      _gotRecent = true;
      _gotLegacy = true;
      renderDispatchBoard();
    }, 3000);
  } else if (_adminWindowAll) {
    // Admin "Load older → all" — dispatcher widened the window to the full
    // history. Stream the entire collection (the original pre-TASK-424
    // behavior), used only after explicit opt-in via "Load older".
    const jobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      orderBy('startDate', 'desc')
    );

    const unsubJobs = onSnapshot(jobsQuery, (snapshot) => {
      DispatchState._jobsLoaded = true;
      DispatchState._jobsComplete = true;
      // TASK-4610 — the unbounded stream DOES return string-dated jobs (no range
      // filter to exclude them), so normalizing here is what makes them land on
      // the right DAY instead of the day before (UTC-midnight parse).
      DispatchState.jobs = snapshot.docs.map(doc => normalizeJobDoc(doc.id, doc.data()));
      console.log('[Dispatch] Jobs updated (admin, ALL):', DispatchState.jobs.length);
      renderDispatchBoard();
    }, (error) => {
      console.error('[Dispatch] Jobs subscription error:', error);
      if (!_jobsRetried && (window.AppState?.isPublicAccess || window.AppState?.driverModePin)) {
        _jobsRetried = true;
        console.log('[Dispatch] Retrying jobs subscription in 2s...');
        setTimeout(() => { if (DispatchState.tenantId === tenantId) subscribeToData(tenantId); }, 2000); // P0 1019 COD — never re-subscribe a company we left
        return;
      }
      // TASK-4588 — a terminal error must not leave the guarded views spinning,
      // but it must NOT be reported as "loaded" either: isDispatchScheduleReady()
      // is what unlocks Notify Crew, and a jobless crew text is irreversible.
      // Renders fall through on _dispatchLoadFailed; irreversible paths do not.
      DispatchState._dispatchLoadFailed = true;
      renderDispatchBoard();
      if (DispatchState.jobs.length === 0 && !_errorToastShown) {
        _errorToastShown = true;
        window.showToast?.('Unable to load data - check connection', 'error');
      }
    });

    DispatchState.unsubscribers.push(unsubJobs);
  } else {
    // Admin / non-PIN session — TASK-424 #5 (C2): windowed jobs stream.
    // Mirrors the PIN-driver dual-query (recent + legacy-active) but with the
    // wider admin lookback (_adminCutoffTs). The `>=` lower bound has NO upper
    // bound, so ALL upcoming/future jobs are always present; only jobs OLDER
    // than the cutoff (whose endDate is also older — i.e. genuinely past) are
    // excluded. "Load older" widens _adminDaysBack and resubscribes; the
    // Search-Past-Jobs view does its own full-history fetch so it is unaffected.
    console.log('[Dispatch] TASK-424: admin date-filtered jobs subscription (window:', _adminDaysBack, 'd back, +active-endDate fallback)');

    const _jobsRecent = new Map();
    const _jobsLegacy = new Map();
    const _jobsStringDated = new Map(); // TASK-4610 quarantine — normally empty
    let _gotRecent = false;
    let _gotLegacy = false;
    let _fellBackToUnbounded = false;

    const _mergeAndPublish = () => {
      if (!_gotRecent || !_gotLegacy) return;
      const merged = new Map(_jobsLegacy);
      for (const [id, job] of _jobsRecent) merged.set(id, job);
      // TASK-4610 — string-dated jobs the two range queries CANNOT see.
      for (const [id, job] of _jobsStringDated) if (!merged.has(id)) merged.set(id, job);
      DispatchState._jobsLoaded = true;
      DispatchState._jobsComplete = true;
      DispatchState.jobs = Array.from(merged.values());
      console.log('[Dispatch] Jobs updated (admin-windowed):', DispatchState.jobs.length, '(recent:', _jobsRecent.size, '+ legacy-active:', _jobsLegacy.size, ')');
      renderDispatchBoard();
    };

    // NO-REGRESSION FALLBACK: if a windowed query errors (e.g. a not-yet-built
    // index, or an unexpected schema issue), fall back to the ORIGINAL
    // unbounded stream so the board never blanks. Fires at most once.
    const _fallbackToUnbounded = (error) => {
      if (_fellBackToUnbounded) return;
      _fellBackToUnbounded = true;
      console.warn('[Dispatch] Windowed admin jobs query failed — falling back to unbounded full-collection stream so the board does not blank.', error);
      const unsubFallback = onSnapshot(
        query(collection(db, 'tenants', tenantId, 'jobs'), orderBy('startDate', 'desc')),
        (snapshot) => {
          DispatchState._jobsLoaded = true;
          DispatchState._jobsComplete = true;
          DispatchState.jobs = snapshot.docs.map(doc => normalizeJobDoc(doc.id, doc.data()));
          console.log('[Dispatch] Jobs updated (admin fallback, unbounded):', DispatchState.jobs.length);
          renderDispatchBoard();
        },
        (err2) => {
          console.error('[Dispatch] Admin fallback jobs subscription error:', err2);
          DispatchState._dispatchLoadFailed = true;   // TASK-4588 — renders fall through; SMS/export do not
          renderDispatchBoard();
          if (DispatchState.jobs.length === 0 && !_errorToastShown) {
            _errorToastShown = true;
            window.showToast?.('Unable to load data - check connection', 'error');
          }
        }
      );
      DispatchState.unsubscribers.push(unsubFallback);
    };

    // Query 1: recent + future jobs (startDate >= cutoff).
    const recentJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('startDate', '>=', _adminCutoffTs),
      orderBy('startDate', 'desc')
    );
    const unsubRecentJobs = onSnapshot(recentJobsQuery, (snapshot) => {
      _jobsRecent.clear();
      snapshot.docs.forEach(d => _jobsRecent.set(d.id, normalizeJobDoc(d.id, d.data())));
      _gotRecent = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[Dispatch] Admin recent jobs subscription error:', error);
      _fallbackToUnbounded(error);
    });
    DispatchState.unsubscribers.push(unsubRecentJobs);

    // Query 2 (legacy multi-week + recently-closed fallback): endDate >= cutoff.
    // Catches long-running jobs whose startDate is older than the window but
    // whose endDate is within it, so renderers can dereference their in-window
    // jobDays.
    //
    // TASK-4646 — the client-side `drop 'finished'` that used to live here is GONE
    // on the ADMIN arm. It was the last place a finished job could be starved out of
    // DispatchState.jobs entirely, which meant getJobsForDate() had nothing to
    // dereference and the day painted empty no matter what any chip said. The PIN-
    // driver arm above KEEPS its drop — see the comment there.
    //
    // Cost is small and bounded, not a widening of the window: Query 1 (startDate >=
    // cutoff) never had a status filter, so every finished job that STARTS inside the
    // window is already streamed today. All this adds is finished jobs that started
    // BEFORE the cutoff and ended inside it — jobs straddling one boundary of a 90-day
    // window, a handful in a tenant of any size. The window itself is unchanged; "Load
    // older" is still the only thing that widens it.
    const legacyJobsQuery = query(
      collection(db, 'tenants', tenantId, 'jobs'),
      where('endDate', '>=', _adminCutoffTs),
      orderBy('endDate', 'asc')
    );
    const unsubLegacyJobs = onSnapshot(legacyJobsQuery, (snapshot) => {
      _jobsLegacy.clear();
      snapshot.docs.forEach(d => {
        _jobsLegacy.set(d.id, normalizeJobDoc(d.id, d.data()));
      });
      _gotLegacy = true;
      _mergeAndPublish();
    }, (error) => {
      console.error('[Dispatch] Admin legacy jobs subscription error:', error);
      _fallbackToUnbounded(error);
    });
    DispatchState.unsubscribers.push(unsubLegacyJobs);

    // Query 3 (TASK-4610 quarantine) — see the PIN-driver branch above for the
    // full rationale. Same window (_adminCutoffTs), zero docs in a healthy
    // tenant, never gates the publish.
    const unsubAdminStringDatedJobs = subscribeStringDatedJobs(tenantId, (snapshot) => {
      _jobsStringDated.clear();
      // TASK-4646 — includeFinished:true to match the admin Query 2 widening above.
      for (const [id, job] of stringDatedJobsInWindow(snapshot, _adminCutoffTs, true)) _jobsStringDated.set(id, job);
      if (_jobsStringDated.size) {
        console.warn('[Dispatch] TASK-4610 — rescued', _jobsStringDated.size, 'in-window job(s) with a non-Timestamp startDate/endDate:', Array.from(_jobsStringDated.keys()));
      }
      _mergeAndPublish();
    });
    DispatchState.unsubscribers.push(unsubAdminStringDatedJobs);
  }

  // Subscribe to job days
  // TASK-345 Slice M: PIN drivers get 7-day window only. JobDays carry no
  // status field — they're daily instances tied to a job — so the single
  // date filter is sufficient. Multi-week-job day instances ARE within the
  // window because they're daily docs (each day's jobDay has date=that-day).
  // A long-running active job from 3 weeks ago still has *recent* jobDays
  // from the last 7 days, which is all a driver needs to see for "today".
  // TASK-424 #5 (C2): admin jobDays is windowed too (date >= _adminCutoffTs),
  // unless the dispatcher has expanded to "all" via Load older. PIN drivers keep
  // their 7d window. Future jobDays are always included (no upper bound).
  const _windowedJobDays = !_isPinDriver && !_adminWindowAll;
  const jobDaysQuery = _isPinDriver
    ? query(
        collection(db, 'tenants', tenantId, 'jobDays'),
        where('date', '>=', _sevenDaysAgoTs),
        orderBy('date', 'asc')
      )
    : _windowedJobDays
      ? query(
          collection(db, 'tenants', tenantId, 'jobDays'),
          where('date', '>=', _adminCutoffTs),
          orderBy('date', 'asc')
        )
      : query(
          collection(db, 'tenants', tenantId, 'jobDays'),
          orderBy('date', 'asc')
        );

  let _jobDaysFellBack = false;
  const unsubJobDays = onSnapshot(jobDaysQuery, (snapshot) => {
    DispatchState.jobDays = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    DispatchState._jobDaysLoaded = true;
    console.log('[Dispatch] JobDays updated:', DispatchState.jobDays.length, _isPinDriver ? '(PIN-filtered)' : (_windowedJobDays ? '(admin-windowed)' : ''));
    renderDispatchBoard();
    // Surgically refresh any visible load-counter wrappers from this snapshot's
    // docChanges. Keeps scroll / focus / text-selection state intact.
    refreshLiveLoadCounters(snapshot);
  }, (error) => {
    console.error('[Dispatch] JobDays subscription error:', error);
    // NO-REGRESSION FALLBACK: if the windowed admin jobDays query errors (e.g.
    // a missing index), fall back to the unbounded full-collection stream so
    // the calendar never blanks. Fires at most once.
    if (_windowedJobDays && !_jobDaysFellBack) {
      _jobDaysFellBack = true;
      console.warn('[Dispatch] Windowed admin jobDays query failed — falling back to unbounded full-collection stream.');
      const unsubJobDaysFallback = onSnapshot(
        query(collection(db, 'tenants', tenantId, 'jobDays'), orderBy('date', 'asc')),
        (snapshot) => {
          DispatchState.jobDays = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          DispatchState._jobDaysLoaded = true;
          console.log('[Dispatch] JobDays updated (admin fallback, unbounded):', DispatchState.jobDays.length);
          renderDispatchBoard();
          refreshLiveLoadCounters(snapshot);
        },
        (err2) => {
          console.error('[Dispatch] Admin fallback jobDays subscription error:', err2);
          DispatchState._dispatchLoadFailed = true;   // TASK-4588 — see the jobs handler
          renderDispatchBoard();
        }
      );
      DispatchState.unsubscribers.push(unsubJobDaysFallback);
      return;
    }
    // Render board to clear loading state even on error. TASK-4588: the render
    // guards fall through on this flag; readiness itself stays honest.
    DispatchState._dispatchLoadFailed = true;
    renderDispatchBoard();
  });

  DispatchState.unsubscribers.push(unsubJobDays);

  // TASK-9359 — licence expiry (a Smart Tools qualification hint below) moved
  // off the broadcast people doc into the peopleIdentity vault. Subscribe so
  // window.PersonIdentity.value() can resolve it; a session without one of the
  // identity modules is denied and simply sends no licence expiry.
  window.PersonIdentity?.subscribe?.(tenantId, { key: 'dispatch' });

  // Subscribe to people (for assignment)
  // No orderBy to avoid requiring Firestore index - sort client-side
  const peopleRef = collection(db, 'tenants', tenantId, 'people');

  const unsubPeople = onSnapshot(peopleRef, (snapshot) => {
    if (DispatchState.tenantId !== tenantId) return; // P0 1019 COD — a listener that outlived a company switch never writes the board
    // Filter to active people and sort by name
    const _allPeople = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    // TASK-4535-family (renders-audit #13) — keep the UNFILTERED roster too:
    // historical job days worked by since-terminated drivers rendered
    // "Unassigned", which is historically false. peopleAll is for DISPLAY
    // resolution of past assignments only — pickers and texting stay on the
    // active-filtered array.
    DispatchState.peopleAll = _allPeople;
    window.ClockMode?.notePeople?.(_allPeople); // TASK-9553 per-person clock mode
    DispatchState.people = _allPeople.filter(p => p.active !== false);
    DispatchState.dataLoaded.people = true;
    console.log('[Dispatch] People updated:', DispatchState.people.length);
    refreshAssignmentModalIfOpen();
    renderDispatchBoard();
    // TASK-1533 — recount the Notify Crew audience live while the modal sits
    // open (a mute made in another tab/session used to leave the stale count
    // until the modal was reopened).
    const mtModal = document.getElementById('modal-mass-text');
    if (mtModal && !mtModal.classList.contains('hidden')) updateRecipientCount();
  }, (error) => {
    console.error('[Dispatch] People subscription error:', error);
    DispatchState._dispatchLoadFailed = true;   // TASK-4588 — export refuses rather than name nothing
    DispatchState.people = [];
    DispatchState.dataLoaded.people = true;
    renderDispatchBoard();
  });

  DispatchState.unsubscribers.push(unsubPeople);

  // wo-legacy-followups (COD): a remount / tenant switch starts with NO whole
  // fleet, so an imported hold never resolves against the previous tenant's lists.
  DispatchState._fleetAll = { trucks: null, trailers: null, assets: null };

  // Subscribe to trucks
  // No orderBy to avoid requiring Firestore index - sort client-side
  const trucksRef = collection(db, 'tenants', tenantId, 'trucks');

  const unsubTrucks = onSnapshot(trucksRef, (snapshot) => {
    if (DispatchState.tenantId !== tenantId) return; // P0 1019 COD
    // Filter to active trucks and sort by unit
    const _allTrucks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    _setFleetAll('trucks', _allTrucks); // wo-legacy-followups — imported holds resolve by name over the WHOLE fleet
    DispatchState.trucks = _allTrucks
      .filter(t => t.active !== false)
      .sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''));
    DispatchState.dataLoaded.trucks = true;
    console.log('[Dispatch] Trucks updated:', DispatchState.trucks.length);
    refreshAssignmentModalIfOpen();
    renderDispatchBoard();
  }, (error) => {
    console.error('[Dispatch] Trucks subscription error:', error);
    DispatchState._dispatchLoadFailed = true;   // TASK-4588 — export refuses rather than name nothing
    DispatchState.trucks = [];
    DispatchState.dataLoaded.trucks = true;
    renderDispatchBoard();
  });

  DispatchState.unsubscribers.push(unsubTrucks);

  // Subscribe to trailers
  // No orderBy to avoid requiring Firestore index - sort client-side
  const trailersRef = collection(db, 'tenants', tenantId, 'trailers');

  const unsubTrailers = onSnapshot(trailersRef, (snapshot) => {
    if (DispatchState.tenantId !== tenantId) return; // P0 1019 COD
    // Filter to active trailers and sort by unit
    const _allTrailers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    _setFleetAll('trailers', _allTrailers); // wo-legacy-followups
    DispatchState.trailers = _allTrailers
      .filter(t => t.active !== false)
      .sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''));
    DispatchState.dataLoaded.trailers = true;
    console.log('[Dispatch] Trailers updated:', DispatchState.trailers.length);
    refreshAssignmentModalIfOpen();
  }, (error) => {
    console.error('[Dispatch] Trailers subscription error:', error);
    DispatchState._dispatchLoadFailed = true;   // TASK-4588 — export refuses rather than name nothing
    DispatchState.trailers = [];
    DispatchState.dataLoaded.trailers = true;
    renderDispatchBoard();
  });

  DispatchState.unsubscribers.push(unsubTrailers);

  // Subscribe to assets
  const assetsRef = collection(db, 'tenants', tenantId, 'assets');

  const unsubAssets = onSnapshot(assetsRef, (snapshot) => {
    if (DispatchState.tenantId !== tenantId) return; // P0 1019 COD
    const _allAssets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    _setFleetAll('assets', _allAssets); // wo-legacy-followups
    DispatchState.assets = _allAssets
      .filter(a => a.active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    DispatchState.dataLoaded.assets = true;
    console.log('[Dispatch] Assets updated:', DispatchState.assets.length);
    refreshAssignmentModalIfOpen();
    renderPinnedAssetStrip(); // TASK-10007 — live numbers on every asset change
  }, (error) => {
    console.error('[Dispatch] Assets subscription error:', error);
    DispatchState._dispatchLoadFailed = true;   // TASK-4588 — export refuses rather than name nothing
    DispatchState.assets = [];
    DispatchState.dataLoaded.assets = true;
    renderPinnedAssetStrip(); // TASK-10007 — no assets readable → no strip
    renderDispatchBoard();   // TASK-4588 — the only roster handler that never repainted,
                             // so nothing re-locked an open Notify Crew preview.
  });

  DispatchState.unsubscribers.push(unsubAssets);

  // Subscribe to tickets (lightweight — for status dots on calendar).
  // TASK-345 S2: PIN drivers never see the dispatcher calendar (jobboard.js drives
  // their UI), so skip this sub to save ~660KB Listen payload on cold load.
  // PIN-mode check matches isDriverPinMode at L2413; getTicketStatusDot returns ''
  // cleanly when tickets is empty so consumers stay safe.
  const _isPinDriverModeForTickets = window.AppState?.driverModePin === true;
  if (!_isPinDriverModeForTickets) {
    // TASK-857: tickets are the fastest-growing collection (1+/job/day). The
    // dispatch board only uses them for cosmetic status dots on jobs, and jobs
    // are themselves windowed to ~7d-back (recent + active-legacy queries above),
    // so a 90-day ticket window is generous margin while bounding what was an
    // unbounded live stream of every ticket the tenant ever created. All
    // app-created tickets store `date` as a Timestamp (clock.js findOrCreateTicket
    // + billing.js create paths), so the date floor matches the live schema; a
    // doc missing `date` would only ever back a job far older than the 7d job
    // window and getTicketStatusDot returns '' cleanly when a dot is absent.
    const _ticketsCutoffTs = Timestamp.fromDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000));
    const ticketsQuery = query(
      collection(db, 'tenants', tenantId, 'tickets'),
      where('date', '>=', _ticketsCutoffTs),
      orderBy('date', 'desc'),
      limit(5000)
    );
    const unsubTickets = onSnapshot(ticketsQuery, (snapshot) => {
      DispatchState.tickets = snapshot.docs.map(d => {
        const data = d.data();
        // invoiceNumber/invoicedAt: the shared isTicketInvoiced predicate (TASK-1387) reads them.
        // TASK-9645 B — paid/paidAt (Paid state), invoicedExternally +
        // externalInvoiceRef (billed elsewhere = Invoiced with a note — the
        // read-side isBilledExternally also needs invoiceNumber's EXT- prefix),
        // originalJobId (a ticket moved to another job still counts on the job
        // it came from, billing's sticky matcher) + originalWorkDate (the
        // _ticketCoversDay matcher the job-modal chips share with the guards).
        // Status/refs only — NO amounts ride this stream into dispatch state.
        return { id: d.id, jobId: data.jobId, status: data.status, invoiced: data.invoiced, invoiceNumber: data.invoiceNumber || null, invoicedAt: data.invoicedAt || null, archived: data.archived, workDate: data.workDate, workDateEnd: data.workDateEnd,
          paid: data.paid === true, paidAt: data.paidAt || null, invoicedExternally: data.invoicedExternally === true, externalInvoiceRef: data.externalInvoiceRef || null,
          originalJobId: data.originalJobId || null, originalWorkDate: data.originalWorkDate || null,
          // TASK-10107 — computed HERE from the full doc (the seal + total never ride this stream).
          sealedReturnEstimate: _isSealedReturnEstimate(data) };
      });
      // Re-render dispatch board to show ticket status dots
      renderDispatchBoard();
    }, (error) => {
      console.error('[Dispatch] Tickets subscription error:', error);
      DispatchState.tickets = [];
    });
    DispatchState.unsubscribers.push(unsubTickets);
  } else {
    // Explicit empty so getTicketStatusDot short-circuits on first call
    DispatchState.tickets = [];
  }

  // Subscribe to customers. TASK-9694 S6a: a crew session (driver PIN, driver
  // link, crew-only login) reads the crew-safe customerDirectory (name + flags)
  // — it can no longer read the full `customers` docs (phones, contacts, notes).
  // Office sessions keep reading `customers` (the job form needs contacts).
  // Either side falls back to the other on permission-denied (deploy window).
  const unsubCustomers = _dispatchCustomerSource().subscribe(tenantId, (list) => {
    DispatchState.customers = list;
    // Only populate if modal is open
    const select = document.getElementById('job-customer');
    if (select) populateCustomerDropdown();
  }, {
    label: 'Dispatch customers',
    orderByName: true,
    prefer: _dispatchCustomerPrefer(),
    // Don't clear existing data - keep cached customers
    onError: (error) => console.error('[Dispatch] Customers subscription error:', error),
  });

  DispatchState.unsubscribers.push(unsubCustomers);

  // Subscribe to saved locations
  const unsubLocations = onSnapshot(
    query(collection(db, 'tenants', tenantId, 'savedLocations'), orderBy('name')),
    (snap) => {
      DispatchState.savedLocations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      _placesDataChanged(); // TASK-9540 — an open manager re-renders
    },
    () => { DispatchState.savedLocations = []; }
  );
  DispatchState.unsubscribers.push(unsubLocations);

  // TASK-9540 — place categories (shared tenant-wide). A failed read leaves
  // the starters, so every place still draws (as its starter or as Other).
  const unsubPlaceCats = onSnapshot(
    collection(db, 'tenants', tenantId, 'waypointCategories'),
    (snap) => {
      DispatchState.waypointCategoryDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      _placesDataChanged();
    },
    (err) => { console.warn('[Dispatch] waypointCategories read failed:', err?.code || err); DispatchState.waypointCategoryDocs = []; }
  );
  DispatchState.unsubscribers.push(unsubPlaceCats);

  // Subscribe to permits (811) - drivers need this for job permits
  const permitsRef = collection(db, 'tenants', tenantId, 'permits');

  const unsubPermits = onSnapshot(permitsRef, (snapshot) => {
    DispatchState.permits = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
    // TASK-9212 — the permits collection has no dataLoaded flag, and the carried
    // 811 ticket needs one: "the box is empty because nothing has loaded" and
    // "the box is empty because that ticket was deleted" are the same empty
    // string, and they call for opposite behaviour at save time.
    DispatchState.permitsLoaded = true;
    console.log('[Dispatch] Permits updated:', DispatchState.permits.length);
    // Re-populate dropdown if modal is open
    const select = document.getElementById('job-permit');
    if (select) populatePermitDropdown();
    renderPermitsList();
  }, (error) => {
    console.error('[Dispatch] Permits subscription error:', error);
  });

  DispatchState.unsubscribers.push(unsubPermits);

  // Skip maintenance subscription for driver PIN mode - they don't need safety holds
  const isDriverPinMode = window.AppState?.driverModePin === true;

  // TASK-9887 — potential jobs/projects (the Locates page's bank). Read by the
  // '811-permits' module or dispatch (rules); never by a driver session.
  if (!isDriverPinMode && !window.AppState?.driverMode && !window.AppState?.isPublicAccess && window.isPartner811ReadOnly?.() !== true) {
    const unsubPots = onSnapshot(collection(db, 'tenants', tenantId, 'potentials'), (snap) => {
      DispatchState.potentials = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      _p811PotentialsChanged();
    }, (err) => {
      DispatchState.potentials = [];
      if (err?.code !== 'permission-denied') console.warn('[Dispatch] potentials read failed:', err?.code || 'error');
    });
    DispatchState.unsubscribers.push(unsubPots);
  }

  // TASK-9258 — job templates (saved equipment kits). Dispatcher-side only: a
  // driver PIN session has no job form, and `jobTemplates` is member-read in the
  // rules stanza this feature needs, so subscribing from a PIN session would
  // produce nothing but a permission-denied loop.
  if (!isDriverPinMode) subscribeToJobTemplates(tenantId);

  if (!isDriverPinMode) {
    // Subscribe to critical maintenance tasks (safety holds)
    const maintenanceRef = collection(db, 'tenants', tenantId, 'maintenance');

    const unsubMaintenance = onSnapshot(maintenanceRef, (snapshot) => {
      // Filter to open critical tasks only — TASK-9657 S0: the ONE rule
      // (window.SafetyHold, generated from functions/src/shared/safetyHold.js,
      // the same file the connector's create_job / assign_crew warning reads).
      DispatchState.safetyHolds = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(task => window.SafetyHold.isCriticalHoldTask(task));
      console.log('[Dispatch] Safety holds updated:', DispatchState.safetyHolds.length);
      renderDispatchBoard();
    }, (error) => {
      console.error('[Dispatch] Maintenance subscription error:', error);
      DispatchState.safetyHolds = [];
    });

    DispatchState.unsubscribers.push(unsubMaintenance);
  } else {
    // Initialize empty safety holds for driver mode
    DispatchState.safetyHolds = [];
  }

  // Subscribe to approved time-off requests (skip for driver PIN mode)
  if (!isDriverPinMode) {
    const timeOffRef = query(
      collection(db, 'tenants', tenantId, 'timeOff'),
      where('status', '==', 'approved')
    );

    const unsubTimeOff = onSnapshot(timeOffRef, (snapshot) => {
      DispatchState.timeOffRequests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('[Dispatch] Time-off requests updated:', DispatchState.timeOffRequests.length);
      renderDispatchBoard();
    }, (error) => {
      console.log('[Dispatch] No timeOff collection yet, that is okay');
      DispatchState.timeOffRequests = [];
    });

    DispatchState.unsubscribers.push(unsubTimeOff);
  } else {
    DispatchState.timeOffRequests = [];
  }

  // Subscribe to opportunity holds (CRM dispatch integration)
  if (!isDriverPinMode) {
    const oppHoldsRef = query(
      collection(db, 'tenants', tenantId, 'opportunityHolds'),
      where('status', '==', 'active')
    );

    const unsubOppHolds = onSnapshot(oppHoldsRef, (snapshot) => {
      DispatchState.opportunityHolds = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('[Dispatch] Opportunity holds updated:', DispatchState.opportunityHolds.length);
      renderDispatchBoard();
    }, (error) => {
      console.log('[Dispatch] No opportunityHolds collection yet, that is okay');
      DispatchState.opportunityHolds = [];
    });

    DispatchState.unsubscribers.push(unsubOppHolds);
  } else {
    DispatchState.opportunityHolds = [];
  }

  // Subscribe to maintenance vehicle holds (skip for driver PIN mode)
  if (!isDriverPinMode) {
    const vehicleHoldsRef = query(
      collection(db, 'tenants', tenantId, 'vehicleHolds'),
      where('status', '==', 'active')
    );

    const unsubVehicleHolds = onSnapshot(vehicleHoldsRef, (snapshot) => {
      DispatchState.vehicleHolds = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('[Dispatch] Vehicle holds updated:', DispatchState.vehicleHolds.length);
      renderDispatchBoard();
    }, (error) => {
      console.log('[Dispatch] No vehicleHolds collection yet, that is okay');
      DispatchState.vehicleHolds = [];
    });

    DispatchState.unsubscribers.push(unsubVehicleHolds);
  } else {
    DispatchState.vehicleHolds = [];
  }

  // Subscribe to recent pretrips for DVIR status indicators.
  // TASK-9886 — keyed per truck + DRIVER + day (window.DvirCoverage). A driver
  // PIN session used to skip this entirely, so every PIN card with a truck said
  // "No DVIR yet" forever, even after the DVIR. PIN mode now reads TODAY's
  // pretrips (rules: canReadTenant incl. driver sessions — pretrips are already
  // broadcast-readable, TASK-9554). Not filtered to driverId == me (COD r1): an
  // office-entered DVIR for this driver (name only) or a driverless one must
  // clear the PIN card exactly as it clears the office board.
  const _dvirPinPid = isDriverPinMode ? (window.AppState?.driverPersonId || null) : null;
  DispatchState.pretripCoverage = {};
  DispatchState.pretripCoverageFromDay = null;
  if (!isDriverPinMode || _dvirPinPid) {
    const since = new Date();
    if (isDriverPinMode) {
      since.setHours(0, 0, 0, 0);
    } else {
      since.setDate(since.getDate() - 30);
    }
    const _cap = isDriverPinMode ? 200 : 500;
    const pretripRef = query(
      collection(db, 'tenants', tenantId, 'pretrips'),
      where('createdAt', '>=', Timestamp.fromDate(since)),
      orderBy('createdAt', 'desc'),
      limit(_cap)
    );
    const sinceDay = _localYmd(since);

    const unsubPretrips = onSnapshot(pretripRef, (snapshot) => {
      const rows = [];
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (!data.truckId || !data.createdAt?.toDate) return;
        rows.push({ truckId: data.truckId, driverId: data.driverId || '', driverName: data.driverName || '', dayKey: _localYmd(data.createdAt.toDate()) });
      });
      DispatchState.pretripCoverage = window.DvirCoverage ? window.DvirCoverage.build(rows) : {};
      // COD r1 — a capped (truncated) snapshot only fully sees the days AFTER
      // its oldest doc's day; claim only those, never the whole window.
      let fromDay = sinceDay;
      if (snapshot.size >= _cap && snapshot.docs.length) {
        const oldest = snapshot.docs[snapshot.docs.length - 1].data()?.createdAt?.toDate?.();
        if (oldest) {
          const next = new Date(oldest); next.setDate(next.getDate() + 1);
          fromDay = _localYmd(next);
        }
      }
      DispatchState.pretripCoverageFromDay = fromDay;
      console.log('[Dispatch] Pretrip DVIR lookup updated:', rows.length, 'pretrips');
      renderDispatchBoard();
    }, (error) => {
      console.log('[Dispatch] Pretrips subscription error:', error);
      DispatchState.pretripCoverage = {};
      DispatchState.pretripCoverageFromDay = null;
    });

    DispatchState.unsubscribers.push(unsubPretrips);
  }

  // Subscribe to recent JSA reports for JSA status indicators (skip for driver PIN mode — drivers get their own myJsas subscription below)
  if (!isDriverPinMode) {
    const jsaThirtyDaysAgo = new Date();
    jsaThirtyDaysAgo.setDate(jsaThirtyDaysAgo.getDate() - 30);
    const jsaRef = query(
      collection(db, 'tenants', tenantId, 'jsaReports'),
      where('createdAt', '>=', Timestamp.fromDate(jsaThirtyDaysAgo)),
      orderBy('createdAt', 'desc'),
      limit(500)
    );

    const unsubJsa = onSnapshot(jsaRef, (snapshot) => {
      const lookup = {};
      // TASK-794: also map jobDayId/jobId → the actual jsaReport id so the job
      // card can surface "Get Signatures" on the existing report. Docs arrive
      // ordered createdAt desc, so the FIRST real (non-dismissed) report we see
      // per jobDay/job is the most recent — keep that one, skip dismissed
      // placeholders (they carry no signable content).
      const idByJobDay = {};
      const idByJob = {};
      const listByJobDay = {};
      snapshot.docs.forEach(d => {
        const data = d.data();
        if (!data.jobDayId) return;
        lookup[data.jobDayId] = true;
        if (data.dismissed === true) return; // dismissed placeholder — nothing to sign
        if (!idByJobDay[data.jobDayId]) idByJobDay[data.jobDayId] = d.id;
        if (data.jobId && !idByJob[data.jobId]) idByJob[data.jobId] = d.id;
        // TASK-9371 — keep EVERY report per jobDay (createdAt desc) so the card's
        // "N JSAs" count never diverges from the job modal's list.
        (listByJobDay[data.jobDayId] = listByJobDay[data.jobDayId] || []).push({
          id: d.id,
          formMode: data.formMode === 'xto' ? 'xto' : (data.formMode === 'template' ? 'template' : 'tits'),
          templateName: data.formMode === 'template' ? String(data.templateName || '') : '',
          createdMs: data.createdAt?.toMillis?.() ?? data.date?.toMillis?.() ?? 0,
          submittedByName: data.submittedByPersonName || data.completedByName || '',
          // TASK-4839: template sign-ins live in a subcollection; the parent has signInCount.
          signatureCount: (data.formMode === 'xto'
            ? ((data.jsaIssuer?.signature ? 1 : 0) + (data.jsaReceiver?.signature ? 1 : 0))
            : data.formMode === 'template'
              ? (Number(data.signInCount) || 0)
              : (Array.isArray(data.crewAcknowledgments) ? data.crewAcknowledgments.filter(a => a && a.signature).length : 0))
            + (Number(data.externalSignersCount) || 0),
          revisionCount: Number(data.revisionCount) || 0,
          signaturesStale: data.signaturesStale === true,
        });
      });
      DispatchState.jsaJobDayIds = lookup;
      DispatchState.jsaIdByJobDay = idByJobDay;
      DispatchState.jsaIdByJob = idByJob;
      DispatchState.jsaListByJobDay = listByJobDay;
      console.log('[Dispatch] JSA lookup updated:', Object.keys(lookup).length, 'entries');
      renderDispatchBoard();
    }, (error) => {
      console.log('[Dispatch] JSA subscription error:', error);
      DispatchState.jsaJobDayIds = {};
      DispatchState.jsaIdByJobDay = {};
      DispatchState.jsaIdByJob = {};
      DispatchState.jsaListByJobDay = {};
    });

    DispatchState.unsubscribers.push(unsubJsa);
  } else {
    DispatchState.jsaJobDayIds = {};
    DispatchState.jsaIdByJobDay = {};
    DispatchState.jsaIdByJob = {};
    DispatchState.jsaListByJobDay = {};
  }

  // TASK-361 (2026-05-26, removed from dispatch.js 2026-05-28) — driver
  // myJsas subscription relocated to jsa.js renderMyJsaSection (Inspections
  // > JSA tab). Removed here to stop the dead-state population.

  // Subscribe to active clock entries (for driver clock access + dispatcher clocked-in indicators)
  // TASK-452 — for a driver-PIN session this sub only needs THIS driver's own
  // active entry (it drives the driver clock button; the clocked-in-card
  // indicators are an office/dispatcher surface). Under the tightened
  // clockEntries READ rule a PIN driver may read ONLY their own entries, so a
  // tenant-wide `where status==active` query is permission-denied for them.
  // Scope to `personId == self` for PIN drivers; signed-in office sessions
  // (dispatcher/admin/owner/workforce) keep the full tenant-wide read for the
  // clocked-in indicators on dispatch cards.
  DispatchState._clockEntries = [];
  const _dpPid = window.AppState?.driverPersonId;
  const _dpScope = !!window.AppState?.driverModePin && !!_dpPid;
  const clockQuery = _dpScope
    ? query(
        collection(db, 'tenants', tenantId, 'clockEntries'),
        where('personId', '==', _dpPid),
        where('status', '==', 'active')
      )
    : query(
        collection(db, 'tenants', tenantId, 'clockEntries'),
        where('status', '==', 'active')
      );
  const unsubClock = onSnapshot(clockQuery, (snapshot) => {
    DispatchState._clockEntries = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    // Update clock button state in driver mode
    if (window.AppState?.driverModePin && window.driverHasClockAccess?.()) {
      updateDriverClockButton();
      // TASK-347 — also sync the new bottom-nav Clock tab.
      updateDriverBottomNavClock();
    }
    // Update clocked-in indicators on dispatch cards in real-time
    updateClockedInIndicators();
    // TASK-348 — refresh SPA Personal Actions Clock In/Out tile label.
    // fix-1999-followups — hand it THIS live snapshot (app.js cannot see
    // DispatchState; it used to read a stale one-shot copy of its own).
    if (typeof window._refreshPersonalClockLabel === 'function') {
      window._refreshPersonalClockLabel(DispatchState._clockEntries);
    }
  }, () => { DispatchState._clockEntries = []; });
  DispatchState.unsubscribers.push(unsubClock);

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    // TASK-9289 S2 — was every job in the tenant, any customer, any status, and
    // the job being edited too: "X already exists in Jobs." fired on a name that
    // is legitimately repeated inside a project, and on the job's own title.
    // Scoped to this customer's open jobs, minus the form's own project (where a
    // repeated name is the expected case and checkJobDuplicate offers the
    // choices). checkJobDuplicate itself is deliberately untouched.
    SA.register('job-title', _jobTitleSaCandidates, "this customer's open jobs", { skipAllCaps: true, excludeIdField: 'job-id' });
    // TASK-9390 — job-type is a <select> of slugs now; SmartAssistant's format
    // pass would title-case 'shop_day' into an option that does not exist.
    // TASK-4782 — `job-po` is DELIBERATELY NOT a Smart Assistant field, and this
    // comment is the reason so it does not get re-added by reflex.
    //
    // A PO is an IDENTIFIER, NOT A NAME. Every branch SmartAssistant runs is
    // correct for a person or a well name and wrong for an identifier:
    //
    //   * cleanFormat() title-cases ALL-CAPS values behind an "Auto-fix" button.
    //     cleanFormat('ND25TU2-U1') === 'Nd25tu2-u1' — and 'Nd25tu2-u1' is LIVE
    //     in TITS today, sitting in the same PO group as 'ND25TU2-U1'. So is
    //     'Po # B531990su' next to 'PO # B531990SU'.
    //     HOW STRONG THAT EVIDENCE IS, stated honestly (COD Code Reviewer asked
    //     for receipts and there are none to give): both live strings are
    //     BYTE-EXACT outputs of cleanFormat() applied to the value beside them,
    //     and nothing recorded who typed what — poNumber had no audit trail
    //     before this change, which is part of why this change adds one. So:
    //     the button demonstrably produces these strings and these strings are
    //     live. Whether a human clicked it or typed the same thing by hand is
    //     not provable, and does not change the fix — a button that offers to
    //     title-case a PO should not be on this field either way.
    //   * the levenshtein "Did you mean X?" branch, also behind an Apply button:
    //     98 of 158 distinct live PO values get one, and they are false matches
    //     between two REAL POs. Typing ND25TU1-U1 (106 docs) offers ND25TU0-U1
    //     (65 docs) at distance 1. Different corridors, different authorizations
    //     — one click re-bills the job to a PO the customer never issued.
    //     `skipAllCaps` does not save this; the threshold stays 2 at 10 chars.
    //   * the duplicate warning is noise here by definition: 180 live jobs
    //     legitimately share one PO.
    //
    // Replaced by poEntryIssues() below — exact group-key matching only, plus
    // the QuickBooks length warning. It is also NOT gated on hasAiPack(), because
    // "this will not reach QuickBooks" is a correctness warning, not an add-on.
    // TASK-4834 — `job-vendor-number` is DELIBERATELY NOT a Smart Assistant
    // field either, for the same reason as job-po above. Same move: removal.
    // A vendor number is an IDENTIFIER the customer's AP system assigns; it is
    // printed verbatim on the field ticket PDF and invoice. On default options
    // the helper (a) title-cased ALL-CAPS behind Auto-fix (cleanFormat('EMI') ===
    // 'Emi', and 'EMI' is a live vendor number) and (b) offered a levenshtein
    // "Did you mean" Apply between two real values (`skipAllCaps` does NOT stop
    // that: the threshold is still 2 at 10 chars). The duplicate warning is noise
    // too — one vendor number repeats across every job for that customer.
    // Measured live 2026-10-03 (scripts/identifier-field-audit.cjs): 18 job docs,
    // 5 distinct values, 1 value offered the Auto-fix, 0 stored corrupted. This
    // removal is prevention, not a rescue. #modal-job is the LIVE job form.
    // tests/vendor-number-identifier.unit.cjs fails if it is re-registered.
    // Follow-up (task-smartassist-names-2) — well name and location keep the
    // exact-duplicate warning and whitespace cleanup, but `noFuzzy` turns off
    // "Did you mean" against ANOTHER job's value. Live 2026-10-03: 6 well names
    // were offered a different well ('Rella East Pad' -> 'Rella West Pad') and 20
    // locations a different site ('Pad 12, FM 1788' -> 'Pad 15, FM 1788').
    SA.register('job-well-name', () => DispatchState.jobs.filter(j => j.wellName).map(j => ({ value: j.wellName, id: j.id })), 'Jobs', { skipAllCaps: true, noFuzzy: true });
    SA.register('job-location', () => DispatchState.jobs.filter(j => j.location).map(j => ({ value: j.location, id: j.id })), 'Jobs', { skipAllCaps: true, noFuzzy: true });
    SA.register('job-notes', () => [], 'Jobs', { formatOnly: true });
  }
}

// ============================================
// RENDER DISPATCH BOARD
// ============================================
// ============================================
// RENDER COALESCING (TASK-424 #4 / C4)
// ============================================
// ~35 Firestore listeners each repaint the board directly. A burst of
// snapshots arriving in one tick used to fire ~35 full re-renders → mobile
// jank. `renderDispatchBoard()` is now the COALESCED entry: N requests in the
// same frame collapse to ONE render via a requestAnimationFrame dirty flag.
// The synchronous render lives in `_renderDispatchBoardNow()` — call THAT
// directly from any user-action / DOM-measuring / optimistic-update path that
// needs the board painted before the next line of code runs (a coalesced
// render would not be visible until the next frame).
//
// SAFETY CONTRACT:
//   1. The trailing render ALWAYS fires within one frame of any dirty signal —
//      no debounce that can starve under continuous signals. Once a frame is
//      scheduled, the flag stays set until that frame's render runs, so a flood
//      of requests in the same tick produces exactly one render NEXT frame, and
//      every subsequent tick that signals again schedules its own next frame.
//   2. `_renderDispatchBoardNow()` remains a direct synchronous render for any
//      caller that depends on the painted result in the same tick.
let _renderQueued = false;
function scheduleRender() {
  if (_renderQueued) return;
  _renderQueued = true;
  const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
  raf(() => {
    _renderQueued = false;
    _renderDispatchBoardNow();
  });
}

// Coalesced entry point. Default for the ~35 snapshot-driven listener call
// sites — a fire-and-forget "the data changed, repaint sometime this frame."
function renderDispatchBoard() {
  scheduleRender();
  // Every job / jobDay publish path funnels through here, which makes it the one
  // place the mass-text modal can learn that the schedule became readable —
  // event-driven, so there is no poll to expire and no cap for a late snapshot
  // to race past. No-ops unless the modal is open and was locked by loading.
  try { refreshMassTextOnDataArrival(); } catch (_) { /* never let a preview refresh break the board */ }
  // TASK-9859 — the 811 library's "Waiting for a job" / "Linked to" read the
  // loaded jobs, so a job publish repaints it while the window is open (an edit
  // row in progress is left alone).
  try {
    const m811 = document.getElementById('modal-811-submission');
    if (m811 && !m811.classList.contains('hidden') && !m811.querySelector('#permits-list .p811-edit')) renderPermitsList();
    _p811RefreshGroupIfOpen(); // TASK-9862
  } catch (_) { /* never let the 811 list break the board */ }
}

// Synchronous render (the original renderDispatchBoard body). Call this
// directly when the caller measures/reads the DOM or acts on the painted
// output in the same tick, or is a user action that expects instant feedback.
function _renderDispatchBoardNow() {
  const content = document.getElementById('dispatch-content');
  if (!content) return;
  // TASK-4812 — the one funnel every board view (week / list / search) paints
  // through, so the injected sheet is in the document before the FIRST job card
  // renders. That matters because the card overflow menus carry their own
  // [hidden] Add Another Day, and a sheet injected only when a modal opens would
  // leave that one visible to a member who cannot add a day until they happened
  // to open a job. Idempotent; the cost after the first call is a getElementById.
  _ensureAddDayWeightStyles();

  // Driver mode - render simplified driver board (link-based or PIN-based)
  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin;
  if (isDriverMode) {
    renderDriverBoard(content);
    return;
  }

  // TASK-9651 — a login without the 'projects' module never lands on the
  // Projects view (its button is not rendered; this catches a stale viewMode).
  if (DispatchState.viewMode === 'projects' && window.canAccessProjects?.() === false) {
    DispatchState.viewMode = 'week';
    document.querySelector('.view-btn[data-view="week"]')?.classList.add('active');
  }
  if (DispatchState.viewMode === 'projects') {
    if (window.ProjectsModule?.renderProjectsView) {
      window.ProjectsModule.renderProjectsView();
    } else {
      content.innerHTML = '<p style="padding:24px;color:var(--text-secondary);">Projects module loading...</p>';
    }
    return;
  } else if (DispatchState.viewMode === 'list') {
    renderListView(content);
  } else if (DispatchState.viewMode === 'month') {
    renderMonthView(content);
  } else if (DispatchState.viewMode === 'lanes') {
    renderLanesView(content);
  } else if (DispatchState.viewMode === 'search') {
    renderSearchView(content);
  } else if (DispatchState.viewMode === 'map') {
    renderMapView(content);
  } else {
    renderWeekView(content);
  }
  // TASK-9694 S6 — the per-person new-updates dot (lazy jobUpdates.js), only
  // once some job has crew Updates; otherwise the board renders as before.
  if (DispatchState.jobs.some(j => (j.crewUpdates || []).some(e => e && e.kind !== 'change'))) __ddChunkWrap(import('./jobUpdates.js?v=96946')).then(m => m.boot({ tenantId: DispatchState.tenantId, jobs: () => DispatchState.jobs }), () => {});

  // Apply sparkles to dynamically rendered elements (like individual text buttons)
  if (typeof window.applyDynamicSparkles === 'function') {
    setTimeout(() => window.applyDynamicSparkles(), 100);
  }
}

// ============================================
// DRIVER BOARD RENDERING
// ============================================
// Tap the date pill → jump back to today (in whichever mode is active).
function driverJumpToToday() {
  const today = new Date();
  DispatchState.currentDate = today;
  DispatchState.driverCalendarMonth = today;
  const container = document.getElementById('dispatch-content');
  if (container) renderDriverBoard(container);
}

// Calendar layer filters (Jobs / Days Off / Completed). Multi-select chips hide/show
// layers on Week + Month views. TASK-461: the Maintenance chip was removed — BOTH vehicle
// holds AND safety holds are always visible now (a critical DVIR safety hold must
// never be hideable — FMCSA §396.9/§396.11). State persists per-device in localStorage.
// TASK-4646 added 'completed'. Note what it is NOT: it does not touch holds of either
// kind, and it does not reach the driver board or the mass-text paths, which pass
// includeFinished=false to getJobsForDate explicitly and are not chip-controlled.
// TASK-4648: List and Lanes are ALSO outside this chip, but for the opposite
// reason — they now show finished jobs unconditionally (dimmed) rather than
// dropping them. The chip is a density control for the two views that have a
// density problem; the full argument is above renderListView.
function toggleLayerFilter(layer) {
  const filters = DispatchState.calendarFilters;
  if (!filters || !(layer in filters)) return;
  filters[layer] = !filters[layer];
  try {
    localStorage.setItem('dd_dispatch_calendar_filters_v1', JSON.stringify(filters));
  } catch (_) {}
  syncLayerFilterChips();
  // Re-render the active view so the filter takes effect immediately.
  // User action — sync render for instant feedback.
  _renderDispatchBoardNow();
}

// Update the "N items hidden" pill in the chip row. Renderers compute the
// hidden count for the range they just drew and pass it here.
function updateLayerFilterWarning(hiddenCount) {
  const el = document.getElementById('layer-filters-warning');
  if (!el) return;
  if (!hiddenCount || hiddenCount <= 0) {
    el.style.display = 'none';
    el.textContent = '';
    return;
  }
  el.style.display = '';
  el.textContent = `⚠ ${hiddenCount} item${hiddenCount === 1 ? '' : 's'} hidden`;
}

// Reflect DispatchState.calendarFilters onto the chip DOM (active class +
// aria-pressed). Also toggles the chip row's own visibility based on the
// active view — chips only appear on Week + Month (where the filter applies).
function syncLayerFilterChips() {
  const row = document.getElementById('dispatch-layer-filters');
  if (!row) return;
  const isFilterableView = DispatchState.viewMode === 'week' || DispatchState.viewMode === 'month';
  row.style.display = isFilterableView ? '' : 'none';
  if (!isFilterableView) {
    updateLayerFilterWarning(0);
    return;
  }
  const filters = DispatchState.calendarFilters || {};
  ['jobs', 'daysoff', 'completed'].forEach(layer => { // TASK-461: 'maintenance' chip removed; TASK-4646: +completed
    const chip = row.querySelector(`.layer-chip[data-layer="${layer}"]`);
    if (!chip) return;
    const on = filters[layer] !== false;
    chip.classList.toggle('active', on);
    chip.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  // TASK-424 #5: reflect the current admin window on the Load older control.
  syncLoadOlderControl();
}

// Toggle driver view between day and calendar modes. Persists per-device
// via localStorage. Triggers a re-render of the driver board.
function toggleDriverViewMode() {
  const next = DispatchState.driverViewMode === 'calendar' ? 'day' : 'calendar';
  DispatchState.driverViewMode = next;
  try { localStorage.setItem('dd_dispatch_driver_view_mode_v1', next); } catch (_) {}
  if (next === 'calendar') {
    DispatchState.driverCalendarMonth = new Date(DispatchState.currentDate);
  }
  const container = document.getElementById('dispatch-content');
  if (container) renderDriverBoard(container);
}

// Tap a calendar cell with jobs → switch to day mode + jump to that date.
function jumpDriverDayFromCalendar(isoDateStr) {
  if (!isoDateStr) return;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  DispatchState.currentDate = new Date(y, m - 1, d);
  DispatchState.driverViewMode = 'day';
  try { localStorage.setItem('dd_dispatch_driver_view_mode_v1', 'day'); } catch (_) {}
  const container = document.getElementById('dispatch-content');
  if (container) renderDriverBoard(container);
}

// Renders a month-grid of jobDays after applying the personalized-schedule
// filter. Mirrors jobboard's calendar — same util, same UX. Cells with
// jobs are tap-able; tapping jumps back to day mode at that date.
function renderDriverCalendarView(container) {
  const anchor = new Date(DispatchState.driverCalendarMonth);
  anchor.setHours(0, 0, 0, 0);
  anchor.setDate(1);

  // Update the date display to "Month Year"
  const dateEl = document.getElementById('current-date');
  if (dateEl) {
    dateEl.textContent = anchor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  // Hide the count line in calendar mode
  const countEl = document.getElementById('driver-job-count');
  if (countEl) { countEl.textContent = ''; countEl.style.display = 'none'; }

  // Apply personalized-schedule filter via shared util
  const driverPersonId = window.AppState?.driverPersonId;
  const driverPerson = driverPersonId
    ? DispatchState.people.find(p => p.id === driverPersonId)
    : null;
  const personalizedFilter = (jd) => _sharedJobDayVisible(jd, {
    settings: window.AppState?.settings,
    driverPersonId,
    driverPerson,
  });
  const isCancelled = (jd) => {
    const job = DispatchState.jobs.find(j => j.id === jd.jobId);
    return !job || job.status === 'cancelled';
  };

  // Pre-index jobDays per ISO date for the visible month (post personalized filter)
  const _anchorYear = anchor.getFullYear();
  const _anchorMonth = anchor.getMonth();
  const _jobDaysByIso = {};
  for (const jd of DispatchState.jobDays) {
    const d = jd.date?.toDate ? jd.date.toDate() : new Date(jd.date);
    if (!d || isNaN(d.getTime())) continue;
    if (d.getFullYear() !== _anchorYear || d.getMonth() !== _anchorMonth) continue;
    if (isCancelled(jd)) continue;
    if (!personalizedFilter(jd)) continue;
    const iso = formatDateISO(d);
    if (!_jobDaysByIso[iso]) _jobDaysByIso[iso] = [];
    _jobDaysByIso[iso].push(jd);
  }

  // TASK-320 Slice 2: kick off lazy fetch of own approved PTO. State-cached
  // so re-renders during the same view (e.g. tap a day that isn't off) don't
  // refetch. Re-renders ON the new data once it arrives.
  const _offDaysByIso = _getDriverOwnApprovedOffDaysForMonth(driverPersonId, _anchorYear, _anchorMonth);

  const grid = _sharedBuildCalendarMonthGridHTML(anchor, {
    getBarsForDate: (y, m, day, iso) => {
      const list = _jobDaysByIso[iso] || [];
      const bars = list.map(jd => {
        const job = DispatchState.jobs.find(j => j.id === jd.jobId);
        return {
          title: job?.title || job?.name || 'Untitled',
          color: getCustomerColor(job?.customerId || job?.customer)
        };
      });
      // TASK-320 Slice 2: prepend OFF bar so it visually anchors regardless
      // of how many job bars share the day. Outlined-red flag tells the
      // shared helper to render border-only (planned absence, not error).
      if (_offDaysByIso[iso]) {
        bars.unshift({ title: 'OFF', color: '#dc2626', outlined: true });
      } else if (driverPersonId && _driverRotationOn(iso, driverPersonId)) {
        // TASK-10183 — the driver's OWN regular day off (the callable serves a PIN session only its own person).
        bars.unshift({ title: 'ROTATION', color: '#4F46E5', outlined: true });
      }
      return bars;
    },
    onCellClick: 'window.DispatchModule.jumpDriverDayFromCalendar',
    wrapperClass: 'jobboard-month-view'
  });

  const personalizedNote = (window.AppState?.settings?.personalizedSchedule && driverPersonId)
    ? `<div style="text-align:center;font-size:0.72rem;color:var(--text-secondary);margin-top:10px;">
         Showing only jobs you're assigned to. Tap any day with jobs to open it.
       </div>`
    : `<div style="text-align:center;font-size:0.72rem;color:var(--text-secondary);margin-top:10px;">
         Tap any day with jobs to open it.
       </div>`;

  container.innerHTML = grid + personalizedNote;
}

// TASK-320 Slice 2: returns a synchronously-readable map of `iso → true` for
// every day in `year/month` covered by an APPROVED time-off request belonging
// to `driverPersonId`. First call kicks off the fetch and returns the empty
// map; once data lands the cache is filled and a re-render is triggered so
// the calendar re-paints with OFF bars. Subsequent calls for the same driver
// return the filled cache synchronously without re-fetching.
function _getDriverOwnApprovedOffDaysForMonth(driverPersonId, year, month) {
  if (!driverPersonId) return {};
  const tenant = window.AppState?.tenant;
  if (!tenant) return {};

  const cache = DispatchState._driverOwnApprovedOffCache;
  // Cache invalidates when personId changes (e.g. PIN switch)
  if (!cache || cache.personId !== driverPersonId || cache.tenant !== tenant) {
    DispatchState._driverOwnApprovedOffCache = {
      tenant,
      personId: driverPersonId,
      loaded: false,
      isoDays: {}
    };
    _fetchDriverOwnApprovedOff(tenant, driverPersonId);
    return {};
  }
  if (!cache.loaded) return {};

  // Filter the cached full-list ISO set down to the visible month
  const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
  const out = {};
  for (const iso in cache.isoDays) {
    if (iso.startsWith(monthPrefix)) out[iso] = true;
  }
  return out;
}

function _driverRotationOn(iso, personId) {
  const R = window.DDRotations;
  if (!R) {
    if (!_rotLoad) _rotLoad = __ddChunkWrap(import('./rotationDays.js?v=10183001')).then(() => {
      if (DispatchState.driverViewMode !== 'calendar') return;
      const c = document.getElementById('dispatch-content');
      if (c) renderDriverCalendarView(c);
    }).catch(() => { _rotLoad = null; });
    return false;
  }
  if (!_rotSubscribed) {
    _rotSubscribed = true;
    R.subscribe(() => {
      if (window.AppState?.driverModePin && DispatchState.driverViewMode === 'calendar') { const c = document.getElementById('dispatch-content'); if (c) renderDriverCalendarView(c); } else renderDispatchBoard();
    });
  }
  return R.forDate(iso).some(x => x.personId === personId);
}

async function _fetchDriverOwnApprovedOff(tenant, personId) {
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'timeOff'),
      where('personId', '==', personId),
      where('status', '==', 'approved')
    ));

    // Snapshot guard: another PIN-switch may have replaced the cache
    // mid-flight. Bail if so.
    const cache = DispatchState._driverOwnApprovedOffCache;
    if (!cache || cache.tenant !== tenant || cache.personId !== personId) return;

    const isoDays = {};
    snap.forEach(d => {
      const r = d.data() || {};
      const start = r.startDate?.toDate?.() || (r.startDate ? new Date(r.startDate) : null);
      const end = r.endDate?.toDate?.() || (r.endDate ? new Date(r.endDate) : null);
      if (!start || isNaN(start.getTime())) return;
      // Single-day fallback if no end set
      const cur = new Date(start);
      cur.setHours(0, 0, 0, 0);
      const stop = (end && !isNaN(end.getTime())) ? new Date(end) : new Date(start);
      stop.setHours(0, 0, 0, 0);
      // Hard cap to prevent runaway loops on bad data
      let guard = 0;
      while (cur <= stop && guard < 366) {
        const iso = formatDateISO(cur);
        isoDays[iso] = true;
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
    });

    cache.isoDays = isoDays;
    cache.loaded = true;

    // Re-render only if still in calendar mode (driver may have tapped to day view)
    if (DispatchState.driverViewMode === 'calendar') {
      const container = document.getElementById('dispatch-content');
      if (container) renderDriverCalendarView(container);
    }
  } catch (err) {
    console.warn('[Dispatch] Driver own-PTO fetch error:', err.message);
  }
}

function renderDriverBoard(container) {
  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) return renderScheduleLoading(container);
  // Show/hide Time Off button based on addon access
  const timeOffBtn = document.getElementById('btn-driver-timeoff');
  if (timeOffBtn) {
    const hasTimeOff = window.AppState?.driverModePin && (
      // TASK-865 — settings flag generalized from the trippin-trucking hardcode
      window.AppState?.settings?.driverTimeOff?.enabled === true ||
      (typeof window.isVipAccount === 'function' && window.isVipAccount()) ||
      window.AppState.driverAddons?.hr === true
    );
    timeOffBtn.style.display = hasTimeOff ? '' : 'none';
  }

  // Initialize timesheet section
  initDriverTimesheet();

  // Show Vehicles button for driver PIN mode
  const vehicleBtn = document.getElementById('btn-driver-vehicles');
  if (vehicleBtn && window.AppState?.driverModePin) {
    vehicleBtn.style.display = '';
  }

  // Show Record Expense button for driver PIN mode (Bug #3 follow-up — dispatch.js
  // is where TITS lands, not jobboard.js). Same gate as jobboard.js button.
  // v1.15.495: dropped legacy BI-OR — gate is now strictly
  // `settings.driverExpenses.enabled === true`. Pre-flight scan confirmed 0 tenants
  // were relying on the BI-OR path.
  const recordExpenseBtn = document.getElementById('btn-driver-record-expense-dispatch');
  if (recordExpenseBtn && window.AppState?.driverModePin) {
    const hasExpenseAccess = window.AppState?.settings?.driverExpenses?.enabled === true;
    recordExpenseBtn.style.display = hasExpenseAccess ? '' : 'none';
  }

  // Driver clock button — three-mode label logic (v1.15.359):
  //   simple     → Clock In  / Clock Out
  //   ticket     → Clock In  / Build Ticket
  //   ticketonly → Build Ticket / Build Ticket (always opens Build Ticket flow)
  // Button is ALWAYS visible (so drivers can pick customer+job for unscheduled
  // walk-up work). The flow underneath is the same; the label tells them what
  // they're doing.
  const clockBtn = document.getElementById('btn-driver-clockin-dispatch');
  if (clockBtn && window.AppState?.driverModePin && window.driverHasClockAccess?.()) {
    const driverId = window.AppState?.driverPersonId;
    const myEntry = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
    const label = document.getElementById('btn-driver-clockin-dispatch-label');
    const icon = clockBtn.querySelector('.material-icons');
    const mode = _dspClockMode(driverId); // TASK-9553 per-person clock mode
    const _isTicket = mode === 'ticket' || mode === 'ticketonly';
    const _isTicketOnly = mode === 'ticketonly';

    clockBtn.style.display = '';
    if (myEntry) {
      clockBtn.style.background = _isTicket ? 'linear-gradient(135deg,#3B82F6,#06B6D4)' : '#DC2626';
      if (label) label.textContent = _isTicket ? 'Build Ticket' : 'Clock Out';
      if (icon) icon.textContent = _isTicket ? 'receipt_long' : 'logout';
    } else {
      clockBtn.style.background = _isTicketOnly
        ? 'linear-gradient(135deg,#3B82F6,#06B6D4)'
        : 'linear-gradient(135deg,#166534,#059669)';
      if (label) label.textContent = _isTicketOnly ? 'Build Ticket' : 'Clock In';
      if (icon) icon.textContent = _isTicketOnly ? 'receipt_long' : 'login';
    }
    // Show Switch Job button only when clocked in
    const switchBtn = document.getElementById('btn-driver-switch-job');
    if (switchBtn) switchBtn.style.display = myEntry ? '' : 'none';
  }

  // TASK-347 — sync new bottom-nav Clock tab on the same render pass.
  if (window.AppState?.driverModePin) {
    updateDriverBottomNavClock();
    // Belt-and-suspenders: hide the generic #mobile-nav via JS as well as the
    // :has() CSS rule. Older Safari (<iOS 15.4) doesn't support :has(); without
    // this JS gate those drivers would see TWO bottom nav strips simultaneously
    // (Skeptic 🔴 / UX ⚠️ catch from COD).
    const generic = document.getElementById('mobile-nav');
    if (generic && document.getElementById('driver-bottom-nav')) {
      generic.classList.add('hidden');
    }
    // One-time trailguide tip pointing at the new bottom nav. Dismissed-once
    // via localStorage. Defer until DOM settles so positioning works.
    _maybeShowBottomNavTip();
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Update mode-toggle icon to reflect current state (icon flips: when in
  // calendar mode, the toggle shows view_day to switch back; vice versa).
  const modeIconEl = document.getElementById('driver-view-mode-icon');
  if (modeIconEl) {
    modeIconEl.textContent = DispatchState.driverViewMode === 'calendar' ? 'view_day' : 'calendar_month';
  }

  // Calendar mode short-circuits the day-render path. Falls through to the
  // existing day-mode logic below otherwise.
  if (DispatchState.driverViewMode === 'calendar') {
    renderDriverCalendarView(container);
    return;
  }

  const currentDate = new Date(DispatchState.currentDate);
  currentDate.setHours(0, 0, 0, 0);

  const dateStr = formatDateISO(currentDate);
  let dayJobs = getJobsForDate(dateStr, false);

  // TASK-9381 — the `AppState.restrictedToJobId` client filter that used to sit
  // here is gone with the retired job-restricted link (see the note by
  // calculateDriverPin). A UI filter is not an access control.

  // Filter jobs if using PIN-based driver mode (delegates to shared util:
  // honors tenant-level personalizedSchedule + per-person showAllJobs
  // precedence + legacy-safe ID/name matching across drivers/helpers/people).
  const driverPersonId = window.AppState?.driverPersonId;
  if (window.AppState?.driverModePin && driverPersonId) {
    const driverPerson = DispatchState.people.find(p => p.id === driverPersonId);
    const beforeCount = dayJobs.length;
    dayJobs = dayJobs.filter(({ jobDay }) => _sharedJobDayVisible(jobDay, {
      settings: window.AppState?.settings,
      driverPersonId,
      driverPerson,
    }));
    if (dayJobs.length !== beforeCount) {
      console.log('[Driver] Personalized filter →', dayJobs.length, 'of', beforeCount, 'jobs');
    }
  }

  // Update job count in header
  const countEl = document.getElementById('driver-job-count');
  if (countEl) {
    countEl.style.display = '';
    countEl.textContent = `${dayJobs.length} Job${dayJobs.length !== 1 ? 's' : ''}`;
  }

  // Update date display — main line shows the actual date, sub-line
  // shows the relative label (Today/Yesterday/Tomorrow) when applicable.
  // Both update inside the unified date-pill.
  const dateEl = document.getElementById('current-date');
  const dateSubEl = document.getElementById('current-date-sub');
  if (dateEl) {
    const isToday = currentDate.toDateString() === today.toDateString();
    const isTomorrow = currentDate.toDateString() === new Date(today.getTime() + 86400000).toDateString();
    const isYesterday = currentDate.toDateString() === new Date(today.getTime() - 86400000).toDateString();

    const fullDate = currentDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    let relLabel = '';
    if (isToday) relLabel = 'Today';
    else if (isTomorrow) relLabel = 'Tomorrow';
    else if (isYesterday) relLabel = 'Yesterday';

    dateEl.textContent = fullDate;
    if (dateSubEl) {
      dateSubEl.textContent = relLabel;
      dateSubEl.style.display = relLabel ? '' : 'none';
    }
  }

  // Render jobs list
  if (dayJobs.length === 0) {
    container.innerHTML = `
      <div class="driver-empty-state">
        <span class="driver-empty-icon">📋</span>
        <p class="driver-empty-title">No jobs scheduled</p>
        <p class="driver-empty-text">Nothing on the board for this day</p>
      </div>
    `;
    return;
  }

  let html = '<div class="driver-jobs-cards">';

  dayJobs.forEach(({ job, jobDay }) => {
    html += renderDriverJobCard(job, jobDay, dateStr);
  });

  html += '</div>';
  container.innerHTML = html;
}

// Extracted card markup, originally inlined in renderDriverBoard's forEach.
// Signature: (job, jobDay, dateStr) -> HTML string for ONE card.
// Caller is responsible for the .driver-jobs-cards wrapper + container write.
// TASK-355 2026-05-22 — My Today was killed; this helper remains because the
// driver-PIN renderDriverBoard still consumes it as the canonical card markup.
function renderDriverJobCard(job, jobDay, dateStr) {
  if (!job || !jobDay) return '';
  const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
  const customerColor = getCustomerColor(job.customerId || job.customer);
  const scheduledTime = formatScheduledTime(job);
  const location = jobDay.location || job.location || job.address || '';

  // Get assigned crew and equipment
  const drivers = getAssignedDriverNames(jobDay);
  const helpers = getAssignedHelperNames(jobDay);
  const trucks = getAssignedTruckUnits(jobDay);
  const trailers = getAssignedTrailerUnits(jobDay);
  const assets = getAssignedAssetNames(jobDay);
  // TASK-9763 — "Your truck: Hawk #19" when dispatch paired this driver today.
  const yourUnits = _driverPairedUnitNames(jobDay);

  return `
      <div class="driver-job-card" style="--card-color: ${customerColor};" onclick="window.DispatchModule.showDriverQuickview('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
        <div class="driver-card-stripe" style="background: ${customerColor};"></div>
        <div class="driver-card-content">
          <div class="driver-card-customer">${escapeHtml(customer.name)}</div>
          ${location ? `<div class="driver-card-location">${escapeHtml(location)}</div>` : ''}
          ${scheduledTime ? `<div class="driver-card-time"><span class="time-badge">${scheduledTime}</span></div>` : ''}
          <div class="driver-card-title">${escapeHtml(job.title || 'Job')}</div>
          ${_dayFlagChipsHtml(jobDay)}
          ${yourUnits.length ? `<div class="driver-card-your-truck" style="margin:6px 0 2px;font-size:0.9rem;font-weight:700;color:var(--text-primary);">🚛 Your truck: ${yourUnits.map(n => escapeHtml(n)).join(' + ')}</div>` : ''}

          <div class="driver-card-crew">
            ${drivers.length ? `<div class="crew-line"><span class="crew-icon${hasActiveClock(jobDay) ? ' clocked-in' : ''}">👤</span> ${drivers.map(d => escapeHtml(d)).join(', ')}</div>` : ''}
            ${helpers.length ? `<div class="crew-line"><span class="crew-icon">🧑‍🔧</span> ${helpers.map(h => escapeHtml(h)).join(', ')}</div>` : ''}
            ${trucks.length ? `<div class="crew-line"><span class="crew-icon">🚛</span> ${trucks.map(t => {
              const truckObj = (DispatchState.trucks || []).find(tr => (window.getUnitDisplay ? window.getUnitDisplay(tr) : tr.unit) === t || tr.unit === t || tr.name === t);
              return truckObj ? `<span style="cursor:pointer;text-decoration:underline;text-decoration-style:dotted;" onclick="event.stopPropagation();window.DispatchModule.showDriverVehicleInfo('truck','${escJsAttr(truckObj.id)}')">${escapeHtml(t)}</span>` : escapeHtml(t);
            }).join(', ')}</div>` : ''}
            ${trailers.length ? `<div class="crew-line"><span class="crew-icon">🚚</span> ${trailers.map(t => {
              const trailerObj = (DispatchState.trailers || []).find(tr => (window.getUnitDisplay ? window.getUnitDisplay(tr) : tr.unit) === t || tr.unit === t || tr.name === t);
              return trailerObj ? `<span style="cursor:pointer;text-decoration:underline;text-decoration-style:dotted;" onclick="event.stopPropagation();window.DispatchModule.showDriverVehicleInfo('trailer','${escJsAttr(trailerObj.id)}')">${escapeHtml(t)}</span>` : escapeHtml(t);
            }).join(', ')}</div>` : ''}
            ${assets.length ? `<div class="crew-line"><span class="crew-icon">🔧</span> ${assets.map(a => escapeHtml(a)).join(', ')}</div>` : ''}
            ${getMissingDvirIndicator(jobDay, dateStr, 'driver-card')}
            ${getMissingJsaIndicator(jobDay, dateStr, 'driver-card')}
            ${getJsaPresentIndicator(jobDay, 'driver-card')}
          </div>

          <button type="button" class="driver-view-details-btn" onclick="event.preventDefault(); event.stopPropagation(); window.DispatchModule.showDriverQuickview('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
            <span class="material-icons">visibility</span> View Details
          </button>
        </div>
      </div>
    `;
}

// Get assigned driver names for a jobDay
function getAssignedDriverNames(jobDay) {
  if (!jobDay.drivers || !Array.isArray(jobDay.drivers)) return [];
  return jobDay.drivers.map(ref => {
    const r = String(ref);
    const person = DispatchState.people.find(p => p.id === r || p.name === r || String(p.id) === r);
    return person ? person.name : null;
  }).filter(Boolean);
}

// Get assigned helper names for a jobDay
function getAssignedHelperNames(jobDay) {
  if (!jobDay.helpers || !Array.isArray(jobDay.helpers)) return [];
  return jobDay.helpers.map(ref => {
    const r = String(ref);
    const person = DispatchState.people.find(p => p.id === r || p.name === r || String(p.id) === r);
    return person ? person.name : null;
  }).filter(Boolean);
}

// Get assigned truck units for a jobDay
function getAssignedTruckUnits(jobDay) {
  if (!jobDay.trucks || !Array.isArray(jobDay.trucks)) return [];
  return jobDay.trucks.map(ref => {
    const r = String(ref);
    const truck = _resolveFleetRef(r, DispatchState.trucks);
    return truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name)) : null;
  }).filter(Boolean);
}

// Get assigned trailer units for a jobDay
function getAssignedTrailerUnits(jobDay) {
  if (!jobDay.trailers || !Array.isArray(jobDay.trailers)) return [];
  return jobDay.trailers.map(ref => {
    const r = String(ref);
    const trailer = _resolveFleetRef(r, DispatchState.trailers);
    return trailer ? (window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit || trailer.name)) : null;
  }).filter(Boolean);
}

// Get assigned asset names for a jobDay
function getAssignedAssetNames(jobDay) {
  if (!jobDay.assets || !Array.isArray(jobDay.assets)) return [];
  return jobDay.assets.map(ref => {
    const r = String(ref);
    const asset = _resolveFleetRef(r, DispatchState.assets);
    return asset ? asset.name : null;
  }).filter(Boolean);
}

// Show driver quickview modal
function showDriverQuickview(jobId, jobDayId) {
  console.log('[Dispatch] showDriverQuickview called:', { jobId, jobDayId });

  const job = DispatchState.jobs.find(j => j.id === jobId);
  const jobDay = DispatchState.jobDays.find(jd => jd.id === jobDayId);

  if (!job) {
    console.warn('[Dispatch] Job not found for quickview:', jobId);
    window.showToast?.('Job not found - try refreshing', 'error');
    return;
  }

  // Check if driver quickview modal exists (it should be in the DOM for driver mode)
  const modal = document.getElementById('modal-driver-quickview');
  if (!modal) {
    console.error('[Dispatch] Driver quickview modal not found in DOM');
    window.showToast?.('Unable to show details - try refreshing the page', 'error');
    return;
  }

  DispatchState.quickviewJob = job;
  DispatchState.quickviewJobDay = jobDay;

  const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
  const customerColor = getCustomerColor(job.customerId || job.customer);

  // Update header (use driver-qv- prefixed IDs to avoid conflicts with dispatcher quickview)
  const headerEl = document.getElementById('driver-qv-header');
  const customerEl = document.getElementById('driver-qv-customer');
  const titleEl = document.getElementById('driver-qv-title');

  if (headerEl) {
    headerEl.style.background = `linear-gradient(135deg, ${customerColor}, ${adjustColor(customerColor, -30)})`;
  }
  if (customerEl) customerEl.textContent = customer.name;
  if (titleEl) titleEl.textContent = job.title || 'Job';

  // Build body content
  const scheduledTime = formatScheduledTime(job);
  const location = job.location || job.address || '';
  const drivers = jobDay ? getAssignedDriverNames(jobDay) : [];
  const trucks = jobDay ? getAssignedTruckUnits(jobDay) : [];
  const qvAssets = jobDay ? getAssignedAssetNames(jobDay) : [];

  // Get POC info - handle both object and string formats
  let pocName = '', pocPhone = '', pocPhone2 = '', pocEmail = '', pocRole = '';
  if (job.pocId && (customer.contacts || customer._mergedContacts)) {
    const allContacts = customer._mergedContacts || customer.contacts || [];
    const poc = allContacts.find(c => c.id === job.pocId || c._crmId === job.pocId || c.name === job.pocId);
    if (poc) {
      pocName = poc.name || '';
      pocPhone = poc.phone || '';
      pocPhone2 = poc.phone2 || '';
      pocEmail = poc.email || '';
      pocRole = poc.role || '';
    }
  } else if (job.poc) {
    // Handle case where poc is an object with name/phone
    if (typeof job.poc === 'object' && job.poc !== null) {
      pocName = job.poc.name || '';
      pocPhone = job.poc.phone || job.pocPhone || '';
      pocPhone2 = job.poc.phone2 || '';
      pocEmail = job.poc.email || '';
      pocRole = job.poc.role || '';
    } else {
      pocName = job.poc;
      pocPhone = job.pocPhone || '';
    }
  }

  // Format date properly (use formatDateDisplay to handle UTC timezone shift)
  let dateDisplay = '';
  if (jobDay?.date) {
    const d = jobDay.date?.toDate?.() || new Date(jobDay.date);
    if (!isNaN(d.getTime())) {
      dateDisplay = formatDateDisplay(d);
    }
  }

  let bodyHtml = '<div class="driver-qv-content">';

  // Date & Time (compact)
  if (dateDisplay || scheduledTime) {
    bodyHtml += `<div class="driver-qv-row">`;
    if (dateDisplay) bodyHtml += `<span>📅 ${dateDisplay}</span>`;
    if (scheduledTime) bodyHtml += `<span>🕐 ${scheduledTime}</span>`;
    bodyHtml += `</div>`;
  }

  // Location
  if (location) {
    bodyHtml += `<div class="driver-qv-row"><span>📍 ${escapeHtml(location)}</span></div>`;
  }

  // Waypoints — each with a directions button
  if (job.waypoints && job.waypoints.length > 0) {
    bodyHtml += `<div class="driver-qv-waypoints">`;
    job.waypoints.forEach((wp, i) => {
      const label = escapeHtml(wp.name || `Destination ${i + 1}`);
      const dest = wp.url || wp.name || '';
      let dirUrl;
      if (!dest.startsWith('http')) {
        dirUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
      } else if (isAppleMapsUrl(dest)) {
        // Convert Apple Maps URL to Google Maps so it works natively on all devices
        const u = new URL(dest);
        const ll = u.searchParams.get('ll');
        const q = u.searchParams.get('q');
        const addr = u.searchParams.get('address');
        const daddr = u.searchParams.get('daddr');
        const loc = ll || q || daddr || addr || '';
        dirUrl = loc ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc)}` : dest;
      } else {
        dirUrl = dest;
      }
      const isMapLink = !dest.startsWith('http') || dest.includes('google.com/maps') || isAppleMapsUrl(dest) || dest.includes('goo.gl');
      const wpIcon = isMapLink ? 'near_me' : 'link';
      // TASK-10116 — same scheme gate as every other stop link.
      const safeDirUrl = waypointOpenHref(dirUrl);
      if (!safeDirUrl) {
        bodyHtml += `
        <div class="driver-qv-waypoint">
          <span class="material-icons">place</span>
          <span class="driver-qv-waypoint-name">${label}</span>
        </div>`;
        return;
      }
      bodyHtml += `
        <a href="${escapeHtml(safeDirUrl)}" target="_blank" rel="noopener noreferrer" class="driver-qv-waypoint">
          <span class="material-icons">${wpIcon}</span>
          <span class="driver-qv-waypoint-name">${label}</span>
          <span class="material-icons" style="margin-left:auto;font-size:16px;opacity:0.6;">open_in_new</span>
        </a>`;
    });
    bodyHtml += `</div>`;
  }

  // Crew (just names, no label)
  if (drivers.length) {
    bodyHtml += `<div class="driver-qv-row"><span>👥 ${drivers.map(d => escapeHtml(d)).join(', ')}</span></div>`;
  }

  // Trucks (simple)
  if (trucks.length) {
    bodyHtml += `<div class="driver-qv-row"><span>🚛 ${trucks.map(t => escapeHtml(t)).join(', ')}</span></div>`;
  }

  // Assets
  if (qvAssets.length) {
    bodyHtml += `<div class="driver-qv-row"><span>🔧 ${qvAssets.map(a => escapeHtml(a)).join(', ')}</span></div>`;
  }

  // Load counter (rich progress-bar OR fallback +/- if assets lack
  // unitWeight). The whole point of the recent driver-view refactor —
  // make sure the counter is ALWAYS reachable from the driver quickview.
  if (jobDay) {
    bodyHtml += buildLoadCounterHTML(jobDay, job.id);
  }

  // Contact (if available) - show full POC card
  if (pocName || pocPhone) {
    bodyHtml += `<div class="driver-qv-poc">`;
    bodyHtml += `<div class="driver-qv-poc-name">📞 ${escapeHtml(pocName)}${pocRole ? ` <span class="driver-qv-poc-role">(${escapeHtml(pocRole)})</span>` : ''}</div>`;
    if (pocPhone) {
      bodyHtml += `<a href="tel:${escapeHtml(pocPhone)}" class="driver-qv-poc-link">${escapeHtml(pocPhone)}</a>`;
    }
    if (pocPhone2) {
      bodyHtml += `<a href="tel:${escapeHtml(pocPhone2)}" class="driver-qv-poc-link">${escapeHtml(pocPhone2)}</a>`;
    }
    if (pocEmail) {
      bodyHtml += `<a href="mailto:${escapeHtml(pocEmail)}" class="driver-qv-poc-link">${escapeHtml(pocEmail)}</a>`;
    }
    bodyHtml += `</div>`;
  }

  // PO Number (only if flagged to show to drivers)
  if (job.poNumber && job.poVisible) {
    bodyHtml += `<div class="driver-qv-row"><span>📋 PO: ${escapeHtml(job.poNumber)}</span></div>`;
  }

  // TASK-9390 — job type, hidden for the default "Job". Tenant-typed text: escaped.
  if (!_jobTypeIsDefault(job.type)) {
    bodyHtml += `<div class="driver-qv-row"><span>🏷️ Type: ${escapeHtml(_jobTypeLabelForJob(job))}</span></div>`;
  }

  // Notes
  if (job.notes) {
    bodyHtml += `<div class="driver-qv-notes">📝 ${escapeHtml(job.notes)}</div>`;
  }

  // Photos (only if flagged to show to drivers)
  if (job.photosVisible && job.photos && job.photos.length > 0) {
    bodyHtml += `<div class="driver-qv-photos">
      <div class="driver-qv-photos-label">📎 Attachments</div>
      <div class="driver-qv-photos-grid">`;
    job.photos.forEach((photo, idx) => {
      // TASK-9855 — SDK read on the stored path (fullPath/thumbPath), not the token URL.
      const isPdf = photo.name?.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        bodyHtml += `
          <a ${window.StorageFile.linkAttrs(photo)} class="driver-qv-photo-item driver-qv-pdf">
            <span class="material-icons">picture_as_pdf</span>
            <span class="driver-qv-pdf-name">${escapeHtml(photo.name || 'Document')}</span>
          </a>`;
      } else {
        bodyHtml += `
          <a ${window.StorageFile.linkAttrs(photo)} class="driver-qv-photo-item">
            <img ${window.StorageFile.imgAttrs(photo, { urlKey: photo.thumbUrl ? 'thumbUrl' : undefined })} alt="Attachment ${idx + 1}" loading="lazy">
          </a>`;
      }
    });
    bodyHtml += `</div></div>`;
  }

  // Load Permits (multi-file uploads attached at dispatch — drivers need on-site).
  // TASK-9694 — a driver's job-screen Doc rides in job.permits[] but is listed
  // under Docs, never as a permit.
  const _qvFiles = Array.isArray(job.permits) ? job.permits : [];
  for (const [_qvLabel, _qvList] of [['📄 Load Permits', _qvFiles.filter(f => !isJobDocEntry(f, job))], ['📎 Docs', _qvFiles.filter(f => isJobDocEntry(f, job))]]) {
    if (!_qvList.length) continue;
    bodyHtml += `<div class="driver-qv-photos">
      <div class="driver-qv-photos-label">${_qvLabel}</div>
      <div class="driver-qv-photos-grid">`;
    _qvList.forEach((permit, idx) => {
      // TASK-9855 — SDK read on the stored path (fullPath/thumbPath), not the token URL.
      const isPdf = permit.isPdf || permit.mimeType === 'application/pdf' || permit.name?.toLowerCase().endsWith('.pdf');
      if (isPdf) {
        bodyHtml += `
          <a ${window.StorageFile.linkAttrs(permit)} class="driver-qv-photo-item driver-qv-pdf">
            <span class="material-icons">picture_as_pdf</span>
            <span class="driver-qv-pdf-name">${escapeHtml(permit.name || (isJobDocEntry(permit, job) ? 'Document' : 'Permit'))}</span>
          </a>`;
      } else {
        bodyHtml += `
          <a ${window.StorageFile.linkAttrs(permit)} class="driver-qv-photo-item">
            <img ${window.StorageFile.imgAttrs(permit, { urlKey: permit.thumbUrl ? 'thumbUrl' : undefined })} alt="${isJobDocEntry(permit, job) ? 'Document' : 'Permit'} ${idx + 1}" loading="lazy">
          </a>`;
      }
    });
    bodyHtml += `</div></div>`;
  }

  // 811 tickets — TASK-9648: EVERY linked ticket (drivers must see every
  // locate, not just the first). Escaped name, http(s)-only href.
  if (is811Enabled()) {
    for (const pid of _jobShown811Ids(job)) { // TASK-9767 — + the project's
      const permit = DispatchState.permits.find(p => p.id === pid);
      const attrs = _permitLinkAttrs(permit);
      if (permit && attrs) {
        bodyHtml += `
        <div class="driver-qv-permit">
          <a ${attrs} class="driver-qv-permit-link">
            <span class="material-icons">assignment</span>
            <span>811 ticket: ${escapeHtml(permit.name || '811 ticket')}</span>
            <span class="material-icons" style="margin-left:auto;font-size:18px;">open_in_new</span>
          </a>
        </div>`;
      } else {
        bodyHtml += `
        <div class="driver-qv-permit driver-qv-permit-missing">
          <span class="material-icons">info</span>
          <span>811 ticket on this job isn't available here. Ask dispatch for a copy before digging.</span>
        </div>`;
      }
    }
  }

  bodyHtml += '</div>';

  const bodyEl = document.getElementById('driver-qv-body');
  if (bodyEl) bodyEl.innerHTML = bodyHtml;

  // Update footer buttons (use driver-qv- prefixed IDs to avoid conflicts with dispatcher quickview)
  const directionsBtn = document.getElementById('driver-qv-directions');
  const vehicleBtn = document.getElementById('driver-qv-vehicle');
  const callBtn = document.getElementById('driver-qv-call');

  if (directionsBtn) {
    if (location) {
      directionsBtn.classList.remove('hidden');
    } else {
      directionsBtn.classList.add('hidden');
    }
  }

  // Show vehicle button if trucks assigned
  if (vehicleBtn) {
    if (jobDay?.trucks?.length > 0) {
      vehicleBtn.classList.remove('hidden');
      // Update button text based on number of trucks
      const btnText = jobDay.trucks.length > 1 ? 'Vehicles' : 'Vehicle';
      vehicleBtn.innerHTML = `<span class="material-icons">local_shipping</span> ${btnText}`;
    } else {
      vehicleBtn.classList.add('hidden');
    }
  }

  if (callBtn) {
    if (pocPhone) {
      callBtn.classList.remove('hidden');
      callBtn.onclick = () => window.open(`tel:${pocPhone}`, '_self');
    } else {
      callBtn.classList.add('hidden');
    }
  }

  // Show modal
  modal.classList.remove('hidden');
}

function openDirections() {
  const job = DispatchState.quickviewJob;
  if (!job) return;

  const waypoints = job.waypoints || [];

  // Multi-waypoint route: chain all stops into Google Maps directions
  if (waypoints.length >= 2) {
    // Use lat/lng if available, fall back to address text
    const stops = waypoints.map(w => {
      // TASK-1615 — an out-of-range pair sends the driver nowhere. Fall back to
      // the address text, which Google can still resolve.
      const c = plottableCoord(w);
      if (c) return `${c.lat},${c.lng}`;
      return w.address || w.location || w.name || '';
    }).filter(s => s);

    if (stops.length >= 2) {
      const destination = stops[stops.length - 1];
      const midStops = stops.slice(0, -1);
      let mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
      if (midStops.length > 0) {
        mapsUrl += `&waypoints=${midStops.map(s => encodeURIComponent(s)).join('|')}`;
      }
      window.open(mapsUrl, '_blank');
      return;
    }
  }

  // Single location fallback
  const location = job.location || job.address || '';
  if (location) {
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`;
    window.open(mapsUrl, '_blank');
  }
}

// Store current driver view truck list for multi-truck selector
let currentDriverTruckIds = [];

async function showDriverTruckDetails(specificTruckRef = null) {
  const jobDay = DispatchState.quickviewJobDay;
  if (!jobDay?.trucks?.length) return;

  // Store all truck IDs for the selector
  currentDriverTruckIds = jobDay.trucks;

  // Get specific truck or first truck (primary)
  const truckRef = specificTruckRef || jobDay.trucks[0];
  const truck = _resolveFleetRef(truckRef, DispatchState.trucks);

  if (!truck) {
    window.showToast?.('Vehicle not found', 'error');
    return;
  }

  // Update title based on number of trucks
  const titleEl = document.getElementById('driver-truck-title');
  if (currentDriverTruckIds.length > 1) {
    titleEl.textContent = `Vehicles (${currentDriverTruckIds.length})`;
  } else {
    titleEl.textContent = `Vehicle - ${window.getUnitDisplay ? window.getUnitDisplay(truck) : truck.unit}`;
  }

  // TASK-9855 — vehicle docs open through the Storage SDK on their path. A
  // driver session is denied truck-docs by storage.rules, so the
  // 'driverVehicle' door (job-day-scoped callable) serves them instead.
  const _vDoor = { door: 'driverVehicle', doorArgs: { vtype: 'truck', vid: String(truck.id) } };

  // Build DOT docs section
  const inspectionDocs = truck.inspectionDocs || [];
  const inspectionDocsHtml = inspectionDocs.length > 0
    ? inspectionDocs.map(doc => `
        <a ${window.StorageFile.linkAttrs(doc, _vDoor)} class="dot-doc-link">
          <span class="material-icons">${doc.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
          <span>${escapeHtml(doc.name || 'DOT Inspection')}</span>
          <span class="material-icons">open_in_new</span>
        </a>
      `).join('')
    : '<p class="no-docs">No documents uploaded</p>';

  // Build registration docs section
  const registrationDocs = truck.registrationDocs || [];
  const registrationDocsHtml = registrationDocs.length > 0
    ? registrationDocs.map(doc => `
        <a ${window.StorageFile.linkAttrs(doc, _vDoor)} class="dot-doc-link">
          <span class="material-icons">${doc.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
          <span>${escapeHtml(doc.name || 'Registration')}</span>
          <span class="material-icons">open_in_new</span>
        </a>
      `).join('')
    : '<p class="no-docs">No documents uploaded</p>';

  // Build insurance docs section
  const insuranceDocs = truck.insuranceDocs || [];
  const insuranceDocsHtml = insuranceDocs.length > 0
    ? insuranceDocs.map(doc => `
        <a ${window.StorageFile.linkAttrs(doc, _vDoor)} class="dot-doc-link">
          <span class="material-icons">${doc.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
          <span>${escapeHtml(doc.name || 'Insurance')}</span>
          <span class="material-icons">open_in_new</span>
        </a>
      `).join('')
    : '<p class="no-docs">No documents uploaded</p>';

  // Build truck info HTML
  let html = '';

  // Add truck selector if multiple trucks
  if (currentDriverTruckIds.length > 1) {
    html += `<div class="truck-selector-tabs">`;
    currentDriverTruckIds.forEach(id => {
      const t = _resolveFleetRef(id, DispatchState.trucks);
      const unit = t ? (window.getUnitDisplay?.(t) || 'Unknown') : id;
      const isActive = (t && t.id === truck.id) || id === truckRef;
      html += `
        <button class="truck-tab ${isActive ? 'active' : ''}" onclick="window.DispatchModule.showDriverTruckDetails('${escJsAttr(id)}')">
          <span class="material-icons">local_shipping</span> ${escapeHtml(unit)}
        </button>
      `;
    });
    html += `</div>`;
  }

  html += `
    <div class="driver-truck-info">
      <div class="truck-info-section">
        <h4><span class="material-icons">local_shipping</span> Vehicle Info</h4>
        <div class="truck-info-grid">
          <div class="info-item"><span class="info-label">Unit #</span><span class="info-value">${escapeHtml(truck.unit)}</span></div>
          ${truck.nickname ? `<div class="info-item"><span class="info-label">Name</span><span class="info-value">${escapeHtml(truck.nickname)}</span></div>` : ''}
          <div class="info-item"><span class="info-label">Type</span><span class="info-value">${escapeHtml(truck.type || 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">Year</span><span class="info-value">${escapeHtml(truck.year || 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">Make</span><span class="info-value">${escapeHtml(truck.make || 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">Model</span><span class="info-value">${escapeHtml(truck.model || 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">VIN</span><span class="info-value">${escapeHtml(truck.vin || 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">License</span><span class="info-value">${escapeHtml(truck.licensePlate || 'N/A')} ${escapeHtml(truck.licensePlateState)}</span></div>
        </div>
      </div>
      ${(truck.gvwr || truck.gcwr) ? `
      <div class="truck-info-section">
        <h4><span class="material-icons">scale</span> Weight Ratings</h4>
        <div class="truck-info-grid">
          <div class="info-item"><span class="info-label">GVWR</span><span class="info-value">${escapeHtml(truck.gvwr ? truck.gvwr.toLocaleString() + ' lbs' : 'N/A')}</span></div>
          <div class="info-item"><span class="info-label">GCWR</span><span class="info-value">${escapeHtml(truck.gcwr ? truck.gcwr.toLocaleString() + ' lbs' : 'N/A')}</span></div>
          ${truck.curbWeight ? `<div class="info-item"><span class="info-label">Curb Weight</span><span class="info-value">${escapeHtml(truck.curbWeight.toLocaleString())} lbs</span></div>` : ''}
        </div>
      </div>
      ` : ''}
    </div>
    <div class="driver-truck-docs">
      <h4><span class="material-icons">verified</span> DOT Inspection</h4>
      ${truck.inspectionExpiry ? `<p class="inspection-expiry">Expires: <strong>${escapeHtml(String(truck.inspectionExpiry))}</strong></p>` : ''}
      <div class="dot-docs-list">
        ${inspectionDocsHtml}
      </div>
    </div>
    <div class="driver-truck-docs">
      <h4><span class="material-icons">description</span> Registration</h4>
      <div class="dot-docs-list">
        ${registrationDocsHtml}
      </div>
    </div>
    <div class="driver-truck-docs">
      <h4><span class="material-icons">shield</span> Insurance</h4>
      <div class="dot-docs-list">
        ${insuranceDocsHtml}
      </div>
    </div>
    ${(truck.documents || []).filter(d => d.showOnJobBoard).map(d => `
    <div class="driver-truck-docs">
      <h4><span class="material-icons">folder_open</span> ${escapeHtml(d.label || d.type || 'Document')}${d.expiresAt ? ` <span style="font-size:0.75rem;color:var(--text-secondary);font-weight:normal;">Exp: ${escapeHtml(d.expiresAt)}</span>` : ''}</h4>
      <div class="dot-docs-list">
        <a ${window.StorageFile.linkAttrs(d, _vDoor)} class="dot-doc-link">
          <span class="material-icons">${d.name?.endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
          <span>${escapeHtml(d.name || 'Document')}</span>
          <span class="material-icons">open_in_new</span>
        </a>
      </div>
    </div>
    `).join('')}
    <div class="driver-truck-dvirs">
      <h4><span class="material-icons">checklist</span> Recent DVIRs</h4>
      <div id="driver-truck-dvirs-list" class="dvirs-loading">
        <span class="material-icons rotating">sync</span> Loading inspections...
      </div>
    </div>
  `;

  document.getElementById('driver-truck-body').innerHTML = html;
  document.getElementById('modal-driver-truck').classList.remove('hidden');

  // Fetch DVIRs for this truck
  try {
    const tenantId = DispatchState.tenantId;
    const dvirsQuery = query(
      collection(db, 'tenants', tenantId, 'pretrips'),
      where('truckId', '==', truck.id),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(dvirsQuery);
    const dvirs = snapshot.docs.slice(0, 10).map(doc => ({ id: doc.id, ...doc.data() }));

    const dvirsContainer = document.getElementById('driver-truck-dvirs-list');
    if (dvirs.length === 0) {
      dvirsContainer.innerHTML = '<p class="no-dvirs">No inspections on file</p>';
    } else {
      dvirsContainer.innerHTML = dvirs.map(dvir => {
        const date = dvir.createdAt?.toDate?.() || new Date();
        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const hasDefects = dvir.status === 'defects' || (dvir.defects && dvir.defects.length > 0);
        return `
          <div class="dvir-item ${hasDefects ? 'has-defects' : 'pass'}">
            <span class="material-icons dvir-status-icon">${hasDefects ? 'warning' : 'check_circle'}</span>
            <div class="dvir-item-info">
              <div class="dvir-item-date">${dateStr}</div>
              <div class="dvir-item-driver">${escapeHtml(dvir.driverName || 'Unknown')}</div>
            </div>
            <span class="dvir-type-badge">${dvir.type === 'pre' ? 'Pre' : 'Post'}</span>
          </div>
        `;
      }).join('');
    }
  } catch (err) {
    console.error('[Dispatch] Error loading DVIRs:', err);
    document.getElementById('driver-truck-dvirs-list').innerHTML = '<p class="dvirs-error">Could not load inspections</p>';
  }
}

function refreshDriverBoard() {
  // User-tapped Refresh — sync render so the toast reflects a painted board.
  _renderDispatchBoardNow();
  window.showToast?.('Refreshed', 'success');
}

function renderWeekView(container) {
  // TASK-4588 — was _jobDaysLoaded only, which still paints an empty week when
  // jobDays lands before jobs. This is the DEFAULT view, so it had the race most.
  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) {
    container.innerHTML = `
      <div style="text-align:center;padding:var(--spacing-xl);">
        <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
        <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
      </div>
    `;
    return;
  }

  const weekStart = getWeekStart(DispatchState.currentDate);
  const days = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(day.getDate() + i);
    days.push(day);
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let html = `<div class="week-view">`;

  // Header row
  html += `<div class="week-header">`;
  days.forEach(day => {
    const isToday = day.toDateString() === today.toDateString();
    const dayName = day.toLocaleDateString('en-US', { weekday: 'short' });
    const dateNum = day.getDate();
    html += `
      <div class="week-day-header ${isToday ? 'today' : ''}">
        <span class="day-name">${dayName}</span>
        <span class="day-num">${dateNum}</span>
      </div>
    `;
  });
  html += `</div>`;

  const filters = DispatchState.calendarFilters || { jobs: true, daysoff: true, maintenance: true, completed: true };
  let hiddenItemsWeek = 0;

  // Content row
  html += `<div class="week-content">`;
  days.forEach(day => {
    const dayStr = formatDateISO(day);
    // TASK-4646 — one call, both answers. `hidden` already accounts for BOTH the
    // Jobs chip (everything) and the Completed chip (finished only).
    const weekJobSplit = getCalendarJobsForDate(dayStr, filters);
    const dayJobs = weekJobSplit.visible;
    const isToday = day.toDateString() === today.toDateString();

    const dayLabel = day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
    html += `<div class="week-day ${isToday ? 'today' : ''}" data-date="${dayStr}">`;
    html += `<div class="week-day-label">${isToday ? '<span class="today-badge">Today</span> ' : ''}${dayLabel}</div>`;

    // Safety holds AND maintenance vehicle holds ALWAYS render — they're a
    // dispatcher safety net, not a layer the user opts out of (TASK-461 removed
    // the Maintenance chip; FMCSA §396.9/§396.11 — never hide a critical defect).
    const dayHolds = getSafetyHoldsForDate(dayStr, isToday);
    // Get approved time-off for this day
    const dayTimeOff = filters.daysoff ? getTimeOffForDate(dayStr) : [];
    // Pipeline holds fold under the Jobs chip (COD decision).
    const dayOppHolds = filters.jobs ? getOpportunityHoldsForDate(dayStr) : [];
    // Maintenance vehicle holds — always visible (chip removed, TASK-461)
    const dayVehicleHolds = getVehicleHoldsForDate(dayStr);
    // Hidden-item counter for the warning pill (vehicle holds never hidden now).
    // TASK-4646 — weekJobSplit.hidden covers jobs hidden by EITHER chip; only the
    // opportunity-hold half still needs its own count when Jobs is off.
    hiddenItemsWeek += weekJobSplit.hidden;
    if (!filters.jobs) hiddenItemsWeek += getOpportunityHoldsForDate(dayStr).length;
    if (!filters.daysoff) hiddenItemsWeek += getTimeOffForDate(dayStr).length;

    if (dayJobs.length === 0 && dayHolds.length === 0 && dayTimeOff.length === 0 && dayOppHolds.length === 0 && dayVehicleHolds.length === 0) {
      const allFiltered = !filters.jobs && !filters.daysoff;
      const anyHidden = !filters.jobs || !filters.daysoff || filters.completed === false;
      const realHasItems = anyHidden && (weekJobSplit.hidden + (filters.daysoff ? 0 : getTimeOffForDate(dayStr).length) + (filters.jobs ? 0 : getOpportunityHoldsForDate(dayStr).length) > 0);
      if (allFiltered) {
        html += `<div class="day-empty"><span class="material-icons">visibility_off</span> All layers hidden</div>`;
      } else if (realHasItems) {
        html += `<div class="day-empty"><span class="material-icons">visibility_off</span> Hidden by filter</div>`;
      } else {
        html += `<div class="day-empty"><span class="material-icons">event_available</span> No jobs</div>`;
      }
    } else {
      // Render safety holds first (they're urgent)
      dayHolds.forEach(hold => {
        // TASK-9198 — was getTruckUnitById(hold.truckId), which searched
        // DispatchState.trucks ONLY and returned null for the trailer/asset ids
        // that pretrip parks in truckId → "Unit ? - Safety Hold" on the board.
        const holdLabel = getSafetyHoldLabel(hold);
        // TASK-9287 sibling — hold.id is a MAINTENANCE doc id, and rows are built
        // `{ id: doc.id, ...doc.data() }` (spread SECOND), so a stored `id` field
        // in the body wins. A driver-PIN session can create a critical task, and
        // the TASK-4778 doc-id charset fence constrains the path, not that field.
        // JS string inside an attribute -> escJsAttr, never escapeHtml.
        html += `
          <div class="safety-hold-card" onclick="window.DispatchModule.showSafetyHoldDetails('${escJsAttr(hold.id)}')">
            <div class="safety-hold-icon"><span class="material-icons">block</span></div>
            <div class="safety-hold-content">
              <div class="safety-hold-title">${escapeHtml(holdLabel.noun)} ${escapeHtml(holdLabel.text)} - Safety Hold</div>
              <div class="safety-hold-desc">${escapeHtml(truncateText(hold.description || hold.text || 'Critical defect', 40))}</div>
            </div>
          </div>
        `;
      });

      // Render maintenance vehicle hold cards
      dayVehicleHolds.forEach(hold => {
        html += renderVehicleHoldCard(hold);
      });

      // Render opportunity hold cards
      dayOppHolds.forEach(hold => {
        html += renderOpportunityHoldCard(hold);
      });

      // Render jobs
      dayJobs.forEach(({ job, jobDay }) => {
        // TASK-4646 — archived history paints DIMMED, never hidden. `.job-card.finished`
        // is already opacity:0.7 in styles.css; the only change is that job.status now
        // counts too, so an auto-closed job whose jobDay was never stamped still reads
        // as past work instead of masquerading as a live assignment.
        const statusClass = jobDay.status === 'cancelled' ? 'cancelled' :
                            (jobDay.status === 'finished' || job.status === 'finished') ? 'finished' : '';
        const customerColor = getCustomerColor(job.customerId || job.customer);
        const truckUnit = getTruckUnit(jobDay);
        const driverName = getDriverName(jobDay);
        // Only show status badge for non-default statuses. 'unassigned' is a
        // default-ish state too (TITS jobDays sit in it until crewed) and the
        // faint truck/person indicators already convey it — badging it painted
        // ghost "Unassigned" text across long placard titles (Chris 2026-08-13).
        const showStatus = jobDay.status && jobDay.status !== 'scheduled' && jobDay.status !== 'assigned' && jobDay.status !== 'unassigned';
        // TASK-4646 — dimming alone is ambiguous at a glance ("is that greyed out or
        // just low contrast?"). Say the word. Falls back to the job-level badge only
        // when the jobDay itself carries no interesting status of its own, so this
        // never overwrites an 'en-route'/'cancelled' badge the dispatcher relies on.
        const statusBadge = showStatus
          ? `<span class="week-status-badge week-status-${jobDay.status}">${jobDay.status}</span>`
          : (job.status === 'finished' ? `<span class="week-status-badge week-status-finished">finished</span>` : '');

        // Compact assignment indicators (clickable popovers)
        const hasAssignments = truckUnit || driverName;
        const dvirIndicator = getMissingDvirIndicator(jobDay, dayStr, 'compact');
        const jsaIndicator = getMissingJsaIndicator(jobDay, dayStr, 'compact');
        const jsaPresentIndicator = getJsaPresentIndicator(jobDay, 'compact'); // TASK-794
        const truckIndicator = truckUnit
          ? `<span class="assign-indicator assigned" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'trucks')">🚛</span>`
          : '<span class="assign-indicator">🚛</span>';
        const clockedIn = hasActiveClock(jobDay);
        const personIndicator = driverName
          ? `<span class="assign-indicator assigned${clockedIn ? ' clocked-in' : ''}" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'people')">👤</span>`
          : '<span class="assign-indicator">👤</span>';
        const weekTicketDot = getTicketStatusDot(job.id, dayStr);
        const weekUnpinnedBadge = buildUnpinnedStopBadge(job, 'compact');   // TASK-9194
        const assignmentIndicators = hasAssignments
          ? `${truckIndicator}${personIndicator}${dvirIndicator}${jsaIndicator}${jsaPresentIndicator}${weekTicketDot}${weekUnpinnedBadge}`
          : `${dvirIndicator}${jsaIndicator}${jsaPresentIndicator}${weekTicketDot}${weekUnpinnedBadge}`;

        const weekRouteMiles = isRoutePlannerEnabled() ? calcRouteMileage(job.waypoints) : null;
        const weekMilesBadge = weekRouteMiles ? `<div style="font-size:0.65rem;color:#10B981;font-weight:600;margin-top:1px;">~${Math.round(weekRouteMiles)} mi</div>` : '';
        const weekLoadBadge = buildLoadProgressBadge(jobDay);
        // TASK-1540 — guaranteed-$0-ticket warning (on clock, nothing billable)
        const weekOcnbBadge = getOnClockNothingBillableBadge(jobDay, dayStr, 'compact');
        // TASK-9500 — driver "I'm here" stamps + an open out-of-zone clock-in.
        const weekArriveChips = getStopArrivalChip(job, jobDay, 'compact') + getOutOfZoneClockChip(jobDay, dayStr, 'compact');
        // TASK-9278 — job number rides the placard token order (legacy path unchanged).
        const weekTitleHtml = getPlacardTitleHtml(job);
        // Chris 10/27 — expired/not-yet locate: red icon in the indicator row + a red right edge (COD: icon alone too quiet).
        const weekP811Icon = _p811CardChipHtml(job, jobDay.date);

        html += `
          <div class="job-card ${statusClass}${isTentativeJobDay(jobDay) ? ' day-tentative' : ''}${weekP811Icon ? ' p811-locate-stop' : ''}" style="border-left: 3px solid ${customerColor};${_tentativeCardStyle(jobDay)}" draggable="true" data-job-id="${escapeHtml(job.id)}" data-job-day-id="${escapeHtml(jobDay.id)}" data-date="${dayStr}" onclick="window.DispatchModule.showJobDetails('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')" oncontextmenu="event.preventDefault(); window.DispatchModule.showJobContextMenu(event, '${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
            <div class="job-card-header">
              <div class="job-card-title">${weekTitleHtml.lead}${weekTitleHtml.title}</div>
              <span class="job-card-indicators">${_reminderBell('job', job.id, job.title || '', { compact: true, onlyWhenSet: true })}${weekP811Icon}${assignmentIndicators}</span>
            </div>
            ${_projectChipHtml(job)}${weekMilesBadge}${weekLoadBadge}${weekOcnbBadge}${weekArriveChips}${_rentalReqCardChipHtml(job, jobDay)}
            ${_dayFlagChipsHtml(jobDay, { confirm: true })}
            ${statusBadge ? `<div class="job-card-status">${statusBadge}</div>` : ''}
          </div>
        `;
      });

      // Render time-off cards (at bottom)
      dayTimeOff.forEach(req => {
        const name = getPersonNameById(req.personId) || req.personName || 'Unknown';
        const typeLabel = (req.type || 'unpaid').charAt(0).toUpperCase() + (req.type || 'unpaid').slice(1);
        const isDaysOn = req.type === 'days_on';
        const isRot = req._rotation === true; // TASK-10183
        html += `
          <div class="timeoff-hold-card${isDaysOn ? ' days-on' : ''}${isRot ? ' rotation' : ''}" title="${(window.escAttr || escapeHtml)(buildTimeOffTooltip(req, name, typeLabel))}">
            <span class="timeoff-hold-tag">${isRot ? 'ROTATION' : isDaysOn ? 'ON' : 'OFF'}</span>
            <span class="timeoff-hold-title">${escapeHtml(name)}</span>
            <span class="timeoff-hold-desc">${escapeHtml(isRot ? (req.rotationLabel || 'Regular day off') : typeLabel)}</span>
          </div>
        `;
      });
    }

    html += `</div>`;
  });
  html += `</div>`;

  html += `</div>`;

  // TASK-583 — floating "Today" jump chip. Hidden by default; shown when
  // today's block is off-screen (scrolled away on mobile, or viewing a
  // past/future week on any viewport). Jump = navigate to current week +
  // auto-scroll (goToToday sets the one-shot scroll trigger).
  html += `
    <button type="button" id="week-today-jump" class="week-today-jump hidden" onclick="window.DispatchModule.goToToday()" aria-label="Jump to today">
      <span class="material-icons">today</span> Today
    </button>
  `;

  container.innerHTML = html;
  updateLayerFilterWarning(hiddenItemsWeek);
  updateTodayJumpChip(container);

  // TASK-583 — one-shot auto-scroll to today (mobile stacked layout only).
  // Consumed here so layer-chip toggles / live data rerenders never re-fire it.
  if (DispatchState._scrollWeekToToday) {
    DispatchState._scrollWeekToToday = false;
    requestAnimationFrame(() => scrollWeekViewToToday(container));
  }
}

// ============================================
// TASK-583 — WEEK VIEW: SCROLL TO TODAY + JUMP CHIP
// ============================================

// Mobile (<=768px) stacks the 7 week days vertically inside the app shell's
// scroll container, so opening the default week view buried today below up
// to 6 days of scroll. These helpers land the user on today.

let _todayChipObserver = null;

// Walk up from the week view to find the element that actually scrolls.
// In the app shell that's #main-content (mobile: height:100dvh + overflow-y:auto),
// but detect it instead of hardcoding so banner modes / future shell changes
// don't silently break the math. Falls back to #main-content.
function findWeekScrollContainer(el) {
  let node = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    try {
      const style = getComputedStyle(node);
      if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
    } catch (_) { /* detached node — fall through */ }
    node = node.parentElement;
  }
  return document.getElementById('main-content');
}

// Offset so today's label isn't hidden under overlays pinned to the top:
// the demo trial banner (position:sticky INSIDE #main-content) and the
// verification/trial banners (position:fixed over the viewport top).
function getWeekScrollTopOffset() {
  let offset = 8; // breathing room
  const demoBanner = document.getElementById('demo-trial-banner');
  if (demoBanner && !demoBanner.classList.contains('hidden') && demoBanner.offsetHeight) {
    offset += demoBanner.offsetHeight;
  }
  document.querySelectorAll('.verification-banner, .trial-banner').forEach(b => {
    try {
      if (b.offsetHeight && getComputedStyle(b).position === 'fixed') offset += b.offsetHeight;
    } catch (_) {}
  });
  return offset;
}

// Scroll today's .week-day block to the top of the scroll container.
// Mobile/stacked layout ONLY — desktop renders 7 side-by-side columns where
// today is already visible, so scrolling would just jump the page (no-op).
function scrollWeekViewToToday(container) {
  if (!window.matchMedia || !window.matchMedia('(max-width: 768px)').matches) return;
  const todayEl = container.querySelector('.week-day.today');
  if (!todayEl) return; // rendered week doesn't contain today
  const scroller = findWeekScrollContainer(todayEl);
  if (!scroller) return;
  const offset = getWeekScrollTopOffset();
  const top = scroller.scrollTop + todayEl.getBoundingClientRect().top - scroller.getBoundingClientRect().top - offset;
  scroller.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
}

// Show the floating "Today" chip when today is off-screen:
// - week doesn't contain today (past/future week) → always shown;
// - week contains today → IntersectionObserver toggles it as the block
//   scrolls in/out of view (covers mobile stack AND a scrolled desktop page).
function updateTodayJumpChip(container) {
  if (_todayChipObserver) {
    _todayChipObserver.disconnect();
    _todayChipObserver = null;
  }
  const chip = container.querySelector('#week-today-jump');
  if (!chip) return;
  const todayEl = container.querySelector('.week-day.today');
  if (!todayEl) {
    chip.classList.remove('hidden'); // viewing another week — today is off-screen by definition
    return;
  }
  if (typeof IntersectionObserver === 'undefined') return; // no IO: chip stays hidden, .today highlight still reachable by scroll
  _todayChipObserver = new IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    chip.classList.toggle('hidden', entry.isIntersecting);
  }, { threshold: 0 });
  _todayChipObserver.observe(todayEl);
}

// TASK-4648 — DECISION: List and Lanes render finished jobs UNCONDITIONALLY
// (dimmed + badged) and do NOT consult the Completed chip. The alternative was
// to show the chip row on these views and route them through
// getCalendarJobsForDate. Why not:
//
//   1. The chip is a CALENDAR DENSITY control, not a data filter. Week and Month
//      squeeze N jobs into one small cell, so hiding closed work there is a real
//      need. List is a vertical scroll with a per-day header and Lanes is a
//      resource grid — neither has a density problem to solve.
//   2. The failure modes are not symmetric. The chip persists per-device in
//      localStorage (:852). Route these views through it and a flag someone
//      flipped on Month last month silently empties a BACKWARD-LOOKING view —
//      which is exactly the bug TASK-4646 exists to kill, recreated one view
//      over. This option's worst case is "you saw a dimmed finished job you
//      didn't ask for", which the reader can resolve by reading.
//   3. Nothing here can strand a user, because there is no state to be stranded
//      by. The chip row is JS-hidden on these views (syncLayerFilterChips :3949)
//      and the "N items hidden" pill lives inside that row, so a chip-driven
//      List would have to grow a second hidden-count surface to be honest.
//   4. List is a HISTORY view by its own construction: it sorts past days
//      most-recent-first (:5285-5292) and offers "Load previous" 30 days back.
//      A history view that drops history is broken by definition, and its
//      "N jobs" day header was reading 0 on days that had work.
//
// Driver carve-out: real PIN drivers never reach these renderers
// (_renderDispatchBoardNow returns into renderDriverBoard first, :3866), but the
// dispatcher-side "view as driver" role toggle (:7946) DOES land here. Finished
// jobs stay excluded in that mode so the preview matches what a driver sees and
// nobody's board sprouts months of closed work — the same stance the
// .dispatcher-only Completed chip takes.
// TASK-10050 — List view "show for every job" toggles (Chris 2026-10-23).
// The 🚛 / 👤 icons on a card open a popover for THAT job; the matching buttons
// in each day header show the same breakdown inline on EVERY card at once.
//
// Names come from _assignmentDisplayNames, the ONE resolver the popover also
// uses — never a second copy of the lookup. The lines are always rendered and
// CSS-hidden until .dispatch-list carries show-trucks / show-crew, so a toggle
// is a class flip: no re-render, no scroll jump, and the next snapshot repaint
// re-reads the saved choice. Per viewer (localStorage), default off.
const LIST_DETAIL_STORAGE_KEY = 'dd_dispatch_list_details_v1';
const LIST_DETAIL_KINDS = [
  { kind: 'trucks', icon: '🚛', on: 'Hide vehicles on every job', off: 'Show vehicles on every job' },
  { kind: 'crew', icon: '👤', on: 'Hide crew on every job', off: 'Show crew on every job' },
];

// Keyed per signed-in user (COD 10/23: one browser can hold several logins).
// The in-memory copy is the working value, so the toggle still flips and the
// next repaint keeps it when localStorage is blocked; storage only seeds it.
let _listDetailMem = null; // { key, prefs }

function _listDetailKey() {
  const uid = auth.currentUser?.uid || window.AppState?.user?.uid || 'anon';
  return `${LIST_DETAIL_STORAGE_KEY}:${uid}`;
}

function _listDetailPrefs() {
  const key = _listDetailKey();
  if (_listDetailMem && _listDetailMem.key === key) return { ..._listDetailMem.prefs };
  const prefs = { trucks: false, crew: false };
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    if (saved && typeof saved === 'object') {
      prefs.trucks = saved.trucks === true;
      prefs.crew = saved.crew === true;
    }
  } catch (_) { /* storage blocked or corrupt — defaults (off) */ }
  _listDetailMem = { key, prefs: { ...prefs } };
  return prefs;
}

function _listDetailClasses(prefs) {
  return `${prefs.trucks ? ' show-trucks' : ''}${prefs.crew ? ' show-crew' : ''}`;
}

function _listDetailHeaderHtml(prefs) {
  return `<span class="list-detail-toggles" role="group" aria-label="Show on every job">${LIST_DETAIL_KINDS.map(k => {
    const pressed = !!prefs[k.kind];
    const label = pressed ? k.on : k.off;
    return `<button type="button" class="list-detail-toggle${pressed ? ' active' : ''}" data-detail="${k.kind}" data-trailguide="list-detail-toggles" aria-pressed="${pressed}" title="${label}" aria-label="${label}" onclick="window.DispatchModule.toggleListDetail('${k.kind}')"><span aria-hidden="true">${k.icon}</span></button>`;
  }).join('')}</span>`;
}

function _listDetailTrailerLabel(name) {
  return /^trailer\b/i.test(String(name)) ? String(name) : `Trailer ${name}`;
}

function _listDetailLinesHtml(jobDay) {
  const n = _assignmentDisplayNames(jobDay);
  const vehicleParts = [
    ...n.trucks.map(name => escapeHtml(name)),
    ...n.trailers.map(name => escapeHtml(_listDetailTrailerLabel(name))),
  ];
  const crewParts = [
    ...n.drivers.map(name => escapeHtml(name)),
    ...n.helpers.map(name => `${escapeHtml(name)} <span class="list-detail-role">(helper)</span>`),
  ];
  const vehicles = vehicleParts.length
    ? vehicleParts.join(' · ')
    : '<span class="list-detail-none">No vehicles assigned</span>';
  const crew = crewParts.length
    ? crewParts.join(', ')
    : '<span class="list-detail-none">No crew assigned</span>';
  return `<div class="list-detail-line list-detail-trucks"><span class="list-detail-icon" aria-hidden="true">🚛</span><span class="list-detail-text"><span class="list-detail-sr">Vehicles: </span>${vehicles}</span></div>`
    + `<div class="list-detail-line list-detail-crew"><span class="list-detail-icon" aria-hidden="true">👤</span><span class="list-detail-text"><span class="list-detail-sr">Crew: </span>${crew}</span></div>`;
}

function toggleListDetail(kind) {
  if (!LIST_DETAIL_KINDS.some(k => k.kind === kind)) return;
  const prefs = _listDetailPrefs();
  prefs[kind] = !prefs[kind];
  const key = _listDetailKey();
  _listDetailMem = { key, prefs: { ...prefs } };
  try { localStorage.setItem(key, JSON.stringify(prefs)); } catch (_) { /* not persisted — the in-memory copy still holds for this page */ }
  const meta = LIST_DETAIL_KINDS.find(k => k.kind === kind);
  const label = prefs[kind] ? meta.on : meta.off;
  document.querySelectorAll('.dispatch-list').forEach(list => {
    list.classList.toggle(`show-${kind}`, prefs[kind]);
  });
  document.querySelectorAll(`.list-detail-toggle[data-detail="${kind}"]`).forEach(btn => {
    btn.classList.toggle('active', prefs[kind]);
    btn.setAttribute('aria-pressed', String(prefs[kind]));
    btn.title = label;
    btn.setAttribute('aria-label', label);
  });
}

function renderListView(container) {
  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) return renderScheduleLoading(container);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use currentDate (controlled by header nav)
  const startDate = new Date(DispatchState.currentDate);
  startDate.setHours(0, 0, 0, 0);

  const daysToShow = 5;
  const statusFilter = DispatchState.statusFilter;
  const dates = [];

  for (let i = 0; i < daysToShow; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    dates.push(date);
  }

  // Sort dates so today/future dates come first, then past dates
  // Within each group, sort chronologically (future: ascending, past: descending)
  dates.sort((a, b) => {
    const aIsPast = a < today;
    const bIsPast = b < today;
    // If one is past and one isn't, non-past comes first
    if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;
    // If both are past, show most recent past first (descending)
    if (aIsPast && bIsPast) return b - a;
    // If both are future/today, show earliest first (ascending)
    return a - b;
  });

  // Check if we can load previous days
  const canLoadPrevious = DispatchState.listOffset > -30; // Allow going back up to 30 days
  const isDriverMode = DispatchState.roleMode === 'driver';
  // TASK-10050 — header toggles are dispatcher-side only; the "view as driver"
  // preview keeps matching what a driver sees.
  const listDetailPrefs = isDriverMode ? { trucks: false, crew: false } : _listDetailPrefs();
  const listDetailHeader = isDriverMode ? '' : _listDetailHeaderHtml(listDetailPrefs);

  let html = `<div class="dispatch-list${_listDetailClasses(listDetailPrefs)}">`;

  // Load previous / Load More button — show at bottom instead of top
  // (We'll add it after the dates loop)

  dates.forEach(date => {
    const dateStr = formatDateISO(date);
    // TASK-4648 — List paints finished jobs UNCONDITIONALLY (dimmed + badged);
    // it does NOT consult the Completed chip. See the decision note above
    // renderListView.
    let dayJobs = getJobsForDate(dateStr, !isDriverMode);

    const isToday = date.toDateString() === today.toDateString();
    const isPast = date < today;
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

    html += `
      <div class="dispatch-day-group ${isToday ? 'today' : ''} ${isPast ? 'past' : ''}">
        <div class="dispatch-day-header">
          <span class="dispatch-day-date">
            ${isToday ? '<span class="today-badge">Today</span>' : ''}
            ${dayName}
          </span>
          <span class="dispatch-day-header-right">
            ${listDetailHeader}
            <span class="dispatch-day-count">${dayJobs.length} job${dayJobs.length !== 1 ? 's' : ''}</span>
          </span>
        </div>
        <div class="dispatch-day-jobs">
    `;

    // Get approved time-off for this day
    const dayTimeOff = getTimeOffForDate(dateStr);
    const dayOppHoldsList = getOpportunityHoldsForDate(dateStr);
    const dayVehicleHoldsList = getVehicleHoldsForDate(dateStr);

    if (dayJobs.length === 0 && dayTimeOff.length === 0 && dayOppHoldsList.length === 0 && dayVehicleHoldsList.length === 0) {
      html += `<div class="day-empty">${isPast ? 'No jobs on this date' : 'No jobs scheduled'}</div>`;
    } else {
      // Render maintenance vehicle hold cards
      dayVehicleHoldsList.forEach(hold => {
        html += renderVehicleHoldCard(hold);
      });

      // Render opportunity hold cards
      dayOppHoldsList.forEach(hold => {
        html += renderOpportunityHoldCard(hold);
      });

      dayJobs.forEach(({ job, jobDay }) => {
        const statusColor = getStatusColor(jobDay.status);
        const customerColor = getCustomerColor(job.customerId || job.customer);
        const cardColor = isDriverMode ? customerColor : statusColor;
        // TASK-4648 — the row now reaches here at all, so it has to LOOK closed.
        // isCompletedCalendarEntry keys on job.status alone, the same predicate
        // getJobsForDate excluded on and the same one the Completed chip uses;
        // jobDay.status stays in the test because a jobDay can be finished under
        // a still-active job and that already dimmed before this change.
        const listJobFinished = isCompletedCalendarEntry({ job, jobDay });
        const statusClass = jobDay.status === 'cancelled' ? 'cancelled' :
                            (jobDay.status === 'finished' || listJobFinished) ? 'finished' : '';
        const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
        const truckUnit = getTruckUnit(jobDay);
        const driverName = getDriverName(jobDay);
        const scheduledTime = formatScheduledTime(job);

        // Type indicator for meetings/shop days
        const typeIcon = job.type === 'meeting' ? `<span class="job-type-badge meeting" title="${escapeHtml(_jobTypeLabel(job.type))}">📋</span>` :
                         job.type === 'shop_day' ? `<span class="job-type-badge shop" title="${escapeHtml(_jobTypeLabel(job.type))}">🔧</span>` : '';

        // Compact assignment indicators (clickable popovers)
        const hasAssignments = truckUnit || driverName;
        const listDvirIndicator = getMissingDvirIndicator(jobDay, dateStr, 'compact');
        const listJsaIndicator = getMissingJsaIndicator(jobDay, dateStr, 'compact');
        const listJsaPresentIndicator = getJsaPresentIndicator(jobDay, 'compact'); // TASK-794
        const listTruckIndicator = truckUnit
          ? `<span class="assign-indicator assigned" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'trucks')">🚛</span>`
          : '<span class="assign-indicator">🚛</span>';
        const listClockedIn = hasActiveClock(jobDay);
        const listPersonIndicator = driverName
          ? `<span class="assign-indicator assigned${listClockedIn ? ' clocked-in' : ''}" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'people')">👤</span>`
          : '<span class="assign-indicator">👤</span>';
        const assignmentIndicators = hasAssignments
          ? `${listTruckIndicator}${listPersonIndicator}${listDvirIndicator}${listJsaIndicator}${listJsaPresentIndicator}`
          : `<span class="meta-item unassigned">Unassigned</span>${listJsaPresentIndicator}`;

        const routeMiles = isRoutePlannerEnabled() ? calcRouteMileage(job.waypoints) : null;
        const milesBadge = routeMiles ? `<span class="route-miles-badge" style="margin-left:6px;font-size:0.7rem;color:#10B981;font-weight:600;">~${Math.round(routeMiles)} mi</span>` : '';
        const listLoadBadge = buildLoadProgressBadge(jobDay);
        // TASK-1540 — guaranteed-$0-ticket warning (on clock, nothing billable).
        // isDriverMode also renders through here; the badge fn returns '' for
        // driver-PIN sessions itself.
        const listOcnbBadge = getOnClockNothingBillableBadge(jobDay, dateStr, 'card');
        // TASK-9500 — driver "I'm here" stamps + an open out-of-zone clock-in ('' for PIN drivers).
        const listArriveChips = getStopArrivalChip(job, jobDay, 'card') + getOutOfZoneClockChip(jobDay, dateStr, 'card');
        // TASK-9278 — drivers never saw the job number on this card; the guard
        // moved INTO the renderer so the token path cannot bypass it.
        const listTitleHtml = getPlacardTitleHtml(job, { hideJobNumber: !!isDriverMode });
        // Chris 10/27 — office card: red locate icon in the indicator row + red right edge; driver mode keeps the bar.
        const listP811Icon = isDriverMode ? '' : _p811CardChipHtml(job, jobDay.date);

        html += `
          <div class="dispatch-job-card ${statusClass}${listP811Icon ? ' p811-locate-stop' : ''}${isDriverMode ? 'driver-mode-card' : ''} ${job.type === 'meeting' ? 'type-meeting' : job.type === 'shop_day' ? 'type-shop' : ''}${isTentativeJobDay(jobDay) ? ' day-tentative' : ''}" style="border-left: 4px solid ${cardColor}; ${_tentativeCardStyle(jobDay)}${isDriverMode ? `--card-color: ${customerColor}; --card-color-dark: ${adjustColor(customerColor, -30)};` : ''}">
            <div class="dispatch-job-content" onclick="window.DispatchModule.${isDriverMode ? 'showQuickview' : 'showJobDetails'}('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
              <div class="dispatch-job-header">
                ${typeIcon}${listTitleHtml.lead}<span class="dispatch-job-title">${listTitleHtml.title}</span>
                <span class="dispatch-job-assignments">${isDriverMode ? '' : _reminderBell('job', job.id, job.title || '')}${listP811Icon}${assignmentIndicators}</span>
              </div>
              ${isDriverMode ? _p811CardChipHtml(job, jobDay.date, { bar: true }) : _projectChipHtml(job)}${isDriverMode ? '' : _rentalReqCardChipHtml(job, jobDay)}
              <div class="dispatch-job-status">${jobDay.status || 'Scheduled'}${listLoadBadge ? ` ${listLoadBadge}` : ''}${listJobFinished ? ' <span class="search-finished-badge">Finished</span>' : ''}</div>
              ${isDriverMode ? '' : _listDetailLinesHtml(jobDay)}
              ${_dayFlagChipsHtml(jobDay, { confirm: !isDriverMode })}
              ${listOcnbBadge}${listArriveChips}
              ${job.waypoints && job.waypoints.length > 0 ? `<div class="dispatch-job-location"><span class="material-icons">location_on</span> ${escapeHtml(job.waypoints[0].name || job.waypoints[0].url || 'Destination')}${job.waypoints.length > 1 ? ` +${job.waypoints.length - 1} more` : ''}${milesBadge}${buildUnpinnedStopBadge(job)} <button class="btn-waypoints-link" onclick="event.stopPropagation(); window.DispatchModule.showWaypointsModal('${escJsAttr(job.id)}')" title="View waypoints"><span class="material-icons">open_in_new</span></button></div>` : ''}
            </div>
            <div class="dispatch-job-actions">
              ${isDriverMode ? `
                <button class="btn btn-view-details" style="background: linear-gradient(135deg, ${customerColor}14 0%, ${customerColor}26 100%); color: ${adjustColor(customerColor, -30)}; border: 1px solid ${customerColor}33; font-weight: 700;" onclick="window.DispatchModule.showQuickview('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
                  View Details
                </button>
              ` : `
                <button class="btn-icon btn-text-crew" ${isTentativeJobDay(jobDay) ? 'hidden' : ''} onclick="event.stopPropagation(); window.DispatchModule.sendIndividualText('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')" title="Text Crew">
                  <span class="material-icons">smartphone</span>
                </button>
                <div class="job-overflow-menu">
                  <button class="job-overflow-btn" title="More actions">
                    <span class="material-icons">more_vert</span>
                  </button>
                  <div class="overflow-dropdown">
                    <button class="overflow-item" onclick="window.DispatchModule.duplicateJob('${escJsAttr(job.id)}')">
                      <span class="material-icons">content_copy</span> Copy to New Job
                    </button>
                    ${_projectNewJobMenuItemHtml(job)}
                    <button class="overflow-item" ${_dispatchCanManageDays() ? "" : "hidden"} onclick="window.DispatchModule.addDayToJob('${escJsAttr(job.id)}')">
                      <span class="material-icons">add_circle</span> Add Another Day
                    </button>
                    <button class="overflow-item" onclick="window.DispatchModule.showJobDetails('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
                      <span class="material-icons">edit</span> Edit Job
                    </button>
                    <button class="overflow-item" onclick="window.DispatchModule.cancelJobDay('${escJsAttr(jobDay.id)}')">
                      <span class="material-icons">cancel</span> Cancel Job
                    </button>
                  </div>
                </div>
              `}
            </div>
          </div>
        `;
      });

      // Render time-off cards (at bottom)
      dayTimeOff.forEach(req => {
        const name = getPersonNameById(req.personId) || req.personName || 'Unknown';
        const typeLabel = (req.type || 'unpaid').charAt(0).toUpperCase() + (req.type || 'unpaid').slice(1);
        const isDaysOn = req.type === 'days_on';
        const isRot = req._rotation === true; // TASK-10183
        html += `
          <div class="timeoff-hold-card${isDaysOn ? ' days-on' : ''}${isRot ? ' rotation' : ''}" title="${(window.escAttr || escapeHtml)(buildTimeOffTooltip(req, name, typeLabel))}">
            <span class="timeoff-hold-tag">${isRot ? 'ROTATION' : isDaysOn ? 'ON' : 'OFF'}</span>
            <span class="timeoff-hold-title">${escapeHtml(name)}</span>
            <span class="timeoff-hold-desc">${escapeHtml(isRot ? (req.rotationLabel || 'Regular day off') : typeLabel)}</span>
          </div>
        `;
      });
    }

    html += `
        </div>
      </div>
    `;
  });

  // Add "Load earlier dates" button at bottom
  if (canLoadPrevious) {
    html += `
      <button class="load-previous-btn" onclick="window.DispatchModule.loadPreviousDays()">
        <span class="material-icons">history</span>
        <span>Load earlier dates</span>
      </button>
    `;
  }

  html += `</div>`;
  container.innerHTML = html;
}

// ============================================
// SEARCH VIEW
// ============================================
function renderSearchView(container) {
  const q = (DispatchState.searchQuery || '').toLowerCase().trim();
  const statusFilter = DispatchState.searchStatusFilter || 'all';

  // Build all job+jobDay pairs (including finished)
  // TASK-424 #5 (C2): prefer the full-history one-shot snapshot (loaded on
  // entering the Search view) so "Search Past Jobs" spans ALL history even
  // though the live board is windowed. Falls back to the windowed live arrays
  // if the one-shot fetch hasn't completed / errored.
  const _searchJobs = DispatchState.searchJobs || DispatchState.jobs;
  const _searchJobDays = DispatchState.searchJobDays || DispatchState.jobDays;
  let allResults = [];
  const jobDayJobIds = new Set();

  _searchJobDays.forEach(jobDay => {
    const job = _searchJobs.find(j => j.id === jobDay.jobId);
    if (!job) return;
    jobDayJobIds.add(job.id);
    allResults.push({ job, jobDay });
  });

  // Legacy jobs without jobDays
  _searchJobs.forEach(job => {
    if (jobDayJobIds.has(job.id)) return;
    const startDate = job.startDate?.toDate?.() || (job.startDate ? new Date(job.startDate) : null);
    if (!startDate) return;
    allResults.push({
      job,
      jobDay: {
        id: `legacy-${job.id}`,
        jobId: job.id,
        date: startDate,
        status: job.status || 'scheduled',
        drivers: job.drivers || [],
        trucks: job.trucks || [],
        trailers: job.trailers || [],
        helpers: job.helpers || [],
        assets: job.assets || []
      }
    });
  });

  // Status filter
  if (statusFilter === 'active') {
    allResults = allResults.filter(({ job, jobDay }) =>
      jobDay.status !== 'finished' && jobDay.status !== 'cancelled' && job.status !== 'finished'
    );
  } else if (statusFilter === 'finished') {
    allResults = allResults.filter(({ job, jobDay }) =>
      jobDay.status === 'finished' || jobDay.status === 'cancelled' || job.status === 'finished'
    );
  }

  // Search filter
  if (q) {
    allResults = allResults.filter(({ job, jobDay }) => {
      // TASK-4781 — job number. This is the WHOLE point of having one: a number
      // is only useful if saying it out loud finds the job. Matches both the
      // spoken/printed form ("j-1042") and the bare digits ("1042"), so a
      // dispatcher reading it off a text message gets a hit either way.
      // Match ONLY — `jobNumber` is never a join key (COD Independent); nothing
      // resolves a job BY this value, it just narrows a list the user is reading.
      if (Number.isFinite(job.jobNumber)) {
        if (String(job.jobNumber).includes(q)) return true;
        if (formatJobNumber(job).toLowerCase().includes(q)) return true;
      }

      // Job title/name
      if ((job.title || '').toLowerCase().includes(q)) return true;
      if ((job.name || '').toLowerCase().includes(q)) return true;

      // Customer name
      const customer = getCustomerById(job.customerId) || { name: job.customer || '' };
      if ((customer.name || '').toLowerCase().includes(q)) return true;

      // Well name / well number
      if ((job.wellName || '').toLowerCase().includes(q)) return true;
      if ((job.wellNumber || '').toLowerCase().includes(q)) return true;

      // PO number: the main PO or any extra (TASK-9811 S2)
      if (jobPoTextMatch(job, q)) return true;

      // Vendor number
      if ((job.vendorNumber || '').toLowerCase().includes(q)) return true;

      // Point of contact
      if ((job.poc?.name || '').toLowerCase().includes(q)) return true;

      // Notes
      if ((job.notes || '').toLowerCase().includes(q)) return true;

      // Drivers
      const driverNames = (jobDay.drivers || []).map(ref => {
        const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
        return p ? p.name.toLowerCase() : String(ref).toLowerCase();
      });
      if (driverNames.some(n => n.includes(q))) return true;

      // Helpers
      const helperNames = (jobDay.helpers || []).map(ref => {
        const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
        return p ? p.name.toLowerCase() : String(ref).toLowerCase();
      });
      if (helperNames.some(n => n.includes(q))) return true;

      // Trucks
      const truckLabels = (jobDay.trucks || []).map(ref => {
        const t = _resolveFleetRef(ref, DispatchState.trucks);
        return t ? `${t.unit || ''} ${t.name || ''} ${t.nickname || ''}`.toLowerCase() : String(ref).toLowerCase();
      });
      if (truckLabels.some(n => n.includes(q))) return true;

      // Trailers
      const trailerLabels = (jobDay.trailers || []).map(ref => {
        const t = _resolveFleetRef(ref, DispatchState.trailers);
        return t ? `${t.unit || ''} ${t.name || ''} ${t.nickname || ''}`.toLowerCase() : String(ref).toLowerCase();
      });
      if (trailerLabels.some(n => n.includes(q))) return true;

      // Waypoints
      if (job.waypoints && job.waypoints.some(wp =>
        (wp.name || '').toLowerCase().includes(q) || (wp.url || '').toLowerCase().includes(q)
      )) return true;

      return false;
    });
  }

  // Sort by date descending (newest first)
  allResults.sort((a, b) => {
    const dateA = a.jobDay.date?.toDate?.() || new Date(a.jobDay.date);
    const dateB = b.jobDay.date?.toDate?.() || new Date(b.jobDay.date);
    return dateB - dateA;
  });

  const totalResults = allResults.length;
  const visibleResults = allResults.slice(0, DispatchState.searchResultsLimit);
  const hasMore = totalResults > DispatchState.searchResultsLimit;

  // Render
  let html = `<div class="dispatch-search-view">`;

  // Search bar
  html += `
    <div class="dispatch-search-bar">
      <span class="material-icons dispatch-search-icon">search</span>
      <input type="search" id="dispatch-search-input" class="dispatch-search-input"
        placeholder="Search jobs..."
        autocomplete="off"
        value="${escapeHtml(DispatchState.searchQuery || '')}"
        oninput="window.DispatchModule.onSearchInput(this.value)">
      ${q ? `<button class="dispatch-search-clear" onclick="window.DispatchModule.clearSearch()">
        <span class="material-icons">close</span>
      </button>` : ''}
    </div>
  `;

  // Status filter pills
  html += `
    <div class="dispatch-search-filters">
      <button class="search-filter-pill ${statusFilter === 'all' ? 'active' : ''}" onclick="window.DispatchModule.setSearchStatus('all')">All</button>
      <button class="search-filter-pill ${statusFilter === 'active' ? 'active' : ''}" onclick="window.DispatchModule.setSearchStatus('active')">Active</button>
      <button class="search-filter-pill ${statusFilter === 'finished' ? 'active' : ''}" onclick="window.DispatchModule.setSearchStatus('finished')">Finished</button>
      <span class="search-result-count">${totalResults} result${totalResults !== 1 ? 's' : ''}</span>
    </div>
  `;

  if (visibleResults.length === 0) {
    html += `<div class="day-empty" style="margin-top:24px;">${q ? 'No jobs match your search' : 'No jobs found'}</div>`;
  }

  // Job cards (flat list with date labels)
  visibleResults.forEach(({ job, jobDay }) => {
    const jobDate = jobDay.date?.toDate?.() || new Date(jobDay.date);
    const dateLabel = jobDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const statusColor = getStatusColor(jobDay.status);
    const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
    const truckUnit = getTruckUnit(jobDay);
    const driverName = getDriverName(jobDay);
    const dateStr = formatDateISO(jobDate);
    const isFinished = jobDay.status === 'finished' || jobDay.status === 'cancelled' || job.status === 'finished';

    const typeIcon = job.type === 'meeting' ? `<span class="job-type-badge meeting" title="${escapeHtml(_jobTypeLabel(job.type))}">📋</span>` :
                     job.type === 'shop_day' ? `<span class="job-type-badge shop" title="${escapeHtml(_jobTypeLabel(job.type))}">🔧</span>` : '';

    const listDvirIndicator = getMissingDvirIndicator(jobDay, dateStr, 'compact');
    const listJsaIndicator = getMissingJsaIndicator(jobDay, dateStr, 'compact');
    const listJsaPresentIndicator = getJsaPresentIndicator(jobDay, 'compact'); // TASK-794
    const hasAssignments = truckUnit || driverName;
    const listTruckIndicator = truckUnit
      ? `<span class="assign-indicator assigned" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'trucks')">🚛</span>`
      : '<span class="assign-indicator">🚛</span>';
    const listViewClockedIn = hasActiveClock(jobDay);
    const listPersonIndicator = driverName
      ? `<span class="assign-indicator assigned${listViewClockedIn ? ' clocked-in' : ''}" onclick="event.stopPropagation(); window.DispatchModule.showAssignmentPopover(event, '${escJsAttr(jobDay.id)}', 'people')">👤</span>`
      : '<span class="assign-indicator">👤</span>';
    const assignmentIndicators = hasAssignments
      ? `${listTruckIndicator}${listPersonIndicator}${listDvirIndicator}${listJsaIndicator}${listJsaPresentIndicator}`
      : `<span class="meta-item unassigned">Unassigned</span>${listJsaPresentIndicator}`;
    // TASK-9278 — job number rides the placard token order (legacy path unchanged).
    const searchTitleHtml = getPlacardTitleHtml(job);

    html += `
      <div class="dispatch-job-card ${isFinished ? 'search-finished' : ''} ${job.type === 'meeting' ? 'type-meeting' : job.type === 'shop_day' ? 'type-shop' : ''}${isTentativeJobDay(jobDay) ? ' day-tentative' : ''}" style="border-left: 4px solid ${statusColor};${_tentativeCardStyle(jobDay)}">
        <div class="dispatch-job-content" onclick="window.DispatchModule.showJobDetails('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
          <div class="dispatch-job-header">
            ${typeIcon}${searchTitleHtml.lead}<span class="dispatch-job-title">${searchTitleHtml.title}</span>
            <span class="dispatch-job-assignments">${_reminderBell('job', job.id, job.title || '')}${assignmentIndicators}</span>
          </div>
          <div class="dispatch-job-meta-row">
            <span class="search-date-label">${dateLabel}</span>
            ${_projectChipHtml(job)}
            <span class="dispatch-job-customer">${escapeHtml(customer.name)}</span>
            ${isFinished ? `<span class="search-finished-badge">${jobDay.status === 'cancelled' ? 'Cancelled' : 'Finished'}</span>` : `<span class="dispatch-job-status">${jobDay.status || 'Scheduled'}</span>`}
          </div>
          ${_dayFlagChipsHtml(jobDay, { confirm: !isFinished })}
          ${job.waypoints && job.waypoints.length > 0 ? (() => {
            const srMiles = isRoutePlannerEnabled() ? calcRouteMileage(job.waypoints) : null;
            const srBadge = srMiles ? `<span class="route-miles-badge" style="margin-left:6px;font-size:0.7rem;color:#10B981;font-weight:600;">~${Math.round(srMiles)} mi</span>` : '';
            return `<div class="dispatch-job-location"><span class="material-icons">location_on</span> ${escapeHtml(job.waypoints[0].name || job.waypoints[0].url || 'Destination')}${job.waypoints.length > 1 ? ` +${job.waypoints.length - 1} more` : ''}${srBadge}${buildUnpinnedStopBadge(job)}</div>`;
          })() : ''}
        </div>
        <div class="dispatch-job-actions">
          <button class="btn-icon btn-text-crew" ${isTentativeJobDay(jobDay) ? 'hidden' : ''} onclick="event.stopPropagation(); window.DispatchModule.sendIndividualText('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')" title="Text Crew">
            <span class="material-icons">smartphone</span>
          </button>
          <div class="job-overflow-menu">
            <button class="job-overflow-btn" title="More actions">
              <span class="material-icons">more_vert</span>
            </button>
            <div class="overflow-dropdown">
              <button class="overflow-item" onclick="window.DispatchModule.duplicateJob('${escJsAttr(job.id)}')">
                <span class="material-icons">content_copy</span> Copy to New Job
              </button>
              ${_projectNewJobMenuItemHtml(job)}
              <button class="overflow-item" ${_dispatchCanManageDays() ? "" : "hidden"} onclick="window.DispatchModule.addDayToJob('${escJsAttr(job.id)}')">
                <span class="material-icons">add_circle</span> Add Another Day
              </button>
              <button class="overflow-item" onclick="window.DispatchModule.showJobDetails('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
                <span class="material-icons">edit</span> Edit Job
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  });

  // Load more button
  if (hasMore) {
    html += `
      <button class="load-previous-btn" onclick="window.DispatchModule.loadMoreSearchResults()">
        <span class="material-icons">expand_more</span>
        <span>Show more (${totalResults - DispatchState.searchResultsLimit} remaining)</span>
      </button>
    `;
  }

  html += `</div>`;
  container.innerHTML = html;

  // Focus search input and restore cursor position
  const input = document.getElementById('dispatch-search-input');
  if (input && document.activeElement !== input) {
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

// Search event handlers
let searchDebounceTimer = null;
function onSearchInput(value) {
  DispatchState.searchQuery = value;
  DispatchState.searchResultsLimit = 50;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => renderSearchView(document.getElementById('dispatch-content')), 200);
}

function clearSearch() {
  DispatchState.searchQuery = '';
  DispatchState.searchResultsLimit = 50;
  renderSearchView(document.getElementById('dispatch-content'));
}

function setSearchStatus(status) {
  DispatchState.searchStatusFilter = status;
  DispatchState.searchResultsLimit = 50;
  renderSearchView(document.getElementById('dispatch-content'));
}

function loadMoreSearchResults() {
  DispatchState.searchResultsLimit += 50;
  renderSearchView(document.getElementById('dispatch-content'));
}

function getTicketStatusDot(jobId, dateStr) {
  const tickets = DispatchState.tickets || window.BillingState?.tickets;
  if (!tickets || !tickets.length) return '';
  // TASK-9645 B (COD Code Reviewer) — originalWorkDate too, the same day match
  // _ticketCoversDay gives the job-modal chips and the ticket guards.
  const _tmd = (t) => t.workDate === dateStr || (t.originalWorkDate && t.originalWorkDate === dateStr) || (t.workDateEnd && dateStr >= t.workDate && dateStr <= t.workDateEnd);
  // TASK-9645 B — jobId OR originalJobId (billing's sticky matcher: a moved
  // ticket keeps its originalJobId), and a `deleted` ticket never counts.
  // TASK-10107 — a sealed return estimate never sets the day's dot (it would read
  // "Draft" over an invoiced crew ticket). Flag on DispatchState, predicate on BillingState.
  const jobTickets = tickets.filter(t => (t.jobId === jobId || (t.originalJobId && t.originalJobId === jobId)) && _tmd(t) && !t.archived && t.status !== 'deleted'
    && !t.sealedReturnEstimate && !_isSealedReturnEstimate(t));
  if (jobTickets.length === 0) return '';
  // TASK-1387 — the decision is billing's, shared via window.BillingDedup (never a
  // ?v= cross-import — Pitfall #1), so the dispatch and billing calendars cannot
  // disagree. Archived/locked tickets (status, not the dead `archived` boolean)
  // now get the grey dot billing shows instead of no dot at all.
  const _bd = window.BillingDedup;
  if (!_bd || typeof _bd.classifyCalendarTicketStatus !== 'function') return '';
  // TASK-9645 B — classifyDayBillingState adds Paid (every ticket invoiced AND
  // paid:true) on top of the calendar decision; falls back to it when absent.
  const _day = typeof _bd.classifyDayBillingState === 'function'
    ? _bd.classifyDayBillingState(jobTickets)
    : { kind: _bd.classifyCalendarTicketStatus(jobTickets), billedElsewhere: false };
  const kind = _day.kind;
  const _isExt = typeof _bd.isBilledExternally === 'function' ? _bd.isBilledExternally : () => false;
  let color, title, icon = '';
  // Invoiced / Paid carry an ICON (lock / check), not colour alone (COD fold 5).
  // Billed elsewhere IS invoiced (BI counts it) — same colour, a note in the title.
  if (kind === 'paid') { color = '#15803D'; title = 'Paid'; icon = 'check'; }
  else if (kind === 'invoiced') { color = '#22C55E'; title = _day.billedElsewhere ? 'Invoiced (billed elsewhere)' : 'Invoiced'; icon = 'lock'; }
  else if (kind === 'draft') { color = '#EF4444'; title = 'Draft — needs review'; }
  else if (kind === 'ready') { color = '#F59E0B'; title = 'Ready to invoice'; }
  else if (kind === 'archived') { color = '#94A3B8'; title = 'Archived — may already be invoiced in your accounting system'; }
  else return '';
  const ticketCount = jobTickets.length;
  // TASK-9344 — encodeURIComponent does NOT encode ' ( ) - ! * ~, so it is not a
  // JS-string escape: a ticket id carrying `'-alert(1)-'` survived it verbatim and
  // closed the string below. Ticket docs are creatable by driver-PIN sessions
  // (hasDriverSession arm, client-chosen doc id), so this was a driver -> office
  // path on the dispatch board. escJsAttr at the interpolation; the callee still
  // receives the identical encoded string.
  // TASK-9645 B — paid / ext (billed elsewhere) / ref (invoice # or the outside
  // system's ref) ride along so the popover can say which ticket is which.
  // Status + reference only — never an amount.
  const ticketsJson = encodeURIComponent(JSON.stringify(jobTickets.map(t => {
    const _inv = _bd.isTicketInvoiced(t);
    const _ext = _inv && _isExt(t);
    return { id: t.id, status: t.status, invoiced: _inv, archived: _bd.isTicketArchivedStatus(t), customer: t.customerName || t.customer || '',
      paid: _inv && t.paid === true, ext: _ext, ref: _inv ? String((_ext ? (t.externalInvoiceRef || t.invoiceNumber) : t.invoiceNumber) || '') : '' };
  })));
  if (icon) {
    // A 14px badge (glyph inside), full opacity — .assign-indicator alone is 0.3.
    // Tap opens the same popover as the dots; no hover-only tooltip.
    return `<span class="assign-indicator" role="button" aria-label="${escJsAttr(title)}" style="width:14px;height:14px;border-radius:50%;background:${color};flex-shrink:0;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;opacity:1;" onclick="event.stopPropagation(); window.DispatchModule.showTicketStatusPopover(event, '${escJsAttr(color)}', '${escJsAttr(title)}', '${escJsAttr(ticketsJson)}', '${escJsAttr(kind)}')"><span class="material-icons" style="font-size:10px;line-height:1;color:#fff;">${icon}</span></span>`;
  }
  return `<span class="assign-indicator" style="width:7px;height:7px;border-radius:50%;background:${color};flex-shrink:0;display:inline-block;cursor:pointer;" onclick="event.stopPropagation(); window.DispatchModule.showTicketStatusPopover(event, '${escJsAttr(color)}', '${escJsAttr(title)}', '${escJsAttr(ticketsJson)}')"></span>`;
}

function renderMonthView(container) {
  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) return renderScheduleLoading(container);
  const year = DispatchState.currentDate.getFullYear();
  const month = DispatchState.currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const monthName = DispatchState.currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  let html = `<div class="month-view">`;

  // Weekday headers (nav is in main header)
  html += `<div class="month-header">`;
  ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].forEach(d => {
    html += `<div class="month-weekday">${d}</div>`;
  });
  html += `</div>`;

  // Days grid
  html += `<div class="month-grid">`;

  // Padding for first week
  for (let i = 0; i < startPad; i++) {
    html += `<div class="month-day empty"></div>`;
  }

  // Actual days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxVisible = 3; // Show up to 3 jobs, then "+N more"

  const filters = DispatchState.calendarFilters || { jobs: true, daysoff: true, maintenance: true, completed: true };
  let hiddenItemsMonth = 0;

  for (let d = 1; d <= totalDays; d++) {
    const date = new Date(year, month, d);
    const dateStr = formatDateISO(date);
    // TASK-4646 — the month grid is where the customer saw "I lost everything":
    // 25 jobDays in Firestore, one cell painted. This now paints all 25 by default.
    const monthJobSplit = getCalendarJobsForDate(dateStr, filters);
    const dayJobs = monthJobSplit.visible;
    const dayTimeOff = filters.daysoff ? getTimeOffForDate(dateStr) : [];
    // Pipeline holds fold under Jobs per COD decision (smaller carriers may
    // never use CRM; a dead 4th chip is wasted space).
    const dayOppHolds = filters.jobs ? getOpportunityHoldsForDate(dateStr) : [];
    // Maintenance vehicle holds — always visible (chip removed, TASK-461)
    const dayVehicleHoldsMonth = getVehicleHoldsForDate(dateStr);
    // Hidden-item counter so the warning pill can show "N items hidden today".
    // TASK-4646 — monthJobSplit.hidden already covers jobs hidden by EITHER chip.
    hiddenItemsMonth += monthJobSplit.hidden;
    if (!filters.jobs) hiddenItemsMonth += getOpportunityHoldsForDate(dateStr).length;
    if (!filters.daysoff) hiddenItemsMonth += getTimeOffForDate(dateStr).length;

    const isToday = date.toDateString() === today.toDateString();
    const totalItems = dayJobs.length + dayTimeOff.length + dayOppHolds.length + dayVehicleHoldsMonth.length;
    let visibleCount = 0;

    html += `
      <div class="month-day ${isToday ? 'today' : ''}" data-date="${dateStr}" onclick="window.DispatchModule.showDayDetails('${escJsAttr(dateStr)}')">
        <div class="month-day-num">${d}</div>
        <div class="month-day-jobs">
    `;

    // Show vehicle hold indicators (red, truck-scoped maintenance holds)
    dayVehicleHoldsMonth.forEach(hold => {
      if (visibleCount >= maxVisible) return;
      const unit = hold.assetUnit || hold.assetId || '?';
      html += `<div class="month-job-indicator month-vehicle-hold-indicator" title="Hold: ${escapeHtml(unit)} — ${escapeHtml(hold.reason || 'Vehicle hold')}">${escapeHtml(unit.substring(0, 8))}</div>`;
      visibleCount++;
    });

    // Show opportunity hold indicators (colored by stage)
    const STAGE_COLORS_MONTH = { initiating: '#94A3B8', qualifying: '#F59E0B', prospecting: '#3B82F6', negotiating: '#06B6D4', closing: '#10B981' };
    dayOppHolds.forEach(hold => {
      if (visibleCount >= maxVisible) return;
      const color = STAGE_COLORS_MONTH[hold.stage] || '#94A3B8';
      const label = (hold.customerName || 'Deal').substring(0, 10);
      html += `<div class="month-job-indicator" style="background:${color};" title="${escapeHtml(hold.customerName || '')} — ${escapeHtml(hold.opportunityTitle || '')}">${escapeHtml(label)}</div>`;
      visibleCount++;
    });

    // Show up to maxVisible job indicators
    const remainingSlots = maxVisible - visibleCount;
    if (dayJobs.length > 0 && remainingSlots > 0) {
      const visibleJobs = dayJobs.slice(0, remainingSlots);
      visibleJobs.forEach(({ job, jobDay }) => {
        const customerColor = getCustomerColor(job.customerId || job.customer);
        const isLegacy = jobDay.id?.startsWith('legacy-');
        const monthDot = getTicketStatusDot(job.id, dateStr);
        const monthLabel = getPlacardTitle(job);
        // TASK-4646 — dim finished history. There is no `.month-job-indicator.finished`
        // rule in styles.css and this task's scope is dispatch.js only, so the opacity
        // rides inline; the customer colour still reads, just muted. The title tooltip
        // says "(finished)" because a 12-char truncated label can't carry a badge.
        // 0.65 matches `.dispatch-job-card.search-finished`, the product's established
        // "this is finished" treatment — deliberately NOT lower. These chips are white
        // text on a saturated customer colour, and fading them further trades the
        // vanishing-history bug for an unreadable one (COD UX Lead).
        const monthFinished = job.status === 'finished' || jobDay.status === 'finished';
        const monthDim = monthFinished ? 'opacity:0.65;' : '';
        html += `<div class="month-job-indicator" style="background: ${customerColor};${monthDim}${_tentativeCardStyle(jobDay)}${monthDot ? 'display:flex;align-items:center;gap:2px;' : ''}" title="${(window.escAttr || escapeHtml)(monthLabel)}${monthFinished ? ' (finished)' : ''}${isTentativeJobDay(jobDay) ? ' (tentative)' : ''}${isHalfDayJobDay(jobDay) ? ' (half day)' : ''}" ${isLegacy ? '' : `draggable="true" data-job-id="${job.id}" data-job-day-id="${jobDay.id}" data-date="${dateStr}"`}>${escapeHtml(getPlacardMonthChipLabel(job))}${monthDot}</div>`;
        visibleCount++;
      });
    }

    // Show time-off indicators last (bright red for off, green for days_on, compact)
    dayTimeOff.forEach(req => {
      if (visibleCount >= maxVisible) return;
      const name = getPersonNameById(req.personId) || req.personName || '?';
      const shortName = name.split(' ')[0];
      const isDaysOn = req.type === 'days_on';
      const isRot = req._rotation === true; // TASK-10183
      const prefix = isRot ? 'ROT' : isDaysOn ? 'ON' : 'OFF';
      html += `<div class="month-job-indicator ${isRot ? 'month-rotation-indicator' : isDaysOn ? 'month-dayson-indicator' : 'month-timeoff-indicator'}" title="${(window.escAttr || escapeHtml)(buildTimeOffTooltip(req, name, (req.type || 'unpaid').charAt(0).toUpperCase() + (req.type || 'unpaid').slice(1)))}">${prefix}: ${escapeHtml(shortName.substring(0, 8))}</div>`;
      visibleCount++;
    });

    // Show "+N more" if more items than visible
    if (totalItems > maxVisible) {
      const moreCount = totalItems - visibleCount;
      if (moreCount > 0) html += `<span class="month-more-link">+${moreCount} more</span>`;
    }

    html += `
        </div>
      </div>
    `;
  }

  html += `</div></div>`;
  container.innerHTML = html;
  updateLayerFilterWarning(hiddenItemsMonth);
}

// Note: Lanes view does NOT show time-off cards — lanes are truck-level, time-off is person-level
function renderLanesView(container) {
  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) return renderScheduleLoading(container);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = formatDateISO(today);

  // Build 7-day range starting from currentDate (controlled by header nav)
  const startDate = new Date(DispatchState.currentDate);
  startDate.setHours(0, 0, 0, 0);
  const days = [];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  for (let i = 0; i < 7; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const iso = formatDateISO(d);
    days.push({
      date: d,
      iso: iso,
      dayName: dayNames[d.getDay()],
      dayNum: d.getDate(),
      monthName: monthNames[d.getMonth()],
      isToday: iso === todayISO
    });
  }

  // Date range labels for header
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  const startLabel = `${monthNames[startDate.getMonth()]} ${startDate.getDate()}`;
  const endLabel = `${monthNames[endDate.getMonth()]} ${endDate.getDate()}`;

  // Get active people, trucks, trailers, and assets
  const people = (DispatchState.people || []).filter(p =>
    p.status !== 'inactive' && p.active !== false
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const trucks = (DispatchState.trucks || []).filter(t =>
    t.status !== 'inactive' && t.active !== false
  ).sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''));

  const trailers = (DispatchState.trailers || []).filter(t =>
    t.status !== 'inactive' && t.active !== false
  ).sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''));

  const assets = (DispatchState.assets || []).filter(a =>
    a.status !== 'inactive' && a.active !== false
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Build lookup: for each day, which jobs are assigned to which person/truck
  const jobsByDateAndResource = {};
  let totalJobsFound = 0;

  // Helper to resolve person ref (ID or name) to actual ID
  const resolvePersonId = (ref) => {
    if (!ref) return null;
    // First try direct ID match
    const byId = people.find(p => p.id === ref);
    if (byId) return byId.id;
    // Then try name match (legacy GAS imports)
    const byName = people.find(p => p.name === ref);
    return byName ? byName.id : null;
  };

  // Helper to resolve truck ref (ID, unit, or name) to actual ID
  const resolveTruckId = (ref) => _resolveFleetRef(ref, trucks)?.id || null; // TASK-4529

  // Helper to resolve trailer ref
  const resolveTrailerId = (ref) => _resolveFleetRef(ref, trailers)?.id || null; // TASK-4529

  // Helper to resolve asset ref
  const resolveAssetId = (ref) => _resolveFleetRef(ref, assets)?.id || null; // TASK-4529

  // TASK-4648 — same decision as List (see the note above renderListView).
  // Finished jobs paint unconditionally here, dimmed. A resource lane is a
  // record of what a person or truck was booked on; dropping finished work made
  // every past day read as FREE, which is worse than a crowded cell — it is a
  // scheduling surface answering a utilisation question with a false negative.
  const lanesIncludeFinished = DispatchState.roleMode !== 'driver';
  days.forEach(day => {
    const dateStr = day.iso;
    const jobs = getJobsForDate(dateStr, lanesIncludeFinished);
    totalJobsFound += jobs.length;
    jobs.forEach(item => {
      const jd = item.jobDay || item;
      const job = item.job;
      // Get drivers from jobDay first, fall back to job
      const drivers = jd.drivers || job?.drivers || [];
      const helpers = jd.helpers || job?.helpers || [];
      const jobTrucks = jd.trucks || job?.trucks || [];
      // Map to drivers (resolve ID or name to actual ID)
      drivers.forEach(driverRef => {
        const driverId = resolvePersonId(driverRef);
        if (driverId) {
          const key = `person-${driverId}-${dateStr}`;
          if (!jobsByDateAndResource[key]) jobsByDateAndResource[key] = [];
          jobsByDateAndResource[key].push(item);
        }
      });
      // Map to helpers (resolve ID or name to actual ID)
      helpers.forEach(helperRef => {
        const helperId = resolvePersonId(helperRef);
        if (helperId) {
          const key = `person-${helperId}-${dateStr}`;
          if (!jobsByDateAndResource[key]) jobsByDateAndResource[key] = [];
          jobsByDateAndResource[key].push(item);
        }
      });
      // Map to trucks (resolve ID, unit, or name to actual ID)
      jobTrucks.forEach(truckRef => {
        const truckId = resolveTruckId(truckRef);
        if (truckId) {
          const key = `truck-${truckId}-${dateStr}`;
          if (!jobsByDateAndResource[key]) jobsByDateAndResource[key] = [];
          jobsByDateAndResource[key].push(item);
        }
      });
      // Map to trailers
      const jobTrailers = jd.trailers || job?.trailers || [];
      jobTrailers.forEach(trailerRef => {
        const trailerId = resolveTrailerId(trailerRef);
        if (trailerId) {
          const key = `trailer-${trailerId}-${dateStr}`;
          if (!jobsByDateAndResource[key]) jobsByDateAndResource[key] = [];
          jobsByDateAndResource[key].push(item);
        }
      });
      // Map to assets
      const jobAssets = jd.assets || job?.assets || [];
      jobAssets.forEach(assetRef => {
        const assetId = resolveAssetId(assetRef);
        if (assetId) {
          const key = `asset-${assetId}-${dateStr}`;
          if (!jobsByDateAndResource[key]) jobsByDateAndResource[key] = [];
          jobsByDateAndResource[key].push(item);
        }
      });
    });
  });
  // Build opportunity holds by truck+date
  const holdsByDateAndTruck = {};
  days.forEach(day => {
    const dateStr = day.iso;
    const dayHolds = getOpportunityHoldsForDate(dateStr);
    dayHolds.forEach(hold => {
      (hold.assets || []).forEach(asset => {
        if (asset.assetType === 'truck') {
          const truckId = resolveTruckId(asset.assetId) || resolveTruckId(asset.assetUnit);
          if (truckId) {
            const key = `truck-${truckId}-${dateStr}`;
            if (!holdsByDateAndTruck[key]) holdsByDateAndTruck[key] = [];
            holdsByDateAndTruck[key].push(hold);
          }
        }
      });
    });
  });

  // Build maintenance vehicle holds by truck+date (scoped to truck rows only)
  const vehicleHoldsByTruck = {};
  days.forEach(day => {
    const dateStr = day.iso;
    const dayVehicleHolds = getVehicleHoldsForDate(dateStr);
    dayVehicleHolds.forEach(hold => {
      if (hold.assetType === 'truck' && hold.assetId) {
        const truckId = resolveTruckId(hold.assetId) || resolveTruckId(hold.assetUnit);
        if (truckId) {
          const key = `truck-${truckId}-${dateStr}`;
          if (!vehicleHoldsByTruck[key]) vehicleHoldsByTruck[key] = [];
          vehicleHoldsByTruck[key].push(hold);
        }
      }
    });
  });

  // The cell / name taps read the week they were drawn from, never a rebuilt one.
  _lanesLastRender = { days, map: jobsByDateAndResource, names: {} };

  console.log('[Dispatch] Lanes view:', { people: people.length, trucks: trucks.length, trailers: trailers.length, assets: assets.length, jobsInRange: totalJobsFound, assignments: Object.keys(jobsByDateAndResource).length });

  const peopleExpanded = DispatchState.lanesPeopleExpanded;
  const trucksExpanded = DispatchState.lanesTrucksExpanded;
  const trailersExpanded = DispatchState.lanesTrailersExpanded;
  const assetsExpanded = DispatchState.lanesAssetsExpanded;

  const dayHeaders = days.map(day => `
    <div class="lanes-mini-day ${day.isToday ? 'is-today' : ''}">${day.dayName.charAt(0)}</div>
  `).join('');

  let html = `
    <div class="lanes-view-compact">
      <!-- People Section -->
      <div class="lanes-collapsible-section">
        <div class="lanes-section-header-compact" onclick="window.DispatchModule.toggleLanesPeople()">
          <span class="section-chevron">${peopleExpanded ? '▼' : '▶'}</span>
          <span class="material-icons section-icon">group</span>
          <span class="section-title">People</span>
          <span class="section-count">${people.length}</span>
        </div>
        ${peopleExpanded ? `
          <div class="lanes-section-content">
            ${people.length === 0 ? `
              <div class="lanes-empty-message">
                No people in roster. <a href="#/roster">Add from Roster</a>
              </div>
            ` : `
              <div class="lanes-mini-grid">
                <div class="lanes-mini-header">
                  <div class="lanes-mini-name-col"></div>
                  ${dayHeaders}
                </div>
                ${people.map(person => renderLaneRow(person, days, 'person', jobsByDateAndResource, holdsByDateAndTruck, vehicleHoldsByTruck)).join('')}
              </div>
            `}
          </div>
        ` : ''}
      </div>

      <!-- Vehicles Section -->
      <div class="lanes-collapsible-section">
        <div class="lanes-section-header-compact" onclick="window.DispatchModule.toggleLanesTrucks()">
          <span class="section-chevron">${trucksExpanded ? '▼' : '▶'}</span>
          <span class="material-icons section-icon">local_shipping</span>
          <span class="section-title">Vehicles</span>
          <span class="section-count">${trucks.length}</span>
        </div>
        ${trucksExpanded ? `
          <div class="lanes-section-content">
            ${trucks.length === 0 ? `
              <div class="lanes-empty-message">
                No vehicles in roster. <a href="#/roster">Add from Roster</a>
              </div>
            ` : `
              <div class="lanes-mini-grid">
                <div class="lanes-mini-header">
                  <div class="lanes-mini-name-col"></div>
                  ${dayHeaders}
                </div>
                ${trucks.map(truck => renderLaneRow(truck, days, 'truck', jobsByDateAndResource, holdsByDateAndTruck, vehicleHoldsByTruck)).join('')}
              </div>
            `}
          </div>
        ` : ''}
      </div>

      <!-- Trailers Section -->
      <div class="lanes-collapsible-section">
        <div class="lanes-section-header-compact" onclick="window.DispatchModule.toggleLanesTrailers()">
          <span class="section-chevron">${trailersExpanded ? '▼' : '▶'}</span>
          <span class="material-icons section-icon">rv_hookup</span>
          <span class="section-title">Trailers</span>
          <span class="section-count">${trailers.length}</span>
        </div>
        ${trailersExpanded ? `
          <div class="lanes-section-content">
            ${trailers.length === 0 ? `
              <div class="lanes-empty-message">
                No trailers in roster. <a href="#/roster">Add from Roster</a>
              </div>
            ` : `
              <div class="lanes-mini-grid">
                <div class="lanes-mini-header">
                  <div class="lanes-mini-name-col"></div>
                  ${dayHeaders}
                </div>
                ${trailers.map(trailer => renderLaneRow(trailer, days, 'trailer', jobsByDateAndResource, holdsByDateAndTruck, vehicleHoldsByTruck)).join('')}
              </div>
            `}
          </div>
        ` : ''}
      </div>

      <!-- Assets Section -->
      <div class="lanes-collapsible-section">
        <div class="lanes-section-header-compact" onclick="window.DispatchModule.toggleLanesAssets()">
          <span class="section-chevron">${assetsExpanded ? '▼' : '▶'}</span>
          <span class="material-icons section-icon">build</span>
          <span class="section-title">Assets</span>
          <span class="section-count">${assets.length}</span>
        </div>
        ${assetsExpanded ? `
          <div class="lanes-section-content">
            ${assets.length === 0 ? `
              <div class="lanes-empty-message">
                No assets in roster. <a href="#/roster">Add from Roster</a>
              </div>
            ` : `
              <div class="lanes-mini-grid">
                <div class="lanes-mini-header">
                  <div class="lanes-mini-name-col"></div>
                  ${dayHeaders}
                </div>
                ${assets.map(asset => renderLaneRow(asset, days, 'asset', jobsByDateAndResource, holdsByDateAndTruck, vehicleHoldsByTruck)).join('')}
              </div>
            `}
          </div>
        ` : ''}
      </div>
    </div>
  `;

  container.innerHTML = html;
}

// ── Lanes: multi-job days ───────────────────────────────────────────────────
// A driver on 2-3 jobs a day is NORMAL, never a conflict. The cell drew one
// 12px dot per job, so three same-customer jobs read as one blob on a phone and
// no dot was anywhere near a 44px target. Now a 2+ cell carries a count, tapping
// the cell lists that day's jobs (one job still opens straight), and tapping the
// name lists the visible week. The pure helpers are sliced by
// tests/dispatch-lanes-multi-job.unit.cjs; keep them free of DispatchState.
let _lanesLastRender = null; // { days, map, names } from the last renderLanesView

// The {jobId, jobDayId} an item points at. Items arrive in two shapes: the
// getJobsForDate wrapper {job, jobDay} and a bare jobDay {id, jobId}.
function _laneItemIds(item) {
  const jd = (item && item.jobDay) || item || {};
  return {
    jobId: (item && item.job && item.job.id) || jd.jobId || (item && item.jobId) || '',
    jobDayId: jd.id || '',
  };
}

// One entry per jobDay for a cell. renderLanesView pushes a person once as a
// driver and again as a helper when they are both on one run; that is one job.
// Two runs of the same job on one day are two jobDays and stay two.
function laneCellItems(jobsByDateAndResource, entityType, entityId, iso) {
  const raw = (jobsByDateAndResource && jobsByDateAndResource[`${entityType}-${entityId}-${iso}`]) || [];
  const seen = new Set();
  const out = [];
  raw.forEach(item => {
    const ids = _laneItemIds(item);
    const key = `${ids.jobId}|${ids.jobDayId}`;
    if (key !== '|' && seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
}

// What a tap on a cell does: nothing, open the one job, or list them.
function laneCellTapAction(items) {
  const list = items || [];
  if (list.length === 0) return { kind: 'none' };
  if (list.length === 1) {
    const ids = _laneItemIds(list[0]);
    return { kind: 'open', jobId: ids.jobId, jobDayId: ids.jobDayId };
  }
  return { kind: 'list', count: list.length };
}

// The visible week for one person / vehicle, grouped by day, empty days dropped.
function buildLaneWeekGroups(jobsByDateAndResource, entityType, entityId, days) {
  return (days || []).map(day => ({
    iso: day.iso,
    label: `${day.dayName} ${day.monthName} ${day.dayNum}`,
    items: laneCellItems(jobsByDateAndResource, entityType, entityId, day.iso),
  })).filter(g => g.items.length > 0);
}

function renderLaneRow(entity, days, entityType, jobsByDateAndResource, holdsByDateAndTruck, vehicleHoldsByTruck) {
  const STAGE_COLORS = { initiating: '#94A3B8', qualifying: '#F59E0B', prospecting: '#3B82F6', negotiating: '#06B6D4', closing: '#10B981', won: '#8B5CF6', lost: '#EF4444' };
  const typeColors = { person: '#6366f1', truck: '#10B981', trailer: '#F59E0B', asset: '#8B5CF6' };
  const color = entity.color || typeColors[entityType] || '#10B981';
  const name = (entityType === 'truck' || entityType === 'trailer')
    ? (window.getUnitDisplay?.(entity) || entity.name)
    : entity.name;
  const entityId = entity.id;
  const displayName = name || 'Unknown';
  if (_lanesLastRender) _lanesLastRender.names[`${entityType}-${entityId}`] = displayName;

  return `
    <div class="lanes-mini-row">
      <button type="button" class="lanes-mini-name-col lanes-mini-name-btn" onclick="window.DispatchModule.openLaneWeek('${escJsAttr(entityType)}', '${escJsAttr(entityId)}')" title="${escapeHtml(displayName)}: jobs this week">
        <span class="lanes-mini-swatch" style="background:${color}"></span>
        <span class="lanes-mini-name">${escapeHtml(displayName)}</span>
      </button>
      ${days.map(day => {
        // One entry per jobDay (driver AND helper on one run = one job).
        const jobs = laneCellItems(jobsByDateAndResource, entityType, entityId, day.iso);
        const jobCount = jobs.length;
        const cellLabel = `${displayName}, ${day.dayName} ${day.monthName} ${day.dayNum}: ${jobCount === 1 ? '1 job' : `${jobCount} jobs`}`;
        // The whole cell is the tap target (one job opens it, 2+ list them).
        // Desktop dots stay individually clickable and stop the cell tap.
        const tapAttrs = jobCount
          ? ` role="button" tabindex="0" aria-label="${escapeHtml(cellLabel)}" onclick="window.DispatchModule.openLaneCell('${escJsAttr(entityType)}', '${escJsAttr(entityId)}', '${escJsAttr(day.iso)}')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}"`
          : '';
        // Opportunity holds for truck rows
        const holdKey = `truck-${entityId}-${day.iso}`;
        const holds = (entityType === 'truck' && holdsByDateAndTruck) ? (holdsByDateAndTruck[holdKey] || []) : [];
        // Maintenance vehicle holds for truck rows only
        const vHolds = (entityType === 'truck' && vehicleHoldsByTruck) ? (vehicleHoldsByTruck[holdKey] || []) : [];
        return `
          <div class="lanes-mini-cell ${day.isToday ? 'is-today' : ''}${jobCount ? ' has-jobs' : ''}${jobCount > 1 ? ' is-multi' : ''}"${tapAttrs}>
            ${vHolds.map(hold => {
              const title = `${hold.assetUnit || hold.assetId || 'Truck'}: ${hold.reason || 'Vehicle hold'} (${hold.startDate} – ${hold.endDate})`;
              return `<span class="lanes-mini-pill lanes-vehicle-hold-pill" title="${escapeHtml(title)}" onclick="event.stopPropagation();window.DispatchModule.openMaintenanceForVehicle('${escJsAttr(hold.assetId)}')"></span>`;
            }).join('')}
            ${holds.map(hold => {
              const stageColor = STAGE_COLORS[hold.stage] || '#94A3B8';
              const title = `${hold.customerName || 'Opp'}: ${hold.opportunityTitle || 'Hold'} (${hold.stage})`;
              return `<span class="lanes-mini-pill lanes-opp-hold-pill" style="background:${stageColor}" title="${escapeHtml(title)}"></span>`;
            }).join('')}
            ${jobs.map(item => {
              const job = item.job || DispatchState.jobs.find(j => j.id === item.jobId);
              const customerColor = getCustomerColor(job?.customerId || job?.customer);
              const title = job?.title || job?.customer || 'Job';
              const jobId = job?.id || item.jobId;
              const jobDayId = item.jobDay?.id || item.id;
              // TASK-4648 — finished lanes work paints dimmed, never hidden.
              // There is no `.lanes-mini-pill.finished` rule in styles.css and
              // this task's scope is dispatch.js only, so the opacity is inline
              // — the same call TASK-4646 made for the month indicator (:5798).
              // The pill is a bare colour dot with no text, so the title is the
              // ONLY place "finished" can be said; it is not decoration.
              const laneFinished = job?.status === 'finished';
              return `<span class="lanes-mini-pill lanes-job-pill" style="background:${customerColor}${laneFinished ? ';opacity:0.45' : ''}" onclick="event.stopPropagation();window.DispatchModule.showJobDetails('${escJsAttr(jobId)}', '${escJsAttr(jobDayId)}')" title="${escapeHtml(title)}${laneFinished ? ' (finished)' : ''}"></span>`;
            }).join('')}
            ${jobCount > 1 ? `<span class="lanes-mini-count" aria-hidden="true">${jobCount}</span>` : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function toggleLanesPeople() {
  DispatchState.lanesPeopleExpanded = !DispatchState.lanesPeopleExpanded;
  _renderDispatchBoardNow(); // user toggle — instant feedback
}

function toggleLanesTrucks() {
  DispatchState.lanesTrucksExpanded = !DispatchState.lanesTrucksExpanded;
  _renderDispatchBoardNow(); // user toggle — instant feedback
}

function toggleLanesTrailers() {
  DispatchState.lanesTrailersExpanded = !DispatchState.lanesTrailersExpanded;
  _renderDispatchBoardNow(); // user toggle — instant feedback
}

function toggleLanesAssets() {
  DispatchState.lanesAssetsExpanded = !DispatchState.lanesAssetsExpanded;
  _renderDispatchBoardNow(); // user toggle — instant feedback
}

// One row in the lanes job list: job #, customer, title, crew/vehicle. The
// whole row is one ≥44px button that opens the job.
function _laneJobRowHtml(item) {
  const { jobId, jobDayId } = _laneItemIds(item);
  const job = item.job || DispatchState.jobs.find(j => j.id === jobId) || {};
  const jd = item.jobDay || item;
  const customer = getCustomerById(job.customerId)?.name || job.customer || 'No customer';
  const jobNo = formatJobNumber(job);
  const finished = job.status === 'finished' || jd.status === 'finished';
  const color = getCustomerColor(job.customerId || job.customer);
  // formatAssignments shows drivers/trucks/assets only. A lane can be a helper's
  // or a trailer's, so fold helpers in with drivers (once each) and add trailers,
  // or a helper-only run would read "Unassigned" in that helper's own list (COD).
  const crewRefs = [...new Set([...(jd.drivers || job.drivers || []), ...(jd.helpers || job.helpers || [])])];
  const trailerNames = (jd.trailers || job.trailers || []).map(ref => {
    const t = _resolveFleetRef(ref, DispatchState.trailers);
    return t ? (window.getUnitDisplay?.(t) || t.name || ref) : ref;
  });
  const crewJd = { ...jd, drivers: crewRefs, trucks: jd.trucks || job.trucks || [], assets: jd.assets || job.assets || [] };
  const hasCrew = crewJd.drivers.length || crewJd.trucks.length || crewJd.assets.length || (jd.subcontractors || []).length;
  const trailerBadge = trailerNames.length ? `<span class="assignment-badge truck">${trailerNames.map(escapeHtml).join(', ')}</span>` : '';
  // Trailer-only run: show the trailer, not "Unassigned" beside it.
  const crewHtml = [hasCrew || !trailerBadge ? formatAssignments(crewJd) : '', trailerBadge].filter(Boolean).join(' ');
  return `
    <button type="button" class="lane-job-row${finished ? ' finished' : ''}" style="border-left-color:${color};${_tentativeCardStyle(jd)}" onclick="window.DispatchModule.openLaneJob('${escJsAttr(jobId)}', '${escJsAttr(jobDayId)}')">
      <span class="lane-job-row-top">
        ${jobNo ? `<span class="lane-job-no">${escapeHtml(jobNo)}</span>` : ''}
        <span class="lane-job-customer">${escapeHtml(customer)}</span>
        ${finished ? '<span class="search-finished-badge">Finished</span>' : ''}
      </span>
      ${/* TASK-9750 — label only: this row is itself a <button>, so no nested Confirm */ _dayFlagChipsHtml(jd, { inline: true })}
      <span class="lane-job-title">${escapeHtml(job.title || 'Untitled')}</span>
      <span class="lane-job-crew">${crewHtml}</span>
    </button>`;
}

function _showLaneJobsModal(title, bodyHtml) {
  document.getElementById('modal-lane-jobs-title').textContent = title;
  document.getElementById('lane-jobs-content').innerHTML = bodyHtml;
  document.getElementById('modal-lane-jobs').classList.remove('hidden');
  // Keyboard users land in the list, not behind the overlay (COD).
  document.querySelector('#lane-jobs-content .lane-job-row')?.focus({ preventScroll: true });
}

// Tap on a lanes cell: one job opens straight (as the dot always did); 2+ list.
function openLaneCell(entityType, entityId, iso) {
  const r = _lanesLastRender;
  if (!r) return;
  const items = laneCellItems(r.map, entityType, entityId, iso);
  const action = laneCellTapAction(items);
  if (action.kind === 'none') return;
  if (action.kind === 'open') { showJobDetails(action.jobId, action.jobDayId || null); return; }
  const day = r.days.find(d => d.iso === iso);
  const name = r.names[`${entityType}-${entityId}`] || '';
  const dayLabel = day ? `${day.dayName} ${day.monthName} ${day.dayNum}` : iso;
  _showLaneJobsModal(`${name} · ${dayLabel} · ${action.count} jobs`,
    `<div class="lane-jobs-list">${items.map(_laneJobRowHtml).join('')}</div>`);
}

// Tap on a person / vehicle name: their jobs for the visible week, by day.
function openLaneWeek(entityType, entityId) {
  const r = _lanesLastRender;
  if (!r || !r.days.length) return;
  const groups = buildLaneWeekGroups(r.map, entityType, entityId, r.days);
  const name = r.names[`${entityType}-${entityId}`] || '';
  const first = r.days[0];
  const last = r.days[r.days.length - 1];
  const range = `${first.monthName} ${first.dayNum} – ${last.monthName} ${last.dayNum}`;
  const body = groups.length === 0
    ? `<div class="day-empty">No jobs ${escapeHtml(range)}.</div>`
    : groups.map(g => `
        <div class="lane-jobs-day">
          <div class="lane-jobs-day-label">${escapeHtml(g.label)} <span class="lane-jobs-day-count">${g.items.length === 1 ? '1 job' : `${g.items.length} jobs`}</span></div>
          <div class="lane-jobs-list">${g.items.map(_laneJobRowHtml).join('')}</div>
        </div>`).join('');
  _showLaneJobsModal(`${name} · ${range}`, body);
}

function openLaneJob(jobId, jobDayId) {
  closeModal('lane-jobs');
  showJobDetails(jobId, jobDayId || null);
}

// ============================================
// MAP VIEW
// ============================================

// Default center: Williston, ND (Bakken hub)
const MAP_DEFAULT_CENTER = { lat: 48.1470, lng: -103.6180 };
const MAP_DEFAULT_ZOOM = 7;
// TASK-1615 — fitBounds clamps. Max stops a one-stop day zooming into a
// driveway; min stops a wide bounds box producing the planet view.
const MAP_MAX_FIT_ZOOM = 14;
const MAP_MIN_FIT_ZOOM = 3;

// Status → marker color mapping
const MAP_STATUS_COLORS = {
  'assigned':   '#3B82F6', // blue
  'en-route':   '#F59E0B', // amber
  'on-site':    '#10B981', // green
  'finished':   '#6B7280', // gray
  'cancelled':  '#EF4444', // red
  'default':    '#3B82F6'  // blue fallback
};

// ── TASK-1616 — MAP DATE RANGE ──────────────────────────────────────────────
// The map used to follow the board's single `currentDate`. The question a
// dispatcher actually asks is "where did loads sit over the last few days, and
// do I already have a truck near this one" — that needs a span, not a day.
//
// Widening it is free. `getJobsForDate` reads DispatchState.jobDays /
// DispatchState.jobs, which are ALREADY streamed for `adminWindowDaysBack` days
// back plus everything upcoming. A range inside that window is a loop over
// arrays that are in memory: no Firestore query, no spinner, no loading state.
//
// The range is deliberately its OWN state and not `currentDate`: changing the
// map span must not drag the Week/Month/List board underneath it to another day.
const MAP_RANGE_MAX_DAYS = 30;  // markers grow linearly with the span
// TASK-4786 — one pin per STOP means the marker count is now stops, not jobs.
// Measured on live TITS 2026-09-08, after the change: yesterday 2 · today 7 ·
// 3d 13 · 7d 39 · 30d (the cap) 157 · busiest 30-day window in all history 164.
// Google Maps draws a few hundred plain markers without complaint, so this is a
// tripwire for a tenant far busier than TITS, not a gate on shipping.
const MAP_CLUSTER_THRESHOLD = 300;
const MAP_RANGE_PRESETS = [
  { id: 'today',     label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '3d',        label: 'Last 3 days' },
  { id: '7d',        label: 'Last 7 days' }
];

// A local-calendar day, N days from today, as YYYY-MM-DD.
//
// NOT `formatDateISO(new Date())` — that is `.toISOString()`, i.e. UTC, so
// after ~18:00 CT "today" silently becomes tomorrow and "yesterday" becomes
// today. Stored `jobDay.date` is anchored to NOON local, so ITS toISOString()
// date IS the local date — which means the range keys have to be local dates
// too or the comparison in getJobsForDate never lines up.
function mapDayISO(offsetDays) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offsetDays);
  return formatDateISOLocal(d);
}

// TODAY is the default (TASK-4786 A2 — Chris, 2026-09-08: "today is default").
// This replaced the TASK-1616 yesterday default: dispatchers work in the
// present, and a yesterday map breaks morning triage. Yesterday stays a chip —
// it is still the answer to "where were we". The multi-day presets are trailing
// windows that INCLUDE today, because today's loads are exactly the ones you
// reuse a truck for. The header prints the resolved dates so there is no guessing.
//
// The final return serves 'today' AND any id this function does not know (a
// stale value, a typo): the default, never silently some other day — the trap
// the old fall-through-to-yesterday set. Every OTHER preset has its own branch.
// .map-range-seg scrolls sideways on a phone (TASK-4786 COD r1): five chips do
// not fit 320-390px and overflow:hidden clipped Custom.
function mapRangeForPreset(id) {
  if (id === 'yesterday') return { from: mapDayISO(-1), to: mapDayISO(-1) };
  if (id === '3d') return { from: mapDayISO(-2), to: mapDayISO(0) };
  if (id === '7d') return { from: mapDayISO(-6), to: mapDayISO(0) };
  return { from: mapDayISO(0), to: mapDayISO(0) }; // 'today' AND any unknown id
}

// Every YYYY-MM-DD in [from, to] inclusive, capped at MAP_RANGE_MAX_DAYS.
//
// The cap keeps the MOST RECENT days of the request, not the oldest: someone
// who asks for six months and gets the first 30 days of it is looking at
// January while trying to decide where a truck is today.
//
// It returns the days it ACTUALLY walked, and the caller labels the header from
// those — the first cut of this printed the requested span ("Jan 31 – Aug 19")
// over a map that only held 30 days of it, which is the same silent-shortfall
// shape this file keeps getting bitten by.
function mapRangeDays(fromISO, toISO) {
  const days = [];
  if (!fromISO || !toISO) return { days, clamped: false };
  // T12:00:00 for the same reason every other date parse in this file uses it:
  // a bare 'YYYY-MM-DD' parses as UTC midnight and reads back a day early.
  const start = new Date(fromISO + 'T12:00:00');
  const end = new Date(toISO + 'T12:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
    return { days, clamped: false };
  }
  const span = Math.round((end - start) / 86400000) + 1;
  const clamped = span > MAP_RANGE_MAX_DAYS;
  const cur = clamped
    ? new Date(end.getTime() - (MAP_RANGE_MAX_DAYS - 1) * 86400000)
    : new Date(start);
  while (cur <= end) {
    days.push(formatDateISOLocal(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return { days, clamped };
}

// The live range, defaulting to today on first paint. Also repairs a
// backwards custom pair rather than rendering an empty map for it.
function getMapRange() {
  const _preset = DispatchState.mapRangePreset || 'today';
  // TASK-4786 A2 (COD r1 Edge/Domain/Blast) — a PRESET is relative to the
  // current day, so it is re-resolved on every paint. Before, the dates were
  // stored once on first paint, so a tab left open past midnight kept the lit
  // "Today" chip over the previous day's map. Only Custom keeps stored dates.
  if (_preset !== 'custom' || !DispatchState.mapDateFrom || !DispatchState.mapDateTo) {
    const r = mapRangeForPreset(_preset);
    DispatchState.mapDateFrom = r.from;
    DispatchState.mapDateTo = r.to;
  }
  if (DispatchState.mapDateTo < DispatchState.mapDateFrom) {
    const t = DispatchState.mapDateFrom;
    DispatchState.mapDateFrom = DispatchState.mapDateTo;
    DispatchState.mapDateTo = t;
  }
  return { from: DispatchState.mapDateFrom, to: DispatchState.mapDateTo };
}

// Earliest date the live subscription actually holds, or null when the window
// is "all history". A range reaching past this returns LESS than the user asked
// for and there is nothing in the returned data that says so — the map just
// looks empty. Surfaced in the header with the "Load older" escape hatch, so it
// can never read as "no jobs there".
function mapWindowFloorISO() {
  const raw = Number(DispatchState.adminWindowDaysBack);
  const daysBack = Number.isFinite(raw) ? raw : 90; // NOT `|| 90` — 0 = "all"
  if (daysBack <= 0) return null;
  return mapDayISO(-daysBack);
}

// 'Aug 17' / 'Aug 17 – Aug 19'. Noon-anchored parse, same as everywhere else.
// TASK-9628 — the Saved waypoints count. A bare "25" beside the row read as
// the TOTAL, so "19 not plotted" looked like it contradicted the map. Say how
// many are on the map out of how many are saved.
function mapSavedCountText(onMap, total) {
  const a = Math.max(0, Number(onMap) || 0);
  const t = Math.max(a, Number(total) || 0);
  return a === t ? `${a} saved place${a !== 1 ? 's' : ''} on the map` : `${a} of ${t} saved places on the map`;
}

function mapRangeLabel(fromISO, toISO) {
  const opts = { month: 'short', day: 'numeric' };
  const fmt = iso => new Date(iso + 'T12:00:00').toLocaleDateString('en-US', opts);
  if (!fromISO || !toISO) return '';
  return fromISO === toISO ? fmt(fromISO) : `${fmt(fromISO)} – ${fmt(toISO)}`;
}

function repaintMapView() {
  const content = document.getElementById('dispatch-content');
  if (content && DispatchState.viewMode === 'map') renderMapView(content);
}

function setMapRangePreset(id) {
  const r = mapRangeForPreset(id);
  // TASK-9175 — picking a date is leaving the project view (the project filter
  // ignores dates; the range comes back when it is cleared).
  DispatchState.mapProjectId = null;
  DispatchState.mapRangePreset = id;
  DispatchState.mapShowCustom = false;
  DispatchState.mapDateFrom = r.from;
  DispatchState.mapDateTo = r.to;
  repaintMapView();
}

function toggleMapCustomRange() {
  DispatchState.mapShowCustom = !DispatchState.mapShowCustom;
  repaintMapView();
}

function applyMapCustomRange() {
  const from = document.getElementById('map-range-from')?.value || '';
  const to = document.getElementById('map-range-to')?.value || '';
  if (!from || !to) {
    window.showToast?.('Pick both a start and an end date.', 'error');
    return;
  }
  DispatchState.mapProjectId = null;  // TASK-9175 — see setMapRangePreset
  DispatchState.mapRangePreset = 'custom';
  DispatchState.mapShowCustom = true;
  DispatchState.mapDateFrom = from <= to ? from : to;
  DispatchState.mapDateTo = from <= to ? to : from;
  repaintMapView();
}

// ── TASK-9174 / TASK-9734 — HOW AN EXISTING JOB GETS ITS ROAD ROUTE ─────────
// TASK-9174 put this behind a "Draw road routes" click so a repaint could never
// become a Directions request storm. TASK-9734 (Chris 2026-10-08: "the job stops
// are not making routes the way they are supposed to") makes it automatic —
// nobody should have to remember a button — WITHOUT giving up that rule:
//
// 🔴 A REPAINT NEVER FETCHES. renderMapView only builds the queue
// (`_routeWarmQueue`: jobs on screen whose routeLookupState is `due`) and calls
// scheduleMapRouteWarm, which is debounced. autoWarmMapRoutes then takes at
// most MAP_ROUTE_WARM_BATCH jobs, MAP_ROUTE_WARM_CONCURRENCY at a time, waits
// MAP_ROUTE_WARM_PAUSE_MS before the next batch, and every lookup goes through
// resolveJobRouteCache's lease. A job is looked up once per change of stops;
// only a TRANSIENT failure is asked again, on its own backoff.
//
// Only a role that can write a job warms (`_dispatchCanManageDays`). A
// view-only viewer sees the honest "not looked up yet" state until someone who
// can write opens the map or saves the job.
//
// COST, stated plainly: one Google Directions request per job per change of
// stops, at Google's Directions rate — the same as a dispatcher clicking the
// old button for every job, just without the click.
let _routeWarmBusy = false;
let _routeWarmRepaintPending = false;
let _routeWarmTimer = null;
let _routeWarmNextAt = 0;
// `${tenantId}|${jobId}` → { fp, at }. Guards the seconds between our own
// write and the jobs snapshot that carries it back — without it, a repaint in
// that gap would queue the same job again. Expires after the lease would have.
const _routeWarmTried = new Map();
const MAP_ROUTE_TRIED_TTL_MS = MAP_ROUTE_LEASE_MS + 30 * 1000;

let _routeWakeTimer = null;
function scheduleMapRouteWake(atMs) {
  if (_routeWakeTimer) { clearTimeout(_routeWakeTimer); _routeWakeTimer = null; }
  if (!Number.isFinite(atMs)) return;
  // setTimeout caps near 24.8 days; the backoff caps at 6 h, so this is far inside it.
  const wait = Math.min(MAP_ROUTE_RETRY_MAX_MS, Math.max(5000, atMs - Date.now() + 1000));
  _routeWakeTimer = setTimeout(() => {
    _routeWakeTimer = null;
    if (DispatchState.viewMode === 'map') repaintMapView();
  }, wait);
}

function scheduleMapRouteWarm() {
  if (_routeWarmBusy || _routeWarmTimer) return;
  if (!(DispatchState._routeWarmQueue || []).length) return;
  const wait = Math.max(1500, _routeWarmNextAt - Date.now());
  _routeWarmTimer = setTimeout(() => {
    _routeWarmTimer = null;
    autoWarmMapRoutes().catch(e => console.warn('[dispatch map] background route lookup failed:', e?.message || e));
  }, wait);
}

async function autoWarmMapRoutes() {
  if (_routeWarmBusy) return { looked: 0 };
  if (DispatchState.viewMode !== 'map' || !_dispatchCanManageDays()) return { looked: 0 };
  if (Date.now() < _routeWarmNextAt) { scheduleMapRouteWarm(); return { looked: 0 }; }
  const tid = DispatchState.tenantId;
  if (!tid) return { looked: 0 };

  const now = Date.now();
  for (const [k, v] of _routeWarmTried) if (now - v.at > MAP_ROUTE_TRIED_TTL_MS) _routeWarmTried.delete(k);
  const batch = [];
  for (const q of (DispatchState._routeWarmQueue || [])) {
    const t = _routeWarmTried.get(tid + '|' + q.jobId);
    if (t && t.fp === q.fp) continue;
    batch.push(q);
    if (batch.length >= MAP_ROUTE_WARM_BATCH) break;
  }
  if (!batch.length) return { looked: 0 };
  batch.forEach(q => _routeWarmTried.set(tid + '|' + q.jobId, { fp: q.fp, at: now }));

  _routeWarmBusy = true;
  _setMapRouteWarmChip(true);
  let wrote = 0, throttled = false, deniedAt = 0;
  const queue = batch.slice();
  const worker = async () => {
    while (queue.length && !throttled) {
      const q = queue.shift();
      // The job's LATEST snapshot, so stops edited since the paint are what we
      // route; resolveJobRouteCache re-checks against the stored doc anyway.
      const job = (DispatchState.jobs || []).find(j => j.id === q.jobId) || q;
      try {
        const rc = await resolveJobRouteCache(q.jobId, job.waypoints, null);
        if (rc) wrote++;
        else if (DispatchState.tenantId !== tid) break;
      } catch (e) {
        // A denied transaction (rules) or a network drop. Nothing was recorded,
        // so the job stays "not looked up yet" — never "no road".
        console.warn('[dispatch map] route lookup failed for job', q.jobId, e?.message || e);
        const code = String(e?.code || e?.message || '');
        if (/resource-exhausted|429/i.test(code)) throttled = true;
        // This viewer cannot write jobs after all — stop asking for a while
        // rather than burning a denied transaction per job every batch.
        if (/permission-denied/i.test(code)) { throttled = true; deniedAt = Date.now(); }
      }
    }
  };
  try {
    await Promise.all(Array.from({ length: Math.min(MAP_ROUTE_WARM_CONCURRENCY, queue.length) }, () => worker()));
  } finally {
    // 🔴 ALWAYS clear the flag: renderMapView defers its repaint while it is set.
    _routeWarmBusy = false;
    _routeWarmNextAt = Date.now() + (deniedAt ? 10 * 60 * 1000 : throttled ? 2 * MAP_ROUTE_WARM_PAUSE_MS : MAP_ROUTE_WARM_PAUSE_MS);
  }
  const pending = _routeWarmRepaintPending;
  _routeWarmRepaintPending = false;
  // One repaint for the whole batch (the viewport is kept — mapViewportToRestore).
  if (wrote || pending) repaintMapView();
  else { _setMapRouteWarmChip(false); scheduleMapRouteWarm(); }
  return { looked: batch.length, wrote };
}

// The header chip is the only on-screen sign a background lookup is running.
// Patched in place: a repaint for a chip would rebuild the whole map.
function _setMapRouteWarmChip(busy) {
  const chip = document.getElementById('map-route-status');
  if (!chip) return;
  chip.toggleAttribute('data-busy', !!busy);
}

// Kept for anything still holding the old export name: same work, no button.
async function warmVisibleJobRoutes() {
  _routeWarmNextAt = 0;
  return autoWarmMapRoutes();
}

// ── TASK-9175 — MAP LAYERS: ONE SIDE KEY THAT IS ALSO THE FILTER PANEL ───────
// Chris 2026-09-11: "maybe make a map key on the side?" The old `.map-legend`
// was crammed into the header opposite the stats; it now lives in an <aside>
// beside the map and every row toggles its layer. Four layers, each owning its
// own marker array so it clears independently:
//   Stops (default on) · Saved waypoints · Last dispatched · Fleet (Samsara)
// plus the 2026-09-29 slice: a Project filter that plots every stop of every
// job in one project, ignoring the date range.
//
// 🔴 "LAST DISPATCHED", NEVER "LAST KNOWN" (Chris decided 2026-09-11). The pin
// is where the dispatch record SENT the unit, taken from that job's stop — not a
// position. The clock only CHOOSES the job on a double-booked day; its own
// GPS fix is never pinned (it resolves to the Williston yard 9 times in 10).
//
// 🔴 SAMSARA IS GATED ON POSITIONS ARRIVING, NOT ON THE FLAG. One live tenant
// has `samsaraConnected: true` and no position doc; it used to get a purple
// "Fleet (Samsara)" legend entry for a layer that could never draw. The row now
// says what is actually true: not connected / connected but nothing arriving /
// last position N ago / N live.
// TASK-4800 — 'equipment' is the fifth row: what DD's own records say is OUT
// at each job site right now (trucks/trailers on today's jobDays, rental lots
// from the job ledger / assetTransactions, individually-tracked gear by
// deployedToJobId). Default OFF, like the other non-stop layers.
// TASK-4800 S4 — 'estimated' is the sixth: a best GUESS for gear the records do
// not put on a job (last record, else home yard). Its own row, default OFF,
// hollow markers — never mixed into Equipment on site (lazy views/mapEstimated.js).
const MAP_LAYER_IDS = ['stops', 'saved', 'lastDispatched', 'fleet', 'equipment', 'estimated'];
// Slate outline on a white fill: reads as "not solid", distinct from every
// filled marker colour on the map.
const MAP_ESTIMATE_COLOR = '#475569';
// Age of the OLDEST lot at a site: green < 7d, amber 7–14d, red > 14d, grey
// when no deploy timestamp exists (COD 2026-09-08 UX lane's spec; the red is
// the stranded-asset signal, TASK-4800 S3).
const MAP_EQUIPMENT_AGE_COLORS = { fresh: '#047857', aging: '#B45309', stale: '#B91C1C', unknown: '#6B7280' };
const MAP_EQUIPMENT_FRESH_DAYS = 7;
const MAP_EQUIPMENT_STALE_DAYS = 14;
// Open-lot jobs outside the board window are fetched one getDoc each when the
// layer is on; past this many the rest are reported as "not loaded" rather
// than fired as a read storm (Split Rock: 109 lots over far fewer jobs).
const MAP_EQUIPMENT_JOB_FETCH_CAP = 200;   // ceiling per tenant session
const MAP_EQUIPMENT_JOB_FETCH_BATCH = 80;  // per paint
// The tell on a pre-ledger job's scheduled-return row (see planEquipmentDeployments ②).
const MAP_EQUIPMENT_PENDING_NOTE_RE = /pending inspection/i;
// The one-shot ledger read is re-taken when the row is switched on and after
// this long — deploys and returns made during the session must show up.
const MAP_EQUIPMENT_TXN_TTL_MS = 2 * 60 * 1000;
const MAP_SAVED_COLOR = '#0E7490';            // teal-700: distinct from all five status colours, white-stroke legible
const MAP_LAST_DISPATCHED_COLOR = '#0F172A';  // brand navy: reads as "equipment", not as a job status
const MAP_FLEET_COLOR = '#8B5CF6';
const MAP_FLEET_FRESH_MS = 15 * 60 * 1000;    // same 15-minute staleness the fleet markers always used
const MAP_PROJECT_PIN_COLOR = '#7C3AED';
// Same-day tie-break, step ②. A real punch (step ①) always beats this.
const MAP_LAST_DISPATCHED_STATUS_RANK = { 'on-site': 4, 'en-route': 3, 'assigned': 2, 'scheduled': 2, 'dispatched': 2, 'finished': 1, 'complete': 1, 'completed': 1 };
// How long a fetched day of clock punches is trusted. Past days are settled;
// today is still being punched, so it is re-read after this.
const MAP_LD_CLOCK_TODAY_TTL_MS = 2 * 60 * 1000;

function mapLayerDefaults(hasSamsara) {
  return { stops: true, saved: false, lastDispatched: false, fleet: !!hasSamsara, equipment: false, estimated: false };
}

function tenantHasSamsaraFlag() {
  return !!(window.AppState?.settings?.samsaraConnected || window.AppState?.settings?.samsaraApiToken);
}

function _mapLayerStorageKey() {
  return 'dd_map_layers_' + (DispatchState.tenantId || window.AppState?.tenant || '');
}

// Per-viewer convenience only: a remembered toggle. Wrapped because storage can
// throw (private mode, blocked site data) and the map must render regardless.
function getMapLayers(hasSamsara) {
  if (!DispatchState.mapLayers) {
    const layers = mapLayerDefaults(hasSamsara);
    try {
      const saved = JSON.parse(localStorage.getItem(_mapLayerStorageKey()) || 'null');
      if (saved && typeof saved === 'object') {
        MAP_LAYER_IDS.forEach(id => { if (typeof saved[id] === 'boolean') layers[id] = saved[id]; });
      }
    } catch (_) { /* no storage — defaults */ }
    DispatchState.mapLayers = layers;
  }
  return DispatchState.mapLayers;
}

function toggleMapLayer(id) {
  if (!MAP_LAYER_IDS.includes(id)) return;
  const layers = getMapLayers(tenantHasSamsaraFlag());
  layers[id] = !layers[id];
  // TASK-4800 — switching Equipment ON re-reads the rental ledger (COD Code
  // lane: a session-long cache hides the deploys made since). The old rows
  // stay on screen until the fresh ones land, so nothing flickers empty.
  if (id === 'equipment' && layers[id] && DispatchState._mapEqTxnsState === 'loaded') {
    DispatchState._mapEqTxnsState = 'idle';
    DispatchState._mapEqJobs = {};   // their ledgers refresh with the rows (COD: a cached ledger outlived a return)
  }
  // TASK-4800 S4 — the estimates read the same ledger: fresh rows on switch-on.
  if (id === 'estimated' && layers[id] && DispatchState._mapEqTxnsState === 'loaded') {
    DispatchState._mapEqTxnsState = 'idle';
    DispatchState._mapEqJobs = {};   // COD 2026-10-27 (Code): same as Equipment — no stale job locations
  }
  try { localStorage.setItem(_mapLayerStorageKey(), JSON.stringify(layers)); } catch (_) { /* not persisted */ }
  repaintMapView();
}

function toggleMapKey() {
  DispatchState.mapKeyOpen = !_mapKeyIsOpen();
  const d = document.querySelector('.dispatch-map-key');
  if (d) d.open = DispatchState.mapKeyOpen;
}

// Open by default on a desktop, closed on a phone (it would otherwise eat the
// map). An explicit toggle wins for the rest of the session.
function _mapKeyIsOpen() {
  if (typeof DispatchState.mapKeyOpen === 'boolean') return DispatchState.mapKeyOpen;
  try { return !window.matchMedia('(max-width: 768px)').matches; } catch (_) { return true; }
}

// Pure. Saved locations split into what can be pinned and what cannot — the
// "N not plotted" half is the point: 14 of TITS's 33 are unresolved share
// links, and dropping them silently is the defect TASK-4786 already fixed once.
// `coordOf(loc)` returns a validated {lat,lng} or null (the caller passes
// plottableCoord with the URL fallback).
// TASK-9631 F2 — pure. Which pin a saved location keeps when its edit form is
// saved. A link that yields a coordinate always wins (the user pasted a new
// place). An UNCHANGED link keeps the STORED pin: a geocoded or map-dropped pin
// has no coordinate in its URL, and re-deriving from the URL alone wiped it on
// every edit. A CHANGED link with no coordinate drops the pin (COD r2): the old
// pin is now for a different place, and "not plotted" is honest where a pin
// at the old yard would send someone to the wrong gate.
// `urlCoords` is extractCoordsFromUrl(url); both go through plottableCoord.
function savedLocationCoordsForSave(prev, url, urlCoords) {
  const fromUrl = url ? plottableCoord(urlCoords || {}) : null;
  if (fromUrl) return { lat: fromUrl.lat, lng: fromUrl.lng };
  const linkUnchanged = !!prev && (prev.url || '') === (url || '');
  const kept = linkUnchanged ? plottableCoord(prev) : null;
  return kept ? { lat: kept.lat, lng: kept.lng } : { lat: null, lng: null };
}

function classifySavedLocationsForMap(locations, coordOf) {
  const plotted = [];
  const unplotted = [];
  for (const loc of (Array.isArray(locations) ? locations : [])) {
    if (!loc) continue;
    const c = coordOf(loc);
    // TASK-9540 — categoryId rides along so the map can draw the shape.
    const categoryId = typeof loc.categoryId === 'string' ? loc.categoryId : '';
    if (c) plotted.push({ id: loc.id, name: loc.name || '', url: loc.url || '', lat: c.lat, lng: c.lng, categoryId });
    else unplotted.push({ id: loc.id, name: loc.name || '', url: loc.url || '', categoryId });
  }
  return { plotted, unplotted };
}

// Pure. Where the dispatch record last SENT each unit.
//   units       [{ id, label, kind: 'truck'|'trailer' }]
//   jobDays     the in-memory board window (jobDay rows)
//   jobsById    Map jobId -> job
//   clockEntries punches available for the tie-break (any subset; a date the
//               caller has not loaded is reported back in clockDatesNeeded)
//   opts        { todayISO, dayISO(ts)->'YYYY-MM-DD'|null, toMs(ts)->number,
//                 unitMatches(ref, unit)->bool, stopFor(job)->stop|null,
//                 clockUnknownForDate(dateISO)->bool }
// THE RULE (measured on TITS 2026-09-11: 45 of 116 truck-days are double-booked,
// so this is the common path): take the unit's LATEST jobDay on or before today.
// One job that day -> it. More than one -> ① the unit's latest clockEntry that
// names one of those jobs, ordered by clockOut || clockIn (a real punch beats a
// status nobody advanced); ② else a unique top status on-site > en-route >
// assigned > finished; ③ still tied -> DO NOT GUESS, list it as ambiguous.
// `createdAt` is deliberately not a tie-break: it records when the row was
// TYPED (backfills land days late), not when the truck ran.
function planLastDispatched(units, jobDays, jobsById, clockEntries, opts) {
  const { todayISO, dayISO, toMs, unitMatches, stopFor } = opts;
  const out = { pins: [], ambiguous: [], unplotted: [], clockDatesNeeded: [] };
  const needDates = new Set();
  const isCancelled = (s) => String(s || '').toLowerCase() === 'cancelled';
  const titleOf = (job) => (job && (job.title || job.name)) || 'Untitled Job';
  const rows = (jobDays || []).filter(jd => jd && jd.jobId && !isCancelled(jd.status));
  for (const unit of (units || [])) {
    if (!unit) continue;
    const field = unit.kind === 'trailer' ? 'trailers' : 'trucks';
    let lastDate = null;
    let sameDay = [];
    for (const jd of rows) {
      const refs = jd[field];
      if (!Array.isArray(refs) || !refs.some(r => unitMatches(r, unit))) continue;
      const job = jobsById.get(jd.jobId);
      if (!job || isCancelled(job.status)) continue;
      const d = dayISO(jd.date);
      if (!d || d > todayISO) continue;
      if (!lastDate || d > lastDate) { lastDate = d; sameDay = [jd]; }
      else if (d === lastDate) sameDay.push(jd);
    }
    if (!lastDate) continue;
    const byJob = new Map();
    sameDay.forEach(jd => { if (!byJob.has(jd.jobId)) byJob.set(jd.jobId, jd); });
    const cands = [...byJob.values()];

    let chosen = null;
    let reason = null;
    if (cands.length === 1) {
      chosen = cands[0];
      reason = 'only';
    } else {
      // COD r1 (Code Reviewer): ask about freshness BEFORE trusting any punch —
      // a single cached punch from this morning must not stand in for today's
      // later ones.
      if (typeof opts.clockUnknownForDate === 'function' && opts.clockUnknownForDate(lastDate)) needDates.add(lastDate);
      const candJobIds = new Set(cands.map(c => c.jobId));
      let best = null;
      let bestMs = -Infinity;
      for (const ce of (clockEntries || [])) {
        if (!ce || !candJobIds.has(ce.jobId)) continue;
        if (!Array.isArray(ce.assetIds) || !ce.assetIds.some(a => unitMatches(a, unit))) continue;
        if (dayISO(ce.clockIn) !== lastDate) continue;
        const ms = toMs(ce.clockOut) || toMs(ce.clockIn) || 0;
        if (ms > bestMs) { bestMs = ms; best = ce; }
      }
      if (best) {
        chosen = cands.find(c => c.jobId === best.jobId) || null;
        reason = 'clock';
      } else {
        const rank =(jd) => MAP_LAST_DISPATCHED_STATUS_RANK[String(jd.status || 'assigned').toLowerCase()] || 0;
        const top = Math.max(...cands.map(rank));
        const atTop = cands.filter(c => rank(c) === top);
        if (top > 0 && atTop.length === 1) { chosen = atTop[0]; reason = 'status'; }
      }
    }

    const base = { unitId: unit.id, unitLabel: unit.label, kind: unit.kind, dateISO: lastDate, sameDayJobs: cands.length };
    if (!chosen) {
      out.ambiguous.push({
        ...base,
        candidates: cands.map(c => ({ jobId: c.jobId, jobDayId: c.id || '', title: titleOf(jobsById.get(c.jobId)), status: c.status || 'assigned' }))
      });
      continue;
    }
    const job = jobsById.get(chosen.jobId);
    const rec = {
      ...base,
      jobId: chosen.jobId,
      jobDayId: chosen.id || '',
      title: titleOf(job),
      jobNumber: job && job.jobNumber != null ? job.jobNumber : null,
      status: chosen.status || 'assigned',
      reason
    };
    const stop = stopFor(job);
    if (stop) out.pins.push({ ...rec, lat: stop.lat, lng: stop.lng, stopName: stop.name, stopNumber: stop.stopNumber, totalStops: stop.totalStops });
    else out.unplotted.push(rec);
  }
  out.clockDatesNeeded = [...needDates].sort();
  return out;
}

// Pure. A project runs for weeks, so its jobs are the board's in-memory jobs
// PLUS whatever the one-shot `projectId ==` query returned (the board only holds
// a window). Deduped by id, the fetched copy losing to the live board copy,
// cancelled jobs left off, oldest first.
function mergeProjectJobs(boardJobs, fetchedJobs, projectId) {
  if (!projectId) return [];
  const byId = new Map();
  for (const j of (Array.isArray(fetchedJobs) ? fetchedJobs : [])) {
    if (j && j.id && j.projectId === projectId) byId.set(j.id, j);
  }
  for (const j of (Array.isArray(boardJobs) ? boardJobs : [])) {
    if (j && j.id && j.projectId === projectId) byId.set(j.id, j);
  }
  const ms = (t) => (t && typeof t.toMillis === 'function') ? t.toMillis()
    : (t && typeof t.seconds === 'number') ? t.seconds * 1000
    : (t instanceof Date ? t.getTime() : (Number.isFinite(Date.parse(t)) ? Date.parse(t) : 0));
  return [...byId.values()]
    .filter(j => String(j.status || '').toLowerCase() !== 'cancelled')
    .sort((a, b) => ms(a.startDate) - ms(b.startDate));
}

// Pure. What the Fleet (Samsara) row may honestly claim.
//   state: 'not-connected' | 'off' | 'waiting' (subscribed, no answer yet) |
//          'unreadable' (permission denied) | 'no-positions' | 'stale' | 'live'
function fleetLayerState({ hasSamsara, layerOn, answered, denied, exists, vehicles, nowMs, toMs, coordOf }) {
  if (!hasSamsara) return { state: 'not-connected', plotted: [], freshCount: 0, newestMs: null };
  if (!layerOn) return { state: 'off', plotted: [], freshCount: 0, newestMs: null };
  if (denied) return { state: 'unreadable', plotted: [], freshCount: 0, newestMs: null };
  if (!answered) return { state: 'waiting', plotted: [], freshCount: 0, newestMs: null };
  if (!exists) return { state: 'no-positions', plotted: [], freshCount: 0, newestMs: null };
  const plotted = [];
  let freshCount = 0;
  let newestMs = null;
  Object.entries(vehicles || {}).forEach(([key, v]) => {
    const c = v ? coordOf(v) : null;
    if (!c) return;
    const at = toMs(v.updatedAt);
    const fresh = at > 0 && (nowMs - at) <= MAP_FLEET_FRESH_MS;
    if (fresh) freshCount++;
    if (at > 0 && (newestMs == null || at > newestMs)) newestMs = at;
    plotted.push({ key, v, lat: c.lat, lng: c.lng, at, fresh });
  });
  if (!plotted.length) return { state: 'no-positions', plotted, freshCount: 0, newestMs };
  return { state: freshCount > 0 ? 'live' : 'stale', plotted, freshCount, newestMs };
}

// ── TASK-4800 — EQUIPMENT DEPLOYMENT, DERIVED ON READ ────────────────────────
// Pure. What DD's own records say is OUT at each job site right now. No new
// write path, no placement ledger (COD 2026-09-08: derive on read;
// `deployedQty` stays the one source of truth for availability — this EXPLAINS
// it and shows the delta, it never repairs it). Spec: docs/spec-asset-location-map-mvp.md.
//
//   units        [{ id, label, kind:'truck'|'trailer' }]  (from _mapFleetUnits)
//   assets       roster asset docs
//   jobDays      the in-memory board window
//   jobsById     Map jobId -> job (board window + any fetched for the layer)
//   transactions assetTransactions rows, or null when not read yet
//   opts { todayISO, dayISO(ts), toMs(ts), nowMs, unitMatches(ref, unit),
//          locateJob(job) -> { lat, lng, name, source } | null,
//          jobMissing(jobId) -> true when a fetch proved the job is gone }
//
// A deployment is one of, in DD data terms:
//   truck/trailer  on a non-cancelled jobDay dated TODAY; "since" = the first
//                  day of the unbroken run of days on that job ending today
//   rental lot     quantity mode. Job HAS rentalLedger -> ledger.out (+
//                  inspected, flagged pending) is the quantity, transactional
//                  with deployedQty. Job has NO ledger (every pre-2026-09-23
//                  job) -> net of its assetTransactions rows: deploy − return.
//                  "since" = the first deploy row's createdAt, either way.
//   individual     deploymentStatus 'deployed' + deployedToJobId -> 1 unit.
// Row vocabulary differs by writer (`deploy`/`return` vs `dispatch`/`ticket`):
// classified by direction||type; anything else on a rental asset is COUNTED
// as unclassified, never dropped. A lot whose job is gone is "deleted-job";
// one whose job is not in memory is "job-not-loaded" and goes in jobsNeeded.
// Location = the job's last plottable stop, else its project pin, else the
// deployment is listed as unplotted (never dropped, never guessed).
function planEquipmentDeployments({ units, assets, jobDays, jobsById, transactions }, opts) {
  const { todayISO, dayISO, toMs, nowMs, unitMatches, locateJob } = opts;
  const jobMissing = typeof opts.jobMissing === 'function' ? opts.jobMissing : () => false;
  const isCancelled = (s) => String(s || '').toLowerCase() === 'cancelled';
  const isRental = (a) => !!a && (a.quantityType === 'rental' || !!a.isRental);
  const num = (v) => { const n = Number(v); return Number.isFinite(n) && n > 0 ? n : 0; };
  const titleOf = (job) => (job && (job.title || job.name)) || 'Untitled Job';
  const deployments = [];   // every deployment, plotted or not
  const jobsNeeded = new Set();
  let unclassifiedRows = 0;

  const jobRef = (jobId) => {
    const job = jobsById.get(jobId) || null;
    if (job) return { job, reason: null };
    if (jobMissing(jobId)) return { job: null, reason: 'deleted-job' };
    jobsNeeded.add(jobId);
    return { job: null, reason: 'job-not-loaded' };
  };
  const push = (rec) => {
    const { job, reason } = jobRef(rec.jobId);
    const loc = job ? locateJob(job) : null;
    deployments.push({
      ...rec,
      title: job ? titleOf(job) : (reason === 'deleted-job' ? 'Job no longer exists' : 'Job not loaded'),
      jobNumber: job && job.jobNumber != null ? job.jobNumber : null,
      customer: job ? (job.customer || job.customerName || '') : '',
      customerId: job ? (job.customerId || '') : '',
      projectId: job ? (job.projectId || '') : '',
      lat: loc ? loc.lat : null, lng: loc ? loc.lng : null,
      locName: loc ? (loc.name || '') : '',
      locSource: loc ? loc.source : null,
      reason: reason || (loc ? null : 'no-location'),
    });
  };

  // ① trucks / trailers on today's jobDays.
  const rows = (jobDays || []).filter(jd => jd && jd.jobId && !isCancelled(jd.status));
  for (const unit of (units || [])) {
    if (!unit) continue;
    const field = unit.kind === 'trailer' ? 'trailers' : 'trucks';
    const datesByJob = new Map();
    for (const jd of rows) {
      const refs = jd[field];
      if (!Array.isArray(refs) || !refs.some(r => unitMatches(r, unit))) continue;
      const d = dayISO(jd.date);
      if (!d || d > todayISO) continue;
      if (!datesByJob.has(jd.jobId)) datesByJob.set(jd.jobId, { dates: new Set(), jobDayId: '', status: '' });
      const e = datesByJob.get(jd.jobId);
      e.dates.add(d);
      if (d === todayISO) { e.jobDayId = jd.id || ''; e.status = jd.status || 'assigned'; }
    }
    // A unit on more than one job today is a normal multiple-run day, and it is
    // shown at EVERY one of them — with the count, so nobody reads two pins as
    // two trucks (Independent lane: Last dispatched refuses to place it; this
    // layer answers a different question, "what is out today").
    const todayJobs = [...datesByJob.values()].filter(e => e.dates.has(todayISO)).length;
    for (const [jobId, e] of datesByJob) {
      if (!e.dates.has(todayISO)) continue;
      // Walk back from today while the previous calendar day is also on this job.
      let since = todayISO;
      for (;;) {
        const prev = new Date(since + 'T12:00:00'); prev.setDate(prev.getDate() - 1);
        const p = formatDateISOLocal(prev);
        if (!e.dates.has(p)) break;
        since = p;
      }
      push({ kind: unit.kind, assetId: unit.id, label: unit.label, qty: 1, pending: 0,
        sinceMs: new Date(since + 'T12:00:00').getTime(), sinceISO: since, sinceKnown: true,
        jobId, jobDayId: e.jobDayId, status: e.status, otherJobsToday: todayJobs - 1 });
    }
  }

  // ② rental lots.
  const assetById = new Map((assets || []).filter(a => a && a.id).map(a => [a.id, a]));
  const lastDeploy = new Map();    // `${assetId}|${jobId}` -> ms (individual mode's "since")
  const seenLots = new Set();      // (asset, job) pairs the ledger answered
  // ②a — jobs that carry a ledger: the ledger is the quantity, transactional
  // with deployedQty (TASK-9336). "since" comes from the job's own deploy rows.
  const ledgerFirst = new Map();
  const ledgerJobIds = new Set();
  for (const job of jobsById.values()) {
    const ledger = job && job.rentalLedger;
    if (!ledger || typeof ledger !== 'object' || Array.isArray(ledger)) continue;
    ledgerJobIds.add(job.id);
  }
  if (Array.isArray(transactions)) {
    for (const t of transactions) {
      if (!t || !t.assetId || !t.jobId) continue;
      const dir = String(t.direction || t.type || '').toLowerCase();
      if (dir !== 'deploy') continue;
      const at = toMs(t.createdAt);
      if (!at) continue;
      const k = `${t.assetId}|${t.jobId}`;
      if (!lastDeploy.has(k) || at > lastDeploy.get(k)) lastDeploy.set(k, at);
      if (ledgerJobIds.has(t.jobId) && (!ledgerFirst.has(k) || at < ledgerFirst.get(k))) ledgerFirst.set(k, at);
    }
  }
  const ledgerLots = [];
  const spillOf = (e) => {
    const dr = e && e.dayReturns && typeof e.dayReturns === 'object' ? Object.values(e.dayReturns) : [];
    // TASK-10006 S3 — the share a real pickup took off its source jobs is
    // already off THEIR ledger lots (fromJobs); only the rest still spills.
    return dr.reduce((n, r) => n + Math.max(0, num(r && r.qty) - Math.min(num(r && r.take), num(r && r.qty)) - Object.values((r && r.fromJobs) || {}).reduce((m, v) => m + num(v), 0)), 0);
  };
  for (const job of jobsById.values()) {
    if (!ledgerJobIds.has(job.id)) continue;
    for (const [assetId, e] of Object.entries(job.rentalLedger)) {
      const asset = assetById.get(assetId);
      if (!isRental(asset) || asset.trackingMode !== 'quantity') continue;
      const out = num(e && e.out), inspected = num(e && e.inspected);
      const k = `${assetId}|${job.id}`;
      seenLots.add(k);
      if (out + inspected <= 0) continue;
      ledgerLots.push({ job, assetId, asset, out, inspected, ms: ledgerFirst.get(k) || 0, startMs: toMs(job.startDate) || 0 });
    }
  }
  // TASK-10006 — a pickup on a LEDGER return job takes units that job never
  // held (its dayReturns qty − take: the "spill"). "Create return job" counted
  // them from the SAME PROJECT's jobs, so they come off that project's ledger
  // lots first, oldest first — otherwise the install job keeps showing every
  // mat on site after they are back. Only `out` (units still on site) is
  // taken, never `inspected`. What the project's lots cannot cover goes on to
  // the older pre-ledger release below, exactly as before (spillLeft).
  const spillLeft = new Map();   // `${jobId}|${assetId}` -> spill not matched in its project
  for (const job of jobsById.values()) {
    if (!ledgerJobIds.has(job.id)) continue;
    for (const [assetId, e] of Object.entries(job.rentalLedger)) {
      const asset = assetById.get(assetId);
      if (!isRental(asset) || asset.trackingMode !== 'quantity') continue;
      let left = spillOf(e);
      if (left <= 0) continue;
      // COD r1: the job it was made from counts too (returnForJobId), so a
      // return saved without a project still clears its source's lot.
      // COD r2: every ANCESTOR (a return of a return of A clears A), 5 levels,
      // cycle-safe — the same depth _loadProjectRentalsOut walks down.
      const ancestors = new Set();
      for (let a = job.returnForJobId, d = 0; a && d < 5 && !ancestors.has(a) && a !== job.id; d++) {
        ancestors.add(a);
        a = (jobsById.get(a) || {}).returnForJobId;
      }
      if (job.projectId || ancestors.size) {
        const lots = ledgerLots.filter(l => l.assetId === assetId && l.job.id !== job.id && l.out > 0
            && ((job.projectId && l.job.projectId === job.projectId) || ancestors.has(l.job.id)))
          .sort((a, b) => (a.ms || a.startMs || Infinity) - (b.ms || b.startMs || Infinity) || String(a.job.id).localeCompare(String(b.job.id)));
        for (const l of lots) {
          if (left <= 0) break;
          const t = Math.min(l.out, left);
          l.out -= t; left -= t;
        }
      }
      spillLeft.set(`${job.id}|${assetId}`, left);
    }
  }
  for (const l of ledgerLots) {
    if (l.out + l.inspected <= 0) continue;
    push({ kind: 'rental', assetId: l.assetId, label: l.asset.name || l.asset.partNumber || l.assetId, qty: l.out + l.inspected, pending: l.inspected,
      sinceMs: l.ms, sinceISO: l.ms ? dayISO(l.ms) : null, sinceKnown: !!l.ms, jobId: l.job.id, jobDayId: '', status: '', basis: 'ledger' });
  }
  // ②b — jobs with NO ledger (every job before 2026-09-23): replay the rows,
  // PER ASSET, in time order.
  //
  // 🔴 MEASURED ON SPLIT ROCK 2026-09-29 (the only tenant with real Ground
  // Protection history): 109 deploy rows on install jobs, 97 return rows on 41
  // SEPARATE "Mat Removal" jobs — not one job carries both. Netting deploy −
  // return per (asset, job) therefore never cancels anything and drew 5,997
  // Megadeck across the install sites against a ledger net of 96. A return is
  // a return of THAT ITEM, whichever job it was booked on: it releases the
  // asset's open lots — the return's own job first, then lots for the same
  // customer, then the oldest first. Sites therefore always sum to the
  // asset's ledger net (Σdeploy − Σreturn), and that net matched the counter
  // exactly on 5 of 11 Split Rock assets. The matching order is an assumption
  // and the UI says so ("matched to returns by customer, oldest first").
  // Rows on ledger jobs are left out here (②a answered them exactly).
  const events = new Map();        // assetId -> rows on non-ledger jobs
  if (Array.isArray(transactions)) {
    for (const t of transactions) {
      if (!t || !t.assetId || !t.jobId) continue;   // receipts / ticket rows carry no job
      if (ledgerJobIds.has(t.jobId)) continue;
      const asset = assetById.get(t.assetId);
      if (!isRental(asset) || asset.trackingMode !== 'quantity') continue;   // consumables are consumed, not deployed; individual mode is ③
      if (!events.has(t.assetId)) events.set(t.assetId, []);
      events.get(t.assetId).push(t);
    }
  }
  // COD on the diff (Edge Case): a return booked on a LEDGER job can take
  // more than that job holds — the ledger caps its own `take` at `out` while
  // deployedQty still drops by the full quantity, and the spill came off a
  // pre-ledger lot. The ledger records exactly that: dayReturns[k] = { qty,
  // take }, so Σ(qty − take) is the spill. It is fed to the release as a
  // return by that job (own job never matches — it holds no pre-ledger lot —
  // so it goes same customer, then oldest). Inspection-required returns on a
  // ledger job are held in `inspected`, not dayReturns, so they are not here.
  if (Array.isArray(transactions)) {
    for (const jobId of ledgerJobIds) {
      const job = jobsById.get(jobId);
      for (const [assetId, e] of Object.entries(job.rentalLedger)) {
        const asset = assetById.get(assetId);
        if (!isRental(asset) || asset.trackingMode !== 'quantity') continue;
        // TASK-10006 — only what its own project's ledger lots did not cover.
        const _sk = `${jobId}|${assetId}`;
        const spill = spillLeft.has(_sk) ? spillLeft.get(_sk) : spillOf(e);
        if (spill <= 0) continue;
        if (!events.has(assetId)) events.set(assetId, []);
        events.get(assetId).push({ assetId, jobId, direction: 'return', quantity: spill, __ledgerSpill: true });
      }
    }
  }
  const custOf = (jobId) => { const j = jobsById.get(jobId); return j ? String(j.customerId || j.customer || '') : ''; };
  // Rows are typed in batches and backfilled: on Split Rock 107 of 205 carry a
  // `jobDate` more than a day from `createdAt` (a removal typed 04-13 for
  // 04-18; an install typed 04-02 for 03-24), and a 543-mat return was typed
  // three days before the 556-mat install it undid. Neither date orders the
  // rows reliably, so the replay does NOT depend on order: every deploy is
  // applied first, then every return releases from what is open. Each asset's
  // sites therefore sum to exactly its ledger net, Σdeploy − Σreturn (measured
  // 2026-09-29: that net equals the counter on System 7 / Durabase / 12'x50' /
  // 508 Wood / 20'x20', and is the "Off by" on the rest). Returns release in
  // operational order (jobDate, else createdAt) so the earlier removal takes
  // the older lot. A lot's "since" is the EARLIER of a deploy row's two dates:
  // the mats were out no later than either record says.
  const evMs = (t) => toMs(t.jobDate) || toMs(t.createdAt) || 0;
  const sinceMsOf = (t) => { const j = toMs(t.jobDate), c = toMs(t.createdAt); return j && c ? Math.min(j, c) : (j || c || 0); };
  const dirOfRow = (t) => String(t.direction || t.type || '').toLowerCase();
  // A removal job outside the board window is worth fetching too (it names the
  // customer the return matches on) — but after the deploy jobs, which carry
  // the locations, when the cap bites.
  const returnJobsWanted = new Set();
  let unmatchedReturns = 0;
  for (const [assetId, rows] of events) {
    const asset = assetById.get(assetId);
    // TASK-10015 — a positive reversal row (a give-back undone) sorts as a deploy.
    const _isOut = (t) => dirOfRow(t) === 'deploy' || (dirOfRow(t) === 'reversal' && Number(t.delta) > 0);
    rows.sort((a, b) => (_isOut(a) ? 0 : 1) - (_isOut(b) ? 0 : 1)
      || (evMs(a) || Infinity) - (evMs(b) || Infinity));
    const lots = new Map();        // jobId -> { qty, pending, firstMs }
    const release = (fromJobId, q, markPending) => {
      const cust = custOf(fromJobId);
      const order = [...lots.entries()].filter(([, l]) => (markPending ? l.qty - l.pending : l.qty) > 0)
        .sort((a, b) => (a[0] === fromJobId ? 0 : 1) - (b[0] === fromJobId ? 0 : 1)
          || ((cust && custOf(a[0]) === cust) ? 0 : 1) - ((cust && custOf(b[0]) === cust) ? 0 : 1)
          || (a[1].firstMs || Infinity) - (b[1].firstMs || Infinity));
      let left = q;
      for (const [, l] of order) {
        if (left <= 0) break;
        const room = markPending ? l.qty - l.pending : l.qty;
        const take = Math.min(room, left);
        if (markPending) l.pending += take; else { l.qty -= take; l.pending = Math.max(0, l.pending - take); }   // a real return retires the pending units first
        left -= take;
      }
      return left;
    };
    for (const t of rows) {
      const dir = String(t.direction || t.type || '').toLowerCase();
      const q = num(t.quantity);
      const at = sinceMsOf(t);
      if (dir === 'deploy') {
        const l = lots.get(t.jobId) || { qty: 0, pending: 0, firstMs: 0 };
        l.qty += q;
        if (at && (!l.firstMs || at < l.firstMs)) l.firstMs = at;
        lots.set(t.jobId, l);
      } else if (dir === 'return') {
        // COD (spec, 2026-09-29 — Edge Case, Code, Independent, convergent): a
        // return that needs an inspection writes a `return` row at SCHEDULING
        // time while `deployedQty` does not move (processRentalTransaction),
        // and the inspection's completion writes a second `return` (pretrip.js
        // / maintenance.js). Replaying both would drop a lot still on site. The
        // scheduled row's only tell is its note; it marks units PENDING (still
        // out, awaiting inspection) and the completion row is the real return.
        // Tripwired in the unit test.
        if (!jobsById.has(t.jobId) && !jobMissing(t.jobId)) returnJobsWanted.add(t.jobId);
        if (MAP_EQUIPMENT_PENDING_NOTE_RE.test(String(t.note || ''))) release(t.jobId, q, true);
        else unmatchedReturns += release(t.jobId, q, false);
      } else if (dir === 'pickup-planned') {
        // TASK-10015 — a booked later-day pickup moved nothing; its `return`
        // row is written when it happens. Known, not unclassified.
      } else if (dir === 'reversal') {
        // TASK-10015 (TASK-4810 #2) — a reversal undoes THIS job's own move:
        // + puts units back on its lot, − takes them off it (own job first).
        const dq = Number(t.delta) || 0;
        if (dq > 0) {
          const l = lots.get(t.jobId) || { qty: 0, pending: 0, firstMs: 0 };
          l.qty += dq;
          if (at && (!l.firstMs || at < l.firstMs)) l.firstMs = at;
          lots.set(t.jobId, l);
        } else if (dq < 0) {
          unmatchedReturns += release(t.jobId, -dq, false);
        }
      } else {
        unclassifiedRows++;
      }
    }
    for (const [jobId, l] of lots) {
      const k = `${assetId}|${jobId}`;
      if (seenLots.has(k) || l.qty <= 0) continue;
      push({ kind: 'rental', assetId, label: asset.name || asset.partNumber || assetId, qty: l.qty, pending: Math.min(l.pending, l.qty),
        sinceMs: l.firstMs, sinceISO: l.firstMs ? dayISO(l.firstMs) : null, sinceKnown: !!l.firstMs,
        jobId, jobDayId: '', status: '', basis: 'transactions' });
    }
  }
  const sinceOf = (k, useLast) => {
    const ms = useLast ? lastDeploy.get(k) : 0;
    return ms ? { sinceMs: ms, sinceISO: dayISO(ms), sinceKnown: true } : { sinceMs: 0, sinceISO: null, sinceKnown: false };
  };
  // ③ individually tracked gear.
  for (const asset of assetById.values()) {
    if (!isRental(asset) || asset.trackingMode === 'quantity') continue;
    if (String(asset.deploymentStatus || '').toLowerCase() !== 'deployed' || !asset.deployedToJobId) continue;
    const k = `${asset.id}|${asset.deployedToJobId}`;
    push({ kind: 'rental', assetId: asset.id, label: asset.name || asset.partNumber || asset.id, qty: 1, pending: 0,
      ...sinceOf(k, true), jobId: asset.deployedToJobId, jobDayId: '', status: '', basis: 'individual' });
  }

  // Sites: one per located job. Age = the OLDEST lot there.
  const ageDays = (ms) => (ms ? Math.max(0, Math.floor((nowMs - ms) / 86400000)) : null);
  const bucket = (d) => (d == null ? 'unknown' : d < MAP_EQUIPMENT_FRESH_DAYS ? 'fresh' : d <= MAP_EQUIPMENT_STALE_DAYS ? 'aging' : 'stale');
  const siteByJob = new Map();
  const unplotted = [];
  deployments.forEach(d => {
    d.ageDays = d.sinceKnown ? ageDays(d.sinceMs) : null;
    d.ageBucket = bucket(d.ageDays);
    if (d.reason) { unplotted.push(d); return; }
    let s = siteByJob.get(d.jobId);
    if (!s) {
      s = { jobId: d.jobId, title: d.title, jobNumber: d.jobNumber, customer: d.customer, customerId: d.customerId, projectId: d.projectId,
        lat: d.lat, lng: d.lng, locName: d.locName, locSource: d.locSource, items: [], units: 0, pending: 0, oldestMs: null, unknownAge: 0 };
      siteByJob.set(d.jobId, s);
    }
    s.items.push(d);
    s.units += d.qty;
    s.pending += d.pending;
    if (d.sinceKnown) { if (s.oldestMs == null || d.sinceMs < s.oldestMs) s.oldestMs = d.sinceMs; }
    else s.unknownAge++;
  });
  const sites = [...siteByJob.values()].map(s => {
    const a = s.oldestMs != null ? ageDays(s.oldestMs) : null;
    return { ...s, ageDays: a, ageBucket: bucket(a) };
  }).sort((x, y) => (y.ageDays ?? -1) - (x.ageDays ?? -1));

  // Reconciliation per quantity-mode rental: the scalar vs what the rows place.
  const recon = [];
  for (const asset of assetById.values()) {
    if (!isRental(asset) || asset.trackingMode !== 'quantity') continue;
    const mine = deployments.filter(d => d.kind === 'rental' && d.assetId === asset.id);
    const placed = mine.filter(d => !d.reason).reduce((n, d) => n + d.qty, 0);
    const unplaced = mine.filter(d => !!d.reason).reduce((n, d) => n + d.qty, 0);
    const deployedQty = Math.max(0, Number(asset.deployedQty) || 0);
    const quantity = Number(asset.quantity) || 0;
    if (deployedQty <= 0 && placed + unplaced <= 0) continue;
    recon.push({ assetId: asset.id, label: asset.name || asset.partNumber || asset.id, deployedQty, quantity,
      placed, unplaced, delta: deployedQty - placed - unplaced, overStock: quantity - deployedQty < 0 });
  }

  return {
    sites, unplotted, deployments,
    recon, unclassifiedRows, unmatchedReturns,
    jobsNeeded: [...[...jobsNeeded].sort(), ...[...returnJobsWanted].filter(id => !jobsNeeded.has(id)).sort()],
    txnState: Array.isArray(transactions) ? 'loaded' : 'not-loaded',
    unitTotal: deployments.reduce((n, d) => n + d.qty, 0),
    plottedUnits: sites.reduce((n, s) => n + s.units, 0),
  };
}

// Firestore Timestamp | Date | ISO string | {seconds} -> epoch ms (0 = unknown).
// `new Date(timestamp)` is NaN for a Firestore Timestamp, which is how the fleet
// staleness check used to silently never fire.
function _mapTsMs(t) {
  if (!t) return 0;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (t instanceof Date) return t.getTime();
  const n = typeof t === 'number' ? t : Date.parse(t);
  return Number.isFinite(n) ? n : 0;
}

function _mapTsDayISO(t) {
  const ms = _mapTsMs(t);
  return ms ? formatDateISOLocal(new Date(ms)) : null;
}

function _mapAgoLabel(ms) {
  const age = Date.now() - ms;
  if (age < 60000) return 'just now';
  if (age < 3600000) return `${Math.round(age / 60000)} min ago`;
  if (age < 86400000 * 2) return `${Math.round(age / 3600000)} h ago`;
  return `${Math.round(age / 86400000)} days ago`;
}

function _mapFleetStatusText(fs) {
  switch (fs.state) {
    case 'not-connected': return 'Samsara isn’t connected. Connect it in Settings to see live truck positions here.';
    case 'off': return 'Hidden.';
    case 'waiting': return 'Checking for positions…';
    case 'unreadable': return 'Positions can’t be read for this login.';
    case 'no-positions': return 'Samsara is connected, but no truck positions have arrived yet.';
    case 'stale': return `${fs.plotted.length} truck${fs.plotted.length !== 1 ? 's' : ''}, none current — last position ${_mapAgoLabel(fs.newestMs)}.`;
    case 'live': return `${fs.freshCount} live${fs.plotted.length > fs.freshCount ? ` · ${fs.plotted.length - fs.freshCount} stale` : ''}.`;
    default: return '';
  }
}

// Units the "Last dispatched" layer walks: the roster's trucks and trailers,
// minus anything retired.
function _mapFleetUnits() {
  const alive = (u) => u && u.active !== false && String(u.status || '').toLowerCase() !== 'inactive';
  const label = (u) => String(u.unit || u.nickname || u.name || u.id);
  return [
    ...(DispatchState.trucks || []).filter(alive).map(t => ({ id: t.id, label: label(t), nickname: t.nickname || '', kind: 'truck', _raw: t })),
    ...(DispatchState.trailers || []).filter(alive).map(t => ({ id: t.id, label: label(t), nickname: t.nickname || '', kind: 'trailer', _raw: t })),
  ];
}

function _mapUnitMatches(ref, unit) {
  if (ref === null || ref === undefined || ref === '') return false;
  if (String(ref) === String(unit.id)) return true;
  const list = unit.kind === 'trailer' ? DispatchState.trailers : DispatchState.trucks;
  const hit = _resolveFleetRef(ref, list);
  return !!hit && String(hit.id) === String(unit.id);
}

// The job's LAST plottable stop — where a haul ends up.
function _mapLastStopOf(job) {
  if (!job) return null;
  const wps = job.waypoints || [];
  const { plottable } = classifyJobStops(wps);
  const s = plottable[plottable.length - 1];
  return s ? { lat: s.lat, lng: s.lng, name: s.name || s.address || '', stopNumber: s.stopNumber, totalStops: wps.length } : null;
}

// Clock punches for the days the tie-break needs, one bounded query per day,
// cached. Past days never re-read; today re-reads after a short TTL. A denied or
// failed read is remembered too, so a repaint can never loop on it — the
// status rule stands in and the pin is still honest about why it was chosen.
async function _mapFetchClockDays(dates) {
  const tid = DispatchState.tenantId;
  if (!tid || !dates.length) return false;
  DispatchState._ldClock = DispatchState._ldClock || {};
  DispatchState._ldClockInflight = DispatchState._ldClockInflight || new Set();
  let fetched = false;
  await Promise.all(dates.map(async (iso) => {
    if (DispatchState._ldClockInflight.has(iso)) return;
    DispatchState._ldClockInflight.add(iso);
    try {
      const start = new Date(iso + 'T00:00:00');
      const end = new Date(start); end.setDate(end.getDate() + 1);
      const snap = await getDocs(query(
        collection(db, 'tenants', tid, 'clockEntries'),
        where('clockIn', '>=', Timestamp.fromDate(start)),
        where('clockIn', '<', Timestamp.fromDate(end))
      ));
      if (DispatchState.tenantId !== tid) return;  // tenant switched mid-read — discard
      DispatchState._ldClock[iso] = { at: Date.now(), entries: snap.docs.map(d => ({ id: d.id, ...d.data() })) };
      fetched = true;
    } catch (e) {
      console.warn('[dispatch map] last-dispatched clock read failed for', iso, e?.code || e?.message || e);
      if (DispatchState.tenantId !== tid) return;
      // COD r2: keep whatever punches we already had — a failed refresh must
      // not erase the punch that decided a pin.
      const prev = DispatchState._ldClock[iso];
      DispatchState._ldClock[iso] = { at: Date.now(), entries: (prev && prev.entries) || [], failed: true };
    } finally {
      DispatchState._ldClockInflight.delete(iso);
    }
  }));
  return fetched;
}

function _mapClockUnknownForDate(iso) {
  const c = DispatchState._ldClock && DispatchState._ldClock[iso];
  if (!c) return true;
  const stale = (Date.now() - c.at) > MAP_LD_CLOCK_TODAY_TTL_MS;
  // Today is still being punched; a FAILED day (offline, denied) is retried
  // too rather than being remembered as "no punches" forever (COD r1). Both
  // at most once per TTL, so neither can loop a repaint.
  if (stale && (iso === mapDayISO(0) || c.failed)) return true;
  return false;
}

function _mapLoadedClockEntries() {
  const out = [...(DispatchState._clockEntries || [])];
  const seen = new Set(out.map(e => e.id));
  Object.values(DispatchState._ldClock || {}).forEach(c => (c.entries || []).forEach(e => {
    if (!seen.has(e.id)) { seen.add(e.id); out.push(e); }
  }));
  return out;
}

// ── Project filter ──────────────────────────────────────────────────────────
function _mapAllProjects() {
  const all = window.ProjectsModule?.getState?.()?.projects || [];
  const rank = (p) => (String(p.status || 'active').toLowerCase() === 'active' ? 0 : 1);
  return all.filter(p => p && p.id && String(p.status || '').toLowerCase() !== 'deleted')
    .slice()
    .sort((a, b) => rank(a) - rank(b) || String(a.name || '').localeCompare(String(b.name || '')));
}

function _mapProjectResultsHtml(q) {
  const escA = window.escAttr || escapeHtml;
  const needle = String(q || '').trim().toLowerCase();
  const hits = _mapAllProjects().filter(p => !needle
    || String(p.name || '').toLowerCase().includes(needle)
    || String(p.primaryCustomerName || '').toLowerCase().includes(needle)).slice(0, 8);
  if (!hits.length) return `<div class="map-key-note">No project matches “${escapeHtml(q)}”.</div>`;
  return hits.map(p => `<button type="button" class="map-key-project-hit" role="option" data-map-project="${escA(p.id)}">
      <span class="map-key-project-name">${escapeHtml(p.name || 'Untitled project')}</span>
      ${p.primaryCustomerName || String(p.status || 'active').toLowerCase() !== 'active' ? `<span class="map-key-project-sub">${escapeHtml(p.primaryCustomerName || '')}${String(p.status || 'active').toLowerCase() !== 'active' ? `${p.primaryCustomerName ? ' · ' : ''}${escapeHtml(p.status)}` : ''}</span>` : ''}
    </button>`).join('');
}

function filterMapProjects(q) {
  const el = document.getElementById('map-project-results');
  if (el) { el.innerHTML = _mapProjectResultsHtml(q); el.hidden = false; }
}

async function setMapProject(projectId) {
  DispatchState.mapProjectId = projectId || null;
  DispatchState._mapProjectJobs = DispatchState._mapProjectJobs || {};
  const needsFetch = !!projectId && !DispatchState._mapProjectJobs[projectId];
  const _tid = DispatchState.tenantId;
  // Flag BEFORE the first paint so the key says "loading" rather than
  // briefly counting only the board-window jobs as if that were all of them.
  if (needsFetch) {
    DispatchState._mapProjectLoading = projectId;
    if (DispatchState._mapProjectLoadFailed === projectId) DispatchState._mapProjectLoadFailed = null;
  }
  repaintMapView();
  if (!needsFetch) return;
  // The board only holds a window of jobs; a project runs for weeks. ONE query
  // on selection — never a widened board listener.
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', DispatchState.tenantId, 'jobs'),
      where('projectId', '==', projectId)
    ));
    if (DispatchState.tenantId !== _tid) return;  // tenant switched mid-read — discard
    DispatchState._mapProjectJobs[projectId] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.warn('[dispatch map] project jobs query failed — showing the jobs already loaded', e?.code || e?.message || e);
    // NOT cached: picking the project again retries the query (COD r1).
    delete DispatchState._mapProjectJobs[projectId];
    DispatchState._mapProjectLoadFailed = projectId;
  } finally {
    if (DispatchState._mapProjectLoading === projectId) DispatchState._mapProjectLoading = null;
  }
  if (DispatchState.mapProjectId === projectId) repaintMapView();
}

function clearMapProject() {
  DispatchState.mapProjectId = null;
  repaintMapView();
}

// ── Modals behind the key's "not plotted" / "ambiguous" chips ───────────────
function _mapListModal(title, subtitle, rowsHtml) {
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) { modal = document.createElement('div'); modal.id = 'dispatch-modal-overlay'; modal.className = 'modal'; document.body.appendChild(modal); }
  modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:88dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:var(--bg-header);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;">
          <h3 id="map-list-modal-title" style="margin:0;font-size:1.05rem;font-weight:700;color:#fff;">${escapeHtml(title)}</h3>
          <button type="button" class="btn-icon" data-map-modal-close style="color:#fff;min-width:44px;min-height:44px;" aria-label="Close"><span class="material-icons">close</span></button>
        </div>
        <div style="font-size:0.78rem;opacity:0.95;margin-top:3px;color:#fff;">${escapeHtml(subtitle)}</div>
      </div>
      <div style="overflow-y:auto;flex:1;padding:12px 16px;background:var(--bg-page);" id="map-list-modal-body">${rowsHtml}</div>
    </div>`;
  modal.querySelector('[data-map-modal-close]')?.addEventListener('click', () => modal.classList.add('hidden'));
  document.getElementById('map-list-modal-body')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-map-job]');
    if (!btn) return;
    modal.classList.add('hidden');
    window.DispatchModule.showJobDetails(btn.dataset.mapJob, btn.dataset.mapJobday || null);
  });
}

function showUnplottedSavedLocations() {
  const list = DispatchState._mapSavedUnplotted || [];
  if (!list.length) return;
  const rows = list.map(l => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);">
      <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${escapeHtml(l.name || 'Unnamed location')}</div>
      <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;word-break:break-all;">${l.url ? escapeHtml(l.url) : 'No address or link saved'}</div>
    </div>`).join('');
  _mapListModal(
    `${list.length} saved location${list.length !== 1 ? 's' : ''} not plotted`,
    'These have no coordinate yet — usually a short share link that was never resolved, or a plain address. Open one from the Saved Locations picker on a job and re-save it with a full map link to pin it.',
    rows
  );
}

function showLastDispatchedIssues() {
  const escA = window.escAttr || escapeHtml;
  const amb = DispatchState._mapLdAmbiguous || [];
  const unp = DispatchState._mapLdUnplotted || [];
  if (!amb.length && !unp.length) return;
  const day = (iso) => escapeHtml(mapRangeLabel(iso, iso));
  const kindLabel = (k) => (k === 'trailer' ? 'Trailer' : 'Truck');
  const openBtn = (jobId, jobDayId, label) => `<button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;flex-shrink:0;" data-map-job="${escA(jobId)}" data-map-jobday="${escA(jobDayId || '')}">${label}</button>`;
  const ambRows = amb.map(a => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);">
      <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${kindLabel(a.kind)} ${escapeHtml(a.unitLabel)} — ${a.candidates.length} jobs on ${day(a.dateISO)}</div>
      <div style="font-size:0.78rem;color:var(--text-secondary);margin:3px 0 6px;">No clock punch and no job status separates them, so the map does not guess which one it went to last.</div>
      ${a.candidates.map(c => `<div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:4px;">
        <span style="font-size:0.82rem;color:var(--text-primary);min-width:0;">${escapeHtml(c.title)} <span style="color:var(--text-secondary);">(${escapeHtml(c.status)})</span></span>
        ${openBtn(c.jobId, c.jobDayId, 'Open job')}
      </div>`).join('')}
    </div>`).join('');
  const unpRows = unp.map(u => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);display:flex;justify-content:space-between;align-items:center;gap:10px;">
      <div style="min-width:0;">
        <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${kindLabel(u.kind)} ${escapeHtml(u.unitLabel)} — ${day(u.dateISO)}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;">Last dispatched on ${escapeHtml(u.title)}, which has no destination with a location, so there is nothing to pin.</div>
      </div>
      ${openBtn(u.jobId, u.jobDayId, 'Open job')}
    </div>`).join('');
  _mapListModal(
    'Last dispatched — not on the map',
    'Units whose last dispatch can’t be placed on the map, and why.',
    (amb.length ? `<div style="font-size:0.8rem;font-weight:700;color:var(--text-primary);margin:4px 0 8px;">More than one job that day (${amb.length})</div>${ambRows}` : '')
    + (unp.length ? `<div style="font-size:0.8rem;font-weight:700;color:var(--text-primary);margin:12px 0 8px;">Job has no located destination (${unp.length})</div>${unpRows}` : '')
  );
}

// ── TASK-4800 — Equipment layer: data, location, list panel ─────────────────
// Where a job IS for the equipment layer: its last plottable stop (where a haul
// ends up — the same rule as Last dispatched), else the project's pin (TASK-4842).
// TASK-4800 S3 (COD Blast lane) — ONE location rule for both surfaces: the
// Maps layer passes the Projects module's list, Roster's On site passes the
// project docs it read. A deleted project's pin is never used (the same filter
// _mapAllProjects applies), so the two can never place a lot differently.
function _mapEquipmentLocateWith(job, projectOf) {
  const stop = _mapLastStopOf(job);
  if (stop) return { lat: stop.lat, lng: stop.lng, name: stop.name, source: 'stop' };
  const p = (job && job.projectId && typeof projectOf === 'function') ? projectOf(job.projectId) : null;
  if (!p || String(p.status || '').toLowerCase() === 'deleted') return null;
  const c = plottableCoord({ lat: p.locationLat, lng: p.locationLng });
  return c ? { lat: c.lat, lng: c.lng, name: p.name || 'Project location', source: 'project' } : null;
}

function _mapEquipmentLocateJob(job) {
  return _mapEquipmentLocateWith(job, (pid) => _mapAllProjects().find(x => x.id === pid));
}

// Jobs the layer can see: the board window, the project layer's fetched jobs,
// and the ones fetched for open lots outside the window.
function _mapEquipmentJobsById() {
  const m = new Map();
  Object.values(DispatchState._mapEqJobs || {}).forEach(j => { if (j && j.id && !j.__failedAt) m.set(j.id, j); });
  Object.values(DispatchState._mapProjectJobs || {}).forEach(list => (list || []).forEach(j => { if (j && j.id) m.set(j.id, j); }));
  (DispatchState.jobs || []).forEach(j => { if (j && j.id) m.set(j.id, j); });   // live board copy wins
  return m;
}

function _mapEquipmentPlan() {
  return planEquipmentDeployments({
    units: _mapFleetUnits(),
    assets: DispatchState.assets || [],
    jobDays: DispatchState.jobDays || [],
    jobsById: _mapEquipmentJobsById(),
    transactions: Array.isArray(DispatchState._mapEqTxns) ? DispatchState._mapEqTxns : null,
  }, {
    todayISO: mapDayISO(0),
    dayISO: _mapTsDayISO,
    toMs: _mapTsMs,
    nowMs: Date.now(),
    unitMatches: _mapUnitMatches,
    locateJob: _mapEquipmentLocateJob,
    jobMissing: (id) => Object.prototype.hasOwnProperty.call(DispatchState._mapEqJobs || {}, id) && DispatchState._mapEqJobs[id] === null,
  });
}

// ONE read of the tenant's assetTransactions the first time the row is turned
// on (206 rows on the largest live tenant). A failed read is remembered as
// failed and SAID in the key — never cached as an empty ledger.
async function _mapFetchEquipmentTxns() {
  const tid = DispatchState.tenantId;
  if (!tid || DispatchState._mapEqTxnsState === 'loading') return false;
  DispatchState._mapEqTxnsState = 'loading';
  try {
    const snap = await getDocs(collection(db, 'tenants', tid, 'assetTransactions'));
    if (DispatchState.tenantId !== tid) return false;
    DispatchState._mapEqTxns = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    DispatchState._mapEqTxnsState = 'loaded';
    DispatchState._mapEqTxnsAt = Date.now();
    return true;
  } catch (e) {
    if (DispatchState.tenantId !== tid) return false;
    console.warn('[dispatch map] assetTransactions read failed — rental lots not shown', e?.code || e?.message || e);
    DispatchState._mapEqTxns = null;
    DispatchState._mapEqTxnsState = 'failed';
    return true;   // repaint so the key says so
  }
}

// Open-lot jobs outside the board window: one getDoc each, cached per tenant.
// A job that no longer exists is cached as null so its lots read "deleted
// job". 🔴 COD on the diff (Edge, Code, Independent — convergent): a read that
// THROWS used to be left out of the cache while this still returned true, so
// the repaint re-fetched it forever, rebuilding the map (and snapping it to
// the default centre) on every pass. A failure is now cached as
// { __failedAt } and retried only after MAP_EQUIPMENT_TXN_TTL_MS; the repaint
// fires only when something actually landed. MAP_EQUIPMENT_JOB_FETCH_CAP is a
// CEILING per tenant, not a batch size: past it, the rest stay "not loaded".
async function _mapFetchEquipmentJobs(ids) {
  const tid = DispatchState.tenantId;
  if (!tid || !ids.length || DispatchState._mapEqJobsInflight) return false;
  const cache = DispatchState._mapEqJobs;
  const now = Date.now();
  const fetchedSoFar = Object.keys(cache).length;
  const room = Math.max(0, MAP_EQUIPMENT_JOB_FETCH_CAP - fetchedSoFar);
  const has = (id) => Object.prototype.hasOwnProperty.call(cache, id);
  const retry = ids.filter(id => has(id) && cache[id] && cache[id].__failedAt && (now - cache[id].__failedAt) > MAP_EQUIPMENT_TXN_TTL_MS);   // past its backoff
  const fresh = ids.filter(id => !has(id)).slice(0, room);
  const want = [...retry, ...fresh].slice(0, MAP_EQUIPMENT_JOB_FETCH_BATCH);
  if (!want.length) return false;
  DispatchState._mapEqJobsInflight = want;
  let landed = 0;
  try {
    await Promise.all(want.map(async id => {
      try {
        const snap = await getDoc(doc(db, 'tenants', tid, 'jobs', id));
        if (DispatchState.tenantId !== tid) return;
        DispatchState._mapEqJobs[id] = snap.exists() ? { id: snap.id, ...snap.data() } : null;
        landed++;
      } catch (e) {
        if (DispatchState.tenantId !== tid) return;
        console.warn('[dispatch map] equipment job read failed — retried after a pause', id, e?.code || e?.message || e);
        DispatchState._mapEqJobs[id] = { id, __failedAt: Date.now() };
      }
    }));
  } finally {
    if (DispatchState.tenantId === tid) DispatchState._mapEqJobsInflight = null;
  }
  return DispatchState.tenantId === tid && landed > 0;
}

// TASK-9508 — PURE. Paint order for the site markers. Every marker stays at
// zIndex 9 (🔴 COD: an age-derived zIndex would climb past the job stops' 10
// and eat their clicks, and a null age would drop under the fleet's 5), so the
// stacking comes from ADD order instead: the marker added last paints on top.
// plan.sites is oldest-first, which buried a stale red site under a newer
// green one at the same spot. Unknown age first, then newest → oldest LAST.
function _mapEquipmentPaintOrder(sites) {
  const list = Array.isArray(sites) ? sites.slice() : [];
  return list
    .map((s, i) => ({ s, i, a: (s && Number.isFinite(s.ageDays)) ? s.ageDays : -1 }))
    .sort((x, y) => x.a - y.a || x.i - y.i)
    .map(x => x.s);
}

// TASK-9508 — PURE. The key's three numbers used to disagree because each
// counted something different with no label: the row showed units ON THE MAP,
// the note showed units IN TOTAL (incl. unplotted), and the chip counted
// DEPLOYMENT ROWS. Each is now named for what it is. A single 3,000-mat
// unplotted row stays visible as rows AND units.
function _mapEquipmentKeySummary(plan) {
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const plural = (n, one, many) => `${fmt(n)} ${n === 1 ? one : many}`;
  const unplotted = (plan && Array.isArray(plan.unplotted)) ? plan.unplotted : [];
  const sites = (plan && Array.isArray(plan.sites)) ? plan.sites.length : 0;
  const onMap = Number(plan && plan.plottedUnits) || 0;
  const total = Number(plan && plan.unitTotal) || 0;
  const unplottedRows = unplotted.length;
  const unplottedUnits = unplotted.reduce((n, d) => n + (Number(d && d.qty) || 0), 0);
  const notLoaded = unplotted.filter(d => d && d.reason === 'job-not-loaded').length;
  return {
    onMap, total, sites, unplottedRows, unplottedUnits, notLoaded,
    rowCountText: fmt(onMap),
    noteText: `${plural(onMap, 'unit', 'units')} on the map at ${plural(sites, 'site', 'sites')} · ${plural(total, 'unit', 'units')} out in total`,
    chipText: unplottedRows
      ? `${plural(unplottedRows, 'deployment', 'deployments')} without a location (${plural(unplottedUnits, 'unit', 'units')})${notLoaded ? ` · ${fmt(notLoaded)} not loaded` : ''}`
      : '',
  };
}

// TASK-9508 — PURE. The list's filter, shared by the body and the header so
// the header can never keep the unfiltered total after a filter change.
function _mapEquipmentFilterRows(deployments, f) {
  const flt = f || { kind: 'all', customer: '', project: '' };
  const kindOk = (d) => flt.kind === 'all' || !flt.kind || (flt.kind === 'rental' ? d.kind === 'rental' : d.kind === flt.kind);
  return (Array.isArray(deployments) ? deployments : [])
    .filter(d => kindOk(d) && (!flt.customer || (d.customerId || d.customer) === flt.customer) && (!flt.project || d.projectId === flt.project));
}

// TASK-9508 — PURE. "Deployed equipment — 11,307 units", or with a filter on,
// "Deployed equipment — 120 of 11,307 units".
function _mapEquipmentListTitle(plan, f) {
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const all = (plan && Array.isArray(plan.deployments)) ? plan.deployments : [];
  const total = all.reduce((n, d) => n + (Number(d.qty) || 0), 0);
  const flt = f || {};
  const filtered = (flt.kind && flt.kind !== 'all') || flt.customer || flt.project;
  const shown = filtered ? _mapEquipmentFilterRows(all, flt).reduce((n, d) => n + (Number(d.qty) || 0), 0) : total;
  return filtered
    ? `Deployed equipment — ${fmt(shown)} of ${fmt(total)} unit${total !== 1 ? 's' : ''}`
    : `Deployed equipment — ${fmt(total)} unit${total !== 1 ? 's' : ''}`;
}

function _mapEquipmentKindLabel(k) {
  return k === 'trailer' ? 'Trailer' : k === 'truck' ? 'Truck' : 'Rental';
}
// "2 vehicles · 200 rental units" — the badge sums them (Skeptic: say which is which).
function _mapEquipmentUnitsText(items) {
  const veh = items.filter(d => d.kind !== 'rental').length;
  const rent = items.filter(d => d.kind === 'rental').reduce((n, d) => n + d.qty, 0);
  const parts = [];
  if (veh) parts.push(`${veh} vehicle${veh !== 1 ? 's' : ''}`);
  if (rent) parts.push(`${rent} rental unit${rent !== 1 ? 's' : ''}`);
  return parts.join(' · ');
}
function _mapEquipmentSinceText(d) {
  if (!d.sinceKnown) return 'since: date unknown';
  const days = d.ageDays == null ? 0 : d.ageDays;
  return `since ${mapRangeLabel(d.sinceISO, d.sinceISO)} · ${days === 0 ? 'today' : `${days} day${days !== 1 ? 's' : ''}`}`;
}
function _mapEquipmentReasonText(reason) {
  switch (reason) {
    case 'deleted-job': return 'The job it was deployed to no longer exists — the units are still counted as out.';
    case 'job-not-loaded': return 'Its job is older than the loaded history and has not been fetched yet.';
    default: return 'Its job has no destination with a location and no project location, so there is nothing to pin.';
  }
}

// The list panel: every deployment, oldest first (the stranded view), with
// type / customer / project filters and the per-asset reconciliation. Rebuilt
// from state on every filter change; the modal shell comes from _mapListModal.
function setEquipmentListFilter(key, value) {
  const f = DispatchState._mapEqFilters || (DispatchState._mapEqFilters = { kind: 'all', customer: '', project: '' });
  if (!['kind', 'customer', 'project'].includes(key)) return;
  f[key] = String(value || '');
  const plan = _mapEquipmentPlan();
  const body = document.getElementById('map-list-modal-body');
  if (body) body.innerHTML = _mapEquipmentListBodyHtml(plan);
  // TASK-9508 — the header used to keep the unfiltered total.
  const title = document.getElementById('map-list-modal-title');
  if (title) title.textContent = _mapEquipmentListTitle(plan, f);
}

function _mapEquipmentListBodyHtml(plan) {
  const escA = window.escAttr || escapeHtml;
  const f = DispatchState._mapEqFilters || { kind: 'all', customer: '', project: '' };
  const all = plan.deployments;
  const customers = [...new Map(all.filter(d => d.customer).map(d => [d.customerId || d.customer, d.customer])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]));
  const projects = _mapAllProjects().filter(p => all.some(d => d.projectId === p.id));
  const rows = _mapEquipmentFilterRows(all, f)   // TASK-9508 — same filter as the header
    .sort((a, b) => {
      // Oldest first; unknown age after the dated ones; unplotted last within a tie.
      const ax = a.sinceKnown ? a.sinceMs : Infinity, bx = b.sinceKnown ? b.sinceMs : Infinity;
      return ax - bx || (a.reason ? 1 : 0) - (b.reason ? 1 : 0) || a.label.localeCompare(b.label);
    });
  const sel = (id, key, opts, current) => `<select id="${id}" class="map-key-project-input" style="min-height:40px;flex:1;min-width:120px;" onchange="window.DispatchModule.setEquipmentListFilter('${key}', this.value)" aria-label="${key}">${opts.map(([v, l]) => `<option value="${escA(v)}"${String(v) === String(current) ? ' selected' : ''}>${escapeHtml(l)}</option>`).join('')}</select>`;
  const filters = `
    <div style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px;">
      ${sel('map-eq-kind', 'kind', [['all', 'All equipment'], ['truck', 'Trucks'], ['trailer', 'Trailers'], ['rental', 'Rentals']], f.kind)}
      ${customers.length ? sel('map-eq-customer', 'customer', [['', 'All customers'], ...customers], f.customer) : ''}
      ${projects.length ? sel('map-eq-project', 'project', [['', 'All projects'], ...projects.map(p => [p.id, p.name || 'Untitled project'])], f.project) : ''}
    </div>`;
  const ageStyle = (d) => `color:${MAP_EQUIPMENT_AGE_COLORS[d.ageBucket] || MAP_EQUIPMENT_AGE_COLORS.unknown};font-weight:700;`;
  const rowHtml = (d) => `
    <div data-map-eq-row style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);display:flex;justify-content:space-between;align-items:center;gap:10px;">
      <div style="min-width:0;">
        <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${_mapEquipmentKindLabel(d.kind)} ${escapeHtml(d.label)}${d.kind === 'rental' ? ` × ${d.qty}` : ''}${d.pending ? ` <span style="font-weight:600;color:#92400E;">(${d.pending} awaiting return inspection)</span>` : ''}${d.otherJobsToday ? ` <span style="font-weight:500;color:var(--text-secondary);">(also on ${d.otherJobsToday} other job${d.otherJobsToday !== 1 ? 's' : ''} today)</span>` : ''}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;">${d.jobNumber != null ? `#${escapeHtml(String(d.jobNumber))} ` : ''}${escapeHtml(d.title)}${d.customer ? ` · ${escapeHtml(d.customer)}` : ''}</div>
        <div style="font-size:0.78rem;margin-top:3px;"><span style="${ageStyle(d)}">${escapeHtml(_mapEquipmentSinceText(d))}</span>${d.reason ? `<span style="color:var(--action-warning);font-weight:600;"> · not on the map</span>` : d.locName ? ` <span style="color:var(--text-secondary);">· at ${escapeHtml(d.locName)}</span>` : ''}</div>
        ${d.reason ? `<div style="font-size:0.76rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(_mapEquipmentReasonText(d.reason))}</div>` : ''}
      </div>
      ${d.reason === 'deleted-job' || d.reason === 'job-not-loaded' ? '' : `<button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;flex-shrink:0;" data-map-job="${escA(d.jobId)}" data-map-jobday="${escA(d.jobDayId || '')}">Open job</button>`}
    </div>`;
  const reconHtml = plan.recon.length ? `
    <div style="font-size:0.8rem;font-weight:700;color:var(--text-primary);margin:14px 0 6px;">Rental counts — does the counter match the sites?</div>
    <div class="map-key-note" style="margin-bottom:8px;">“Out” is the stock counter DispatchDeck bills and blocks on. “Placed” is what the records put at a known site; “unplaced” is what they put on a job with no location or no job. “Off by” is the gap between them — usually a return or a cancelled deploy that never wrote a ledger row. Check it before a spot count.</div>
    ${plan.recon.map(r => `<div style="display:flex;flex-wrap:wrap;gap:4px 12px;padding:6px 10px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:6px;font-size:0.8rem;color:var(--text-primary);background:var(--bg-card);">
      <span style="font-weight:600;flex-basis:100%;">${escapeHtml(r.label)}${r.overStock ? ` <span style="color:#B91C1C;font-weight:700;">&#9888; over stock — ${r.deployedQty} out of ${r.quantity} owned</span>` : ''}</span>
      <span>Out: <strong>${r.deployedQty}</strong></span><span>Placed: <strong>${r.placed}</strong></span><span>Unplaced: <strong>${r.unplaced}</strong></span>
      <span style="${r.delta !== 0 ? 'color:#B91C1C;font-weight:700;' : ''}">Off by: ${r.delta > 0 ? '+' : ''}${r.delta}</span>
    </div>`).join('')}` : '';
  const state = plan.txnState !== 'loaded'
    ? `<div class="map-key-note" style="margin-bottom:8px;">${DispatchState._mapEqTxnsState === 'failed' ? 'The rental ledger couldn’t be read for this login, so rental lots are missing here.' : 'Loading the rental ledger…'}</div>`
    : (plan.deployments.some(d => d.basis === 'transactions')
      ? `<div class="map-key-note" style="margin-bottom:8px;">Rental lots on jobs from before the per-job ledger are worked out from the deploy and return records: a return is matched to that item’s open lots — the return’s own job first, then the same customer, then the oldest lot. That matching is an assumption; the site totals are not.${plan.unmatchedReturns ? ` ${plan.unmatchedReturns} returned unit${plan.unmatchedReturns !== 1 ? 's' : ''} had no recorded deploy to match.` : ''}</div>`
      : '');
  return `${filters}${state}${rows.length ? rows.map(rowHtml).join('') : '<div class="map-key-note">Nothing matches these filters.</div>'}${plan.unclassifiedRows ? `<div class="map-key-note" style="margin-top:6px;">${plan.unclassifiedRows} ledger row${plan.unclassifiedRows !== 1 ? 's' : ''} on rental equipment ${plan.unclassifiedRows !== 1 ? 'are' : 'is'} neither a deploy nor a return, so ${plan.unclassifiedRows !== 1 ? 'they were' : 'it was'} counted but not placed.</div>` : ''}${reconHtml}`;
}

function showEquipmentList() {
  const plan = _mapEquipmentPlan();
  _mapListModal(
    _mapEquipmentListTitle(plan, DispatchState._mapEqFilters),   // TASK-9508 — honours a filter left on
    'Oldest on site first. From the schedule and the rental ledger, not GPS.',
    _mapEquipmentListBodyHtml(plan)
  );
}

function showEquipmentUnplotted() {
  const plan = _mapEquipmentPlan();
  if (!plan.unplotted.length) return;
  DispatchState._mapEqFilters = { kind: 'all', customer: '', project: '' };
  const escA = window.escAttr || escapeHtml;
  const rows = plan.unplotted.map(d => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);display:flex;justify-content:space-between;align-items:center;gap:10px;">
      <div style="min-width:0;">
        <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${_mapEquipmentKindLabel(d.kind)} ${escapeHtml(d.label)}${d.kind === 'rental' ? ` × ${d.qty}` : ''} — ${escapeHtml(d.title)}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;">${escapeHtml(_mapEquipmentReasonText(d.reason))} ${escapeHtml(_mapEquipmentSinceText(d))}.</div>
      </div>
      ${d.reason === 'no-location' ? `<button type="button" class="btn btn-sm btn-secondary" style="min-height:44px;flex-shrink:0;" data-map-job="${escA(d.jobId)}" data-map-jobday="${escA(d.jobDayId || '')}">Open job</button>` : ''}
    </div>`).join('');
  _mapListModal(
    `${plan.unplotted.length} deployment${plan.unplotted.length !== 1 ? 's' : ''} not on the map`,
    'Counted in every total, but nothing to pin — and why.',
    rows
  );
}

// ── TASK-4800 S4 — ESTIMATED LOCATIONS (the logic lives in the LAZY chunk
// views/mapEstimated.js; this is the glue). A best GUESS for gear the records
// do not put on a job — last record, else home yard — on its own layer row,
// default OFF, with hollow markers. Never presented as a confirmed location.
// The chunk is loaded the first time the row is on and never imports this
// file (Pitfall #1): everything it needs is handed to it here.
let _mapEstMod = null;
let _mapEstLoading = null;
function _mapLoadEstimated() {
  if (_mapEstMod) return Promise.resolve(_mapEstMod);
  if (!_mapEstLoading) {
    _mapEstLoading = __ddChunkWrap(import('./mapEstimated.js?v=10295'))
      .then((m) => { _mapEstMod = m; return m; })
      .catch((err) => { _mapEstLoading = null; DispatchState._mapEstLoadFailed = true; console.warn('[dispatch map] estimated locations failed to load:', err); return null; });
  }
  return _mapEstLoading;
}

// A yard's coordinate, only when the yard really carries one: lat/lng fields,
// or a maps link / bare "lat, lng" in its address (extractCoordsFromUrl, the
// waypoint parser). A plain street address is NOT geocoded — no guess stacked
// on a guess.
function _mapYardCoord(yard) {
  if (!yard) return null;
  const direct = plottableCoord({ lat: yard.lat != null ? yard.lat : yard.locationLat, lng: yard.lng != null ? yard.lng : yard.locationLng });
  if (direct) return direct;
  const addr = String(yard.address || '').trim();
  return addr ? plottableCoord(extractCoordsFromUrl(addr)) : null;
}

// null until the chunk has loaded.
function _mapEstimatedPlan(eqPlan) {
  if (!_mapEstMod) return null;
  // Units Equipment on site places per rental — never also "in stock at the yard".
  const outByAsset = new Map();
  ((eqPlan || _mapEquipmentPlan()).deployments || []).forEach(d => {
    if (d && d.kind === 'rental') outByAsset.set(d.assetId, (outByAsset.get(d.assetId) || 0) + (Number(d.qty) || 0));
  });
  return _mapEstMod.planEstimatedLocations({
    assets: DispatchState.assets || [],
    jobDays: DispatchState.jobDays || [],
    jobsById: _mapEquipmentJobsById(),
    transactions: Array.isArray(DispatchState._mapEqTxns) ? DispatchState._mapEqTxns : null,
    yards: window.AppState?.settings?.yards || [],
  }, {
    todayISO: mapDayISO(0),
    dayISO: _mapTsDayISO,
    toMs: _mapTsMs,
    resolveAsset: (ref) => _resolveFleetRef(ref, DispatchState.assets),
    locateJob: _mapEquipmentLocateJob,
    yardCoord: _mapYardCoord,
    outByAsset,
    jobMissing: (id) => Object.prototype.hasOwnProperty.call(DispatchState._mapEqJobs || {}, id) && DispatchState._mapEqJobs[id] === null,
  });
}

function showEstimatedList() {
  const plan = _mapEstimatedPlan();
  if (!plan) return;
  _mapListModal(
    _mapEstMod.listTitle(plan),
    'Best guesses from the last record, never confirmed. A spot check is what confirms it.',
    _mapEstMod.listBodyHtml(plan, { escapeHtml, escA: window.escAttr || escapeHtml, fmtDay: (iso) => mapRangeLabel(iso, iso), txnState: DispatchState._mapEqTxnsState })
  );
}

// ── TASK-4800 S3 — the same derivation for Roster › Assets › On site ─────────
// PURE: reads no DispatchState, so Roster can call it through
// window.DispatchModule (never a cross-import — Pitfall #1) whether or not
// Dispatch was ever opened this session. Rentals only — units:[] and
// jobDays:[] — because trucks/trailers on today's board are a Maps question,
// not an inventory one. Location is the same rule as the Maps layer: the
// job's last plottable stop, else its project's pin (projectsById, so the
// caller decides which project docs it has), else "not plotted". Every input
// the planner trusts is passed in; nothing is guessed.
function planEquipmentOnSite({ assets, transactions, jobsById, projectsById, missingJobIds } = {}) {
  // Duck-typed, not instanceof: a Map built in another realm (a test vm, an
  // iframe) is still a Map, and `instanceof` would silently treat it as empty.
  const isMapLike = (m) => !!m && typeof m.get === 'function' && typeof m.values === 'function';
  const jobs = isMapLike(jobsById) ? jobsById : new Map();
  const projects = isMapLike(projectsById) ? projectsById : new Map();
  const missing = (missingJobIds && typeof missingJobIds.has === 'function') ? missingJobIds : new Set();
  return planEquipmentDeployments({
    units: [],
    assets: Array.isArray(assets) ? assets : [],
    jobDays: [],
    jobsById: jobs,
    transactions: Array.isArray(transactions) ? transactions : null,
  }, {
    todayISO: mapDayISO(0),
    dayISO: _mapTsDayISO,
    toMs: _mapTsMs,
    nowMs: Date.now(),
    unitMatches: () => false,
    locateJob: (job) => _mapEquipmentLocateWith(job, (pid) => projects.get(pid)),
    jobMissing: (id) => missing.has(id),
  });
}

// TASK-4800 S3 — Roster's "View on map". A one-shot hand-off: the flag is
// stamped, Dispatch is opened, and initDispatch consumes it ONCE (within 30s,
// so a navigation that was refused — PIN, no access — can't ambush the next
// visit). It turns the Equipment layer on exactly as a tap on its key row
// would, then selects Maps through the real view button.
const EQUIPMENT_MAP_HANDOFF_TTL_MS = 30 * 1000;
function openEquipmentMap() {
  if (window.checkModuleAccess?.('dispatch', true) !== true) {
    window.showToast?.('The map is in Dispatch, and this login does not have Dispatch.', 'info');
    return;
  }
  DispatchState._pendingEquipmentMapAt = Date.now();
  if (typeof window.navigateToView === 'function') window.navigateToView('dispatch');
  else window.location.hash = '#/dispatch';
}

function _consumePendingEquipmentMap() {
  const at = DispatchState._pendingEquipmentMapAt || 0;
  DispatchState._pendingEquipmentMapAt = 0;
  if (!at || (Date.now() - at) > EQUIPMENT_MAP_HANDOFF_TTL_MS) return;
  const btn = document.querySelector('.view-btn[data-view="map"]');
  if (!btn) return;   // driver board / 811-only mode: there is no map here
  const layers = getMapLayers(tenantHasSamsaraFlag());
  if (!layers.equipment) {
    layers.equipment = true;
    try { localStorage.setItem(_mapLayerStorageKey(), JSON.stringify(layers)); } catch (_) { /* not persisted */ }
  }
  // Same freshness rule as switching the row on (toggleMapLayer).
  if (DispatchState._mapEqTxnsState === 'loaded') {
    DispatchState._mapEqTxnsState = 'idle';
    DispatchState._mapEqJobs = {};
  }
  btn.click();
}

// Injected once, same precedent as _ensureAddDayWeightStyles: this module owns
// the map, so the key's layout (incl. the <=768px drawer) lives beside it
// rather than in styles.css, whose ?v= nothing syncs.
function _ensureMapKeyStyles() {
  if (typeof document === 'undefined' || document.getElementById('dispatch-map-key-styles')) return;
  const el = document.createElement('style');
  el.id = 'dispatch-map-key-styles';
  el.textContent = `
.dispatch-map-body { display:flex; flex:1; min-height:0; overflow:hidden; }
.dispatch-map-body #dispatch-map-container { flex:1; min-width:0; border:none; border-radius:0; }
.dispatch-map-key { width:264px; flex-shrink:0; background:var(--bg-card); border-left:1px solid var(--border-light); overflow-y:auto; }
.dispatch-map-key > summary { list-style:none; cursor:pointer; display:flex; align-items:center; justify-content:space-between; gap:6px; min-height:44px; padding:8px 12px; font-size:0.85rem; font-weight:700; color:var(--text-primary); border-bottom:1px solid var(--border-light); }
.dispatch-map-key > summary::-webkit-details-marker { display:none; }
.dispatch-map-key > summary:focus-visible, .map-key-row:focus-visible, .map-key-chip:focus-visible, .map-key-project-hit:focus-visible { outline:2px solid var(--brand-blue); outline-offset:-2px; }
.map-key-section { padding:8px 10px; border-bottom:1px solid var(--border-light); }
.map-key-label { display:block; font-size:0.72rem; font-weight:700; text-transform:uppercase; letter-spacing:0.04em; color:var(--text-secondary); margin-bottom:6px; }
.map-key-row { display:flex; align-items:center; gap:8px; width:100%; min-height:44px; padding:6px 8px; background:none; border:1px solid transparent; border-radius:var(--radius-md); cursor:pointer; text-align:left; font:inherit; color:var(--text-primary); }
.map-key-row:hover { background:var(--bg-elevated); }
.map-key-row[aria-pressed="false"] .map-key-row-name { color:var(--text-secondary); }
.map-key-row[aria-pressed="false"] .map-key-swatch { opacity:0.35; }
.map-key-check { font-size:20px; color:var(--brand-blue); flex-shrink:0; }
.map-key-row[aria-pressed="false"] .map-key-check { color:var(--text-secondary); }
.map-key-row-name { flex:1; min-width:0; font-size:0.85rem; font-weight:600; }
.map-key-count { font-size:0.8rem; font-weight:600; color:var(--text-secondary); }
.map-key-swatch { width:14px; height:14px; flex-shrink:0; border:1.5px solid #fff; box-shadow:0 1px 2px rgba(0,0,0,0.25); }
.map-key-sub { padding:0 8px 6px 36px; display:flex; flex-direction:column; gap:4px; }
.map-key-sub .map-legend-item { font-size:0.76rem; color:var(--text-secondary); }
.map-key-note { font-size:0.76rem; line-height:1.35; color:var(--text-secondary); }
.map-key-chip { align-self:flex-start; min-height:32px; padding:2px 8px; background:none; border:1px solid var(--border-medium); border-radius:var(--radius-sm); font:inherit; font-size:0.76rem; font-weight:600; color:var(--action-warning); cursor:pointer; }
.map-key-project-input { width:100%; min-height:44px; font-size:16px; padding:6px 10px; border:1px solid var(--border-medium); border-radius:var(--radius-md); background:var(--bg-card); color:var(--text-primary); }
.map-key-project-results { display:flex; flex-direction:column; gap:2px; margin-top:4px; max-height:240px; overflow-y:auto; }
.map-key-project-hit { display:flex; flex-direction:column; align-items:flex-start; width:100%; min-height:44px; padding:6px 8px; background:none; border:1px solid transparent; border-radius:var(--radius-md); text-align:left; font:inherit; cursor:pointer; }
.map-key-project-hit:hover { background:var(--bg-elevated); }
.map-key-project-name { font-size:0.85rem; font-weight:600; color:var(--text-primary); }
.map-key-project-sub { font-size:0.74rem; color:var(--text-secondary); }
.map-key-project-active { display:flex; align-items:center; gap:6px; padding:6px 8px; border:1px solid var(--brand-blue); border-radius:var(--radius-md); background:var(--bg-elevated); }
.map-key-project-active .map-key-project-name { flex:1; min-width:0; }
.map-key-clear { min-width:44px; min-height:44px; background:none; border:none; cursor:pointer; color:var(--text-primary); border-radius:var(--radius-md); }
@media (max-width: 768px) {
  .dispatch-map-body { flex-direction:column; }
  .dispatch-map-key { width:auto; order:-1; border-left:none; border-bottom:1px solid var(--border-light); max-height:45%; flex-shrink:0; }
  .dispatch-map-key:not([open]) { max-height:none; }
  .dispatch-map-body #dispatch-map-container { min-height:260px; }
}
/* ── TASK-9628 — Maps polish (BEGIN). Range bar + stat strip + map + key read
   as ONE card. Lives here, not styles.css: this sheet sets the map chrome and
   would beat styles.css. No new wrapper — the flex/height chain
   (.dispatch-map-view → .dispatch-map-body → #dispatch-map-container) is
   untouched; Google Maps renders blank at 0 height. ── */
.dispatch-map-view { background:var(--bg-card); border:1px solid var(--border-light); border-radius:var(--radius-lg); box-shadow:var(--shadow-sm); overflow:hidden; }
.dispatch-map-view .dispatch-map-header { border:none; border-bottom:1px solid var(--border-light); border-radius:0; }
.dispatch-map-body #dispatch-map-container { position:relative; }
.dispatch-map-body #dispatch-map-container::after { content:''; position:absolute; inset:0; pointer-events:none; z-index:1; box-shadow:inset 0 1px 4px rgba(15,23,42,0.14), inset 0 0 0 1px rgba(15,23,42,0.05); }
.map-range-showing { display:inline-flex; align-items:center; gap:4px; font-size:0.8rem; font-weight:600; color:var(--text-primary); }
.map-range-seg { display:inline-flex; flex-wrap:nowrap; border:1px solid var(--border-light); border-radius:9999px; background:var(--bg-elevated); overflow:hidden; transition:border-color 0.15s; }
.map-range-seg:hover { border-color:var(--border-medium); }
.map-range-seg .filter-chip { border:none; border-radius:0; background:transparent; white-space:nowrap; justify-content:center; }
.map-range-seg .filter-chip + .filter-chip { border-left:1px solid var(--border-light); }
.map-range-seg .filter-chip:hover { background:var(--bg-card); color:var(--text-primary) !important; }
.map-range-seg .filter-chip.active, .map-range-seg .filter-chip.active:hover { background:var(--brand-blue); color:#fff !important; }
.map-range-seg .filter-chip:focus-visible { outline:2px solid var(--brand-blue); outline-offset:-3px; }
.map-range-seg .filter-chip.map-range-custom-open { color:var(--brand-blue) !important; box-shadow:inset 0 0 0 2px var(--brand-blue); }
.map-range-pill { display:inline-flex; align-items:center; gap:6px; min-height:36px; padding:4px 12px; border:1px solid var(--border-light); border-radius:9999px; background:var(--bg-elevated); font-size:0.85rem; font-weight:700; color:var(--text-primary); white-space:nowrap; }
.map-range-pill .material-icons { font-size:16px; color:var(--brand-blue); }
.dispatch-map-view button.map-stat { min-height:32px; padding:2px 10px; background:var(--bg-card); border:1px solid var(--border-light); border-radius:9999px; font:inherit; font-size:13px; font-weight:600; text-decoration:none; cursor:pointer; transition:border-color 0.15s, background 0.15s; }
.dispatch-map-view button.map-stat:hover { border-color:var(--border-medium); background:var(--bg-elevated); }
.dispatch-map-view button.map-stat:focus-visible { outline:2px solid var(--brand-blue); outline-offset:2px; }
.dispatch-map-view button.map-stat:disabled { cursor:progress; opacity:0.75; }
.map-key-chip { border-color:var(--border-light); transition:border-color 0.15s, background 0.15s; }
.map-key-chip:hover { border-color:var(--border-medium); background:var(--bg-elevated); }
.map-key-count { display:inline-flex; align-items:center; justify-content:center; min-width:24px; padding:1px 8px; border:1px solid var(--border-light); border-radius:9999px; background:var(--bg-elevated); font-size:0.74rem; font-weight:700; color:var(--text-secondary); white-space:nowrap; flex-shrink:0; }
.map-key-count:empty { display:none; }
.map-key-row .map-key-count { margin-left:auto; }
.map-key-callout { display:block; padding:6px 8px; background:var(--bg-elevated); border-left:3px solid var(--border-medium); border-radius:var(--radius-sm); }
/* The header date pager pages the WEEK board; the map has its own range above.
   Hidden while Maps is on screen only — never bound to the map (that would move
   the board underneath and drop project mode). No :has() support = it shows. */
body:has(.dispatch-map-view) .dispatch-datenav-center .date-nav { display:none; }
@media (max-width: 768px) {
  .dispatch-map-view { border-radius:var(--radius-md); }
  .map-range-showing { display:none; }
  .map-range-seg { display:flex; width:100%; overflow-x:auto; scrollbar-width:none; }
  .map-range-seg .filter-chip { flex:1 0 auto; padding-left:8px; padding-right:8px; font-size:0.8rem; }
  .dispatch-map-view button.map-stat { min-height:44px; }
}
/* ── TASK-9628 (END) ── */`;
  document.head.appendChild(el);
}

// Samsara fleet listener state
let _samsaraUnsubscribe = null;
let _samsaraMarkers = [];
// TASK-9175 — each layer owns its markers so it clears on its own.
let _savedLocMarkers = [];
let _lastDispatchedMarkers = [];
let _projectPinMarkers = [];
let _equipmentMarkers = [];   // TASK-4800
let _estimatedMarkers = [];   // TASK-4800 S4

// TASK-4789 slice 1 — drawn map layers (routes, red lines, areas, no-go areas).
// The editor is a LAZY chunk (views/mapDrawLayers.js): loaded the first time
// the Maps view paints, never in the cold-load bundle. It is handed a ctx on
// every map build and never imports this file (Pitfall #1).
let _mapDrawMod = null;
let _mapDrawLoading = null;
function _drawnLayersCtx(map, container) {
  const st = window.AppState || {};
  return {
    map,
    tenantId: DispatchState.tenantId,
    canEdit: _canEditPlaces(true),   // same gate as saved places — rules placesWriter
    keyMount: container.querySelector('[data-map-drawn-layers]'),
    repaint: repaintMapView,
    pinSession: !!(st.driverMode || st.isPublicAccess || st.driverModePin),
  };
}
function _attachDrawnLayers(map, container) {
  const ctx = _drawnLayersCtx(map, container);
  if (!ctx.tenantId || ctx.pinSession) { if (ctx.keyMount) ctx.keyMount.remove(); return; }
  if (_mapDrawMod) { _mapDrawMod.attachMapLayers(ctx); return; }
  if (!_mapDrawLoading) {
    _mapDrawLoading = __ddChunkWrap(import('./mapDrawLayers.js?v=10295'))
      .then((m) => { _mapDrawMod = m; return m; })
      .catch((err) => { _mapDrawLoading = null; console.warn('[dispatch map] drawn layers failed to load:', err); return null; });
  }
  _mapDrawLoading.then((m) => {
    if (m) { if (DispatchState.mapInstance === map) m.attachMapLayers(_drawnLayersCtx(map, container)); return; }
    const mount = container.querySelector('[data-map-drawn-layers]');
    if (mount) mount.innerHTML = '<span class="map-key-label">Drawn layers</span><span class="map-key-note">Couldn’t load. Refresh to try again.</span>';
  });
}

// TASK-4786 A4 — customer filter + colour by customer (LAZY views/mapCustomers.js;
// never imports this file). It fills the [data-map-cust*] placeholders itself.
let _mapCustMod = null, _mapCustLoading = null;
function _attachMapCustomers(container, rangeJobs, map) {
  const go = (m) => m && DispatchState.mapInstance === map && m.attach({ container, rangeJobs, DS: DispatchState, colorOf: getCustomerColor, esc: escapeHtml, escA: window.escAttr || escapeHtml, repaint: repaintMapView });
  if (_mapCustMod) return go(_mapCustMod);
  (_mapCustLoading = _mapCustLoading || __ddChunkWrap(import('./mapCustomers.js?v=1')).then((m) => (_mapCustMod = m), (e) => { _mapCustLoading = null; console.warn('[map] customers', e); })).then(go);
}

// TASK-9631 S2 — "+ Pin" on the map (add / move / delete saved places). A LAZY
// chunk (views/mapPins.js), loaded only for a login that can add saved places
// (same gate as the place form — rules placesWriter). Handed a ctx on every map
// build; never imports this file (Pitfall #1).
let _mapPinsMod = null;
let _mapPinsLoading = null;
const _savedLocMarkerById = new Map();
// The card's Move pin / Delete buttons (data-map-pin-*) are handled inside the
// chunk (one delegated listener on the map element), so they cost no
// cold-load bytes here.
function _attachMapPins(map, infoWindow, savedLayerOn) {
  const st = window.AppState || {};
  const ctx = {
    map, tenantId: DispatchState.tenantId,
    canAdd: _canEditPlaces(false), canEdit: _canEditPlaces(true),
    pinSession: !!(st.driverMode || st.isPublicAccess || st.driverModePin),
    savedLayerOn: !!savedLayerOn,
    showSaved: () => { if (getMapLayers(tenantHasSamsaraFlag()).saved) return false; toggleMapLayer('saved'); return true; },
    categories: _placeCats,
    placeById: (id) => (DispatchState.savedLocations || []).find(l => l && l.id === id) || null,
    markerFor: (id) => _savedLocMarkerById.get(id) || null,
    currentTenantId: () => DispatchState.tenantId,
    closeInfo: () => infoWindow.close(),
    drawBusy: () => !!(_mapDrawMod && _mapDrawMod.isMapDrawBusy()),
    repaint: repaintMapView,
    viewOnlyMsg: PLACES_VIEW_ONLY_MSG,
  };
  if (!ctx.tenantId || ctx.pinSession || !ctx.canAdd) { if (_mapPinsMod) _mapPinsMod.resetMapPins(); return; }
  if (!_mapPinsLoading) {
    _mapPinsLoading = __ddChunkWrap(import('./mapPins.js?v=10295'))
      .then((m) => { _mapPinsMod = m; return m; })
      .catch((err) => { _mapPinsLoading = null; console.warn('[dispatch map] map pins failed to load:', err); return null; });
  }
  _mapPinsLoading.then((m) => { if (m && DispatchState.mapInstance === map) m.attachMapPins(ctx); });
}

// TASK-9631 S3 — saved named maps (pins + categories + view). A LAZY chunk
// (views/mapViews.js), loaded for any office login on the Maps view (never a
// driver PIN / link / public session). Every read and write is the
// mapViewWrite callable; the server re-checks access, so canSave only hides
// buttons. Never imports this file (Pitfall #1).
let _mapViewsMod = null;
let _mapViewsLoading = null;
function _mapViewPlaces(places) {
  const a = DispatchState.mapViewActive;
  if (!a || a.tenantId !== DispatchState.tenantId || !(a.excluded instanceof Set) || !Array.isArray(places)) return places;
  return places.filter(l => !(l && a.excluded.has(l.id)));
}
function _attachMapViews(map, infoWindow) {
  const st = window.AppState || {};
  const pinSession = !!(st.driverMode || st.isPublicAccess || st.driverModePin);
  if (!DispatchState.tenantId || pinSession) { if (_mapViewsMod) _mapViewsMod.resetMapViews(); return; }
  if (!_mapViewsLoading) {
    _mapViewsLoading = __ddChunkWrap(import('./mapViews.js?v=3')) /* TASK-9631 S3 QA fix (integ/2129 addendum) */
      .then((m) => { _mapViewsMod = m; return m; })
      .catch((err) => { _mapViewsLoading = null; console.warn('[dispatch map] saved maps failed to load:', err); return null; });
  }
  // integ/2128 (app cold-load budget): the ctx (open / close / drop / rename)
  // is built by the chunk's dispatchCtx from these dispatch.js hooks.
  _mapViewsLoading.then((m) => {
    if (m && DispatchState.mapInstance === map) m.attachMapViews(m.dispatchCtx({
      S: DispatchState, map, infoWindow, canSave: _canEditPlaces(true),
      cats: getMapSavedHiddenCats, setCats: _setMapSavedHiddenCats, repaint: repaintMapView,
      savedOn: () => getMapLayers(tenantHasSamsaraFlag()).saved, showSaved: () => toggleMapLayer('saved'),
      busy: () => !!((_mapDrawMod && _mapDrawMod.isMapDrawBusy()) || (_mapPinsMod && _mapPinsMod.isMapPinsBusy())),
    }));
  });
}

// TASK-9631 F4 — pure. What the user CHOSE for the map, as one string. A
// change here means "show me something else", so the map refits. Data changes
// (a job edited, a stop geocoded) do not touch it, so the view is kept.
function mapViewportKey({ from, to, layers, projectId } = {}) {
  const on = MAP_LAYER_IDS.filter(id => layers && layers[id]).join(',');
  return [from || '', to || '', on, projectId || ''].join('|');
}

// TASK-9631 F4 — pure. The saved view to restore, or null to auto-fit. Only a
// view the USER moved, under the same key, is restored; anything else refits.
function mapViewportToRestore(saved, key) {
  if (!saved || !saved.userMoved || saved.key !== key) return null;
  const c = plottableCoord(saved.center);
  const z = Number(saved.zoom);
  if (!c || !Number.isFinite(z)) return null;
  return { center: c, zoom: z };
}

// TASK-9631 F4 — remember the live view. `dragstart` is user-only. A zoom only
// counts as the user's once the CALLER says the programmatic fit is done:
// renderMapView awaits the pings query between building the map and calling
// fitBounds, so the constructor's own first idle comes BEFORE the fit and must
// not unlock zoom tracking (COD r2 — it stored the auto-fit as a user move).
// A restored view starts user-owned and pre-filled, so a second snapshot
// before the first idle still keeps it. Returns settle(); the map is rebuilt
// each render and its listeners go with it.
function _trackMapViewport(map, key, keptView) {
  const state = {
    key, userMoved: !!keptView,
    center: keptView ? keptView.center : null, zoom: keptView ? keptView.zoom : null,
  };
  DispatchState._mapViewport = state;
  let settled = !!keptView;
  google.maps.event.addListener(map, 'dragstart', () => { state.userMoved = true; });
  google.maps.event.addListener(map, 'zoom_changed', () => { if (settled) state.userMoved = true; });
  google.maps.event.addListener(map, 'idle', () => {
    const c = map.getCenter && map.getCenter();
    if (c) state.center = { lat: c.lat(), lng: c.lng() };
    state.zoom = map.getZoom && map.getZoom();
  });
  return () => { settled = true; };
}

async function renderMapView(container) {
  // TASK-9174 — the route warm writes up to MAP_ROUTE_WARM_BATCH job documents
  // back to back, and EVERY one of those writes bounces a jobs snapshot through
  // renderDispatchBoard into here. Tearing down and rebuilding the whole
  // google.maps.Map per write mid-warm is pure churn. The warm repaints ONCE when
  // it finishes; the flag is cleared in a finally, so this can never stick.
  // TASK-9734 — the warm is automatic now, so a deferred paint must not be a
  // LOST one: a range click made mid-batch is remembered and painted at the end
  // (a batch is a few seconds).
  if (_routeWarmBusy && DispatchState.mapInstance && document.getElementById('dispatch-map-container')) {
    _routeWarmRepaintPending = true;
    return;
  }
  // TASK-4789 — a shape being drawn or reshaped lives on THIS map instance; a
  // rebuild would throw it away mid-tap. Skip, and repaint once it ends.
  if (_mapDrawMod && _mapDrawMod.isMapDrawBusy() && DispatchState.mapInstance && document.getElementById('dispatch-map-container')) {
    _mapDrawMod.notePendingRepaint();
    return;
  }
  // TASK-9631 S2 — same for a pin being dropped, named or dragged.
  if (_mapPinsMod && _mapPinsMod.isMapPinsBusy() && DispatchState.mapInstance && document.getElementById('dispatch-map-container')) {
    _mapPinsMod.notePendingRepaint();
    return;
  }

  if (!isDispatchBoardRenderable() && !DispatchState._dispatchLoadFailed) {  // TASK-4588 — jobs too, not jobDays alone
    container.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:400px;color:var(--text-secondary);">
      <span class="btn-spinner" style="margin-right:8px;"></span> Loading dispatch data...
    </div>`;
    return;
  }

  if (!window.__gmapsLoaded) {
    container.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;height:400px;color:var(--text-secondary);">
      <span class="btn-spinner" style="margin-right:8px;"></span> Loading Google Maps...
    </div>`;
    const checkInterval = setInterval(() => {
      if (window.__gmapsLoaded) {
        clearInterval(checkInterval);
        if (DispatchState.viewMode === 'map') renderMapView(container);
      }
    }, 300);
    return;
  }

  // Check if Samsara is connected. TASK-1621 — the raw token no longer lives
  // in `settings` (moved to the backend-only secrets/samsara sibling);
  // `samsaraConnected` is the non-secret marker. `samsaraApiToken` fallback
  // covers a tenant that connected before this change and hasn't been
  // migrated yet (scripts/task1621-migrate-samsara-token.cjs --apply not
  // run) — drop that fallback once the migration has run everywhere.
  const hasSamsara = tenantHasSamsaraFlag();
  // TASK-9175 — which layers are on, and whether a project filter replaces the
  // date range for the Stops layer.
  _ensureMapKeyStyles();
  const layers = getMapLayers(hasSamsara);
  const mapProjects = _mapAllProjects();
  const projectId = DispatchState.mapProjectId || null;
  const activeProject = projectId ? (mapProjects.find(p => p.id === projectId) || { id: projectId, name: 'Selected project' }) : null;
  const projectMode = !!activeProject;

  // TASK-1616 — gather jobs across the selected RANGE (default: today).
  const range = getMapRange();
  const { days: rangeDays, clamped: rangeClamped } = mapRangeDays(range.from, range.to);
  // The EFFECTIVE range — what the map actually walked after the cap. Every
  // label and warning below reads from this, never from the request, so the
  // header can never claim a span the map isn't showing.
  const shownFrom = rangeDays[0] || range.from;
  const shownTo = rangeDays[rangeDays.length - 1] || range.to;
  const windowFloor = mapWindowFloorISO();
  const rangeStartsBeforeWindow = !!(windowFloor && shownFrom < windowFloor);

  // Iterate getJobsForDate per day — do NOT filter DispatchState.jobs by
  // startDate. A multi-day job carries ONE startDate for the whole engagement
  // and would vanish from every day but the first (the mistake that sent a
  // wrong crew lineup on 2026-08-10). getJobsForDate walks jobDays and carries
  // the legacy-import fallback for jobs that predate jobDays.
  //
  // Deduped by jobId: ONE marker per job for the whole range, not one per day —
  // a job working five days would otherwise stack five identical pins on the
  // same coordinate. The day count goes in the info window instead.
  const rangeJobs = [];
  const rangeJobIndex = new Map(); // jobId -> index into rangeJobs
  if (projectMode) {
    // TASK-9175 (2026-09-29 slice) — a project runs for weeks, so its stops
    // ignore the date range. Each job carries its LATEST jobDay (status, crew,
    // trucks) when the board has one; a job with no jobDay in memory gets a
    // stub so its stops still plot.
    // COD r1: the latest jobDay ON OR BEFORE today that is not cancelled — a
    // future booking must not colour a finished stop, nor date it. A job with
    // no usable jobDay in memory takes its colour from the job's own status.
    const _todayISO = mapDayISO(0);
    const latestDayByJob = new Map();
    (DispatchState.jobDays || []).forEach(jd => {
      if (!jd || !jd.jobId) return;
      if (String(jd.status || '').toLowerCase() === 'cancelled') return;
      const d = _mapTsDayISO(jd.date);
      if (!d || d > _todayISO) return;
      const prev = latestDayByJob.get(jd.jobId);
      if (!prev || _mapTsMs(jd.date) > _mapTsMs(prev.date)) latestDayByJob.set(jd.jobId, jd);
    });
    const fetched = (DispatchState._mapProjectJobs || {})[projectId] || [];
    mergeProjectJobs(DispatchState.jobs, fetched, projectId).forEach(job => {
      const _js = String(job.status || '').toLowerCase();
      const jd = latestDayByJob.get(job.id) || {
        id: '', jobId: job.id, drivers: [], trucks: [],
        status: ['completed', 'complete', 'finished', 'closed', 'invoiced'].includes(_js) ? 'finished' : 'assigned'
      };
      const d = _mapTsDayISO(jd.date) || _mapTsDayISO(job.startDate) || null;
      rangeJobIndex.set(job.id, rangeJobs.length);
      rangeJobs.push({ job, jobDay: jd, dayCount: 1, firstDay: d, lastDay: d });
    });
  }
  (projectMode ? [] : rangeDays).forEach(dayStr => {
    getJobsForDate(dayStr, true).forEach(entry => {
      const jid = entry.job?.id;
      if (jid == null) return;
      const at = rangeJobIndex.get(jid);
      if (at === undefined) {
        rangeJobs.push({
          job: entry.job,
          jobDay: entry.jobDay,
          dayCount: 1,
          firstDay: dayStr,
          lastDay: dayStr
        });
        rangeJobIndex.set(jid, rangeJobs.length - 1);
      } else {
        const rec = rangeJobs[at];
        rec.dayCount++;
        rec.lastDay = dayStr;
        // Days are walked oldest → newest, so this keeps the LATEST day's
        // assignment and status — what a dispatcher asking "where is this now"
        // wants on the pin.
        rec.jobDay = entry.jobDay;
      }
    });
  });
  // TASK-4786 A4 — the customer filter (lazy views/mapCustomers.js; until it
  // loads nothing can be picked, so every job is drawn).
  const todaysJobs = _mapCustMod ? _mapCustMod.filterRangeJobs(rangeJobs, DispatchState.mapCustKeys, DispatchState.customers) : rangeJobs;
  const custColors = !!_mapCustMod && DispatchState.mapColorMode === 'customer';

  // Build marker data from jobs with geocoded waypoints
  const jobMarkers = [];
  // TASK-1615 — jobs carrying a coordinate we refused to plot. Surfaced in the
  // map header as its own count so a dropped stop is visible, not silent.
  const badLocationJobs = [];
  // TASK-4786 — stops with NO coordinate at all, kept per-stop rather than
  // collapsed into the old job-level "without coordinates" count. A job with
  // one geocoded stop and one blank one used to report NOTHING: it was on the
  // map, so it never reached the unmapped bucket, and the blank stop simply
  // vanished. That is the silent 30% (771 of 2,541 waypoints on TITS).
  const unlocatedStopJobs = [];
  let unlocatedStopCount = 0;
  let stopMarkerCount = 0;
  todaysJobs.forEach(({ job, jobDay, dayCount, firstDay, lastDay }) => {
    const waypoints = job.waypoints || [];
    // TASK-1615 — was `waypoints.find(wp => wp.lat && wp.lng)`, a truthiness
    // test that any non-zero number passes, lat 5304 included. Validate, and
    // carry the coerced numbers forward so nothing downstream re-parses.
    // TASK-4786 — the same three-way split, now a shared pure function.
    const { plottable: plottableWps, bad: badWps, unlocated: unlocatedWps } = classifyJobStops(waypoints);
    if (unlocatedWps.length > 0) {
      unlocatedStopCount += unlocatedWps.length;
      unlocatedStopJobs.push({
        jobId: job.id,
        jobDayId: jobDay.id,
        title: job.title || job.name || 'Untitled Job',
        stops: unlocatedWps.map(wp => ({
          name: wp.name || wp.address || 'Unnamed destination',
          address: wp.address || '',
          url: wp.url || '',
          stopNumber: wp.stopNumber,
          // TASK-9497 — the server's loose match for this stop, if any.
          id: typeof wp.id === 'string' ? wp.id : '',
          suggestion: stopSuggestionView(wp)
        })),
        stillMapped: plottableWps.length > 0
      });
    }
    if (badWps.length > 0) {
      badLocationJobs.push({
        jobId: job.id,
        jobDayId: jobDay.id,
        title: job.title || job.name || 'Untitled Job',
        stops: badWps.map(wp => ({
          name: wp.name || wp.address || 'Unnamed destination',
          lat: wp.lat,
          lng: wp.lng
        })),
        stillMapped: plottableWps.length > 0
      });
    }
    const primaryWp = plottableWps[0];

    if (primaryWp) {
      stopMarkerCount += plottableWps.length;
      const driverNames = (jobDay.drivers || []).map(ref => {
        const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
        return p ? (p.nickname || p.name) : ref;
      });
      const truckUnits = (jobDay.trucks || []).map(ref => {
        const t = _resolveFleetRef(ref, DispatchState.trucks);
        return t ? (t.unit || t.name) : ref;
      });
      const _cref = job.customerId || job.customer;
      const customer = DispatchState.customers.find(c => c.id === _cref || c.name === _cref);

      jobMarkers.push({
        lat: primaryWp.lat,
        lng: primaryWp.lng,
        jobId: job.id,
        jobDayId: jobDay.id,
        title: job.title || job.name || 'Untitled Job',
        jobNumber: job.jobNumber != null ? job.jobNumber : null,
        location: primaryWp.name || primaryWp.address || job.location || '',
        status: jobDay.status || 'assigned',
        drivers: driverNames,
        trucks: truckUnits,
        customerName: customer?.name || '',
        customerColor: getCustomerColor(job.customerId || job.customer),
        customerKey: job.customerId || job.customer || '',
        waypoints: plottableWps,
        // TASK-4786 — "STOP 2 OF 5" has to count the job's REAL stops, not just
        // the plottable ones, or a job whose stop 3 never geocoded reads as
        // "stop 4 of 4" and the dispatcher trusts a route that is missing a leg.
        totalStops: waypoints.length,
        unlocatedStops: unlocatedWps.length,
        badStops: badWps.length,
        // TASK-9174 — the cached ROAD geometry, already checked against this
        // job's CURRENT stops (a stale cache reads as absent), plus the permit
        // gate that decides how confident the line is allowed to look.
        cachedRoute: jobCachedRoute(job),
        // TASK-9734 — which of the route states this job is in (key, info
        // window, background warm), and the stops the warm would route.
        routeState: routeLookupState(job),
        routeWaypoints: waypoints,
        permitRequired: job.permitRequired === true,
        // TASK-1616 — one pin covers every day this job worked inside the
        // range; the info window is where that collapse is disclosed.
        dayCount: dayCount || 1,
        firstDay,
        lastDay
      });
    }
  });

  const totalJobs = todaysJobs.length;
  const mappedJobs = jobMarkers.length;
  // TASK-1615 — three buckets, no double counting: on the map / never geocoded /
  // has a coordinate that is garbage. A job that dropped off the map because its
  // only coordinate was bad belongs in the bad bucket, not in "without
  // coordinates" — otherwise the fixable problem hides inside the vague one.
  const badLocationCount = badLocationJobs.length;
  const unmappedBadJobs = badLocationJobs.filter(b => !b.stillMapped).length;
  const unmappedJobs = Math.max(0, totalJobs - mappedJobs - unmappedBadJobs);
  DispatchState._badLocationJobs = badLocationJobs;
  // TASK-4786 — the stop-level "never geocoded" list behind the new chip.
  DispatchState._unlocatedStopJobs = unlocatedStopJobs;

  // ── TASK-9174 — WHAT THE LINES ON THIS MAP CAN SAY, THIS RANGE ─────────────
  // A job only gets a line at all when two or more of its stops are plottable.
  // The three line styles are a VOCABULARY, so the key lists all three whenever
  // any line is drawn — it is not a census, and deliberately so: tallying the
  // styles here and drawing them below would be two copies of one decision,
  // free to drift apart.
  const anyRouteLine = jobMarkers.some(m => m.totalStops > 1 && m.waypoints.length >= 2 && m.routeState.state === 'ok');
  // TASK-9734 — the tally of jobs whose route is NOT drawn, by reason. These
  // are counted (not just listed in the key) because they are the ones with no
  // line at all, and "why is there no line" has three different answers.
  const routeTally = { unlooked: 0, looking: 0, failed: 0, noroad: 0 };
  jobMarkers.forEach(m => {
    if (m.waypoints.length >= 2 && routeTally[m.routeState.state] != null) routeTally[m.routeState.state]++;
  });
  // Jobs on screen that are DUE a lookup: no answer for their current stops, a
  // lapsed lease, or a transient failure past its backoff. A job answered "no
  // road" (ZERO_RESULTS, NO_GEOMETRY…) is NOT here — it was asked and answered,
  // and re-asking every visit is the retry storm the cache exists to prevent.
  // 🔴 Building this list is ALL a render does. The lookups happen in
  // autoWarmMapRoutes, debounced and paced — never one per repaint.
  DispatchState._routeWarmQueue = jobMarkers
    .filter(m => m.waypoints.length >= 2 && m.routeState.due)
    .map(m => ({ jobId: m.jobId, fp: m.routeState.fp, waypoints: m.routeWaypoints }));
  const canWarmRoutes = layers.stops && _dispatchCanManageDays()
    && !!window.google?.maps?.geometry?.encoding?.decodePath;
  const routePendingCount = routeTally.unlooked + routeTally.looking;

  // ── TASK-9175 — the other layers' data. All in memory: savedLocations is
  // already streamed, jobDays/jobs are the board window. The only read any of
  // this can cause is the per-day clock lookup for a double-booked unit, and
  // only while "Last dispatched" is on.
  // TASK-9631 S3 — while a saved map is open, only its pins (see _mapViewPlaces).
  const savedCls = classifySavedLocationsForMap(_mapViewPlaces(DispatchState.savedLocations),
    (l) => plottableCoord(l) || plottableCoord(extractCoordsFromUrl(l.url || '')));
  DispatchState._mapSavedUnplotted = savedCls.unplotted;
  // TASK-9540 — saved places draw as their CATEGORY's shape + color; the key
  // switches categories on and off (per viewer, remembered like the layers).
  const placeCats = _placeCats();
  const savedHidden = new Set(getMapSavedHiddenCats());
  const savedShown = filterPlacesByHiddenCategories(savedCls.plotted, placeCats, savedHidden);
  const savedCatCounts = countPlacesByCategory(savedCls.plotted, placeCats);

  let ldPlan = { pins: [], ambiguous: [], unplotted: [], clockDatesNeeded: [] };
  let ldNotInWindow = 0;
  if (layers.lastDispatched) {
    const jobsById = new Map((DispatchState.jobs || []).map(j => [j.id, j]));
    const _ldUnits = _mapFleetUnits();
    ldPlan = planLastDispatched(_ldUnits,DispatchState.jobDays, jobsById, _mapLoadedClockEntries(), {
      todayISO: mapDayISO(0),
      dayISO: _mapTsDayISO,
      toMs: _mapTsMs,
      unitMatches: _mapUnitMatches,
      stopFor: _mapLastStopOf,
      clockUnknownForDate: _mapClockUnknownForDate,
    });
    if (ldPlan.clockDatesNeeded.length) {
      // Fire-and-forget; the status rule stands in until it lands, then ONE
      // repaint. A failed day is cached as failed, so this cannot loop.
      _mapFetchClockDays(ldPlan.clockDatesNeeded).then(got => { if (got && DispatchState.viewMode === 'map') repaintMapView(); });
    }
    // COD r1: a unit with no dispatch inside the loaded window is said, not
    // silently absent.
    ldNotInWindow = Math.max(0, _ldUnits.length - ldPlan.pins.length - ldPlan.ambiguous.length - ldPlan.unplotted.length);
  }
  DispatchState._mapLdAmbiguous = ldPlan.ambiguous;
  DispatchState._mapLdUnplotted = ldPlan.unplotted;
  const ldIssues = ldPlan.ambiguous.length + ldPlan.unplotted.length;

  // ── TASK-4800 — EQUIPMENT layer data. Trucks/trailers/ledger/individual are
  // all in memory; the one-shot assetTransactions read and the per-job fetch
  // for open lots outside the window fire only while the row is ON, then ONE
  // repaint each. Both are cached per tenant and cannot loop: a failed ledger
  // read is remembered as failed; a fetched-missing job is cached as null.
  // TASK-4800 S4 — the Estimated row shares this read and this fetch (same
  // caches, same loop guards); its logic is a lazy chunk, loaded the first
  // time the row is on, then ONE repaint (a failed load is remembered).
  let eqPlan = null;
  let estPlan = null;
  if (layers.equipment || layers.estimated) {
    const _eqStale = DispatchState._mapEqTxnsState === 'loaded' && (Date.now() - (DispatchState._mapEqTxnsAt || 0)) > MAP_EQUIPMENT_TXN_TTL_MS;
    if (DispatchState._mapEqTxnsState === 'idle' || _eqStale) {
      _mapFetchEquipmentTxns().then(got => { if (got && DispatchState.viewMode === 'map') repaintMapView(); });
    }
    if (layers.estimated && !_mapEstMod && !DispatchState._mapEstLoadFailed) {
      _mapLoadEstimated().then(m => { if (m && DispatchState.viewMode === 'map') repaintMapView(); });
    }
    eqPlan = layers.equipment ? _mapEquipmentPlan() : null;
    estPlan = layers.estimated ? _mapEstimatedPlan(eqPlan) : null;
    const _jobsWanted = [...new Set([...(eqPlan ? eqPlan.jobsNeeded : []), ...(estPlan ? estPlan.jobsNeeded : [])])];
    if (_jobsWanted.length && !DispatchState._mapEqJobsInflight) {
      // Resolves false (no repaint) when nothing landed — a failed read backs
      // off, the ceiling is reached, or every id is already cached.
      // TASK-9508 — nothing landed means no repaint, so the "Loading older
      // jobs…" chip has to be taken down here or it would sit there forever
      // (unless the ledger read is still the thing loading).
      const _eqTid = DispatchState.tenantId;
      _mapFetchEquipmentJobs(_jobsWanted).then(got => {
        if (got && DispatchState.viewMode === 'map') { repaintMapView(); return; }
        // COD (Code) — never take down another tenant's / another fetch's chip.
        if (DispatchState.tenantId === _eqTid && !DispatchState._mapEqJobsInflight
          && DispatchState._mapEqTxnsState !== 'loading' && typeof document !== 'undefined') {
          document.querySelectorAll('[data-map-eq-loading], [data-map-est-loading]').forEach(el => el.remove());
        }
      });
    }
  }
  const estLoading = !!estPlan && (DispatchState._mapEqTxnsState === 'loading' || !!DispatchState._mapEqJobsInflight);
  const eqUnplotted = eqPlan ? eqPlan.unplotted.length : 0;
  // TASK-9508 — one labelled source for the key's numbers, and a loading state
  // while the ledger / older jobs are still being read (~10 s on a big tenant,
  // which used to look like a finished, too-small map).
  const eqSum = eqPlan ? _mapEquipmentKeySummary(eqPlan) : null;
  const eqLoading = !!eqPlan && (DispatchState._mapEqTxnsState === 'loading' || !!DispatchState._mapEqJobsInflight);
  const eqStale = eqPlan ? eqPlan.sites.filter(s => s.ageBucket === 'stale').length : 0;

  const projectPinCoord = activeProject ? plottableCoord({ lat: activeProject.locationLat, lng: activeProject.locationLng }) : null;
  const projectStopTotal = projectMode ? rangeJobs.reduce((n, r) => n + ((r.job.waypoints || []).length), 0) : 0;
  const projectLoading = projectMode && DispatchState._mapProjectLoading === projectId;

  // The key's Fleet row: state is filled in live by the listener below.
  const fleetInitial = fleetLayerState({ hasSamsara, layerOn: layers.fleet, answered: false });

  const keyRow = (id, name, swatchStyle, count, extraAttrs = '') => `
    <button type="button" class="map-key-row" data-map-layer="${id}" aria-pressed="${layers[id] ? 'true' : 'false'}"${extraAttrs}>
      <span class="material-icons map-key-check" aria-hidden="true">${layers[id] ? 'check_box' : 'check_box_outline_blank'}</span>
      <span class="map-key-swatch" aria-hidden="true" style="${swatchStyle}"></span>
      <span class="map-key-row-name">${name}</span>
      ${count != null ? `<span class="map-key-count">${count}</span>` : ''}
    </button>`;

  const projectSectionHtml = mapProjects.length || projectMode ? `
    <div class="map-key-section">
      <label class="map-key-label" for="map-project-search">Project</label>
      ${projectMode ? `
        <div class="map-key-project-active">
          <span class="material-icons" style="font-size:18px;color:${MAP_PROJECT_PIN_COLOR};" aria-hidden="true">folder_special</span>
          <span class="map-key-project-name">${escapeHtml(activeProject.name || 'Untitled project')}</span>
          <button type="button" class="map-key-clear" data-map-project-clear aria-label="Clear the project filter and go back to dates"><span class="material-icons" aria-hidden="true">close</span></button>
        </div>
        <div class="map-key-note" style="margin-top:6px;">${projectLoading
          ? 'Loading this project’s older jobs…'
          : `${rangeJobs.length} job${rangeJobs.length !== 1 ? 's' : ''} · ${projectStopTotal} destination${projectStopTotal !== 1 ? 's' : ''}, every date.${DispatchState._mapProjectLoadFailed === projectId ? ' Older jobs couldn’t be loaded — showing the ones on the board.' : ''}${activeProject && !projectPinCoord ? ' The project itself has no map location yet.' : ''}`}</div>`
        : `
        <input type="search" id="map-project-search" class="map-key-project-input" placeholder="Find a project…" autocomplete="off"
          aria-controls="map-project-results" oninput="window.DispatchModule.filterMapProjects(this.value)" onfocus="window.DispatchModule.filterMapProjects(this.value)">
        <div id="map-project-results" class="map-key-project-results" role="listbox" aria-label="Projects" hidden></div>`}
    </div>` : '';

  const keyHtml = `
    <details class="dispatch-map-key"${_mapKeyIsOpen() ? ' open' : ''}>
      <summary data-map-key-toggle>
        <span style="display:inline-flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;" aria-hidden="true">layers</span> Map key &amp; layers</span>
        <span class="map-key-count">${MAP_LAYER_IDS.filter(id => layers[id]).length} on${eqLoading ? '<span data-map-eq-loading> · loading…</span>' : ''}</span>
      </summary>
      ${projectSectionHtml}
      <div class="map-key-section" data-map-cust hidden></div>
      <div class="map-key-section">
        <span class="map-key-label">Layers</span>
        ${keyRow('stops', projectMode ? 'Project destinations' : 'Job destinations', 'border-radius:50%;background:#3B82F6;', layers.stops ? stopMarkerCount : null)}
        ${layers.stops ? `<div class="map-key-sub">
          ${custColors ? '<span data-map-cust-legend></span>' : `
          <span class="map-legend-item"><span class="map-legend-dot" style="background:#3B82F6;"></span>Assigned</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:#F59E0B;"></span>En Route</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:#10B981;"></span>On Site</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:#6B7280;"></span>Finished</span>`}
          ${anyRouteLine ? `
          <span class="map-legend-item" title="Follows real roads, from the Directions API, cached on the job. It is the route we EXPECT the truck to take — DispatchDeck has no record of the route actually driven, which is what the dots say.">
            <span style="display:inline-block;width:20px;border-top:3px dotted var(--text-primary);vertical-align:middle;margin-right:2px;"></span>Proposed route &mdash; actual route unknown</span>
          <span class="map-legend-item" title="This job needs a permit, so the STATE has already specified its route by road and mile marker. The road route below is a rough idea of the trip — it is NOT the permitted route, and the truck may not be legally allowed to drive it.">
            <span style="display:inline-block;width:20px;border-top:3px dashed var(--text-primary);vertical-align:middle;margin-right:2px;"></span>Permitted load — rough idea, not the permit route</span>
          ` : ''}
          ${routePendingCount > 0 ? `<span class="map-legend-item" data-map-route-tally="pending" title="These jobs have numbered destination pins but no road route yet. ${canWarmRoutes ? 'The map is looking them up now, a few at a time — each job is looked up once and remembered.' : 'Someone who can edit jobs needs to open this map (or save the job) for the route to be looked up.'}">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;" aria-hidden="true">hourglass_empty</span>${canWarmRoutes ? 'Looking up road route' : 'Road route not looked up yet'}: ${routePendingCount} job${routePendingCount !== 1 ? 's' : ''}, pins only</span>` : ''}
          ${routeTally.failed > 0 ? `<span class="map-legend-item" data-map-route-tally="failed" title="The route lookup hit a temporary error (network, or the maps service). Nothing is drawn rather than a guess. It is retried on its own${canWarmRoutes ? '' : ' the next time someone who can edit jobs opens this map'}.">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;color:#B45309;" aria-hidden="true">sync_problem</span>Route lookup failed, retrying: ${routeTally.failed} job${routeTally.failed !== 1 ? 's' : ''}, pins only</span>` : ''}
          ${routeTally.noroad > 0 ? `<span class="map-legend-item" data-map-route-tally="noroad" title="Google has no driving route between these destinations (or the destinations are the same place). The numbered pins give the order; no line is drawn, because a line that ignores roads would look like a route.">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;" aria-hidden="true">wrong_location</span>No road between the destinations: ${routeTally.noroad} job${routeTally.noroad !== 1 ? 's' : ''}, pins only</span>` : ''}
          ${projectPinCoord ? `<span class="map-legend-item"><span class="map-legend-dot" style="background:${MAP_PROJECT_PIN_COLOR};border-radius:2px;"></span>Project location</span>` : ''}
        </div>` : ''}
        <div class="map-key-row-wrap" style="display:flex;align-items:center;gap:4px;">
          ${keyRow('saved', 'Saved destinations', `transform:rotate(45deg) scale(0.8);background:${MAP_SAVED_COLOR};`, null, ' style="flex:1;min-width:0;"')}
          <button type="button" class="map-key-chip" data-map-saved-manage style="min-height:44px;min-width:44px;display:inline-flex;align-items:center;gap:4px;color:var(--brand-blue);flex-shrink:0;" title="Categories, edit and delete — the same list as Add Job" aria-label="Manage saved places"><span class="material-icons" style="font-size:18px;" aria-hidden="true">settings</span>Manage</button>
        </div>
        ${layers.saved ? `<div class="map-key-sub">
          ${!DispatchState.savedLocations?.length ? '<span class="map-key-note">No saved locations yet. Bookmark a destination on any job to add one.</span>' : `<span class="map-key-note" data-map-saved-count style="color:var(--text-primary);font-weight:600;">${mapSavedCountText(savedShown.length, savedCls.plotted.length + savedCls.unplotted.length)}</span>`}
          ${savedCls.plotted.length ? `<span style="display:flex;flex-wrap:wrap;gap:6px;" role="group" aria-label="Saved place categories">${placeCats.filter(c => savedCatCounts[c.id]).map(c => {
            const on = !savedHidden.has(c.id);
            return `<button type="button" class="map-key-chip" data-map-saved-cat="${escapeHtml(c.id)}" aria-pressed="${on ? 'true' : 'false'}" style="min-height:40px;display:inline-flex;align-items:center;gap:6px;${on ? '' : 'opacity:0.55;text-decoration:line-through;'}" title="${on ? 'Hide' : 'Show'} ${escapeHtml(c.name)} on the map">${placeShapeSvg(c.shape, c.color, 16)}<span>${escapeHtml(c.name)}</span><span class="map-key-count">${savedCatCounts[c.id]}</span></button>`;
          }).join('')}</span>` : ''}
          ${savedCls.unplotted.length ? `<button type="button" class="map-key-chip" data-map-saved-unplotted title="These saved locations have no coordinate, so there is nothing to pin. Click to see which.">${savedCls.unplotted.length} can’t be placed — no coordinate</button>` : ''}
        </div>` : ''}
        ${keyRow('lastDispatched', 'Last dispatched', `border-radius:2px;background:${MAP_LAST_DISPATCHED_COLOR};`, layers.lastDispatched ? ldPlan.pins.length : null)}
        ${layers.lastDispatched ? `<div class="map-key-sub">
          <span class="map-key-note">Where each truck or trailer was last dispatched to — its job’s last destination. From the schedule, not GPS.${ldNotInWindow > 0 ? ` ${ldNotInWindow} unit${ldNotInWindow !== 1 ? 's have' : ' has'} no dispatch in the loaded history, so ${ldNotInWindow !== 1 ? 'they are' : 'it is'} not shown.` : ''}</span>
          ${ldIssues ? `<button type="button" class="map-key-chip" data-map-ld-issues title="Units whose last dispatch can't be placed on the map. Click to see which, and why.">${ldPlan.ambiguous.length ? `${ldPlan.ambiguous.length} with 2+ jobs that day` : ''}${ldPlan.ambiguous.length && ldPlan.unplotted.length ? ' · ' : ''}${ldPlan.unplotted.length ? `${ldPlan.unplotted.length} not plotted` : ''}</button>` : ''}
        </div>` : ''}
        ${keyRow('equipment', 'Equipment on site', `border-radius:3px;background:${MAP_EQUIPMENT_AGE_COLORS.fresh};`, eqSum ? eqSum.rowCountText : null)}
        ${eqPlan ? `<div class="map-key-sub">
          <span class="map-key-note">${eqPlan.unitTotal === 0 && DispatchState._mapEqTxnsState === 'loading'
            ? 'Loading the rental ledger…'
            : eqPlan.unitTotal === 0
              ? 'Nothing is out today by the schedule or the rental ledger.'
              : `${eqSum.noteText} — trucks and trailers on today’s jobs, plus rental lots still out. Badge = units | days on site. From the schedule and the ledger, not GPS.${eqPlan.deployments.some(d => d.basis === 'transactions') ? ' Older rental lots: returns matched to open lots by job, then customer, then oldest — see the list.' : ''}`}${DispatchState._mapEqTxnsState === 'failed' ? ' The rental ledger couldn’t be read for this login, so rental lots are missing.' : ''}</span>
          ${eqPlan.unitTotal ? `<span class="map-legend-item"><span class="map-legend-dot" style="background:${MAP_EQUIPMENT_AGE_COLORS.fresh};border-radius:2px;"></span>Under ${MAP_EQUIPMENT_FRESH_DAYS} days</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:${MAP_EQUIPMENT_AGE_COLORS.aging};border-radius:2px;"></span>${MAP_EQUIPMENT_FRESH_DAYS}–${MAP_EQUIPMENT_STALE_DAYS} days</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:${MAP_EQUIPMENT_AGE_COLORS.stale};border-radius:2px;"></span>Over ${MAP_EQUIPMENT_STALE_DAYS} days${eqStale ? ` (${eqStale} site${eqStale !== 1 ? 's' : ''})` : ''}</span>
          <span class="map-legend-item"><span class="map-legend-dot" style="background:${MAP_EQUIPMENT_AGE_COLORS.unknown};border-radius:2px;"></span>Date unknown</span>` : ''}
          <span style="display:flex;flex-wrap:wrap;gap:6px;">
            ${eqLoading ? `<span class="map-key-chip" data-map-eq-loading role="status" aria-live="polite"><span class="loading-spinner-small"></span> ${DispatchState._mapEqTxnsState === 'loading' ? 'Loading the rental ledger…' : 'Loading older jobs…'}</span>` : ''}
            ${eqPlan.unitTotal ? `<button type="button" class="map-key-chip" style="color:var(--brand-blue);" data-map-eq-list title="Every deployed unit, oldest on site first, with filters and the rental count check.">Open list</button>` : ''}
            ${eqUnplotted ? `<button type="button" class="map-key-chip" data-map-eq-unplotted title="Deployments that are counted in the total but have nothing to pin. Click to see which, and why.">${escapeHtml(eqSum.chipText)}</button>` : ''}
          </span>
        </div>` : ''}
        ${keyRow('estimated', 'Estimated locations', `border-radius:50%;background:#fff;border:2px dashed ${MAP_ESTIMATE_COLOR};box-sizing:border-box;`, estPlan ? estPlan.items.length : null)}
        ${estPlan ? _mapEstMod.keySubHtml(estPlan, { escapeHtml, color: MAP_ESTIMATE_COLOR, txnState: DispatchState._mapEqTxnsState, loading: estLoading })
          : layers.estimated ? `<div class="map-key-sub"><span class="map-key-note">${DispatchState._mapEstLoadFailed ? 'Estimated locations couldn’t load. Refresh to try again.' : 'Loading estimated locations…'}</span></div>` : ''}
        ${keyRow('fleet', 'Fleet (Samsara)', `clip-path:polygon(50% 0,100% 100%,50% 75%,0 100%);box-shadow:none;border:none;background:${MAP_FLEET_COLOR};`, '')}
        <div class="map-key-sub">
          <span class="map-key-note map-key-callout" id="map-fleet-status">${escapeHtml(_mapFleetStatusText(fleetInitial))}</span>
        </div>
      </div>
      <div class="map-key-section" data-map-drawn-layers><span class="map-key-label">Drawn layers</span><span class="map-key-note">Loading…</span></div>
    </details>`;

  // TASK-1616 — the range bar. Its own row above the stats header so the map
  // keeps its existing two-column layout; the header below therefore loses its
  // top radius/border to the bar. Styled inline against DESIGN.md tokens
  // (light background → --text-primary/--text-secondary, never --text-muted)
  // and reusing the existing .filter-chip component for the presets.
  // escapeHtml() is NOT attribute-safe (it skips quotes) — the date values land
  // inside value="", so they go through window.escAttr.
  const escA = window.escAttr || escapeHtml;
  const chipBase = 'min-height:44px;color:var(--text-secondary);';
  const activePreset = DispatchState.mapRangePreset || 'today';
  // TASK-9162 — the Custom chip's third state. Opening the editor does NOT
  // apply anything (the map still shows the preset until Apply), so it must not
  // take the filled .active look — but a plain gray chip sitting over its own
  // open panel reads as a dead button. Outline it in brand blue + a caret.
  const customOpen = !!DispatchState.mapShowCustom;
  const customOpenNotApplied = customOpen && activePreset !== 'custom';
  const presetChips = MAP_RANGE_PRESETS.map(p => `
    <button type="button" class="filter-chip${activePreset === p.id ? ' active' : ''}"
      style="${activePreset === p.id ? 'min-height:44px;' : chipBase}" aria-pressed="${activePreset === p.id ? 'true' : 'false'}"
      onclick="window.DispatchModule.setMapRangePreset('${escJsAttr(p.id)}')">${p.label}</button>`).join('');

  // COD r1 (Independent): every jobs/jobDays snapshot re-renders this view,
  // which would wipe a project search mid-keystroke. Carry the typed text,
  // focus and caret across the rebuild.
  const _prevSearch = document.getElementById('map-project-search');
  const _searchState = _prevSearch ? {
    value: _prevSearch.value,
    focused: document.activeElement === _prevSearch,
    start: _prevSearch.selectionStart, end: _prevSearch.selectionEnd,
  } : null;

  container.innerHTML = `
    <div class="dispatch-map-view">
      <div class="dispatch-map-range" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:8px 12px;background:var(--bg-card);border-bottom:1px solid var(--border-light);">
        <span class="map-range-showing">
          <span class="material-icons" style="font-size:16px;" aria-hidden="true">date_range</span> Showing
        </span>
        <span class="map-range-seg" role="group" aria-label="Date range shown on the map">
        ${presetChips}
        <button type="button" class="filter-chip${activePreset === 'custom' ? ' active' : ''}${customOpenNotApplied ? ' map-range-custom-open' : ''}"
          style="${activePreset === 'custom' ? 'min-height:44px;' : chipBase}display:inline-flex;align-items:center;gap:2px;"
          aria-pressed="${activePreset === 'custom' ? 'true' : 'false'}" aria-expanded="${customOpen ? 'true' : 'false'}"
          onclick="window.DispatchModule.toggleMapCustomRange()">Custom <span class="material-icons" aria-hidden="true" style="font-size:18px;">${customOpen ? 'expand_less' : 'expand_more'}</span></button>
        </span>
        ${projectMode
          ? `<span class="map-stat" style="font-size:0.8rem;color:var(--text-primary);"><span class="material-icons" style="font-size:16px;vertical-align:middle;color:${MAP_PROJECT_PIN_COLOR};">folder_special</span> Every date for project “${escapeHtml(activeProject.name || 'Untitled project')}” — pick a date above to leave it</span>`
          : `<span class="map-range-pill" data-map-range-label title="The dates the map is showing"><span class="material-icons" aria-hidden="true">event</span>${escapeHtml(mapRangeLabel(shownFrom, shownTo))}</span>`}
        ${projectMode ? '' : rangeClamped ? `<span class="map-stat map-stat-warn" title="You asked for ${escapeHtml(mapRangeLabel(range.from, range.to))}. Ranges are capped at ${MAP_RANGE_MAX_DAYS} days so the map doesn't drown in markers, so this is the most recent ${MAP_RANGE_MAX_DAYS} days of that." style="font-size:0.8rem;"><span class="material-icons" style="font-size:16px;vertical-align:middle;">info</span> newest ${MAP_RANGE_MAX_DAYS} days of your range</span>` : ''}
        ${!projectMode && rangeStartsBeforeWindow ? `<button type="button" class="map-stat map-stat-bad" onclick="window.DispatchModule.loadOlderDispatch()" title="Jobs older than the loaded window aren't in memory yet, so this range is showing less than you asked for. Click to load more history and repaint." style="font-size:0.8rem;"><span class="material-icons" style="font-size:16px;vertical-align:middle;">history</span> only loaded back to ${escapeHtml(mapRangeLabel(windowFloor, windowFloor))} — load older</button>` : ''}
        ${DispatchState.mapShowCustom ? `
          <span style="display:inline-flex;flex-wrap:wrap;align-items:center;gap:6px;font-size:0.8rem;color:var(--text-secondary);">
            <input type="date" id="map-range-from" class="control-auto" value="${escA(range.from)}" style="min-height:44px;font-size:16px;padding:4px 8px;border:1px solid var(--border-medium);border-radius:var(--radius-sm);background:var(--bg-card);color:var(--text-primary);" aria-label="Range start date">
            <span>to</span>
            <input type="date" id="map-range-to" class="control-auto" value="${escA(range.to)}" style="min-height:44px;font-size:16px;padding:4px 8px;border:1px solid var(--border-medium);border-radius:var(--radius-sm);background:var(--bg-card);color:var(--text-primary);" aria-label="Range end date">
            <button type="button" class="btn btn-sm btn-primary" style="min-height:44px;" onclick="window.DispatchModule.applyMapCustomRange()">Apply</button>
          </span>` : ''}
      </div>
      <div class="dispatch-map-header" style="border-radius:0;">
        <div class="map-stats">
          ${!layers.stops ? `<span class="map-stat"><span class="material-icons" style="font-size:16px;vertical-align:middle;">visibility_off</span> Job destinations hidden — turn them on in the map key</span>` : `
          <span class="map-stat"><span class="material-icons" style="font-size:16px;vertical-align:middle;">pin_drop</span> ${mappedJobs} job${mappedJobs !== 1 ? 's' : ''} on map</span><span data-map-cust-chip></span>
          ${stopMarkerCount > mappedJobs ? `<span class="map-stat" title="Every destination on every job gets its own pin. Multi-destination jobs are numbered in route order."><span class="material-icons" style="font-size:16px;vertical-align:middle;">route</span> ${stopMarkerCount} destinations</span>` : ''}
          ${unmappedJobs > 0 ? `<span class="map-stat map-stat-warn"><span class="material-icons" style="font-size:16px;vertical-align:middle;">warning</span> ${unmappedJobs} job${unmappedJobs !== 1 ? 's' : ''} without coordinates</span>` : ''}
          ${unlocatedStopCount > 0 ? `<button type="button" class="map-stat map-stat-warn" onclick="window.DispatchModule.showUnlocatedStops()" title="These destinations have never been geocoded, so there is nothing to pin. Click to see which jobs they belong to."><span class="material-icons" style="font-size:16px;vertical-align:middle;">location_off</span> ${unlocatedStopCount} destination${unlocatedStopCount !== 1 ? 's' : ''} not plotted</button>` : ''}
          ${badLocationCount > 0 ? `<button type="button" class="map-stat map-stat-bad" onclick="window.DispatchModule.showBadLocationJobs()" title="These destinations were left off the map because their stored coordinates are out of range. Click to see which."><span class="material-icons" style="font-size:16px;vertical-align:middle;">wrong_location</span> ${badLocationCount} with a bad location</button>` : ''}`}
          ${layers.stops && (routePendingCount > 0 || routeTally.failed > 0) ? `<span class="map-stat" id="map-route-status" role="status"${_routeWarmBusy ? ' data-busy' : ''}
            title="${canWarmRoutes ? 'Road routes are looked up automatically, a few jobs at a time, once per job — then remembered on the job.' : 'Road routes are looked up when someone who can edit jobs opens this map or saves the job.'}">
            <span class="btn-spinner" data-route-spin style="margin-right:6px;vertical-align:middle;${canWarmRoutes && routePendingCount > 0 ? '' : 'display:none;'}"></span>${routePendingCount > 0
              ? (canWarmRoutes ? `Looking up ${routePendingCount} road route${routePendingCount !== 1 ? 's' : ''}…` : `${routePendingCount} road route${routePendingCount !== 1 ? 's' : ''} not looked up yet`)
              : ''}${routePendingCount > 0 && routeTally.failed > 0 ? ' · ' : ''}${routeTally.failed > 0 ? `${routeTally.failed} lookup${routeTally.failed !== 1 ? 's' : ''} failed, retrying` : ''}
          </span>` : ''}
        </div>
      </div>
      <div class="dispatch-map-body">
        <div id="dispatch-map-container"></div>
        ${keyHtml}
      </div>
    </div>
  `;

  const mapEl = document.getElementById('dispatch-map-container');
  if (!mapEl) return;

  if (_searchState && (_searchState.value || _searchState.focused)) {
    const s = document.getElementById('map-project-search');
    if (s) {
      s.value = _searchState.value;
      if (_searchState.value) filterMapProjects(_searchState.value);
      if (_searchState.focused) {
        s.focus();
        try { s.setSelectionRange(_searchState.start, _searchState.end); } catch (_) { /* type=search may refuse */ }
      }
    }
  }

  // TASK-1613 — the carrier-assistant FAB (#dd-assistant-fab: position fixed,
  // bottom 24px / right 24px, 56x56, z-index 2200) sits exactly on top of
  // Google's default bottom-right "Map camera controls" toggle (40x40) — total
  // occlusion, and that toggle is the ONLY zoom/pan cluster on this map
  // (streetViewControl is off, mapTypeControl and fullscreenControl are both
  // top-right). With it covered the dispatch map can only be zoomed by scroll
  // wheel or pinch.
  //
  // Move OUR control rather than hide a shipped feature or offset Google-owned
  // DOM from CSS (that breaks the next time Google renames a class). RIGHT_CENTER
  // is expressed as a fraction of the map height, so it survives a resize and the
  // ≤768px .dispatch-map-view height change for free, and it never approaches the
  // bottom-right corner the FAB owns on any viewport.
  //
  // Read defensively: an older Maps JS build without RIGHT_CENTER would otherwise
  // pass `undefined` as a position and silently drop the control entirely, which
  // is the exact bug we are fixing.
  const mapControlSidePos = google.maps.ControlPosition?.RIGHT_CENTER;
  const sideControlOptions = typeof mapControlSidePos === 'number' ? {
    cameraControlOptions: { position: mapControlSidePos },
    zoomControlOptions: { position: mapControlSidePos }
  } : {};

  // TASK-9631 F4 — every jobs snapshot rebuilds this map from scratch and
  // used to fitBounds again, so a dispatcher who had panned or zoomed was
  // thrown back to the full extent whenever ANY job changed. Keep their view
  // when nothing THEY chose changed (range, layers, project); refit when it did.
  const _viewKey = mapViewportKey({ from: range.from, to: range.to, layers, projectId });
  // TASK-9631 S3 — a saved map just opened brings its own view (taken once).
  const _openedView = DispatchState._mapViewPending;
  DispatchState._mapViewPending = null;
  const _keepView = (_openedView && plottableCoord(_openedView.center) && Number.isFinite(Number(_openedView.zoom))
    ? { center: plottableCoord(_openedView.center), zoom: Number(_openedView.zoom) } : null)
    || mapViewportToRestore(DispatchState._mapViewport, _viewKey);
  const map = new google.maps.Map(mapEl, {
    zoom: _keepView ? _keepView.zoom : MAP_DEFAULT_ZOOM,
    center: _keepView ? _keepView.center : MAP_DEFAULT_CENTER,
    ...sideControlOptions,
    mapTypeControl: true,
    mapTypeControlOptions: {
      style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
      position: google.maps.ControlPosition.TOP_RIGHT
    },
    streetViewControl: false,
    fullscreenControl: true,
    styles: [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] }
    ]
  });
  DispatchState.mapInstance = map;
  const _settleViewport = _trackMapViewport(map, _viewKey, _keepView);

  // Clear old markers
  DispatchState.mapMarkers.forEach(m => m.setMap(null));
  DispatchState.mapMarkers = [];
  _samsaraMarkers.forEach(m => m.setMap(null));
  _samsaraMarkers = [];
  _savedLocMarkers.forEach(m => m.setMap(null));
  _savedLocMarkers = [];
  _savedLocMarkerById.clear();   // TASK-9631 S2
  _lastDispatchedMarkers.forEach(m => m.setMap(null));
  _lastDispatchedMarkers = [];
  _projectPinMarkers.forEach(m => m.setMap(null));
  _projectPinMarkers = [];
  _equipmentMarkers.forEach(m => m.setMap(null));
  _equipmentMarkers = [];
  _estimatedMarkers.forEach(m => m.setMap(null));
  _estimatedMarkers = [];
  (DispatchState.pingMarkers || []).forEach(m => m.setMap(null));
  DispatchState.pingMarkers = [];

  const bounds = new google.maps.LatLngBounds();
  const infoWindow = new google.maps.InfoWindow();
  let hasBounds = false;

  // Add job markers
  //
  // TASK-4786 — ONE PIN PER STOP, not one pin per job. This loop used to build a
  // single marker at `markerData.lat/lng` (waypoint[0]) and then draw a polyline
  // through every waypoint, so stops 2..n were line vertices with nothing on
  // them. Measured on live TITS 2026-09-08: 276 of 697 plottable stops, 39.6%.
  // Worst case after the change is 164 markers (the busiest 30-day window in all
  // of TITS history, and MAP_RANGE_MAX_DAYS caps the span at 30 days) — Google
  // Maps renders that without clustering. If the built array ever exceeds
  // MAP_CLUSTER_THRESHOLD this switches to a warning, not a silent slideshow.
  // TASK-9175 — the Stops layer can be switched off in the key.
  (layers.stops ? jobMarkers : []).forEach(markerData => {
    const statusTone = MAP_STATUS_COLORS[markerData.status] || MAP_STATUS_COLORS.default;
    // TASK-4786 A4 — pins + route lines take the customer colour in that mode;
    // the info window keeps the real status colour (statusTone).
    const statusColor = custColors ? _mapCustMod.pinColor(markerData.customerKey, getCustomerColor, DispatchState.customers) : statusTone;
    const stops = markerData.waypoints;
    // 🔴 COD Code Reviewer, 2026-09-09: this was `stops.length > 1`, i.e. the
    // count of PLOTTABLE stops. A job shaped [blank, good] has one plottable
    // stop, so it drew an unnumbered lone pin and no "STOP 2 OF 2" badge — the
    // dispatcher reads it as a one-stop job when it is the SECOND stop of two.
    // The route length is what makes a job multi-stop, not how many of its
    // stops we happened to be able to draw.
    const isMultiStop = markerData.totalStops > 1;
    // Stops the map could not draw at all — split, because they have different
    // fixes: a blank one needs an address, a bad one needs a CORRECTED address.
    const undrawnStops = markerData.unlocatedStops + markerData.badStops;

    stops.forEach((stop, idx) => {
      const isPrimary = idx === 0;
      const marker = new google.maps.Marker({
        position: { lat: stop.lat, lng: stop.lng },
        map: map,
        // The hover tooltip has to name the STOP now, or every pin on a
        // multi-stop job claims to be the same thing.
        // TASK-9175 — in the project view a stop is named by JOB NUMBER + stop,
        // because one project's pins come from many jobs.
        title: projectMode
          ? `${markerData.jobNumber != null ? `#${markerData.jobNumber} · ` : ''}${stop.name || stop.address || 'Unnamed destination'}${isMultiStop ? ` (destination ${stop.stopNumber} of ${markerData.totalStops})` : ''}`
          : isMultiStop
            ? `${markerData.title} — destination ${stop.stopNumber}: ${stop.name || stop.address || 'Unnamed destination'}`
            : markerData.title,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: statusColor,
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
          // Stop 1 keeps the size it has always had, so a single-stop job is
          // pixel-identical to before. Later stops are smaller so the first
          // stop still reads as the head of the route.
          scale: isPrimary ? 10 : 8
        },
        // Numbers only on multi-stop jobs — a lone pin with a "1" on it is
        // noise. White on a saturated status colour clears contrast on all five.
        label: isMultiStop ? {
          text: String(stop.stopNumber),
          color: '#fff',
          fontSize: isPrimary ? '11px' : '10px',
          fontWeight: '700'
        } : undefined,
        // Stop 1 stays above the rest so overlapping pins at a yard still let
        // you click the job's first stop.
        zIndex: isPrimary ? 10 : 9
      });

      marker.addListener('click', () => {
        const driversStr = markerData.drivers.length > 0 ? markerData.drivers.join(', ') : 'Unassigned';
        const trucksStr = markerData.trucks.length > 0 ? markerData.trucks.join(', ') : 'None';
        const stopLabel = stop.name || stop.address || markerData.location || '';
        // TASK-9540 — the category the stop was picked as: a badge here, never
        // the marker fill (status colors own the fill). Unknown/deleted id = no badge.
        const stopCat = typeof stop.categoryId === 'string' && stop.categoryId ? placeCats.find(c => c.id === stop.categoryId) : null;

        infoWindow.setContent(`
          <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:200px;max-width:300px;">
            ${isMultiStop ? `<div style="display:inline-block;background:${statusTone};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;margin-bottom:6px;">DESTINATION ${stop.stopNumber} OF ${markerData.totalStops}</div>` : ''}
            <div style="font-weight:700;font-size:15px;margin-bottom:6px;color:#0f172a;">${markerData.jobNumber != null ? `<span style="color:#475569;">#${escapeHtml(String(markerData.jobNumber))}</span> ` : ''}${escapeHtml(markerData.title)}</div>
            ${markerData.customerName ? `<div style="font-size:13px;color:#64748b;margin-bottom:8px;">${escapeHtml(markerData.customerName)}</div>` : ''}
            <div style="font-size:13px;color:#475569;margin-bottom:4px;">
              <strong>${isMultiStop ? `Destination ${stop.stopNumber}` : 'Location'}:</strong> ${escapeHtml(stopLabel)}
            </div>
            ${stopCat ? `<div style="display:flex;align-items:center;gap:6px;font-size:12px;color:#475569;margin-bottom:4px;" data-map-stop-cat="${escA(stopCat.id)}">${placeShapeSvg(stopCat.shape, stopCat.color, 14)} ${escapeHtml(stopCat.name)}</div>` : ''}
            <div style="font-size:13px;color:#475569;margin-bottom:4px;">
              <strong>Status:</strong> <span style="color:${statusTone};font-weight:600;">${escapeHtml(markerData.status)}</span>
            </div>
            <div style="font-size:13px;color:#475569;margin-bottom:4px;">
              <strong>Driver${markerData.drivers.length !== 1 ? 's' : ''}:</strong> ${escapeHtml(driversStr)}
            </div>
            <div style="font-size:13px;color:#475569;margin-bottom:8px;">
              <strong>Truck${markerData.trucks.length !== 1 ? 's' : ''}:</strong> ${escapeHtml(trucksStr)}
            </div>
            ${isMultiStop && stops.length >= 2 ? `<div style="font-size:12px;color:#475569;margin-bottom:8px;" data-map-route-state="${escA(markerData.routeState.state)}">
              <strong>Route:</strong> ${escapeHtml(mapRouteStateText(markerData.routeState.state, markerData.cachedRoute, markerData.permitRequired))}
            </div>` : ''}
            ${undrawnStops > 0 ? `<div style="font-size:12px;color:#B45309;background:#FEF3C7;border-radius:6px;padding:6px 8px;margin-bottom:8px;">
              ${undrawnStops} more destination${undrawnStops !== 1 ? 's' : ''} on this job ${undrawnStops !== 1 ? 'are' : 'is'} not on the map${markerData.badStops > 0 && markerData.unlocatedStops > 0
                ? ` (${markerData.unlocatedStops} with no address yet, ${markerData.badStops} with a bad location)`
                : markerData.badStops > 0 ? ' — the stored location is out of range' : ' — no coordinates yet'}.
            </div>` : ''}
            ${projectMode && markerData.firstDay ? `<div style="font-size:13px;color:#475569;margin-bottom:8px;"><strong>Date:</strong> ${escapeHtml(mapRangeLabel(markerData.firstDay, markerData.firstDay))}</div>` : ''}
            ${!projectMode && rangeDays.length > 1 ? `<div style="font-size:13px;color:#475569;margin-bottom:8px;">
              <strong>In range:</strong> ${markerData.dayCount} day${markerData.dayCount !== 1 ? 's' : ''}${markerData.firstDay ? ` (${escapeHtml(mapRangeLabel(markerData.firstDay, markerData.lastDay))})` : ''}
            </div>` : ''}
            <div style="text-align:center;">
              <button type="button" data-map-job="${escA(markerData.jobId)}" data-map-jobday="${escA(markerData.jobDayId)}"
                style="background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;">
                View Job Details
              </button>
            </div>
            ${_mapDrawMod?.stopCardHtml?.(markerData.jobId, stop.id) || ''}
          </div>
        `);
        infoWindow.open(map, marker);
      });

      DispatchState.mapMarkers.push(marker);
      // TASK-4786 — bounds now cover EVERY stop. Before, fitBounds only saw
      // waypoint[0], so a multi-stop job's polyline could run clean off the
      // viewport with its far end never brought into view.
      bounds.extend({ lat: stop.lat, lng: stop.lng });
      hasBounds = true;
    });

    // ── TASK-9174 — THE LINE BETWEEN A JOB'S STOPS ────────────────────────────
    // This used to be ONE rendering for three different situations: a geodesic
    // polyline straight through the raw stop coordinates, dressed in the job's
    // status colour at 60% opacity. Bozeman → Williston came out as a single
    // diagonal across Montana that a dispatcher could not read miles, hours or
    // highway off — decoration that looks like information.
    //
    // Now there are three renderings for three meanings, and the map key spells
    // all three out. They differ in SHAPE (solid / long dashes / dots), not just
    // in opacity, because a shape difference survives a small screen, a colour
    // shift from the job's status, and a colour-blind dispatcher.
    //
    // 🔴 NOTHING HERE FETCHES. `decodeCachedRoutePath` reads what a save already
    // cached on the job document. This callback runs once per job on every
    // repaint, and a repaint happens on every range preset, every custom Apply
    // and every jobs/jobDays snapshot settle.
    if (isMultiStop && stops.length >= 2) {
      const roadPath = decodeCachedRoutePath(markerData.cachedRoute);
      let routePath;

      if (roadPath && markerData.permitRequired) {
        // 🟢 CHRIS DECIDED THIS 2026-09-11. A permitted load never gets a
        // confident line, EVEN WHEN the road route is available. On a permitted
        // load the STATE has already specified the route by road and mile
        // marker; a confident line down a route the truck is not legally
        // allowed to take is a worse defect than the straight line this task
        // exists to remove. DASHED — a different shape from the dots below, so
        // "permit-restricted" never reads as merely "proposed".
        routePath = new google.maps.Polyline({
          path: roadPath,
          strokeColor: statusColor,
          strokeOpacity: 0,                 // the dashes ARE the line
          strokeWeight: 4,
          icons: [{
            icon: { path: 'M 0,-1 0,1', strokeColor: statusColor, strokeOpacity: 0.55, strokeWeight: 4, scale: 3 },
            offset: '0',
            repeat: '18px'
          }],
          zIndex: 1
        });
      } else if (roadPath) {
        // 🔴 CHRIS CORRECTED THIS 2026-09-15, and the correction is the whole
        // point of the style: "its supposed to have a semi decent proposed
        // route that is dotted, and the dots are to identify 'proposed -
        // unknown actual route'".
        //
        // The first cut had this backwards — it drew the road route SOLID and
        // reserved dots for the FAILURE case. That made a dotted line mean "we
        // could not answer", and made a road-following line look like a record
        // of where the truck actually went. DispatchDeck has no GPS breadcrumb
        // for these jobs (confirmed with Chris: there is no actual-route source),
        // so EVERY line on this map is a proposal and none of them should look
        // authoritative.
        //
        // So: the road-following route is DOTTED. The dots are not an apology
        // for missing data — they are the honest claim "this is the route we
        // think he takes, not the one he drove." It follows real roads, so it
        // is genuinely useful for reading miles and highways; the dots stop it
        // being mistaken for history.
        routePath = new google.maps.Polyline({
          path: roadPath,
          geodesic: false,
          strokeColor: statusColor,
          strokeOpacity: 0,                 // the dots ARE the line
          strokeWeight: 4,
          icons: [{
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: statusColor, fillOpacity: 0.9,
              strokeColor: statusColor, strokeOpacity: 0.9, strokeWeight: 1,
              scale: 2.4
            },
            offset: '0',
            repeat: '9px'
          }],
          zIndex: 2
        });
      }
      // TASK-9734 — NO road answer draws NOTHING. TASK-9174 kept a faint, sparse
      // crow-flies dot line here ("No route found — stops only"); Chris read it
      // on show-account as a bad guessed route (2026-10-08), because a line that
      // ignores roads still looks like a route. The numbered pins already give
      // the stop order, and the map key + this job's info window say WHICH of
      // the three non-answers it is (not looked up yet / lookup failed / no road).
      // 🔴 Never restore a straight line between stops, faint or solid.
      if (routePath) routePath.setMap(map);
    }
  });

  // TASK-9734 — hand the due jobs to the paced background lookup. Debounced and
  // rate-paced inside scheduleMapRouteWarm; this line never fetches.
  if (canWarmRoutes) {
    scheduleMapRouteWarm();
    // COD — a failure's backoff ending, or another session's lease lapsing,
    // changes nothing on screen, so no paint would ever re-queue it. Wake once
    // at the earliest such moment (only while this map stays open).
    const wakes = jobMarkers.map(m => m.routeState.wakeAt).filter(t => Number.isFinite(t));
    scheduleMapRouteWake(wakes.length ? Math.min(...wakes) : null);
  }

  // 🔴 COD Code Reviewer, 2026-09-09 — the info window's "View Job Details" used
  // to interpolate the ids into an inline `onclick` string. escAttr is NOT enough
  // there: the browser HTML-decodes an attribute BEFORE parsing it as JavaScript,
  // so an id carrying a quote breaks out of the string literal. Same delegated
  // data-attribute pattern showBadLocationJobs already uses. Bound on the map
  // element, which the info window renders inside; container.innerHTML above
  // creates a fresh #dispatch-map-container each render, so this never stacks.
  mapEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-map-job]');
    if (!btn) return;
    window.DispatchModule.showJobDetails(btn.dataset.mapJob, btn.dataset.mapJobday || null);
  });

  // ── TASK-9175 — SAVED WAYPOINTS layer ──────────────────────────────────────
  // 🔴 XSS: `savedLocations` is writable by the owner or any dispatch-module
  // member (TASK-9631 F1 / TASK-9540 narrowed it from any member) — still with no server
  // sanitisation, so its content stays untrusted. Every name and url below goes through escapeHtml, and
  // the only link is an http(s) href through escAttr. Nothing lands in an
  // inline onclick.
  const escHref = (u) => (/^https?:\/\//i.test(String(u || '').trim()) ? escA(String(u).trim()) : '');
  if (layers.saved) {
    // TASK-9540 — shape + color come from the category ALLOWLISTS only
    // (categoryForPlace -> mergePlaceCategories re-checks both), never from
    // a stored string, so nothing free-text reaches the marker symbol.
    const canEditPins = _canEditPlaces(true);   // TASK-9631 S2 — Move / Delete on the card
    savedShown.forEach(loc => {
      const cat = categoryForPlace(loc, placeCats);
      const marker = new google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map,
        title: `Saved (${cat.name}): ${loc.name || 'Unnamed location'}`,
        icon: {
          path: (PLACE_SHAPES[cat.shape] || PLACE_SHAPES.diamond).path,
          fillColor: cat.color, fillOpacity: 0.95,
          strokeColor: '#fff', strokeWeight: 2,
          scale: 9
        },
        zIndex: 6
      });
      marker.addListener('click', () => {
        const href = escHref(loc.url);
        infoWindow.setContent(`
          <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:180px;max-width:280px;">
            <div style="display:inline-flex;align-items:center;gap:6px;background:${cat.color};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;margin-bottom:6px;" data-map-saved-cat-badge="${escA(cat.id)}">${placeShapeSvg(cat.shape, cat.color, 12)}SAVED · ${escapeHtml(cat.name.toUpperCase())}</div>
            <div style="font-weight:700;font-size:15px;margin-bottom:6px;color:#0f172a;">${escapeHtml(loc.name || 'Unnamed location')}</div>
            ${href ? `<a href="${href}" target="_blank" rel="noopener noreferrer" style="font-size:13px;color:#1D4ED8;">Open in Maps</a>`
              : loc.url ? `<div style="font-size:13px;color:#475569;">${escapeHtml(loc.url)}</div>` : ''}
            ${canEditPins ? `<div class="mpn-ia"><button type="button" class="mpn-ib" data-map-pin-move="${escA(loc.id)}">Move pin</button><button type="button" class="mpn-ib mpn-ib-d" data-map-pin-delete="${escA(loc.id)}">Delete</button></div>` : ''}
            ${DispatchState.mapViewActive ? `<div class="mvw-ia"><button type="button" class="mvw-ib" data-map-view-drop="${escA(loc.id)}">Leave off this map</button></div>` : ''}
          </div>`);
        infoWindow.open(map, marker);
      });
      _savedLocMarkerById.set(loc.id, marker);   // TASK-9631 S2
      _savedLocMarkers.push(marker);
      bounds.extend({ lat: loc.lat, lng: loc.lng });
      hasBounds = true;
    });
  }

  // ── TASK-9175 — LAST DISPATCHED layer ──────────────────────────────────────
  // A navy square with the unit number on it, at the chosen job's last stop.
  // The info window says in words what the pin is: the dispatch record, not a
  // position — and WHY this job was chosen when the unit had more than one.
  if (layers.lastDispatched) {
    const reasonText = {
      only: 'Its only job that day.',
      clock: 'It had more than one job that day; this one has the latest clock punch.',
      status: 'It had more than one job that day and no clock punch decided it; this one is furthest along by status.'
    };
    ldPlan.pins.forEach(pin => {
      const unitText = String(pin.unitLabel || '').slice(0, 4);
      const marker = new google.maps.Marker({
        position: { lat: pin.lat, lng: pin.lng },
        map,
        title: `${pin.kind === 'trailer' ? 'Trailer' : 'Truck'} ${pin.unitLabel} — last dispatched ${mapRangeLabel(pin.dateISO, pin.dateISO)}`,
        icon: {
          path: 'M -1.2,-1 1.2,-1 1.2,1 -1.2,1 z',
          fillColor: MAP_LAST_DISPATCHED_COLOR, fillOpacity: 0.95,
          strokeColor: '#fff', strokeWeight: 2,
          scale: 11
        },
        label: { text: unitText, color: '#fff', fontSize: '10px', fontWeight: '700' },
        zIndex: 8
      });
      marker.addListener('click', () => {
        infoWindow.setContent(`
          <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:200px;max-width:300px;">
            <div style="display:inline-block;background:${MAP_LAST_DISPATCHED_COLOR};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;margin-bottom:6px;">LAST DISPATCHED</div>
            <div style="font-weight:700;font-size:15px;margin-bottom:6px;color:#0f172a;">${pin.kind === 'trailer' ? 'Trailer' : 'Truck'} ${escapeHtml(pin.unitLabel)}</div>
            <div style="font-size:13px;color:#475569;margin-bottom:4px;"><strong>Dispatched:</strong> ${escapeHtml(mapRangeLabel(pin.dateISO, pin.dateISO))}</div>
            <div style="font-size:13px;color:#475569;margin-bottom:4px;"><strong>Job:</strong> ${pin.jobNumber != null ? `#${escapeHtml(String(pin.jobNumber))} ` : ''}${escapeHtml(pin.title)}</div>
            <div style="font-size:13px;color:#475569;margin-bottom:4px;"><strong>Pinned at:</strong> ${escapeHtml(pin.stopName || 'Unnamed destination')}${pin.totalStops > 1 ? ` (destination ${pin.stopNumber} of ${pin.totalStops})` : ''}</div>
            <div style="font-size:12px;color:#475569;margin-bottom:8px;">${escapeHtml(reasonText[pin.reason] || '')}</div>
            <div style="font-size:12px;color:#92400E;background:#FEF3C7;border-radius:6px;padding:6px 8px;margin-bottom:8px;">From the schedule, not GPS — the truck may not have rolled, or may have been swapped.</div>
            <div style="text-align:center;">
              <button type="button" data-map-job="${escA(pin.jobId)}" data-map-jobday="${escA(pin.jobDayId || '')}"
                style="background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;min-height:36px;">
                View Job Details
              </button>
            </div>
          </div>`);
        infoWindow.open(map, marker);
      });
      _lastDispatchedMarkers.push(marker);
      bounds.extend({ lat: pin.lat, lng: pin.lng });
      hasBounds = true;
    });
  }

  // ── TASK-9175 — the project's own pin (appears once TASK-4842 geocodes
  // project addresses; today every TITS project has a blank location).
  if (projectMode && projectPinCoord && layers.stops) {
    const marker = new google.maps.Marker({
      position: projectPinCoord,
      map,
      title: `Project: ${activeProject.name || 'Untitled project'}`,
      icon: {
        path: 'M -1,-1 1,-1 1,1 -1,1 z',
        fillColor: MAP_PROJECT_PIN_COLOR, fillOpacity: 0.95,
        strokeColor: '#fff', strokeWeight: 2, scale: 8, rotation: 45
      },
      zIndex: 7
    });
    marker.addListener('click', () => {
      infoWindow.setContent(`
        <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:180px;max-width:280px;">
          <div style="display:inline-block;background:${MAP_PROJECT_PIN_COLOR};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;margin-bottom:6px;">PROJECT</div>
          <div style="font-weight:700;font-size:15px;margin-bottom:4px;color:#0f172a;">${escapeHtml(activeProject.name || 'Untitled project')}</div>
          ${activeProject.location ? `<div style="font-size:13px;color:#475569;">${escapeHtml(activeProject.location)}</div>` : ''}
        </div>`);
      infoWindow.open(map, marker);
    });
    _projectPinMarkers.push(marker);
    bounds.extend(projectPinCoord);
    hasBounds = true;
  }

  // ── TASK-4800 — EQUIPMENT ON SITE layer ────────────────────────────────────
  // ONE marker per SITE (job), a badge reading `units | days`, coloured by the
  // age of the oldest lot there — a red badge with a big number is the
  // stranded-inventory signal. The marker shows the deployed COUNT, never
  // availability (TASK-4810: a negative availability must not reach a pin).
  // Every string is tenant-written and goes through escapeHtml/escAttr; the
  // only handler is the delegated data-map-job button.
  if (layers.equipment && eqPlan) {
    // TASK-9508 — oldest added LAST so a stale red site paints over a newer one.
    _mapEquipmentPaintOrder(eqPlan.sites).forEach(site => {
      const color = MAP_EQUIPMENT_AGE_COLORS[site.ageBucket] || MAP_EQUIPMENT_AGE_COLORS.unknown;
      const daysText = site.ageDays == null ? '?' : `${site.ageDays}d`;
      const badge = `${site.units} | ${daysText}`;
      const marker = new google.maps.Marker({
        position: { lat: site.lat, lng: site.lng },
        map,
        title: `${site.units} unit${site.units !== 1 ? 's' : ''} at ${site.title}${site.ageDays != null ? ` — ${site.ageDays} day${site.ageDays !== 1 ? 's' : ''} on site` : ''}`,
        icon: {
          path: 'M -2.2,-1 2.2,-1 2.2,1 -2.2,1 z',
          fillColor: color, fillOpacity: 0.95,
          strokeColor: '#fff', strokeWeight: 2,
          scale: 12
        },
        label: { text: badge.slice(0, 12), color: '#fff', fontSize: '10px', fontWeight: '700' },
        zIndex: 9
      });
      marker.addListener('click', () => {
        const items = site.items.slice().sort((a, b) => (a.sinceKnown ? a.sinceMs : Infinity) - (b.sinceKnown ? b.sinceMs : Infinity));
        infoWindow.setContent(`
          <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:220px;max-width:320px;">
            <div style="display:inline-block;background:${color};color:#fff;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;margin-bottom:6px;">ON SITE PER RECORDS — NOT GPS${site.ageDays != null ? ` · ${site.ageDays} DAY${site.ageDays !== 1 ? 'S' : ''}` : ''}</div>
            <div style="font-weight:700;font-size:15px;margin-bottom:2px;color:#0f172a;">${site.jobNumber != null ? `#${escapeHtml(String(site.jobNumber))} ` : ''}${escapeHtml(site.title)}</div>
            ${site.customer ? `<div style="font-size:13px;color:#475569;margin-bottom:4px;">${escapeHtml(site.customer)}</div>` : ''}
            <div style="font-size:12px;color:#475569;margin-bottom:6px;">At ${escapeHtml(site.locName || 'the job site')}${site.locSource === 'project' ? ' (project location)' : ''} · ${_mapEquipmentUnitsText(site.items)}</div>
            ${items.map(d => `<div style="font-size:13px;color:#0f172a;margin-bottom:3px;"><strong>${escapeHtml(_mapEquipmentKindLabel(d.kind))} ${escapeHtml(d.label)}</strong>${d.kind === 'rental' ? ` × ${d.qty}` : ''}${d.pending ? ` <span style="color:#92400E;">(${d.pending} awaiting return inspection)</span>` : ''}${d.otherJobsToday ? ` <span style="color:#475569;">(also on ${d.otherJobsToday} other job${d.otherJobsToday !== 1 ? 's' : ''} today)</span>` : ''}<div style="font-size:12px;color:${MAP_EQUIPMENT_AGE_COLORS[d.ageBucket] || MAP_EQUIPMENT_AGE_COLORS.unknown};">${escapeHtml(_mapEquipmentSinceText(d))}</div></div>`).join('')}
            <div style="font-size:12px;color:#92400E;background:#FEF3C7;border-radius:6px;padding:6px 8px;margin:8px 0;">From the schedule and the rental ledger, not GPS — a spot count is what confirms it.${site.items.some(d => d.basis === 'transactions') ? ' The split between sites is estimated from the deploy/return records (the item total is not).' : ''}</div>
            <div style="text-align:center;">
              <button type="button" data-map-job="${escA(site.jobId)}" data-map-jobday="${escA((site.items.find(d => d.jobDayId) || {}).jobDayId || '')}"
                style="background:linear-gradient(135deg,#3B82F6,#06B6D4);color:#fff;border:none;padding:8px 16px;border-radius:6px;cursor:pointer;font-size:13px;font-weight:600;min-height:36px;">
                View Job Details
              </button>
            </div>
          </div>`);
        infoWindow.open(map, marker);
      });
      _equipmentMarkers.push(marker);
      bounds.extend({ lat: site.lat, lng: site.lng });
      hasBounds = true;
    });
  }

  // ── TASK-4800 S4 — ESTIMATED LOCATIONS layer ──────────────────────────────
  // Painted by the lazy chunk (mapEstimated.js paintMarkers): a HOLLOW circle
  // with a "~N" label under Equipment's solid squares (zIndex 8); the native
  // tooltip says which record each guess came from; every string escaped.
  if (layers.estimated && estPlan && _mapEstMod) {
    const painted = _mapEstMod.paintMarkers({ google, map, infoWindow, plan: estPlan, escapeHtml, escA,
      fmtDay: (iso) => mapRangeLabel(iso, iso), color: MAP_ESTIMATE_COLOR });
    painted.forEach(m => _estimatedMarkers.push(m));
    estPlan.sites.forEach(site => bounds.extend({ lat: site.lat, lng: site.lng }));
    if (estPlan.sites.length) hasBounds = true;
  }

  // ── TASK-9175 — the key panel's controls. One delegated listener on the
  // aside; the whole view is rebuilt by innerHTML each render, so it never
  // stacks. Ids reach handlers through data-* (escAttr), never inline JS.
  const keyEl = container.querySelector('.dispatch-map-key');
  if (keyEl) {
    keyEl.addEventListener('toggle', () => { DispatchState.mapKeyOpen = keyEl.open; });
    keyEl.addEventListener('click', (e) => {
      const layerBtn = e.target.closest('[data-map-layer]');
      if (layerBtn) { toggleMapLayer(layerBtn.dataset.mapLayer); return; }
      const hit = e.target.closest('[data-map-project]');
      if (hit) { setMapProject(hit.dataset.mapProject); return; }
      if (e.target.closest('[data-map-project-clear]')) { clearMapProject(); return; }
      if (e.target.closest('[data-map-saved-unplotted]')) { showUnplottedSavedLocations(); return; }
      if (e.target.closest('[data-map-saved-manage]')) { openPlacesManager('map'); return; }   // TASK-9540
      const catChip = e.target.closest('[data-map-saved-cat]');
      if (catChip) { toggleMapSavedCategory(catChip.dataset.mapSavedCat); return; }
      if (e.target.closest('[data-map-ld-issues]')) { showLastDispatchedIssues(); return; }
      if (e.target.closest('[data-map-eq-list]')) { showEquipmentList(); return; }        // TASK-4800
      if (e.target.closest('[data-map-eq-unplotted]')) { showEquipmentUnplotted(); return; }
      if (e.target.closest('[data-map-est-list]')) { showEstimatedList(); return; }       // TASK-4800 S4
    });
  }
  // TASK-4789 — drawn layers paint onto this map and fill their key section
  // (the section handles its own clicks).
  _attachDrawnLayers(map, container);
  // TASK-9631 S2 — "+ Pin" control + Move / Delete on saved-place cards.
  _attachMapPins(map, infoWindow, layers.saved);
  // TASK-9631 S3 — "Saved maps" control (open / save / rename / delete).
  _attachMapViews(map, infoWindow);
  _attachMapCustomers(container, rangeJobs, map);

  // TASK-4786 — a threshold, not a prerequisite. Measured worst case on TITS is
  // 164; this only fires if a busier tenant blows past what plain markers do
  // comfortably, and it says so in the console rather than quietly dropping
  // frames. Clustering is the fix if this ever prints.
  // TASK-9175 — the layers share the one budget: it is markers on screen that
  // cost frames, whichever layer drew them.
  const _markersOnScreen = DispatchState.mapMarkers.length + _savedLocMarkers.length + _lastDispatchedMarkers.length + _equipmentMarkers.length + _estimatedMarkers.length;
  if (_markersOnScreen > MAP_CLUSTER_THRESHOLD) {
    console.warn(`[dispatch map] ${_markersOnScreen} markers on screen (threshold ${MAP_CLUSTER_THRESHOLD}) — this range may feel slow. Narrow the range or turn a layer off; clustering is now worth building.`);
  }

  // Samsara fleet overlay — real-time listener.
  // TASK-9175 — subscribed only while the Fleet row is ON, and the key row says
  // what is actually true (not connected / nothing arriving / stale / live)
  // instead of a purple legend dot on a layer that could not draw. Reads
  // tenants/{tid}/live/vehicleLocations, which firestore.rules had NO match for
  // until this task (default-deny for every client, every tenant).
  if (_samsaraUnsubscribe) { _samsaraUnsubscribe(); _samsaraUnsubscribe = null; }
  const setFleetStatus = (fs) => {
    const el = document.getElementById('map-fleet-status');
    if (el) el.textContent = _mapFleetStatusText(fs);
    const row = document.querySelector('.map-key-row[data-map-layer="fleet"] .map-key-count');
    if (row) row.textContent = fs.plotted.length ? String(fs.plotted.length) : '';
  };
  if (layers.fleet && hasSamsara && window.AppState?.tenant) {
    const liveDocRef = doc(db, 'tenants', window.AppState.tenant, 'live', 'vehicleLocations');
    // COD r1 (Code Reviewer): freshness is a function of the CLOCK, not only of
    // snapshots — if Samsara stops sending, no snapshot arrives to say so. The
    // last answer is re-judged every minute by the timer below.
    let _lastFleet = null;
    const paintFleet = (exists, data) => {
      _lastFleet = { exists, data };
      if (DispatchState.viewMode !== 'map') return;
      const fs = fleetLayerState({
        hasSamsara, layerOn: true, answered: true, exists,
        vehicles: data?.vehicles || {}, nowMs: Date.now(), toMs: _mapTsMs, coordOf: plottableCoord
      });
      setFleetStatus(fs);

      // Clear old fleet markers
      _samsaraMarkers.forEach(m => m.setMap(null));
      _samsaraMarkers = [];

      const currentMap = DispatchState.mapInstance;
      if (!currentMap) return;

      fs.plotted.forEach(({ key, v, lat, lng, at, fresh }) => {
        const speedMph = v.speed || 0;
        const unitName = String(v.truckUnit || v.samsaraName || key || 'Unknown');
        // Truck icon: purple for fleet, dimmed if stale
        const marker = new google.maps.Marker({
          position: { lat, lng },
          map: currentMap,
          title: unitName,
          icon: {
            path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            fillColor: fresh ? MAP_FLEET_COLOR : '#9CA3AF',
            fillOpacity: fresh ? 0.9 : 0.5,
            strokeColor: '#fff',
            strokeWeight: 1.5,
            scale: 6,
            rotation: v.heading || 0
          },
          zIndex: 5
        });

        // Info window for fleet vehicles. The unit name comes from Samsara (an
        // external API) — escaped like everything else.
        marker.addListener('click', () => {
          const statsLines = [];
          if (Number.isFinite(v.odometerMiles)) statsLines.push(`<strong>Odometer:</strong> ${escapeHtml(v.odometerMiles.toLocaleString())} mi`);
          if (Number.isFinite(v.engineHours)) statsLines.push(`<strong>Engine:</strong> ${escapeHtml(v.engineHours.toLocaleString())} hrs`);
          if (Number.isFinite(v.fuelPercent)) {
            const fuelColor = v.fuelPercent < 20 ? '#DC2626' : v.fuelPercent < 40 ? '#B45309' : '#047857';
            statsLines.push(`<strong>Fuel:</strong> <span style="color:${fuelColor};font-weight:600;">${escapeHtml(String(v.fuelPercent))}%</span>`);
          }
          infoWindow.setContent(`
            <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:180px;">
              <div style="font-weight:700;font-size:15px;margin-bottom:6px;color:#0f172a;">
                <span style="color:${MAP_FLEET_COLOR};">●</span> ${escapeHtml(unitName)}
              </div>
              <div style="font-size:13px;color:#475569;margin-bottom:4px;">
                <strong>Speed:</strong> ${escapeHtml(String(speedMph))} mph
              </div>
              <div style="font-size:13px;color:#475569;margin-bottom:4px;">
                <strong>Heading:</strong> ${v.heading ? Math.round(Number(v.heading)) + '°' : '—'}
              </div>
              ${statsLines.map(l => `<div style="font-size:13px;color:#475569;margin-bottom:4px;">${l}</div>`).join('')}
              <div style="font-size:13px;color:${fresh ? '#475569' : '#DC2626'};">
                <strong>Updated:</strong> ${at ? escapeHtml(_mapAgoLabel(at)) : 'unknown'}${fresh ? '' : ' — not current'}
              </div>
            </div>
          `);
          infoWindow.open(currentMap, marker);
        });

        _samsaraMarkers.push(marker);
      });
    };
    const _unsubFleetSnap = onSnapshot(liveDocRef, (snap) => {
      paintFleet(snap.exists(), snap.exists() ? snap.data() : null);
    }, (err) => {
      // Permission-denied (rules not yet deployed, or a login without access)
      // must read as that — never as "no trucks".
      console.warn('[dispatch map] fleet positions unreadable:', err?.code || err?.message || err);
      setFleetStatus(fleetLayerState({ hasSamsara, layerOn: true, denied: true }));
    });
    const _fleetTimer = setInterval(() => {
      if (_lastFleet && DispatchState.mapInstance) paintFleet(_lastFleet.exists, _lastFleet.data);
    }, 60 * 1000);
    _samsaraUnsubscribe = () => { _unsubFleetSnap(); clearInterval(_fleetTimer); };

    // Clean up listener (and the freshness timer) when leaving map view
    DispatchState.unsubscribers.push(() => {
      if (_samsaraUnsubscribe) { _samsaraUnsubscribe(); _samsaraUnsubscribe = null; }
    });
  } else {
    setFleetStatus(fleetLayerState({ hasSamsara, layerOn: layers.fleet }));
  }

  // ============================================
  // GPS PINGS LAYER — slip-created + clock-in/out events (Mode A)
  // Wrapped in try/catch so any failure here does NOT break the rest of
  // the dispatch map for tenants without slip / GPS features enabled.
  // ============================================
  try {
    const tenantSettings = window.AppState?.settings || {};
    const pingsEnabled = tenantSettings.enableShipSlips === true || tenantSettings.captureGpsOnClock === true;
    if (pingsEnabled && window.AppState?.tenant) {
      // Load most recent 200 pings from the last 14 days.
      // Simple 30s in-memory cache to prevent re-querying on rapid map renders
      // (status snapshots, view toggles). Architect/Skeptic flagged repeated
      // 200-doc reads as a quota concern — this caps it to one query / 30s.
      const tenant = window.AppState.tenant;
      const cacheKey = `pings_${tenant}`;
      const now = Date.now();
      DispatchState._pingsCache = DispatchState._pingsCache || {};
      const cached = DispatchState._pingsCache[cacheKey];
      let pingDocs = null;

      if (cached && (now - cached.fetchedAt) < 30000) {
        pingDocs = cached.docs;
      } else {
        const since = new Date();
        since.setDate(since.getDate() - 14);
        const pingsSnap = await getDocs(query(
          collection(db, 'tenants', tenant, 'pings'),
          where('timestamp', '>=', Timestamp.fromDate(since)),
          orderBy('timestamp', 'desc'),
          limit(200)
        )).catch(err => {
          console.warn('[Dispatch] Pings query failed (non-fatal):', err.code || err.message);
          return null;
        });
        if (pingsSnap && !pingsSnap.empty) {
          pingDocs = pingsSnap.docs.map(d => d.data());
          DispatchState._pingsCache[cacheKey] = { fetchedAt: now, docs: pingDocs };
        }
      }

      if (pingDocs && pingDocs.length > 0) {
        const PING_COLORS = {
          slip_created: '#10B981', // emerald
          clock_in:    '#3B82F6', // brand blue
          clock_out:   '#06B6D4', // accent teal
          arrive_site: '#F59E0B', // amber
          depart_site: '#EAB308'  // yellow
        };

        pingDocs.forEach(p => {
          if (!p || p.gpsAvailable !== true || typeof p.lat !== 'number' || typeof p.lng !== 'number') return;
          const color = PING_COLORS[p.eventType] || '#94A3B8';

          const marker = new google.maps.Marker({
            position: { lat: p.lat, lng: p.lng },
            map,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: color,
              fillOpacity: 0.85,
              strokeColor: '#fff',
              strokeWeight: 1.5,
              scale: 5
            },
            zIndex: 4 // below jobs (10) and Samsara (5)
          });

          marker.addListener('click', () => {
            const ts = p.timestamp?.toDate ? p.timestamp.toDate() : new Date(p.clientTime || Date.now());
            const tsStr = ts.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
            const eventLabel = {
              slip_created: 'Ship slip created',
              clock_in:    'Clock-in',
              clock_out:   'Clock-out',
              arrive_site: 'Arrived on site',
              depart_site: 'Departed site'
            }[p.eventType] || p.eventType || 'Event';

            const lines = [];
            if (p.driverName) lines.push(`<strong>Driver:</strong> ${escapeHtml(p.driverName)}`);
            if (p.slipId) lines.push(`<strong>Slip:</strong> ${escapeHtml(p.slipId)}`);
            if (p.ticketId) lines.push(`<strong>Ticket:</strong> ${escapeHtml(p.ticketId)}`);
            if (typeof p.accuracy === 'number') lines.push(`<strong>Accuracy:</strong> ±${Math.round(p.accuracy)}m`);

            infoWindow.setContent(`
              <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;min-width:200px;max-width:280px;">
                <div style="font-weight:700;font-size:14px;margin-bottom:4px;color:${color};">
                  ● ${escapeHtml(eventLabel)}
                </div>
                <div style="font-size:12px;color:#475569;margin-bottom:6px;">${escapeHtml(tsStr)}</div>
                ${lines.map(l => `<div style="font-size:12px;color:#475569;margin-bottom:3px;">${l}</div>`).join('')}
              </div>
            `);
            infoWindow.open(map, marker);
          });

          DispatchState.pingMarkers.push(marker);
        });

        // Pings layer toggle button — top-left of map
        try {
          const toggleBtn = document.createElement('button');
          toggleBtn.textContent = `📍 ${DispatchState.pingMarkers.length} pings (last 14d) — hide`;
          toggleBtn.style.cssText = 'background:#fff;border:1px solid #d1d5db;padding:10px 14px;border-radius:6px;font-size:13px;cursor:pointer;margin:8px;color:#0F172A;font-weight:500;box-shadow:0 1px 2px rgba(0,0,0,0.1);min-height:44px;min-width:44px;';
          let pingsVisible = true;
          toggleBtn.addEventListener('click', () => {
            pingsVisible = !pingsVisible;
            DispatchState.pingMarkers.forEach(m => m.setMap(pingsVisible ? map : null));
            toggleBtn.textContent = `📍 ${DispatchState.pingMarkers.length} pings (last 14d) — ${pingsVisible ? 'hide' : 'show'}`;
          });
          map.controls[google.maps.ControlPosition.LEFT_TOP].push(toggleBtn);
        } catch (e) { /* control add failure is non-fatal */ }
      }
    }
  } catch (err) {
    console.warn('[Dispatch] Pings layer render failed (non-fatal):', err);
  }

  // TASK-9631 F4 — a view the user moved wins over the auto-fit; the
  // "No locations" note below still shows when there is nothing to pin.
  // COD r2: a drag DURING the awaited pings query above is a live user move;
  // re-check it here rather than trusting the _keepView decided before the await.
  const _userMovedLive = !!(DispatchState._mapViewport && DispatchState._mapViewport.userMoved);
  if (hasBounds && !_keepView && !_userMovedLive) {
    map.fitBounds(bounds);
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      // TASK-1615 — belt and braces behind the coordinate validation above. The
      // max cap stops a single-stop day from zooming into someone's driveway; the
      // min floor stops ANY future bounds box, however it got wide, from landing
      // on the world-repeating planet view that reads as a broken map. Zoom 3
      // still frames the whole of North America, so a genuine long-haul spread
      // is never clipped by it.
      const z = map.getZoom();
      if (z > MAP_MAX_FIT_ZOOM) map.setZoom(MAP_MAX_FIT_ZOOM);
      else if (z < MAP_MIN_FIT_ZOOM) map.setZoom(MAP_MIN_FIT_ZOOM);
    });
    // TASK-9631 F4 — zooms count as the user's only after the fit (and the
    // clamp above, which runs inside bounds_changed) has come to rest.
    google.maps.event.addListenerOnce(map, 'idle', _settleViewport);
  } else {
    _settleViewport(); // no programmatic fit this render
  }
  if (!hasBounds && !(layers.fleet && hasSamsara)) {
    // Nothing to fit — show message
    const infoDiv = document.createElement('div');
    infoDiv.style.cssText = 'background:#fff;padding:16px 20px;border-radius:8px;box-shadow:0 2px 8px rgba(0,0,0,0.15);margin:10px;max-width:300px;';
    infoDiv.innerHTML = `
      <div style="font-weight:600;margin-bottom:8px;color:#1e293b;">No locations to show</div>
      <div style="color:#64748b;font-size:14px;line-height:1.5;">
        ${!MAP_LAYER_IDS.some(id => layers[id])
          ? 'Every layer is switched off. Turn one on in the map key.'
          : projectMode
            ? (projectLoading ? 'Loading this project’s jobs…' : `${totalJobs} job${totalJobs !== 1 ? 's' : ''} in this project, but no destination has a map location yet.`)
            : !layers.stops
              ? 'Nothing to show for the layers that are on.'
              : totalJobs === 0
                ? `No jobs scheduled for ${escapeHtml(mapRangeLabel(shownFrom, shownTo))}.${rangeStartsBeforeWindow ? ' Older history is not loaded yet — use "Load older" in the range bar above.' : ''}${hasSamsara ? '' : ' Connect Samsara in Settings to see live fleet positions.'}`
                : `${totalJobs} job${totalJobs !== 1 ? 's' : ''} scheduled in ${escapeHtml(mapRangeLabel(shownFrom, shownTo))} but none have destinations with a map location. Add addresses to job destinations to see them on the map.`
        }
      </div>
    `;
    map.controls[google.maps.ControlPosition.TOP_LEFT].push(infoDiv);
  }
}

// ============================================
// HELPER FUNCTIONS
// ============================================
// Check if any driver on a jobDay is currently clocked in
function hasActiveClock(jobDay) {
  const entries = DispatchState._clockEntries || [];
  if (!entries.length) return false;
  const driverRefs = jobDay.drivers || [];
  return driverRefs.some(ref => {
    // Match by personId (most common) or name
    return entries.some(e => e.personId === ref || e.driverName === ref);
  });
}

// TASK-1540 — "someone is ON THE CLOCK against this job and the day has
// NOTHING BILLABLE attached", i.e. a guaranteed-$0 ticket in the making.
// SMS-assistant jobs are born with empty jobDays by design; a driver clocking
// into one gets assetIds:[] and the ticket auto-fill dead-ends (per-operator
// fill = entry.assetIds ∩ the day's assets — an empty day fills NOTHING even
// when the driver bound a truck). On 2026-08-12 three cards sat in this state
// and it took an admin-SDK query to notice.
//
// MODEL-AGNOSTIC on purpose (hard requirement): the condition is trucks +
// trailers + assets ALL empty — no "equipment = trucking" type logic. Split
// Rock rents mats and names assets after people; all-three-empty covers them
// identically.
//
// Match on entry.jobId, NOT the drivers[] assignment (hasActiveClock's key):
// the incident class is precisely a job whose day has NO assignments at all,
// so a drivers[]-based match can never fire on it. _clockEntries is already
// scoped to status=='active' by its query; !clockOut is defense-in-depth.
function jobDayOnClockNothingBillable(jobDay) {
  if (!jobDay || !jobDay.jobId) return false;
  if ((jobDay.trucks || []).length || (jobDay.trailers || []).length || (jobDay.assets || []).length) return false;
  // Integration-COD Code Reviewer — clock entries carry jobId, not jobDayId,
  // so a bare jobId match lit EVERY equipment-less day-card of a multi-day
  // job while the punch belonged to one day. Bind the badge to the day: the
  // entry's clock-in calendar day must BE this jobDay's day, or — for an open
  // shift spanning midnight — this jobDay must be today's card (an open punch
  // right now is "on the clock" today, whichever day it started). Stored
  // jobDay dates are noon-normalized → formatDateISO; the punch instant is
  // wall-clock → formatDateISOLocal (the TASK-273 split).
  const jdDate = jobDay.date?.toDate?.() || (jobDay.date ? new Date(jobDay.date) : null);
  if (!jdDate || isNaN(jdDate.getTime())) return false;
  const jdDay = formatDateISO(jdDate);
  const isTodayCard = jdDay === formatDateISOLocal(new Date());
  const entries = DispatchState._clockEntries || [];
  return entries.some(e => {
    if (e.jobId !== jobDay.jobId || e.clockOut) return false;
    if (isTodayCard) return true;
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return !isNaN(cin.getTime()) && formatDateISOLocal(cin) === jdDay;
  });
}

// Live-update clocked-in indicators without full re-render
function updateClockedInIndicators() {
  const allJobDays = DispatchState.jobDays || [];
  allJobDays.forEach(jd => {
    const clockedIn = hasActiveClock(jd);
    // Find all person indicator spans for this jobDay
    document.querySelectorAll(`[onclick*="'${jd.id}', 'people'"]`).forEach(el => {
      if (el.classList.contains('assign-indicator')) {
        el.classList.toggle('clocked-in', clockedIn);
      }
    });
    // Also update crew-icon in driver board view
    const crewIcon = document.querySelector(`[data-jobday-id="${jd.id}"] .crew-icon`);
    if (crewIcon) crewIcon.classList.toggle('clocked-in', clockedIn);
    // TASK-1540 — flip the "on clock, nothing billable" warning live as punches
    // land, over the same no-full-render path as the clocked-in dots above.
    // The badge element only exists on today/past cards (render-time gate), so
    // this can never reveal it on a future day. style.display (not [hidden])
    // because the badge carries an inline display:flex that would win over the
    // UA's [hidden] rule.
    const ocnb = jobDayOnClockNothingBillable(jd);
    document.querySelectorAll(`[data-ocnb-jobday="${jd.id}"]`).forEach(el => {
      el.style.display = ocnb ? 'flex' : 'none';
    });
    // TASK-9500 — out-of-zone clock-in chip, same live path.
    const ooz = document.querySelectorAll(`[data-ooz-jobday="${jd.id}"]`);
    if (ooz.length) {
      const oozEntries = _outOfZoneEntriesForDay(jd);
      ooz.forEach(el => {
        el.innerHTML = oozEntries.length ? _outOfZoneChipInner(oozEntries) : '';
        el.style.display = oozEntries.length ? 'flex' : 'none';
      });
    }
  });
}

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDateISO(date) {
  return date.toISOString().split('T')[0];
}

// TASK-273: Local-timezone YYYY-MM-DD formatter for <input type="date"> defaults.
// formatDateISO uses .toISOString() which is UTC — after ~18:00 CT, "today" silently
// becomes tomorrow's UTC date, causing default startDate to land on the wrong day.
// Use this for form-default sites only; date-comparison and stored-date paths still
// use formatDateISO because stored Firestore dates are normalized to T12:00:00.
function formatDateISOLocal(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// TASK-273: tomorrow at local midnight. Audit shows dispatchers almost always
// change the default-today date to tomorrow on JOB_CREATE, so defaulting to
// tomorrow is the 90% case (calendar-click prefilledDate still wins via
// openAddJobWithDate short-circuit at L5560).
function tomorrowLocal() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
}

// TASK-9407 — Dispatch Settings "Default start date for new jobs".
// settings.defaultJobStartDate ∈ 'today' | 'tomorrow' | 'nextBusinessDay'.
// Absent or any other value = 'tomorrow', the TASK-273 status quo, so no
// tenant's form changes until they pick something. Next business day is
// Mon–Fri only, no holiday calendar: Fri/Sat/Sun → the following Monday.
// Returns local midnight of the chosen day; callers format it with
// formatDateISOLocal (never toISOString — the TASK-273 after-6pm-CT bug).
const JOB_START_DATE_DEFAULTS = ['today', 'tomorrow', 'nextBusinessDay'];

function normalizeDefaultJobStartDate(v) {
  return JOB_START_DATE_DEFAULTS.includes(v) ? v : 'tomorrow';
}

function defaultJobStartDateLocal(setting, now) {
  const d = now ? new Date(now.getTime()) : new Date();
  d.setHours(0, 0, 0, 0);
  const mode = normalizeDefaultJobStartDate(setting);
  if (mode === 'today') return d;
  if (mode === 'nextBusinessDay') {
    // 5 (Fri) → +3, 6 (Sat) → +2, 0 (Sun) → +1, Mon–Thu → +1.
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 5 ? 3 : dow === 6 ? 2 : 1));
    return d;
  }
  d.setDate(d.getDate() + 1);
  return d;
}

// Format date for display using UTC to avoid timezone shift
// (dates stored as UTC midnight would show previous day in western timezones)
function formatDateDisplay(date, options = { weekday: 'short', month: 'short', day: 'numeric' }) {
  // Create a new date adjusted to UTC to display the stored date correctly
  const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  return utcDate.toLocaleDateString('en-US', options);
}

function formatDateRange(start, end) {
  const opts = { month: 'short', day: 'numeric' };
  return `${formatDateDisplay(start, opts)} - ${formatDateDisplay(end, opts)}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-9112 — WHAT DATES IS THE BOARD ACTUALLY SHOWING?
//
// A job saved for a date off the visible board saves fine, toasts "Job created:
// … Thursday, Sep 24" in green, and then renders NOWHERE. The board does not
// move. Chris read the unchanged board as a refusal — "it won't let them" — and
// the field consequence of that belief is a re-booked truck and two crews on the
// same load. Reproduced in a browser 2026-09-10 before a line was written:
// week view Sep 6–12, job dated Sep 24, green success toast, board untouched,
// job absent from the DOM. The save was never the problem.
//
// 🔴 THE REASON THIS IS A HELPER AND NOT AN INLINE CHECK: "is this date visible?"
// has FIVE different answers in this file and had no single place to ask.
//   week   renderWeekView  :5524  getWeekStart(currentDate) + 7  — SNAPS to Sunday
//   list   renderListView  :5843  currentDate + 5               — no snap
//   lanes  renderLanesView :6498  currentDate + 7               — no snap
//   month  renderMonthView :6357  the calendar month
//   map    getMapRange     :6974  its OWN from/to inputs, not currentDate at all
// Any check that re-derives a window inline is wrong in at least three of them.
// Everything downstream — the inline hint, the save toast, the jump — reads THIS.
//
// 🔴 AND IT DELIBERATELY MATCHES THE RENDERERS BUG-FOR-BUG. Each branch below
// builds its dates exactly the way its renderer does and formats them with the
// same formatDateISO(). formatDateISO is .toISOString(), i.e. UTC, so for a user
// east of Greenwich a local-midnight Date can format as the PREVIOUS day — which
// is a real latent defect, but it is the renderers' defect, and the board paints
// what the board paints. A helper that "corrected" it here would disagree with
// the grid the user is looking at, and this feature exists precisely to stop the
// app disagreeing with itself. Fix it in the renderers or not at all.
//
// Returns { from, to, canJump } as YYYY-MM-DD, or null when the question has no
// answer: not on the dispatch board, driver mode, or a view with no date window
// (projects, search). null means "say nothing" — never "outside".
//
// `map` gets a range but canJump:false. The council voted 3–1 to return null for
// map; that vote's actual concern was jumping the map's viewport, which nothing
// here does. Returning null instead would leave a map user with the exact silent
// off-screen save this task exists to kill, since the Add Job button lives in the
// shared toolbar and is present in every view. So: tell the truth, offer no jump.
// Reads mapDateFrom/To directly rather than through getMapRange(), which lazily
// WRITES those fields — a read from a modal must not mutate the map's state.
// ─────────────────────────────────────────────────────────────────────────────
function getVisibleDateRange() {
  // Not looking at the dispatch board at all (Billing's addJobFromSplit, Projects'
  // add-job hand-off) — "outside the dates you're viewing" would be a lie.
  if (window.AppState?.currentView !== 'dispatch') return null;
  if (DispatchState.roleMode === 'driver' || window.AppState?.driverMode
    || window.AppState?.isPublicAccess || window.AppState?.driverModePin) return null;

  const cur = DispatchState.currentDate;
  if (!(cur instanceof Date) || isNaN(cur.getTime())) return null;

  const spanFrom = (start, days) => {
    const s = new Date(start);
    s.setHours(0, 0, 0, 0);
    const e = new Date(s);
    e.setDate(e.getDate() + (days - 1));
    return { from: formatDateISO(s), to: formatDateISO(e), canJump: true };
  };

  switch (DispatchState.viewMode) {
    case 'week':
      return spanFrom(getWeekStart(cur), 7);      // renderWeekView: 7 from the week start
    case 'lanes':
      return spanFrom(cur, 7);                    // renderLanesView: 7 from currentDate
    case 'list':
      return spanFrom(cur, 5);                    // renderListView: daysToShow = 5
    case 'month': {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      return { from: formatDateISO(new Date(y, m, 1)), to: formatDateISO(new Date(y, m + 1, 0)), canJump: true };
    }
    case 'map': {
      const from = DispatchState.mapDateFrom;
      const to = DispatchState.mapDateTo;
      if (!from || !to) return null;              // map never painted — nothing to be outside of
      return from <= to ? { from, to, canJump: false } : { from: to, to: from, canJump: false };
    }
    default:
      return null;                                // projects, search — not date-windowed
  }
}

// TASK-9112 — is [startISO, endISO] WHOLLY outside the visible range?
//
// The overlap rule, decided explicitly here because nothing in this file decided
// it before: a job running Sep 10 → Sep 19 seen from the Sep 6–12 week IS on the
// board — its first days are painted right there. Warning about it would be false
// and would train dispatchers to dismiss the hint. Interval overlap, not
// start-date containment. Fires only when every day of the job is off-board.
// Plain string compares are safe and correct on YYYY-MM-DD, and they keep every
// Date/timezone/noon-anchor trap out of this path entirely.
function isRangeWhollyOffBoard(startISO, endISO, range) {
  if (!range || !startISO) return false;
  const end = endISO && endISO >= startISO ? endISO : startISO;
  return end < range.from || startISO > range.to;
}

// TASK-9112 — "Sep 24" / "Sep 6 – Sep 12" for the hint and the toast button.
function formatBoardDateShort(iso) {
  if (!iso) return '';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// TASK-9112 — move the board to a date, from the save toast's action button.
//
// 🔴 NOTHING calls this automatically. The council was unanimous across two
// rounds and it is the whole shape of this fix: dispatchers batch-enter next
// week's loads WHILE reading this week's board. Auto-navigating on save yanks
// the page out from under the thing they are reading — a second way to lose
// someone, dressed up as helpfulness. The user asks, or the board does not move.
//
// Local NOON, never midnight: every renderer re-derives from currentDate with
// setHours(0,0,0,0) / getMonth(), and a midnight anchor is the documented way
// this codebase lands a job on nothing at all.
function goToBoardDate(iso) {
  if (!iso) return;
  const d = new Date(iso + 'T12:00:00');
  if (isNaN(d.getTime())) return;
  DispatchState.currentDate = d;
  DispatchState.listOffset = 0; // matches the view-switch handler
  updateDateDisplay();
  _renderDispatchBoardNow();    // user-initiated — instant feedback, not debounced
}

// TASK-962 (S2): spelled-out weekday+date label for the job save toasts. The
// wrong-day incident (Steve/TITS, evening AI build) was caught only by manual
// re-check — the weekday in the confirmation toast is the flinch-trigger.
// Multi-day jobs show the full range. Inputs are the form's YYYY-MM-DD strings;
// T12:00:00 avoids the UTC-midnight previous-day shift (same convention as saveJob).
function formatJobToastDates(startDateVal, endDateVal) {
  const opts = { weekday: 'long', month: 'short', day: 'numeric' };
  const startLabel = formatDateDisplay(new Date(startDateVal + 'T12:00:00'), opts);
  if (endDateVal && endDateVal !== startDateVal) {
    return `${startLabel} – ${formatDateDisplay(new Date(endDateVal + 'T12:00:00'), opts)}`;
  }
  return startLabel;
}

// TASK-9112 — the post-save half. The save already toasted green with the right
// weekday and date and Chris still read the unchanged board as a refusal, so the
// date alone is not the signal. The toast has to say the board did not move.
//
// 🔴 THE COPY CARRIES THE OUTCOME ON ITS OWN. The action button is the
// convenience; a dispatcher who never notices it must still finish reading
// certain the job is saved. So: "Saved to …", then where it landed, then the
// button. Never a bare "Go to Sep 24" hanging off an unchanged-looking screen.
//
// Duration: 30s, passed explicitly. showToast's 15000 is the DEFAULT when an
// action is present (app.js:13422, `durationMs != null ? durationMs : (action ?
// 15000 : …)`), not a ceiling — no new banner component is needed for this.
//
// Returns null when there is nothing to add, so callers keep their existing
// single-argument showToast call unchanged in the common case.
function decorateOffBoardSaveToast(baseMessage, startDateVal, endDateVal, range) {
  if (!isRangeWhollyOffBoard(startDateVal, endDateVal, range)) return null;
  const message = `${baseMessage}. Not on the board you're viewing (${formatBoardDateShort(range.from)} – ${formatBoardDateShort(range.to)}).`;
  // Map view keeps its own from/to inputs and its own model of a range; moving
  // the board's currentDate would not move the map, so it is told the truth and
  // offered no button rather than a button that does nothing visible.
  if (!range.canJump) return { message, action: null, durationMs: 15000 };
  return {
    message,
    action: {
      label: `Go to ${formatBoardDateShort(startDateVal)}`,
      callback: () => goToBoardDate(startDateVal),
    },
    durationMs: 30000,
  };
}

// 🔴 IS THE SCHEDULE ACTUALLY READABLE YET?
//
// Chris, 2026-08-23: "if user hits Notify Crew before job board loaded, notify
// crew text has no jobs." That is not a cosmetic empty state — it is a real SMS
// to the whole crew reading "No jobs scheduled." on a day that HAS jobs.
//
// The trap is that EMPTINESS CANNOT BE THE TEST. A genuinely empty day is
// legitimate and the format deliberately supports it ("No jobs scheduled." for a
// single empty day), so refusing on `dayJobs.length === 0` would break a real
// Sunday blast. The only honest signal is whether both subscriptions have
// delivered a first snapshot — the same denied/absent-vs-empty distinction that
// bit peopleComp and costAllocations.
//
// `_jobDaysLoaded` already existed and is honoured by renderWeekView and
// renderMapView; the mass-text path simply never consulted it, and nothing
// tracked `jobs` at all.
// Paint the not-ready state into the mass-text modal and LOCK Send. Called by
// every path that builds a preview, so no entry point can quietly compose a
// jobless message. Returns true when it took over (i.e. NOT ready).
// Called after every dispatch data publish. If the mass-text modal is open and
// the schedule has just become readable, rebuild the preview and unlock Send.
// Cheap and idempotent — it returns immediately unless the modal is on screen
// AND the loading guard is what locked the button.
function refreshMassTextOnDataArrival() {
  if (!document.getElementById('mass-text-content')) return;
  const modal = document.getElementById('modal-mass-text');
  if (modal && modal.classList.contains('hidden')) return;
  // TASK-4588 — this used to only ever UNLOCK: it early-returned when not ready,
  // so a roster error arriving AFTER the preview unlocked left Send enabled until
  // the click-time refusal. renderMassTextNotReady() locks when not ready and
  // unlocks when it is, and returns true when it took over — so the preview lock
  // and the send gate can no longer disagree.
  // Read the lock marker BEFORE renderMassTextNotReady() — on the ready path it
  // DELETES lockedByLoading, so checking afterwards always reads "not locked" and
  // the rebuild is skipped: Send re-enables while the textarea still holds
  // "Still loading the schedule…", and sendMassText() ships the textarea verbatim.
  // That would have texted the placeholder to the crew (COD Code Reviewer).
  const btn = document.getElementById('btn-send-mass-text');
  const wasLocked = btn?.dataset.lockedByLoading === '1';
  if (renderMassTextNotReady()) return;   // still not ready — it locked and painted why
  if (!wasLocked) return;                 // nothing was locked, nothing to rebuild
  if (massTextPersonalized) updatePersonalizedPreview(); else updateMassTextContent();
}

// TASK-4588 — the two placeholder openers, in ONE place, so the send-time
// refusal below can recognise them. Any path that can put text in the mass-text
// textarea before the real preview exists must use one of these.
const MASS_TEXT_PLACEHOLDERS = ['Still loading the schedule', 'Could not load the schedule'];

function renderMassTextNotReady() {
  if (isDispatchScheduleReady() && !DispatchState._dispatchLoadFailed) {
    const btn = document.getElementById('btn-send-mass-text');
    if (btn && btn.dataset.lockedByLoading === '1') {
      btn.disabled = false;
      delete btn.dataset.lockedByLoading;
      if (btn.dataset.labelBeforeLoading) { btn.innerHTML = btn.dataset.labelBeforeLoading; delete btn.dataset.labelBeforeLoading; }
    }
    return false;
  }
  _flushMassTextDraftSave();   // TASK-9532 — keep the edit before the placeholder lands on it
  const textarea = document.getElementById('mass-text-content');
  if (textarea) {
    textarea.value = DispatchState._dispatchLoadFailed
      ? 'Could not load the schedule.\n\nReload the page before sending — the crew text would be missing jobs or crew. Nothing has been sent.'
      : 'Still loading the schedule…\n\nThe crew text will be built as soon as the job board finishes loading. Nothing has been sent.';
  }
  _removeMassTextDraftBanner();   // TASK-9532 — a placeholder is not a restored draft
  const btn = document.getElementById('btn-send-mass-text');
  if (btn && !btn.disabled) {
    btn.dataset.lockedByLoading = '1';
    btn.dataset.labelBeforeLoading = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Loading schedule\u2026';
  }
  // Recovery is EVENT-DRIVEN, not polled. Every job/jobDay publish already calls
  // renderDispatchBoard(), which now calls refreshMassTextOnDataArrival() — so
  // there is no timer to expire and no cap for a late snapshot to race past.
  // (COD Independent flagged the first cut, a 250ms x 60 poll, for exactly that:
  // it could leave Send disabled on a tenant whose data had actually arrived.)
  return true;
}

function isDispatchScheduleReady() {
  return !!(DispatchState._jobDaysLoaded && DispatchState._jobsComplete);
}

// TASK-4588 — the RENDER-level bar. getJobsForDate() walks jobDays and then
// resolves each one against DispatchState.jobs, so jobDays alone is not enough:
// if jobDays lands first every lookup misses and the view paints a confidently
// empty day. Renders may use the looser `_jobsLoaded` ("something is renderable")
// because every publish repaints them; anything that does NOT repaint — a modal,
// a printed artifact, an SMS — must use isDispatchScheduleReady() instead.
function isDispatchBoardRenderable() {
  return !!(DispatchState._jobDaysLoaded && DispatchState._jobsLoaded);
}

// TASK-4588 — every view that reads getJobsForDate() has to answer "has the board
// loaded?" before it paints, or it renders a confidently empty day. renderWeekView
// and renderMapView already did; the rest carried the same race with lower stakes.
// Blocking a RENDER is safe: every job/jobDay publish calls renderDispatchBoard()
// again, so the view repaints the moment data lands. Gated on _jobDaysLoaded (the
// "renderable" flag) to match the two views that were already guarded — NOT on
// emptiness, because a genuinely empty day is legitimate and must still render.
function renderScheduleLoading(container) {
  if (!container) return true;
  container.innerHTML = `
    <div style="text-align:center;padding:var(--spacing-xl);">
      <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
      <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
    </div>
  `;
  return true;
}

// TASK-4646 — is THIS calendar row finished work?
//
// Deliberately keyed on `job.status` ALONE, which is the exact predicate
// getJobsForDate() used to exclude on. That makes the Completed chip a precise
// inverse of the old behavior: chip OFF reproduces what shipped before, chip ON
// gives the history back. Widening this to jobDay.status would newly HIDE rows
// that render today (a finished jobDay under a still-active job), which is a
// different change and not one anybody asked for. Cancelled is likewise NOT
// folded in — cancelled rows have always painted and still do, in both states.
function isCompletedCalendarEntry(entry) {
  return entry?.job?.status === 'finished';
}

// TASK-4646 — the calendar's single source of "what does this day paint, and what
// did I just hide?". The bug this closes: finished jobs were dropped inside
// getJobsForDate(), i.e. BEFORE any renderer could see them, so no chip could ever
// bring them back and the hidden-count pill had nothing to count. Filtering moved
// HERE, at the render layer, where the chips actually live.
//
// Returns {visible, hidden} rather than just an array on purpose — the caller needs
// an honest hidden count to feed updateLayerFilterWarning(), and recomputing it with
// a second getJobsForDate() call was how the week/month views were already paying
// double for the Jobs chip.
//
// Holds are NOT routed through here. Safety holds and vehicle holds stay on their
// own always-render path (TASK-461, FMCSA §396.9/§396.11) and no filter reaches them.
function getCalendarJobsForDate(dateStr, filters) {
  const all = getJobsForDate(dateStr, true); // everything, finished included
  const f = filters || {};
  if (f.jobs === false) return { visible: [], hidden: all.length };
  if (f.completed !== false) return { visible: all, hidden: 0 }; // absent key === ON
  const visible = all.filter(e => !isCompletedCalendarEntry(e));
  return { visible, hidden: all.length - visible.length };
}

function getJobsForDate(dateStr, includeFinished = false) {
  const results = [];
  const jobIdsWithJobDays = new Set();

  // First, get jobs that have jobDays for this date
  DispatchState.jobDays.forEach(jobDay => {
    const jobDayDate = jobDay.date?.toDate?.() || new Date(jobDay.date);
    if (formatDateISO(jobDayDate) === dateStr) {
      const job = DispatchState.jobs.find(j => j.id === jobDay.jobId);
      if (job && (includeFinished || job.status !== 'finished')) {
        results.push({ job, jobDay });
        jobIdsWithJobDays.add(job.id);
      }
    }
  });

  // Then, find legacy jobs (from GAS import) that have date range but no jobDays
  // These jobs store assignments directly on the job document
  // Only applies to jobs with ZERO jobDays anywhere — if a job has any jobDays, it's not legacy
  const jobIdsWithAnyJobDays = new Set(DispatchState.jobDays.map(jd => jd.jobId));
  // TASK-4739 — noon-local. Visibility below is a STRING compare, so this only
  // sets syntheticJobDay.date; a UTC-midnight object renders as the previous day.
  const targetDate = new Date(dateStr + 'T12:00:00');
  DispatchState.jobs.forEach(job => {
    if (jobIdsWithJobDays.has(job.id)) return; // Already shown via jobDay on this date
    if (jobIdsWithAnyJobDays.has(job.id)) return; // Has jobDays on other dates — not legacy
    if (!includeFinished && job.status === 'finished') return;

    // Check if this job's date range includes the target date
    const startDate = job.startDate?.toDate?.() || (job.startDate ? new Date(job.startDate) : null);
    const endDate = job.endDate?.toDate?.() || (job.endDate ? new Date(job.endDate) : null);

    if (!startDate) return; // No date info

    const jobStartStr = formatDateISO(startDate);
    const jobEndStr = endDate ? formatDateISO(endDate) : jobStartStr;

    // Check if target date falls within job's date range
    if (dateStr >= jobStartStr && dateStr <= jobEndStr) {
      // Create a synthetic jobDay from job's assignments
      const syntheticJobDay = {
        id: `legacy-${job.id}-${dateStr}`,
        jobId: job.id,
        date: targetDate,
        // Pull assignments from job document (GAS import format)
        drivers: job.drivers || [],
        helpers: job.helpers || [],
        trucks: job.trucks || [],
        trailers: job.trailers || [],
        assets: job.assets || [],
        status: (job.drivers?.length > 0 || job.trucks?.length > 0) ? 'assigned' : 'scheduled',
        isLegacy: true // Flag to identify synthetic jobDays
      };
      results.push({ job, jobDay: syntheticJobDay });
    }
  });

  return results;
}

// ============================================================================
// TASK-9750 — TENTATIVE / HALF-DAY JOB DAYS.
//
// "They might need a side dump for half the day" is a soft request. It has to
// sit on the board without looking confirmed and without texting anybody.
//
//   jobDay.tentative === true  → dashed card + "Tentative" chip; NO crew text
//                                from any path until a dispatcher confirms it.
//   jobDay.halfDay   === true  → "½ day" chip. Display only — it does NOT
//                                change billing (Chris decides before it ever does).
//
// Strict `=== true` both ways: absent / false is a normal confirmed full day, so
// every day written before these fields existed renders and texts as before.
//
// WHERE "NO TEXT" IS ENFORCED (keep this list current):
//   • crew assignment alerts — server, crewAlertCore.planCrewAlertEvents /
//     planJobDayChangeEvents / pickJobChangeRecipients + the sweeper's delivery
//     re-check (crewAssignmentAlerts.js). Confirming = everyone on the day is
//     "added" → the normal notice, once.
//   • Notify Crew (standard + personalized + draft fingerprint) —
//     getCrewTextJobsForDate below.
//   • the per-card "Text Crew" button — hidden on a tentative card, and
//     sendIndividualText refuses a tentative day.
//   • NOT the DVIR / JSA safety reminders: they fire for tentative days too
//     (dispatcher decision — the DVIR duty does not depend on confirmation).
//   • Claude connector create_job `tentative: true` — writes tentative days.
// ============================================================================
function isTentativeJobDay(jobDay) {
  return !!jobDay && jobDay.tentative === true;
}

function isHalfDayJobDay(jobDay) {
  return !!jobDay && jobDay.halfDay === true;
}

// TASK-10288 C — a tentative day whose date has PASSED with no decision (not
// confirmed, moved or cancelled). Flagged on the card so it gets a decision; it
// is still skipped by the no-ticket check (dispatchedNoTicket.js) and by every
// crew text. "Today" is LOCAL; jobDay.date is noon-anchored, so formatDateISO
// lands on its calendar day (lesson_jobday_date_anchors_to_noon_local).
function isStaleTentativeJobDay(jobDay, nowMs) {
  if (!isTentativeJobDay(jobDay)) return false;
  const st = String(jobDay.status || '').toLowerCase();
  if (st === 'cancelled' || st === 'finished') return false;
  const ms = _jobDayDateMs(jobDay);
  if (ms === null) return false;
  const todayStr = formatDateISOLocal(new Date(Number.isFinite(nowMs) ? nowMs : Date.now()));
  return formatDateISO(new Date(ms)) < todayStr;
}

// Every crew-TEXT surface reads this, never getJobsForDate directly: a tentative
// day is on the board but must never reach a crew text until it is confirmed.
function getCrewTextJobsForDate(dateStr) {
  return getJobsForDate(dateStr).filter(({ jobDay }) => !isTentativeJobDay(jobDay));
}

// Inline styles on purpose: these chips render on cards in several views, and
// none of them should need a styles.css cache-buster.
// Wraps rather than clips: a week-view column is ~130px wide on a laptop.
const _DAY_FLAG_CHIP_BASE = 'display:inline-flex;flex-wrap:wrap;align-items:center;gap:3px;max-width:100%;box-sizing:border-box;text-align:left;font-size:0.7rem;font-weight:700;line-height:1.2;border-radius:10px;padding:2px 8px;vertical-align:middle;';

// opts.confirm — render the Tentative chip as a one-tap Confirm button
// (dispatch board only; never on driver surfaces, never for view-only access).
function _dayFlagChipsHtml(jobDay, opts = {}) {
  if (!jobDay) return '';
  let html = '';
  if (isTentativeJobDay(jobDay)) {
    const _driverSide = !!(window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin)
      || DispatchState.roleMode === 'driver';
    const canConfirm = opts.confirm && !_driverSide && !String(jobDay.id || '').startsWith('legacy-') && _dispatchCanManageDays();
    // TASK-10288 C — the date passed with no decision. Same amber tentative
    // styling; the chip opens the card's menu (Move Day / Confirm Day / Cancel
    // Day) instead of a one-tap Confirm, because confirming a day that is
    // already over is rarely the answer.
    if (isStaleTentativeJobDay(jobDay)) {
      html += (canConfirm && jobDay.jobId)
        ? `<button type="button" class="day-flag-chip day-flag-tentative day-flag-tentative-stale" data-day-flag="tentative-stale" style="${_DAY_FLAG_CHIP_BASE}min-height:26px;cursor:pointer;background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;" title="Tentative, and the date has passed with no decision. Tap to move it to a new day, confirm it, or cancel it." onclick="event.stopPropagation(); window.DispatchModule.showJobContextMenu(event, '${escJsAttr(jobDay.jobId)}', '${escJsAttr(jobDay.id)}')"><span class="material-icons" style="font-size:14px;">event_busy</span><span>Tentative — date passed ·</span><span style="text-decoration:underline;">Move / Cancel</span></button>`
        : `<span class="day-flag-chip day-flag-tentative day-flag-tentative-stale" data-day-flag="tentative-stale" style="${_DAY_FLAG_CHIP_BASE}background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;" title="Tentative, and the date has passed with no decision"><span class="material-icons" style="font-size:14px;">event_busy</span>Tentative — date passed</span>`;
    } else
    html += canConfirm
      ? `<button type="button" class="day-flag-chip day-flag-tentative" data-day-flag="tentative" style="${_DAY_FLAG_CHIP_BASE}min-height:26px;cursor:pointer;background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;" title="Tentative: the crew has not been texted. Tap to confirm the day and send the normal notice." onclick="event.stopPropagation(); window.DispatchModule.confirmJobDay('${escJsAttr(jobDay.id)}')"><span class="material-icons" style="font-size:14px;">help_outline</span><span>Tentative ·</span><span style="text-decoration:underline;">Confirm</span></button>`
      : `<span class="day-flag-chip day-flag-tentative" data-day-flag="tentative" style="${_DAY_FLAG_CHIP_BASE}background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;" title="Tentative: not confirmed yet"><span class="material-icons" style="font-size:14px;">help_outline</span>Tentative</span>`;
  }
  // TASK-10006 S3 — only on a day whose planned rental pickup is overdue
  // (office board; never a driver surface; nothing for jobs without rentals).
  if (!(window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin) && DispatchState.roleMode !== 'driver') {
    html += _pickupOverdueChipHtml(jobDay);
  }
  if (isHalfDayJobDay(jobDay)) {
    html += `<span class="day-flag-chip day-flag-half" data-day-flag="half" style="${_DAY_FLAG_CHIP_BASE}background:rgba(59,130,246,0.12);color:var(--text-primary);border:1px solid rgba(59,130,246,0.35);" title="Half day">½ day</span>`;
  }
  // opts.inline — a <span> wrapper for hosts that are themselves inline/phrasing
  // content (a <button> row cannot hold a <div>).
  const tag = opts.inline ? 'span' : 'div';
  return html ? `<${tag} class="day-flag-chips" style="display:flex;flex-wrap:wrap;gap:4px;margin-top:3px;">${html}</${tag}>` : '';
}

// A CONFIRMED day with people on it cannot be switched (back) to tentative:
// they may already have been texted, and a tentative day texts nobody — not
// even that it was cancelled or moved (COD round 2). Tentative is for a day
// that has never been confirmed to anyone. A confirmed day with nobody on it
// (or only equipment) can still be marked tentative.
function _assignmentTentativeLocked(jobDay) {
  if (!jobDay || isTentativeJobDay(jobDay)) return false;
  return (jobDay.drivers || []).length + (jobDay.helpers || []).length > 0;
}

// The card's own outline: dashed amber, so a tentative day never reads as booked.
function _tentativeCardStyle(jobDay) {
  return isTentativeJobDay(jobDay) ? 'outline:1px dashed #F59E0B;outline-offset:-1px;' : '';
}

// One tap: tentative → confirmed. The crew text is NOT sent from here — the
// server's onJobDayCrewWrite sees the tentative→confirmed write and stages the
// normal assignment notice for everyone on the day, once (crewAlertCore).
const _confirmingJobDays = new Set();
async function confirmJobDay(jobDayId) {
  if (!jobDayId || _confirmingJobDays.has(jobDayId)) return;
  if (!_guardManageDays()) return;
  const jd = _resolveJobDayAnywhere(jobDayId);
  if (!jd || !isTentativeJobDay(jd)) return;
  _confirmingJobDays.add(jobDayId);
  try {
    const now = Timestamp.now();
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), {
      tentative: false,
      confirmedAt: now,
      confirmedBy: window.AppState?.user?.uid || null,
      updatedAt: now,
    });
    // Optimistic local mirror — the live listener lands the same values.
    for (const list of [DispatchState.jobDays, DispatchState.searchJobDays]) {
      const row = Array.isArray(list) ? list.find(d => d.id === jobDayId) : null;
      if (row) { row.tentative = false; row.confirmedAt = now; }
    }
    const crewCount = new Set([...(jd.drivers || []), ...(jd.helpers || [])]).size;
    const _d = jd.date?.toDate?.() || (jd.date ? new Date(jd.date) : null);
    window.logAudit?.('dispatch', 'CONFIRM_JOBDAY', {
      jobDayId, jobId: jd.jobId || null, crewCount,
      date: _d && !isNaN(_d) ? formatDateISO(_d) : null,
    });
    _renderDispatchBoardNow();
    // Say what really happens next: the auto-text only exists when crew alerts
    // are on (Dispatch Settings); either way the day now joins Notify Crew.
    const _alertsOn = ['assignment', 'assignment_changes'].includes(window.AppState?.settings?.crewJobAlerts);
    // Undo (COD UX/Domain: a fat-fingered chip must be reversible). Crew texts
    // wait out a ~3 min batching window and the sweeper re-reads the day before
    // sending, so a 15 s undo always lands inside it and cancels them. This is
    // the ONLY way a day with crew goes back to tentative (see
    // _assignmentTentativeLocked).
    window.showToast?.(crewCount && _alertsOn
      ? 'Day confirmed. The crew on it gets the assignment text in a few minutes.'
      : 'Day confirmed. It now goes out with Notify Crew.', 'success',
    { label: 'Undo', callback: () => _undoConfirmJobDay(jobDayId) }, 15000);
  } catch (err) {
    console.error('[Dispatch] TASK-9750 confirm failed:', err);
    window.showToast?.("Couldn't confirm that day. Nothing changed; try again.", 'error');
  } finally {
    _confirmingJobDays.delete(jobDayId);
  }
}

// The one "make this day tentative" write: TASK-9750's Undo, and (TASK-10288 D)
// the right-click Mark Tentative, which passes its own audit action + words.
// Callers own the eligibility check (_assignmentTentativeLocked).
async function _undoConfirmJobDay(jobDayId, msg) {
  msg = msg || {
    audit: 'UNDO_CONFIRM_JOBDAY',
    toast: 'Back to tentative.',
    errorToast: "Couldn't undo. Open Assign Crew and tick Tentative.",
  };
  try {
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), {
      tentative: true,
      confirmedAt: null,
      confirmedBy: null,
      updatedAt: Timestamp.now(),
    });
    for (const list of [DispatchState.jobDays, DispatchState.searchJobDays]) {
      const row = Array.isArray(list) ? list.find(d => d.id === jobDayId) : null;
      if (row) { row.tentative = true; row.confirmedAt = null; }
    }
    window.logAudit?.('dispatch', msg.audit, { jobDayId });
    _renderDispatchBoardNow();
    window.showToast?.(msg.toast, 'info');
  } catch (err) {
    console.error('[Dispatch] set tentative failed:', err);
    window.showToast?.(msg.errorToast, 'error');
  }
}

function getSafetyHoldsForDate(dateStr, isToday) {
  const results = [];
  // TASK-9657 S0 — LOCAL day, not formatDateISO (UTC): after ~7pm Central the
  // UTC "today" is tomorrow and an open critical hold dropped off today's column.
  const todayStr = formatDateISOLocal(new Date());

  DispatchState.safetyHolds.forEach(hold => {
    const createdDate = hold.createdAt?.toDate?.() || new Date(hold.createdAt);
    const createdStr = isNaN(createdDate?.getTime?.()) ? '' : formatDateISOLocal(createdDate);

    // Show on created date OR on today (if still open)
    if (createdStr === dateStr || (isToday && dateStr === todayStr)) {
      // Avoid duplicates if created date is today
      if (createdStr === dateStr || createdStr !== todayStr) {
        results.push(hold);
      }
    }
  });

  return results;
}

// TASK-10183 — regular days off (weekly rotation). The days come from the getRotationDays
// callable (no client Firestore access to `rotations`), expanded server-side on company-local
// dates; the lazy chunk rotationDays.js holds the cache and is read only via window.DDRotations.
let _rotLoad = null; let _rotSubscribed = false;
function _rotationsFor(dateStr) {
  if (window.AppState?.driverModePin) return [];
  const R = window.DDRotations;
  if (R) {
    if (!_rotSubscribed) { _rotSubscribed = true; R.subscribe(() => renderDispatchBoard()); }
    return R.forDate(dateStr);
  }
  if (!_rotLoad) _rotLoad = __ddChunkWrap(import('./rotationDays.js?v=10183001')).then(() => renderDispatchBoard()).catch((e) => { _rotLoad = null; console.warn('[Dispatch] rotations chunk failed', e?.message || e); });
  return [];
}

// Waits for the rotation days covering dateStr (loads the chunk and the window if needed). Never throws.
async function _rotationsReady(dateStr) {
  let r = null;
  try {
    if (!window.DDRotations) { _rotationsFor(dateStr); await _rotLoad; }
    r = await window.DDRotations?.ensureFor(dateStr);
  } catch (e) { console.warn('[Dispatch] rotation days not loaded', e?.message || e); }
  // COD r2: never a silent "nobody is off" — say the check did not run (still never blocks).
  if (r == null) window.showToast?.("Couldn't check regular days off for that day. Check the board before dispatching.", 'warning');
}

function getTimeOffForDate(dateStr) {
  const real = _getApprovedTimeOffForDate(dateStr);
  // Approved time off on the same day wins (one chip, not two); a person no longer on the board never paints.
  const offIds = new Set(real.filter(r => r.type !== 'days_on').map(r => r.personId));
  const rot = _rotationsFor(dateStr)
    .filter(x => !offIds.has(x.personId) && (DispatchState.people || []).some(p => p.id === x.personId))
    .map(x => ({ id: `rot_${x.personId}_${dateStr}`, personId: x.personId, type: 'rotation', _rotation: true, rotationLabel: x.label }));
  return rot.length ? real.concat(rot) : real;
}

function _getApprovedTimeOffForDate(dateStr) {
  return DispatchState.timeOffRequests.filter(req => {
    const start = req.startDate?.toDate ? formatDateISO(req.startDate.toDate()) : (typeof req.startDate === 'string' ? req.startDate : formatDateISO(new Date(req.startDate)));
    const end = req.endDate?.toDate ? formatDateISO(req.endDate.toDate()) : (typeof req.endDate === 'string' ? req.endDate : formatDateISO(new Date(req.endDate)));
    if (dateStr < start || dateStr > end) return false;
    if (Array.isArray(req.excludedDates) && req.excludedDates.includes(dateStr)) return false;
    return true;
  });
}

// ─── TASK-10184 — warn (never block) when crew is put on a day they have
// APPROVED time off. Same coverage rule as the server (functions/src/services/
// timeOff.js coversDate): `days_on` is an ON marker, not off; excludedDates are
// honoured; and days compare as UTC calendar days, where every writer's instant
// lands (UTC midnight, 12:00Z, local noon). getTimeOffForDate's local
// formatting reads a UTC-midnight row as the day before in Central, so it is
// not reused for a warning that names dates. Only people NEWLY put on the day
// are checked, so re-saving a day does not nag about someone already on it.
function _timeOffUtcKey(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  const d = v?.toDate ? v.toDate()
    : (typeof v === 'object' && typeof (v.seconds ?? v._seconds) === 'number') ? new Date((v.seconds ?? v._seconds) * 1000)
      : new Date(v);
  return isNaN(d?.getTime?.()) ? null : d.toISOString().slice(0, 10);
}
function _timeOffCoversKey(req, dateKey) {
  if (!req || req.status !== 'approved' || String(req.type || '').toLowerCase() === 'days_on') return false;
  const s = _timeOffUtcKey(req.startDate); const e = _timeOffUtcKey(req.endDate);
  if (!s || !e || e < s || dateKey < s || dateKey > e) return false;
  return !(Array.isArray(req.excludedDates) && req.excludedDates.some(x => String(x).slice(0, 10) === dateKey));
}
// [{ personId, name, type, start, end, days[] }] — one row per person + request.
function timeOffConflictsFor(personIds, dateKeys, prevIds = []) {
  const prev = new Set((prevIds || []).map(String));
  const ids = [...new Set((personIds || []).map(String))].filter(id => id && !prev.has(id));
  const keys = [...new Set((dateKeys || []).filter(k => /^\d{4}-\d{2}-\d{2}$/.test(String(k))))].sort();
  if (!ids.length || !keys.length) return [];
  const out = [];
  for (const id of ids) {
    // Pitfall #5 — a legacy day / request can carry a NAME instead of an id.
    const person = (DispatchState.people || []).find(p => p.id === id || p.name === id);
    const name = person?.name || id;
    for (const req of (DispatchState.timeOffRequests || [])) {
      const mine = req.personId === id || (person && (req.personId === person.id || (!req.personId && req.personName === person.name)));
      if (!mine) continue;
      const days = keys.filter(k => _timeOffCoversKey(req, k));
      if (!days.length) continue;
      out.push({ personId: person?.id || id, name, type: String(req.type || 'time off'), start: _timeOffUtcKey(req.startDate), end: _timeOffUtcKey(req.endDate), days });
    }
  }
  return out;
}
function timeOffConflictLine(c) {
  const fmt = (k) => new Date(k + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const type = c.type.replace(/_/g, ' ');
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);
  const range = c.start === c.end ? fmt(c.start) : `${fmt(c.start)}\u2013${fmt(c.end)}`;
  return `${c.name} is off \u2014 ${typeLabel}, ${range}.`;
}
// true = go ahead (nothing to warn about, or the dispatcher chose Assign anyway).
async function confirmTimeOffConflicts(personIds, dateKeys, prevIds) {
  return confirmTimeOffConflictList(timeOffConflictsFor(personIds, dateKeys, prevIds));
}
async function confirmTimeOffConflictList(conflicts) {
  if (!conflicts || !conflicts.length) return true;
  if (typeof window.showConfirm !== 'function') return true; // warn, never block
  const ok = await window.showConfirm(
    conflicts.length === 1 ? 'On time off' : 'Crew on time off',
    `${conflicts.map(timeOffConflictLine).join('\n')}\n\nAssign anyway?`,
    { confirmText: 'Assign anyway', cancelText: 'Go back' }
  );
  return ok === true;
}

// TASK — hover tooltip for the OFF/ON chips. Every writer of `timeOff`
// (dispatch.js submitDriverTimeOff, hr.js, timeoff.js, clock.js) stores the
// free-text note under `reason`, but none of the three board surfaces showed
// it, so a dispatcher looking at "OFF — Vacation" had to leave the board to
// find out why. Plain-text only: this feeds a native title="" attribute, so a
// newline renders as a line break and no layout/overflow risk is taken inside the
// scrollable week columns.
function buildTimeOffTooltip(req, name, typeLabel) {
  if (req._rotation) return `ROTATION: ${name} — regular days off${req.rotationLabel ? ` (${req.rotationLabel})` : ''}`;
  // `days_on` is a stored enum value, not display text — the raw label reads
  // "Days_on". Only the tooltip is normalised; the card text is left alone.
  const label = String(typeLabel || '').replace(/_/g, ' ');
  const head = `${req.type === 'days_on' ? 'ON' : 'OFF'}: ${name} — ${label}`;
  const range = _timeOffRangeLabel(req);
  const reason = (req.reason || '').trim();
  return [range ? `${head} (${range})` : head, reason].filter(Boolean).join('\n');
}

// Accepts the same three shapes getTimeOffForDate parses (Timestamp, ISO
// string, date-ish) and returns a short display range, or '' if unparseable —
// a bad date must never blank the whole tooltip.
function _timeOffRangeLabel(req) {
  const toDate = (v) => {
    if (!v) return null;
    // A date-only string is parsed as UTC midnight by the spec, which renders as
    // the PREVIOUS day in Central — the same midnight-local trap the jobDay rows
    // anchor around. Pin it to noon local, matching how every writer stores it.
    const d = v?.toDate ? v.toDate()
      : (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(v + 'T12:00:00') : new Date(v));
    return isNaN(d?.getTime?.()) ? null : d;
  };
  const start = toDate(req.startDate);
  const end = toDate(req.endDate);
  if (!start) return '';
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const a = fmt(start);
  const b = end ? fmt(end) : '';
  return !b || a === b ? a : `${a} - ${b}`;
}

function getOpportunityHoldsForDate(dateStr) {
  return (DispatchState.opportunityHolds || []).filter(hold => {
    if (!hold.startDate || !hold.endDate) return false;
    return dateStr >= hold.startDate && dateStr <= hold.endDate;
  });
}

// TASK-9657 S0 — the ONE vehicleHolds day rule (window.SafetyHold, shared with
// the connector's create_job / assign_crew warning and buildAsset).
function getVehicleHoldsForDate(dateStr) {
  return (DispatchState.vehicleHolds || []).filter(hold => window.SafetyHold.vehicleHoldCoversDay(hold, dateStr));
}

function renderOpportunityHoldCard(hold) {
  const STAGE_COLORS = {
    initiating: '#94A3B8', qualifying: '#F59E0B', prospecting: '#3B82F6',
    negotiating: '#06B6D4', closing: '#10B981', won: '#8B5CF6', lost: '#EF4444'
  };
  const color = STAGE_COLORS[hold.stage] || '#94A3B8';
  const assetStr = (hold.assets || []).map(a => a.assetUnit).filter(Boolean).join(', ');
  const probPct = hold.probability != null ? hold.probability + '%' : '';

  return `
    <div class="opp-hold-card" style="border-left-color:${color};background:${color}10;" onclick="window.CRMModule?.openCustomerDetail?.('${escJsAttr(hold.customerId)}')">
      <div style="display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:${color};">handshake</span>
        <span class="opp-hold-card-title">${escapeHtml(hold.customerName || 'Customer')}</span>
      </div>
      <div class="opp-hold-card-meta">
        ${escapeHtml(hold.opportunityTitle || '')}
        ${probPct ? ` (${escapeHtml(probPct)})` : ''}
        ${hold.revenue ? ` &middot; $${escapeHtml((hold.revenue || 0).toLocaleString())}` : ''}
        ${assetStr ? ` &middot; ${escapeHtml(assetStr)}` : ''}
      </div>
    </div>
  `;
}

function renderVehicleHoldCard(hold) {
  const unit = hold.assetUnit || hold.assetId || '?';
  const reason = hold.reason || 'Vehicle hold';
  const dateRange = hold.startDate && hold.endDate
    ? `${hold.startDate} – ${hold.endDate}`
    : '';
  const assetId = hold.assetId || '';
  return `
    <div class="vehicle-hold-card" onclick="window.DispatchModule.openMaintenanceForVehicle('${escJsAttr(assetId)}')">
      <div class="vehicle-hold-icon"><span class="material-icons">do_not_disturb_on</span></div>
      <div class="vehicle-hold-content">
        <div class="vehicle-hold-title">Unit ${escapeHtml(unit)} — Out of Service</div>
        <div class="vehicle-hold-desc">${escapeHtml(truncateText(reason, 40))}${dateRange ? ` &middot; ${escapeHtml(dateRange)}` : ''}</div>
      </div>
    </div>
  `;
}

function openMaintenanceForVehicle(assetId) {
  // Navigate to maintenance view; the maintenance module will show vehicle holds
  window.location.hash = '#/maintenance';
}

// Device-local YYYY-MM-DD — the same day convention as the board's dateStr.
function _localYmd(date) {
  return date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');
}

// TASK-9886 — jobDay.drivers refs → [{ id, name }] via the legacy dual lookup
// (id OR name — GAS-imported days hold names). An unresolved ref is kept as
// both id and name so a pretrip naming it still matches.
function _dvirDriversFor(jobDay) {
  return (Array.isArray(jobDay?.drivers) ? jobDay.drivers : []).filter(Boolean).map(ref => {
    const p = (DispatchState.people || []).find(x => x.id === ref || x.name === ref);
    return p ? { id: p.id, name: p.name || '' } : { id: String(ref), name: String(ref) };
  });
}

// TASK-9886 — does this truck have the DVIR(s) it owes on dateStr? Owed per
// truck + DRIVER + day: every assigned driver needs their own; no driver on
// the day = any DVIR on the truck clears it. `drivers` overrides the jobDay's
// list (the PIN card asks about the viewer only).
function hasDvirForTruck(truckId, dateStr, drivers) {
  const DC = window.DvirCoverage;
  if (!DC) return true; // mirror missing — never claim a DVIR is missing on a broken load
  return DC.truckCovered(DispatchState.pretripCoverage, truckId, dateStr, drivers || []);
}

// TASK-4536 — the DVIR/JSA lookup caches are 30-DAY WINDOWED (see the pretrip
// and jsa subscriptions above). For a date older than the window, "no cache
// hit" means "the cache cannot see that far back", NOT "missing" — search
// cards reach any date, and rendering the warning there is a FALSE compliance
// claim on an insurer-facing screen. Suppress the badge instead of widening
// the caches (perf). 29 days, not 30: the subscription cutoff is a timestamp
// 30 days ago at now-o'clock, so the boundary DAY is only partially covered —
// claim only days the cache fully sees.
function _indicatorWindowCovers(dateStr) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 29);
  const cutoffStr = cutoff.getFullYear() + '-' + String(cutoff.getMonth() + 1).padStart(2, '0') + '-' + String(cutoff.getDate()).padStart(2, '0');
  return dateStr >= cutoffStr;
}

// Get missing DVIR indicator HTML for a job day card
// Returns HTML string or empty string if no trucks missing DVIRs
function getMissingDvirIndicator(jobDay, dateStr, mode) {
  if (!jobDay.trucks || jobDay.trucks.length === 0) return '';

  const today = new Date();
  const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  const isPast = dateStr < todayStr;
  const isToday = dateStr === todayStr;

  // Only show for past dates and today — not future
  if (!isPast && !isToday) return '';
  if (!_indicatorWindowCovers(dateStr)) return ''; // TASK-4536 — cache can't see that far back
  // TASK-9886 — the coverage map is not loaded (or failed), or starts after this
  // day (a PIN session reads today only): "no hit" would be a false claim.
  const _covFrom = DispatchState.pretripCoverageFromDay;
  if (!_covFrom || dateStr < _covFrom) return '';

  // TASK-9886 — whose DVIR is owed. A PIN driver's card asks about the VIEWER
  // only (it can read only its own pretrips, and a crewmate's missing DVIR is
  // not this driver's to fix); a viewer who is not a driver on the day (helper)
  // owes none. Office surfaces ask about every assigned driver.
  let _owing = _dvirDriversFor(jobDay);
  if (window.AppState?.driverModePin === true) {
    const pid = window.AppState?.driverPersonId;
    const me = pid ? (DispatchState.people || []).find(p => p.id === pid) : null;
    const mine = _owing.filter(d => (pid && d.id === pid) || (me?.name && d.name === me.name));
    if (mine.length === 0) return '';
    _owing = [{ id: pid, name: me?.name || '' }];
  }

  // Find trucks missing DVIRs
  const missingTrucks = [];
  jobDay.trucks.forEach(ref => {
    const truck = _resolveFleetRef(ref, DispatchState.trucks);
    const truckId = truck ? truck.id : ref;
    const truckName = truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name)) : ref;
    if (!hasDvirForTruck(truckId, dateStr, _owing)) {
      missingTrucks.push(truckName);
    }
  });

  if (missingTrucks.length === 0) return '';

  if (mode === 'driver-card') {
    // Full crew-line style for driver mode cards
    const label = isPast ? 'Missing DVIR' : 'No DVIR yet';
    return `<div class="crew-line missing-dvir-line ${isPast ? 'past' : 'today'}"><span class="material-icons">warning</span> ${label} &mdash; ${missingTrucks.map(t => escapeHtml(t)).join(', ')}</div>`;
  }

  // Compact indicator for week/list view — clickable warning icon
  const label = isPast ? 'Missing DVIR' : 'No DVIR yet';
  const trucksJson = escapeHtml(JSON.stringify(missingTrucks));
  return `<span class="assign-indicator missing-dvir ${isPast ? 'past' : 'today'}" onclick="event.stopPropagation(); window.DispatchModule.showMissingDvirPopover(event, '${escJsAttr(isPast ? 'past' : 'today')}', ${trucksJson})"><span class="material-icons" style="font-size:14px;">warning</span></span>`;
}

// Check if a JSA has been submitted for a given jobDay
function hasJsaForJobDay(jobDayId) {
  return !!DispatchState.jsaJobDayIds[jobDayId];
}

// Get missing JSA indicator HTML for a job day card
function getMissingJsaIndicator(jobDay, dateStr, mode) {
  const today = new Date();
  const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  const isPast = dateStr < todayStr;
  const isToday = dateStr === todayStr;

  // Only show for past dates and today — not future
  if (!isPast && !isToday) return '';
  if (!_indicatorWindowCovers(dateStr)) return ''; // TASK-4536 — cache can't see that far back

  // Check if a JSA exists for this jobDay
  if (hasJsaForJobDay(jobDay.id)) return '';

  const timeClass = isPast ? 'past' : 'today';

  if (mode === 'driver-card') {
    const label = isPast ? 'Missing JSA' : 'No JSA yet';
    return `<div class="crew-line missing-jsa-line ${timeClass}" onclick="event.stopPropagation(); window.DispatchModule.showMissingJsaPopover(event, '${escJsAttr(jobDay.id)}', '${escJsAttr(timeClass)}')"><span class="material-icons">health_and_safety</span> ${label}</div>`;
  }

  // Compact indicator for week/list view — clickable
  return `<span class="assign-indicator missing-jsa ${timeClass}" onclick="event.stopPropagation(); window.DispatchModule.showMissingJsaPopover(event, '${escJsAttr(jobDay.id)}', '${escJsAttr(timeClass)}')" title="${isPast ? 'Missing JSA' : 'No JSA yet'}"><span class="material-icons" style="font-size:14px;">health_and_safety</span></span>`;
}

// TASK-794: resolve the jsaReport id for a job day (prefer exact jobDay match,
// fall back to the job-level most-recent). Returns null when none exists or the
// only report is a dismissed placeholder. Drivers never reach this — the
// jsaReports subscription that populates these lookups is gated !isDriverPinMode.
function getJsaIdForJobDay(jobDay) {
  if (!jobDay) return null;
  return DispatchState.jsaIdByJobDay?.[jobDay.id]
    || (jobDay.jobId ? DispatchState.jsaIdByJob?.[jobDay.jobId] : null)
    || null;
}

// TASK-794: "JSA ✓ · Get Signatures" indicator — shown on the dispatch card when
// a real JSA exists for the job day. Opens the existing dispatcher link+QR modal
// (showJsaSignatureLink) so crew/anyone-on-site can scan to sign. Dispatcher-only
// by construction (driver-PIN sessions have no jsaReports data — see above).
function getJsaPresentIndicator(jobDay, mode) {
  const jsaId = getJsaIdForJobDay(jobDay);
  if (!jsaId) return '';
  // TASK-9371 — more than one JSA on this day → a tappable "N JSAs ›" pill
  // (UX Lead: must LOOK tappable, chevron not plain text) that opens the list;
  // exactly one keeps the original single-JSA affordance below.
  const list = jobDay?.id ? (DispatchState.jsaListByJobDay?.[jobDay.id] || []) : [];
  if (list.length > 1) {
    const stale = list.some(s => s.signaturesStale);
    const label = `${list.length} JSAs`;
    const pillStyle = `display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:${stale ? '#FEF2F2' : '#ECFDF5'};color:${stale ? '#991B1B' : '#059669'};border:1px solid ${stale ? '#FCA5A5' : '#A7F3D0'};font-size:0.78rem;font-weight:600;cursor:pointer;`;
    const onclick = `event.stopPropagation(); window.DispatchModule.showJsaListForJobDay('${escJsAttr(jobDay.id)}')`;
    if (mode === 'driver-card') {
      return `<div class="crew-line jsa-present-line" style="margin-top:4px;"><span style="${pillStyle}" onclick="${onclick}"><span class="material-icons" style="font-size:16px;">verified</span> ${label}${stale ? ' · re-sign' : ''} <span class="material-icons" style="font-size:16px;">chevron_right</span></span></div>`;
    }
    return `<span class="assign-indicator jsa-present" style="${pillStyle}" onclick="${onclick}" title="${list.length} JSAs filed on this day — tap to see them"><span class="material-icons" style="font-size:14px;">verified</span> ${label} <span class="material-icons" style="font-size:14px;">chevron_right</span></span>`;
  }
  // TASK-9344 — this used a `safeId` built with .replace(/'/g, '&#39;'), which
  // never protected a JS string inside an attribute: the HTML parser decodes
  // &#39; back to ' before the handler compiles. jsaId is a jsaReports DOC ID,
  // and that collection's create rule is canWriteTenant (driver-PIN sessions
  // included), so a client setDoc can choose it. escJsAttr, at each
  // interpolation below.
  if (mode === 'driver-card') {
    return `<div class="crew-line jsa-present-line" style="color:#059669;font-size:0.8rem;font-weight:600;display:flex;align-items:center;gap:4px;margin-top:4px;cursor:pointer;" onclick="event.stopPropagation(); window.DispatchModule.openJsaSignatures('${escJsAttr(jsaId)}')"><span class="material-icons" style="font-size:16px;color:#059669;">verified</span> JSA · Get Signatures</div>`;
  }
  return `<span class="assign-indicator jsa-present" style="color:#059669;cursor:pointer;" onclick="event.stopPropagation(); window.DispatchModule.openJsaSignatures('${escJsAttr(jsaId)}')" title="JSA filed — Get Signatures (show signing link / QR)"><span class="material-icons" style="font-size:14px;color:#059669;">verified</span></span>`;
}

// TASK-1540: the dispatch-card warning for jobDayOnClockNothingBillable (see
// that helper for the why). Follows the getMissingDvirIndicator family shape:
// (jobDay, dateStr, mode) -> HTML string, '' when not applicable.
//
// The element is ALWAYS emitted (display:none when clean) so the clock-entry
// onSnapshot can flip it live via updateClockedInIndicators without a full
// board re-render — a punch landing mid-view surfaces the warning immediately.
// The user-facing signal is still by PRESENCE: an amber badge appears when the
// state is bad, nothing shows when it's fine.
//
// Deliberately NOT rendered for driver-PIN sessions: billing visibility is a
// dispatcher concern, and a PIN session's _clockEntries subscription is scoped
// to the driver's own entries (TASK-452) so the condition would under-fire
// there anyway. Future days never get the element — an active punch belongs to
// today (or a still-open overnight shift from a past day), never tomorrow.
// Colors = DESIGN.md Warning alert palette (#FEF3C7 / #92400E / #FCD34D);
// inline because dispatch indicators of this family carry their own styling
// (see getJsaPresentIndicator) and this lane doesn't touch the CSS bundle.
function getOnClockNothingBillableBadge(jobDay, dateStr, mode) {
  if (window.AppState?.driverModePin) return '';
  if (!jobDay || !jobDay.id || !jobDay.jobId) return '';

  // Local-date "today", same convention as getMissingDvirIndicator.
  const today = new Date();
  const todayStr = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  if (dateStr > todayStr) return '';

  const active = jobDayOnClockNothingBillable(jobDay);
  const title = 'Someone is clocked in on this job, but this day has no trucks, trailers, or assets attached — the ticket will bill $0. Assign equipment (or whatever this job bills) now, while they are still on the clock.';
  const compact = mode === 'compact';
  return `<div class="ocnb-badge" data-ocnb-jobday="${jobDay.id}" title="${title}" style="display:${active ? 'flex' : 'none'};align-items:center;gap:${compact ? '3px' : '4px'};width:fit-content;max-width:100%;margin-top:${compact ? '2px' : '4px'};padding:${compact ? '1px 5px' : '2px 8px'};border-radius:${compact ? '4px' : '6px'};background:#FEF3C7;border:1px solid #FCD34D;color:#92400E;font-size:${compact ? '0.6rem' : '0.72rem'};font-weight:700;letter-spacing:0.02em;line-height:1.3;"><span class="material-icons" style="font-size:${compact ? '11px' : '14px'};color:#D97706;flex-shrink:0;">warning</span><span>ON CLOCK — NOTHING BILLABLE</span></div>`;
}

// TASK-9500 — the driver's "I'm here" taps (jobDays/{id}.stopArrivals, written
// only by the stampStopArrival callable) on the board card. Worded "Reported
// by driver" on purpose: the location is whatever the phone said (spoofable),
// so this is never shown as proof of arrival. Office sessions only. A stamp
// whose jobDayId is not this day (a copied day) is ignored.
function _stopArrivalsForDay(job, jobDay) {
  const map = jobDay && jobDay.stopArrivals;
  if (!map || typeof map !== 'object') return [];
  const wps = Array.isArray(job && job.waypoints) ? job.waypoints : [];
  const out = [];
  for (const [stopId, st] of Object.entries(map)) {
    if (!st || typeof st !== 'object' || (st.jobDayId && st.jobDayId !== jobDay.id)) continue;
    const ms = st.at?.toMillis?.() || (typeof st.at?.seconds === 'number' ? st.at.seconds * 1000 : 0);
    if (!ms) continue;
    const idx = wps.findIndex(w => w && w.id === stopId);
    const nm = idx >= 0 ? String(wps[idx].name || '').trim() : '';
    out.push({ ms, st, stopId, label: idx < 0 ? 'Removed destination' : `Destination ${idx + 1}${nm && nm.toLowerCase() !== 'location' ? ' · ' + nm : ''}` });
  }
  return out.sort((a, b) => a.ms - b.ms);
}
// One amber/blue chip, the ocnb-badge family's sizing.
function _boardChip(attrs, show, compact, colors, inner) {
  const c = compact;
  return `<div ${attrs} style="display:${show ? 'flex' : 'none'};align-items:center;gap:${c ? 3 : 4}px;width:fit-content;max-width:100%;margin-top:${c ? 2 : 4}px;padding:${c ? '1px 5px' : '2px 8px'};border-radius:${c ? 4 : 6}px;${colors};font-size:${c ? '0.6rem' : '0.72rem'};font-weight:700;line-height:1.3;">${inner}</div>`;
}
const _fmtClockTime = (ms) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
function getStopArrivalChip(job, jobDay, mode) {
  if (window.AppState?.driverModePin || !job || !jobDay || !jobDay.id) return '';
  const list = _stopArrivalsForDay(job, jobDay);
  if (!list.length) return '';
  const n = (job.waypoints || []).filter(w => w && w.id).length;
  const more = list.length > 1 || n > 1 ? ` (${list.length} of ${Math.max(n, list.length)} destinations)` : '';
  const ft = (m) => Math.round(m * 3.281) + ' ft';
  const lines = list.map(({ ms, st, label }) => `${label}: reported by ${st.personName || 'driver'} ${_fmtClockTime(ms)} — `
    + (typeof st.lat !== 'number' ? 'no phone location'
      : typeof st.distM === 'number' ? `phone said ~${st.distM < 1609 ? ft(st.distM) : (st.distM / 1609.34).toFixed(1) + ' mi'} from the destination${typeof st.acc === 'number' ? ` (±${ft(st.acc)})` : ''}` : 'phone location saved'));
  lines.push("Reported by the driver's phone — not proof of arrival.");
  return _boardChip(`class="stop-arrival-chip" title="${escapeHtml(lines.join('\n'))}"`, true, mode === 'compact', 'background:#EFF6FF;border:1px solid #93C5FD;color:#1E3A8A',
    `<span class="material-icons" style="font-size:1.4em;color:#1D4ED8;">where_to_vote</span><span>Reported by driver ${escapeHtml(_fmtClockTime(list[list.length - 1].ms) + more)}</span>`
    + (_canClearStopArrival() ? `<button type="button" class="stop-arrival-clear" title="Clear a wrong stamp" onclick="event.stopPropagation();window.DispatchStopArrivalClear?.('${escJsAttr(jobDay.id)}')" style="margin-left:6px;padding:1px 8px;border:1px solid #93C5FD;border-radius:4px;background:#fff;color:#1E3A8A;font:inherit;font-weight:600;cursor:pointer;">Clear</button>` : ''));
}
// TASK-10073 — the office clears a wrong stamp (no driver undo, Chris
// 2026-10-25). Shown to the owner or a Dispatch member who is not view-only;
// the clearStopArrival callable is the real gate. The server clears only the
// stamp the office saw (same `at`) and writes the jobAuditLog row; the board
// updates from the jobDays listener, and the driver can then tap again.
function _canClearStopArrival() {
  const st = window.AppState || {};
  if (st.isDemo || st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (st.user?.uid && st.user.uid === st.tenantData?.ownerId) return true;
  const ma = st.memberAccess;
  if (!ma || ma.readOnly === true || ma.viewUploadOnly === true || (ma.status && ma.status !== 'active')) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('dispatch');
}
window.DispatchStopArrivalClear = async function clearStopArrivalPrompt(jobDayId) {
  const jobDay = (DispatchState.jobDays || []).find(d => d.id === jobDayId);
  const job = jobDay && (DispatchState.jobs || []).find(j => j.id === jobDay.jobId);
  const list = job ? _stopArrivalsForDay(job, jobDay) : [];
  if (!list.length) return;
  const tenantId = DispatchState.tenantId; // COD r1: fixed when the dialog opens
  const m = await __ddChunkWrap(import('./stopArrivalClear.js?v=10295')).catch(() => null);
  if (!m) { window.showToast?.('Could not open that. Check your connection and try again.', 'error'); return; }
  m.openClearStopArrival({
    items: list.map(({ ms, st, label }) => ({ label: `${label} — ${st.personName || 'driver'} ${_fmtClockTime(ms)}` })),
    clear: (i, reason) => httpsCallable(functions, 'clearStopArrival')({ tenantId, jobDayId, stopId: list[i].stopId, atMs: list[i].ms, reason }),
    toast: (msg, kind) => window.showToast?.(msg, kind),
  });
};

// TASK-9500 — an OPEN clock-in on this day's job that landed outside the job
// geofence (clockEntries.outOfGeofence, set at punch time — TASK-191; advisory,
// the punch went through). Same day-binding as jobDayOnClockNothingBillable.
// Always emitted (hidden when clean) so updateClockedInIndicators flips it live.
function _outOfZoneEntriesForDay(jobDay) {
  const jdDate = jobDay?.jobId && (jobDay.date?.toDate?.() || (jobDay.date ? new Date(jobDay.date) : null));
  if (!jdDate || isNaN(jdDate.getTime())) return [];
  const jdDay = formatDateISO(jdDate);
  const isTodayCard = jdDay === formatDateISOLocal(new Date());
  return (DispatchState._clockEntries || []).filter(e => {
    if (!e || e.jobId !== jobDay.jobId || e.clockOut || e.outOfGeofence !== true) return false;
    if (isTodayCard) return true;
    const cin = e.clockIn?.toDate?.() || new Date(e.clockIn);
    return !isNaN(cin.getTime()) && formatDateISOLocal(cin) === jdDay;
  });
}
function _outOfZoneChipInner(entries) {
  const e = entries[0];
  const txt = entries.length > 1 ? entries.length + ' clocked in out of zone'
    : (getPersonNameById(e.personId) || e.personName || 'Someone') + ' clocked in out of zone'
      + (typeof e.geofenceDistanceFt === 'number' ? ` (${Math.round(e.geofenceDistanceFt).toLocaleString('en-US')} ft from the site)` : '');
  return `<span class="material-icons" style="font-size:1.4em;color:#B45309;">wrong_location</span><span>${escapeHtml(txt)}</span>`;
}
function getOutOfZoneClockChip(jobDay, dateStr, mode) {
  if (window.AppState?.driverModePin || !jobDay || !jobDay.id || !jobDay.jobId) return '';
  if (dateStr > formatDateISOLocal(new Date())) return '';
  const entries = _outOfZoneEntriesForDay(jobDay);
  return _boardChip(`class="ooz-chip" data-ooz-jobday="${escapeHtml(jobDay.id)}" title="Clock-in was outside the job geofence (advisory — the punch still went through)."`,
    entries.length, mode === 'compact', 'background:#FEF3C7;border:1px solid #F59E0B;color:#92400E', entries.length ? _outOfZoneChipInner(entries) : '');
}

// TASK-794: open the existing JSA signing link+QR modal. Lives in jsa.js
// (window.JSAModule.showJsaSignatureLink) which mints the token + renders the QR.
function openJsaSignatures(jsaId) {
  if (window.JSAModule?.showJsaSignatureLink) {
    window.JSAModule.showJsaSignatureLink(jsaId);
  } else {
    window.showToast?.('Open Inspections → JSA to get signatures', 'info');
  }
}

// TASK-9371 — the list behind the dispatch card's "N JSAs" pill: every
// non-dismissed JSA on the jobDay (newest first) with time · format ·
// submitted-by · signature count · Revised / Re-sign badges and the same
// "Get Signatures" action the single-JSA indicator has. Read-only render over
// DispatchState.jsaListByJobDay — no JSA mutation logic lives in dispatch.js.
function showJsaListForJobDay(jobDayId) {
  const list = DispatchState.jsaListByJobDay?.[jobDayId] || [];
  if (!list.length) { window.showToast?.('No JSA on file for this day', 'info'); return; }
  const existing = document.getElementById('dispatch-jsa-list-overlay');
  if (existing) existing.remove();
  const overlay = document.createElement('div');
  overlay.id = 'dispatch-jsa-list-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  const rows = list.map(s => {
    const when = s.createdMs ? new Date(s.createdMs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
    const kind = s.formMode === 'xto' ? 'Oilfield JSA' : (s.formMode === 'template' ? escapeHtml(s.templateName || 'JSA') : 'JSA');
    const badges = [
      s.revisionCount > 0 ? `<span style="background:#FEF3C7;color:#92400E;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Revised ×${s.revisionCount}</span>` : '',
      s.signaturesStale ? `<span style="background:#FEF2F2;color:#991B1B;font-size:11px;padding:2px 8px;border-radius:999px;font-weight:600;">Re-sign needed</span>` : '',
    ].join('');
    return `
      <div style="border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;background:#fff;">
        <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;flex-wrap:wrap;">
          <div style="min-width:0;flex:1;">
            <div style="font-weight:600;color:var(--text-primary);">${escapeHtml(when)}${when ? ' · ' : ''}${kind}</div>
            <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;">${s.submittedByName ? escapeHtml(s.submittedByName) + ' · ' : ''}${s.signatureCount} ${s.signatureCount === 1 ? 'signature' : 'signatures'}</div>
          </div>
          ${badges ? `<div style="display:flex;gap:6px;flex-wrap:wrap;">${badges}</div>` : ''}
        </div>
        <button type="button" class="btn btn-secondary" style="width:100%;margin-top:8px;display:flex;align-items:center;gap:8px;justify-content:center;min-height:44px;" onclick="event.stopPropagation();document.getElementById('dispatch-jsa-list-overlay')?.remove();window.DispatchModule.openJsaSignatures('${escJsAttr(s.id)}')">
          <span class="material-icons" style="color:#10B981;">qr_code_2</span> Get Signatures
        </button>
      </div>`;
  }).join('');
  overlay.innerHTML = `
    <div style="background:#fff;border-radius:14px;max-width:520px;width:100%;max-height:85vh;max-height:85dvh;overflow:auto;padding:20px;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:12px;">
        <h3 style="margin:0;font-size:18px;color:var(--text-primary);">${list.length} JSAs on this day</h3>
        <button type="button" aria-label="Close" onclick="document.getElementById('dispatch-jsa-list-overlay').remove()" style="background:transparent;border:0;cursor:pointer;color:var(--text-secondary);">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="display:flex;flex-direction:column;gap:8px;">${rows}</div>
    </div>`;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  document.body.appendChild(overlay);
}

function getPersonNameById(personId) {
  if (!personId) return null;
  const person = DispatchState.people.find(p => p.id === personId || p.name === personId);
  // No-match returns NULL, never the raw id (2026-08-12, Kegan): a TERMINATED
  // person drops out of DispatchState.people, and returning the id here made
  // every caller's `|| req.personName` fallback dead code — the board rendered
  // "eSmcKsn1h9f..." on his still-approved days off. All 3 callers chain
  // `|| req.personName || 'Unknown'`, which is the right display for a person
  // who no longer resolves. (A legacy name-string ref still resolves via the
  // p.name === personId arm above — that path is unchanged.)
  return person ? person.name : null;
}

function getTruckUnitById(truckId) {
  if (!truckId) return null;
  const truck = DispatchState.trucks.find(t => t.id === truckId);
  return truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name)) : null;
}

// TASK-9198 — resolve the ONE fleet item a safety hold points at, across ALL
// THREE fleet lists. Why this exists: pretrip writes every maintenance doc with
// assetType:'truck' and parks the doc id in `truckId` even when the defect is on
// a TRAILER (live TITS: trailer 109's open critical air-leak write-up sits in
// truckId with assetType:'truck'). So `assetType` is NOT trustworthy and is
// deliberately never consulted here — we resolve by reference, not by label.
// COLLISION BOUND: every doc-id ref is exhausted across trucks → trailers →
// assets BEFORE any unit-string ref is considered, so a truck and a trailer both
// numbered "109" cannot cross-warn off a legacy `assetUnit`. Reuses
// _resolveFleetRef (TASK-4529, id-first-then-unit/nickname/name) per list rather
// than re-implementing matching — that resolver has 7 byte-identical copies
// pinned by tests/task4529-fleet-ref-resolver.unit.cjs, so it is CALLED, never
// edited. Ref order for trucks is unchanged from the original TASK-462 matcher
// (truckId → assetId → assetUnit), so truck behaviour is preserved exactly.
const _SAFETY_HOLD_FLEETS = [
  { kind: 'truck',   noun: 'Unit' },
  { kind: 'trailer', noun: 'Trailer' },
  { kind: 'asset',   noun: 'Asset' }
];

// wo-legacy-followups — the WHOLE fleet (active + retired, as read) for the
// imported-order name rule; each list is set once its snapshot lands, and stays
// null until then. A name unique in a half-loaded or active-only fleet must not
// attach a hold (a retired unit's name stays ambiguous, as on Maintenance).
function _setFleetAll(kind, docs) {
  if (!DispatchState._fleetAll) DispatchState._fleetAll = { trucks: null, trailers: null, assets: null };
  DispatchState._fleetAll[kind] = docs;
}

// wo-legacy-followups — an IMPORTED (old-app) work order names its unit by TYPED
// NAME; its assetId is the old app's ROW NUMBER (trucks and trailers were numbered
// separately, so "6" is a truck's row AND a trailer's). Resolved by the shared
// rule (window.LegacyWorkOrder, the Maintenance screen's), never by that number
// or a unit-string guess. No unique match (or the fleet still loading) → no
// target: the hold card still shows with the typed name, and NO unit is warned.
function _legacyHoldTarget(hold, lists) {
  const L = window.LegacyWorkOrder;
  const all = DispatchState._fleetAll;
  if (!L || !all || !all.trucks || !all.trailers || !all.assets) return null;
  const hit = L.resolveLegacyVehicle(hold, all);
  if (!hit) return null;
  const entry = _SAFETY_HOLD_FLEETS.find(e => e.kind === hit.kind);
  const item = entry && (lists[hit.kind] || []).find(x => x && x.id === hit.v.id);
  return item ? { item, kind: entry.kind, noun: entry.noun } : null;
}

function resolveSafetyHoldTarget(hold, fleets) {
  if (!hold) return null;
  const lists = fleets || {
    truck: DispatchState.trucks || [],
    trailer: DispatchState.trailers || [],
    asset: DispatchState.assets || []
  };
  if (window.SafetyHold?.isLegacyOrderShape?.(hold) || window.LegacyWorkOrder?.isLegacyWorkOrder?.(hold)) {
    return _legacyHoldTarget(hold, lists);
  }
  const idRefs = [hold.truckId, hold.trailerId, hold.assetId];
  const unitRefs = [hold.assetUnit];
  for (const refs of [idRefs, unitRefs]) {
    for (const ref of refs) {
      if (ref === null || ref === undefined || ref === '') continue;
      for (const entry of _SAFETY_HOLD_FLEETS) {
        const item = _resolveFleetRef(ref, lists[entry.kind]);
        if (item) return { item, kind: entry.kind, noun: entry.noun };
      }
    }
  }
  return null;
}

// TASK-9198 — label for a safety hold. Trailers and trucks both populate `unit`
// on this codebase (verified on live TITS: 12/12 trailers have one), so once the
// item resolves, getUnitDisplay handles the nickname case ("Lowboy — #109").
// ASSETS are different: they carry NO unit and NO nickname, only `name`, which
// is why the noun is part of the label instead of a hardcoded "Unit".
// Last resort: the maintenance doc already carries a DENORMALIZED unit that no
// renderer consulted before — use it so a hold on a deleted/hidden vehicle still
// shows a number instead of "?".
function getSafetyHoldLabel(hold) {
  const target = resolveSafetyHoldTarget(hold);
  if (target) {
    const disp = window.getUnitDisplay
      ? window.getUnitDisplay(target.item)
      : (target.item.unit || target.item.nickname || target.item.name);
    if (disp) return { noun: target.noun, text: String(disp), kind: target.kind };
  }
  const denorm = hold?.trailerUnit || hold?.truckUnit || hold?.assetUnit || hold?.assetName;
  if (denorm) return { noun: 'Unit', text: String(denorm), kind: null };
  return { noun: 'Unit', text: '?', kind: null };
}

function truncateText(text, maxLength) {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

function formatAssignments(jobDay) {
  const parts = [];

  if (jobDay.drivers?.length > 0) {
    // TASK-9763 S3 — drivers in "first on" order when the day stores one or the
    // company ranks seniority; otherwise exactly the stored order (unchanged).
    let _drv = jobDay.drivers;
    try {
      const _CP = window.CrewPairing;
      // personKey: a legacy name ref on the day resolves to its doc id, so it
      // still finds its rank in settings.crewSeniority (COD Code Reviewer r1).
      const _pk = (ref) => { const p = DispatchState.people.find(x => x.id === ref || x.name === ref); return p ? p.id : ref; };
      const _v = _CP && jobDay.drivers.length > 1 ? _CP.orderView(jobDay, window.AppState?.settings?.crewSeniority, { personKey: _pk }) : null;
      if (_v && (_v.stored || _v.ranked)) {
        // Sort the day's own refs (id or legacy name) by their place in the
        // order; a ref the view somehow lacks sorts last, nothing is dropped.
        const _at = (ref) => { const i = _v.order.indexOf(_pk(ref)); return i < 0 ? 1e9 : i; };
        _drv = jobDay.drivers.map((ref, i) => ({ ref, i })).sort((a, b) => (_at(a.ref) - _at(b.ref)) || (a.i - b.i)).map(x => x.ref);
      }
    } catch (_) { /* display only */ }
    const names = _drv.map(ref => {
      // Support both Firestore ID and name string (legacy GAS imports)
      const p = DispatchState.people.find(x => x.id === ref || x.name === ref);
      if (p) return p.name.split(' ')[0];
      // If no match, try to extract first name from legacy string
      if (typeof ref === 'string' && ref.includes(' ')) return ref.split(' ')[0];
      return ref;
    });
    parts.push(`<span class="assignment-badge driver">${names.map(escapeHtml).join(', ')}</span>`);
  }

  if (jobDay.trucks?.length > 0) {
    const names = jobDay.trucks.map(ref => {
      // Support both Firestore ID and unit/name string (legacy GAS imports)
      const t = _resolveFleetRef(ref, DispatchState.trucks);
      return t ? (window.getUnitDisplay?.(t) || t.name || ref) : ref;
    });
    parts.push(`<span class="assignment-badge truck">${names.map(escapeHtml).join(', ')}</span>`);
  }

  if (jobDay.assets?.length > 0) {
    const names = jobDay.assets.map(ref => {
      const a = _resolveFleetRef(ref, DispatchState.assets);
      return a ? a.name : ref;
    });
    parts.push(`<span class="assignment-badge asset">${names.map(escapeHtml).join(', ')}</span>`);
  }

  if (jobDay.subcontractors?.length > 0) {
    const names = jobDay.subcontractors.map(s => s.vendorName || 'Sub');
    parts.push(`<span class="assignment-badge subcontractor">${names.map(escapeHtml).join(', ')}</span>`);
  }

  return parts.join(' ') || '<span class="unassigned">Unassigned</span>';
}

function escapeHtml(str) {
  if (str == null) return '';
  // Convert to string in case it's a number, object, or array
  const s = String(str);
  return s.replace(/[&<>"']/g, m => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[m]));
}

// TASK-9287 — delegating wrapper for the shared JS-STRING-IN-AN-ATTRIBUTE escaper
// (sharedFormat.js, TASK-4838). For `onclick="window.Mod.fn('${…}')"` ONLY: the
// HTML parser decodes `&#39;` back to an apostrophe BEFORE the handler compiles,
// so the escapeHtml above gives that position no protection. Hoisted wrapper,
// window lookup deferred to call time (Pitfall #1 — never import sharedFormat).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

function getCustomerById(customerIdOrName) {
  if (!customerIdOrName) return null;
  // Dual lookup: support both ID and name for legacy GAS data
  return DispatchState.customers.find(c => c.id === customerIdOrName || c.name === customerIdOrName);
}

// TASK-1385 — Dispatch Settings toggle: lead job placards with the customer
// name (Cassian request 2026-08-06). Off by default; applies to the calendar
// job cards (week/month/day/search). Jobs with no customer fall back to title.
// TASK-4688 generalised this 1-bit setting into settings.placardTokens, an
// ordered array. This boolean is the back-compat floor and is still read.
function isPlacardCustomerFirst() {
  return window.AppState?.settings?.placardCustomerFirst === true;
}

// TASK-4688 — composable job card title.
// Measured on Split Rock (124 jobs, 2026-08-31): 91 titles hand-repeat the
// customer the dispatcher had just picked, and wellName — filled 112 times —
// never reaches the board at all. One ordered token list fixes both.
const PLACARD_TOKEN_DEFS = [
  { key: 'customer', label: 'Customer' },
  { key: 'title',    label: 'Job title' },
  { key: 'type',     label: 'Job type' },
  { key: 'wellName', label: 'Well / pad name' },
  { key: 'location', label: 'Location' },
  { key: 'project',  label: 'Project' },
  { key: 'po',       label: 'PO number' },
  // TASK-9278 — the job number. NEVER added to getPlacardTokens()'s legacy
  // fallback: every tenant that has not saved an order runs that fallback, and
  // its ' — ' join would turn today's badge into `J-1054 — Pronghorn` on every
  // board before anyone touched a setting. Legacy tenants keep the badge via
  // getPlacardTitleHtml(); the Dispatch Settings draft seeds it first instead.
  { key: 'jobNumber', label: 'Job number' },
];
const PLACARD_TOKEN_KEYS = PLACARD_TOKEN_DEFS.map(d => d.key);

// True only once the tenant has explicitly saved a token order. Everything
// downstream keys off this so a tenant that has never opened the new control
// gets byte-identical output from the TASK-1385 code path — separator included.
function _hasExplicitPlacardTokens() {
  const t = window.AppState?.settings?.placardTokens;
  return Array.isArray(t) && t.some(k => PLACARD_TOKEN_KEYS.includes(k));
}

function getPlacardTokens() {
  const s = window.AppState?.settings || {};
  if (_hasExplicitPlacardTokens()) {
    const seen = new Set();
    return s.placardTokens.filter(k => PLACARD_TOKEN_KEYS.includes(k) && !seen.has(k) && seen.add(k));
  }
  // Back-compat floor: the 1-bit ancestor.
  return isPlacardCustomerFirst() ? ['customer', 'title'] : ['title'];
}

// Returns a PLAIN STRING for one token. Never HTML — escaping stays at the
// call sites (escapeHtml at the three job-card render paths), which is where it
// already lives; moving it in here would double-escape those.
function getPlacardTokenValue(job, token) {
  if (!job) return '';
  switch (token) {
    case 'customer':
      return getCustomerById(job.customerId || job.customer)?.name || job.customer || '';
    case 'title':
      return job.title || '';
    // 122 of 124 Split Rock jobs carry the literal default 'job' — rendering
    // that on every card is noise, so the default type reads as empty.
    case 'type':
      // TASK-9390 — the LABEL (a renamed custom type shows its new name);
      // still blank for the default type.
      return _jobTypeIsDefault(job.type) ? '' : _jobTypeLabelForJob(job);
    case 'wellName':
      return job.wellName || '';
    case 'location':
      return job.location || '';
    case 'project':
      return job.projectId
        ? ((window.ProjectsModule?.getActiveProjects?.() || []).find(p => p.id === job.projectId)?.name || '')
        : '';
    case 'po':
      return jobPoLabel(job); // TASK-9811 S2: 'A (+1)'
    // TASK-9278 — '' for an unnumbered or jobNumberPending job, so it drops
    // out of the join exactly like an empty well name.
    case 'jobNumber':
      return formatJobNumber(job);
    default:
      return '';
  }
}

// ' · ' for an explicit token order, ' — ' for the legacy boolean, so an
// existing placardCustomerFirst tenant's board does not silently restyle.
function _placardSeparator() {
  return _hasExplicitPlacardTokens() ? ' · ' : ' — ';
}

function _placardValues(job, tokens) {
  return (tokens || []).map(t => String(getPlacardTokenValue(job, t) || '').trim());
}

// THE plain-text chokepoint. Callers: the month cell chip + its tooltip, the
// conflict index label, and (through getPlacardTitleHtml) the three job cards.
// Returns a plain string; do not fork it. Once a tenant saves an order that
// includes jobNumber, `J-1054` is part of this string like any other token.
function getPlacardTitle(job) {
  if (!job) return 'Untitled';
  const parts = _placardValues(job, getPlacardTokens()).filter(Boolean);
  return parts.length ? parts.join(_placardSeparator()) : (job.title || 'Untitled');
}

// TASK-9278 — the three job cards (Week/Month grid `.job-card`, List
// `.dispatch-job-card`, Search). Returns { lead, title }, both HTML:
//   lead  — goes where jobNumberBadgeHtml() used to sit (before the title span)
//   title — goes inside the title element, already escaped
// LEGACY PATH (no saved order — every tenant at build time): byte-identical to
// the old `${jobNumberBadgeHtml(job)}${escapeHtml(getPlacardTitle(job))}`.
// TOKEN PATH: walks the saved order. A leading job number becomes `lead`, so a
// jobNumber-first card looks exactly like the legacy one. A job number further
// along renders as the same badge inline, with no ' · ' next to it (the badge's
// own margin is the gap, as it always was).
// hideJobNumber — the List card passes isDriverMode. That suppression used to
// live at the call site; it lives HERE now because on the token path the badge
// comes out of the title, not out of a separate call the site could skip.
function getPlacardTitleHtml(job, opts = {}) {
  const hideNo = opts.hideJobNumber === true;
  if (!_hasExplicitPlacardTokens()) {
    return { lead: hideNo ? '' : jobNumberBadgeHtml(job), title: escapeHtml(getPlacardTitle(job)) };
  }
  const items = getPlacardTokens()
    .filter(t => !(hideNo && t === 'jobNumber'))
    .map(t => ({ badge: t === 'jobNumber', v: String(getPlacardTokenValue(job, t) || '').trim() }))
    .filter(x => x.v);
  if (!items.length) return { lead: '', title: escapeHtml((job && job.title) || 'Untitled') };
  let lead = '';
  if (items[0].badge) { lead = jobNumberBadgeHtml(job); items.shift(); }
  const sep = escapeHtml(_placardSeparator());
  let title = '';
  items.forEach((x, i) => {
    if (x.badge) {
      title += jobNumberBadgeHtml(job, i === items.length - 1 ? 'margin-left:6px;' : 'margin:0 6px;');
    } else {
      if (i > 0 && !items[i - 1].badge) title += sep;
      title += escapeHtml(x.v);
    }
  });
  return { lead, title };
}

// TASK-9513 (Chris 2026-10-05, reopening TASK-9278 Decision 2): the non-number cut is
// now ~20 characters (was 12); the chip's title= tooltip carries the FULL title.
// TASK-9278 Decision 2 (Chris) — the month chip truncates at 12 characters, so
// a joined `J-1054 · Pro` is the number plus debris. When the tenant's saved
// order includes jobNumber and this job has one, the chip is the number ALONE,
// wherever it sits in the order. Otherwise it is exactly what it was. The chip's
// title= tooltip keeps the full getPlacardTitle() string either way.
function getPlacardMonthChipLabel(job) {
  const MONTH_CHIP_MAX = 20;
  const no = getPlacardTokens().includes('jobNumber') ? formatJobNumber(job) : '';
  if (no) return no;
  const full = getPlacardTitle(job);
  return full.length > MONTH_CHIP_MAX ? full.substring(0, MONTH_CHIP_MAX - 1).trimEnd() + '…' : full;
}

// The sixth consumer — the day-details placard — is TWO LINES, not a joined
// string, so an ordered array does not map onto it directly (COD R3).
// Resolution: the first non-empty token becomes the headline, the rest become
// the sub-line. With no explicit token order this returns the TASK-1385
// two-field swap unchanged.
function getPlacardLines(job, customerNameFallback) {
  const fallbackCustomer = customerNameFallback || '';
  if (!_hasExplicitPlacardTokens()) {
    const first = isPlacardCustomerFirst();
    return {
      primary: first ? fallbackCustomer : (job.title || 'Untitled'),
      secondary: first ? (job.title || 'Untitled') : fallbackCustomer
    };
  }
  const tokens = getPlacardTokens();
  const vals = _placardValues(job, tokens);
  const firstIdx = vals.findIndex(Boolean);
  const primary = firstIdx === -1 ? (job.title || 'Untitled') : vals[firstIdx];
  const rest = vals.filter((v, i) => v && i !== firstIdx).join(_placardSeparator());
  // Customer is the historic sub-line here. If the tenant did not put customer
  // in the tokens, keep it rather than blanking a line they have always had.
  const secondary = rest || (tokens.includes('customer') ? '' : fallbackCustomer);
  return { primary, secondary };
}

// Generate consistent color for customer
const customerColors = [
  '#3B82F6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];

function getCustomerColor(customerIdOrName) {
  if (!customerIdOrName) return customerColors[0];
  // Use a simple hash to get consistent color
  let hash = 0;
  const str = String(customerIdOrName);
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return customerColors[Math.abs(hash) % customerColors.length];
}

function getGoogleMapsUrl(location) {
  if (!location) return '#';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
}

// Status border colors for job cards
const STATUS_COLORS = {
  pending: '#6B7280',
  scheduled: '#6B7280',
  assigned: '#3B82F6',
  'en-route': '#F59E0B',
  'on-site': '#10B981',
  completed: '#059669',
  finished: '#059669',
  cancelled: '#EF4444'
};

function getStatusColor(status) {
  return STATUS_COLORS[status] || STATUS_COLORS.pending;
}

function formatScheduledTime(job) {
  if (job.allDay || job.allDay === undefined) return null;
  return job.defaultStart || null;
}

function getDriverName(jobDay) {
  if (!jobDay.drivers || jobDay.drivers.length === 0) return null;
  const driverRef = jobDay.drivers[0];
  const s = String(driverRef);
  // Support both Firestore ID and name string (legacy GAS imports), handle numeric refs
  const _matchP = (p) => p.id === s || String(p.id) === s || p.name === s || p.name === driverRef;
  // renders-audit #13 — fall back to the unfiltered roster so a terminated
  // driver's name still renders on the days they actually worked.
  const driver = DispatchState.people.find(_matchP)
    || (DispatchState.peopleAll || []).find(_matchP);
  if (!driver) {
    // If no match found, it might be a legacy name string - return as-is
    if (typeof driverRef === 'string' && driverRef.includes(' ')) {
      const parts = driverRef.split(' ');
      return parts.length > 1 ? `${parts[0]} ${parts[1][0]}.` : parts[0];
    }
    return null; // Don't show raw numeric IDs
  }
  // Return first name and last initial
  const parts = driver.name.split(' ');
  if (parts.length > 1) {
    return `${parts[0]} ${parts[1][0]}.`;
  }
  return parts[0];
}

function getTruckUnit(jobDay) {
  if (!jobDay.trucks || jobDay.trucks.length === 0) return null;
  const truckRef = jobDay.trucks[0];
  // Support both Firestore ID and unit/name string (legacy GAS imports), handle numeric refs
  const truck = _resolveFleetRef(truckRef, DispatchState.trucks);
  return truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name)) : null; // Don't show raw numeric IDs
}

// ============================================
// EVENT HANDLERS
// ============================================
// First-run attention wiggle for the ⋮ overflow menu (new UI — Chris 2026-07-06).
// Plays a couple of shakes on the first 2 Dispatch visits, then stops; also
// stopped permanently the moment the user opens the menu (handler above).
// TASK-1263 — key bumped to _v2 because the control MOVED (into the view bar). Existing
// users had already burned the old counter, so they'd never see it in its new spot.
function _maybeWiggleMoreMenu() {
  let seen = 0;
  try { seen = parseInt(localStorage.getItem('dd_dispatch_more_seen_v2') || '0', 10) || 0; } catch (_) {}
  if (seen >= 2) return;
  const kebab = document.querySelector('.dispatch-more-btn');
  if (!kebab) return;
  try { localStorage.setItem('dd_dispatch_more_seen_v2', String(seen + 1)); } catch (_) {}
  // Delay so it plays after the board settles (not lost under the load spinner).
  setTimeout(() => {
    if (!document.body.contains(kebab)) return;
    kebab.classList.add('dispatch-more-wiggle');
    setTimeout(() => kebab.classList.remove('dispatch-more-wiggle'), 1600);
  }, 1200);
}

// TASK-1664 — keep the magnifier button telling the truth about what it does.
// getDispatchHTML() only runs on view entry, so the icon/label swap is done in
// the DOM at click time rather than in the template.
function syncDispatchSearchBtn() {
  const btn = document.querySelector('.dispatch-search-btn');
  if (!btn) return;
  const searching = DispatchState.viewMode === 'search';
  const icon = btn.querySelector('.material-icons');
  const label = btn.querySelector('.view-btn-label');
  if (icon) icon.textContent = searching ? 'close' : 'search';
  if (label) label.textContent = searching ? 'Close' : 'Search';
  btn.title = searching ? 'Close search' : 'Search Past Jobs';
  btn.setAttribute('aria-pressed', searching ? 'true' : 'false');
}

function setupEventHandlers() {
  // View toggle buttons
  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      // TASK-1664 — the magnifier is a TOGGLE, not a one-way door. On mobile the
      // search view swallows the board and the only other exit is the small
      // view-icon strip, which doesn't read as "leave search" — so tapping the
      // magnifier again returns to whatever view you were on when you entered.
      let targetView = btn.dataset.view;
      if (targetView === 'search') {
        if (DispatchState.viewMode === 'search') {
          targetView = DispatchState._preSearchViewMode || 'week';
        } else {
          DispatchState._preSearchViewMode = DispatchState.viewMode;
        }
      }
      const targetBtn = document.querySelector(`.view-btn[data-view="${targetView}"]`) || btn;
      document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
      targetBtn.classList.add('active');
      DispatchState.viewMode = targetView;
      DispatchState.listOffset = 0; // Reset list offset when changing views
      // TASK-583 — re-entering week view (e.g. from month/list) lands on today
      if (targetView === 'week') DispatchState._scrollWeekToToday = true;
      // TASK-424 #5 (C2): entering "Search Past Jobs" loads the full job/jobDay
      // history once (the live board is windowed; search must span everything).
      if (targetView === 'search') loadDispatchSearchHistory();
      syncDispatchSearchBtn();
      updateDateDisplay();
      _renderDispatchBoardNow(); // user clicked a view toggle — instant feedback
      syncLayerFilterChips();
    });
  });

  // Initial sync — chip row reflects state on first paint
  syncLayerFilterChips();

  // Status filter pills + overflow menus (delegated on document). initDispatch()
  // runs setupEventHandlers() on EVERY entry into Dispatch; this listener is
  // anonymous so it can't be removed — without this guard it ACCUMULATED one
  // copy per visit, and an even count toggled the ⋮ overflow open-then-shut
  // (= "nothing happens" on mobile after re-entering Dispatch). Attach ONCE.
  if (!window.__dispatchDocClickWired) {
   window.__dispatchDocClickWired = true;
   document.addEventListener('click', (e) => {
    const pill = e.target.closest('.status-pill');
    if (pill) {
      DispatchState.statusFilter = pill.dataset.status;
      _renderDispatchBoardNow(); // user clicked a status filter — instant feedback
    }

    // Overflow menu toggle. position:fixed escapes the parent card's
    // overflow:hidden clip; we anchor it to the kebab's bounding rect so
    // it opens flush-right and just below the button regardless of where
    // the row lives in the viewport.
    const overflowBtn = e.target.closest('.job-overflow-btn');
    if (overflowBtn) {
      e.stopPropagation();
      // Swallow a mobile GHOST/double click on the kebab: iOS can fire two clicks
      // for a single tap, toggling the menu twice (the close-tap left it re-opened
      // → the next real tap only closed it → "needs a second click to reopen").
      // A 60ms window catches the near-simultaneous ghost but is far below any
      // human re-tap, so a deliberate close-then-reopen still works. (Date.now OK
      // in the browser.)
      const _now = Date.now();
      if (_now - (window.__ddOverflowTapTs || 0) < 60) return;
      window.__ddOverflowTapTs = _now;
      // User discovered the ⋮ — stop the first-run attention wiggle for good.
      if (overflowBtn.classList.contains('dispatch-more-btn')) {
        overflowBtn.classList.remove('dispatch-more-wiggle');
        try { localStorage.setItem('dd_dispatch_more_seen_v2', '2'); } catch (_) {} // TASK-1263
      }
      const dropdown = overflowBtn.nextElementSibling;
      // TASK-10010 — Recently deleted shows for the owner only.
      if (dropdown && dropdown.classList.contains('dispatch-overflow-dropdown')) {
        const _rd = dropdown.querySelector('#btn-recently-deleted');
        if (_rd) _rd.hidden = !_canRestoreDeletedJobs();
      }
      document.querySelectorAll('.overflow-dropdown.open').forEach(d => {
        if (d !== dropdown) d.classList.remove('open');
      });
      // The header (⋮) menu (.dispatch-overflow-dropdown) is CSS-anchored to its
      // relative parent — skip the JS fixed-positioning (which mis-placed it while
      // the board was still loading). Job-row menus still need the fixed-escape.
      if (dropdown && !dropdown.classList.contains('open')
          && !dropdown.classList.contains('dispatch-overflow-dropdown')) {
        const r = overflowBtn.getBoundingClientRect();
        const gap = 4;
        const menuMinWidth = 180;
        const wouldOverflowBottom = r.bottom + 220 > window.innerHeight;
        if (wouldOverflowBottom) {
          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${window.innerHeight - r.top + gap}px`;
        } else {
          dropdown.style.bottom = 'auto';
          dropdown.style.top = `${r.bottom + gap}px`;
        }
        dropdown.style.right = `${Math.max(8, window.innerWidth - r.right)}px`;
        dropdown.style.left = 'auto';
        dropdown.style.minWidth = `${menuMinWidth}px`;
      }
      dropdown?.classList.toggle('open');
      return;
    }

    // TASK-964: clicking an item in the dispatch header "more" menu runs its own
    // handler (inline onclick / the .view-btn delegate — both fire in the target
    // phase, before this document-level bubble handler) and then closes the menu.
    // Scoped to .dispatch-overflow-dropdown so job-row overflow behavior is unchanged.
    const headerMenuItem = e.target.closest('.dispatch-overflow-dropdown .overflow-item');
    if (headerMenuItem) {
      document.querySelectorAll('.overflow-dropdown.open').forEach(d => d.classList.remove('open'));
      return;
    }

    // Close overflow menus on outside click
    if (!e.target.closest('.overflow-dropdown')) {
      document.querySelectorAll('.overflow-dropdown.open').forEach(d => d.classList.remove('open'));
    }
   });
  }

  // First-run attention wiggle on the new ⋮ menu.
  _maybeWiggleMoreMenu();

  // Role toggle buttons
  document.querySelectorAll('.role-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.role-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      DispatchState.roleMode = btn.dataset.role;
      updateRoleModeUI();
      _renderDispatchBoardNow(); // user clicked a role toggle — instant feedback
    });
  });
}

function updateRoleModeUI() {
  const isDriver = DispatchState.roleMode === 'driver';
  document.querySelectorAll('.dispatcher-only').forEach(el => {
    el.style.display = isDriver ? 'none' : '';
  });
}

// ============================================
// MODAL HANDLERS
// ============================================
// TASK-10008 — "tap a day, add a job on it" (Guy 10/21). Two doors call this:
// the month-view day popup's "+ Add job on <day>" button and a tap on empty
// space in a week-view day column. Before this the function existed and was
// exported but had no caller, so a calendar day never led to Add Job.
//
// Who gets the door — the same people the header "+ Add Job" serves, and no
// one the server would refuse:
//   * office: not in driver role-view, and _canCreateJobs() (the client mirror
//     of the firestore.rules jobs CREATE arm — modules, never roles);
//   * PIN driver: only when the company turned on driver job editing
//     (driverJobEditEnabled), and then through the trimmed driver form;
//   * anyone else (driver link / public / view-only member): no door.
// Returns 'office' | 'driver' | null.
function _calendarAddJobMode() {
  const st = window.AppState || {};
  if (st.driverModePin === true) return driverJobEditEnabled() ? 'driver' : null;
  if (st.driverMode || st.isPublicAccess) return null;
  if (DispatchState.roleMode === 'driver') return null;
  return _canCreateJobs() ? 'office' : null;
}

// TASK-10008 — client mirror of firestore.rules jobs CREATE
// (dispOk(tid, false) && !isComplianceOnly): demo, the OWNER via
// tenants.ownerId, or an active member whose modules hold `dispatch` and who
// is not readOnly. That is exactly the predicate _canEditPlaces(false) answers
// (placesWriter is built on the same dispOk), so it is reused, not copied.
function _canCreateJobs() {
  return _canEditPlaces(false);
}

// "Tue, Oct 28" for the popup button. Date-only string → local noon, so no
// timezone can roll it to the neighbouring day.
function _calendarDayShortLabel(dateStr) {
  const d = new Date(String(dateStr) + 'T12:00:00');
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function openAddJobWithDate(dateStr) {
  const mode = _calendarAddJobMode();
  if (!mode) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || ''))) return false;
  closeModal('day-details');
  if (mode === 'driver') driverOpenAddJob(dateStr);
  else showAddJob(dateStr);
  return true;
}

// TASK-10008 — week view: a tap on EMPTY space in a day column opens Add Job on
// that day. Never from a card, hold, chip, button or link (they have their own
// action), and never as the tail of a drag (a drop can be followed by a click
// on some browsers; _weekDragEndedAt fences it).
let _weekDragEndedAt = 0;
const _WEEK_EMPTY_TAP_SKIP = '[class*="-card"], button, a, input, select, textarea, label, [onclick], [draggable="true"], [role="button"], .assign-indicator, .week-today-jump';
function _weekEmptyTapDate(target, nowMs) {
  if (!target || typeof target.closest !== 'function') return null;
  const col = target.closest('.week-day[data-date]');
  if (!col) return null;
  const hit = target.closest(_WEEK_EMPTY_TAP_SKIP);
  if (hit && col.contains(hit) && hit !== col) return null;
  if (col.classList.contains('is-dragging')) return null;
  if (typeof nowMs === 'number' && nowMs - _weekDragEndedAt < 500) return null;
  return col.dataset.date || null;
}

// ============================================
// THE PROJECT <select>, KEPT LIVE
// ============================================
// Was: two byte-identical copies of this block (showAddJob + editJob) reading a
// list projects.js fetched ONCE, with getDocs, and only when the Dispatch view
// was entered. A project created after that — from AR/AP "Group into project",
// from the Projects tab, or by anyone else on the tenant — did not exist in the
// dropdown until the dispatcher navigated away and back, or reloaded. Reported
// from the field 2026-09-01: a project created in AR/AP was invisible to a
// dispatcher who was already sitting in the Add Job modal, and window.ProjectsModule
// does not exist AT ALL until the Dispatch view has been entered once.
//
// projects.js now holds a real onSnapshot and calls refreshProjectSelects() on
// every change, so the list is already current when the dropdown is opened
// rather than being re-fetched on click — swapping <option>s under an
// already-open native popup is what makes a select flicker and lose its
// highlight mid-choice.
//
// Reached through window.ProjectsModule.* — NEVER a cross-import with ?v=
// (Pitfall #1: duplicate module instances).
//
// 🔴 The customer half of the label reads `primaryCustomerName`, which is what
// projects.js createProject and the groupTicketsIntoProject callable actually
// write. The old `p.customerName` was undefined on every project ever created,
// so that half silently never rendered — which is exactly the disambiguation a
// dispatcher needs when two customers have a similarly-named project.
//
// TASK-9606 — filtered by the form's customer (Chris: "this should intuitively
// just filter by customer after customer selection"). Customer picked → that
// customer's projects (customerIds has it), then "No customer yet" for the
// projects TASK-9599 lets adopt it; other customers' projects are hidden — save
// would refuse them anyway. No customer → every project, grouped by customer.
// UX only: saveJob, projects.js and firestore.rules jobProjectAdmits stay the
// authority. `keepId` / `selectedId` name a project that must stay listed even
// when it fits no group (the edited job's own link, a link being restored), so
// opening and saving a legacy cross-customer job never silently unlinks it.
function _projectSelectOptionsHtml(selectedId, customerId, keepId) {
  const projects = (window.ProjectsModule?.getActiveProjects?.() || []).filter(Boolean);
  // data-po carries the project's standing PO so picking a project can prefill the
  // visible PO field — the dispatcher SEES the number before saving and can override
  // it, instead of it appearing silently on the job after the write.
  const _escA = window.escAttr || escapeHtml;
  const opt = (p, withCust) => {
    const cust = withCust ? (p.primaryCustomerName || p.customerName || '') : '';
    return `<option value="${_escA(p.id)}" data-po="${_escA(p.poNumber || '')}">${escapeHtml(p.name || 'Untitled project')}${cust ? ' — ' + escapeHtml(cust) : ''}</option>`;
  };
  const group = (label, list, withCust) => list.length
    ? `<optgroup label="${_escA(label)}">${list.map(p => opt(p, withCust)).join('')}</optgroup>` : '';
  // TASK-4695 — the picker is always visible and optional: blank = no project.
  const head = '<option value="">No project</option>';
  const cust = typeof customerId === 'string' ? customerId : '';
  if (cust) {
    const mine = [], adoptable = [], kept = [];
    for (const p of projects) {
      if (!_projectCustomerConflict(p, cust)) mine.push(p);
      else if (_projectAdoptsCustomer(p, cust)) adoptable.push(p);
      else if ((selectedId && p.id === selectedId) || (keepId && p.id === keepId)) kept.push(p);
    }
    // Wolverine 2026-10-21 — the customer's Active price books that have no
    // project of the same name yet. Picking one starts that project with this
    // job (onJobProjectChange → _startProjectFromPriceBook).
    const books = _jobFormPriceBookStarts(cust, projects);
    const bookGroup = books.length
      ? `<optgroup label="Start a project from a price book">${books.map(b =>
        `<option value="${_escA(_PB_OPT_PREFIX + b.id)}" data-po="">${escapeHtml(b.title)}</option>`).join('')}</optgroup>`
      : '';
    return head + mine.map(p => opt(p, false)).join('')
      + bookGroup
      + group('No customer yet', adoptable, false)
      + group('Linked now — another customer', kept, true);
  }
  const byCust = new Map();
  for (const p of projects) {
    const name = p.primaryCustomerName || p.customerName || '';
    if (!byCust.has(name)) byCust.set(name, []);
    byCust.get(name).push(p);
  }
  const names = [...byCust.keys()].filter(Boolean).sort((a, b) => a.localeCompare(b));
  return head + names.map(n => group(n, byCust.get(n), false)).join('')
    + group('No customer yet', byCust.get('') || [], false);
}

// TASK-9606 — does this project belong in the picker for `customerId`? Its own
// project, or one with no customer that TASK-9599 lets adopt it. No customer
// picked = everything is offered (picking one fills the customer).
function _projectFitsCustomer(project, customerId) {
  if (!customerId) return true;
  return !_projectCustomerConflict(project, customerId) || _projectAdoptsCustomer(project, customerId);
}

// TASK-9289 B1 — keepId (was keepInactiveId): an EDIT of a job whose project is
// completed or archived. The active-only list has no <option> for it, so the
// select read '' and the save either unlinked the job or refused ("Select a
// project"). Keep the job's own project selectable; a NEW job never gets this.
// TASK-9606 — it now also keeps an ACTIVE project that names another customer
// (a legacy link) via the options builder. customerId: the form's customer,
// read off #job-customer when not passed.
function _populateProjectSelect(projectSelect, selectedId, keepInactiveId, customerId) {
  if (!projectSelect) return;
  const cust = customerId !== undefined ? (customerId || '')
    : (document.getElementById?.('job-customer')?.value || '');
  projectSelect.innerHTML = _projectSelectOptionsHtml(selectedId || '', cust, keepInactiveId || '');
  if (projectSelect.dataset) projectSelect.dataset.forCustomer = cust;
  if (keepInactiveId && !Array.from(projectSelect.options).some(o => o.value === keepInactiveId)) {
    const all = window.ProjectsModule?.getState?.()?.projects || [];
    const p = all.find(x => x && x.id === keepInactiveId);
    const opt = document.createElement('option');
    opt.value = keepInactiveId;
    opt.textContent = `${p?.name || 'Current project'} (${p?.status || 'not active'})`;
    opt.dataset.po = p?.poNumber || '';
    projectSelect.appendChild(opt);
  }
  projectSelect.value = selectedId || ''; // explicit: '' is a real choice (TASK-9606 clear)
}

// Called by projects.js on every projects snapshot. Repopulating an element the
// dispatcher may be part-way through using has exactly one rule: never move
// their selection. Read the CURRENT value first and restore it. The one case
// where it legitimately cannot be restored is a project someone else just
// archived or deleted — say so out loud rather than leaving a blank field that
// reads as "nothing picked".
function refreshProjectSelects() {
  try { _811RefreshProjectSelects(); } catch (_) { /* 811 project dropdown */ }
  // TASK-9175 — projects.js streams projects in AFTER the map may already have
  // painted without them, which leaves the map key with no Project filter
  // until something else repaints. Repaint once when the list first arrives —
  // never while the dispatcher is typing in the search box.
  if (DispatchState.viewMode === 'map' && !DispatchState.mapProjectId
      && document.getElementById('dispatch-map-container')
      && !document.getElementById('map-project-search')
      && _mapAllProjects().length) {
    repaintMapView();
  }
  const sel = document.getElementById('job-project-id');
  if (!sel) return;
  // Nothing to preserve, and nothing to disturb, while the modal is closed —
  // the next open repopulates from the same live list anyway.
  if (document.getElementById('modal-job')?.classList.contains('hidden')) return;
  // 🔴 COD (UX Lead): never replace a <select>'s innerHTML while the dispatcher is
  // IN it. On a phone or tablet the native picker is open at that moment, and
  // swapping the options out from under it collapses the picker mid-tap — a
  // mis-picked project is worse than a list that is a few seconds stale. Defer to
  // blur, once, so the refresh still lands rather than being dropped.
  if (document.activeElement === sel) {
    if (!sel.dataset.pendingProjectRefresh) {
      sel.dataset.pendingProjectRefresh = '1';
      sel.addEventListener('blur', function _onProjectSelectBlur() {
        sel.removeEventListener('blur', _onProjectSelectBlur);
        delete sel.dataset.pendingProjectRefresh;
        refreshProjectSelects();
      });
    }
    return;
  }
  const keep = sel.value || '';
  // TASK-9606 — same customer filter as every other render (read off
  // #job-customer); the current value stays (the builder keeps a selected
  // active project even when it fits no group), and so does the edited job's
  // OWN project even after the dispatcher moved off it.
  _populateProjectSelect(sel, keep, document.getElementById('job-id')?.value ? (_editJobOriginalProjectId || null) : null);
  _syncJobProjectLinkBox(); // TASK-4695 — keep the hidden link mirror on what the picker shows
  _renderJobProjectCustomerNote(sel.dataset.forCustomer || '', null, { refreshOnly: true });
  // TASK-10031 — a repaint must not put "No project" back over a project being
  // started from a price book.
  if (_jobFormBookStart && document.getElementById('job-create-new-project')?.checked) {
    const blank = sel.querySelector?.('option[value=""]');
    const nm = document.getElementById('job-new-project-name')?.value?.trim();
    if (blank && nm) blank.textContent = `New: ${nm}`;
  }
  if (keep && sel.value !== keep) {
    _releaseProjectJobDefaultClaims(); // TASK-4696 COD r3 — the archived project's values are the job's now
    window.showToast?.('The project this job was linked to is no longer active — pick another before saving.', 'warning');
  }
}

// TASK-9606 — a real customer change re-filters the picker. The project is
// cleared ONLY when the customer actually changed AND the project no longer
// fits it; then one amber line says so (PO untouched, its own hint as before).
// Runs AFTER _recheckCarriedProjectCustomer, which owns a Copy's carried
// project and its single toast.
function _refilterJobProjectsForCustomer(customerId) {
  const sel = document.getElementById('job-project-id');
  if (!sel) return;
  const cust = customerId || '';
  const realChange = (sel.dataset.forCustomer || '') !== cust;
  const prev = sel.value || '';
  let removed = null;
  if (prev && realChange && cust) {
    const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === prev)
      || (window.ProjectsModule?.getState?.()?.projects || []).find(p => p && p.id === prev);
    if (proj && !_projectFitsCustomer(proj, cust)) removed = proj;
  }
  const editKeep = document.getElementById('job-id')?.value ? (_editJobOriginalProjectId || null) : null;
  _populateProjectSelect(sel, removed ? '' : prev, editKeep, cust);
  _syncJobProjectLinkBox();
  if (removed) { _releaseProjectJobDefaultClaims(); _showUnlinkedProjectPoHint(); } // TASK-4696 COD r2
  if (realChange || removed) _renderJobProjectCustomerNote(cust, removed);
}

// One line under the picker: "removed" after a customer change dropped the
// project, or the empty state for a customer with no projects (shown only on a
// tenant that has projects at all). textContent only — names are user data.
// refreshOnly (projects snapshot): never invent a note, never wipe a "removed"
// one; just drop an empty-state line that a new project made stale.
function _renderJobProjectCustomerNote(customerId, removed, opts) {
  const note = document.getElementById('job-project-customer-note');
  if (!note) return;
  const hide = () => { note.textContent = ''; note.style.display = 'none'; delete note.dataset.kind; };
  if (opts?.refreshOnly && note.dataset.kind !== 'empty') return;
  if (removed) {
    const owner = removed.primaryCustomerName || removed.customerName || 'another customer';
    note.textContent = `${removed.name || 'That project'} is for ${owner} — removed from this job.`;
    note.dataset.kind = 'removed';
    note.style.display = 'block';
    return;
  }
  const projects = (window.ProjectsModule?.getActiveProjects?.() || []).filter(Boolean);
  if (!customerId || !projects.length || projects.some(p => _projectFitsCustomer(p, customerId))
      || _jobFormPriceBookStarts(customerId, projects).length) { hide(); return; } // Wolverine 2026-10-21 — its price books are in the list
  if (opts?.refreshOnly) return; // still none fit — the line already says so
  const name = getCustomerById(customerId)?.name || 'this customer';
  note.textContent = `No projects for ${name} — use + to start one.`;
  note.dataset.kind = 'empty';
  note.style.display = 'block';
}

// TASK-9606 — project picked while the customer is still blank: fill the
// customer ONLY when the project names exactly one. Never overwrites a chosen
// customer; a customer missing from the dropdown leaves it blank.
function _fillCustomerFromProject(projectId) {
  const cs = document.getElementById('job-customer');
  if (!cs || cs.value || !projectId) return false;
  const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === projectId);
  const ids = Array.isArray(proj?.customerIds) ? proj.customerIds.filter(Boolean) : [];
  if (ids.length !== 1) return false;
  cs.value = ids[0];
  if (cs.value !== ids[0]) { cs.value = ''; return false; }
  onCustomerChange();
  return true;
}

// ============================================
// Wolverine 2026-10-21 — PROJECT LIST FROM PRICE BOOKS + SITE FROM THE CUSTOMER
// ============================================
// Steffani: "the projects are still not populating from the contracts nor is the
// site populating from the customer." Their data (read-only census 2026-10-22):
// one customer holds 8 Active price books, each named for a job site / project
// ("Line 5 - Ashland WI"), plus customer sites (customers/{id}/sites) whose names
// echo them — and 1 project doc in the whole tenant. The Project picker read only
// the projects collection and nothing on the job form read the customer's sites.
//
// Now, for every company:
//   • customer picked → its Active price books with no same-name project are in
//     the Project list ("Start a project from a price book"). Picking one opens the
//     inline New project form filled from the book (name, dates, standing PO),
//     sets the job's price book + PO, and the existing save path creates and
//     links the project. Next time the project itself is listed and the book is
//     not (same-name match).
//   • the customer's sites fill a Site picker; picking one fills Location. A
//     customer with ONE site fills it on pick; a project/price book fills the
//     site whose name clearly matches (refuses ties and weak overlaps — a wrong
//     address on a job is worse than a blank one).
//   • a Location the dispatcher typed or picked is never overwritten: only a
//     value WE filled (dataset.autofilledLoc, the job-po autofilledPo pattern)
//     is replaced or cleared.
const _PB_OPT_PREFIX = 'pb:';
let _jobFormBooks = null;          // { customerId, contracts } — last price-book read for this form
let _jobFormBookStart = null;      // { customerId, contractId } — the New project form was opened from this book
let _jobSiteState = { customerId: '', sites: [] };
const _jobSiteCache = new Map();   // `${tenant}/${customerId}` → { sites, loadedAt }

function _jobNormName(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// Price books that can start a project for `customerId`. Never on the PIN form
// (it cannot read price books; it owns its own project list).
function _jobFormPriceBookStarts(customerId, projects) {
  if (!customerId || window.AppState?.driverModePin === true) return [];
  if (!_jobFormBooks || _jobFormBooks.customerId !== customerId) return [];
  const books = Array.isArray(_jobFormBooks.contracts) ? _jobFormBooks.contracts : [];
  if (!books.length) return [];
  const taken = new Set((projects || [])
    .filter(p => p && !_projectCustomerConflict(p, customerId))
    .map(p => _jobNormName(p.name)).filter(Boolean));
  return books
    .filter(b => b && b.id && String(b.title || '').trim() && !taken.has(_jobNormName(b.title)))
    .sort((a, b) => String(a.title).localeCompare(String(b.title)));
}

async function _loadCustomerSites(customerId) {
  const key = `${DispatchState.tenantId}/${customerId}`;
  const hit = _jobSiteCache.get(key);
  if (hit && (Date.now() - hit.loadedAt) < 120000) return hit.sites;
  try {
    const snap = await getDocs(collection(db, 'tenants', DispatchState.tenantId, 'customers', customerId, 'sites'));
    const sites = snap.docs.map(d => {
      const x = d.data() || {};
      return { id: d.id, name: String(x.name || '').trim(), address: String(x.address || '').trim() };
    }).filter(s => s.name || s.address)
      .sort((a, b) => (a.name || a.address).localeCompare(b.name || b.address));
    _jobSiteCache.set(key, { sites, loadedAt: Date.now() });
    return sites;
  } catch (e) {
    console.warn('[Dispatch] customer sites read failed:', e?.code || e);
    return [];
  }
}

function _jobSiteLocationText(site) {
  return site ? (site.address || site.name || '') : '';
}

function _setJobSiteHint(text) {
  const h = document.getElementById('job-site-hint');
  if (!h) return;
  h.textContent = text || '';
  h.style.display = text ? 'block' : 'none';
}

function _renderJobSitePicker(customerId, sites) {
  const list = Array.isArray(sites) ? sites : [];
  _jobSiteState = { customerId: customerId || '', sites: list };
  const group = document.getElementById('job-site-group');
  const sel = document.getElementById('job-site-select');
  if (!group || !sel) return;
  const _escA = window.escAttr || escapeHtml;
  sel.innerHTML = '<option value="">Pick a site…</option>'
    + list.map(s => `<option value="${_escA(s.id)}">${escapeHtml(s.name || s.address)}</option>`).join('');
  group.classList.toggle('hidden', list.length === 0);
  if (!list.length) _setJobSiteHint('');
}

// Location still empty, or still exactly what we filled → ours to replace.
function _jobLocationIsOurs() {
  const loc = document.getElementById('job-location');
  if (!loc) return false;
  const v = String(loc.value || '').trim();
  const ours = String(loc.dataset.autofilledLoc || '').trim();
  return !v || (!!ours && v === ours);
}

// Writes Location. how: 'auto' (we chose it — replaceable later) or 'user'.
function _applyJobLocation(text, how, site) {
  const loc = document.getElementById('job-location');
  if (!loc) return;
  const prev = loc.value;
  const val = String(text || '');
  loc.value = val;
  if (how === 'auto' && val) loc.dataset.autofilledLoc = val; else delete loc.dataset.autofilledLoc;
  const sel = document.getElementById('job-site-select');
  if (sel) sel.value = site ? site.id : (_jobSiteState.sites.find(s => _jobSiteLocationText(s) === val)?.id || '');
  // A project being started with this job takes the same location unless one
  // was typed for it.
  const np = document.getElementById('job-new-project-location');
  if (np && document.getElementById('job-create-new-project')?.checked
      && (!np.value.trim() || np.value === prev)) np.value = val;
  if (!val) _setJobSiteHint('');
  else if (how === 'auto') _setJobSiteHint(`Location from ${site ? `site “${site.name || site.address}”` : 'the project'}: ${val}`);
  else _setJobSiteHint(`Location: ${val}`);
  try { refreshJobLocationPlace(); } catch (_) { /* place line is cosmetic */ }
  try { _refreshJobFormGroups(); } catch (_) { /* accordion summary is cosmetic */ }
}

const _JOB_SITE_STOP = new Set(['project', 'projects', 'site', 'sites', 'station', 'the', 'and', 'of', 'at', 'for', 'inc', 'llc', 'co', 'job', 'usa']);
function _jobSiteTokens(s) {
  return new Set(_jobNormName(s).split(' ').filter(t => t && !_JOB_SITE_STOP.has(t)));
}
// US state codes / names, for the conflict check below. Codes only count when
// written in capitals ("Manning, ND"), so words like "in" / "or" never match.
const _US_STATES = {
  AL: 'alabama', AK: 'alaska', AZ: 'arizona', AR: 'arkansas', CA: 'california', CO: 'colorado', CT: 'connecticut',
  DE: 'delaware', FL: 'florida', GA: 'georgia', HI: 'hawaii', ID: 'idaho', IL: 'illinois', IN: 'indiana', IA: 'iowa',
  KS: 'kansas', KY: 'kentucky', LA: 'louisiana', ME: 'maine', MD: 'maryland', MA: 'massachusetts', MI: 'michigan',
  MN: 'minnesota', MS: 'mississippi', MO: 'missouri', MT: 'montana', NE: 'nebraska', NV: 'nevada', NH: 'new hampshire',
  NJ: 'new jersey', NM: 'new mexico', NY: 'new york', NC: 'north carolina', ND: 'north dakota', OH: 'ohio', OK: 'oklahoma',
  OR: 'oregon', PA: 'pennsylvania', RI: 'rhode island', SC: 'south carolina', SD: 'south dakota', TN: 'tennessee',
  TX: 'texas', UT: 'utah', VT: 'vermont', VA: 'virginia', WA: 'washington', WV: 'west virginia', WI: 'wisconsin', WY: 'wyoming',
};
function _jobTextStates(text) {
  const out = new Set();
  const raw = String(text || '');
  for (const m of raw.matchAll(/\b([A-Z]{2})\b/g)) if (_US_STATES[m[1]]) out.add(m[1]);
  const norm = ` ${_jobNormName(raw)} `;
  for (const [code, full] of Object.entries(_US_STATES)) if (norm.includes(` ${full} `)) out.add(code);
  return out;
}
// The one site whose NAME clearly matches `name`: at least 2 shared words that
// cover at least half of the site's words, strictly ahead of every other
// qualifying site, and no state conflict (the name says ND, the site's name +
// address say IL → not that site, however many words they share). Otherwise
// null — never a guess.
// `common` (COD r2): words that name the COMPANY, not the place — the
// customer's own name, and words used by 2+ of its sites or 2+ of its price
// books ("TC Energy" on both the Woodstock and the Manning book). At least one
// shared word must be outside it, so two jobs for the same end client never
// match each other's site on the client's name alone.
function _bestJobSiteMatch(name, sites, common) {
  const want = _jobSiteTokens(name);
  const isCommon = (t) => !!(common && common.has(t));
  if (!want.size) return null;
  const wantStates = _jobTextStates(name);
  let best = null, bestScore = 0, second = 0;
  for (const s of sites || []) {
    const have = _jobSiteTokens(s.name);
    if (!have.size) continue;
    let shared = 0, distinct = 0;
    have.forEach(t => { if (want.has(t)) { shared++; if (!isCommon(t)) distinct++; } });
    if (shared < 2 || shared / have.size < 0.5 || distinct < 1) continue;
    if (wantStates.size) {
      const siteStates = _jobTextStates(`${s.name} ${s.address || ''}`);
      if (siteStates.size && ![...wantStates].some(c => siteStates.has(c))) continue;
    }
    if (shared > bestScore) { second = bestScore; bestScore = shared; best = s; }
    else if (shared > second) second = shared;
  }
  return best && bestScore > second ? best : null;
}

// Company words for _bestJobSiteMatch, from what this form has loaded.
function _jobSiteCommonTokens() {
  const cust = _jobSiteState.customerId;
  const out = new Set(_jobSiteTokens(getCustomerById(cust)?.name || ''));
  const count = (texts) => {
    const n = new Map();
    for (const t of texts) for (const w of _jobSiteTokens(t)) n.set(w, (n.get(w) || 0) + 1);
    n.forEach((c, w) => { if (c >= 2) out.add(w); });
  };
  count(_jobSiteState.sites.map(x => x.name));
  if (_jobFormBooks && _jobFormBooks.customerId === cust) count((_jobFormBooks.contracts || []).map(b => b.title || ''));
  return out;
}

// Pick a site for the job ONLY when Location is still ours. A customer pick
// (no matchName) → the customer's only site. A project / price book pick →
// only the site clearly named like it, even when the customer has one site
// (COD r1: a sole site is not where every project of that customer is). No
// pick → our earlier fill is cleared, a typed one is kept.
function _autoPickJobSite(matchName) {
  if (!_jobLocationIsOurs()) return null;
  const sites = _jobSiteState.sites;
  const site = matchName ? _bestJobSiteMatch(matchName, sites, _jobSiteCommonTokens()) : (sites.length === 1 ? sites[0] : null);
  if (site) { _applyJobLocation(_jobSiteLocationText(site), 'auto', site); return site; }
  if (document.getElementById('job-location')?.dataset.autofilledLoc) _applyJobLocation('', 'auto', null);
  return null;
}

// A real project was picked: its own location first, else the matching site.
function _fillJobSiteFromProject(projectId) {
  const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === projectId);
  if (!proj) return;
  const own = String(proj.location || '').trim();
  if (own) { if (_jobLocationIsOurs()) _applyJobLocation(own, 'auto', null); return; }
  _autoPickJobSite(proj.name);
}

function onJobSiteChange(selectEl) {
  const sel = selectEl || document.getElementById('job-site-select');
  const site = _jobSiteState.sites.find(s => s.id === sel?.value) || null;
  if (!site) { _setJobSiteHint(''); return; } // "Pick a site…" leaves Location alone
  _applyJobLocation(_jobSiteLocationText(site), 'user', site);
}

// Clears the site picker and our claim on Location for a fresh form open.
function _resetJobSitePicker() {
  _jobFormBooks = null;
  _jobFormBookStart = null;
  _renderJobSitePicker('', []);
  const loc = document.getElementById('job-location');
  if (loc) delete loc.dataset.autofilledLoc;
}

// Customer picked (autofill) or an edit opened (no autofill): read its price
// books and sites, then repaint the Project and Site pickers. Every async step
// re-checks the form generation + customer (TASK-388 / TASK-4877 rule).
function _loadJobFormCustomerSources(customerId, opts = {}) {
  const gen = _jobFormGen;
  _jobFormBooks = null;
  _renderJobSitePicker(customerId, []);
  if (!customerId || window.AppState?.driverModePin === true) return;
  const still = () => gen === _jobFormGen && (document.getElementById('job-customer')?.value || '') === customerId;
  if (!_jobFormProjectsHidden()) {
    loadCustomerLineItems(customerId).then(({ contracts }) => {
      if (!still()) return;
      _jobFormBooks = { customerId, contracts: Array.isArray(contracts) ? contracts : [] };
      refreshProjectSelects();
    });
  }
  _loadCustomerSites(customerId).then(sites => {
    if (!still()) return;
    _renderJobSitePicker(customerId, sites);
    const loc = document.getElementById('job-location');
    if (opts.autofill) {
      // A project / price book picked while the sites were loading gets its match.
      const sel = document.getElementById('job-project-id');
      const proj = sel?.value ? (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === sel.value) : null;
      const creating = document.getElementById('job-create-new-project')?.checked;
      const name = proj ? proj.name : (creating ? document.getElementById('job-new-project-name')?.value : '');
      if (proj && String(proj.location || '').trim()) return;
      _autoPickJobSite(name || null);
    } else if (loc) {
      const m = sites.find(s => _jobSiteLocationText(s) === loc.value);
      const sel = document.getElementById('job-site-select');
      if (sel && m) sel.value = m.id;
    }
  });
}

// The job's price book follows a price book picked as a project — through the
// Price Book picker when it is on screen (the human path, onContractPick),
// else straight into the pending state the save reads.
function _selectJobPriceBook(contractId) {
  const pick = document.querySelector('#job-contract-area select');
  if (pick && Array.from(pick.options).some(o => o.value === contractId)) {
    pick.value = contractId;
    onContractPick(pick, true);
    return;
  }
  if (!window.hasAddOnPackage?.('clockbilling')) return;
  _projCarriedBook = null; // a human choice — cancel any carried-book check
  if (DispatchState.pendingJobSelectedContractId !== contractId) {
    DispatchState.pendingJobSelectedContractId = contractId;
    DispatchState.pendingJobLineItemSelections = {};
  }
}

function _startProjectFromPriceBook(contractId) {
  const sel = document.getElementById('job-project-id');
  const cust = document.getElementById('job-customer')?.value || '';
  const book = (_jobFormBooks && _jobFormBooks.customerId === cust)
    ? (_jobFormBooks.contracts || []).find(c => c && c.id === contractId) : null;
  if (sel) sel.value = '';
  _releaseProjectJobDefaultClaims(); // TASK-4696 COD r2 — leaving the last project unlinks it
  if (!book) { _syncJobProjectLinkBox(); return; }
  setJobNewProjectMode(true);
  _jobFormBookStart = { customerId: cust, contractId };
  const head = document.getElementById('job-new-project-heading');
  if (head) head.textContent = 'New project from this price book';
  const set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v; };
  const day = (v) => {
    const d = v?.toDate ? v.toDate() : null;
    return d && !isNaN(d) ? formatDateISOLocal(d) : '';
  };
  set('job-new-project-name', String(book.title || '').trim());
  // The picker is disabled while the inline form is open; say what it holds
  // instead of "No project" (value stays '' — every reader is unchanged).
  const blank = sel?.querySelector('option[value=""]');
  if (blank) blank.textContent = `New: ${String(book.title || '').trim()}`;
  set('job-new-project-start', day(book.startDate) || formatDateISOLocal(new Date()));
  set('job-new-project-end', day(book.endDate));
  const po = book.permanentPo && book.permanentPoNumber ? String(book.permanentPoNumber).trim() : '';
  set('job-new-project-po', po);
  _selectJobPriceBook(contractId);
  // The job's PO follows the book exactly as a project's standing PO does: a
  // PO the dispatcher typed is kept (applyProjectPo's autofilledPo rule).
  applyProjectPo({ selectedOptions: [{ dataset: { po } }] });
  set('job-new-project-location', '');
  _autoPickJobSite(book.title);
  set('job-new-project-location', document.getElementById('job-location')?.value || '');
  _renderJobProjectCustomerNote('', null);
}


function showAddJob(prefilledDate = null) {
  // TASK-388: new form generation — invalidates any in-flight duplicate pre-fill
  // and cancels its pending timeout so a stale callback can't overwrite this form.
  _jobFormGen++;
  if (_dupePrefillTimer) { clearTimeout(_dupePrefillTimer); _dupePrefillTimer = null; }
  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-job'));
  _editJobOriginalCustomerId = null;
  _editJobOriginalProjectId = null; // TASK-9289
  _jobFormDotUnconfirmed = false;   // TASK-9370
  _jobFormDotReviewPending = false;
  _renderJobDotReviewChip();
  // TASK-9645 C — a new job (or a Copy) is never invoice-locked; the form is
  // shared with Edit Job, so clear any lock a previous edit painted.
  _editJobLockBaseline = null;
  _applyEditJobInvoiceLock(null);

  // TASK-398: breadcrumb — new-job form opened (no initial customer).
  _breadcrumbCustomerId = null;
  window.ActionLog?.record('form_open', { form: 'job', jobId: null, initialCustomerId: null });

  document.getElementById('modal-job-title').textContent = 'New Job';
  _renderJobCopySourceBanner(null); // TASK-9598 — a plain New Job never says "copied"
  document.getElementById('form-job').reset();
  _syncJobNotesHeight({ reset: true }); // TASK-4808 — reset() keeps the last job's inline height
  document.getElementById('job-id').value = '';
  // TASK-4877 — same reason as the crew snapshot below: an earlier edit's field
  // baseline must not describe this form.
  _jobFormSnapshot = null;
  // TASK-1672 — never let an earlier edit's crew snapshot leak into a new job.
  DispatchState.jobCrewSnapshot = null;
  DispatchState.jobCrewSeedIntent = null;
  DispatchState.jobCrewSeedPending = null;
  // TASK-9212 — and never let a previous COPY's raw crew carry leak either.
  // duplicateJob calls showAddJob FIRST and sets the carry afterwards, so
  // clearing here is what keeps a plain New Job from re-normalising (and
  // re-seeding) somebody else's crew when a roster snapshot lands.
  _dupeCrewCarry = null;
  DispatchState.returnJobCtx = null; // TASK-10006 — only a return Copy sets it, after this
  _resetCopyCarryGuard(); // TASK-9363 r3
  _dupeDropAnnounced = '';
  _setJobPermitIds([]); // TASK-9648 — a hidden input survives form.reset(); clear the linked 811 tickets
  // 🔴 TASK-9238 — and never let the last form's BILLED asset numbers leak into
  // a brand-new job. These maps are modal-scoped state that only saveJob and
  // saveAssignment cleared, so open-a-job → Cancel → New Job carried the edited
  // job's qty/direction straight into the create path's assetQtys/assetDirections
  // write. editJob overwrites both maps wholesale, and duplicateJob assigns them
  // AFTER its showAddJob() call (see the seed block there), so neither path
  // regresses — this only closes the plain New Job case, which had no seeder at
  // all and therefore no reset either.
  DispatchState.pendingAssetQtys = {};
  DispatchState.pendingAssetDirections = {};
  // TASK-9258 — same class of leak, one field over: `templateApplied` is what
  // stamps provenance onto every jobDay the save writes, so a template applied
  // to a form that was then cancelled must not claim the NEXT job. Reset before
  // the bar re-renders, so the picker and the notice open clean.
  resetJobTemplateState();
  renderJobTemplateBar();
  _renderJobPrevNotesBox(null); // TASK-9391 — the reused form node must open clean
  _projCarriedBook = null;      // TASK-9391 (COD r1) — no carry check / kit intent
  _dupeCarriedProjectId = null; // TASK-9363 — a copy's project claim belongs to that copy
  _pendingProjectKit = null;    // outlives the form it belonged to
  // TASK-9392 — and never let the last form's customer request claim this job.
  // (Also bound to the form generation, so a stale one can't reach saveJob.)
  _jobFormCustomerRequest = null;
  _renderJobCustomerRequestBox(null);
  _jobFormClaudeDraft = null; // TASK-9587 — same rule for a Claude job request
  _renderJobClaudeDraftBox(null);
  // TASK-4614 — a NEW job assigns its crew to every day it creates, so clear the
  // edit-scope day and restore the all-days hint. Leaving a previous edit's day
  // id here would scope the next edit to a day that isn't even on this job.
  _editJobDayId = null;
  _renderJobMultiDayNote(null); // TASK-1111 — hide the multi-day note for a new job
  _renderJobCrewScopeHint(null);
  // TASK-1221 — reset the ticketing toggle to combined + clear any mid-day lock
  // (form.reset() restores the hidden input's default value but not the visual
  // toggle state / hint / disabled state).
  setJobSplitBy('combined');
  _applyJobSplitLock(null);

  // Clear duplicate warning
  const dupWarn = document.getElementById('job-duplicate-warning');
  if (dupWarn) dupWarn.style.display = 'none';
  _duplicateMatchJobId = null;

  // Show AI button for new jobs
  const aiBtn = document.getElementById('btn-ai-create');
  if (aiBtn) {
    aiBtn.style.display = '';
    aiBtn.classList.remove('btn-ai-disabled');
    aiBtn.title = 'Auto-fill from description';
  }

  // TASK-273: Set default dates — use prefilled (calendar-click) date if provided,
  // otherwise default to TOMORROW in local time. UTC-based default was causing
  // (a) silent shift to tomorrow's UTC date after ~18:00 CT and (b) wrong-day
  // job creates because today is rarely the dispatcher's intent (audit log
  // shows JOB_UPDATE-date entries fire seconds after JOB_CREATE most of the time).
  // TASK-9407 — the no-prefill default now follows Dispatch Settings
  // (settings.defaultJobStartDate); absent = tomorrow, unchanged from TASK-273.
  const dateToUse = prefilledDate
    || formatDateISOLocal(defaultJobStartDateLocal(window.AppState?.settings?.defaultJobStartDate));
  document.getElementById('job-start-date').value = dateToUse;
  document.getElementById('job-end-date').value = dateToUse;
  document.getElementById('job-end-date').min = dateToUse;
  document.getElementById('job-start-date').max = '';
  setDateAssumedFlag(false); // TASK-962 (S3): never persist across modal reopens

  // TASK-273: Reset multi-day toggle to OFF + hide end-date wrapper for new jobs.
  // The end-date field already supports multi-day saveJob loops (L6042-6070), but
  // dispatchers were creating 2 separate jobs for Thu+Fri instead of 1×2-jobDay
  // because the second date field invited per-day re-entry. Hiding it by default
  // forces a deliberate "this spans multiple days" toggle.
  const multiToggle = document.getElementById('job-multi-day-toggle');
  const endDateWrapper = document.getElementById('job-end-date-wrapper');
  if (multiToggle) multiToggle.checked = false;
  if (endDateWrapper) endDateWrapper.style.display = 'none';
  // TASK-9750 — new job: flags shown, both OFF (never carried from the last form).
  { const r = document.getElementById('job-day-flags-row'); if (r) r.style.display = ''; }
  { const t = document.getElementById('job-tentative'); if (t) t.checked = false; }
  { const h = document.getElementById('job-half-day'); if (h) h.checked = false; }
  // TASK-9112 — AFTER the multi-day reset, because the hint reads that toggle to
  // decide whether the end-date field means anything. This covers the tomorrow
  // default AND a calendar day-click (openAddJobWithDate -> showAddJob(dateStr)),
  // which is how a dispatcher reaches an off-board date from Month view.
  refreshJobDateOffscreenHint();

  // TASK-273: Reset per-form duplicate-bypass flag (set when dispatcher
  // confirms "create separate job" in the dupe modal). Resets every time
  // the form is opened so state never leaks across job creates.
  _dupeBypassConfirmed = false;
  _dupeBypassContext = null; // TASK-4660

  // Default to All Day checked
  document.getElementById('job-all-day').checked = true;
  document.getElementById('time-fields').style.display = 'none';

  // Default PO visible to drivers
  document.getElementById('job-po-visible').checked = true;

  // Default job type (TASK-9390 — options rebuilt from the tenant list each open)
  _renderJobTypeSelect('job');
  onJobTypeChange();

  // Reset POC fields
  populateCustomerDropdown();
  _jobProjDocsSet([]); // TASK-9650
  populatePermitDropdown();
  _setJobCustomerAiCue(null); // TASK-4579 — a fresh form has no AI pick
  document.getElementById('poc-section').classList.add('hidden');
  document.getElementById('job-poc-select').innerHTML = '<option value="">Select POC</option>';
  document.getElementById('poc-info-card').classList.add('hidden');

  // Reset waypoints
  document.getElementById('job-waypoints-list').innerHTML = '';
  _resetWaypointMileage(); // TASK-10291 — also drops the last job's pending driving fetch

  // TASK-191 Phase 2 — reset geofence section for a new job (form.reset() clears
  // the checkbox + address but we must clear the cached coords + collapse the body).
  _jobGeofenceCoords = null;
  const gfBody = document.getElementById('job-geofence-body');
  if (gfBody) gfBody.classList.add('hidden');
  const gfReadout = document.getElementById('job-geofence-coords-readout');
  if (gfReadout) gfReadout.textContent = '';
  const gfStatus = document.getElementById('job-geofence-geocode-status');
  if (gfStatus) gfStatus.textContent = '';

  // Reset photos
  DispatchState.pendingJobPhotos = [];
  DispatchState.existingJobPhotos = [];
  DispatchState.loadedPhotoCount = 0;
  DispatchState.photosVisibleTouched = false; // TASK-10224
  renderJobPhotoGrid();

  // Reset attachments toggle
  const attachToggle = document.getElementById('job-attachments-toggle');
  if (attachToggle) { attachToggle.checked = false; }
  toggleAttachmentsSection();

  // Reset permits
  DispatchState.pendingPermitFiles = [];
  DispatchState.existingPermitFiles = [];
  DispatchState.existingPermitDocs = null;
  DispatchState.loadedPermitCount = 0;
  renderJobSignedDocs(null); // TASK-9740 — a new job has no signed documents
  // TASK-9151 — a previous save's failure banner must never outlive the form it
  // was raised in, or the next job opens already claiming permits failed.
  resetPermitUploadState();
  renderPermitGrid();
  togglePermitsSection(); // TASK-9546 — always open; there is no checkbox to untick
  const aiParse = document.getElementById('permit-ai-parse');
  if (aiParse) aiParse.checked = true;
  const fastTrax = document.getElementById('permit-fasttrax');
  if (fastTrax) fastTrax.checked = false;

  // TASK-4649 — Permit Required checkbox: unticked, badge off, heuristic re-armed.
  resetJobFormPermitRequired();

  // Reset and populate assignment dropdowns (legacy single-selects)
  populateAssignmentDropdowns();

  // Populate crew multi-select sections (empty selections for new job)
  populateJobCrewSections([], [], [], [], []);

  // Hide edit-only actions
  document.getElementById('job-edit-actions').classList.add('hidden');

  // Reset line item + contract selections for new job
  DispatchState.pendingJobLineItemSelections = {};
  DispatchState.pendingJobSelectedContractId = null;
  const jobContractArea = document.getElementById('job-contract-area');
  if (jobContractArea) jobContractArea.innerHTML = '';
  { const _pbHint = document.getElementById('job-pricebook-hint'); if (_pbHint) _pbHint.innerHTML = ''; } // TASK-9446

  // Populate project dropdown and reset the link (TASK-4695: "No project" +
  // the hidden link mirror off, new-project form closed).
  const projectSelect = document.getElementById('job-project-id');
  const projectCheckbox = document.getElementById('job-add-to-project');
  if (projectCheckbox) { projectCheckbox.checked = false; }
  _resetJobNewProjectMode();
  // Fresh modal — we have no claim on the PO field yet. The <input> is reused
  // across opens, so a leftover marker would let a later project switch clobber
  // a PO the dispatcher typed for THIS job.
  { const _po = document.getElementById('job-po'); if (_po) { delete _po.dataset.autofilledPo; delete _po.dataset.autofilledPoFrom; } } // TASK-4579: + whose blanket
  // TASK-4782 — a stale PO warning from the last job must not follow the form
  // into a new one. The suggestion list is rebuilt when a customer is picked.
  { const _h = document.getElementById('job-po-hint'); if (_h) _h.innerHTML = ''; }
  { const _dl = document.getElementById('job-po-datalist'); if (_dl) _dl.innerHTML = ''; }
  _openJobExtraPos(null); // TASK-9811 S1 — a new job (or a Copy) starts with no extra POs
  { const _h = document.getElementById('job-project-po-hint'); if (_h) { _h.style.display = 'none'; _h.textContent = ''; } }
  _resetProjectJobDefaultClaims(); // TASK-4696 — no claim on a fresh form
  _renderJobProjectCustomerNote('', null); // TASK-9606 — the last form's note stays with it
  _resetJobSitePicker(); // Wolverine 2026-10-21 — the last form's sites / price books / Location claim
  if (projectSelect) {
    projectSelect.disabled = false;
    _populateProjectSelect(projectSelect, '');

    // Pre-fill new project start date — TASK-273: local-tz formatter, not UTC
    // (project starts can be backdated, so default to today not tomorrow).
    const today = formatDateISOLocal(new Date());
    const npStart = document.getElementById('job-new-project-start');
    if (npStart) npStart.value = today;

    // Check for pending project link (from "New Job" button in project detail)
    if (window.__pendingProjectLink) {
      const link = window.__pendingProjectLink;
      // Consumed FIRST (it used to be cleared last): a throw anywhere below must
      // not leave it armed for the next, unrelated New Job.
      window.__pendingProjectLink = null;
      if (projectCheckbox) { projectCheckbox.checked = true; }
      projectSelect.value = link.projectId || '';
      if (link.customerId) {
        const custSelect = document.getElementById('job-customer');
        if (custSelect) { custSelect.value = link.customerId; onCustomerChange(); }
      }
      // TASK-9606 — the customer re-filter must not drop the project this New
      // Job was opened for (project set first, customer second on this path).
      if (link.projectId && projectSelect.value !== link.projectId) {
        _populateProjectSelect(projectSelect, link.projectId, null);
        _syncJobProjectLinkBox();
        _renderJobProjectCustomerNote('', null);
      }
      // Setting .value programmatically does NOT fire onchange, so the project's
      // standing PO would never be applied on this path — and onCustomerChange()
      // above may have just dropped the customer's blanket contract PO in the field.
      // Run it explicitly, AFTER the customer fill, so the more specific project PO wins.
      applyProjectPo(projectSelect);
      // TASK-9391 Slice 2 — the project's latest job, per its New job defaults.
      // null (no jobs in the project yet) = exactly the old prefill.
      const _projCarry = _buildProjectNewJobCarry(link);
      if (link.location && !_projCarry?.location) {
        addWaypoint();
        const firstWpName = document.querySelector('.waypoint-name');
        if (firstWpName) firstWpName.value = link.location;
      }
      if (_projCarry) _applyProjectNewJobCarry(_projCarry, _jobFormGen);
      // TASK-4696 — the project's own defaults AFTER the last-job carry (they are
      // the explicit choice), BEFORE a customer request's facts (those win).
      applyProjectJobDefaults(link.projectId, { force: true });
      if (link.defaults?.jobTemplateId) _applyProjectJobKit(link.defaults.jobTemplateId, _jobFormGen);
      // TASK-9392 — a customer request: its own facts (name, date, PO) land
      // LAST so they win over the project's; the WHO/WHICH EQUIPMENT stay the
      // project's (the carry above).
      if (link.customerRequest) _applyCustomerRequestFacts(link.customerRequest, _jobFormGen);
      // TASK-10126 — "New job in this project" never fires onchange; fill an
      // empty title here too (after the request, whose own title wins).
      _fillJobTitleFromProject(projectSelect);
    }
  }
  _applyJobProjectAccess(); // TASK-9651

  // TASK-4695 — a new job opens with the extra-field groups closed.
  _closeJobFormGroups();
  // TASK-4688 — new job: no data to protect, so the tenant's toggles win outright.
  applyJobFormSectionVisibility(null);

  _clearDeleteBlockAlert(); // TASK-4736 — never open a form under a stale refusal
  document.getElementById('modal-job').classList.remove('hidden');

  // Refresh sparkle on AI button
  if (typeof window.refreshSparkle === 'function') {
    setTimeout(() => window.refreshSparkle('btn-ai-create', 'ai-create'), 100);
  }
}

// ============================================
// TASK-9370 — "Drivers can add and edit jobs": the driver-PIN leg of this form
// ============================================
// Chris decided: SAME modal, trimmed to Customer / Title / Dates / Location /
// Notes; one toggle covers add AND edit; edit = any job; delete stays off.
// COD decided the mechanism: the save goes to the `driverSaveJob` callable
// (see saveJob's PIN branch), never to Firestore from here. The entry points
// live on the driver Job Board (jobboard.js) and reach these through
// window.DispatchModule — never a ?v= import (Pitfall #1).
//
// The PIN board is a different route from dispatch, so nothing here may assume
// initDispatch ran: the modal DOM is mounted on demand, customers are read
// once (PIN-readable), and the job being edited is handed over explicitly.

function driverJobEditEnabled() {
  const cfg = window.AppState?.settings?.driverJobEdit;
  // TASK-9710 — the per-driver opt-out (Roster card). The server re-checks it
  // against the bound PIN session; this only keeps the form from opening.
  const pid = window.AppState?.driverPersonId;
  const blocked = !!pid && Array.isArray(cfg?.blockedPersonIds) && cfg.blockedPersonIds.includes(pid);
  return window.AppState?.settings?.driverJobEdit?.enabled === true && !blocked;
}

// Every job-form element a driver must not see. TASK-9563 (Chris 2026-10-05:
// "full capability for a driver to dispatch another driver") opened Project,
// Waypoints and Crew & Equipment to the driver form; what stays hidden is what
// stays OFFICE-ONLY on the server too (driverSaveJob never writes it). Hidden
// with inline display:none — it outranks the `.hidden` class toggling
// applyJobFormSectionVisibility does on some of these, and `#job-dot-flags-row`
// is display:contents, which a class alone would not beat. The inputs STAY in
// the DOM so saveJob's unguarded getElementById reads keep working; the server
// ignores their values anyway.
const _DRIVER_JOB_FORM_HIDE = [
  'btn-ai-create',           // Smart Job Builder — spends AI quota, fills hidden fields
  // TASK-10106 S2 — #job-type-group (Type) is SHOWN: the company's own active
  // types (settings.jobTypes, off the tenant doc the PIN session already
  // loads). The edit-list pencil stays hidden (_canManageJobTypes is false for
  // a PIN session, and the trim hides every .btn-add-inline). driverSaveJob
  // accepts only an active type on the list, or the job's own unchanged type.
  'btn-job-new-project',     // TASK-9563 — pick a project, never create one
  'job-new-project-fields',
  'job-project-po-hint',     // project-PO talk — the driver picker carries no project PO
  // TASK-9703 S2 — #poc-section (Point of Contact) is SHOWN: populatePocDropdown
  // reads the list through driverCustomerContacts (lazy driverJobPoc.js).
  // TASK-10106 S5 — #job-po-row is SHOWN (PO on a new job / a job with a blank
  // PO only); its "show PO to drivers" switch and extra POs stay hidden
  // (_driverApplyPoAndFiles). driverSaveJob refuses changing an existing PO.
  'job-afe-vendor-row',      // Vendor / AFE
  'job-dot-flags-row',       // Interstate / Hazmat / Liquid tank / Permit Required
  'job-dot-review-chip',     // TASK-9370 — the dispatcher's "confirm DOT flags" prompt
  'job-ticketing-section',   // Per-truck vs combined ticketing
  'job-permit-group',        // 811 permit
  'job-geofence-section',    // Geofence
  // TASK-10106 S5 — #job-attachments-section (photos + load permits) is SHOWN:
  // a driver ADDS files (driverSaveJob files mode) and removes only their own
  // upload; the "include photos in driver's view" switch and the AI permit
  // billing options stay hidden (_driverApplyPoAndFiles).
  'job-template-bar',       // TASK-9563 — templates carry assets + rates
  'job-template-notice',
  'job-contract-area',       // Price book / rates — office-only
  'job-pricebook-hint',
  'compliance-warnings-job', // reads office-only roster data; the server enforces the hard stop
  // TASK-10106 S1a — #job-section-assets is SHOWN, with STANDARD equipment only
  // (_driverSeedRoster loads no rental / consumable / PPE item). driverSaveJob
  // re-checks every asset's type and refuses the whole save on anything else.
  'job-load-calculator-live',
  // TASK-9703 — 'job-edit-actions' (Add Another Day / Copy to New Job / Delete)
  // is NO LONGER hidden: each button branches to its driver twin
  // (_driverAddDayPrompt / _driverCopyJob / _driverDeleteCurrentJob), and
  // _driverApplyEditActions shows Delete only on a job this driver added.
  'job-date-offscreen-hint', // "off the board you're viewing" — no dispatcher board here
];

function _applyDriverJobFormTrim() {
  for (const id of _DRIVER_JOB_FORM_HIDE) {
    const el = document.getElementById(id);
    if (el) el.style.display = 'none';
  }
  // The inline "edit customer" / "add customer" buttons beside the picker write
  // to `customers`, which a PIN session cannot do — hide both, keep the select.
  document.querySelectorAll('#modal-job .input-with-action .btn-add-inline').forEach(b => { b.style.display = 'none'; });
  // TASK-9563 — the per-stop bookmark (WRITES savedLocations) is an office
  // tool; .dd-driver-job-form hides it via CSS because rows are added after
  // this runs. TASK-10106 S4 — the Saved Locations PICKER is shown: a PIN
  // session reads a trimmed, read-only list through driverSavedLocations
  // (_driverLoadSavedLocations), never the collection itself.
  document.getElementById('modal-job')?.classList.add('dd-driver-job-form');
  document.querySelectorAll('#job-waypoints-section .waypoint-actions button').forEach(b => {
    if (/showSavedLocationsPicker/.test(b.getAttribute('onclick') || '')) b.style.display = '';
  });
  const title = document.getElementById('modal-job-title');
  if (title && document.getElementById('job-id')?.value === '') title.textContent = 'Add Job';
  // TASK-4695 — location + well are two of the driver's sections and live in
  // the Site group: drop the groups the trim emptied (billing), and open Site
  // so a driver never has to find a collapsed row to reach them.
  _refreshJobFormGroups();
  const site = document.getElementById('job-acc-site');
  if (site) site.open = true;
}

// The job modals are painted by getDispatchHTML() into the dispatch view. On
// the Job Board route that view is not mounted, so mount just the modals on
// <body>. initDispatch removes this host before painting its own copy.
function _ensureDriverJobModalMounted() {
  if (document.getElementById('modal-job')) return;
  const host = document.createElement('div');
  host.id = 'dd-driver-job-modal-host';
  host.innerHTML = getJobModalsHTML();
  document.body.appendChild(host);
}

// TASK-9694 S6a — customer reads for crew sessions go through the directory
// (window.CustomerSourceCore, utils/customerSource.js; read at call time).
function _dispatchCustomerSource() {
  return window.CustomerSourceCore.create({ db, collection, doc, getDoc, getDocs, onSnapshot, query, orderBy });
}
function _dispatchCustomerPrefer() {
  return window.CustomerSourceCore.sessionReadsDirectory(window.AppState) ? 'customerDirectory' : 'customers';
}

// populateCustomerDropdown reads DispatchState.customers, which only the
// dispatch view's subscription fills. One read — TASK-9694 S6a: the directory
// (a PIN session can no longer read `customers`; the form needs names + flags).
async function _driverSeedCustomers() {
  const tid = window.AppState?.tenant;
  if (!tid) return;
  _syncDispatchTenant(tid); // P0 1019 — a previous company's customers never seed this form
  if (Array.isArray(DispatchState.customers) && DispatchState.customers.length) return;
  try {
    const _custList = await _dispatchCustomerSource().getAll(tid, { prefer: 'customerDirectory' });
    if (!_dispatchDataIsFor(tid) || window.AppState?.tenant !== tid) return; // switched companies mid-read (P0 1019)
    DispatchState.customers = _custList;
  } catch (e) {
    console.warn('[Dispatch] TASK-9370: customer list unavailable for the driver form', e?.message || e);
  }
}

// TASK-9563 — the crew picker reads DispatchState.people/trucks/trailers, which
// only the dispatch view's subscriptions fill. One-shot reads, all PIN-readable
// (canReadTenant), same filters as subscribeToData. Seeded BEFORE showAddJob /
// editJob so the form's open-time crew snapshot is built from a loaded roster
// (an empty roster would paint no ticks and the snapshot would be empty).
// TASK-10106 S1a — assets ARE loaded now, but ONLY standard ones (never a
// rental, consumable or stock-tracked PPE item: a driver never moves stock).
// Server twin: functions/src/index.js _driverJobAssetIsStandard — keep in step.
function _driverAssetIsStandard(a) {
  return !!a && !a.consumable && !a.stockTracked && a.quantityType !== 'rental' && !a.isRental;
}
async function _driverSeedRoster() {
  const tid = window.AppState?.tenant;
  if (!tid) return;
  _syncDispatchTenant(tid); // P0 1019 — a failed read below must not leave the previous company's roster
  const load = async (name) => {
    try {
      const snap = await getDocs(collection(db, 'tenants', tid, name));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn(`[Dispatch] TASK-9563: ${name} unavailable for the driver form`, e?.message || e);
      return null;
    }
  };
  const [people, trucks, trailers, assets] = await Promise.all([load('people'), load('trucks'), load('trailers'), load('assets')]);
  if (!_dispatchDataIsFor(tid) || window.AppState?.tenant !== tid) return; // switched companies mid-read
  const byLabel = (a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || '');
  if (people) {
    const all = people.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    DispatchState.peopleAll = all;
    window.ClockMode?.notePeople?.(all); // TASK-9553
    DispatchState.people = all.filter(p => p.active !== false && p.terminated !== true);
  }
  if (trucks) DispatchState.trucks = trucks.filter(t => t.active !== false).sort(byLabel);
  if (trailers) DispatchState.trailers = trailers.filter(t => t.active !== false).sort(byLabel);
  // A failed read leaves the picker empty (never a previous company's list).
  DispatchState.assets = (assets || []).filter(a => a.active !== false && _driverAssetIsStandard(a))
    .sort((a, b) => (window.getUnitDisplay?.(a) || a.name || '').localeCompare(window.getUnitDisplay?.(b) || b.name || ''));
  // TASK-10106 S1b — rentals the driver may ASK for (names only; never ticked
  // onto a day, never in DispatchState.assets).
  DispatchState.driverRentals = (assets || []).filter(a => a.active !== false && _driverAssetIsRental(a))
    .sort((a, b) => (window.getUnitDisplay?.(a) || a.name || '').localeCompare(window.getUnitDisplay?.(b) || b.name || ''));
  DispatchState.dataLoaded.people = true;
  DispatchState.dataLoaded.trucks = true;
  DispatchState.dataLoaded.trailers = true;
  DispatchState.dataLoaded.assets = true;
}

// TASK-9563 — what the driver form looked like when it opened, so the save
// sends project / stops / crew ONLY when the driver actually changed them
// (driverSaveJob writes nothing it is not sent — a title-only edit must never
// clear dispatch's stops or crew).
let _driverFormOrig = null;
function _driverCaptureFormOrig(projectId) {
  let stops = [];
  try { stops = collectWaypoints(); } catch (_) { stops = []; }
  _driverFormOrig = {
    projectId: projectId || null,
    stopsSig: _driverStopsSig(stops),
    crew: DispatchState.jobCrewSnapshot ? _driverCrewSide(DispatchState.jobCrewSnapshot) : null,
    // TASK-10106 S2 — the Type the form opened with (an old job's legacy or
    // archived type is its own option, so an untouched edit never sends it).
    type: document.getElementById('job-type')?.value || 'job',
    // TASK-10106 S1a — the standard equipment ticked when the form opened.
    equip: _driverEquipSide(),
    // TASK-10106 S5 — the PO the form opened with ('' = the driver may fill it).
    po: normalizePoForSave(document.getElementById('job-po')?.value),
  };
}
// TASK-10106 S1a — the ticked STANDARD equipment as [{ id, qty }]. qty only for
// a quantity-tracked asset (an individual one is always 1, so none is sent).
// Only rows the driver picker rendered can be ticked, and it renders standard
// assets only, so a rental already on the day is never in this list.
function _driverEquipSide() {
  const pool = DispatchState.assets || [];
  return getJobCrewSelected('assets').map(String).filter(id => _driverAssetIsStandard(pool.find(a => a && a.id === id)))
    .map(id => {
      const a = pool.find(x => x && x.id === id);
      if (a.trackingMode !== 'quantity') return { id };
      const n = Number(DispatchState.pendingAssetQtys?.[id]);
      return { id, qty: Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1 };
    });
}
function _driverEquipSig(list) {
  return JSON.stringify((list || []).map(x => [x.id, x.qty == null ? 1 : x.qty]).sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0)));
}

// ── TASK-10106 S1b — RENTALS (Chris 2026-10-27: the driver ASKS, the office
// CONFIRMS). Nothing here touches the day or stock: driverSaveJob stores the
// ask on the job (jobs.assetRequests) and dispatch deploys it from the job with
// its own Assign Crew save. Server twin of the class: functions/src/index.js
// _driverJobAssetIsRental — keep in step. No stock, price, availability,
// deploy/return toggle or load calculator is ever shown to the driver.
function _driverAssetIsRental(a) {
  return !!a && !a.consumable && !a.stockTracked && (a.quantityType === 'rental' || !!a.isRental);
}
const _RENTAL_REQ_MAX = 10;
let _driverReq = { job: null, put: [], withdraw: [] };
function _rentalReqMs(v) {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  const n = new Date(v).getTime();
  return Number.isFinite(n) ? n : null;
}
function _rentalReqAgo(v) {
  const ms = _rentalReqMs(v);
  if (ms == null) return '';
  const m = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.round(h / 24)}d ago`;
}
// The job's requests as rows (bad keys / shapes dropped), newest first.
function _rentalReqEntries(job) {
  const m = job && job.assetRequests;
  if (!m || typeof m !== 'object' || Array.isArray(m)) return [];
  return Object.entries(m)
    .filter(([k, e]) => /^[A-Za-z0-9_-]{1,128}$/.test(k) && e && typeof e === 'object')
    .map(([assetId, e]) => ({ assetId, ...e }))
    .sort((a, b) => (_rentalReqMs(b.at) || 0) - (_rentalReqMs(a.at) || 0));
}
// One plain sentence for an entry (driver side). Text only — callers put it in
// textContent, never innerHTML.
function _rentalReqDriverLine(e) {
  const nm = String(e.name || 'Rental').slice(0, 80);
  const q = Number(e.qty) || 1;
  if (e.status === 'declined') return `Office said no: ${q} ${nm}`;
  if (e.status === 'edited') return `Office OK'd ${Number(e.resolvedQty) || 1} ${nm} (you asked for ${q})`;
  if (e.status === 'confirmed') return `Office OK'd ${q} ${nm}`;
  return `${q} ${nm} — ${String(e.personName || 'a driver').slice(0, 60)}, ${_rentalReqAgo(e.at)}`;
}
function _driverRenderRentalRequests(job) {
  _driverReq = { job: job || null, put: [], withdraw: [] };
  const assetsSec = document.getElementById('job-section-assets');
  let box = document.getElementById('job-rental-requests-driver');
  const rentals = DispatchState.driverRentals || [];
  const entries = _rentalReqEntries(job);
  if (window.AppState?.driverModePin !== true || !assetsSec || (!rentals.length && !entries.length)) {
    if (box) box.style.display = 'none';
    return;
  }
  if (!box) {
    box = document.createElement('div');
    box.id = 'job-rental-requests-driver';
    box.className = 'form-section';
    box.style.cssText = 'margin:8px 0 12px;padding:10px 12px;border-radius:8px;border:1px solid rgba(245,158,11,0.45);background:rgba(245,158,11,0.08);';
    assetsSec.after(box);
  }
  box.style.display = '';
  box.textContent = '';
  const h = document.createElement('div');
  h.style.cssText = 'font-weight:600;color:var(--text-primary);margin-bottom:4px;';
  h.textContent = 'Rentals (mats, etc.) — ask the office';
  box.appendChild(h);
  const tag = document.createElement('div');
  tag.className = 'form-hint';
  tag.style.cssText = 'color:var(--text-secondary);font-size:0.8rem;margin-bottom:8px;';
  tag.textContent = 'Office must OK — nothing is held until they do.';
  box.appendChild(tag);
  const list = document.createElement('div');
  box.appendChild(list);
  const myPid = window.AppState?.driverPersonId || null;
  const paint = () => {
    list.textContent = '';
    const isWaiting = (e) => !e.status || e.status === 'deploying'; // 'deploying' = the office is saving it now
    const pending = entries.filter(e => isWaiting(e) && !_driverReq.withdraw.includes(e.assetId) && !_driverReq.put.some(p => p.id === e.assetId));
    const answered = entries.filter(e => !isWaiting(e) && !_driverReq.put.some(p => p.id === e.assetId));
    const row = (text, color, btnLabel, onBtn) => {
      const r = document.createElement('div');
      r.style.cssText = `display:flex;align-items:center;gap:8px;padding:6px 0;color:${color};font-size:0.9rem;`;
      const s = document.createElement('span');
      s.style.flex = '1';
      s.textContent = text;
      r.appendChild(s);
      if (btnLabel) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn-secondary btn-sm';
        b.style.minHeight = '40px';
        b.textContent = btnLabel;
        b.onclick = onBtn;
        r.appendChild(b);
      }
      list.appendChild(r);
    };
    if (pending.length || _driverReq.put.length) {
      const t = document.createElement('div');
      t.style.cssText = 'font-size:0.8rem;font-weight:600;color:#B45309;margin-top:4px;';
      t.textContent = 'Asked the office for (not reserved yet)';
      list.appendChild(t);
    }
    for (const e of pending) {
      row(_rentalReqDriverLine(e), '#B45309', e.personId && e.personId === myPid && !e.status ? 'Take back' : null,
        () => { _driverReq.withdraw.push(e.assetId); paint(); });
    }
    for (const p of _driverReq.put) {
      const a = rentals.find(x => x.id === p.id);
      row(`${p.qty} ${window.getUnitDisplay?.(a) || a?.name || 'Rental'} — asks when you save`, '#B45309', 'Remove',
        () => { _driverReq.put = _driverReq.put.filter(x => x.id !== p.id); paint(); });
    }
    for (const e of answered) {
      row(_rentalReqDriverLine(e), e.status === 'declined' ? '#B91C1C' : 'var(--text-primary)', null, null);
    }
    const waiting = pending.length + _driverReq.put.length;
    if (!rentals.length || waiting >= _RENTAL_REQ_MAX) return;
    const add = document.createElement('div');
    add.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;margin-top:8px;';
    const sel = document.createElement('select');
    sel.className = 'form-control';
    sel.style.cssText = 'flex:1 1 180px;min-height:44px;';
    sel.setAttribute('aria-label', 'Rental to ask for');
    const o0 = document.createElement('option');
    o0.value = '';
    o0.textContent = 'Pick a rental…';
    sel.appendChild(o0);
    for (const a of rentals) {
      if (_driverReq.put.some(x => x.id === a.id)) continue;
      const o = document.createElement('option');
      o.value = a.id;
      o.textContent = window.getUnitDisplay?.(a) || a.name || 'Rental';
      sel.appendChild(o);
    }
    const qty = document.createElement('input');
    qty.type = 'number';
    qty.min = '1';
    qty.max = '10000';
    qty.step = '1';
    qty.value = '1';
    qty.inputMode = 'numeric';
    qty.className = 'form-control';
    qty.style.cssText = 'width:96px;min-height:44px;';
    qty.setAttribute('aria-label', 'How many');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-secondary';
    btn.style.minHeight = '44px';
    btn.textContent = 'Ask for it';
    btn.onclick = () => {
      const a = rentals.find(x => x.id === sel.value);
      if (!a) { window.showToast?.('Pick a rental first.', 'info'); return; }
      const n = Number(qty.value);
      const counted = a.trackingMode === 'quantity';
      if (counted && (!Number.isInteger(n) || n < 1 || n > 10000)) { window.showToast?.('How many: a whole number from 1 to 10,000.', 'info'); return; }
      _driverReq.withdraw = _driverReq.withdraw.filter(id => id !== a.id);
      _driverReq.put.push({ id: a.id, qty: counted ? n : 1 });
      paint();
    };
    add.append(sel, qty, btn);
    list.appendChild(add);
  };
  paint();
}
// The payload's `rentalRequests`, or null when the driver asked / took back nothing.
function _driverRentalReqPayload() {
  const put = (_driverReq.put || []).map(x => ({ id: x.id, qty: x.qty, direction: 'deploy' }));
  const withdraw = (_driverReq.withdraw || []).slice();
  return (put.length || withdraw.length) ? { put, withdraw } : null;
}

// ── TASK-10106 S1b — the OFFICE side of a driver's rental request. A chip on
// the day card + the job details; Confirm opens that day's Assign Crew with the
// rental pre-ticked at the asked qty, and NOTHING moves until the office
// presses Save — the save runs the existing ledger settle with every guard
// (_asgSettleRentalsLedger), so a request can never drift the counts. Only a
// rental that verifiably landed on the day marks the request answered, and
// only while its `rev` is unchanged (a driver re-ask mid-confirm survives).
// Decline marks it answered 'declined' (rev-checked). Both are idempotent, and
// a 2-minute claim keeps two office users from deploying the same ask twice.
const _RENTAL_REQ_CLAIM_MS = 2 * 60 * 1000;
const _RENTAL_REQ_CLOSED_DAY = ['finished', 'completed', 'cancelled', 'archived', 'deleted', 'closed'];
function _rentalReqJobDays(jobId) {
  const seen = new Set();
  const out = [];
  for (const d of [...(DispatchState.jobDays || []), ...(DispatchState.searchJobDays || []), ..._externalJobDays()]) {
    if (!d || d.jobId !== jobId || seen.has(d.id) || String(d.id).startsWith('legacy-')) continue;
    seen.add(d.id);
    out.push(d);
  }
  return out.sort((a, b) => (_jobDayDateMs(a) || 0) - (_jobDayDateMs(b) || 0));
}
// The day an entry is for — null when job-level, or when its day is gone (a
// date edit removed it): then it is job-level again and the office picks.
function _rentalReqDayOf(e, days) {
  return (e && e.dayId && days.find(d => d.id === e.dayId)) || null;
}
function _rentalReqStale(e, days) {
  const day = _rentalReqDayOf(e, days);
  if (day && _rentalDayKeyOf(day) && _rentalDayKeyOf(day) < _rentalTodayKey()) return true;
  const ms = _rentalReqMs(e.at);
  return ms != null && Date.now() - ms > 24 * 60 * 60 * 1000;
}
function _rentalReqAssetLabel(e) {
  const a = (DispatchState.assets || []).find(x => x && x.id === e.assetId);
  return String((a && (window.getUnitDisplay?.(a) || a.name)) || e.name || 'Rental').slice(0, 80);
}
// COD r3 🔴 (Code Reviewer) — the pre-save recheck CONSUMES the ask: status
// 'deploying' (+ this confirm's token) is written before any stock moves, so
// no other Confirm / tab can start on it while that save runs. Answered →
// confirmed/edited; a save that did not deploy it releases it back to pending.
// A 'deploying' mark older than the claim window (a closed tab mid-save) is
// open again, so an ask can never be stuck.
function _rentalReqDeployingFresh(e) {
  const ms = _rentalReqMs(e && e.deployingAt);
  return !!e && e.status === 'deploying' && ms != null && Date.now() - ms < _RENTAL_REQ_CLAIM_MS;
}
function _rentalReqOpen(e, token) {
  if (!e) return false;
  if (!e.status) return true;
  return e.status === 'deploying' && ((!!token && e.claimToken === token) || !_rentalReqDeployingFresh(e));
}
function _rentalReqPending(job) {
  return _rentalReqEntries(job).filter(e => !e.status || e.status === 'deploying');
}
// Day card chip (office board only). Every string escaped.
function _rentalReqCardChipHtml(job, jobDay) {
  if (window.AppState?.driverModePin === true) return '';
  const pend = _rentalReqPending(job);
  if (!pend.length) return '';
  const days = _rentalReqJobDays(job.id);
  const here = pend.filter(e => !_rentalReqDayOf(e, days) || e.dayId === jobDay?.id);
  if (!here.length) return '';
  const red = here.some(e => _rentalReqStale(e, days));
  const e0 = here[0];
  const txt = here.length === 1 ? `Driver asked for ${Number(e0.qty) || 1} ${_rentalReqAssetLabel(e0)}` : `${here.length} driver rental asks`;
  return `<div class="rental-req-chip" title="Open the job to confirm or decline" style="display:inline-flex;align-items:center;gap:4px;margin:2px 0;padding:1px 8px;border-radius:10px;font-size:0.72rem;font-weight:600;${red ? 'background:#FEE2E2;border:1px solid #FCA5A5;color:#991B1B;' : 'background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;'}"><span class="material-icons" aria-hidden="true" style="font-size:13px;">front_hand</span>${escapeHtml(txt)}</div>`;
}
// Job details block: each pending ask + Confirm / Decline. A job-level ask gets
// a day picker defaulting to the first live day on/after today (never silently
// the first day).
function _rentalReqOfficeHtml(job) {
  if (window.AppState?.driverModePin === true) return '';
  const pend = _rentalReqPending(job);
  if (!pend.length) return '';
  const days = _rentalReqJobDays(job.id);
  const today = _rentalTodayKey();
  const live = days.filter(d => !_RENTAL_REQ_CLOSED_DAY.includes(d.status));
  const def = live.find(d => _rentalDayKeyOf(d) >= today) || null;
  const can = _dispatchCanManageDays();
  const rows = pend.map(e => {
    const day = _rentalReqDayOf(e, days);
    const red = _rentalReqStale(e, days);
    const line = `Driver asked for ${Number(e.qty) || 1} ${_rentalReqAssetLabel(e)} — ${String(e.personName || 'a driver').slice(0, 60)}, ${_rentalReqAgo(e.at)}${_rentalReqDeployingFresh(e) ? ` (being deployed now by ${String(e.claimedByName || 'the office').slice(0, 60)})` : ''}`;
    const selId = `rental-req-day-${e.assetId}`;
    const dayHtml = day
      ? `<div style="font-size:0.78rem;color:var(--text-secondary);">For ${escapeHtml(formatDateDisplay(new Date(_jobDayDateMs(day)), { weekday: 'short', month: 'short', day: 'numeric' }))}</div>`
      : (days.length ? `<label style="display:block;font-size:0.78rem;color:var(--text-secondary);margin-top:4px;">Deploy on <select id="${escapeHtml(selId)}" class="form-control" style="display:inline-block;width:auto;min-height:36px;"><option value="">Pick a day…</option>${days.map(d => `<option value="${escapeHtml(d.id)}"${def && d.id === def.id ? ' selected' : ''}>${escapeHtml(formatDateDisplay(new Date(_jobDayDateMs(d)), { weekday: 'short', month: 'short', day: 'numeric' }))}</option>`).join('')}</select></label>` : '');
    const btns = can ? `<div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap;">
        <button type="button" class="btn btn-primary btn-sm" style="min-height:40px;" onclick="event.stopPropagation();window.DispatchModule.rentalReqConfirm('${escJsAttr(job.id)}','${escJsAttr(e.assetId)}',${Number(e.rev) || 0})">Confirm</button>
        <button type="button" class="btn btn-secondary btn-sm" style="min-height:40px;" onclick="event.stopPropagation();window.DispatchModule.rentalReqDecline('${escJsAttr(job.id)}','${escJsAttr(e.assetId)}',${Number(e.rev) || 0})">Decline</button></div>` : '';
    return `<div style="padding:8px 10px;margin-top:6px;border-radius:8px;${red ? 'background:#FEE2E2;border:1px solid #FCA5A5;' : 'background:#FEF3C7;border:1px solid #FDE68A;'}color:var(--text-primary);">
      <div style="font-weight:600;font-size:0.85rem;">${escapeHtml(line)}</div>${dayHtml}${btns}</div>`;
  }).join('');
  return `<div class="job-details-section rental-req-section"><h4>Rental requests — nothing is held yet</h4>
    <div style="font-size:0.78rem;color:var(--text-secondary);">Confirm opens Assign Crew with the rental ticked at the asked number; it deploys only when you Save. Change the number there if needed.</div>${rows}</div>`;
}
function _rentalReqMe() {
  const u = window.AppState?.user || {};
  return { uid: u.uid || null, name: String(u.displayName || u.email || 'Office').slice(0, 120) };
}
// Rev-checked write of ONE entry in a transaction. `mutate(e, now)` returns the
// new entry, or a string outcome ('gone' | 'changed' | 'claimed') to stop.
async function _rentalReqTx(jobId, assetId, rev, mutate, token = null) {
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(assetId)) return 'gone';
  const jobRef = doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId);
  return runTransaction(db, async (tx) => {
    const s = await tx.get(jobRef);
    const m = s.exists() ? (s.data() || {}).assetRequests : null;
    const e = m && typeof m === 'object' ? m[assetId] : null;
    if (!_rentalReqOpen(e, token)) return 'gone';
    if (rev != null && e.rev !== rev) return 'changed';
    const next = mutate(e, Timestamp.now());
    if (typeof next === 'string') return next;
    tx.update(jobRef, { [`assetRequests.${assetId}`]: next });
    return { ok: true, entry: next };
  });
}
function _rentalReqBase(e) {
  // The driver's own fields, carried as-is (claim fields dropped).
  return { qty: e.qty, dayId: e.dayId || null, personId: e.personId || null, personName: e.personName || '', name: e.name || '', at: e.at || null, rev: e.rev };
}
function _rentalReqMirror(jobId, assetId, entry) {
  const job = _resolveJobAnywhere(jobId);
  if (job && job.assetRequests && typeof job.assetRequests === 'object') job.assetRequests = { ...job.assetRequests, [assetId]: entry };
}
async function rentalReqConfirm(jobId, assetId, rev) {
  const job = _resolveJobAnywhere(jobId);
  const e = job && job.assetRequests && job.assetRequests[assetId];
  if (!_rentalReqOpen(e, null) || e.rev !== rev) { window.showToast?.('That request changed, is being deployed, or was already answered. Reopen the job.', 'info'); return; }
  if (!_guardManageDays()) return;
  // Re-read the asset: it must STILL be an active rental (never trust the request).
  let a = null;
  try {
    const as = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'assets', assetId));
    a = as.exists() ? { id: as.id, ...as.data() } : null;
  } catch (err) { window.showToast?.("Couldn't check that rental. Nothing was changed. Try again.", 'error'); return; }
  if (!a || !_driverAssetIsRental(a) || a.active === false) {
    window.showToast?.(`${_rentalReqAssetLabel({ ...e, assetId })} is no longer an active rental, so it can't be deployed from this request. Decline it, or add equipment from Assign Crew.`, 'warning', null, 10000);
    return;
  }
  const days = _rentalReqJobDays(jobId);
  let day = _rentalReqDayOf(e, days);
  if (!day) {
    const pick = document.getElementById(`rental-req-day-${assetId}`)?.value || '';
    day = days.find(d => d.id === pick) || null;
    if (!day) { window.showToast?.('Pick the day to deploy it on first.', 'info'); return; }
  }
  if ((day.assetDirections || {})[assetId] === 'return') {
    window.showToast?.('That day has a pickup of this rental. Pick another day, or change it in Assign Crew.', 'warning', null, 10000);
    return;
  }
  const k = _rentalDayKeyOf(day);
  if ((k && k < _rentalTodayKey()) || _RENTAL_REQ_CLOSED_DAY.includes(day.status)) {
    const ok = (await window.showConfirm?.('Deploy on a past or closed day?',
      `${k || 'That day'} is ${_RENTAL_REQ_CLOSED_DAY.includes(day.status) ? day.status : 'in the past'}. Deploying the rental there puts it out from that day.\n\nOpen it anyway?`,
      { confirmText: 'Open Assign Crew', danger: true })) === true;
    if (!ok) return;
  }
  const me = _rentalReqMe();
  // COD r2 — the claim names THIS confirm (a token), not just the login, so a
  // second tab / modal of the same user takes it over and the first one's
  // Save is refused by _rentalReqRecheckBeforeSave.
  const claimToken = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
  let out;
  try {
    out = await _rentalReqTx(jobId, assetId, rev, (cur, now) => {
      const cms = _rentalReqMs(cur.claimedAt);
      if (cur.claimedBy && cur.claimedBy !== me.uid && cms != null && Date.now() - cms < _RENTAL_REQ_CLAIM_MS) return 'claimed:' + String(cur.claimedByName || 'Someone');
      return { ..._rentalReqBase(cur), claimedBy: me.uid, claimedByName: me.name, claimedAt: now, claimToken };
    });
  } catch (err) {
    console.warn('[Dispatch] TASK-10106 S1b claim failed', err);
    window.showToast?.(err?.code === 'permission-denied' ? "Your login can't change this job." : "Couldn't start the confirm. Nothing was changed. Try again.", 'error');
    return;
  }
  if (out === 'gone') { window.showToast?.('That request was already answered or taken back.', 'info'); return; }
  if (out === 'changed') { window.showToast?.('The driver just changed that request. Reopen the job to see the new one.', 'info'); return; }
  if (typeof out === 'string' && out.startsWith('claimed:')) { window.showToast?.(`${out.slice(8)} is confirming that request right now.`, 'info'); return; }
  _rentalReqMirror(jobId, assetId, out.entry);
  await showAssignment(jobId, day.id, { assetRequest: { jobId, dayId: day.id, assetId, rev, claimToken, qty: Math.max(1, Number(e.qty) || 1), name: _rentalReqAssetLabel({ ...e, assetId }), personName: String(e.personName || 'the driver').slice(0, 60) } });
}
// COD r1 (Code Reviewer) — BEFORE saveAssignment moves any stock: the ask must
// still be pending, on the rev Confirm started from, and claimed by THIS user
// (an expired claim taken over by someone else refuses). Re-stamps the claim
// so it cannot lapse mid-save. → true to proceed; false = nothing was saved.
// COD r2: the claim must be THIS confirm's token (another user, or another tab
// of the same login, refuses); a save that no longer carries the asked rental
// deploys nothing of the ask and is not checked; a refusal closes the modal so
// a second Save cannot slip past with the stale picks.
async function _rentalReqRecheckBeforeSave(jobDayId, selectedAssets) {
  const r = DispatchState.pendingAssetRequest;
  if (!r || r.dayId !== jobDayId) return true;
  if (Array.isArray(selectedAssets) && !selectedAssets.includes(r.assetId)) return true;
  let out;
  try {
    out = await _rentalReqTx(r.jobId, r.assetId, r.rev, (cur, now) => {
      if (!r.claimToken || cur.claimToken !== r.claimToken) return 'claimed:' + String(cur.claimedByName || 'Someone else');
      // COD r3 🔴 — consume it NOW (before any stock moves): no other Confirm
      // or tab can open it while this save runs.
      return { ..._rentalReqBase(cur), claimedBy: cur.claimedBy || null, claimedByName: cur.claimedByName || '', claimedAt: now, claimToken: r.claimToken, status: 'deploying', deployingAt: now };
    }, r.claimToken);
  } catch (err) {
    console.warn('[Dispatch] TASK-10106 S1b pre-save recheck failed', err);
    window.showToast?.("Couldn't check the driver's request — nothing was saved. Try again.", 'error');
    return false;
  }
  if (out && out.ok) return true;
  DispatchState.pendingAssetRequest = null;
  try { closeModal('assignment'); } catch (_) { /* nothing was saved either way */ }
  window.showToast?.(out === 'changed' ? `${r.personName} changed the request — nothing was saved. Reopen the job to confirm the new one.`
    : (typeof out === 'string' && out.startsWith('claimed:')) ? `${out.slice(8)} is confirming that request now — nothing was saved.`
      : 'That request was already answered or taken back — nothing was saved. Reopen the job.', 'warning', null, 10000);
  return false;
}
// COD r3 — a save that consumed the ask ('deploying') but did not deploy it
// (rental unticked/refused, or the save threw) puts it back to pending under
// the same claim, so the office can retry. Best effort: a mark that cannot be
// released reopens on its own after the claim window.
async function _rentalReqRelease(r) {
  if (!r || !r.claimToken) return null;
  try {
    return await _rentalReqTx(r.jobId, r.assetId, r.rev, (cur, now) => {
      if (cur.claimToken !== r.claimToken || cur.status !== 'deploying') return 'gone';
      return { ..._rentalReqBase(cur), claimedBy: cur.claimedBy || null, claimedByName: cur.claimedByName || '', claimedAt: now, claimToken: r.claimToken };
    }, r.claimToken);
  } catch (err) {
    console.warn('[Dispatch] TASK-10106 S1b release failed', err);
    return 'error';
  }
}
// After saveAssignment's day write LANDED: mark the request answered only when
// the rental is on the day and was not refused (held by another job).
async function _rentalReqResolveAfterSave(jobDayId, data, refused) {
  const r = DispatchState.pendingAssetRequest;
  DispatchState.pendingAssetRequest = null;
  if (!r || r.dayId !== jobDayId) return null;
  // COD r1 (Code Reviewer): only a DEPLOY answers a deploy ask — a rental
  // flipped to Return in the modal is a pickup, not this request.
  const landed = Array.isArray(data.assets) && data.assets.includes(r.assetId)
    && !(data.assetDirections && data.assetDirections[r.assetId] === 'return')
    && !(refused || []).some(x => x && x.assetId === r.assetId);
  if (!landed) {
    await _rentalReqRelease(r);
    window.showToast?.(`Saved, but ${r.name} was not deployed, so ${r.personName}'s request is still waiting.`, 'warning', null, 10000);
    return 'pending';
  }
  const finalQty = Math.max(1, Number(data.assetQtys && data.assetQtys[r.assetId]) || 1);
  const me = _rentalReqMe();
  const answer = (cur, now) => ({
    ..._rentalReqBase(cur),
    status: finalQty === (Number(cur.qty) || 1) ? 'confirmed' : 'edited',
    resolvedQty: finalQty, resolvedBy: me.uid, resolvedByName: me.name, resolvedAt: now, resolvedDayId: jobDayId,
  });
  try {
    // COD r2 (Domain) — one retry: a failed answer leaves a deployed ask pending.
    let out;
    try { out = await _rentalReqTx(r.jobId, r.assetId, r.rev, answer, r.claimToken); } catch (e1) { out = await _rentalReqTx(r.jobId, r.assetId, r.rev, answer, r.claimToken); }
    if (out === 'changed') window.showToast?.(`${r.personName} changed the request while you saved. The new ask is still waiting on the job.`, 'warning', null, 10000);
    else if (out && out.ok) _rentalReqMirror(r.jobId, r.assetId, out.entry);
    return out;
  } catch (err) {
    console.warn('[Dispatch] TASK-10106 S1b resolve failed', err);
    window.showToast?.(`Saved and deployed, but ${r.personName}'s request could not be marked done. Decline it from the job so it isn't deployed twice.`, 'warning', null, 12000);
    return 'error';
  }
}
async function rentalReqDecline(jobId, assetId, rev) {
  const job = _resolveJobAnywhere(jobId);
  const e = job && job.assetRequests && job.assetRequests[assetId];
  if (!_rentalReqOpen(e, null)) { window.showToast?.('That request is being deployed or was already answered.', 'info'); return; }
  if (!_guardManageDays()) return;
  const ok = (await window.showConfirm?.('Decline this request?',
    `Tell ${String(e.personName || 'the driver').slice(0, 60)} no to ${Number(e.qty) || 1} ${_rentalReqAssetLabel({ ...e, assetId })}? Nothing is deployed. They see it on the job.`,
    { confirmText: 'Decline', danger: true })) === true;
  if (!ok) return;
  const me = _rentalReqMe();
  let out;
  try {
    out = await _rentalReqTx(jobId, assetId, rev, (cur, now) => {
      const cms = _rentalReqMs(cur.claimedAt);
      if (cur.claimedBy && cur.claimedBy !== me.uid && cms != null && Date.now() - cms < _RENTAL_REQ_CLAIM_MS) return 'claimed:' + String(cur.claimedByName || 'Someone');
      return { ..._rentalReqBase(cur), status: 'declined', resolvedQty: 0, resolvedBy: me.uid, resolvedByName: me.name, resolvedAt: now };
    });
  } catch (err) {
    console.warn('[Dispatch] TASK-10106 S1b decline failed', err);
    window.showToast?.(err?.code === 'permission-denied' ? "Your login can't change this job." : "Couldn't decline it. Nothing was changed. Try again.", 'error');
    return;
  }
  if (out === 'gone') { window.showToast?.('That request was already answered or taken back.', 'info'); return; }
  if (out === 'changed') { window.showToast?.('The driver just changed that request. Reopen the job to see the new one.', 'info'); return; }
  if (typeof out === 'string' && out.startsWith('claimed:')) { window.showToast?.(`${out.slice(8)} is confirming that request right now.`, 'info'); return; }
  _rentalReqMirror(jobId, assetId, out.entry);
  window.showToast?.('Request declined. The driver sees it on the job.', 'success');
  if (!document.getElementById('modal-job-details')?.classList.contains('hidden')) showJobDetails(jobId);
}
function _driverStopsSig(list) {
  return JSON.stringify((list || []).map(w => [String(w?.name || '').trim(), String(w?.url || '').trim(),
    w?.lat == null ? null : Number(w.lat), w?.lng == null ? null : Number(w.lng)]));
}
// Only the four arrays a driver may change — never assets / rates.
function _driverCrewSide(c) {
  const pick = (k) => (Array.isArray(c?.[k]) ? c[k].map(String) : []);
  return { drivers: pick('drivers'), helpers: pick('helpers'), trucks: pick('trucks'), trailers: pick('trailers') };
}

// TASK-9563 — the Project picker on the driver form. A PIN session cannot read
// `projects` (TASK-9563 slice 0), so the options come from the session-bound
// driverProjectInfo callable: the chosen customer's ACTIVE projects only — the
// same set driverSaveJob accepts. Appears once a Customer is chosen
// (progressive: no customer → only "No project"). The job's current project is
// kept as an option even when it is no longer active, so opening an edit never
// silently reads as "No project".
let _driverProjectReqSeq = 0;
async function _driverRefreshProjectOptions(keepId, opts = {}) {
  if (window.AppState?.driverModePin !== true) return;
  const sel = document.getElementById('job-project-id');
  const section = document.getElementById('job-project-section');
  if (!sel) return;
  const customerId = document.getElementById('job-customer')?.value || '';
  // OPEN (keepId passed): the job's own project stays as an option even when it
  // is no longer active. CUSTOMER CHANGE (keepId undefined): the pick survives
  // ONLY if it is one of the new customer's projects — COD 2026-10-05 (Code
  // Reviewer): re-inserting the old one defeated TASK-9606's customer filter and
  // made driverSaveJob refuse the save as a customer/project mismatch.
  // TASK-9703 — COPY (opts.prefer): the source job's project, judged like a
  // customer change (kept only if it is one of this customer's ACTIVE
  // projects — the set driverSaveJob's create accepts).
  const initial = keepId !== undefined;
  const preferring = !initial && opts.prefer !== undefined;
  const keep = initial ? (keepId || '') : (preferring ? (opts.prefer || '') : (sel.value || ''));
  const seq = ++_driverProjectReqSeq;
  const esc = window.escAttr || escapeHtml;
  const paint = (list) => {
    if (seq !== _driverProjectReqSeq) return; // a newer customer pick owns the picker
    const opts = ['<option value="">No project</option>']
      .concat(list.map(p => `<option value="${esc(p.id)}" data-po="" data-name="${esc(p.name || '')}">${escapeHtml(p.name || 'Untitled project')}${p.status && p.status !== 'active' ? ` (${escapeHtml(p.status)})` : ''}</option>`));
    sel.innerHTML = opts.join('');
    const kept = list.some(p => p.id === keep);
    sel.value = kept ? keep : '';
    if (keep && !kept) _releaseProjectJobDefaultClaims(); // TASK-4696 COD r2 — dropped project's values are the job's
    _syncJobProjectLinkBox();
    if (section) section.style.display = (customerId || (initial && keep)) ? '' : 'none';
    if (!initial && keep && !kept) {
      window.showToast?.(preferring
        ? "The original job's project isn't open for this customer, so the copy has No project. Pick one, or leave it."
        : "That project isn't for the new customer, so it was taken off this job. Pick one of their projects, or leave it on No project.", 'info', null, 8000);
    }
  };
  if (!customerId && !(initial && keep)) { paint([]); return; }
  try {
    const call = httpsCallable(functions, 'driverProjectInfo');
    const [forCustomer, current] = await Promise.all([
      customerId ? call({ customerId }) : Promise.resolve({ data: { projects: [] } }),
      (initial && keep) ? call({ ids: [keep] }) : Promise.resolve({ data: { projects: [] } }),
    ]);
    const list = (forCustomer?.data?.projects || []).slice();
    const cur = (current?.data?.projects || [])[0];
    if (cur && !list.some(p => p.id === cur.id)) list.unshift(cur);
    paint(list);
  } catch (e) {
    console.warn('[Dispatch] TASK-9563: project list unavailable for the driver form', e?.message || e);
    // Never strand the link on OPEN: keep the job's project as the only choice.
    // On a customer change the old project cannot be right — drop it.
    paint(initial && keep ? [{ id: keep, name: 'Current project', status: '' }] : []);
  }
}

// TASK-9563 — Edit with no day in hand: crew is per-day, and the server refuses
// a crew change with no crewDayId. Say so on the form instead of letting a
// driver tick boxes that cannot save.
function _driverApplyCrewScope() {
  const section = document.getElementById('job-crew-section');
  if (!section) return;
  const editing = !!document.getElementById('job-id')?.value;
  const noDay = editing && !_editJobDayId;
  // Hide the pickers rather than disable them: a tile tap toggles via
  // toggleJobCheckbox, which a disabled input would not stop.
  // TASK-10106 S1a — equipment is per-day too (same crew-day rule on the server).
  ['job-section-drivers', 'job-section-trucks', 'job-section-trailers', 'job-section-assets'].forEach(id => {
    const s = document.getElementById(id);
    if (s) s.style.display = noDay ? 'none' : '';
  });
  // What the driver's Assets list is (and is not): standard equipment only.
  const assetsSec = document.getElementById('job-section-assets');
  if (assetsSec) {
    let an = document.getElementById('job-assets-driver-note');
    if (!an) {
      an = document.createElement('div');
      an.id = 'job-assets-driver-note';
      an.className = 'form-hint';
      an.style.cssText = 'margin:4px 0 8px;color:var(--text-secondary);font-size:0.8rem;';
      assetsSec.querySelector('.collapsible-body')?.before(an);
    }
    an.textContent = editing
      ? 'Your own equipment only. Ask the office for rentals below; stock items are added by dispatch, and anything dispatch put on this day stays.'
      : 'Your own equipment only. Ask the office for rentals below; stock items are added by dispatch.';
  }
  let note = document.getElementById('job-crew-driver-note');
  if (!note) {
    note = document.createElement('div');
    note.id = 'job-crew-driver-note';
    note.className = 'form-hint';
    note.style.cssText = 'margin:4px 0 8px;color:var(--text-secondary);font-size:0.8rem;';
    section.querySelector('.form-section-header')?.after(note);
  }
  note.textContent = noDay
    ? 'To change who or what is on this job, open it from a day on the board.'
    : (editing ? 'Crew changes apply to the day you opened this job from. Everyone added or removed gets the usual job text.' : 'Crew is put on every day of this job. Everyone added gets the usual job text.');
}

// ── TASK-10106 S5 — PO on a blank job + photos / load permits (driver) ──────
// PO: editable on Add Job and on an edit whose PO is blank (the driver fills it
// once). A job that already has a PO shows it read-only when dispatch shares it
// with drivers (poVisible), else not at all. "Show PO to drivers" and the extra
// POs stay office-only. driverSaveJob re-checks all of it (functions/src/
// index.js _driverJobPo + the poWrite guard).
// Files: the office Attachments section minus its office-only parts. A driver
// ADDS photos / load permits (uploaded after the job saves, then driverSaveJob
// { jobId, files }); the X shows only on a file THIS driver added from the
// driver board (server stamp uploadedVia 'driver_app' + personId), and the
// server refuses that too once the job has a ticket. Office photos the job does
// not share with drivers (photosVisible off) are not listed.
// TASK-9694 S6 (Chris 2026-10-28, "force for add only"): a job's files are the
// office's record — a driver never removes one, not even their own, so no ✕
// for a driver (driverSaveJob refuses the removal too). Kept as the one gate.
function _driverOwnsJobFile(f) {
  void f;
  return false;
}
function _driverApplyPoAndFiles(job, opts = {}) {
  const editing = !!job && !opts.copy;
  const po = document.getElementById('job-po');
  const row = document.getElementById('job-po-row');
  if (opts.copy && po) { po.value = ''; delete po.dataset.autofilledPo; onJobPoInput(); } // a PO belongs to one job
  const hasPo = editing && !!normalizePoForSave(job.poNumber);
  if (row) row.style.display = (hasPo && job.poVisible !== true) ? 'none' : '';
  DispatchState.driverPoLocked = hasPo; // _applyEditJobInvoiceLock keeps it read-only too
  if (po) po.readOnly = hasPo;
  const vis = document.getElementById('job-po-visible')?.closest('.form-group');
  if (vis) vis.style.display = 'none';
  const extraBtn = document.querySelector('#job-po-row [data-job-extra-po-add]');
  if (extraBtn) extraBtn.style.display = 'none';
  const extra = document.getElementById('job-extra-pos');
  if (extra) extra.style.display = 'none';
  let poNote = document.getElementById('job-po-driver-note');
  if (!poNote && po) {
    poNote = document.createElement('div');
    poNote.id = 'job-po-driver-note';
    poNote.className = 'form-hint';
    poNote.style.cssText = 'margin-top:4px;color:var(--text-secondary);font-size:0.8rem;';
    po.parentElement?.appendChild(poNote);
  }
  if (poNote) poNote.textContent = hasPo ? 'Only dispatch can change the PO.' : 'Add the PO if the customer gave you one. Once saved, only dispatch can change it.';

  DispatchState.driverFileRemovals = [];
  if (opts.copy) {
    // A copy is a CREATE: the source job's files are not carried (the server
    // create takes none), so the form must not pretend they will be.
    DispatchState.existingJobPhotos = [];
    DispatchState.existingPermitFiles = [];
    DispatchState.existingPermitDocs = null;
    DispatchState.loadedPhotoCount = 0;
    DispatchState.loadedPermitCount = 0;
  }
  const tog = document.getElementById('job-attachments-toggle');
  if (tog) {
    tog.checked = true;
    toggleAttachmentsSection();
    if (tog.parentElement) tog.parentElement.style.display = 'none';
  }
  const pv = document.getElementById('job-photos-visible')?.closest('.form-group');
  if (pv) pv.style.display = 'none';
  document.querySelectorAll('#job-attachments-section .permit-billing-options').forEach(el => { el.style.display = 'none'; });
  renderJobPhotoGrid();
  renderPermitGrid();
}

// After driverSaveJob saved the job: upload the files picked on the form to the
// office paths (the job id is in the path), then attach them through
// driverSaveJob { jobId, files } — which re-reads every object's own metadata.
// Never throws: the job is already saved, so a file problem is a toast.
// `pauseHang` (from saveJob's PIN branch) clears saveJob's own 25s hang timer.
async function _driverSaveJobFiles(jobId, pauseHang) {
  const photos = (DispatchState.pendingJobPhotos || []).slice();
  const permits = (DispatchState.pendingPermitFiles || []).filter(p => p && p.blob);
  const removals = (DispatchState.driverFileRemovals || []).slice();
  DispatchState.driverFileRemovals = [];
  if (!jobId || (!photos.length && !permits.length && !removals.length)) return;
  // The 25s hang detector must not re-enable Save while files upload: on a
  // create that second press would add the job twice (TASK-9151 rule). COD r1:
  // the timer is saveJob's local, so saveJob hands in the way to pause it.
  if (typeof pauseHang === 'function') { try { pauseHang(); } catch (_) { /* never blocks the files */ } }
  const tid = window.AppState?.tenant || DispatchState.tenantId;
  const items = [];
  let failed = 0;
  try {
    if (photos.length) {
      const up = await uploadJobPhotos(tid, jobId); // clears pendingJobPhotos; toasts its own failures
      for (const e of up) items.push({ kind: 'photo', id: e.id });
    }
    for (const p of permits) {
      try {
        const e = await _uploadOnePermit(permitStorageBasePath(tid, jobId), p, () => {});
        items.push({ kind: 'permit', id: e.id, name: String(e.name || '').slice(0, 120) });
      } catch (err) {
        failed++;
        window.logError?.(err, { module: 'dispatch', operation: 'driver-permit-upload', jobId });
      }
      if (p.previewUrl) { try { URL.revokeObjectURL(p.previewUrl); } catch (_) {} }
      if (p.fullPreviewUrl) { try { URL.revokeObjectURL(p.fullPreviewUrl); } catch (_) {} }
    }
  } finally {
    DispatchState.pendingPermitFiles = [];
  }
  if (failed) window.showToast?.(`${failed} file${failed === 1 ? '' : 's'} didn't upload. Open the job and add ${failed === 1 ? 'it' : 'them'} again.`, 'warning', null, 9000);
  // COD r1-r3 (Edge Case) — removals and adds ride in ONE call (removals
  // first): the server applies them in one transaction, removals before the
  // cap check, so swapping a photo on a full job is all-or-nothing and an old
  // photo is never lost to a failed add. If that call is refused (e.g. the job
  // got a ticket, so removals are dispatch's now), the adds are retried ALONE,
  // so a refused removal never drops the files just uploaded.
  const rem = removals.map(r => ({ kind: r.kind, id: r.id, remove: true }));
  const all = rem.concat(items);
  const chunks = [];
  // COD r4 — the whole set in ONE call (the server takes up to 30 = every
  // photo + permit each way), so a swap never commits half. Chunk only past that.
  for (let i = 0; i < all.length; i += 30) chunks.push(all.slice(i, i + 30));
  const filesOf = (chunk) => ({
    add: chunk.filter(x => !x.remove).map(x => (x.name != null ? { kind: x.kind, id: x.id, name: x.name } : { kind: x.kind, id: x.id })),
    remove: chunk.filter(x => x.remove).map(x => ({ kind: x.kind, id: x.id })),
  });
  let added = 0;
  let removed = 0;
  const call = async (files) => {
    const res = await httpsCallable(functions, 'driverSaveJob')({ jobId, files });
    added += Number(res?.data?.added) || 0;
    removed += Number(res?.data?.removed) || 0;
  };
  const fileErr = (x) => window.showToast?.(`The job saved, but some files didn't: ${x?.message || 'check your connection and try again.'}`, 'error', null, 10000);
  for (const chunk of chunks) {
    const files = filesOf(chunk);
    try {
      await call(files);
    } catch (e) {
      console.error('[Dispatch] TASK-10106 S5 driver files failed', e);
      window.logError?.(e, { module: 'dispatch', operation: 'driverSaveJob-files', jobId });
      // TASK-10106 S5 follow-up (COD re-review) — ONE retry, and only a server
      // refusal of the removals splits the swap:
      //  - quota (resource-exhausted): no retry.
      //  - failed-precondition (a ticket, or a file only dispatch may remove):
      //    the adds ALONE, so a refused removal never drops the new uploads.
      //  - anything else (no reply, server hiccup): the SAME set, removals
      //    included — driverSaveJob skips an already-gone removal and an
      //    already-present add, so a call that did commit re-runs as a no-op.
      //    Retried as adds alone, a 4-permit swap became 8 permits.
      const code = String(e?.code || '');
      const retry = /resource-exhausted/.test(code) ? null
        : !/failed-precondition/.test(code) ? files // same set
          : (files.remove.length && files.add.length ? { add: files.add, remove: [] } : null);
      if (retry !== files) fileErr(e);
      if (!retry) continue;
      try {
        const before = added + removed;
        await call(retry);
        // COD r1 — a same-set retry that changed nothing means the first call
        // committed and only its reply was lost: say so, or the driver re-picks
        // the files (new ids) and the job doubles up.
        if (retry === files && added + removed === before) { added += files.add.length; removed += files.remove.length; }
      } catch (e2) {
        window.logError?.(e2, { module: 'dispatch', operation: 'driverSaveJob-files-retry', jobId });
        fileErr(e2);
      }
    }
  }
  if (added || removed) {
    const bits = [];
    if (added) bits.push(`added ${added} file${added === 1 ? '' : 's'}`);
    if (removed) bits.push(`removed ${removed}`);
    window.showToast?.(`Job files: ${bits.join(', ')}.`, 'success');
  }
}

async function driverOpenAddJob(prefilledDate = null) {
  if (!driverJobEditEnabled()) {
    window.showToast?.('Adding jobs from the driver board is turned off for your company.', 'info');
    return;
  }
  try {
    _ensureDriverJobModalMounted();
    await Promise.all([_driverSeedCustomers(), _driverSeedRoster()]);
    showAddJob(prefilledDate);
    _applyDriverJobFormTrim();
    _driverApplyPoAndFiles(null); // TASK-10106 S5
    _driverCaptureFormOrig(null);
    _driverApplyCrewScope();
    _driverRenderRentalRequests(null); // TASK-10106 S1b
    _driverRefreshProjectOptions('');
  } catch (e) {
    console.error('[Dispatch] TASK-9370 driverOpenAddJob failed', e);
    window.showToast?.('Could not open the job form — refresh and try again.', 'error');
  }
}

async function driverOpenEditJob(job, jobDay = null) {
  if (!driverJobEditEnabled()) {
    window.showToast?.('Editing jobs from the driver board is turned off for your company.', 'info');
    return;
  }
  if (!job || !job.id) return;
  try {
    _ensureDriverJobModalMounted();
    await Promise.all([_driverSeedCustomers(), _driverSeedRoster()]);
    DispatchState.driverBoardJobs = [job]; // _resolveJobAnywhere finds it here
    // TASK-9563 — the day the details modal was opened from IS the crew scope
    // (editJob → _pickEditJobSeedDay reads this pool; never a guessed day).
    DispatchState.driverBoardJobDays = (jobDay && jobDay.id && jobDay.jobId === job.id) ? [jobDay] : [];
    editJob(job.id, jobDay?.id || null);
    _applyDriverJobFormTrim();
    _driverApplyEditActions(job);
    _driverApplyPoAndFiles(job); // TASK-10106 S5
    _driverCaptureFormOrig(job.projectId || null);
    _driverFormOrig.po = normalizePoForSave(job.poNumber); // the STORED PO decides, not the box
    _driverApplyCrewScope();
    _driverRenderRentalRequests(job); // TASK-10106 S1b — this job's asks + answers
    _driverRefreshProjectOptions(job.projectId || '');
  } catch (e) {
    console.error('[Dispatch] TASK-9370 driverOpenEditJob failed', e);
    window.showToast?.('Could not open the job — refresh and try again.', 'error');
  }
}

// ── TASK-9703 — the Edit Job footer for a driver: Add Another Day, Copy to New
// Job, and Delete (own jobs only). The buttons are the office's own; each
// office handler branches here on a PIN session. The server (driverAddJobDay /
// driverSaveJob / driverDeleteJob) re-checks the switch + per-driver block
// list and every refusal; these gates only decide what to SHOW.
function _driverJobEditOffToast(verb) {
  const cfg = window.AppState?.settings?.driverJobEdit;
  const pid = window.AppState?.driverPersonId;
  const blocked = !!pid && Array.isArray(cfg?.blockedPersonIds) && cfg.blockedPersonIds.includes(pid);
  window.showToast?.(blocked
    ? `${verb} jobs from the driver board is turned off for you. Ask dispatch.`
    : `${verb} jobs from the driver board is turned off for your company.`, 'info');
}

// A job THIS driver added on the driver board — the only kind a driver may
// delete (the server's driverJobDeleteGuard is the real check).
function _driverOwnsJob(job) {
  const pid = window.AppState?.driverPersonId;
  return !!job && !!pid && job.source === 'driver-board' && job.createdByDriverPersonId === pid;
}

function _driverApplyEditActions(job) {
  const bar = document.getElementById('job-edit-actions');
  if (!bar) return;
  const del = bar.querySelector('button[onclick*="deleteCurrentJob"]');
  if (del) del.style.display = _driverOwnsJob(job) ? '' : 'none';
}

// Add Another Day: the date, then the driverAddJobDay callable. Reached
// straight from the button on a clean form, or from saveJob's PIN branch once
// the driver's unsaved edits have saved (the office order: save, then add).
async function _driverAddDayPrompt(jobId) {
  if (!jobId) return;
  if (!driverJobEditEnabled()) { _driverJobEditOffToast('Adding'); return; }
  const dateStr = await showDatePickerModal('Add Day to Job', 'Select the date to add:');
  if (!dateStr) return;
  try {
    const res = await httpsCallable(functions, 'driverAddJobDay')({ jobId, date: dateStr });
    const d = res?.data || {};
    const label = new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (d.mode === 'exists') {
      window.showToast?.(`${label} is already on this job — nothing was added.`, 'info');
    } else {
      window.showToast?.(d.crewCarried ? `Day added: ${label}, with the same crew as the last day.` : `Day added: ${label}`, 'success');
      // The company's hard stop is off, so it saved — but say who/what is out
      // of date (labels built server-side: "First L." / "Vehicle 12").
      if (Array.isArray(d.credentialWarnings) && d.credentialWarnings.length) {
        const what = { cdl: 'CDL', medCard: 'medical card', annualInspection: 'annual inspection', trailerAnnualInspection: 'annual inspection' };
        const parts = d.credentialWarnings.slice(0, 4).map(w => `${w.label}: ${what[w.kind] || 'credential'} expired`);
        window.showToast?.(`Saved, but check with dispatch — ${parts.join('; ')}.`, 'warning', null, 12000);
      }
    }
    const modal = document.getElementById('modal-job');
    if (modal && !modal.classList.contains('hidden') && document.getElementById('job-id')?.value === jobId) closeModal('job');
  } catch (e) {
    console.error('[Dispatch] TASK-9703 driverAddJobDay failed', e);
    window.logError?.(e, { module: 'dispatch', operation: 'driverAddJobDay', jobId });
    window.showToast?.(e?.message || 'Could not add the day — check your connection and try again.', 'error');
  }
}

function _driverAddDayToCurrentJob(jobId) {
  if (!driverJobEditEnabled()) { _driverJobEditOffToast('Adding'); return; }
  if (!_jobFormIsDirty()) { _driverAddDayPrompt(jobId); return; }
  // Same hand-off as the office: saveJob's PIN branch runs the add-day only
  // when its save succeeds.
  _pendingAddDayAfterSave = jobId;
  window.showToast?.('Saving your changes first…', 'info');
  const form = document.getElementById('form-job');
  if (form?.requestSubmit) form.requestSubmit();
  else form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
}

// Copy to New Job: the office duplicateJob fills the SAME form (title, customer,
// location, stops, the crew of the day the driver opened); the save is a
// driverSaveJob CREATE, which re-checks every person / vehicle carried and
// strips everything off the allowlist. TASK-10106 S1a — STANDARD equipment
// carries (duplicateJob filters the copy); rentals / stock never do.
function _driverCopyJob(jobId) {
  if (!driverJobEditEnabled()) { _driverJobEditOffToast('Adding'); return; }
  const job = _resolveJobAnywhere(jobId);
  if (!job) { window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error'); return; }
  try {
    duplicateJob(jobId, {
      onPrefilled: () => {
        _applyDriverJobFormTrim();
        // TASK-10106 S2 (COD) — a copy is a CREATE, and driverSaveJob takes
        // only an ACTIVE type on a new job: a legacy / archived source type
        // starts the copy as 'job' instead of failing the save.
        const jt = document.getElementById('job-type');
        if (jt && !_jobTypeList().some(x => x.value === jt.value && !x.archived)) _renderJobTypeSelect('job');
        const t = document.getElementById('modal-job-title');
        if (t) t.textContent = _copyJobModalTitle(job);
        _driverApplyCrewScope();
        _driverApplyPoAndFiles(null, { copy: true }); // TASK-10106 S5 — no PO / files carried
        _driverRefreshProjectOptions(undefined, { prefer: job.projectId || '' });
      },
    });
    _applyDriverJobFormTrim();
    _driverApplyPoAndFiles(null, { copy: true }); // TASK-10106 S5
    const t = document.getElementById('modal-job-title');
    if (t) t.textContent = _copyJobModalTitle(job);
    // A copy is a CREATE: the open-time baseline is an empty form, so every
    // carried stop and crew member goes to the server as part of the new job.
    _driverFormOrig = { projectId: null, stopsSig: '[]', crew: null };
    _driverApplyCrewScope();
    _driverRenderRentalRequests(null); // TASK-10106 S1b — a copy never carries requests
  } catch (e) {
    console.error('[Dispatch] TASK-9703 driver copy failed', e);
    window.showToast?.('Could not copy the job — refresh and try again.', 'error');
  }
}

// Delete: the SAME server door as the office Delete (deleteJobLedgered, in
// driver mode, through driverDeleteJob). Preflight first so a refusal lands in
// the footer before the confirm, exactly like deleteJobFromDetails.
async function _driverDeleteCurrentJob(jobId, btn) {
  if (!jobId) return;
  if (!driverJobEditEnabled()) { _driverJobEditOffToast('Editing'); return; }
  const job = _resolveJobAnywhere(jobId);
  if (!_driverOwnsJob(job)) {
    window.showToast?.('You can only delete a job you added from the driver board. Ask dispatch.', 'info');
    return;
  }
  // TASK-10010 — same busy state + one-at-a-time guard as the office Delete.
  const key = String(jobId);
  if (_jobDeleteInFlight.has(key)) { window.showToast?.('Still working on deleting this job — one moment.', 'info'); return; }
  _jobDeleteInFlight.add(key);
  const busy = _jobDeleteBusy(btn);
  busy.set('Checking…');
  try {
    const call = httpsCallable(functions, 'driverDeleteJob');
    let days = 0;
    try {
      const pre = await call({ jobId, preflight: true });
      days = Number(pre?.data?.days) || 0;
    } catch (pe) {
      _showDeleteBlockAlert(pe?.message || "Can't delete this job right now.", 'Nothing was deleted.', 'job');
      return;
    }
    const note = 'The office can bring it back for 30 days if you need it.';
    busy.pause();
    const ok = await window.showConfirm('Delete Job',
      days > 1 ? `This job has ${days} scheduled days. Deleting it removes all ${days}. ${note}` : `Delete this job? ${note}`,
      { confirmText: 'Delete', danger: true });
    if (!ok) return;
    busy.set('Deleting…');
    try {
      await call({ jobId });
      window.showToast?.(`${_jobDeleteLabel(job)} deleted`, 'success');
      closeModal('job');
    } catch (e) {
      console.error('[Dispatch] TASK-9703 driverDeleteJob failed', e);
      window.logError?.(e, { module: 'dispatch', operation: 'driverDeleteJob', jobId });
      _showDeleteBlockAlert(e?.message || "Couldn't delete the job.", 'Nothing was deleted.', 'job');
    }
  } finally {
    busy.idle();
    _jobDeleteInFlight.delete(key);
  }
}

// The driver payload → driverSaveJob. Sends the date STRINGS only — the server
// anchors jobDay.date to NOON in the TENANT's timezone (COD 2026-09-24: a client
// offset let a driver shift which calendar day a jobDay belongs to). Never sends
// tenantId (bound from the claim), never PO / rates / status (not on the
// allowlist — the server would strip them anyway, so don't even carry them).
// Equipment rides only as `equipment` {before, after} (TASK-10106 S1a), never
// the day's assetQtys / assetDirections / lineItemSelections maps.
// TASK-9563 — project / stops / crew ride along ONLY when the driver changed
// them since the form opened; crew goes as {before, after} so the server applies
// the driver's DELTA to the day's stored crew (never a blind overwrite).
async function _driverSaveJobViaCallable({ jobId, jobData, startDateVal, endDateVal, done, pauseHang }) {
  const payload = {
    jobId: jobId || null,
    title: jobData.title || '',
    customerId: jobData.customerId || null,
    startDate: startDateVal,
    endDate: endDateVal,
    allDay: jobData.allDay === true,
    defaultStart: jobData.defaultStart || null,
    defaultEnd: jobData.defaultEnd || null,
    location: jobData.location || '',
    wellName: jobData.wellName || '',
    notes: jobData.notes || '',
    poc: jobData.poc || {},
  };
  const orig = _driverFormOrig || { projectId: null, stopsSig: '[]', crew: null };
  // TASK-10106 S2 — Type: always on a new job (incl. Copy to New Job); on an
  // edit only when the driver changed it (presence-based, like project / stops).
  const typeNow = jobData.type || 'job';
  if (!jobId || typeNow !== (orig.type || 'job')) payload.type = typeNow;
  // TASK-10106 S5 — PO: on a new job, or on an edit whose PO was blank when the
  // form opened. Never a change or a clear (the server refuses a change).
  const poNow = normalizePoForSave(document.getElementById('job-po')?.value);
  if (poNow && (!jobId || !orig.po)) payload.poNumber = poNow;
  const projectNow = document.getElementById('job-project-id')?.value || null;
  if ((projectNow || null) !== (orig.projectId || null)) payload.projectId = projectNow;
  const stopsNow = Array.isArray(jobData.waypoints) ? jobData.waypoints : [];
  if (_driverStopsSig(stopsNow) !== orig.stopsSig) {
    // TASK-9541 — the stop id rides along; driverSaveJob keeps a well-formed one
    // and mints any that is missing.
    payload.waypoints = stopsNow.map(w => ({ id: w.id || '', name: w.name || '', url: w.url || '', lat: w.lat ?? null, lng: w.lng ?? null }));
  }
  const crewBefore = orig.crew || { drivers: [], helpers: [], trucks: [], trailers: [] };
  const crewNow = _driverCrewSide(_buildJobCrewPayload());
  if (['drivers', 'helpers', 'trucks', 'trailers'].some(k => !_sameIdSet(crewBefore[k], crewNow[k]))) {
    payload.crew = { before: crewBefore, after: crewNow };
    if (jobId) payload.crewDayId = _editJobDayId || null;
  }
  // TASK-10106 S1a — standard equipment, only when the driver changed it, as
  // {before, after}: the server applies the DELTA to the day's stored list, so
  // rentals / stock on that day and anything the driver left alone stay put.
  const equipBefore = Array.isArray(orig.equip) ? orig.equip : [];
  const equipNow = _driverEquipSide();
  if (_driverEquipSig(equipBefore) !== _driverEquipSig(equipNow)) {
    payload.equipment = { before: equipBefore, after: equipNow };
    if (jobId) payload.crewDayId = _editJobDayId || null;
  }
  // TASK-10106 S1b — rental ASKS (and take-backs). Never a deploy: the office
  // confirms from the job. On an edit the ask is for the day it was opened from.
  const reqPayload = _driverRentalReqPayload();
  if (reqPayload) {
    payload.rentalRequests = reqPayload;
    if (jobId) payload.crewDayId = _editJobDayId || null;
  }
  try {
    const res = await httpsCallable(functions, 'driverSaveJob')(payload);
    const d = res?.data || {};
    const label = jobData.title || 'Untitled';
    window.showToast?.(
      jobId
        ? `Job updated: ${label} — ${formatJobToastDates(startDateVal, endDateVal)}`
        : `Job added: ${label} — ${formatJobToastDates(startDateVal, endDateVal)}`,
      'success');
    if (d.jobNumberPending) {
      window.showToast?.('Job saved, but it could not be given a job number yet.', 'info');
    }
    if (d.rentalRequested > 0) {
      window.showToast?.(`Asked the office for ${d.rentalRequested === 1 ? 'a rental' : d.rentalRequested + ' rentals'}. Nothing is held until the office OKs it.`, 'info', null, 8000);
    }
    // TASK-9563 — a stop the server could not pin still saved; say which, the
    // same honest message the office gets (never a silent missing pin).
    if (Array.isArray(d.unpinned) && d.unpinned.length) {
      const names = d.unpinned.slice(0, 3).join(', ') + (d.unpinned.length > 3 ? ` +${d.unpinned.length - 3} more` : '');
      window.showToast?.(`Couldn't find ${names} on the map — the job saved, but ${d.unpinned.length > 1 ? 'those destinations have' : 'that destination has'} no pin. Paste a full map link or type the street address.`, 'warning', null, 10000);
    }
    // TASK-9563 (COD Domain) — the company's hard stop is OFF, so the server
    // saved it, but say out loud who/what is out of date (the office gets a
    // dialog for this; the driver must not seat someone blind).
    if (Array.isArray(d.credentialWarnings) && d.credentialWarnings.length) {
      // Id first, THEN the legacy name/unit string (TASK-4529: never one
      // mixed pass — an id can equal another unit's number).
      const nameOf = (id, list, f) => {
        const arr = list || [];
        const x = arr.find(p => p && p.id === id) || arr.find(p => p && (p.name === id || p.unit === id));
        return x ? (f(x) || id) : id;
      };
      const label = { cdl: 'CDL', medCard: 'medical card', annualInspection: 'annual inspection', trailerAnnualInspection: 'annual inspection' };
      const parts = d.credentialWarnings.slice(0, 4).map(w => {
        const who = w.kind === 'annualInspection' ? nameOf(w.id, DispatchState.trucks, t => t.unit || t.nickname || t.name)
          : w.kind === 'trailerAnnualInspection' ? nameOf(w.id, DispatchState.trailers, t => t.unit || t.nickname || t.name)
            : nameOf(w.id, DispatchState.peopleAll || DispatchState.people, p => p.name);
        return `${who}: ${label[w.kind] || 'credential'} expired`;
      });
      window.showToast?.(`Saved, but check with dispatch — ${parts.join('; ')}.`, 'warning', null, 12000);
    }
    // TASK-10106 S5 — photos / load permits go up AFTER the job exists (its id
    // is in the Storage path); never fails the save that already landed.
    try { await _driverSaveJobFiles(d.jobId || jobId || null, pauseHang); } catch (fe) { console.error('[Dispatch] TASK-10106 S5 files', fe); }
    window.ActionLog?.flush?.('save', { jobId: d.jobId || jobId || null });
    done(true);
  } catch (e) {
    // HttpsError.message IS the server's plain-English refusal (setting off,
    // ticketed day, flagged person, project mismatch) — show it verbatim.
    console.error('[Dispatch] TASK-9370 driverSaveJob failed', e);
    window.logError?.(e, { module: 'dispatch', operation: 'driverSaveJob', jobId: jobId || null });
    window.showToast?.(e?.message || 'Could not save the job — check your connection and try again.', 'error');
    done(false);
  }
}

async function saveJob(e) {
  // TASK-1408 — `jobs` carries the SAME create/update asymmetry as jobDays:
  // create allows any dispatch member, update and delete both require
  // !isViewUploadOnly. So a view-and-upload member could create a job (and its
  // jobDays, below) and then be unable to edit either. Guards create AND edit —
  // rules deny their update regardless, so there is nothing legitimate lost.
  // preventDefault FIRST (COD Code Reviewer): returning from the guard before it
  // would let the browser run the native form submit and reload the page.
  e.preventDefault();
  if (!_guardManageDays()) return;

  // TASK-4877 — read AND clear before any bail-out below can return. Left
  // standing, a save that never ran would hand this to the NEXT save and open a
  // date picker nobody asked for.
  const _addDayAfterThisSave = _pendingAddDayAfterSave;
  _pendingAddDayAfterSave = null;
  // TASK-10006 S3 — quantity rentals this save sent OUT ({ jobId, out }); the
  // "Create the return job now?" prompt opens after a successful save.
  let _s3ReturnPrompt = null;
  // …and when that save then doesn't happen, SAY so. Dropping the request is
  // right — a day must never be added on top of edits that were refused — but
  // dropping it in silence leaves the dispatcher believing they got their day
  // (COD Edge Case Hunter + Independent Reviewer, 2026-09-10).
  const _addDayRequestNotHonored = () => {
    if (!_addDayAfterThisSave) return;
    window.showToast?.('Nothing was saved, so no day was added — fix the problem above, then press Add Another Day again.', 'error');
  };

  // TASK-9587 — a job Claude asked for: every value Claude guessed (amber) is
  // confirmed or changed before anything is written.
  if (_claudeDraftBlocksSave()) { _addDayRequestNotHonored(); return; }

  // In-flight lock — prevents rapid taps / duplicate job creates on mobile
  if (window.__saveJobInFlight) {
    window.showToast?.('Save already in progress — please wait', 'info');
    _addDayRequestNotHonored();
    return;
  }
  // TASK-9151 — the Save button is already disabled while a permit is being
  // prepared or uploaded, but Enter in a text field submits the form anyway.
  // Saving now would snapshot an upload queue that the in-flight file has not
  // joined yet, and then close the modal over it.
  if (_permitPrepBusy || _permitUploadBusy) {
    window.showToast?.('Still working on that permit — one moment, then press Save.', 'info');
    _addDayRequestNotHonored();
    return;
  }
  // TASK-9397 — same for a job photo still converting (not in the list yet).
  if (_jobPhotoPrep.busy) {
    window.showToast?.('Still getting that photo ready — one moment, then press Save.', 'info');
    _addDayRequestNotHonored();
    return;
  }
  // 🔴 TASK-9151 — the submit lock has to be enforced HERE, not only by disabling
  // the button. "Add Another Day" submits through form.requestSubmit(), which
  // ignores a disabled button entirely. Letting that through re-runs the save on a
  // form whose job is already written: on the create path that is a DUPLICATE JOB,
  // and on the edit path it rebuilds `permits` from a stale existingPermitFiles —
  // which never received the permits the first save uploaded — so the write drops
  // them off the job. The day is not refused, it is DEFERRED to closeModal.
  if (DispatchState._permitModalSubmitLock) {
    if (_addDayAfterThisSave) DispatchState._permitPendingAddDayJobId = _addDayAfterThisSave;
    if (hasFailedPermitUploads()) {
      _syncPermitFailureBanner(_addDayAfterThisSave
        ? 'The day gets added as soon as these are sorted out.'
        : 'This job is already saved — sort these out first.');
    } else {
      window.showToast?.('This job is already saved.', 'info');
    }
    return;
  }
  window.__saveJobInFlight = true;

  const saveBtn = document.getElementById('btn-save-job');
  const saveBtnOriginal = saveBtn?.innerHTML;
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // Safety timer — force-restore if Firestore writes hang on flaky mobile networks.
  // TASK-4567 — `let` + a named body so a mid-save CONFIRM can pause it. The
  // jobDay-delete confirm asks the dispatcher to read a list of days and hours;
  // that takes longer than 25s, and the timer firing under it would toast a
  // false "taking too long", re-enable Save and invite a concurrent second save.
  const _armSaveJobSafetyTimer = () => setTimeout(() => {
    if (window.__saveJobInFlight) {
      window.__saveJobInFlight = false;
      if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
      window.showToast?.('Save is taking too long — check your connection and try again', 'error');
    }
  }, 25000);
  let _saveJobSafetyTimer = _armSaveJobSafetyTimer();

  // TASK-4610 (COD Code Reviewer) — every validation bail-out must DISARM the
  // 25s hang detector, the way the waypoint bail-out at :2109 already does.
  // `__saveJobInFlight=false` alone is not enough: if the dispatcher fixes the
  // field and re-saves inside 25s, the ORPHANED first timer fires mid-save,
  // sees the flag true again, toasts a false "taking too long" and re-enables
  // Save — inviting a concurrent second write. One helper, used by all four.
  const _bailOutOfSave = () => {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
    window.__saveJobInFlight = false;
    clearTimeout(_saveJobSafetyTimer);
    _addDayRequestNotHonored(); // TASK-4877
  };

  if (!window.validateRequiredFields(e.target)) {
    _bailOutOfSave();
    return;
  }

  const jobTitle = document.getElementById('job-title').value.trim();
  const startDateVal = document.getElementById('job-start-date').value;
  const endDateVal = document.getElementById('job-end-date').value;
  const jobId = document.getElementById('job-id').value;
  const _p811PotAtSave = _p811FormPotentialForSave(jobId); // TASK-9887
  // TASK-9112 — snapshot the visible window NOW, before the first await, for the
  // same reason the crew scope below is snapshotted: the dispatcher can click a
  // view toggle or a date chevron while this save is in flight, and the toast
  // must describe the board they pressed Save on, not the one they wandered to.
  const _boardRangeAtSave = getVisibleDateRange();
  // TASK-4614 (COD Edge Case Hunter) — snapshot the crew scope NOW, before the
  // first await. `_editJobDayId` is module state: a save that is mid-flight when
  // the dispatcher opens Edit Job on a DIFFERENT day would otherwise read the
  // new day's id after its await and write the wrong day — the same class of
  // bug, one race removed. Every crew write below uses this local.
  const _crewScopeDayId = _editJobDayId;

  // TASK-398: breadcrumb — save clicked, for which job (null = new).
  window.ActionLog?.record('save_click', { form: 'job', jobId: jobId || null });

  // TASK-4610 — ONE coercion point for both fields. Anchors a date-only value
  // at NOON local (UTC midnight shifts to the previous day in western
  // timezones) and REFUSES an unparsable one. Before this, a blank/garbled
  // date produced an Invalid Date, sailed past the `endDate < startDate` check
  // (every NaN comparison is false) and blew up inside Timestamp.fromDate with
  // an opaque "seconds out of range: NaN" that the user saw as a generic
  // "Error saving job".
  const startTs = coerceJobTimestamp(startDateVal);
  const endTs = coerceJobTimestamp(endDateVal);
  if (!startTs) {
    window.showToast?.('Start date is missing or invalid.', 'error');
    document.getElementById('job-start-date')?.focus();
    _bailOutOfSave();
    return;
  }
  if (!endTs) {
    window.showToast?.('End date is missing or invalid.', 'error');
    document.getElementById('job-end-date')?.focus();
    _bailOutOfSave();
    return;
  }
  const startDate = startTs.toDate();
  const endDate = endTs.toDate();

  if (endDate < startDate) {
    window.showToast?.('End date cannot be before start date.', 'error');
    document.getElementById('job-end-date').focus();
    _bailOutOfSave();
    return;
  }

  // TASK-731: hard-block silent no-op project paths. If "Add to Project" is checked,
  // the user must EITHER pick an existing project OR (create-new + non-empty name).
  // Previously a checked box with no selection / blank name silently created nothing
  // and gave zero feedback (Chris repro'd live on the Split Rock call). Validate
  // BEFORE the job write so the user fixes it in the still-open modal. Covers both
  // the new-job and edit-job paths (saveJob is shared).
  // TASK-9363 — a project carried by Copy to New Job must still be the
  // customer's at save (the project list or the customer may have moved since
  // the form opened). Runs first so the validation below and the link after the
  // write both see the corrected box.
  _recheckCarriedProjectCustomer();
  // TASK-4695 (COD Blast/Code/Edge) — the mirror is DERIVED from the picker the
  // dispatcher can see. A writer that set the select without it (a projects
  // snapshot dropping the picked project, a New Job-from-project whose option is
  // gone) would otherwise block every save with the picker already on "No project".
  _syncJobProjectLinkBox();
  // TASK-9599 — set only when the dispatcher said yes to adding this job's
  // customer to a project that names no customer yet. Handed to the gate
  // (changeJobProject adoptCustomer) on whichever path makes the link.
  // COD r1 — holds the customer id the dispatcher agreed to (consent is for
  // that customer only; the gate refuses if the job's customer moved since).
  let _projectAdoptCustomer = '';
  // TASK-9651 — a job in a project can only change customer through the
  // project gate (changeJobProject reads the project), which a login without
  // Projects cannot do. Say so up front instead of a "Nothing was saved".
  if (_jobFormProjectsHidden() && _editJobOriginalProjectId && document.getElementById('job-id')?.value
      && (document.getElementById('job-customer')?.value || null) !== (_editJobOriginalCustomerId || null)) {
    window.showToast?.('This job is in a project, so its customer can only be changed by someone with Projects access. Nothing was saved.', 'error', null, 9000);
    _bailOutOfSave();
    return;
  }
  {
    const _projectChecked = document.getElementById('job-add-to-project')?.checked;
    if (_projectChecked) {
      const _createNew = document.getElementById('job-create-new-project')?.checked;
      const _selectedProjectId = document.getElementById('job-project-id')?.value?.trim();
      const _newProjectNameEl = document.getElementById('job-new-project-name');
      const _newProjectName = _newProjectNameEl?.value?.trim();
      if (_createNew) {
        if (!_newProjectName) {
          window.showToast?.('Project name is required to create a new project.', 'error');
          _newProjectNameEl?.focus();
          _bailOutOfSave(); // TASK-4614 COD: was leaving _saveJobSafetyTimer armed
          return;
        }
        // TASK-9420 — same name as an existing project: a soft warning, never a
        // block. HERE, before any write: on a NEW job createProject runs only
        // after addDoc + the day batch, so a warning there could cancel nothing.
        // Same shape as the job-duplicate gate below (release Save, clear the
        // in-flight flag and the 25s timer, re-fire on "create anyway").
        const _PM = window.ProjectsModule;
        if (_PM?.checkDuplicateProjectName && _PM?.showDuplicateProjectNameDialog) {
          const _dupCust = document.getElementById('job-customer')?.value || '';
          clearTimeout(_saveJobSafetyTimer); // the ≤2s wait for the list is not a hang
          let _dupMatches = [];
          try { _dupMatches = await _PM.checkDuplicateProjectName(_newProjectName, { customerId: _dupCust }); } catch (_) { _dupMatches = []; }
          // "Create new anyway" is remembered on the name box for THIS name,
          // customer, job and set of matches. Any new match (e.g. the project the
          // last save just made) changes the key, so the warning comes back.
          const _dupKey = [_PM.normalizeProjectName?.(_newProjectName) || _newProjectName.toLowerCase(), _dupCust,
            document.getElementById('job-id')?.value || '', _dupMatches.map(p => p.id).sort().join(',')].join('|');
          if (_dupMatches.length && _newProjectNameEl?.dataset?.dupNameOk !== _dupKey) {
            if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
            window.__saveJobInFlight = false;
            clearTimeout(_saveJobSafetyTimer);
            // "Use the existing project" only for an ACTIVE match of this job's
            // customer (the link guard requires the customer anyway).
            const _useProj = _dupMatches.find(p => p && p.status === 'active' && _projectHasCustomer(p, _dupCust)) || null;
            const _choice = await _PM.showDuplicateProjectNameDialog({
              name: _newProjectName, matches: _dupMatches, offerExisting: !!_useProj,
              existingLabel: 'Use the existing project', createLabel: 'Create new anyway', cancelLabel: 'Go back'
            });
            if (_choice === 'create') {
              if (_newProjectNameEl?.dataset) _newProjectNameEl.dataset.dupNameOk = _dupKey;
              _pendingAddDayAfterSave = _addDayAfterThisSave; // TASK-4877 — the re-fired save honours it
              document.getElementById('btn-save-job')?.click();
            } else if (_choice === 'existing' && _useProj) {
              // Back to the still-open job form with the project picked. No
              // navigation: opening the project would repaint under this modal.
              setJobNewProjectMode(false);
              const _sel = document.getElementById('job-project-id');
              if (_sel) _sel.value = _useProj.id;
              if (_sel && _sel.value === _useProj.id) onJobProjectChange(_sel);
              _sel?.focus();
              if (!_addDayAfterThisSave) window.showToast?.(`Switched to “${_useProj.name || 'the existing project'}”. Review and press Save.`, 'info');
              _addDayRequestNotHonored();
            } else {
              _newProjectNameEl?.focus();
              _addDayRequestNotHonored();
            }
            return;
          }
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
        }
      } else if (!_selectedProjectId) {
        window.showToast?.('Pick a project from the Project list, or set it to "No project".', 'error');
        document.getElementById('job-project-id')?.focus();
        _bailOutOfSave(); // TASK-4614 COD: was leaving _saveJobSafetyTimer armed
        return;
      } else {
        // TASK-9289 B2 — the project must name this job's customer, or the
        // job's tickets sum into a rollup that still reads single-customer.
        // Only when the link or the customer is CHANGING: an edit that touches
        // neither must not be blocked by a link that predates this check.
        const _custNow = document.getElementById('job-customer')?.value || '';
        const _isEditing = !!document.getElementById('job-id')?.value;
        const _linkChanging = !_isEditing || _selectedProjectId !== _editJobOriginalProjectId
          || (_custNow || null) !== (_editJobOriginalCustomerId || null);
        const _proj = (window.ProjectsModule?.getState?.()?.projects || []).find(p => p && p.id === _selectedProjectId);
        if (_linkChanging && _projectCustomerConflict(_proj, _custNow)) {
          // TASK-9599 — a project with NO customer yet (made from a customer-
          // less job) is not "for another customer": offer to add this one to
          // it, in the same save (changeJobProject writes both in one
          // transaction; firestore.rules reads the project with getAfter).
          // A project that names anyone else still blocks, unchanged.
          const _custName = getCustomerById(_custNow)?.name || '';
          if (_projectAdoptsCustomer(_proj, _custNow)) {
            const _q = `“${_proj?.name || 'This project'}” has no customer yet. Add ${_custName || 'this job’s customer'} to the project and save this job in it? Jobs already in the project stay in it and bill under this customer.`;
            clearTimeout(_saveJobSafetyTimer); // a person is reading — pause the hang detector
            let _yes = false;
            try {
              _yes = typeof window.showConfirm === 'function'
                ? !!(await window.showConfirm('Add customer to project?', _q, { confirmText: 'Add customer & save', cancelText: 'Cancel' }))
                : confirm(_q);
            } finally {
              _saveJobSafetyTimer = _armSaveJobSafetyTimer();
            }
            if (!_yes) {
              window.showToast?.(_projectCustomerConflictMessage(_proj, _custNow, _custName), 'info', null, 9000);
              document.getElementById('job-project-id')?.focus();
              _bailOutOfSave();
              return;
            }
            _projectAdoptCustomer = _custNow;
          } else {
            window.showToast?.(_projectCustomerConflictMessage(_proj, _custNow, _custName), 'error', null, 9000);
            document.getElementById('job-project-id')?.focus();
            _bailOutOfSave();
            return;
          }
        }
      }
    }
  }

  const allDay = document.getElementById('job-all-day').checked;
  const customerId = document.getElementById('job-customer').value;
  const customer = getCustomerById(customerId);

  // TASK-9645 C (COD fold 6) — the invoice lock, RE-CHECKED AT SAVE with a fresh
  // two-query read (jobId + originalJobId), so a day invoiced while this modal
  // sat open is still caught. Runs only when a LOCKED field (title / customer /
  // PO) actually changed — every other field, and clock/hours (Workforce), stay
  // editable. A read that throws FAILS CLOSED for that change. UI-only: the
  // jobs rules cannot see tickets, so an SDK write is not stopped (known gap).
  // COD Code Reviewer — snapshot the two typed locked fields ONCE: the lock is
  // judged on these values and jobData below writes these same values, so text
  // typed during a later await can never slip past the check.
  const _titleAtSave = document.getElementById('job-title').value;
  const _poAtSave = document.getElementById('job-po').value;
  if (jobId) {
    const _lockBase = (_editJobLockBaseline && _editJobLockBaseline.jobId === jobId) ? _editJobLockBaseline : null;
    const _lockChanged = _jobLockedFieldsChanged(_lockBase, {
      title: _titleAtSave,
      customerId: customerId || null,
      poNumber: _poAtSave
    }, normalizePoForSave);
    if (_lockChanged.length > 0) {
      const _lockRead = await _fetchLiveTicketsForJob(jobId, { fromServer: true });
      if (!_lockRead.ok) {
        window.showToast?.("Couldn't check whether this job is invoiced, so the title/customer/PO change was not saved. Check your connection and try again.", 'error', null, 9000);
        _bailOutOfSave();
        return;
      }
      const _lockNow = _jobInvoiceLockFromTickets(_lockRead.tickets, jobId);
      if (_lockNow.locked) {
        _applyEditJobInvoiceLock(_lockNow);
        window.showToast?.(_jobInvoiceLockMessage(_lockNow), 'error', null, 9000);
        _bailOutOfSave();
        return;
      }
    }
  }

  // TASK-388 defense-in-depth: when editing an EXISTING job, if the selected
  // customer differs from the job's stored customer, confirm before writing.
  // Catches ANY customer-crossing (the duplicate-pre-fill race, a mis-click, a
  // future regression) — not just the one bug we know about. _editJobOriginalCustomerId
  // is stashed when editJob() opens; null on new-job creates (jobId empty) so this
  // never fires for new jobs. Normalize null/'' so "no customer ↔ no customer"
  // doesn't trip it.
  if (jobId) {
    const origCust = _editJobOriginalCustomerId || null;
    const newCust = customerId || null;
    // Only gate when showConfirm is actually available. If it isn't (shouldn't
    // happen — app.js exposes it globally), DON'T silently abort the save:
    // `await undefined` would resolve falsy and swallow a legitimate save. The
    // token guard above is the real protection; this confirm is belt-and-suspenders.
    if (origCust !== newCust && typeof window.showConfirm === 'function') {
      const oldName = getCustomerById(origCust)?.name || (origCust ? 'another customer' : 'no customer');
      const newName = customer?.name || (newCust ? 'another customer' : 'no customer');
      const ok = await window.showConfirm(
        'Change customer?',
        `Move this job from ${oldName} to ${newName}?`,
        { confirmText: 'Yes, move it', cancelText: 'Cancel' }
      );
      if (!ok) {
        if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
        window.__saveJobInFlight = false;
        clearTimeout(_saveJobSafetyTimer);
        _addDayRequestNotHonored(); // TASK-4877
        return;
      }
    }
  }

  // TASK-9862 — HARD STOP: a day of this job after a linked locate ticket's
  // good-through date (the job's own tickets and its project's). Renew, cancel,
  // or override with a reason (jobAuditLog; a new job's row is written once it
  // has an id).
  {
    const _p811Proj = document.getElementById('job-add-to-project')?.checked && !document.getElementById('job-create-new-project')?.checked
      ? (document.getElementById('job-project-id')?.value || '') : '';
    const _p811Days = [...new Set([..._p811JobFormDayKeys(), ..._p811RangeKeys(startDateVal, endDateVal)])];
    const _p811Stops = _p811StopsFor(_permitIdsForSave(), _p811Proj, _p811Days);
    _p811PendingOverride = null;
    if (_p811Stops.length && !(await _p811HardStopGate(_p811Stops, { jobId, jobLabel: document.getElementById('job-title')?.value || '', source: 'job-form' }))) {
      _bailOutOfSave();
      return;
    }
    // TASK-10184 — approved time off for crew on this form. On an edit a
    // person counts when they are NEW to the crew (vs DispatchState.
    // jobCrewSnapshot, the TASK-1672 on-open snapshot) on any of the job's
    // days, or already on the crew but the form ADDS a day (the range now
    // reaches past the job's existing days) that they are off (COD r1).
    const _offCrew = _buildJobCrewPayload();
    const _offNow = [...(_offCrew.drivers || []), ...(_offCrew.helpers || [])];
    const _offBefore = jobId ? (DispatchState.jobCrewSnapshot || {}) : {};
    const _offPrev = [...(_offBefore.drivers || []), ...(_offBefore.helpers || [])].map(String);
    const _offOldDays = new Set(jobId ? _p811JobFormDayKeys() : []);
    const _offNewDays = _p811Days.filter(k => !_offOldDays.has(k));
    const _offList = [
      ...timeOffConflictsFor(_offNow, _p811Days, _offPrev),
      ...timeOffConflictsFor(_offNow.filter(id => _offPrev.includes(String(id))), _offNewDays),
    ];
    if (!(await confirmTimeOffConflictList(_offList))) {
      _bailOutOfSave();
      return;
    }
  }

  // Wait for any in-flight address geocoding so plain-text addresses don't save
  // with lat/lng = null. Toast lets the dispatcher know we're holding briefly.
  if (_pendingGeocodes.size > 0) {
    window.showToast?.('Resolving addresses…', 'info');
    const geoWait = await awaitPendingGeocodes();
    // TASK-1662 — say it out loud instead of swallowing it. Not user-facing:
    // the save-time backstop below fills whatever is still missing and names
    // anything it can't. This is the breadcrumb for when it doesn't.
    if (geoWait && geoWait.timedOut) {
      console.warn('[Dispatch] Address lookups did not all finish before save — falling through to the save-time geocode pass.');
    }
  }
  const waypoints = collectWaypoints();

  // TASK-359 — guard against silent-drop of empty waypoint rows. If dispatcher
  // clicked "Add Waypoint" but didn't fill name+url (or used an autocomplete
  // chip that only populated dataset.lat/lng), collectWaypoints() filters those
  // rows out. Detect the mismatch and block save with a visible warning.
  const rowCount = document.querySelectorAll('.waypoint-row').length;
  if (rowCount > waypoints.length) {
    const dropped = rowCount - waypoints.length;
    // Mark incomplete rows so the dispatcher sees what's wrong
    document.querySelectorAll('.waypoint-row').forEach(row => {
      const hasName = row.querySelector('.waypoint-name')?.value.trim();
      const hasUrl  = row.querySelector('.waypoint-url')?.value.trim();
      row.classList.toggle('waypoint-incomplete', !hasName && !hasUrl);
    });
    const msg = `${dropped} destination row${dropped > 1 ? 's are' : ' is'} empty — `
              + `fill in the location or click the red X to remove `
              + `${dropped > 1 ? 'them' : 'it'} before saving.`;
    window.showToast?.(msg, 'error');
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
    window.__saveJobInFlight = false;
    clearTimeout(_saveJobSafetyTimer);
    _addDayRequestNotHonored(); // TASK-4877
    return;
  }

  // TASK-1617 — fill in any stop that still has an address but no usable
  // coordinate. Runs AFTER the empty-row guard above so an aborted save never
  // burns geocoding quota. Bounded and failure-tolerant: this can slow a save
  // by up to a few seconds but it can never fail one.
  let _geocodeWarning = null;
  // TASK-9563 — a PIN driver's save is geocoded by driverSaveJob behind the
  // driver session (the anonymous-OK geocodeAddress is never called from the
  // driver form); its unpinned stops come back in the callable's reply.
  if (window.AppState?.driverModePin !== true) try {
    const geo = await geocodeMissingWaypoints(waypoints);
    // Say it out loud, and say it AFTER the save succeeds. A stop that saved
    // without a location is invisible on the map and contributes nothing to
    // route mileage — the dispatcher has to know which one so they can paste
    // a pin. Silently dropping it is how this became an 8-row incident.
    const _list = arr => {
      const names = arr.slice(0, 3).join(', ');
      return names + (arr.length > 3 ? ` +${arr.length - 3} more` : '');
    };
    const _parts = [];
    // 🔴 TASK-9194 — THIS SENTENCE FIRST, AND IT DOES NOT BLAME THE LINK.
    // These stops failed because we never got an answer back in time. Chris was
    // about to re-copy a URL that was already correct because the old copy told
    // him to. The stop still saved without a pin — say that plainly, and say
    // what actually fixes it (open the job and tap the retry arrow), not what
    // cannot (re-pasting a good link).
    if (geo.unreachable.length > 0) {
      const many = geo.unreachable.length > 1;
      _parts.push(
        `Couldn't reach the map service for ${_list(geo.unreachable)} — the link looks fine, we just didn't get an answer. `
        + `${many ? 'Those destinations' : 'That destination'} saved without a pin; reopen the job and tap the ⟳ on the row to try again.`
      );
    }
    if (geo.unresolved.length > 0) {
      // TASK-4805 — "add a map link" was the wrong advice for the case that
      // actually dominates: the dispatcher DID add a map link, it was a share
      // link, and it never resolved. Say something true of both shapes.
      _parts.push(`Couldn't pin ${_list(geo.unresolved)} — open ${geo.unresolved.length > 1 ? 'them' : 'it'} in Maps and paste the full URL, or type the address.`);
    }
    if (_parts.length) _geocodeWarning = _parts.join(' ');
  } catch (e) {
    // Belt and braces: never let coordinate enrichment break a job save.
    console.warn('[Dispatch] Save-time geocoding skipped:', e?.message || e);
  }

  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const jobData = {
    title: _clamp(_titleAtSave, 100), // TASK-9645 C — the value the invoice lock judged
    type: document.getElementById('job-type').value || 'job',
    // TASK-9390 — the label at save time, so a surface without the tenant list
    // (the server ticket-move twin) never has to print a raw custom slug.
    typeLabel: _jobTypeLabel(document.getElementById('job-type').value || 'job'),
    location: _clamp(document.getElementById('job-location').value, 200),
    customerId: customerId || null,
    customer: customer?.name || '', // Keep for backwards compat
    waypoints: waypoints,
    // TASK-4782 — trim, and nothing else. This used to store `.value` raw, which
    // is why 18 live documents carry an invisible leading/trailing space and sit
    // in a different PO "group" than the identical PO next to them. Trim is the
    // ONLY transform we apply: no case change, no "PO #" prefix strip, no
    // truncation to QuickBooks' 15 — those are judgement calls about what a
    // customer's AP department has to match, and they belong to a human.
    // When this changes a stored value, the JOB_UPDATE audit row below records
    // old AND new ([[lesson_workweek_silently_flipped_sun_sat]]).
    poNumber: normalizePoForSave(_poAtSave), // TASK-9645 C — the value the invoice lock judged
    poVisible: document.getElementById('job-po-visible').checked,
    // TASK-4834 — trim only, same as the PO (normalizePoForSave). Never case.
    vendorNumber: normalizePoForSave(document.getElementById('job-vendor-number').value),
    afeNumber: document.getElementById('job-afe').value,
    photosVisible: document.getElementById('job-photos-visible').checked,
    wellName: document.getElementById('job-well-name').value,
    wellNumber: '',
    // TASK-9648 — every linked 811 ticket; permitId mirrors the first so older
    // readers keep working. See _permitIdsForSave / _jobPermitLinkFields.
    ..._jobPermitLinkFields(_permitIdsForSave()),
    permitSettings: {
      aiParseForBilling: document.getElementById('permit-ai-parse')?.checked || false,
      usedFastTrax: document.getElementById('permit-fasttrax')?.checked || false
    },
    notes: document.getElementById('job-notes').value,
    interstate: document.getElementById('job-interstate').checked,
    hazmat: document.getElementById('job-hazmat').checked,
    liquidTank: document.getElementById('job-liquid-tank').checked,
    // TASK-1221 — per-job ticketing mode. Default 'combined' = today's behavior.
    // Forward-only: a mid-day change is locked by _applyJobSplitLock, so this
    // write only takes effect for FUTURE clock-ins (existing tickets untouched).
    splitBy: document.getElementById('job-split-by')?.value || 'combined',
    // TASK-4610 — always a real Timestamp. A string here is invisible to the
    // board's range queries: the job exists, nothing throws, the driver never
    // sees it.
    startDate: startTs,
    endDate: endTs,
    allDay: allDay,
    defaultStart: allDay ? null : document.getElementById('job-start-time').value,
    defaultEnd: allDay ? null : (document.getElementById('job-end-time').value || null), // TASK-9737: an empty end time is no end time
    poc: {
      name: document.getElementById('job-poc-name').value,
      role: document.getElementById('job-poc-role').value,
      phone: document.getElementById('job-poc-phone').value,
      phone2: document.getElementById('job-poc-phone2').value,
      email: document.getElementById('job-poc-email').value
    },
    projectId: _jobFormProjectIdForSave(),
    projectDocs: _jobProjDocsForSave(_jobProjDocsSel, _jobFormProjectIdForSave()), // TASK-9650
    // TASK-191 Phase 2 — per-job geofence config (clock-in/out location flag).
    geofence: collectJobGeofence(waypoints),
    status: 'active',
    updatedAt: Timestamp.now()
  };

  // TASK-9370 — a driver-created job's DOT flags. Still unconfirmed: DROP the
  // three keys so this save leaves them null (the unticked boxes mean "nobody
  // looked", not "no"). Confirmed (a box ticked, or "None apply — confirm"):
  // write them as the dispatcher set them and retire the review flag.
  if (jobId && _jobFormDotUnconfirmed) {
    delete jobData.interstate; delete jobData.hazmat; delete jobData.liquidTank;
  } else if (jobId && _jobFormDotReviewPending) {
    jobData.needsDispatchReview = false;
    jobData.dotFlagsConfirmedBy = window.AppState?.user?.email || null;
    jobData.dotFlagsConfirmedAt = Timestamp.now();
  }

  // TASK-9811 S1 — extra POs: cleaned against the main PO being saved; an extra a
  // ticket of this job bills to cannot be dropped (fresh server read, fail closed).
  {
    const _xp = await _jobExtraPosForSave(jobId, jobData.poNumber);
    if (!_xp.ok) { window.showToast?.(_xp.message, 'error', null, 9000); _bailOutOfSave(); return; }
    if (_xp.write) jobData.extraPoNumbers = _xp.list;
  }

  // TASK-4649 — permitRequired tri-state. Written ONLY while permit tracking is
  // on and the checkbox rendered: with the feature off (or the element missing)
  // the key is omitted entirely, so saves never clobber a stored decision and
  // the 675+ legacy jobs keep "absent = nobody decided". With the feature on,
  // the checkbox state IS the decision (checked=true / unchecked=false); the
  // AR/AP flag in billing.js fires on === true only. Source distinguishes a
  // human tick from an unconfirmed heuristic pre-tick (S5) so downstream can
  // tell a compliance declaration from a suggestion.
  if (isPermitTrackingEnabled()) {
    const _prEl = document.getElementById('job-permit-required');
    if (_prEl) {
      jobData.permitRequired = _prEl.checked === true;
      jobData.permitRequiredSource = (_prEl.checked && _permitAutoState.suggested && !_permitAutoState.userTouched)
        ? 'auto-suggested' : 'manual';
    }
  }

  // ── TASK-9370 PIN BRANCH ──────────────────────────────────────────────────
  // A driver-PIN session never writes Firestore from this form. Everything
  // below this line — the flag gate, jobs-update, propagateJobTitle, the jobDay
  // date-sync, crew write-through, the audit addDoc, the create batch — is a
  // write a PIN session either cannot make (rules deny it) or must not make
  // (the audit row would be denied and swallowed, COD Adversary 🔴). The
  // five-section payload goes to the driverSaveJob callable, which owns the
  // allowlist, the jobDay sync, the refusals and the server-stamped audit row.
  if (window.AppState?.driverModePin === true) { // TASK-9370 PIN BRANCH — tripwire anchor, keep on this line
    await _driverSaveJobViaCallable({
      jobId: jobId || null, jobData, startDateVal, endDateVal,
      pauseHang: () => clearTimeout(_saveJobSafetyTimer), // TASK-10106 S5 — files upload after the save
      done: (ok) => {
        clearTimeout(_saveJobSafetyTimer);
        window.__saveJobInFlight = false;
        if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
        if (ok) closeModal('job');
        // TASK-9703 — Add Another Day on a form with unsaved edits: the save
        // went first; the day is added only when it succeeded (office order).
        if (ok && _addDayAfterThisSave && jobId === _addDayAfterThisSave) _driverAddDayPrompt(_addDayAfterThisSave);
        else if (!ok) _addDayRequestNotHonored();
      },
    });
    return;
  }

  // TASK-9227 — ONE compliance-flag gate for every saveJob branch that can seat
  // crew: the create batch (`drivers: selectedDrivers`), the orphaned-job jobDays
  // backfill, and the Edit-Job write-through. It sits HERE, on the last line
  // before the first write, because (a) all three branches read the same two DOM
  // containers so one check covers them and (b) asking about a flagged driver
  // and THEN failing validation on a missing title would be the warning landing
  // in the wrong place. `_jobCrewForCreate` is the same reader the create branch
  // uses — it returns the DOM selection unless a duplicate-save carry is pending,
  // in which case it returns the carry, which is exactly the set about to be
  // written. Helpers included: nothing else in this file checks them.
  const _dfJobCrewRefs = [
    ..._jobCrewForCreate('people', 'drivers'),
    ..._jobCrewForCreate('people', 'helpers'),
  ];
  // 🔴 PAUSE THE 25s HANG DETECTOR ACROSS THE DIALOG (COD Code Reviewer +
  // Independent, 2026-09-22). `_armSaveJobSafetyTimer` clears
  // `__saveJobInFlight` and RE-ENABLES Save when it fires; a dispatcher reading
  // "who flagged this and how long ago" can easily take longer than 25s, and the
  // timer firing under the modal toasts a false "Save is taking too long", frees
  // the lock for a concurrent second save, and then lets "Assign Anyway" write
  // with Save already live. Identical pause/rearm to the ticketed-day confirm
  // below and the jobDay-delete confirm — `__saveJobInFlight` stays SET, so Save
  // is still blocked while the human reads.
  clearTimeout(_saveJobSafetyTimer);
  const _dfJobGate = await _dfConfirmSeating(_dfJobCrewRefs, {
    surface: jobId ? 'job-form-edit' : 'job-form-create',
    jobId: jobId || null,
  });
  _saveJobSafetyTimer = _armSaveJobSafetyTimer();
  if (!_dfJobGate.ok) {
    // Declined. `_bailOutOfSave` is the one helper every validation bail-out
    // uses — button, in-flight lock, safety timer and the pending add-day —
    // rather than a fourth hand-rolled copy that drifts from it.
    window.showToast?.('Nothing was saved. Clear the flag in Compliance, or pick a different person.', 'info');
    _bailOutOfSave();
    return;
  }
  // What the dialog actually showed, so the write-time re-check below can tell a
  // person the dispatcher was warned about from one ticked after they answered.
  const _dfVettedIds = new Set((_dfJobGate.entries || []).map(e => e.personId));

  // TASK-620: names the actual write step in flight so a permission-denied (or
  // any) save failure logs WHICH write failed instead of phase:'unknown'. Set
  // BEFORE each await write below; surfaced in the catch's logged context.
  let phase = 'unknown';
  // TASK-9587 — a Claude request claimed for this save and not yet written.
  // A throw between the claim and the job write hands it back (catch below),
  // so a retry is not told "being saved in another window" for 2 minutes.
  let _cdHeldClaim = null;
  // TASK-9289 r5 — true only between an edit's project step landing (it has to
  // go BEFORE the job update) and that update landing, so a failed update is
  // reported as what it is: the project/customer change saved, the rest not.
  let _projectStepLanded = false;
  // TASK-9289 COD r1 — projectId is NOT written by the job doc write on either
  // branch. Every change to it goes through ProjectsModule.changeJobProject: one
  // path that confirms before moving invoiced tickets between project rollups
  // (billing.js _billingProjectRollup keys on job.projectId), applies the
  // customer check, and refuses — in a transaction — a link someone else changed
  // since this form opened. Captured here, before any await, from the same read
  // saveJob always used.
  const _projectTarget = jobData.projectId || null;
  delete jobData.projectId;
  const _projectExpected = jobId ? (_editJobOriginalProjectId || null) : null;
  const _projectCreateNew = !!(document.getElementById('job-add-to-project')?.checked
    && document.getElementById('job-create-new-project')?.checked);
  // TASK-9464 — the stops the job had BEFORE this edit, read off the same
  // pre-update snapshot below. The edit branch's waypoints-create phase diffs
  // against it so an unchanged re-save writes zero `waypoints` rows.
  let _preEditWaypoints = [];
  try {
    if (jobId) {
      // Check if title changed — propagate to clock entries + draft/ready tickets
      phase = 'jobs-read';
      const oldJobSnap = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId));
      _preEditWaypoints = oldJobSnap.exists() && Array.isArray(oldJobSnap.data().waypoints)
        ? oldJobSnap.data().waypoints : [];
      const oldTitle = oldJobSnap.exists() ? oldJobSnap.data().title : null;
      const newTitle = jobData.title;

      // TASK-4793 B — a date edit may not push a ticketed or clocked-in day out
      // of the job's dates. The server answers (removeJobDay mode check-range,
      // same predicate as Remove Day) BEFORE anything below writes, so a refusal
      // leaves the job exactly as it was — never a saved range with a blocked
      // day stranded outside it. A failed check saves nothing (fail closed).
      if (!isDemoMode() && DispatchState.tenantId !== 'demo' && oldJobSnap.exists()) {
        const _pre = await _dateSyncPrecheck(jobId, oldJobSnap.data() || {}, startDate, endDate);
        if (!_pre.ok) {
          window.showToast?.(_pre.message, 'error', null, 15000);
          _bailOutOfSave();
          return;
        }
      }

      // TASK-9289 COD r1 — the job's projectId AFTER this save, for the audit
      // row. Starts as what was stored; only a successful gated change moves it.
      let _projectFinal = oldJobSnap.exists() ? (oldJobSnap.data().projectId || null) : null;

      // TASK-9289 r4 (COD r3) — firestore.rules checks the (customer, project)
      // PAIR whenever either changes, so a customer change on a job that is in
      // a project cannot ride the plain update below: the rule would judge the
      // new customer against the OLD project. It goes through the gate FIRST,
      // carrying the new customer, in ONE write.
      // r5 (COD r4) — "create a new project instead" goes first too, as ONE
      // move (old project → new one): r4 unlinked here and linked after the
      // update, so a declined second invoiced-ticket prompt (or a failed create)
      // left the job on NO project while the toast said "left as it was". Now
      // _saveJobProjectBeforeUpdate asks once, creates, moves, and rolls the new
      // project back if the move does not go through. Either the customer +
      // project change lands, or NOTHING is saved and the toast says so.
      // It also hands back the PO the move inherited (poApplied), already
      // folded into jobData — the update below would otherwise write the
      // form's blank PO straight over it.
      const _storedProjectId = _projectFinal;
      const _custChanging = oldJobSnap.exists()
        && (jobData.customerId || null) !== (oldJobSnap.data().customerId || null);
      let _projectHandledEarly = false;
      if (_projectCreateNew || (_custChanging && _storedProjectId)) {
        phase = _projectCreateNew ? 'jobs-create-project' : 'jobs-project-customer';
        clearTimeout(_saveJobSafetyTimer);
        let _early;
        try {
          _early = await _saveJobProjectBeforeUpdate({
            jobId, jobData,
            storedProjectId: _storedProjectId,
            custChanging: _custChanging,
            target: _projectTarget,
            expected: _projectExpected,
            newProject: _projectCreateNew ? _readInlineNewProjectForEdit(jobData) : null,
            adoptCustomer: _projectCreateNew ? '' : _projectAdoptCustomer,
          });
        } finally {
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
        }
        if (!_early || _early.ok !== true) {
          window.showToast?.(_early?.message || 'Nothing was saved — the project change did not go through.', _early?.level || 'error', null, 10000);
          _bailOutOfSave();
          return;
        }
        _projectHandledEarly = true;
        _projectStepLanded = true;
        _projectFinal = _early.projectId;
      }

      // Update existing job
      phase = 'jobs-update';
      // TASK-9516 — the editor stamp behind the optional "by <name>" in the
      // Dispatch Admin text. Spread into the WRITE only, never into jobData
      // (reused below for audit/propagation). firestore.rules jobUpdatedByOk
      // pins updatedBy to the caller whenever updatedByAt moves; the server
      // resolves the name from the uid, so no name is written here.
      const _editorUid = auth.currentUser?.uid || null;
      await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId),
        _editorUid ? { ...jobData, updatedBy: _editorUid, updatedByAt: serverTimestamp() } : jobData);
      _projectStepLanded = false;

      // TASK-9174 — THE ONE PLACE ROAD GEOMETRY IS RESOLVED: a save, not a map
      // repaint. Fire-and-forget on purpose. It is a second write, it talks to a
      // billed API, and a dispatcher who just saved a job must not wait on it or
      // be shown its failure — the map falls back to a visibly-different line
      // when the cache is absent, which is the honest outcome either way.
      // The prior cache comes from the snapshot we already read above, so an
      // edit that did not move any stop costs ZERO Directions calls.
      kickJobRouteCache(jobId, waypoints, oldJobSnap.exists() ? oldJobSnap.data().routeCache : null);

      if (!_projectHandledEarly && _projectTarget !== _projectExpected) {
        // TASK-9289 COD r1 — untick-to-unlink AND switching to another project
        // both land here, through the gate, AFTER the update (so the gate's
        // fill-if-blank PO reads the PO this save just wrote). The dialog it may
        // raise pauses the 25s hang detector exactly like the other in-save
        // confirms. A decline here is honestly "job saved, project left as it was".
        phase = 'jobs-project-change';
        clearTimeout(_saveJobSafetyTimer);
        let _pcRes;
        try {
          _pcRes = await _saveJobChangeProject(jobId, _projectTarget, _projectExpected, 'job-edit',
            _projectAdoptCustomer ? { adoptCustomer: true, adoptFor: _projectAdoptCustomer } : {});
        } finally {
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
        }
        if (_pcRes && _pcRes.projectId !== undefined) _projectFinal = _pcRes.projectId;
      }

      // Audit: job update (always log, especially date changes)
      try {
        const auditData = {
          action: 'JOB_UPDATE', source: 'dispatch-form', timestamp: Timestamp.now(),
          user: window.AppState?.user?.email || 'unknown',
          jobId, jobTitle: jobData.title,
          customerId: jobData.customerId, customerName: jobData.customer,
          newStartDate: startDateVal, newEndDate: endDateVal,
          // Track waypoint/permit shape so we can diagnose missing-data reports
          newWaypointCount: waypoints.length,
          newWaypointsWithCoords: waypoints.filter(w => w.lat != null && w.lng != null).length,
          newPermitCount: ((jobData.permits && jobData.permits.length) || (oldJobSnap.exists() ? (oldJobSnap.data().permits?.length || 0) : 0))
        };
        if (oldJobSnap.exists()) {
          const od = oldJobSnap.data();
          const oldS = od.startDate?.toDate?.();
          const oldE = od.endDate?.toDate?.();
          auditData.oldStartDate = oldS ? oldS.toISOString().slice(0, 10) : null;
          auditData.oldEndDate = oldE ? oldE.toISOString().slice(0, 10) : null;
          auditData.dateChanged = (auditData.oldStartDate !== startDateVal || auditData.oldEndDate !== endDateVal);
          auditData.titleChanged = (od.title !== jobData.title);
          // TASK-388: record the PRIOR customer so a future swap incident shows
          // the exact before→after at a glance (the Loenbro→KS Industries bug
          // was invisible in the audit because only the NEW customer was logged).
          auditData.oldCustomerId = od.customerId || null;
          auditData.oldCustomerName = od.customer || '';
          auditData.customerChanged = ((od.customerId || null) !== (jobData.customerId || null));
          // TASK-4782 — the PO decides which authorization the customer's AP desk
          // pays this against, so it never changes without a row showing old AND
          // new ([[lesson_workweek_silently_flipped_sun_sat]]). This also catches
          // the one automatic change on the path: a stored PO with stray
          // whitespace gets trimmed by normalizePoForSave when the job is next
          // saved, and that must be visible in the record rather than silent.
          auditData.oldPoNumber = od.poNumber || '';
          auditData.newPoNumber = jobData.poNumber || '';
          auditData.poChanged = ((od.poNumber || '') !== (jobData.poNumber || ''));
          // Whitespace-only means WE changed it, not the dispatcher — worth
          // being able to tell apart when reading the log back.
          // COD Code Reviewer — this must use the SAME normalizer the save used.
          // `.trim()` does not strip U+200B / NBSP, which normalizePoForSave does,
          // so a zero-width-space cleanup would have been logged as a dispatcher
          // edit. The classifier and the transform have to agree.
          auditData.poWhitespaceOnly = auditData.poChanged &&
            normalizePoForSave(od.poNumber) === normalizePoForSave(jobData.poNumber);
          // TASK-9811 S1 — the extra POs, old AND new, whenever this save wrote them.
          if (Object.prototype.hasOwnProperty.call(jobData, 'extraPoNumbers')) {
            auditData.oldExtraPoNumbers = Array.isArray(od.extraPoNumbers) ? od.extraPoNumbers : [];
            auditData.newExtraPoNumbers = jobData.extraPoNumbers;
            auditData.extraPosChanged = JSON.stringify(auditData.oldExtraPoNumbers) !== JSON.stringify(jobData.extraPoNumbers);
          }
          const oldWps = Array.isArray(od.waypoints) ? od.waypoints : [];
          auditData.oldWaypointCount = oldWps.length;
          auditData.oldWaypointsWithCoords = oldWps.filter(w => w && w.lat != null && w.lng != null).length;
          auditData.oldPermitCount = Array.isArray(od.permits) ? od.permits.length : 0;
          // TASK-9289 S3 — job.notes is ONE string shared by every day of the job
          // with no history, and this row never captured it: a note overwritten
          // from day 5 was unrecoverable. Old AND new, capped so one huge note
          // cannot push the row past Firestore's doc limit.
          const _notesDiff = _jobNotesAuditDiff(od.notes, jobData.notes);
          auditData.notesChanged = _notesDiff.changed;
          if (_notesDiff.changed) { auditData.oldNotes = _notesDiff.oldNotes; auditData.newNotes = _notesDiff.newNotes; }
          // TASK-9289 — a project link/unlink moves the job's tickets between rollups.
          auditData.oldProjectId = od.projectId || null;
          // TASK-9289 COD r1 — the FINAL projectId (after create-new-project or a
          // gated change), not the form's intent, which is no longer on jobData.
          auditData.newProjectId = _projectFinal;
          auditData.projectChanged = auditData.oldProjectId !== auditData.newProjectId;
          // Red flag: previous save had geocoded coords on a waypoint, this save lost them.
          auditData.coordsLost = auditData.oldWaypointsWithCoords > auditData.newWaypointsWithCoords;
          auditData.waypointsCleared = auditData.oldWaypointCount > 0 && auditData.newWaypointCount === 0;
          // TASK-398: one scannable list of which STRUCTURAL fields changed, so
          // support reads `changedFields` instead of N separate booleans. Mirrors
          // the existing diff flags above — no free-text/PII fields included.
          auditData.changedFields = [
            auditData.customerChanged && 'customer',
            auditData.dateChanged && 'date',
            auditData.titleChanged && 'title',
            (auditData.oldWaypointCount !== auditData.newWaypointCount) && 'waypoints',
            auditData.poChanged && 'po',
            auditData.extraPosChanged && 'extraPos', // TASK-9811 S1
            auditData.notesChanged && 'notes',     // TASK-9289 S3
            auditData.projectChanged && 'project', // TASK-9289
            jobData.needsDispatchReview === false && 'dotFlagsConfirmed', // TASK-9370
          ].filter(Boolean);
        }
        await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), auditData);
      } catch (ae) { console.warn('[Dispatch] Job audit log failed:', ae); }

      // TASK-1672 — write crew/equipment back to the job's jobDays ONLY when the
      // crew section actually changed. The unconditional write-through that used
      // to live here flattened per-day crews (set via Add Day) on every unrelated
      // edit — PO, notes, location, dates. `_jobCrewNow` is built by the same
      // helper that took the on-open snapshot in editJob, so the two sides
      // normalize identically.
      const _jobCrewNow = _buildJobCrewPayload();
      const _jobCrewBefore = DispatchState.jobCrewSnapshot;
      const _crewDirty = _jobCrewDirty(_jobCrewBefore, _jobCrewNow);
      const _ratesDirty = _jobRatesDirty(_jobCrewBefore, _jobCrewNow);
      const selectedDrivers = _jobCrewNow.drivers;
      const selectedHelpers = _jobCrewNow.helpers;
      const selectedTrucks = _jobCrewNow.trucks;
      const selectedTrailers = _jobCrewNow.trailers;
      const selectedAssets = _jobCrewNow.assets;
      const assetQtys = _jobCrewNow.assetQtys;
      // COD (c): assetDirections was seeded into the form at edit-open but never
      // written back, so a deploy/return flip made here was silently discarded.
      // NOTE: this path does NOT run processRentalTransaction — the same is
      // already true of `assets`/`assetQtys` here, so rental inventory
      // reconciliation on the Edit Job path stays a known pre-existing gap
      // (saveAssignment is the path that reconciles). Not widened by this fix.
      const assetDirections = _jobCrewNow.assetDirections;
      // TASK-4810 — ...and that gap is how Split Rock's Rubber Mat reached 3,765
      // out of 1,597: a job created with 4081 was corrected to 472 HERE, and the
      // counter kept 4081. Moving stock from this form is a ledger-design change
      // (TASK-9336) and out of scope; until then, never let it pass silently.
      // TASK-10015 — a LEDGER job's rental edits here now move stock (settled
      // in the day write below); only an older job with no ledger still warns.
      const _editJobLedger = oldJobSnap.exists() && _jobHasRentalLedger(oldJobSnap.data());
      if (_crewDirty && !_editJobLedger) {
        const _rentalNoMove = rentalChangesNotMovingStock(_jobCrewBefore, _jobCrewNow, DispatchState.assets);
        if (_rentalNoMove.length > 0) {
          console.warn('[Rental] TASK-4810 Edit Job rental change did not move stock', { jobId, changes: _rentalNoMove });
          window.showToast?.(
            `Rental out-counts not updated by this edit (${_rentalNoMove.map(c => c.name).join(', ')}). Move equipment with Assign Crew on the day, or recount in Roster › Assets.`,
            'warning', null, 12000);
        }
      }
      const hasAssignments = selectedDrivers.length > 0 || selectedTrucks.length > 0;
      // TASK-9227 — this is the set the EDIT path actually writes, re-read after
      // the flag dialog resolved. Anyone flagged in here who was not in the
      // dialog was ticked while the save was already in flight.
      _dfNoticeUnvetted([...selectedDrivers, ...selectedHelpers], _dfVettedIds,
        { surface: 'job-form-edit', jobId });

      phase = 'jobDays-read';
      const _ftsRestamped = new Set(); // TASK-10190 — days whose sig stamp this save already rewrote
      const existingJobDays = await getDocs(
        query(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), where('jobId', '==', jobId))
      );
      if (existingJobDays.docs.length === 0 && startDate && endDate) {
        // Orphaned job (batch failed on creation or legacy import) — create jobDays now.
        // TASK-9392 COD r2: ALL missing days in ONE batch. The old sequential
        // addDoc loop could fail after day 1, leaving a partial schedule; the
        // retry then saw "has days" and skipped creation, and the customer-
        // request link approved an incomplete schedule. Now all land or none do.
        phase = 'jobDays-create';
        const _orphanRows = _orphanJobDayRows(jobId, startDate, endDate, {
          status: hasAssignments ? 'assigned' : 'scheduled',
          drivers: selectedDrivers, helpers: selectedHelpers,
          trucks: selectedTrucks, trailers: selectedTrailers,
          assets: selectedAssets, assetQtys, assetDirections,
          lineItemSelections: _jobCrewNow.lineItemSelections,
          selectedContractId: _jobCrewNow.selectedContractId
        });
        await _createMissingJobDaysAtomically(_orphanRows);
        console.log(`[saveJob] Created ${_orphanRows.length} missing jobDays for orphaned job:`, jobId);
      } else if (_crewDirty || _ratesDirty) {
        phase = 'jobDays-update';
        // TASK-4614 — crew/equipment/rate edits made in Edit Job land on the ONE
        // day the form was opened from, never on every day of the job.
        //
        // The loop this replaces walked EVERY jobDay and wrote the form's crew to
        // each divergent one, so a single save on the Aug 26 card rewrote five
        // earlier days — three archived tickets, one ready, one draft (Apollo to
        // Pal bore #4, 2026-08-25 22:19:00-01, six days, 6 drivers/4 trucks down
        // to 2 drivers/1 truck). TASK-1672's dirty check only skipped days that
        // already MATCHED, so the days that legitimately diverged — the per-day
        // crews Add Day and the assign modal exist to create — were exactly the
        // ones it flattened. Crew is per-day by design: each jobDay owns its own
        // arrays, buildInheritedJobDay COPIES rather than shares, and
        // saveAssignment writes a single day. This form was the only writer that
        // ignored the model.
        //
        // Rates ride the same scope. jobDay.lineItemSelections drives billing,
        // and a day-wide rate rewrite is the same flatten one field over on a
        // money surface (the reason _planJobDayCrewUpdate already refuses to
        // write rates onto a day someone customized).
        const _scopeDocs = existingJobDays.docs.filter(d => d.id === _crewScopeDayId);
        if (_scopeDocs.length === 0) {
          // No day in hand — REFUSE rather than guess. Reachable when the job's
          // days are all outside the 90-day board window and not in the search
          // cache, or when the seeded day was deleted while the form sat open.
          // The job's own fields are already saved; only the crew half is
          // dropped, and the dispatcher is told so and where to go instead.
          if (_crewDirty || _ratesDirty) {
            window.showToast?.(
              'Job details saved. The crew change was NOT applied — open that day on the board and use Assign Crew.',
              'warning'
            );
            console.warn('[saveJob] TASK-4614: crew edit had no jobDay in scope; crew not written', {
              jobId, editJobDayId: _crewScopeDayId, dayCount: existingJobDays.docs.length
            });
          }
        } else {
          // TASK-4614 SLICE B — the day is in scope, but is it already ticketed?
          // Slice A made the write land on ONE day; that day can still be a past
          // day whose work is already priced. Nothing here is silent: the write
          // either proceeds, is confirmed, or is refused out loud.
          const _tGuard = await _checkTicketedDayCrewGuard(jobId, _scopeDocs[0].data());
          let _crewWriteOk = _tGuard.allow === true;
          if (!_crewWriteOk && _tGuard.needsConfirm) {
            // Draft / ready ticket: the legitimate "fix yesterday's crew so the
            // ticket bills right" flow lives here, so it is a confirmation and
            // not a wall — but it names the day and says what does NOT follow the
            // crew, because the ticket's totals are frozen (verified on
            // srIDSdNHAOH58LeOkTi6 / OvqukRdZ3pPd5c42E7KP: totalHours and
            // derivedLinesReconciledAt do not recompute from the jobDay).
            // `?.` returning undefined leaves _crewWriteOk false — fail closed.
            //
            // Pause the 25s hang detector while a human reads this, exactly as
            // the jobDay-delete confirm at :8785 does. Leaving it armed under a
            // confirm toasts a false "Save is taking too long", re-enables Save
            // mid-save and invites a concurrent second save (TASK-4567).
            // `__saveJobInFlight` stays set, so Save is still blocked meanwhile.
            clearTimeout(_saveJobSafetyTimer);
            _crewWriteOk = (await window.showConfirm?.(
              'That day is already ticketed',
              `${_tGuard.label} already has a ${_tGuard.detail}. Changing the crew here changes who is on record for that day's work, and the ticket's saved hours and totals do NOT recalculate.\n\nChange the crew on ${_tGuard.label} anyway?`,
              { confirmText: 'Change Crew', danger: true }
            )) === true;
            _saveJobSafetyTimer = _armSaveJobSafetyTimer();
          }
          if (!_crewWriteOk) {
            // The job's own fields are already saved above; only the crew half is
            // dropped. Say which day was skipped and why — "scoped away silently"
            // is the defect, not the fix.
            const _why = _tGuard.failedCheck
              ? `couldn't check whether ${_tGuard.label} is already ticketed — check your connection and try again`
              : _tGuard.blocked
                ? `${_tGuard.label} is already billed (${_tGuard.detail}). Unarchive that ticket in Billing if the crew is wrong`
                : `the crew change was NOT applied to ${_tGuard.label}`;
            window.showToast?.(
              `Job details saved. Crew unchanged — ${_why}.`,
              _tGuard.failedCheck ? 'error' : 'warning'
            );
            // The reason token goes in the MESSAGE, not only the payload object.
            // Support reads these as text, and "refused" without "why" cannot
            // tell a real billed-day block from a query that failed to run —
            // which is the same thing from the dispatcher's seat and completely
            // different from ours.
            const _reason = _tGuard.failedCheck ? 'unverifiable' : _tGuard.blocked ? 'billed' : 'declined';
            console.warn(`[saveJob] TASK-4614B: crew write refused on a ticketed past day (${_reason})`, {
              jobId, jobDayId: _crewScopeDayId, ticketDetail: _tGuard.detail || null
            });
          } else {
          // Single-day by construction now, so one save can never be a multi-day
          // overwrite. The audit flag stays in the payload shape so the existing
          // forensic viewer and any saved query keep rendering.
          const _multiDayOverwrite = false;
          for (const jdDoc of _scopeDocs) {
            const _prev = jdDoc.data();
            const _plan = _planJobDayCrewUpdate(_prev, _jobCrewNow, _crewDirty, _ratesDirty, _jobCrewBefore);
            if (!_plan) continue; // this day already matches — no write, no audit noise
            // TASK-10015 (TASK-4810 root cause #3) — on a LEDGER job, rentals
            // added / removed / flipped / re-quantified here move stock exactly
            // as Assign Crew does (same settle, same shared holding math),
            // BEFORE the day write — the order saveAssignment uses.
            let _ejSettled = null;
            if (_editJobLedger && Array.isArray(_plan.update.assets)) {
              const _isRent = (id) => { const a = DispatchState.assets.find(x => x.id === id); return !!a && (a.quantityType === 'rental' || a.isRental); };
              try {
                _ejSettled = await _asgSettleRentalsLedger({
                  tenantId: DispatchState.tenantId, jobId, jobDayId: jdDoc.id,
                  previousAssets: _prev.assets || [], previousDirections: _prev.assetDirections || {}, previousQtys: _prev.assetQtys || {},
                  selectedAssets: _plan.update.assets, pendingDirections: _plan.update.assetDirections || {},
                  pendingQtys: _plan.update.assetQtys || {}, isRental: _isRent, overDeploys: [],
                  pickupDateKey: _rentalDayKeyOf(_prev),
                });
              } catch (_rentErr) {
                // A login that can't move equipment counts (rules: dispatcher+
                // or Roster): the job's own fields are saved, the crew half is
                // NOT — say why, the same "crew not applied" shape as above.
                const _msg = _rentErr?.code === 'day-changed' ? _rentErr.message
                  : _rentalWriteRefusedMsg(_rentErr, 'change the rental equipment on this day');
                if (!_msg) throw _rentErr;
                window.showToast?.(`Job details saved. Crew & equipment unchanged — ${_msg}`, 'error', null, 12000);
                continue;
              }
            }
            // TASK-10072 (COD r4) — a single-tracked unit another job holds was
            // refused by the settle (TASK-9396): it never reaches this day (same
            // strip as saveAssignment), then the "Move from #A" choice is offered.
            const _ejRefused = (_ejSettled && Array.isArray(_ejSettled.refused)) ? _ejSettled.refused : [];
            for (const r of _ejRefused) {
              const u = _plan.update;
              if (r.flipped) {
                u.assetDirections = { ...(u.assetDirections || {}) };
                if (((_prev.assetDirections || {})[r.assetId] || 'deploy') === 'return') u.assetDirections[r.assetId] = 'return';
                else delete u.assetDirections[r.assetId];
                continue;
              }
              u.assets = (u.assets || []).filter(id => id !== r.assetId);
              if (u.assetQtys) { u.assetQtys = { ...u.assetQtys }; delete u.assetQtys[r.assetId]; }
              if (u.assetDirections) { u.assetDirections = { ...u.assetDirections }; delete u.assetDirections[r.assetId]; }
              if (u.lineItemSelections) { u.lineItemSelections = { ...u.lineItemSelections }; delete u.lineItemSelections[`asset:${r.assetId}`]; }
            }
            await _restampJobDaySigOnBookChange(_plan.update, _prev, oldJobSnap.exists() ? (oldJobSnap.data().customerId || null) : undefined, jobData.customerId || null, _ftsRestamped, jdDoc.id); // TASK-10190
            await updateDoc(jdDoc.ref, { ..._rentalDayWriteWithoutSettledQtys(_plan.update, _ejSettled), updatedAt: Timestamp.now() });
            if (_ejRefused.length > 0) {
              window.showToast?.(
                `Saved, but not deployed: ${_ejRefused.map(r => _rentalUnitRefusedMsg(r.name, r.heldByJobId)).join('; ')}. A rental unit stays on the job that has it until it is returned there.`,
                'warning', null, 12000);
              _offerRentalUnitMoves(_ejRefused, { jobId, jobDayId: jdDoc.id })
                .catch((err) => console.warn('[Rental] TASK-10072 move offer failed', err));
            }
            // TASK-10006 S3 — an edit that newly sends a rental OUT (added, or
            // flipped to deploy) offers the return job too.
            if (_ejSettled && Array.isArray(_ejSettled.arriving) && _ejSettled.arriving.length) {
              const _o = _rentalsGoingOut(_ejSettled.arriving, _plan.update.assetDirections || {}, _plan.update.assetQtys || {}, DispatchState.assets);
              if (Object.keys(_o).length) {
                if (!_s3ReturnPrompt) _s3ReturnPrompt = { jobId, out: {} };
                for (const [k, v] of Object.entries(_o)) _s3ReturnPrompt.out[k] = Math.max(_s3ReturnPrompt.out[k] || 0, v);
              }
            }
            if (_plan.dayCrewChanged) {
              _logJobEditCrewAudit(jobId, jdDoc.id, _prev, _jobCrewNow, _multiDayOverwrite);
            }
          }
          } // end TASK-4614B guard pass
        }
      }

      // TASK-10190 (COD Code Reviewer) — the job moved to another customer: every
      // OTHER day still carries the old customer's signature-pad stamp, and a
      // customer-only edit never reaches the per-day write above. Clear those
      // stamps (the render hint only — no crew, no rates, no updatedAt) so the
      // Build Ticket resolves the new customer's books at read time
      // (jobboard.js _jbEnsureJobDayFts). A refused write (compliance-only
      // login) is logged and skipped; the job itself is already saved.
      if (oldJobSnap.exists() && (oldJobSnap.data().customerId || null) !== (jobData.customerId || null)) {
        phase = 'jobDays-sig-clear';
        let _sigClearFailed = 0;
        for (const _d of existingJobDays.docs) {
          if (_ftsRestamped.has(_d.id) || !_d.data()?.fieldTicketSettings) continue;
          try { await updateDoc(_d.ref, { fieldTicketSettings: null }); }
          catch (_e) { _sigClearFailed++; console.warn('[saveJob] TASK-10190: stale signature stamp not cleared', { jobId, jobDayId: _d.id, code: _e?.code }); }
        }
        // COD r2 — never silent: those days keep the OLD customer's pads until re-assigned.
        if (_sigClearFailed) window.showToast?.(`Job saved, but ${_sigClearFailed} day${_sigClearFailed === 1 ? '' : 's'} still show the old customer's signature settings on the driver ticket. Re-assign crew on ${_sigClearFailed === 1 ? 'that day' : 'those days'} to refresh.`, 'warning', null, 10000);
      }

      // Propagate renamed title to clock entries + draft/ready tickets. Shared,
      // CHUNKED routine (TASK-412) — the old inline single writeBatch here died
      // past Firestore's 500-op limit on long-running jobs.
      if (oldTitle && newTitle && oldTitle !== newTitle) {
        phase = 'title-propagate';
        await propagateJobTitle(DispatchState.tenantId, jobId, newTitle);
      }

      // Sync jobDay dates when job date range changes
      const oldData = oldJobSnap.exists() ? oldJobSnap.data() : {};
      const oldStart = oldData.startDate?.toDate?.() || null;
      const oldEnd = oldData.endDate?.toDate?.() || null;
      if (oldStart && oldEnd) {
        const newStartStr = startDate.toISOString().slice(0, 10);
        const newEndStr = endDate.toISOString().slice(0, 10);
        const oldStartStr = oldStart.toISOString().slice(0, 10);
        const oldEndStr = oldEnd.toISOString().slice(0, 10);

        if (newStartStr !== oldStartStr || newEndStr !== oldEndStr) {
          phase = 'jobDays-datesync-read';
          const tenant = DispatchState.tenantId;
          const existingJDs = await getDocs(
            query(collection(db, 'tenants', tenant, 'jobDays'), where('jobId', '==', jobId))
          );

          // Build new date range set
          const dayMs = 24 * 60 * 60 * 1000;
          const newDates = new Set();
          let cur = new Date(startDate);
          while (cur <= endDate) {
            newDates.add(cur.toISOString().slice(0, 10));
            cur = new Date(cur.getTime() + dayMs);
          }

          // Map existing jobDays by date string
          const existingByDate = {};
          existingJDs.docs.forEach(jdDoc => {
            const jdDate = jdDoc.data().date?.toDate?.();
            if (jdDate) {
              existingByDate[jdDate.toISOString().slice(0, 10)] = jdDoc;
            }
          });

          // Template: copy assignments from the most recent existing jobDay.
          // TASK-1672 — this read `docs[docs.length - 1]` while calling itself
          // "most recent"; the where('jobId') query is unordered, so it picked an
          // arbitrary day. Same defect class as the Edit Job crew seed.
          const _templateDoc = _pickLatestJobDayDoc(existingJDs.docs);
          const templateJD = _templateDoc ? _templateDoc.data() : {};
          // TASK-4685 — remembered here so the create arm below can leave a
          // forensic row saying the new days were NOT born in the template's
          // terminal state. Null on every normal save.
          const _terminalTemplateStatus = _isTerminalJobDayStatus(templateJD.status) ? templateJD.status : null;

          // ── TASK-4654 ────────────────────────────────────────────────────
          // Date-sync is the third door onto a ticketed jobDay, and the only one
          // that can DELETE the day rather than merely restate its crew. Both
          // branches below break the ticket join the same way the drag path did
          // before TASK-4651: nothing here rewrites the ticket's workDate, and
          // tickets carry no jobDayId (0 of 903, measured), so the DATE *is* the
          // join. Move the day and the ticket is orphaned on the old date; delete
          // it and the ticket is orphaned with nothing left to point at.
          //
          // POLICY = the reschedule policy (refuse), not the crew policy
          // (confirm). A stale crew list is still reconcilable by a human because
          // both rows still name the same day. A moved or deleted day is not —
          // the ticket stops being findable from its day at all, billing's
          // job+day dedup then mints a SECOND ticket for work already billed
          // (TASK-1398's double-pay shape), and this very guard would wave
          // through every later edit to the now-"clean" day.
          //
          // ONE fetch for the whole range (see _fetchLiveTicketsForJob) — the
          // per-day answer comes from _ticketCoversDay + _ticketIsBilled, the
          // same two predicates the other two guards run. No fourth copy of the
          // lock predicate; TASK-4641 already had to clean three of those out of
          // jobboard.js.
          phase = 'jobDays-datesync-ticketcheck';
          const _dsFetch = await _fetchLiveTicketsForJob(jobId);
          // FAIL CLOSED: a read that threw yields `null`, which
          // _classifyDayAgainstTickets turns into locked+failedCheck. For an
          // operation whose failure mode is DELETION that is the only safe
          // default — "couldn't prove it's empty" must never mean "delete it".
          const _dsTickets = _dsFetch.ok ? _dsFetch.tickets : null;
          if (!_dsFetch.ok) {
            console.warn('[saveJob] TASK-4654: ticket read failed — treating every day as ticket-locked', { jobId });
          }

          // The single-shift candidate, normalized the way the guards normalize:
          // _jobDayDateMs handles both the Timestamp rows and the GAS-imported
          // string ones, where existingByDate's `.toDate?.()` silently drops the
          // latter. A row with no usable date has no day key to match a ticket
          // ON, so it stays unlocked — the same stance both shipped guards take,
          // and never silent.
          const _soleMs = existingJDs.docs.length === 1 ? _jobDayDateMs(existingJDs.docs[0].data()) : null;
          const _soleDayStr = _soleMs === null ? null : formatDateISO(new Date(_soleMs));
          const _soleLock = _soleDayStr
            ? _classifyDayAgainstTickets(_dsTickets, _soleDayStr)
            : { locked: false };
          if (existingJDs.docs.length === 1 && _soleDayStr === null) {
            console.warn('[saveJob] TASK-4654: sole jobDay has no usable date — ticket guard not applied', { jobId });
          }

          // Single-day shift: update the existing jobDay's date instead of delete+create.
          // TASK-4654 — a ticketed day is NOT shiftable. Turning the flag off here
          // rather than bolting a second refusal onto the shift branch is
          // deliberate: control then falls into the delete+create branch, where
          // the ticketed day becomes an out-of-range day the delete filter below
          // already refuses to touch, and the new date gets created from the same
          // template (so the crew carries). That is exactly the escape hatch
          // rescheduleJobDay offers by hand ("Add a new day on X instead?") — the
          // ticketed work happened on the ORIGINAL date and does not move.
          const isSingleDayShift = existingJDs.docs.length === 1 && newDates.size === 1
            && !existingByDate[[...newDates][0]] && !_soleLock.locked;
          if (existingJDs.docs.length === 1 && newDates.size === 1
              && !existingByDate[[...newDates][0]] && _soleLock.locked) {
            console.warn('[saveJob] TASK-4654: single-day shift refused — day is ticketed', {
              jobId, jobDayId: existingJDs.docs[0].id, dayStr: _soleDayStr,
              reason: _soleLock.failedCheck ? 'unverifiable' : (_soleLock.billed ? 'billed-ticket' : 'open-ticket'),
              detail: _soleLock.detail || null
            });
          }
          if (isSingleDayShift) {
            phase = 'jobDays-datesync-shift';
            const newDateStr = [...newDates][0];
            // TASK-4685 — THIS is the one that bit Chris. The shift wrote date +
            // updatedAt and nothing else, so a cancelled/finished day dragged
            // forward by a date edit kept its terminal status on the new date.
            //
            // RESET, don't refuse. By the time control reaches here the JOB doc's
            // startDate/endDate have already been committed above (measured on the
            // live doc: job updatedAt was 2ms EARLIER than the jobDay's). A refusal
            // here would leave the job range on the new date and its only day on the
            // old one — an inconsistency the legacy renderer papers over with
            // synthetic days. There is also no un-cancel affordance inside the job
            // form; Restore Day is a right-click on the board card. So the reset is
            // the only coherent act, and the toast + audit row below are what make
            // it visible.
            const _shiftDoc = existingJDs.docs[0];
            const _shiftPrev = _shiftDoc.data();
            const _shiftReset = _statusAfterJobDayMove(_shiftPrev);
            const _shiftUpdate = {
              // NOON-anchored (17:00Z in CDT). Midnight-local renders nowhere,
              // silently — unchanged from before, and it must stay that way.
              date: Timestamp.fromDate(new Date(newDateStr + 'T12:00:00')),
              updatedAt: Timestamp.now()
            };
            if (_shiftReset) _shiftUpdate.status = _shiftReset;
            await updateDoc(_shiftDoc.ref, _shiftUpdate);
            if (_shiftReset) {
              phase = 'jobDays-datesync-shift-statusreset';
              await _announceJobDayStatusReset({
                jobId,
                jobDayId: _shiftDoc.id,
                prevStatus: _shiftPrev.status,
                newStatus: _shiftReset,
                oldDateStr: _soleDayStr,
                newDateStr,
                source: 'dispatch-form-datesync-shift'
              });
            }
          } else {
            // Atomic batch: delete old + create new in one commit
            const dateSyncBatch = writeBatch(db);
            let deletedCount = 0;
            let createdCount = 0;

            // Delete jobDays outside new range.
            // TASK-4567 — THIS is the loop with payroll teeth: it takes the
            // day's crew, trucks, trailers, assets, lineItemSelections and load
            // counts. clockEntries carry jobId, NOT jobDayId (verified: dispatch.js
            // :19984, jobboard.js:6218, clock.js — none of them writes jobDayId), so
            // the hours are not orphaned by the delete — but the day they were worked
            // on is, which is why the confirm below buckets clock entries by their own
            // clock-in day rather than looking for a link that does not exist.
            // All under a "Job updated." toast.
            // just after a back-dated add, so the startDate widen alone does not
            // close it. Name what is about to be lost and let the dispatcher
            // keep it (declining leaves the days in place; they still render on
            // their own date, and the next date edit re-asks).
            const _allOutOfRange = Object.entries(existingByDate)
              .filter(([dateStr]) => !newDates.has(dateStr))
              .sort((a, b) => a[0].localeCompare(b[0]));

            // ── TASK-4654 ──────────────────────────────────────────────────
            // A ticketed day is never deleted, and never silently. It is pulled
            // OUT of the delete set instead of the whole edit being refused, and
            // that split is the decision, not an accident:
            //
            //  • The delete set and the create set are independent. Refusing the
            //    whole edit would also block creating the new days — punishing a
            //    legitimate range extension because one unrelated older day
            //    happens to carry a ticket.
            //  • By the time this runs the job doc's own startDate/endDate are
            //    ALREADY committed (the job update ran ~200 lines up). A
            //    whole-edit refusal would therefore need a compensating write to
            //    put them back — a new write with its own failure mode, which on
            //    failure leaves precisely the inconsistency it was added to
            //    prevent.
            //  • "Job range changed, out-of-range days kept" is not a new state
            //    this invents: it is the shipped outcome of declining the
            //    TASK-4567 confirm (_keptDoomedDays), it has a toast and an audit
            //    row, and the board renders those days on their own date.
            //  • The harm here is exactly the DELETE. Excluding ticketed days
            //    from it removes the harm precisely, with no collateral.
            //
            // On the brief's "partial failure mid-range" worry — measured, not
            // assumed: the deletes and creates are one writeBatch and commit
            // atomically, so there is no half-migrated range below Firestore's
            // 500-op batch limit. The real pre-existing gap is different (job
            // range saved + batch commit failed), it already has its own toast,
            // and this change does not widen it.
            // TASK-4793 B — outside the demo that is no longer one batch: the
            // creates commit here and the SERVER deletes after (removeJobDay mode
            // date-sync), so a failure between them leaves extra days on their
            // own dates, never fewer days than the range. And a ticketed or
            // clocked-in day can no longer reach this point by being pushed out
            // of the range: _dateSyncPrecheck refused that edit before the job
            // doc was written. What still arrives here locked is a day that was
            // ALREADY outside the old range (kept by an earlier edit).
            const _dsServer = !isDemoMode() && tenant !== 'demo'; // TASK-4793 B
            const _lockedOutOfRange = [];
            const _doomedDays = [];
            for (const entry of _allOutOfRange) {
              const _lock = _classifyDayAgainstTickets(_dsTickets, entry[0]);
              // TASK-4793 B (COD r1) — outside the demo the server re-checks every
              // day before deleting it, so a browser ticket read that FAILED must
              // not strand the day outside the saved range: let the server decide.
              if (_lock.locked && !(_lock.failedCheck && _dsServer)) _lockedOutOfRange.push([entry[0], entry[1], _lock]);
              else _doomedDays.push(entry);
            }
            if (_lockedOutOfRange.length) {
              console.warn('[saveJob] TASK-4654: refused to delete ticketed jobDays on date sync', {
                jobId,
                kept: _lockedOutOfRange.map(([d, jd, l]) => ({
                  date: d, jobDayId: jd.id,
                  reason: l.failedCheck ? 'unverifiable' : (l.billed ? 'billed-ticket' : 'open-ticket'),
                  detail: l.detail || null
                }))
              });
            }
            let _keptDoomedDays = false;
            if (_doomedDays.length) {
              phase = 'jobDays-datesync-confirm';
              // Pause the 25s hang-detector while a human reads the list; the
              // in-flight lock stays set, so Save is still blocked meanwhile.
              clearTimeout(_saveJobSafetyTimer);
              _keptDoomedDays = !(await _confirmJobDayDeletions(tenant, jobId, _doomedDays));
              _saveJobSafetyTimer = _armSaveJobSafetyTimer();
            }
            // TASK-4793 B — outside the demo the SERVER deletes them (removeJobDay
            // mode date-sync, after this batch's creates commit — see below);
            // firestore.rules refuse a client jobDay delete there.
            let _dsServerRes = null;
            if (!_keptDoomedDays && !_dsServer) {
              for (const [, jdDoc] of _doomedDays) {
                dateSyncBatch.delete(jdDoc.ref);
                deletedCount++;
              }
            }
            // TASK-9331 — does the template day outlive this batch? If it is
            // being deleted (the whole window moved), its 'return' describes a
            // day that no longer exists and must not be stamped on the new ones.
            const _templateSurvives = !_templateDoc || _keptDoomedDays
              || !_doomedDays.some(([, jdDoc]) => jdDoc.id === _templateDoc.id);

            // Create missing jobDays for new range.
            // 🔴 TASK-4792 — EXCEPT a date somebody deliberately REMOVED. This
            // loop invents a day for every date in the range that lacks one,
            // which is right for a range EXTENSION and wrong for a hole a
            // dispatcher made on purpose: removeJobDay deletes the Sep 7 of a
            // Sep 6–8 job, the surviving days still span Sep 6–8, so the range
            // never changes and the next date edit rebuilds Sep 7 with crew
            // carried forward. `jobs.removedDates` is the record that says the
            // hole is intentional; `_applyAddDayJobRange` clears a date from it
            // the moment a dispatcher adds that day back, so this can never
            // refuse a day somebody actually wants.
            const _removedDates = new Set(
              Array.isArray(oldData.removedDates) ? oldData.removedDates.map(String) : []
            );
            if (_removedDates.size) {
              console.log('[saveJob] TASK-4792: skipping deliberately removed dates on create',
                { jobId, removedDates: [..._removedDates].filter(d => newDates.has(d)) });
            }
            // 🔴 TASK-4794 — and only across the span the edit CHANGED. A hole
            // inside the OLD range was never a day; see dateSyncCreatableDates.
            // When NO existing day survives in the new range (a move, or a
            // shrink onto holes) the removal record does not veto either: the
            // dispatcher just put the job on exactly these dates, and honouring
            // a removal from its old placement would leave it with no day at
            // all (COD r2 — e.g. Sep 7 removed, range set to Sep 7 only).
            //
            // TASK-4794 follow-up — a day KEPT outside the new range because a
            // ticket locks it (TASK-4654 above) also survives the edit: the job
            // is not left dayless, so the gap rule applies and moving the range
            // onto an empty stretch inside the old range does not re-create
            // every date with crew. (The driver-edit twin refuses such an edit.)
            // A kept day only counts while the new range still gets a day of its
            // own (dateSyncCreatableDates falls back to fill-every-date when it
            // would not) — and the removal veto follows the same rule, so it can
            // never empty the new range either (COD cod-20261020T072023Z-73236).
            const _dsKeptDates = _lockedOutOfRange.map(([d]) => d);
            const _dsExistingSurvivor = Object.keys(existingByDate).some(d => newDates.has(d));
            const _dsCreatable = dateSyncCreatableDates(newDates, oldStartStr, oldEndStr, Object.keys(existingByDate), _dsKeptDates);
            const _dsKeptGapApplied = !_dsExistingSurvivor && _dsKeptDates.length > 0 && _dsCreatable.length < newDates.size;
            const _dsHasSurvivor = _dsExistingSurvivor
              || (_dsKeptGapApplied && _dsCreatable.some(d => !_removedDates.has(d)));
            for (const dateStr of _dsCreatable) {
              if (_dsHasSurvivor && _removedDates.has(dateStr)) continue;
              if (!existingByDate[dateStr]) {
                const newJdRef = doc(collection(db, 'tenants', tenant, 'jobDays'));
                dateSyncBatch.set(newJdRef, {
                  jobId,
                  date: Timestamp.fromDate(new Date(dateStr + 'T12:00:00')),
                  // TASK-4685 — a brand-new day must NEVER be born in a terminal
                  // state. templateJD is _pickLatestJobDayDoc(...), so if the
                  // latest existing day happened to be cancelled or finished,
                  // EVERY day created across the new range was born cancelled or
                  // finished — work that has not happened yet, rendered as already
                  // done or already called off, and skipped by conflict detection.
                  // The `finished` arm is the sharper half: it renders as completed
                  // work that was never performed.
                  //
                  // Only the TERMINAL arm is rewritten. `templateJD.status ||
                  // 'scheduled'` still carries a live status ('assigned',
                  // 'en-route') forward exactly as it did before — flattening those
                  // to 'scheduled' would be a second, unasked-for behaviour change
                  // on every range-extend save.
                  status: _statusAfterJobDayMove(templateJD) || templateJD.status || 'scheduled',
                  drivers: templateJD.drivers || [],
                  helpers: templateJD.helpers || [],
                  trucks: templateJD.trucks || [],
                  trailers: templateJD.trailers || [],
                  assets: templateJD.assets || [],
                  // 🔴 TASK-9331 — the asset DETAIL travels with `assets`, the same
                  // way buildInheritedJobDay carries it for add-day and the
                  // assignment modal. Without these two maps generateTicketFromJob's
                  // per-day rental fallback resolves every rental on an extended
                  // day to qty 1 / direction 'deploy'. See _dateSyncAssetDetail
                  // for the one place this is NOT verbatim (dates before the
                  // template day never inherit a 'return').
                  ..._dateSyncAssetDetail(templateJD, dateStr, _templateSurvives),
                  // TASK-9258 / TASK-9331 — THE MARK FOLLOWS THE NUMBER. The 9258
                  // note here held the template stamp back only because this lane
                  // dropped the quantities. Now that they carry, a template-seeded
                  // "6 movers, nobody checked" must carry its provenance too, as
                  // the board Add-Day paths do: inheritJobDayTemplateFields,
                  // filtered to the carried assets, null ⇒ no keys on an
                  // ordinary day.
                  ...(inheritJobDayTemplateFields(
                    _templateDoc ? { ...templateJD, id: _templateDoc.id } : null,
                    templateJD.assets || []) || {}),
                  lineItemSelections: templateJD.lineItemSelections || {},
                  // TASK-9763 — "who's in what" travels with the crew it describes.
                  ...((() => {
                    const _CP = (typeof window !== 'undefined' && window.CrewPairing) || null;
                    const _p = _CP && templateJD.crewAssetAssignments ? _CP.prune(templateJD.crewAssetAssignments, templateJD) : null;
                    // S3 — so does the day's "first on" order.
                    const _o = _CP && templateJD.crewOrder ? _CP.orderPrune(templateJD.crewOrder, templateJD) : null;
                    // TASK-9797 item 1 — and the trucks' named slots (same trucks as the template).
                    return { ...(_p ? { crewAssetAssignments: _p } : {}), ...(_o ? { crewOrder: _o } : {}), ...(templateJD.unitSlots && { unitSlots: templateJD.unitSlots }) };
                  })()),
                  selectedContractId: templateJD.selectedContractId || null,
                  fieldTicketSettings: templateJD.fieldTicketSettings || null, // TASK-395 carry-forward
                  subcontractors: templateJD.subcontractors || [],
                  createdAt: Timestamp.now()
                });
                createdCount++;
              }
            }

            let _syncCommitted = false;
            try {
              phase = 'jobDays-datesync-commit';
              await dateSyncBatch.commit();
              _syncCommitted = true;
            } catch (syncErr) {
              console.error('[saveJob] JobDay date sync batch failed:', syncErr);
              window.logError(syncErr, { module: 'dispatch', phase: 'jobday-date-sync', jobId, deletedCount, createdCount });
              window.showToast('Job saved but schedule dates failed to sync — please try editing again', 'error');
            }

            // TASK-4793 B — the creates landed first, so a failure here leaves
            // extra days on their own dates (the "kept" state), never a job with
            // fewer days than its range. The server re-checks every day in one
            // transaction: a day a ticket or clock-in now covers is KEPT and named.
            if (_syncCommitted && _dsServer && !_keptDoomedDays && _doomedDays.length) {
              phase = 'jobDays-datesync-server-delete';
              _dsServerRes = await _dateSyncServerDelete(tenant, jobId, _doomedDays);
              deletedCount = _dsServerRes.deleted;
              for (const k of _dsServerRes.kept) {
                const jd = _doomedDays.find(([, d]) => d.id === k.jobDayId);
                _lockedOutOfRange.push([k.date, jd ? jd[1] : { id: k.jobDayId }, { detail: k.why, billed: false, failedCheck: false, server: true }]);
              }
            }

            // TASK-9227 — the WORST of the nine crew-writing paths: this loop
            // copies `templateJD.drivers` / `.helpers` onto N brand-new days with
            // no human, no toast and no advisory of any kind. The job-form gate
            // above vetted the FORM's crew; templateJD is a DIFFERENT set (crew
            // is per-day — _pickLatestJobDayDoc picks whichever day was last),
            // so this is not a duplicate check. Post-commit because there is no
            // one on screen to ask: name who was carried and onto how many days.
            if (_syncCommitted && createdCount > 0) {
              _dfNoticeEntries(
                _dfFlaggedEntries([...(templateJD.drivers || []), ...(templateJD.helpers || [])]),
                { surface: 'job-form-datesync', jobId, dayCount: createdCount, action: 'FLAGGED_CREW_CARRIED' }
              );
            }

            // TASK-4780 — the row for the days that were actually DESTROYED.
            // Gated on `_syncCommitted` so a failed batch never claims a delete
            // that did not happen, and on `deletedCount` so an ordinary save
            // that drops no day writes no spurious row. See
            // _auditJobDayDateSyncDeletes for why this logs rather than refuses.
            // (Outside the demo the server wrote this row in the delete's commit.)
            if (_syncCommitted && !_keptDoomedDays && deletedCount > 0 && !_dsServer) {
              phase = 'jobDays-datesync-delete-audit';
              await _auditJobDayDateSyncDeletes({
                tenant,
                jobId,
                doomed: _doomedDays,
                jobTitle: jobData?.title || null,
                customerName: jobData?.customer || null,
                newRange: { start: newStartStr, end: newEndStr }
              });
            }

            // TASK-4685 — forensic row for the create arm. No toast here: these
            // days now render exactly as a dispatcher expects a newly added day
            // to render, so there is nothing surprising to announce. The row
            // exists because the OLD behaviour (born terminal) is what a support
            // question six months from now would be about.
            if (_syncCommitted && _terminalTemplateStatus && createdCount > 0) {
              phase = 'jobDays-datesync-templatereset-audit';
              try {
                await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
                  action: 'JOB_DAY_STATUS_RESET_ON_CREATE',
                  source: 'dispatch-form-datesync-create',
                  timestamp: Timestamp.now(),
                  user: window.AppState?.user?.email || 'unknown',
                  jobId: jobId || null,
                  jobDayId: null,
                  templateStatus: _terminalTemplateStatus,
                  newStatus: _statusAfterJobDayMove(templateJD) || 'scheduled',
                  createdCount: createdCount
                });
              } catch (e) { console.warn('[saveJob] TASK-4685: template-reset audit log failed:', e); }
            }

            // TASK-4654 — say out loud which days were kept BECAUSE they are
            // ticketed, and leave a forensic row. This is the only signal the
            // dispatcher gets that the date edit did not fully apply — a
            // silently-skipped delete is the same invisible-loss defect
            // TASK-4567 closed, just pointing the other way. It also covers the
            // single-day-shift case, which now arrives here as an out-of-range
            // ticketed day rather than as a move.
            if (_lockedOutOfRange.length) {
              const _fmtShort = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const _named = _lockedOutOfRange.slice(0, 3)
                .map(([d, , l]) => `${_fmtShort(d)} (${l.failedCheck ? 'ticket check failed' : l.detail})`).join(', ');
              const _more = _lockedOutOfRange.length > 3 ? ` +${_lockedOutOfRange.length - 3} more` : '';
              const _n = _lockedOutOfRange.length;
              const _createdBit = createdCount > 0
                ? ` The new date${createdCount === 1 ? '' : 's'} ${createdCount === 1 ? 'was' : 'were'} added as ${createdCount === 1 ? 'a new day' : 'new days'}.`
                : '';
              // TASK-4793 B — a day the SERVER kept may be clocked-in rather than
              // ticketed; say "day" then. Its audit row is already in the server's
              // commit, so only the browser-kept ones get the row below.
              const _allTicketed = _lockedOutOfRange.every(([, , l]) => !l.server || /^on /.test(l.detail || ''));
              const _clientLocked = _lockedOutOfRange.filter(([, , l]) => !l.server);
              setTimeout(() => window.showToast?.(_allTicketed
                ? `Kept ${_n} ticketed day${_n === 1 ? '' : 's'} — ${_named}${_more}. A ticketed day can't be moved or deleted; the ticket keeps its own date.${_createdBit}`
                : `Kept ${_n} day${_n === 1 ? '' : 's'} — ${_named}${_more}. A day with a ticket or someone clocked in can't be deleted; it stays on its own date.${_createdBit}`,
                'warning'), 900);
              if (_clientLocked.length) try {
                await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
                  action: 'JOB_DAY_DELETE_BLOCKED_TICKETED',
                  source: 'dispatch-form',
                  timestamp: Timestamp.now(),
                  user: window.AppState?.user?.email || 'unknown',
                  jobId,
                  keptDates: _clientLocked.map(([d]) => d),
                  keptJobDayIds: _clientLocked.map(([, jd]) => jd.id),
                  reasons: _clientLocked.map(([, , l]) => l.failedCheck ? 'unverifiable' : (l.billed ? 'billed-ticket' : 'open-ticket')),
                  newRange: { start: newStartStr, end: newEndStr }
                });
              } catch (ae) { console.warn('[saveJob] TASK-4654 ticket-locked audit log failed:', ae); }
            }

            // TASK-4567 — say it out loud when days were kept, and leave a row.
            // The old silent-delete was invisible in BOTH directions; this is the
            // one the dispatcher needs to be able to find later.
            if (_keptDoomedDays) {
              setTimeout(() => window.showToast?.(
                `Kept ${_doomedDays.length} scheduled day${_doomedDays.length === 1 ? '' : 's'} outside the new dates — they still show on their own date.`,
                'info'), 700);
              try {
                await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
                  action: 'JOB_DAY_DELETE_DECLINED',
                  source: 'dispatch-form',
                  timestamp: Timestamp.now(),
                  user: window.AppState?.user?.email || 'unknown',
                  jobId,
                  keptDates: _doomedDays.map(([d]) => d),
                  newRange: { start: newStartStr, end: newEndStr }
                });
              } catch (ae) { console.warn('[saveJob] kept-days audit log failed:', ae); }
            }
          }
          console.log(`[saveJob] Synced jobDays: ${Object.keys(existingByDate).length} existing → ${newDates.size} new dates`);
        }
      }

      // TASK-962 (S2): show the saved weekday+date so a wrong-day save is caught
      // at the toast, not by manual re-check.
      const _updatedMsg = `Job updated: ${jobData.title || 'Untitled'} — ${formatJobToastDates(startDateVal, endDateVal)}`;
      // TASK-9112 — an edit that moves a job off the visible board makes it
      // vanish from the board it was just on. Same signal, same jump.
      const _offBoardUpd = decorateOffBoardSaveToast(_updatedMsg, startDateVal, endDateVal, _boardRangeAtSave);
      if (_offBoardUpd) {
        window.showToast(_offBoardUpd.message, 'success', _offBoardUpd.action, _offBoardUpd.durationMs);
      } else {
        window.showToast(_updatedMsg, 'success');
      }
      // TASK-1617 — the save succeeded; now name any stop that saved without a
      // location, so it can't go unnoticed.
      if (_geocodeWarning) setTimeout(() => window.showToast?.(_geocodeWarning, 'warning'), 400);
      // TASK-398: flush the breadcrumb trail for this save (one telemetry doc,
      // 90-day TTL), anchored to the job so support can replay the steps.
      window.ActionLog?.flush?.('save', { jobId });
    } else {
      // TASK-273: Pre-save dupe-detect gate — replaces native confirm() that was
      // dismissed reflexively. Now: 60-min recency filter (audit shows misfires
      // happen within ~5 min, 60 min is conservative headroom) + custom 3-button
      // modal where "Add Day to Existing Job" is the preselected primary action.
      // _dupeBypassConfirmed short-circuits this gate on second-pass save (user
      // explicitly chose "create separate" the first time).
      if (customerId && jobData.title && !_dupeBypassConfirmed) {
        const newTitle = jobData.title.trim().toLowerCase();
        const _normalize = s => s.replace(/\s+/g, ' ').trim().toLowerCase();
        const normalizedNew = _normalize(newTitle);
        const cutoffMs = Date.now() - 60 * 60 * 1000; // 60-min recency window
        // TASK-493: two triggers for the soft dupe warning. Both require same
        // customer + a non-closed job ('finished'/'cancelled' are closed states;
        // job docs default to 'active' at create). Tenant-scoped via in-memory
        // DispatchState.jobs (already tenant-scoped). NEVER collectionGroup.
        //   (1) RECENCY  — fuzzy title match within the last 60 min (rapid double-submit).
        //   (2) RECURRING — EXACT title match (case-insensitive) on ANY open job,
        //       regardless of recency, so a new "8\" Replacement Line" for a customer
        //       that already has an open one of the same name gets the soft warning.
        const _isOpen = j => j.status !== 'finished' && j.status !== 'cancelled';
        // Note on scope: DispatchState.jobs is windowed to jobs whose startDate or
        // endDate is within the last 7d (or future) — see the onSnapshot at ~L2367.
        // So a recurring job that ended >7d ago is ALREADY out of memory and cannot
        // false-trigger; the in-memory set IS effectively an "active/recent" view,
        // which is the open-job scoping this gate wants (generous time-window proxy
        // per the task). No extra Firestore read needed, no collectionGroup.
        const _jobTs = j => {
          const tsRef = j.updatedAt || j.createdAt;
          return tsRef?.toDate?.()?.getTime?.() || (tsRef ? new Date(tsRef).getTime() : 0);
        };
        // TASK-795: well-aware dedupe. jobData.wellName carries the combined
        // wellName+wellNumber string (wellNumber is saved as ''). Two jobs for
        // the same customer at DIFFERENT wells are legit, distinct oilfield work
        // (Guy/Split Rock — data-verified), so they must NOT trip the warning.
        // If either side has no well, fall back to title-only (no regression).
        const normalizedNewWell = _normalize([jobData.wellName, jobData.wellNumber].filter(Boolean).join(' '));
        const similarJobs = DispatchState.jobs.filter(j => {
          if (j.customerId !== customerId || !_isOpen(j) || !j.title) return false;
          // Well-distinctness short-circuit (applies to BOTH triggers below):
          // both jobs name a well and the wells differ → different work, not a dup.
          const existingWell = _normalize([j.wellName, j.wellNumber].filter(Boolean).join(' '));
          if (normalizedNewWell && existingWell && normalizedNewWell !== existingWell) return false;
          const existing = _normalize(j.title);
          // Trigger (2): exact title match on any open job — no time window.
          if (existing === normalizedNew) return true;
          // Trigger (1): fuzzy match only within the 60-min recency window.
          const ts = _jobTs(j);
          if (!ts || ts < cutoffMs) return false;
          return existing.startsWith(normalizedNew) || normalizedNew.startsWith(existing);
        // Most-recently-active match first, so the modal references the freshest
        // open job (not an arbitrary array-order pick) when several share a title.
        }).sort((a, b) => _jobTs(b) - _jobTs(a));
        if (similarJobs.length > 0) {
          // Show 3-button blocking modal. saveJob is paused; control returns
          // here only after the user picks an action (which may re-invoke
          // saveJob with _dupeBypassConfirmed=true, or call _dupeBlockAddDay).
          if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
          window.__saveJobInFlight = false;
          clearTimeout(_saveJobSafetyTimer);
          showJobDuplicateBlockingModal(similarJobs[0], startDateVal, (minsAgo) => {
            // "Create separate job anyway" callback — set bypass + re-fire save
            _dupeBypassConfirmed = true;
            // TASK-4660 — pin what the dispatcher was warned about. The audit row
            // is written AFTER the create succeeds (next to JOB_CREATE) so it can
            // name both jobs. Every field is coerced to a non-undefined value:
            // one `undefined` silently drops the whole write
            // ([[lesson_audit_writes_fail_silently_on_undefined]]).
            _dupeBypassContext = {
              matchedJobId: similarJobs[0].id || null,
              matchedTitle: similarJobs[0].title || '',
              minutesSinceMatch: Number.isFinite(minsAgo) ? minsAgo : null,
              matchedJobStartDate: _tsToYmdOrNull(similarJobs[0].startDate),
              matchedJobPo: String(similarJobs[0].poNumber || '') || null,
              // TASK-9289 S2 — a "new job in this project" choice is not a
              // dismissed warning; the row says which it was.
              sameProjectId: (_addDayProjectCarry(similarJobs[0], _formProjectContext()).kind === 'same' && similarJobs[0].projectId) || null
            };
            // Re-fire saveJob from the form's submit handler by clicking the button
            const btn = document.getElementById('btn-save-job');
            if (btn) btn.click();
          });
          return; // Stop save until user resolves the modal
        }
      }

      // Create new job
      const _opStart = Date.now();
      jobData.createdAt = Timestamp.now();
      // TASK-1333 — creator attribution. Stamped ONLY on this create branch, NOT
      // in the jobData literal: that same object is reused by the EDIT path above
      // (updateDoc at :7282), so a literal field would silently overwrite the
      // original creator with whoever edited last — the exact opposite of the
      // question this field exists to answer. `auth.currentUser.uid` (not
      // AppState) is the identity firestore.rules compares against, so the two
      // can never drift; AppState is only a fallback. Same shape as
      // projects.js createProject (:441-442). The uid is the ENFORCED field
      // (rules pin it to request.auth.uid); the name is display convenience and
      // is NOT trustworthy on its own.
      jobData.createdBy = auth.currentUser?.uid || window.AppState?.user?.uid || null;
      jobData.createdByName = window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown';
      // TASK-9336 — every job born here carries a rental ledger, so its rental
      // stock is reversed against what it really deployed, never against a
      // day's copied labels. Create branch ONLY, like createdBy: the EDIT path
      // reuses jobData, and writing `{}` there would wipe a live ledger.
      jobData.rentalLedger = {};
      // TASK-10006 — a return job remembers the job it was made from, so the
      // count still nets its pickups when it is not on a project (or was
      // taken off it). Create only; never on an edit.
      // TASK-10006 S3b — firestore.rules accept the link only to a job of the
      // SAME customer; a copy moved to another customer is a new job, no link.
      if (DispatchState.returnJobCtx && DispatchState.returnJobCtx.sourceJobId
          && (DispatchState.returnJobCtx.customerId ?? null) === (jobData.customerId ?? null)) {
        jobData.returnForJobId = String(DispatchState.returnJobCtx.sourceJobId);
      }
      // TASK-4681 — PRE-FLIGHT the consumable cap BEFORE the first write.
      //
      // COD ❌ x3 (Code Reviewer, Domain, Blast-Radius) on the first two cuts, and
      // both blocks were right:
      //   v1 refused down in the inventory lane, which runs AFTER the job doc and
      //   its jobDays have committed — so an over-cap save left a real job on the
      //   board carrying consumables with no stock deducted, and pendingAssetQtys
      //   clears right after, so assigning from the board wouldn't deduct either.
      //   Silent inventory desync: the exact class this task removes.
      //   v2 moved the check up but read `selectedAssets` above its own `const` in
      //   the same block — a temporal-dead-zone ReferenceError that would have
      //   thrown on EVERY new-job save, not just over-cap ones.
      // So the asset selection is hoisted here, above the first write, and reused
      // by the jobDay loop and the inventory lane below — one read, one source.
      const selectedAssets = _jobCrewForCreate('assets', 'assets');
      // TASK-9363 r2 — which of these a Copy carried from a source job, captured
      // NOW (the carry is cleared by the next form open, and saveJob awaits below).
      const _copyRentalGuard = _copyRentalGuardFor(selectedAssets, _dupeCrewCarry, _dupeAutoCarried, _dupeCarryRetired);
      const _copyRentalRefused = [];
      const _unitMoveOffers = []; // TASK-10072 — units another job holds: offered "Move from #A" after the save
      let _copyRentalQtyRefused = false; // TASK-9396 — "add it if more are going" only fits a quantity lot
      const _copyRentalUndoFailed = []; // r4 — days still list a rental that did not deploy
      // TASK-9363 r3 — deployability is decided HERE, before the job or any
      // jobDay is written (the same rule the consumable pre-flight below
      // follows): a refused rental never enters the day payload, so no day can
      // list — and bill — stock this job did not deploy.
      if (_copyRentalGuard) {
        phase = 'assets-carry-precheck';
        const _preRefused = await _precheckCarriedRentals(DispatchState.tenantId, _copyRentalGuard, selectedAssets,
          (id) => DispatchState.pendingAssetQtys[id] || 1,
          (id) => DispatchState.pendingAssetDirections[id] || 'deploy');
        for (const r of _preRefused) {
          const i = selectedAssets.indexOf(r.assetId);
          if (i >= 0) selectedAssets.splice(i, 1);
          delete DispatchState.pendingAssetQtys[r.assetId];
          delete DispatchState.pendingAssetDirections[r.assetId];
          _copyRentalRefused.push(r.verdict === 'source-out'
            ? `${r.name} — still out on ${_copyRentalGuard.sourceLabel}`
            : `${r.name} — the yard does not have ${r.qty}`);
          _copyRentalQtyRefused = true;
        }
      }
      // TASK-9396 — every single-tracked unit going out (carried OR hand-picked):
      // one that another job holds never enters the day payload. Same rule and
      // the same spot as the carry pre-check — before the first write.
      phase = 'assets-unit-precheck';
      const _unitRefused = await _precheckRentalUnits(DispatchState.tenantId, selectedAssets,
        (id) => DispatchState.pendingAssetDirections[id] || 'deploy');
      for (const r of _unitRefused) {
        const i = selectedAssets.indexOf(r.assetId);
        if (i >= 0) selectedAssets.splice(i, 1);
        delete DispatchState.pendingAssetQtys[r.assetId];
        delete DispatchState.pendingAssetDirections[r.assetId];
        _copyRentalRefused.push(_rentalUnitRefusedMsg(r.name, r.heldByJobId));
        _unitMoveOffers.push(r);
      }
      const _JOB_INV_MAX = 240;
      const _preflightConsumables = selectedAssets.filter((id) => {
        const a = DispatchState.assets.find((x) => x.id === id);
        return !!(a && a.consumable && !a.stockTracked); // identical predicate to the collector below (TASK-9720)
      }).length;
      if (_preflightConsumables > _JOB_INV_MAX) {
        console.warn('[Dispatch] TASK-4681: consumable count exceeds one atomic batch — refusing before any write', { count: _preflightConsumables });
        window.showToast(
          `Too many consumable items on one job (${_preflightConsumables}, max ${_JOB_INV_MAX}) — nothing was saved. Split them across days.`,
          'error');
        _bailOutOfSave();   // COD: without this the Save button stays disabled and the
        return;             // spinner runs until the 25s safety timer fires.
      }

      // TASK-4810 — the UPPER bound. Nothing looked at owned stock before a
      // rental deploy, so a typed 4081 (against ~1,600 owned) went straight onto
      // Split Rock's Rubber Mat counter. Ask BEFORE the first write so "Go Back"
      // leaves nothing behind; "Save Anyway" is always allowed (warn, never
      // block) and the deploy row is stamped. Watchdog paused while a human
      // reads it (TASK-4567); __saveJobInFlight stays set meanwhile.
      const _overDeploys = rentalOverDeploys(DispatchState.assets, selectedAssets
        .filter(id => (DispatchState.pendingAssetDirections[id] || 'deploy') === 'deploy')
        .map(id => ({ assetId: id, qty: DispatchState.pendingAssetQtys[id] || 1 })));
      if (_overDeploys.length > 0) {
        clearTimeout(_saveJobSafetyTimer);
        const _overOk = await _confirmRentalOverDeploy(_overDeploys, { source: 'saveJob-create' });
        _saveJobSafetyTimer = _armSaveJobSafetyTimer();
        if (!_overOk) { _bailOutOfSave(); return; }
      }

      // TASK-4781 — mint the job number BEFORE the job write, and carry it in the
      // CREATE payload. Never a write-then-update: an update-after would leave a
      // numberless job on any failure of the second write, and would additionally
      // have to satisfy the jobs UPDATE rule.
      //
      // Stamped ONLY on this create branch, NOT in the jobData literal — that same
      // object is reused by the EDIT path above (updateDoc), so a literal field
      // would RE-ALLOCATE a new number on every edit. A renumbered job is worse
      // than an unnumbered one. Exactly the trap createdBy documents above.
      // On the degraded path the field is OMITTED, not written as null: Firestore
      // drops a doc with a MISSING field from an ordered query but KEEPS one whose
      // field is explicitly null, and the probe above documents pending jobs as
      // omitted. Omitting keeps that true.
      // TASK-9587 — a job Claude asked for: claim the request BEFORE a job
      // number is spent. The server re-checks it (still waiting, 24 h, this
      // person's, token live, Dispatch module) and holds it while we write, so
      // it cannot also be approved. The job then takes the id it names
      // (jobs/mcpd_<proposalId>), written create-only with its days below.
      const _cdCtx = _activeJobFormClaudeDraft();
      let _cdJobId = null;
      let _cdClaimId = null;
      if (_cdCtx) {
        let _cdClaim;
        try {
          _cdClaim = await _claudeJobCall('claim', { proposalId: _cdCtx.proposalId });
        } catch (cErr) {
          _bailOutOfSave();
          window.showToast?.(cErr?.message || 'Could not save this Claude request. Nothing was saved.', 'warning', null, 9000);
          return;
        }
        if (_cdClaim.state === 'done') {
          _bailOutOfSave();
          window.showToast?.(`${_cdClaim.jobNumber != null ? `J-${_cdClaim.jobNumber}` : 'This job'} from that Claude request is already on the board — nothing new was saved.`, 'warning', null, 8000);
          closeModal('job');
          return;
        }
        _cdJobId = _cdClaim.jobId;
        _cdClaimId = _cdClaim.claimId || null;
        _cdHeldClaim = { proposalId: _cdCtx.proposalId, claimId: _cdClaimId };
        if (_cdJobId !== `mcpd_${_cdCtx.proposalId}`) {
          _cdHeldClaim = null;
          _bailOutOfSave();
          _releaseClaudeDraft(_cdCtx.proposalId, _cdClaim.claimId);
          window.showToast?.('Could not save this Claude request. Nothing was saved.', 'error');
          return;
        }
      }

      phase = 'jobs-number';
      const _jobNo = await allocateJobNumberSafe(DispatchState.tenantId);
      if (_jobNo.jobNumberPending) jobData.jobNumberPending = true;
      else jobData.jobNumber = _jobNo.jobNumber;

      phase = 'jobs-create';
      // TASK-9392 — a job saved from a customer request takes that request's
      // deterministic id (cr_{draftId}, the same id approveCustomerRequest uses).
      // COD (merge round): it is NOT written here. The job and ALL its jobDays
      // are written together in ONE transaction further down
      // (_commitCustomerRequestJobAtomically), which also re-reads the draft, so
      // a request declined or approved in the meantime refuses the save and a
      // jobDay failure can never leave a job with no day on the board.
      const _crCtx = _activeJobFormCustomerRequest();
      let jobRef;
      if (_crCtx) {
        Object.assign(jobData, _crCtx.jobStamp);
        jobRef = doc(db, 'tenants', DispatchState.tenantId, 'jobs', _crCtx.jobId);
      } else if (_cdCtx) {
        // TASK-9587 — written with its days in one transaction further down.
        jobData.claudeRequest = { proposalId: _cdCtx.proposalId };
        jobRef = doc(db, 'tenants', DispatchState.tenantId, 'jobs', _cdJobId);
      } else {
        jobRef = await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobs'), jobData);
      }

      // Everything that assumes the job doc EXISTS — the route-cache resolve and
      // the two audit rows. Runs right away on the normal path; on the customer-
      // request path only after the atomic commit has landed.
      const _afterJobDocCreated = async () => {
      if (_p811PotAtSave) p811PotentialMarkUsed(_p811PotAtSave, 'job', jobRef.id); // TASK-9887 — not awaited; a failure leaves it suggesting, never blocks the job
      // TASK-9174 — same eager resolve on the create path. No prior cache
      // exists on a brand-new job, so this always fetches when the job has two
      // or more plottable stops, and never when it doesn't.
      kickJobRouteCache(jobRef.id, waypoints, null);

      // Audit: job creation
      try {
        await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
          action: 'JOB_CREATE', source: 'dispatch-form', timestamp: Timestamp.now(),
          user: window.AppState?.user?.email || 'unknown',
          jobId: jobRef.id, jobTitle: jobData.title,
          customerId: jobData.customerId, customerName: jobData.customer,
          startDate: startDateVal, endDate: endDateVal
        });
      } catch (ae) { console.warn('[Dispatch] Job audit log failed:', ae); }
      _p811FlushOverride(jobRef.id); // TASK-9862 — the expired-locate override, now that the job has an id

      // TASK-4660 — receipt for a duplicate-gate bypass. This row is NOT new:
      // JOB_CREATE_DUPLICATE_BYPASS has existed since TASK-273 and the job
      // history viewer already renders it as 'Dup override' (:~24323). What
      // changed is WHERE it is written and WHAT it carries. It used to fire at
      // click time inside showJobDuplicateBlockingModal, which meant it (a) could
      // not name the job that got created, because that job did not exist yet,
      // and (b) landed even when the save was then abandoned. Moving it here —
      // same action, same collection, superset of the same fields — makes one
      // row per duplicate ACTUALLY created, naming both jobs, so the pair can be
      // attributed. jobAuditLog is `allow update: if false`, so back-stamping the
      // new id onto a click-time row was never an option.
      if (_dupeBypassContext) {
        const _byp = _dupeBypassContext;
        _dupeBypassContext = null; // one receipt per bypass, whatever happens below
        try {
          await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
            action: 'JOB_CREATE_DUPLICATE_BYPASS', source: 'dispatch-form', timestamp: Timestamp.now(),
            user: window.AppState?.user?.email || 'unknown',
            // The job that was just created despite the warning. NEW — this is the
            // half that made the old row unattributable.
            jobId: jobRef.id, jobTitle: jobData.title || null,
            // The open job the gate matched it against (original field names kept
            // so new rows and the ~65 existing ones share one schema).
            matchedJobId: _byp.matchedJobId, matchedTitle: _byp.matchedTitle,
            minutesSinceMatch: _byp.minutesSinceMatch,
            matchedJobStartDate: _byp.matchedJobStartDate, matchedJobPo: _byp.matchedJobPo,
            sameProjectId: _byp.sameProjectId || null, // TASK-9289 S2
            customerId: jobData.customerId || null, customerName: jobData.customer || null,
            startDate: startDateVal || null, endDate: endDateVal || null
          });
        } catch (ae) { console.warn('[Dispatch] Dupe-bypass audit log failed:', ae); }
      }
      };
      if (!_crCtx && !_cdCtx) await _afterJobDocCreated();

      // Get crew/equipment assignments from multi-select sections
      // TASK-9212 — _jobCrewForCreate is getJobCrewSelected for every save but
      // one: a COPY saved while a roster collection is still in flight, where the
      // pickers read empty and the jobDays would be created with no crew.
      const selectedDrivers = _jobCrewForCreate('people', 'drivers');
      const selectedHelpers = _jobCrewForCreate('people', 'helpers');
      const selectedTrucks = _jobCrewForCreate('trucks', 'trucks');
      const selectedTrailers = _jobCrewForCreate('trailers', 'trailers');
      // selectedAssets is hoisted above the job create for the TASK-4681 pre-flight.
      const hasAssignments = selectedDrivers.length > 0 || selectedTrucks.length > 0;
      // TASK-9227 — same write-time re-check on the CREATE path. These are the
      // arrays the jobDay batch below stamps onto every day of the range.
      _dfNoticeUnvetted([...selectedDrivers, ...selectedHelpers], _dfVettedIds,
        { surface: 'job-form-create', jobId: jobRef.id });

      // Build lineItemSelections from job creation pickers
      const jobLineItemSelections = { ...DispatchState.pendingJobLineItemSelections };
      const jobSelectedContractId = DispatchState.pendingJobSelectedContractId || null;
      // TASK-395 Slice 1: resolve once before the batch loop, stamp on every jobDay.
      const jobSigSettings = await resolveJobDaySigSettings(jobData.customerId, jobSelectedContractId);

      // TASK-9258 — template provenance, resolved ONCE so every day of the range
      // carries an identical stamp (a per-day recompute would drift the moment
      // anything below it mutated the pending maps). null when no template was
      // applied, so a hand-built job gains no template keys at all.
      const _jobTplFields = buildJobDayTemplateFields(
        DispatchState.templateApplied, DispatchState.templateUnconfirmed, selectedAssets);
      // TASK-9750 — read once, stamped on every created day. Only present when
      // ticked, so an ordinary job's days gain no new keys. The flags row is
      // hidden on edit, and a hidden row is never read.
      const _newDayFlags = {};
      if (document.getElementById('job-day-flags-row')?.style.display !== 'none') {
        if (document.getElementById('job-tentative')?.checked) _newDayFlags.tentative = true;
        if (document.getElementById('job-half-day')?.checked) _newDayFlags.halfDay = true;
      }

      // Create job days for each day in range (atomic batch — all or nothing)
      const dayMs = 24 * 60 * 60 * 1000;
      let current = new Date(startDate);
      const jobDayBatch = writeBatch(db);
      const _crDayWrites = []; // TASK-9392 — [ref, data] for the atomic customer-request commit
      const _createdJobDayRefs = [];
      let jobDayCount = 0;

      while (current <= endDate) {
        const jobDayRef = doc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'));
        const jobDayData = {
          jobId: jobRef.id,
          date: Timestamp.fromDate(new Date(current)),
          status: hasAssignments ? 'assigned' : 'scheduled',
          drivers: selectedDrivers,
          helpers: selectedHelpers,
          trucks: selectedTrucks,
          trailers: selectedTrailers,
          assets: selectedAssets,
          assetQtys: buildSavedAssetQtys(selectedAssets, DispatchState.pendingAssetQtys, DispatchState.assets), // TASK-9510
          assetDirections: (() => { const d = {}; selectedAssets.forEach(id => { const dir = DispatchState.pendingAssetDirections[id]; if (dir === 'return') d[id] = 'return'; }); return d; })(),
          createdAt: Timestamp.now()
        };
        if (Object.keys(jobLineItemSelections).length > 0) {
          jobDayData.lineItemSelections = jobLineItemSelections;
        }
        if (jobSelectedContractId) {
          jobDayData.selectedContractId = jobSelectedContractId;
        }
        if (jobSigSettings) {
          jobDayData.fieldTicketSettings = jobSigSettings; // TASK-395 render hint
        }
        // TASK-9258 — which template, what it actually resolved to at apply
        // time, and which of its assets nobody ever checked.
        if (_jobTplFields) Object.assign(jobDayData, _jobTplFields);
        Object.assign(jobDayData, _newDayFlags); // TASK-9750
        // TASK-9392 — provenance on every row a customer request causes (9260).
        if (_crCtx) {
          Object.assign(jobDayData, _crCtx.jobStamp);
          _crDayWrites.push([jobDayRef, jobDayData]);
        } else if (_cdCtx) {
          _crDayWrites.push([jobDayRef, jobDayData]); // TASK-9587 — same atomic write
        } else {
          jobDayBatch.set(jobDayRef, jobDayData);
        }
        _createdJobDayRefs.push(jobDayRef); // TASK-9363 r2 — a refused carried rental comes back off these
        jobDayCount++;
        current = new Date(current.getTime() + dayMs);
      }

      if (_crCtx) {
        // TASK-9392 — job + every jobDay in ONE transaction, gated on the draft.
        try {
          await _commitCustomerRequestJobAtomically(_crCtx, jobRef, jobData, _crDayWrites);
        } catch (crErr) {
          _bailOutOfSave();
          if (crErr?.code === 'cr-job-exists') {
            window.showToast?.('This customer request is already on the board — nothing new was saved.', 'warning', null, 7000);
            closeModal('job');
            _linkCustomerRequestAfterSave(_crCtx.draftId, _crCtx.jobId);
          } else if (crErr?.code === 'cr-draft-not-ready') {
            window.showToast?.(crErr.message, 'warning', null, 9000);
            closeModal('job');
          } else {
            console.error('[Dispatch] customer-request job save failed:', crErr);
            window.logError?.(crErr, { module: 'dispatch', phase: 'cr-job-atomic', jobId: jobRef.id, expectedDays: jobDayCount });
            window.showToast?.('Could not save this job — nothing was saved. Try again.', 'error');
          }
          return;
        }
        await _afterJobDocCreated();
      } else if (_cdCtx) {
        // TASK-9587 — job + every jobDay in ONE create-only transaction.
        try {
          await _commitClaudeDraftJobAtomically(jobRef, jobData, _crDayWrites);
          _cdHeldClaim = null;
          // The job is on the board: mark Claude's request done NOW (not
          // awaited), before the project link / inventory / permit steps that
          // can throw. If it fails, re-opening the link or a later claim
          // finds the job and finishes it (COD r3).
          _finishClaudeDraftAfterSave(_cdCtx.proposalId, jobRef.id);
        } catch (cdErr) {
          _cdHeldClaim = null;
          _bailOutOfSave();
          if (cdErr?.code === 'cd-job-exists') {
            window.showToast?.('This Claude request is already on the board — nothing new was saved.', 'warning', null, 7000);
            closeModal('job');
            _finishClaudeDraftAfterSave(_cdCtx.proposalId, jobRef.id);
          } else {
            _releaseClaudeDraft(_cdCtx.proposalId, _cdClaimId);
            console.error('[Dispatch] Claude-request job save failed:', cdErr);
            window.logError?.(cdErr, { module: 'dispatch', phase: 'cd-job-atomic', jobId: jobRef.id, expectedDays: jobDayCount });
            window.showToast?.(cdErr?.code === 'cd-too-many-days' ? cdErr.message : 'Could not save this job — nothing was saved. Try again.', 'error');
          }
          return;
        }
        await _afterJobDocCreated();
      } else try {
        await jobDayBatch.commit();
      } catch (batchErr) {
        console.error('[Dispatch] JobDay batch failed:', batchErr);
        window.logError(batchErr, { module: 'dispatch', phase: 'jobday-batch', jobId: jobRef.id, expectedDays: jobDayCount });
        window.logOperation('dispatch', 'JOB_CREATE', { status: 'error', phase: 'jobday-batch', jobId: jobRef.id, expectedDays: jobDayCount, error: batchErr, durationMs: Date.now() - _opStart });
        window.showToast('Job created but schedule failed — please edit the job to retry', 'error');
        closeModal('job');
        return;
      }
      // TASK-9289 COD r1 — the ONE gated path, create side. The create payload
      // carried no projectId; the link is made here, through
      // ProjectsModule.changeJobProject (customer check; expected previous
      // project = none, enforced in a transaction). newJob: a job created in this
      // save has no tickets, so there is no billed-ticket read. Runs BEFORE the
      // customer-request approval below, which reports a project mismatch off the
      // job it reads. Intent was already validated upstream (TASK-731).
      if (_projectCreateNew) {
        phase = 'jobs-create-project';
        if (!window.ProjectsModule?.createProject) {
          console.warn('[Dispatch] ProjectsModule.createProject unavailable — cannot create project from job.');
          window.showToast('Job created but project module is unavailable — create the project manually.', 'error');
        } else {
          try {
            const npName = document.getElementById('job-new-project-name')?.value?.trim();
            const npStartVal = document.getElementById('job-new-project-start')?.value;
            const npEndVal = document.getElementById('job-new-project-end')?.value;
            const npLocation = document.getElementById('job-new-project-location')?.value?.trim() || '';
            // TASK-4782 (COD Blast-Radius) — a project's standing PO inherits onto
            // every job in the corridor (projects.js PROJECT_PO_INHERITED), so it is a
            // job.poNumber writer at one remove. Route it through the SAME normalizer
            // the job form uses.
            const npPo = normalizePoForSave(document.getElementById('job-new-project-po')?.value);
            const npDesc = document.getElementById('job-new-project-description')?.value?.trim() || '';
            const custId = jobData.customerId || null;
            const custName = jobData.customer || '';
            const newProjectId = await window.ProjectsModule.createProject({
              name: npName,
              customerIds: custId ? [custId] : [],
              primaryCustomerId: custId,
              primaryCustomerName: custName,
              startDate: npStartVal ? Timestamp.fromDate(new Date(npStartVal + 'T12:00:00')) : Timestamp.now(),
              endDate: npEndVal ? Timestamp.fromDate(new Date(npEndVal + 'T12:00:00')) : null,
              location: npLocation,
              ..._newProjectPinFields(npLocation, jobData), // TASK-10013 — the stop's pin, when it names the same place
              poNumber: npPo,
              description: npDesc,
              tags: [],
              estimatedRevenue: null,
              onSiteContacts: []
            });
            if (newProjectId) {
              await _saveJobChangeProject(jobRef.id, newProjectId, null, 'job-create-new-project', { newJob: true });
            } else {
              // createProject already toasts its own failure reason on every
              // null-return path, so log here only — a second toast double-ups (COD Blast).
              console.warn('[Dispatch] createProject returned null — project not created/linked.');
            }
          } catch (npErr) {
            console.warn('[Dispatch] Inline project creation failed:', npErr);
            window.logError?.(npErr, { module: 'dispatch', operation: 'job-create-project', jobId: jobRef.id });
            window.showToast('Job created but project creation failed', 'error');
          }
        }
      } else if (_projectTarget) {
        phase = 'jobs-project-link';
        await _saveJobChangeProject(jobRef.id, _projectTarget, null, 'job-create',
          _projectAdoptCustomer ? { newJob: true, adoptCustomer: true, adoptFor: _projectAdoptCustomer } : { newJob: true });
      }

      // TASK-9392 — the work is on the board (job + jobDays), so the request
      // can be marked approved. Not awaited: the rest of the save (inventory,
      // photos, permits) must not wait on it, and a failure leaves the request
      // pending with a retry path (Add to dispatch finds the saved job).
      if (_crCtx) _linkCustomerRequestAfterSave(_crCtx.draftId, jobRef.id);
      // TASK-9587 — Claude's request is marked done right after the atomic
      // commit above (not here: a later step that throws must not skip it).

      // Deduct inventory for quantity-tracked assets (only once, not per jobDay)
      //
      // TASK-4681 — this lane used to deduct with an updateDoc and THEN write the
      // ledger row with a separate addDoc. Two sequential awaits, no batch: if the
      // addDoc threw, the stock was already down and no assetTransactions row
      // existed to explain it. It also wrote `partNumber: asset.partNumber` and
      // `assetName: asset.name` RAW, and addDoc rejects an undefined field — 49 of
      // 152 live assets carry no partNumber, so one consumable import away this
      // throws AFTER the quantity moved. That is exactly the defect TASK-4656
      // fixed in saveAssignment.
      //
      // 🔴 And this is the lane that actually runs. Measured across all 86 tenants:
      // saveAssignment has written 0 of 243 live assetTransactions rows; saveJob
      // carries 7 of 7 consumable rows and all 206 rental rows. The earlier fix
      // landed on the path nobody uses.
      //
      // Mirrors saveAssignment (dispatch.js ~:12347) exactly: collect first, refuse
      // over one atomic batch rather than chunking, one writeBatch per save, and
      // mutate the local cache only after the commit lands.
      phase = 'assets-inventory-update';
      const _jobConsumableAdds = [];
      for (const assetId of selectedAssets) {
        const asset = DispatchState.assets.find(a => a.id === assetId);
        // TASK-9720 — skip a stock-tracked item (server ledger only; see saveAssignment).
        if (asset && asset.consumable && !asset.stockTracked) {
          const qty = DispatchState.pendingAssetQtys[assetId] || 1;
          const prevQty = asset.quantity || 0;
          const newQty = prevQty - qty;
          if (newQty < 0) {
            window.showToast(`Warning: ${asset.name} will go to ${newQty} (negative inventory)`, 'warning');
          }
          _jobConsumableAdds.push({ assetId, asset, qty, prevQty, newQty });
        }
      }
      // The cap was already enforced pre-flight, before the first write. This is
      // a backstop for a future edit that moves the collector without moving the
      // pre-flight with it: it must never fire in practice, so it logs loudly
      // rather than quietly declining, and it still refuses to chunk. Chunking
      // would keep large saves working by silently reintroducing the defect —
      // chunk 1 commits, chunk 2 throws, stock has moved with no ledger.
      if (_jobConsumableAdds.length > _JOB_INV_MAX) {
        console.error('[Dispatch] TASK-4681: consumable count exceeded the cap AFTER the pre-flight passed — the pre-flight and the collector have drifted', { count: _jobConsumableAdds.length });
        window.logError?.(new Error('TASK-4681 inventory pre-flight drift'), { module: 'dispatch', operation: 'assets-inventory-update', count: _jobConsumableAdds.length });
        window.showToast(
          `Too many consumable items (${_jobConsumableAdds.length}, max ${_JOB_INV_MAX}) — inventory was NOT deducted. Deduct them from the board.`,
          'error');
      } else if (_jobConsumableAdds.length > 0) {
        // TASK-10067 — one transaction re-reads each asset and deducts from the
        // STORED count (a cached absolute write re-added org-transferred units);
        // deduct + ledger row still land together. Cache updated after commit.
        await _deductConsumablesTx(DispatchState.tenantId, _jobConsumableAdds, (it) => ({
          assetId: it.assetId,
          assetName: it.asset.name || it.asset.partNumber || '',
          partNumber: it.asset.partNumber || '',
          type: 'dispatch', quantity: it.qty,
          // TASK-9575 slice D — no unitCost / totalCost on a ledger row: every driver session reads this collection; cost lives in assetCosts (TASK-9730 owns the real cost stamp).
          jobId: jobRef.id, note: 'Dispatched with new job',
          issuedBy: window.AppState?.user?.email || 'unknown',
          createdAt: Timestamp.now(),
          // TASK-9258 — the after-the-fact half of the mitigation. `{}` when no
          // template was applied, so the ledger shape is unchanged for every
          // hand-built job. `templateConfirmed: false` is the fact a biller
          // cannot get any other way: this quantity came from a template and
          // nobody touched the row before it saved.
          ...jobTemplateAssetStamp(DispatchState.templateApplied, DispatchState.templateUnconfirmed, it.assetId)
        }));
      }
      // Deploy/return inventory for rental assets (only once, not per jobDay)
      phase = 'assets-rental-update';
      // TASK-10006 — a return job typed above what its project has out saves
      // (the field wins) and says so once.
      const _retOver = _returnJobOverTyped(DispatchState.returnJobCtx, selectedAssets,
        DispatchState.pendingAssetDirections, DispatchState.pendingAssetQtys,
        (id) => { const a = DispatchState.assets.find(x => x.id === id); return (a && (a.name || a.partNumber)) || id; });
      DispatchState.returnJobCtx = null;
      _s3ReturnPrompt = { jobId: jobRef.id, out: _rentalsGoingOut(selectedAssets, DispatchState.pendingAssetDirections, DispatchState.pendingAssetQtys, DispatchState.assets) };
      if (_retOver.length) window.showToast?.(`Saved. Returning more than this project has out: ${_retOver.join('; ')}. The extra comes off the yard count when it is picked up.`, 'warning', null, 9000);
      const jobStartDate = document.getElementById('job-start-date')?.value || formatDateISO(new Date());
      const jobEndDate = document.getElementById('job-end-date')?.value || jobStartDate;
      for (const assetId of selectedAssets) {
        const asset = DispatchState.assets.find(a => a.id === assetId);
        const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
        if (!isRental) continue;

        const direction = DispatchState.pendingAssetDirections[assetId] || 'deploy';
        const qty = DispatchState.pendingAssetQtys[assetId] || 1;
        const isQty = asset.trackingMode === 'quantity';

        // TASK-9336 — the job was just created WITH a ledger, so the stock move
        // and the ledger entry commit together. A return inspection opened here
        // is job-level (jobDayId null): no single day of the range owns it.
        // null = no ledger on the doc (cannot happen on this branch, but the
        // pre-ledger code below stays as the fallback rather than a throw).
        const _guardMode = (direction === 'deploy' && isQty && _copyRentalGuard) ? (_copyRentalGuard.modes.get(assetId) || null) : null;
        // TASK-10015 — a removal created for a LATER day (the job's first day)
        // holds its pickup as planned; the units stay out until that day.
        const _led = await _rentalMoveLedgerTx(DispatchState.tenantId, asset, direction, qty, jobRef.id, false, null,
          _guardMode ? { carryGuardSourceJobId: _copyRentalGuard.sourceJobId, carryGuardMode: _guardMode } : { pickupDateKey: jobStartDate });
        if (_led && _led.deferred) {
          await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'assetTransactions'), {
            assetId, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '',
            type: 'pickup-planned', direction: 'pickup-planned', quantity: 0, plannedQty: _led.deferred.qty,
            pickupDate: _led.deferred.dateKey, jobId: jobRef.id, jobDate: jobStartDate, jobEndDate,
            note: `Pickup of ${_led.deferred.qty} planned for ${_led.deferred.dateKey} — stays out until then (with job)`,
            issuedBy: window.AppState?.user?.email || 'unknown', createdAt: Timestamp.now()
          });
          continue;
        }
        if (_led && _led.refused) {
          // TASK-9363 r3 — BACKSTOP only: the pre-check passed and the stock moved
          // before the deploy. Nothing moved here; take it off the days just
          // written in one atomic batch, with a blocking Retry on failure.
          // r4: pause the 25s watchdog across the Retry dialog (TASK-4567
          // pattern) — the in-flight lock stays set, so Save stays blocked.
          const _undoOk = await _takeRefusedRentalOffDays(_createdJobDayRefs, assetId, asset.name || asset.partNumber || assetId, {
            onPause: () => { clearTimeout(_saveJobSafetyTimer); },
            onResume: () => { _saveJobSafetyTimer = _armSaveJobSafetyTimer(); }
          });
          if (!_undoOk) _copyRentalUndoFailed.push(asset.name || asset.partNumber || assetId);
          if (_s3ReturnPrompt && _s3ReturnPrompt.out) delete _s3ReturnPrompt.out[assetId]; // TASK-10006 S3 — it did not go out
          // TASK-9396 — 'unit-out': a single-tracked unit another job took between
          // the pre-check and this deploy. Named by that job, no "add more" hint.
          if (_led.refused === 'unit-out') {
            _copyRentalRefused.push(_rentalUnitRefusedMsg(asset.name || asset.partNumber || assetId, _led.heldByJobId));
            _unitMoveOffers.push({ assetId, name: asset.name || asset.partNumber || assetId, heldByJobId: _led.heldByJobId });
            continue;
          }
          _copyRentalQtyRefused = true;
          _copyRentalRefused.push(_led.refused === 'source-out'
            ? `${asset.name || asset.partNumber || assetId} — still out on ${_copyRentalGuard.sourceLabel}`
            : `${asset.name || asset.partNumber || assetId} — the yard no longer has ${qty}`);
          continue;
        }
        if (_led) {
          if (direction === 'deploy' && isQty) asset.deployedQty = (asset.deployedQty || 0) + qty;
          if (_led.deployedQty != null) asset.deployedQty = _led.deployedQty;
          if (_led.deploymentStatus) asset.deploymentStatus = _led.deploymentStatus;
        } else if (direction === 'deploy') {
          if (isQty) {
            await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'assets', assetId), {
              deployedQty: increment(qty), updatedAt: Timestamp.now()
            });
            asset.deployedQty = (asset.deployedQty || 0) + qty;
          } else {
            await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'assets', assetId), {
              deploymentStatus: 'deployed', deployedToJobId: jobRef.id, updatedAt: Timestamp.now()
            });
            asset.deploymentStatus = 'deployed';
          }
        } else {
          // Return — if inspection required, DON'T update inventory yet (inspection form handles it)
          if (asset.requireReturnInspection) {
            await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'returnInspections'), {
              assetId, assetName: asset.name || asset.partNumber || '',
              jobId: jobRef.id,
              expectedQty: isQty ? qty : 1,
              status: 'pending',
              createdAt: Timestamp.now()
            });
          } else {
            // No inspection required — update inventory immediately
            if (isQty) {
              // TASK-1255 — transactional, PERSISTED zero-floor (was fire-and-forget increment(-qty)).
              asset.deployedQty = await decrementDeployedQtyFloored(DispatchState.tenantId, assetId, qty);
            } else {
              await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'assets', assetId), {
                deploymentStatus: 'available', deployedToJobId: null, updatedAt: Timestamp.now()
              });
              asset.deploymentStatus = 'available';
            }
          }
        }

        // Log transaction with job date and yard info for history
        await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'assetTransactions'), {
          assetId, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '',
          type: direction, direction, quantity: isQty ? qty : 1,
          jobId: jobRef.id, jobDate: jobStartDate, jobEndDate,
          fromYardId: asset.yardId || '', fromYardName: asset.yardName || '',
          note: `${direction === 'deploy' ? 'Deployed' : 'Return scheduled'}${asset.requireReturnInspection && direction === 'return' ? ' (pending inspection)' : ''} with job`,
          issuedBy: window.AppState?.user?.email || 'unknown',
          createdAt: Timestamp.now(),
          // TASK-9258 — a RENTAL bills as a PERIOD, so an over-deployed unit is a
          // longer-lived error than an over-counted consumable. This is the row
          // that makes "template-placed, never confirmed" answerable for it.
          ...jobTemplateAssetStamp(DispatchState.templateApplied, DispatchState.templateUnconfirmed, assetId),
          // TASK-4810 — this deploy was confirmed past owned stock.
          ...(direction === 'deploy' ? rentalOverDeployStamp(_overDeploys, assetId) : {})
        });
      }
      if (_copyRentalRefused.length > 0) {
        // TASK-9396 — the "more are going" hint fits a quantity lot the source
        // still holds, never a single-tracked unit another job has.
        window.showToast?.(
          `Job saved, but not deployed: ${_copyRentalRefused.join('; ')}.${_copyRentalQtyRefused ? ' Add it to the job if more are going.' : ' A rental unit stays on the job that has it until it is returned there.'}`,
          'warning', null, 10000
        );
      }
      // TASK-10072 — then the choice: move each held unit here from the job that has it.
      if (_unitMoveOffers.length > 0) {
        _offerRentalUnitMoves(_unitMoveOffers, { jobId: jobRef.id, allDays: true })
          .catch((err) => console.warn('[Rental] TASK-10072 move offer failed', err));
      }

      // TASK-9258 — usage counters, at SAVE and never at apply. Fire-and-forget:
      // the job and its days are already committed and a counter must never fail
      // them. Read the id BEFORE the state reset two lines down.
      bumpJobTemplateUse(DispatchState.templateApplied);

      DispatchState.pendingAssetQtys = {};
      DispatchState.pendingAssetDirections = {};
      // TASK-9258 — same clear, same reason: the template is spent once its
      // provenance is on the jobDays.
      DispatchState.templateApplied = null;
      DispatchState.templateUnconfirmed = [];

      // Save waypoints to tenant-level collection
      phase = 'waypoints-create';
      for (const wp of waypoints) {
        await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'waypoints'), {
          name: wp.name,
          url: wp.url,
          lat: wp.lat || null,
          lng: wp.lng || null,
          jobId: jobRef.id,
          createdAt: Timestamp.now()
        });
      }
      // TASK-4783 — fold the rows we just wrote into the cached place index
      // rather than dropping it (a re-read costs the tenant's whole waypoint
      // collection). Falls back to a plain invalidate when there is no cache.
      _ppNoteSavedWaypoints(waypoints, jobRef.id, jobData.customerId);

      // Upload photos if any (non-fatal — job + schedule already saved)
      let photoUploadFails = 0;
      if (DispatchState.pendingJobPhotos.length > 0) {
        try {
          const photos = await uploadJobPhotos(DispatchState.tenantId, jobRef.id);
          photoUploadFails = DispatchState._lastUploadFailCount || 0;
          await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobRef.id), {
            photos: photos,
            photoCount: photos.length
          });
          // JOB_MEDIA_SAVE — track media attach so we can diagnose array-overwrite + async-upload races
          try {
            window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
              source: 'dispatch-form-create',
              jobId: jobRef.id, mediaType: 'photo',
              count: photos.length,
              before: { photoCount: 0 },
              after: { photoCount: photos.length },
              redFlags: { uploadFailures: photoUploadFails, photoCountDrop: false, wipedAll: false }
            });
          } catch (_) { /* fire-and-forget */ }
          _auditJobFilesAdded(jobRef.id, 'job_photos', photos); // TASK-9958
        } catch (photoErr) {
          console.error('[Dispatch] Photo upload failed:', photoErr);
          window.logError(photoErr, { module: 'dispatch', phase: 'photo-upload', jobId: jobRef.id });
          window.showToast('Job saved but photos failed to upload', 'error');
        }
      }

      // Upload permits if any (non-fatal)
      let permitUploadFails = 0;
      let permitUploadOk = 0;
      if (DispatchState.pendingPermitFiles.length > 0) {
        try {
          // TASK-9151 — DISARM the 25s hang detector across the permit upload.
          // A batch of permits on LTE legitimately runs past 25s; leaving the
          // timer armed made it fire mid-upload, toast a false "Save is taking
          // too long" and RE-ENABLE Save on a job that was already written.
          // Same pause/re-arm shape as the mid-save confirms at :9877 / :10158.
          clearTimeout(_saveJobSafetyTimer);
          const permits = await uploadPermitFiles(DispatchState.tenantId, jobRef.id);
          permitUploadFails = DispatchState._lastUploadFailCount || 0;
          permitUploadOk = permits.length;
          // 🔴 TASK-9151 — park BEFORE the write, clear only after it commits.
          // uploadPermitFiles releases a blob the instant its bytes land, so
          // between these two lines the buffer is the ONLY handle on those files:
          // without it a thrown updateDoc leaves them in Storage with no job
          // pointing at them, and the next Save rewrites `permits` WITHOUT them.
          _parkPermitsForAttach(jobRef.id, permits);
          // 🔴 From the PARK BUFFER, not from `permits`. If an earlier attempt's
          // write threw, its rows are in Storage and in the buffer but on no job —
          // building this array from just the newest upload leaves them out, and
          // the clear below then abandons them. The buffer is every row that has
          // landed and is still unattached, which is exactly what belongs here.
          const allPermits = [...DispatchState.existingPermitFiles, ...(DispatchState._permitPendingAttach || [])];
          await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobRef.id), {
            permits: allPermits,
            permitCount: allPermits.length
          });
          // 🔴 Re-arm only AFTER the attach lands, never between the upload and the
          // write. A write that stalls past 25s with the timer running clears
          // __saveJobInFlight and re-enables Save — on the create path `#job-id` is
          // still empty and the submit lock is not set until further down, so the
          // next press creates a SECOND JOB. The pause has to cover the attach.
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
          _auditJobFilesAdded(jobRef.id, 'job_permits', DispatchState._permitPendingAttach); // TASK-9958 — committed above
          DispatchState._permitPendingAttach = [];
          // TASK-9151 — what is ON THE JOB is now allPermits, so say so. This array
          // used to go stale the moment a save uploaded anything, which only did not
          // bite because the modal always closed straight afterwards. It no longer
          // always closes, and a second write built from a stale copy drops every
          // permit the first write added.
          DispatchState.existingPermitFiles = allPermits;
          DispatchState.loadedPermitCount = allPermits.length;
          // JOB_MEDIA_SAVE — same instrumentation for permits (the closest mirror to
          // yesterday's waypoint bug — array overwrite + async race).
          try {
            window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
              source: 'dispatch-form-create',
              jobId: jobRef.id, mediaType: 'permit',
              count: allPermits.length,
              before: { permitCount: 0 },
              after: { permitCount: allPermits.length },
              redFlags: { uploadFailures: permitUploadFails, permitCountDrop: false, wipedAll: false }
            });
          } catch (_) { /* fire-and-forget */ }

          // Trigger AI parsing if AI checked + hasAiPack + has image permits
          const aiParseChecked = document.getElementById('permit-ai-parse')?.checked;
          if (aiParseChecked && window.hasAiPack?.()) {
            const imagePermits = allPermits.filter(p => p.mimeType?.startsWith('image/'));
            if (imagePermits.length > 0) {
              // TASK-9585 S7 — a SUGGESTION the user confirms; not awaited.
              _offerPermitCostSuggestion(jobRef.id, imagePermits);
            }
          }
        } catch (permitErr) {
          // The save continues past this non-fatal block, so the detector has to
          // come back on even when the attach threw before re-arming it above.
          clearTimeout(_saveJobSafetyTimer);
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
          console.error('[Dispatch] Permit upload failed:', permitErr);
          window.logError(permitErr, { module: 'dispatch', phase: 'permit-upload', jobId: jobRef.id });
          // The banner knows which files and what state they are in, and it does
          // not time out on top of the Retry button. Toast only when there is
          // nothing unresolved for it to describe.
          if (hasFailedPermitUploads()) {
            _syncPermitFailureBanner();
          } else {
            window.showToast('Job saved but permits failed to upload', 'error');
          }
        }
      } else if (DispatchState.existingPermitFiles.length > 0) {
        // TASK-9212 — CARRIED permits, no new uploads. Copy to New Job pre-fills
        // existingPermitFiles with clones of the source job's rows, and every
        // branch above is gated on pendingPermitFiles — so without this the copy
        // showed its permits in the form, saved cleanly, and opened with none.
        //
        // Nothing is uploaded here: the rows already point at Storage objects the
        // source job put there, and this tenant can read the whole
        // tenants/{tid}/job-permits/** prefix. Both jobs naming one object is the
        // intent — removing a permit from either side is an array edit, not a
        // delete, so neither can break the other's copy.
        //
        // permitCount is written alongside permits everywhere else; a job whose
        // count disagrees with its array reads as "permits vanished" to anyone
        // auditing the media rows.
        try {
          phase = 'jobs-media-carry';
          const carried = DispatchState.existingPermitFiles.map(p => ({ ...p }));
          // 🔴 DISARM the 25s hang detector across the attach, exactly as the
          // upload branch above does — and for a worse reason than slowness. On
          // this path `#job-id` is still empty and the submit lock is not set
          // yet, so a timer that fires mid-write clears __saveJobInFlight and
          // RE-ENABLES Save on a job that is already written: the next press
          // creates a SECOND JOB. One updateDoc is fast until it isn't (COD Code
          // Reviewer, round 3). Re-armed only after the write lands, never
          // between it and the job create.
          clearTimeout(_saveJobSafetyTimer);
          await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobRef.id), {
            permits: carried,
            permitCount: carried.length
          });
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
          DispatchState.loadedPermitCount = carried.length;
          permitUploadOk = carried.length;
          try {
            window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
              source: 'dispatch-form-create-carried',
              jobId: jobRef.id, mediaType: 'permit',
              count: carried.length,
              before: { permitCount: 0 },
              after: { permitCount: carried.length },
              redFlags: { uploadFailures: 0, permitCountDrop: false, wipedAll: false }
            });
          } catch (_) { /* fire-and-forget */ }
        } catch (carryErr) {
          // The save continues past this non-fatal block, so the detector has to
          // come back on even when the attach threw before re-arming it above —
          // same shape as the upload branch's catch.
          clearTimeout(_saveJobSafetyTimer);
          _saveJobSafetyTimer = _armSaveJobSafetyTimer();
          // Non-fatal, same as every other media lane on this path: the JOB is
          // written and is the thing the dispatcher asked for. Say what is
          // missing so it is re-attachable rather than silently absent.
          console.error('[Dispatch] Carried permit attach failed:', carryErr);
          window.logError(carryErr, { module: 'dispatch', phase: 'permit-carry', jobId: jobRef.id });
          window.showToast('Job created but the copied permits did not attach — reopen the job to add them', 'error');
        }
      }

      // Log successful operation
      window.logOperation('dispatch', 'JOB_CREATE', {
        status: 'success', jobId: jobRef.id, jobDayCount,
        photoCount: DispatchState.pendingJobPhotos.length - photoUploadFails,
        // TASK-9151 — this used to read pendingPermitFiles.length AFTER the
        // upload, and the old uploadPermitFiles cleared that array to [] on its
        // way out — so this field logged 0 - failCount, i.e. a NEGATIVE permit
        // count on every partial failure. The tray no longer self-clears, which
        // would make it wrong in the other direction, so count what landed.
        permitCount: permitUploadOk,
        photoFails: photoUploadFails, permitFails: permitUploadFails,
        customer: jobData.customer, title: jobData.title,
        durationMs: Date.now() - _opStart
      });

      // TASK-962 (S2): show the created weekday+date (range for multi-day) so a
      // wrong-day save is caught at the toast, not by manual re-check.
      const _createdMsg = `Job created: ${jobData.title || 'Untitled'} — ${formatJobToastDates(startDateVal, endDateVal)}${jobDayCount > 1 ? ` (${jobDayCount} days)` : ''}`;
      // TASK-9112 — when the job landed off the visible board, say so and offer
      // the jump. Unchanged toast otherwise.
      const _offBoard = decorateOffBoardSaveToast(_createdMsg, startDateVal, endDateVal, _boardRangeAtSave);
      // TASK-9151 — when a permit failed, the modal is staying open with a banner
      // that already opens with "The job is saved." and carries the Retry button.
      // Measured at a 390px viewport: the success toast lands ON TOP of that
      // button. A toast that covers the recovery action is worse than no toast,
      // and it is saying the same thing twice.
      if (hasFailedPermitUploads()) {
        // nothing — the pinned banner is the notification, and it does not vanish.
        // TASK-1617's geocode warning would land in the same strip (an 8s warning
        // toast on top of the Retry button), so it goes INTO the banner instead of
        // being dropped — the whole failure path speaks through one surface.
        _syncPermitFailureBanner(_geocodeWarning || null);
      } else if (_copyRentalUndoFailed.length > 0) {
        // TASK-9363 r4 — NOT a success: the days list a rental that did not
        // deploy (the sticky error from _takeRefusedRentalOffDays names it).
        // A green "Job created" toast here would bury that.
        window.showToast(`Job created with a problem: ${_copyRentalUndoFailed.join(', ')} is listed on its days but was not deployed. Fix it before billing.`, 'error', null, Infinity);
      } else {
        if (_offBoard) {
          window.showToast(_offBoard.message, 'success', _offBoard.action, _offBoard.durationMs);
        } else {
          window.showToast(_createdMsg, 'success');
        }
        // TASK-1617 — same as the update path: surface any stop with no location.
        if (_geocodeWarning) setTimeout(() => window.showToast?.(_geocodeWarning, 'warning'), 400);
      }
      // TASK-398: flush the breadcrumb trail for this create (one telemetry doc,
      // 90-day TTL), anchored to the new job so support can replay the steps.
      window.ActionLog?.flush?.('save', { jobId: jobRef.id });

      // TASK-9289 COD r1 — the project link that used to sit here now runs as
      // soon as the job and its days exist (search "the ONE gated path"), before
      // the customer-request approval reads the job's project.

      // Dispatch event for tutorial integration
      window.dispatchEvent(new CustomEvent('dispatch:job-saved', {
        detail: { jobId: jobRef.id }
      }));
    }

    // For edits: handle waypoints + photos after update
    if (jobId) {
      // Save waypoints to tenant-level collection
      // TASK-9464 — ONLY stops that are new or changed versus the pre-edit job.
      // This loop used to addDoc EVERY stop on EVERY edit, so a job saved five
      // times carried five copies of each stop (TITS 2026-09-29: 1,938 of 3,297
      // rows were exact duplicates, pushing real older places past the
      // past-places picker's PAST_PLACE_SCAN_LIMIT).
      //
      // COD r1 (Code Reviewer + Independent, 2026-09-29): diff against the rows
      // ACTUALLY LOGGED for this job, not the pre-edit job doc. The job's
      // waypoints[] is committed before this loop runs, so a row whose addDoc
      // failed last time — or a stop another writer (MCP update_job, AI
      // builder, import) put on the job without logging it — would otherwise
      // look "already there" forever. Keyed on the log, a re-save backfills it.
      // One equality query per edit; if it fails, fall back to the job doc.
      phase = 'waypoints-read';
      let _loggedRows = null;
      try {
        const _wpSnap = await getDocs(query(
          collection(db, 'tenants', DispatchState.tenantId, 'waypoints'),
          where('jobId', '==', jobId)
        ));
        _loggedRows = _wpSnap.docs.map(d => d.data());
      } catch (_wpReadErr) {
        console.warn('[Dispatch] TASK-9464 logged-waypoint read failed — diffing against the pre-edit job instead', _wpReadErr?.code || _wpReadErr);
      }
      phase = 'waypoints-create';
      const _newStops = waypointsNewOrChanged(waypoints, _loggedRows || _preEditWaypoints);
      for (const wp of _newStops) {
        await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'waypoints'), {
          name: wp.name,
          url: wp.url,
          lat: wp.lat || null,
          lng: wp.lng || null,
          jobId: jobId,
          createdAt: Timestamp.now()
        });
      }
      // TASK-4783 — same as the create path: extend the cached index, don't
      // throw the whole waypoint collection away and re-read it. Only the rows
      // actually written, or the cache would grow the duplicates we no longer
      // store. Still called with an empty list: it also re-maps the job's
      // customer, which an edit may have changed.
      _ppNoteSavedWaypoints(_newStops, jobId, jobData.customerId);

      // Upload new photos if any
      if (DispatchState.pendingJobPhotos.length > 0) {
        phase = 'jobs-media-update';
        const newPhotos = await uploadJobPhotos(DispatchState.tenantId, jobId);
        const allPhotos = [...DispatchState.existingJobPhotos, ...newPhotos];
        await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId), {
          photos: allPhotos,
          photoCount: allPhotos.length
        });
        _auditJobFilesAdded(jobId, 'job_photos', newPhotos); // TASK-9958
        // JOB_MEDIA_SAVE — edit path. Compare against previously-existing photos
        // to surface the array-overwrite class of bugs (photoCountDrop / wipedAll).
        try {
          const prevPhotoCount = DispatchState.loadedPhotoCount;
          window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
            source: 'dispatch-form-edit',
            jobId, mediaType: 'photo',
            count: allPhotos.length,
            before: { photoCount: prevPhotoCount },
            after: { photoCount: allPhotos.length },
            redFlags: {
              photoCountDrop: allPhotos.length < prevPhotoCount,
              wipedAll: prevPhotoCount > 0 && allPhotos.length === 0
            }
          });
        } catch (_) { /* fire-and-forget */ }
      } else if (DispatchState.existingJobPhotos.length !== DispatchState.loadedPhotoCount) {
        // Removal-only save. This branch did not exist: the photo write was
        // reachable ONLY when the same save also uploaded a new photo, so
        // deleting a photo on its own never persisted.
        phase = 'jobs-media-update';
        await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId), {
          photos: DispatchState.existingJobPhotos,
          photoCount: DispatchState.existingJobPhotos.length
        });
        try {
          window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
            source: 'dispatch-form-edit-removal',
            jobId, mediaType: 'photo',
            count: DispatchState.existingJobPhotos.length,
            before: { photoCount: DispatchState.loadedPhotoCount },
            after: { photoCount: DispatchState.existingJobPhotos.length },
            redFlags: {
              photoCountDrop: DispatchState.existingJobPhotos.length < DispatchState.loadedPhotoCount,
              wipedAll: DispatchState.loadedPhotoCount > 0 && DispatchState.existingJobPhotos.length === 0,
              removeOnlyPath: true
            }
          });
        } catch (_) { /* fire-and-forget */ }
      }

      // Upload new permits if any (or update existing list if removals occurred)
      if (DispatchState.pendingPermitFiles.length > 0) {
        phase = 'jobs-media-update';
        clearTimeout(_saveJobSafetyTimer);   // TASK-9151 — see the create path
        const newPermits = await uploadPermitFiles(DispatchState.tenantId, jobId);
        _parkPermitsForAttach(jobId, newPermits);   // TASK-9151 — see the create path
        const allPermits = [...DispatchState.existingPermitFiles, ...(DispatchState._permitPendingAttach || [])];
        await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId), {
          permits: allPermits,
          permitCount: allPermits.length
        });
        _saveJobSafetyTimer = _armSaveJobSafetyTimer();   // after the attach — see the create path
        _auditJobFilesAdded(jobId, 'job_permits', DispatchState._permitPendingAttach); // TASK-9958 — committed above
        DispatchState._permitPendingAttach = [];
        // JOB_MEDIA_SAVE — edit path, permits side.
        try {
          const prevPermitCount = DispatchState.loadedPermitCount;   // read BEFORE the refresh below
          window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
            source: 'dispatch-form-edit',
            jobId, mediaType: 'permit',
            count: allPermits.length,
            before: { permitCount: prevPermitCount },
            after: { permitCount: allPermits.length },
            redFlags: {
              permitCountDrop: allPermits.length < prevPermitCount,
              wipedAll: prevPermitCount > 0 && allPermits.length === 0
            }
          });
        } catch (_) { /* fire-and-forget */ }
        // TASK-9151 — refresh AFTER the audit read above, for the same reason as the
        // create path: allPermits is what the job now holds, and the modal no longer
        // always closes straight after a save.
        DispatchState.existingPermitFiles = allPermits;
        DispatchState.loadedPermitCount = allPermits.length;

        // Trigger AI parsing if AI checked + hasAiPack + has image permits
        const aiParseChecked = document.getElementById('permit-ai-parse')?.checked;
        if (aiParseChecked && window.hasAiPack?.()) {
          const imagePermits = allPermits.filter(p => p.mimeType?.startsWith('image/'));
          if (imagePermits.length > 0) {
            // TASK-9585 S7 — a SUGGESTION the user confirms; not awaited.
            _offerPermitCostSuggestion(jobId, imagePermits);
          }
        }
      } else if (DispatchState.existingPermitFiles.length !== DispatchState.loadedPermitCount) {
        // Removal-only save. Gate on "differs from what was loaded", NOT on
        // "> 0" — the old guard skipped the write when the user removed the
        // LAST permit, so the job kept permits[] and the file reappeared on
        // reopen. Comparing against the load-time count also avoids a
        // pointless write on saves where the media did not change.
        phase = 'jobs-media-update';
        await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId), {
          permits: DispatchState.existingPermitFiles,
          permitCount: DispatchState.existingPermitFiles.length
        });
        // JOB_MEDIA_SAVE — pure-removal path. The before-value is the count as
        // LOADED, so permitCountDrop/wipedAll actually mean something.
        try {
          const prevPermitCount = DispatchState.loadedPermitCount;
          window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
            source: 'dispatch-form-edit-removal',
            jobId, mediaType: 'permit',
            count: DispatchState.existingPermitFiles.length,
            before: { permitCount: prevPermitCount },
            after: { permitCount: DispatchState.existingPermitFiles.length },
            redFlags: {
              permitCountDrop: DispatchState.existingPermitFiles.length < prevPermitCount,
              wipedAll: prevPermitCount > 0 && DispatchState.existingPermitFiles.length === 0,
              removeOnlyPath: true
            }
          });
        } catch (_) { /* fire-and-forget */ }
      }
    }

    // TASK-9151 — if a permit failed to upload, the modal STAYS OPEN. The job
    // itself is saved (that already happened above, and the successful permits
    // are on it); what is still in the room is the handful of files that did not
    // make it, their blobs, and a Retry button. Closing here is what used to
    // destroy them — the tray was wiped and the user was left with a red toast
    // naming a count and no files. The banner (_syncPermitFailureBanner) is
    // already on screen saying exactly this, pinned above the footer.
    if (hasFailedPermitUploads()) {
      // TASK-4877 x TASK-9151 — "Add Another Day" was read-and-cleared at the top
      // of this save, so returning here would DROP it in silence on a save that
      // actually succeeded. The job is on disk; the day is still owed. Carry it
      // so retry/discard can honour it when the modal finally closes.
      DispatchState._permitPendingAddDayJobId =
        (_addDayAfterThisSave && _addDayAfterThisSave === jobId) ? jobId : null;
      // 🔴 This form has already written its job. `#job-id` is still empty on the
      // create path, so ANY later submit of this same form runs the create branch
      // again and writes a SECOND job. Parking the button on the tray state alone
      // is not enough — deleting the last failed tile empties the tray and would
      // hand Save straight back. The lock is what the form is, not what the tray
      // holds, and only closing the modal clears it.
      DispatchState._permitModalSubmitLock = true;
      return;
    }
    DispatchState._permitPendingAddDayJobId = null;
    DispatchState._permitModalSubmitLock = false;

    closeModal('job');

    // TASK-4877 — the edits are ON DISK now, so add-day's own closeModal('job')
    // has nothing left to throw away. Success path only, and only for the job
    // whose form the button was pressed on.
    if (_addDayAfterThisSave && _addDayAfterThisSave === jobId) addDayToJob(jobId);
    // TASK-10006 S3 — rentals went out: offer the return job (office only; a
    // save that sent no quantity rental out never asks).
    if (_s3ReturnPrompt && _s3ReturnPrompt.jobId && Object.keys(_s3ReturnPrompt.out || {}).length && !_addDayAfterThisSave) {
      const _p = _s3ReturnPrompt;
      setTimeout(() => _offerReturnJobPrompt(_p.jobId, _p.out), 350);
    }
  } catch (error) {
    // TASK-620: log the LIVE phase (which write step was in flight) + the
    // FirebaseError code so a permission-denied names the failing step instead
    // of phase:'unknown'. Guarded — never let logging throw inside the catch.
    let errCode = 'unknown';
    try { errCode = error?.code || 'unknown'; } catch (_) {}
    console.error(`[Dispatch] Save job error (phase=${phase} code=${errCode}):`, error);
    window.logError(error, { module: 'dispatch', operation: jobId ? 'job-update' : 'job-create', phase, errCode });
    window.logOperation('dispatch', 'JOB_SAVE', { status: 'error', phase, errCode, error, durationMs: typeof _opStart !== 'undefined' ? Date.now() - _opStart : null });
    window.showToast(_projectStepLanded
      ? 'The job’s project/customer change WAS saved, but its other edits were NOT: ' + (error.message || 'unknown error') + ' — reopen the job and save again.'
      : 'Error saving job: ' + (error.message || 'unknown error'), 'error');
    // TASK-9151 — if the throw happened between "permits uploaded" and "permits
    // attached", the toast above fades and the files would be forgotten. Raise
    // the persistent banner so Retry is still reachable.
    if (hasFailedPermitUploads()) _syncPermitFailureBanner();
    _addDayRequestNotHonored(); // TASK-4877
    // TASK-9587 — the save threw after the Claude request was claimed and
    // before the job was written: hand the claim back so a retry can save.
    if (_cdHeldClaim) { _releaseClaudeDraft(_cdHeldClaim.proposalId, _cdHeldClaim.claimId); _cdHeldClaim = null; }
  } finally {
    clearTimeout(_saveJobSafetyTimer);
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = saveBtnOriginal; }
    window.__saveJobInFlight = false;
    // 🔴 TASK-9151 — MUST run after `__saveJobInFlight` is cleared and after the
    // restore above, or it is immediately undone. When a permit failed the modal
    // stays open, and on the CREATE path `#job-id` is still empty — so a second
    // press of Save would create a DUPLICATE JOB rather than re-saving the one
    // that was just written. Park the button on the banner's Retry/Discard until
    // the tray is resolved; both of those close the modal.
    //
    // 🔴 And the LOCK is set here, not only at the early return, because a THROWN
    // save skips that return entirely. An attach write that rejects leaves rows
    // parked and unattached; without this the next Save runs unlocked and writes
    // the job without them.
    if (hasFailedPermitUploads()) DispatchState._permitModalSubmitLock = true;
    _paintPermitSaveButton(saveBtnOriginal);
  }
}

function showSafetyHoldDetails(holdId) {
  const hold = DispatchState.safetyHolds.find(h => h.id === holdId);
  if (!hold) return;

  // TASK-9198 — same trucks-only lookup bug as the board card: this modal read
  // getTruckUnitById and printed "Unknown" for every trailer/asset hold.
  const holdLabel = getSafetyHoldLabel(hold);
  const createdDate = hold.createdAt?.toDate?.() || new Date(hold.createdAt);
  const dateStr = createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const unitLabelEl = document.getElementById('hold-detail-unit-label');
  if (unitLabelEl) unitLabelEl.textContent = `${holdLabel.noun}:`;
  document.getElementById('hold-detail-unit').textContent = holdLabel.text === '?' ? 'Unknown' : holdLabel.text;
  document.getElementById('hold-detail-defect').textContent = hold.description || hold.text || 'Critical defect'; // imported orders carry `text`
  // TASK-9198 — pretrip writes `createdBy`, never `reportedBy`, so this line
  // rendered "Unknown" for EVERY DVIR hold, truck or trailer. Ethan Young's name
  // is on the live TITS doc and the modal would not show it.
  document.getElementById('hold-detail-reporter').textContent = hold.reportedBy || hold.createdBy || 'Unknown';
  document.getElementById('hold-detail-date').textContent = dateStr;

  document.getElementById('modal-safety-hold-details').classList.remove('hidden');
}

// Store current truck list for multi-truck selector
let currentTruckIds = [];

function showTruckInfo(truckIdOrIds) {
  // Handle both single ID (string) and array of IDs
  const truckIds = Array.isArray(truckIdOrIds) ? truckIdOrIds : [truckIdOrIds];
  currentTruckIds = truckIds;

  // Default to first truck
  const truckId = truckIds[0];
  renderTruckInfo(truckId, truckIds);
}

function renderTruckInfo(truckId, allTruckIds = currentTruckIds) {
  const truck = _resolveFleetRef(truckId, DispatchState.trucks);
  if (!truck) {
    window.showToast?.('Truck not found', 'error');
    return;
  }

  // Update title
  const titleEl = document.getElementById('truck-info-title');
  if (allTruckIds.length > 1) {
    titleEl.textContent = `Vehicles (${allTruckIds.length})`;
  } else {
    titleEl.textContent = `Vehicle - ${window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name || 'Unknown')}`;
  }

  const now = new Date();
  let bodyHtml = '';

  // Add truck selector if multiple trucks
  if (allTruckIds.length > 1) {
    bodyHtml += `<div class="truck-selector-tabs">`;
    allTruckIds.forEach(id => {
      const t = _resolveFleetRef(id, DispatchState.trucks);
      const unit = t ? (window.getUnitDisplay?.(t) || 'Unknown') : id;
      const isActive = (t && t.id === truck.id) || id === truckId;
      bodyHtml += `
        <button class="truck-tab ${isActive ? 'active' : ''}" onclick="window.DispatchModule.renderTruckInfo('${escJsAttr(id)}')">
          <span class="material-icons">local_shipping</span> ${escapeHtml(unit)}
        </button>
      `;
    });
    bodyHtml += `</div>`;
  }

  bodyHtml += `<div class="truck-info-sections">`;

  // ====== REGISTRATION SECTION (DOT Required) ======
  // TASK-9814 follow-up: "Doesn't expire" wins over a stale date left on the doc.
  const regNoExp = truck.registrationNoExpiry === true;
  const regExpiry = !regNoExp && truck.registrationExpiry ? new Date(truck.registrationExpiry) : null;
  const regIsExpired = regExpiry && regExpiry < now;
  const regExpiryStr = regNoExp ? 'Doesn’t expire' : regExpiry ? regExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">badge</span> Registration</h4>
      <div class="compliance-status ${regIsExpired ? 'expired' : (regExpiry || regNoExp) ? 'valid' : 'unknown'}">
        <span class="material-icons">${regIsExpired ? 'warning' : (regExpiry || regNoExp) ? 'check_circle' : 'help_outline'}</span>
        <span>${regIsExpired ? 'EXPIRED' : regNoExp ? 'Doesn’t expire' : regExpiry ? 'Valid' : 'No expiry set'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Plate</span>
          <span class="value">${escapeHtml(truck.licensePlate || 'N/A')} ${truck.licensePlateState ? `(${escapeHtml(truck.licensePlateState)})` : ''}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Registration #</span>
          <span class="value">${escapeHtml(truck.registrationNumber || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${regIsExpired ? 'text-danger' : ''}">${regExpiryStr}</span>
        </div>
      </div>
    </div>
  `;

  // ====== INSURANCE SECTION (DOT Required) ======
  const insExpiry = truck.insuranceExpiry ? new Date(truck.insuranceExpiry) : null;
  const insIsExpired = insExpiry && insExpiry < now;
  const insExpiryStr = insExpiry ? insExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">verified_user</span> Insurance</h4>
      <div class="compliance-status ${insIsExpired ? 'expired' : insExpiry ? 'valid' : 'unknown'}">
        <span class="material-icons">${insIsExpired ? 'warning' : insExpiry ? 'check_circle' : 'help_outline'}</span>
        <span>${insIsExpired ? 'EXPIRED' : insExpiry ? 'Valid' : 'No expiry set'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Company</span>
          <span class="value">${escapeHtml(truck.insuranceCompany || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Policy #</span>
          <span class="value">${escapeHtml(truck.insurancePolicy || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${insIsExpired ? 'text-danger' : ''}">${insExpiryStr}</span>
        </div>
        ${truck.insurancePhone ? `
          <div class="truck-info-item full-width">
            <span class="label">Claims Phone</span>
            <span class="value"><a href="tel:${escapeHtml(truck.insurancePhone)}">${escapeHtml(truck.insurancePhone)}</a></span>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // ====== ANNUAL INSPECTION (DOT Required) ======
  const inspExpiry = truck.inspectionExpiry ? new Date(truck.inspectionExpiry) : null;
  const inspIsExpired = inspExpiry && inspExpiry < now;
  const inspExpiryStr = inspExpiry ? inspExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';
  const inspDateStr = truck.inspectionDate ? new Date(truck.inspectionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">fact_check</span> Annual DOT Inspection</h4>
      <div class="compliance-status ${inspIsExpired ? 'expired' : inspExpiry ? 'valid' : 'unknown'}">
        <span class="material-icons">${inspIsExpired ? 'warning' : inspExpiry ? 'check_circle' : 'help_outline'}</span>
        <span>${inspIsExpired ? 'EXPIRED' : inspExpiry ? 'Valid' : 'No inspection on file'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Last Inspection</span>
          <span class="value">${inspDateStr}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${inspIsExpired ? 'text-danger' : ''}">${inspExpiryStr}</span>
        </div>
        ${truck.inspectionLocation ? `
          <div class="truck-info-item full-width">
            <span class="label">Location</span>
            <span class="value">${escapeHtml(truck.inspectionLocation)}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // ====== VEHICLE DETAILS ======
  bodyHtml += `
    <div class="truck-info-section">
      <h4><span class="material-icons">info</span> Vehicle Details</h4>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Unit #</span>
          <span class="value">${escapeHtml(truck.unit || 'N/A')}</span>
        </div>
        ${truck.nickname ? `<div class="truck-info-item"><span class="label">Name</span><span class="value">${escapeHtml(truck.nickname)}</span></div>` : ''}
        <div class="truck-info-item">
          <span class="label">Type</span>
          <span class="value">${escapeHtml((window.getTypeLabel ? window.getTypeLabel(truck.type, 'truckTypes') : capitalizeFirst(truck.type)) || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Make</span>
          <span class="value">${escapeHtml(truck.make || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Model</span>
          <span class="value">${escapeHtml(truck.model || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Year</span>
          <span class="value">${escapeHtml(truck.year || 'N/A')}</span>
        </div>
        <div class="truck-info-item full-width">
          <span class="label">VIN</span>
          <span class="value" style="font-family: monospace; font-size: 0.85rem;">${escapeHtml(truck.vin || 'N/A')}</span>
        </div>
      </div>
    </div>
  `;

  // ====== AXLE SERIALS (if any) ======
  if (truck.axles && truck.axles.length > 0) {
    bodyHtml += `
      <div class="truck-info-section">
        <h4><span class="material-icons">settings</span> Axle Serial Numbers</h4>
        <div class="axle-list">
          ${truck.axles.map((serial, i) => `
            <div class="axle-item">
              <span class="axle-num">Axle ${i + 1}</span>
              <span class="axle-serial">${escapeHtml(serial)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  // ====== RECENT DVIRs / INSPECTIONS ======
  bodyHtml += `
    <div class="truck-info-section">
      <h4><span class="material-icons">assignment_turned_in</span> Recent Inspections (DVIRs)</h4>
      <div id="truck-recent-dvirs" class="recent-dvirs-list">
        <p class="loading-text"><span class="material-icons rotating">sync</span> Loading...</p>
      </div>
    </div>
  `;

  bodyHtml += `</div>`;

  document.getElementById('truck-info-body').innerHTML = bodyHtml;
  document.getElementById('modal-truck-info').classList.remove('hidden');

  // Load recent DVIRs asynchronously
  loadRecentDvirs(truckId);
}

async function loadRecentDvirs(truckId) {
  const container = document.getElementById('truck-recent-dvirs');
  if (!container) return;

  try {
    const tenant = window.AppState?.tenant || 'demo';
    const { collection, query, where, orderBy, limit, getDocs } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
    const { db } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));

    // Query last 10 DVIRs for this truck
    const dvirs = [];
    const q = query(
      collection(db, 'tenants', tenant, 'pretrips'),
      where('truckId', '==', truckId),
      orderBy('createdAt', 'desc'),
      limit(10)
    );

    const snapshot = await getDocs(q);
    snapshot.forEach(doc => dvirs.push({ id: doc.id, ...doc.data() }));

    if (dvirs.length === 0) {
      container.innerHTML = '<p class="no-dvirs">No recent inspections found for this vehicle.</p>';
      return;
    }

    container.innerHTML = dvirs.map(dvir => {
      const date = dvir.createdAt?.toDate?.() || new Date(dvir.createdAt);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const hasDefects = dvir.defects && dvir.defects.length > 0;
      const statusClass = hasDefects ? 'has-defects' : 'passed';
      const statusIcon = hasDefects ? 'warning' : 'check_circle';
      const statusText = hasDefects ? `${dvir.defects.length} defect(s)` : 'Passed';

      return `
        <div class="dvir-item ${statusClass}">
          <div class="dvir-date">${dateStr}</div>
          <div class="dvir-status">
            <span class="material-icons">${statusIcon}</span>
            <span>${statusText}</span>
          </div>
          <div class="dvir-driver">${escapeHtml(dvir.driverName || 'Unknown')}</div>
        </div>
      `;
    }).join('');

  } catch (error) {
    console.error('[Dispatch] Error loading DVIRs:', error);
    container.innerHTML = '<p class="no-dvirs">Could not load inspections.</p>';
  }
}

// Store current trailer list for multi-trailer selector
let currentTrailerIds = [];

function showTrailerInfo(trailerIdOrIds) {
  // Handle both single ID (string) and array of IDs
  const trailerIds = Array.isArray(trailerIdOrIds) ? trailerIdOrIds : [trailerIdOrIds];
  currentTrailerIds = trailerIds;

  // Default to first trailer
  const trailerId = trailerIds[0];
  renderTrailerInfo(trailerId, trailerIds);
}

function renderTrailerInfo(trailerId, allTrailerIds = currentTrailerIds) {
  const trailer = _resolveFleetRef(trailerId, DispatchState.trailers);
  if (!trailer) {
    window.showToast?.('Trailer not found', 'error');
    return;
  }

  // Update title
  const titleEl = document.getElementById('trailer-info-title');
  if (allTrailerIds.length > 1) {
    titleEl.textContent = `Trailers (${allTrailerIds.length})`;
  } else {
    titleEl.textContent = `Trailer - ${window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit || trailer.name || 'Unknown')}`;
  }

  const now = new Date();
  let bodyHtml = '';

  // Add trailer selector if multiple trailers
  if (allTrailerIds.length > 1) {
    bodyHtml += `<div class="truck-selector-tabs">`;
    allTrailerIds.forEach(id => {
      const t = _resolveFleetRef(id, DispatchState.trailers);
      const unit = t ? (window.getUnitDisplay?.(t) || 'Unknown') : id;
      const isActive = (t && t.id === trailer.id) || id === trailerId;
      bodyHtml += `
        <button class="truck-tab ${isActive ? 'active' : ''}" onclick="window.DispatchModule.renderTrailerInfo('${escJsAttr(id)}')">
          <span class="material-icons">rv_hookup</span> ${escapeHtml(unit)}
        </button>
      `;
    });
    bodyHtml += `</div>`;
  }

  bodyHtml += `<div class="truck-info-sections">`;

  // ====== REGISTRATION SECTION (DOT Required) ======
  // TASK-9814 follow-up: "Doesn't expire" wins over a stale date left on the doc.
  const regNoExp = trailer.registrationNoExpiry === true;
  const regExpiry = !regNoExp && trailer.registrationExpiry ? new Date(trailer.registrationExpiry) : null;
  const regIsExpired = regExpiry && regExpiry < now;
  const regExpiryStr = regNoExp ? 'Doesn’t expire' : regExpiry ? regExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">badge</span> Registration</h4>
      <div class="compliance-status ${regIsExpired ? 'expired' : (regExpiry || regNoExp) ? 'valid' : 'unknown'}">
        <span class="material-icons">${regIsExpired ? 'warning' : (regExpiry || regNoExp) ? 'check_circle' : 'help_outline'}</span>
        <span>${regIsExpired ? 'EXPIRED' : regNoExp ? 'Doesn’t expire' : regExpiry ? 'Valid' : 'No expiry set'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Plate</span>
          <span class="value">${escapeHtml(trailer.licensePlate || 'N/A')} ${trailer.licensePlateState ? `(${escapeHtml(trailer.licensePlateState)})` : ''}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Registration #</span>
          <span class="value">${escapeHtml(trailer.registrationNumber || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${regIsExpired ? 'text-danger' : ''}">${regExpiryStr}</span>
        </div>
      </div>
    </div>
  `;

  // ====== INSURANCE SECTION (DOT Required) ======
  const insExpiry = trailer.insuranceExpiry ? new Date(trailer.insuranceExpiry) : null;
  const insIsExpired = insExpiry && insExpiry < now;
  const insExpiryStr = insExpiry ? insExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">verified_user</span> Insurance</h4>
      <div class="compliance-status ${insIsExpired ? 'expired' : insExpiry ? 'valid' : 'unknown'}">
        <span class="material-icons">${insIsExpired ? 'warning' : insExpiry ? 'check_circle' : 'help_outline'}</span>
        <span>${insIsExpired ? 'EXPIRED' : insExpiry ? 'Valid' : 'No expiry set'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Company</span>
          <span class="value">${escapeHtml(trailer.insuranceCompany || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Policy #</span>
          <span class="value">${escapeHtml(trailer.insurancePolicy || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${insIsExpired ? 'text-danger' : ''}">${insExpiryStr}</span>
        </div>
        ${trailer.insurancePhone ? `
          <div class="truck-info-item full-width">
            <span class="label">Claims Phone</span>
            <span class="value"><a href="tel:${escapeHtml(trailer.insurancePhone)}">${escapeHtml(trailer.insurancePhone)}</a></span>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // ====== ANNUAL INSPECTION (DOT Required) ======
  const inspExpiry = trailer.inspectionExpiry ? new Date(trailer.inspectionExpiry) : null;
  const inspIsExpired = inspExpiry && inspExpiry < now;
  const inspExpiryStr = inspExpiry ? inspExpiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set';
  const inspDateStr = trailer.inspectionDate ? new Date(trailer.inspectionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';

  bodyHtml += `
    <div class="truck-info-section dot-section">
      <h4><span class="material-icons">fact_check</span> Annual DOT Inspection</h4>
      <div class="compliance-status ${inspIsExpired ? 'expired' : inspExpiry ? 'valid' : 'unknown'}">
        <span class="material-icons">${inspIsExpired ? 'warning' : inspExpiry ? 'check_circle' : 'help_outline'}</span>
        <span>${inspIsExpired ? 'EXPIRED' : inspExpiry ? 'Valid' : 'No inspection on file'}</span>
      </div>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Last Inspection</span>
          <span class="value">${inspDateStr}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Expires</span>
          <span class="value ${inspIsExpired ? 'text-danger' : ''}">${inspExpiryStr}</span>
        </div>
        ${trailer.inspectionLocation ? `
          <div class="truck-info-item full-width">
            <span class="label">Location</span>
            <span class="value">${escapeHtml(trailer.inspectionLocation)}</span>
          </div>
        ` : ''}
      </div>
    </div>
  `;

  // ====== TRAILER DETAILS ======
  bodyHtml += `
    <div class="truck-info-section">
      <h4><span class="material-icons">info</span> Trailer Details</h4>
      <div class="truck-info-grid">
        <div class="truck-info-item">
          <span class="label">Unit #</span>
          <span class="value">${escapeHtml(trailer.unit || 'N/A')}</span>
        </div>
        ${trailer.nickname ? `<div class="truck-info-item"><span class="label">Name</span><span class="value">${escapeHtml(trailer.nickname)}</span></div>` : ''}
        <div class="truck-info-item">
          <span class="label">Type</span>
          <span class="value">${escapeHtml((window.getTypeLabel ? window.getTypeLabel(trailer.type, 'trailerTypes') : capitalizeFirst(trailer.type)) || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Make</span>
          <span class="value">${escapeHtml(trailer.make || 'N/A')}</span>
        </div>
        <div class="truck-info-item">
          <span class="label">Year</span>
          <span class="value">${escapeHtml(trailer.year || 'N/A')}</span>
        </div>
        <div class="truck-info-item full-width">
          <span class="label">VIN</span>
          <span class="value" style="font-family: monospace; font-size: 0.85rem;">${escapeHtml(trailer.vin || 'N/A')}</span>
        </div>
      </div>
    </div>
  `;

  // ====== AXLE SERIALS (if any) ======
  if (trailer.axles && trailer.axles.length > 0) {
    bodyHtml += `
      <div class="truck-info-section">
        <h4><span class="material-icons">settings</span> Axle Serial Numbers</h4>
        <div class="axle-list">
          ${trailer.axles.map((serial, i) => `
            <div class="axle-item">
              <span class="axle-num">Axle ${i + 1}</span>
              <span class="axle-serial">${escapeHtml(serial)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  // ====== NOTES ======
  if (trailer.notes) {
    bodyHtml += `
      <div class="truck-info-section">
        <h4><span class="material-icons">notes</span> Notes</h4>
        <p style="color: var(--text-secondary);">${escapeHtml(trailer.notes)}</p>
      </div>
    `;
  }

  bodyHtml += `</div>`;

  document.getElementById('trailer-info-body').innerHTML = bodyHtml;
  document.getElementById('modal-trailer-info').classList.remove('hidden');
}

function capitalizeFirst(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// TASK-1333 — resolve a creator UID to a human label from the members doc.
// The members collection is readable by any tenant member (firestore.rules
// ~:1185), so this needs no extra privilege. Cached per session: a member's
// name doesn't change mid-session and the job-details modal gets reopened
// dozens of times a day. ANY failure (missing doc, denied read, offline)
// resolves back to the UID — we never substitute an unverified string, which
// is the whole point of resolving instead of rendering job.createdByName.
const _memberLabelCache = new Map();
async function resolveMemberLabel(tenantId, uid) {
  if (!tenantId || !uid) return uid || '';
  const key = `${tenantId}/${uid}`;
  if (_memberLabelCache.has(key)) return _memberLabelCache.get(key);
  let label = uid;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'members', uid));
    if (snap.exists()) {
      const m = snap.data();
      label = m.name || m.displayName || m.email || uid;
    }
  } catch (err) {
    console.warn('[Dispatch] Could not resolve creator label:', err?.message || err);
  }
  _memberLabelCache.set(key, label);
  return label;
}

// TASK-4535 — shared resolvers for handlers reachable from SEARCH-rendered
// cards. The live arrays are windowed + finished-pruned (:2845, :3003); Search
// loads the FULL collections into searchJobs/searchJobDays (:2750). A handler
// resolving a search-rendered id only against the live arrays dead-ends
// SILENTLY — the exact bug showJobDetails fixed for TASK-1668. Same fallback,
// now shared by showAssignment / editJob / duplicateJob / showAssignmentPopover
// / promptSetLoadsExpected (addDayToJob instead REFUSES out loud — see there).
function _resolveJobAnywhere(jobId) {
  return DispatchState.jobs.find(j => j.id === jobId)
    || (DispatchState.searchJobs || []).find(j => j.id === jobId)
    // TASK-9370 — a PIN driver opens Edit from the Job Board, whose jobs live in
    // JobBoardState, not here. driverOpenEditJob hands the one job over.
    || (DispatchState.driverBoardJobs || []).find(j => j.id === jobId)
    // TASK-9175 COD r2 — a project's older jobs plot from the map's one-shot
    // projectId query, outside the board window; they must open too.
    || _mapProjectJobById(jobId)
    // Projects view hand-off (openJobDetailsById) — one job fetched by id.
    || _externalJobById(jobId)
    || null;
}
function _mapProjectJobById(jobId) {
  for (const list of Object.values(DispatchState._mapProjectJobs || {})) {
    const hit = (list || []).find(j => j && j.id === jobId);
    if (hit) return hit;
  }
  return null;
}
function _resolveJobDayAnywhere(jobDayId) {
  return DispatchState.jobDays.find(d => d.id === jobDayId)
    || (DispatchState.searchJobDays || []).find(d => d.id === jobDayId)
    || _externalJobDays().find(d => d.id === jobDayId)
    || null;
}

// A job opened from OUTSIDE the board (Projects → Jobs/Overview/Timeline) by id.
// The live arrays are windowed (90d back) and only filled once Dispatch has been
// mounted, so a project's older job — or any job, before the user ever opened
// Dispatch — resolved nowhere and the click did nothing. openJobDetailsById
// fetches the one job + its days and parks them here; the resolvers above and
// showJobDetails read this LAST, so board/search data always wins. Keyed by
// tenant so a tenant switch can never paint the previous tenant's job.
function _externalJobCacheFor() {
  const c = DispatchState._externalJobCache;
  return c && c.tenantId && c.tenantId === DispatchState.tenantId ? c : null;
}
function _externalJobById(jobId) {
  return _externalJobCacheFor()?.jobs?.[jobId] || null;
}
function _externalJobDays() {
  return _externalJobCacheFor()?.jobDays || [];
}

// Harvest q15 (Chris 2026-10-12: "Warn"). A job opened from a Project can be any
// age — finished, invoiced, or weeks past its last day — and the details modal
// is as editable as a live one. Say so ONCE before it opens: Cancel leaves it
// closed, Continue opens it exactly as before (never read-only). Returns null
// when the job is current, else { reason, message } for the confirm.
// "Past" = every day of the job is before today (local midnight); a job whose
// last day is TODAY is current. Days come from every cache showJobDetails reads
// (board, search, the by-id fetch), falling back to job.endDate for legacy jobs.
function _jobClosedForEdits(jobId, job) {
  if (!job) return null;
  const status = String(job.status || '').toLowerCase();
  if (status === 'finished' || status === 'invoiced' || status === 'completed') {
    return { reason: status, message: `This job is marked ${status}. Changes here will update it.` };
  }
  const dayMs = []
    .concat(DispatchState.jobDays || [], DispatchState.searchJobDays || [], _externalJobDays())
    .filter(d => d && d.jobId === jobId)
    .map(_jobDayDateMs)
    .filter(ms => ms !== null);
  let lastMs = dayMs.length ? Math.max(...dayMs) : null;
  if (lastMs === null && job.endDate) {
    const end = job.endDate?.toDate?.() || new Date(job.endDate);
    if (Number.isFinite(end.getTime())) { end.setHours(12, 0, 0, 0); lastMs = end.getTime(); }
  }
  if (lastMs === null) return null;
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  if (lastMs >= startOfToday.getTime()) return null;
  const when = new Date(lastMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return { reason: 'past', lastMs, message: `This job ended ${when}. Changes here will update a finished job.` };
}

// One job + its days, fetched by id into the external cache the resolvers read
// LAST (board/search data always wins). 'ok' | 'missing' | 'switched'; throws
// on a read error so each caller words its own toast. Shared by
// openJobDetailsById and the Smart Job Builder's "Copy from that job"
// (TASK-4803 S3b), whose earlier job can sit outside the 90-day board window.
async function _fetchJobIntoExternalCache(tid, jobId) {
  const [jobSnap, daysSnap] = await Promise.all([
    getDoc(doc(db, 'tenants', tid, 'jobs', jobId)),
    getDocs(query(collection(db, 'tenants', tid, 'jobDays'), where('jobId', '==', jobId))),
  ]);
  if (DispatchState.tenantId !== tid || window.AppState?.tenant !== tid) return 'switched';
  if (!jobSnap.exists()) return 'missing';
  const days = daysSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (_jobDayDateMs(a) ?? 0) - (_jobDayDateMs(b) ?? 0));
  const prev = _externalJobCacheFor() || { tenantId: tid, jobs: {}, jobDays: [] };
  DispatchState._externalJobCache = {
    tenantId: tid,
    jobs: { ...prev.jobs, [jobId]: normalizeJobDoc(jobId, jobSnap.data()) },
    jobDays: [...prev.jobDays.filter(d => d.jobId !== jobId), ...days],
  };
  return 'ok';
}

// Open a job's details modal from any view, by id. Resolves true when the modal
// opened. Never fails silently: every miss says why in a toast.
// opts.warnIfClosed (Projects → job click): confirm before opening an old job —
// see _jobClosedForEdits. Board/search/Claude-reply callers never pass it.
async function openJobDetailsById(jobId, jobDayId = null, opts = null) {
  const toast = (msg, type = 'error') => window.showToast?.(msg, type);
  const tid = window.AppState?.tenant;
  if (!jobId || !tid) { toast('That job could not be loaded. Try refreshing the page.'); return false; }
  // Access = modules. Only an explicit true opens (the gate can also answer
  // 'needs-pin' / 'loading'); no dispatch module → say so instead of nothing.
  const access = typeof window.checkModuleAccess === 'function' ? window.checkModuleAccess('dispatch', true) : true;
  // TASK-9779 — an 811-only login reaches the Dispatch VIEW for the 811 window, not jobs.
  if (access === true && window.isMember811Only?.() === true) {
    toast('Job details are part of Dispatch, and your login only includes Locates (811). Ask an admin to add Dispatch if you need to open jobs.', 'warning');
    return false;
  }
  if (access !== true) {
    toast(access === 'needs-pin'
      ? 'Job details are part of Dispatch, which is locked on this device. Open Dispatch and enter your PIN, then try again.'
      : 'Job details are part of Dispatch, and your login does not include Dispatch. Ask an admin to add it if you need to open jobs.', 'warning');
    return false;
  }
  // Same seeding the PIN Job Board path uses (a static import never sets it).
  if (!DispatchState.tenantId) DispatchState.tenantId = tid;
  if (DispatchState.tenantId !== tid) {
    // Board state still belongs to the previous company; acting on it here
    // could write to the wrong tenant. initDispatch resets it on next mount.
    toast('Open Dispatch once to finish switching companies, then try again.', 'warning');
    return false;
  }
  // The job modals are painted with the Dispatch view; mount them on <body>
  // when that view is not on screen (initDispatch drops this host on mount).
  _ensureDriverJobModalMounted();

  if (!DispatchState.jobs.some(j => j.id === jobId)) {
    try {
      const got = await _fetchJobIntoExternalCache(tid, jobId);
      if (got === 'switched') return false; // switched mid-flight
      if (got === 'missing') { toast('That job is no longer on file — it may have been deleted.'); return false; }
    } catch (err) {
      console.warn('[Dispatch] openJobDetailsById: load failed', jobId, err);
      toast(err?.code === 'permission-denied'
        ? 'You do not have access to open this job’s details.'
        : 'That job could not be loaded. Check your connection and try again.');
      return false;
    }
  }
  // Harvest q15 — an old job opened from a Project warns once; Cancel keeps it
  // closed, Continue opens it as editable as ever. Only the Projects path asks.
  if (opts && opts.warnIfClosed) {
    const closed = _jobClosedForEdits(jobId, _resolveJobAnywhere(jobId));
    if (closed) {
      const go = typeof window.showConfirm === 'function'
        ? await window.showConfirm('Older job', closed.message, { confirmText: 'Continue', cancelText: 'Cancel', icon: 'history' })
        : true;
      if (!go) return false;
      // The company could have been switched while the warning sat open.
      if (DispatchState.tenantId !== tid || window.AppState?.tenant !== tid) return false;
    }
  }
  // TASK-9671 S1 (COD r1) — a caller whose screen moved on while the job loaded opens nothing.
  if (opts && typeof opts.stillWanted === 'function' && !opts.stillWanted()) return false;
  showJobDetails(jobId, jobDayId);
  return !document.getElementById('modal-job-details')?.classList.contains('hidden');
}

function showJobDetails(jobId, jobDayId = null) {
  // A FINISHED job is renderable but was not openable. The live board caches
  // (DispatchState.jobs / .jobDays) deliberately drop finished jobs (:2845, :3003)
  // to keep the working set small, but Search loads the FULL collection into
  // searchJobs/searchJobDays (:2726-2727) and renders those cards with this very
  // onclick. So clicking a finished result looked up a job that was never in the
  // cache, hit `if (!job) return`, and did NOTHING — no modal, no error, no toast.
  // Chris, 2026-08-19: "these are 'finished' but i want to open them to see some
  // information." Fall back to the search caches before giving up.
  const boardJob = DispatchState.jobs.find(j => j.id === jobId)
    || (DispatchState.searchJobs || []).find(j => j.id === jobId)
    || _mapProjectJobById(jobId);  // TASK-9175 COD r2 — map project pins outside the board window
  // openJobDetailsById — a job fetched by id from the Projects view, any date.
  // Read last so board/search/map data always wins.
  const job = boardJob || _externalJobById(jobId);
  if (!job) {
    // Never fail silently again — a dead click is indistinguishable from a bug.
    window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error');
    console.warn('[Dispatch] showJobDetails: job not found in live or search cache', jobId);
    return;
  }

  let jobDays = DispatchState.jobDays.filter(d => d.jobId === jobId);
  // Same split for the days: a finished job's days are only in searchJobDays.
  // This MUST run before the legacy synthetic-day block below, so real recorded
  // days always win over dates reconstructed from the job's start/end range.
  if (jobDays.length === 0 && Array.isArray(DispatchState.searchJobDays)) {
    jobDays = DispatchState.searchJobDays.filter(d => d.jobId === jobId);
  }
  // openJobDetailsById fetched this job's real days — they beat synthesized ones.
  if (jobDays.length === 0) jobDays = _externalJobDays().filter(d => d.jobId === jobId);

  // For legacy jobs (GAS imports) with no jobDays, generate synthetic entries from date range
  if (jobDays.length === 0 && job.startDate) {
    const startDate = job.startDate?.toDate?.() || new Date(job.startDate);
    const endDate = job.endDate?.toDate?.() || (job.endDate ? new Date(job.endDate) : startDate);
    const dayMs = 24 * 60 * 60 * 1000;
    let current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = formatDateISO(current);
      jobDays.push({
        id: `legacy-${jobId}-${dateStr}`,
        jobId: jobId,
        date: new Date(current),
        drivers: job.drivers || [],
        helpers: job.helpers || [],
        trucks: job.trucks || [],
        trailers: job.trailers || [],
        assets: job.assets || [],
        status: (job.drivers?.length > 0 || job.trucks?.length > 0) ? 'assigned' : 'scheduled',
        isLegacy: true
      });
      current = new Date(current.getTime() + dayMs);
    }
  }

  const content = document.getElementById('job-details-content');
  content.innerHTML = `
    <div class="job-details">
      ${_rentalReqOfficeHtml(job)}
      ${jobDays.length > 1 ? `<div style="margin:0 0 10px;padding:8px 12px;border-radius:8px;background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.25);font-size:0.8rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;line-height:1.4;"><span class="material-icons" style="font-size:16px;color:var(--brand-blue);flex-shrink:0;">link</span><span><strong>${jobDays.length} linked days</strong> — one job. Shared details (customer, PO, location and <strong>notes</strong>) apply to every day; crew is set per day.</span></div>` : ''}
      <div class="job-details-section">
        <h4>Job Information</h4>
        <div class="detail-row"><span>Title:</span><span>${escapeHtml(job.title)}</span></div>
        <div class="detail-row"><span>Customer:</span><span>${escapeHtml(job.customer || 'None')}</span></div>
        ${job.needsDispatchReview === true ? `<div class="detail-row"><span>DOT:</span><span><span title="Created from the driver board — Interstate / Hazmat / Liquid Tank were never set. Open Edit Job to confirm." style="display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:10px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">help</span>Driver-created — confirm DOT flags</span></span></div>` : ''}
        ${job.waypoints && job.waypoints.length > 0 ? `
          <div class="detail-row"><span>Destinations:</span><span>
            ${job.waypoints.map(wp => {
              // TASK-10116 — a saved stop link is only clickable when it is
              // http(s); escapeHtml does not stop a javascript: scheme.
              const _h = wp.url ? waypointOpenHref(wp.url) : '';
              return `<div>${escapeHtml(wp.name || 'Destination')}${_h
                ? ` — <a href="${escapeHtml(_h)}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary btn-small"><span class="material-icons">map</span> Open in Maps</a>`
                : (wp.url ? ` — <span style="color:var(--text-secondary);word-break:break-all;">${escapeHtml(wp.url)}</span>` : '')}</div>`;
            }).join('')}
          </span></div>
        ` : `<div class="detail-row"><span>Destinations:</span><span>None</span></div>`}
        <div class="detail-row"><span>PO Number:</span><span>${escapeHtml(jobPoList(job).join(', ') || 'None')}</span></div>
        <div class="detail-row"><span>Vendor Number:</span><span>${escapeHtml(job.vendorNumber || 'None')}</span></div>
        <div class="detail-row"><span>Well:</span><span>${escapeHtml(job.wellName || '')} ${escapeHtml(job.wellNumber || '')}</span></div>
        ${job.notes ? `<div class="detail-row"><span>Notes:</span><span style="white-space:pre-wrap;">${escapeHtml(job.notes)}${jobDays.length > 1 ? `<span title="Job-wide — the same note on every day of this job" style="display:inline-block;margin-left:6px;padding:1px 8px;border-radius:9px;background:#EFF6FF;border:1px solid #BFDBFE;color:#1D4ED8;font-size:0.7rem;font-weight:600;vertical-align:middle;white-space:nowrap;">All ${jobDays.length} days</span>` : ''}</span></div>` : ''}
        ${(() => {
          // TASK-1333 — read-only creator attribution. OPTIONAL BY DESIGN: there is
          // no backfill, so every job created before this shipped has no createdBy
          // and the row is simply omitted entirely (never "Unknown" — that would
          // read as a real answer to a question we can't answer).
          //
          // 🔴 The rendered name is resolved from the UID, NOT from the stored
          // `createdByName` (COD Adversary ⚠️). Rules pin `createdBy` to
          // request.auth.uid, but nothing constrains the display string — a
          // dispatcher could SDK-write a correct uid alongside a COWORKER'S NAME
          // and this row would show the frame-up, laundering the exact forgery
          // the rule exists to block. So the only field this row ever trusts is
          // the enforced uid; the label comes from the members doc, and if that
          // lookup can't resolve we show the raw uid rather than an unverified
          // string. `createdByName` stays on the doc as denormalized metadata for
          // exports — it is never the answer displayed here.
          if (!job.createdBy) return '';
          const when = job.createdAt?.toDate?.() || (job.createdAt ? new Date(job.createdAt) : null);
          const whenStr = when && !isNaN(when) ? ` — ${formatDateDisplay(when, { year: 'numeric', month: 'short', day: 'numeric' })}` : '';
          return `<div class="detail-row"><span>Created by:</span><span><span id="job-created-by-label" data-job-id="${escapeHtml(jobId)}">${escapeHtml(job.createdBy)}</span>${escapeHtml(whenStr)}</span></div>`;
        })()}
        ${job.photos && job.photos.length > 0 ? `
          <div class="detail-row"><span>Photos:</span><span>${job.photos.length} attached</span></div>
          <div class="photo-grid">${job.photos.map(p => `<a ${window.StorageFile.linkAttrs(p)}><img ${window.StorageFile.imgAttrs(p, { urlKey: p.thumbUrl ? 'thumbUrl' : undefined })} class="photo-thumb"></a>`).join('')}</div>
        ` : ''}
      </div>

      ${buildRoutePlannerHTML(job)}
      ${jobDays.length > 0 ? buildLoadSummaryHTML(jobDays.find(d => d.id === jobDayId) || jobDays[0]) : ''}
      ${jobDays.length > 0 ? buildLoadCounterHTML(jobDays.find(d => d.id === jobDayId) || jobDays[0], jobId) : ''}

      ${(() => {
        // TASK-10011 — Start JSA from the office. The day is the one this
        // screen was opened from, else today's day of the job.
        if (!_canStartJsaFromOffice()) return '';
        const jsaDay = _officeJsaDayFor(jobDays, jobDayId, formatDateISO(new Date()));
        const dayNote = jsaDay
          ? `For ${escapeHtml(formatDateDisplay(jsaDay.date?.toDate ? jsaDay.date.toDate() : new Date(jsaDay.date)))}. The crew signs it from their job.`
          : 'Linked to this job. The crew signs it from their job.';
        return `
      <div class="job-details-section job-details-jsa">
        <h4>Safety (JSA)</h4>
        ${jsaDay ? getJsaPresentIndicator(jsaDay, 'driver-card') : ''}
        <p style="margin:6px 0 8px;font-size:0.85rem;color:var(--text-secondary);">${dayNote}</p>
        <div style="display:flex;">${_officeStartJsaButtonHtml(jobId, jsaDay ? jsaDay.id : '', jsaDay)}</div>
      </div>`;
      })()}

      <div class="job-details-section">
        <div class="schedule-header">
          <h4>Schedule (${jobDays.length} ${jobDays.length === 1 ? 'day' : 'days'})</h4>
        </div>
        <div class="job-days-list">
          ${jobDays.map(d => {
            const date = d.date?.toDate?.() || new Date(d.date);
            const hasAssignments = (d.drivers?.length || d.trucks?.length || d.helpers?.length || d.trailers?.length || d.assets?.length);
            // TASK-9645 B — per-day billing chip slot, filled by
            // _refreshJobDetailsBillingChips (board stream now, live read next).
            // Same day key the ticket guards use (_jobDayDateMs + formatDateISO,
            // jobDay.date is anchored to noon local).
            const _billMs = _jobDayDateMs(d);
            const _billDay = _billMs === null ? '' : formatDateISO(new Date(_billMs));
            // TASK-4792 / TASK-9236 — per-day removal. The control is built by
            // _renderJobDayRemoveControl (next to _dispatchCanManageDays) so its
            // gates are testable on their own: absent for view-and-upload members
            // and synthesized legacy rows, a labeled "Remove Day" otherwise.
            return `
              <div class="job-day-row" onclick="window.DispatchModule.showAssignment('${escJsAttr(jobId)}', '${escJsAttr(d.id)}')" title="Tap to assign crew & equipment">
                <span class="job-day-date">${formatDateDisplay(date)}</span>
                ${_jobDayDetailsStatusHtml(jobId, d, _dispatchCanManageDays())}
                ${_billDay ? `<span class="job-day-billing" data-billing-chip-job="${escapeHtml(jobId)}" data-billing-chip-day="${escapeHtml(_billDay)}" style="display:inline-flex;min-width:0;max-width:100%;"></span>` : ''}
                <span class="job-day-assign">${formatAssignments(d)}</span>
                <span class="job-day-action ${hasAssignments ? '' : 'highlight'}">
                  <span class="material-icons">${hasAssignments ? 'groups' : 'person_add'}</span>
                  <span class="job-day-action-label">${hasAssignments ? 'Crew' : 'Assign'}</span>
                </span>
                ${_renderJobDayCancelControl(jobId, d, _dispatchCanManageDays())}
                ${_renderJobDayRemoveControl(jobId, d, jobDays.length, formatDateDisplay(date), _dispatchCanManageDays())}
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <p class="job-details-hint">${_jobDetailsScheduleHint(_dispatchCanManageDays() && jobDays.some(d => !d.isLegacy))}</p>
      <div id="job-details-rental-pickup"></div>
      <div id="job-details-rentals-out"></div>
    </div>
  `;
  // TASK-10006 — "Out on this project" + Create return job (filled async;
  // empty when nothing is out). Office only: PIN drivers never see it.
  if (window.AppState?.driverModePin !== true) _renderJobRentalsOut(jobId);
  if (window.AppState?.driverModePin !== true) _renderJobPickupPanel(jobId); // TASK-10006 S3 — empty unless this job holds a planned rental pickup

  // TASK-9645 B — per-day Invoiced / Paid chips (status only, no amounts). PIN
  // drivers have no tickets stream and never see this office modal's billing.
  if (window.AppState?.driverModePin !== true) _refreshJobDetailsBillingChips(jobId);

  // Render footer buttons separately so they stay fixed
  //
  // TASK-4812 — Add Day used to be `btn-primary` with `grid-column:1/-1`, so it
  // rendered as a 686px full-bleed blue gradient banner over four 337px grey
  // cells: twice the width of anything else and the only gradient in the modal.
  // It is not what this modal is FOR. The primary action of this screen lives in
  // the BODY — the day rows above, which the hint under them names ("Tap a
  // scheduled day above to assign crew & equipment") — and the most-used footer
  // control is Edit Job. So the footer is five management actions of equal
  // weight, ordered most-used first, and nothing here shouts over the day list.
  // Add Day is NOT hidden and NOT moved: same modal, same labels, one cell.
  _ensureJobDayRemoveStyles(); // TASK-9236
  _ensureAddDayWeightStyles();
  document.getElementById('job-details-footer').innerHTML = `
    <div class="job-details-actions">
      <button class="btn btn-secondary job-action-btn" onclick="window.DispatchModule.editJob('${escJsAttr(jobId)}', '${escJsAttr(jobDayId || '')}', event)">
        <span class="job-action-stack">
          <span class="job-action-main"><span class="material-icons">edit_note</span> Edit Job</span>
          <span class="job-action-sub">Change title, customer, details</span>
        </span>
      </button>
      <!-- TASK-9363 — Copy is the repeat-run flow, so it comes before Add Day. -->
      <button class="btn btn-secondary job-action-btn" onclick="window.DispatchModule.duplicateJob('${escJsAttr(jobId)}')">
        <span class="job-action-stack">
          <span class="job-action-main"><span class="material-icons">content_copy</span> Copy to New Job</span>
          <span class="job-action-sub">Separate job, new job ID</span>
        </span>
      </button>
      <button class="btn btn-secondary job-action-btn" ${_dispatchCanManageDays() ? "" : "hidden"} onclick="window.DispatchModule.addDayToJob('${escJsAttr(jobId)}')">
        <span class="job-action-stack">
          <span class="job-action-main"><span class="material-icons">add_circle</span> Add Day</span>
          <span class="job-action-sub">Same work, same instructions</span>
        </span>
      </button>
      <button class="btn btn-secondary job-action-btn" onclick="window.DispatchModule.archiveJob('${escJsAttr(jobId)}')">
        <span class="job-action-stack">
          <span class="job-action-main"><span class="material-icons">archive</span> Archive</span>
          <span class="job-action-sub">Hide from active board</span>
        </span>
      </button>
      <button class="btn btn-danger job-action-btn" id="btn-job-details-delete" onclick="window.DispatchModule.deleteJobFromDetails('${escJsAttr(jobId)}', { jobDayId: '${escJsAttr(jobDayId || '')}', button: this })">
        <span class="job-action-stack">
          <span class="job-action-main"><span class="material-icons">delete</span> Delete</span>
          <span class="job-action-sub">Permanently remove</span>
        </span>
      </button>
    </div>
  `;

  // TASK-1550 — the signed-in user's own reminders on this job. window.Reminders
  // renders + self-gates (dispatch module / owner; never driver or link sessions).
  window.Reminders?.mountAppend(content, { type: 'job', id: jobId, label: job.title || '' });

  document.getElementById('modal-job-details-title').textContent = job.title;
  // TASK-10009 — job type under the title ("Devon Albert North Pad" / "Mat
  // Removal"); nothing for the default type. textContent, never innerHTML.
  const _typeEl = document.getElementById('modal-job-details-type');
  if (_typeEl) {
    const _typeLabel = _jobTypeSubtitleText(job);
    _typeEl.textContent = _typeLabel;
    _typeEl.style.display = _typeLabel ? '' : 'none';
  }
  document.getElementById('modal-job-details').classList.remove('hidden');

  // TASK-1333 — swap the "Created by" UID for the resolved member name.
  // Fire-and-forget by design: the row is already populated with the UID, so a
  // slow or denied members read degrades to a less friendly answer instead of
  // blocking the modal. The data-job-id guard stops a late resolve from
  // stamping the wrong name if the dispatcher opened a different job meanwhile.
  if (job.createdBy) {
    resolveMemberLabel(DispatchState.tenantId, job.createdBy).then(label => {
      const el = document.getElementById('job-created-by-label');
      if (el && el.dataset.jobId === jobId) el.textContent = label;
    });
  }
}

// opts.dayScopeNote — TASK-1331: set by the add-day paths so the editor states out
// loud that crew is per-day. Optional so the existing 2-arg caller (the calendar
// day-row onclick, :8642) is unchanged.
async function showAssignment(jobId, jobDayId, opts = {}) {
  // TASK-4535 — day rows inside the details modal TASK-1668 opened were dead
  // clicks for finished/out-of-window jobs. Resolve against search too.
  const job = _resolveJobAnywhere(jobId);
  if (!job) {
    window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error');
    return;
  }

  let jobDay = _resolveJobDayAnywhere(jobDayId);

  // Handle legacy jobs - create real jobDay on-the-fly
  // TASK-1408 (COD Code Reviewer) — this backfill CREATES a jobDay just because
  // someone opened the editor on a legacy job. For a viewUploadOnly member that is
  // the create-then-deny trap again by a different door: the doc lands, then rules
  // refuse the save. Don't create on their behalf. Guarded here rather than at the
  // top of showAssignment on purpose — viewing is allowed, only the write is not.
  if (!jobDay && jobDayId.startsWith('legacy-') && !_dispatchCanManageDays()) {
    window.showToast?.('This job has not been set up for scheduling yet — ask an admin to open it once', 'error');
    return;
  }
  if (!jobDay && jobDayId.startsWith('legacy-')) {
    // Parse date from legacy ID: legacy-{jobId}-{YYYY-MM-DD}
    const dateStr = jobDayId.split('-').slice(-3).join('-'); // Get YYYY-MM-DD
    // TASK-4739 — anchor NOON LOCAL. A bare 'YYYY-MM-DD' parses as UTC midnight.
    // The day was already correct in memory and got destroyed by a string
    // round-trip. jobboard.js formatDateISO is LOCAL despite the name, so this
    // landed one day EARLY on the driver board while dispatch's UTC filter still
    // showed the right day — the two boards disagreed and dispatch looked fine.
    const date = new Date(dateStr + 'T12:00:00');

    // Create a real jobDay document
    try {
      const newJobDayData = {
        jobId: jobId,
        date: Timestamp.fromDate(date),
        drivers: job.drivers || [],
        helpers: job.helpers || [],
        trucks: job.trucks || [],
        trailers: job.trailers || [],
        assets: job.assets || [],
        status: (job.drivers?.length > 0 || job.trucks?.length > 0) ? 'assigned' : 'scheduled',
        createdAt: Timestamp.now(),
        migratedFromLegacy: true
      };

      const docRef = await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), newJobDayData);

      // Update local state immediately so UI works
      jobDay = { id: docRef.id, ...newJobDayData, date: date };
      DispatchState.jobDays.push(jobDay);
      jobDayId = docRef.id;

      console.log('[Dispatch] Migrated legacy job day:', jobDayId);
      // TASK-9227 — this write seats crew on a REAL jobDay just by OPENING the
      // day, before the dispatcher has touched anything, so a gate on the save
      // path never sees it. The picker that opens a beat later carries the
      // badge; this names it out loud and puts a row in the audit log. Legacy
      // crew arrays are NAME strings, which is exactly why _dfPersonById does
      // the dual id-or-name lookup.
      _dfNoticeEntries(
        _dfFlaggedEntries([...(newJobDayData.drivers || []), ...(newJobDayData.helpers || [])]),
        { surface: 'legacy-day-materialize', jobId, jobDayId, action: 'FLAGGED_CREW_CARRIED' }
      );
    } catch (err) {
      console.error('[Dispatch] Error creating jobDay for legacy job:', err);
      window.showToast?.('Error opening assignment', 'error');
      return;
    }
  }

  if (!jobDay) return;

  const date = jobDay.date?.toDate?.() || new Date(jobDay.date);

  document.getElementById('assignment-job-day-id').value = jobDayId;
  document.getElementById('assignment-job-id').value = jobId;
  // TASK-9750 — this day's own flags (never carried over from the last day opened).
  {
    const t = document.getElementById('assignment-tentative');
    if (t) {
      const locked = _assignmentTentativeLocked(jobDay);
      t.checked = isTentativeJobDay(jobDay);
      t.disabled = locked;
      const lbl = t.closest('label');
      if (lbl) {
        lbl.style.opacity = locked ? '0.6' : '';
        lbl.title = locked ? 'This day is already confirmed with crew on it. To hold it again, take the crew off first.' : '';
      }
    }
  }
  { const h = document.getElementById('assignment-half-day'); if (h) h.checked = isHalfDayJobDay(jobDay); }
  document.getElementById('modal-assignment-title').textContent = `Assign: ${job.title}`;
  const _dateLabel = formatDateDisplay(date, { weekday: 'long', month: 'long', day: 'numeric' });
  document.getElementById('assignment-info').innerHTML = `
    <p><strong>Date:</strong> ${_dateLabel}</p>
  `;

  // TASK-1331 — the belief that editing a later day rewrites the earlier days' crew is
  // what drove dispatchers to create duplicate JOBS instead of adding days. It is false
  // (buildInheritedJobDay writes a snapshot into a separate jobDays doc, and this editor
  // is keyed to one jobDayId), so when we arrive here straight off an add-day, say so.
  if (opts.dayScopeNote) {
    document.getElementById('assignment-info').insertAdjacentHTML('beforeend',
      `<div style="margin:8px 0 0;padding:10px 12px;border-radius:8px;background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.25);font-size:0.82rem;color:var(--text-primary);line-height:1.45;">
        <strong>Crew is per day.</strong> What you set here applies to ${_dateLabel} only — earlier days keep the crew they already have.
      </div>`);
  }

  // TASK-4653 — say it on OPEN, not only in the refusal toast, so a dispatcher
  // on a phone is not told "already billed on invoice X" after tapping through
  // the whole form. ADVISORY ONLY, and the same cheap read the Edit Job header
  // hint uses: DispatchState.tickets is 90-day windowed and its projection drops
  // originalWorkDate, so its SILENCE proves nothing — never invert this into
  // "no badge means safe". The authoritative check is the tenant-scoped query
  // saveAssignment now runs, which is what actually refuses.
  if (_seedDayLooksTicketed(jobDay)) {
    document.getElementById('assignment-info').insertAdjacentHTML('beforeend',
      `<div style="margin:8px 0 0;padding:10px 12px;border-radius:8px;background:rgba(245,158,11,0.10);border:1px solid rgba(245,158,11,0.35);font-size:0.82rem;color:var(--text-primary);line-height:1.45;">
        <strong>This day is already ticketed.</strong> Changing the crew changes who is on record for that day's work — and the ticket's saved hours and totals do NOT recalculate. If it is already billed, the save will be refused.
      </div>`);
  }

  // Populate multi-selects (show loading if data not yet loaded)
  // Filter by showInDispatch but preserve already-selected items
  const selDrivers = jobDay.drivers || [];
  const selHelpers = jobDay.helpers || [];
  const selTrucks = jobDay.trucks || [];
  const selTrailers = jobDay.trailers || [];
  // TASK-10106 S1b — Confirm on a driver's rental request: the rental joins
  // this day's selection (pre-ticked, deploy, the asked qty) BEFORE the tiles
  // render. Nothing moves until Save; every open without it resets the marker.
  const _arq = (opts && opts.assetRequest && opts.assetRequest.jobId === jobId && opts.assetRequest.dayId === jobDayId) ? opts.assetRequest : null;
  DispatchState.pendingAssetRequest = _arq;
  const selAssets = _arq && !(jobDay.assets || []).includes(_arq.assetId) ? [...(jobDay.assets || []), _arq.assetId] : (jobDay.assets || []);
  const visiblePeople = DispatchState.people
    .filter(p => p.showInDispatch !== false || selDrivers.includes(p.id) || selHelpers.includes(p.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const visibleTrucks = DispatchState.trucks
    .filter(t => t.showInDispatch !== false || selTrucks.includes(t.id));
  const visibleTrailers = DispatchState.trailers
    .filter(t => t.showInDispatch !== false || selTrailers.includes(t.id));
  const visibleAssets = DispatchState.assets
    .filter(a => a.showInDispatch !== false || selAssets.includes(a.id));
  // TASK-1584 — advisory badge: who/what is already on ANOTHER job this date
  const doubleBookIndex = buildDoubleBookIndex(jobDay);

  // 🔴 TASK-9253 — SEED THE BILLED SIDE MAPS BEFORE THE ASSET TILES RENDER.
  // These two statements used to sit ~14 lines BELOW, under "Load asset
  // directions and quantities for rental assets", and that ordering is the whole
  // defect: populateMultiSelect reads `DispatchState.pendingAssetQtys?.[item.id]
  // || 1` while it builds each asset tile, so opening Assign Crew on a day that
  // stores 4 painted the fallback 1 over it — or, before any seed had ever run
  // for THIS day, whatever the PREVIOUS modal left in the map (open day A with
  // qty 4 → Cancel → open day B → B's tile paints 4).
  //
  // 💵 Why this one carries further than TASK-9238's edit form: saveAssignment
  // builds its assetQtys / assetDirections from the MAPS, not the DOM, and hands
  // the same map values to processRentalTransaction and to the consumable deduct
  // batch. So the pre-fix screen and the write disagreed about a number that can
  // move REAL STOCK.
  //
  // Stated precisely, because COD was right to push back on the loose version:
  // a no-touch reopen-and-save of an asset ALREADY on the day moves nothing —
  // the inventory block runs on `newlyAdded` (selectedAssets minus
  // previousAssets), so an existing asset is skipped and the stored 4 survives
  // while the screen said 1. That is the 9238 failure: form and doc disagree,
  // silently. Stock actually moves in the two shapes the mispaint CAUSES:
  //   1. stale carry — day A stores 4, Cancel, open day B which stores none;
  //      pre-fix the tile paints A's 4, the dispatcher ticks the asset, and the
  //      save deducts the MAP's 1. The yard moves by a number never displayed.
  //   2. corrected off a lie — the dispatcher sees 1 on a day that stores 4 and
  //      retypes relative to that false baseline; setAssetQty writes it to the
  //      map and the save persists (and, if newly added, deducts) it.
  //
  // Direction is deliberately NOT part of the paint bug here: the Deploy/Return
  // toggle (`dirToggle`) and buildInventoryPreview are gated on `containerId ===
  // 'job-select-assets'` and exist ONLY in populateJobMultiSelect — the Assign
  // Crew modal renders neither, so pendingAssetDirections has no reader in this
  // render path. It is moved with its pair anyway: the two are written together
  // by every other seeder, and leaving one behind is how the next person
  // re-introduces the split.
  //
  // Nothing between here and the old position reads either map: the four
  // non-asset populateMultiSelect calls and the subcontractor one short-circuit
  // the qty read on `isQtyAsset`, which is false unless containerId is
  // 'select-assets', and the line-item seed touches different state. Pure
  // reorder, no other side effect.
  //
  // Note on why this was never self-healing: refreshAssignmentModalIfOpen
  // re-renders the same tiles from the maps when a late snapshot lands, and by
  // then the seed HAS run — so the bug is first-paint-only and disappears the
  // moment any roster/jobDay snapshot fires. That is exactly what makes it a
  // tripwire's job and not a manual QA pass's.
  //
  // This is also the reset: the assignment path — unlike showAddJob, which had no
  // seeder at all until TASK-9238 added one — assigns a FRESH object from this
  // day's own jobDay on every open, so moving it above the render is what makes
  // that wholesale replace actually cover the first paint.
  DispatchState.pendingAssetDirections = { ...(jobDay.assetDirections || {}) };
  DispatchState.pendingAssetQtys = { ...(jobDay.assetQtys || {}) };
  if (_arq) {
    delete DispatchState.pendingAssetDirections[_arq.assetId];
    const _arqA = DispatchState.assets.find(x => x.id === _arq.assetId);
    if (_arqA && _arqA.trackingMode === 'quantity') DispatchState.pendingAssetQtys[_arq.assetId] = _arq.qty;
    const _arqNote = document.createElement('div');
    _arqNote.style.cssText = 'margin:8px 0 0;padding:10px 12px;border-radius:8px;background:rgba(245,158,11,0.10);border:1px solid rgba(245,158,11,0.35);font-size:0.82rem;color:var(--text-primary);line-height:1.45;';
    _arqNote.textContent = `Confirming ${_arq.personName}'s request: ${_arq.qty} ${_arq.name} is ticked below. Nothing moves until you Save. Change the number first if needed.`;
    document.getElementById('assignment-info')?.appendChild(_arqNote);
  }

  populateMultiSelect('select-drivers', visiblePeople, selDrivers, !DispatchState.dataLoaded.people, doubleBookIndex);
  populateMultiSelect('select-helpers', visiblePeople, selHelpers, !DispatchState.dataLoaded.people, doubleBookIndex);
  populateMultiSelect('select-trucks', visibleTrucks, selTrucks, !DispatchState.dataLoaded.trucks, doubleBookIndex);
  populateMultiSelect('select-trailers', visibleTrailers, selTrailers, !DispatchState.dataLoaded.trailers, doubleBookIndex);
  populateMultiSelect('select-assets', visibleAssets, selAssets, !DispatchState.dataLoaded.assets);

  // Subcontractors from CRM customers
  const selSubs = jobDay.subcontractors?.map(s => s.vendorId) || [];
  const visibleSubs = DispatchState.customers
    .filter(c => (c.type === 'subcontractor' || c.type === 'vendor') && (c.active !== false || selSubs.includes(c.id)))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  populateMultiSelect('select-subcontractors', visibleSubs, selSubs, false);

  // Load line item selections from jobDay
  DispatchState.pendingLineItemSelections = { ...(jobDay.lineItemSelections || {}) };
  DispatchState.pendingSelectedContractId = jobDay.selectedContractId || null;
  // TASK-9763 — this day's own "who's in what" (never carried from the last day opened).
  DispatchState.pendingUnitPairsSrc = jobDay.crewAssetAssignments || null;
  DispatchState.pendingUnitPairs = null; // seeded from Src by the panel on first use
  // TASK-10193 S2 — team-truck "May drive" ticks, untouched picks + the 1:1 auto-link: per open, like the picks.
  DispatchState.pendingUnitSeats = null;
  DispatchState.pendingPairTouched = null;
  DispatchState.pendingAutoLink = null;
  DispatchState.pendingUnitPairsHadMap = !!(jobDay.crewAssetAssignments && typeof jobDay.crewAssetAssignments === 'object' && Object.keys(jobDay.crewAssetAssignments).length);
  // fix/2096-preship — the day these picks belong to. saveAssignment writes
  // pairings only when this matches the day it is saving.
  DispatchState.pendingUnitPairsDayId = jobDayId;
  // TASK-9763 S3 — this day's stored "first on" order (crewOrder); the panel
  // extends it with whoever is checked. Reset per open, like the pairing.
  DispatchState.pendingCrewOrderSrc = Array.isArray(jobDay.crewOrder) ? jobDay.crewOrder.slice() : null;
  DispatchState.pendingCrewOrder = null;
  // TASK-9797 item 1 — this day's named slots. Reset per open.
  DispatchState.pendingUnitSlotsSrc = jobDay.unitSlots || null;
  DispatchState.pendingUnitSlots = null;
  renderAssignmentPairing();

  // (Asset directions + quantities are seeded ABOVE, before the tiles render —
  //  TASK-9253. Do not move them back down here.)

  // Clear contract picker area
  const contractArea = document.getElementById('assignment-contract-area');
  if (contractArea) contractArea.innerHTML = '';
  const rateArea = document.getElementById('assignment-rate-area');
  if (rateArea) rateArea.innerHTML = '';

  closeModal('job-details');
  document.getElementById('modal-assignment').classList.remove('hidden');

  // Run DOT compliance check on existing assignments
  setTimeout(runAssignmentComplianceCheck, 200);

  // Gate: only show pickers for clockbilling subscribers
  if (job.customerId && window.hasAddOnPackage?.('clockbilling')) {
    loadCustomerLineItems(job.customerId).then(({ contracts, items }) => {
      if (contracts.length > 0) {
        renderContractPicker('assignment-contract-area', contracts, DispatchState.pendingSelectedContractId, false);
        // Auto-select if only 1 contract
        if (contracts.length === 1) {
          DispatchState.pendingSelectedContractId = contracts[0].id;
        }
        const contractItems = DispatchState.pendingSelectedContractId
          ? items.filter(i => i.contractId === DispatchState.pendingSelectedContractId)
          : items;
        if (contractItems.length > 0) {
          renderLineItemPickers('select-trucks', 'truck', contractItems);
          renderLineItemPickers('select-trailers', 'trailer', contractItems);
          renderLineItemPickers('select-assets', 'asset', contractItems);
          renderLineItemPickers('select-drivers', 'person', contractItems);
          renderLineItemPickers('select-helpers', 'person', contractItems);
        }
      }
      // TASK-1673 — pre-fill from the dispatcher's own last pick for the same
      // resource + customer, then paint the always-visible rate panel. Runs
      // AFTER the inline pickers so `_syncRateControls` has controls to update.
      // Guarded on the modal still being open for this same day: the fetch is
      // async and the dispatcher may have closed it or moved to another day.
      if (document.getElementById('assignment-job-day-id')?.value === jobDay.id &&
          !document.getElementById('modal-assignment')?.classList.contains('hidden')) {
        applyLastUsedRateDefaults(job.customerId, jobDay.id);
        Object.keys(DispatchState.pendingLineItemSelections || {}).forEach(_syncRateControls);
        renderAssignmentRatePanel();
      }
    });
  }
}

// Refresh assignment modal when data loads
function refreshAssignmentModalIfOpen() {
  const modal = document.getElementById('modal-assignment');
  if (modal && !modal.classList.contains('hidden')) {
    const jobDayId = document.getElementById('assignment-job-day-id')?.value;
    const jobId = document.getElementById('assignment-job-id')?.value;
    if (jobDayId && jobId) {
      const jobDay = DispatchState.jobDays.find(d => d.id === jobDayId);
      if (jobDay) {
        // Read current DOM selections so we don't wipe unsaved user choices
        const getChecked = (id) => Array.from(document.getElementById(id)?.querySelectorAll('input:checked') || []).map(i => i.value);
        const rDrivers = getChecked('select-drivers').length > 0 ? getChecked('select-drivers') : (jobDay.drivers || []);
        const rHelpers = getChecked('select-helpers').length > 0 ? getChecked('select-helpers') : (jobDay.helpers || []);
        const rTrucks = getChecked('select-trucks').length > 0 ? getChecked('select-trucks') : (jobDay.trucks || []);
        const rTrailers = getChecked('select-trailers').length > 0 ? getChecked('select-trailers') : (jobDay.trailers || []);
        const rAssets = getChecked('select-assets').length > 0 ? getChecked('select-assets') : (jobDay.assets || []);
        const sortedPeopleRefresh = DispatchState.people
          .filter(p => p.showInDispatch !== false || rDrivers.includes(p.id) || rHelpers.includes(p.id))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        // TASK-1584 — a jobDay snapshot can land AFTER the modal opened, so the
        // advisory has to be rebuilt on the refresh path too, not just showAssignment
        const refreshDoubleBookIndex = buildDoubleBookIndex(jobDay);
        populateMultiSelect('select-drivers', sortedPeopleRefresh, rDrivers, !DispatchState.dataLoaded.people, refreshDoubleBookIndex);
        populateMultiSelect('select-helpers', sortedPeopleRefresh, rHelpers, !DispatchState.dataLoaded.people, refreshDoubleBookIndex);
        populateMultiSelect('select-trucks', DispatchState.trucks.filter(t => t.showInDispatch !== false || rTrucks.includes(t.id)), rTrucks, !DispatchState.dataLoaded.trucks, refreshDoubleBookIndex);
        populateMultiSelect('select-trailers', DispatchState.trailers.filter(t => t.showInDispatch !== false || rTrailers.includes(t.id)), rTrailers, !DispatchState.dataLoaded.trailers, refreshDoubleBookIndex);
        populateMultiSelect('select-assets', DispatchState.assets.filter(a => a.showInDispatch !== false || rAssets.includes(a.id)), rAssets, !DispatchState.dataLoaded.assets);

        // Subcontractors
        const rSubs = getChecked('select-subcontractors').length > 0 ? getChecked('select-subcontractors') : (jobDay.subcontractors?.map(s => s.vendorId) || []);
        const refreshSubs = DispatchState.customers
          .filter(c => (c.type === 'subcontractor' || c.type === 'vendor') && (c.active !== false || rSubs.includes(c.id)))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        populateMultiSelect('select-subcontractors', refreshSubs, rSubs, false);
        renderAssignmentPairing(); // TASK-9763 — follow the re-rendered crew/unit lists

        // TASK-1673 — every populateMultiSelect above just replaced its
        // container's innerHTML, which DESTROYS the inline `.line-item-picker`
        // elements rendered under each checked resource. Nothing put them back,
        // so any roster/jobDay snapshot while the modal sat open silently
        // removed the rate control (the selection in
        // DispatchState.pendingLineItemSelections survived, but the dispatcher
        // could no longer see or change it). Snapshots fire constantly — this is
        // a plausible share of why line-pick adoption was 0.9%.
        const _refreshJob = DispatchState.jobs.find(j => j.id === jobId);
        const _refreshCustomerId = _refreshJob?.customerId;
        if (_refreshCustomerId && window.hasAddOnPackage?.('clockbilling')) {
          const _items = getContractScopedItems(_refreshCustomerId, false);
          if (_items.length > 0) {
            renderLineItemPickers('select-trucks', 'truck', _items);
            renderLineItemPickers('select-trailers', 'trailer', _items);
            renderLineItemPickers('select-assets', 'asset', _items);
            renderLineItemPickers('select-drivers', 'person', _items);
            renderLineItemPickers('select-helpers', 'person', _items);
            Object.keys(DispatchState.pendingLineItemSelections || {}).forEach(_syncRateControls);
          }
          // Deliberately NOT re-running applyLastUsedRateDefaults here: a
          // snapshot is not a dispatcher action, and re-seeding on one would let
          // a suggestion reappear moments after it was cleared to "Auto".
          renderAssignmentRatePanel();
        }
      }
    }
  }

  // Also refresh job modal crew sections if open
  refreshJobModalCrewIfOpen();
}

// Refresh job modal crew sections when data loads (preserve current selections)
function refreshJobModalCrewIfOpen() {
  const modal = document.getElementById('modal-job');
  // TASK-9391 (COD r1) — a project's equipment kit waiting on the roster is
  // retried once this re-seed has finished (deferred so it applies on top of it).
  // typeof: this function is also sliced into test sandboxes that predate the kit.
  if (typeof _pendingProjectKit !== 'undefined' && _pendingProjectKit) setTimeout(_flushPendingProjectKit, 0);
  if (modal && !modal.classList.contains('hidden')) {
    // TASK-1672 (COD integrated-diff pass) — if Edit Job opened BEFORE a
    // collection's snapshot landed, populateJobMultiSelect rendered a
    // "Loading..." placeholder and nothing was checked. Re-reading the DOM here
    // would preserve that empty set forever: the dispatcher sees an empty crew on
    // a job that has one, and anything they pick looks like a full replacement.
    // PER-DIMENSION, because each picker has its own dataLoaded flag — a second
    // COD pass caught a blanket re-seed clobbering truck/asset edits the
    // dispatcher made while only PEOPLE was still loading. Only a dimension that
    // was pending AND has now loaded is restored; every other picker keeps the
    // dispatcher's current selection, and the snapshot is patched for exactly the
    // restored dimensions so their pending edits still read as dirty on save.
    const _pending = DispatchState.jobCrewSeedPending;
    const _intent = DispatchState.jobCrewSeedIntent;
    if (_pending && _intent && ['people', 'trucks', 'trailers', 'assets'].some(k => _pending[k] && DispatchState.dataLoaded[k])) {
      const restore = {
        people: !!(_pending.people && DispatchState.dataLoaded.people),
        trucks: !!(_pending.trucks && DispatchState.dataLoaded.trucks),
        trailers: !!(_pending.trailers && DispatchState.dataLoaded.trailers),
        assets: !!(_pending.assets && DispatchState.dataLoaded.assets)
      };
      // 🔴 TASK-9212 — a COPY's intent is not a fixed list. Both of
      // _normalizeCarriedCrew's transforms need collections that were still
      // loading when Copy was clicked: legacy name→id resolution, and the
      // unique-rental / carried-'return' asset drop. Restoring the intent
      // verbatim hands back exactly what the prefill could not yet filter —
      // which is how a unique rental reached saveJob's CREATE inventory write and
      // took `deployedToJobId` off the job that still had the unit. Re-derive
      // from the raw carry, and ONLY for the dimensions being restored: a
      // dispatcher who edited trucks while people was still loading keeps their
      // edit, and their asset qty/direction changes are not overwritten either.
      if (_dupeCrewCarry) {
        const _renorm = _normalizeCarriedCrew(_dupeCrewCarry);
        if (restore.people) { _intent.drivers = _renorm.drivers; _intent.helpers = _renorm.helpers; }
        if (restore.trucks) _intent.trucks = _renorm.trucks;
        if (restore.trailers) _intent.trailers = _renorm.trailers;
        if (restore.assets) {
          _intent.assets = _renorm.assets;
          DispatchState.pendingAssetQtys = _renorm.assetQtys;
          DispatchState.pendingAssetDirections = _renorm.assetDirections;
          _announceDroppedCarriedAssets(_renorm.dropped);
          _markCopyAutoCarried(_renorm.assets); // TASK-9363 r3 — minus anything a human already touched
        }
      }
      const nextDrivers = restore.people ? _intent.drivers : getJobCrewSelected('drivers');
      const nextHelpers = restore.people ? _intent.helpers : getJobCrewSelected('helpers');
      const nextTrucks = restore.trucks ? _intent.trucks : getJobCrewSelected('trucks');
      const nextTrailers = restore.trailers ? _intent.trailers : getJobCrewSelected('trailers');
      const nextAssets = restore.assets ? _intent.assets : getJobCrewSelected('assets');
      Object.keys(restore).forEach(k => { if (restore[k]) _pending[k] = false; });
      populateJobCrewSections(nextDrivers, nextHelpers, nextTrucks, nextTrailers, nextAssets);
      if (DispatchState.jobCrewSnapshot) {
        const snap = DispatchState.jobCrewSnapshot;
        if (restore.people) { snap.drivers = _intent.drivers; snap.helpers = _intent.helpers; }
        if (restore.trucks) snap.trucks = _intent.trucks;
        if (restore.trailers) snap.trailers = _intent.trailers;
        if (restore.assets) {
          // Restore the qty/direction maps alongside the ids. editJob seeds
          // pendingAssetQtys/Directions from the jobDay, but the snapshot taken
          // during the loading window saw an EMPTY asset list, so it recorded
          // empty maps — patching ids alone would make an untouched form read
          // dirty and flatten per-day asset qty/direction (COD third pass).
          snap.assets = _intent.assets;
          const sideMaps = _deriveAssetSideMaps(_intent.assets);
          snap.assetQtys = sideMaps.assetQtys;
          snap.assetDirections = sideMaps.assetDirections;
        }
      }
      return;
    }
    // Read current selections from DOM before re-rendering
    const currentDrivers = getJobCrewSelected('drivers');
    const currentHelpers = getJobCrewSelected('helpers');
    const currentTrucks = getJobCrewSelected('trucks');
    const currentTrailers = getJobCrewSelected('trailers');
    const currentAssets = getJobCrewSelected('assets');
    populateJobCrewSections(currentDrivers, currentHelpers, currentTrucks, currentTrailers, currentAssets);
  }
}

// Populate crew/equipment sections in the Add/Edit Job modal
function populateJobCrewSections(selectedDrivers = [], selectedHelpers = [], selectedTrucks = [], selectedTrailers = [], selectedAssets = []) {
  // Merge drivers and helpers into one "People" selection (for meetings, everyone can be assigned)
  const allSelected = [...new Set([...selectedDrivers, ...selectedHelpers])];

  // Filter by showInDispatch but preserve already-selected items
  const visiblePeople = DispatchState.people
    .filter(p => p.showInDispatch !== false || allSelected.includes(p.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const visibleTrucks = DispatchState.trucks
    .filter(t => t.showInDispatch !== false || selectedTrucks.includes(t.id));
  const visibleTrailers = DispatchState.trailers
    .filter(t => t.showInDispatch !== false || selectedTrailers.includes(t.id));
  // TASK-10007 — starred assets first (pin order), the rest in name order as before.
  // (typeof: suites lift this function out alone.)
  const _assetsInPicker = DispatchState.assets
    .filter(a => a.showInDispatch !== false || selectedAssets.includes(a.id));
  const visibleAssets = typeof sortPinnedAssetsFirst === 'function'
    ? sortPinnedAssetsFirst(_assetsInPicker, _pinnedAssetIds(window.AppState?.settings)) : _assetsInPicker;

  populateJobMultiSelect('job-select-drivers', visiblePeople, allSelected, !DispatchState.dataLoaded.people);
  // Helpers section is now hidden - we use single "People" section
  populateJobMultiSelect('job-select-trucks', visibleTrucks, selectedTrucks, !DispatchState.dataLoaded.trucks);
  populateJobMultiSelect('job-select-trailers', visibleTrailers, selectedTrailers, !DispatchState.dataLoaded.trailers);
  populateJobMultiSelect('job-select-assets', visibleAssets, selectedAssets, !DispatchState.dataLoaded.assets);
}

/** TASK-9275 — a quantity-tracked asset whose on-hand count is BELOW ZERO.
 *  Same rule as roster.js `assetStockIsNegative` and billing.js
 *  `reviewAssetStockIsNegative`; deliberately duplicated rather than cross-imported
 *  (Pitfall #1 — a `?v=` cross-import is a second module instance), and
 *  deliberately NOT gated on `consumable` so the flag describes the data rather
 *  than the writer that produced it. */
function assetStockIsNegative(asset) {
  return !!asset && asset.trackingMode === 'quantity' && Number(asset.quantity || 0) < 0;
}

/** TASK-4810 — ONE reading of a quantity-mode RENTAL's stock. PURE.
 *  `quantity` = units owned, `deployedQty` = units out right now (the counter
 *  every deploy/return writer moves). `available` is floored at 0: when more is
 *  out than is owned (Split Rock's Rubber Mat, 3,765 out of 1,597) the true
 *  number is unknown, so the caller shows `drift` as "needs recount"
 *  instead of printing an impossible negative. Same rule as roster.js's copy
 *  (duplicated, not cross-imported — Pitfall #1). Non-rentals → null. */
function rentalStockView(asset) {
  if (!asset || asset.trackingMode !== 'quantity' || !(asset.quantityType === 'rental' || asset.isRental)) return null;
  const owned = Number(asset.quantity) || 0;
  const out = Math.max(0, Number(asset.deployedQty) || 0);
  return { owned, out, available: Math.max(0, owned - out), overBy: Math.max(0, out - owned), drift: out > owned };
}

// ============================================
// TASK-10007 — PINNED ASSET TOTALS strip (above the board) + starred-first
// Add Job picker. The company's list is tenants/{tid}.settings.pinnedAssetIds,
// starred on the Roster asset cards (roster.js togglePinnedAsset — Roster
// module or owner). Numbers are rentalStockView above, the SAME rule the
// Roster card prints from its own copy (task4810 pins the copies equal):
// owned = quantity, out = deployedQty floored at 0, available = owned − out
// floored at 0, and "Needs recount" whenever drift fires (TASK-4810 S3 —
// never a negative). Nothing here re-totals stock or adds a formula.
// ============================================

/** The stored pin list, cleaned: strings only, de-duplicated, order kept. PURE.
 *  (Same cleaning as roster.js pinnedAssetIdsFrom — a read-side parse, no math.) */
function _pinnedAssetIds(settings) {
  const raw = settings && Array.isArray(settings.pinnedAssetIds) ? settings.pinnedAssetIds : [];
  const out = [];
  for (const id of raw) {
    if (typeof id === 'string' && id && !out.includes(id)) out.push(id);
  }
  return out;
}

/**
 * One strip row per pinned id that still names an ACTIVE quantity-tracked
 * asset, in pin order. PURE. Ids that match nothing are skipped (a deleted or
 * deactivated asset never paints a blank chip). Rentals carry
 * rentalStockView's numbers untouched; other quantity items carry their
 * Total exactly as the card shows it (and `negative` = the TASK-9275 rule).
 */
function pinnedAssetStripRows(pinnedIds, assets, yards) {
  const pool = Array.isArray(assets) ? assets : [];
  const rows = [];
  for (const id of (Array.isArray(pinnedIds) ? pinnedIds : [])) {
    const a = pool.find(x => x && x.id === id);
    if (!a || a.active === false || a.trackingMode !== 'quantity') continue;
    const name = String(a.name || a.partNumber || 'Unnamed asset');
    // COD r1 (Domain): a yard transfer splits one item into one doc per yard,
    // so say which yard this count is (the card shows it too).
    const yard = a.yardId ? String(((yards || []).find(y => y && y.slug === a.yardId) || {}).label || a.yardName || '') : '';
    const v = rentalStockView(a);
    if (v) rows.push({ id, name, yard, rental: true, owned: v.owned, out: v.out, available: v.available, drift: v.drift });
    else rows.push({ id, name, yard, rental: false, total: Number(a.quantity) || 0, negative: assetStockIsNegative(a) });
  }
  return rows;
}

/** Who sees the strip. PURE. Never a driver-PIN / driver-link / public session
 *  (drivers can read the tenant doc, so this is gated in code, not CSS); else
 *  whoever holds Dispatch or Roster (checkModuleAccess — member modules are
 *  absolute; owner / legacy admin / demo pass there). */
function _pinnedAssetStripGate({ driverModePin, driverMode, isPublicAccess, accessToken, canDispatch, canRoster } = {}) {
  if (driverModePin === true || driverMode === true || isPublicAccess === true || !!accessToken) return false;
  return canDispatch === true || canRoster === true;
}
function _pinnedAssetStripVisible() {
  const S = window.AppState || {};
  const can = (m) => typeof window.checkModuleAccess === 'function' && window.checkModuleAccess(m, true) === true;
  return _pinnedAssetStripGate({ driverModePin: S.driverModePin, driverMode: S.driverMode, isPublicAccess: S.isPublicAccess,
    accessToken: S.accessToken, canDispatch: can('dispatch'), canRoster: can('roster') });
}

/** Strip markup. PURE apart from escapeHtml. `canOpen` = chips link to Roster. */
function pinnedAssetStripHtml(rows, canOpen) {
  if (!Array.isArray(rows) || rows.length === 0) return '';
  const esc = escapeHtml;
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  const chips = rows.map(r => {
    let line;
    if (r.rental && r.drift) {
      line = `<span style="color:#92400E;font-weight:700;">&#9888; Needs recount</span> &middot; ${fmt(r.out)} out &middot; ${fmt(r.owned)} owned`;
    } else if (r.rental) {
      // COD r2 (Domain): "now" in the text — a phone has no hover for the tooltip.
      line = `<strong style="color:var(--text-primary);">${fmt(r.available)}</strong> available now &middot; ${fmt(r.out)} out &middot; ${fmt(r.owned)} owned`;
    } else if (r.negative) {
      line = `<span style="color:#B91C1C;font-weight:700;">&#9888; Below zero ${fmt(r.total)}</span>`;
    } else {
      line = `Total <strong style="color:var(--text-primary);">${fmt(r.total)}</strong>`;
    }
    const inner = `<span class="pinned-asset-name" style="display:block;max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:700;font-size:0.85rem;color:var(--text-primary);">${esc(r.name)}${r.yard ? `<span class="pinned-asset-yard" style="font-weight:400;color:var(--text-secondary);"> &middot; ${esc(r.yard)}</span>` : ''}</span>
      <span class="pinned-asset-nums" style="display:block;white-space:nowrap;font-size:0.78rem;color:var(--text-secondary);">${line}</span>`;
    const box = 'flex:0 0 auto;text-align:left;padding:8px 12px;min-height:48px;border:1px solid var(--border-light,#E2E8F0);border-radius:8px;background:var(--bg-card,#fff);';
    const jsId = escJsAttr(r.id);
    // COD r1 (Domain): "available" is what is out right now — jobs booked for later days are not taken off.
    const tip = `${r.name}${r.yard ? ` (${r.yard})` : ''}: counts right now, same as the Roster card. Jobs booked for later days are not taken off.${canOpen ? ' Tap to open it in Roster.' : ''}`;
    return canOpen
      ? `<button type="button" class="pinned-asset-chip" data-asset-id="${esc(r.id)}" style="${box}cursor:pointer;font:inherit;" onclick="window.DispatchModule.openPinnedAsset('${jsId}')" title="${esc(tip)}">${inner}</button>`
      : `<div class="pinned-asset-chip" data-asset-id="${esc(r.id)}" style="${box}" title="${esc(tip)}">${inner}</div>`;
  }).join('');
  return `<div class="pinned-asset-row" role="list" aria-label="Pinned assets" style="display:flex;align-items:stretch;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;max-width:100%;min-width:0;padding-bottom:2px;">
    <span class="pinned-asset-label" style="flex:0 0 auto;display:flex;align-items:center;gap:4px;font-size:0.75rem;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;"><span class="material-icons" style="font-size:18px;color:#B45309;">star</span>Pinned</span>
    ${chips}
  </div>`;
}

function renderPinnedAssetStrip() {
  const el = document.getElementById('pinned-assets-strip');
  if (!el) return;
  const rows = _pinnedAssetStripVisible() ? pinnedAssetStripRows(_pinnedAssetIds(window.AppState?.settings), DispatchState.assets, window.AppState?.settings?.yards) : [];
  if (!rows.length) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  const canOpen = typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('roster', true) === true;
  el.innerHTML = pinnedAssetStripHtml(rows, canOpen);
  el.style.display = '';
}

/** Tap a chip → that asset in Roster (the maintenance.js _rosterPendingEdit hand-off). */
function openPinnedAsset(assetId) {
  if (!assetId) return;
  window._rosterPendingEdit = { type: 'asset', id: String(assetId) };
  window.location.hash = '/roster';
}

/** Add Job picker order: starred first (in pin order), everything else as it was. PURE. */
function sortPinnedAssetsFirst(items, pinnedIds) {
  const list = Array.isArray(items) ? items : [];
  const ids = Array.isArray(pinnedIds) ? pinnedIds : [];
  if (!ids.length) return list.slice();
  const rank = new Map(ids.map((id, i) => [id, i]));
  const pinned = list.filter(a => a && rank.has(a.id)).sort((a, b) => rank.get(a.id) - rank.get(b.id));
  return pinned.concat(list.filter(a => !(a && rank.has(a.id))));
}

// A star in Roster (this tab) or in another session (app.js tenant snapshot):
// repaint the strip and re-sort an open picker. refreshAssignmentModalIfOpen
// keeps what the dispatcher has ticked (reads the DOM first).
window.addEventListener('dd:pinned-assets-changed', () => {
  try { renderPinnedAssetStrip(); } catch (e) { console.warn('[Dispatch] pinned strip repaint failed:', e?.message || e); }
  try { refreshAssignmentModalIfOpen(); } catch (_) { /* no modal open */ }
});

/** TASK-4810 — which of the deploys a save is about to make would put MORE out
 *  than is owned. PURE. `deploys` = [{ assetId, qty }]; `assets` = the local
 *  roster mirror. An asset already in drift is always listed (nothing is known
 *  to be available). This feeds a WARNING, never a refusal: the field wins over
 *  the counter (TASK-4810 S2), so a stale stock number must not stop a
 *  dispatcher recording work that physically happened. */
function rentalOverDeploys(assets, deploys) {
  const list = [];
  const pool = Array.isArray(assets) ? assets : [];
  for (const d of (Array.isArray(deploys) ? deploys : [])) {
    const a = d && pool.find(x => x && x.id === d.assetId);
    const v = rentalStockView(a);
    if (!v) continue;
    const qty = Math.max(0, Number(d.qty) || 0);
    // `move` = what the save will actually add to deployedQty when that differs
    // from the typed qty (COD 2026-09-29: a pre-ledger return→deploy flip
    // reverses the return AND deploys — +2×qty). Display stays the typed qty.
    const move = d.move != null ? Math.max(0, Number(d.move) || 0) : qty;
    if (move <= 0) continue;
    if (v.drift || move > v.available) {
      list.push({ assetId: a.id, name: a.name || a.partNumber || 'Rental', qty, owned: v.owned, out: v.out,
        available: v.available, move, outAfter: v.out + move, drift: v.drift });
    }
  }
  return list;
}

/** TASK-4810 — the confirm for rentalOverDeploys(). Returns true to proceed.
 *  FAILS OPEN (no showConfirm → proceed), same as _dfConfirmSeating: a missing
 *  helper must never turn a warning into a silent block. Either answer is
 *  logged; the per-row stamp (rentalOverDeployStamp) is the durable record. */
async function _confirmRentalOverDeploy(overs, ctx) {
  if (!Array.isArray(overs) || overs.length === 0) return true;
  const fmt = (n) => Number(n || 0).toLocaleString('en-US');
  // A pre-ledger return→deploy flip moves 2× what was typed — say so in BOTH arms.
  const moves = (o) => (o.move && o.move !== o.qty ? ` (this undoes the day's return first, so ${fmt(o.move)} go out in total)` : '');
  const lines = overs.map(o => o.drift
    ? `• ${o.name}: ${fmt(o.out)} already out against ${fmt(o.owned)} owned — the count needs reconciling. Deploying ${fmt(o.qty)} more${moves(o)}.`
    : `• ${o.name}: deploying ${fmt(o.qty)}${moves(o)}, only ${fmt(o.available)} available (${fmt(o.owned)} owned) — ${fmt(o.outAfter)} would be out.`).join('\n');
  console.warn('[Rental] TASK-4810 over-deploy', { ...ctx, overs });
  let ok = true;
  if (typeof window.showConfirm === 'function') {
    ok = (await window.showConfirm(
      overs.length === 1 ? 'More out than you own' : `${overs.length} rentals: more out than you own`,
      `${lines}\n\nIf a quantity is a typo, go back and fix it. If the equipment really is going out, save anyway — the out count will read "needs recount" until someone recounts it in Roster › Assets.`,
      { confirmText: 'Save Anyway', cancelText: 'Go Back' }
    )) === true;
  }
  try {
    window.logAudit?.('inventory', ok ? 'RENTAL_OVER_DEPLOY_CONFIRMED' : 'RENTAL_OVER_DEPLOY_DECLINED', {
      ...ctx, items: overs.map(o => ({ assetId: o.assetId, qty: o.qty, owned: o.owned, outBefore: o.out }))
    });
  } catch (e) { console.warn('[Rental] over-deploy audit failed:', e); }
  return ok;
}

/** TASK-4810 — rentals whose membership, quantity or direction differs between
 *  the Edit Job form's on-open snapshot and what it is saving. That form writes
 *  the day but moves no stock, so each of these leaves the out count as it
 *  was. PURE. A missing snapshot → [] (nothing known to compare). */
function rentalChangesNotMovingStock(before, after, assets) {
  if (!before || !after) return [];
  const pool = Array.isArray(assets) ? assets : [];
  const isRental = (id) => { const a = pool.find(x => x && x.id === id); return !!a && (a.quantityType === 'rental' || a.isRental); };
  const nameOf = (id) => { const a = pool.find(x => x && x.id === id); return (a && (a.name || a.partNumber)) || 'Rental'; };
  const bA = Array.isArray(before.assets) ? before.assets : [];
  const aA = Array.isArray(after.assets) ? after.assets : [];
  const q = (m, id) => Math.max(1, Number(m && m[id]) || 1);
  const d = (m, id) => (m && m[id]) || 'deploy';
  const out = [];
  for (const id of new Set([...bA, ...aA])) {
    if (!isRental(id)) continue;
    const was = bA.includes(id), now = aA.includes(id);
    let change = null;
    if (was && !now) change = 'removed';
    else if (!was && now) change = 'added';
    else if (d(before.assetDirections, id) !== d(after.assetDirections, id)) change = 'direction';
    else if (q(before.assetQtys, id) !== q(after.assetQtys, id)) change = 'quantity';
    if (change) out.push({ assetId: id, name: nameOf(id), change });
  }
  return out;
}

/** TASK-4810 — stamp for the assetTransactions row of a deploy that pushed
 *  out past owned. `{}` when it did not, so ordinary rows are unchanged. PURE. */
function rentalOverDeployStamp(overs, assetId) {
  const o = (Array.isArray(overs) ? overs : []).find(x => x && x.assetId === assetId);
  return o ? { overDeploy: { ownedAtDeploy: o.owned, outBefore: o.out, availableBefore: o.available } } : {};
}

/** TASK-9275 — the stock pill on an asset row in either crew picker.
 *
 *  Both write paths warn-and-proceed by design (saveJob ~:11234, billing's
 *  saveReviewTicket) — refusing the write would strand the dispatcher mid-save.
 *  So the picker is where the negative has to STAY visible: before, this rendered
 *  `Stock: -17` in `--text-secondary`, typographically identical to `Stock: 17`,
 *  which is how a count walks -1 → -3 → -11 → -17 with nobody noticing.
 *
 *  Hard-coded hex, not a CSS var: semantic warning colour is separate from the
 *  brand accent, an undefined var() silently kills the whole property, and this
 *  matches the `#B45309` double-book tag two functions down. */
function assetStockTagHtml(item, availableQty, qtyLabel) {
  const neg = assetStockIsNegative(item);
  // TASK-4810 — a rental with more out than owned printed "Available: -2168".
  // The real number is unknown, so say that instead of printing a wrong one.
  const rv = neg ? null : rentalStockView(item);
  if (rv && rv.drift) {
    const escAttrFn = window.escAttr || escapeHtml;
    const fmt = (n) => Number(n || 0).toLocaleString('en-US');
    const tip = escAttrFn(`${fmt(rv.out)} out against ${fmt(rv.owned)} owned — more is out than you own, so the available count is unknown. A return was missed, a quantity was edited after deploying, or stock was never entered. Recount in Roster › Assets.`);
    return `<span class="asset-qty-tag is-drift" title="${tip}" style="margin-left:auto;font-size:0.75rem;font-weight:700;color:#92400E;">&#9888; Needs recount</span>`;
  }
  if (!neg) {
    return `<span class="asset-qty-tag" style="margin-left:auto;font-size:0.75rem;color:var(--text-secondary);">${qtyLabel}: ${availableQty}</span>`;
  }
  const escAttrFn = window.escAttr || escapeHtml;
  // The pill prints `availableQty` (quantity − deployedQty) while the flag tests
  // `quantity`, so the tooltip names the on-hand count explicitly rather than
  // leaving a reader to assume the two numbers are the same thing. They cannot
  // contradict each other in the flagged direction — deployedQty is floored at 0
  // (`decrementDeployedQtyFloored`), so quantity < 0 always implies availableQty
  // <= quantity < 0. The REVERSE case (available below zero because a rental is
  // over-deployed while stock is fine) is a different condition and is NOT
  // flagged here on purpose — that is over-deployment, not negative stock.
  const tip = escAttrFn(`On hand: ${Number(item.quantity || 0)}. Stock is below zero — more has been consumed than was ever booked in. Fix the count in Roster › Assets.`);
  return `<span class="asset-qty-tag is-negative" title="${tip}" style="margin-left:auto;font-size:0.75rem;font-weight:700;color:#B91C1C;">&#9888; ${qtyLabel}: ${availableQty}</span>`;
}

// Helper function for job modal multi-selects (uses job- prefix for IDs)
function populateJobMultiSelect(containerId, items, selected, isLoading = false) {
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn('[Dispatch] Job crew container not found:', containerId);
    return;
  }

  // Extract section key (e.g., 'job-select-drivers' → 'drivers')
  const sectionKey = containerId.replace('job-select-', '');
  const selectedCount = selected.length;
  const sectionEl = document.getElementById(`job-section-${sectionKey}`);
  const countEl = document.getElementById(`job-count-${sectionKey}`);

  // Show loading state if data hasn't loaded yet
  if (isLoading) {
    container.innerHTML = '<p class="text-muted"><span class="loading-spinner-small"></span> Loading...</p>';
    return;
  }

  if (!items || items.length === 0) {
    container.innerHTML = '<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">inbox</span>No items available</div>';
    if (sectionEl) sectionEl.classList.remove('open');
    return;
  }

  // Add search bar when 5+ items
  const sectionLabelMap = { drivers: 'people', trucks: 'vehicles', trailers: 'trailers', assets: 'assets' };
  let searchHtml = '';
  if (items.length >= 5) {
    searchHtml = `<input type="search" class="form-control form-control-sm multi-select-search"
      placeholder="Search ${sectionLabelMap[sectionKey] || sectionKey}..." autocomplete="off"
      oninput="window.DispatchModule.filterMultiSelect('${escJsAttr(containerId)}', this.value)"
      style="margin-bottom:8px;font-size:0.8rem;">`;
  }

  // Select-all button inside the body (only visible when section is expanded).
  // Previously this was a button in the collapsible header, but on mobile it was
  // too easy to tap accidentally when trying to expand the section, causing all
  // items to get checked unintentionally.
  let selectAllHtml = '';
  if (items.length > 1) {
    selectAllHtml = `<button type="button" class="btn-select-all-inline"
      onclick="event.stopPropagation(); window.DispatchModule.selectAllCrew('${escJsAttr(sectionKey)}')"
      style="display:block;margin-bottom:8px;padding:4px 10px;font-size:0.75rem;border:1px solid var(--border-light);background:#fff;border-radius:6px;color:var(--text-secondary);cursor:pointer;">
      Select all ${sectionLabelMap[sectionKey] || sectionKey}
    </button>`;
  }

  // TASK-9227 — the job form's ONE people picker is `job-select-drivers`:
  // populateJobCrewSections merges drivers + helpers into it (`allSelected`) and
  // the `job-select-helpers` section is deliberately hidden and never populated
  // with checkboxes — `renderLineItemPickers('job-select-helpers', …)` only
  // decorates rows that would already be there, so it is a no-op today.
  // (COD Blast-Radius called this out; the truth turned out to be neither its
  // claim nor mine — verified by grepping every `job-select-helpers` site and
  // every `populateJobMultiSelect(` call at HEAD.) `helpers` is kept in the test
  // below so the badge survives if that section is ever un-hidden. This renderer
  // has neither a role tag nor a double-book badge, so it is the poorer of the
  // two crew pickers and this is the only compliance signal on it.
  const _dfIsJobPeople = sectionKey === 'drivers' || sectionKey === 'helpers';
  // TASK-9510 — yard/branch on same-name asset rows.
  const _assetRowLabels = containerId === 'job-select-assets' ? assetPickerRowLabels(items, window.AppState?.settings?.yards, _assetPickerName) : null;
  // TASK-10007 — a ★ on starred asset rows (populateJobCrewSections sorts them first).
  const _pinnedRowIds = (containerId === 'job-select-assets' && typeof _pinnedAssetIds === 'function') ? new Set(_pinnedAssetIds(window.AppState?.settings)) : null;

  container.innerHTML = searchHtml + selectAllHtml + items.map(item => {
    const isSelected = selected.includes(item.id);
    const flagTag = _dfIsJobPeople ? _dfBadgeHtml(item) : '';
    // TASK-9258 — "a template put this here and nobody has checked it". Only
    // ever true for a row this form's apply newly added; it clears the moment
    // the row is touched. People are excluded structurally, not by this test: a
    // template stores crew COUNTS and no person ids, so no people key is ever
    // added to the unconfirmed list.
    const tplChip = jobTemplateChipHtml(sectionKey, item.id);
    // Quantity input for quantity-tracked assets (standard, consumable, rental)
    const isQtyAsset = containerId === 'job-select-assets' && item.trackingMode === 'quantity';
    const existingQty = isQtyAsset ? (DispatchState.pendingAssetQtys?.[item.id] || 1) : 0;
    const qtyLabel = item.consumable ? 'Stock' : 'Available';
    const availableQty = (item.quantity || 0) - (item.deployedQty || 0);
    // TASK-9275 — flagged when the count is below zero; plain pill otherwise.
    const qtyTag = isQtyAsset ? `${assetStockTagHtml(item, availableQty, qtyLabel)}
      <input type="number" class="asset-dispatch-qty" data-asset-id="${escapeHtml(item.id)}" min="1" value="${existingQty}" onclick="event.stopPropagation();" oninput="window.DispatchModule.setAssetQty('${escJsAttr(item.id)}', this.value, this)" onchange="window.DispatchModule.setAssetQty('${escJsAttr(item.id)}', this.value, this)" style="width:56px;flex-shrink:0;text-align:center;padding:2px 4px;font-size:0.8rem;border:1px solid var(--border-light);border-radius:4px;margin-left:4px;">` : '';

    // Deploy/Return toggle for rental assets
    const isRentalAsset = containerId === 'job-select-assets' && (item.quantityType === 'rental' || item.isRental);
    const curDir = DispatchState.pendingAssetDirections?.[item.id] || 'deploy';
    const dirToggle = isRentalAsset ? `<button type="button" class="asset-direction-toggle ${curDir}" onclick="event.stopPropagation(); window.DispatchModule.toggleAssetDirection('${escJsAttr(item.id)}', this)" title="${curDir === 'deploy' ? 'Deploying to job site' : 'Returning to yard'}">
      <span class="material-icons" style="font-size:13px;">${curDir === 'deploy' ? 'arrow_forward' : 'arrow_back'}</span>
      <span>${curDir === 'deploy' ? 'Deploy' : 'Return'}</span>
    </button>` : '';

    // Inventory preview for selected rental assets
    let invPreview = '';
    if (isRentalAsset && isSelected) {
      invPreview = buildInventoryPreview(item, curDir, existingQty || 1);
    }

    return `
      <div class="checkbox-item ${isSelected ? 'checked' : ''}" onclick="window.DispatchModule.toggleJobCheckbox(this, '${escJsAttr(sectionKey)}')">
        <input type="checkbox" value="${item.id}" ${isSelected ? 'checked' : ''}>
        <span class="checkbox-name">${_pinnedRowIds && _pinnedRowIds.has(item.id) ? '<span class="material-icons asset-pinned-mark" title="Pinned" aria-label="Pinned" style="font-size:16px;vertical-align:-3px;margin-right:2px;color:#B45309;">star</span>' : ''}${escapeHtml(window.getUnitDisplay ? (window.getUnitDisplay(item) || item.name || 'Unknown') : (item.name || item.unit || 'Unknown'))}${_assetRowLabels && _assetRowLabels.has(item.id) ? `<span class="asset-yard-tag" style="display:block;font-size:0.72rem;font-weight:400;line-height:1.2;color:var(--text-secondary);">${escapeHtml(_assetRowLabels.get(item.id))}</span>` : ''}${_rentalHeldTagHtml(item, containerId)}</span>
        ${flagTag}
        ${tplChip}
        ${qtyTag}
        ${dirToggle}
        ${invPreview}
      </div>
    `;
  }).join('');

  // Update count badge
  if (countEl) {
    countEl.textContent = selectedCount;
    countEl.style.display = selectedCount > 0 ? '' : 'none';
  }

  // Keep sections closed by default
  if (sectionEl) {
    sectionEl.classList.remove('open');
  }
}

// Toggle checkbox in job modal crew sections
function toggleJobCheckbox(element, sectionKey) {
  const checkbox = element.querySelector('input[type="checkbox"]');
  if (checkbox) {
    checkbox.checked = !checkbox.checked;
    element.classList.toggle('checked', checkbox.checked);
    updateJobSectionCount(sectionKey);
    // TASK-9363 r3 — the dispatcher touched this asset: on a Copy form it is
    // now their pick, checked against the yard only (never "still out on #N").
    if (sectionKey === 'assets' && _dupeCrewCarry) _retireCopyCarriedAsset(checkbox.value);
    // TASK-10006 S2 — a rental ticked on a "come back" type starts as Return.
    if (sectionKey === 'assets') _jobTypeRentalDirOnTick(checkbox.value, checkbox.checked);

    // TASK-9258 — a human touched the row a template seeded, in either
    // direction. Ticking it is confirming it; UN-ticking it is confirming it
    // too, and the mark has to go either way — a removed row that stayed marked
    // would be filtered out of the jobDay's unconfirmed list by
    // buildJobDayTemplateFields anyway, but it would keep the count in the
    // notice claiming a row that is no longer on the job.
    confirmTemplateRowFromDom(sectionKey, checkbox.value, element);

    // DOT compliance check on driver/truck/trailer/asset change. TASK-9198 —
    // 'assets' was missing, so an asset safety hold would never re-run the check.
    if (['drivers', 'helpers', 'trucks', 'trailers', 'assets'].includes(sectionKey)) {
      setTimeout(runJobComplianceCheck, 50);
    }

    // Update live load calculator when trucks or assets change
    if (sectionKey === 'trucks' || sectionKey === 'assets') updateJobLoadCalcLive();

    // Line item picker: add/remove on toggle (gated by clockbilling)
    if (!window.hasAddOnPackage?.('clockbilling')) return;
    // TASK-10106 S1a — a PIN driver never sees a rate (Chris 10/26): no price
    // book read, no Rate picker. (The rules deny the read anyway.)
    if (window.AppState?.driverModePin === true) return;

    const resourceId = checkbox.value;
    const kindMap = { trucks: 'truck', trailers: 'trailer', assets: 'asset', drivers: 'person', helpers: 'person' };
    const kind = kindMap[sectionKey];
    if (kind) {
      const resourceKey = `${kind}:${resourceId}`;
      if (!checkbox.checked) {
        element.parentElement?.querySelector(`.line-item-picker[data-resource="${resourceKey}"]`)?.remove();
        delete DispatchState.pendingJobLineItemSelections[resourceKey];
      } else {
        const customerId = document.getElementById('job-customer')?.value;
        if (customerId) {
          const cached = DispatchState.lineItemCache[customerId];
          if (cached?.items?.length > 0) {
            const scopedItems = getContractScopedItems(customerId, true);
            renderPickerForResource(element, kind, resourceId, scopedItems);
          } else {
            // Cache not warm yet — load and render when ready
            loadCustomerLineItems(customerId).then(({ items }) => {
              if (items.length > 0 && checkbox.checked) {
                const scopedItems = getContractScopedItems(customerId, true);
                renderPickerForResource(element, kind, resourceId, scopedItems);
              }
            });
          }
        }
      }
    }
  }
}

// Update count badge in job modal crew sections
function updateJobSectionCount(sectionKey) {
  setTimeout(() => {
    const container = document.getElementById(`job-select-${sectionKey}`);
    const countEl = document.getElementById(`job-count-${sectionKey}`);
    if (!container || !countEl) return;
    const count = container.querySelectorAll('input:checked').length;
    countEl.textContent = count;
    countEl.style.display = count > 0 ? '' : 'none';
  }, 10);
}

// Get selected items from job modal crew sections
function getJobCrewSelected(sectionKey) {
  const container = document.getElementById(`job-select-${sectionKey}`);
  if (!container) return [];
  return Array.from(container.querySelectorAll('input:checked')).map(i => i.value);
}

// ─── TASK-1672 — Edit Job crew: deterministic seed + dirty-check ─────────────
// Two compounding defects lived in the Edit Job path. (1) The crew picker
// seeded from `DispatchState.jobDays.find(d => d.jobId === jobId)` — an
// ARBITRARY day, because that array arrives in snapshot order, never date
// order. (2) saveJob's edit branch then wrote that one selection onto EVERY
// jobDay with no dirty check, so editing only a PO / notes / location flattened
// the per-day crews a dispatcher had set through Add Day — and WHICH crew
// survived depended on in-memory array order, which is why Steve reported it as
// "not sticking correctly" rather than consistently wrong.
// Crew is PER-DAY: saveAssignment writes a single jobDay and
// buildInheritedJobDay COPIES rather than shares. jobDay crew is read as ground
// truth by clock.js, jobboard.js, billing.js, bi.js, jsa.js and
// driverScheduleHelpers.js — a flatten corrupts far more than this board.
// Kept dependency-light (pure, or DOM/DispatchState-only) so they unit-test
// outside a browser — see tests/jobday-crew-flatten.unit.cjs.

// jobDay.date is a Firestore Timestamp on live rows and a string on some
// GAS-imported ones — same normalization buildInheritedJobDay uses.
function _jobDayDateMs(d) {
  const v = d?.date?.toDate?.() || (d?.date != null ? new Date(d.date) : null);
  const ms = v ? v.getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
}

// ── TASK-4685 ───────────────────────────────────────────────────────────────
// TERMINAL jobDay statuses, and what a day carrying one becomes when its DATE
// moves (or when a new day is minted off it as a template).
//
// Why this is not cosmetic. `jd.status === 'cancelled'` is a hard SKIP in
// buildDoubleBookIndex. A day that rides a date change with a stale `cancelled`
// on it is therefore INVISIBLE to conflict detection — the truck and the driver
// read as FREE while a live job sits on them. (The TASK-4685 board entry also
// cited the month-cell counter; that one is `job.status === 'cancelled'` in
// renderDriverCalendarView, not the DAY's status, so it is NOT part of this
// blast radius. Verified at HEAD.) It also paints struck-through with a red
// Cancelled badge and drops out of the default search filter.
// Nothing throws, no toast, no audit row: the fail-open-and-quiet family from
// [[lesson_control_chars_fail_open_silently]]. Chris hit it live 2026-08-31 on
// jobDay Fpxtwqv42s6GU79OWmHb (re-added Loenbro/Hunt Oil-323 rendered struck
// through with a red Cancelled badge). TASK-4567/4654 closed this door for
// DELETES; the MOVE door was never checked.
//
// The rule is restoreJobDay's rule (:18844) — deliberately the same one, not a
// second copy of the policy. Scope is TERMINAL ONLY: 'en-route', 'assigned' and
// every other live status move date untouched.
const TERMINAL_JOBDAY_STATUSES = ['cancelled', 'finished'];

function _isTerminalJobDayStatus(status) {
  return TERMINAL_JOBDAY_STATUSES.includes(status);
}

// Returns the status a moved/templated day should land on, or NULL when the
// status must be left exactly as it is. Null is the overwhelmingly common
// answer and is what keeps this change tight — a caller that treats null as
// "write 'scheduled'" would flatten every live status on the board.
function _statusAfterJobDayMove(prevData) {
  if (!_isTerminalJobDayStatus(prevData?.status)) return null;
  const hasAssignments = (prevData?.drivers?.length > 0 || prevData?.trucks?.length > 0);
  return hasAssignments ? 'assigned' : 'scheduled';
}

// TASK-9331 — PURE. The rental detail (`assetQtys` / `assetDirections`) a day
// minted by saveJob's date-sync carries from its template day. Same stance as
// buildInheritedJobDay: copy a map only when it is non-empty, so an ordinary
// day gains no keys.
//
// ONE deliberate difference. The template is the LATEST existing day, and a
// date-sync can create days BEFORE it (start date moved earlier). A 'return'
// on the latest day describes the END of the rental; stamping it onto an
// earlier date would zero-bill a day the unit was still out and make that day
// look like a second return (removeJobDay's stillReturning, cancelJobDay's
// returnInspections sweep). So a date earlier than the template drops its
// 'return' entries (they fall back to 'deploy'); quantities still carry. Dates
// after the template carry verbatim — a returned rental stays returned —
// BUT ONLY WHILE THAT RETURN DAY STILL EXISTS. When the date edit moves the
// whole window, the template day is in the same batch's delete set; its return
// then describes nothing, and copying it would zero-bill every new day (COD
// round 2, Independent Code Reviewer). `templateSurvives === false` drops the
// returns everywhere; the dispatcher re-marks the new last day.
// `dateStr` is the loop's UTC `YYYY-MM-DD` key; the template date is keyed the
// same way. An undatable template carries verbatim (add-day parity).
function _dateSyncAssetDetail(templateJD, dateStr, templateSurvives) {
  const out = {};
  const qtys = templateJD?.assetQtys;
  if (qtys && Object.keys(qtys).length > 0) out.assetQtys = qtys;
  let dirs = templateJD?.assetDirections;
  if (dirs && Object.keys(dirs).length > 0) {
    const tplMs = _jobDayDateMs(templateJD);
    const tplStr = tplMs === null ? null : new Date(tplMs).toISOString().slice(0, 10);
    const beforeTemplate = tplStr && typeof dateStr === 'string' && dateStr < tplStr;
    if (templateSurvives === false || beforeTemplate) {
      dirs = Object.fromEntries(Object.entries(dirs).filter(([, d]) => d !== 'return'));
    }
    if (Object.keys(dirs).length > 0) out.assetDirections = dirs;
  }
  return out;
}

// SAY IT OUT LOUD. A status change that alters what a driver sees needs a row
// and a sentence — [[lesson_workweek_silently_flipped_sun_sat]]. House pattern
// from TASK-4567/4654.
//
// Writes to `jobAuditLog`, NOT the general `auditLog`: every other JOB_DAY_*
// action goes there and the tech-support runbook routes job forensics there
// (docs/runbooks/tech-support-starts-here.md:41). jobAuditLog is a raw addDoc
// with no undefined-stripping (unlike window.logAudit), so EVERY field is
// coerced to null — an undefined value throws inside addDoc and the catch turns
// a whole audit action into zero rows ([[lesson_audit_writes_fail_silently_on_undefined]]).
async function _announceJobDayStatusReset(ctx) {
  const prevStatus = ctx?.prevStatus || null;
  const newStatus = ctx?.newStatus || null;
  const newDateStr = ctx?.newDateStr || null;
  // silentToast: the caller already told the user, in a dialog they had to
  // answer. Only the drag path sets it; the saveJob shift has no dialog, so
  // there the toast IS the entire signal and must never be suppressed.
  if (!ctx?.silentToast) {
    try {
      const label = newDateStr
        ? new Date(newDateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        : 'the new date';
      window.showToast?.(
        `That day was ${prevStatus === 'finished' ? 'marked finished' : 'cancelled'} — it's back on the board as ${newStatus} for ${label}.`,
        'warning'
      );
    } catch (e) {
      console.warn('[Dispatch] TASK-4685: status-reset toast failed', e);
    }
  }
  try {
    await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
      action: 'JOB_DAY_STATUS_RESET_ON_MOVE',
      source: ctx?.source || 'unknown',
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'unknown',
      jobId: ctx?.jobId || null,
      jobDayId: ctx?.jobDayId || null,
      oldStatus: prevStatus,
      newStatus: newStatus,
      oldDate: ctx?.oldDateStr || null,
      newDate: newDateStr
    });
  } catch (e) {
    console.warn('[Dispatch] TASK-4685: status-reset audit log failed:', e);
  }
}

// Chronologically FIRST day of a job. Undated rows sort last, so a row with no
// usable date can never win the seed over a real one.
function _pickFirstJobDayByDate(jobDays, jobId) {
  const days = (jobDays || []).filter(d => d && d.jobId === jobId);
  if (days.length === 0) return undefined;
  return days.slice().sort((a, b) => {
    const ma = _jobDayDateMs(a), mb = _jobDayDateMs(b);
    if (ma === null && mb === null) return 0;
    if (ma === null) return 1;
    if (mb === null) return -1;
    return ma - mb;
  })[0];
}

// TASK-9212 — chronologically LAST day of a job, same undated-rows-sort-last
// rule as the first-day pick above. Copy to New Job seeds its crew from here,
// not from day one: a copy is forward-looking work, so the crew that ran the job
// MOST RECENTLY is the better default. On a single-day job the two are the same
// row. Pass more than one pool (live + search caches) because a finished job's
// days live only in searchJobDays; duplicates across pools are harmless, the
// comparison is on date.
function _pickLastJobDayByDate(jobDays, jobId) {
  const days = (jobDays || []).filter(d => d && d.jobId === jobId);
  if (days.length === 0) return undefined;
  let best, bestMs = null;
  for (const d of days) {
    const ms = _jobDayDateMs(d);
    if (ms === null) continue;
    if (bestMs === null || ms > bestMs) { bestMs = ms; best = d; }
  }
  // Every row undated — fall back to the first one rather than returning nothing,
  // so a legacy job still carries SOME crew instead of silently carrying none.
  return best || days[0];
}

// TASK-4614 — the day the Edit Job form must seed from. showJobDetails already
// knows which day-card was clicked; editJob dropped that argument and always
// seeded from the chronologically FIRST day, so a crew edit made while standing
// on Aug 26 was handed Aug 6's crew and then saved it back ("keeps saving back
// to the original crew" — Steve, 2026-08-25). The first-day pick survives only
// as the no-context fallback: a search card, the quickview escape hatch, or any
// caller with no day in hand. `pool2` is the search cache — a finished job opened
// from Search has its days in searchJobDays, not the 90-day-windowed live array.
function _pickEditJobSeedDay(jobDays, jobId, jobDayId, pool2) {
  const wanted = String(jobDayId == null ? '' : jobDayId).trim();
  if (wanted) {
    // COD Code Reviewer ⚠️ — a day id that was ASKED FOR and did not resolve
    // (deleted while the details modal sat open, or a synthetic legacy id) must
    // NOT silently fall through to day one. Falling back there is the whole
    // defect wearing a different hat: the dispatcher believes they are on the
    // day they clicked and the write lands three weeks earlier, possibly on a
    // ticketed day. Return nothing; editJob turns that into a null scope and
    // saveJob refuses the crew write out loud.
    return (jobDays || []).find(d => d && d.id === wanted && d.jobId === jobId)
      || (pool2 || []).find(d => d && d.id === wanted && d.jobId === jobId)
      || undefined;
  }
  return _pickFirstJobDayByDate(jobDays, jobId) || _pickFirstJobDayByDate(pool2, jobId);
}

// Human label for the day the crew section is scoped to. jobDay.date is anchored
// to NOON local precisely so this renders the intended calendar day in every US
// timezone (memory/lesson_jobday_date_anchors_to_noon_local).
function _formatJobDayLabel(jobDay) {
  const ms = _jobDayDateMs(jobDay);
  if (ms === null) return '';
  try {
    return new Date(ms).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch (e) { return ''; }
}

// Chronologically LAST jobDay doc snapshot. saveJob's date-sync used
// `docs[docs.length - 1]` and its comment called that "the most recent existing
// jobDay" — the query is unordered, so it was the same arbitrary-index bug as
// the seed. Falls back to the old index only when no row carries a usable date.
function _pickLatestJobDayDoc(docs) {
  const list = (docs || []).filter(Boolean);
  if (list.length === 0) return null;
  let best = null, bestMs = null;
  for (const d of list) {
    const data = typeof d.data === 'function' ? d.data() : d;
    const ms = _jobDayDateMs(data);
    if (ms === null) continue;
    if (bestMs === null || ms > bestMs) { bestMs = ms; best = d; }
  }
  return best || list[list.length - 1];
}

// Crew arrays are MEMBERSHIP lists downstream — order carries no meaning, so
// compare as sets. Entries are Firestore ids OR GAS-imported name strings.
function _sameIdSet(a, b) {
  const A = new Set((a || []).map(String));
  const B = new Set((b || []).map(String));
  if (A.size !== B.size) return false;
  for (const v of A) if (!B.has(v)) return false;
  return true;
}

// Key-order-independent deep compare for the object-valued fields
// (assetQtys, assetDirections, lineItemSelections).
function _stableStringify(v) {
  if (v === undefined) return 'null';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return '[' + v.map(_stableStringify).join(',') + ']';
  return '{' + Object.keys(v).sort().map(k => JSON.stringify(k) + ':' + _stableStringify(v[k])).join(',') + '}';
}
function _sameDeep(a, b) { return _stableStringify(a) === _stableStringify(b); }

// The exact payload the Edit Job form would write. Used for BOTH the on-open
// snapshot and the on-save read so the two sides normalize identically —
// assetQtys only carries qty>1 on a selected asset, assetDirections only
// carries 'return'. Reading the DOM (not the raw jobDay) means a roster row
// hidden by showInDispatch, or an id no longer on the roster, can't register as
// a user edit.
// assetQtys/assetDirections hang off the SELECTED asset ids, so they have to be
// derived from the same list — a snapshot patch that restores `assets` without
// them reads as a crew edit on the next save (COD third pass).
function _deriveAssetSideMaps(assets) {
  const assetQtys = {};
  const assetDirections = {};
  (assets || []).forEach(id => {
    const qty = Number(DispatchState.pendingAssetQtys?.[id]);
    if (Number.isFinite(qty) && qty > 1) assetQtys[id] = qty;
    if (DispatchState.pendingAssetDirections?.[id] === 'return') assetDirections[id] = 'return';
  });
  return { assetQtys, assetDirections };
}

function _buildJobCrewPayload() {
  const assets = getJobCrewSelected('assets');
  const { assetQtys, assetDirections } = _deriveAssetSideMaps(assets);
  return {
    drivers: getJobCrewSelected('drivers'),
    helpers: getJobCrewSelected('helpers'),
    trucks: getJobCrewSelected('trucks'),
    trailers: getJobCrewSelected('trailers'),
    assets,
    assetQtys,
    assetDirections,
    lineItemSelections: { ...(DispatchState.pendingJobLineItemSelections || {}) },
    selectedContractId: DispatchState.pendingJobSelectedContractId || null
  };
}

// Also reused per-day against a stored jobDay, to skip days that already match.
// A false NEGATIVE here (thinks unchanged when changed) only drops a crew edit —
// recoverable, and the dispatcher sees it immediately. A false POSITIVE
// re-flattens every day, which IS the bug. So a MISSING snapshot falls back to
// the legacy always-write rather than silently swallowing a real crew edit.
function _jobCrewDirty(before, after) {
  if (!before) return true;
  // TASK-9510 — create/Assign Crew now store qty 1 explicitly, while the edit
  // form's payload still omits it (`_deriveAssetSideMaps`, qty > 1). {a:1} and
  // {} mean the same thing; without this a stored day with an explicit 1 would
  // read as a crew edit and get re-flattened (the false positive this compare
  // exists to prevent). Inline, not a helper, so every harness that lifts this
  // function keeps working.
  const noOnes = (m) => {
    const out = {};
    Object.keys(m || {}).forEach(k => { const n = Number(m[k]); if (!(Number.isFinite(n) && n <= 1)) out[k] = m[k]; });
    return out;
  };
  return !(
    _sameIdSet(before.drivers, after.drivers) &&
    _sameIdSet(before.helpers, after.helpers) &&
    _sameIdSet(before.trucks, after.trucks) &&
    _sameIdSet(before.trailers, after.trailers) &&
    _sameIdSet(before.assets, after.assets) &&
    _sameDeep(noOnes(before.assetQtys), noOnes(after.assetQtys)) &&
    _sameDeep(before.assetDirections || {}, after.assetDirections || {})
  );
}

// Rates were NEVER written by the edit path, so a missing snapshot must not
// start writing them — only a proven change does.
function _jobRatesDirty(before, after) {
  if (!before) return false;
  return !_sameDeep(before.lineItemSelections || {}, after.lineItemSelections || {}) ||
    (before.selectedContractId || null) !== (after.selectedContractId || null);
}

// The whole flatten decision for ONE jobDay, in one place so it is testable
// from source (tests/jobday-crew-flatten.unit.cjs) instead of buried in a
// 900-line saveJob. Returns null when this day needs no write at all — that
// null IS the fix: an Edit Job save that never touched the crew section now
// leaves every day's crew exactly as Add Day left it.
// `updatedAt` is deliberately NOT included; the caller stamps it, because a
// Timestamp isn't constructible outside the module.
// COD (integrated-diff pass, Code Reviewer ❌): the first cut wrote rates to
// EVERY day whenever they were dirty — the same flatten this task exists to
// kill, one field over, on a MONEY surface (jobDay.lineItemSelections drives
// billing). Rates were never written by this path at all before, so the write
// only lands on days that still carry the SEEDED rates, i.e. days nobody
// customized. `seed` is DispatchState.jobCrewSnapshot; when it is missing we
// refuse the rate write entirely rather than guess.
function _planJobDayCrewUpdate(prevData, next, crewDirty, ratesDirty, seed) {
  const hasAssignments = (next.drivers || []).length > 0 || (next.trucks || []).length > 0;
  const dayCrewChanged = !!crewDirty && _jobCrewDirty(prevData, next);
  const nextStatus = hasAssignments
    ? (prevData?.status === 'cancelled' ? 'cancelled' : prevData?.status === 'finished' ? 'finished' : 'assigned')
    : prevData?.status;
  // Still heal a stale status (crew present, status left at 'scheduled') on a
  // crew-dirty save — the old unconditional write did that as a side effect.
  const statusChanged = !!crewDirty && nextStatus !== prevData?.status;
  const dayRatesUntouched = !!seed &&
    _sameDeep(prevData?.lineItemSelections || {}, seed.lineItemSelections || {}) &&
    (prevData?.selectedContractId || null) === (seed.selectedContractId || null);
  const writeRates = !!ratesDirty && dayRatesUntouched;
  if (!dayCrewChanged && !statusChanged && !writeRates) return null;
  const update = {};
  if (crewDirty) {
    update.drivers = next.drivers;
    update.helpers = next.helpers;
    update.trucks = next.trucks;
    update.trailers = next.trailers;
    update.assets = next.assets;
    update.assetQtys = next.assetQtys;
    update.assetDirections = next.assetDirections;
    update.status = nextStatus;
    // TASK-9258 — this write can falsify the day's template mark, so reconcile
    // it in the SAME update rather than leaving a stale "nobody checked this"
    // on a quantity somebody just typed. Only `templateMeta` moves;
    // `templateIds` and the snapshot are history and stay put.
    const _tplMeta = reconcileStoredTemplateMeta(prevData, next.assets, next.assetQtys, next.assetDirections);
    if (_tplMeta) update.templateMeta = _tplMeta;
    // TASK-9763 — the same write drops removed people/units from "who's in
    // what". Only when the day HAS a map (an ordinary day gains no key), and
    // only when the shared rule is loaded (the read-time filter covers the rest).
    const _CP = (typeof window !== 'undefined' && window.CrewPairing) || null;
    if (_CP && prevData && prevData.crewAssetAssignments && typeof prevData.crewAssetAssignments === 'object') {
      update.crewAssetAssignments = _CP.prune(prevData.crewAssetAssignments, next) || null;
    }
    // S3 — a day that HAS a "first on" order keeps it: removed people drop,
    // people this edit adds go on the end (by seniority). Nobody left → null.
    if (_CP && prevData && Array.isArray(prevData.crewOrder) && prevData.crewOrder.length) {
      const _o = _CP.orderNext(prevData.crewOrder, [].concat(next.drivers || [], next.helpers || []), window.AppState?.settings?.crewSeniority);
      update.crewOrder = _o.length ? _o : null;
    }
    // TASK-9797 item 1 (COD r1) — a truck taken off here loses its named slot
    // too, so re-adding it never revives an old name. Only when the day HAS slots.
    // No trucks (picker still loading, COD r3) → leave them; the read-time filter covers the rest.
    if (prevData && prevData.unitSlots && next.trucks?.length) update.unitSlots = _slotsOn(prevData.unitSlots, next.trucks);
  }
  if (writeRates) {
    update.lineItemSelections = next.lineItemSelections;
    update.selectedContractId = next.selectedContractId;
  }
  return { update, dayCrewChanged };
}

// ---------------------------------------------------------------------------
// TASK-4614 SLICE B — the ticketed-past-day guard.
//
// Slice A stopped Edit Job from reaching days the dispatcher never opened. This
// stops it from SILENTLY rewriting the one day they did open when that day's
// work is already on a billing ticket. A past day carrying a ticket is billed or
// billable work, and jobDay.drivers is the record of who was on it — rewriting
// it changes who is credited for work that has already been priced.
//
// VERIFIED LIVE SHAPES (census over 500 TITS tickets, 2026-08-26) — this is why
// the matching below looks the way it does, do NOT re-derive it from intuition:
//   • jobDayId          — 0 of 500. Tickets carry NO day pointer. Date is the
//                         only available join, and that is not a shortcut.
//   • workDate          — 'YYYY-MM-DD' STRING on 496/500, absent on 4, NEVER a
//                         Timestamp. (rescheduleJobDay called `.toDate?.()` on
//                         this field and therefore NEVER fired — fixed in
//                         TASK-4651, which now shares the matcher below.)
//   • originalWorkDate  — string on 364/500. It holds the date a ticket was
//                         moved OFF of, so a ticket dragged to another day still
//                         protects the day the work actually happened.
//   • workDateEnd       — absent on 499/500; honoured when present.
//   • invoiced          — `false` on srIDSdNHAOH58LeOkTi6 while its
//                         invoiceNumber is 'INV-202608-103'. Reading `invoiced`
//                         alone MISSES invoiced tickets. The predicate must OR
//                         in invoiceNumber/invoicedAt.
//   • status            — draft | ready | archived | locked | sent | deleted.
//
// RE-CENSUSED 2026-08-26 over ALL 903 live TITS tickets (the numbers above are
// the earlier 500-row sample; the full sweep agrees and sharpens them):
//   workDate string 899 / absent 4 / Timestamp 0 · jobDayId truthy 0 of 903 ·
//   originalJobId !== jobId on 98 (~11%) · status archived 732, deleted 115,
//   draft 52, ready 4 · invoiced === false while invoiceNumber/invoicedAt is set
//   on 9 rows — so `t.invoiced` alone misses 9 genuinely invoiced tickets.
// clockEntries.clockIn, by contrast, IS a real Timestamp on 500 of 500 sampled,
// which is why rescheduleJobDay's clock half works and only its ticket half was
// dead.
//
// Deliberately NOT read: DispatchState.tickets. That stream is 90-day windowed
// (:3288, :3296, :3298), so a guard built on it silently stops protecting the
// exact old days most likely to be already invoiced. Query tenant-scoped by
// jobId — the same indexed, composite-free shape deleteJobFromDetails uses.
// 'invoiced' is here because billing.js:894 counts it, not because the census saw
// it (it did not: 500 live tickets were draft | ready | archived | deleted). It is
// billing's own defensive catch for a legacy/odd row, and a status this guard does
// not recognise degrades to a CONFIRM instead of a refusal — so mirroring the
// wider list costs nothing and errs toward protecting money.
const _TICKET_LOCKED_STATUSES = ['archived', 'locked', 'sent', 'invoiced'];

// Mirror of billing.js isTicketInvoiced() (:782) plus the archived/locked/sent
// lock. NOT a cross-import: importing billing.js under a ?v= buster would mint a
// second module instance (Critical Pitfall #1).
function _ticketIsBilled(t) {
  if (!t) return false;
  return t.invoiced === true || !!t.invoiceNumber || !!t.invoicedAt ||
    _TICKET_LOCKED_STATUSES.includes(t.status);
}

// Does this ticket cover `dayStr` (a 'YYYY-MM-DD' local day key)? String
// compare throughout — every date field on a ticket is a string, see census.
function _ticketCoversDay(t, dayStr) {
  if (!t || !dayStr) return false;
  if (t.workDate === dayStr) return true;
  if (t.originalWorkDate === dayStr) return true;
  if (t.workDate && t.workDateEnd && t.workDate <= dayStr && dayStr <= t.workDateEnd) return true;
  return false;
}

// TASK-10107 — a SEALED return estimate ($0 planning draft for a rental pickup)
// never locks a job day: the reschedule / remove-day / date-sync guards skip it
// (the job-modal chip still shows it). Twin of functions/src/shared/
// returnEstimate.js isReturnEstimate (tests/task10107-return-estimate-seal.unit.cjs).
function _isSealedReturnEstimate(t) {
  if (!t || t.returnEstimate !== true) return false;
  const s = t.returnEstimateSeal;
  if (!s || typeof s !== 'object' || !t.jobId || s.jobId !== t.jobId || s.mirrorOf !== t.mirrorOf) return false;
  if (t.status !== 'draft' || t.invoiced === true || t.invoiceNumber || t.invoicedAt) return false;
  return !(Number(t.grandTotal) > 0);
}

// TASK-4651 — THE shared ticket→day matcher. Extracted from
// _checkTicketedDayCrewGuard so the reschedule guard runs the identical lookup
// instead of growing a second, divergent copy (TASK-4641 had to clean exactly
// that up in jobboard.js: three copies of one lock predicate, two of them
// wrong). The POLICY differs between callers and stays with them; the MATCHING
// is one function, so a census-driven fix to the join lands on both paths at
// once.
//
// Returns { ok:true, covering:[ticketData,...] } or { ok:false } when the read
// threw. There is no third answer — a caller that cannot tell "none" from
// "couldn't look" is how a guard fails open.
//
//   • TWO queries, not one (COD Code Reviewer ⚠️, then MEASURED). Billing's own
//     sticky matcher is `t.jobId === jobId || t.originalJobId === jobId`
//     (billing.js:874) because moving a ticket to another job DELIBERATELY
//     leaves originalJobId immutable (billing.js:21497). A jobId-only query
//     goes blind on exactly the tickets that have been re-keyed — and that is
//     not theoretical: re-censused 2026-08-26 over ALL 903 live TITS tickets,
//     98 (~11%) have originalJobId !== jobId. rescheduleJobDay ran the
//     jobId-only shape, so even once its date bug is fixed it would still have
//     missed ~1 ticket in 9.
//   • Both queries are single-field equality (no composite index needed) and
//     can return the SAME doc, so de-dupe by id before classifying.
//   • 'deleted' is dropped here, not by the callers: 115 of the 903 live
//     tickets carry it, and a guard that counted them would refuse nearly every
//     day on the board and be turned off by the first dispatcher who hit it.
// TASK-4654 — the READ half of the matcher, split out from the DAY half. The
// date-sync path has to classify N days of one job at once (a 22-day job
// shrinking to one leaves 21 candidate days); calling _findTicketsCoveringDay
// per day would fire 2N queries for data that never changes between them. This
// is the identical two-query, de-duped, deleted-filtered read — the split moves
// no logic, it just lets one fetch answer many days through the SAME
// _ticketCoversDay predicate. Still one query shape, still one coverage test.
//
// Returns { ok:true, tickets:[data,...] } or { ok:false } when the read threw.
async function _fetchLiveTicketsForJob(jobId, opts) {
  const ticketsCol = collection(db, 'tenants', DispatchState.tenantId, 'tickets');
  // TASK-9645 C — `{ fromServer:true }` refuses a cache answer (the invoice
  // lock's save-time re-check); offline that throws -> { ok:false } -> the
  // caller fails closed. Every existing caller keeps plain getDocs.
  const _get = (opts && opts.fromServer === true) ? getDocsFromServer : getDocs;
  let snaps;
  try {
    snaps = await Promise.all([
      _get(query(ticketsCol, where('jobId', '==', jobId))),
      _get(query(ticketsCol, where('originalJobId', '==', jobId)))
    ]);
  } catch (err) {
    console.warn('[Dispatch] ticket lookup failed', { jobId }, err);
    return { ok: false };
  }
  const byId = new Map();
  // TASK-9645 — carry the doc id (the job modal's "View invoice" needs it); the
  // guards read fields only, so an extra `id` key changes nothing for them.
  for (const s of snaps) for (const d of s.docs) byId.set(d.id, { ...d.data(), id: d.id });
  return { ok: true, tickets: Array.from(byId.values()).filter(t => (t.status || '') !== 'deleted') };
}

async function _findTicketsCoveringDay(jobId, dayStr) {
  const found = await _fetchLiveTicketsForJob(jobId);
  if (!found.ok) {
    console.warn('[Dispatch] ticket-day lookup failed', { jobId, dayStr });
    return { ok: false };
  }
  return { ok: true, covering: found.tickets.filter(t => _ticketCoversDay(t, dayStr) && !_isSealedReturnEstimate(t)) };   // TASK-10107
}

// TASK-10288 B — the invoiced ticket among a day's covering tickets, by the
// TASK-9645 invoiced predicate (_ticketCountsAsInvoiced: billing's
// isTicketInvoiced, billed-elsewhere included). Not a second "is it invoiced"
// rule — the same one the Edit Job invoice lock and the board's Invoiced chip read.
function _invoicedTicketOf(covering) {
  return (covering || []).find(t => _ticketCountsAsInvoiced(t)) || null;
}
// The invoice's name for a refusal: its number, else the outside system's ref
// (billed elsewhere), else ''. Same fields the board's ticket popover shows.
function _invoiceRefOf(t) {
  if (!t) return '';
  const bd = (typeof window !== 'undefined') ? window.BillingDedup : null;
  const ext = bd && typeof bd.isBilledExternally === 'function' && bd.isBilledExternally(t);
  return String((ext ? (t.externalInvoiceRef || t.invoiceNumber) : t.invoiceNumber) || '').trim();
}
// TASK-10288 B — the refusal for moving an INVOICED day (Edit Job date change
// AND the board drag). "Clear the ticket / unarchive it" is the wrong advice
// here: it sends people to undo billed work. Moving the day would not change
// the invoice, so the fix is in Billing first. PURE.
function _invoicedDayMoveMessage(label, invoiceRef) {
  const inv = invoiceRef ? `invoice ${invoiceRef}` : 'an invoice';
  return `${label || 'That day'} is on ${inv}. Moving the day won't change the invoice — it keeps the old date. `
    + `If the date is wrong, fix it in Billing first (someone with Billing can uninvoice it), then move the day.`;
}

// TASK-4654 — POLICY for the date-sync path, expressed over an already-fetched
// ticket list. Same two predicates the other guards use (_ticketCoversDay,
// _ticketIsBilled) and the same refusal stance as
// _checkTicketedDayRescheduleGuard: any covering ticket locks the day, billed or
// not, because moving/deleting the day orphans the ticket either way. The only
// thing billed changes is the WORDS. `tickets:null` means the read threw —
// locked, failedCheck, fail CLOSED.
function _classifyDayAgainstTickets(tickets, dayStr) {
  if (!tickets) return { locked: true, failedCheck: true };
  const covering = tickets.filter(t => _ticketCoversDay(t, dayStr) && !_isSealedReturnEstimate(t));   // TASK-10107
  if (covering.length === 0) return { locked: false };
  const billed = covering.find(_ticketIsBilled);
  const t = billed || covering[0];
  const inv = _invoicedTicketOf(covering);
  return {
    locked: true,
    billed: !!billed,
    // TASK-10288 B — INVOICED (TASK-9645's predicate), a subset of billed. Only
    // the words change: an invoiced day is told to go to Billing, not to clear
    // the ticket.
    invoiced: !!inv,
    invoiceRef: inv ? _invoiceRefOf(inv) : '',
    detail: t.invoiceNumber
      ? `invoice ${t.invoiceNumber}`
      : `${t.status || (billed ? 'archived' : 'draft')} ticket${t.ticketNo ? ` #${t.ticketNo}` : ''}`
  };
}

// Classify the day the Edit Job crew section is scoped to.
//   { allow:true }                          -> write it
//   { needsConfirm:true, label, detail }     -> draft/ready ticket: ask first
//   { blocked:true, label, detail }          -> already billed: refuse
//   { blocked:true, failedCheck:true }       -> could not verify: refuse
// Never throws.
async function _checkTicketedDayCrewGuard(jobId, jobDayData) {
  const ms = _jobDayDateMs(jobDayData);
  if (ms === null) {
    // A jobDay with no usable date cannot be shown to be in the PAST, so the
    // past-day guard does not apply to it. Legacy undated rows exist (see
    // _pickFirstJobDayByDate) and blocking them would trap real users on data
    // this guard was never written about. Logged so it is never silent.
    console.warn('[saveJob] TASK-4614B: scoped jobDay has no usable date — ticket guard not applied', { jobId });
    return { allow: true };
  }
  // jobDay.date is anchored to NOON local precisely so the UTC-based
  // formatDateISO lands on the intended calendar day
  // (memory/lesson_jobday_date_anchors_to_noon_local). "Today" must be LOCAL,
  // or after ~18:00 CT the dispatcher's own today reads as tomorrow and today's
  // in-progress ticket starts getting guarded as history.
  const dayStr = formatDateISO(new Date(ms));
  const todayStr = formatDateISOLocal(new Date());
  const label = _formatJobDayLabel(jobDayData) || dayStr;
  if (dayStr >= todayStr) return { allow: true }; // today / future — the live working flow

  // TASK-4651 — the two-query, de-duped, deleted-filtered lookup now lives in
  // _findTicketsCoveringDay so rescheduleJobDay runs the SAME join. The reasons
  // it looks the way it does are documented there.
  const found = await _findTicketsCoveringDay(jobId, dayStr);
  if (!found.ok) {
    // FAIL CLOSED. If we cannot prove the day is unticketed we do not write it —
    // the same stance deleteJobFromDetails takes after its own check threw and
    // "proceeded" into deleting a job with a live draft ticket (TASK-959).
    console.warn('[saveJob] TASK-4614B: ticket check failed — refusing the crew write');
    return { blocked: true, failedCheck: true, label };
  }
  const covering = found.covering;
  if (covering.length === 0) return { allow: true };

  const billed = covering.find(_ticketIsBilled);
  if (billed) {
    return {
      blocked: true, label,
      detail: billed.invoiceNumber ? `invoice ${billed.invoiceNumber}` : `${billed.status || 'archived'} ticket`
    };
  }
  return { needsConfirm: true, label, detail: `${covering[0].status || 'draft'} ticket` };
}

// ---------------------------------------------------------------------------
// TASK-4651 — the reschedule guard. Same matcher as above, DELIBERATELY
// different policy. Two things differ from the crew guard, and both are on
// purpose:
//
// 1) NO draft/ready confirm-and-proceed. The crew guard lets a draft/ready day
//    through behind a confirm because the outcome is merely STALE: the ticket's
//    saved hours stop agreeing with the jobDay's crew, but both rows still
//    describe the same day and a human can still find and reconcile them.
//    Moving the day is not that. Nothing here rewrites the ticket's workDate,
//    so after the move the jobDay says Aug 20 and the ticket still says Aug 18
//    — and since tickets carry NO jobDayId (0 of 903, measured), the date IS
//    the join. The move does not make the record stale, it ORPHANS it:
//      • the ticket stops being findable from its day, and this very guard
//        would then wave through any future edit to the now-"clean" new day;
//      • billing's job+day dedup sees no ticket on the new date, so "Generate
//        Ticket" mints a SECOND ticket for work already billed — the exact
//        double-pay shape TASK-1398 exists to prevent;
//      • on an invoiced ticket the customer's invoice and the board now name
//        different dates.
//    A confirm cannot honestly describe that, so reschedule refuses on ANY
//    covering ticket and points at the escape hatch the code already had: add a
//    NEW day on the target date. That is the correct act anyway — the ticketed
//    work happened on the original date and does not move.
//
// 2) NO past-day gate. The crew guard returns early on today/future because
//    editing today's crew is the live working flow with nothing billed yet.
//    Here the ticket's EXISTENCE is the signal, not the day's pastness: 56 of
//    the live tickets are open draft/ready, they sit on current days, and
//    dragging one of those days breaks the same join. rescheduleJobDay has
//    never had a past-day restriction and adding one would be a regression.
//
// Billed vs draft still changes the WORDS — an archived/invoiced ticket has to
// be unarchived in Billing, a draft can just be deleted — because a refusal
// that does not say what to do next is a refusal the dispatcher works around.
//
//   { allow:true }                       -> no ticket covers the day
//   { blocked:true, label, detail }       -> a ticket covers it; detail names it
//   { blocked:true, failedCheck:true }    -> could not verify: refuse
// Never throws.
async function _checkTicketedDayRescheduleGuard(jobId, jobDayData) {
  const ms = _jobDayDateMs(jobDayData);
  const label = _formatJobDayLabel(jobDayData) || '';
  if (ms === null) {
    // No usable date means no date to match a ticket ON — the string join this
    // guard runs has nothing to compare. Same stance as the crew guard: allow,
    // but never silently. Refusing here would trap the legacy undated rows
    // (_pickFirstJobDayByDate) whose whole repair IS giving them a date, and a
    // ticket cannot be matched to a day that has none regardless.
    console.warn('[Dispatch] TASK-4651: jobDay has no usable date — ticket guard not applied', { jobId });
    return { allow: true };
  }
  // jobDay.date is anchored to NOON local so the UTC-based formatDateISO lands
  // on the intended calendar day (memory/lesson_jobday_date_anchors_to_noon_local).
  const dayStr = formatDateISO(new Date(ms));
  const found = await _findTicketsCoveringDay(jobId, dayStr);
  if (!found.ok) {
    // FAIL CLOSED — if we cannot prove the day is unticketed, we do not move it.
    return { blocked: true, failedCheck: true, label: label || dayStr };
  }
  if (found.covering.length === 0) return { allow: true };
  const billed = found.covering.find(_ticketIsBilled);
  const t = billed || found.covering[0];
  const inv = _invoicedTicketOf(found.covering); // TASK-10288 B
  return {
    blocked: true,
    billed: !!billed,
    invoiced: !!inv,
    invoiceRef: inv ? _invoiceRefOf(inv) : '',
    label: label || dayStr,
    detail: t.invoiceNumber
      ? `invoice ${t.invoiceNumber}`
      : `${t.status || (billed ? 'archived' : 'draft')} ticket${t.ticketNo ? ` #${t.ticketNo}` : ''}`
  };
}
// ---------------------------------------------------------------------------

// saveAssignment writes a CREW_ASSIGN_UPDATE row on every per-day crew change;
// saveJob's write-to-all-days wrote NONE — so the one path that can overwrite N
// days at once was invisible to the forensic log support uses to answer "the
// crew isn't sticking." Same action + same shape so the audit viewer renders
// both. IDs only, never driver names.
function _logJobEditCrewAudit(jobId, jobDayId, prevData, next, multiDayOverwrite) {
  try {
    const prevDrivers = Array.isArray(prevData?.drivers) ? prevData.drivers : [];
    const prevHelpers = Array.isArray(prevData?.helpers) ? prevData.helpers : [];
    const prevTrucks = Array.isArray(prevData?.trucks) ? prevData.trucks : [];
    const prevTrailers = Array.isArray(prevData?.trailers) ? prevData.trailers : [];
    const nextDrivers = next.drivers || [];
    const nextHelpers = next.helpers || [];
    const nextTrucks = next.trucks || [];
    const nextTrailers = next.trailers || [];
    const swappedAll = prevDrivers.length > 0 && nextDrivers.length > 0 &&
      !_sameIdSet(prevDrivers, nextDrivers) && prevDrivers.every(id => !nextDrivers.includes(id));
    window.logAudit?.('crew', 'CREW_ASSIGN_UPDATE', {
      source: 'dispatch-job-edit',
      jobDayId,
      jobId: jobId || null,
      before: {
        drivers: prevDrivers, helpers: prevHelpers, trucks: prevTrucks, trailers: prevTrailers,
        driverCount: prevDrivers.length, truckCount: prevTrucks.length
      },
      after: {
        drivers: nextDrivers, helpers: nextHelpers, trucks: nextTrucks, trailers: nextTrailers,
        driverCount: nextDrivers.length, truckCount: nextTrucks.length
      },
      redFlags: {
        driverCountDrop: nextDrivers.length < prevDrivers.length,
        truckCountDrop: nextTrucks.length < prevTrucks.length,
        crewCleared: prevDrivers.length > 0 && nextDrivers.length === 0,
        swappedAll,
        // TASK-4614 scoped this form's crew write to ONE day, so on the current
        // code path this is always false. It stays in the payload because the
        // forensic viewer and the historical rows written BEFORE that fix both
        // read it — a true here is the shape of Steve's complaint, and its
        // absence going forward is how you tell old damage from new.
        multiDayCrewOverwrite: !!multiDayOverwrite
      },
      previousUpdatedAt: prevData?.updatedAt || null
    });
  } catch (auditErr) { console.warn('[Dispatch] CREW_ASSIGN_UPDATE (job edit) audit failed:', auditErr); }
}

// Select all items in a crew section (toggle behavior)
function selectAllCrew(sectionKey) {
  const container = document.getElementById(`job-select-${sectionKey}`);
  if (!container) return;

  const checkboxes = container.querySelectorAll('input[type="checkbox"]');
  const allChecked = Array.from(checkboxes).every(cb => cb.checked);

  // Toggle: if all checked, uncheck all; otherwise check all
  // TASK-10006 S2 — only rows the dispatcher can SEE take the type's Return
  // (COD r2: a search-filtered, hidden row stays as today, Deploy).
  const _newlyChecked = [];
  checkboxes.forEach(checkbox => {
    if (!allChecked && !checkbox.checked && checkbox.closest('.checkbox-item')?.style?.display !== 'none') _newlyChecked.push(checkbox.value);
    checkbox.checked = !allChecked;
    const item = checkbox.closest('.checkbox-item');
    if (item) item.classList.toggle('checked', checkbox.checked);
  });
  if (sectionKey === 'assets') _jobTypeRentalDirOnAdded(_newlyChecked); // TASK-10006 S2 — come-back type → Return

  updateJobSectionCount(sectionKey);

  // Open the section so user can see selection
  const section = document.getElementById(`job-section-${sectionKey}`);
  if (section && !allChecked) section.classList.add('open');
}

// ============================================
// TASK-9390 — Job "Type": tenant-managed list (settings.jobTypes)
// ============================================
// The list + label resolver live in public/src/utils/jobTypes.js (window.JobTypes)
// so jobboard / clock / billing read the SAME rules. Jobs store the slug in
// `job.type`; every surface shows the LABEL. A rename edits the label only —
// no job doc is rewritten. Archive hides a type from new picks; there is no
// hard delete (existing jobs reference the slug).
function _jobTypeList() {
  const JT = window.JobTypes;
  return JT ? JT.list(window.AppState?.settings || {}) : [{ value: 'job', label: 'Job', archived: false }];
}
function _jobTypeLabel(value) {
  const JT = window.JobTypes;
  return JT ? JT.label(value, _jobTypeList()) : (String(value == null ? '' : value).trim() || 'Job');
}
function _jobTypeLabelForJob(job) {
  const JT = window.JobTypes;
  return JT ? JT.labelForJob(job, _jobTypeList()) : _jobTypeLabel(job && job.type);
}
function _jobTypeIsDefault(value) {
  const JT = window.JobTypes;
  if (JT) return JT.isDefault(value);
  const k = String(value == null ? '' : value).trim().toLowerCase();
  return k === '' || k === 'job';
}

// TASK-10009 — the line under a job's title on the quickview and Job Details:
// the type LABEL (never the slug), '' for the default type. Plain text; the
// HTML variant escapes at the sink.
function _jobTypeSubtitleText(job) {
  if (!job || _jobTypeIsDefault(job.type)) return '';
  return String(_jobTypeLabelForJob(job) || '').trim();
}
function _jobTypeSubtitleHtml(job, cls, style) {
  const label = _jobTypeSubtitleText(job);
  if (!label) return '';
  return `<div class="${escapeHtml(cls || 'job-type-subtitle')}" style="${escapeHtml(style || '')}">${escapeHtml(label)}</div>`;
}

// ============================================
// TASK-10011 — START A JSA FROM THE OFFICE JOB SCREENS
// ============================================
// Guy 10/21: "if I could start the JSA … and when they get out there they just
// tap into it, sign in." The office Job Details and the quickview offer Start
// JSA / Add another JSA, opening the SAME form the crew uses
// (window.JSAModule.startForJob — shared with the Job Board, no ?v= import),
// prefilled with this job + day, returning to this job afterwards.
//
// Gate (modules, never roles), shown only where the server would accept it:
//   1. the JSA access drivers use — the pretrip/JSA area is open to every login
//      except a compliance-only carrier (app.js isModuleUnlocked('pretrip')),
//      and a jsaReports CREATE needs canWriteTenant (firestore.rules), which a
//      readOnly member fails;
//   2. the office job access — the OWNER (tenants.ownerId), the demo tenant, or
//      an active member whose modules hold `dispatch`.
// Driver-PIN / driver-link / public sessions never see it here: their Start
// JSA lives on the Job Board (unchanged).
function _canStartJsaFromOffice() {
  const st = window.AppState || {};
  if (st.driverMode || st.driverModePin || st.isPublicAccess) return false;
  if (st.tenantData?.complianceOnly === true) return false;
  if (typeof window.isModuleUnlocked === 'function' && window.isModuleUnlocked('pretrip') === false) return false;
  if (!window.JSAModule || typeof window.JSAModule.startForJob !== 'function') return false;
  if (st.isDemo || st.tenant === 'demo' || DispatchState.tenantId === 'demo') return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('dispatch');
}

// "Start JSA" when this day has none on file yet, "Add another JSA" otherwise.
// The office jsaReports stream (jsaListByJobDay / getJsaIdForJobDay) is what
// the board's JSA pill reads, so both say the same thing.
function _officeStartJsaButtonHtml(jobId, jobDayId, jobDay, opts) {
  const hasOne = !!(jobDay && getJsaIdForJobDay(jobDay));
  // The quickview footer already has its own primary (Directions); there the
  // JSA button is always secondary so it never out-shouts Edit Job.
  const primary = !hasOne && !(opts && opts.secondary);
  return `
      <button type="button" class="btn ${primary ? 'btn-primary' : 'btn-secondary'} office-start-jsa" data-job-id="${escapeHtml(jobId)}" style="min-height:44px;display:inline-flex;align-items:center;justify-content:center;gap:6px;flex:1 1 auto;" onclick="window.DispatchModule.startJsaForJob('${escJsAttr(jobId)}', '${escJsAttr(jobDayId || '')}')">
        <span class="material-icons">${hasOne ? 'add' : 'health_and_safety'}</span> ${hasOne ? 'Add another JSA' : 'Start JSA'}
      </button>
    `;
}

// The day a JSA started from Job Details belongs to: the day it was opened
// from, else today's day of this job, else none (a multi-day job opened from a
// search row with no day — the form still links the job). Synthesized legacy
// rows are not real jobDays, so they never become a jobDayId.
function _officeJsaDayFor(jobDays, jobDayId, todayIso) {
  const real = (jobDays || []).filter(d => d && d.id && !d.isLegacy && !String(d.id).startsWith('legacy-'));
  if (jobDayId) {
    const hit = real.find(d => d.id === jobDayId);
    if (hit) return hit;
  }
  const today = real.find(d => {
    const dt = d.date?.toDate ? d.date.toDate() : (d.date ? new Date(d.date) : null);
    return dt && !isNaN(dt) && formatDateISO(dt) === todayIso;
  });
  if (today) return today;
  return real.length === 1 ? real[0] : null;
}

function startJsaForJob(jobId, jobDayId) {
  if (!jobId) return false;
  if (!_canStartJsaFromOffice()) {
    window.showToast?.("Your login can't start a JSA here. Ask the owner for the Dispatch module.", 'info');
    return false;
  }
  closeModal('quickview');
  closeModal('job-details');
  return window.JSAModule.startForJob(String(jobId), jobDayId ? String(jobDayId) : '', {
    returnTo: { hash: window.location.hash || '#/dispatch', jobId: String(jobId), jobDayId: jobDayId ? String(jobDayId) : '', surface: 'dispatch' },
  });
}

// Called by jsa.js (returnToOpeningJob) after a JSA started here is saved or
// cancelled: reopen this job's details once the board has it. Returns false —
// quietly, no toast — until the dispatch view is mounted and the job is known,
// so the caller's bounded retry can wait for the board to load.
function reopenJobDetails(jobId, jobDayId) {
  if (!jobId) return false;
  if (!document.getElementById('modal-job-details')) return false;
  if (!isDispatchScheduleReady()) return false;
  if (!_resolveJobAnywhere(jobId)) return false;
  showJobDetails(jobId, jobDayId || null);
  return true;
}

/**
 * Fill #job-type with the tenant's active types and select `value` EXACTLY.
 * A value the list does not carry as an active option (legacy free text such as
 * 'Hydrovac ', or an archived type) is added as its own option, labelled as-is,
 * so an untouched Save writes back the very same string.
 */
function _renderJobTypeSelect(value) {
  const sel = document.getElementById('job-type');
  if (!sel) return;
  const want = (value == null || String(value) === '') ? 'job' : String(value);
  const opts = window.JobTypes
    ? window.JobTypes.options(_jobTypeList(), want)
    : [{ value: 'job', label: 'Job' }].concat(want !== 'job' ? [{ value: want, label: want.trim(), custom: true }] : []);
  sel.innerHTML = opts.map(o =>
    `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}${o.archived ? ' (archived)' : ''}</option>`
  ).join('');
  sel.value = want;
  const btn = document.getElementById('btn-manage-job-types');
  if (btn) btn.style.display = _canManageJobTypes() ? '' : 'none';
}

// MODULE access, never a role (Chris 2026-09-24): the tenant OWNER via
// tenants/{tid}.ownerId, or a member whose `modules` includes 'dispatch'.
// Mirrors firestore.rules — settings.jobTypes is in t9337DispatchKeys, so the
// write passes the module arm for exactly these people. Driver sessions and
// demo never see the pencil (rules deny both).
function _canManageJobTypes() {
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return false;
  if (S.isDemo || S.tenant === 'demo') return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed') return false;
  return Array.isArray(m.modules) && m.modules.includes('dispatch');
}

/**
 * PURE — apply one edit to a sanitised list. Returns { list, value, from, to }
 * or { error }. Never mutates `list`.
 *   { kind:'add', label }                 → new entry, slug generated ONCE
 *   { kind:'rename', value, label }       → label only; the slug never changes
 *   { kind:'archive', value, archived }   → hide from / restore to new picks
 *   { kind:'direction', value, direction } → TASK-10006 S2: rentals on this
 *       type 'out' | 'back' | null (null = neither, today's behaviour)
 * Every row keeps its rentalDirection through every op (COD r1 #4: it must
 * round-trip, or a rename would silently clear it).
 */
function _applyJobTypeOp(list, op) {
  const JT = window.JobTypes;
  const _rd = (v) => (v === 'out' || v === 'back' ? v : null);
  const cur = (Array.isArray(list) ? list : []).map(t => {
    const row = { value: t.value, label: t.label, archived: t.archived === true };
    if (_rd(t.rentalDirection)) row.rentalDirection = t.rentalDirection;
    return row;
  });
  const clean = (v) => (JT ? JT.cleanLabel(v) : String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, 40));
  const max = JT ? JT.LIST_MAX : 100;
  const sameLabel = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
  if (!op || typeof op !== 'object') return { error: 'Nothing to change.' };

  if (op.kind === 'add') {
    const label = clean(op.label);
    if (!label) return { error: 'Type a name for the new job type.' };
    if (cur.length >= max) return { error: `The list is full (${max} types). Archive one you no longer use.` };
    const dup = cur.find(t => sameLabel(t.label, label));
    if (dup) return { error: dup.archived ? `"${dup.label}" is archived — restore it instead.` : `"${dup.label}" is already on the list.` };
    const value = JT ? JT.makeValue(label, cur.map(t => t.value)) : 'ct_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    return { list: cur.concat([{ value, label, archived: false }]), value, from: null, to: label };
  }

  const idx = cur.findIndex(t => t.value === op.value);
  if (idx === -1) return { error: 'That type is no longer on the list — reopen the editor.' };

  if (op.kind === 'rename') {
    const label = clean(op.label);
    if (!label) return { error: 'A job type needs a name.' };
    const from = cur[idx].label;
    if (label === from) return { list: cur, value: op.value, from, to: label, unchanged: true };
    const dup = cur.find((t, i) => i !== idx && sameLabel(t.label, label));
    if (dup) return { error: `"${dup.label}" is already on the list.` };
    const next = cur.slice();
    next[idx] = { ...next[idx], label };
    return { list: next, value: op.value, from, to: label };
  }

  if (op.kind === 'archive') {
    if (op.value === 'job' && op.archived) return { error: 'The default "Job" type cannot be archived — rename it instead.' };
    const next = cur.slice();
    next[idx] = { ...next[idx], archived: op.archived === true };
    return { list: next, value: op.value, from: cur[idx].archived, to: op.archived === true };
  }

  if (op.kind === 'direction') {
    if (op.direction != null && op.direction !== '' && !_rd(op.direction)) return { error: 'Unknown rental setting.' };
    const to = _rd(op.direction);
    const from = _rd(cur[idx].rentalDirection);
    if (to === from) return { list: cur, value: op.value, from, to, unchanged: true };
    const next = cur.slice();
    const row = { value: next[idx].value, label: next[idx].label, archived: next[idx].archived };
    if (to) row.rentalDirection = to;
    next[idx] = row;
    return { list: next, value: op.value, from, to };
  }
  return { error: 'Unknown change.' };
}

// One edit = one transaction on the tenant doc, re-reading the CURRENT list, so
// two dispatchers editing at once can never erase each other's type.
async function _persistJobTypeOp(op) {
  const tid = window.AppState?.tenant;
  if (!tid) { window.showToast?.('Not logged in', 'error'); return null; }
  if (!_canManageJobTypes()) { window.showToast?.('You need Dispatch access to edit job types.', 'error'); return null; }
  const ref = doc(db, 'tenants', tid);
  let result = null;
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const cur = window.JobTypes ? window.JobTypes.list(snap.data()?.settings || {}) : _jobTypeList();
      result = _applyJobTypeOp(cur, op);
      if (result.error || result.unchanged) return;
      tx.update(ref, { 'settings.jobTypes': result.list });
    });
  } catch (e) {
    console.error('[Dispatch] job type save failed', e);
    window.showToast?.('Could not save the job type list: ' + (e?.message || 'unknown error'), 'error');
    return null;
  }
  if (!result) return null;
  if (result.error) { window.showToast?.(result.error, 'error'); return null; }
  if (result.unchanged) return result;
  if (!window.AppState.settings) window.AppState.settings = {};
  window.AppState.settings.jobTypes = result.list;
  // Audited old -> new by name: a label is what prints on tickets and texts.
  window.logAudit?.('settings', 'SAVE_JOB_TYPES', {
    op: op.kind, value: result.value,
    ...(op.kind === 'archive' ? { archivedFrom: result.from, archivedTo: result.to }
      : op.kind === 'direction' ? { rentalDirectionFrom: result.from || 'none', rentalDirectionTo: result.to || 'none' } // TASK-10006 S2
      : { labelFrom: result.from, labelTo: result.to }),
  });
  return result;
}

function _manageJobTypesBodyHTML() {
  const list = _jobTypeList();
  const rows = list.map(t => `
      <div class="jt-row" data-jt-value="${escapeHtml(t.value)}" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;padding:6px 0;border-bottom:1px solid var(--border-light);${t.archived ? 'opacity:0.6;' : ''}">
        <input type="text" class="jt-label" maxlength="40" value="${escapeHtml(t.label)}" aria-label="Job type name" style="flex:1;min-width:140px;">
        ${t.archived ? '<span style="font-size:0.75rem;color:var(--text-secondary);">Archived</span>' : ''}
        ${t.value === 'job' ? '<span style="font-size:0.75rem;color:var(--text-secondary);">Default</span>' : `
        <button type="button" class="btn btn-small btn-secondary jt-archive" data-archived="${t.archived ? '1' : '0'}">${t.archived ? 'Restore' : 'Archive'}</button>`}
        ${_jobTypeRentalDirSelectHtml(t)}
      </div>`).join('');
  return `
    <p style="margin:0 0 10px;font-size:0.85rem;color:var(--text-secondary);">Rename a type by editing its name — jobs that already use it show the new name. Archive hides a type from new jobs; old jobs keep it.</p>
    ${_jobTypeRentalDirHelpHtml()}
    <div id="jt-rows">${rows}</div>
    <div style="display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;">
      <input type="text" id="jt-new-label" maxlength="40" placeholder="New type, e.g. Hydrovac" style="flex:1;min-width:160px;">
      <button type="button" class="btn btn-primary" id="jt-add-btn"><span class="material-icons" style="font-size:18px;">add</span> Add</button>
    </div>`;
}

function _refreshJobTypeUI(selectValue) {
  const body = document.getElementById('jt-modal-body');
  if (body) body.innerHTML = _manageJobTypesBodyHTML();
  const sel = document.getElementById('job-type');
  if (sel) {
    _renderJobTypeSelect(selectValue != null ? selectValue : sel.value);
    onJobTypeChange();
  }
}

function showManageJobTypes() {
  if (!_canManageJobTypes()) { window.showToast?.('You need Dispatch access to edit job types.', 'error'); return; }
  closeManageJobTypes();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-job-types';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3>Job Types</h3>
        <button type="button" class="modal-close" id="jt-close-btn" aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" id="jt-modal-body">${_manageJobTypesBodyHTML()}</div>
    </div>`;
  document.body.appendChild(modal);

  // Delegated handlers — values ride in escaped data-* attributes, never in a
  // JS string inside an onclick (a legacy console-written slug could hold a quote).
  modal.addEventListener('click', async (ev) => {
    if (ev.target === modal || ev.target.closest('#jt-close-btn')) { closeManageJobTypes(); return; }
    if (ev.target.closest('#jt-add-btn')) {
      const input = document.getElementById('jt-new-label');
      const r = await _persistJobTypeOp({ kind: 'add', label: input ? input.value : '' });
      if (r) { window.showToast?.(`Added "${r.to}"`, 'success'); _refreshJobTypeUI(r.value); document.getElementById('jt-new-label')?.focus(); }
      return;
    }
    const arch = ev.target.closest('.jt-archive');
    if (arch) {
      const value = arch.closest('.jt-row')?.dataset.jtValue;
      const r = await _persistJobTypeOp({ kind: 'archive', value, archived: arch.dataset.archived !== '1' });
      if (r) _refreshJobTypeUI();
    }
  });
  modal.addEventListener('change', async (ev) => {
    // TASK-10006 S2 — the per-type "Rentals" setting.
    const dirSel = ev.target.closest('.jt-dir');
    if (dirSel) {
      const value = dirSel.closest('.jt-row')?.dataset.jtValue;
      const r = await _persistJobTypeOp({ kind: 'direction', value, direction: dirSel.value || null });
      if (r && !r.unchanged) window.showToast?.(_jobTypeRentalDirToast(r), 'success');
      _refreshJobTypeUI();
      return;
    }
    const input = ev.target.closest('.jt-label');
    if (!input) return;
    const value = input.closest('.jt-row')?.dataset.jtValue;
    const r = await _persistJobTypeOp({ kind: 'rename', value, label: input.value });
    if (r && !r.unchanged) window.showToast?.(`Renamed to "${r.to}"`, 'success');
    _refreshJobTypeUI();
  });
  modal.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter' && ev.target.id === 'jt-new-label') { ev.preventDefault(); document.getElementById('jt-add-btn')?.click(); }
    else if (ev.key === 'Enter' && ev.target.classList.contains('jt-label')) { ev.preventDefault(); ev.target.blur(); }
    else if (ev.key === 'Escape') closeManageJobTypes();
  });
}

function closeManageJobTypes() {
  document.getElementById('modal-job-types')?.remove();
}


// ============================================
// TASK-10006 S2 — per job type: rentals GO OUT / COME BACK / neither
// ============================================
// Guy 10/21: "If I say mat install, I'm adding mats … if I'm doing a mat
// removal…" — the company says it once per type (settings.jobTypes[i].
// rentalDirection, Job Types editor) instead of the dispatcher flipping every
// rental by hand. No type NAME is ever read: the setting rides on the slug, so
// a renamed type keeps it. Absent = neither = today's behaviour exactly.
//   'back' — a rental ticked on the form starts as Return; changing the type
//            to it moves the form's untouched picks to Return; a return job
//            (Create return job / the save prompt) takes this type; with a
//            project picked, one tap fills in everything still out on it.
//   'out'  — picks start as Deploy (the default) and a pick the TYPE had set
//            to Return goes back to Deploy.
// Never: a rental already on the SAVED job (edit), or one whose direction a
// person or a copy/return prefill chose. The server reads none of this — a
// driver can't put a rental on a job, MCP/SMS creates carry no rentals, and
// every stock move keys off the day's assetDirections, as before.
const _JT_RENTAL_DIR_LABEL = Object.freeze({ '': 'Rentals: as picked', out: 'Rentals go out', back: 'Rentals come back' });

// Per form generation (a new form never inherits the last one's):
//   ids  — directions the TYPE set. Anything else was a person's (or a
//          prefill's) choice and the type leaves it alone.
//   here — rentals a person ticked (or Select All / a template added) on THIS
//          form. A type change only ever touches these: a copy's carried
//          rentals and an edit's seeded rentals were never ticked here, so
//          they never flip (COD r1 Edge / Independent).
let _jtDirTyped = { gen: -1, ids: new Set(), here: new Set() };
function _jtDirState() {
  if (_jtDirTyped.gen !== _jobFormGen) _jtDirTyped = { gen: _jobFormGen, ids: new Set(), here: new Set() };
  return _jtDirTyped;
}
function _jtDirTypedSet() { return _jtDirState().ids; }
function _jobTypeRentalDirForget(assetId) { _jtDirTypedSet().delete(String(assetId)); }

function _jobTypeRentalDirOf(value) {
  const JT = window.JobTypes;
  return JT && typeof JT.rentalDirection === 'function' ? JT.rentalDirection(value, _jobTypeList()) : null;
}

// Office forms only — a PIN / link driver never picks rentals.
function _jobTypeRentalDirActive() {
  const S = window.AppState || {};
  return !(S.driverMode || S.driverModePin || S.isPublicAccess);
}

function _jtIsRentalAsset(a) { return !!a && (a.quantityType === 'rental' || !!a.isRental); }

// The editor shows the setting only to a company that rents (or already set one).
function _jobTypeRentalDirShown(list) {
  if ((list || []).some(t => t && (t.rentalDirection === 'out' || t.rentalDirection === 'back'))) return true;
  return (DispatchState.assets || []).some(_jtIsRentalAsset);
}

function _jobTypeRentalDirSelectHtml(t) {
  if (!t || !_jobTypeRentalDirShown(_jobTypeList())) return '';
  const cur = t.rentalDirection === 'out' || t.rentalDirection === 'back' ? t.rentalDirection : '';
  return `<select class="jt-dir" aria-label="Rentals on ${escapeHtml(t.label)}" style="flex:0 0 auto;min-height:36px;font-size:0.85rem;max-width:100%;">${['', 'out', 'back'].map(v =>
    `<option value="${v}"${v === cur ? ' selected' : ''}>${escapeHtml(_JT_RENTAL_DIR_LABEL[v])}</option>`).join('')}</select>`;
}

function _jobTypeRentalDirHelpHtml() {
  if (!_jobTypeRentalDirShown(_jobTypeList())) return '';
  return `<p style="margin:0 0 10px;font-size:0.85rem;color:var(--text-secondary);">Rentals: on a type that <b>comes back</b>, rentals you pick start as Return and a return job uses that type. On a type that <b>goes out</b>, they start as Deploy. Jobs already saved don't change.</p>`;
}

function _jobTypeRentalDirToast(r) {
  const t = _jobTypeList().find(x => x.value === r.value);
  const what = r.to === 'back' ? 'rentals come back' : r.to === 'out' ? 'rentals go out' : 'rentals as picked';
  return `${t ? t.label : 'Job type'}: ${what}`;
}

// Is this asset on the job being EDITED, as saved? (Never flipped by a type.)
// Reads every day pool editJob can seed from — the board, search, and the
// out-of-window cache a Projects open fills (COD r1 Independent ❌). A job
// whose days are in none of them answers true: unknown = never flip.
function _jobTypeRentalDirOnSavedJob(assetId) {
  const jobId = document.getElementById('job-id')?.value || '';
  if (!jobId) return false;
  const id = String(assetId);
  const ext = typeof _externalJobDays === 'function' ? _externalJobDays() : [];
  const days = [...(DispatchState.jobDays || []), ...(DispatchState.searchJobDays || []), ...(ext || [])]
    .filter(d => d && d.jobId === jobId);
  if (!days.length) return true;
  return days.some(d => Array.isArray(d.assets) && d.assets.map(String).includes(id));
}

/**
 * PURE. What the job type does to ONE ticked rental. → 'return' | 'deploy' |
 * null (leave it).
 *   typeDir — the type's setting: 'out' | 'back' | null
 *   cur     — the form's current direction for it (undefined = never set = Deploy)
 *   typed   — that direction was set by the TYPE (not a person / prefill)
 *   onSaved — it is on the saved job being edited
 */
function _jobTypeRentalDirFor(typeDir, cur, typed, onSaved) {
  if (onSaved) return null;
  if (cur != null && !typed) return null;
  if (typeDir === 'back') return cur === 'return' ? null : 'return';
  return typed && cur === 'return' ? 'deploy' : null;
}

function _jobTypeRentalDirPaint(assetId) {
  const box = document.getElementById('job-select-assets');
  if (!box) return;
  const cb = Array.from(box.querySelectorAll('input[type="checkbox"]')).find(i => i.value === String(assetId));
  const tile = cb?.closest('.checkbox-item');
  if (!tile) return;
  const dir = DispatchState.pendingAssetDirections[assetId] || 'deploy';
  const btn = tile.querySelector('.asset-direction-toggle');
  if (btn) {
    btn.className = `asset-direction-toggle ${dir}`;
    btn.title = dir === 'deploy' ? 'Deploying to job site' : 'Returning to yard';
    btn.innerHTML = `<span class="material-icons" style="font-size:13px;">${dir === 'deploy' ? 'arrow_forward' : 'arrow_back'}</span><span>${dir === 'deploy' ? 'Deploy' : 'Return'}</span>`;
  }
  const asset = (DispatchState.assets || []).find(a => a.id === assetId);
  const prev = tile.querySelector('.asset-inv-preview');
  if (asset && prev) prev.outerHTML = buildInventoryPreview(asset, dir, DispatchState.pendingAssetQtys[assetId] || 1);
}

function _jobTypeRentalDirApply(assetId, typeDir) {
  const id = String(assetId);
  const typed = _jtDirTypedSet();
  if (!DispatchState.pendingAssetDirections) DispatchState.pendingAssetDirections = {};
  const next = _jobTypeRentalDirFor(typeDir, DispatchState.pendingAssetDirections[id], typed.has(id), _jobTypeRentalDirOnSavedJob(id));
  if (!next) return false;
  if (next === 'return') { DispatchState.pendingAssetDirections[id] = 'return'; typed.add(id); }
  else { delete DispatchState.pendingAssetDirections[id]; typed.delete(id); }
  _jobTypeRentalDirPaint(id);
  return true;
}

// A rental was just ticked on the Add/Edit Job form (one tile, Select All,
// or a template apply — every way a person adds equipment here).
function _jobTypeRentalDirOnTick(assetId, checked) {
  if (!checked || !_jobTypeRentalDirActive()) return;
  if (!_jtIsRentalAsset((DispatchState.assets || []).find(a => a && a.id === assetId))) return;
  _jtDirState().here.add(String(assetId));
  _jobTypeRentalDirApply(assetId, _jobTypeRentalDirOf(document.getElementById('job-type')?.value));
}
function _jobTypeRentalDirOnAdded(assetIds) {
  for (const id of (assetIds || [])) _jobTypeRentalDirOnTick(String(id), true);
}

// The Type changed (or the form opened / the list was edited). Only rentals
// ticked on THIS form follow it.
function _jobTypeRentalDirOnTypeChange(jobType) {
  if (!_jobTypeRentalDirActive()) { _jobTypeRentalNoteRender(null); return; }
  const typeDir = _jobTypeRentalDirOf(jobType);
  const here = _jtDirState().here;
  for (const id of getJobCrewSelected('assets')) {
    if (here.has(String(id))) _jobTypeRentalDirApply(id, typeDir);
  }
  _jobTypeRentalNoteRender(typeDir);
}

// The line under Type: what this type does with rentals, and — on a NEW job
// of a "come back" type with a project picked — one tap to fill in what is
// still out on that project. Not automatic: a dispatcher mid-edit keeps
// what they typed until they ask.
function _jobTypeRentalNoteRender(typeDir) {
  const sel = document.getElementById('job-type');
  if (!sel) return;
  let note = document.getElementById('job-type-rental-note');
  if (!typeDir) { if (note) note.remove(); return; }
  if (!note) {
    note = document.createElement('div');
    note.id = 'job-type-rental-note';
    note.style.cssText = 'margin-top:6px;font-size:0.8rem;color:var(--text-secondary);display:flex;flex-wrap:wrap;align-items:center;gap:8px;min-width:0;';
    (sel.closest('.form-group') || sel.parentElement).appendChild(note);
  }
  const projSel = document.getElementById('job-project-id');
  if (projSel && !projSel.dataset.jtRentalWatch) {
    projSel.dataset.jtRentalWatch = '1';
    projSel.addEventListener('change', () => _jobTypeRentalNoteRender(_jobTypeRentalDirOf(document.getElementById('job-type')?.value)));
  }
  const isNew = !(document.getElementById('job-id')?.value);
  const offer = typeDir === 'back' && isNew && !!projSel?.value && !DispatchState.returnJobCtx && _returnJobCanCreate();
  note.innerHTML = `<span style="flex:1 1 200px;min-width:0;overflow-wrap:anywhere;">${typeDir === 'back'
    ? 'Rentals on this type come back — new picks start as Return.'
    : 'Rentals on this type go out — new picks start as Deploy.'}</span>${offer
    ? `<button type="button" class="btn btn-secondary btn-small" id="btn-jt-fill-returns" style="min-height:40px;white-space:nowrap;" onclick="window.DispatchModule.fillProjectReturnsForType(this)"><span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:-3px;">assignment_return</span> Fill in what's still out</button>`
    : ''}`;
}

// One tap: every rental the picked project still has out, ticked as Return at
// that count (S1's count, _loadProjectRentalsOut). Editable after.
let _jtFillReturnsBusy = false;
async function fillProjectReturnsForType(btn) {
  if (_jtFillReturnsBusy || !_jobTypeRentalDirActive()) return;
  if (!_returnJobCanCreate()) { window.showToast?.('Your access can’t create jobs — ask an admin to add the Dispatch module.', 'error'); return; }
  const projectId = document.getElementById('job-project-id')?.value || '';
  if (!projectId || document.getElementById('job-id')?.value) return;
  const gen = _jobFormGen;
  _jtFillReturnsBusy = true;
  if (btn) btn.disabled = true;
  try {
    let loaded;
    try { loaded = await _loadProjectRentalsOut({ id: '', projectId }); } catch (e) {
      console.warn('[Dispatch] TASK-10006 S2: project count failed', e?.code || e?.message || e);
      window.showToast?.('Couldn’t add up what’s out on this project. Check your connection and try again.', 'error');
      return;
    }
    if (!loaded || gen !== _jobFormGen || document.getElementById('job-project-id')?.value !== projectId) return;
    // COD r1 Code: the type may have changed while the count loaded.
    if (_jobTypeRentalDirOf(document.getElementById('job-type')?.value) !== 'back') return;
    const rows = loaded.res.rows.filter(r => r.pickable > 0);
    if (!rows.length) {
      const booked = loaded.res.rows.some(r => r.planned > 0 || r.inspecting > 0);
      window.showToast?.(booked ? 'Everything still out already has a pickup booked or is waiting on inspection.' : 'Nothing is out on this project to bring back.', 'info');
      return;
    }
    const n = _jobTypeApplyReturnRows(rows);
    DispatchState.returnJobCtx = {
      sourceJobId: null, projectId, customerId: document.getElementById('job-customer')?.value || null,
      pickable: Object.fromEntries(rows.map(r => [r.assetId, r.pickable]))
    };
    _jobTypeRentalNoteRender('back');
    window.showToast?.(`Filled in ${n} rental${n === 1 ? '' : 's'} to bring back. Change any number for a partial return.`, 'success');
  } finally {
    _jtFillReturnsBusy = false;
    if (btn && btn.isConnected) btn.disabled = false;
  }
}

// Tick each row as Return at its count. A person's explicit choice now.
function _jobTypeApplyReturnRows(rows) {
  const typed = _jtDirTypedSet();
  if (!DispatchState.pendingAssetDirections) DispatchState.pendingAssetDirections = {};
  if (!DispatchState.pendingAssetQtys) DispatchState.pendingAssetQtys = {};
  const box = document.getElementById('job-select-assets');
  const tileOf = (id) => box ? Array.from(box.querySelectorAll('input[type="checkbox"]')).find(i => i.value === id)?.closest('.checkbox-item') : null;
  const missing = [];
  for (const r of rows) {
    const id = String(r.assetId);
    DispatchState.pendingAssetDirections[id] = 'return';
    typed.delete(id);
    const a = (DispatchState.assets || []).find(x => x && x.id === id);
    if (a && a.trackingMode === 'quantity') DispatchState.pendingAssetQtys[id] = r.pickable;
    const tile = tileOf(id);
    if (!tile) { missing.push(id); continue; }
    if (!tile.querySelector('input[type="checkbox"]').checked) toggleJobCheckbox(tile, 'assets');
    const q = tile.querySelector('.asset-dispatch-qty');
    if (q && a && a.trackingMode === 'quantity') { q.value = String(r.pickable); setAssetQty(id, r.pickable, q); }
    _jobTypeRentalDirPaint(id);
  }
  if (missing.length) {
    // Hidden from the picker (showInDispatch off): re-render Assets with them ticked.
    const sel = [...new Set([...getJobCrewSelected('assets'), ...missing])];
    const vis = (DispatchState.assets || []).filter(a => a.showInDispatch !== false || sel.includes(a.id));
    populateJobMultiSelect('job-select-assets', typeof sortPinnedAssetsFirst === 'function' ? sortPinnedAssetsFirst(vis, _pinnedAssetIds(window.AppState?.settings)) : vis, sel, !DispatchState.dataLoaded.assets);
    updateJobSectionCount('assets');
  }
  return rows.length;
}

// A return job (S1 Create return job / S3 prompt) takes the company's
// "come back" type. No such type → the copied type stays, as today.
function _jobTypeApplyReturnType(gen) {
  if (gen !== _jobFormGen) return;
  const sel = document.getElementById('job-type');
  const JT = window.JobTypes;
  if (!sel || !JT || typeof JT.returnTypeFor !== 'function') return;
  const want = JT.returnTypeFor(_jobTypeList(), sel.value);
  if (!want || want === sel.value) return;
  _renderJobTypeSelect(want);
  onJobTypeChange();
}

// Handle job type change (show/hide relevant fields)
function onJobTypeChange() {
  const jobType = document.getElementById('job-type').value;
  const customerGroup = document.getElementById('job-customer')?.closest('.form-group');
  const pocSection = document.getElementById('poc-section');

  // For meetings/shop days, customer is optional - dim it but don't hide
  if (customerGroup) {
    customerGroup.style.opacity = (jobType === 'meeting' || jobType === 'shop_day') ? '0.6' : '1';
  }

  // Auto-set title placeholder based on type
  const titleInput = document.getElementById('job-title');
  if (titleInput) {
    if (jobType === 'meeting') {
      titleInput.placeholder = 'Safety Meeting, Team Standup, etc.';
    } else if (jobType === 'shop_day') {
      titleInput.placeholder = 'Shop Day, Truck Maintenance, etc.';
    } else {
      titleInput.placeholder = 'Oil field service - Well #123';
    }
  }
  // TASK-9844 — a job type can need crew skills; re-check the picked crew.
  setTimeout(runJobComplianceCheck, 0);
  // TASK-10006 S2 — the type's rental direction (go out / come back).
  _jobTypeRentalDirOnTypeChange(jobType);
}

// TASK-1584 — advisory "also on another job today" for people + vehicles.
// Splitting a crew member or a truck across two jobs in one day is a normal,
// frequent pattern here (start one bore, hand it off when relief arrives), so
// this NEVER blocks, never toasts and never gates the save — it just says so
// out loud, because before this a WRONG double-book looked identical to a right
// one. Blocking would be the TASK-428 mistake again: dispatchers would route
// around it mid-dispatch.
//
// Best-effort by design. DispatchState.jobDays is windowed (admin = 90d back +
// all future, subscribeToData), so a same-date conflict on a current or future
// day is always in-window; the only false negative is editing a jobDay older
// than the admin window before "Load older" ran. Advisory-only, so a miss costs
// a hint and not a safety gate — do NOT add a Firestore round-trip to close it.
function buildDoubleBookIndex(currentJobDay) {
  // Two separate maps so a person named "Hooty" and a truck nicknamed "Hooty"
  // can't badge each other. ref string (Firestore id OR legacy name/unit) -> Set of job titles.
  const index = { people: new Map(), equipment: new Map() };
  if (!currentJobDay) return index;
  const curDate = currentJobDay.date?.toDate?.() || (currentJobDay.date ? new Date(currentJobDay.date) : null);
  if (!curDate || isNaN(curDate.getTime())) return index;
  const curDateStr = formatDateISOLocal(curDate);

  DispatchState.jobDays.forEach(jd => {
    // Exclude the day being edited, or every assignment badges itself
    if (!jd || jd.id === currentJobDay.id) return;
    if (jd.status === 'cancelled') return;
    const jdDate = jd.date?.toDate?.() || (jd.date ? new Date(jd.date) : null);
    if (!jdDate || isNaN(jdDate.getTime()) || formatDateISOLocal(jdDate) !== curDateStr) return;
    const job = DispatchState.jobs.find(j => j.id === jd.jobId);
    // Legacy/GAS rows have no status at all — test !== 'finished', never === 'active'
    if (!job || job.status === 'finished' || job.status === 'cancelled') return;
    const title = getPlacardTitle(job);
    const add = (map, ref) => {
      if (!ref) return;
      const key = String(ref);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(title);
    };
    // Union drivers + helpers so a driver-here / helper-there conflict shows
    // once rather than zero or twice
    (jd.drivers || []).forEach(r => add(index.people, r));
    (jd.helpers || []).forEach(r => add(index.people, r));
    // Vehicles get the same advisory, not a stronger gate — slip-seating is
    // normal in small fleets and there's no HOS/legal distinction here
    (jd.trucks || []).forEach(r => add(index.equipment, r));
    (jd.trailers || []).forEach(r => add(index.equipment, r));
  });
  return index;
}

// Dual ref matching (Pitfall #5) — crew/equipment arrays hold Firestore IDs OR
// name strings from the GAS import, and a vehicle's display name lives in
// `nickname`, not `name` (TASK-1324).
function getDoubleBookTitles(index, item, scope) {
  const map = index?.[scope];
  if (!map || map.size === 0 || !item) return [];
  const titles = new Set();
  const keys = scope === 'people' ? [item.id, item.name] : [item.id, item.unit, item.nickname, item.name];
  keys.forEach(k => {
    if (!k) return;
    const hit = map.get(String(k));
    if (hit) hit.forEach(t => titles.add(t));
  });
  return Array.from(titles);
}

// Every doc id in the picker's roster. The legacy name/unit fallback below
// must never claim a value that is a real doc id — TITS's vehicles carry
// numeric doc ids ("2", "15", "16") AND numeric units ("15", "16"), so the two
// namespaces collide.
// Scoped to the picker's OWN roster (COD Code Reviewer): a truck picker must
// not let a person's or trailer's doc id veto a legitimate legacy truck unit.
function _multiSelectKnownIds(items, containerId) {
  const ROSTER = {
    'select-trucks': 'trucks', 'select-trailers': 'trailers', 'select-assets': 'assets',
    'select-drivers': 'people', 'select-helpers': 'people', 'select-people': 'people',
  };
  const ids = new Set();
  const add = (arr) => { for (const x of (arr || [])) { if (x && x.id != null) ids.add(String(x.id)); } };
  add(items);
  const S = (typeof DispatchState !== 'undefined' && DispatchState) ? DispatchState : {};
  const key = ROSTER[containerId];
  if (key) add(S[key]);
  return ids;
}

// Is `item` selected by the stored `selected` array? A doc-id match is the
// truth. The name/unit match only exists for legacy GAS imports that stored a
// name or unit instead of an id, so it only applies to a stored value that is
// NOT any known doc id — otherwise "15" (Dove's id) also ticks unit 15 (Blue
// Jay) and the save writes a truck nobody picked. Pure: no DOM, no Firestore.
function multiSelectIsSelected(item, selected, knownIds) {
  if (!item) return false;
  const sel = Array.isArray(selected) ? selected : [];
  if (sel.includes(item.id)) return true;
  const isLegacyValue = (v) => v != null && v !== '' && !(knownIds && knownIds.has(String(v)));
  if (item.name && sel.includes(item.name) && isLegacyValue(item.name)) return true;
  if (item.unit && sel.includes(item.unit) && isLegacyValue(item.unit)) return true;
  return false;
}

function populateMultiSelect(containerId, items, selected, isLoading = false, doubleBookIndex = null) {
  const container = document.getElementById(containerId);
  if (!container) {
    console.warn('[Dispatch] Container not found:', containerId);
    return;
  }

  const sectionKey = containerId.replace('select-', '');
  const selectedCount = selected.length;
  const sectionEl = document.getElementById(`section-${sectionKey}`);

  console.log(`[Dispatch] Populating ${containerId}:`, { itemCount: items?.length, isLoading, selected });

  // Show loading state if data hasn't loaded yet
  if (isLoading) {
    container.innerHTML = '<p class="text-muted"><span class="loading-spinner-small"></span> Loading...</p>';
    return;
  }

  if (!items || items.length === 0) {
    container.innerHTML = '<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">inbox</span>No items available</div>';
    // Close the section if no items
    if (sectionEl) sectionEl.classList.remove('open');
    return;
  }

  // Add search bar when 5+ items
  const isPeopleSection = containerId === 'select-drivers' || containerId === 'select-helpers' || containerId === 'select-people';
  // TASK-1584 — which map in the double-book index this section reads from.
  // Assets and subcontractors are deliberately out of scope (quantity-tracked /
  // external), so they stay null and render no badge.
  const doubleBookScope = isPeopleSection ? 'people'
    : (containerId === 'select-trucks' || containerId === 'select-trailers') ? 'equipment' : null;
  const assignLabelMap = { 'select-drivers': 'people', 'select-helpers': 'people', 'select-trucks': 'vehicles', 'select-trailers': 'trailers', 'select-assets': 'assets' };
  let searchHtml = '';
  if (items.length >= 5) {
    searchHtml = `<input type="search" class="form-control form-control-sm multi-select-search"
      placeholder="Search ${assignLabelMap[containerId] || ''}..." autocomplete="off" oninput="window.DispatchModule.filterMultiSelect('${escJsAttr(containerId)}', this.value)"
      style="margin-bottom:8px;font-size:0.8rem;">`;
  }

  // TASK-9510 — yard/branch on same-name asset rows.
  const _assetRowLabels = containerId === 'select-assets' ? assetPickerRowLabels(items, window.AppState?.settings?.yards, _assetPickerName) : null;
  // Steve (TITS) 2026-10-08 — "Blue Jay is still showing up in Add a Day as a
  // truck." It was being TICKED, not just listed: a day storing Dove's doc id
  // "15" also ticked Blue Jay, whose UNIT is "15", and the next Save wrote
  // Blue Jay ("2") onto the day. See multiSelectIsSelected.
  const _knownIds = _multiSelectKnownIds(items, containerId);
  container.innerHTML = searchHtml + items.map(item => {
    const isSelected = multiSelectIsSelected(item, selected, _knownIds);
    // Show role tag for people sections
    let roleTag = '';
    if (isPeopleSection) {
      const roleSlug = item.crewType || item.role || (item.roles && item.roles[0]) || '';
      if (roleSlug) {
        const roleLabel = window.getTypeLabel?.(roleSlug, 'crewTypes') || roleSlug; // TASK-9884 — (slug, kind); was reversed: every person read "CrewTypes"
        roleTag = `<span class="role-tag">${escapeHtml(roleLabel)}</span>`;
      }
    }
    // TASK-9227 — compliance flag, BEFORE selection. `select-helpers` is included
    // deliberately: the TASK-258 hard stop maps `selectedDrivers` only (documented
    // convention below) and checkDOTCompliance has no helper parameter at all, so
    // the helper slot was the one seat a flagged person could take with nothing
    // whatsoever looking at them.
    const flagTag = isPeopleSection ? _dfBadgeHtml(item) : '';
    // Quantity input for quantity-tracked assets (standard, consumable, rental)
    const isQtyAsset = containerId === 'select-assets' && item.trackingMode === 'quantity';
    const existingQty = isQtyAsset ? (DispatchState.pendingAssetQtys?.[item.id] || 1) : 0;
    const qtyLabel = item.consumable ? 'Stock' : 'Available';
    const availableQty = (item.quantity || 0) - (item.deployedQty || 0);
    // TASK-9275 — flagged when the count is below zero; plain pill otherwise.
    const qtyTag = isQtyAsset ? `${assetStockTagHtml(item, availableQty, qtyLabel)}
      <input type="number" class="asset-dispatch-qty" data-asset-id="${escapeHtml(item.id)}" min="1" value="${existingQty}" onclick="event.stopPropagation();" oninput="window.DispatchModule.setAssetQty('${escJsAttr(item.id)}', this.value, this)" onchange="window.DispatchModule.setAssetQty('${escJsAttr(item.id)}', this.value, this)" style="width:56px;flex-shrink:0;text-align:center;padding:2px 4px;font-size:0.8rem;border:1px solid var(--border-light);border-radius:4px;margin-left:4px;">` : '';
    // TASK-1584 — advisory only. Job titles are tenant-writable free text, so
    // escapeHtml() the text node and escAttr the title= attribute; raw
    // interpolation here is stored XSS in every dispatcher's session.
    let doubleBookTag = '';
    if (doubleBookIndex && doubleBookScope) {
      const otherTitles = getDoubleBookTitles(doubleBookIndex, item, doubleBookScope);
      if (otherTitles.length > 0) {
        const label = otherTitles.join(', ');
        const escAttrFn = window.escAttr || escapeHtml;
        const tooltip = escAttrFn('Also scheduled today on: ' + label);
        doubleBookTag = `<span class="checkbox-double-book" title="${tooltip}" style="flex:0 0 100%;margin-left:32px;margin-top:-2px;font-size:0.7rem;line-height:1.3;color:#B45309;white-space:normal;">&#9888; On: ${escapeHtml(label)}</span>`;
      }
    }
    return `
      <div class="checkbox-item ${isSelected ? 'checked' : ''}"${doubleBookTag ? ' style="flex-wrap:wrap;"' : ''} onclick="window.DispatchModule.toggleCheckbox(this, '${escJsAttr(sectionKey)}')">
        <input type="checkbox" value="${item.id}" ${isSelected ? 'checked' : ''}>
        <span class="checkbox-name">${escapeHtml(window.getUnitDisplay ? (window.getUnitDisplay(item) || item.name || 'Unknown') : (item.name || item.unit || 'Unknown'))}${_assetRowLabels && _assetRowLabels.has(item.id) ? `<span class="asset-yard-tag" style="display:block;font-size:0.72rem;font-weight:400;line-height:1.2;color:var(--text-secondary);">${escapeHtml(_assetRowLabels.get(item.id))}</span>` : ''}${_rentalHeldTagHtml(item, containerId)}</span>
        ${roleTag}
        ${flagTag}
        ${qtyTag}
        ${doubleBookTag}
      </div>
    `;
  }).join('');

  // Update count badge
  const countEl = document.getElementById(`count-${sectionKey}`);
  if (countEl) {
    countEl.textContent = selectedCount;
    countEl.style.display = selectedCount > 0 ? '' : 'none';
  }

  // Always keep sections closed by default
  if (sectionEl) {
    sectionEl.classList.remove('open');
  }
}

// TASK-9510 — the name the picker row shows (same expression as the row).
function _assetPickerName(item) {
  return window.getUnitDisplay ? (window.getUnitDisplay(item) || item.name || 'Unknown') : (item.name || item.unit || 'Unknown');
}

// TASK-9510 — PURE. A tenant with "Durabase" in three yards got three identical
// picker rows, so the dispatcher could not tell which yard they were pulling
// from. Returns Map(id -> distinguishing label) for rows whose DISPLAY name
// collides with another row (case/space-insensitive); unique names get nothing,
// so the common picker stays as quiet as it was. Label = live yard label
// (settings.yards), else the denormalized yardName, else "No yard". If two
// colliding rows share that label too, the unit/serial (or an id tail) is added.
// Callers MUST escape the label — yard labels are tenant free text.
function assetPickerRowLabels(items, yards, displayNameOf) {
  const out = new Map();
  if (!Array.isArray(items) || items.length < 2) return out;
  const nameOf = typeof displayNameOf === 'function' ? displayNameOf : (it) => (it && (it.name || it.unit)) || '';
  const yardList = Array.isArray(yards) ? yards : [];
  const norm = (s) => String(s == null ? '' : s).trim().replace(/\s+/g, ' ').toLowerCase();
  const yardLabelOf = (it) => {
    if (!it.yardId) return it.yardName ? String(it.yardName) : 'No yard';
    const y = yardList.find(x => x && x.slug === it.yardId);
    if (y && y.label) return String(y.label);
    return it.yardName ? String(it.yardName) : 'Former facility';
  };
  const groups = new Map();
  items.forEach(it => {
    if (!it || it.id == null) return;
    const k = norm(nameOf(it));
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(it);
  });
  groups.forEach(group => {
    if (group.length < 2) return;
    const labels = group.map(yardLabelOf);
    const seen = {};
    labels.forEach(l => { seen[l] = (seen[l] || 0) + 1; });
    const finals = group.map((it, i) => {
      let l = labels[i];
      if (seen[l] > 1) {
        const tail = it.serialNumber || it.serial || it.unit || it.assetTag || `#${String(it.id).slice(-4)}`;
        l = `${l} · ${tail}`;
      }
      return l;
    });
    // COD (Code) — the yard + unit can tie too; the id tail then guarantees
    // every row in the group reads differently.
    const finalSeen = {};
    finals.forEach(l => { finalSeen[l] = (finalSeen[l] || 0) + 1; });
    group.forEach((it, i) => {
      // COD r2 — a 6-char tail can tie too (first-123456 / second-123456); the full id cannot.
      out.set(it.id, finalSeen[finals[i]] > 1 ? `${finals[i]} · #${String(it.id)}` : finals[i]);
    });
  });
  return out;
}

// TASK-9510 — PURE. The assetQtys a CREATE / Assign Crew save writes. A
// quantity-tracked asset always gets an explicit qty (1 included): before, qty 1
// was omitted (`> 1`) and every reader had to know to default it. Non-quantity
// assets keep the legacy `> 1` rule. Only SELECTED ids are ever written — an
// unticked row's typed qty never reaches here (setAssetQty ticks the row).
function buildSavedAssetQtys(selectedIds, pendingQtys, assets) {
  const out = {};
  const pool = Array.isArray(assets) ? assets : [];
  const pending = pendingQtys || {};
  (Array.isArray(selectedIds) ? selectedIds : []).forEach(id => {
    const a = pool.find(x => x && x.id === id);
    const raw = Number(pending[id]);
    const qty = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : null;
    if (a && a.trackingMode === 'quantity') out[id] = qty || 1;
    else if (qty && qty > 1) out[id] = qty;
  });
  return out;
}

// TASK-9510 — typing a quantity on an UNticked row used to write
// pendingAssetQtys and nothing else; save reads only the ticked rows, so the
// rental was dropped with no warning (and went unbilled). A positive qty now
// ticks its row. 🔴 Only ever unticked → ticked: an already-ticked row is left
// alone (routing a qty edit through the toggle would UN-tick it and drop it).
// The toggle is chosen by the row's container so Add Job and Assign Crew each
// get their own side effects (line-item picker, compliance, copy-carry retire).
function _tickAssetRowForQty(inputEl, value) {
  const n = parseInt(value, 10);
  if (!inputEl || !Number.isFinite(n) || n < 1) return false;
  const row = inputEl.closest?.('.checkbox-item');
  const cb = row?.querySelector('input[type="checkbox"]');
  if (!row || !cb || cb.checked) return false;
  if (row.closest('#job-select-assets')) toggleJobCheckbox(row, 'assets');
  else if (row.closest('#select-assets')) toggleCheckbox(row, 'assets');
  else return false;
  if (!cb.checked) { cb.checked = true; row.classList.add('checked'); }
  return true;
}

function setAssetQty(assetId, value, inputEl) {
  DispatchState.pendingAssetQtys[assetId] = Math.max(1, parseInt(value) || 1);
  _tickAssetRowForQty(inputEl, value);
  // TASK-9258 — retyping the quantity is the strongest confirmation there is:
  // this is the number that moves stock and the number that bills. No-op in the
  // Assign Crew modal, which never carries a template chip.
  confirmTemplateRowFromDom('assets', assetId);
  updateJobLoadCalcLive();
  // Refresh inventory preview for rental assets
  const asset = DispatchState.assets.find(a => a.id === assetId);
  if (asset && (asset.quantityType === 'rental' || asset.isRental)) {
    const previewEl = document.querySelector(`.asset-inv-preview[data-asset-id="${assetId}"]`);
    if (previewEl) {
      const dir = DispatchState.pendingAssetDirections[assetId] || 'deploy';
      const qty = DispatchState.pendingAssetQtys[assetId] || 1;
      previewEl.outerHTML = buildInventoryPreview(asset, dir, qty);
    }
  }
}

function toggleAssetDirection(assetId, btnEl) {
  const cur = DispatchState.pendingAssetDirections[assetId] || 'deploy';
  const next = cur === 'deploy' ? 'return' : 'deploy';
  DispatchState.pendingAssetDirections[assetId] = next;
  if (typeof _jobTypeRentalDirForget === 'function') _jobTypeRentalDirForget(assetId); // TASK-10006 S2 — a human choice; the type never flips it again (typeof: suites lift this function out alone)
  // TASK-9258 — flipping deploy/return on a templated row is a human decision
  // about that row. A template can never preset this (it stores no directions),
  // so reaching here at all means someone chose it.
  confirmTemplateRowFromDom('assets', assetId, btnEl?.closest?.('.checkbox-item'));

  // Update button
  if (btnEl) {
    btnEl.className = `asset-direction-toggle ${next}`;
    btnEl.title = next === 'deploy' ? 'Deploying to job site' : 'Returning to yard';
    btnEl.innerHTML = `<span class="material-icons" style="font-size:13px;">${next === 'deploy' ? 'arrow_forward' : 'arrow_back'}</span><span>${next === 'deploy' ? 'Deploy' : 'Return'}</span>`;
  }

  // Update inventory preview
  const asset = DispatchState.assets.find(a => a.id === assetId);
  if (asset) {
    const previewEl = document.querySelector(`.asset-inv-preview[data-asset-id="${assetId}"]`);
    if (previewEl) {
      const qty = DispatchState.pendingAssetQtys[assetId] || 1;
      previewEl.outerHTML = buildInventoryPreview(asset, next, qty);
    }
  }
}

function buildInventoryPreview(asset, direction, qty) {
  if (!asset) return '';
  const isQty = asset.trackingMode === 'quantity';
  if (isQty) {
    const total = asset.quantity || 0;
    const deployed = asset.deployedQty || 0;
    // TASK-4810 — more out than owned: the available count is unknown, and a
    // "-2168 available → -2178" line reads as a number rather than a problem.
    const rv = rentalStockView(asset);
    if (rv && rv.drift) {
      const fmt = (n) => Number(n || 0).toLocaleString('en-US');
      return `<div class="asset-inv-preview" data-asset-id="${asset.id}" style="width:100%;padding:2px 0 0 28px;font-size:0.7rem;color:#92400E;font-weight:600;">&#9888; Needs recount: ${fmt(rv.out)} out against ${fmt(rv.owned)} owned</div>`;
    }
    const available = total - deployed;
    const projected = direction === 'deploy' ? available - qty : available + qty;
    const color = projected < 0 ? '#DC2626' : 'var(--text-secondary)';
    // TASK-4810 — say it in words when a deploy would put more out than owned
    // (the save will ask before it writes).
    const overNote = (rv && direction === 'deploy' && projected < 0) ? ` — ${-projected} more than you have` : '';
    return `<div class="asset-inv-preview" data-asset-id="${asset.id}" style="width:100%;padding:2px 0 0 28px;font-size:0.7rem;color:${color};">${available} available → ${overNote ? 0 : projected} after ${direction}${overNote}</div>`;
  } else {
    // Individual asset
    const status = asset.deploymentStatus || 'available';
    const projected = direction === 'deploy' ? 'deployed' : 'available';
    return `<div class="asset-inv-preview" data-asset-id="${asset.id}" style="width:100%;padding:2px 0 0 28px;font-size:0.7rem;color:var(--text-secondary);">Currently ${status} → ${projected} after ${direction}</div>`;
  }
}

// TASK-1255 — transactional, zero-FLOORED decrement of a rental asset's
// deployedQty counter. Reads the committed value INSIDE the transaction and
// writes Math.max(0, current - qty), so the floor is PERSISTED to Firestore.
// The old fire-and-forget `deployedQty: increment(-qty)` had no floor, so
// cumulative returns > deploys (historical returns, reversal double-decrements)
// drove the stored counter negative — which inflates availableQty (= quantity
// - deployedQty) and shows more units deployable than physically exist.
// runTransaction auto-retries on concurrent-return contention. Returns the
// committed floored value so the caller sets its in-memory mirror from THAT,
// never recomputing from a stale local deployedQty. `extraFields` are merged
// into the same atomic write (e.g. yard/hours on a pretrip return).
// TASK-4810 #2 — `alsoInTx(tx, moved)` (optional) runs inside the SAME
// transaction with what the counter really dropped by (floored), so a caller can
// write its ledger row atomically with the change. A retried transaction re-runs
// it and only the committed attempt's writes land, so it can never write twice.
async function decrementDeployedQtyFloored(tenantId, assetId, qty, extraFields = {}, alsoInTx = null) {
  const ref = doc(db, 'tenants', tenantId, 'assets', assetId);
  return await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = (snap.exists() ? snap.data().deployedQty : 0) || 0;
    const floored = Math.max(0, current - qty);
    // TASK-1255 detector — the floor prevents negative drift, but a hit means
    // returns exceeded deploys (double-return / duplicate-submit / bad seed).
    // Log it so the ROOT cause is visible instead of silently masked.
    if (current - qty < 0) console.warn(`[Rental] deployedQty floor hit on asset ${assetId}: ${current} - ${qty} < 0 -> clamped to 0. Returns exceeded deploys — investigate if recurring.`);
    tx.update(ref, { deployedQty: floored, updatedAt: Timestamp.now(), ...extraFields });
    if (typeof alsoInTx === 'function') alsoInTx(tx, current - floored);
    return floored;
  });
}

// ── TASK-9336 — THE JOB-LEVEL RENTAL LEDGER ─────────────────────────────────
//
// THE DEFECT. A rental's stock moves ONCE PER JOB: saveJob's create deploys it
// once for the whole range, and add-day, the date-sync range-extend (TASK-9331)
// and templates COPY `assets` + `assetQtys` + `assetDirections` onto new days
// without moving anything. cancelJobDay / removeJobDay / saveAssignment then
// reversed per DAY from that day's own labels, so every copied day handed back
// a deploy it never made (floored at 0, so stock silently under-reported).
//
// WHY A LEDGER AND NOT A RULE. A first attempt derived "the last live day
// holding the rental gives it back" from the days' labels (branch
// worktree-agent-afd21a0c3220a5159, NOT merged). COD sank it, correctly: a
// day's `assetDirections` label is not proof stock moved. Edit Job flips and
// removes rentals without moving stock, add-day copies `return` forward, and
// date-sync deletes days without reversing anything — so any rule derived from
// labels has sequences that never give the units back, or give them back twice.
//
// THE RECORD. `jobs/{jobId}.rentalLedger` = { [assetId]: { out, parked } }
//   out    — units THIS job has actually deployed and not yet handed back or
//            handed to a return (a pending return inspection holds its own
//            share as `ledgerQty` on the returnInspections doc).
//   parked — units given back because the job's last live day holding the
//            rental left. restoreJobDay re-deploys them; a fresh deploy clears it.
// Written by: saveJob create (the ledger is born `{}` on the job doc),
// processRentalTransaction (every deploy/return on a ledger job), and every
// reversal below — each in ONE transaction with the deployedQty /
// deploymentStatus change it describes, so the two can never disagree.
//
// THE RULE on a day leaving (cancel, remove, or a rental taken off it): the
// day's labels decide NOTHING about quantity. It cancels the pending return
// inspections it owns (`jobDayId`) and puts their `ledgerQty` back in `out`;
// then, only if no OTHER live day of the job still carries the rental, it gives
// back exactly `out` — whatever the job really holds, which may be 0. "Live" =
// exists and is not cancelled. A reversal can therefore never return more than
// the job deployed, and running it twice gives back 0 the second time.
//
// Jobs WITHOUT `rentalLedger` (everything created before this shipped, and the
// billing / projects create paths) keep the pre-ledger behaviour exactly. There
// is no backfill: the ledger cannot be reconstructed from labels, which is the
// whole point. Live exposure at ship: 3 rental jobDays, all demo.
//
// The ledger lives on the JOB doc, not a new collection: no rules change, and
// the job doc is only ever written by partial updateDoc (demoSeeder's setDoc is
// the one full overwrite — a reseeded demo job simply has no ledger). Its
// writes touch no `onJobUpdate` significant field, so they notify nobody.
//
// Not reviewed-in by the first cut (independent review 2026-09-23, 🔴): an
// IMMEDIATE return (no inspection) moves stock at the moment it is scheduled,
// so the ledger also records it per day (`dayReturns`) and a day leaving puts
// exactly those units back out; restoreJobDay re-applies what a cancel undid.

// dayReturns key for an immediate return made by saveJob's create — no single
// day of the range owns it. NOT '__job__': Firestore rejects any field name
// that begins AND ends with "__", nested map keys included (review 🔴).
const RENTAL_LEDGER_JOB_KEY = 'job';

function _jobHasRentalLedger(job) {
  const l = job && job.rentalLedger;
  return !!l && typeof l === 'object' && !Array.isArray(l);
}

function _isRentalAssetData(a) {
  return !!a && (a.quantityType === 'rental' || !!a.isRental);
}

// TASK-10015 — the rental-count math lives in ONE file:
// functions/src/shared/rentalLedgerMath.js, mirrored to the browser as
// window.RentalLedgerMath (app.js loads it before this view). The two pure
// helpers below used to be written out here; they are now thin delegates so
// the board, the hourly pickup settle and the owner's recount cannot drift.
function _rlm() {
  const m = (typeof window !== 'undefined' && window.RentalLedgerMath) || null;
  if (!m) throw new Error('RentalLedgerMath is not loaded (app.js imports shared/rental-ledger-math.js first)');
  return m;
}

// PURE. A ledger entry, normalised — garbage and negatives read as 0.
//   dayReturns  { [dayKey]: { qty, take } } — IMMEDIATE returns (no inspection)
//               this job made: `qty` units came off deployedQty on that day,
//               `take` of them out of this job's own `out`. dayKey is the
//               jobDayId, or RENTAL_LEDGER_JOB_KEY for a return made by
//               saveJob's create (job-level). Undone when that day leaves.
//   heldReturns same shape — immediate returns a CANCEL undid; restoreJobDay
//               re-applies them.
//   inspected   units handed to return inspections still open (ledgerQty).
//   pendingPickups / heldPickups (TASK-10015, only when non-empty) — pickups
//               booked for a day that has not come yet; no stock moved for them.
function _rentalLedgerEntry(ledger, assetId) {
  return _rlm().rlmEntry(ledger, assetId);
}

// PURE. One deploy or return by THIS job, applied to its entry (shared math).
//   deploy            → out += q; a fresh (non-reversal) deploy clears `parked`.
//   return            → the job hands over at most what it holds: `take`.
//   immediate return  → also recorded under `dayKey` in dayReturns.
// recQty = what stock ACTUALLY moved (a floored decrement moves less than q —
// recording more would put phantom units out on undo, COD R2 🔴).
function _rentalLedgerMovePlan(entry, direction, qty, isQty, isReversal, dayKey, recQty) {
  return _rlm().rlmMovePlan(entry, direction, qty, isQty, isReversal, dayKey, recQty);
}

// TASK-10015 — tenant-local calendar day ('YYYY-MM-DD') for an instant. The
// D1 rule ("a pickup moves stock on its day") compares these keys, and the
// server settle uses the same zone source (tenant.timezone → settings.timezone).
function _rentalTenantTz() {
  const t = (typeof window !== 'undefined' && window.AppState?.tenantData) || {};
  return (typeof t.timezone === 'string' && t.timezone) || (t.settings && typeof t.settings.timezone === 'string' && t.settings.timezone) || 'America/Chicago'; // same fallback as the server settle (COD r10)
}
function _rentalDateKey(ms) {
  return _rlm().rlmDateKey(ms, _rentalTenantTz());
}
function _rentalTodayKey() {
  return _rentalDateKey(Date.now());
}
// A jobDay's own calendar day (date is noon-anchored local, so any zone works).
function _rentalDayKeyOf(jd) {
  const ms = _jobDayDateMs(jd);
  return ms == null ? '' : _rentalDateKey(ms);
}

// TASK-10015 (TASK-4810 root cause #2) — a REVERSAL moves stock too, and used to
// leave no assetTransactions row, so a replay of the rows could never explain
// the counter. `delta` is the signed stock change (+ back out, − back home);
// quantity 0 so rental billing (deploy/return only) and the Projects inventory
// tab (Σ|quantity|) ignore it. Returns the row, or null when nothing moved.
function _rentalReversalRow(asset, delta, jobId, via, extra) {
  const d = Number(delta) || 0;
  if (!d) return null;
  return {
    assetId: asset.id, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '',
    type: 'reversal', direction: 'reversal', quantity: 0, delta: d, via: via || 'reversal',
    jobId: jobId || '', note: `Reversal (${via || 'undo'}): ${d > 0 ? '+' : ''}${d} out`,
    issuedBy: (typeof window !== 'undefined' && window.AppState?.user?.email) || 'unknown', createdAt: Timestamp.now(),
    ...(extra && typeof extra === 'object' ? extra : {})
  };
}

// What a _txGiveBackRental write really moved: the floored counter change in
// quantity mode, ±1 when an individual unit's status was written, else 0.
function _rentalMovedBy(w, aData, delta) {
  if (!w) return 0;
  if (w.deployedQty != null) return w.deployedQty - ((aData && aData.deployedQty) || 0);
  return w.deploymentStatus ? delta : 0;
}

// Job-doc flag the hourly settle queries for (`rentalPickupsPending == true`).
// Returned only when it CHANGES, so a ledger write that touches no planned
// pickup writes exactly the fields it wrote before.
// 2123 stale-tab fix (COD r3 Code) — every client rentalLedger write goes
// through here, and it also stamps `ledgerAt` (a fresh value each write).
// firestore.rules refuse a rentalLedger change without it, so a tab still
// running an older bundle (which moved due pickups at save, by date) cannot
// move rental stock once 2123 rules are live; it must reload first.
function _rentalPendingFlag(jobData, ledger) {
  const has = _rlm().rlmLedgerHasPending(ledger);
  const o = { ledgerAt: Timestamp.now() };
  if (!!(jobData && jobData.rentalPickupsPending) !== has) o.rentalPickupsPending = has;
  return o;
}

// PURE. A day leaving the job for ONE rental (see THE RULE above).
//   days       — the job's OTHER days as committed (read inside the
//                transaction); the leaving day itself is excluded by selfId.
//   pendingRis — the job's PENDING return inspections for this rental.
//   mode       — 'cancel' keeps what it undoes in heldReturns for a restore.
// Order: (1) undo the immediate returns this day made (and the job-level ones
// when it is the last holder) — those units go back OUT, `redeploy`; (2) cancel
// the inspections this day owns (+ orphans/job-level when last holder), their
// ledgerQty back to `out`; (3) if no other live day carries the rental, give
// back exactly `out`. Stock changes by redeploy − giveBack.
// An inspection is cancelled when the leaving day owns it, or when it belongs
// to no live day (created by saveJob's create, which is job-level, or owned by
// a day since deleted/cancelled) AND no other live day still carries the rental.
function _rentalLedgerLeavePlan(o) {
  // integ/2113 — the body moved VERBATIM to the shared math (rlmLeavePlan) so
  // deleteJobLedgered (TASK-10010) runs the same copy on the server.
  return _rlm().rlmLeavePlan(o);
}

// PURE. Restoring a cancelled day for ONE rental: re-deploy what was parked,
// re-apply the immediate returns the cancel undid (this day's, and job-level
// ones), then re-open the inspections that cancel closed (each takes its
// ledgerQty back out of `out`). Stock changes by redeploy − reapplied.
// stockAfterRedeploy (quantity mode): deployedQty once `redeploy` lands. A held
// return re-applies at most that much — the decrement is floored at 0, and
// recording more than actually moved would put phantom units out on the next
// cancel (COD R3 🔴). null = no cap (individually tracked).
function _rentalLedgerRestorePlan({ entry, reopenRis, isQty, selfId, skipParked, stockAfterRedeploy }) {
  const cap = (v) => (isQty ? v : Math.min(v, 1));
  const dayReturns = { ...(entry.dayReturns || {}) };
  const heldReturns = { ...(entry.heldReturns || {}) };
  const redeploy = skipParked ? 0 : entry.parked;
  let out = cap(entry.out + redeploy);
  let reapplied = 0;
  for (const k of [selfId, RENTAL_LEDGER_JOB_KEY]) {
    const r = k && heldReturns[k];
    if (!r) continue;
    const moved = (stockAfterRedeploy == null) ? r.qty
      : Math.max(0, Math.min(r.qty, stockAfterRedeploy - reapplied));
    const take = Math.min(out, r.take, moved);
    out -= take;
    reapplied += moved;
    if (moved > 0) {
      const prev = dayReturns[k] || { qty: 0, take: 0 };
      dayReturns[k] = { qty: cap(prev.qty + moved), take: cap(prev.take + take) };
    }
    delete heldReturns[k];
  }
  const reopen = [];
  let inspected = Math.max(0, Number(entry.inspected) || 0);
  for (const ri of (Array.isArray(reopenRis) ? reopenRis : [])) {
    const lq = Number(ri.ledgerQty);
    const want = Number.isFinite(lq) && lq > 0 ? lq : 0;
    const take = Math.min(out, want);
    out -= take;
    inspected += take;
    reopen.push({ id: ri.id, ledgerQty: take });
  }
  // TASK-10015 — pickups a cancel set aside go back on the books (no stock
  // moves now; the hourly settle moves them on their day, or at once if it
  // has already come).
  const pendingPickups = { ...(entry.pendingPickups || {}) };
  const heldPickups = { ...(entry.heldPickups || {}) };
  for (const k of [selfId, RENTAL_LEDGER_JOB_KEY]) {
    const r = k && heldPickups[k];
    if (!r) continue;
    const prevQ = pendingPickups[k] ? Number(pendingPickups[k].qty) || 0 : 0;
    pendingPickups[k] = { qty: cap(prevQ + (Number(r.qty) || 0)) };
    delete heldPickups[k];
  }
  return {
    redeploy, reapplied: cap(reapplied), reopen,
    entry: _rlm().rlmTidy({ out, parked: skipParked ? entry.parked : 0, inspected, dayReturns, heldReturns, pendingPickups, heldPickups })
  };
}

// PURE. PRE-LEDGER jobs only (cancelJobDay's fallback): should cancelling
// `selfId` cancel this pending return inspection of `assetId`?
//   stamped with this day            → yes
//   stamped with another LIVE day    → no, that day still owns it
//   unstamped / owner gone/cancelled → only if no other live day of the job
//                                      still RETURNS this asset (the same test
//                                      removeJobDay uses, minus its counting of
//                                      cancelled days as still returning)
// `days` = the job's full schedule; [] when it was not needed/loaded, which
// reads as "no other live day" — the pre-TASK-9336 behaviour.
function _legacyRiBelongsToLeavingDay(ri, selfId, days, assetId) {
  const owner = (ri && ri.jobDayId) || null;
  if (owner === selfId) return true;
  const list = Array.isArray(days) ? days : [];
  const live = (d) => !!d && d.id !== selfId && d.status !== 'cancelled';
  if (owner && list.some(d => live(d) && d.id === owner)) return false;
  return !list.some(d => live(d) && Array.isArray(d.assets) && d.assets.includes(assetId)
    && ((d.assetDirections && d.assetDirections[assetId]) || 'deploy') === 'return');
}

// Apply a net stock change for one rental inside a transaction. `aData` is the
// asset as read in the SAME transaction. delta > 0 = units go back OUT on this
// job, delta < 0 = units come back to the yard. Quantity mode is floored at 0.
// Returns { deployedQty, deploymentStatus } of what was written (nulls if not).
function _txGiveBackRental(tx, tenantId, assetId, aData, delta, jobId) {
  const res = { deployedQty: null, deploymentStatus: null };
  if (!aData || !delta) return res;
  const aRef = doc(db, 'tenants', tenantId, 'assets', assetId);
  // integ/2113 — the floor / other-job rule is the shared rlmStockChange (the
  // server's job delete + restore use the same one); this only writes it.
  const ch = _rlm().rlmStockChange(aData, delta, jobId);
  if (aData.trackingMode === 'quantity') {
    const current = aData.deployedQty || 0;
    if (current + delta < 0) console.warn(`[Rental] deployedQty floor hit on asset ${assetId}: ${current} + ${delta} < 0 -> clamped to 0.`);
  }
  if (!ch.write) return res;
  tx.update(aRef, { ...ch.write, updatedAt: Timestamp.now() });
  if (ch.write.deployedQty != null) res.deployedQty = ch.write.deployedQty;
  else res.deploymentStatus = ch.write.deploymentStatus;
  return res;
}

// TASK-9336 — cancelling, removing or restoring a day that carries rentals
// moves equipment stock, and assets are writable only by a dispatcher-or-higher
// role or a Roster-module holder (firestore.rules assets.update). A member who
// has only the Dispatch module is refused by the rules as permission-denied;
// the ledger path runs in one transaction, so nothing at all was written. Say
// that, and who can do it, instead of a bare "Error cancelling job day".
// Returns null for any other error (the caller keeps its generic message).
function _rentalWriteRefusedMsg(err, verbPhrase) {
  const code = String((err && err.code) || '');
  const msg = String((err && err.message) || '');
  if (!/permission-denied/.test(code) && !/insufficient permissions/i.test(msg)) return null;
  return `Couldn't ${verbPhrase}: it moves rental equipment in or out of stock, and your login can't change equipment counts. Nothing was changed. Ask an admin, a dispatcher, or someone with Roster access.`;
}

// TASK-9396 — PURE. Is this INDIVIDUALLY tracked rental unit out on a job other
// than `jobId`? Returns that job's id, else null. Quantity lots never answer
// (Chris 2026-09-29: a lot is counted, never blocked); an absent or available
// unit is free; `jobId` null (a job not yet created) means any holder counts.
// Case-insensitive on the status like every reader of it (roster.js, the map).
function _rentalUnitHeldElsewhere(aData, jobId) {
  if (!aData || aData.trackingMode === 'quantity') return null;
  if (String(aData.deploymentStatus || '').toLowerCase() !== 'deployed') return null;
  const holder = aData.deployedToJobId ? String(aData.deployedToJobId) : '';
  if (!holder || (jobId && holder === String(jobId))) return null;
  return holder;
}

// TASK-9396 — how the holding job is named in a refusal: its job number, else
// its title, else "another job" (the job may be outside the loaded window).
function _rentalUnitHeldLabel(heldByJobId) {
  const job = heldByJobId ? _resolveJobAnywhere(heldByJobId) : null;
  return (job && (formatJobNumber(job) || String(job.title || '').trim())) || 'another job';
}
function _rentalUnitRefusedMsg(name, heldByJobId) {
  return `${name} — already out on ${_rentalUnitHeldLabel(heldByJobId)}`;
}
// TASK-10072 — the refusal above becomes a CHOICE: "Move from #A" records A's
// return and this job's deploy together, in one server transaction (callable
// moveRentalUnitToJob — it re-checks that A still has the unit, that this job
// and its day are live, and the caller's access; no override). "Leave it" keeps
// the 9396 outcome. Runs after the save landed, one unit at a time.
//   refused  [{ assetId, name, heldByJobId }]
//   target   { jobId, jobDayId } (Assign Crew: that day) | { jobId, allDays: true } (Add / Edit Job)
async function _offerRentalUnitMoves(refused, target) {
  // A unit that needs a return inspection is never moved site to site (the
  // server refuses it too) — the 9396 toast already says to return it there.
  const list = (refused || []).filter((r) => r && r.assetId && r.heldByJobId && target && target.jobId && r.heldByJobId !== target.jobId
    && !(DispatchState.assets || []).some((x) => x.id === r.assetId && x.requireReturnInspection));
  const tid = DispatchState.tenantId; // COD r10 — the company these offers belong to
  for (const r of list) {
    if (!_dispatchDataIsFor(tid)) return; // switched company: drop the rest
    const from = _rentalUnitHeldLabel(r.heldByJobId);
    const fromBtn = from === 'another job' ? 'the other job' : from;
    const ok = await window.showConfirm?.(
      'Rental unit is on another job',
      `${r.name} is out on ${from}. Move it here from ${target.allDays ? "this job's first day" : 'this day'}? It is returned on ${fromBtn} that day, comes off ${fromBtn}'s days from then on, and is billed here from then.`,
      { confirmText: `Move from ${fromBtn}`, cancelText: 'Leave it', icon: 'swap_horiz' });
    if (!ok) continue;
    if (!_dispatchDataIsFor(tid)) return;
    const moveId = `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    const payload = { tenantId: tid, assetId: r.assetId, fromJobId: r.heldByJobId, toJobId: target.jobId, moveId, via: 'dispatch-move' };
    if (target.allDays) payload.toAllDays = true; else payload.toJobDayId = target.jobDayId;
    try {
      const res = (await httpsCallable(functions, 'moveRentalUnitToJob')(payload)).data || {};
      const a = (DispatchState.assets || []).find((x) => x.id === r.assetId);
      // COD r8 — mirror only what the server committed, and only while the local
      // copy still shows the unit on A (never overwrite a newer holder).
      if (a && !res.already && res.asset && a.deployedToJobId === r.heldByJobId && _dispatchDataIsFor(payload.tenantId)) {
        a.deploymentStatus = res.asset.deploymentStatus; a.deployedToJobId = res.asset.deployedToJobId;
      }
      window.showToast?.(res.already ? `${r.name} is already on this job.` : `${r.name} moved from ${fromBtn} to this job${res.moveDayKey ? ` from ${res.moveDayKey}` : ''}.`, 'success', null, 6000);
    } catch (err) {
      console.warn('[Rental] TASK-10072 move refused', { assetId: r.assetId, code: err && err.code, message: err && err.message });
      window.showToast?.(`${r.name} was not moved: ${(err && err.message) || 'try again'}`, 'error', null, 10000);
    }
  }
}
// TASK-9396 (COD UX Lead 2026-10-11) — say it in the picker, before the save:
// a single-tracked rental that is out on a job gets an "Out on #1042" line under
// its name in both asset pickers. Advisory, never disabled — the row must stay
// tickable so the unit can be RETURNED from the job that has it.
function _rentalHeldTagHtml(item, containerId) {
  if (containerId !== 'job-select-assets' && containerId !== 'select-assets') return '';
  if (!_isRentalAssetData(item) || item.trackingMode === 'quantity') return '';
  const holder = _rentalUnitHeldElsewhere(item, null);
  if (!holder) return '';
  return `<span class="asset-held-tag" style="display:block;font-size:0.72rem;font-weight:400;line-height:1.2;color:#B45309;">${escapeHtml(`Out on ${_rentalUnitHeldLabel(holder)}`)}</span>`;
}

// TASK-9396 — decide a single-tracked unit's deployability BEFORE the job or any
// jobDay is written (the TASK-9363 r3 rule: a refused rental never enters the
// day payload, so no day can list — and bill — a unit this job never got). One
// read per unit going out as a deploy. Returns [{ assetId, name, heldByJobId }]
// for the refused ones; a failed read throws, and saveJob has written nothing.
async function _precheckRentalUnits(tenantId, selectedAssets, dirOf) {
  const refused = [];
  for (const id of (selectedAssets || [])) {
    const asset = (DispatchState.assets || []).find(a => a.id === id);
    if (!asset || !_isRentalAssetData(asset) || asset.trackingMode === 'quantity') continue;
    if (dirOf(id) !== 'deploy') continue;
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'assets', id));
    const heldBy = _rentalUnitHeldElsewhere(snap.exists() ? snap.data() : null, null);
    if (heldBy) refused.push({ assetId: id, name: asset.name || asset.partNumber || id, heldByJobId: heldBy });
  }
  return refused;
}

// Mirror committed asset values into DispatchState so the pills repaint right.
function _mirrorRentalAssets(changes) {
  for (const c of (changes || [])) {
    const a = (DispatchState.assets || []).find(x => x.id === c.assetId);
    if (!a) continue;
    if (c.deployedQty != null) a.deployedQty = c.deployedQty;
    if (c.deploymentStatus) a.deploymentStatus = c.deploymentStatus;
  }
}

// TASK-9336 — one deploy/return by a LEDGER job, atomically with its stock
// change. Returns null when the job has no ledger (the caller then runs the
// pre-ledger path unchanged). `jobDayId` stamps the return inspection with the
// day that owns it (null = job-level, from saveJob's create).
// opts.carryGuardSourceJobId (TASK-9363 r2) — this deploy is a Copy's auto-
// carried quantity rental: inside the SAME transaction, read the source job and
// the asset as they are NOW and refuse (no writes, returns { refused }) when the
// source's ledger shows the rental out or the yard cannot cover the quantity.
// A concurrent deploy by another dispatcher changes one of the docs read here,
// so Firestore retries this transaction against the new state.
async function _rentalMoveLedgerTx(tenantId, asset, direction, qty, jobId, isReversal, jobDayId, opts) {
  if (!jobId || !asset?.id) return null;
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  const assetRef = doc(db, 'tenants', tenantId, 'assets', asset.id);
  const isQty = asset.trackingMode === 'quantity';
  const wantsInspection = direction !== 'deploy' && !isReversal && !!asset.requireReturnInspection;
  const riRef = wantsInspection ? doc(collection(db, 'tenants', tenantId, 'returnInspections')) : null;
  return await runTransaction(db, async (tx) => {
    const jobSnap = await tx.get(jobRef);
    if (!jobSnap.exists() || !_jobHasRentalLedger(jobSnap.data())) return null;
    const guardSrc = (direction === 'deploy' && isQty && !isReversal && opts && opts.carryGuardSourceJobId)
      ? String(opts.carryGuardSourceJobId) : null;
    if (guardSrc) {
      // Reads before any write (transaction rule) — nothing is written above.
      // r3: a 'yard' guard (hand re-add) never reads the source.
      const guardMode = opts.carryGuardMode === 'yard' ? 'yard' : 'carried';
      const srcSnap = guardMode === 'carried' ? await tx.get(doc(db, 'tenants', tenantId, 'jobs', guardSrc)) : null;
      const aSnap = await tx.get(assetRef);
      const refused = _carriedRentalDeployVerdict(srcSnap && srcSnap.exists() ? srcSnap.data() : null,
        aSnap.exists() ? aSnap.data() : null, asset.id, Math.max(0, Number(qty) || 0), guardMode);
      if (refused) return { refused, deployedQty: null, deploymentStatus: null, riId: null };
    }
    const ledger = { ...jobSnap.data().rentalLedger };
    // An immediate return (no inspection) is recorded against the day that
    // made it (job-level for saveJob's create), so that day leaving undoes it.
    const dayKey = (direction !== 'deploy' && !wantsInspection && !isReversal)
      ? (jobDayId || RENTAL_LEDGER_JOB_KEY) : null;
    // TASK-10015 (TASK-10006 D1) — a booked pickup moves no stock at save: it
    // is held as "pickup planned" on the ledger.
    // TASK-10006 S3 (Chris 2026-10-22) — and it moves only when the pickup
    // REALLY happens (first crew clock-in / a ticket on that day / the office's
    // "Mark picked up"), in ONE server step (functions settleJobPickups) that
    // also takes the units off the jobs they came from and closes their rental
    // billing — never on its date, never here. A pickup added to a day the
    // server already marked picked up is planned too, and the server is asked
    // to settle it now (_kickPickupSettle after the transaction).
    let _dayPickedUp = false;
    if (dayKey && jobDayId && opts && opts.pickupDateKey) {
      const _ds = await tx.get(doc(db, 'tenants', tenantId, 'jobDays', jobDayId));
      _dayPickedUp = _ds.exists() && _rlm().rlmDayPickedUp(_ds.data());
    }
    if (dayKey && opts && opts.pickupDateKey) {
      const dp = _rlm().rlmDeferPickupPlan(_rentalLedgerEntry(ledger, asset.id), dayKey, qty, isQty);
      ledger[asset.id] = dp.entry;
      tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger) });
      return { deployedQty: null, deploymentStatus: null, riId: null, deferred: { qty: dp.q, dateKey: opts.pickupDateKey, stampedDay: _dayPickedUp } };
    }
    // Reads first (transaction rule): the asset, when a floored decrement needs
    // it, or (TASK-9396) when a single-tracked unit is going out — the unit's
    // CURRENT holder decides whether this deploy may happen at all.
    const unitDeploy = direction === 'deploy' && !isQty;
    // TASK-9396 (COD r1 Code Reviewer) — a REVERSAL return of a unit (undoing
    // this job's own deploy) reads the holder too: another job may have it now.
    const unitUndo = direction !== 'deploy' && isReversal && !isQty;
    const needsAssetRead = (direction !== 'deploy' && !wantsInspection && isQty) || unitDeploy || unitUndo;
    const aData = needsAssetRead ? ((await tx.get(assetRef)).data() || null) : null;
    // TASK-9396 — Chris 2026-09-29: ONE physical unit can never count "out" on
    // two jobs. Read here, inside the transaction, so a concurrent deploy by
    // another dispatcher is seen (Firestore re-runs us against the new state),
    // and firestore.rules (rentalUnitHoldOk) refuses the write if a stale bundle
    // ever skips this. A fresh deploy is REFUSED with nothing written (the caller
    // names the holding job); a REVERSAL (undoing this job's own return) keeps
    // its ledger entry but leaves the asset on the job that has it, the same way
    // _txGiveBackRental does.
    const heldBy = unitDeploy ? _rentalUnitHeldElsewhere(aData, jobId) : null;
    if (heldBy && !isReversal) return { refused: 'unit-out', heldByJobId: heldBy, deployedQty: null, deploymentStatus: null, riId: null };
    const recQty = (aData && direction !== 'deploy') ? Math.min(Math.max(0, Number(qty) || 0), aData.deployedQty || 0) : null;
    const plan = _rentalLedgerMovePlan(_rentalLedgerEntry(ledger, asset.id), direction, qty, isQty, isReversal, dayKey, recQty);
    const out = { deployedQty: null, deploymentStatus: null, riId: null };
    if (direction === 'deploy') {
      // TASK-9396 — `lastDeployJobId` names the job for the server's over-booking
      // log (_logRentalOverbook reads the asset doc alone). Informational.
      if (isQty) tx.update(assetRef, { deployedQty: increment(plan.q), lastDeployJobId: jobId, updatedAt: Timestamp.now() });
      else if (heldBy) console.warn(`[Rental] TASK-9396 reversal left unit ${asset.id} on job ${heldBy} (not re-deployed to ${jobId})`);
      else { tx.update(assetRef, { deploymentStatus: 'deployed', deployedToJobId: jobId, updatedAt: Timestamp.now() }); out.deploymentStatus = 'deployed'; }
    } else if (wantsInspection) {
      // Stock does not move until the inspection closes (maintenance/pretrip).
      // `ledgerQty` = the share of THIS job's deploy the inspection now holds;
      // cancelling the inspection puts exactly that back in `out`.
      tx.set(riRef, {
        assetId: asset.id, assetName: asset.name || asset.partNumber || '',
        jobId, jobDayId: jobDayId || null, expectedQty: isQty ? qty : 1,
        ledgerQty: plan.take, status: 'pending', createdAt: Timestamp.now()
      });
      plan.entry = { ...plan.entry, inspected: (plan.entry.inspected || 0) + plan.take };
      out.riId = riRef.id;
    } else {
      // Immediate return. A plain return is a pickup and moves q (it can be
      // picking up units another job deployed — the pre-ledger behaviour); a
      // REVERSAL undoes this job's own deploy, so it moves only what is held.
      const stockQty = isReversal ? plan.take : plan.q;
      if (isQty) {
        if (stockQty > 0) out.deployedQty = _txGiveBackRental(tx, tenantId, asset.id, aData, -stockQty, jobId).deployedQty;
      } else if (stockQty > 0 && unitUndo) {
        // TASK-9396 — the holder-aware release (rlmStockChange): undoing THIS
        // job's deploy never frees a unit another job now holds.
        out.deploymentStatus = _txGiveBackRental(tx, tenantId, asset.id, aData, -1, jobId).deploymentStatus;
      } else if (stockQty > 0) {
        // A plain return is a pickup and may release a unit another job
        // deployed (TASK-10006 return jobs) — unchanged.
        tx.update(assetRef, { deploymentStatus: 'available', deployedToJobId: null, updatedAt: Timestamp.now() });
        out.deploymentStatus = 'available';
      }
      // TASK-10015 — a reversal logs what it moved, in the same transaction
      // (floored: the row carries what the counter really dropped by).
      if (isReversal && stockQty > 0 && (isQty || out.deploymentStatus)) {
        const moved = isQty && out.deployedQty != null && aData ? Math.max(0, (aData.deployedQty || 0) - out.deployedQty) : stockQty;
        const row = _rentalReversalRow(asset, -moved, jobId, (opts && opts.reversalVia) || 'undo-return', jobDayId ? { jobDayId } : null);
        if (row) { tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row); out.loggedReversal = true; }
      }
    }
    // TASK-9396 — a unit another job holds was not moved back out: no row.
    if (direction === 'deploy' && isReversal && !heldBy) {
      const row = _rentalReversalRow(asset, plan.q, jobId, (opts && opts.reversalVia) || 'undo-deploy', jobDayId ? { jobDayId } : null);
      if (row) { tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row); out.loggedReversal = true; }
    }
    ledger[asset.id] = plan.entry;
    tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger) });
    return out;
  });
}

async function processRentalTransaction(asset, direction, qty, jobId, isReversal = false, opts = {}) {
  const isQty = asset.trackingMode === 'quantity';
  const tenantId = DispatchState.tenantId;
  const jobDayId = opts.jobDayId || null;

  // TASK-9336 — a ledger job moves stock and its ledger in one transaction.
  // TASK-10015 — opts.pickupDateKey: the pickup's own day ('YYYY-MM-DD'); a
  // later day holds it as planned (no stock moves until that day).
  const led = await _rentalMoveLedgerTx(tenantId, asset, direction, qty, jobId, isReversal, jobDayId,
    { pickupDateKey: opts.pickupDateKey || null, reversalVia: opts.reversalVia || null });
  if (led && led.deferred) {
    if (led.deferred.stampedDay) _kickPickupSettle(jobId);
    await addDoc(collection(db, 'tenants', tenantId, 'assetTransactions'), {
      assetId: asset.id, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '',
      type: 'pickup-planned', direction: 'pickup-planned', quantity: 0, plannedQty: led.deferred.qty,
      pickupDate: led.deferred.dateKey, jobId, ...(jobDayId ? { jobDayId } : {}),
      note: `Pickup of ${led.deferred.qty} planned for ${led.deferred.dateKey} — stays out until then`,
      issuedBy: window.AppState?.user?.email || 'unknown', createdAt: Timestamp.now()
    });
    return;
  }
  // TASK-9396 — a single-tracked unit another job holds: nothing was written;
  // the caller takes it off the day and names the holding job.
  if (led && led.refused) return { refused: led.refused, heldByJobId: led.heldByJobId || null };
  // TASK-4810 #2 — a pre-ledger REVERSAL writes its signed `reversal` row inside
  // the SAME transaction as the stock change it describes (it used to be a
  // separate addDoc afterwards: a failed or interrupted add left the counter
  // moved with no row, the drift this row exists to explain). The row is
  // created inside the transaction callback, so a Firestore retry discards the
  // earlier attempt's write and only ONE row commits. `delta` is what really
  // moved (a floored return logs the floored amount); 0 writes nothing.
  const _revTx = (tx, delta) => {
    if (!isReversal) return;
    const row = _rentalReversalRow(asset, delta, jobId,
      opts.reversalVia || (direction === 'deploy' ? 'undo-return' : 'undo-deploy'), jobDayId ? { jobDayId } : null);
    if (row) tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row);
  };
  if (led) {
    if (direction === 'deploy' && isQty) asset.deployedQty = (asset.deployedQty || 0) + qty;
    if (led.deployedQty != null) asset.deployedQty = led.deployedQty;
    if (led.deploymentStatus) asset.deploymentStatus = led.deploymentStatus;
  } else if (direction === 'deploy') {
    if (isQty) {
      const assetFields = { deployedQty: increment(qty), lastDeployJobId: jobId, updatedAt: Timestamp.now() };
      if (isReversal) {
        await runTransaction(db, async (tx) => {
          tx.update(doc(db, 'tenants', tenantId, 'assets', asset.id), assetFields);
          _revTx(tx, qty);
        });
      } else {
        await updateDoc(doc(db, 'tenants', tenantId, 'assets', asset.id), assetFields);
      }
      asset.deployedQty = (asset.deployedQty || 0) + qty;
    } else {
      // TASK-9396 — pre-ledger job, single-tracked unit: same hold check as the
      // ledger lane, in a transaction so the holder read and the write agree.
      const unitRef = doc(db, 'tenants', tenantId, 'assets', asset.id);
      const heldBy = await runTransaction(db, async (tx) => {
        const s = await tx.get(unitRef);
        const h = _rentalUnitHeldElsewhere(s.exists() ? s.data() : null, jobId);
        if (h) return h;
        tx.update(unitRef, { deploymentStatus: 'deployed', deployedToJobId: jobId, updatedAt: Timestamp.now() });
        _revTx(tx, 1);
        return null;
      });
      if (heldBy) {
        if (isReversal) { console.warn(`[Rental] TASK-9396 reversal left unit ${asset.id} on job ${heldBy} (not re-deployed to ${jobId})`); return null; }
        return { refused: 'unit-out', heldByJobId: heldBy };
      }
      asset.deploymentStatus = 'deployed';
    }
  } else {
    // Return — if inspection required, DON'T update inventory (inspection form handles it)
    if (asset.requireReturnInspection && !isReversal) {
      await addDoc(collection(db, 'tenants', tenantId, 'returnInspections'), {
        assetId: asset.id, assetName: asset.name || asset.partNumber || '',
        jobId, expectedQty: isQty ? qty : 1,
        // TASK-9336 — the day that owns this inspection, so cancelling a
        // DIFFERENT day of the job can leave it alone. Omitted when unknown.
        ...(jobDayId ? { jobDayId } : {}),
        status: 'pending',
        createdAt: Timestamp.now()
      });
    } else if (!asset.requireReturnInspection || isReversal) {
      // No inspection required OR reversing — update inventory immediately
      if (isQty) {
        // TASK-1255 — transactional, PERSISTED zero-floor (was fire-and-forget increment(-qty)).
        asset.deployedQty = await decrementDeployedQtyFloored(tenantId, asset.id, qty, {},
          isReversal ? (tx, moved) => _revTx(tx, -moved) : null);
      } else if (isReversal) {
        // TASK-9396 (COD r1 Code Reviewer) — undoing this job's own deploy
        // releases the unit only if no OTHER job holds it now (same rule as
        // rlmStockChange); otherwise nothing moves and no reversal row is written.
        const unitRef = doc(db, 'tenants', tenantId, 'assets', asset.id);
        const heldBy = await runTransaction(db, async (tx) => {
          const s = await tx.get(unitRef);
          const h = _rentalUnitHeldElsewhere(s.exists() ? s.data() : null, jobId);
          if (h) return h;
          tx.update(unitRef, { deploymentStatus: 'available', deployedToJobId: null, updatedAt: Timestamp.now() });
          _revTx(tx, -1);
          return null;
        });
        if (heldBy) { console.warn(`[Rental] TASK-9396 reversal left unit ${asset.id} on job ${heldBy} (not released by ${jobId})`); return null; }
        asset.deploymentStatus = 'available';
      } else {
        await updateDoc(doc(db, 'tenants', tenantId, 'assets', asset.id), {
          deploymentStatus: 'available', deployedToJobId: null, updatedAt: Timestamp.now()
        });
        asset.deploymentStatus = 'available';
      }
    }
  }

  // TASK-10015 / TASK-4810 #2 — every reversal's `reversal` row was written in
  // the transaction that moved the stock (ledger jobs: _rentalMoveLedgerTx;
  // pre-ledger jobs: _revTx above), never as a deploy or return row.
  if (!isReversal) {
    await addDoc(collection(db, 'tenants', tenantId, 'assetTransactions'), {
      assetId: asset.id, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '',
      type: direction, direction, quantity: isQty ? qty : 1,
      jobId, fromYardId: asset.yardId || '', fromYardName: asset.yardName || '',
      note: `${direction === 'deploy' ? 'Deployed' : 'Return scheduled'}${asset.requireReturnInspection && direction === 'return' ? ' (pending inspection)' : ''} via assignment`,
      issuedBy: window.AppState?.user?.email || 'unknown',
      createdAt: Timestamp.now(),
      // TASK-4810 — `overDeploy` when this deploy was confirmed past owned stock.
      ...((opts && opts.txStamp && typeof opts.txStamp === 'object') ? opts.txStamp : {})
    });
  }
}

// TASK-9336 — saveAssignment's rental half for a LEDGER job.
//   LEAVING  (a rental removed from the day, or flipped return→deploy): the
//            day leaves that rental via _rentalLedgerLeaveTx — it undoes its own
//            immediate return / closes its own inspection, and gives back only
//            what the ledger holds, only when no other live day carries it. That
//            is the copied-day fix: removing a rental from a day that inherited
//            it via add-day no longer returns a deploy the day never made.
//   ARRIVING (added, or the new direction of a flip): processRentalTransaction,
//            which commits the stock move with its ledger entry.
// A deploy→return flip is NOT a leave: the new return takes the job's own
// units out of the ledger (independent review ⚠️ 2026-09-23 — giving them back
// first and then picking the same units up again moved them twice).
// Runs before saveAssignment's own day write; a retry after that write fails
// re-runs the arriving moves (pre-existing, same as the pre-ledger loops).
//
// TASK-10015 (TASK-4810 root cause #3) — a QUANTITY edit on a rental the day
// already carries (same direction) used to be written to the day while moving
// no stock, from Assign Crew AND Edit Job. Now (when the caller passes
// `previousQtys`):
//   deploy → the job's holding (shared rlmHoldingDelta: the LARGEST deploy qty
//            on any live day — days are copies) moves by the difference: more
//            is a deploy, less is a reversal;
//   return → the job's pickup holding (largest return qty on its live days)
//            moves by the difference: more books the extra, less takes it off
//            the job's pickup records (rlmReducePickupPlan). All in ONE transaction that reads the job's days and writes the day (_rentalLedgerQtyEditTx).
// Added / removed / flipped rentals settle exactly as before. `pickupDateKey`
// = this day's date: a return booked for a later day is held as planned (D1).
async function _asgSettleRentalsLedger({ tenantId, jobId, jobDayId, previousAssets, previousDirections,
  selectedAssets, pendingDirections, pendingQtys, isRental, overDeploys, previousQtys, pickupDateKey }) {
  const dirOf = (m, id) => (m && m[id]) || 'deploy';
  const prev = Array.isArray(previousAssets) ? previousAssets : [];
  const sel = Array.isArray(selectedAssets) ? selectedAssets : [];
  const removed = prev.filter(id => !sel.includes(id) && isRental(id));
  const added = sel.filter(id => !prev.includes(id) && isRental(id));
  const flipped = sel.filter(id => prev.includes(id) && isRental(id)
    && dirOf(previousDirections, id) !== dirOf(pendingDirections, id));
  const qtyOf = (m, id) => Math.max(1, Number(m && m[id]) || 1);
  const qtyEdited = (previousQtys && typeof previousQtys === 'object')
    ? sel.filter(id => prev.includes(id) && isRental(id) && !flipped.includes(id)
      && qtyOf(previousQtys, id) !== qtyOf(pendingQtys, id))
    : [];
  // The job's days as committed (this day still at its OLD values — the
  // caller writes the day after this settles). Read only when a holding is
  // needed; same known limit as _rentalLedgerLeaveTx's pre-query.
  let _days = null;
  const jobDaysNow = async () => {
    if (_days) return _days;
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'jobDays'), where('jobId', '==', jobId)));
    _days = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    return _days;
  };
  const assetOf = (id) => DispatchState.assets.find(a => a.id === id);
  const isQtyAsset = (id) => assetOf(id)?.trackingMode === 'quantity';
  // return→deploy flips: the day undoes its own return (units back in `out`)
  // but KEEPS carrying the rental — no give-back — and then deploys only what
  // the undo did not already put back (COD R2 🔴: undo + a full deploy moved
  // the same units twice, 4 → 8).
  const toDeploy = flipped.filter(id => dirOf(previousDirections, id) !== 'deploy');
  const leaving = [...removed, ...toDeploy];
  let restoredBy = {};
  let blockedBy = {};
  if (leaving.length > 0) {
    const res = await _rentalLedgerLeaveTx(tenantId, jobId, jobDayId, 'unassign',
      { assetIds: leaving, removeFromDay: removed, stillHeld: toDeploy });
    if (res.outcome === 'gone') throw new Error('That day no longer exists');
    _mirrorRentalAssets(res.changes);
    restoredBy = res.restored || {};
    blockedBy = res.blocked || {};
  }
  const arriving = [...added, ...flipped];
  // TASK-9396 — single-tracked units another job holds: not deployed, nothing
  // written for them; saveAssignment takes them off the day and says which job.
  // A return→deploy flip the leave transaction refused to undo is refused here
  // too (flipped:true → the day keeps its 'return'), with no deploy attempted.
  const refused = [];
  for (const id of arriving) {
    const asset = DispatchState.assets.find(a => a.id === id);
    if (!asset) continue;
    if (blockedBy[id]) { refused.push({ assetId: id, name: asset.name || asset.partNumber || id, heldByJobId: blockedBy[id], flipped: true }); continue; }
    let q = (pendingQtys && pendingQtys[id]) || 1;
    if (toDeploy.includes(id)) q = Math.max(0, q - (restoredBy[id] || 0));
    // (An ADDED rental still deploys its full qty, as before TASK-10015 — a
    // day that newly carries it may be a separate delivery. COD Domain: under-
    // counting mats that are on location is the worse error.)
    if (q <= 0) continue;
    const r = await processRentalTransaction(asset, dirOf(pendingDirections, id), q, jobId, false, {
      jobDayId, pickupDateKey: pickupDateKey || null,
      txStamp: dirOf(pendingDirections, id) === 'deploy' ? rentalOverDeployStamp(overDeploys, id) : {} });
    if (r && r.refused) refused.push({ assetId: id, name: asset.name || asset.partNumber || id, heldByJobId: r.heldByJobId, flipped: flipped.includes(id) });
  }
  const qtyMoved = [];
  for (const id of qtyEdited) {
    const asset = assetOf(id);
    if (!asset || !isQtyAsset(id)) continue;   // an individual unit is always 1
    const dir = dirOf(pendingDirections, id) === 'return' ? 'return' : 'deploy';
    // Inspection items move stock only when the inspection closes — a pickup
    // quantity edit on one stays a warning (TASK-10006 S1 owns that flow).
    // Never silent (TASK-4810 rule): the old warning is off for ledger jobs.
    if (dir === 'return' && asset.requireReturnInspection) {
      qtyMoved.push({ assetId: id, skipped: 'inspection' });
      window.showToast?.(`Rental out-count not updated for the pickup quantity change on ${asset.name || asset.partNumber || 'this item'} — it moves when its return inspection is closed. If the count is wrong, recount in Roster › Assets.`, 'warning', null, 12000);
      continue;
    }
    // ONE transaction decides the move from what it reads and writes the day
    // (_rentalLedgerQtyEditTx) — never a delta computed before it (COD r2-r4).
    const days = await jobDaysNow();
    const r = await _rentalLedgerQtyEditTx(tenantId, jobId, jobDayId, asset, dir, qtyOf(previousQtys, id), qtyOf(pendingQtys, id),
      days.map(d => d.id), { pickupDateKey: pickupDateKey || null, txStamp: dir === 'deploy' ? rentalOverDeployStamp(overDeploys, id) : {} });
    _mirrorRentalAssets([{ assetId: id, deployedQty: r.deployedQty, deploymentStatus: r.deploymentStatus }]);
    if (r.kickSettle) _kickPickupSettle(jobId); // TASK-10006 S3 — the server moves it
    qtyMoved.push({ assetId: id, dir, delta: r.delta, deferred: r.deferred });
  }
  return { leaving, arriving, qtyMoved, refused };
}

// TASK-10015 (COD r5 🔴) — the day write that FOLLOWS a settle must not
// rewrite a rental qty the settle's transaction already wrote (and owns): a
// stale editor resuming after someone else's later edit would put the old qty
// back over a ledger that holds the new one. When the settle moved any qty,
// the assetQtys map is written per asset (field paths), skipping the settled
// ones. With nothing settled the payload is returned unchanged. PURE.
function _rentalDayWriteWithoutSettledQtys(payload, settled) {
  const ids = ((settled && Array.isArray(settled.qtyMoved)) ? settled.qtyMoved : [])
    .filter(m => m && !m.skipped && m.assetId).map(m => m.assetId);
  if (!ids.length || !payload || !payload.assetQtys || typeof payload.assetQtys !== 'object') return payload;
  const out = { ...payload };
  delete out.assetQtys;
  for (const [k, v] of Object.entries(payload.assetQtys)) {
    if (ids.includes(k) || !/^[A-Za-z0-9_-]+$/.test(k)) continue;
    out[`assetQtys.${k}`] = v;
  }
  return out;
}

// TASK-10015 — _asgSettleRentalsLedger, with any failure tagged as the RENTAL
// half, so the caller can say "your login can't move equipment counts"
// (_rentalWriteRefusedMsg) instead of a generic crew-permission message.
async function _asgSettleRentalsLedgerTagged(args) {
  try { return await _asgSettleRentalsLedger(args); }
  catch (e) { if (e && typeof e === 'object') { try { e._rentalSettle = true; } catch (_) {} } throw e; }
}

// TASK-10015 (TASK-4810 root cause #3; COD r2/r3/r4 🔴) — a QUANTITY edit on a
// rental a LEDGER job's day already carries, settled in ONE transaction that
// also writes the day's new qty. Everything is decided from what the
// transaction READ, never from values read before it:
//   - the edited day must still hold the qty the edit started from (else
//     'day-changed', nothing written);
//   - the job's days (ids pre-queried, every one re-read here) give the
//     holding before/after (shared rlmHoldingDelta, direction `dir`);
//   - deploy: more = deploy the rise, less = give back (a reversal, floored);
//     return: more = book the extra pickup (held as planned when its day has
//     not come — D1), less = rlmReducePickupPlan (planned first, then
//     immediate — those units go back out).
// It writes the job doc + the day EVERY time — a zero-delta edit too — so
// concurrent edits of the same job serialize: the later one re-reads the
// earlier one's day and computes from it (two days 100/100 → 120 each move +20
// once; A 120→100 with B 100→110 at once ends at 110). Known limit (as
// _rentalLedgerLeaveTx): a day CREATED after the pre-query is not seen.
async function _rentalLedgerQtyEditTx(tenantId, jobId, jobDayId, asset, dir, beforeQty, newQty, dayIds, opts = {}) {
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  const assetRef = doc(db, 'tenants', tenantId, 'assets', asset.id);
  const dayRef = doc(db, 'tenants', tenantId, 'jobDays', jobDayId);
  const isQty = asset.trackingMode === 'quantity';
  const id = asset.id;
  const ids = Array.from(new Set([jobDayId, ...(Array.isArray(dayIds) ? dayIds : [])]));
  return await runTransaction(db, async (tx) => {
    // ── reads ──
    const jobSnap = await tx.get(jobRef);
    if (!jobSnap.exists() || !_jobHasRentalLedger(jobSnap.data())) {
      const e = new Error('This job has no rental ledger'); e.code = 'no-ledger'; throw e;
    }
    const days = [];
    for (const did of ids) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'jobDays', did));
      if (s.exists()) days.push({ id: did, ...s.data() });
    }
    const self = days.find(d => d.id === jobDayId);
    if (!self) throw new Error('That day no longer exists');
    const cur = Math.max(1, Number((self.assetQtys || {})[id]) || 1);
    if (cur !== Math.max(1, Number(beforeQty) || 1)) {
      const e = new Error("Someone else changed this day's equipment quantities while you were editing. Nothing was moved — reopen the day and try again.");
      e.code = 'day-changed'; throw e;
    }
    const aSnap = await tx.get(assetRef);
    let aData = aSnap.exists() ? aSnap.data() : null;
    // ── decide ──
    const next = {
      ...self, assets: Array.from(new Set([...(Array.isArray(self.assets) ? self.assets : []), id])),
      assetQtys: { ...(self.assetQtys || {}), [id]: newQty },
      assetDirections: { ...(self.assetDirections || {}), [id]: dir }
    };
    const delta = _rlm().rlmHoldingDelta(days, jobDayId, id, isQty, next, dir);
    const ledger = { ...jobSnap.data().rentalLedger };
    let entry = _rentalLedgerEntry(ledger, id);
    const rows = [];
    const res = { delta, deployedQty: null, deploymentStatus: null, deferred: null };
    const by = (typeof window !== 'undefined' && window.AppState?.user?.email) || 'unknown';
    const base = { assetId: id, assetName: asset.name || asset.partNumber || '', partNumber: asset.partNumber || '', jobId, jobDayId, issuedBy: by, createdAt: Timestamp.now() };
    const giveBack = (d, via) => {
      if (!aData || !d) return;
      const w = _txGiveBackRental(tx, tenantId, id, aData, d, jobId);
      if (w.deployedQty != null) res.deployedQty = w.deployedQty;
      if (w.deploymentStatus) res.deploymentStatus = w.deploymentStatus;
      const row = _rentalReversalRow({ ...aData, id }, _rentalMovedBy(w, aData, d), jobId, via, { jobDayId });
      if (row) rows.push(row);
    };
    if (dir === 'deploy') {
      if (delta > 0) {
        const p = _rentalLedgerMovePlan(entry, 'deploy', delta, isQty, false, null, null);
        entry = p.entry;
        if (isQty) { tx.update(assetRef, { deployedQty: increment(p.q), updatedAt: Timestamp.now() }); res.deployedQty = ((aData && aData.deployedQty) || 0) + p.q; }
        else { tx.update(assetRef, { deploymentStatus: 'deployed', deployedToJobId: jobId, updatedAt: Timestamp.now() }); res.deploymentStatus = 'deployed'; }
        rows.push({ ...base, type: 'deploy', direction: 'deploy', quantity: isQty ? p.q : 1,
          note: 'Deployed (quantity raised on the day)', ...((opts.txStamp && typeof opts.txStamp === 'object') ? opts.txStamp : {}) });
      } else if (delta < 0) {
        const p = _rentalLedgerMovePlan(entry, 'return', -delta, isQty, true, null, null);
        entry = p.entry;
        if (p.take > 0) giveBack(-p.take, 'qty-edit');
      }
    } else {
      // A PICKUP quantity edit (COD r7/r8 🔴): stock moves only for the part of
      // the pickup that has HAPPENED (R = largest due return qty — the same R
      // the recount uses); the planned part never moves stock and is re-laid
      // per later day with the recount's own allocation (rlmPlannedPickups).
      //   ΔR > 0 → the extra units came home now (immediate return, floored);
      //   ΔR < 0 → that many go back out (taken off the IMMEDIATE records only —
      //            the planned ones are re-laid right after).
      // TASK-10006 S3 — "happened" = the day's pickup really took place (the
      // server's `rentalPickup` stamp, or the legacy date rule for a pickup
      // that already moved stock — rlmAnnotatePickedUp), never its date.
      const todayKey = _rentalTodayKey();
      const entry0 = entry;
      const keyed = (arr) => _rlm().rlmAnnotatePickedUp(arr.map(d => ({ ...d, dateKey: _rentalDayKeyOf(d) })), entry0, todayKey);
      // COD r9 🔴 — "picked up" is not "settled". TASK-10006 S3: the ONE
      // server pickup step (settleJobPickups) is the only place a planned
      // pickup moves stock — and it also takes the units off the jobs they
      // came from and closes their billing. So when a planned pickup sits on a
      // day the server already marked picked up (it is about to settle it),
      // this edit refuses, nothing written, instead of settling it here.
      const kd = keyed(days);
      const curQty = Math.max(0, Number(aData && aData.deployedQty) || 0);
      const _stampedRet = kd.some(d => _rlm().rlmDayLive(d) && _rlm().rlmDayCarries(d, id) && _rlm().rlmDayDir(d, id) === 'return' && d.rentalPickup && _rlm().rlmDayPickedUp(d));
      for (const k of Object.keys(entry.pendingPickups || {})) {
        const kDay = kd.find(d => d.id === k);
        const settling = k === RENTAL_LEDGER_JOB_KEY ? _stampedRet : !!(kDay && kDay.rentalPickup && _rlm().rlmDayPickedUp(kDay));
        if (!settling) continue;
        const e = new Error('This pickup is being recorded right now. Nothing was changed — try again in a minute.');
        e.code = 'day-changed';
        throw e;
      }
      if (aData && isQty) aData = { ...aData, deployedQty: curQty };
      const nextDays = days.map(d => (d.id === jobDayId ? next : d));
      const before = _rlm().rlmPlannedPickups(keyed(days), id, isQty, todayKey);
      const after = _rlm().rlmPlannedPickups(keyed(nextDays), id, isQty, todayKey);
      const dueDelta = after.R - before.R;
      res.delta = dueDelta;
      // TASK-10006 S3 — more picked up on a day that already happened is a
      // PLANNED pickup on that day: the server step moves it (and takes it off
      // the jobs it came from), asked right after this transaction.
      const stampedAdd = {};
      if (dueDelta > 0) {
        stampedAdd[jobDayId] = { qty: dueDelta };
        res.kickSettle = true;
      } else if (dueDelta < 0) {
        const rp = _rlm().rlmReducePickupPlan({ ...entry, pendingPickups: {} }, [jobDayId, RENTAL_LEDGER_JOB_KEY], -dueDelta, isQty);
        // Units a real pickup brought back FROM other jobs stay settled on them;
        // lowering the pickup below that is not an edit this screen can make
        // (review #1) — refuse, nothing written.
        if (rp.left > 0) {
          const e = new Error(`That pickup already brought ${asset.name || asset.partNumber || 'these'} back from other jobs, so it can't be lowered here. Nothing was changed. If the count is wrong, use Recount or fix the yard count in Roster.`);
          e.code = 'day-changed';
          throw e;
        }
        entry = rp.entry;
        if (rp.redeploy > 0) giveBack(rp.redeploy, 'pickup-qty-edit');
      }
      const mergedPending = { ...after.pending };
      for (const [k, r] of Object.entries(stampedAdd)) mergedPending[k] = { qty: (Number(mergedPending[k] && mergedPending[k].qty) || 0) + r.qty };
      const oldPlanned = Object.values(entry.pendingPickups || {}).reduce((n, r) => n + (Number(r && r.qty) || 0), 0);
      const newPlanned = Object.values(mergedPending).reduce((n, r) => n + (Number(r && r.qty) || 0), 0);
      entry = _rlm().rlmTidy({ ...entry, pendingPickups: mergedPending });
      if (newPlanned > oldPlanned) {
        res.deferred = { qty: newPlanned - oldPlanned, dateKey: opts.pickupDateKey || '' };
        rows.push({ ...base, type: 'pickup-planned', direction: 'pickup-planned', quantity: 0, plannedQty: newPlanned - oldPlanned,
          pickupDate: opts.pickupDateKey || '', note: `Pickup of ${newPlanned - oldPlanned} more planned for a later day — stays out until then` });
      }
    }
    // ── writes (job doc + day ALWAYS — that is what serializes concurrent edits) ──
    ledger[id] = entry;
    tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger) });
    tx.update(dayRef, { [`assetQtys.${id}`]: newQty, updatedAt: Timestamp.now() });
    for (const r of rows) tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), r);
    return res;
  });
}

// TASK-9336 — a day LEAVING a ledger job, for its rentals, in ONE transaction:
// the day's own write (cancel → status, remove → delete, unassign → none; the
// caller writes the day), the job ledger, every asset's stock, and every
// return inspection it cancels. Idempotent: a retry finds the day already
// cancelled/gone, or the ledger already at 0.
//
//   mode 'cancel' | 'remove' | 'unassign'
//   opts.assetIds  — unassign only: the rentals coming off the day
//   opts.jobUpdate — extra job fields committed in the same write (remove's range)
//
// Two queries run BEFORE the transaction (a client transaction cannot query):
// the job's day ids and its pending inspection ids. Each is then re-read
// INSIDE the transaction, so every decision uses committed values. The job doc
// is read and written by every ledger transaction, so two of them on the same
// job (two dispatchers cancelling the last two days) serialize: the later one
// fails its precondition, retries, and sees the earlier one. Known limit: a
// day CREATED between the pre-query and the commit is not seen; the worst case
// is an early give-back (parked, never a double one — the ledger is at 0).
// Throws on any read/commit failure with NOTHING written.
async function _rentalLedgerLeaveTx(tenantId, jobId, dayId, mode, opts = {}) {
  const [daysSnap, riSnap] = await Promise.all([
    getDocs(query(collection(db, 'tenants', tenantId, 'jobDays'), where('jobId', '==', jobId))),
    getDocs(query(collection(db, 'tenants', tenantId, 'returnInspections'),
      where('jobId', '==', jobId), where('status', '==', 'pending'))),
  ]);
  const siblingIds = daysSnap.docs.map(d => d.id).filter(id => id !== dayId);
  const riIds = riSnap.docs.map(d => d.id);
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  const dayRef = doc(db, 'tenants', tenantId, 'jobDays', dayId);

  return await runTransaction(db, async (tx) => {
    // ── reads (all of them before any write) ──
    const jobSnap = await tx.get(jobRef);
    if (!jobSnap.exists() || !_jobHasRentalLedger(jobSnap.data())) {
      const e = new Error('This job has no rental ledger'); e.code = 'no-ledger'; throw e;
    }
    const selfSnap = await tx.get(dayRef);
    if (!selfSnap.exists()) return { outcome: 'gone', changes: [] };
    const self = { id: dayId, ...selfSnap.data() };
    if (mode === 'cancel' && self.status === 'cancelled') return { outcome: 'already', self, changes: [] };
    const candidateIds = Array.from(new Set(mode === 'unassign'
      ? (Array.isArray(opts.assetIds) ? opts.assetIds : [])
      : (Array.isArray(self.assets) ? self.assets : [])));
    const assetData = {};
    for (const aid of candidateIds) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'assets', aid));
      assetData[aid] = s.exists() ? s.data() : null;
    }
    const rentalIds = candidateIds.filter(aid => _isRentalAssetData(assetData[aid]));
    const days = [];
    const pendingRis = [];
    if (rentalIds.length > 0) {
      for (const sid of siblingIds) {
        const s = await tx.get(doc(db, 'tenants', tenantId, 'jobDays', sid));
        if (s.exists()) days.push({ id: sid, ...s.data() });
      }
      for (const rid of riIds) {
        const s = await tx.get(doc(db, 'tenants', tenantId, 'returnInspections', rid));
        if (!s.exists()) continue;
        const r = s.data();
        // Re-checked HERE: an inspection completed or dismissed after the
        // pre-query already moved stock, and must not be cancelled on top.
        if (r.status !== 'pending' || r.jobId !== jobId || !rentalIds.includes(r.assetId)) continue;
        pendingRis.push({ id: rid, ...r });
      }
    }

    // ── decide ──
    const ledger = { ...jobSnap.data().rentalLedger };
    const moves = rentalIds.map(aid => {
      const a = assetData[aid];
      const isQty = a.trackingMode === 'quantity';
      const stillHeld = Array.isArray(opts.stillHeld) && opts.stillHeld.includes(aid);
      // TASK-9396 (COD Code Reviewer 2026-10-11) — a return→deploy FLIP of a
      // single-tracked unit that ANOTHER job now holds: the undo would put it
      // back in this job's `out` while _txGiveBackRental (rightly) leaves the
      // asset on that job — ledger says out here, asset says out there, no
      // refusal anywhere. Skip the move entirely (ledger entry and stock
      // untouched, the day keeps its 'return') and report WHO holds it; the
      // settle names it as refused and saveAssignment keeps the old direction.
      const blockedBy = stillHeld && !isQty ? _rentalUnitHeldElsewhere(a, jobId) : null;
      if (blockedBy) return { aid, a, plan: null, blockedBy };
      const plan = _rentalLedgerLeavePlan({
        assetId: aid, selfId: dayId, days, entry: _rentalLedgerEntry(ledger, aid), pendingRis, isQty, mode, stillHeld
      });
      ledger[aid] = plan.entry;
      return { aid, a, plan };
    });

    // ── writes ──
    const changes = [];
    for (const m of moves) {
      if (!m.plan) continue; // TASK-9396 — blocked flip: nothing moves for it
      // Net stock change: units an undone immediate return puts back out,
      // minus what this job gives back to the yard.
      const delta = m.plan.redeploy - m.plan.giveBack;
      if (delta !== 0) {
        const w = _txGiveBackRental(tx, tenantId, m.aid, m.a, delta, jobId);
        changes.push({ assetId: m.aid, giveBack: m.plan.giveBack, redeploy: m.plan.redeploy, ...w });
        // TASK-10015 — the stock move is logged (signed, what really moved).
        const row = _rentalReversalRow({ ...m.a, id: m.aid }, _rentalMovedBy(w, m.a, delta), jobId, `day-${mode}`, { jobDayId: dayId });
        if (row) tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row);
      }
      for (const rid of m.plan.cancelRis) {
        tx.update(doc(db, 'tenants', tenantId, 'returnInspections', rid), {
          status: 'cancelled', cancelledByJobDayId: dayId, cancelledVia: mode, updatedAt: Timestamp.now()
        });
      }
    }
    // The job doc is written whenever a rental is involved — even with the
    // ledger unchanged — because that write is what serializes two
    // transactions settling the same job (each read it; the later commit fails
    // its precondition and retries against the earlier one's result).
    if (rentalIds.length > 0 || opts.jobUpdate) {
      tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger), ...(opts.jobUpdate || {}) });
    }
    if (mode === 'cancel') tx.update(dayRef, { status: 'cancelled', updatedAt: Timestamp.now() });
    else if (mode === 'remove') tx.delete(dayRef);
    else if (mode === 'unassign' && Array.isArray(opts.removeFromDay) && opts.removeFromDay.length > 0) {
      // Take the removed rentals off the day IN this transaction (COD Code
      // Reviewer 🔴 2026-09-23): two dispatchers removing the rental from the
      // last two days at once must each see the other's removal, or both skip
      // the give-back and strand the units. saveAssignment's own day write that
      // follows sets the same assets list.
      const _gone = new Set(opts.removeFromDay);
      tx.update(dayRef, {
        assets: (Array.isArray(self.assets) ? self.assets : []).filter(id => !_gone.has(id)),
        updatedAt: Timestamp.now()
      });
    }
    return {
      outcome: 'done', self, changes,
      cancelledRis: moves.reduce((n, m) => n + (m.plan ? m.plan.cancelRis.length : 0), 0),
      restored: Object.fromEntries(moves.filter(m => m.plan).map(m => [m.aid, m.plan.restored])),
      // TASK-9396 — { assetId: holdingJobId } for flips this transaction refused to undo.
      blocked: Object.fromEntries(moves.filter(m => m.blockedBy).map(m => [m.aid, m.blockedBy])),
    };
  });
}

// TASK-9336 — restoreJobDay's half for a ledger job: flip the day back and
// undo what its cancel did, in one transaction. Idempotent: a day that is not
// cancelled is left alone.
async function _rentalLedgerRestoreTx(tenantId, jobId, dayId) {
  // Only inspections a CANCEL closed are re-opened (never ones closed by
  // taking a rental off the day in the assignment modal — independent review
  // 🔴 2026-09-23: that re-opened a second inspection for the same units):
  // this day's own, and job-level ones (no owning day), whichever day of the
  // job is restored first.
  const _reopenable = (r) => !!r && r.status === 'cancelled' && r.jobId === jobId && r.cancelledVia === 'cancel'
    && (r.cancelledByJobDayId === dayId || !r.jobDayId);
  const riSnap = await getDocs(query(collection(db, 'tenants', tenantId, 'returnInspections'),
    where('jobId', '==', jobId), where('status', '==', 'cancelled')));
  const riIds = riSnap.docs.filter(d => _reopenable(d.data())).map(d => d.id);
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  const dayRef = doc(db, 'tenants', tenantId, 'jobDays', dayId);
  return await runTransaction(db, async (tx) => {
    const jobSnap = await tx.get(jobRef);
    if (!jobSnap.exists() || !_jobHasRentalLedger(jobSnap.data())) {
      const e = new Error('This job has no rental ledger'); e.code = 'no-ledger'; throw e;
    }
    const selfSnap = await tx.get(dayRef);
    if (!selfSnap.exists()) return { outcome: 'gone', changes: [] };
    const self = { id: dayId, ...selfSnap.data() };
    if (self.status !== 'cancelled') return { outcome: 'not-cancelled', self, changes: [] };
    const assetIds = Array.from(new Set(Array.isArray(self.assets) ? self.assets : []));
    const assetData = {};
    for (const aid of assetIds) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'assets', aid));
      assetData[aid] = s.exists() ? s.data() : null;
    }
    const ris = [];
    for (const rid of riIds) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'returnInspections', rid));
      if (!s.exists()) continue;
      const r = s.data();
      if (!_reopenable(r)) continue;
      ris.push({ id: rid, ...r });
    }
    const ledger = { ...jobSnap.data().rentalLedger };
    const changes = [];
    for (const aid of assetIds) {
      const a = assetData[aid];
      if (!_isRentalAssetData(a)) continue;
      const isQty = a.trackingMode === 'quantity';
      const full = _rentalLedgerEntry(ledger, aid);
      // An individually-tracked unit that has since gone out on ANOTHER job
      // cannot be re-deployed here — it stays parked rather than be stolen.
      const blocked = !isQty && full.parked > 0 && a.deploymentStatus === 'deployed'
        && !!a.deployedToJobId && a.deployedToJobId !== jobId;
      const plan = _rentalLedgerRestorePlan({
        entry: full, reopenRis: ris.filter(r => r.assetId === aid), isQty, selfId: dayId, skipParked: blocked,
        stockAfterRedeploy: isQty ? (a.deployedQty || 0) + (blocked ? 0 : full.parked) : null
      });
      const delta = plan.redeploy - plan.reapplied;
      if (delta !== 0) {
        const w = _txGiveBackRental(tx, tenantId, aid, a, delta, jobId);
        changes.push({ assetId: aid, redeploy: plan.redeploy, reapplied: plan.reapplied, ...w });
        const row = _rentalReversalRow({ ...a, id: aid }, _rentalMovedBy(w, a, delta), jobId, 'day-restore', { jobDayId: dayId });
        if (row) tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row);
      }
      for (const r of plan.reopen) tx.update(doc(db, 'tenants', tenantId, 'returnInspections', r.id), {
        status: 'pending', ledgerQty: r.ledgerQty, cancelledByJobDayId: null, cancelledVia: null,
        reopenedAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      ledger[aid] = plan.entry;
    }
    const hasAssignments = (self.drivers?.length > 0 || self.trucks?.length > 0);
    tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger) });
    tx.update(dayRef, { status: hasAssignments ? 'assigned' : 'scheduled', updatedAt: Timestamp.now() });
    return { outcome: 'done', self, changes };
  });
}

// TASK-9336 — deleting a WHOLE ledger job: cancel every pending return
// inspection it has (their ledgerQty comes back to `out`) and give back every
// unit the ledger says is out. Label-free, so a cancelled or copied first day
// no longer decides anything. Returns null for a job with no ledger (the
// caller keeps the pre-ledger first-day reversal).
async function _rentalLedgerJobDeleteTx(tenantId, jobId) {
  const riSnap = await getDocs(query(collection(db, 'tenants', tenantId, 'returnInspections'),
    where('jobId', '==', jobId), where('status', '==', 'pending')));
  const riIds = riSnap.docs.map(d => d.id);
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  return await runTransaction(db, async (tx) => {
    const jobSnap = await tx.get(jobRef);
    if (!jobSnap.exists() || !_jobHasRentalLedger(jobSnap.data())) return null;
    const ledger = { ...jobSnap.data().rentalLedger };
    const ris = [];
    for (const rid of riIds) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'returnInspections', rid));
      if (!s.exists()) continue;
      const r = s.data();
      if (r.status !== 'pending' || r.jobId !== jobId) continue;
      ris.push({ id: rid, ...r });
    }
    const assetIds = Array.from(new Set([...Object.keys(ledger), ...ris.map(r => r.assetId)])).filter(Boolean);
    const assetData = {};
    for (const aid of assetIds) {
      const s = await tx.get(doc(db, 'tenants', tenantId, 'assets', aid));
      assetData[aid] = s.exists() ? s.data() : null;
    }
    const changes = [];
    for (const aid of assetIds) {
      const a = assetData[aid];
      const isQty = a?.trackingMode === 'quantity';
      // Everything is leaving: no other live day, every pending inspection orphaned.
      const plan = _rentalLedgerLeavePlan({
        assetId: aid, selfId: null, days: [], entry: _rentalLedgerEntry(ledger, aid),
        pendingRis: ris.filter(r => r.assetId === aid), isQty, mode: 'job-delete'
      });
      // Immediate returns (pickups) this job made are undone too — the job never
      // happened — then everything the job holds goes back to the yard.
      const delta = plan.redeploy - plan.giveBack;
      if (a && delta !== 0) {
        const w = _txGiveBackRental(tx, tenantId, aid, a, delta, jobId);
        changes.push({ assetId: aid, giveBack: plan.giveBack, redeploy: plan.redeploy, ...w });
        // TASK-10015 — logged, signed, in the same transaction.
        const row = _rentalReversalRow({ ...a, id: aid }, _rentalMovedBy(w, a, delta), jobId, 'job-delete');
        if (row) tx.set(doc(collection(db, 'tenants', tenantId, 'assetTransactions')), row);
      }
      for (const rid of plan.cancelRis) {
        tx.update(doc(db, 'tenants', tenantId, 'returnInspections', rid), {
          status: 'cancelled', cancelledVia: 'job-delete', updatedAt: Timestamp.now()
        });
      }
      ledger[aid] = { out: 0, parked: 0 };
    }
    tx.update(jobRef, { rentalLedger: ledger, ..._rentalPendingFlag(jobSnap.data(), ledger) });
    return { outcome: 'done', changes };
  });
}

function toggleCheckbox(element, sectionKey) {
  const checkbox = element.querySelector('input[type="checkbox"]');
  if (checkbox) {
    checkbox.checked = !checkbox.checked;
    element.classList.toggle('checked', checkbox.checked);
    updateSectionCount(sectionKey);

    // DOT compliance check on driver/truck/trailer/asset change. TASK-9198 —
    // 'assets' was missing, so an asset safety hold would never re-run the check.
    if (['drivers', 'helpers', 'trucks', 'trailers', 'assets'].includes(sectionKey)) {
      setTimeout(runAssignmentComplianceCheck, 50);
      // TASK-9763 — "Who's in what" follows the picked crew/units (assignment modal only).
      const _pm = document.getElementById('modal-assignment');
      if (_pm && !_pm.classList.contains('hidden')) renderAssignmentPairing();
    }

    // Line item picker: add/remove on toggle (gated by clockbilling)
    if (!window.hasAddOnPackage?.('clockbilling')) return;

    const resourceId = checkbox.value;
    const kindMap = { trucks: 'truck', trailers: 'trailer', assets: 'asset', drivers: 'person', helpers: 'person' };
    const kind = kindMap[sectionKey];
    if (kind) {
      const resourceKey = `${kind}:${resourceId}`;
      // TASK-1673 — the rate panel only exists in the assignment modal; the job
      // modal keeps the inline picker it has always had.
      const _asgModal = document.getElementById('modal-assignment');
      const _inAssignment = !!_asgModal && !_asgModal.classList.contains('hidden');
      if (!checkbox.checked) {
        // Remove picker and selection
        element.parentElement?.querySelector(`.line-item-picker[data-resource="${resourceKey}"]`)?.remove();
        delete DispatchState.pendingLineItemSelections[resourceKey];
        delete DispatchState.pendingJobLineItemSelections[resourceKey];
        if (_inAssignment) renderAssignmentRatePanel();
      } else {
        // Try to show picker if line items are loaded
        const jobId = document.getElementById('assignment-job-id')?.value || document.getElementById('job-id')?.value;
        const job = DispatchState.jobs.find(j => j.id === jobId);
        const customerId = job?.customerId || document.getElementById('job-customer')?.value;
        // Only the day being edited is excluded from the last-used search, so a
        // pick already on THIS day never suggests itself back.
        const _dayId = document.getElementById('assignment-job-day-id')?.value || null;
        if (customerId) {
          const cached = DispatchState.lineItemCache[customerId];
          if (cached?.items?.length > 0) {
            const scopedItems = getContractScopedItems(customerId, false);
            renderPickerForResource(element, kind, resourceId, scopedItems);
            if (_inAssignment) {
              applyLastUsedRateDefaults(customerId, _dayId);
              _syncRateControls(resourceKey);
              renderAssignmentRatePanel();
            }
          } else {
            // Cache not warm yet — load and render when ready
            loadCustomerLineItems(customerId).then(({ items }) => {
              if (items.length > 0 && checkbox.checked) {
                const scopedItems = getContractScopedItems(customerId, false);
                renderPickerForResource(element, kind, resourceId, scopedItems);
                if (_inAssignment && !_asgModal.classList.contains('hidden') &&
                    document.getElementById('assignment-job-day-id')?.value === _dayId) {
                  applyLastUsedRateDefaults(customerId, _dayId);
                  _syncRateControls(resourceKey);
                  renderAssignmentRatePanel();
                }
              }
            });
          }
        }
      }
    }
  }
}

function updateSectionCount(sectionKey) {
  setTimeout(() => {
    const container = document.getElementById(`select-${sectionKey}`);
    const countEl = document.getElementById(`count-${sectionKey}`);
    if (!container || !countEl) return;
    const count = container.querySelectorAll('input:checked').length;
    countEl.textContent = count;
    countEl.style.display = count > 0 ? '' : 'none';
  }, 10);
}

function filterMultiSelect(containerId, query) {
  const container = document.getElementById(containerId);
  if (!container) return;
  const q = query.toLowerCase().trim();
  container.querySelectorAll('.checkbox-item').forEach(item => {
    const name = item.querySelector('.checkbox-name')?.textContent?.toLowerCase() || '';
    const role = item.querySelector('.role-tag')?.textContent?.toLowerCase() || '';
    const yard = item.querySelector('.asset-yard-tag')?.textContent?.toLowerCase() || ''; // TASK-9510
    item.style.display = (!q || name.includes(q) || role.includes(q) || yard.includes(q)) ? '' : 'none';
  });
}

// ============================================
// LINE ITEM PICKER — Rendering
// ============================================
function renderLineItemPickers(containerId, kind, allLineItems) {
  const container = document.getElementById(containerId);
  if (!container) return;

  // Remove any existing pickers first
  container.querySelectorAll('.line-item-picker').forEach(el => el.remove());

  container.querySelectorAll('.checkbox-item').forEach(item => {
    const checkbox = item.querySelector('input[type="checkbox"]');
    if (checkbox?.checked) {
      renderPickerForResource(item, kind, checkbox.value, allLineItems);
    }
  });
}

function renderPickerForResource(checkboxItem, kind, resourceId, allLineItems) {
  const resourceKey = `${kind}:${resourceId}`;
  // Don't duplicate. CSS.escape because a legacy GAS name string can carry a
  // quote, which would silently break the selector and render a second picker.
  if (checkboxItem.parentElement?.querySelector(`.line-item-picker[data-resource="${CSS.escape(resourceKey)}"]`)) return;

  // Find matching roster item for type-aware matching
  let resource = null;
  if (kind === 'truck') resource = DispatchState.trucks.find(t => t.id === resourceId);
  else if (kind === 'trailer') resource = DispatchState.trailers.find(t => t.id === resourceId);
  else if (kind === 'asset') resource = DispatchState.assets.find(a => a.id === resourceId);
  else if (kind === 'person') resource = DispatchState.people.find(p => p.id === resourceId);

  const matches = getMatchingLineItems(allLineItems, kind, resource);
  if (matches.length < 2) return; // No picker needed for 0-1 matches

  // Determine which pending selections map to use
  const isJobModal = !!document.getElementById('modal-job') && !document.getElementById('modal-job').classList.contains('hidden');
  const selections = isJobModal ? DispatchState.pendingJobLineItemSelections : DispatchState.pendingLineItemSelections;
  const existing = selections[resourceKey];

  const unitLabel = { Hourly: 'hr', Daily: 'day', Monthly: 'mo' };
  const hasSelection = !!existing;
  const pickerDiv = document.createElement('div');
  pickerDiv.className = 'line-item-picker';
  pickerDiv.dataset.resource = resourceKey;
  pickerDiv.innerHTML = `
    <div class="picker-label">Rate${!hasSelection ? '<span class="rate-unset-flag" title="No rate selected — auto-match will be used at billing">●</span>' : ''}</div>
    <select onchange="window.DispatchModule.onInlineLineItemPick(this)">
      <option value="">Auto (best match)</option>
      ${matches.map(li => {
        const unit = unitLabel[li.rateUnit] || li.rateUnit || '';
        const sel = existing?.lineItemId === li.id ? 'selected' : '';
        // TASK-1673 — value built by the shared `_rateOptionValue` so this
        // control and the rate panel encode identically (and a `|` inside a
        // tenant-written description can no longer shift every field of the
        // parse one slot to the right, which silently wrote the WRONG rate).
        // escAttr, not the old bare `"`→`&quot;`: descriptions are tenant text.
        const escA = window.escAttr || escapeHtml;
        return `<option value="${escA(_rateOptionValue(li))}" ${sel}>${escapeHtml(li.description || 'Line Item')} — $${escapeHtml(String(li.rate))}/${escapeHtml(unit)}</option>`;
      }).join('')}
    </select>
  `;

  // Insert after the checkbox item
  checkboxItem.insertAdjacentElement('afterend', pickerDiv);
}

// `isJobModalOverride` — the rate panel lives only in the assignment modal and
// says so explicitly rather than re-deriving it from which modal looks open.
function onLineItemPick(selectEl, resourceKey, isJobModalOverride) {
  const val = selectEl.value;
  // Determine which pending selections map to use
  const isJobModal = typeof isJobModalOverride === 'boolean'
    ? isJobModalOverride
    : (!!document.getElementById('modal-job') && !document.getElementById('modal-job').classList.contains('hidden'));
  const selections = isJobModal ? DispatchState.pendingJobLineItemSelections : DispatchState.pendingLineItemSelections;

  // Toggle the unset flag on the picker label
  const pickerDiv = selectEl.closest('.line-item-picker');
  const flag = pickerDiv?.querySelector('.rate-unset-flag');
  if (!val) {
    // "Auto" selected — remove override, show flag
    delete selections[resourceKey];
    if (!flag && pickerDiv) {
      const label = pickerDiv.querySelector('.picker-label');
      if (label) label.insertAdjacentHTML('beforeend', '<span class="rate-unset-flag" title="No rate selected — auto-match will be used at billing">●</span>');
    }
    if (!isJobModal) { _syncRateControls(resourceKey); renderAssignmentRatePanel(); }
    return;
  }

  // Explicit selection — remove flag
  if (flag) flag.remove();

  const parts = val.split('|');
  selections[resourceKey] = {
    lineItemId: parts[0],
    contractId: parts[1],
    rate: parseFloat(parts[2]) || 0,
    rateUnit: parts[3] || '',
    description: parts[4] || '',
    // TASK-9489 — three-way: '1' Always, '0' Never, '' Company default (null).
    roundToHalfHour: parts[5] === '1' ? true : (parts[5] === '0' ? false : null),
    revenueCategory: parts[6] || '',
    // TASK-1673 — a human touched this control. Distinguishes a hand-pick from
    // the last-used SUGGESTION the panel pre-fills, which is what lets the panel
    // say "Suggested — confirm" instead of claiming the rate was chosen.
    source: 'picked'
  };
  if (!isJobModal) { _syncRateControls(resourceKey); renderAssignmentRatePanel(); }
}

// ============================================================================
// TASK-9763 — "WHO'S IN WHAT": dispatch pairs crew to units per day.
// ============================================================================
// The per-unit editor is a LAZY chunk (views/assignPairing.js) — office-only,
// kept off the cold bundle every driver downloads. These are its thin doors.
// Never a static import (Pitfall #1 / perf budget); the module gets a ctx.
let _asgPairMod = null;
let _asgPairLoading = null;
function _asgLoadPairing() {
  if (_asgPairMod) return Promise.resolve(_asgPairMod);
  if (!_asgPairLoading) {
    _asgPairLoading = __ddChunkWrap(import('./assignPairing.js?v=6'))
      .then(m => { _asgPairMod = m; return m; })
      .catch(err => { _asgPairLoading = null; console.warn('[Dispatch] TASK-9763 pairing panel failed to load:', err?.message || err); return null; });
  }
  return _asgPairLoading;
}

function _asgPairCtx() {
  return { state: DispatchState, resolveFleetRef: _resolveFleetRef, escapeHtml };
}

function renderAssignmentPairing() {
  if (_asgPairMod) { _asgPairMod.renderAssignmentPairing(_asgPairCtx()); return; }
  _asgLoadPairing().then(m => {
    const md = document.getElementById('modal-assignment');
    if (m && md && !md.classList.contains('hidden')) m.renderAssignmentPairing(_asgPairCtx());
  });
}

function asgSetUnitPair(sel) {
  if (_asgPairMod) _asgPairMod.asgSetUnitPair(_asgPairCtx(), sel);
}
// TASK-9763 S4 — "+ Add a second person" on a unit (team driving / ride-along).
function asgAddUnitPair(btn) {
  if (_asgPairMod) _asgPairMod.asgAddUnitPair(_asgPairCtx(), btn);
}
// TASK-10193 S2 — "May drive" on a team truck's 2nd+ person (who gets the pre-trip reminder).
function asgSetUnitSeat(box) {
  if (_asgPairMod) _asgPairMod.asgSetUnitSeat(_asgPairCtx(), box);
}

// fix/2096-preship (integ/2096 COD) — the picks are SNAPSHOTTED synchronously
// at the TOP of saveAssignment, before its first await, with the day they were
// opened for. saveAssignment awaits the locate hard-stop and the sig settings
// before it builds the map; if the dispatcher opens another day in that window,
// showAssignment resets DispatchState's pairing state to day B — day A must
// never save day B's picks. No snapshot for the day being saved → null.
function _asgSnapshotUnitPairs(jobDayId) {
  if (!jobDayId || DispatchState.pendingUnitPairsDayId !== jobDayId) return null;
  return {
    dayId: jobDayId,
    pendingUnitPairs: DispatchState.pendingUnitPairs ? JSON.parse(JSON.stringify(DispatchState.pendingUnitPairs)) : null,
    pendingUnitPairsSrc: DispatchState.pendingUnitPairsSrc ? JSON.parse(JSON.stringify(DispatchState.pendingUnitPairsSrc)) : null,
    hadMap: !!DispatchState.pendingUnitPairsHadMap,
    assets: DispatchState.assets,
    // TASK-10193 S2 — the seat ticks + auto-link state (and the roster, for the driver-role test).
    pendingUnitSeats: DispatchState.pendingUnitSeats ? JSON.parse(JSON.stringify(DispatchState.pendingUnitSeats)) : null,
    pendingPairTouched: DispatchState.pendingPairTouched ? { ...DispatchState.pendingPairTouched } : null,
    pendingAutoLink: DispatchState.pendingAutoLink ? { ...DispatchState.pendingAutoLink } : null,
    people: DispatchState.people,
    // TASK-9763 S3 — the day's crew order as the dispatcher left it.
    pendingCrewOrder: Array.isArray(DispatchState.pendingCrewOrder) ? DispatchState.pendingCrewOrder.slice() : null,
    pendingCrewOrderSrc: Array.isArray(DispatchState.pendingCrewOrderSrc) ? DispatchState.pendingCrewOrderSrc.slice() : null,
    // TASK-9797 item 1 — the day's named slots as left (copied: edits during the save's awaits
    // never leak in), and the fleet the chunk resolves stored keys against (COD r3).
    pendingUnitSlotsSrc: JSON.parse(JSON.stringify(DispatchState.pendingUnitSlots || DispatchState.pendingUnitSlotsSrc || null)),
    trucks: DispatchState.trucks
  };
}

// TASK-9763 S3 — the crewOrder to store: people already placed keep their
// place, newcomers go on the end by company seniority (shared rule, sync — no
// lazy chunk on the save path). undefined = no snapshot for THIS day or no
// rule → leave the stored field alone. [] = nobody on the day.
function _asgBuildCrewOrder(snap, jobDayId, people) {
  const CP = window.CrewPairing;
  if (!CP || !snap || snap.dayId !== jobDayId) return undefined;
  try {
    return CP.orderNext(snap.pendingCrewOrder || snap.pendingCrewOrderSrc || [], people, window.AppState?.settings?.crewSeniority);
  } catch (_) { return undefined; }
}

function asgMoveCrew(btn) {
  if (_asgPairMod) _asgPairMod.asgMoveCrew(_asgPairCtx(), btn);
}
function asgResetCrewOrder() {
  if (_asgPairMod) _asgPairMod.asgResetCrewOrder(_asgPairCtx());
}

// S3 — Dispatch Settings → "Crew seniority" (same lazy chunk; office-only).
function _asgSenCtx() {
  return { ..._asgPairCtx(), canSave: _dispatchSettingsCanSave() };
}
function renderSeniorityEditor() {
  DispatchState.pendingSeniority = null; // seed from the saved list on every open
  _asgLoadPairing().then(m => {
    if (m) { m.renderSeniorityEditor(_asgSenCtx()); return; }
    const box = document.getElementById('dispatch-settings-seniority');
    if (box) box.innerHTML = '<p style="font-size:0.8rem;color:var(--text-secondary);margin:0;">Could not load this section — reload the page and try again.</p>';
  });
}
function senMove(btn) { if (_asgPairMod) _asgPairMod.senMove(_asgSenCtx(), btn); }
function senSort(how) { if (_asgPairMod) _asgPairMod.senSort(_asgSenCtx(), how); }
function senSave() { if (_asgPairMod && _dispatchSettingsCanSave()) _asgPairMod.senSave(_asgSenCtx()); }
// TASK-9797 item 1 — a day's named slots kept only for trucks on the day.
// Keys are the truck refs Assign Crew saved (the day's own ids). typeof
// guards: the add-day builders also run standalone (vm) in tripwires.
function _slotsOn(m, t) {
  const o = {};
  for (const k in m) if ((t || []).includes(k)) o[k] = m[k];
  for (const k in o) return o;
  return null;
}
function senClear() { if (_asgPairMod && _dispatchSettingsCanSave()) _asgPairMod.senClear(_asgSenCtx()); }

// undefined = module/rule unavailable, or no snapshot for THIS day → the save
// leaves the stored field alone. Reads only the snapshot, never DispatchState.
async function _asgBuildCrewAssetAssignments(snap, jobDayId, day) {
  if (!snap || snap.dayId !== jobDayId) return undefined;
  const m = await _asgLoadPairing();
  return m ? m.buildCrewAssetAssignments({ state: snap, resolveFleetRef: _resolveFleetRef, escapeHtml }, day) : undefined;
}

// Driver card (driver-PIN board): display names of the units dispatch paired to
// THIS driver on the day, after the read-time filter. [] = no line.
function _driverPairedUnitNames(jobDay) {
  const CP = window.CrewPairing;
  const pid = window.AppState?.driverPersonId;
  if (!CP || !pid || !jobDay || !jobDay.crewAssetAssignments) return [];
  try {
    const all = [...(DispatchState.trucks || []), ...(DispatchState.trailers || []), ...(DispatchState.assets || [])];
    const unitKey = (ref) => { const u = _resolveFleetRef(String(ref), all); return u && u.id != null ? String(u.id) : String(ref); };
    const personKey = (ref) => { const s = String(ref); const p = (DispatchState.people || []).find(x => x && (String(x.id) === s || x.name === s)); return p && p.id != null ? String(p.id) : s; };
    const view = CP.view(jobDay, { unitKey, personKey });
    const viewer = [String(pid)]; // id only — a name could match a namesake (COD Edge Case)
    // TASK-10193 S2 — the viewer's seat in a team truck: "May drive" → co-driver,
    // rider flag → riding along; no flag = drives it (no suffix, as before).
    const mineEntry = jobDay.crewAssetAssignments[String(pid)];
    const flagKeys = (f) => (mineEntry && Array.isArray(mineEntry[f]) ? mineEntry[f] : []).map(unitKey);
    const coKeys = flagKeys('coDriverTruckIds');
    const riderKeys = flagKeys('riderTruckIds');
    return CP.mine(view, viewer).map(id => {
      const u = _resolveFleetRef(id, all);
      const n0 = (u && ((window.getUnitDisplay && window.getUnitDisplay(u)) || u.nickname || u.unit || u.name)) || null;
      if (!n0) return null;
      const n = coKeys.includes(id) ? `${n0} (co-driver)` : riderKeys.includes(id) ? `${n0} (riding along)` : n0; // TASK-10193 S2
      const sl = n && CP.slot ? CP.slot(jobDay, id, window.AppState?.settings) : ''; // TASK-9797 S2
      return sl ? n + ' · your slot ' + sl : n;
    }).filter(Boolean);
  } catch (_) { return []; }
}

// TASK-10067 — the consumable deduct for saveJob + saveAssignment (and, via
// window.*, billing.js saveReviewTicket — TASK-10067b). It used to write
// `quantity: <cached count> - qty`, an ABSOLUTE value from this tab's copy of
// the asset, so units an org transfer (TASK-9375 S4) had moved out came back.
// TASK-10067 item 2 — the deduct now runs on the SERVER: `adjustAssetQuantity`
// (functions/src/services/assetQuantity.js), because firestore.rules refuse a
// client `quantity` change on a stored consumable. One Admin transaction
// re-reads each asset and deducts from the STORED count; the ledger row carries
// the real before/after. Same rules as before: untracked consumables only (a
// stock-tracked item is skipped — the server ledger owns it, TASK-9720),
// negative stock still writes (TASK-9275: warn, never block), deduct + ledger
// row land together or not at all (TASK-4656), two lines for one asset chain
// and the asset is written once (TASK-10067b), an asset deleted / no longer
// consumable meanwhile is skipped and named. The kind is the row type
// (`ledgerOf(it).type`: 'dispatch' | 'ticket') and picks the module the server
// checks (dispatch / billing); the tenant comes from the auth claim (`tenantId`
// is sent only to be checked against it, and to reach the open `demo` tenant). Offline -> an error with code 'unavailable' and a
// plain message, nothing written. Returns the deducts that landed; the local
// cache is updated only after the server commits.
const _DEDUCT_LEDGER_KEYS = ['jobId', 'jobDayId', 'ticketId', 'ticketNumber', 'note', 'source', 'templateId', 'templateName', 'templateConfirmed'];
async function _deductConsumablesTx(tenantId, adds, ledgerOf) {
  const rows = adds.map(it => ledgerOf(it));
  const items = adds.map((it, i) => {
    const ledger = {};
    for (const k of _DEDUCT_LEDGER_KEYS) if (rows[i][k] !== undefined) ledger[k] = rows[i][k];
    return { assetId: it.assetId, qty: it.qty, ledger };
  });
  if (rows.some(r => r.type !== rows[0].type)) throw new Error('inventory deduct: one kind per call');
  // COD r1 — a lost response may still have committed: ONE retry with the SAME
  // requestId replays the server's result instead of deducting twice. Still no
  // answer -> the outcome is unknown, and the message says so.
  // tenantId: the server binds the claim's tenant (a mismatch is refused); only `demo` is served from it.
  const payload = { kind: rows[0] && rows[0].type, items, tenantId, requestId: 'q' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10) };
  const transport = (err) => {
    const code = String(err?.code || '');
    return (typeof navigator !== 'undefined' && navigator.onLine === false) || code === 'functions/unavailable' || code === 'functions/deadline-exceeded'
      || (code === 'functions/internal' && /^internal$/i.test(String(err?.message || '').trim()));
  };
  let res;
  for (let attempt = 0; ; attempt++) {
    try {
      res = await httpsCallable(functions, 'adjustAssetQuantity')(payload);
      break;
    } catch (err) {
      if (!transport(err)) throw err;
      if (attempt === 0 && !(typeof navigator !== 'undefined' && navigator.onLine === false)) continue;
      throw Object.assign(new Error('No connection. Could not confirm the inventory deduct; check the item counts in Roster before deducting again.'), { code: 'unavailable', cause: err });
    }
  }
  const done = ((res && res.data && res.data.landed) || [])
    .map(l => ({ it: adds[l.i], prevQty: l.prevQty, newQty: l.newQty }))
    .filter(d => d.it);
  for (const d of done) {
    d.it.asset.quantity = d.newQty;
    // The pre-write warning read the cache; say so when only the stored count goes below zero.
    if (d.newQty < 0 && !(d.it.newQty < 0)) {
      window.showToast(`Warning: ${d.it.asset.name} is now at ${d.newQty} (negative inventory)`, 'warning');
    }
  }
  if (done.length < adds.length) {
    // Say which items were NOT deducted, so nobody assumes the count moved.
    const landed = new Set(done.map(d => d.it));
    const names = adds.filter(it => !landed.has(it)).map(it => it.asset.name || it.asset.partNumber || 'an item');
    console.warn('[Dispatch] TASK-10067: skipped consumables deleted, stock-tracked or no longer consumable since load', names.length);
    window.showToast(`Not deducted from inventory (deleted or changed since this opened): ${names.join(', ')}`, 'warning');
  }
  return done;
}
// TASK-10067b — billing.js saveReviewTicket deducts through the same helper
// (window.*, never a cross-import with ?v=; CLAUDE.md Pitfall #1).
window._deductConsumablesTx = _deductConsumablesTx;

async function saveAssignment(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const jobDayId = document.getElementById('assignment-job-day-id').value;
  // TASK-9763 / fix/2096-preship — BEFORE ANY AWAIT: this day's "who's in what"
  // picks, bound to the day the modal was opened for (_asgSnapshotUnitPairs).
  const _asgPairSnap = _asgSnapshotUnitPairs(jobDayId);

  const getSelected = (containerId) => {
    const container = document.getElementById(containerId);
    return Array.from(container.querySelectorAll('input:checked')).map(i => i.value);
  };

  const selectedTrucks = getSelected('select-trucks');
  const selectedTrailers = getSelected('select-trailers');
  const selectedAssets = getSelected('select-assets');
  const selectedDrivers = getSelected('select-drivers');
  const selectedHelpers = getSelected('select-helpers');
  // TASK-9763 S3 — "first on" order for THIS day, from the same pre-await
  // snapshot as the pairing. Sync; nothing below can move it.
  const _asgOrder = _asgBuildCrewOrder(_asgPairSnap, jobDayId, selectedDrivers.concat(selectedHelpers));
  const selectedSubcontractorIds = getSelected('select-subcontractors');
  const subcontractors = selectedSubcontractorIds.map(id => {
    const c = DispatchState.customers.find(x => x.id === id);
    return { vendorId: id, vendorName: c?.name || 'Unknown' };
  });

  // Build lineItemSelections: keep only selections for resources that are still checked
  const checkedResourceKeys = new Set();
  selectedTrucks.forEach(id => checkedResourceKeys.add(`truck:${id}`));
  selectedTrailers.forEach(id => checkedResourceKeys.add(`trailer:${id}`));
  selectedAssets.forEach(id => checkedResourceKeys.add(`asset:${id}`));
  selectedDrivers.forEach(id => checkedResourceKeys.add(`person:${id}`));
  selectedHelpers.forEach(id => checkedResourceKeys.add(`person:${id}`));

  const lineItemSelections = {};
  for (const [key, val] of Object.entries(DispatchState.pendingLineItemSelections)) {
    if (checkedResourceKeys.has(key)) {
      lineItemSelections[key] = val;
    }
  }

  // Build assetQtys map for quantity-tracked assets (TASK-9510 — qty 1 explicit)
  const assetQtys = buildSavedAssetQtys(selectedAssets, DispatchState.pendingAssetQtys, DispatchState.assets);

  // Build assetDirections map (only store 'return', deploy is default)
  const assetDirections = {};
  for (const assetId of selectedAssets) {
    const dir = DispatchState.pendingAssetDirections[assetId];
    if (dir === 'return') assetDirections[assetId] = 'return';
  }

  // TASK-395 Slice 1: stamp sig flags onto the jobDay (render hint for the driver builder).
  // TASK-4655 — resolve through the SAME search-aware resolvers showAssignment
  // opened the modal with (:10388). The live arrays are 90-day windowed (:3332);
  // a day opened from Search is only in searchJobDays, so the live-only lookup
  // resolved null and stamped fieldTicketSettings computed from customerId
  // `undefined` onto the day. Third lookup of the same id in this function —
  // all three must agree or the guard vets one day while the write targets another.
  const _asgJobDay = _resolveJobDayAnywhere(jobDayId);
  const _asgJob = _asgJobDay ? _resolveJobAnywhere(_asgJobDay.jobId) : null;
  // TASK-10184 — someone newly put on this day has approved time off: say who,
  // what and when; "Assign anyway" saves, "Go back" leaves the modal open.
  if (_asgJobDay && (selectedDrivers.length || selectedHelpers.length)) {
    const _asgOffKey = _p811DayKey(_asgJobDay.date, _p811Tz());
    const _asgPrevPeople = [...(_asgJobDay.drivers || []), ...(_asgJobDay.helpers || [])];
    if (_asgOffKey && !(await confirmTimeOffConflicts(selectedDrivers.concat(selectedHelpers), [_asgOffKey], _asgPrevPeople))) return;
  }
  // TASK-9862 — HARD STOP: putting a crew on a day after a linked locate's good-through date.
  if (_asgJob && _asgJobDay && (selectedDrivers.length || selectedHelpers.length || selectedTrucks.length || selectedTrailers.length || selectedAssets.length)) {
    const _p811Stops = _p811StopsFor(_jobPermitIdsOf(_asgJob), _asgJob.projectId || '', [_p811DayKey(_asgJobDay.date, _p811Tz())]);
    if (_p811Stops.length && !(await _p811HardStopGate(_p811Stops, { jobId: _asgJob.id, jobLabel: _asgJob.title || '', source: 'assign-crew' }))) return;
  }
  const _asgSigSettings = await resolveJobDaySigSettings(_asgJob?.customerId, DispatchState.pendingSelectedContractId || null);

  // TASK-9763 — "who's in what" for THIS day, pruned to what is still checked.
  // Not applicable (1 person / 1 truck) or nothing paired → no map; a day that
  // HAD one is cleared with null (absent/null = today's behaviour everywhere).
  const _asgPairs = await _asgBuildCrewAssetAssignments(_asgPairSnap, jobDayId, {
    drivers: selectedDrivers, helpers: selectedHelpers,
    trucks: selectedTrucks, trailers: selectedTrailers, assets: selectedAssets
  });
  const _asgHadPairs = !!((_asgPairSnap && _asgPairSnap.hadMap) || (_asgJobDay && _asgJobDay.crewAssetAssignments));
  const assignmentData = {
    drivers: selectedDrivers,
    helpers: selectedHelpers,
    trucks: selectedTrucks,
    trailers: selectedTrailers,
    assets: selectedAssets,
    assetQtys,
    assetDirections,
    subcontractors,
    ...(_asgPairs === undefined ? {} : _asgPairs ? { crewAssetAssignments: _asgPairs } : (_asgHadPairs ? { crewAssetAssignments: null } : {})),
    // TASK-9797 item 1 — named slots: stashed by the build above for THIS day only (absent = leave alone).
    ..._asgPairSnap?.slotPatch,
    // TASK-9763 S3 — the day's "first on" order. Written whenever the day has
    // crew (so the order people were added is captured from now on); a day
    // emptied of crew that HAD one is cleared with null. No rule → untouched.
    ...(_asgOrder === undefined ? {} : _asgOrder.length ? { crewOrder: _asgOrder }
      : ((_asgPairSnap?.pendingCrewOrderSrc?.length || _asgJobDay?.crewOrder?.length) ? { crewOrder: null } : {})),
    lineItemSelections,
    selectedContractId: DispatchState.pendingSelectedContractId || null,
    fieldTicketSettings: _asgSigSettings || null,
    updatedAt: Timestamp.now()
  };
  // TASK-9750 — the day's flags. Written only when set or when clearing a set
  // one, so an ordinary day gains no new keys. Ticked tentative → the server
  // stages no crew text for this write; unticked from tentative → confirmed,
  // and everyone on the day gets the normal assignment notice once.
  {
    // 🔴 A confirmed day that already has people on it can NOT go back to
    // tentative here (_assignmentTentativeLocked): its crew may have been told,
    // and a tentative day texts nobody — not even "cancelled" or "moved".
    const _tent = !!document.getElementById('assignment-tentative')?.checked
      && !_assignmentTentativeLocked(_asgJobDay);
    const _half = !!document.getElementById('assignment-half-day')?.checked;
    if (_tent || isTentativeJobDay(_asgJobDay)) assignmentData.tentative = _tent;
    if (_half || isHalfDayJobDay(_asgJobDay)) assignmentData.halfDay = _half;
    if (isTentativeJobDay(_asgJobDay) && !_tent) {
      assignmentData.confirmedAt = assignmentData.updatedAt;
      assignmentData.confirmedBy = window.AppState?.user?.uid || null;
    }
  }
  // TASK-9258 — same reconcile as the Edit Job writer, for the same reason: this
  // door changes assets/quantities on a day that may carry a template mark, and
  // a mark that survives the edit says nobody checked a number a person just
  // typed. `_asgJobDay` is the third resolve of this id in this function and the
  // one the write targets, so it is the right baseline to diff against.
  const _asgTplMeta = reconcileStoredTemplateMeta(_asgJobDay, selectedAssets, assetQtys, assetDirections);
  if (_asgTplMeta) assignmentData.templateMeta = _asgTplMeta;

  // ── TASK-4653 ──────────────────────────────────────────────────────────────
  // The per-day Assign Crew door gets the SAME ticketed-day guard the Edit Job
  // form got in TASK-4614 Slice B. It is the same act — rewriting who is on
  // record for a day's work — and until now it was the one crew door with NO
  // ticket check at all, so bypassing TASK-4614 needed no SDK, just the other
  // button. The product's own multi-day banner sends people here ("open that day
  // on the board and use Assign Crew", _renderJobMultiDayNote), and the audit log
  // says this is the busier path: 72 CREW_ASSIGN_UPDATE rows through here
  // (source:'dispatcher') vs 44 through the guarded Edit Job form.
  //
  // 🔴 PLACEMENT IS LOAD-BEARING. This sits above the try block on purpose:
  //   • the rental/inventory block below deducts consumable `assets.quantity`,
  //     writes assetTransactions and fires processRentalTransaction BEFORE the
  //     jobDay updateDoc. A guard at the write would refuse the crew change
  //     AFTER stock had already moved — a refused save that still consumed
  //     inventory, unwound by nothing.
  //   • it is also above the hard-stop gate, so a dispatcher is never made to
  //     type an expired-credential override reason for a save we then refuse.
  const _asgGuardDay = _resolveJobDayAnywhere(jobDayId);
  const _asgGuardJobId = _asgGuardDay?.jobId || document.getElementById('assignment-job-id')?.value || null;
  if (!_asgGuardDay || !_asgGuardJobId) {
    // 🔴 FAIL CLOSED — and deliberately NOT by leaning on the helper's default.
    // _checkTicketedDayCrewGuard returns {allow:true} when it cannot read a date
    // off what it is handed, so passing it an unresolved day would silently
    // PERMIT the write: the exact fail-open this task exists to close, wearing
    // the guard's own clothes. That {allow:true} is right in saveJob (the seed
    // day is known-good there, and legacy undated rows are a real population)
    // and wrong here, where a missing day means we do not know what we are
    // writing to. Nothing has been written at this point — the modal stays open.
    console.warn('[Dispatch] TASK-4653: jobDay not resolvable — refusing the crew write', { jobDayId });
    window.showToast?.("Couldn't read that day — nothing was saved. Refresh and try again.", 'error');
    return;
  }
  const _asgTGuard = await _checkTicketedDayCrewGuard(_asgGuardJobId, _asgGuardDay);
  let _asgWriteOk = _asgTGuard.allow === true;
  if (!_asgWriteOk && _asgTGuard.needsConfirm) {
    // Draft/ready ticket: the legitimate "fix yesterday's crew so the ticket
    // bills right" flow lives here, so it is a confirmation and not a wall —
    // same three outcomes and the same words saveJob uses, because it is the
    // same act. `?.` yielding undefined leaves this false: fail closed.
    _asgWriteOk = (await window.showConfirm?.(
      'That day is already ticketed',
      `${_asgTGuard.label} already has a ${_asgTGuard.detail}. Changing the crew here changes who is on record for that day's work, and the ticket's saved hours and totals do NOT recalculate.\n\nChange the crew on ${_asgTGuard.label} anyway?`,
      { confirmText: 'Change Crew', danger: true }
    )) === true;
  }
  if (!_asgWriteOk) {
    // Say WHICH day and WHY. A silent no-op here reads as "the app is broken",
    // and a refusal that cannot distinguish a real billed-day block from a
    // query that failed to run is the same thing from the dispatcher's seat and
    // completely different from ours — so the reason token goes in the MESSAGE.
    const _why = _asgTGuard.failedCheck
      ? `couldn't check whether ${_asgTGuard.label} is already ticketed — check your connection and try again`
      : _asgTGuard.blocked
        ? `${_asgTGuard.label} is already billed (${_asgTGuard.detail}). Unarchive that ticket in Billing if the crew is wrong`
        : `nothing was changed on ${_asgTGuard.label}`;
    window.showToast?.(`Assignment NOT saved — ${_why}.`, _asgTGuard.failedCheck ? 'error' : 'warning');
    const _reason = _asgTGuard.failedCheck ? 'unverifiable' : _asgTGuard.blocked ? 'billed' : 'declined';
    console.warn(`[Dispatch] TASK-4653: assignment refused on a ticketed past day (${_reason})`, {
      jobId: _asgGuardJobId, jobDayId, ticketDetail: _asgTGuard.detail || null
    });
    return;
  }
  // ───────────────────────────────────────────────────────────────────────────

  // TASK-10183 — someone ADDED on their regular day off (weekly rotation): warn, never block.
  // "Assign anyway" stores a one-day "working this day" exception, so the chip clears.
  // COD r1: the day's rotation window is LOADED first (a cold cache must not skip the warning),
  // and the exception is written only after the crew write succeeds (below, after the save).
  const _asgRotDate = _p811DayKey(_asgGuardDay.date, _p811Tz());
  const _asgPrevCrew = new Set([...(_asgGuardDay.drivers || []), ...(_asgGuardDay.helpers || [])].map(String));
  if (_asgRotDate && [...selectedDrivers, ...selectedHelpers].some(id => !_asgPrevCrew.has(String(id)))) await _rotationsReady(_asgRotDate);
  const _asgRotHits = _asgRotDate ? getTimeOffForDate(_asgRotDate).filter(r => r._rotation
    && [...selectedDrivers, ...selectedHelpers].includes(r.personId) && !_asgPrevCrew.has(r.personId)) : [];
  if (_asgRotHits.length) {
    const _rotLines = _asgRotHits.map(r => `${getPersonNameById(r.personId) || 'Someone'} is off — rotation, ${r.rotationLabel || 'regular day off'}.`).join('\n');
    const _rotYes = (await window.showConfirm?.('Regular day off', `${_rotLines}\n\nAssign anyway?`, { confirmText: 'Assign anyway' })) === true;
    if (!_rotYes) return;
  }

  // TASK-258 v1.15.535 — Hard-stop client enforcement on expired credentials.
  // Gate: tenant.compliance.hardStopExpiredCredentials === true (Chris-controlled
  // via Overlord, default OFF). When ON + any selected driver has expired CDL
  // or MedCard, OR any selected truck has expired AVI → blocking modal +
  // HARD_STOP_BLOCK audit. Helpers are not checked (no CDL/MedCard required
  // for helper roles; same convention as the existing checkDOTCompliance flow
  // which iterates `selectedDrivers` only).
  if (_hsIsHardStopEnabled()) {
    const driverObjs = selectedDrivers
      .map(id => DispatchState.people.find(p => p.id === id))
      .filter(Boolean);
    const truckObjs = selectedTrucks
      .map(id => DispatchState.trucks.find(t => t.id === id))
      .filter(Boolean);
    const blocked = _hsCollectExpiredForAssignment(driverObjs, truckObjs);
    if (blocked.length > 0) {
      try {
        window.logAudit?.('compliance', 'HARD_STOP_BLOCK', {
          source: 'dispatcher',
          attemptedAction: 'assignment',
          jobDayId,
          blockedCount: blocked.length,
          blocked: blocked.map(b => ({
            personId: b.personId,
            personName: b.personName,
            credentials: b.expired.map(e => e.kind),
            credentialDetail: b.expired.map(e => ({
              kind: e.kind,
              daysOverdue: e.daysOverdue,
              detail: e.detail || null
            }))
          })),
          at: new Date().toISOString()
        });
      } catch (err) { console.warn('[Dispatch] HARD_STOP_BLOCK audit failed:', err); }
      const result = await _hsShowAssignmentBlockModal(blocked);
      // TASK-258 — admin override path. Admin/owner/founder may proceed with
      // a written reason; everyone else stays blocked.
      if (!result?.proceeded) {
        return;
      }
      try {
        window.logAudit?.('compliance', 'HARD_STOP_OVERRIDE', {
          source: 'dispatcher',
          attemptedAction: 'assignment',
          jobDayId,
          blockedCount: blocked.length,
          blocked: blocked.map(b => ({
            personId: b.personId,
            personName: b.personName,
            credentials: b.expired.map(e => e.kind),
            credentialDetail: b.expired.map(e => ({
              kind: e.kind,
              daysOverdue: e.daysOverdue,
              detail: e.detail || null
            }))
          })),
          overrideReason: result.reason,
          overrideBy: window.AppState?.user?.email || 'unknown',
          at: new Date().toISOString()
        });
      } catch (err) { console.warn('[Dispatch] HARD_STOP_OVERRIDE audit failed:', err); }
    }
  }

  // TASK-9227 — compliance flag on the picker's own save path. Runs AFTER the
  // TASK-258 hard stop on purpose: that gate can refuse outright, and asking
  // about a follow-up flag on an assignment that is about to be refused for an
  // expired CDL is noise stacked on a wall. HELPERS ARE INCLUDED here even
  // though the hard stop excludes them by documented convention above — the
  // helper slot is precisely how a flagged person reached a day unchecked.
  const _dfAsgGate = await _dfConfirmSeating([...selectedDrivers, ...selectedHelpers], {
    surface: 'assignment-modal',
    jobId: _asgGuardJobId || null,
    jobDayId,
  });
  if (!_dfAsgGate.ok) {
    window.showToast?.('Assignment not saved — nothing was changed.', 'info');
    return;
  }

  // TASK-4810 — the UPPER bound, on the Assign Crew door (the busier one). A
  // rental arriving on this day as a deploy (newly added, or flipped
  // return→deploy) that would put more out than is owned is asked about BEFORE
  // any stock moves. Warn, never block — "Save Anyway" always proceeds.
  const _asgPrevAssets = _asgGuardDay.assets || [];
  const _asgPrevDirs = _asgGuardDay.assetDirections || {};
  // A pre-ledger return→deploy flip reverses the return (+qty) and deploys
  // (+qty); a ledger job's flip nets about qty. Unknown ledger state reads as
  // pre-ledger — the larger number, so the warning errs toward asking.
  const _asgLedgerKnown = _jobHasRentalLedger(_resolveJobAnywhere(_asgGuardJobId));
  const _asgOverDeploys = rentalOverDeploys(DispatchState.assets, selectedAssets
    .filter(id => (DispatchState.pendingAssetDirections[id] || 'deploy') === 'deploy'
      && (!_asgPrevAssets.includes(id) || (_asgPrevDirs[id] || 'deploy') !== 'deploy'))
    .map(id => {
      const qty = DispatchState.pendingAssetQtys[id] || 1;
      const flipped = _asgPrevAssets.includes(id);
      return { assetId: id, qty, move: (flipped && !_asgLedgerKnown) ? qty * 2 : qty };
    }));
  if (_asgOverDeploys.length > 0
    && !(await _confirmRentalOverDeploy(_asgOverDeploys, { source: 'saveAssignment', jobId: _asgGuardJobId || null, jobDayId }))) {
    window.showToast?.('Assignment not saved — nothing was changed.', 'info');
    return;
  }
  // TASK-10106 S1b (COD r1) — confirming a driver's rental ask: re-check it
  // is still ours to answer BEFORE the first stock move below.
  if (DispatchState.pendingAssetRequest && !(await _rentalReqRecheckBeforeSave(jobDayId, selectedAssets))) return;

  try {
    // Deduct inventory for newly assigned quantity-tracked assets
    //
    // ── TASK-4655 ────────────────────────────────────────────────────────────
    // 🔴 MONEY/STOCK. This is the BEFORE side of the inventory diff, and it must
    // be the same day the modal was opened on. showAssignment resolves through
    // _resolveJobDayAnywhere (:10388) which includes searchJobDays; this line
    // used to re-resolve against DispatchState.jobDays alone — the live array,
    // windowed to `date >= adminCutoff` (default 90 days back, :3332).
    //
    // So for a job opened from SEARCH and older than that window, the lookup
    // returned undefined, previousAssets fell back to [], and EVERY asset
    // already on the day was classified `newlyAdded`: consumables deducted
    // a second time from assets.quantity, rentals re-deployed through
    // processRentalTransaction, and an assetTransactions row written for a
    // movement that never physically happened. Nothing threw. The count was
    // just wrong, and the ledger agreed with the wrong count.
    //
    // The undefined also poisoned the two lines below it: `jobDay?.jobId ||
    // jobDayId` passed a JOBDAYS id into processRentalTransaction's jobId
    // parameter, so the rental row pointed at a doc in the wrong collection.
    //
    // RE-RESOLVE, then fall back to _asgGuardDay (COD Code Reviewer ⚠️).
    // _asgGuardDay is captured above the ticket-guard await, the draft/ready
    // confirm, and the hard-stop credential modal — all of which can sit open
    // while a snapshot replaces DispatchState.jobDays wholesale (:3345). The
    // pre-fix line ran its own .find() down HERE, so it read post-await state;
    // reusing the pre-await snapshot would have quietly traded this bug for a
    // staler `previousAssets` on a money surface. Re-resolving keeps the old
    // freshness exactly and adds only the searchJobDays arm that was missing.
    //
    // The `||` can never actually fire — TASK-4653's fail-closed gate returned
    // if this id did not resolve, and searchJobDays is a one-shot cache that is
    // never emptied — but it is what makes `jobDay` non-null by construction
    // rather than by argument, so previousAssets can never silently become [].
    // Both arms resolve the same DOC ID, so the guard and the diff can still
    // never be vetting different days.
    const jobDay = _resolveJobDayAnywhere(jobDayId) || _asgGuardDay;
    const previousAssets = jobDay?.assets || [];
    const previousDirections = jobDay?.assetDirections || {};
    const newlyAdded = selectedAssets.filter(id => !previousAssets.includes(id));

    // TASK-9336 — does this day's job keep a rental ledger? Read BEFORE the
    // first write below (the consumable batch), so a failed read refuses the
    // save with nothing written instead of stranding a deducted batch. Only
    // read when a rental is actually moving on this save.
    const _asgJobId = jobDay?.jobId || null;
    const _asgIsRental = (id) => {
      const a = DispatchState.assets.find(x => x.id === id);
      return !!a && (a.quantityType === 'rental' || a.isRental);
    };
    const _asgRentalMoving = selectedAssets.some(id => _asgIsRental(id) && (
        !previousAssets.includes(id)
        || (previousDirections[id] || 'deploy') !== (DispatchState.pendingAssetDirections[id] || 'deploy')
        // TASK-10015 — a quantity edit moves stock on a ledger job too.
        || Math.max(1, Number(jobDay?.assetQtys?.[id]) || 1) !== Math.max(1, Number(DispatchState.pendingAssetQtys?.[id]) || 1)))
      || previousAssets.some(id => !selectedAssets.includes(id) && _asgIsRental(id));
    let _asgLedger = false;
    if (_asgJobId && _asgRentalMoving) {
      try {
        const _js = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', _asgJobId));
        _asgLedger = _js.exists() && _jobHasRentalLedger(_js.data());
      } catch (ledErr) {
        console.warn('[Dispatch] TASK-9336: job read for the rental ledger failed — refusing the save:', ledErr);
        window.showToast("Couldn't load this job's rental record — nothing was saved. Try again.", 'error');
        return;
      }
    }

    // ── TASK-4656 ────────────────────────────────────────────────────────────
    // 🔴 The deduct and its ledger row were two independently-failable awaits,
    // and the IRREVERSIBLE one ran FIRST. addDoc rejects any `undefined` field
    // value, so an asset doc with no `partNumber` threw on the ledger write
    // AFTER assets.quantity had already been decremented: stock silently down,
    // no row explaining why, and the operator got a bare "Error saving
    // assignment". roster.js:12426 always writes partNumber, so UI-created
    // assets never hit it — imported/legacy ones can (49 of 152 live asset docs
    // have no partNumber, measured 2026-08-27).
    //
    // Defaulting partNumber alone would only close THIS field. The next field
    // that goes missing reintroduces the identical half-applied write, because
    // the shape of the bug is two writes that can fail apart, not one bad value.
    // So the pair goes into ONE batch — Firestore applies a batch entirely or
    // not at all, and it is all-or-nothing against firestore.rules too, so a
    // rules refusal can no longer land the deduct without the ledger either.
    //
    // COD Code Reviewer ⚠️ — and the LOOP gets one batch, not one per asset.
    // Per-asset batches fixed the pair but left the same defect one level up:
    // five consumables where the third throws would leave two permanently
    // deducted with the jobDay update below never running, so the board would
    // not even show them as assigned. Collect first, commit once.
    //
    // Belt and braces: still default the two free-text fields. Atomicity alone
    // would turn a legacy asset from "silently mis-counted" into "impossible to
    // dispatch" — batch.set() validates eagerly and throws at .set(), before
    // commit, so nothing is written (fail-closed, the correct half to fail on)
    // but the dispatcher is walled out of a legitimate save with no way forward.
    // `|| ''` matches every other assetTransactions writer in the app
    // (roster.js:13042/13133, dispatch.js:9312, :11746) so rentalBilling.js and
    // the history views keep reading one shape.
    const _consumableAdds = [];
    for (const assetId of newlyAdded) {
      const asset = DispatchState.assets.find(a => a.id === assetId);
      // TASK-9720 — a stock-tracked item (PPE "Track stock") is counted by the
      // server ledger only; firestore.rules refuses a client quantity write on
      // it, which would fail this whole batch. Dispatch does not move it (S8).
      if (asset && asset.consumable && !asset.stockTracked) {
        const qty = DispatchState.pendingAssetQtys[assetId] || 1;
        const prevQty = asset.quantity || 0;
        const newQty = prevQty - qty;
        if (newQty < 0) {
          window.showToast(`Warning: ${asset.name} will go to ${newQty} (negative inventory)`, 'warning');
        }
        _consumableAdds.push({ asset, assetId, qty, prevQty, newQty });
      }
    }
    // A Firestore batch caps at 500 writes and each asset costs 2 (the deduct +
    // its ledger row), so 240 assets is the most that can commit atomically.
    //
    // COD Code Reviewer ⚠️ round 2 — REFUSE above that rather than chunking.
    // Chunking would keep large saves working, but it would do so by silently
    // reintroducing exactly the defect this task exists to remove: chunk 1
    // commits, chunk 2 throws, and stock has moved for 240 assets with no
    // assignment saved. A guarantee that holds "unless you cross an invisible
    // line" is not a guarantee. Refusing keeps it stateable in one sentence:
    // either every consumable on this save commits, or none of them do.
    //
    // Nothing has been written at this point — the consumable batch below is the
    // first write in the whole try block — so this returns cleanly with the
    // modal open and the dispatcher's selections intact. What it blocks: a
    // single job day carrying more than 240 DISTINCT consumable part numbers.
    // The largest real day measured across all live tenants carries 4.
    const _INV_MAX = 240;
    if (_consumableAdds.length > _INV_MAX) {
      console.warn('[Dispatch] TASK-4656: consumable count exceeds one atomic batch — refusing', { count: _consumableAdds.length });
      window.showToast(
        `Too many consumable items on one day (${_consumableAdds.length}, max ${_INV_MAX}) — nothing was saved. Split them across days.`,
        'error');
      return;
    }
    // TASK-10067 — one transaction re-reads each asset and deducts from the
    // STORED count (a cached absolute write re-added org-transferred units).
    // Deduct + ledger row still land together; the helper updates the local
    // cache only after the commit lands.
    if (_consumableAdds.length > 0) {
      await _deductConsumablesTx(DispatchState.tenantId, _consumableAdds, (it) => ({
        assetId: it.assetId,
        assetName: it.asset.name || it.asset.partNumber || '',
        partNumber: it.asset.partNumber || '',
        type: 'dispatch', quantity: it.qty,
        // TASK-9575 slice D — no unitCost / totalCost on a ledger row: every driver session reads this collection; cost lives in assetCosts (TASK-9730 owns the real cost stamp).
        jobDayId, note: `Dispatched to job`,
        issuedBy: window.AppState?.user?.email || 'unknown',
        createdAt: Timestamp.now()
      }));
    }

    // TASK-9336 — LEDGER JOB: the rentals coming off / going onto this day
    // settle against the job ledger (_asgSettleRentalsLedger), never against
    // this day's own copied qty.
    // TASK-9396 — single-tracked units another job still holds. The deploy
    // transaction refuses them with nothing written; they come off this day's
    // payload below (a day that lists a unit bills it) and the toast names the
    // job that has each one. A flipped (return→deploy) unit keeps its old
    // 'return' direction instead of being dropped.
    const _asgUnitRefused = [];
    let _asgSettled = null;
    if (_asgLedger) {
      _asgSettled = await _asgSettleRentalsLedgerTagged({
        tenantId: DispatchState.tenantId, jobId: _asgJobId, jobDayId,
        previousAssets, previousDirections, selectedAssets,
        pendingDirections: DispatchState.pendingAssetDirections, pendingQtys: DispatchState.pendingAssetQtys,
        isRental: _asgIsRental, overDeploys: _asgOverDeploys,
        // TASK-10015 — quantity edits move stock; a later-dated pickup waits for its day.
        previousQtys: jobDay?.assetQtys || {}, pickupDateKey: _rentalDayKeyOf(jobDay),
      });
      _asgUnitRefused.push(...((_asgSettled && _asgSettled.refused) || []));
    }

    // Handle rental deploy/return for newly added rental assets (pre-ledger jobs only)
    if (!_asgLedger) {
    for (const assetId of newlyAdded) {
      const asset = DispatchState.assets.find(a => a.id === assetId);
      const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
      if (!isRental) continue;
      const _r = await processRentalTransaction(asset, DispatchState.pendingAssetDirections[assetId] || 'deploy', DispatchState.pendingAssetQtys[assetId] || 1, jobDay?.jobId || jobDayId, false,
        { jobDayId, txStamp: rentalOverDeployStamp(_asgOverDeploys, assetId) });
      if (_r && _r.refused) _asgUnitRefused.push({ assetId, name: asset.name || asset.partNumber || assetId, heldByJobId: _r.heldByJobId, flipped: false });
    }
    }

    // Handle direction changes for existing rental assets (pre-ledger jobs only)
    for (const assetId of (_asgLedger ? [] : selectedAssets)) {
      if (newlyAdded.includes(assetId)) continue; // already handled above
      const asset = DispatchState.assets.find(a => a.id === assetId);
      const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
      if (!isRental) continue;
      const oldDir = previousDirections[assetId] || 'deploy';
      const newDir = DispatchState.pendingAssetDirections[assetId] || 'deploy';
      if (oldDir !== newDir) {
        // Reverse old transaction, apply new
        const qty = DispatchState.pendingAssetQtys[assetId] || 1;
        await processRentalTransaction(asset, oldDir === 'deploy' ? 'return' : 'deploy', qty, jobDay?.jobId || jobDayId, true);
        const _r = await processRentalTransaction(asset, newDir, qty, jobDay?.jobId || jobDayId, false,
          { jobDayId, txStamp: newDir === 'deploy' ? rentalOverDeployStamp(_asgOverDeploys, assetId) : {} });
        if (_r && _r.refused) _asgUnitRefused.push({ assetId, name: asset.name || asset.partNumber || assetId, heldByJobId: _r.heldByJobId, flipped: true });
      }
    }

    // Handle removed rental assets — reverse their transactions (pre-ledger jobs only)
    const removedAssets = _asgLedger ? [] : previousAssets.filter(id => !selectedAssets.includes(id));
    for (const assetId of removedAssets) {
      const asset = DispatchState.assets.find(a => a.id === assetId);
      const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
      if (!isRental) continue;
      const oldDir = previousDirections[assetId] || 'deploy';
      const oldQty = jobDay?.assetQtys?.[assetId] || 1;
      await processRentalTransaction(asset, oldDir === 'deploy' ? 'return' : 'deploy', oldQty, jobDay?.jobId || jobDayId, true);
    }


    // TASK-4810 (COD 2026-09-29) — a QUANTITY change on a rental already on this
    // day (same direction) is written to the day but moves no stock on either
    // path above (added / removed / flipped do). Name it after the write lands.
    // TASK-10015 — a LEDGER job's quantity edit now moves stock
    // (_asgSettleRentalsLedger above); only an older job with no ledger warns.
    const _asgQtyNoMove = _asgLedger ? [] : rentalChangesNotMovingStock(
      { assets: previousAssets, assetQtys: jobDay?.assetQtys || {}, assetDirections: previousDirections },
      { assets: selectedAssets, assetQtys, assetDirections }, DispatchState.assets
    ).filter(c => c.change === 'quantity');

    // TASK-9396 — a refused unit never reaches the day: newly added → off the
    // list (and its qty/direction); flipped → back to the direction it had.
    for (const r of _asgUnitRefused) {
      if (r.flipped) {
        if ((previousDirections[r.assetId] || 'deploy') === 'return') assignmentData.assetDirections[r.assetId] = 'return';
        else delete assignmentData.assetDirections[r.assetId];
        continue;
      }
      assignmentData.assets = assignmentData.assets.filter(id => id !== r.assetId);
      delete assignmentData.assetQtys[r.assetId];
      delete assignmentData.assetDirections[r.assetId];
      delete assignmentData.lineItemSelections[`asset:${r.assetId}`];
    }

    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId), _rentalDayWriteWithoutSettledQtys(assignmentData, _asgSettled));
    // TASK-10106 S1b — a driver's rental request this save confirmed: marked
    // answered only now (day write landed), only if the rental is on the day
    // and was not refused, and only while its rev is unchanged.
    if (DispatchState.pendingAssetRequest) await _rentalReqResolveAfterSave(jobDayId, assignmentData, _asgUnitRefused);

    if (_asgUnitRefused.length > 0) {
      console.warn('[Rental] TASK-9396 single-tracked unit(s) held by another job — not deployed', { jobDayId, refused: _asgUnitRefused });
      window.showToast?.(
        `Saved, but not deployed: ${_asgUnitRefused.map(r => _rentalUnitRefusedMsg(r.name, r.heldByJobId)).join('; ')}. A rental unit stays on the job that has it until it is returned there.`,
        'warning', null, 12000);
      // TASK-10072 — then the choice: move each held unit onto this day from the job that has it.
      _offerRentalUnitMoves(_asgUnitRefused, { jobId: jobDay?.jobId || null, jobDayId })
        .catch((err) => console.warn('[Rental] TASK-10072 move offer failed', err));
    }

    if (_asgQtyNoMove.length > 0) {
      console.warn('[Rental] TASK-4810 Assign Crew qty change did not move stock', { jobDayId, changes: _asgQtyNoMove });
      window.showToast?.(
        `Rental out-counts not updated for a quantity change (${_asgQtyNoMove.map(c => c.name).join(', ')}). If the out count is now wrong, recount in Roster › Assets.`,
        'warning', null, 12000);
    }

    // TASK-4655 (integrated-diff COD) — clear the retry state ONLY after the write
    // that can fail has actually landed. These two lines used to run BEFORE the
    // updateDoc, so a failed save left stock already moved, the modal open, and the
    // pending qty/direction maps EMPTY — the user's retry then computed a different
    // diff than the one they were looking at. A refusal you can retry is fine; a
    // refusal that quietly changes what retrying does is not.
    DispatchState.pendingAssetQtys = {};
    DispatchState.pendingAssetDirections = {};
    // CREW_ASSIGN_UPDATE — red-flag audit so we can forensically reconstruct
    // "driver vanished off the board" complaints. Counts + before/after id arrays
    // + cleared/swap detection. Keep payload bounded (id arrays, not full rows).
    try {
      const prevDrivers = Array.isArray(jobDay?.drivers) ? jobDay.drivers : [];
      const prevHelpers = Array.isArray(jobDay?.helpers) ? jobDay.helpers : [];
      const prevTrucks = Array.isArray(jobDay?.trucks) ? jobDay.trucks : [];
      const prevTrailers = Array.isArray(jobDay?.trailers) ? jobDay.trailers : [];
      const nextDrivers = assignmentData.drivers || [];
      const nextHelpers = assignmentData.helpers || [];
      const nextTrucks = assignmentData.trucks || [];
      const nextTrailers = assignmentData.trailers || [];
      const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));
      const swappedAll = prevDrivers.length > 0 && nextDrivers.length > 0 && !sameSet(prevDrivers, nextDrivers) &&
                        prevDrivers.every(id => !nextDrivers.includes(id));
      window.logAudit?.('crew', 'CREW_ASSIGN_UPDATE', {
        source: 'dispatcher',
        jobDayId,
        jobId: jobDay?.jobId || null,
        before: {
          drivers: prevDrivers, helpers: prevHelpers, trucks: prevTrucks, trailers: prevTrailers,
          driverCount: prevDrivers.length, truckCount: prevTrucks.length
        },
        after: {
          drivers: nextDrivers, helpers: nextHelpers, trucks: nextTrucks, trailers: nextTrailers,
          driverCount: nextDrivers.length, truckCount: nextTrucks.length
        },
        redFlags: {
          driverCountDrop: nextDrivers.length < prevDrivers.length,
          truckCountDrop: nextTrucks.length < prevTrucks.length,
          crewCleared: prevDrivers.length > 0 && nextDrivers.length === 0,
          swappedAll
        },
        previousUpdatedAt: jobDay?.updatedAt || null
      });
    } catch (auditErr) { console.warn('[Dispatch] CREW_ASSIGN_UPDATE audit failed:', auditErr); }
    // Legacy ASSIGNMENT_CHANGE entry preserved for backward-compat with existing
    // audit-viewer queries; remove once viewer migrates to CREW_ASSIGN_UPDATE.
    window.logAudit?.('dispatch', 'ASSIGNMENT_CHANGE', {
      jobDayId, jobId: assignmentData.jobId || null,
      drivers: assignmentData.drivers, helpers: assignmentData.helpers,
      trucks: assignmentData.trucks, trailers: assignmentData.trailers
    });
    window.showToast('Assignment saved', 'success');
    // TASK-10183 — "Assign anyway" on a rotation day: the one-day working exception, only now the crew is saved.
    for (const r of _asgRotHits) {
      window.DDRotations?.markWorking(r.personId, _asgRotDate).catch((err) => {
        console.warn('[Dispatch] TASK-10183 working-day exception not saved', err?.message || err);
        window.showToast?.(`Saved, but ${getPersonNameById(r.personId) || 'they'} still shows ROTATION that day (${err?.message || 'not saved'}).`, 'warning');
      });
    }
    closeModal('assignment');
  } catch (error) {
    console.error('[Dispatch] Save assignment error:', error);
    // TASK-10106 S1b (COD r4) — a save that THREW may have moved stock before
    // it failed, so the consumed ask is NOT handed back here (that could let it
    // deploy twice). It stays 'being deployed' and reopens after the 2-minute
    // window; say so, so the office checks the count before confirming again.
    if (DispatchState.pendingAssetRequest) {
      window.showToast?.(`The driver's request for ${DispatchState.pendingAssetRequest.name} is on hold for 2 minutes. Check that rental's count on this day before you confirm it again.`, 'warning', null, 12000);
    }
    // TASK-1331 — the add-day paths now open this editor, and jobDays create/update are
    // gated differently: create allows any `dispatch` member (firestore.rules:1815),
    // update additionally requires !isViewUploadOnly (:1830-1832). So a viewUploadOnly
    // member can add the day and then be refused on save. Name that instead of a generic
    // "Error saving" the operator can only read as "the app is broken".
    if (error?.code === 'day-changed') {
      window.showToast(error.message, 'error');
    } else if (error?.code === 'permission-denied' && error?._rentalSettle) {
      // TASK-10015 — the rental half (now also quantity edits) moves stock,
      // which the rules allow only to a dispatcher+ / Roster holder.
      window.showToast(_rentalWriteRefusedMsg(error, 'save that rental change'), 'error');
    } else if (error?.code === 'permission-denied') {
      window.showToast("You don't have permission to change crew on this day — the day was added, ask an admin to assign it", 'error');
    } else {
      window.showToast('Error saving assignment', 'error');
    }
  }
}

// TASK-4812 — the three rules the demoted Add Day controls need, injected ONCE.
// They live here rather than in styles.css because this task's surface is
// dispatch.js only; every selector is scoped to classes this file owns. Same
// pattern as TASK-4740 and TASK-4745 in billing.js.
//
// 🔴 NO BACKTICKS ANYWHERE INSIDE THE TEMPLATE LITERAL BELOW, comments included.
// TASK-4745 hit exactly that: a backtick in a CSS comment closed the template
// early, node --check still passed, and the whole injected stylesheet silently
// became a fragment. It fails open and nothing throws.
//
//  1. [hidden] actually hiding. styles.css:1638 sets .btn{display:inline-flex}
//     and :21236 sets .job-action-btn{display:flex}, both of which OUTRANK the
//     UA stylesheet's [hidden]{display:none} (author origin beats user-agent).
//     Measured in a browser against the real stylesheet: a
//     .job-action-btn[hidden] computes display:flex and lays out 618px wide. So
//     the "hidden" Add Day the view-and-upload branch emits has been rendering
//     for members who cannot add a day at all — the loudest control in the modal,
//     shown to the one role that can only be refused by it. Scoped here to the
//     two classes dispatch.js owns. 🔴 The same collision exists app-wide for
//     every .btn[hidden]; that is a styles.css fix and a separate task.
//  2. The duplicate-warning action row wraps, and its two buttons stay the same
//     height. "No, this is a different job" is a much longer label than
//     "Dismiss": MEASURED without wrap, the pair stops shrinking at its
//     min-content width and overflows its own card by 13px at a 330px column and
//     83px at 260px. Wrap fixes that, but a wrapping row sizes each button to its
//     own content, which left them 8px apart (the material icon's 24px line box
//     against a 16px text line). The icon size plus a shared min-height puts them
//     back on one baseline — two symmetric choices have to LOOK symmetric.
//  3. Touch targets, keyed on (pointer: coarse) and NOT a viewport width — a
//     cab tablet at 1024 needs 44px and a 1024px mouse window does not. Measured
//     before this change: the two duplicate-warning buttons were 36px at 1024
//     coarse, and the Edit Job footer's secondaries drop to 40px under
//     styles.css:9691 (.btn-secondary{min-height:40px!important}, inside a
//     max-width:768px query). That rule carries !important, so these must too or
//     a 768-and-under coarse device keeps the 40px — verified by measurement,
//     not assumed: a non-important 44px was observed losing to it at 370px wide.
function _ensureAddDayWeightStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('dispatch-addday-weight-styles')) return;
  const el = document.createElement('style');
  el.id = 'dispatch-addday-weight-styles';
  el.textContent = `
.job-details-actions .job-action-btn[hidden],
.overflow-dropdown .overflow-item[hidden] { display: none !important; }
.smart-warning-actions { flex-wrap: wrap; }
.smart-warning-actions .btn { min-height: 32px; }
.smart-warning-actions .btn .material-icons { font-size: 18px; }
@media (pointer: coarse) {
  .modal-footer-edit-actions .btn { min-height: 44px !important; }
  .smart-warning-actions .btn { min-height: 44px !important; }
}`;
  document.head.appendChild(el);
}

// TASK-1111 SLICE B / TASK-4614 — multi-day linkage note in the EDIT-job form.
// The shared template fields (title, customer, dates, PO, notes) are inherently
// job-wide, so editing here changes every day of the job; CREW is not, and since
// TASK-4614 saveJob writes it to the opened day only. The banner's job is to draw
// that line — it used to claim everything reached every day, which was true of the
// code and wrong for the product. Injected above the form fields; hidden for
// new-job / single-day.
function _renderJobMultiDayNote(jobId, seedDay) {
  _ensureAddDayWeightStyles(); // TASK-4812 — runs on EVERY job-form open (new + edit)
  const anchor = document.getElementById('job-title')?.closest('.form-group') || document.getElementById('job-title');
  let note = document.getElementById('job-multiday-note');
  // COD Code Reviewer ⚠️ — count across BOTH pools. A finished job opened from
  // Search has its days in searchJobDays only, so counting the live (90-day
  // windowed) array alone hid the multi-day banner on exactly the jobs whose
  // days _pickEditJobSeedDay can still seed from.
  const _seenDays = new Map();
  if (jobId) {
    for (const pool of [DispatchState.jobDays, DispatchState.searchJobDays]) {
      // Live jobDays wins over the search cache for the same id (first write kept),
      // which is the same precedence the rest of the file uses.
      for (const d of (pool || [])) if (d && d.jobId === jobId && !_seenDays.has(d.id)) _seenDays.set(d.id, d);
    }
  }
  const dayCount = _seenDays.size;
  // TASK-4811 — the field-level scope markers ride this exact same count, so the
  // banner and the chips can never disagree about how many days this job has.
  // Called on EVERY open including the single-day and new-job paths, because that
  // call is what CLEARS a previous job's chips out of the reused form.
  const rangeLabel = _formatJobDayRange(Array.from(_seenDays.values()));
  _renderJobWideScopeMarkers(dayCount, rangeLabel);
  if (dayCount < 2) { if (note) note.style.display = 'none'; return; }
  if (!note && anchor && anchor.parentNode) {
    note = document.createElement('div');
    note.id = 'job-multiday-note';
    note.style.cssText = 'margin:0 0 12px;padding:10px 12px;border-radius:8px;background:rgba(59,130,246,0.08);border:1px solid rgba(59,130,246,0.25);font-size:0.82rem;color:var(--text-primary);line-height:1.45;';
    anchor.parentNode.insertBefore(note, anchor);
  }
  if (note) {
    note.style.display = 'block';
    // TASK-4614 A4 — the old banner claimed every change here reached every day,
    // and offered Copy to New Job as the only escape. Both were wrong: crew
    // is per-day (each jobDay owns its own arrays; Add Day COPIES, saveAssignment
    // writes ONE day), and copying forks the job in two and splits its ticket
    // history. Say exactly which half is job-wide and which half is not.
    const label = _formatJobDayLabel(seedDay);
    const scope = label ? escapeHtml(label) : 'the day you opened';
    // TASK-4811 — name the actual dates. "runs 2 days" does not tell a dispatcher
    // that one of those days is YESTERDAY's finished work he is about to overwrite.
    const range = rangeLabel ? ` (${escapeHtml(rangeLabel)})` : '';
    note.innerHTML = `<strong>This job runs ${dayCount} days${range}.</strong> Title, customer, dates, PO and notes apply to all ${dayCount}.`
      + `<br><strong>Crew &amp; equipment below apply to ${scope} only.</strong> To change another day, open that day on the board and use Assign Crew.`;
  }
}

// TASK-4811 — compact "Sep 8 – Sep 9" for a set of jobDay docs. Returns '' when no
// day carries a usable date, and a single date when every day lands on one. jobDay.date
// is anchored to NOON local on purpose, so this renders the intended calendar day in
// every US timezone (memory/lesson_jobday_date_anchors_to_noon_local) — do not switch
// this to a UTC formatter.
function _formatJobDayRange(days) {
  const stamps = (days || []).map(_jobDayDateMs).filter(ms => ms !== null);
  if (stamps.length === 0) return '';
  const lo = new Date(Math.min(...stamps));
  const hi = new Date(Math.max(...stamps));
  try {
    // Years only appear when the job actually spans a year boundary; a same-year
    // range reads as noise with them and dispatchers are looking at this mid-typing.
    const sameYear = lo.getFullYear() === hi.getFullYear();
    const opts = sameYear ? { month: 'short', day: 'numeric' } : { year: 'numeric', month: 'short', day: 'numeric' };
    const a = lo.toLocaleDateString('en-US', opts);
    const b = hi.toLocaleDateString('en-US', opts);
    return a === b ? a : `${a} – ${b}`;
  } catch (e) { return ''; }
}

// TASK-4811 — the fix, and the reason it is not a label tweak: Notes, title, PO,
// location, waypoints and POC are stored on jobs/{id}. A jobDay carries only
// date/drivers/helpers/trucks/trailers/assets/status/selectedContractId/
// fieldTicketSettings — verified against the live docs on the job this was reported
// from; neither jobDay had a `notes` key at all. But the dispatcher reaches these
// fields THROUGH a day (click a day row -> Edit Job), and TASK-1331/TASK-4614 have
// been teaching per-day scoping right next to them, so the form reads per-day and is
// not. The banner at the top of the form says so and gets scrolled past. These
// markers put the scope on the field itself, in the dispatcher's eye line while they
// are typing into it.
//
// Deliberately NOT a data-model change: making notes per-day is a migration plus a
// ticket-snapshot change (jobNotes is copied at clock.js and billing.js) and is
// Chris's call, filed as TASK-4811 Slice B.
function _renderJobWideScopeMarkers(dayCount, rangeLabel) {
  const form = document.getElementById('form-job');
  const chips = form ? form.querySelectorAll('[data-job-scope]') : [];
  const warn = document.getElementById('job-notes-scope-warning');
  // Single-day and new jobs: clear everything. This branch is load-bearing — the
  // job form is one reused DOM node, so a stale chip from the last multi-day job
  // would otherwise sit there lying about a single-day job.
  // `typeof === 'number'` on purpose: a STRING count would coerce through `>= 2`
  // and render a confident "All 2 days" from a caller that has lost its type.
  // The only real caller passes a Map size; anything else is a bug upstream and
  // must render nothing rather than a plausible number.
  if (!(typeof dayCount === 'number' && dayCount >= 2)) {
    chips.forEach(c => { c.style.display = 'none'; c.textContent = ''; c.removeAttribute('title'); });
    if (warn) { warn.style.display = 'none'; warn.innerHTML = ''; }
    return;
  }
  const range = rangeLabel ? ` (${rangeLabel})` : '';
  chips.forEach(c => {
    c.textContent = `All ${dayCount} days`;
    // Tooltips do not exist on a cab tablet, so this is a bonus for mouse users
    // only — the text a finger-driven dispatcher needs is in the chip and in the
    // Notes callout below, never only in a title attribute.
    c.title = `Job-wide — the same on every day of this job${range}`;
    // text-transform:none is not decoration — the Waypoints chip sits inside an
    // h4 that the form-section rule uppercases, so without it one chip of the six
    // reads ALL 2 DAYS and the set stops looking like one control. Measured in a
    // real browser off the computed style: 6.16:1, AA for this size on light.
    c.style.cssText = 'display:inline-block;margin-left:6px;padding:1px 8px;border-radius:9px;'
      + 'background:#EFF6FF;border:1px solid #BFDBFE;color:#1D4ED8;'
      + 'font-size:0.7rem;font-weight:600;letter-spacing:0.01em;text-transform:none;'
      + 'vertical-align:middle;white-space:nowrap;';
  });
  if (warn) {
    // Amber, matching the "This day is already ticketed" callout in showAssignment —
    // the established idiom in this file for "this reaches further than you think".
    // The top-of-form note is blue; a second blue block here would read as the same
    // message repeated and get skipped the same way.
    warn.style.cssText = 'display:flex;gap:8px;align-items:flex-start;margin:0 0 8px;padding:10px 12px;'
      + 'border-radius:8px;background:rgba(245,158,11,0.10);border:1px solid rgba(245,158,11,0.35);'
      + 'font-size:0.82rem;color:var(--text-primary);line-height:1.45;';
    warn.innerHTML = `<span class="material-icons" style="font-size:18px;color:#B45309;flex-shrink:0;">event_repeat</span>`
      + `<span><strong>One note for all ${dayCount} days${escapeHtml(range)}.</strong> There is no separate note per day &mdash; `
      + `what you save here replaces the note on every day of this job, including days already worked. `
      + `To leave a note about one day only, put the date in the text.</span>`;
  }
}

// TASK-4614 — the crew section header hint. Read literally "Assigned to all days
// of this job", which was the behaviour this task exists to kill.
function _renderJobCrewScopeHint(seedDay) {
  const hint = document.getElementById('job-crew-scope-hint');
  if (!hint) return;
  if (!seedDay) {
    hint.textContent = 'Assigned to all days of this job';
    return;
  }
  const label = _formatJobDayLabel(seedDay);
  const base = label ? `Applies to ${label} only` : 'Applies to the day you opened only';
  // TASK-4614 SLICE B — say it BEFORE the save, not only in the refusal toast.
  // This is ADVISORY and cheap: it reads the already-streamed ticket list rather
  // than firing a query on every Edit Job open. That list is 90-day windowed, so
  // its SILENCE proves nothing — the authoritative check is the tenant-scoped
  // query in _checkTicketedDayCrewGuard at save time, which is what actually
  // refuses. Never invert this: an absent warning must not read as "safe" —
  // the stream's projection (:3517) also drops originalWorkDate, so a ticket
  // moved off this day matches at save time and not here.
  hint.textContent = _seedDayLooksTicketed(seedDay) ? `${base} — already ticketed` : base;
}

// Advisory only — see the caller. Past days only, mirroring the guard's scope.
function _seedDayLooksTicketed(seedDay) {
  try {
    const ms = _jobDayDateMs(seedDay);
    if (ms === null || !seedDay?.jobId) return false;
    const dayStr = formatDateISO(new Date(ms));
    if (dayStr >= formatDateISOLocal(new Date())) return false;
    return (DispatchState.tickets || []).some(t =>
      t.jobId === seedDay.jobId && (t.status || '') !== 'deleted' && _ticketCoversDay(t, dayStr) && !t.sealedReturnEstimate);   // TASK-10107 (DispatchState snapshot flag)
  } catch (_) { return false; }
}

// TASK-1111 SLICE B — escape-hatch dirty-guard: duplicateJob() -> showAddJob()
// unconditionally BLANKS the form, silently dropping the user's unsaved edits with
// no restore. So confirm a discard before firing it from inside the edit form.
function copyToNewJobFromEdit(jobId) {
  const ok = window.confirm('Copy to New Job starts a separate job and will discard any unsaved changes to THIS job. Continue?');
  if (!ok) return;
  duplicateJob(jobId);
}

// TASK-1221 — set the per-job ticketing mode (splitBy). Toggles the .active
// segment, writes the hidden input, and swaps the live disclaimer. Exposed on
// window.DispatchModule (called from the toggle buttons + editJob/showAddJob).
function setJobSplitBy(mode) {
  const val = (mode === 'vehicle') ? 'vehicle' : 'combined';
  const hidden = document.getElementById('job-split-by');
  if (hidden) hidden.value = val;
  document.querySelectorAll('#job-split-toggle .ib-mode-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.split === val);
  });
  const hint = document.getElementById('job-split-hint');
  if (hint) {
    hint.textContent = val === 'vehicle'
      ? 'Each vehicle — and the trailer and gear on it — gets its own ticket, with the operators who ran it. Use this to bill each rig separately.'
      : 'All crew and equipment on this job roll onto one ticket per day.';
  }
  _refreshJobFormGroups(); // TASK-4695 — the billing group's summary names the split
}

// TASK-1221 — mid-day lock (Domain invariant: splitBy is forward-only, never
// retroactive). If the job being edited already has draft/ready tickets for
// TODAY, DISABLE the toggle and show the lock note pointing at Billing's
// Combine/Split. Uses the already-loaded DispatchState.tickets stream (no query).
function _applyJobSplitLock(jobId) {
  const toggle = document.getElementById('job-split-toggle');
  const lock = document.getElementById('job-split-lock');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const hasTodayTicket = jobId && (DispatchState.tickets || []).some(t =>
    t.jobId === jobId && !t.invoiced && !t.archived && !t.sealedReturnEstimate &&   // TASK-10107 — the return estimate does not lock Combine/Split
    (t.status === 'draft' || t.status === 'ready') &&
    (t.workDate === todayStr || t.workDateEnd === todayStr ||
      (t.workDate && t.workDateEnd && t.workDate <= todayStr && todayStr <= t.workDateEnd))
  );
  const locked = !!hasTodayTicket;
  if (toggle) {
    toggle.style.opacity = locked ? '0.55' : '';
    toggle.style.pointerEvents = locked ? 'none' : '';
    toggle.querySelectorAll('.ib-mode-btn').forEach(b => { b.disabled = locked; });
  }
  if (lock) lock.style.display = locked ? '' : 'none';
  _refreshJobFormGroups(); // TASK-4695 — the Billing summary says "locked today"
}

function editJob(jobId, jobDayId = null, ev = null) {
  // TASK-4614 A5 — job-details closes and this form opens under the pointer, so
  // the second click of a double-click lands on the new dialog and can re-enter
  // editJob before jobDayId has propagated. MouseEvent.detail is the click count
  // (a programmatic .click() is 0, so tests and automation still work). Never a
  // timing window — memory/lesson_modal_handoff_stray_click_event_detail.
  if (ev && typeof ev.detail === 'number' && ev.detail >= 2) return;
  const job = _resolveJobAnywhere(jobId); // TASK-4535 — search cards too
  if (!job) {
    window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error');
    return;
  }

  // TASK-388: new form generation — invalidates any in-flight duplicate pre-fill
  // and cancels its pending timeout. This is the primary fix for the prod
  // customer-swap bug: opening an existing job to edit must hard-stop any
  // scheduled duplicate pre-fill before it can clobber #job-customer.
  _jobFormGen++;
  if (_dupePrefillTimer) { clearTimeout(_dupePrefillTimer); _dupePrefillTimer = null; }
  // HOTFIX 2026-11-04 — reused modal: drop any Smart Assist banner computed from the
  // previous record's values (J-1361's notes were offered as Auto-fix on J-1355).
  window.SmartAssistant?.clearAll?.(document.getElementById('modal-job'));
  // TASK-4877 — the previous job's baseline must not outlive it; re-captured
  // below once the POC dropdown resolves. null reads as "dirty", so an editJob
  // that dies before that capture errs toward saving, never toward discarding.
  _jobFormSnapshot = null;
  // TASK-9212 — an EDIT is not a copy. Drop any raw crew carry so a snapshot
  // landing mid-edit cannot re-seed this form with the copied job's crew.
  _dupeCrewCarry = null;
  DispatchState.returnJobCtx = null; // TASK-10006 — an edit is not a return Copy
  _resetCopyCarryGuard(); // TASK-9363 r3
  _dupeDropAnnounced = '';
  _setJobPermitIds([]); // TASK-9648 — a hidden input survives form.reset(); clear the linked 811 tickets
  // TASK-9258 — an EDIT is not a template application either. Templates are a
  // create-path feature (the write-through provenance lives in saveJob's create
  // branch), so this both clears any leftover marks and blanks the bar. Blanking
  // is done by renderJobTemplateBar's own editing check, which reads #job-id —
  // set by the caller below — so the bar is repainted at the end of the DOM fill
  // rather than here.
  resetJobTemplateState();
  // Stash the job's stored customer so saveJob can confirm before moving it.
  _editJobOriginalCustomerId = job.customerId || null;
  _editJobOriginalProjectId = job.projectId || null; // TASK-9289
  // TASK-9370 — driver-created job whose DOT flags nobody has confirmed yet.
  _jobFormDotReviewPending = job.needsDispatchReview === true;
  _jobFormDotUnconfirmed = _jobFormDotReviewPending
    && [job.interstate, job.hazmat, job.liquidTank].some(v => v === null || v === undefined);
  _renderJobDotReviewChip();

  // TASK-398: breadcrumb — record which job was opened for edit + its starting
  // customer, so a later wrong-customer save can be replayed. IDs only, no text.
  _breadcrumbCustomerId = job.customerId || null;
  window.ActionLog?.record('form_open', { form: 'job', jobId, initialCustomerId: job.customerId || null });

  // Hide AI button for editing (AI only for new jobs)
  const aiBtn = document.getElementById('btn-ai-create');
  if (aiBtn) aiBtn.style.display = 'none';

  // Hide AI prompt section if open
  document.getElementById('ai-prompt-section')?.classList.add('hidden');
  document.getElementById('job-builder-lookup-redirect')?.remove(); // TASK-9163

  // TASK-4781 — the number in the modal header, read-only by construction. It is
  // NOT a form field: there is no input to submit, so no edit path can carry it,
  // and `jobData` (built from the form) never gains a jobNumber key on the update
  // branch. textContent, never innerHTML — and formatJobNumber returns '' for a
  // legacy job, which reads exactly as 'Edit Job' does today.
  document.getElementById('modal-job-title').textContent =
    `Edit Job${formatJobNumber(job) ? ' · ' + formatJobNumber(job) : ''}`;
  _renderJobCopySourceBanner(null); // TASK-9598 — an edit is not a copy
  document.getElementById('job-id').value = jobId;
  // TASK-9258 — now that #job-id identifies this as an edit, repaint the
  // template bar (which reads that field) so it blanks itself.
  renderJobTemplateBar();
  document.getElementById('job-title').value = job.title || '';

  // Populate customer dropdown and select
  populateCustomerDropdown(job.customerId);
  document.getElementById('job-customer').value = job.customerId || '';
  _setJobCustomerAiCue(null); // TASK-4579 — an edited job's customer is the saved one

  // Show POC section if customer selected
  const pocSection = document.getElementById('poc-section');
  if (job.customerId) {
    pocSection.classList.remove('hidden');
  } else {
    pocSection.classList.add('hidden');
  }

  _restoreInheritedPoMarker(job);

  // TASK-9645 C — invoice lock. Baseline = what this form opened with, so
  // saveJob can tell whether a LOCKED field (title / customer / PO) changed.
  // Starts unlocked; _refreshEditJobInvoiceLock paints it from the board stream
  // at once and from the live two-query read a moment later. Same lock for
  // owner / Billing — the way out is Uninvoice in Billing, not an override.
  _editJobLockBaseline = { jobId, title: job.title || '', customerId: job.customerId || null, poNumber: String(job.poNumber || '') };
  _applyEditJobInvoiceLock(null);
  _refreshEditJobInvoiceLock(jobId, _jobFormGen);
  _openJobExtraPos({ id: jobId, extraPoNumbers: job.extraPoNumbers }); // TASK-9811 S1

  document.getElementById('job-po-visible').checked = job.poVisible || false;
  _resetProjectJobDefaultClaims(); // TASK-4696 — the job's saved values are its own, never "ours"
  document.getElementById('job-vendor-number').value = job.vendorNumber || '';
  document.getElementById('job-afe').value = job.afeNumber || '';

  // Populate waypoints
  // TASK-359 belt-and-suspenders — filter legacy data where a saved waypoint
  // has both name+url empty (would re-create an empty row + spuriously fire
  // the save-side guard on every edit of an existing job).
  const waypointsList = document.getElementById('job-waypoints-list');
  waypointsList.innerHTML = '';
  if (job.waypoints && job.waypoints.length > 0) {
    job.waypoints.forEach(wp => {
      if (!wp.name && !wp.url) return;
      // TASK-9541 — the SAME stop on the SAME job keeps its id. A legacy stop
      // with none gets one here and keeps it from this save on.
      addWaypoint(wp.name, wp.url, '', wp.lat, wp.lng, wp.id, wp.categoryId || '');
      // TASK-9497 — where the server's pin came from (link / address, and how
      // sure it was) rides on the row, and collectWaypoints keeps it only while
      // the row still saves the SAME coordinate.
      const _c = plottableCoord(wp);
      const _row = waypointsList.lastElementChild;
      if (_c && wp.geo && typeof wp.geo === 'object' && _row && _row.dataset) {
        _row.dataset.geo = JSON.stringify({ src: String(wp.geo.src || '').slice(0, 20), conf: String(wp.geo.conf || '').slice(0, 12), at: Number(wp.geo.at) || 0, lat: _c.lat, lng: _c.lng });
      }
    });
  }
  // TASK-10291 — with zero saved stops nothing above repaints the mileage line,
  // so the LAST job's "94.7 mi · 1h 46m driving (2 stops)" stayed showing.
  updateWaypointMileage();

  // TASK-191 Phase 2 — restore per-job geofence config.
  populateGeofenceSection(job);

  // Populate photos
  DispatchState.pendingJobPhotos = [];
  DispatchState.existingJobPhotos = job.photos || [];
  DispatchState.loadedPhotoCount = DispatchState.existingJobPhotos.length;
  // TASK-10224 — a job that opens WITH photos already has its setting: removing
  // them all and adding a replacement must not flip it (COD r1, Code Reviewer).
  DispatchState.photosVisibleTouched = DispatchState.existingJobPhotos.length > 0;
  renderJobPhotoGrid();
  document.getElementById('job-photos-visible').checked = job.photosVisible || false;

  // Toggle attachments checkbox if job has photos
  const attachToggle = document.getElementById('job-attachments-toggle');
  if (attachToggle) {
    attachToggle.checked = (job.photos?.length > 0);
    toggleAttachmentsSection();
  }

  // Load existing permits
  DispatchState.pendingPermitFiles = [];
  DispatchState.existingPermitFiles = job.permits || [];
  DispatchState.existingPermitDocs = job.driverDocs || null; // TASK-9694 — server-only Doc map (permit grid Doc tag)
  DispatchState.loadedPermitCount = DispatchState.existingPermitFiles.length;
  resetPermitUploadState();   // TASK-9151
  renderPermitGrid();
  togglePermitsSection(); // TASK-9546 — always open
  renderJobSignedDocs(job); // TASK-9740 — signed BOL / disposal PDFs on this job
  // Set AI/FastTrax checkboxes from saved settings (every tenant; AI parse needs hasAiPack)
  const aiParseEl = document.getElementById('permit-ai-parse');
  if (aiParseEl) aiParseEl.checked = job.permitSettings?.aiParseForBilling ?? true;
  const fastTraxEl = document.getElementById('permit-fasttrax');
  if (fastTraxEl) fastTraxEl.checked = job.permitSettings?.usedFastTrax ?? false;

  document.getElementById('job-interstate').checked = job.interstate || false;
  document.getElementById('job-hazmat').checked = job.hazmat || false;
  document.getElementById('job-liquid-tank').checked = job.liquidTank || false;
  // TASK-4649 — restore the stored tri-state (=== true only; absent = undecided).
  loadJobPermitRequired(job);
  // TASK-1221 — restore the ticketing mode, then apply the mid-day lock (a job
  // with tickets already out for today can't change its splitBy retroactively).
  setJobSplitBy(job.splitBy || 'combined');
  _applyJobSplitLock(jobId);
  document.getElementById('job-location').value = job.location || '';
  document.getElementById('job-well-name').value = [job.wellName, job.wellNumber].filter(Boolean).join(' ') || '';
  _setJobPermitIds(_jobPermitIdsOf(job)); // TASK-9648 — every linked 811 ticket
  document.getElementById('job-notes').value = job.notes || '';
  _syncJobNotesHeight({ reset: true }); // TASK-4808 — size to THIS job's notes, not the last one's
  _renderJobPrevNotesBox(null); // TASK-9391 — an edit shows its own notes only
  // TASK-9694 S6 (lazy) — the office sees the crew updates live; a PIN driver's
  // Edit Job gets the notes box read-only (office notes; driverSaveJob ignores them).
  __ddChunkWrap(import('./jobUpdates.js?v=96946')).then(m => m.mountOffice(jobId, DispatchState.tenantId), () => {});
  _projCarriedBook = null;      // TASK-9391 (COD r1) — an edit is not a project carry
  _dupeCarriedProjectId = null; // TASK-9363 — an edit is not a copy
  _pendingProjectKit = null;
  _jobFormCustomerRequest = null; // TASK-9392 — an edit is never a customer-request create
  _renderJobCustomerRequestBox(null);
  _jobFormClaudeDraft = null; // TASK-9587 — nor a Claude job request
  _renderJobClaudeDraftBox(null);

  // Job type (default to 'job' for legacy data). TASK-9390: a stored value the
  // list does not know (old free text, an archived type) gets its own option,
  // so opening + saving an old job never rewrites its type.
  _renderJobTypeSelect(job.type || 'job');
  onJobTypeChange();

  // All day toggle
  const allDay = job.allDay !== false; // Default to true if not set
  document.getElementById('job-all-day').checked = allDay;
  document.getElementById('time-fields').style.display = allDay ? 'none' : '';
  document.getElementById('job-start-time').value = job.defaultStart || '06:00';
  // TASK-9737 (fold COD): a timed job saved with no end time reopens with none,
  // so a re-save never writes an 18:00 nobody entered. All-day jobs keep the default.
  document.getElementById('job-end-time').value = job.defaultEnd || (allDay ? '18:00' : '');

  // POC fields
  document.getElementById('poc-info-card').classList.add('hidden');
  document.getElementById('job-poc-name').value = job.poc?.name || '';
  document.getElementById('job-poc-role').value = job.poc?.role || '';
  document.getElementById('job-poc-phone').value = job.poc?.phone || '';
  document.getElementById('job-poc-phone2').value = job.poc?.phone2 || '';
  document.getElementById('job-poc-email').value = job.poc?.email || '';
  // The dropdown fills asynchronously (CRM contacts come from a subcollection read),
  // so the saved POC can only be re-selected AFTER it resolves. Without this the
  // modal always read "Select POC" on a job that has one — Chris, 2026-08-23.
  // TASK-4877 — the generation this read BELONGS to, captured before it starts.
  // The job form is one reused DOM node, so a callback that lands after the
  // dispatcher has opened a DIFFERENT job would otherwise write job A's contact
  // into job B's open form and then bake it into B's dirty-check baseline.
  const _pocRestoreGen = _jobFormGen;
  populatePocDropdown(job.customerId).then(() => {
    if (_pocRestoreGen !== _jobFormGen) return; // this form is somebody else's now
    const _pocBefore = _readJobPocFields();
    restoreJobPocSelection(job.poc);
    // Move the baseline for the poc fields that call actually rewrote, and only
    // those — a POC typed while the contact read was in flight stays dirty.
    _rebaselineJobFormPocFields(_pocBefore, _pocRestoreGen);
  });

  // TASK-4659 — jobs created from Billing (addJobFromSplit/addJobFromReview)
  // carried no endDate; new Date(undefined) is an Invalid Date and
  // formatDateISO's .toISOString() throws, killing the whole modal open.
  // Resolve each date defensively: end falls back to start, start to today.
  const _validDate = (d) => d instanceof Date && !isNaN(d);
  const _rawStart = job.startDate?.toDate?.() || (job.startDate ? new Date(job.startDate) : null);
  const _rawEnd = job.endDate?.toDate?.() || (job.endDate ? new Date(job.endDate) : null);
  const startDate = _validDate(_rawStart) ? _rawStart : (_validDate(_rawEnd) ? _rawEnd : new Date());
  const endDate = _validDate(_rawEnd) ? _rawEnd : startDate;
  const startStr = formatDateISO(startDate);
  const endStr = formatDateISO(endDate);
  document.getElementById('job-start-date').value = startStr;
  document.getElementById('job-end-date').value = endStr;
  document.getElementById('job-end-date').min = startStr;
  // TASK-10288 A — the start's max is the END date only on a multi-day job. On a
  // one-day job the end is hidden and equals the start, so `max = endStr` meant
  // the start could only move EARLIER (Chris 11/4, J-1355). No max there;
  // onJobStartDateChange carries the hidden end along. toggleMultiDay keeps it
  // in sync when the box is flipped.
  document.getElementById('job-start-date').max = startStr !== endStr ? endStr : '';
  setDateAssumedFlag(false); // TASK-962 (S3): stored date is real — never show a stale flag on edit

  // TASK-273: Multi-day toggle reflects existing job state — check + reveal end-date
  // wrapper when start ≠ end. Always editable on existing jobs (never hide-collapse
  // a multi-day job's end-date silently).
  const multiToggleEdit = document.getElementById('job-multi-day-toggle');
  const endWrapperEdit = document.getElementById('job-end-date-wrapper');
  const isMultiDay = startStr !== endStr;
  if (multiToggleEdit) multiToggleEdit.checked = isMultiDay;
  if (endWrapperEdit) endWrapperEdit.style.display = isMultiDay ? '' : 'none';
  // TASK-9750 — editing: the flags are per DAY (Assign Crew / the card's Confirm),
  // so the job-level checkboxes are hidden and cleared — an edit never stamps them.
  { const r = document.getElementById('job-day-flags-row'); if (r) r.style.display = 'none'; }
  { const t = document.getElementById('job-tentative'); if (t) t.checked = false; }
  { const h = document.getElementById('job-half-day'); if (h) h.checked = false; }
  // TASK-9112 — moving an EXISTING job's date off the visible board has the
  // identical failure, and is arguably worse: the job disappears from the board
  // it was just on. Called here (after the multi-day toggle is set) so the hint
  // is also correct on OPEN — a job you reached from Search can already be
  // off-board before you touch anything.
  refreshJobDateOffscreenHint();

  // TASK-273: Always reset bypass flag on edit too (edit path never triggers
  // dupe modal but keep it clean to avoid leaking into a subsequent create).
  _dupeBypassConfirmed = false;
  _dupeBypassContext = null; // TASK-4660

  // Populate assignment dropdowns with the crew of the day the form was OPENED
  // from. TASK-1672 made the fallback deterministic (sort by date, not snapshot
  // order); TASK-4614 stops using that fallback whenever a day is actually in
  // hand. `firstJobDay` keeps its name because it is still the first-day pick in
  // the no-context case, and it is also the SCOPE of every crew write below.
  // TASK-9563 — on the driver PIN form the dispatch subscriptions never ran, so
  // the day the Job Board details modal was opened from is handed over in
  // DispatchState.driverBoardJobDays (empty / absent everywhere else).
  const firstJobDay = _pickEditJobSeedDay(
    DispatchState.jobDays, jobId, jobDayId,
    (DispatchState.searchJobDays || []).concat(DispatchState.driverBoardJobDays || [], _externalJobDays())
  );
  // TASK-4614 — the one day this form's crew/equipment edits may write to. null
  // when the job has no resolvable jobDay (zero days, or every day outside the
  // 90-day board window and not in the search cache); saveJob refuses the crew
  // write and says so rather than guessing a day.
  _editJobDayId = firstJobDay?.id || null;
  _renderJobMultiDayNote(jobId, firstJobDay); // TASK-1111 / TASK-4614 — scope note
  _renderJobCrewScopeHint(firstJobDay);
  const selectedTruckId = firstJobDay?.trucks?.[0] || '';
  const selectedDriverId = firstJobDay?.drivers?.[0] || '';
  const selectedTrailerId = firstJobDay?.trailers?.[0] || '';
  populateAssignmentDropdowns(selectedTruckId, selectedDriverId, selectedTrailerId);

  // Populate crew multi-select sections with existing assignments
  const existingDrivers = firstJobDay?.drivers || [];
  const existingHelpers = firstJobDay?.helpers || [];
  const existingTrucks = firstJobDay?.trucks || [];
  const existingTrailers = firstJobDay?.trailers || [];
  const existingAssets = firstJobDay?.assets || [];

  // 🔴 TASK-9238 — SEED THE SIDE MAPS BEFORE THE TILES RENDER. These four
  // statements used to sit BELOW populateJobCrewSections, and that ordering is
  // the whole defect: populateJobMultiSelect reads
  // `pendingAssetQtys?.[id] || 1` and `pendingAssetDirections?.[id] || 'deploy'`
  // while it builds each asset tile, so an edit of a job storing 4 / Return
  // painted 1 / Deploy — or, worse, whatever the PREVIOUS form left behind,
  // because nothing cleared these between opens.
  //
  // 💵 Asset quantity and direction are BILLED, and the save reads the MAPS, not
  // the DOM (_deriveAssetSideMaps → _buildJobCrewPayload). So the pre-fix form
  // and the pre-fix write DISAGREED, in both directions:
  //   • the tile said "Deploy" on a job that would save 'return', and
  //   • toggleAssetDirection derives `next` from the MAP while the dispatcher
  //     reasons from the LABEL — so one click on a stored 'return' flipped it to
  //     'deploy' while the button text never changed. A billed value moved and
  //     the screen showed nothing.
  // Seeding first makes paint == map == write, which is the only state in which
  // a dispatcher can trust what they are looking at.
  //
  // Same fix, same reason, as TASK-9212's copy path (duplicateJob) — but NOT the
  // same data flow: a copy seeds from a SOURCE job through _normalizeCarriedCrew
  // (which drops uniques it cannot cover), an edit seeds from this job's OWN
  // stored jobDay verbatim. Do not merge them.
  //
  // Nothing between here and the old position reads these maps
  // (populateAssignmentDropdowns does not; the two setTimeouts below run after
  // this turn either way), so this is a pure reorder with no other side effect.
  DispatchState.pendingAssetQtys = {};
  if (firstJobDay?.assetQtys) {
    Object.entries(firstJobDay.assetQtys).forEach(([assetId, qty]) => {
      DispatchState.pendingAssetQtys[assetId] = qty;
    });
  }
  DispatchState.pendingAssetDirections = {};
  if (firstJobDay?.assetDirections) {
    Object.entries(firstJobDay.assetDirections).forEach(([assetId, dir]) => {
      DispatchState.pendingAssetDirections[assetId] = dir;
    });
  }

  populateJobCrewSections(existingDrivers, existingHelpers, existingTrucks, existingTrailers, existingAssets);

  // Run DOT compliance check on existing assignments
  setTimeout(runJobComplianceCheck, 200);
  // Apply customer required field warnings
  setTimeout(applyJobRequiredFieldWarnings, 100);

  // Show edit-only actions
  document.getElementById('job-edit-actions').classList.remove('hidden');
  // TASK-9943 — Remove Day: office members who can manage days, never a PIN driver.
  { const _rd = document.getElementById('btn-job-remove-day');
    if (_rd) _rd.hidden = window.AppState?.driverModePin === true || !_dispatchCanManageDays(); }

  // Initialize line item + contract selections from first jobDay
  DispatchState.pendingJobLineItemSelections = { ...(firstJobDay?.lineItemSelections || {}) };
  DispatchState.pendingJobSelectedContractId = firstJobDay?.selectedContractId || null;
  const jobContractArea = document.getElementById('job-contract-area');
  if (jobContractArea) jobContractArea.innerHTML = '';
  { const _pbHint = document.getElementById('job-pricebook-hint'); if (_pbHint) _pbHint.innerHTML = ''; } // TASK-9446

  // TASK-1672 — snapshot what the form was SEEDED with, read back through the
  // SAME builder saveJob uses, so an untouched crew section compares equal and
  // the save leaves every day's crew alone. Must come after populateJobCrewSections
  // and after pendingAssetQtys / pendingAssetDirections / line-item seeding above.
  // The intent is kept separately because the DOM read above is EMPTY when the
  // roster snapshot hasn't landed yet — refreshJobModalCrewIfOpen re-seeds from
  // it exactly once (COD integrated-diff pass).
  DispatchState.jobCrewSeedIntent = {
    drivers: existingDrivers, helpers: existingHelpers, trucks: existingTrucks,
    trailers: existingTrailers, assets: existingAssets
  };
  DispatchState.jobCrewSeedPending = {
    people: !DispatchState.dataLoaded.people,
    trucks: !DispatchState.dataLoaded.trucks,
    trailers: !DispatchState.dataLoaded.trailers,
    assets: !DispatchState.dataLoaded.assets
  };
  DispatchState.jobCrewSnapshot = _buildJobCrewPayload();

  // Populate project dropdown and select current project
  const projectSelect = document.getElementById('job-project-id');
  const projectCheckbox = document.getElementById('job-add-to-project');
  _resetJobNewProjectMode();
  _renderJobProjectCustomerNote('', null); // TASK-9606
  if (projectSelect) {
    projectSelect.disabled = false;
    // TASK-9606 — filtered for job.customerId (#job-customer is set above); the
    // job's own project stays listed even when it names another customer.
    _jobProjDocsSet(job.projectDocs); // TASK-9650 — before the picker repaints them
    _populateProjectSelect(projectSelect, job.projectId || '', job.projectId || null);
    populatePermitDropdown(); // TASK-9767 — the project's 811 rows need the project set
    // The hidden link mirror follows the job's own link (TASK-4695: the picker
    // itself shows it — there is no checkbox to tick any more).
    if (projectCheckbox) { projectCheckbox.checked = !!job.projectId; }
  }
  _applyJobProjectAccess(); // TASK-9651
  // Wolverine 2026-10-21 — this customer's price books + sites; an edit never
  // auto-fills (it would read as an unsaved change), it only shows the site
  // matching the saved Location.
  _resetJobSitePicker();
  _loadJobFormCustomerSources(job.customerId || '', { autofill: false });

  // TASK-4695 — every edit opens with the groups closed; their summary rows
  // name what is inside (refreshed by applyJobFormSectionVisibility below).
  _closeJobFormGroups();
  // TASK-4688 — edit: a hidden section that already HAS data renders anyway,
  // so an edit can never silently drop waypoints/geofence/photos/AFE.
  applyJobFormSectionVisibility(job);

  closeModal('job-details');
  _clearDeleteBlockAlert(); // TASK-4736 — never open a form under a stale refusal
  document.getElementById('modal-job').classList.remove('hidden');

  // TASK-4877 — the dirty-check baseline, taken SYNCHRONOUSLY at the end of the
  // seed so nothing the dispatcher types can land inside the window and be
  // mistaken for seeded state. The only field group that legitimately moves
  // after this is the hidden poc set, and _rebaselineJobFormPocFields above
  // moves the baseline with it.
  _captureJobFormSnapshot();

  // Async: load line items and render pickers (gated by clockbilling)
  if (job.customerId && window.hasAddOnPackage?.('clockbilling')) {
    loadCustomerLineItems(job.customerId).then(({ contracts, items }) => {
      _refreshJobPriceBookDraftHint(job.customerId); // TASK-9446
      if (contracts.length > 0) {
        renderContractPicker('job-contract-area', contracts, DispatchState.pendingJobSelectedContractId, true);
        // Auto-select if only 1 contract
        if (contracts.length === 1) {
          DispatchState.pendingJobSelectedContractId = contracts[0].id;
          // TASK-1672 — an AUTO-selected sole price book is not a user edit. Move
          // the dirty-check snapshot with it, or every later save on a
          // single-contract customer would write rates onto all of the job's days.
          if (DispatchState.jobCrewSnapshot && !DispatchState.jobCrewSnapshot.selectedContractId) {
            DispatchState.jobCrewSnapshot.selectedContractId = contracts[0].id;
          }
        }
        const contractItems = DispatchState.pendingJobSelectedContractId
          ? items.filter(i => i.contractId === DispatchState.pendingJobSelectedContractId)
          : items;
        if (contractItems.length > 0) {
          renderLineItemPickers('job-select-trucks', 'truck', contractItems);
          renderLineItemPickers('job-select-trailers', 'trailer', contractItems);
          renderLineItemPickers('job-select-assets', 'asset', contractItems);
          renderLineItemPickers('job-select-drivers', 'person', contractItems);
          renderLineItemPickers('job-select-helpers', 'person', contractItems);
        }
      }
    });
  }
}

async function archiveJob(jobId) {
  const confirmed = await window.showConfirm('Archive Job', 'Archive this job? It will be hidden from the dispatch board.', { confirmText: 'Archive' });
  if (!confirmed) return;

  try {
    const jobSnap = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId));
    const jobTitle = jobSnap.exists() ? jobSnap.data().title : 'unknown';
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId), {
      status: 'finished',
      updatedAt: Timestamp.now()
    });
    try {
      await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
        action: 'JOB_ARCHIVE', source: 'dispatch-details', timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        jobId, jobTitle
      });
    } catch (ae) { console.warn('[Dispatch] Job audit log failed:', ae); }
    window.showToast('Job archived', 'success');
    closeModal('job-details');
  } catch (error) {
    console.error('[Dispatch] Archive job error:', error);
    window.showToast('Error archiving job', 'error');
  }
}

// TASK-4736 — a REFUSED job delete used to say so only in a 3-second toast, and
// the toast named neither the person who is clocked in nor how many tickets are
// in the way. A dispatcher who could delete this job yesterday and cannot today
// looks for *why* on the screen; three seconds later there is nothing there.
// So a refusal now leaves a persistent, dismissible, high-contrast alert sitting
// right above the Delete button that refused, on BOTH entry points.
//
// This changes PRESENTATION ONLY. Every refusal predicate below is untouched —
// nothing that could be deleted before is refused now, and nothing that was
// refused before slips through. (TASK-428: a tightening with no enumeration of
// the flows it blocks trapped drivers for 14+ hours. There is no tightening here.)
//
// No string is interpolated into markup: the person's name goes in through
// textContent, never innerHTML, so escapeHtml's attribute blind spot cannot bite.
const _DELETE_BLOCK_ALERT_ID = 'job-delete-blocked-alert';

function _clearDeleteBlockAlert() {
  document.querySelectorAll('#' + _DELETE_BLOCK_ALERT_ID).forEach(el => el.remove());
}

// Where the alert lands for each entry point:
//   'job-details' → inside #job-details-footer, above the Delete/Archive grid
//   'job'         → a SIBLING inserted before .modal-form-footer (that footer is
//                   display:flex row — a child would land beside the buttons)
function _deleteBlockAlertMount(closeModalName) {
  // TASK-9943 — 'toast' = no on-screen host (the crew editor's Undo has just
  // closed); 'remove-day-sheet' = the Edit Job Remove Day list, which sits on
  // top of the form and would hide an alert mounted under it.
  if (closeModalName === 'toast') return null;
  if (closeModalName === 'remove-day-sheet') {
    const host = document.getElementById('jdux-remove-day-alert');
    return host ? { parent: host, before: null, margin: '0 0 10px' } : null;
  }
  if (closeModalName === 'job') {
    const footer = document.querySelector('#modal-job .modal-form-footer');
    if (!footer || !footer.parentNode) return null;
    return { parent: footer.parentNode, before: footer, margin: '12px 20px 0' };
  }
  const host = document.getElementById('job-details-footer');
  if (!host) return null;
  return { parent: host, before: host.firstChild, margin: '14px 20px 0' };
}

// Light background, so per the house contrast rule the text is var(--text-primary)
// — never var(--text-muted). The red carries in the border and the icon.
function _showDeleteBlockAlert(headline, fix, closeModalName) {
  _clearDeleteBlockAlert();
  const mount = _deleteBlockAlertMount(closeModalName);
  if (!mount) { window.showToast?.(headline + ' ' + fix, 'error'); return; }

  const box = document.createElement('div');
  box.id = _DELETE_BLOCK_ALERT_ID;
  box.setAttribute('role', 'alert');
  box.style.cssText =
    'display:flex;gap:10px;align-items:flex-start;flex:0 0 auto;' +
    'margin:' + mount.margin + ';padding:12px 14px;' +
    'background:#FEF2F2;border:2px solid var(--danger, #DC2626);border-left-width:6px;' +
    'border-radius:var(--radius-md, 8px);color:var(--text-primary, #0F172A);' +
    'font-size:0.95rem;line-height:1.4;overflow-wrap:anywhere;';
  box.innerHTML =
    '<span class="material-icons" aria-hidden="true" style="color:var(--danger, #DC2626);font-size:22px;flex:0 0 auto;">block</span>' +
    '<div style="flex:1;min-width:0;">' +
      '<div data-jdb="headline" style="font-weight:700;color:var(--text-primary, #0F172A);"></div>' +
      '<div data-jdb="fix" style="margin-top:2px;color:var(--text-primary, #0F172A);"></div>' +
    '</div>' +
    '<button type="button" data-jdb="dismiss" aria-label="Dismiss" ' +
      'style="flex:0 0 auto;background:none;border:none;cursor:pointer;padding:0 2px;color:var(--text-primary, #0F172A);line-height:1;">' +
      '<span class="material-icons" style="font-size:20px;">close</span></button>';
  box.querySelector('[data-jdb="headline"]').textContent = headline;
  box.querySelector('[data-jdb="fix"]').textContent = fix;
  box.querySelector('[data-jdb="dismiss"]').addEventListener('click', _clearDeleteBlockAlert);

  mount.parent.insertBefore(box, mount.before);
  try { box.scrollIntoView({ block: 'nearest' }); } catch (_) { /* older browsers */ }
}

// TASK-4735 slice 2a — the `deletedBy` BREADCRUMB.
//
// Slice 1 put the JOB_DELETE audit row on a Firestore onDelete trigger, so a
// raw devtools `deleteDoc` can no longer remove a job with no trail. But a
// Firestore trigger CANNOT NAME THE ACTOR — firebase-functions@4.9.0
// lib/v1/cloud-functions.d.ts:73, `authType` is null "for all except Realtime
// Database". So the server row answers WHAT and WHEN unforgeably and cannot
// answer WHO, and the client-written row is currently the only carrier of an
// identity. These two functions are what makes removing that client row (slice
// 2b) safe: we stamp the caller's uid onto the job doc immediately before
// deleting it, and the trigger reads it off the pre-delete snapshot.
//
// firestore.rules pins `deletedBy` to `request.auth.uid` exactly the way
// `createdBy` is pinned (jobDeletedByOk, rules ~:768) — you may claim yourself,
// clear it, or leave a stored value alone, never rewrite it to a coworker.
// A raw-SDK deleter simply never stamps and the row lands with `deletedBy:
// null`; that is the point, not a gap. The row still exists, and an absent
// breadcrumb is itself evidence the delete did not come through the product.
//
// BEST EFFORT ON PURPOSE. A failed stamp must never block the delete the user
// asked for — the outcome of a failed stamp is exactly the raw-delete outcome
// (a row with no actor), which is strictly better than a dispatcher who cannot
// delete a job. Every role that can delete a job can also update it, checked
// rather than assumed: delete = isTenantAdmin || (dispatch && !viewUploadOnly),
// update = isDispatcherOrHigher || (dispatch && !viewUploadOnly), and
// isTenantAdmin (owner|admin) is a strict subset of isDispatcherOrHigher
// (owner|admin|dispatcher), so the two arms line up and the stamp cannot
// introduce a role-based refusal.
//
// `deletedAt` is not decoration: the trigger uses it as a FRESHNESS check. If a
// stamp is left behind by a delete that failed, a later raw delete of that same
// job would otherwise pin the blame on whoever last TRIED to delete it. A stale
// stamp is reported as stale instead of being believed.
async function _stampJobDeletedBy(tenant, jobId, source) {
  // Same accessor order the createdBy stamp uses (dispatch.js ~:9549):
  // auth.currentUser is the identity the rule actually compares against, so it
  // wins; AppState is the fallback for a render that ran before auth settled.
  const uid = auth.currentUser?.uid || window.AppState?.user?.uid || null;
  if (!uid) return false; // nothing to pin; the row will say so
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'jobs', jobId), {
      deletedBy: uid,
      deletedAt: Timestamp.now(),
      deletedVia: source || null,
    });
    return true;
  } catch (e) {
    console.warn('[Dispatch] TASK-4735 deletedBy stamp failed — delete proceeds, row will have no actor:', e);
    return false;
  }
}

// Roll the breadcrumb back when the stamp landed but the delete did not.
// Without this the job is still LIVE on the board and permanently marked
// deleted-by-someone, which is a lie in the forensic direction that matters.
// Cleared with explicit nulls rather than deleteField() so no new import is
// needed and the rules arm that accepts a null claim covers it.
async function _clearJobDeletedBy(tenant, jobId) {
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'jobs', jobId), {
      deletedBy: null, deletedAt: null, deletedVia: null,
    });
  } catch (e) {
    // Loud: a job left carrying a stale stamp is the one case that can produce
    // a WRONG name later. The trigger's freshness check is the backstop.
    console.error('[Dispatch] TASK-4735 deletedBy rollback FAILED — job ' + jobId +
      ' is live and still stamped; the trigger will mark a later delete of it as stale-breadcrumb:', e);
  }
}

// "Ivan" / "Ivan and Kyle" / "Ivan, Kyle and Dale" / "Ivan, Kyle, Dale and 2 more"
function _formatBlockerNames(names) {
  const u = Array.from(new Set(names.filter(Boolean)));
  if (u.length === 0) return '';
  if (u.length === 1) return u[0];
  if (u.length === 2) return u[0] + ' and ' + u[1];
  if (u.length === 3) return u[0] + ', ' + u[1] + ' and ' + u[2];
  return u[0] + ', ' + u[1] + ', ' + u[2] + ' and ' + (u.length - 3) + ' more';
}

// TASK-4734 — `opts` exists so the Edit Job modal's Delete can reuse this ENTIRE
// function instead of carrying its own stripped-down copy. It used to have one
// (`deleteCurrentJob`, ~140 lines below) which deleted the jobDays and the job
// and wrote NOTHING: no audit row, no clocked-in check, no live-ticket check, no
// rental reversal. On 2026-09-04 a dispatcher deleted a live job through it and
// there was no record it had ever existed — not who, not when, not what was on
// it. Recovered only because Firestore PITR was on (a 7-day window).
//   opts.closeModalName — which modal to close afterwards ('job-details' | 'job')
//   opts.source         — recorded on the audit row so the two entry points stay
//                         distinguishable in the trail
// ── TASK-10010 ─────────────────────────────────────────────────────────────
// Guy (Split Rock demo, 10/21) tapped Delete five times and saw nothing: the
// safety reads, then the preflight callable, a rental transaction and the
// delete callable all ran before any toast, with no busy state in between.
//   • ONE delete per job at a time (a second tap says so instead of starting
//     a second run), and the tapped button says what is happening from the
//     first tap: "Checking…" → (question) → "Deleting…".
//   • opts.jobDayId (Job Details / Edit Job pass the day they were opened
//     from): a multi-day job asks "Remove just <day>" vs "Delete all N days"
//     BEFORE the whole-job blockers (another day's ticket must not hide a valid
//     single-day removal — COD r1). "Remove just" is removeJobDay with every
//     one of its guards (ticket incl. billing-moved, clock, rental stock).
//   • A ledger job's rentals are given back by the SERVER inside the delete
//     transaction (rentals: 'server'), so the ledger row keeps what a restore
//     must put back. Success says what happened ("Job J-1234 deleted") and, for
//     the owner, carries Restore.
const _jobDeleteInFlight = new Set();

function _jobDeleteBusy(btn) {
  const ok = !!btn && typeof btn === 'object' && 'disabled' in btn;
  const prevHtml = ok ? btn.innerHTML : '';
  const prevDisabled = ok ? !!btn.disabled : false;
  let idled = false;
  const set = (label) => {
    if (!ok || idled) return;
    btn.disabled = true;
    btn.classList?.add('is-busy');
    btn.setAttribute?.('aria-busy', 'true');
    btn.innerHTML = '<span class="material-icons" aria-hidden="true">hourglass_top</span> <span class="job-delete-busy-label"></span>';
    const l = btn.querySelector('.job-delete-busy-label');
    if (l) l.textContent = label;
  };
  // While a question is up the button reads as itself again but stays disabled
  // (the dialog covers it; a tap behind it must not start a second delete).
  const pause = () => {
    if (!ok || idled) return;
    btn.innerHTML = prevHtml;
    btn.disabled = true;
    btn.classList?.remove('is-busy');
  };
  const idle = () => {
    if (!ok || idled) return;
    idled = true;
    btn.innerHTML = prevHtml;
    btn.disabled = prevDisabled;
    btn.classList?.remove('is-busy');
    btn.removeAttribute?.('aria-busy');
  };
  return { set, pause, idle };
}

// The owner (tenants.ownerId) — the one login restoreDeletedJob admits (it
// recreates jobs and moves rental stock; the `audit` module is read access —
// COD r1). Identity arm by ownerId, never a role. UI only; the server decides.
function _canRestoreDeletedJobs() {
  if (isDemoMode() || DispatchState.tenantId === 'demo') return false;
  if (window.AppState?.driverModePin === true) return false;
  const uid = window.AppState?.user?.uid || null;
  const ownerId = window.AppState?.tenantData?.ownerId || null;
  return !!(uid && ownerId && uid === ownerId);
}

// "Job J-1234" / "Job “Pad 7 mats”" — what the result toast names.
function _jobDeleteLabel(job) {
  const n = formatJobNumber(job);
  if (n) return `Job ${n}`;
  const t = String(job?.title || '').trim();
  return t ? `Job “${t}”` : 'The job';
}

let _jdelMod = null;
let _jdelLoading = null;
function _jobDeleteUx() {
  if (_jdelMod) return Promise.resolve(_jdelMod);
  if (!_jdelLoading) {
    _jdelLoading = __ddChunkWrap(import('./jobDeleteUx.js?v=10010001'))
      .then(m => { _jdelMod = m; return m; })
      .catch(err => { _jdelLoading = null; console.warn('[Dispatch] TASK-10010 jobDeleteUx failed to load:', err?.message || err); return null; });
  }
  return _jdelLoading;
}

// Every door into deleteJobFromDetails passes through here first: one run per
// job, the busy state on, and both released however the run ends.
async function _deleteJobGuarded(jobId, opts) {
  const key = String(jobId || '');
  if (!key) return;
  if (_jobDeleteInFlight.has(key)) {
    window.showToast?.('Still working on deleting this job — one moment.', 'info');
    return;
  }
  _jobDeleteInFlight.add(key);
  const busy = _jobDeleteBusy(opts.button);
  busy.set('Checking…');
  try {
    return await deleteJobFromDetails(jobId, { ...opts, _busy: busy });
  } finally {
    busy.idle();
    _jobDeleteInFlight.delete(key);
  }
}

async function deleteJobFromDetails(jobId, opts = {}) {
  // TASK-10010 — not yet inside the guard? Go through it (it calls back here).
  if (!opts || !opts._busy) return _deleteJobGuarded(jobId, opts || {});
  const busy = opts._busy;
  const tenant = DispatchState.tenantId;
  const _closeModalName = opts.closeModalName || 'job-details';
  const _auditSource = opts.source || 'dispatch-details';
  // A retry must never read against a stale refusal from the previous attempt.
  _clearDeleteBlockAlert();

  // TASK-959 — safety check. Query by jobId ONLY (indexed, no composite) and
  // classify client-side; the old query used `where('status','not-in',['deleted'])`
  // which needs a composite index that doesn't exist, so it THREW and the catch
  // "proceeded" (FAIL-OPEN) — that let a job with a draft ticket + 17h of clock
  // time get deleted on 2026-07-02, orphaning the data. Mirrors rescheduleJobDay's
  // safe pattern (~L15003).
  let activeClockCount = 0, completedClockCount = 0, completedHours = 0, liveTicketCount = 0;
  // TASK-4736 — who is actually on the clock, so the refusal can name them.
  // DispatchState.people is the good name; entry.personName is the denormalized
  // fallback (it is what clock.js renders from when the roster read is scoped out).
  const activeClockNames = [];
  // TASK-9627 — EVERY day of the job, read from Firestore by jobId, NOT the
  // board's `DispatchState.jobDays`. That array is a WINDOWED subscription
  // (`date >= _adminCutoffTs`, default 90 days back, until "Load older"), so a
  // long job whose early days predate the cutoff used to lose only its LOADED
  // days here: the job doc went, the hidden days stayed behind as orphans that
  // nothing cleans up server-side (onJobDelete only notifies; onJobDeleteAudit
  // only snapshots). Measured 2026-10-06: 51 live jobs across 3 tenants straddle
  // the 90-day line, one with 30 days below it. Same single-field, composite-free
  // query removeJobDay uses. It rides the safety check's Promise.all so a read
  // that throws hits the same FAIL-CLOSED refusal — never delete half a job.
  let allJobDays = [];
  try {
    const [clockSnap, ticketSnap, jobDaySnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId))),
      getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('jobId', '==', jobId))),
      getDocs(query(collection(db, 'tenants', tenant, 'jobDays'), where('jobId', '==', jobId)))
    ]);
    allJobDays = jobDaySnap.docs.map(d => ({ id: d.id, ...d.data() }));
    clockSnap.forEach(d => {
      const x = d.data();
      if (x.clockOut == null && x.status !== 'completed') {
        activeClockCount++;
        const p = (DispatchState.people || []).find(pp => pp.id === x.personId);
        const nm = String(p?.name || x.personName || '').trim();
        if (nm) activeClockNames.push(nm);
      }
      else { completedClockCount++; completedHours += (x.totalHours || 0); }
    });
    liveTicketCount = ticketSnap.docs.filter(d => (d.data().status || '') !== 'deleted').length;
  } catch (err) {
    // FAIL-CLOSED: if we can't verify the job is safe to delete, do NOT delete.
    console.warn('[Dispatch] Delete safety check failed — blocking delete:', err);
    _showDeleteBlockAlert(
      "Can't delete: we couldn't check whether anyone is clocked in or billing this job.",
      'Check your connection and try again.',
      _closeModalName
    );
    return;
  }
  // TASK-10010 — the copy every delete question ends with. Outside the demo the
  // server keeps the whole job (and what it moved in rental stock) in the
  // ledger, and the owner can bring it back from Recently deleted.
  const _restorable = !isDemoMode() && tenant !== 'demo';
  const _recoveryNote = _restorable
    ? (_canRestoreDeletedJobs()
      ? 'You can bring it back from Dispatch → ⋮ → Recently deleted for 30 days.'
      : 'The account owner can bring it back from Recently deleted for 30 days.')
    : "It comes off the board now and there's no undo.";
  const _hoursNote = completedClockCount > 0
    ? `${completedClockCount === 1 ? '1 person has' : `${completedClockCount} people have`} logged ${completedHours.toFixed(1)}h on this job. Their time stays on the timesheet for payroll but will be detached from the job.`
    : '';

  // TASK-10010 S2 — "Remove just this day" vs "Delete all N days", asked
  // BEFORE the whole-job blockers below (COD r1): a ticket or a clock-in on a
  // DIFFERENT day refuses the whole job, and must not hide the single-day
  // removal the dispatcher actually wanted. Only when the opened day is one of
  // this job's days and the login may manage days (view & upload may not).
  let _askedAlready = false;
  const _openDayId = (typeof opts.jobDayId === 'string' && opts.jobDayId) ? opts.jobDayId : null;
  const _openDay = _openDayId ? allJobDays.find(d => d.id === _openDayId) : null;
  if (_openDay && allJobDays.length > 1 && _dispatchCanManageDays()) {
    const m = await _jobDeleteUx();
    // No chunk (offline mid-load): fall through to the plain whole-job confirm
    // below — the dispatcher still gets asked, just without the single-day choice.
    if (m) {
    const _jobForTitle = DispatchState.jobs.find(j => j.id === jobId) || _resolveJobAnywhere(jobId);
    busy.pause();
    const choice = await m.askDeleteDayOrJob({
      dayLabel: _formatJobDayLabel(_openDay) || 'this day',
      dayCount: allJobDays.length,
      jobTitle: _jobForTitle?.title || '',
      recoveryNote: _recoveryNote,
      hoursNote: _hoursNote,
    });
    if (!choice) return;
    if (choice === 'day') {
      busy.idle();
      // Every remove-day guard still runs (ticket incl. originalJobId, clock,
      // rental stock via _rentalLedgerLeaveTx); only its wording dialog is
      // skipped — the dispatcher just chose this exact day.
      return removeJobDay(jobId, _openDayId, {
        preConfirmed: true, alertTo: _closeModalName, source: 'dispatch-delete-just-day', button: opts.button,
      });
    }
    _askedAlready = true;
    busy.set('Checking…');
    }
  }

  // Hard blocks: someone currently clocked in, or live billing tickets.
  if (activeClockCount > 0) {
    const named = _formatBlockerNames(activeClockNames);
    const namedCount = Array.from(new Set(activeClockNames.filter(Boolean))).length;
    const headline = named
      ? `Can't delete: ${named} ${namedCount === 1 ? 'is' : 'are'} clocked in on this job.`
      : `Can't delete: ${activeClockCount} ${activeClockCount === 1 ? 'person is' : 'people are'} clocked in on this job.`;
    _showDeleteBlockAlert(headline, 'Clock them out first, then delete.', _closeModalName);
    return;
  }
  if (liveTicketCount > 0) {
    // TASK-4792 — on a MULTI-DAY job this refusal used to be a dead end. The
    // ticket sits on ONE day; the dispatcher is usually trying to get rid of a
    // DIFFERENT one (Mike, 2026-09-08: added a test day to a 2-day job, pressed
    // Delete, and was told about the other day's ticket). "Reassign or delete
    // it first" asks them to disturb billed work to undo a scheduling mistake.
    // So when there is more than one day, name the real option instead.
    const _multiDay = allJobDays.length > 1;   // TASK-9627: all days, not the loaded window
    _showDeleteBlockAlert(
      `Can't delete: this job has ${liveTicketCount} billing ticket${liveTicketCount === 1 ? '' : 's'}.`,
      _multiDay
        ? ('Deleting the job would take every day with it. To drop just one day, '
           + (_closeModalName === 'job-details'
              ? 'tap Remove Day on it in the Schedule list above'
              : 'close this and tap Remove Day on that day in the job’s Schedule list')
           + ' — that checks the tickets day by day.')
        : (liveTicketCount === 1 ? 'Reassign or delete it first.' : 'Reassign or delete them first.'),
      _closeModalName
    );
    return;
  }

  // TASK-9627 — the confirm count, the audit snapshot, the rental first-day
  // reversal and the deletes below all work off the FULL set read above.
  const jobDays = allJobDays;
  const dayCount = jobDays.length;
  // TASK-4736 replaced the old "gone forever" wording (wrong: Firestore PITR has
  // a verified 7-day window) with "call the office within about a week".
  // TASK-10010 replaced THAT: _recoveryNote (above, before the S2 question) now
  // names Recently deleted — the server ledger keeps the whole job, and what it
  // moved in rental stock, so the owner restores it in the app for 30 days. The
  // demo (browser delete, no ledger) still says there is no undo.
  let message = dayCount > 1
    ? `This job has ${dayCount} scheduled days. Deleting it removes ALL ${dayCount} days and their assignments. ${_recoveryNote}`
    : `Delete this job and its scheduled day? ${_recoveryNote}`;
  // Warn (don't block) when people already clocked out of this job — their hours
  // stay on the timesheet for payroll but detach from the deleted job.
  if (_hoursNote) message = `${_hoursNote}\n\n${message}`;
  if (!_askedAlready) {
    busy.pause();
    const confirmed = await window.showConfirm('Delete Job', message, { confirmText: 'Delete', danger: true });
    if (!confirmed) return;
  }
  busy.set('Deleting…');

  // Snapshot the job BEFORE any deletes — once deleteDoc fires, snapshot listeners may empty DispatchState.jobs
  const jobSnap2 = DispatchState.jobs.find(j => j.id === jobId);
  // TASK-4734 — snapshot the WHOLE jobDay, not `{id, date, assignments}`.
  // `assignments` DOES NOT EXIST on a jobDay: measured 2026-09-04 across 40 live
  // jobDays on trippin-trucking, 0 of 40 had the field. The crew and equipment
  // actually live in `drivers`, `helpers`, `trucks`, `trailers`, `assets`,
  // `assetQtys`, `assetDirections` — so this snapshot has been silently writing
  // `assignments: null` and preserving nothing but the date. That is exactly why
  // the 2026-09-04 delete could not be rebuilt from the audit row and needed
  // Firestore PITR instead. Store the object whole; a jobDay is small, and the
  // one thing this row exists to answer is "what was on it".
  const jobDaysSnapshot = jobDays.map(d => ({ ...d }));

  // TASK-9467 S1 — outside the demo the SERVER deletes the job: firestore.rules
  // deny the client delete, so the actor comes from request.auth and the ledger
  // row, the full snapshot and every day go in one batch (deleteJobLedgered).
  // Preflight FIRST: a server refusal (someone clocked in a second ago) must
  // land before the rental stock below is reversed, never after.
  const _serverDelete = !isDemoMode() && tenant !== 'demo';
  // TASK-10010 — a LEDGER job's rentals are given back by the server, inside
  // the delete transaction (rentals: 'server'), so no client reversal and no
  // preflight: one callable. A PRE-LEDGER job keeps the browser's first-day
  // reversal, so it still preflights first (a refusal must land before stock
  // moves). The job doc is read fresh; if that read fails the preflight says.
  let _serverRentals = false;
  if (_serverDelete) {
    let _ledgerKnown = null;
    try {
      const _js = await getDoc(doc(db, 'tenants', tenant, 'jobs', jobId));
      _ledgerKnown = _js.exists() ? _jobHasRentalLedger(_js.data()) : null;
    } catch (_) { _ledgerKnown = null; }
    const _firstPeek = _ledgerKnown === false ? _pickFirstJobDayByDate(jobDays, jobId) : null;
    const _preLedgerRentals = !!_firstPeek && Array.isArray(_firstPeek.assets) && _firstPeek.assets.some((id) => {
      const a = DispatchState.assets.find(x => x.id === id);
      return !!a && (a.quantityType === 'rental' || a.isRental);
    });
    if (_ledgerKnown === true) {
      _serverRentals = true;
    } else if (_ledgerKnown === null || _preLedgerRentals) {
      try {
        const _pre = await httpsCallable(functions, 'deleteJobLedgered')({ tenantId: tenant, jobId, via: _auditSource, preflight: true });
        if (_pre?.data?.hasRentalLedger === true) _serverRentals = true;
      } catch (pe) {
        console.warn('[Dispatch] server delete check refused:', pe);
        _showDeleteBlockAlert(pe?.message || "Can't delete this job right now.", 'Nothing was deleted.', _closeModalName);
        return;
      }
    }
  }

  try {
    // Audit log BEFORE delete so the snapshot survives (demo only — outside the
    // demo deleteJobLedgered writes this row server-side, in the delete's batch).
    if (!_serverDelete) try {
      await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
        action: 'JOB_DELETE',
        source: _auditSource,
        timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        jobId,
        jobTitle: jobSnap2?.title || null,
        customerName: jobSnap2?.customer || null,
        jobSnapshot: jobSnap2 || null,
        jobDaysDeleted: jobDaysSnapshot.length,
        jobDaysSnapshot
      });
    } catch (ae) { console.warn('[Dispatch] Audit log failed:', ae); }

    // TASK-9336 — a LEDGER job gives back exactly what its ledger says is out
    // and closes its pending return inspections, in one transaction. No day's
    // labels are read, so a cancelled or copied first day decides nothing.
    // null = pre-ledger job → the first-day reversal below, unchanged.
    // TASK-10010 — outside the demo a ledger job's give-back runs on the server
    // (deleteJobLedgered rentals: 'server'), so the browser does nothing here.
    const _ledDel = _serverRentals ? { serverSide: true, changes: [] } : await _rentalLedgerJobDeleteTx(tenant, jobId);
    if (_ledDel) _mirrorRentalAssets(_ledDel.changes);

    // Reverse rental deploy/return transactions before deleting (use first jobDay only — transactions fire once)
    // TASK-9627 — chronologically first, not array-first: the Firestore read is
    // unordered, and the old `DispatchState.jobDays[0]` was only the first LOADED
    // day, i.e. the wrong day whenever the real first day sat below the window.
    const firstJD = _ledDel ? null : _pickFirstJobDayByDate(jobDays, jobId);
    if (firstJD?.assets?.length > 0) {
      for (const assetId of firstJD.assets) {
        const asset = DispatchState.assets.find(a => a.id === assetId);
        const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
        if (!isRental) continue;
        const dir = firstJD.assetDirections?.[assetId] || 'deploy';
        const qty = firstJD.assetQtys?.[assetId] || 1;
        // Reverse: deploy→undo deploy (decrement), return→undo return (increment)
        await processRentalTransaction(asset, dir === 'deploy' ? 'return' : 'deploy', qty, jobId, true);
      }
    }

    let _delRes = null;
    if (_serverDelete) {
      // TASK-9467 S1 — job, every day, the JOB_DELETE breadcrumb and the ledger
      // row, server-side in one batch. TASK-10010 — plus the rental give-back.
      _delRes = (await httpsCallable(functions, 'deleteJobLedgered')({
        tenantId: tenant, jobId, via: _auditSource, ...(_serverRentals ? { rentals: 'server' } : {}),
      }))?.data || null;
      if (_delRes && Array.isArray(_delRes.rentalChanges)) {
        _mirrorRentalAssets(_delRes.rentalChanges.map(c => ({
          assetId: c.assetId,
          deployedQty: c.after && c.after.deployedQty != null ? c.after.deployedQty : null,
          deploymentStatus: c.after && c.after.deploymentStatus ? c.after.deploymentStatus : null,
        })));
      }
    } else {
      const deletePromises = jobDays.map(d =>
        deleteDoc(doc(db, 'tenants', tenant, 'jobDays', d.id))
      );
      await Promise.all(deletePromises);
      // TASK-4735 slice 2a — stamp WHO immediately before the delete, so the
      // server-side trigger row (which cannot see the caller) can name a person.
      // Best effort; see _stampJobDeletedBy. If the stamp lands and the delete
      // then fails, the job is still live and must not stay marked deleted.
      const _stamped = await _stampJobDeletedBy(tenant, jobId, _auditSource);
      try {
        await deleteDoc(doc(db, 'tenants', tenant, 'jobs', jobId));
      } catch (delErr) {
        if (_stamped) await _clearJobDeletedBy(tenant, jobId);
        throw delErr;
      }
    }
    // TASK-10010 — say what happened, and offer Restore to whoever may.
    const _label = _jobDeleteLabel(jobSnap2 || _resolveJobAnywhere(jobId));
    const _rowId = _delRes && typeof _delRes.ledgerRowId === 'string' ? _delRes.ledgerRowId : null;
    if (_rowId && _canRestoreDeletedJobs()) {
      window.showToast?.(`${_label} deleted`, 'success', {
        label: 'Restore', callback: () => restoreDeletedJob(_rowId, { surface: 'undo-toast', label: _label }),
      });
    } else {
      window.showToast?.(`${_label} deleted`, 'success');
    }
    closeModal(_closeModalName);
  } catch (error) {
    console.error('[Dispatch] Delete job error:', error);
    // TASK-9467 S1 — a server refusal says why (and that nothing was deleted).
    const _srv = String(error?.code || '').startsWith('functions/') && error?.message;
    if (_srv) _showDeleteBlockAlert(error.message, 'Nothing was deleted.', _closeModalName);
    else window.showToast?.('Error deleting job. Check the board before trying again.', 'error');
  }
}

// ── TASK-10010 S3 — bring a deleted job back (owner only) ─────────────
// The server (restoreDeletedJob) re-checks access, refuses on any collision
// (job id, a day id, the J-number, a live ticket) and refuses rather than put
// wrong rental numbers back; it writes the job, its days, the rental counts,
// a ledger row and a jobAuditLog JOB_RESTORE row in one commit and texts nobody.
// The UI glue lives in the lazy jobDeleteUx.js (cold-load budget); this hands
// it the one callable door, bound to this tenant.
function _jobRestoreCtx() {
  return { call: async (name, data) => (await httpsCallable(functions, name)({ tenantId: DispatchState.tenantId, ...(data || {}) }))?.data || null };
}

async function restoreDeletedJob(ledgerRowId, o = {}) {
  if (!ledgerRowId) return null;
  if (!_canRestoreDeletedJobs()) { window.showToast?.('Only the account owner can bring back a deleted job.', 'info'); return null; }
  const m = await _jobDeleteUx();
  if (!m) { window.showToast?.("Couldn't bring the job back. Nothing was changed — check your connection and try again.", 'error'); return null; }
  return m.restoreDeletedJobVia(_jobRestoreCtx(), ledgerRowId, o);
}

async function openRecentlyDeleted() {
  if (!_canRestoreDeletedJobs()) { window.showToast?.('Only the account owner can see recently deleted jobs.', 'info'); return; }
  const m = await _jobDeleteUx();
  if (!m) { window.showToast?.("Couldn't open Recently deleted. Check your connection and try again.", 'error'); return; }
  m.openRecentlyDeletedVia(_jobRestoreCtx());
}

// ── TASK-4792 ──────────────────────────────────────────────────────────────
// REMOVE ONE DAY FROM A MULTI-DAY JOB.
//
// Until this there was no way to do it. The board's overflow menu has "Cancel
// Job" (which is really `cancelJobDay` — it marks the day cancelled and leaves
// it on the board), and Job Details has "Delete" (which takes the WHOLE job and
// every day on it). So a dispatcher who added one day by mistake had exactly two
// options: leave a cancelled day sitting on the schedule forever, or edit the
// job's date range and hope the day fell outside it.
//
// Mike hit the sharp edge of that on 2026-09-08: he added a test day to HANSEN
// SWD (a 2-day job), pressed Delete, and got "Can't delete: this job has 1
// billing ticket." — a refusal about the OTHER day. The guard was right; the
// only action it offered was wrong. The ticket lives on Sep 6; the day he wanted
// gone was Sep 9 and carried nothing.
//
// 🔴 THE REMOVAL MUST BE RECORDED, NOT JUST IMPLIED. `saveJob`'s date-sync
// CREATES a jobDay for every date inside job.startDate..endDate that doesn't
// have one (~:9345). Delete a jobDay and leave the range covering its date, and
// the next Edit Job save RESURRECTS it with crew carried forward from a template
// day — the deletion silently undone by an unrelated edit.
//
// Re-deriving the range from the surviving days is NECESSARY and NOT SUFFICIENT,
// and the first draft of this shipped only that half. It protects the ENDS of
// the range and nothing else: remove Sep 7 from a Sep 6–8 job and the survivors
// still span Sep 6–8, so the range does not move and Sep 7 comes back. (Three
// COD lanes found that independently, 2026-09-08.) So BOTH happen, in one
// batch: the range shrinks, AND the date goes into `jobs.removedDates`, which
// the create loop skips. `_applyAddDayJobRange` clears the date from that list
// whenever the day is deliberately added back.
//
// Guards, in order, and all of them fail CLOSED:
//   • last day        — that is a job delete; say so and point at Delete.
//   • ticketed day    — the same matcher every other day-guard uses
//                       (_findTicketsCoveringDay). A covering ticket refuses,
//                       billed or not: tickets carry no jobDayId (0 of 903
//                       measured), the DATE is the join, so removing the day
//                       orphans the ticket. Read threw -> refuse.
//   • clocked in      — anyone on the clock whose clock-in lands on that day.
//   • hours logged    — warn, don't block; the hours stay on the timesheet.
async function _removeJobDayRun(jobId, jobDayId, rctx) {
  // TASK-9943 — where a refusal lands (the details footer by default; the
  // Edit Job Remove Day sheet or a toast from the other two doors), and what
  // the caller gets back so it can record a refused or cancelled remove.
  const _rc = rctx || {};
  const _to = _rc.alertTo || 'job-details';
  const _rmDayOut = (outcome, reason, date) => ({ outcome, reason: reason || null, date: date || null });
  if (!_guardManageDays()) return _rmDayOut('refused', 'no-access');
  const tenant = DispatchState.tenantId;
  _clearDeleteBlockAlert();

  // 🔴 THE JOB'S DAYS COME FROM FIRESTORE, NOT FROM DispatchState.
  // `DispatchState.jobDays` is a WINDOWED subscription — `date >= _adminCutoffTs`
  // unless the dispatcher has pressed "Load older" (:3458). Deriving the new
  // start/end from that partial set is how this function would DESTROY data
  // rather than merely mis-render: a job whose first days predate the cutoff
  // would have its start advanced past them, and saveJob's date-sync would then
  // delete those now-out-of-range historical days on the next edit. (COD Code
  // Reviewer ❌, 2026-09-08 — the same windowed-state trap addDayToJob already
  // refuses over at :21103.) One single-field, composite-free query; a read that
  // throws REFUSES.
  let allDays;
  try {
    const _snap = await getDocs(
      query(collection(db, 'tenants', tenant, 'jobDays'), where('jobId', '==', jobId))
    );
    allDays = _snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Dispatch] TASK-4792: jobDays read failed — refusing the removal:', err);
    _showDeleteBlockAlert(
      "Can't remove that day: we couldn't load the job's full schedule.",
      'Check your connection and try again.',
      _to
    );
    return _rmDayOut('refused', 'schedule-read-failed');
  }
  const day = allDays.find(d => d.id === jobDayId);
  if (!day) {
    window.showToast?.('That day could not be loaded. Try refreshing the page.', 'error');
    return _rmDayOut('refused', 'day-not-found');
  }
  if (allDays.length <= 1) {
    window.showToast?.("That's the job's only scheduled day — use Delete to remove the whole job.", 'error');
    return _rmDayOut('refused', 'only-day');
  }

  const ms = _jobDayDateMs(day);
  if (ms === null) {
    // No date means the ticket guard's string join has nothing to compare and
    // the range re-derive below has nothing to place. Refuse rather than remove
    // a day we cannot check. (Legacy undated rows exist — _pickFirstJobDayByDate.)
    window.showToast?.('That day has no date on it — open it and set a date before removing it.', 'error');
    return _rmDayOut('refused', 'no-date');
  }
  const dayStr = formatDateISO(new Date(ms));
  const label = _formatJobDayLabel(day) || dayStr;

  // ── ticket guard ────────────────────────────────────────────────────────
  const found = await _findTicketsCoveringDay(jobId, dayStr);
  if (!found.ok) {
    _showDeleteBlockAlert(
      `Can't remove ${label}: we couldn't check whether that day is on a billing ticket.`,
      'Check your connection and try again.',
      _to
    );
    return _rmDayOut('refused', 'ticket-check-failed', dayStr);
  }
  if (found.covering.length > 0) {
    const billed = found.covering.find(_ticketIsBilled);
    const t = billed || found.covering[0];
    const which = t.invoiceNumber ? `invoice ${t.invoiceNumber}`
      : `${t.status || (billed ? 'archived' : 'draft')} ticket${t.ticketNo ? ` #${t.ticketNo}` : ''}`;
    _showDeleteBlockAlert(
      `Can't remove ${label}: that day's work is on ${which}.`,
      billed
        ? 'Unarchive or credit that ticket in Billing first, then remove the day.'
        : 'Delete or move that ticket in Billing first, then remove the day.',
      _to
    );
    return _rmDayOut('refused', 'ticketed', dayStr);
  }

  // ── clock guard ─────────────────────────────────────────────────────────
  // clockEntries carry jobId, NOT jobDayId, so bucket by the clock-in's LOCAL
  // day — the same join _confirmJobDayDeletions uses. Query by jobId only:
  // single-field, no composite index, so it cannot throw the way the TASK-959
  // composite did and fail open.
  let activeOnDay = 0, hoursOnDay = 0;
  try {
    const clockSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId))
    );
    clockSnap.forEach(d => {
      const x = d.data();
      const ci = x.clockIn?.toDate?.();
      if (!ci || formatDateISOLocal(ci) !== dayStr) return;
      if (x.clockOut == null && x.status !== 'completed') activeOnDay++;
      else hoursOnDay += (Number(x.totalHours) || 0);
    });
  } catch (err) {
    // FAIL CLOSED. The first draft only set `hoursUnknown` and carried on, which
    // meant a clockEntries read that threw skipped the clocked-in check entirely
    // — the operation whose failure mode is DELETION was the one guard willing
    // to proceed unproven, while the ticket guard right above it refuses. Two
    // COD lanes caught the inconsistency independently (Code Reviewer ❌,
    // Domain ⚠️, 2026-09-08). "Couldn't prove nobody is on it" is not "nobody
    // is on it".
    console.warn('[Dispatch] TASK-4792 clock lookup failed for day removal — refusing:', err);
    _showDeleteBlockAlert(
      `Can't remove ${label}: we couldn't check whether anyone is clocked in on it.`,
      'Check your connection and try again.',
      _to
    );
    return _rmDayOut('refused', 'clock-check-failed', dayStr);
  }
  if (activeOnDay > 0) {
    _showDeleteBlockAlert(
      `Can't remove ${label}: ${activeOnDay === 1 ? 'someone is' : `${activeOnDay} people are`} clocked in on it.`,
      'Clock them out first, then remove the day.',
      _to
    );
    return _rmDayOut('refused', 'clocked-in', dayStr);
  }

  // ── confirm — name what is on the day, don't ask "are you sure" ──────────
  const crew = [...(Array.isArray(day.drivers) ? day.drivers : []),
                ...(Array.isArray(day.helpers) ? day.helpers : [])]
    .map(id => String(getPersonNameById(id) || '').trim()).filter(Boolean);
  const unitName = (list, id) => {
    const m = (list || []).find(x => x && x.id === id);
    return String(window.getUnitDisplay?.(m) || m?.unit || m?.nickname || m?.name || '').trim();
  };
  const gear = [
    ...(Array.isArray(day.trucks) ? day.trucks : []).map(id => unitName(DispatchState.trucks, id)),
    ...(Array.isArray(day.trailers) ? day.trailers : []).map(id => unitName(DispatchState.trailers, id)),
  ].filter(Boolean);

  const onIt = [];
  if (crew.length) onIt.push(crew.join(', '));
  if (gear.length) onIt.push(gear.join(', '));
  const remaining = allDays.length - 1;
  const staysLine = `The other ${remaining} day${remaining === 1 ? '' : 's'} stay${remaining === 1 ? 's' : ''} exactly as ${remaining === 1 ? 'it is' : 'they are'}.`;
  let message = onIt.length
    ? `Remove ${label} from this job? ${onIt.join(' · ')} come off that day.\n\n${staysLine}`
    : `Remove ${label} from this job? Nothing is assigned to it.\n\n${staysLine}`;
  if (hoursOnDay > 0) {
    message = `${hoursOnDay.toFixed(1)}h was logged on ${label}. That time stays on the timesheet for payroll but detaches from this day.\n\n${message}`;
  }
  // TASK-9943 — the button stops saying "Checking…" the moment the question is up.
  if (typeof _rc.idle === 'function') _rc.idle();
  // TASK-9943 — the crew editor's Undo already asked "Remove the day you just
  // added?"; asking again would be the same question twice. Every guard above
  // still ran; only the wording dialog is skipped.
  const confirmed = _rc.preConfirmed === true ? true : await window.showConfirm('Remove This Day', message, {
    confirmText: 'Remove Day', cancelText: 'Keep It', danger: true
  });
  if (!confirmed) return _rmDayOut('cancelled', 'kept', dayStr);

  // TASK-4793 — outside the demo the SERVER removes the day (removeJobDay):
  // it re-runs the only-day / ticket / clocked-in checks with committed data and
  // writes the delete, the range, removedDates, a ledger job's rentals and the
  // JOB_DAY_DELETE audit row in one transaction. firestore.rules refuse a
  // client delete of a day still inside its job's range, so the batch below is
  // the demo's path only.
  if (!isDemoMode() && tenant !== 'demo') {
    return _removeJobDayViaServer(jobId, jobDayId, { tenant, label, dayStr, day, to: _to, source: _rc.source || 'dispatch-remove-day' });
  }

  // Snapshot BEFORE the delete — the audit row is built from it, and the
  // snapshot listener drops the row from DispatchState the moment the doc goes.
  const daySnapshot = { ...day };

  // 🔴 RE-READ THE SCHEDULE AFTER THE CONFIRM. The dialog is where the seconds
  // go, and every guard above ran before it. Two dispatchers each removing the
  // OTHER day of the same two-day job would both have passed the "not the only
  // day" check and between them deleted every day, leaving a job with no
  // schedule and a range describing days that no longer exist (COD Code
  // Reviewer ❌, 2026-09-08). Firestore's batch has no version precondition, so
  // this cannot be made airtight client-side — but the practical window is the
  // human's reading time, and re-reading immediately before the write closes
  // it. Fail CLOSED on a read that throws: the last check before a delete is
  // the last place to guess.
  let liveDays;
  try {
    const _reSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'jobDays'), where('jobId', '==', jobId))
    );
    liveDays = _reSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Dispatch] TASK-4792: pre-write jobDays re-read failed — refusing:', err);
    window.showToast?.("Couldn't confirm the job's schedule just now — nothing was removed. Try again.", 'error');
    return _rmDayOut('refused', 'recheck-failed', dayStr);
  }
  if (!liveDays.some(d => d.id === jobDayId)) {
    window.showToast?.(`${label} is already gone — somebody else removed it.`, 'info');
    _renderDispatchBoardNow();
    return _rmDayOut('refused', 'already-gone', dayStr);
  }
  if (liveDays.length <= 1) {
    window.showToast?.(`${label} is now the job's only day — removing it would delete the job, so nothing was changed.`, 'error');
    return _rmDayOut('refused', 'only-day', dayStr);
  }

  // The days that SURVIVE, and the range they imply. Computed before any write
  // so the batch below can carry the range in the SAME commit as the delete.
  const survivors = liveDays
    .filter(d => d.id !== jobDayId)
    .map(d => _jobDayDateMs(d))
    .filter(v => v !== null)
    .sort((a, b) => a - b);
  const newRange = survivors.length
    ? {
      start: formatDateISO(new Date(survivors[0])),
      end: formatDateISO(new Date(survivors[survivors.length - 1]))
    }
    : { start: null, end: null };

  try {
    // 🔴 ONE COMMIT for the delete AND the range write. They were two separate
    // awaits in the first draft, which left a real window: the jobDay is gone,
    // the range write fails, and the job now spans a date with no day on it —
    // precisely the state that lets saveJob's date-sync recreate the removed day
    // (COD Code Reviewer ❌, 2026-09-08). A writeBatch is all-or-nothing, so the
    // day and the range that describes it can never disagree.
    const batch = writeBatch(db);
    batch.delete(doc(db, 'tenants', tenant, 'jobDays', jobDayId));

    // 🔴 removedDates — the RESURRECTION FIX, and the reason min/max alone is
    // not enough. Three COD lanes landed on the same hole independently: the
    // range re-derive protects the ENDS of the range and nothing else. Remove
    // Sep 7 from a Sep 6–8 job and the survivors still span Sep 6–8, so the
    // range is unchanged and saveJob's create loop (~:9345) rebuilds Sep 7 on
    // the next date edit — the exact resurrection this function exists to
    // prevent, for the interior case the first draft's own header claimed was
    // covered. It was not.
    //
    // So the removal is RECORDED, not merely implied by the dates. saveJob's
    // create loop skips any date listed here; `_applyAddDayJobRange` clears the
    // date from the list whenever a dispatcher deliberately adds that day back,
    // so the list can never trap a day the dispatcher actually wants. Note this
    // also closes a PRE-EXISTING hole of the same shape: add-day widens the
    // range without filling the interior, so gap days (HANSEN SWD is missing
    // Sep 7 and Sep 8 inside a Sep 6–9 range right now) already get invented by
    // the next date edit. That older gap is filed separately — this field only
    // speaks for days somebody explicitly removed.
    const jobUpdate = { updatedAt: Timestamp.now(), removedDates: arrayUnion(dayStr) };
    if (survivors.length) {
      const startTs = coerceJobTimestamp(new Date(survivors[0]));
      const endTs = coerceJobTimestamp(new Date(survivors[survivors.length - 1]));
      if (startTs) jobUpdate.startDate = startTs;
      if (endTs) jobUpdate.endDate = endTs;
    }
    batch.update(doc(db, 'tenants', tenant, 'jobs', jobId), jobUpdate);

    // TASK-9336 — a LEDGER job does the delete, the range write, AND the rental
    // settlement in ONE transaction (the batch above is then never committed):
    // the day, its range, the stock, the ledger and any inspection it closes
    // land together or not at all, and the decision reads the day's status as
    // committed at that moment — not the copy loaded before the confirm. A
    // read that throws lands in the catch below with nothing written.
    const _remHasGear = liveDays.some(d => d.id === jobDayId && Array.isArray(d.assets) && d.assets.length > 0);
    const _jobSnapForLedger = _remHasGear ? await getDoc(doc(db, 'tenants', tenant, 'jobs', jobId)) : null;
    const _ledgerJob = !!_jobSnapForLedger && _jobSnapForLedger.exists() && _jobHasRentalLedger(_jobSnapForLedger.data());
    let _ledgerRes = null;
    if (_ledgerJob) {
      _ledgerRes = await _rentalLedgerLeaveTx(tenant, jobId, jobDayId, 'remove', { jobUpdate });
      if (_ledgerRes.outcome === 'gone') {
        window.showToast?.(`${label} is already gone — somebody else removed it.`, 'info');
        _renderDispatchBoardNow();
        return _rmDayOut('refused', 'already-gone', dayStr);
      }
      _mirrorRentalAssets(_ledgerRes.changes);
    } else {
      await batch.commit();
    }

    // 🔴 RENTAL REVERSAL RUNS AFTER THE COMMIT, NOT BEFORE. It used to run
    // first, which meant a failed batch left the units already given back
    // while the day still existed — and a retry gave them back a second time.
    // (COD Code Reviewer ❌, 2026-09-08.) The delete is the authoritative act,
    // so it lands first; if the reversal below then fails, the inventory is the
    // only thing wrong, it is stated out loud, and a human can correct it.
    // These writes are NOT in the batch because processRentalTransaction does
    // its own reads and transaction bookkeeping.
    // Rental reversal, mirroring cancelJobDay (~:19492) — a removed day must
    // give its rental units back exactly like a cancelled one does. A deploy is
    // reversed; a pending return inspection is cancelled.
    //
    // 🔴 SKIPPED ENTIRELY on an already-cancelled day. cancelJobDay ALREADY ran
    // this exact reversal when the day was cancelled; running it again on the
    // removal would return the same units a SECOND time and inflate on-hand
    // quantity by qty per unit. (COD Code Reviewer ❌, 2026-09-08 — a cancelled
    // day is a completely ordinary thing to then remove from the schedule, so
    // this is the common path, not an exotic one.)
    // Its own try — the day is ALREADY gone by now, so a rental failure must
    // not be reported as "error removing that day" and must not skip the audit
    // row. Say exactly what did and did not happen.
    try {
      // TASK-9336 — read the day as re-read AFTER the confirm (`liveDays`), not
      // the copy loaded before it: a day cancelled while the dialog sat open
      // had already given its units back, and the stale status re-reversed it.
      // A ledger job settled inside its transaction above and skips this.
      const _remDay = liveDays.find(d => d.id === jobDayId) || day;
      if (!_ledgerJob) {
        await _removedDayPreLedgerReversal(tenant, jobId, jobDayId, _remDay, label,
          (assetId) => liveDays.some(d =>
            d.id !== jobDayId
            && Array.isArray(d.assets) && d.assets.includes(assetId)
            && (d.assetDirections?.[assetId] || 'deploy') === 'return'
          ));
      }
    } catch (rentErr) {
      console.error('[Dispatch] TASK-4792: rental reversal failed AFTER the day was removed:', rentErr, { jobId, jobDayId });
      window.logError?.(rentErr, { module: 'dispatch', phase: 'removeday-rental-reversal', jobId });
      window.showToast?.(`${label} was removed, but the rental units on it were not returned to stock — `
        + 'check Assets and adjust the count by hand.', 'warning');
    }


    const jobRef = DispatchState.jobs.find(j => j.id === jobId);
    await _auditJobDayDateSyncDeletes({
      tenant,
      jobId,
      source: _rc.source || 'dispatch-remove-day',
      detailLead: 'Removed 1 scheduled day from the job',
      doomed: [[dayStr, { id: jobDayId, data: () => daySnapshot }]],
      jobTitle: jobRef?.title || null,
      customerName: jobRef?.customer || null,
      newRange
    });
    // Optimistic local removal so the details modal repaints without waiting on
    // the snapshot round-trip; the listener overwrites this with the truth.
    DispatchState.jobDays = DispatchState.jobDays.filter(d => d.id !== jobDayId);
    window.showToast?.(`Day removed: ${label}`, 'success');
    if (!document.getElementById('modal-job-details')?.classList.contains('hidden')) {
      showJobDetails(jobId);
    }
    _renderDispatchBoardNow();
    return { outcome: 'removed', reason: null, date: dayStr, newRange };
  } catch (error) {
    console.error('[Dispatch] Remove day error:', error);
    window.showToast?.(_rentalWriteRefusedMsg(error, 'remove that day') || 'Error removing that day', 'error');
    return _rmDayOut('error', String(error?.code || 'write-failed'), dayStr);
  }
}

// TASK-4792 / TASK-4793 — a removed day's PRE-LEDGER rentals go back, AFTER the
// day is gone (moved out of _removeJobDayRun unchanged so the demo batch and the
// server removal run the same loop). Skipped on an already-cancelled day:
// cancelJobDay already returned those units. `stillReturning(assetId)` = a
// surviving day of this job still asks for a return of that asset. Throws on a
// failed write — the caller reports that the day is gone and the stock is not.
async function _removedDayPreLedgerReversal(tenant, jobId, jobDayId, _remDay, label, stillReturning) {
  if (!_remDay || _remDay.status === 'cancelled' || !Array.isArray(_remDay.assets) || _remDay.assets.length === 0) return;
  for (const assetId of _remDay.assets) {
    const asset = DispatchState.assets.find(a => a.id === assetId);
    // 🔴 An id we cannot resolve is NOT "not a rental" — it is "we don't
    // know", and the first draft could not tell the two apart: an assets
    // stream that had not loaded (or an asset since removed from the
    // roster) skipped the reversal in silence. Say it out loud; the day is
    // already gone by here, so a human has to be the backstop.
    // (COD Code Reviewer ❌, 2026-09-08.)
    if (!asset) {
      console.warn('[Dispatch] TASK-4792: asset on the removed day is not in local state — '
        + 'reversal skipped, cannot tell whether it was a rental', { jobId, jobDayId, assetId });
      window.showToast?.(`${label} was removed, but one of its assets could not be looked up — `
        + 'check Assets if it was a rental.', 'warning');
      continue;
    }
    if (!(asset.quantityType === 'rental' || asset.isRental)) continue;
    const dir = _remDay.assetDirections?.[assetId] || 'deploy';
    const qty = _remDay.assetQtys?.[assetId] || 1;
    if (dir === 'deploy') {
      await processRentalTransaction(asset, 'return', qty, jobId, true);
    } else if (dir === 'return') {
      // 🔴 Day-scoped, unlike cancelJobDay's. `returnInspections` carries
      // assetId + jobId and NO day pointer, so the query matches every
      // pending inspection for this asset on this job — including one a
      // SURVIVING day owns. Removing one day would then cancel another
      // day's pending return, and that day would still render as a return
      // with no inspection behind it. So: only cancel when no surviving day
      // of this job still asks for a return of this asset. (COD Code
      // Reviewer ❌, 2026-09-08. cancelJobDay has the same unscoped query —
      // that is pre-existing and filed separately, not silently inherited
      // here.)
      if (stillReturning(assetId)) continue;
      const riSnap = await getDocs(query(
        collection(db, 'tenants', tenant, 'returnInspections'),
        where('assetId', '==', assetId), where('jobId', '==', jobId), where('status', '==', 'pending')
      ));
      for (const riDoc of riSnap.docs) {
        await updateDoc(doc(db, 'tenants', tenant, 'returnInspections', riDoc.id), {
          status: 'cancelled', updatedAt: Timestamp.now()
        });
      }
    }
  }
}

// TASK-4793 — the server half of Remove Day (outside the demo). The browser's
// guards above already ran (fast answers + the hours warning); the callable
// re-runs only-day / ticket / clocked-in on committed data and does the whole
// removal in one transaction, so its answer is the one that counts. A refusal
// says why and that nothing was removed; a pre-ledger job's rentals go back
// here afterwards, from the day AS COMMITTED (the server's copy), like before.
const _RM_DAY_REASON = {
  only_day: 'only-day', no_date: 'no-date', ticketed: 'ticketed', clocked_in: 'clocked-in',
  not_found: 'job-gone', invalid: 'invalid', demo: 'demo',
};
async function _removeJobDayViaServer(jobId, jobDayId, c) {
  const { tenant, label, dayStr, to } = c;
  const out = (outcome, reason, date) => ({ outcome, reason: reason || null, date: date || null });
  let res;
  try {
    res = (await httpsCallable(functions, 'removeJobDay')({ tenantId: tenant, jobId, jobDayId, source: c.source }))?.data || null;
  } catch (error) {
    const code = String(error?.code || '');
    const reason = error?.details?.reason || null;
    // A deliberate refusal (checks, access) carries the server's own words.
    if (/^functions\/(failed-precondition|permission-denied|not-found|invalid-argument)$/.test(code) && error?.message) {
      _showDeleteBlockAlert(error.message, 'Nothing was removed.', to);
      const why = reason === 'rental_counts' ? 'rental-counts'
        : (code === 'functions/permission-denied' ? 'no-access' : (_RM_DAY_REASON[reason] || 'server-refused'));
      return out('refused', why, dayStr);
    }
    // A lost reply is NOT "nothing changed": the commit may have landed (COD r1).
    // Look at the day itself before saying anything, and never report "not
    // removed" (the door's REFUSED audit row) for a day that is gone (COD r2).
    console.error('[Dispatch] TASK-4793 removeJobDay failed:', error);
    window.logError?.(error, { module: 'dispatch', phase: 'remove-day-server', jobId });
    let still = null;
    try { still = (await getDoc(doc(db, 'tenants', tenant, 'jobDays', jobDayId))).exists(); } catch (_) { still = null; }
    if (still === true) {
      window.showToast?.(`${label} was not removed. Check your connection and try again.`, 'error');
      return out('error', code || 'call-failed', dayStr);
    }
    if (still === false) {
      // It committed. The server's reply (and with it the pre-ledger rental
      // give-back) is lost, so say so rather than guess at stock.
      DispatchState.jobDays = DispatchState.jobDays.filter(d => d.id !== jobDayId);
      const hadGear = Array.isArray(c.day?.assets) && c.day.assets.length > 0;
      window.showToast?.(hadGear
        ? `${label} was removed, but we couldn't confirm its rental units went back to stock — check Assets.`
        : `Day removed: ${label}`, hadGear ? 'warning' : 'success');
      _renderDispatchBoardNow();
      return { outcome: 'removed', reason: 'reply-lost', date: dayStr, newRange: null };
    }
    window.showToast?.(`Couldn't confirm whether ${label} was removed. Check the board before trying again.`, 'error');
    return out('unconfirmed', code || 'call-failed', dayStr);
  }
  if (!res || res.outcome === 'gone') {
    window.showToast?.(`${label} is already gone — somebody else removed it.`, 'info');
    _renderDispatchBoardNow();
    return out('refused', 'already-gone', dayStr);
  }
  if (res.outcome !== 'removed') {
    window.showToast?.("Couldn't confirm the day was removed. Check the board before trying again.", 'error');
    return out('error', 'unexpected-reply', dayStr);
  }
  if (res.ledger) {
    if (Array.isArray(res.rentalChanges)) _mirrorRentalAssets(res.rentalChanges);
  } else {
    try {
      const keep = new Set(Array.isArray(res.stillReturning) ? res.stillReturning : []);
      await _removedDayPreLedgerReversal(tenant, jobId, jobDayId, res.day || null, label, (assetId) => keep.has(assetId));
    } catch (rentErr) {
      console.error('[Dispatch] TASK-4793: rental reversal failed AFTER the day was removed:', rentErr, { jobId, jobDayId });
      window.logError?.(rentErr, { module: 'dispatch', phase: 'removeday-rental-reversal', jobId });
      window.showToast?.(`${label} was removed, but the rental units on it were not returned to stock — `
        + 'check Assets and adjust the count by hand.', 'warning');
    }
  }
  // The JOB_DAY_DELETE audit row was written by the server, in the same commit.
  DispatchState.jobDays = DispatchState.jobDays.filter(d => d.id !== jobDayId);
  window.showToast?.(`Day removed: ${label}`, 'success');
  if (!document.getElementById('modal-job-details')?.classList.contains('hidden')) {
    showJobDetails(jobId);
  }
  _renderDispatchBoardNow();
  return { outcome: 'removed', reason: null, date: res.date || dayStr, newRange: res.newRange || null };
}

// ── TASK-9943 ─────────────────────────────────────────────────────────────
// Steve Alandt (TITS, 10/18) "couldn't remove" a day he had added to the wrong
// job. His access passed every gate and Remove Day worked in a repro, so the
// fixes are about what the screen SAYS and where the control IS:
//   • removeJobDay is now the DOOR. It puts the tapped button into a
//     "Checking…" state the moment it is tapped (the guard chain runs up to
//     four reads before its confirm appears: 0.8s in Chromium, 6.6s in WebKit,
//     which on a phone looks like a dead button), stops a second tap, and
//     records a refused or cancelled remove in jobAuditLog. The guards
//     themselves live in _removeJobDayRun, unchanged.
//   • opts.alertTo picks where a refusal lands ('job-details' footer by
//     default, the Edit Job Remove Day sheet, or 'toast'), opts.source labels
//     the JOB_DAY_DELETE row, opts.preConfirmed skips the wording dialog for
//     the crew editor's Undo (which already asked).
// Returns { outcome: 'removed' | 'refused' | 'cancelled' | 'error' | 'busy',
// reason, date } so a caller can repaint.
const _rmDayInFlight = new Set();

// The tapped control says it is working until the confirm is up or it fails.
// Restores exactly what was there. Safe on a button that has been re-rendered
// away (restoring a detached node is a no-op to the user).
function _rmDayBusy(btn) {
  if (!btn || typeof btn !== 'object' || !('disabled' in btn)) return () => {};
  const prevHtml = btn.innerHTML;
  const prevDisabled = !!btn.disabled;
  btn.disabled = true;
  btn.classList?.add('is-busy');
  btn.setAttribute?.('aria-busy', 'true');
  btn.innerHTML = '<span class="material-icons" aria-hidden="true">hourglass_top</span>'
    + '<span class="job-day-remove-label">Checking…</span>';
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    btn.innerHTML = prevHtml;
    btn.disabled = prevDisabled;
    btn.classList?.remove('is-busy');
    btn.removeAttribute?.('aria-busy');
  };
}

// One jobAuditLog row for a day event the other writers do not cover:
// JOB_DAY_ADD, JOB_DAY_REMOVE_REFUSED, JOB_DAY_REMOVE_CANCELLED. Same shape and
// same `user` expression as every other jobAuditLog writer (firestore.rules
// jobAuditActorIsSelf). Best effort and never throws: a failed history row must
// not undo or block the add/remove the dispatcher asked for — but it is logged,
// not swallowed.
function _auditJobDayEvent(action, f) {
  const tenant = DispatchState.tenantId;
  if (!tenant || !f || !f.jobId) return Promise.resolve(false);
  const job = (DispatchState.jobs || []).find(j => j.id === f.jobId)
    || (DispatchState.searchJobs || []).find(j => j.id === f.jobId);
  let body;
  try {
    body = _stripUndefinedDeep({
      action,
      source: f.source || 'dispatch',
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'unknown',
      jobId: f.jobId,
      jobDayId: f.jobDayId || null,
      jobTitle: job?.title || null,
      customerName: job?.customer || null,
      date: f.date || null,
      reason: f.reason || null,
      detail: f.detail || null,
      ...(f.extra && typeof f.extra === 'object' ? f.extra : {}),
    });
  } catch (be) {
    console.warn('[Dispatch] TASK-9943 day audit row build failed:', be);
    return Promise.resolve(false);
  }
  return addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), body)
    .then(() => true)
    .catch((ae) => {
      console.warn(`[Dispatch] TASK-9943 ${action} audit row failed:`, ae);
      window.logError?.(ae, { module: 'dispatch', phase: 'jobday-event-audit', action, jobId: f.jobId });
      return false;
    });
}

async function removeJobDay(jobId, jobDayId, opts) {
  const o = (opts && typeof opts === 'object') ? opts : {};
  // One removal per JOB at a time, not per day (COD Code Reviewer r1): two
  // different days of one job removed at once could each pass the "not the
  // last day" re-read and between them empty the job.
  const key = String(jobId || jobDayId || '');
  if (_rmDayInFlight.has(key)) {
    window.showToast?.('Still checking a day on this job — one moment.', 'info');
    return { outcome: 'busy', reason: 'in-flight', date: null };
  }
  _rmDayInFlight.add(key);
  try { _ensureJobDayRemoveStyles(); } catch (_) { /* styles are cosmetic */ }
  const idle = _rmDayBusy(o.button);
  const source = o.source || 'dispatch-remove-day';
  let out;
  try {
    out = await _removeJobDayRun(jobId, jobDayId, {
      alertTo: o.alertTo, source, preConfirmed: o.preConfirmed === true, idle
    });
  } catch (err) {
    // The guard chain reports its own refusals; anything that escapes it is a
    // read or code failure BEFORE the write (the write has its own catch).
    console.error('[Dispatch] TASK-9943 remove day failed:', err);
    window.logError?.(err, { module: 'dispatch', phase: 'remove-day', jobId });
    window.showToast?.("Couldn't remove that day. Check your connection and try again.", 'error');
    out = { outcome: 'error', reason: String(err?.code || err?.message || 'unknown').slice(0, 120), date: null };
  } finally {
    idle();
    _rmDayInFlight.delete(key);
  }
  if (out && (out.outcome === 'refused' || out.outcome === 'cancelled' || out.outcome === 'error')) {
    // reason is the code (only-day, ticketed, clocked-in, …) — the refusal
    // text the dispatcher saw is in _removeJobDayRun next to each guard.
    const why = out.reason || 'unknown';
    const cancelled = out.outcome === 'cancelled';
    _auditJobDayEvent(cancelled ? 'JOB_DAY_REMOVE_CANCELLED' : 'JOB_DAY_REMOVE_REFUSED', {
      jobId, jobDayId, source, date: out.date, reason: out.reason,
      detail: cancelled
        ? `Started to remove ${out.date || 'a day'}, then kept it`
        : `Tried to remove ${out.date || 'a day'} — not removed: ${why}`,
    });
  }
  return out || { outcome: 'error', reason: 'no-result', date: null };
}

// ── TASK-9943 — lazy UI chunk (views/jobDayUx.js) ─────────────────────────
// Stale-job question, Edit Job Remove Day sheet, and the pure helpers behind
// the crew editor's Undo. Never a static import (Pitfall #1 / perf budget).
let _jdUxMod = null;
let _jdUxLoading = null;
function _jdUx() {
  if (_jdUxMod) return Promise.resolve(_jdUxMod);
  if (!_jdUxLoading) {
    _jdUxLoading = __ddChunkWrap(import('./jobDayUx.js?v=9997945'))
      .then(m => { _jdUxMod = m; return m; })
      .catch(err => { _jdUxLoading = null; console.warn('[Dispatch] TASK-9943 jobDayUx failed to load:', err?.message || err); return null; });
  }
  return _jdUxLoading;
}

// (c) — the crew editor that opens straight after an add-day. Its Cancel and X
// offer to take that day back off instead of leaving it on the board.
let _justAddedDay = null;

async function cancelAssignment() {
  const jobId = document.getElementById('assignment-job-id')?.value || '';
  const jobDayId = document.getElementById('assignment-job-day-id')?.value || '';
  const marker = _justAddedDay;
  const m = marker ? await _jdUx() : null;
  const offer = m ? m.undoOfferFor(marker, jobId, jobDayId, Date.now()) : null;
  if (!offer) { closeModal('assignment'); return; }
  const job = _resolveJobAnywhere(jobId);
  const undo = await window.showConfirm(
    'Remove the day you just added?',
    `You just added ${offer.dateLabel || 'this day'} to ${String(job?.title || 'this job').trim()}. Remove it again? Nothing else on the job changes.`,
    { confirmText: 'Remove the Day', cancelText: 'Keep the Day', danger: true }
  );
  closeModal('assignment');
  if (!undo) return;
  await removeJobDay(jobId, jobDayId, { preConfirmed: true, alertTo: 'toast', source: 'dispatch-undo-add-day' });
}

// (b) — Edit Job → Remove Day. Lists this job's scheduled days, each with the
// SAME removeJobDay (same guards, same confirm). Office only: a PIN driver's
// form never shows the button (the server-side driver tools have no day
// removal), and view & upload members are refused by _guardManageDays.
function _editJobDaysForSheet(jobId) {
  return (DispatchState.jobDays || [])
    .filter(d => d && d.jobId === jobId && d.id && !d.isLegacy)
    .map(d => ({ d, ms: _jobDayDateMs(d) }))
    .filter(x => x.ms !== null)
    .sort((a, b) => a.ms - b.ms)
    .map(({ d, ms }) => {
      const crew = [...(Array.isArray(d.drivers) ? d.drivers : []), ...(Array.isArray(d.helpers) ? d.helpers : [])]
        .map(id => String(getPersonNameById(id) || '').trim()).filter(Boolean);
      return {
        id: d.id,
        label: formatDateDisplay(new Date(ms)) + (d.status === 'cancelled' ? ' (cancelled)' : ''),
        crew: crew.length ? crew.join(', ') : ''
      };
    });
}

async function openEditJobRemoveDay(btn) {
  if (window.AppState?.driverModePin === true) return;
  if (!_guardManageDays()) return;
  const jobId = document.getElementById('job-id')?.value || '';
  if (!jobId) return;
  // A removal moves the job's start/end. If the dispatcher has TYPED new dates
  // into this form, removing a day underneath them would leave two answers to
  // "what are this job's dates", so those must be saved first. Any other
  // unsaved edit is fine: only the two date inputs are moved afterwards.
  if (!_editJobDatesUntouched()) {
    window.showToast?.('You changed the dates on this job. Save first, then remove the day.', 'info', null, 6000);
    return;
  }
  const idle = _rmDayBusy(btn);
  const m = await _jdUx();
  idle();
  if (!m) { window.showToast?.("Couldn't open the day list. Check your connection and try again.", 'error'); return; }
  try { _ensureJobDayRemoveStyles(); } catch (_) { /* cosmetic */ }
  const job = _resolveJobAnywhere(jobId);
  m.openRemoveDaySheet({
    jobTitle: job?.title || '',
    listDays: () => _editJobDaysForSheet(jobId),
    remove: async (dayId, button) => {
      // Captured BEFORE the reads (COD Code Reviewer r1): if the dispatcher
      // closes the sheet, edits the dates or reopens the form while the
      // removal runs, the result must not overwrite what is on screen now.
      const before = _editJobFormDatesStamp();
      const out = await removeJobDay(jobId, dayId, { button, alertTo: 'remove-day-sheet', source: 'dispatch-edit-job-remove-day' });
      if (out?.outcome === 'removed') _syncEditJobFormAfterDayRemove(jobId, out.newRange, before);
      return out;
    }
  });
}

const _EDIT_JOB_DATE_IDS = ['job-start-date', 'job-end-date'];
function _editJobDatesUntouched() {
  if (!_jobFormSnapshot || _jobFormSnapshotGen !== _jobFormGen) return true;
  return _EDIT_JOB_DATE_IDS.every(id => String(document.getElementById(id)?.value ?? '') === _jobFormSnapshot[id]);
}

function _editJobFormDatesStamp() {
  return {
    gen: _jobFormGen,
    jobId: document.getElementById('job-id')?.value || '',
    dates: _EDIT_JOB_DATE_IDS.map(id => String(document.getElementById(id)?.value ?? ''))
  };
}

// After a removal from the Edit Job sheet the job doc's range has moved. Move
// the form's two date inputs (and their min/max) to match and re-baseline ONLY
// those two fields, the _rebaselineJobFormPocFields way: a later Save must not
// write the old range back, and any other unsaved edit stays unsaved. Only when
// it is still the SAME form (generation) for the same job with the date inputs
// untouched since the tap — otherwise the dispatcher's own edit wins and the
// form is left alone (its next Save is checked by saveJob's date-sync as usual).
function _syncEditJobFormAfterDayRemove(jobId, newRange, before) {
  if (document.getElementById('job-id')?.value !== jobId) return;
  if (before) {
    const now = _editJobFormDatesStamp();
    if (now.gen !== before.gen || now.jobId !== before.jobId
      || now.dates.some((v, i) => v !== before.dates[i])) return;
  }
  const s = document.getElementById('job-start-date');
  const e = document.getElementById('job-end-date');
  if (newRange?.start && s) s.value = newRange.start;
  if (newRange?.end && e) e.value = newRange.end;
  if (s && e) { e.min = s.value; s.max = e.value; }
  if (_jobFormSnapshot && _jobFormSnapshotGen === _jobFormGen) {
    for (const id of _EDIT_JOB_DATE_IDS) _jobFormSnapshot[id] = String(document.getElementById(id)?.value ?? '');
  }
}

// (d) — Add Day on a job that has not run in over a week asks whether this is
// the right job. Returns 'add' | 'new' | 'cancel'. A chunk that will not load
// never blocks the add: the question is a safety net, not a gate.
async function _askStaleJobBeforeAddDay(jobId, newDate) {
  const job = DispatchState.jobs.find(j => j.id === jobId);
  if (!job) return 'add';
  const lastDay = _pickLastJobDayByDate(DispatchState.jobDays, jobId);
  const lastDayMs = lastDay ? _jobDayDateMs(lastDay) : null;
  const endMs = _finiteDateFrom(job.endDate)?.getTime() ?? null;
  const lastMs = [lastDayMs, endMs].filter(v => Number.isFinite(v)).reduce((a, b) => Math.max(a, b), -Infinity);
  if (!Number.isFinite(lastMs)) return 'add';
  const m = await _jdUx();
  if (!m) return 'add';
  const lastDate = new Date(lastMs);
  if (!m.staleJobGap(lastDate, newDate, new Date()).ask) return 'add';
  return m.askStaleJob(m.staleJobPrompt({
    lastDate, newDate, today: new Date(), title: job.title, notes: job.notes
  }));
}

// "Start a new job" from that question: the existing Copy to New Job flow,
// with the date the dispatcher just picked filled in.
function _startNewJobFromStaleAddDay(jobId, dateStr) {
  // From inside Edit Job the form is open (and clean — addDayToCurrentJob saves
  // a dirty one first). Close it the way duplicateCurrentJob does, honouring a
  // refusal (permit upload in flight).
  if (!document.getElementById('modal-job')?.classList.contains('hidden') && !closeModal('job')) return;
  duplicateJob(jobId, {
    onPrefilled: () => {
      const s = document.getElementById('job-start-date');
      const e = document.getElementById('job-end-date');
      if (s) { s.value = dateStr; s.dispatchEvent(new Event('change', { bubbles: true })); }
      if (e) { e.value = dateStr; e.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  });
}

function duplicateCurrentJob() {
  const jobId = document.getElementById('job-id').value;
  if (jobId) {
    // 🔴 TASK-9151 — HONOUR THE REFUSAL. closeModal can decline (an upload in
    // flight, or unresolved permits waiting on a confirm that has not resolved
    // yet) and duplicateJob calls showAddJob, which calls resetPermitUploadState
    // and wipes the tray. Firing it regardless turns "the modal refused to close"
    // into "the modal was rebuilt underneath you and your files are gone".
    if (!closeModal('job')) return;
    // TASK-9703 — a PIN driver's copy is created through driverSaveJob.
    if (window.AppState?.driverModePin === true) { _driverCopyJob(jobId); return; }
    duplicateJob(jobId);
  }
}

// TASK-1408 — jobDays CREATE was the only write in this family that did NOT exclude
// viewUploadOnly: create allowed any `dispatch` member (firestore.rules ~:1892) while
// update AND delete both required `!isViewUploadOnly`. So a view-and-upload member
// could add a day and then be refused when they saved it — a half-created day and an
// error they had no way to act on. TASK-1331 improved the wording of that error; it
// did not stop the day being created.
//
// The flag's own promise is "no edit/delete of MODULE-granted data" (firestore.rules
// ~:345), and every sibling collection — catalog, vehicleHolds, returnInspections —
// already excludes it everywhere. Creating a job day is dispatch work, so create was
// the oversight, not update. The house pattern is client hides the affordance, server
// rejects the write (hrCanManageModule, app.js ~:18097; equipmentNotes.js ~:76) —
// this is the client half. Absent memberAccess = owner / legacy-admin = unrestricted.
function _dispatchCanManageDays() {
  const ma = window.AppState?.memberAccess;
  if (!ma) return true;
  return ma.viewUploadOnly !== true;
}

// Backstop for every add-day entry point, so a render path I did not gate can still
// never produce the half-created day. Says what to do next rather than just refusing.
function _guardManageDays() {
  if (_dispatchCanManageDays()) return true;
  window.showToast?.('Your access is view & upload only — ask an admin to add a day to this job', 'error');
  return false;
}

// TASK-9236 — "mike keeps forgetting" how to take one day off a job. The only
// entry point used to be an unlabeled × glyph drawn ONLY on multi-day jobs,
// taught by a hint that ALSO rendered only on multi-day jobs: learn it once,
// open a one-day job, find nothing, conclude you misremembered.
//
// Now a labeled "Remove Day" control on every real day row for anyone who can
// manage days. Same removeJobDay call, same guards — this changes how the
// control LOOKS and WHEN it is drawn, never what it does.
//
//   • view-and-upload member -> NOTHING rendered. House pattern is hide, not
//     disable; a disabled control the role can never use is the TASK-9114
//     hide-vs-display leak. Absent, not a [hidden] attribute.
//   • synthesized legacy row -> nothing (no Firestore doc to delete).
//   • one day SHOWING -> muted with a reason, but still clickable.
//     🔴 `windowedCount` is the board's DATE-WINDOWED day list, not the job's
//     full schedule: a job with one in-window day and older days outside the
//     window CAN legally lose this day. So the reason never claims "this is
//     the job's only day" — it says one day is showing — and a tap runs
//     removeJobDay, whose full-schedule read gives the authoritative answer
//     (its only-day refusal toast, or the normal confirm if older days exist).
//   • two or more showing -> the normal labeled control.
//
// event.stopPropagation() is load-bearing: the row body is showAssignment, so
// without it a tap on Remove Day would ALSO open crew assignment.
// TASK-10288 F1 — the Job details day row's status pill. A TENTATIVE day said
// "Scheduled" here while the board card said "Tentative · Confirm" (J-1355).
// Tentative = the board's amber dashed styling, and a one-tap Confirm for
// anyone who can manage days (confirmJobDay — the board chip's own write). A
// cancelled day reads "Cancelled" (its Restore is the Cancel Day control's
// other face, F2). stopPropagation: the row body opens Assign Crew.
function _jobDayDetailsStatusHtml(jobId, day, canManage) {
  if (!day) return '';
  const st = String(day.status || 'scheduled');
  if (st.toLowerCase() === 'cancelled') return `<span class="job-day-status cancelled">Cancelled</span>`;
  if (isTentativeJobDay(day)) {
    const label = isStaleTentativeJobDay(day) ? 'Tentative — date passed' : 'Tentative';
    const style = 'background:rgba(245,158,11,0.15);color:var(--text-primary);border:1px dashed #F59E0B;text-transform:none;';
    if (canManage && day.id && !day.isLegacy && !String(day.id).startsWith('legacy-')) {
      return `<button type="button" class="job-day-status job-day-status-tentative" style="${style}cursor:pointer;font:inherit;font-size:0.8125rem;font-weight:500;" title="Tentative: the crew has not been texted. Tap to confirm the day and send the normal notice." onclick="event.stopPropagation(); window.DispatchModule.jobDayActionFromDetails('confirm', '${escJsAttr(jobId)}', '${escJsAttr(day.id)}')">${label} · <span style="text-decoration:underline;">Confirm</span></button>`;
    }
    return `<span class="job-day-status job-day-status-tentative" style="${style}">${label}</span>`;
  }
  return `<span class="job-day-status ${escapeHtml(st)}">${escapeHtml(st)}</span>`;
}

// TASK-10288 F2 — per-day Cancel Day / Restore Day on the Job details row, so
// "call this day off but keep it on the board" is not only a board right-click
// (the panel offered just Remove Day / Delete, which take the day off the
// board). Same gate as Remove Day (_dispatchCanManageDays; never a synthesized
// legacy row) and the existing writes (cancelJobDay / restoreJobDay). Same
// look as Remove Day; placed before it: Assign · Cancel Day · Remove Day.
function _renderJobDayCancelControl(jobId, day, canManage) {
  if (!canManage || !day || day.isLegacy || !day.id || String(day.id).startsWith('legacy-')) return '';
  const cancelled = String(day.status || '').toLowerCase() === 'cancelled';
  const action = cancelled ? 'restore' : 'cancel';
  const label = cancelled ? 'Restore Day' : 'Cancel Day';
  const title = cancelled
    ? 'Put this day back on the schedule.'
    : 'Call this day off but keep it on the board, marked cancelled. Restore it any time.';
  const escA = (typeof window !== 'undefined' && window.escAttr) || escapeHtml;
  return `
                <button type="button" class="job-day-remove job-day-remove-labeled job-day-cancel"
                        title="${escA(title)}" aria-label="${escA(label)}"
                        onclick="event.stopPropagation(); window.DispatchModule.jobDayActionFromDetails('${action}', '${escJsAttr(jobId)}', '${escJsAttr(day.id)}')">
                  <span class="material-icons" aria-hidden="true">${cancelled ? 'undo' : 'cancel'}</span>
                  <span class="job-day-remove-label">${label}</span>
                </button>`;
}

// TASK-10288 F — run the existing day action, then repaint the open Job details
// panel once the day's state has landed locally (confirm mirrors at once;
// cancel / restore arrive through the live jobDays listener).
async function jobDayActionFromDetails(action, jobId, jobDayId) {
  const find = () => _resolveJobDayAnywhere(jobDayId); // TASK-10288 fold 3 — search-opened jobs too
  const before = find();
  const sig = (d) => d ? `${d.status || ''}|${d.tentative === true}` : '';
  const was = sig(before);
  if (action === 'cancel') {
    // TASK-10288 fold 4 — refuse a clocked / ticketed / invoiced day, like the move.
    const jd = before;
    if (!jd) { window.showToast?.("Couldn't read that day — refresh and try again.", 'error'); return; }
    const g = await _checkDayCancelGuard(jobId, jd);
    if (!g.ok) {
      if (window.showAlert) await window.showAlert("Can't Cancel Day", g.message);
      else window.showToast?.(g.message, 'error', null, 9000);
      return;
    }
  }
  if (action === 'confirm') await confirmJobDay(jobDayId);
  else if (action === 'cancel') await cancelJobDay(jobDayId);
  else if (action === 'restore') await restoreJobDay(jobDayId);
  else return;
  const open = () => !document.getElementById('modal-job-details')?.classList.contains('hidden');
  for (let i = 0; i < 12; i++) {
    if (!open()) return;
    if (sig(find()) !== was) break;
    await new Promise(r => setTimeout(r, 250));
  }
  if (open()) showJobDetails(jobId);
}

function _renderJobDayRemoveControl(jobId, day, windowedCount, dateLabel, canManage) {
  if (!canManage || !day || day.isLegacy || !day.id) return '';
  const single = !(windowedCount > 1);
  const escA = (typeof window !== 'undefined' && window.escAttr) || escapeHtml;
  const date = String(dateLabel || '');
  const title = single
    ? 'Only one day is showing for this job — a job needs at least one day. To remove the whole job, use Delete below.'
    : 'Take just this day off the job. The other days stay as they are.';
  const aria = single
    ? `Remove ${date} from this job (only day showing)`
    : `Remove ${date} from this job`;
  return `
                <button type="button" class="job-day-remove job-day-remove-labeled${single ? ' is-single' : ''}"
                        title="${escA(title)}" aria-label="${escA(aria)}"
                        onclick="event.stopPropagation(); window.DispatchModule.removeJobDay('${escJsAttr(jobId)}', '${escJsAttr(day.id)}', { button: this })">
                  <span class="material-icons" aria-hidden="true">event_busy</span>
                  <span class="job-day-remove-label">Remove Day</span>
                </button>`;
}

// TASK-9236 — the sentence that teaches Remove Day renders whenever the member
// can manage days, not only when a removable day happens to be on screen. The
// caller also requires one real (non-legacy) row: a GAS-imported job drawn only
// from synthesized rows has no Remove Day control, so the hint must not name one.
// Static copy, no interpolation. Keep "Remove Day" in sync with the row label
// AND deleteJobFromDetails' multi-day ticket refusal, which names it.
function _jobDetailsScheduleHint(canManage) {
  const base = 'Tap a scheduled day above to assign crew &amp; equipment';
  if (!canManage) return base;
  return base + '. To take one day off this job, tap <strong>Remove Day</strong> on that day — <strong>Delete</strong> below removes the whole job.';
}

// TASK-9236 — one notch louder than the old bare glyph, still quieter than
// Crew/Assign (no fill, no shadow at rest; the row's primary job is crew).
// Injected (the _ensureAddDayWeightStyles pattern) so it ships with the
// dispatch.js bundle instead of depending on a hand-bumped styles.css buster.
// Overrides TASK-4792's 32px circle. The hint moves off --text-muted (~2.5:1 on
// the light panel) to --text-secondary: it was the sentence being hidden.
function _ensureJobDayRemoveStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('dispatch-jobday-remove-styles')) return;
  const el = document.createElement('style');
  el.id = 'dispatch-jobday-remove-styles';
  el.textContent = `
.job-day-remove.job-day-remove-labeled {
  width: auto; min-width: 0; height: auto; min-height: 32px;
  gap: 4px; padding: 6px 10px; border-radius: var(--radius-md, 8px);
  border: 1px solid var(--border-light, #E2E8F0); background: transparent;
  color: var(--text-secondary, #475569); font-size: 0.75rem; font-weight: 500;
  white-space: nowrap;
}
.job-day-remove.job-day-remove-labeled .material-icons { font-size: 16px; }
.job-day-remove.job-day-remove-labeled.is-single { border-style: dashed; }
.job-day-remove.job-day-remove-labeled:hover,
.job-day-remove.job-day-remove-labeled:focus-visible,
.job-day-remove.job-day-remove-labeled:active {
  background: #FDE8E8; border-color: var(--danger, #DC2626); color: var(--danger, #DC2626);
}
.job-details-hint { color: var(--text-secondary, #475569); }
.job-day-remove.is-busy, #btn-job-remove-day.is-busy { opacity: 0.75; cursor: progress; }
.job-day-remove.is-busy .material-icons, #btn-job-remove-day.is-busy .material-icons { animation: jdr-spin 1.2s linear infinite; }
@keyframes jdr-spin { to { transform: rotate(360deg); } }
@media (max-width: 768px), (pointer: coarse) {
  .job-day-remove.job-day-remove-labeled { width: auto; min-width: 0; height: auto; min-height: 44px; }
  .job-day-remove.job-day-remove-labeled .material-icons { font-size: 18px; }
}
@media (max-width: 480px) {
  .job-days-list .job-day-row { flex-wrap: wrap; }
}`;
  document.head.appendChild(el);
}

// TASK-4877 — THE UNSAVED-EDIT DISCARD BEHIND "the POC doesn't stick".
//
// "Add Another Day" lives in the Edit Job FOOTER, two buttons from Save Job, and
// every add-day path ends in openNewDayCrewEditor -> closeModal('job'). That
// close throws the open form's unsaved edits away with no warning and no
// restore — so a dispatcher who types a point of contact and then reaches for
// Add Another Day loses it: the day gets created, the job doc is never written,
// and the success toast says nothing is wrong.
//
// Measured, not inferred. SALSBURY 1-27 (jobs/k854xW9RByVYoEuM17m3) carries a
// jobDay created 2026-09-09T23:27:13.042Z, a jobs.updatedAt of :13.352Z that is
// _applyAddDayJobRange's range write and nothing else, an all-empty `poc` map,
// and NOT ONE JOB_UPDATE row in jobAuditLog for the job's whole life. The form
// was open, the day was added, saveJob never ran.
//
// The answer is NOT the TASK-1111 confirm that Copy to New Job got. The
// dispatcher pressed a button on an edit form; the intent is "keep what I typed
// AND give me another day", so SAVE first and add the day on the way out.
//
// Only when the form actually CHANGED. An unconditional save would fire
// saveJob's date-sync jobDay-delete confirm on any job that already holds a day
// outside its own start/end range (HANSEN SWD is missing Sep 7-8 inside a
// Sep 6-9 range right now) — a "saving DELETES these days" dialog is not a thing
// to spring on somebody who asked for a new day.
const _JOB_FORM_DIRTY_FIELD_IDS = [
  'job-title', 'job-customer', 'job-type', 'job-location', 'job-well-name',
  'job-po', 'job-po-visible', 'job-vendor-number', 'job-afe',
  'job-permit', 'job-permit-required', 'permit-ai-parse', 'permit-fasttrax',
  'job-notes', 'job-interstate', 'job-hazmat', 'job-liquid-tank',
  'job-split-by', 'job-photos-visible',
  'job-start-date', 'job-end-date', 'job-all-day', 'job-start-time', 'job-end-time',
  'job-poc-name', 'job-poc-role', 'job-poc-phone', 'job-poc-phone2', 'job-poc-email',
  'job-project-id', 'job-add-to-project', 'job-create-new-project', 'job-new-project-name'
];
const _JOB_FORM_POC_FIELD_IDS = [
  'job-poc-name', 'job-poc-role', 'job-poc-phone', 'job-poc-phone2', 'job-poc-email'
];

// The job-wide state as it stood once editJob finished seeding the form.
// null — or a snapshot taken under a stale _jobFormGen — reads as "assume
// dirty", the only safe direction: the cost of a wrong "clean" is exactly the
// silent data loss this block exists to close.
let _jobFormSnapshot = null;
let _jobFormSnapshotGen = -1;

// Set by addDayToCurrentJob, read-and-CLEARED at the top of saveJob so it can
// never survive into an unrelated later save and pop a surprise date picker.
let _pendingAddDayAfterSave = null;

function _readJobFormFields() {
  if (!document.getElementById('form-job')) return null;
  const snap = {};
  for (const id of _JOB_FORM_DIRTY_FIELD_IDS) {
    const el = document.getElementById(id);
    snap[id] = el ? (el.type === 'checkbox' ? !!el.checked : String(el.value ?? '')) : null;
  }
  try {
    const wps = collectWaypoints();
    // name + url ONLY. lat/lng move on their own when a pending geocode lands
    // after the form opens, and a coordinate arriving by itself is not an edit.
    snap._waypoints = wps.map(w => ({ name: w.name, url: w.url }));
  } catch (_) { return null; }
  // The geofence inputs read DIRECTLY, not through collectJobGeofence() —
  // that builder stamps a fresh `configuredAt: Timestamp.now()` on every call,
  // so comparing its output would report a geofenced job as dirty forever.
  snap._geofence = {
    enabled: !!document.getElementById('job-geofence-enabled')?.checked,
    radius: document.getElementById('job-geofence-radius')?.value ?? null,
    address: document.getElementById('job-geofence-address')?.value ?? null,
    lat: (typeof _jobGeofenceCoords?.lat === 'number') ? _jobGeofenceCoords.lat : null,
    lng: (typeof _jobGeofenceCoords?.lng === 'number') ? _jobGeofenceCoords.lng : null
  };
  // Photos and permit files are not form fields — they live in DispatchState,
  // so an add or a remove changes nothing the loop above can see. Counts on
  // BOTH sides: `pending*` is what is queued to upload, `existing*` vs
  // `*loadedCount` is what was removed (COD Code Reviewer, 2026-09-10).
  snap._media = {
    pendingPhotos: (DispatchState.pendingJobPhotos || []).length,
    photos: (DispatchState.existingJobPhotos || []).length,
    photosLoaded: DispatchState.loadedPhotoCount,
    pendingPermits: (DispatchState.pendingPermitFiles || []).length,
    permits: (DispatchState.existingPermitFiles || []).length,
    permitsLoaded: DispatchState.loadedPermitCount
  };
  // TASK-9370 (COD round 2) — "None apply — confirm" on a driver-created job
  // changes NO input, only this module flag. It must still count as an edit,
  // or Add Another Day takes the clean-form shortcut, skips Save, and the
  // confirmation (needsDispatchReview:false) is silently lost.
  snap._dotUnconfirmed = _jobFormDotUnconfirmed === true;
  return snap;
}

function _captureJobFormSnapshot() {
  _jobFormSnapshot = _readJobFormFields();
  _jobFormSnapshotGen = _jobFormGen;
}

function _readJobPocFields() {
  const out = {};
  for (const id of _JOB_FORM_POC_FIELD_IDS) {
    const el = document.getElementById(id);
    out[id] = el ? String(el.value ?? '') : null;
  }
  return out;
}

// The POC dropdown resolves only after a Firestore read of the customer's
// contacts, and restoreJobPocSelection -> onPocChange then rewrites the five
// hidden poc inputs out of that list. The baseline has to move WITH them or
// every job that has a POC reads as dirty the moment it opens.
//
// 🔴 But it moves ONLY for the fields that call ACTUALLY rewrote — `before` is
// read immediately in front of it, and a field it left alone keeps its old
// baseline. Copying the live DOM wholesale here would bake a POC the dispatcher
// typed DURING the read into the "clean" baseline and hand this very discard
// bug straight back: restoreJobPocSelection returns immediately when the job has
// no saved poc, so on exactly the jobs Chris was fixing, nothing would have been
// rewritten and everything would have been marked clean. (COD Code Reviewer +
// Independent Reviewer, round 2, 2026-09-10.)
//
// `gen` is the _jobFormGen captured BEFORE the read started — see the caller.
function _rebaselineJobFormPocFields(before, gen) {
  if (!_jobFormSnapshot || _jobFormSnapshotGen !== _jobFormGen || gen !== _jobFormGen) return;
  const after = _readJobPocFields();
  for (const id of _JOB_FORM_POC_FIELD_IDS) {
    if (after[id] !== before[id]) _jobFormSnapshot[id] = after[id];
  }
}

function _jobFormIsDirty() {
  if (!_jobFormSnapshot || _jobFormSnapshotGen !== _jobFormGen) return true;
  const now = _readJobFormFields();
  if (!now) return true;
  try {
    if (JSON.stringify(now) !== JSON.stringify(_jobFormSnapshot)) return true;
  } catch (_) { return true; }
  // Crew, gear and price-book selections live in DispatchState, not in the
  // form. Diff them with the SAME two helpers saveJob uses, so the dirty check
  // and the write can never disagree about what "untouched" means.
  try {
    const before = DispatchState.jobCrewSnapshot;
    if (!before) return true;
    const nowCrew = _buildJobCrewPayload();
    if (_jobCrewDirty(before, nowCrew) || _jobRatesDirty(before, nowCrew)) return true;
  } catch (_) { return true; }
  return false;
}

function addDayToCurrentJob() {
  if (!_guardManageDays()) return;
  const jobId = document.getElementById('job-id').value;
  if (!jobId) return;
  // TASK-9703 — a PIN session cannot write jobDays; its twin goes to driverAddJobDay.
  if (window.AppState?.driverModePin === true) { _driverAddDayToCurrentJob(jobId); return; }
  if (!_jobFormIsDirty()) { addDayToJob(jobId); return; }

  // Hand the add-day off to saveJob's SUCCESS tail. A save that bails on
  // validation, on the customer-change confirm, or on a write error adds NO day
  // — the dispatcher fixes the field and presses the button again, with what
  // they typed still on the screen.
  _pendingAddDayAfterSave = jobId;
  window.showToast?.('Saving your changes first…', 'info');
  const form = document.getElementById('form-job');
  if (form?.requestSubmit) form.requestSubmit();
  else form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
}

// TASK-4734 — this used to be a SECOND, stripped-down delete implementation.
// It deleted the jobDays and the job and wrote no audit row, ran no clocked-in
// or live-ticket safety check, and never reversed rental asset transactions —
// all of which the details-panel Delete has done for a long time. Two buttons
// labelled "Delete", one of them quietly far more dangerous, and the dangerous
// one sits in the Edit Job footer next to Save Job where it gets used all day.
//
// A dispatcher deleted a live job through here on 2026-09-04. There was no
// record it had ever existed. It was recoverable only because Firestore
// point-in-time recovery happens to be enabled, and that is a 7-day window —
// the same delete reported on day 8 is gone for good.
//
// So this is now a thin delegation, NOT a copy. Do not re-inline it: the whole
// defect was that a copy drifted from the original and lost every guard.
async function deleteCurrentJob(btn) {
  const jobId = document.getElementById('job-id').value;
  if (!jobId) return;
  // TASK-9703 — a PIN driver deletes through driverDeleteJob (the same
  // deleteJobLedgered door, driver mode, stricter guard).
  if (window.AppState?.driverModePin === true) return _driverDeleteCurrentJob(jobId, btn);
  // TASK-10010 — the button shows the busy state; the day the form was opened
  // from (_editJobDayId) gets "Remove just this day" on a multi-day job.
  return deleteJobFromDetails(jobId, { closeModalName: 'job', source: 'dispatch-edit-modal', button: btn, jobDayId: _editJobDayId || null });
}

/**
 * 🔴 TASK-9151 — EVERY exit from the job form funnels through closeModal: the X,
 * duplicateCurrentJob, the add-day flow, app.js's Settings force-close, and
 * saveJob's own success path. Guarding only the X left four other doors open on
 * the same unresolved permits, so the guard sits at the funnel.
 *
 * Refusing is safe because every RESOLVED path clears the tray before it calls
 * closeModal — so this can only fire on work that is genuinely still outstanding:
 * a file whose bytes never landed, or one whose bytes landed but which no job doc
 * points at yet.
 *
 * Kept as its own function, and the call kept to one line, deliberately: the
 * TASK-4736 tripwire reads the first 600 characters of closeModal's body looking
 * for _clearDeleteBlockAlert(), and a fat guard inlined at the top pushes it out
 * of that window and turns an unrelated gate red.
 *
 * @returns {boolean} true when the caller must NOT close.
 */
function _permitCloseIsBlocked() {
  if (_permitUploadBusy || _permitPrepBusy) {
    window.showToast?.(_permitPrepBusy
      ? 'Still getting that permit ready — one moment.'
      : 'Still uploading permits — one moment.', 'info');
    return true;
  }
  if (hasFailedPermitUploads()) {
    discardFailedPermitUploads();   // confirms, then closes through closeModal again
    return true;
  }
  return false;
}

/**
 * @returns {boolean} true if the modal actually closed. Callers that do more
 * than close — duplicateCurrentJob rebuilds the whole form — MUST honour a false.
 */
function closeModal(name) {
  if (name === 'job' && _permitCloseIsBlocked()) return false;   // TASK-9151
  const modal = document.getElementById(`modal-${name}`);
  if (modal) modal.classList.add('hidden');
  if (name === 'mass-text') _unsubscribeMassTextRecent();   // TASK-9842 — no listener outlives the panel
  // TASK-4736 — a refusal alert must never outlive the modal it was raised in.
  // The Edit Job footer is STATIC markup (unlike #job-details-footer, which is
  // re-rendered), so without this the alert would still be there next open.
  _clearDeleteBlockAlert();
  if (name === 'assignment') _justAddedDay = null; // TASK-9943 — Undo is for this one editor only
  if (name === 'job') {
    document.getElementById('dot-assistant-warnings')?.remove();
    document.getElementById('job-builder-history-note')?.remove(); // TASK-4803
    document.getElementById('job-builder-crew-suggest')?.remove(); // TASK-4803 S3a
    document.getElementById('job-builder-customer-hints')?.remove(); // TASK-4803 S3b
    _setJobCustomerAiCue(null); // TASK-4579
    clearJobBuilderProposal(true); // TASK-4803 S2 — a proposal belongs to this form; its conversation ends with it
    // TASK-388: closing the job form invalidates any pending duplicate pre-fill.
    _jobFormGen++;
    if (_dupePrefillTimer) { clearTimeout(_dupePrefillTimer); _dupePrefillTimer = null; }
    _editJobOriginalCustomerId = null;
    // TASK-9645 C — the invoice lock belongs to the edit that painted it.
    _editJobLockBaseline = null;
    _applyEditJobInvoiceLock(null);
    // TASK-9151 — the form is gone, so it can no longer be submitted; drop the
    // lock here rather than in each caller. And a deferred "Add Another Day" that
    // survived a permit detour is owed now: saveJob read-and-cleared it before it
    // returned early, so this is the last place it can still be honoured.
    DispatchState._permitModalSubmitLock = false;
    const _permitAddDayJobId = DispatchState._permitPendingAddDayJobId || null;
    DispatchState._permitPendingAddDayJobId = null;
    if (_permitAddDayJobId) addDayToJob(_permitAddDayJobId);
  }
  return true;
}

// Surgical live-update for any load-counter wrappers currently in the DOM.
// Iterates docChanges from the jobDays snapshot and swaps innerHTML on
// matching wrappers. Avoids full modal re-render so scroll/focus/selection
// state survive remote writes.
function refreshLiveLoadCounters(snapshot) {
  if (!isLoadCalcEnabled() || !snapshot) return;
  let changes;
  try {
    changes = snapshot.docChanges();
  } catch (err) {
    return;
  }
  for (const change of changes) {
    if (change.type !== 'modified' && change.type !== 'added') continue;
    const id = change.doc.id;
    const wrapper = document.getElementById(`live-load-counter-${id}`);
    if (!wrapper) continue;
    const jd = { id, ...change.doc.data() };
    try {
      wrapper.innerHTML = renderLiveLoadCounterInner(jd);
    } catch (err) {
      console.warn('[Dispatch] live counter refresh failed:', err);
    }
  }
}

function showQuickAddCustomer() {
  // Clear form
  document.getElementById('form-quick-customer').reset();
  // Show modal
  document.getElementById('modal-quick-customer').classList.remove('hidden');
}

async function saveQuickCustomer(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const name = document.getElementById('quick-customer-name').value.trim();
  const phone = document.getElementById('quick-customer-phone').value.trim();
  const email = document.getElementById('quick-customer-email').value.trim();
  const pocName = document.getElementById('quick-poc-name').value.trim();
  const pocRole = document.getElementById('quick-poc-role').value.trim();
  const pocPhone = document.getElementById('quick-poc-phone').value.trim();
  const pocPhone2 = document.getElementById('quick-poc-phone2').value.trim();
  const pocEmail = document.getElementById('quick-poc-email').value.trim();

  if (!name) {
    window.showToast?.('Customer name is required', 'error');
    return;
  }

  try {
    const customerData = {
      name,
      phone: phone || null,
      email: email || null,
      status: 'active',
      contacts: [],
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Add primary contact if provided
    if (pocName) {
      customerData.contacts.push({
        name: pocName,
        role: pocRole || null,
        phone: pocPhone || null,
        phone2: pocPhone2 || null,
        email: pocEmail || null,
        isPrimary: true
      });
    }

    // Save to Firestore
    const docRef = await addDoc(
      collection(db, 'tenants', DispatchState.tenantId, 'customers'),
      customerData
    );

    // Add to local state only if not already present (avoids race condition
    // where the onSnapshot listener may have already added it)
    if (!DispatchState.customers.some(c => c.id === docRef.id)) {
      DispatchState.customers.push({ id: docRef.id, ...customerData });
    }

    // Refresh customer dropdown and select the new customer
    populateCustomerDropdown();
    document.getElementById('job-customer').value = docRef.id;

    // Trigger customer change to show POC section and populate POC dropdown
    onCustomerChange();

    // Close modal
    closeModal('quick-customer');

    window.showToast('Customer added successfully', 'success');
  } catch (error) {
    console.error('[Dispatch] Error saving customer:', error);
    window.showToast('Failed to save customer', 'error');
  }
}

// TASK-395 Slice 1: denormalize the two field-ticket signature flags onto the
// jobDay at dispatch time. PIN drivers (anonymous auth + driverSessions doc) can
// read jobDays but NOT contracts (firestore.rules:858 = isTenantMember only), so
// they can't see fieldTicketSettings to know which signature pad / stamp / email-copy
// section to render in the Build Ticket modal. This copies the request flags (a
// render hint) off the already-loaded contract; the contract stays authoritative
// for billing. Returns
// null when there's no contract / no flags → the builder shows no pads, matching
// clock.js when ClockState.fieldTicketSettings is null. Stale on later contract
// edits is accepted (per-dispatch snapshot, like selectedContractId itself).
//
// TASK-10188 — WHICH book: the one picked on the job when it is one of the
// customer's Active books; otherwise (no book picked — a customer with two
// Active books and no pick — or the picked one is no longer Active) every
// Active book combined, strictest wins (window.FieldTicketSettings, the shared
// rule clock.js and the getFieldTicketItems callable use). It used to return
// null with no pick, so that customer's PIN drivers never got a signature pad.
async function resolveJobDaySigSettings(customerId, contractId) {
  if (!customerId) return null;
  try {
    // TASK-10188 COD — through the TTL-aware loader (10-min cache), not the raw
    // cache: with no book picked a stale list could miss a newly requested pad.
    const { contracts } = await loadCustomerLineItems(customerId);
    const FTS = window.FieldTicketSettings;
    let fts;
    if (FTS && typeof FTS.resolveFieldTicketSettings === 'function') {
      fts = FTS.resolveFieldTicketSettings(contracts || [], contractId || null).settings;
    } else {
      // Global missing (should not happen — app.js imports it): the picked
      // book if Active, else ANY Active book asking for a flag asks for it.
      const _all = (contracts || []).filter(c => c && c.fieldTicketSettings);
      const _picked = contractId ? _all.find(c => c.id === contractId) : null;
      if (_picked) fts = _picked.fieldTicketSettings;
      else if (_all.length) {
        fts = {};
        for (const k of ['requireDriverSignature', 'requireCustomerSignature', 'requireStamp', 'requireEmailCopy']) {
          fts[k] = _all.some(c => c.fieldTicketSettings[k] === true);
        }
      }
    }
    if (!fts) return null;
    return {
      requireDriverSignature: !!fts.requireDriverSignature,
      requireCustomerSignature: !!fts.requireCustomerSignature,
      // TASK-395-B: stamp + email-copy flags also denormalized for the phone builder.
      requireStamp: !!fts.requireStamp,
      requireEmailCopy: !!fts.requireEmailCopy,
      defaultEmailRecipient: fts.defaultEmailRecipient || null,
    };
  } catch (e) {
    return null;
  }
}

// TASK-10190 — Edit Job changed this day's Price Book (or moved the job to
// another customer): restamp the PIN-driver Build Ticket hint
// (jobDay.fieldTicketSettings) in the SAME day write, with the same rule as
// assign time. Before, the day kept the old book's pads. `oldCustomerId`
// undefined = unknown (no old job doc) -> only a book change restamps.
// Mutates `update`; a failed resolve stamps null, which the Build Ticket
// resolves at read time (jobboard.js _jbEnsureJobDayFts).
async function _restampJobDaySigOnBookChange(update, prevDay, oldCustomerId, newCustomerId, restampedIds, dayId) {
  if (!update || typeof update !== 'object') return;
  const writesBook = Object.prototype.hasOwnProperty.call(update, 'selectedContractId');
  const bookChanged = writesBook && (update.selectedContractId || null) !== (prevDay?.selectedContractId || null);
  const custChanged = oldCustomerId !== undefined && (oldCustomerId || null) !== (newCustomerId || null);
  if (!bookChanged && !custChanged) return;
  const bookNow = writesBook ? update.selectedContractId : prevDay?.selectedContractId;
  update.fieldTicketSettings = await resolveJobDaySigSettings(newCustomerId || null, bookNow || null);
  if (restampedIds && dayId) restampedIds.add(dayId);
}

// ============================================
// LINE ITEM PICKER — Loading & Matching
// ============================================
// TASK-9446 — a Price Book is billable from dispatch only when status is
// exactly 'Active'. crm.js renders a MISSING status as 'Draft' (`status ||
// 'Draft'`), so a missing status counts as Draft here too. Expired/Cancelled
// are finished books and get no hint.
function _isDraftPriceBook(c) {
  return !!c && (c.status === 'Draft' || c.status == null || c.status === '');
}

// TASK-9446 — per-customer cache generation. invalidateCustomerPriceBooks()
// bumps it; a read that STARTED under an older generation must not write its
// (now stale) result back into a cache that was just cleared.
const _priceBookCacheGen = {};

function invalidateCustomerPriceBooks(customerId) {
  if (!customerId) return;
  _priceBookCacheGen[customerId] = (_priceBookCacheGen[customerId] || 0) + 1;
  delete DispatchState.lineItemCache[customerId];
  // If the job form is open on this customer right now, repaint its Price Book
  // area so it reflects the change without a page reload.
  try {
    const modal = document.getElementById('modal-job');
    if (modal && !modal.classList.contains('hidden')
        && document.getElementById('job-customer')?.value === customerId) {
      _refreshJobPriceBookDraftHint(customerId);
    }
  } catch (_) { /* a repaint must never break the writer that called us */ }
}

async function loadCustomerLineItems(customerId) {
  if (!customerId) return { contracts: [], items: [], drafts: [] };

  // Check cache (10-min TTL)
  const cached = DispatchState.lineItemCache[customerId];
  if (cached && (Date.now() - cached.loadedAt) < 600000) return { contracts: cached.contracts, items: cached.items, drafts: cached.drafts || [] };

  // Wolverine 2026-10-21 — the job form now asks for the same customer's books
  // twice in one tick (Project list + Price Book picker): share the read.
  const flightKey = `${DispatchState.tenantId}/${customerId}/${_priceBookCacheGen[customerId] || 0}`;
  if (_lineItemsInFlight.has(flightKey)) return _lineItemsInFlight.get(flightKey);
  const flight = _loadCustomerLineItemsFresh(customerId).finally(() => _lineItemsInFlight.delete(flightKey));
  _lineItemsInFlight.set(flightKey, flight);
  return flight;
}
const _lineItemsInFlight = new Map();

async function _loadCustomerLineItemsFresh(customerId) {
  const tenant = DispatchState.tenantId;
  const gen = _priceBookCacheGen[customerId] || 0;
  try {
    // TASK-9446 — read every book, then split. Only ACTIVE books go into
    // `contracts` / `items` (the arrays pickers and getContractScopedItems /
    // _resolveCarriedPriceBook bill from — unchanged). Drafts ride alongside in
    // their own `drafts` field, title + id only, no line items, so nothing can
    // price a job from a half-built book.
    const contractsSnap = await getDocs(
      collection(db, 'tenants', tenant, 'customers', customerId, 'contracts')
    );
    let contracts = [];
    let items = [];
    const drafts = [];
    for (const cDoc of contractsSnap.docs) {
      const data = cDoc.data() || {};
      if (data.status !== 'Active') {
        if (_isDraftPriceBook(data)) drafts.push({ id: cDoc.id, title: data.title || '' });
        continue;
      }
      contracts.push({ id: cDoc.id, ...data });
      const linesSnap = await getDocs(
        query(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', cDoc.id, 'lineItems'),
          where('active', '==', true))
      );
      linesSnap.docs.forEach(d => {
        items.push({ id: d.id, contractId: cDoc.id, ...d.data() });
      });
    }
    if ((_priceBookCacheGen[customerId] || 0) === gen) {
      DispatchState.lineItemCache[customerId] = { contracts, items, drafts, loadedAt: Date.now() };
    }
    return { contracts, items, drafts };
  } catch (err) {
    console.warn('[Dispatch] Error loading line items:', err);
    return { contracts: [], items: [], drafts: [] };
  }
}

// TASK-9446 — the job form's "a Draft Price Book exists" hint. Pure: returns
// '' when there is no Draft. Says how many ACTIVE books the job can use
// (0, 1 or 2+) and how many are in Draft, with an "Open it" link to CRM for
// anyone who holds the CRM module. With 0 Active it REPLACES the amber
// "No Price Book" banner (same class), otherwise it is a quiet blue note.
function _priceBookDraftHintHtml(customerId, activeContracts, drafts, canOpen) {
  const d = Array.isArray(drafts) ? drafts : [];
  if (!customerId || d.length === 0) return '';
  const active = Array.isArray(activeContracts) ? activeContracts : [];
  const n = d.length;
  const oneTitle = n === 1 && d[0].title ? `“${escapeHtml(d[0].title)}”` : '';
  const target = n === 1 ? d[0].id : '';
  const link = canOpen
    ? ` <a href="#" onclick="event.preventDefault(); window.DispatchModule.openDraftPriceBook('${escJsAttr(customerId)}', '${escJsAttr(target)}')">${n === 1 ? 'Open it' : 'Open Price Books'}</a>`
    : ' Someone with CRM access can set it to Active.';
  if (active.length === 0) {
    const what = n === 1
      ? `${oneTitle || 'This customer’s Price Book'} is still in Draft`
      : `${n} Price Books are still in Draft`;
    return `
      <div class="no-contract-banner" data-pricebook-draft-hint="0">
        <span class="material-icons">warning_amber</span>
        <div>
          <strong>No active Price Book</strong> — ${what}, so rates will use defaults until it is set to Active.${link}
        </div>
      </div>`;
  }
  const lead = active.length === 1
    ? `Using ${escapeHtml(active[0].title || 'this customer’s Price Book')}.`
    : `${active.length} active Price Books to pick from.`;
  const more = n === 1
    ? `1 more Price Book${oneTitle ? ` (${oneTitle})` : ''} is in Draft and can’t be used until it’s Active.`
    : `${n} more Price Books are in Draft and can’t be used until they’re Active.`;
  return `
    <div data-pricebook-draft-hint="${active.length === 1 ? '1' : '2+'}" style="display:flex;align-items:flex-start;gap:6px;margin:0 0 8px;padding:6px 8px;background:#EFF6FF;border:1px solid #93C5FD;border-radius:6px;color:#1E40AF;font-size:0.8rem;line-height:1.35;">
      <span class="material-icons" style="font-size:16px;flex:0 0 auto;margin-top:1px;">info</span>
      <span>${lead} ${more}${link}</span>
    </div>`;
}

// Paint the hint into #job-pricebook-hint for the customer on the form. Reads
// the cache/loader, never throws, and drops a result that lands after the
// customer on the form changed.
function _refreshJobPriceBookDraftHint(customerId) {
  const el = document.getElementById('job-pricebook-hint');
  if (!el) return;
  el.innerHTML = '';
  if (!customerId || !window.hasAddOnPackage?.('clockbilling')) return;
  loadCustomerLineItems(customerId).then(({ contracts, drafts }) => {
    if (document.getElementById('job-customer')?.value !== customerId) return;
    const canOpen = typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('crm', true) === true;
    el.innerHTML = _priceBookDraftHintHtml(customerId, contracts, drafts, canOpen);
    // With 0 Active books the hint IS the banner — don't show both.
    if ((drafts || []).length && (contracts || []).length === 0) {
      const area = document.getElementById('job-contract-area');
      if (area && area.querySelector('.no-contract-banner')) area.innerHTML = '';
    }
  }).catch(() => {});
}

// "Open it" — go to the customer's Price Book in CRM. Leaving Dispatch closes
// the job form, so ask first when there is anything on it to lose.
async function openDraftPriceBook(customerId, contractId) {
  if (!customerId) return;
  if (typeof window.checkModuleAccess !== 'function' || window.checkModuleAccess('crm', true) !== true) {
    window.showToast?.('Price Books live in CRM — ask an admin for CRM access.', 'info');
    return;
  }
  const modal = document.getElementById('modal-job');
  const formOpen = !!modal && !modal.classList.contains('hidden');
  if (formOpen) {
    if (_jobFormIsDirty() && typeof window.showConfirm === 'function') {
      const ok = await window.showConfirm(
        'Leave this job?',
        'Opening the Price Book takes you to CRM. This job has not been saved, and what you entered here will be lost.',
        { confirmText: 'Open Price Book', cancelText: 'Stay here' }
      );
      if (!ok) return;
    }
    if (!closeModal('job')) return;
  }
  window.AppState = window.AppState || {};
  window.AppState.crmOpenContract = { customerId, contractId: contractId || null };
  window.location.hash = '#/crm';
}

function getMatchingLineItems(allLineItems, kind, resource) {
  return getMatchingLineItemInfo(allLineItems, kind, resource).items;
}

// TASK-1673 — same matcher, but it also reports HOW it matched so the rate panel
// can say "no price-book line covers this vehicle type" instead of rendering an
// empty control the dispatcher can't act on.
//
// The fallback arm is the fix for a live hole: TITS's Falcon #17 is
// `type: "truck"` and the RENTAL vac is `type: "vac"`, while every truck line in
// every TITS price book is typed `trucking`, `hydrovac` or `pickup`. Tiers 1-3
// therefore returned ZERO candidates, `renderPickerForResource` bailed on
// `matches.length < 2`, and the dispatcher had no control at all — the rate fell
// through to the seeded `TT-DAYCAB` $150 default with no way to override it from
// dispatch. When nothing matches on type, offer every priced line of that KIND
// rather than nothing; the row is labelled so the pick is a deliberate one.
function getMatchingLineItemInfo(allLineItems, kind, resource) {
  // kind: 'truck', 'trailer', 'asset', 'person'
  // resource: the roster object (truck/trailer/asset/person) — used for type matching
  const resourceType = resource?.type || resource?.vehicleType || resource?.crewType || '';

  // Tier 1: exact kind + type match
  const exactMatch = allLineItems.filter(li =>
    li.appliesTo?.kind === kind && li.appliesTo?.type && li.appliesTo.type === resourceType && li.rate > 0
  );

  // Tier 2: kind-only match (appliesTo.type is null/empty = any of this kind)
  const kindOnly = allLineItems.filter(li =>
    li.appliesTo?.kind === kind && !li.appliesTo?.type && li.rate > 0
  );

  // Tier 3: legacy — no appliesTo at all, match by category
  const categoryMap = {
    truck: ['Trucks & Trailer', 'Heavy Haul', 'Hydrovac'],
    trailer: ['Trucks & Trailer', 'Heavy Haul'],
    asset: ['Equipment', 'Other'],
    person: ['Personnel']
  };
  const cats = categoryMap[kind] || [];
  const legacy = allLineItems.filter(li =>
    !li.appliesTo && cats.includes(li.category) && li.rate > 0
  );

  // If exact type matches exist, prefer them over generic kind-only matches.
  // This prevents e.g. "Booster" (any truck) showing up for a Hydrovac truck
  // that has its own specific rates.
  let pool = exactMatch.length > 0 ? exactMatch : [...kindOnly, ...legacy];
  let via = exactMatch.length > 0 ? 'type' : (pool.length > 0 ? 'kind' : 'none');

  // Nothing matched on type OR category — offer every priced line of this kind
  // (plus the legacy category lines) so the dispatcher still has a control.
  // Never reached when a type/kind match exists, so no existing pick changes.
  if (pool.length === 0) {
    const anyOfKind = allLineItems.filter(li => li.appliesTo?.kind === kind && li.rate > 0);
    const fallback = [...anyOfKind, ...allLineItems.filter(li => !li.appliesTo && cats.includes(li.category) && li.rate > 0)];
    if (fallback.length > 0) { pool = fallback; via = 'fallback'; }
  }

  // Deduplicate by id
  const seen = new Set();
  const result = [];
  for (const li of pool) {
    if (!seen.has(li.id)) {
      seen.add(li.id);
      result.push(li);
    }
  }
  return { items: result, via };
}

// ============================================
// CONTRACT PICKER — Level 1 (which price book)
// ============================================
function renderContractPicker(containerId, contracts, selectedContractId, isJobModal = false) {
  const container = document.getElementById(containerId);
  if (!container) return;
  container.innerHTML = '';

  // No picker needed for 0-1 contracts
  if (contracts.length < 2) return;

  const pickerDiv = document.createElement('div');
  pickerDiv.className = 'contract-picker';
  const paymentLabel = (c) => c.paymentTerms ? ` — ${escapeHtml(c.paymentTerms)}` : '';
  pickerDiv.innerHTML = `
    <label>Price Book</label>
    <select onchange="window.DispatchModule.onContractPick(this, ${isJobModal})">
      <option value="">Auto (any contract)</option>
      ${contracts.map(c => {
        const title = c.title || 'Untitled Price Book';
        const sel = selectedContractId === c.id ? 'selected' : '';
        return `<option value="${c.id}" ${sel}>${escapeHtml(title)}${paymentLabel(c)}</option>`;
      }).join('')}
    </select>
  `;
  container.appendChild(pickerDiv);
}

function onContractPick(selectEl, isJobModal) {
  const contractId = selectEl.value || null;

  if (isJobModal) {
    // TASK-9391 (COD r1) — a HUMAN picked a book: cancel any pending
    // carried-book check so it can never overwrite this choice.
    _projCarriedBook = null;
    DispatchState.pendingJobSelectedContractId = contractId;
    // Clear all line item selections — different contract = different rates
    DispatchState.pendingJobLineItemSelections = {};
    document.querySelectorAll('#modal-job .line-item-picker').forEach(el => el.remove());
    // Re-render pickers scoped to new contract
    const customerId = document.getElementById('job-customer')?.value;
    if (customerId) {
      const cached = DispatchState.lineItemCache[customerId];
      if (cached) {
        const items = contractId ? cached.items.filter(i => i.contractId === contractId) : cached.items;
        renderLineItemPickers('job-select-trucks', 'truck', items);
        renderLineItemPickers('job-select-trailers', 'trailer', items);
        renderLineItemPickers('job-select-assets', 'asset', items);
        renderLineItemPickers('job-select-drivers', 'person', items);
        renderLineItemPickers('job-select-helpers', 'person', items);
      }
    }
  } else {
    DispatchState.pendingSelectedContractId = contractId;
    // Clear all line item selections
    DispatchState.pendingLineItemSelections = {};
    document.querySelectorAll('#modal-assignment .line-item-picker').forEach(el => el.remove());
    // Re-render pickers scoped to new contract
    const jobId = document.getElementById('assignment-job-id')?.value;
    const job = DispatchState.jobs.find(j => j.id === jobId);
    const customerId = job?.customerId;
    if (customerId) {
      const cached = DispatchState.lineItemCache[customerId];
      if (cached) {
        const items = contractId ? cached.items.filter(i => i.contractId === contractId) : cached.items;
        renderLineItemPickers('select-trucks', 'truck', items);
        renderLineItemPickers('select-trailers', 'trailer', items);
        renderLineItemPickers('select-assets', 'asset', items);
        renderLineItemPickers('select-drivers', 'person', items);
        renderLineItemPickers('select-helpers', 'person', items);
      }
    }
    // TASK-1673 — the contract switch wiped every selection above, so re-seed
    // from last-used *within the new contract* and repaint the panel. The
    // last-used search validates lineItemIds against the new scope, so a pick
    // that only existed in the old price book cannot leak across.
    const _dayId = document.getElementById('assignment-job-day-id')?.value || null;
    if (customerId) applyLastUsedRateDefaults(customerId, _dayId);
    Object.keys(DispatchState.pendingLineItemSelections || {}).forEach(_syncRateControls);
    renderAssignmentRatePanel();
  }
}

function getContractScopedItems(customerId, isJobModal) {
  const cached = DispatchState.lineItemCache[customerId];
  if (!cached) return [];
  const contractId = isJobModal ? DispatchState.pendingJobSelectedContractId : DispatchState.pendingSelectedContractId;
  return contractId ? cached.items.filter(i => i.contractId === contractId) : cached.items;
}

// ============================================
// TASK-1673 — EQUIPMENT RATE PANEL (assignment modal)
// ============================================
// Why this exists: `determineEquipmentRateServer` already honours
// `jobDay.lineItemSelections['{kind}:{id}']` verbatim and never overwrites it,
// but only 8 of 978 TITS jobDays carried one while 370 carried a
// `selectedContractId`. People picked the CONTRACT because the contract picker
// is the only rate control visible without expanding a section. Everything
// below raises the LINE pick to the same prominence and pre-fills it from the
// dispatcher's own last pick for the same truck + customer.
//
// 🔴 INVARIANT: never overwrite an existing selection for a resource key —
// including a deliberate $0. Presence of the KEY is the test, never truthiness
// of `.rate`; `{ rate: 0 }` is an object and stays.

const RATE_PANEL_KINDS = [
  { key: 'trucks', kind: 'truck', label: 'Vehicle', state: 'trucks' },
  { key: 'trailers', kind: 'trailer', label: 'Trailer', state: 'trailers' },
  { key: 'assets', kind: 'asset', label: 'Asset', state: 'assets' }
];
// Drivers/helpers are deliberately absent: TITS bills the truck, never the
// person, and every seeded CREW-* line is inactive. Their inline picker still
// exists for tenants that do bill labour — this panel is about the money lines.

function _rateResourceLookup(kind, id) {
  if (kind === 'truck') return DispatchState.trucks.find(t => t.id === id);
  if (kind === 'trailer') return DispatchState.trailers.find(t => t.id === id);
  if (kind === 'asset') return DispatchState.assets.find(a => a.id === id);
  if (kind === 'person') return DispatchState.people.find(p => p.id === id);
  return null;
}

function _rateResourceName(resource, id) {
  if (!resource) return id;
  return (window.getUnitDisplay ? window.getUnitDisplay(resource) : null) ||
    resource.unit || resource.nickname || resource.name || id;
}

// The dispatcher's most recent explicit pick for this exact resource on THIS
// customer, restricted to line items the currently-scoped contract still
// carries. Same customer only — a different customer is a different price book,
// so its lineItemId would not resolve here.
//
// Source is `DispatchState.jobDays`, which is windowed to ~90 days back on the
// admin path. That is a deliberate ceiling, not an oversight: a pick from last
// spring is not evidence about today's load, and widening it would mean an
// unbounded read on every assignment-modal open.
function findLastUsedLineSelection(customerId, kind, resourceId, scopedItems, excludeJobDayId) {
  if (!customerId || !resourceId || !Array.isArray(scopedItems) || scopedItems.length === 0) return null;
  const resourceKey = `${kind}:${resourceId}`;
  const validIds = new Set(scopedItems.map(i => i.id));
  const candidates = [];
  for (const jd of (DispatchState.jobDays || [])) {
    if (!jd || jd.id === excludeJobDayId) continue;
    const sel = jd.lineItemSelections?.[resourceKey];
    if (!sel || !sel.lineItemId || !validIds.has(sel.lineItemId)) continue;
    // COD (Code Reviewer ⚠️, Domain ⚠️) — only a HAND-PICK is evidence. Without
    // this, a suggestion we wrote yesterday becomes tomorrow's "last used" and
    // the machine ends up citing itself: one accepted guess propagates forward
    // forever and reads as a human decision. Rows from before this task carry no
    // `source` at all, so they still count — it is specifically our own echo
    // that is excluded.
    if (sel.source === 'last-used') continue;
    const job = DispatchState.jobs.find(j => j.id === jd.jobId);
    if (!job || job.customerId !== customerId) continue;
    const ms = _jobDayDateMs(jd);
    if (ms == null) continue;
    candidates.push({ ms, sel, jobDayId: jd.id });
  }
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => b.ms - a.ms);
  const best = candidates[0];
  const li = scopedItems.find(i => i.id === best.sel.lineItemId);
  // Re-read rate/description from the CURRENT price book rather than trusting
  // the snapshot: the contract may have been repriced since that day, and a
  // suggestion must offer today's number.
  return {
    lineItemId: li.id,
    contractId: li.contractId,
    rate: typeof li.rate === 'number' ? li.rate : (parseFloat(li.rate) || 0),
    rateUnit: li.rateUnit || '',
    description: li.description || '',
    roundToHalfHour: li.roundToHalfHour === true ? true : (li.roundToHalfHour === false ? false : null), // TASK-9489 three-way
    revenueCategory: li.revenueCategory || '',
    source: 'last-used',
    // COD (Domain ⚠️) — what this costs, stated plainly: an accepted suggestion
    // reaches `determineEquipmentRateServer` as a selection, which stamps
    // `rateGuessed: false` exactly as it would for a hand-pick. So for the ~19%
    // of repeats where last-used is wrong, the biller loses the "this was
    // guessed" flag they get today from `lookupEquipmentContractRate`. The
    // server is out of this task's scope, so the trade is made deliberately:
    // 80.9% right and VISIBLE beats a coin flip that is neither. These two
    // fields are the durable trail a Review-side badge (TASK-1673 item 4) needs
    // to tell "dispatcher chose this" from "carried forward, nobody looked."
    rateSuggested: true,
    suggestedFromJobDayId: best.jobDayId,
    suggestedFromDate: new Date(best.ms).toISOString().slice(0, 10)
  };
}

// Fill the gaps only. Returns the number of suggestions applied.
function applyLastUsedRateDefaults(customerId, excludeJobDayId) {
  if (!customerId || !window.hasAddOnPackage?.('clockbilling')) return 0;
  const scopedItems = getContractScopedItems(customerId, false);
  if (scopedItems.length === 0) return 0;
  const selections = DispatchState.pendingLineItemSelections;
  let applied = 0;
  for (const grp of RATE_PANEL_KINDS) {
    for (const id of _getAssignmentChecked(`select-${grp.key}`)) {
      const resourceKey = `${grp.kind}:${id}`;
      // 🔴 key presence, NOT rate truthiness — a deliberate $0 pick survives.
      if (Object.prototype.hasOwnProperty.call(selections, resourceKey)) continue;
      const resource = _rateResourceLookup(grp.kind, id);
      const matches = getMatchingLineItems(scopedItems, grp.kind, resource);
      if (matches.length === 0) continue;
      const suggestion = findLastUsedLineSelection(customerId, grp.kind, id, matches, excludeJobDayId);
      if (!suggestion) continue;
      selections[resourceKey] = suggestion;
      applied++;
    }
  }
  return applied;
}

function _getAssignmentChecked(containerId) {
  const c = document.getElementById(containerId);
  if (!c) return [];
  return Array.from(c.querySelectorAll('input:checked')).map(i => i.value);
}

// Pipe-delimited because that is the shape `onLineItemPick` has always parsed.
// COD (Supply-Chain) — EVERY field is stripped, not just the two free-text ones:
// a `|` anywhere shifts the whole parse one slot right and writes a wrong rate,
// and "contractId can't contain a pipe" is an assumption, not a guarantee.
function _rateOptionValue(li) {
  const nopipe = v => String(v == null ? '' : v).replace(/\|/g, ' ');
  return [
    nopipe(li.id),
    nopipe(li.contractId),
    nopipe(li.rate),
    nopipe(li.rateUnit),
    nopipe(li.description),
    li.roundToHalfHour === true ? '1' : (li.roundToHalfHour === false ? '0' : ''), // TASK-9489: '' = Company default
    nopipe(li.revenueCategory)
  ].join('|');
}

const RATE_UNIT_SHORT = { Hourly: 'hr', Daily: 'day', Monthly: 'mo' };

// "2026-08-19" → "Aug 19". Parsed at NOON local, never midnight: a bare
// YYYY-MM-DD goes through Date as UTC midnight and renders as the PREVIOUS day
// west of Greenwich — the same shift every jobDay date in this module dodges.
function _shortDayLabel(iso) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function renderAssignmentRatePanel() {
  const host = document.getElementById('assignment-rate-area');
  if (!host) return;
  const modal = document.getElementById('modal-assignment');
  if (!modal || modal.classList.contains('hidden')) { host.innerHTML = ''; return; }
  if (!window.hasAddOnPackage?.('clockbilling')) { host.innerHTML = ''; return; }

  const jobId = document.getElementById('assignment-job-id')?.value;
  const job = DispatchState.jobs.find(j => j.id === jobId);
  const customerId = job?.customerId;
  if (!customerId) { host.innerHTML = ''; return; }

  const scopedItems = getContractScopedItems(customerId, false);
  // No price book for this customer at all → nothing here is actionable, and a
  // permanent "rate will be guessed" banner on every assignment is nagging, not
  // information. The no-contract case already has its own banner elsewhere.
  if (scopedItems.length === 0) { host.innerHTML = ''; return; }
  const selections = DispatchState.pendingLineItemSelections || {};

  const rows = [];
  let unsetCount = 0;
  for (const grp of RATE_PANEL_KINDS) {
    for (const id of _getAssignmentChecked(`select-${grp.key}`)) {
      const resourceKey = `${grp.kind}:${id}`;
      const resource = _rateResourceLookup(grp.kind, id);
      const info = getMatchingLineItemInfo(scopedItems, grp.kind, resource);
      // Trailers/assets with no priced line at all are noise, not a decision —
      // TITS's trailer lines are every one of them inactive and $0. Trucks are
      // ALWAYS shown: a truck with no candidate line is exactly the silent
      // $150-default case this task exists for, and the dispatcher needs to see
      // it even though the panel can't fix it from here.
      if (info.items.length === 0 && grp.kind !== 'truck') continue;
      const sel = Object.prototype.hasOwnProperty.call(selections, resourceKey) ? selections[resourceKey] : null;
      if (!sel) unsetCount++;
      rows.push({ grp, id, resourceKey, resource, info, sel });
    }
  }

  if (rows.length === 0) { host.innerHTML = ''; return; }

  const esc = escapeHtml;
  const escA = window.escAttr || escapeHtml;
  const body = rows.map(r => {
    const name = _rateResourceName(r.resource, r.id);
    const opts = r.info.items.map(li => {
      const unit = RATE_UNIT_SHORT[li.rateUnit] || li.rateUnit || '';
      const on = r.sel?.lineItemId === li.id ? ' selected' : '';
      return `<option value="${escA(_rateOptionValue(li))}"${on}>${esc(li.description || 'Line Item')} — $${esc(String(li.rate))}${unit ? '/' + esc(unit) : ''}</option>`;
    }).join('');

    let chip, control;
    if (r.info.items.length === 0) {
      chip = `<span class="rate-chip rate-chip-warn" title="No line in this price book applies to this vehicle type — the rate will be guessed when the crew clocks out">No price-book line</span>`;
      control = `<div class="rate-row-note">Nothing in this price book covers <strong>${esc(r.resource?.type || 'this type')}</strong>. Add a line for it in the customer's Price Book, or the rate is guessed at clock-out.</div>`;
    } else {
      if (!r.sel) { chip = `<span class="rate-chip rate-chip-warn" title="No rate picked — one will be guessed when the crew clocks out">Rate will be guessed</span>`; }
      else if (r.sel.source === 'last-used') {
        // The DATE is the whole point of the chip — "suggested" alone isn't
        // checkable, "from Aug 19" lets the dispatcher judge it at a glance.
        const from = r.sel.suggestedFromDate ? _shortDayLabel(r.sel.suggestedFromDate) : null;
        chip = `<span class="rate-chip rate-chip-suggest" title="Carried from the last time this ${esc(r.grp.label.toLowerCase())} ran for this customer. Change it, or save to accept it.">Suggested${from ? ' — ' + esc(from) : ''}</span>`;
      }
      else { chip = `<span class="rate-chip rate-chip-set">Rate set</span>`; }
      const fallbackNote = r.info.via === 'fallback'
        ? `<div class="rate-row-note">No line matches type <strong>${esc(r.resource?.type || '—')}</strong> — showing every ${esc(r.grp.label.toLowerCase())} rate in this price book.</div>`
        : '';
      // Resource key comes off the row's data attribute, NOT interpolated into
      // this onchange: crew/equipment arrays can hold legacy NAME strings, and a
      // name with an apostrophe would break the inline handler.
      control = `<select onchange="window.DispatchModule.onRatePanelPick(this)">
          <option value="">Auto — let billing guess</option>
          ${opts}
        </select>${fallbackNote}`;
    }
    const amount = r.sel && r.sel.rate != null
      ? `<span class="rate-row-amount">$${esc(String(r.sel.rate))}${r.sel.rateUnit ? '/' + esc(RATE_UNIT_SHORT[r.sel.rateUnit] || r.sel.rateUnit) : ''}</span>`
      : '';
    return `<div class="rate-row" data-resource="${escA(r.resourceKey)}">
        <div class="rate-row-head">
          <span class="rate-row-kind">${esc(r.grp.label)}</span>
          <span class="rate-row-name">${esc(name)}</span>
          ${amount}
          ${chip}
        </div>
        ${control}
      </div>`;
  }).join('');

  host.innerHTML = `<div class="assignment-rate-panel">
      <div class="assignment-rate-head">
        <span class="material-icons">payments</span>
        <span class="assignment-rate-title">Billing rate</span>
        ${unsetCount > 0 ? `<span class="rate-chip rate-chip-warn">${unsetCount} unset</span>` : ''}
      </div>
      ${body}
    </div>`;
}

function onRatePanelPick(selectEl) {
  const resourceKey = selectEl.closest('.rate-row')?.dataset?.resource;
  if (!resourceKey) return;
  onLineItemPick(selectEl, resourceKey, false);
}

// COD (Code Reviewer ⚠️) — the inline picker used to interpolate the resource key
// straight into its own onchange attribute. Crew/equipment arrays legitimately
// hold legacy GAS NAME strings (CLAUDE.md Pitfall #5), so a name containing an
// apostrophe produced a broken handler and a rate control that silently did
// nothing. `pickerDiv.dataset.resource` was already being set — read it.
function onInlineLineItemPick(selectEl) {
  const resourceKey = selectEl.closest('.line-item-picker')?.dataset?.resource;
  if (!resourceKey) return;
  onLineItemPick(selectEl, resourceKey);
}

// The same pick can be made from two controls (this panel and the small inline
// picker under the checkbox). Keep the one that did NOT fire in step.
function _syncRateControls(resourceKey) {
  const sel = DispatchState.pendingLineItemSelections?.[resourceKey];
  const value = sel?.lineItemId || '';
  document.querySelectorAll(`#modal-assignment .line-item-picker[data-resource="${CSS.escape(resourceKey)}"] select`).forEach(el => {
    const match = Array.from(el.options).find(o => o.value.split('|')[0] === value);
    el.value = match ? match.value : '';
  });
}

// P0 2026-10-06 — vendors/subcontractors share the customers collection (QBO
// vendor import); a job is billed to a CUSTOMER, so they are dropped here via
// window.BillableCustomer (GENERATED from functions/src/shared/billableCustomer.js).
// keepId = the job's existing customerId: an old job already on a vendor doc
// still renders its value instead of silently going blank.
function populateCustomerDropdown(keepId) {
  const select = document.getElementById('job-customer');
  if (!select) return;
  const currentValue = select.value;

  // Clear and repopulate. TASK-9548 slice 3 — Shop/Office under "Internal — not
  // billed", then "Customers" A–Z (shared builder; vendors dropped, the current
  // pick and keepId kept).
  select.innerHTML = '<option value="">Select customer...</option>'
    + window.PickerGroups.customerOptionsHtml(DispatchState.customers || [], {
      nameOf: c => c.name || '',
      keepId: [currentValue, keepId],
      excludeInactive: true, // TASK-10030 — a new job never offers an Inactive / merged customer; an edit keeps its own
    });

  // Restore selection if it still exists
  if (currentValue) {
    select.value = currentValue;
  }
}

// Populate assignment dropdowns (trucks, drivers, trailers)
function populateAssignmentDropdowns(selectedTruckId = '', selectedDriverId = '', selectedTrailerId = '') {
  // Populate trucks
  const truckSelect = document.getElementById('job-truck');
  if (truckSelect) {
    truckSelect.innerHTML = '<option value="">Select vehicle...</option>';
    DispatchState.trucks
      .filter(t => t.active !== false)
      .sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''))
      .forEach(truck => {
        const option = document.createElement('option');
        option.value = truck.id;
        option.textContent = window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name);
        if (truck.id === selectedTruckId) option.selected = true;
        truckSelect.appendChild(option);
      });
  }

  // Populate drivers (CDL-licensed or driver-role people)
  const driverSelect = document.getElementById('job-driver');
  if (driverSelect) {
    driverSelect.innerHTML = '<option value="">Select driver...</option>';
    DispatchState.people
      .filter(p => p.active !== false && (p.cdlLicensed === true || /cdl|driver/i.test(p.crewType || p.role || '')))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .forEach(person => {
        const option = document.createElement('option');
        option.value = person.id;
        option.textContent = person.name;
        if (person.id === selectedDriverId) option.selected = true;
        driverSelect.appendChild(option);
      });
  }

  // Populate trailers
  const trailerSelect = document.getElementById('job-trailer');
  if (trailerSelect) {
    trailerSelect.innerHTML = '<option value="">Select trailer...</option>';
    DispatchState.trailers
      .filter(t => t.active !== false)
      .sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''))
      .forEach(trailer => {
        const option = document.createElement('option');
        option.value = trailer.id;
        option.textContent = window.getUnitDisplay ? window.getUnitDisplay(trailer) : (trailer.unit ? `Trailer ${trailer.unit}` : trailer.name);
        if (trailer.id === selectedTrailerId) option.selected = true;
        trailerSelect.appendChild(option);
      });
  }
}

// ============================================
// DAY DETAILS MODAL
// ============================================
function showDayDetails(dateStr) {
  // TASK-4588 — this modal does not repaint on the next publish, so a pre-load
  // open would read "No jobs scheduled for this day" and stay that way. Say what
  // is actually true instead.
  if (!isDispatchScheduleReady()) {
    window.showToast?.(DispatchState._dispatchLoadFailed
      ? 'Could not load the schedule — reload the page.'
      : 'Still loading the schedule — try again in a second.', 'warning');
    return;
  }
  // TASK-4646 — this modal IS the month grid's click-through. It has to obey the
  // same chips the grid does, or a day that visibly shows three finished jobs opens
  // onto "No jobs scheduled for this day" — the exact dead-end that made the original
  // bug feel like data loss rather than a display choice.
  const dayJobs = getCalendarJobsForDate(dateStr, DispatchState.calendarFilters).visible;
  const date = new Date(dateStr + 'T00:00:00');
  const formattedDate = date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  document.getElementById('modal-day-details-title').textContent = formattedDate;

  let html = `<div class="day-details-list">`;

  if (dayJobs.length === 0) {
    html += `<div class="day-empty">No jobs scheduled for this day</div>`;
  } else {
    dayJobs.forEach(({ job, jobDay }) => {
      const customerColor = getCustomerColor(job.customerId || job.customer);
      const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
      const statusClass = jobDay.status === 'cancelled' ? 'cancelled' :
                          (jobDay.status === 'finished' || job.status === 'finished') ? 'finished' : ''; // TASK-4646

      // TASK-1385 / TASK-4688 (COD R3) — this placard is two LINES, so the
      // token order is split: first non-empty token on top, the rest beneath.
      // Legacy behaviour is preserved exactly when no token order is saved.
      const { primary: placardPrimary, secondary: placardSecondary } = getPlacardLines(job, customer.name);
      html += `
        <div class="day-job-placard ${statusClass}" style="border-left-color: ${customerColor};${statusClass === 'finished' ? 'opacity:0.7;' : ''}" onclick="window.DispatchModule.showQuickview('${escJsAttr(job.id)}', '${escJsAttr(jobDay.id)}')">
          <div class="placard-header">
            <span class="placard-title">${escapeHtml(placardPrimary)}</span>
            ${statusClass === 'finished' ? '<span class="search-finished-badge">Finished</span>' : ''}
            ${!job.allDay && job.defaultStart ? `<span class="placard-time">${job.defaultStart}</span>` : ''}
          </div>
          <div class="placard-customer">${escapeHtml(placardSecondary)}</div>
          <div class="placard-assignments">${formatAssignments(jobDay)}</div>
        </div>
      `;
    });
  }

  html += `</div>`;
  // TASK-10008 — every day (empty or not) offers Add Job on THAT day, for the
  // same logins the header "+ Add Job" serves (_calendarAddJobMode).
  if (_calendarAddJobMode()) {
    html += `
      <button type="button" class="btn btn-primary day-details-add-job" style="width:100%;margin-top:12px;min-height:44px;display:flex;align-items:center;justify-content:center;gap:6px;" onclick="window.DispatchModule.openAddJobWithDate('${escJsAttr(dateStr)}')">
        <span class="material-icons">add</span> Add job on ${escapeHtml(_calendarDayShortLabel(dateStr))}
      </button>
    `;
  }
  document.getElementById('day-details-content').innerHTML = html;
  document.getElementById('modal-day-details').classList.remove('hidden');
}

// ============================================
// JOB QUICKVIEW MODAL
// ============================================
function showQuickview(jobId, jobDayId = null) {
  const job = DispatchState.jobs.find(j => j.id === jobId);
  if (!job) return;

  const jobDay = jobDayId ? DispatchState.jobDays.find(d => d.id === jobDayId) : null;
  const customer = getCustomerById(job.customerId) || { name: job.customer || 'No customer' };
  const customerColor = getCustomerColor(job.customerId || job.customer);

  // Header
  document.getElementById('quickview-header').innerHTML = `
    <div class="quickview-header-content" style="background: linear-gradient(135deg, ${customerColor} 0%, ${adjustColor(customerColor, -20)} 100%);">
      <button class="btn-icon quickview-close" onclick="window.DispatchModule.closeModal('quickview')">
        <span class="material-icons">close</span>
      </button>
      <div class="quickview-customer">${escapeHtml(customer.name)}</div>
      <div class="quickview-title">${escapeHtml(job.title || 'Untitled')}</div>
      ${_jobTypeSubtitleHtml(job, 'quickview-type', 'font-size:0.9rem;font-weight:600;opacity:0.92;margin-top:2px;')}
    </div>
  `;

  // Body
  let bodyHtml = `<div class="quickview-sections">`;

  // Date & Time
  const startDate = job.startDate?.toDate?.() || new Date(job.startDate);
  const endDate = job.endDate?.toDate?.() || new Date(job.endDate);
  const dateDisplay = formatDateISO(startDate) === formatDateISO(endDate)
    ? formatDateDisplay(startDate, { weekday: 'long', month: 'short', day: 'numeric' })
    : `${formatDateRange(startDate, endDate)}`;

  bodyHtml += `
    <div class="quickview-section">
      <div class="quickview-section-icon"><span class="material-icons">calendar_today</span></div>
      <div class="quickview-section-content">
        <div class="quickview-section-label">Date</div>
        <div class="quickview-section-value">${dateDisplay}</div>
      </div>
    </div>
  `;

  // PO Number(s), main first (TASK-9811 S2). jobPoList drops non-strings, so a
  // corrupted date/timestamp still never shows.
  const _qvPos = jobPoList(job);
  if (_qvPos.length) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">receipt_long</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">PO Number${_qvPos.length > 1 ? 's' : ''}</div>
          <div class="quickview-section-value">${escapeHtml(_qvPos.join(', '))}</div>
        </div>
      </div>
    `;
  }

  // Vendor Number
  if (job.vendorNumber && typeof job.vendorNumber === 'string' && job.vendorNumber.trim()) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">business</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Vendor Number</div>
          <div class="quickview-section-value">${escapeHtml(job.vendorNumber)}</div>
        </div>
      </div>
    `;
  }

  if (!job.allDay && job.defaultStart) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">schedule</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Time</div>
          <div class="quickview-section-value">${job.defaultStart}${job.defaultEnd ? ` - ${job.defaultEnd}` : ''}</div>
        </div>
      </div>
    `;
  }

  // Crew (from jobDay)
  if (jobDay) {
    const driverNames = (jobDay.drivers || []).map(id => {
      const s = String(id);
      const p = DispatchState.people.find(x => x.id === s || String(x.id) === s || x.name === s);
      return p ? p.name : null;
    }).filter(Boolean);
    const helperNames = (jobDay.helpers || []).map(id => {
      const s = String(id);
      const p = DispatchState.people.find(x => x.id === s || String(x.id) === s || x.name === s);
      return p ? p.name : null;
    }).filter(Boolean);
    const crewList = [...driverNames, ...helperNames];

    if (crewList.length > 0) {
      bodyHtml += `
        <div class="quickview-section">
          <div class="quickview-section-icon"><span class="material-icons">people</span></div>
          <div class="quickview-section-content">
            <div class="quickview-section-label">Crew</div>
            <div class="quickview-section-value">${crewList.join(', ')}</div>
          </div>
        </div>
      `;
    }

    // Equipment
    const truckNames = (jobDay.trucks || []).map(id => {
      const s = String(id);
      const t = _resolveFleetRef(s, DispatchState.trucks);
      return t ? (window.getUnitDisplay?.(t) || t.name || t.unit) : null;
    }).filter(Boolean);
    const trailerNames = (jobDay.trailers || []).map(id => {
      const s = String(id);
      const t = _resolveFleetRef(s, DispatchState.trailers);
      return t ? (window.getUnitDisplay?.(t) || t.name || t.unit) : null;
    }).filter(Boolean);
    const equipmentList = [...truckNames, ...trailerNames];

    if (equipmentList.length > 0) {
      bodyHtml += `
        <div class="quickview-section">
          <div class="quickview-section-icon"><span class="material-icons">local_shipping</span></div>
          <div class="quickview-section-content">
            <div class="quickview-section-label">Equipment</div>
            <div class="quickview-section-value">${equipmentList.join(', ')}</div>
          </div>
        </div>
      `;
    }
  }

  // Load Calculator
  if (jobDay) {
    bodyHtml += buildLoadSummaryHTML(jobDay);
    bodyHtml += buildLoadCounterHTML(jobDay, job.id);
  }

  // Route Planner (multi-stop directions with distance)
  bodyHtml += buildRoutePlannerHTML(job);

  // Waypoints — each gets its own "Get Directions" link
  if (job.waypoints && job.waypoints.length > 0) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">location_on</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Destinations</div>
          <div class="quickview-section-value">
            ${job.waypoints.map((wp, i) => {
              const label = escapeHtml(wp.name || `Destination ${i + 1}`);
              const dest = wp.url || wp.name || '';
              const dirUrl = dest.startsWith('http') ? dest : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
              return `<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;padding:4px 0;">
                <span>${label}</span>
                ${dest ? `<a href="${escapeHtml(dirUrl)}" target="_blank" class="btn btn-sm btn-primary" style="white-space:nowrap;font-size:0.75rem;padding:4px 10px;"><span class="material-icons" style="font-size:14px;">near_me</span> Directions</a>` : ''}
              </div>`;
            }).join('')}
          </div>
        </div>
      </div>
    `;
  }

  // Well info
  if (job.wellName || job.wellNumber) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">oil_barrel</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Well</div>
          <div class="quickview-section-value">${escapeHtml(job.wellName || '')} ${escapeHtml(job.wellNumber || '')}</div>
        </div>
      </div>
    `;
  }

  // 811 tickets — TASK-9648: every linked ticket, one line each.
  const _qv811 = is811Enabled()
    ? _jobShown811Ids(job).map(pid => DispatchState.permits.find(p => p.id === pid)).filter(Boolean)
    : [];
  if (_qv811.length) {
    bodyHtml += `
        <div class="quickview-section">
          <div class="quickview-section-icon"><span class="material-icons" style="color:#B45309;">assignment</span></div>
          <div class="quickview-section-content">
            <div class="quickview-section-label">811 ticket${_qv811.length > 1 ? 's' : ''}</div>
            <div class="quickview-section-value" style="display:flex;flex-direction:column;gap:4px;">
              ${_qv811.map(permit => {
                const attrs = _permitLinkAttrs(permit);
                const label = `<span class="material-icons" style="font-size:16px;">picture_as_pdf</span> ${escapeHtml(permit.name || '811 ticket')}`;
                return attrs
                  ? `<a ${attrs} style="display:inline-flex;align-items:center;gap:4px;color:var(--action-primary);">${label}</a>`
                  : `<span style="display:inline-flex;align-items:center;gap:4px;">${label}</span>`;
              }).join('')}
            </div>
          </div>
        </div>
      `;
  }

  // POC
  if (job.poc?.name) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">contact_phone</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Point of Contact</div>
          <div class="quickview-section-value">
            ${escapeHtml(job.poc.name)}${job.poc.role ? ` (${escapeHtml(job.poc.role)})` : ''}
            ${job.poc.phone ? `<br><a href="tel:${job.poc.phone}">${escapeHtml(job.poc.phone)}</a>` : ''}
            ${job.poc.phone2 ? `<br><a href="tel:${job.poc.phone2}">${escapeHtml(job.poc.phone2)}</a>` : ''}
          </div>
        </div>
      </div>
    `;
  }

  // Notes — TASK-9694 S6: a job WITH crew Updates shows the shared job log
  // (jobUpdates.js, filled below); with none, exactly today's Notes section.
  const _jl = (job.crewUpdates || []).some(e => e && e.kind !== 'change');
  if (job.notes || _jl) {
    bodyHtml += `
      <div class="quickview-section">
        <div class="quickview-section-icon"><span class="material-icons">notes</span></div>
        <div class="quickview-section-content">
          <div class="quickview-section-label">Notes</div>
          <div class="quickview-section-value"${_jl ? ` data-joblog="${escapeHtml(job.id)}"` : ''}>${escapeHtml(job.notes || '')}</div>
        </div>
      </div>
    `;
  }

  bodyHtml += `</div>`;
  document.getElementById('quickview-body').innerHTML = bodyHtml;
  if (_jl) __ddChunkWrap(import('./jobUpdates.js?v=96946')).then(m => m.fillJobLogs(document.getElementById('quickview-body'), DispatchState.jobs, { mark: true, tenantId: DispatchState.tenantId }), () => {});

  // Footer
  let footerHtml = `<div class="quickview-actions">`;

  // Directions button only if no waypoints (waypoints have inline direction buttons)
  if ((!job.waypoints || job.waypoints.length === 0) && job.location) {
    const directionsUrl = getGoogleMapsUrl(job.location);
    footerHtml += `
      <a href="${directionsUrl}" target="_blank" class="btn btn-primary">
        <span class="material-icons">near_me</span> Get Directions
      </a>
    `;
  }

  // Truck Info button if trucks are assigned
  if (jobDay && jobDay.trucks && jobDay.trucks.length > 0) {
    // TASK-9344 — a JSON array as a JS EXPRESSION inside a double-quoted attribute.
    // Escaping only `"` left `&` alone, so a stored id containing the literal text
    // `&quot;` decoded to a real quote and closed the JSON string. escapeHtml
    // (full set incl. &) round-trips to the exact JSON, which is a valid JS literal.
    const truckIds = escapeHtml(JSON.stringify(jobDay.trucks));
    footerHtml += `
      <button class="btn btn-secondary" onclick="window.DispatchModule.showTruckInfo(${truckIds})">
        <span class="material-icons">local_shipping</span> ${jobDay.trucks.length > 1 ? 'Trucks' : 'Truck'} Info
      </button>
    `;
  }

  // Trailer Info button if trailers are assigned
  if (jobDay && jobDay.trailers && jobDay.trailers.length > 0) {
    const trailerIds = escapeHtml(JSON.stringify(jobDay.trailers)); // TASK-9344, see truckIds above
    footerHtml += `
      <button class="btn btn-secondary" onclick="window.DispatchModule.showTrailerInfo(${trailerIds})">
        <span class="material-icons">rv_hookup</span> ${jobDay.trailers.length > 1 ? 'Trailers' : 'Trailer'} Info
      </button>
    `;
  }

  // 811 button — TASK-9648: one ticket opens it; several are listed in the body above.
  const _ft811 = is811Enabled()
    ? _jobShown811Ids(job).map(pid => DispatchState.permits.find(p => p.id === pid)).filter(p => p && _permitLinkAttrs(p))
    : [];
  if (_ft811.length === 1) {
    footerHtml += `
        <a ${_permitLinkAttrs(_ft811[0])} class="btn btn-secondary" style="text-decoration:none;">
          <span class="material-icons">assignment</span> 811 ticket
        </a>
      `;
  }

  // TASK-10011 — start a JSA for this job/day from the office (gate in
  // _canStartJsaFromOffice: the JSA access drivers use + the office job access).
  if (_canStartJsaFromOffice()) {
    footerHtml += _officeStartJsaButtonHtml(jobId, jobDayId || '', jobDay, { secondary: true });
  }

  // TASK-10009 — Edit Job opens the full form in ONE tap (it used to open Job
  // Details, whose own Edit Job button was the second tap). The day list /
  // archive / delete screen stays one small "Details" link away.
  if (DispatchState.roleMode === 'dispatcher') {
    footerHtml += `
      <button class="btn btn-secondary quickview-edit-job" onclick="window.DispatchModule.closeModal('quickview'); window.DispatchModule.editJob('${escJsAttr(jobId)}', '${escJsAttr(jobDayId || '')}', event);">
        <span class="material-icons">edit</span> Edit Job
      </button>
      <button type="button" class="btn btn-link quickview-details-link" style="background:none;border:none;color:var(--action-primary);text-decoration:underline;padding:6px 8px;min-height:44px;font-weight:600;" onclick="window.DispatchModule.closeModal('quickview'); window.DispatchModule.showJobDetails('${escJsAttr(jobId)}', '${escJsAttr(jobDayId || '')}');">
        Details
      </button>
    `;
  }

  footerHtml += `</div>`;
  document.getElementById('quickview-footer').innerHTML = footerHtml;

  closeModal('day-details');
  document.getElementById('modal-quickview').classList.remove('hidden');
}

// Color helper for gradients
function adjustColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`;
}

// ============================================
// MASS TEXT MODAL
// ============================================
let massTextDay = 'today';
let massTextRecipients = [];
// TASK-1530 — exclusion tally by reason, computed alongside the recipient list.
// "13 recipients" told us nothing on 2026-08-12; "13 included, 0 muted excluded"
// would have told us everything. Shipped with the send telemetry.
let massTextExclusions = { noPhone: 0, inactive: 0, muted: 0, noTag: 0 };
let massTextAwaitingReply = 0;
// TASK-10112 — set once a send in this open modal has a result; Send then
// reads "Done" and only closes (no accidental second blast). showMassText resets.
let _massTextSentDone = false;

// TASK-10112 — the text that actually goes on the wire for the standard
// (same-to-everyone) send, so the preview's credit estimate bills what the
// server bills: the optional "Reply 1" line, plus the server's opt-out tail
// ("\n\nReply STOP to opt out", every tenant but the founder) unless the text
// already carries an opt-out instruction (same test as _massTextPrepare).
// The founder skip mirrors the server's ALLOW-listed sendMassText routing flag
// (functions/src/index.js isFounderAccount, TASK-932) through the one CLIENT
// founder definition, app.js isFounderAccount() (FOUNDER_TENANTS) — no second
// tenant literal here. The two lists are still separate definitions (server
// literal vs FOUNDER_TENANTS); keep them in step. Only when the tenant this send
// goes to (the same `DispatchState.tenantId || AppState.tenant` the send passes)
// IS AppState.tenant; any mismatch counts the line (over-estimate, never under).
// Estimate only — it grants nothing and never decides whether the line is sent.
function _massTextWireEstimateText() {
  const raw = document.getElementById('mass-text-content')?.value?.trim() || '';
  if (!raw || MASS_TEXT_PLACEHOLDERS.some(pre => raw.startsWith(pre))) return '';
  let t = withConfirmLine(raw, _massTextConfirmAskOn());
  const sendTid = DispatchState.tenantId || window.AppState?.tenant;
  const serverSkipsOptOut = !!sendTid && sendTid === window.AppState?.tenant
    && typeof window.isFounderAccount === 'function' && window.isFounderAccount() === true;
  // Same three arms as the server's hasOptOutInstruction (_massTextPrepare).
  const hasOptOut = /\b(?:reply|text|txt|send|type)\s+stop\b/i.test(t)
    || /\b(?:reply|text|txt|send|type)\s+(?:unsubscribe|opt[\s-]?out)\b/i.test(t)
    || /\bstop\s+to\s+(?:opt[\s-]?out|unsubscribe)\b/i.test(t);
  if (!serverSkipsOptOut && !hasOptOut) t += '\n\nReply STOP to opt out';
  return t;
}

// TASK-10112 — paint the recipient panel through the shared preview. Called by
// updateRecipientCount (audience changed) and onMassTextInput (cost changed).
function _renderMassTextPreview() {
  const panel = document.getElementById('mass-text-recipient-panel');
  if (!panel) return;
  const SF = window.SendFeedback;
  if (!SF) { _loadSendFeedback().then(() => _renderMassTextPreview()).catch(() => {}); return; }
  const recipients = massTextRecipients || [];
  const ex = massTextExclusions || {};
  const wire = massTextPersonalized ? '' : _massTextWireEstimateText();
  panel.innerHTML = SF.previewHtml({
    showCount: false,   // the footer already says "→ N recipients"
    recipients: recipients.map((p) => ({ name: p.name || 'Unknown', awaiting: !_massTextReplied(p) })),
    excluded: [
      { label: 'muted', count: ex.muted || 0, strong: true },
      { label: 'no phone', count: ex.noPhone || 0 },
      { label: 'inactive', count: ex.inactive || 0 },
      { label: 'no Job Alerts tag', count: ex.noTag || 0 },
    ],
    cost: wire ? SF.estimateSms(wire, recipients.length) : null,
    noun: 'Job Alerts',
    empty: 'No JOB ALERTS recipients',
  });
  if (massTextPersonalized && recipients.length) {
    panel.insertAdjacentHTML('beforeend', '<div style="margin-top:6px;font-size:0.8rem;color:var(--text-secondary,#475569);">Personalized: each driver gets only their own jobs, so the size (and credits) differ per person.</div>');
  }
}

// TASK-1530 — recipient resolution as a PURE function so the audience and the
// exclusion tally come from one place (the modal render, the live recount, and
// the telemetry all read this) and so it is unit-testable outside the browser
// (tests/mute-slate.unit.cjs extracts + runs this exact source in node:vm —
// keep it self-contained: no references to module state, only `window.*` with
// an inline fallback).
// JOBALERTS = people who receive mass text broadcasts (drivers/crews)
// DISPATCH_ADMIN = admins who get auto-notified on job changes (different purpose)
// TASK-1533 — remember how many people the last successful crew text reached
// (per tenant, this device) so sendMassText can flag a materially different
// audience before sending. Best-effort: localStorage failure never blocks.
function _stampLastMassTextCount(n) {
  try {
    const key = `dd_massTextLastCount_${DispatchState.tenantId || window.AppState?.tenant || ''}`;
    localStorage.setItem(key, String(n));
  } catch (_) { /* non-fatal */ }
}

// TASK-9893 — replied FROM a phone still on the record (twin of knownSender.hasReplied).
function _massTextReplied(p) {
  if (!p || !p.smsRepliedAt) return false;
  const last10 = (v) => String(v == null ? '' : v).replace(/\D/g, '').slice(-10);
  const from = last10(p.smsRepliedFrom);
  return !from || ['phone', 'cellPhone', 'mobilePhone'].some((k) => last10(p[k]) === from);
}

function computeMassTextAudience(people) {
  const included = [];
  const excluded = { noPhone: 0, inactive: 0, muted: 0, noTag: 0 };
  for (const p of (people || [])) {
    if (!p.phone) { excluded.noPhone++; continue; }
    if (p.active === false || p.status === 'inactive') { excluded.inactive++; continue; }
    // TASK-1531 — shared fail-closed mute predicate (either flag mutes). Was
    // nested-only `p.notifications?.muted`: a flat-only mute showed a MUTED
    // badge in Roster and STILL got mass-texted. The inline fallback keeps the
    // exact same semantics for the node:vm unit harness (no window there).
    const _muted = typeof window !== 'undefined' && window.isNotificationMuted
      ? window.isNotificationMuted(p)
      : !!(p.notifyMuted || (p.notifications && p.notifications.muted));
    if (_muted) { excluded.muted++; continue; }
    // Check for JOBALERTS tag (or ALL which includes everything)
    const tags = p.notifications?.tags || p.notifyTags || [];
    const normalizedTags = tags.map(t => String(t).toUpperCase().replace(/[\s_-]/g, ''));
    if (!normalizedTags.some(t => t === 'JOBALERTS' || t === 'ALL')) { excluded.noTag++; continue; }
    included.push(p);
  }
  return { included, excluded };
}
let massTextPersonalized = false;

function togglePersonalizedMode() {
  _flushMassTextDraftSave();   // TASK-9532
  massTextPersonalized = document.getElementById('mass-text-personalized')?.checked || false;

  const personalizedInfo = document.getElementById('mass-text-personalized-info');

  if (massTextPersonalized) {
    if (personalizedInfo) personalizedInfo.style.display = 'block';
    // Update preview to show personalized format
    updatePersonalizedPreview();
  } else {
    if (personalizedInfo) personalizedInfo.style.display = 'none';
    // Restore normal message
    updateMassTextContent();
  }
}

function toggleFullNotes(mode) {
  if (mode === 'mass') {
    if (massTextPersonalized) {
      updatePersonalizedPreview();
    } else {
      updateMassTextContent();
    }
  } else if (mode === 'individual' && currentIndividualTextData) {
    const { jobId, jobDayId } = currentIndividualTextData;
    sendIndividualText(jobId, jobDayId);
  }
}

function updatePersonalizedPreview() {
  if (renderMassTextNotReady()) return;   // readiness FIRST (tripwire); the not-ready branch flushes the draft itself
  _flushMassTextDraftSave();   // TASK-9532 — the standard-mode edit is kept before the example replaces it
  // Show a preview of what the personalized message will look like
  const textarea = document.getElementById('mass-text-content');
  if (!textarea) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let startDate = new Date(today);
  let endDate = new Date(today);

  switch (massTextDay) {
    case 'tomorrow':
      startDate.setDate(startDate.getDate() + 1);
      endDate = new Date(startDate);
      break;
    case '3days':
      endDate.setDate(endDate.getDate() + 2);
      break;
    case 'week':
      endDate.setDate(endDate.getDate() + 6);
      break;
  }

  // Show example personalized message
  let preview = 'SCHEDULE';
  if (startDate.getTime() === endDate.getTime()) {
    preview += ` - ${startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}\n`;
  } else {
    preview += ` - ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}\n`;
  }

  preview += '\n[Example shown - each driver gets their own jobs]\n';
  preview += '\n[CUSTOMER NAME]\n';
  preview += 'Job Title\n';
  preview += '> Driver Name / Truck #\n';
  preview += '@ Location\n';   // TASK-10301 — the real text ends here (no footer)
  preview += '\n--- Preview Only ---\n';
  preview += 'Each driver will receive a message with ONLY their assigned jobs.';

  textarea.value = preview;
  _removeMassTextDraftBanner();   // TASK-9532 — the example box is not a draft
  onMassTextInput();
}

// ============================================================================
// TASK-9532 — Notify Crew keeps your edited message if you close without sending.
//
// A dispatcher who rewrites the crew text, closes the modal to check something,
// and reopens it used to get a freshly generated preview — the edit was gone
// (TITS, 2026-09-23). The draft now lives in localStorage on this device, keyed
// by tenant + user + the exact day range it was written for, and comes back when
// that same range is opened again. Next to the text we keep a fingerprint of the
// jobs/crew in the range at edit time; when the live schedule differs on reopen
// the restored draft is flagged "schedule changed since you edited", so a stale
// message gets reviewed rather than sent as-is. Nothing here sends anything: the
// draft is put back in the textarea and Send stays a human click.
//
// Lifetime: cleared the moment a send succeeds (standard or personalized), by the
// banner's Discard/Rebuild button, by the user restoring the generated text, or
// by pruning once the range's last day has passed. localStorage is best-effort —
// every read and write is wrapped, and a failure means "no draft", never a
// blocked modal or a blocked send.
//
// The first six helpers are PURE (storage passed in, no module state) so
// tests/notify-crew-draft-9532.unit.cjs can run this exact source in node:vm.
// ============================================================================

// Key = prefix + tenant + user + range. Keyed on the DATES, not the picker label:
// a "Tomorrow" draft written today is the same range as "Today" tomorrow, and it
// comes back; a "Week" range rolls to new dates every day, so yesterday's never
// matches again and pruning removes it.
function massTextDraftKey(tenantId, uid, startISO, endISO) {
  const clean = (s) => String(s || '').replace(/[^A-Za-z0-9_.@-]/g, '_');
  return `dd_massTextDraft_v1_${clean(tenantId)}_${clean(uid)}_${clean(startISO)}_${clean(endISO)}`;
}

// Same math as updateMassTextContent / updatePersonalizedPreview, in one pure
// place: local-midnight start/end for a picker value.
function getMassTextDateRange(day, today) {
  const startDate = new Date(today || Date.now());
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(startDate);
  switch (day) {
    case 'tomorrow':
      startDate.setDate(startDate.getDate() + 1);
      endDate.setTime(startDate.getTime());
      break;
    case '3days':
      endDate.setDate(endDate.getDate() + 2);
      break;
    case 'week':
      endDate.setDate(endDate.getDate() + 6);
      break;
  }
  return { startDate, endDate };
}

// Cheap, deterministic fingerprint of what the crew text is built from: for each
// {date, job, jobDay} in the range — the fields the message renders (title,
// customer, time, first stop, notes) plus the crew/equipment refs on the day.
// Order-insensitive (rows and ref arrays are sorted). Two FNV-1a lanes — this is
// a change detector, not a hash anyone relies on for anything else.
function computeMassTextScheduleFingerprint(entries, tokens) {
  const sortRefs = (arr) => (Array.isArray(arr) ? arr.map(x => String(x ?? '')).sort() : []);
  const rows = (entries || []).map((e) => {
    const j = (e && e.job) || {};
    const d = (e && e.jobDay) || {};
    return [
      String((e && e.date) || ''),
      String(j.id || ''),
      String(j.title || ''),
      String(j.customerId || j.customer || ''),
      j.allDay === false ? String(j.defaultStart || '') : '',
      // The text prints the FIRST stop's name and "+N" for the rest, so hash
      // exactly that — a rename of stop 3 must not cry "schedule changed".
      String((Array.isArray(j.waypoints) && j.waypoints[0] && j.waypoints[0].name) || ''),
      Array.isArray(j.waypoints) ? j.waypoints.length : 0,
      String(j.notes || ''),
      String(d.id || ''),
      sortRefs(d.drivers),
      sortRefs(d.helpers),
      sortRefs(d.trucks),
      sortRefs(d.assets),
      // TASK-9597 — only when the tenant picked a Crew text layout: the fields it
      // can print that the base row above does not read, plus the layout itself,
      // so a restored draft cannot hide a change. No layout = the row is exactly
      // what it was (existing saved drafts keep matching).
      ...(Array.isArray(tokens) && tokens.length ? [[
        tokens.join(','),
        sortRefs(d.trailers),
        String(j.poNumber || ''),
        String(j.jobNumber == null ? '' : j.jobNumber),
        String(j.type || ''),
        String((j.poc && j.poc.name) || ''),
        String((j.poc && j.poc.phone) || ''),
        Array.isArray(j.waypoints) ? j.waypoints.map(w => String((w && w.name) || '')) : [],
      ]] : []),
    ];
  });
  const rowKey = (r) => JSON.stringify([r[0], r[1], r[7]]);
  rows.sort((a, b) => (rowKey(a) < rowKey(b) ? -1 : rowKey(a) > rowKey(b) ? 1 : 0));
  const s = JSON.stringify(rows);
  let h1 = 0x811c9dc5;
  let h2 = 0x9747b28c;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x01000193) >>> 0;
  }
  return `${rows.length}:${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

// A record is {text, fingerprint, savedAt, expiresAt}. Expired or unparseable
// records read as "no draft" and are removed on the way out.
function readMassTextDraft(storage, key, nowMs) {
  let raw = null;
  try { raw = storage.getItem(key); } catch (_) { return null; }
  if (!raw) return null;
  let rec = null;
  try { rec = JSON.parse(raw); } catch (_) { rec = null; }
  const drop = () => { try { storage.removeItem(key); } catch (_) {} return null; };
  if (!rec || typeof rec.text !== 'string') return drop();
  if (Number.isFinite(rec.expiresAt) && rec.expiresAt <= nowMs) return drop();
  return {
    text: rec.text,
    fingerprint: String(rec.fingerprint || ''),
    savedAt: Number(rec.savedAt) || 0,
    expiresAt: Number(rec.expiresAt) || 0,
  };
}

function writeMassTextDraft(storage, key, rec) {
  try {
    if (!rec || typeof rec.text !== 'string' || !rec.text.trim()) return false;
    if (rec.text.length > 20000) return false;   // a crew text is a few hundred chars; never fill the quota
    storage.setItem(key, JSON.stringify({
      text: rec.text,
      fingerprint: String(rec.fingerprint || ''),
      savedAt: Number(rec.savedAt) || 0,
      expiresAt: Number(rec.expiresAt) || 0,
    }));
    return true;
  } catch (_) {
    return false;
  }
}

function deleteMassTextDraft(storage, key) {
  try { storage.removeItem(key); return true; } catch (_) { return false; }
}

// Drop every draft whose range has ended (or that cannot be parsed). Only
// touches our own prefix. Returns how many it removed.
function pruneMassTextDrafts(storage, nowMs) {
  let removed = 0;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && k.startsWith('dd_massTextDraft_v1_')) keys.push(k);
    }
    for (const k of keys) {
      let drop = false;
      try {
        const rec = JSON.parse(storage.getItem(k));
        drop = !rec || typeof rec.text !== 'string' || !Number.isFinite(rec.expiresAt) || rec.expiresAt <= nowMs;
      } catch (_) { drop = true; }
      if (drop) { try { storage.removeItem(k); removed++; } catch (_) {} }
    }
  } catch (_) { /* storage unavailable — nothing to prune */ }
  return removed;
}

// ---- module glue (DOM + DispatchState) -------------------------------------
let _massTextGeneratedText = null;    // the last auto-built standard preview for the open range
let _massTextDraftSaveTimer = null;

function _massTextStorage() {
  try { return window.localStorage || null; } catch (_) { return null; }
}

function _massTextDraftKeyForCurrentRange() {
  const tenantId = DispatchState.tenantId || window.AppState?.tenant || '';
  const uid = auth?.currentUser?.uid || window.AppState?.user?.uid || '';
  if (!tenantId || !uid) return null;
  const { startDate, endDate } = getMassTextDateRange(massTextDay, new Date());
  return {
    key: massTextDraftKey(tenantId, uid, formatDateISOLocal(startDate), formatDateISOLocal(endDate)),
    startDate,
    endDate,
  };
}

// Same walk formatMassTextMessageRange makes, reduced to what the fingerprint reads.
function _massTextRangeEntries(startDate, endDate) {
  const out = [];
  const cur = new Date(startDate);
  while (cur <= endDate) {
    const dateStr = formatDateISO(cur);
    for (const { job, jobDay } of getCrewTextJobsForDate(dateStr)) out.push({ date: dateStr, job, jobDay });
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function _currentMassTextScheduleFingerprint(startDate, endDate) {
  return computeMassTextScheduleFingerprint(_massTextRangeEntries(startDate, endDate), getCrewTextTokens());
}

// Debounced from the textarea's oninput (real keystrokes only — programmatic
// writes call onMassTextInput() without the flag and never persist).
//
// The edit CONTEXT (which range, which generated text, which mode) is captured
// at the keystroke, not when the timer fires. COD Code Reviewer + Independent
// Reviewer, pre-ship: with fire-time reads, "edit Today, tap Tomorrow within
// 300 ms" keyed Today's text under Tomorrow's range, could delete the other
// range's draft (text == its generated preview) or rewrite a restored draft's
// fingerprint and bury the amber warning. And every programmatic overwrite of
// the textarea (preview rebuild, day switch, mode toggle, placeholder) first
// FLUSHES a pending save, so the user's text is persisted before it is replaced.
let _massTextDraftPending = null;   // { key, startDate, endDate, generated, personalized }

function _scheduleMassTextDraftSave() {
  if (_massTextDraftSaveTimer) clearTimeout(_massTextDraftSaveTimer);
  const k = _massTextDraftKeyForCurrentRange();
  if (!k) { _massTextDraftSaveTimer = null; _massTextDraftPending = null; return; }
  _massTextDraftPending = { ...k, generated: _massTextGeneratedText, personalized: massTextPersonalized };
  _massTextDraftSaveTimer = setTimeout(saveMassTextDraftNow, 300);
}

// Run a pending save NOW (before the textarea is overwritten). No-op otherwise.
function _flushMassTextDraftSave() {
  if (!_massTextDraftSaveTimer) return;
  clearTimeout(_massTextDraftSaveTimer);
  saveMassTextDraftNow();
}

function saveMassTextDraftNow() {
  _massTextDraftSaveTimer = null;
  const ctx = _massTextDraftPending;
  _massTextDraftPending = null;
  if (!ctx || ctx.personalized) return;   // the personalized box is an example, not a message
  const textarea = document.getElementById('mass-text-content');
  if (!textarea) return;
  const text = textarea.value;
  if (MASS_TEXT_PLACEHOLDERS.some(pre => text.startsWith(pre))) return;
  const storage = _massTextStorage();
  if (!storage) return;
  if (!text.trim() || text === ctx.generated) {
    // Emptied, or typed back to the generated preview — nothing worth keeping.
    deleteMassTextDraft(storage, ctx.key);
    _removeMassTextDraftBanner();
    return;
  }
  const expires = new Date(ctx.endDate);
  expires.setHours(24, 0, 0, 0);   // local midnight after the range's last day
  writeMassTextDraft(storage, ctx.key, {
    text,
    fingerprint: _currentMassTextScheduleFingerprint(ctx.startDate, ctx.endDate),
    savedAt: Date.now(),
    expiresAt: expires.getTime(),
  });
}

// Called by updateMassTextContent right after it writes the generated preview.
// Puts the draft back (via .value — it is user text, never markup) and shows the
// banner; the amber variant when the schedule no longer matches the fingerprint.
function _applyMassTextDraftIfAny(generatedMessage) {
  _removeMassTextDraftBanner();
  const storage = _massTextStorage();
  const k = _massTextDraftKeyForCurrentRange();
  if (!storage || !k) return false;
  const draft = readMassTextDraft(storage, k.key, Date.now());
  if (!draft) return false;
  if (draft.text === generatedMessage) { deleteMassTextDraft(storage, k.key); return false; }
  const textarea = document.getElementById('mass-text-content');
  if (!textarea) return false;
  textarea.value = draft.text;
  const changed = !!draft.fingerprint
    && draft.fingerprint !== _currentMassTextScheduleFingerprint(k.startDate, k.endDate);
  _renderMassTextDraftBanner({ savedAt: draft.savedAt, changed });
  return true;
}

function _removeMassTextDraftBanner() {
  document.getElementById('mass-text-draft-banner')?.remove();
}

function _renderMassTextDraftBanner({ savedAt, changed }) {
  const textarea = document.getElementById('mass-text-content');
  if (!textarea) return;
  _removeMassTextDraftBanner();
  const when = Number.isFinite(savedAt) && savedAt > 0
    ? new Date(savedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '';
  const el = document.createElement('div');
  el.id = 'mass-text-draft-banner';
  el.setAttribute('role', changed ? 'alert' : 'status');
  el.dataset.changed = changed ? '1' : '0';
  // Compact on purpose (COD UX Lead): on a 390px phone a tall banner pushes the
  // textarea and Send below the fold.
  el.style.cssText = changed
    ? 'margin:0 0 6px;padding:8px 10px;background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;font-size:0.82rem;line-height:1.35;color:#92400e;'
    : 'margin:0 0 6px;padding:8px 10px;background:var(--bg-elevated,#f1f5f9);border:1px solid var(--border-color,#e2e8f0);border-radius:8px;font-size:0.82rem;line-height:1.35;color:var(--text-primary,#0F172A);';
  const accent = changed ? '#d97706' : 'var(--brand-blue,#3B82F6)';
  const headline = changed
    ? 'Schedule changed since you edited this message.'
    : 'Your unsent edits are back.';
  const detail = changed
    ? 'Jobs or crew in this date range are different now. Review the text before sending, or rebuild it from the schedule.'
    : `Saved${when ? ' ' + escapeHtml(when) : ''} on this device. Nothing was sent.`;
  el.innerHTML = `
    <div style="display:flex;align-items:flex-start;gap:8px;">
      <span class="material-icons" style="font-size:18px;color:${accent};">${changed ? 'warning' : 'history'}</span>
      <div style="flex:1;min-width:0;">
        <strong>${headline}</strong><br>
        <span>${detail}</span>
        <div style="margin-top:8px;">
          <button type="button" id="mass-text-draft-discard" onclick="window.DispatchModule.discardMassTextDraft()" style="min-height:36px;background:${accent};color:#fff;border:none;padding:6px 12px;border-radius:6px;font-weight:600;cursor:pointer;font-size:0.85rem;">${changed ? 'Rebuild from schedule' : 'Discard edits'}</button>
        </div>
      </div>
    </div>`;
  textarea.insertAdjacentElement('beforebegin', el);
}

// Banner button: forget the draft for this range and rebuild the preview.
function discardMassTextDraft() {
  clearMassTextDraft();
  if (massTextPersonalized) updatePersonalizedPreview(); else updateMassTextContent();
}

// After a successful send (and from Discard): the draft for the open range is
// gone, and so is any keystroke save still waiting on the debounce — otherwise
// it would land 300 ms after the clear and resurrect the sent text.
function clearMassTextDraft() {
  if (_massTextDraftSaveTimer) { clearTimeout(_massTextDraftSaveTimer); _massTextDraftSaveTimer = null; }
  _massTextDraftPending = null;
  const storage = _massTextStorage();
  const k = _massTextDraftKeyForCurrentRange();
  if (storage && k) deleteMassTextDraft(storage, k.key);
  _removeMassTextDraftBanner();
}

/**
 * TASK-4665 — can this tenant send a text RIGHT NOW, by the same rule the
 * server's pre-flight (sendMassText / sendTenantAwareSms) applies? Reads
 * `tenants/{tid}.twilio.*` only — `addons.smsCredits` is legacy and meaningless.
 * Provisioned = a status or a balance; suspended / abuse_suspended / exhausted
 * = no; an explicit `smsUnlimited` = yes; otherwise there must be credit left.
 * Pure; tests/task4665-meter-founder-sms.unit.cjs runs it.
 */
function massTextHasSmsCapacity(tenantData) {
  const tw = (tenantData && tenantData.twilio) || {};
  if (tw.status === 'suspended' || tw.status === 'abuse_suspended' || tw.status === 'exhausted') return false;
  if (!tw.status && !tw.creditBalance) return false;
  const balance = Number(tw.creditBalance) || 0;
  const used = Number(tw.creditUsed) || 0;
  // A positive balance is a ceiling even with smsUnlimited — the server refuses
  // at creditUsed >= creditBalance before the unlimited flag matters (COD).
  if (balance > 0) return used < balance;
  return tw.smsUnlimited === true;
}

// TASK-10299 — pure: does Notify Crew open in Personalized Messages?
function massTextOpensPersonalized(settings, hasDraft) {
  return !!settings && settings.personalizedSchedule === true && !hasDraft;
}

function _massTextHasDraftForCurrentRange() {
  try {
    const storage = _massTextStorage();
    const k = _massTextDraftKeyForCurrentRange();
    return !!(storage && k && readMassTextDraft(storage, k.key, Date.now()));
  } catch (_) { return false; }
}

function showMassText(opts) {
  window.Telemetry?.logUserAction('sms_modal_opened', { source: 'mass_text' });
  massTextDay = 'today';
  massTextPersonalized = false;
  // TASK-10112 — a fresh open is a fresh send: no leftover result, Send is Send.
  _massTextSentDone = false;
  {
    const sb = document.getElementById('btn-send-mass-text');
    const st = document.getElementById('mass-text-send-status');
    if (!window.SendFeedback?.isBusy(sb)) {
      if (st) { st.innerHTML = ''; st.style.display = 'none'; }
      if (sb && sb.dataset.lockedByLoading !== '1') sb.innerHTML = '<span class="material-icons">send</span> Send';
    }
  }
  _loadSendFeedback().then(() => _renderMassTextPreview()).catch(() => {});
  // TASK-9532 — drafts whose range has ended are dead; sweep them on open.
  { const s = _massTextStorage(); if (s) pruneMassTextDrafts(s, Date.now()); }

  // Reset toggle state
  const personalizedToggle = document.getElementById('mass-text-personalized');
  if (personalizedToggle) personalizedToggle.checked = false;
  const fullNotesToggle = document.getElementById('mass-text-full-notes');
  if (fullNotesToggle) fullNotesToggle.checked = false;
  // TASK-9842 — remembered per device; the Recent panel starts collapsed and
  // only subscribes when opened.
  _restoreMassTextConfirmAsk();
  { const rec = document.getElementById('mass-text-recent'); if (rec) rec.open = false; }
  _unsubscribeMassTextRecent();

  // TASK-10299 — Personalized Schedule on → open in Personalized Messages (the
  // dispatcher can untick it for this send). A saved standard draft for the
  // range wins (TASK-9532): it opens standard so the edit comes back.
  // The "Send new link" notice opens standard: a pasted driver link only rides a
  // standard text (the personalized box is an example, rebuilt per driver).
  const personalizedInfo = document.getElementById('mass-text-personalized-info');
  massTextPersonalized = !(opts && opts.forceStandard === true)
    && massTextOpensPersonalized(window.AppState?.settings, _massTextHasDraftForCurrentRange());
  if (personalizedToggle) personalizedToggle.checked = massTextPersonalized;
  if (personalizedInfo) personalizedInfo.style.display = massTextPersonalized ? 'block' : 'none';

  Promise.resolve(updateMassTextContent()).then(() => { if (massTextPersonalized) updatePersonalizedPreview(); }).catch(() => {});
  updateRecipientCount();

  // Show SMS upsell banner if the account cannot actually send (non-demo only).
  // TASK-4665 — capacity comes from twilio.* ONLY. The old check also accepted
  // `addons.smsCredits > 0`, a dead legacy field (TITS carried 99999 in it), so
  // the modal reported capacity the server would refuse.
  const tenantData = window.AppState?.tenantData;
  const hasSms = massTextHasSmsCapacity(tenantData);
  const isDemo = isDemoMode();
  const upsellBanner = document.getElementById('mass-text-sms-upsell');
  const sendBtn = document.getElementById('btn-send-mass-text');
  if (upsellBanner) upsellBanner.style.display = (!hasSms && !isDemo) ? 'block' : 'none';
  const personalizedSmsWarning = document.getElementById('personalized-sms-warning');
  if (personalizedSmsWarning) personalizedSmsWarning.style.display = (!hasSms && !isDemo) ? 'block' : 'none';
  if (sendBtn) {
    // Do NOT unlock a button the loading guard deliberately locked. This reset
    // ran AFTER updateMassTextContent() above and undid the lock on the exact
    // path this bug is about, so the modal opened with a live-looking Send
    // (COD Code Reviewer). The send guard still refused — but the button lied.
    // TASK-10112 — nor one a send still in flight holds (closed + reopened
    // mid-send): that would re-arm a double send.
    if (sendBtn.dataset.lockedByLoading !== '1' && !window.SendFeedback?.isBusy(sendBtn)) {
      sendBtn.disabled = false;
      sendBtn.title = '';
    }
  }

  window.paintTenantSendNotices?.(document.getElementById('modal-mass-text')); // TASK-10027 muted-tenant UI hints
  document.getElementById('modal-mass-text').classList.remove('hidden');

  // Render SMS credit meter
  if (window.renderSmsCreditMeter) {
    window.renderSmsCreditMeter('mass-text-sms-meter', { variant: 'compact' });
  }

  // Focus textarea after modal opens for easy mobile input
  setTimeout(() => {
    const textarea = document.getElementById('mass-text-content');
    if (textarea) textarea.focus();
  }, 100);

  // Apply sparkles to date range buttons
  if (typeof window.applyDynamicSparkles === 'function') {
    setTimeout(() => window.applyDynamicSparkles(), 100);
  }
}

function updateRecipientCount() {
  // TASK-1530 — audience + exclusion tally from the single pure resolver above.
  const { included: recipients, excluded } = computeMassTextAudience(DispatchState.people);

  massTextRecipients = recipients;
  massTextExclusions = excluded;

  const countEl = document.getElementById('mass-text-recipient-count');
  if (countEl) {
    if (recipients.length === 0) {
      countEl.textContent = 'No JOB ALERTS recipients';
      countEl.style.color = 'var(--status-danger)';
    } else {
      countEl.textContent = `→ ${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}`;
      countEl.style.color = 'var(--text-secondary)';
    }
  }

  // TASK-1533 — a count is not reviewable; show WHO. Names (collapsible past
  // 5) + the exclusion tally with reasons. The excluded half is what makes a
  // wrong send visible before it leaves — "13 recipients — 0 muted" would
  // have stopped the 2026-08-12 send. Names/tally re-render on every call,
  // and the people onSnapshot recounts live while the modal is open.
  // TASK-9893 — whose links won't open on an iPhone yet (the count the shared
  // preview prints; the grey link_off marker per name comes from the same flag).
  const awaitingReply = recipients.filter((p) => !_massTextReplied(p)).length;
  massTextAwaitingReply = awaitingReply;
  // TASK-10112 — the panel is the shared send preview (window.SendFeedback):
  // the SAME names / START line / "Not receiving this" tally as before, plus
  // what the text costs. Audience still comes from computeMassTextAudience.
  _renderMassTextPreview();

  // Defense-in-depth nudge: when 0 recipients, show an actionable hint pointing to Roster.
  // Common cause is people in Roster missing the JOB ALERTS tag (especially on tenants
  // imported via CSV before the role-based default landed) or muted notifications.
  let nudge = document.getElementById('mass-text-zero-nudge');
  if (recipients.length === 0) {
    if (!nudge) {
      nudge = document.createElement('div');
      nudge.id = 'mass-text-zero-nudge';
      nudge.style.cssText = 'margin:12px 0;padding:12px;background:#fef3c7;border:1px solid #fcd34d;border-radius:8px;font-size:0.85rem;color:#92400e;';
      nudge.innerHTML = `
        <div style="display:flex;align-items:flex-start;gap:8px;">
          <span class="material-icons" style="font-size:18px;color:#d97706;">info</span>
          <div>
            <strong>Nobody is set up to receive texts yet.</strong><br>
            Open Roster, edit each crew member, and check the <strong>Job Alerts</strong> notification tag.
            Make sure they have a phone number and aren't muted.
            <button type="button" onclick="window.AppState.navigate?.('roster'); window.DispatchModule.closeModal('mass-text');" style="margin-top:8px;background:#d97706;color:#fff;border:none;padding:6px 12px;border-radius:6px;font-weight:600;cursor:pointer;font-size:0.85rem;">Open Roster</button>
          </div>
        </div>
      `;
      const charCount = document.getElementById('mass-text-char-count');
      if (charCount && charCount.parentElement) {
        charCount.parentElement.insertAdjacentElement('afterend', nudge);
      }
    }
    nudge.style.display = '';
  } else if (nudge) {
    nudge.style.display = 'none';
  }
}

// `fromUser` is true only from the textarea's own oninput. Every programmatic
// caller (preview rebuilds, day switch, draft restore) leaves it off, so the
// generated text and a restored draft are never re-saved as a new edit
// (TASK-9532 — re-saving the restore would overwrite the fingerprint and bury
// the "schedule changed" warning).
function onMassTextInput(fromUser = false) {
  if (fromUser === true) _scheduleMassTextDraftSave();
  _renderMassTextPreview();   // TASK-10112 — the credit estimate follows the text
  const textarea = document.getElementById('mass-text-content');
  const charCount = document.getElementById('mass-text-char-count');
  if (textarea && charCount) {
    const len = textarea.value.length;
    // Calculate estimated message parts (server splits at 600 chars)
    const estParts = Math.ceil(len / 600) || 1;

    let countText = `${len} chars`;
    if (estParts > 1) {
      countText += ` • ${estParts} texts`;
    }
    charCount.textContent = countText;

    // Warn based on length/parts
    // Server will auto-split at 600 chars, but more parts = more cost
    if (len > 1800) {
      charCount.style.color = 'var(--status-danger)';
      charCount.title = 'Very long - will send as multiple texts';
    } else if (len > 600) {
      charCount.style.color = 'var(--status-warning)';
      charCount.title = 'Will be split into multiple texts';
    } else {
      charCount.style.color = 'var(--text-secondary)';
      charCount.title = '';
    }
  }
}

// ============================================================================
// TASK-9219 — a QUEUED crew text must never report success it cannot prove.
//
// THE INCIDENT (2026-09-14). Chris rotated the Twilio auth token. sendMassText and
// processScheduledReminders were redeployed to pick it up; onMassTextJobCreate was
// NOT, and secrets bind at DEPLOY time — so the background worker held a dead
// credential. Three separate Notify Crew blasts (17 recipients each) failed ALL 17
// with the Twilio error "Authenticate", and all three rendered as SUCCESS in the
// browser. He only found out because the crew never replied.
//
// WHY THE UI COULD LIE. Past MASS_TEXT_SYNC_MSG_THRESHOLD (20 outbound messages,
// functions/src/index.js ~:15811) the callable stops sending and QUEUES a job,
// returning {ok:true, queued:true, jobId, count}. The outcome is written later by
// the worker, and nothing ever revisited it. Everything needed was already
// recorded — it was simply never read back.
//
// 🔴 WHY NOT onSnapshot THE JOB DOC — the obvious shape, and it is NOT available.
// tenants/{tid}/massTextJobs/{jobId} is "allow read, write: if false" in
// firestore.rules (~:8886), and that deny-all is the PRIMARY gate there, not
// defence-in-depth: the doc holds recipient phones and message bodies, and a
// client-writable job would let any tenant member relay SMS to arbitrary off-roster
// numbers on the tenant's credit (the worker runs under the Admin SDK and does NOT
// re-run the callable's roster gate). Subscribing to it throws permission-denied,
// and widening that rule to win a toast is not a trade worth making.
//
// WHAT WE SUBSCRIBE TO INSTEAD. tenants/{tid}/notificationLogs is already
// client-readable ("allow read: if isTenantMember"). The SAME worker writes exactly
// ONE row per recipient, inside the claim -> send path, carrying the REAL
// per-recipient outcome: {type:'MASS_TEXT', status:'sent'|'failed', error, toName,
// sentBy, sentAt} (functions/src/index.js ~:16309). For this purpose it beats the
// job doc: it arrives progressively, and it names WHO failed and WHY.
//
// Those rows carry no jobId, so one send is identified by (sentAt >= click time)
// AND (sentBy === this user). Read-only: no backend change, no rules change, no new
// collection, no new index (the query filters and orders on the SAME field, so it
// rides Firestore's automatic single-field index).
//
// 🔴 THE ONE RULE THIS FILE ENFORCES: "accepted for sending" and "handed to the
// carrier" are DIFFERENT CLAIMS and are never printed as the same thing. A spinner
// that never resolves is the same lie more slowly, so every state is time-boxed and
// the timeout reads as NOT SENT — never as pending, never as success.
// ============================================================================

// Only ever holds what is needed to re-attach; no message bodies, no phone numbers.
const MASS_TEXT_WATCH_KEY = 'dd_massTextWatch_v1';
// A watch older than this is dropped rather than resumed — stale forensics are
// worse than none, and the notificationLogs window would be long since polluted.
const MASS_TEXT_WATCH_MAX_AGE_MS = 6 * 60 * 60 * 1000;
// Clock skew between this browser and the Cloud Function that stamps sentAt.
// Small on purpose: a wide floor lets a PREVIOUS blast's rows count toward this
// one, and over-counting is how a failure gets rendered as success.
const MASS_TEXT_WATCH_SKEW_MS = 15000;

let _massTextWatchUnsub = null;
let _massTextWatchTimer = null;
let _massTextWatchRecord = null;
let _massTextWatchRows = null;

/**
 * PURE. Given the expected recipient count and whatever per-recipient rows have
 * landed, decide what the dispatcher is told. Kept free of DOM and Firestore so
 * the tripwire can run the real thing instead of a re-typed copy of the rule.
 *
 * `settled` means "stop listening" — either every recipient reported, or the
 * deadline passed. There is deliberately no state that means "still waiting"
 * forever.
 */
function classifyMassTextOutcome(input) {
  const expected = Math.max(0, Number(input && input.expected) || 0);
  const rows = Array.isArray(input && input.rows) ? input.rows : [];
  const nowMs = Number(input && input.nowMs) || 0;
  const deadlineMs = Number(input && input.deadlineMs) || 0;

  const sent = rows.filter(r => r && r.status === 'sent').length;
  const failed = rows.filter(r => r && r.status === 'failed').length;
  const confirmed = sent + failed;
  const unreported = Math.max(0, expected - confirmed);
  const pastDeadline = nowMs >= deadlineMs;

  const failures = rows
    .filter(r => r && r.status === 'failed')
    .map(r => ({
      name: (r && r.name) || '(unknown recipient)',
      error: String((r && r.error) || 'no error reported').slice(0, 120),
    }));

  const base = { expected, sent, failed, confirmed, unreported, failures };

  // --- Settled: every recipient reported -----------------------------------
  if (expected > 0 && confirmed >= expected) {
    if (failed === 0) {
      return Object.assign(base, {
        phase: 'all-sent', tone: 'success', settled: true,
        headline: 'All ' + expected + ' messages were handed to the carrier',
        detail: 'The carrier accepted every message. Delivery to each handset is not confirmed here.',
      });
    }
    if (sent === 0) {
      return Object.assign(base, {
        phase: 'all-failed', tone: 'error', settled: true,
        headline: 'NOT SENT — all ' + expected + ' messages failed',
        detail: 'The crew did not get this text. Nothing was handed to the carrier.',
      });
    }
    return Object.assign(base, {
      phase: 'partial', tone: 'warn', settled: true,
      headline: failed + ' of ' + expected + ' messages did not send',
      detail: sent + ' were handed to the carrier. The ' + failed + ' below did not go out.',
    });
  }

  // --- Unsettled and still inside the time box ------------------------------
  if (!pastDeadline) {
    if (confirmed === 0) {
      return Object.assign(base, {
        phase: 'accepted', tone: 'info', settled: false,
        headline: 'Queued — ' + expected + ' message(s) accepted, none sent yet',
        detail: 'Accepted for sending is not the same as sent. Waiting for the carrier to confirm each one — no need to send again.',
      });
    }
    return Object.assign(base, {
      phase: 'working', tone: failed > 0 ? 'warn' : 'info', settled: false,
      headline: 'Sending — ' + confirmed + ' of ' + expected + ' confirmed',
      detail: sent + ' handed to the carrier' + (failed > 0 ? ', ' + failed + ' failed so far' : '') + '. Still working.',
    });
  }

  // --- Time box blown --------------------------------------------------------
  // 🔴 This is the incident. The worker was not deployed, so it never ran and no
  // row was ever written. The honest reading of "nothing reported back" is NOT
  // SENT — never a spinner that keeps spinning, and never success.
  if (confirmed === 0) {
    return Object.assign(base, {
      phase: 'stalled-none', tone: 'error', settled: true,
      headline: 'NOT SENT — nothing was confirmed',
      detail: 'The send was accepted but not one of the ' + expected + ' messages came back confirmed in time. Treat this as NOT sent. Check SMS setup in Settings, or contact support, before sending again.',
    });
  }
  return Object.assign(base, {
    phase: 'stalled-partial', tone: 'error', settled: true,
    headline: 'Only ' + confirmed + ' of ' + expected + ' were confirmed',
    detail: sent + ' handed to the carrier' + (failed > 0 ? ', ' + failed + ' failed' : '') + ', and ' + unreported + ' never reported back. Treat the ' + unreported + ' unreported as NOT sent.',
  });
}

function _massTextWatchToneColor(tone) {
  if (tone === 'success') return 'var(--status-success, #16A34A)';
  if (tone === 'warn') return 'var(--status-warning, #D97706)';
  if (tone === 'error') return 'var(--status-danger, #DC2626)';
  return 'var(--brand-blue, #3B82F6)';
}

/**
 * The surface, and the reason it is NOT a toast: Chris closed the modal and walked
 * away, which is exactly the behaviour the old code punished. This card is appended
 * to document.body, so it survives closeModal(), survives switching views inside the
 * SPA, and is torn down only by a settle + dismiss, a tenant switch, or a full
 * reload (after which _resumeMassTextWatch brings it back).
 *
 * Built with createElement/textContent throughout — no innerHTML interpolation, so
 * a recipient name or a Twilio error string can never become markup.
 */
function _renderMassTextWatch(state) {
  const existing = document.getElementById('dd-mass-text-watch');
  if (existing) existing.remove();
  if (!state) return;

  const accent = _massTextWatchToneColor(state.tone);
  const card = document.createElement('div');
  card.id = 'dd-mass-text-watch';
  // A PARTIAL failure is announced as loudly as a total one: "15 of 17 went out"
  // is a real operational fact (a bad number, a STOP'd driver) and the two crew
  // who did not get the text are exactly the ones nobody will notice.
  const urgent = state.tone === 'error' || state.tone === 'warn';
  card.setAttribute('role', urgent ? 'alert' : 'status');
  card.setAttribute('aria-live', urgent ? 'assertive' : 'polite');
  // Contrast: this is a LIGHT card, so every string on it is --text-primary.
  // Sits above the modal layer (modals here run 9999-10000) because the whole
  // point is that it outlives the modal it was launched from. Side insets plus a
  // max-width make it a full-width strip on a phone and a corner card on desktop.
  card.style.cssText = [
    'position:fixed', 'z-index:10001', 'bottom:16px', 'left:12px', 'right:12px',
    'max-width:420px', 'margin-left:auto',
    'background:var(--bg-card, #FFFFFF)',
    'color:var(--text-primary, #0F172A)',
    'border:1px solid var(--border-color, #E2E8F0)',
    'border-left:4px solid ' + accent,
    'border-radius:10px', 'padding:14px 40px 14px 16px',
    'box-shadow:0 8px 24px rgba(15,23,42,0.18)',
    'font-size:0.9rem', 'line-height:1.45',
  ].join(';') + ';';

  const head = document.createElement('div');
  head.style.cssText = 'font-weight:700;color:var(--text-primary, #0F172A);display:flex;align-items:center;gap:8px;';
  if (!state.settled) {
    const spin = document.createElement('span');
    spin.className = 'loading-spinner-small';
    spin.setAttribute('aria-hidden', 'true');
    head.appendChild(spin);
  }
  const headText = document.createElement('span');
  headText.textContent = state.headline;
  head.appendChild(headText);
  card.appendChild(head);

  const detail = document.createElement('div');
  detail.style.cssText = 'margin-top:6px;color:var(--text-primary, #0F172A);font-size:0.85rem;';
  detail.textContent = state.detail;
  card.appendChild(detail);

  // WHO and WHY. This is the half the job doc could not give a dispatcher at all,
  // and it is what turns "something broke" into "that number is bad" or "the
  // credential is dead".
  if (state.failures && state.failures.length) {
    const list = document.createElement('ul');
    list.style.cssText = 'margin:8px 0 0;padding-left:18px;color:var(--text-primary, #0F172A);font-size:0.82rem;';
    state.failures.slice(0, 6).forEach((f) => {
      const li = document.createElement('li');
      li.textContent = f.name + ' — ' + f.error;
      list.appendChild(li);
    });
    if (state.failures.length > 6) {
      const li = document.createElement('li');
      li.textContent = '+' + (state.failures.length - 6) + ' more';
      list.appendChild(li);
    }
    card.appendChild(list);
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Dismiss send status');
  close.textContent = '×';
  close.style.cssText = 'position:absolute;top:8px;right:8px;background:none;border:none;font-size:1.25rem;line-height:1;cursor:pointer;color:var(--text-primary, #0F172A);padding:4px 8px;';
  // addEventListener, never an inline onclick — nothing here is ever interpolated
  // into an attribute (no html escaper makes a JS string inside an attribute safe
  // anyway; that is what window.escJsAttr exists for, and this avoids needing it).
  close.addEventListener('click', () => {
    _stopMassTextWatch('dismissed');
    const el = document.getElementById('dd-mass-text-watch');
    if (el) el.remove();
  });
  card.appendChild(close);

  card.dataset.phase = state.phase;
  document.body.appendChild(card);

  // Only a clean, fully-confirmed send is allowed to disappear on its own. Every
  // other outcome stays on screen until the dispatcher actually acknowledges it.
  if (state.settled && state.phase === 'all-sent') {
    setTimeout(() => {
      const el = document.getElementById('dd-mass-text-watch');
      if (el && el.dataset.phase === 'all-sent') el.remove();
    }, 12000);
  }
}

function _persistMassTextWatch(rec) {
  try {
    if (rec) localStorage.setItem(MASS_TEXT_WATCH_KEY, JSON.stringify(rec));
    else localStorage.removeItem(MASS_TEXT_WATCH_KEY);
  } catch (_) { /* private mode / quota — the live listener still works this session */ }
}

function _stopMassTextWatchListenerOnly() {
  if (_massTextWatchUnsub) { try { _massTextWatchUnsub(); } catch (_) {} _massTextWatchUnsub = null; }
  if (_massTextWatchTimer) { clearInterval(_massTextWatchTimer); _massTextWatchTimer = null; }
}

function _stopMassTextWatch(reason) {
  _stopMassTextWatchListenerOnly();
  _massTextWatchRecord = null;
  _massTextWatchRows = null;
  _persistMassTextWatch(null);
  if (reason) console.log('[massTextWatch] stopped:', reason);
}

function _massTextWatchIdentity() {
  try {
    return String(auth?.currentUser?.email || auth?.currentUser?.uid || '').toLowerCase();
  } catch (_) { return ''; }
}

function _evaluateMassTextWatch() {
  const rec = _massTextWatchRecord;
  if (!rec) return;
  const rows = Array.from((_massTextWatchRows || new Map()).values());
  const state = classifyMassTextOutcome({
    expected: rec.expected,
    rows,
    nowMs: Date.now(),
    deadlineMs: rec.deadlineMs,
  });
  _renderMassTextWatch(state);
  if (!state.settled) return;

  // Flat numeric fields only, never undefined (TASK-1479 — addDoc throws on it).
  window.Telemetry?.logUserAction('sms_queued_outcome', {
    source: 'mass_text',
    phase: state.phase,
    expected: state.expected,
    sent: state.sent,
    failed: state.failed,
    unreported: state.unreported,
  });
  if (state.phase !== 'all-sent') {
    console.error('[massTextWatch] queued send did NOT fully succeed', {
      jobId: rec.jobId, phase: state.phase, sent: state.sent, failed: state.failed, unreported: state.unreported,
    });
  }
  // Settled means stop listening; the card stays until dismissed (or, for a clean
  // send only, until it auto-clears).
  _stopMassTextWatchListenerOnly();
  _persistMassTextWatch(null);
  _massTextWatchRecord = null;
}

function _attachMassTextWatch(rec) {
  _massTextWatchRecord = rec;
  _massTextWatchRows = new Map();
  const floor = new Date(rec.startedAtMs - MASS_TEXT_WATCH_SKEW_MS);
  const exclude = new Set(Array.isArray(rec.excludeIds) ? rec.excludeIds : []);
  let seededExclusions = Array.isArray(rec.excludeIds);

  // Same field in the range filter and the sort, so this needs only Firestore's
  // automatic single-field index — no composite index to deploy.
  const q = query(
    collection(db, 'tenants', rec.tenantId, 'notificationLogs'),
    where('sentAt', '>=', floor),
    orderBy('sentAt', 'asc'),
    limit(500)
  );

  _massTextWatchUnsub = onSnapshot(q, (snap) => {
    // The first SERVER snapshot of a fresh watch is the pre-existing contents of
    // the window; those rows belong to an earlier blast, not this one. Excluding
    // them is what stops a previous send's successes from being counted as this
    // send's — i.e. from re-manufacturing the exact false "all sent" this task
    // exists to kill. If the worker is somehow fast enough that one of OUR rows
    // lands in that first snapshot we under-count and time out pessimistically,
    // which is the safe direction to be wrong in.
    if (!seededExclusions) {
      if (snap.metadata.fromCache) return;
      snap.forEach((d) => exclude.add(d.id));
      seededExclusions = true;
      rec.excludeIds = Array.from(exclude);
      _persistMassTextWatch(rec);
    }
    snap.forEach((d) => {
      if (exclude.has(d.id)) return;
      const data = d.data() || {};
      if (data.type !== 'MASS_TEXT') return;
      if (rec.sentBy && String(data.sentBy || '').toLowerCase() !== rec.sentBy) return;
      // toName only. These rows also carry the raw phone and the message body;
      // neither is ever read here and neither is ever rendered.
      _massTextWatchRows.set(d.id, {
        status: data.status === 'sent' ? 'sent' : (data.status === 'failed' ? 'failed' : 'unknown'),
        name: data.toName || null,
        error: data.error || null,
      });
    });
    _evaluateMassTextWatch();
  }, (err) => {
    // A denied or dropped listener must not leave a spinner running. Collapse to
    // the deadline verdict immediately rather than implying the send is still in
    // flight.
    console.error('[massTextWatch] listener error', err);
    if (_massTextWatchRecord) _massTextWatchRecord.deadlineMs = 0;
    _evaluateMassTextWatch();
  });

  // The listener alone can never fire the timeout, because a worker that never
  // runs writes nothing and therefore produces no snapshot. The clock is what
  // makes "nothing came back" reachable at all.
  _massTextWatchTimer = setInterval(_evaluateMassTextWatch, 5000);
  _evaluateMassTextWatch();
}

function startMassTextWatch(tenantId, jobId, expected) {
  if (!tenantId || !expected) return;
  _stopMassTextWatch('superseded');
  const startedAtMs = Date.now();
  // Measured cost is ~1.67s per outbound message plus 300ms inter-recipient
  // spacing (functions/src/index.js ~:15795), so 8s per recipient is generous
  // even for a 2-part message, and the 30s floor covers a cold start.
  const deadlineMs = startedAtMs + Math.min(600000, 30000 + expected * 8000);
  const rec = {
    tenantId,
    jobId: jobId || null,
    expected,
    startedAtMs,
    deadlineMs,
    sentBy: _massTextWatchIdentity(),
  };
  _persistMassTextWatch(rec);
  _attachMassTextWatch(rec);
}

/**
 * Called on every dispatch mount. Reattaches a watch the dispatcher navigated away
 * from or reloaded past, and evicts one belonging to a different tenant or to a
 * session old enough that the verdict is no longer trustworthy.
 */
function _resumeMassTextWatch(tenantId) {
  let rec = null;
  try {
    const raw = localStorage.getItem(MASS_TEXT_WATCH_KEY);
    rec = raw ? JSON.parse(raw) : null;
  } catch (_) { rec = null; }
  if (!rec || !rec.tenantId || !rec.expected) return;
  if (rec.tenantId !== tenantId) { _stopMassTextWatch('tenant switch'); return; }
  if (!(Date.now() - Number(rec.startedAtMs) < MASS_TEXT_WATCH_MAX_AGE_MS)) { _stopMassTextWatch('stale'); return; }
  if (_massTextWatchRecord && _massTextWatchRecord.startedAtMs === rec.startedAtMs) return; // already live
  _stopMassTextWatchListenerOnly();
  _attachMassTextWatch(rec);
}

// ============================================================================
// TASK-9842 — who GOT a crew text, and who REPLIED (Wolverine / Steffani 10/13).
//
// Reads tenants/{tid}/massTextBroadcasts (one doc per send, written only by the
// Admin SDK: sendMassText / onMassTextJobCreate open and close it, Twilio's
// status callback stamps per-message delivery, twilioIncomingSms stamps replies).
// Read-only here; no client write path exists (firestore.rules: write if false).
//
// 🔴 THE ONE RULE: "Delivered" is the handset acknowledging the message, not a
// person reading it. The only proof someone SAW a text is a reply — and iPhones
// hide links from numbers the person has never replied to
// (lesson_ios_blocks_links_from_unknown_senders), so a delivered text with a
// link can still be dead to them. The panel says so every time it renders.
// ============================================================================
const MASS_TEXT_CONFIRM_LINE = 'Reply 1 to confirm you got this.';
const MASS_TEXT_CONFIRM_KEY = 'dd_massTextConfirmAsk_v1';
const MASS_TEXT_RECENT_LIMIT = 8;
const MASS_TEXT_HONESTY_LINE = 'Delivered means it reached the phone, not that it was read. A reply is the only proof someone saw it. iPhones hide links from numbers the person has never replied to.';
let _massTextRecentUnsub = null;

/**
 * PURE. One recipient entry → what the dispatcher is told. A reply beats
 * everything (they saw it); a refused send or a carrier failure beats a
 * "delivered" on another part; delivered needs EVERY part delivered.
 */
function classifyBroadcastRecipient(rec) {
  const r = rec || {};
  const reply = r.reply && typeof r.reply.text === 'string' && r.reply.text ? r.reply : null;
  const deliv = r.delivery && typeof r.delivery === 'object' ? Object.values(r.delivery) : [];
  const sids = Array.isArray(r.sids) ? r.sids : [];
  const bad = deliv.find(d => d && (d.status === 'undelivered' || d.status === 'failed'));
  if (reply) {
    const more = Number(r.replyCount) > 1 ? ' (+' + (Number(r.replyCount) - 1) + ' more)' : '';
    // Only a literal "1" to a "Reply 1 to confirm" text is a CONFIRMATION; any
    // other body is shown as a reply WITH its words, so "who is this?" never
    // reads as an acknowledgement (COD Domain).
    const label = reply.confirmed === true ? 'Confirmed' : 'Replied';
    // A "1" that came AFTER a first reply is shown too, so the confirmation is
    // never hidden behind the earlier words (COD round 2).
    const late = reply.confirmed === true && reply.confirmText && reply.confirmText !== reply.text
      ? ' · then “' + String(reply.confirmText).slice(0, 40) + '”' : '';
    return { state: 'replied', label, detail: '“' + String(reply.text).slice(0, 240) + '”' + late + more };
  }
  if (r.send === 'failed') {
    return { state: 'failed', label: 'Not sent', detail: String(r.error || 'send failed').slice(0, 120) };
  }
  if (bad) {
    return { state: 'undelivered', label: 'Not delivered', detail: bad.errorCode ? 'Carrier error ' + String(bad.errorCode) : 'The carrier could not deliver it' };
  }
  const deliveredCount = deliv.filter(d => d && d.status === 'delivered').length;
  const expected = Math.max(1, sids.length);
  if (r.send === 'sent' && deliveredCount >= expected) {
    return { state: 'delivered', label: 'Delivered', detail: 'Reached the phone — not proof they read it' };
  }
  if (r.send === 'sent') {
    return { state: 'sent', label: 'Sent', detail: deliveredCount ? deliveredCount + ' of ' + expected + ' parts delivered so far' : 'Carrier has not confirmed delivery yet' };
  }
  return { state: 'pending', label: 'No report', detail: 'Nothing came back for this person' };
}

/** PURE. Counts for one send's summary line. */
function summarizeBroadcast(bc) {
  const recips = bc && bc.recipients && typeof bc.recipients === 'object' ? Object.values(bc.recipients) : [];
  const out = { total: recips.length, replied: 0, delivered: 0, sent: 0, undelivered: 0, failed: 0, pending: 0 };
  recips.forEach(rec => { const s = classifyBroadcastRecipient(rec).state; if (s in out) out[s] += 1; });
  const notThere = out.undelivered + out.failed;
  const bits = [out.replied + ' replied', out.delivered + ' delivered'];
  if (notThere) bits.push(notThere + ' failed');
  if (out.sent) bits.push(out.sent + ' sent, unconfirmed');
  if (out.pending) bits.push(out.pending + ' no report');
  out.line = bits.join(' · ');
  return out;
}

function _massTextStateColor(state) {
  if (state === 'replied' || state === 'delivered') return 'var(--status-success, #16A34A)';
  if (state === 'failed' || state === 'undelivered') return 'var(--status-danger, #DC2626)';
  if (state === 'sent') return 'var(--brand-blue, #3B82F6)';
  return 'var(--status-warning, #D97706)';
}

function _massTextWhen(ts) {
  try {
    const d = ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : (ts ? new Date(ts) : null));
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return ''; }
}

function _el(tag, css, text) {
  const n = document.createElement(tag);
  if (css) n.style.cssText = css;
  if (text != null) n.textContent = text;
  return n;
}

/** No innerHTML anywhere in here: names and reply bodies arrive off an SMS. */
function _renderMassTextRecent(docs) {
  const body = document.getElementById('mass-text-recent-body');
  if (!body) return;
  body.textContent = '';
  if (!docs || !docs.length) {
    body.appendChild(_el('div', 'font-size:0.85rem;color:var(--text-secondary);', 'No crew texts sent from this account yet. The next one will show here with who got it and who replied.'));
    return;
  }
  docs.forEach((bc) => {
    const sum = summarizeBroadcast(bc);
    const wrap = document.createElement('details');
    wrap.style.cssText = 'border:1px solid var(--border-color);border-radius:var(--radius-md);padding:8px 10px;margin-bottom:6px;background:var(--bg-card, #fff);';
    const head = document.createElement('summary');
    head.style.cssText = 'cursor:pointer;display:flex;flex-wrap:wrap;gap:6px 10px;align-items:baseline;color:var(--text-primary);font-size:0.85rem;list-style:none;';
    head.appendChild(_el('strong', '', _massTextWhen(bc.sentAt) || 'Sending…'));
    head.appendChild(_el('span', 'color:var(--text-secondary);', sum.total + (sum.total === 1 ? ' person' : ' people')));
    head.appendChild(_el('span', 'color:var(--text-primary);', sum.line));
    if (bc.status !== 'done') head.appendChild(_el('span', 'color:var(--status-warning, #D97706);', 'still sending'));
    wrap.appendChild(head);

    const msg = _el('div', 'margin:6px 0 8px;font-size:0.8rem;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', String(bc.label || bc.message || '').replace(/\s+/g, ' ').slice(0, 160));
    msg.title = String(bc.message || '');
    wrap.appendChild(msg);

    const list = document.createElement('ul');
    list.style.cssText = 'list-style:none;margin:0;padding:0;font-size:0.82rem;';
    const recips = bc.recipients && typeof bc.recipients === 'object' ? Object.values(bc.recipients) : [];
    const order = { replied: 0, failed: 1, undelivered: 1, pending: 2, sent: 3, delivered: 4 };
    recips
      .map(rec => ({ rec, c: classifyBroadcastRecipient(rec) }))
      .sort((a, b) => (order[a.c.state] - order[b.c.state]) || String(a.rec.name || '').localeCompare(String(b.rec.name || '')))
      .forEach(({ rec, c }) => {
        const li = _el('li', 'display:flex;gap:8px;align-items:baseline;padding:3px 0;border-top:1px solid var(--border-color);');
        li.appendChild(_el('span', 'flex:0 0 38%;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--text-primary);', (rec.name || 'Unknown') + (rec.phoneLast4 ? ' ·' + rec.phoneLast4 : '')));
        li.appendChild(_el('span', 'flex:0 0 auto;font-weight:700;color:' + _massTextStateColor(c.state) + ';', c.label));
        li.appendChild(_el('span', 'flex:1;min-width:0;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;', c.detail));
        list.appendChild(li);
      });
    wrap.appendChild(list);
    body.appendChild(wrap);
  });
  body.appendChild(_el('div', 'margin-top:8px;font-size:0.78rem;color:var(--text-secondary);line-height:1.4;', MASS_TEXT_HONESTY_LINE));
}

function _unsubscribeMassTextRecent() {
  if (_massTextRecentUnsub) { try { _massTextRecentUnsub(); } catch (_) {} _massTextRecentUnsub = null; }
}

function _subscribeMassTextRecent() {
  _unsubscribeMassTextRecent();
  const body = document.getElementById('mass-text-recent-body');
  const tenantId = DispatchState.tenantId || window.AppState?.tenant;
  if (!body || !tenantId) return;
  if (isDemoMode()) {
    body.textContent = '';
    body.appendChild(_el('div', 'font-size:0.85rem;color:var(--text-secondary);', 'Demo mode simulates sends, so there is nothing to report here. On a live account this lists each crew text with who got it and who replied.'));
    return;
  }
  body.textContent = 'Loading…';
  // Single field, same in the sort and the limit — Firestore's automatic index.
  const q = query(collection(db, 'tenants', tenantId, 'massTextBroadcasts'), orderBy('sentAt', 'desc'), limit(MASS_TEXT_RECENT_LIMIT));
  _massTextRecentUnsub = onSnapshot(q, (snap) => {
    const docs = [];
    snap.forEach(d => docs.push(Object.assign({ id: d.id }, d.data() || {})));
    _renderMassTextRecent(docs);
  }, (err) => {
    console.error('[massTextRecent] listener error', err);
    body.textContent = '';
    body.appendChild(_el('div', 'font-size:0.85rem;color:var(--status-danger, #DC2626);', 'Could not load recent crew texts.'));
  });
}

function onMassTextRecentToggle(open) {
  if (open) _subscribeMassTextRecent(); else _unsubscribeMassTextRecent();
}

function _massTextConfirmAskOn() {
  return !!document.getElementById('mass-text-confirm-ask')?.checked;
}

function toggleMassTextConfirmAsk() {
  const on = _massTextConfirmAskOn();
  try { const s = _massTextStorage(); if (s) s.setItem(MASS_TEXT_CONFIRM_KEY, on ? '1' : '0'); } catch (_) {}
  window.Telemetry?.logUserAction('sms_confirm_ask_toggled', { source: 'mass_text', on });
}

/** PURE. Appends the confirm line once; never doubles it on a re-send. */
function withConfirmLine(message, on) {
  const m = String(message || '').replace(/\s+$/, '');
  if (!on || !m) return m;
  if (m.toLowerCase().includes(MASS_TEXT_CONFIRM_LINE.toLowerCase())) return m;
  return m + '\n' + MASS_TEXT_CONFIRM_LINE;
}

function _restoreMassTextConfirmAsk() {
  const box = document.getElementById('mass-text-confirm-ask');
  if (!box) return;
  try { const s = _massTextStorage(); box.checked = !!(s && s.getItem(MASS_TEXT_CONFIRM_KEY) === '1'); } catch (_) { box.checked = false; }
}
// ======================= end TASK-9842 block =================================

// TASK-9750 (COD Code Reviewer) — the standard Notify Crew text is the textarea,
// sent verbatim. If a day in the range is made tentative AFTER the text was
// built (another dispatcher, another tab), the old text still names it. Send
// refuses when the range holds a tentative day that was not tentative at build
// time, and rebuilds the text so the dispatcher can look again.
let _massTextTentativeAtBuild = new Set();
function _massTextTentativeIdsInRange(startDate, endDate) {
  const out = new Set();
  const cur = new Date(startDate);
  while (cur <= endDate) {
    for (const { jobDay } of getJobsForDate(formatDateISO(cur))) if (isTentativeJobDay(jobDay)) out.add(jobDay.id);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// The tentative jobDays in the open range NOW, and those that were NOT tentative
// when the text was built. Read twice by sendMassText: before its dialogs, and
// again right before the callable (COD round 7 — a day can be Undone while a
// "Send to N people?" dialog is open).
function _massTextNewlyTentative() {
  const { startDate, endDate } = getMassTextDateRange(massTextDay, new Date());
  const tentNow = _massTextTentativeIdsInRange(startDate, endDate);
  return { tentNow, newlyTentative: [...tentNow].filter(id => !_massTextTentativeAtBuild.has(id)) };
}

// TASK-10027 muted-tenant UI hints (Chris 10/22) — a muted company's crew texts are
// refused by the server, and an internal no-contact company must reach nobody,
// so say it here instead of after a round trip. Returns true when it refused.
function _refuseCrewTextWhenPaused() {
  if ((window.getTenantSendMode?.() || { mode: 'live' }).mode === 'live') return false;
  window.showToast?.(window.tenantSendNoticeText?.('crewText') || 'Texts are paused for this company.', 'error', null, 9000);
  return true;
}

async function sendMassText() {
  // 🔴 FAIL CLOSED. The preview guards are UX; this is the one that matters,
  // because the cost of getting it wrong is a real SMS to the whole crew saying
  // "No jobs scheduled." on a day that has jobs. Checked FIRST, before the SMS
  // gate and before recipients, so no earlier return can skip it.
  // TASK-4588 — _dispatchLoadFailed is checked alongside readiness: a ROSTER
  // subscription error leaves jobs/jobDays complete but the recipient list short,
  // so readiness alone would let a partial crew text go out (COD Code Reviewer).
  if (!isDispatchScheduleReady() || DispatchState._dispatchLoadFailed) {
    renderMassTextNotReady();
    window.showToast?.(DispatchState._dispatchLoadFailed
      ? 'Could not load the schedule — nothing sent. Reload the page.'
      : 'Still loading the schedule — nothing sent. Try again in a moment.', 'error');
    return;
  }
  // TASK-10112 — a send already running holds the button: a second tap is a no-op.
  if (window.SendFeedback?.isBusy(document.getElementById('btn-send-mass-text'))) return;
  // After a result is on screen the button reads "Done": it closes, it never
  // re-sends (a partial failure re-sent would text everyone who DID get it again).
  if (_massTextSentDone) { _closeMassTextAfterResult(); return; }
  if (_refuseCrewTextWhenPaused()) return; // TASK-10027 muted-tenant UI hints
  // Check if tenant has SMS capability (testing phase: open to all via hasSmsAccess())
  if (!window.hasSmsAccess?.() && !isDemoMode()) {
    window.Telemetry?.logUserAction('sms_gate_blocked', { source: 'mass_text', reason: 'no_sms_access' });
    window.showSmsUpgradeModal?.('mass-text');
    return;
  }

  // Get all active recipients with phone numbers (managed in Roster)
  const selectedRecipients = massTextRecipients.map(p => ({
    personId: p.id,
    phone: p.phone,
    name: p.name || 'Unknown',
    // TASK-856 (H3 follow-up): carry the custom-PIN signal so personalized
    // sends never text the guessable computed PIN to a driver who set their own.
    driverPinCustomSet: !!p.driverPinCustomSet,
    // TASK-4873 — a pending setup code switches the computed code OFF at login
    // (and it may now resolve to someone else), so never text it.
    pinResetRequested: !!p.pinResetRequested
  }));

  if (selectedRecipients.length === 0) {
    window.showToast?.('No recipients. Add JOB ALERTS tag to people in Roster.', 'error');
    return;
  }

  // TASK-9750 — standard text only (personalized rebuilds per driver at send).
  if (!massTextPersonalized) {
    const { tentNow: _tentNow, newlyTentative: _newlyTentative } = _massTextNewlyTentative();
    if (_newlyTentative.length) {
      window.showToast?.('A day in this range was just marked tentative, so nothing was sent. The text was rebuilt without it; check it and send again.', 'warning', null, 9000);
      await updateMassTextContent();
      return;
    }
    // A restored DRAFT was typed before the schedule changed (TASK-9532 banner)
    // and the range now holds a tentative day: the draft may still name it, and
    // we cannot tell (COD round 2, Edge Case Hunter). Ask for a rebuild.
    if (_tentNow.size && document.getElementById('mass-text-draft-banner')?.dataset.changed === '1') {
      const _go = typeof window.showConfirm === 'function'
        ? await window.showConfirm('Check for a tentative day',
            'This saved message was written before the schedule changed, and a day in this range is now tentative. Make sure the message does not name it — the crew is not supposed to hear about tentative days yet.',
            { confirmText: 'Send as written', cancelText: 'Go back', icon: 'sms', iconColor: '#B45309' })
        : false;
      if (_go !== true) return;
    }
  }

  // TASK-1533 — broker the gap between the dispatcher's mental model and the
  // actual audience. On 2026-08-12 Chris expected 1 recipient; the UI's model
  // was 13; nothing flagged the mismatch. When this send's count differs
  // materially from the LAST send's (2x either way, and by at least 3), ask
  // once. Same crew size sails through untouched.
  try {
    const _lcKey = `dd_massTextLastCount_${DispatchState.tenantId || window.AppState?.tenant || ''}`;
    const _last = parseInt(localStorage.getItem(_lcKey), 10);
    const _cur = selectedRecipients.length;
    if (Number.isFinite(_last) && _last > 0 && Math.abs(_cur - _last) >= 3 &&
        (_cur >= _last * 2 || _cur <= Math.ceil(_last / 2))) {
      const proceed = typeof window.showConfirm === 'function'
        ? await window.showConfirm(
            `Send to ${_cur} people?`,
            `Your last crew text went to ${_last} ${_last === 1 ? 'person' : 'people'}; this one goes to ${_cur}. Check the recipient list above if that's not what you expect.`,
            { confirmText: `Send to ${_cur}`, cancelText: 'Cancel', icon: 'sms', iconColor: '#B45309' })
        : confirm(`Your last crew text went to ${_last}; this one goes to ${_cur}. Send anyway?`);
      if (!proceed) return;
    }
  } catch (_) { /* localStorage unavailable — never block a send over the nudge */ }

  const btn = document.getElementById('btn-send-mass-text');
  if (window.SendFeedback?.isBusy(btn)) return;   // TASK-10112 — re-checked after the dialogs
  const originalText = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="loading-spinner-small"></span> Sending...';
  // TASK-10112 — DURING: the button is already locked (synchronously, above —
  // a second tap lands on a disabled button); the run adds the steps, the
  // elapsed timer and, from the server's per-recipient rows, "Sending 4 of 15".
  const _sf = window.SendFeedback || await _loadSendFeedback().catch(() => null);
  const run = _sf ? _sf.createRun({
    host: document.getElementById('mass-text-send-status'),
    button: btn,
    total: selectedRecipients.length,
    steps: ['Checking credits', 'Sending', 'Done'],
    hint: 'Keep this open — the result shows here. It will not be sent twice.',
  }) : null;
  if (_sf && !run) return;   // another run took the button while the module loaded

  const tenantId = DispatchState.tenantId || window.AppState?.tenant;

  console.log('[sendMassText] Click handler entered', { tenantId, recipients: selectedRecipients.length, personalized: massTextPersonalized });
  // TASK-1530 — ship the exclusion tally with the send telemetry. The incident's
  // `sms_send_initiated` row carried only `recipientCount: 13`, which reads as
  // normal because 13 IS the crew size; `excludedMuted: 0` is the field that
  // would have named the problem in one glance. Flat numeric fields, never
  // undefined (TASK-1479 — Firestore addDoc throws on undefined).
  window.Telemetry?.logUserAction('sms_send_initiated', {
    source: 'mass_text',
    recipientCount: selectedRecipients.length,
    personalized: massTextPersonalized,
    excludedMuted: massTextExclusions.muted || 0,
    excludedNoPhone: massTextExclusions.noPhone || 0,
    excludedInactive: massTextExclusions.inactive || 0,
    excludedNoTag: massTextExclusions.noTag || 0,
  });

  try {
    // Check if personalized mode is enabled
    if (massTextPersonalized) {
      await sendPersonalizedMassText(selectedRecipients, tenantId, btn, originalText, run, _sf);
    } else {
      // Standard mode - send same message to everyone
      const message = document.getElementById('mass-text-content')?.value?.trim();
      if (!message) {
        window.showToast?.('No message to send', 'error');
        btn.disabled = false;
        btn.innerHTML = originalText;
        return;
      }
      // TASK-4588 — the LAST line of defence, and the only one that does not
      // depend on ordering. renderMassTextNotReady() enables Send before the
      // async preview rebuild has written the textarea, so there is a real
      // window where Send is live and the box still holds the placeholder.
      // Rather than chase that ordering everywhere, refuse the placeholder
      // itself at the boundary — the crew must never receive it (COD Code
      // Reviewer, three revisions running).
      if (MASS_TEXT_PLACEHOLDERS.some(pre => message.startsWith(pre))) {
        window.showToast?.('The preview has not finished building — nothing sent. Try again in a second.', 'error');
        btn.disabled = false;
        btn.innerHTML = originalText;
        return;
      }

      // TASK-1563 — the textarea is dispatcher-editable AFTER the formatter's
      // sanitize pass (pasting from Word/iMessage reintroduces curly quotes),
      // so re-assert GSM-7 at the callable boundary. Sanitizes + telemetry-logs
      // on a leak; never blocks the send.
      // TASK-9842 — optional "Reply 1 to confirm" line, appended at the boundary
      // so the editable preview stays the dispatcher's own words.
      const confirmAsk = _massTextConfirmAskOn();
      const wireMessage = ensureGsm7BeforeSend(withConfirmLine(message, confirmAsk), 'mass_text');

      // TASK-9750 (COD round 7) — the LAST tentative check, after every dialog
      // and immediately before the callable: a day Undone while "Send to N
      // people?" was open must not go out in this text.
      if (_massTextNewlyTentative().newlyTentative.length) {
        window.showToast?.('A day in this range was just marked tentative, so nothing was sent. The text was rebuilt without it; check it and send again.', 'warning', null, 9000);
        btn.disabled = false;
        btn.innerHTML = originalText;
        await updateMassTextContent();
        return;
      }

      const sendMassTextFn = httpsCallable(functions, 'sendMassText');
      console.log('[sendMassText] Calling cloud function...', { chars: wireMessage.length, recipients: selectedRecipients.length });
      // Wrap in a client-side timeout race so the spinner never hangs longer than 60s
      const timeoutMs = 60000;
      // TASK-10112 — the server checks credits first, then texts one person at a
      // time and writes one notificationLogs row per person as it goes; those
      // rows drive "Sending 4 of 15…" (read-only, the TASK-9219 query shape).
      const _stageTimer = run ? setTimeout(() => run.step(1), 1500) : null;
      const _stopLive = run ? _massTextLiveProgress(tenantId, selectedRecipients.length, (n) => run.progress(n, selectedRecipients.length)) : null;
      let result;
      try {
        result = await Promise.race([
          sendMassTextFn({ tenantId, message: wireMessage, recipients: selectedRecipients, confirmAsk }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out after 60s — check your connection and try again')), timeoutMs))
        ]);
      } finally {
        if (_stageTimer) clearTimeout(_stageTimer);
        if (_stopLive) _stopLive();
      }
      console.log('[sendMassText] Cloud function returned', result?.data);

      const sentCount = result.data?.sent || 0;
      const failedCount = (result.data?.errors?.length) || (selectedRecipients.length - sentCount);
      // TASK-9219 — do NOT log a completion for a send that has not completed. On
      // the queued path `sent` is absent, so this row used to record every queued
      // blast as sent:0 / failed:<everyone> the instant it was accepted. That is
      // the same conflation the UI made, written into the forensics trail that
      // would otherwise be the way to find an incident like 2026-09-14. The real
      // outcome is logged later as `sms_queued_outcome` by the watch.
      if (!result.data?.queued) {
        window.Telemetry?.logUserAction('sms_send_completed', {
          source: 'mass_text',
          sent: sentCount,
          failed: failedCount,
          total: selectedRecipients.length,
        });
      }

      if (result.data?.queued) {
        // TASK-505 — large lists send asynchronously (the callable enqueued a job
        // and returned immediately so the modal never hangs / times out).
        //
        // 🔴 TASK-9219 — this branch used to close the modal on a GREEN toast
        // reading "Sending messages to 17 recipient(s)", and nothing ever revisited
        // the outcome. On 2026-09-14 that rendered three consecutive all-17-failed
        // blasts as success. Two things changed and both matter:
        //   1. the copy no longer conflates ACCEPTED with SENT, and the toast is no
        //      longer styled as a success, and
        //   2. startMassTextWatch() subscribes to the per-recipient outcome rows
        //      the worker writes, so the verdict actually comes back — to a card on
        //      document.body that outlives this modal and this view.
        const dropped = (result.data?.droppedOffRoster
          ? ` (${result.data.droppedOffRoster} not on roster were skipped)` : '')
          + (result.data?.droppedMuted ? ` (${result.data.droppedMuted} muted were skipped)` : '');
        const queuedCount = result.data.count || selectedRecipients.length;
        window.showToast?.(`Queued ${queuedCount} message(s) — accepted for sending, not sent yet${dropped}. Watching for the result; don't send again.`, 'info');
        startMassTextWatch(tenantId, result.data.jobId, queuedCount);
        _stampLastMassTextCount(queuedCount); // TASK-1533
        run?.dispose(); // TASK-10112 — the TASK-9219 watch card carries this send from here
        clearMassTextDraft(); // TASK-9532 — accepted for sending; the unsent draft is spent
        closeModal('mass-text');
      } else if (result.data?.ok) {
        // TASK-1531 — the server now independently drops muted recipients; say
        // so instead of letting "sent to N" quietly disagree with the list shown.
        const mutedNote = result.data?.droppedMuted ? ` (${result.data.droppedMuted} muted were skipped)` : '';
        if (result.data?.failed > 0) {
          // TASK-10112 — ok:true only means at least ONE went out. This used to
          // close on a green "SMS sent to N" and hide who did not get it.
          _massTextShowFailures(run, result.data, selectedRecipients.length);
        } else {
          // TASK-10112 — AFTER: "Sent to 14 people" + credits used, on a card
          // that outlives the modal (which closes, as before).
          if (run) { const _res = _massTextResult(result.data, selectedRecipients.length, mutedNote.trim()); run.finish(_res); _sf.showResultCard(_res); }
          else window.showToast?.(`SMS sent to ${result.data.sent} recipient(s)${mutedNote}`, 'success');
          _stampLastMassTextCount(result.data.sent || selectedRecipients.length); // TASK-1533
          clearMassTextDraft(); // TASK-9532 — sent, so the unsent draft is gone
          closeModal('mass-text');
        }
      } else {
        _massTextShowFailures(run, result.data, selectedRecipients.length);
      }
    }
  } catch (error) {
    console.error('[Dispatch] Send mass text error:', error);
    // 🔴 scrubPii, not a bare slice. TASK-9251 taught the audit pane to render a
    // telemetry row's `context`, which had been invisible since the sink was
    // built — so this pre-existing error string STARTED being displayed without
    // ever being reviewed for PII. A Twilio failure body can echo the recipient's
    // phone number. Caught on the integrated diff, not by either task alone.
    window.Telemetry?.logUserAction('sms_send_error', { source: 'mass_text', error: window.Telemetry?.scrubPii?.(error?.message || 'unknown') ?? 'unknown' });
    // TASK-1531 (COD round 2) — don't blanket-map every failed-precondition to
    // the SMS-pack upsell: the server now also throws failed-precondition with
    // an ACTIONABLE message when all selected recipients are muted (and for
    // suspension/exhaustion). Show the SMS-pack toast only for genuine
    // setup-missing errors; otherwise surface the server's own message.
    const _msg = error.message || '';
    if (/SMS (is )?not (enabled|set up)/i.test(_msg)) {
      window.showToast?.('SMS not enabled. Add an SMS package in Settings.', 'error');
      run?.fail('Not sent — texting is not set up on this account. Add an SMS package in Settings.');
    } else if (/^Timed out/i.test(_msg) || /deadline-exceeded|unavailable/.test(String(error.code || ''))) {
      // TASK-10112 — we lost the ANSWER, not necessarily the send: the server
      // may still be texting. Never invite a second blast; point at the record.
      window.showToast?.(`Error: ${_msg || 'Unknown error'}`, 'error');
      run?.fail('Lost contact while sending — the text may still be going out. Open "Recent crew texts" below to see who got it before sending again.', { tone: 'warn' });
      if (run) _massTextSentDone = true;
    } else if (error.code === 'failed-precondition' && _msg) {
      window.showToast?.(_msg, 'error');
      run?.fail(`Not sent — ${_msg}`);
    } else {
      window.showToast?.(`Error: ${_msg || 'Unknown error'}`, 'error');
      run?.fail(`Not sent — ${_msg || 'unknown error'}`);
    }
  } finally {
    // TASK-10112 — an early return inside the try leaves the run open; drop it
    // so the button is never held by a send that is not happening.
    run?.dispose();
    // Always reset button state
    btn.disabled = false;
    btn.innerHTML = originalText;
    if (_massTextSentDone) btn.innerHTML = '<span class="material-icons">check</span> Done';
  }
}

// TASK-10112 — some (or all) failed: the result stays IN the modal with who
// failed and why, and Send becomes Done — pressing it again would re-text
// everyone who did get it.
function _massTextShowFailures(run, data, total) {
  const d = data || {};
  if (run) run.finish(_massTextResult(d, total, ''));
  else window.showToast?.(`Some messages failed: ${d.errors?.join(', ') || 'Unknown error'}`, 'warning');
  if (d.sent > 0) { _stampLastMassTextCount(d.sent); clearMassTextDraft(); }
  _massTextSentDone = true;
}

// TASK-10112 — close the Notify Crew modal from its "Done" state.
function _closeMassTextAfterResult() {
  _massTextSentDone = false;
  closeModal('mass-text');
}

// TASK-10112 — a sendMassText answer -> the shared result shape (the mapping
// lives in the lazy chunk; only called once a run exists, i.e. it is loaded).
function _massTextResult(data, total, note) {
  return window.SendFeedback.fromMassText(data, total, note);
}

// TASK-10112 — live "Sending N of M" for the SYNCHRONOUS send. sendMassTextLegacy
// writes one notificationLogs row per recipient AS it texts them (status
// sent|failed, messageParts set); this counts those rows for this user since
// the click. Same read-only query as the TASK-9219 watch (rules: tenant member
// read; automatic single-field index), and the same "first server snapshot is
// the old contents" exclusion. Only `status` is read — never phone or body.
// Rows without messageParts (the callable's own post-loop log copy) are ignored.
function _massTextLiveProgress(tenantId, expected, onCount) {
  if (!tenantId || !expected) return () => {};
  const startedAtMs = Date.now();
  const me = _massTextWatchIdentity();
  const seen = new Set();
  const exclude = new Set();
  let seeded = false;
  let unsub = null;
  try {
    const q = query(
      collection(db, 'tenants', tenantId, 'notificationLogs'),
      where('sentAt', '>=', new Date(startedAtMs - MASS_TEXT_WATCH_SKEW_MS)),
      orderBy('sentAt', 'asc'),
      limit(500)
    );
    unsub = onSnapshot(q, (snap) => {
      if (!seeded) {
        if (snap.metadata.fromCache) return;
        snap.forEach((d) => exclude.add(d.id));
        seeded = true;
        return;
      }
      snap.forEach((d) => {
        if (exclude.has(d.id) || seen.has(d.id)) return;
        const x = d.data() || {};
        if (x.type !== 'MASS_TEXT' || typeof x.messageParts !== 'number') return;
        if (me && String(x.sentBy || '').toLowerCase() !== me) return;
        if (x.status === 'sent' || x.status === 'failed') seen.add(d.id);
      });
      onCount(Math.min(seen.size, expected));
    }, () => { /* no live count — the staged steps + timer still run */ });
  } catch (_) { /* same: staged steps only */ }
  return () => { try { if (unsub) unsub(); } catch (_) {} };
}

async function sendPersonalizedMassText(recipients, tenantId, btn, originalText, run = null, sf = null) {
  // Calculate date range based on selected day
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let startDate = new Date(today);
  let endDate = new Date(today);

  switch (massTextDay) {
    case 'tomorrow':
      startDate.setDate(startDate.getDate() + 1);
      endDate = new Date(startDate);
      break;
    case '3days':
      endDate.setDate(endDate.getDate() + 2);
      break;
    case 'week':
      endDate.setDate(endDate.getDate() + 6);
      break;
  }

  let sent = 0;
  let failed = 0;
  const errors = [];
  const failures = [];   // TASK-10112 — {name, reason} for the result summary
  let creditsUsed = 0;   // TASK-10112 — segments billed (server totalSegments)
  if (run) run.step(1);

  // TASK-9842 — one callable per driver, ONE broadcast record for the run: the
  // server merges every send carrying this id into the same doc. Client-minted,
  // server-validated (shape only), scoped under this tenant's path.
  const broadcastGroupId = 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  const broadcastLabel = 'Personalized schedule (' + massTextDay + ')';
  const confirmAsk = _massTextConfirmAskOn();

  // Send personalized message to each recipient
  for (const recipient of recipients) {
    try {
      // TASK-856 (H3 follow-up): a driver who set their own private PIN
      // (driverPinCustomSet) must NEVER be texted the guessable computed PIN —
      // it won't log them in AND it re-exposes the exact credential H3 killed.
      // Legacy on-ramp drivers (no custom PIN) still need the computed PIN to
      // bootstrap, so preserve that path. formatDriverScheduleMessage renders
      // reset guidance instead of a Code line when pin is null.
      const isCustomSet = !!recipient.driverPinCustomSet;
      // TASK-4873 — a driver with a pending setup code (pinResetRequested) is NOT
      // on the computed code any more: classifyDriverPinMatch ignores it while a
      // bootstrap hash exists, and it can resolve to ANOTHER driver's login.
      const isSetupPending = !isCustomSet && !!recipient.pinResetRequested;
      // TASK-9941 F S3 (Chris 10/18) — the name + phone code is never printed in
      // a text any more, for anyone: it is a name tag, not a password, and a text
      // is the easiest place to copy it from. TASK-10301 — and no PIN / setup-code
      // footer either: formatDriverScheduleMessage ignores pin + setupPending.

      // Get jobs assigned to this driver for the date range
      const driverJobs = getJobsAssignedToDriver(recipient.personId, startDate, endDate);

      // Format personalized message
      const message = formatDriverScheduleMessage(driverJobs, startDate, endDate, null, isSetupPending);

      // Send via existing SMS function
      const sendMassTextFn = httpsCallable(functions, 'sendMassText');
      const result = await sendMassTextFn({
        tenantId,
        // TASK-1563 belt-and-braces — formatDriverScheduleMessage already ran
        // toGsm7, but the callable boundary re-asserts so a future refactor of
        // the formatter can't silently reopen the UCS-2 leak.
        message: ensureGsm7BeforeSend(withConfirmLine(message, confirmAsk), 'mass_text_personalized'),
        recipients: [recipient],
        broadcastGroupId, broadcastLabel, confirmAsk,   // TASK-9842
      });

      if (result.data?.ok) {
        sent++;
        if (typeof result.data?.totalSegments === 'number') creditsUsed += result.data.totalSegments;
      } else {
        failed++;
        errors.push(`${recipient.name}: ${result.data?.errors?.[0] || 'failed'}`);
        failures.push({ name: recipient.name || 'Unknown', reason: String(result.data?.errors?.[0] || 'failed').replace(/^[^:]*:\s*/, '') });
      }
    } catch (err) {
      console.error(`[Dispatch] Failed to send to ${recipient.name}:`, err);
      failed++;
      errors.push(`${recipient.name}: ${err.message}`);
      failures.push({ name: recipient.name || 'Unknown', reason: err.message || 'failed' });
    }

    // Update button to show progress (TASK-10112: the run shows it in the modal)
    if (run) run.progress(sent + failed, recipients.length);
    else btn.innerHTML = `<span class="loading-spinner-small"></span> Sending ${sent + failed}/${recipients.length}...`;
  }
  const _res = { total: recipients.length, sent, failed, failures, creditsUsed };

  // Done sending - show result (button reset handled by caller's finally block)
  window.Telemetry?.logUserAction('sms_send_completed', {
    source: 'mass_text_personalized',
    sent,
    failed,
    total: recipients.length,
  });
  if (failed === 0) {
    if (run && sf) { run.finish(_res); sf.showResultCard(_res); }
    else window.showToast?.(`Personalized messages sent to ${sent} driver(s)`, 'success');
    _stampLastMassTextCount(sent); // TASK-1533
    clearMassTextDraft(); // TASK-9532 — this range went out; drop any standard-mode draft for it
    closeModal('mass-text');
  } else {
    // TASK-10112 — who failed and why, in the modal; Send becomes Done.
    if (run) run.finish(_res);
    else window.showToast?.(`Sent ${sent}, failed ${failed}. Check console for details.`, 'warning');
    console.error('[Dispatch] Personalized SMS errors:', errors.length);
    if (sent > 0) { _stampLastMassTextCount(sent); clearMassTextDraft(); }
    _massTextSentDone = true;
  }
}

function getJobsAssignedToDriver(personId, startDate, endDate) {
  // Filter jobDays where this person is in drivers[] or helpers[]
  const results = [];

  // Generate array of dates in range
  const current = new Date(startDate);
  while (current <= endDate) {
    const dateStr = formatDateISO(current);
    const dayJobs = getCrewTextJobsForDate(dateStr); // TASK-9750 — tentative days are never texted

    for (const { job, jobDay } of dayJobs) {
      // Check if driver is assigned - support both ID and name lookups for legacy data
      const drivers = jobDay.drivers || [];
      const helpers = jobDay.helpers || [];
      const allAssigned = [...drivers, ...helpers];

      // Find by ID or name
      const isAssigned = allAssigned.some(ref => {
        if (ref === personId) return true;
        // Check if ref is a name that matches this person
        const person = DispatchState.people.find(p => p.id === personId);
        return person && (ref === person.name || ref === person.displayName);
      });

      if (isAssigned) {
        results.push({ job, jobDay, date: new Date(current) });
      }
    }

    current.setDate(current.getDate() + 1);
  }

  return results;
}

function formatDriverScheduleMessage(jobs, startDate, endDate, pin, setupPending = false) {
  // COMPACT FORMAT for personalized messages - no URL, no footer (TASK-10301)
  let msg = 'SCHEDULE';

  // Date header
  if (startDate.getTime() === endDate.getTime()) {
    msg += ` - ${startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}\n`;
  } else {
    msg += ` - ${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} to ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}\n`;
  }

  if (jobs.length === 0) {
    msg += '\nNo jobs assigned to you.\n';
  } else {
    // Group jobs by customer for clean display
    const byCustomer = {};
    for (const { job, jobDay, date } of jobs) {
      const customer = getCustomerById(job.customerId) || getCustomerById(job.customer) || { name: job.customer || 'Customer' };
      const customerName = customer.name.toUpperCase();
      if (!byCustomer[customerName]) {
        byCustomer[customerName] = [];
      }
      byCustomer[customerName].push({ job, jobDay, date });
    }

    for (const [customerName, customerJobs] of Object.entries(byCustomer)) {
      msg += `\n[${customerName}]\n`;

      for (const { job, jobDay } of customerJobs) {
        // Job title
        msg += `${job.title || 'Job'}`;

        // Show time if not all-day
        if (job.allDay === false && job.defaultStart) {
          const [hours, mins] = job.defaultStart.split(':');
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
          msg += ` @${hour12}:${mins}${ampm}`;
        }
        msg += '\n';

        // Crew and trucks assigned (compact)
        const parts = [];

        // Crew names - first name only
        // Drivers first, then helpers (a helper-only crew member was dropped from the text — Kayden, 2026-09-23).
        const crewNames = [...new Set([...(jobDay.drivers || []), ...(jobDay.helpers || [])])].map(ref => {
          const p = DispatchState.people.find(x => x.id === ref || x.name === ref);
          return p ? p.name.split(' ')[0] : (typeof ref === 'string' && ref.includes(' ') ? ref.split(' ')[0] : ref);
        }).filter(Boolean);
        if (crewNames.length > 0) {
          parts.push(crewNames.join(', '));
        }

        // Trucks
        const truckNames = (jobDay.trucks || []).map(ref => {
          const t = _resolveFleetRef(ref, DispatchState.trucks);
          return t ? (window.getUnitDisplay?.(t) || t.name || ref) : ref;
        }).filter(Boolean);
        if (truckNames.length > 0) {
          parts.push(truckNames.join(', '));
        }

        if (parts.length > 0) {
          msg += `> ${parts.join(' / ')}\n`;
        }

        // First waypoint location
        if (job.waypoints && job.waypoints.length > 0) {
          const wp = job.waypoints[0];
          const label = wp.name || 'Location';
          msg += `@ ${label}\n`;
        }
      }
    }
  }

  // TASK-10301 (Chris 11/04) — no footer: the text ends after the driver's own
  // job lines. The "View details" line and the PIN / setup-code line only cost
  // credits; a driver who needs a PIN gets it from Driver Code / dispatch.
  // TASK-9941 F S3 — never a Code line either. `pin` and `setupPending` are
  // ignored; they stay in the signature so no call site shifts.

  // TASK-1563 — the personalized path assembles its own message (this function,
  // not formatMassTextMessageRange), so it needs its own pass through the
  // sanitizer. Truck names via getUnitDisplay leak the em dash here too.
  msg = msg.replace(/\s+$/, '');   // TASK-10301 — no trailing blank line either
  return toGsm7(msg);
}

function setMassTextDay(day) {
  _flushMassTextDraftSave();   // TASK-9532 — before the range flips (the pending ctx holds the OLD range anyway)
  massTextDay = day;
  document.querySelectorAll('.mass-text-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.day === day);
  });

  // Update content based on mode
  if (massTextPersonalized) {
    updatePersonalizedPreview();
  } else {
    updateMassTextContent();
  }
}

async function updateMassTextContent() {
  if (renderMassTextNotReady()) return;   // readiness FIRST (tripwire); the not-ready branch flushes the draft itself
  _flushMassTextDraftSave();   // TASK-9532 — persist a pending edit before the box is rebuilt
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let startDate = new Date(today);
  let endDate = new Date(today);
  let dateLabel = '';

  switch (massTextDay) {
    case 'tomorrow':
      startDate.setDate(startDate.getDate() + 1);
      endDate = new Date(startDate);
      dateLabel = startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      break;
    case '3days':
      endDate.setDate(endDate.getDate() + 2); // Today + 2 more days = 3 days
      dateLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      break;
    case 'week':
      endDate.setDate(endDate.getDate() + 6); // Today + 6 more days = 7 days
      dateLabel = `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      break;
    default: // today
      dateLabel = startDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }

  document.getElementById('mass-text-date').textContent = dateLabel;

  const { message, jobCount } = await formatMassTextMessageRange(startDate, endDate);
  _massTextTentativeAtBuild = _massTextTentativeIdsInRange(startDate, endDate); // TASK-9750
  const textarea = document.getElementById('mass-text-content');
  textarea.value = message;
  // TASK-9532 — remember what the generator produced (so typing back to it
  // drops the draft), then offer any unsent edit for this range back on top.
  _massTextGeneratedText = message;
  _applyMassTextDraftIfAny(message);

  // Update character count
  onMassTextInput();

  const jobCountEl = document.getElementById('mass-text-job-count');
  if (jobCountEl) {
    jobCountEl.textContent = jobCount === 0 ? '• No jobs' : `• ${jobCount} job${jobCount !== 1 ? 's' : ''}`;
  }
}

// ============================================
// GSM-7 SANITIZER (TASK-1563)
// ============================================
// Twilio only encodes a message as GSM-7 (160 chars/segment) when EVERY char
// is in the GSM-7 set. One char outside it flips the ENTIRE message to UCS-2:
// 70-char segments, roughly double the SMS credits, and long multi-segment
// unicode blasts are exactly what carrier spam filters key on. The leak that
// prompted this: window.getUnitDisplay() renders trucks as `Nick — #12`
// (U+2014 EM DASH — correct typography ON SCREEN), so every Notify Crew blast
// that mentioned a truck silently shipped as UCS-2. Measured live 2026-08-12:
// the same 498-char schedule = 4 segments GSM-7 vs ~8 UCS-2.
//
// The fix lives HERE, on the assembled message, NOT in getUnitDisplay — the em
// dash is right for the UI and wrong on the wire. Sanitizing the formatter's
// final output makes this a single choke point: customer names, job titles,
// notes and prospect data all flow through the same assembly, so the NEXT
// unicode leak (whatever helper it rides in on) is caught here too.
//
// Map table: visually-equivalent ASCII for the characters that actually show
// up in dispatch text. Anything unmapped and non-ASCII becomes '?' AND logs a
// console.warn — that last resort firing means a new leak class, not noise.
const GSM7_CHAR_MAP = {
  // dashes (em, en, figure, horizontal bar, hyphens, minus) → hyphen
  '‐': '-', '‑': '-', '‒': '-', '–': '-', '—': '-', '―': '-', '−': '-',
  // curly/smart single quotes + prime → apostrophe
  '‘': "'", '’': "'", '‚': "'", '‛': "'", '′': "'",
  // curly/smart double quotes + double prime → straight quote
  '“': '"', '”': '"', '„': '"', '‟': '"', '″': '"',
  // ellipsis → three dots
  '…': '...',
  // unicode spaces (nbsp, narrow nbsp, figure/thin/hair space, ideographic) → plain space
  '\u00A0': ' ', '\u2007': ' ', '\u2009': ' ', '\u200A': ' ', '\u202F': ' ', '\u3000': ' ',
  // zero-width space / BOM → drop entirely
  '\u200B': '', '\uFEFF': '',
  // multiplication sign (dims like "8×10") → x
  '×': 'x',
  // bullets → hyphen (list-shaped, keeps the line readable)
  '•': '-', '·': '-',
};

function toGsm7(str) {
  if (!str) return str || '';
  let out = '';
  const unmapped = [];
  // for...of iterates CODE POINTS, so an emoji (surrogate pair) becomes one
  // '?' instead of two.
  for (const ch of String(str)) {
    const code = ch.codePointAt(0);
    if (code < 128) { out += ch; continue; }
    const mapped = GSM7_CHAR_MAP[ch];
    if (mapped !== undefined) { out += mapped; continue; }
    // Last resort — an unmapped non-ASCII char means a NEW leak source that
    // should be added to the map above. Name it so the console tells us which.
    if (unmapped.length < 20) unmapped.push(`U+${code.toString(16).toUpperCase().padStart(4, '0')} "${ch}"`);
    out += '?';
  }
  if (unmapped.length) {
    console.warn('[Dispatch] toGsm7 replaced unmapped non-GSM-7 char(s) with "?" — add to GSM7_CHAR_MAP:', unmapped.join(', '));
  }
  return out;
}

// TASK-1563 belt-and-braces — the dispatcher can TYPE unicode into the
// textarea AFTER the formatter ran (paste from Word/iMessage = curly quotes),
// and a future call site can skip the formatter entirely. So the last line of
// defense sits at the sendMassText callable boundary itself: assert every char
// is <128; if not, sanitize again and telemetry-log the offending codepoints.
// NEVER blocks the send — a sanitized message going out beats no message.
function ensureGsm7BeforeSend(message, source) {
  let leakCount = 0;
  const codepoints = [];
  for (const ch of String(message || '')) {
    const code = ch.codePointAt(0);
    if (code >= 128) {
      leakCount++;
      if (codepoints.length < 20) codepoints.push(`U+${code.toString(16).toUpperCase().padStart(4, '0')}`);
    }
  }
  if (!leakCount) return message;
  window.Telemetry?.logUserAction('sms_non_gsm7_leak', {
    source,
    count: leakCount,
    codepoints: codepoints.join(','),
  });
  return toGsm7(message);
}

// ============================================
// TASK-9597 — "Crew text" layout: which fields Notify Crew texts contain
// ============================================
// settings.crewTextTokens is an ORDERED list of ids from CREW_TEXT_TOKEN_DEFS.
// ABSENT (or nothing valid left after whitelisting) = CREW_TEXT_DEFAULT_TOKENS,
// which renders byte-for-byte what Notify Crew sent before this setting existed.
// Rendering rules: customer is a bracketed NAME line; title (plus time, if the
// job is not all-day) is one line; crew/trucks/trailers/assets share ONE
// "> a / b / c" line (in token order, placed where the first of them sits);
// stops/allStops, notes/notesFull, po, jobNumber, jobType, contact are one line
// each. Time rides the title line when title is picked, else it is its own line.
const CREW_TEXT_TOKEN_DEFS = [
  { key: 'customer',   label: 'Customer' },
  { key: 'title',      label: 'Job title' },
  { key: 'time',       label: 'Start time (when not all-day)' },
  { key: 'crew',       label: 'Crew (first names)' },
  { key: 'trucks',     label: 'Trucks' },
  { key: 'trailers',   label: 'Trailers' },
  { key: 'assets',     label: 'Assets / equipment' },
  { key: 'stops',      label: 'First destination (+ how many more)' },
  { key: 'allStops',   label: 'Every destination' },
  { key: 'notes',      label: 'Notes (short preview)' },
  { key: 'notesFull',  label: 'Notes (full text)' },
  { key: 'po',         label: 'PO number' },
  { key: 'jobNumber',  label: 'Job number' },
  { key: 'jobType',    label: 'Job type' },
  { key: 'contact',    label: 'Site contact (name + phone)' },
];
const CREW_TEXT_TOKEN_KEYS = CREW_TEXT_TOKEN_DEFS.map(d => d.key);
// What Notify Crew has always sent. Contact, PO, number, type, trailers: off.
const CREW_TEXT_DEFAULT_TOKENS = ['customer', 'title', 'time', 'crew', 'trucks', 'assets', 'stops', 'notes'];
const CREW_TEXT_MAX_TOKENS = 20;
// Mutually exclusive pairs: the first one listed wins, the other is dropped.
const CREW_TEXT_EXCLUSIVE = [['stops', 'allStops'], ['notes', 'notesFull']];

// Whitelist + de-dupe + cap. Never trusts a stored list (a future server copy,
// TASK-9572, must do the same). Returns null when nothing valid is left, so the
// caller falls back to today's layout.
function sanitizeCrewTextTokens(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  for (const k of raw) {
    if (typeof k !== 'string' || !CREW_TEXT_TOKEN_KEYS.includes(k) || out.includes(k)) continue;
    if (CREW_TEXT_EXCLUSIVE.some(pair => pair.includes(k) && pair.some(o => o !== k && out.includes(o)))) continue;
    out.push(k);
    if (out.length >= CREW_TEXT_MAX_TOKENS) break;
  }
  return out.length ? out : null;
}

// null = the tenant has not chosen a layout (today's text).
function getCrewTextTokens() {
  return sanitizeCrewTextTokens(window.AppState?.settings?.crewTextTokens);
}

// Strip control chars (keep newline, return, tab); values are job/customer text.
function _crewTextClean(v) {
  return String(v == null ? '' : v).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ');
}

function _crewTextTime(job) {
  if (job && job.allDay === false && job.defaultStart) {
    const [hours, mins] = String(job.defaultStart).split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
    return `${hour12}:${mins}${ampm}`;
  }
  return '';
}

// DOM-free body builder. days = [{ date: Date, entries: [{ job, jobDay }] }].
// r = resolvers { customer(job), people[], fleet(ref, kind), unit(t), jobNumber(job), jobType(job) }.
// opts = { fullNotes } (the Full notes checkbox, which only widens the short-notes token).
// Returns the RAW text (the caller runs toGsm7 on the final string) and the job count.
function buildCrewTextBody(days, tokens, opts, r) {
  const tk = sanitizeCrewTextTokens(tokens) || CREW_TEXT_DEFAULT_TOKENS;
  const has = (k) => tk.includes(k);
  const fullNotes = !!(opts && opts.fullNotes);
  let message = `SCHEDULE\n`;
  let totalJobCount = 0;
  const multi = days.length > 1;

  for (const { date, entries } of days) {
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    if (multi || entries.length > 0) message += `-- ${dayName} --\n`;
    if (entries.length === 0) {
      if (!multi) message += `No jobs scheduled.\n`;
      continue;
    }
    totalJobCount += entries.length;
    for (const { job, jobDay } of entries) {
      const lineFor = {};
      const groupParts = {};
      const customer = r.customer(job);
      lineFor.customer = `\n[${_crewTextClean(customer.name).toUpperCase()}]\n`;
      const time = _crewTextTime(job);
      if (has('title')) {
        let titleLine = `${_crewTextClean(job.title || 'Job')}`;
        if (has('time') && time) titleLine += ` @${time}`;
        lineFor.title = `${titleLine}\n`;
      } else if (has('time') && time) {
        lineFor.time = `@${time}\n`;
      }
      // Crew: drivers first, then helpers (a helper-only crew member was dropped once).
      groupParts.crew = [...new Set([...(jobDay.drivers || []), ...(jobDay.helpers || [])])].map(ref => {
        const p = (r.people || []).find(x => x.id === ref || x.name === ref);
        return p ? String(p.name).split(' ')[0] : (typeof ref === 'string' && ref.includes(' ') ? ref.split(' ')[0] : ref);
      }).filter(Boolean);
      groupParts.trucks = (jobDay.trucks || []).map(ref => {
        const t = r.fleet(ref, 'trucks');
        return t ? (r.unit(t) || t.name || ref) : ref;
      }).filter(Boolean);
      groupParts.trailers = (jobDay.trailers || []).map(ref => {
        const t = r.fleet(ref, 'trailers');
        return t ? (r.unit(t) || t.unit || t.name || ref) : ref;
      }).filter(Boolean);
      groupParts.assets = (jobDay.assets || []).map(ref => {
        const a = r.fleet(ref, 'assets');
        return a ? a.name : ref;
      }).filter(Boolean);
      const wps = Array.isArray(job.waypoints) ? job.waypoints : [];
      if (has('stops') && wps.length > 0) {
        const label = _crewTextClean(wps[0].name || 'Location');
        lineFor.stops = wps.length > 1 ? `@ ${label} +${wps.length - 1}\n` : `@ ${label}\n`;
      }
      if (has('allStops') && wps.length > 0) {
        lineFor.allStops = `@ ${wps.map(w => _crewTextClean((w && w.name) || 'Location')).join(' > ')}\n`;
      }
      if (job.notes && (has('notes') || has('notesFull'))) {
        if (has('notesFull') || fullNotes) {
          lineFor[has('notesFull') ? 'notesFull' : 'notes'] = `Notes: ${_crewTextClean(job.notes)}\n`;
        } else {
          const flat = _crewTextClean(job.notes).replace(/\n/g, ' ').trim();
          lineFor.notes = `Note: ${flat.length > 40 ? flat.substring(0, 40) + '...' : flat}\n`;
        }
      }
      if (has('po') && job.poNumber) lineFor.po = `PO: ${_crewTextClean(job.poNumber)}\n`;
      if (has('jobNumber')) { const n = r.jobNumber(job); if (n) lineFor.jobNumber = `${_crewTextClean(n)}\n`; }
      if (has('jobType')) { const t = r.jobType(job); if (t) lineFor.jobType = `Type: ${_crewTextClean(t)}\n`; }
      // Job/site contact ONLY (never a driver phone or PIN).
      if (has('contact') && job.poc && job.poc.name) {
        lineFor.contact = `Contact: ${_crewTextClean(job.poc.name)}${job.poc.phone ? ' ' + _crewTextClean(job.poc.phone) : ''}\n`;
      }
      // Emit in token order; the shared "> " line sits at the first group token.
      const GROUP = ['crew', 'trucks', 'trailers', 'assets'];
      let groupDone = false;
      for (const k of tk) {
        if (GROUP.includes(k)) {
          if (groupDone) continue;
          groupDone = true;
          // COD 9597 — the shared line follows the SAVED token order, not a fixed one.
          const parts = tk.filter(g => GROUP.includes(g)).map(g => groupParts[g].map(_crewTextClean).join(', ')).filter(Boolean);
          if (parts.length) message += `> ${parts.join(' / ')}\n`;
        } else if (k === 'time' && has('title')) {
          continue;   // rides the title line
        } else if (lineFor[k]) {
          message += lineFor[k];
        }
      }
    }
  }
  if (totalJobCount === 0 && multi) message += `No jobs scheduled.\n`;
  return { message, jobCount: totalJobCount };
}

async function formatMassTextMessageRange(startDate, endDate) {
  // COMPACT FORMAT: Uses GSM-7 encoding (160 chars/segment) instead of UCS-2 (70 chars/segment)
  // Avoids emojis and special Unicode to prevent carrier filtering on long messages.
  // The promise above is ENFORCED by toGsm7() on the final assembled message —
  // see the sanitizer block right before this function (TASK-1563).
  // TASK-9597 — the body is built by buildCrewTextBody from the tenant's Crew text
  // layout (absent = the layout this function always produced).
  const days = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    const d = new Date(current);
    days.push({ date: d, entries: getCrewTextJobsForDate(formatDateISO(d)) /* TASK-9750 */ });
    current.setDate(current.getDate() + 1);
  }
  const { message, jobCount } = buildCrewTextBody(
    days,
    getCrewTextTokens(),
    { fullNotes: !!document.getElementById('mass-text-full-notes')?.checked },
    _crewTextResolvers()
  );
  // Note: We no longer include URLs in mass texts due to carrier filtering
  // Drivers should use PIN access at dispatchdeck.app instead
  // TASK-1563 — sanitize the FINAL assembled message, not the pieces. This is
  // the single choke point: truck names (em dash from getUnitDisplay), customer
  // names, job titles and notes all funnel through here, and the sanitized text
  // is what lands in the preview textarea — so the char count the dispatcher
  // sees matches what actually goes over the wire.
  return { message: toGsm7(message), jobCount };
}

function _crewTextResolvers() {
  return {
    // Dual lookup: customerId first, then customer name for legacy data
    customer: (job) => getCustomerById(job.customerId) || getCustomerById(job.customer) || { name: job.customer || 'Customer' },
    people: DispatchState.people,
    fleet: (ref, kind) => _resolveFleetRef(ref, DispatchState[kind]),
    unit: (t) => window.getUnitDisplay?.(t),
    jobNumber: (job) => formatJobNumber(job),
    jobType: (job) => (_jobTypeIsDefault(job.type) ? '' : _jobTypeLabelForJob(job)),
  };
}

function copyMassText() {
  const textarea = document.getElementById('mass-text-content');
  navigator.clipboard.writeText(textarea.value).then(() => {
    window.showToast?.('Copied to clipboard', 'success') || alert('Copied!');
  }).catch(() => {
    // Fallback
    textarea.select();
    document.execCommand('copy');
    window.showToast?.('Copied to clipboard', 'success') || alert('Copied!');
  });
}

// Read the dispatcher-chosen link expiry (7/14/30 days) from the Notify Crew
// selector. Defaults to 7 if the control is absent or holds an unexpected value.
// Range is clamped to 7–30 so a tampered <select> can't mint a forever link.
function getDriverLinkExpiryDays() {
  const sel = document.getElementById('driver-link-expiry');
  const raw = parseInt(sel?.value, 10);
  if (![7, 14, 30].includes(raw)) return 7;
  return raw;
}

async function copyDriverLink() {
  try {
    // READ-ONLY share (H3): the access-link is now a view-only job-board share
    // with a dispatcher-chosen expiry (default 7 days). The PIN remains the ONLY
    // write credential — see getOrCreateAccessToken (stamps readOnly:true) +
    // grantAccessTokenMembership (read-only members) + firestore.rules
    // isReadOnlyMember. Existing forever links are grandfathered (untouched).
    const expiryDays = getDriverLinkExpiryDays();
    const token = await getOrCreateAccessToken(expiryDays);
    if (!token) {
      window.showToast?.('Failed to generate link', 'error');
      return false; // TASK-9905: regenerate reads this
    }

    // Use dispatchdeck.app domain
    const driverLink = `https://dispatchdeck.app/?access=${token}`;

    await navigator.clipboard.writeText(driverLink);
    const label = expiryDays === 1 ? '1 day' : `${expiryDays} days`;
    window.showToast?.(`Read-only link copied (expires in ${label})`, 'success');
    return true;
  } catch (error) {
    console.error('[Dispatch] Copy driver link error:', error);
    window.showToast?.('Failed to copy link', 'error');
    return false;
  }
}

async function getOrCreateAccessToken(expiryDays = 7) {
  const tenantId = DispatchState.tenantId || window.AppState?.tenant;
  if (!tenantId) {
    console.error('[Dispatch] No tenant ID');
    return null;
  }

  // H3 READ-ONLY: every NEW link is read-only + expiring. expiryDays=0 (forever)
  // is no longer a caller option from the UI; clamp any stray 0 to the 7-day
  // default so a forever read-only link can never be minted here. (Legacy
  // forever/full tokens already in accessTokens are untouched — grandfathered.)
  if (!expiryDays || expiryDays < 1) expiryDays = 7;

  try {
    // Check if we have a valid token already with matching expiry
    const tokensQuery = query(
      collection(db, 'accessTokens'),
      where('tenantId', '==', tenantId)
    );
    const existingTokens = await getDocs(tokensQuery);

    const now = new Date();
    let validToken = null;

    existingTokens.forEach(docSnap => {
      const data = docSnap.data();

      // Only reuse a token that is itself a READ-ONLY share with matching
      // duration and time left. A legacy forever/full-access token (readOnly
      // unset) is NEVER handed out as a read-only share — that would silently
      // give the recipient a full-access link. Those are left to expire on
      // their own terms (grandfather).
      // Typeless only (COD r2): regenerateDriverLink now keeps typed tokens,
      // so a portal / docs token must never be handed out as the driver link.
      const isDriverLink = data.type === undefined || data.type === null || data.type === '';
      if (isDriverLink && data.readOnly === true && data.expiryDays === expiryDays) {
        const expiresAt = data.expiresAt?.toDate?.() || new Date(data.expiresAt);
        if (expiresAt > new Date(now.getTime() + 60 * 60 * 1000)) {
          validToken = docSnap.id;
        }
      }
    });

    if (validToken) {
      console.log('[Dispatch] Reusing existing read-only token');
      return validToken;
    }

    // Generate new token
    const token = generateAccessToken();

    const expiresAt = new Date(now.getTime() + expiryDays * 24 * 60 * 60 * 1000);
    const tokenData = {
      tenantId,
      expiryDays,
      // H3 — view-only flag consumed by grantAccessTokenMembership (server) to
      // stamp the granted member doc readOnly:true, which firestore.rules
      // isReadOnlyMember() then blocks from ALL writes (tickets, JSA, clock…).
      readOnly: true,
      expiresAt: Timestamp.fromDate(expiresAt),
      createdAt: Timestamp.fromDate(now),
      createdBy: window.AppState?.user?.email || 'unknown'
    };
    console.log('[Dispatch] Created new read-only access token, expires:', expiresAt);

    await setDoc(doc(db, 'accessTokens', token), tokenData);

    return token;
  } catch (error) {
    console.error('[Dispatch] Error creating access token:', error);
    return null;
  }
}

function generateAccessToken() {
  // 16-char token from cryptographically secure RNG.
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
  const bytes = new Uint32Array(16);
  crypto.getRandomValues(bytes);
  let token = '';
  for (let i = 0; i < 16; i++) {
    token += chars.charAt(bytes[i] % chars.length);
  }
  return token;
}

let _regenDriverLinkInFlight = false; // TASK-9905 (COD UX): a double tap on slow yard LTE runs once
async function regenerateDriverLink() {
  const tenantId = DispatchState.tenantId || window.AppState?.tenant;
  if (!tenantId || _regenDriverLinkInFlight) return;
  _regenDriverLinkInFlight = true;
  try {
    await _regenerateDriverLinkOnce(tenantId);
  } finally {
    _regenDriverLinkInFlight = false;
  }
}
async function _regenerateDriverLinkOnce(tenantId) {
  try {
    // TASK-9905 -- server-side. This used to delete the tenant's driver-link
    // (typeless) tokens from the client and nothing else, so every phone that
    // had opened an old link kept its member doc -- and with it the whole
    // tenant's data -- forever. The callable deletes the same tokens (typed
    // portal / docs / order / ticket links are still left alone) AND removes
    // every link member whose token is gone, then signs those browsers out.
    await httpsCallable(functions, 'regenerateDriverLink')({ tenantId });
  } catch (error) {
    console.error('[Dispatch] Error regenerating link:', error);
    const msg = error?.code === 'functions/permission-denied' || error?.code === 'functions/failed-precondition'
      ? error.message : 'Failed to regenerate link';
    window.showToast?.(msg, 'error');
    return;
  }
  // Old links are dead either way; now mint + copy the fresh one. On a copy
  // failure copyDriverLink has already said so -- don't paper over it.
  if (await copyDriverLink()) {
    window.showToast?.('New link generated! Old links are now invalid.', 'success');
  } else {
    window.showToast?.('Old links are now invalid. Tap Copy Driver Link for a new one.', 'warning');
  }
}

// ============================================
// CUSTOMER EDIT MODAL
// ============================================
async function showEditCustomer(externalCustomerId) {
  const customerId = externalCustomerId || document.getElementById('job-customer')?.value;
  if (!customerId) {
    window.showToast?.('Please select a customer first', 'warning');
    return;
  }

  const customer = getCustomerById(customerId);
  if (!customer) return;

  const idEl = document.getElementById('edit-customer-id');
  if (!idEl) { window.showToast?.('Open this customer from Dispatch to edit', 'info'); return; }

  idEl.value = customerId;
  // Mark the modal's contact-storage mode so saveEditCustomer routes correctly
  // even if the in-memory customer object changes underneath (TASK-823).
  idEl.dataset.crmManaged = customer.crmManaged ? '1' : '';
  document.getElementById('edit-customer-name').value = customer.name || '';
  document.getElementById('edit-customer-phone').value = customer.phone || '';
  document.getElementById('edit-customer-email').value = customer.email || '';
  // TASK-9588 — "No ticket emails". Remember what it was when the modal opened
  // so the save only audits a real flip.
  const _teBox = document.getElementById('edit-customer-ticket-emails-off');
  if (_teBox) {
    _teBox.checked = customer.ticketEmailsOff === true;
    _teBox.dataset.prior = customer.ticketEmailsOff === true ? '1' : '';
    _teBox.onchange = null; // _fillEditCustomerTicketEmailNote re-arms it for this customer
  }
  const _teNote = document.getElementById('edit-customer-ticket-email-note');
  if (_teNote) { _teNote.style.display = 'none'; _teNote.textContent = ''; }

  // Show the modal immediately; contacts may load async for CRM-managed customers.
  document.getElementById('modal-edit-customer').classList.remove('hidden');

  // Populate price books section (clockbilling only)
  renderEditCustomerContracts(customerId);

  // Populate contacts. TASK-823: CRM-managed customers keep contacts in the
  // CRM-owned subcollection (what the POC dropdown reads); legacy customers use
  // the embedded contacts[] array. Reading from the wrong store was why a POC
  // added here never showed in the job POC dropdown.
  if (customer.crmManaged && window.CRMModule?.loadContacts) {
    // contactsLoaded gates the save-side reconcile: it stays '0' until the
    // subcollection has actually loaded. CRITICAL (COD data-loss): if the load
    // fails (or the user saves before it finishes), saving must NOT reconcile
    // against an empty list — that would delete every existing contact. The save
    // path skips the contact reconcile unless this flips to '1'.
    idEl.dataset.contactsLoaded = '0';
    const listEl = document.getElementById('customer-contacts-list');
    if (listEl) listEl.innerHTML = `<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;">Loading contacts…</div>`;
    try {
      const rows = await window.CRMModule.loadContacts(customerId);
      // Guard against a stale async resolve after the user switched customers.
      if (idEl.value !== customerId) return;
      renderContactsList(rows);
      idEl.dataset.contactsLoaded = '1';
    } catch (err) {
      console.warn('[Dispatch] Could not load CRM contacts for edit:', err.message);
      if (listEl) listEl.innerHTML = `<div class="empty-state-small" style="color:var(--action-danger,#DC2626);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">error_outline</span>Couldn't load contacts — close and reopen to edit them. Saving now will leave existing contacts untouched.</div>`;
      // contactsLoaded stays '0' → save preserves the subcollection.
    }
  } else {
    idEl.dataset.contactsLoaded = '1'; // legacy embedded: always "loaded"
    renderContactsList(customer.contacts || []);
  }
}

// TASK-9588 — "No ticket emails" doesn't change the driver close-out box; the
// contract's fieldTicketSettings.requireEmailCopy still governs it. Say so
// under the checkbox while it is ticked. Stale-modal guarded by the hidden id.
function _fillEditCustomerTicketEmailNote(customerId, contracts) {
  if (document.getElementById('edit-customer-id')?.value !== customerId) return;
  const note = document.getElementById('edit-customer-ticket-email-note');
  const box = document.getElementById('edit-customer-ticket-emails-off');
  if (!note || !box) return;
  const names = (contracts || [])
    .filter(c => c && c.fieldTicketSettings && c.fieldTicketSettings.requireEmailCopy === true)
    .map(c => c.title || c.name || 'a contract');
  if (!names.length) { note.textContent = ''; note.style.display = 'none'; box.onchange = null; return; }
  note.textContent = `Drivers are still asked to email a copy on ${names.length === 1 ? 'contract ' + names[0] : names.length + ' contracts (' + names.join(', ') + ')'}.`;
  const sync = () => { note.style.display = box.checked ? '' : 'none'; };
  box.onchange = sync; // property, not addEventListener: the modal is reused across customers
  sync();
}

async function renderEditCustomerContracts(customerId) {
  const container = document.getElementById('edit-customer-contracts');
  if (!container) return;

  // Only show for clockbilling subscribers
  if (!window.hasAddOnPackage?.('clockbilling')) {
    container.innerHTML = '';
    return;
  }

  container.innerHTML = `
    <div class="form-section">
      <div class="section-header">
        <h4>Price Books</h4>
      </div>
      <p class="text-muted" style="font-size:0.8rem;">Loading...</p>
    </div>`;

  try {
    // Invalidate cache so we get fresh data
    invalidateCustomerPriceBooks(customerId); // TASK-9446 — also bumps the in-flight guard
    const { contracts } = await loadCustomerLineItems(customerId);
    _fillEditCustomerTicketEmailNote(customerId, contracts);

    if (contracts.length > 0) {
      container.innerHTML = `
        <div class="form-section">
          <div class="section-header">
            <h4>Price Books</h4>
          </div>
          ${contracts.map(c => `
            <div class="contract-summary-card" style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--bg-secondary,#F1F5F9);border-radius:8px;margin-bottom:6px;border:1px solid var(--border-light,#E2E8F0);">
              <span class="material-icons" style="color:var(--brand-blue);font-size:18px;">description</span>
              <div style="flex:1;min-width:0;">
                <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(c.title || 'Untitled')}</div>
                <div style="font-size:0.75rem;color:var(--text-secondary);">${c._lineItemCount || 0} line items</div>
              </div>
              <span class="badge badge-active-green">Active</span>
            </div>
          `).join('')}
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">info</span>
            Manage line items in Billing → Line Items
          </p>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="form-section">
          <div class="section-header">
            <h4>Price Books</h4>
          </div>
          <div id="quick-contract-empty" style="text-align:center;padding:12px;background:var(--bg-secondary,#F1F5F9);border-radius:8px;border:1px dashed var(--border-light,#E2E8F0);">
            <span class="material-icons" style="font-size:28px;color:var(--text-secondary);margin-bottom:4px;">menu_book</span>
            <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">No price books yet — rates will use defaults.</p>
            <button type="button" class="btn btn-secondary btn-small" onclick="window.DispatchModule.showQuickContractForm('${escJsAttr(customerId)}')">
              <span class="material-icons">add</span> Add Price Book
            </button>
          </div>
          <div id="quick-contract-form" class="hidden" style="margin-top:8px;padding:12px;background:var(--bg-secondary,#F1F5F9);border-radius:8px;border:1px solid var(--border-light,#E2E8F0);">
            <div class="form-group" style="margin-bottom:8px;">
              <label style="font-size:0.8rem;font-weight:600;color:var(--text-primary);">Price Book Title</label>
              <input type="text" id="quick-contract-title" placeholder="e.g. Standard Rates" style="font-size:0.85rem;">
            </div>
            <div style="display:flex;gap:8px;justify-content:flex-end;">
              <button type="button" class="btn btn-secondary btn-small" onclick="document.getElementById('quick-contract-form').classList.add('hidden');document.getElementById('quick-contract-empty').classList.remove('hidden');">Cancel</button>
              <button type="button" class="btn btn-primary btn-small" onclick="window.DispatchModule.createQuickContract('${escJsAttr(customerId)}')">
                <span class="material-icons">save</span> Create
              </button>
            </div>
          </div>
        </div>`;
    }
  } catch (err) {
    console.warn('[Dispatch] Error loading contracts for edit customer:', err);
    container.innerHTML = '';
  }
}

function showQuickContractForm(customerId) {
  const emptyState = document.getElementById('quick-contract-empty');
  const form = document.getElementById('quick-contract-form');
  if (emptyState) emptyState.classList.add('hidden');
  if (form) {
    form.classList.remove('hidden');
    // Pre-fill with customer name
    const customer = getCustomerById(customerId);
    const titleInput = document.getElementById('quick-contract-title');
    if (titleInput && customer?.name) {
      titleInput.value = customer.name + ' Rates';
    }
    titleInput?.focus();
  }
}

async function createQuickContract(customerId) {
  const titleInput = document.getElementById('quick-contract-title');
  const title = titleInput?.value?.trim();
  if (!title) {
    window.showToast?.('Price book title is required', 'error');
    return;
  }

  const tenant = DispatchState.tenantId;
  if (!tenant || !customerId) return;

  try {
    await addDoc(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'), {
      title,
      status: 'Active',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      _lineItemCount: 0
    });

    window.showToast?.('Price book created — add line items in Billing', 'success');

    // Invalidate cache so pickers refresh
    invalidateCustomerPriceBooks(customerId); // TASK-9446 — also bumps the in-flight guard

    // Re-render contracts section
    await renderEditCustomerContracts(customerId);

    // Also refresh the job modal contract area if open
    const jobCustomer = document.getElementById('job-customer')?.value;
    if (jobCustomer === customerId) {
      const jobContractArea = document.getElementById('job-contract-area');
      if (jobContractArea) jobContractArea.innerHTML = '';
      loadCustomerLineItems(customerId).then(({ contracts, items }) => {
        if (contracts.length > 0) {
          renderContractPicker('job-contract-area', contracts, null, true);
          if (contracts.length === 1) {
            DispatchState.pendingJobSelectedContractId = contracts[0].id;
          }
        }
      });
    }
  } catch (err) {
    console.error('[Dispatch] Error creating quick contract:', err);
    window.showToast?.('Failed to create price book', 'error');
  }
}

function renderContactsList(contacts) {
  const container = document.getElementById('customer-contacts-list');
  if (contacts.length === 0) {
    container.innerHTML = `<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">person_off</span>No contacts added yet</div>`;
    return;
  }

  container.innerHTML = contacts.map((contact, index) => `
    <div class="contact-row" data-index="${index}" data-crm-id="${escapeHtml(contact._crmId || '')}">
      <div class="form-row">
        <div class="form-group flex-1">
          <input type="text" class="contact-name" value="${escapeHtml(contact.name || '')}" placeholder="Name">
        </div>
        <div class="form-group flex-1">
          <input type="text" class="contact-role" value="${escapeHtml(contact.role || '')}" placeholder="Role">
        </div>
        <button type="button" class="btn-icon btn-remove-contact" onclick="window.DispatchModule.removeContactRow(${index})">
          <span class="material-icons">delete</span>
        </button>
      </div>
      <div class="form-row">
        <div class="form-group flex-1">
          <input type="tel" class="contact-phone" value="${escapeHtml(contact.phone || '')}" placeholder="Phone">
        </div>
        <div class="form-group flex-1">
          <input type="tel" class="contact-phone2" value="${escapeHtml(contact.phone2 || '')}" placeholder="Phone 2">
        </div>
      </div>
      <div class="form-row">
        <div class="form-group flex-2">
          <input type="email" class="contact-email" value="${escapeHtml(contact.email || '')}" placeholder="Email">
        </div>
        <div class="form-group">
          <label class="checkbox-label">
            <input type="checkbox" class="contact-primary" ${contact.isPrimary ? 'checked' : ''}>
            <span>Primary</span>
          </label>
        </div>
      </div>
    </div>
  `).join('');
}

function addContactRow() {
  const container = document.getElementById('customer-contacts-list');
  // TASK-1395 — renderContactsList emits the empty state as `.empty-state-small`
  // (:12140), not `.text-muted`, so "No contacts added yet" survived under a
  // freshly added row. Remove both; the old selector stays as a cheap fallback.
  const emptyMsg = container.querySelector('.empty-state-small, .text-muted');
  if (emptyMsg) emptyMsg.remove();

  const index = container.querySelectorAll('.contact-row').length;
  const newRow = document.createElement('div');
  newRow.className = 'contact-row';
  newRow.dataset.index = index;
  newRow.innerHTML = `
    <div class="form-row">
      <div class="form-group flex-1">
        <input type="text" class="contact-name" placeholder="Name">
      </div>
      <div class="form-group flex-1">
        <input type="text" class="contact-role" placeholder="Role">
      </div>
      <button type="button" class="btn-icon btn-remove-contact" onclick="window.DispatchModule.removeContactRow(${index})">
        <span class="material-icons">delete</span>
      </button>
    </div>
    <div class="form-row">
      <div class="form-group flex-1">
        <input type="tel" class="contact-phone" placeholder="Phone">
      </div>
      <div class="form-group flex-1">
        <input type="tel" class="contact-phone2" placeholder="Phone 2">
      </div>
    </div>
    <div class="form-row">
      <div class="form-group flex-2">
        <input type="email" class="contact-email" placeholder="Email">
      </div>
      <div class="form-group">
        <label class="checkbox-label">
          <input type="checkbox" class="contact-primary">
          <span>Primary</span>
        </label>
      </div>
    </div>
  `;
  // Add new contact at the TOP of the list
  container.prepend(newRow);

  // Focus the name field for quick entry
  newRow.querySelector('.contact-name')?.focus();
}

function removeContactRow(index) {
  const row = document.querySelector(`.contact-row[data-index="${index}"]`);
  if (row) row.remove();
}

async function saveEditCustomer(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const customerId = document.getElementById('edit-customer-id').value;
  const name = document.getElementById('edit-customer-name').value.trim();

  if (!name) {
    window.showToast?.('Customer name is required', 'error');
    return;
  }

  // TASK-823 — which contact store this customer uses, captured when the modal
  // opened (data attr survives even if the in-memory customer object changes).
  const idElForSave = document.getElementById('edit-customer-id');
  const crmManaged = idElForSave?.dataset.crmManaged === '1';
  // Only reconcile the subcollection if contacts actually loaded — otherwise a
  // load failure (or a save before load finished) would wipe every contact.
  const contactsLoaded = idElForSave?.dataset.contactsLoaded === '1';

  // Collect contacts from form (carry _crmId so CRM-managed saves update the
  // right subcollection doc instead of duplicating it).
  const contactRows = document.querySelectorAll('.contact-row');
  const contacts = [];
  // TASK-1395 — a row with a phone/email but no name used to be dropped SILENTLY
  // here (and again by saveContacts' own name filter), so the user saw "Customer
  // updated" and then "No contacts added yet". Stop the save and say why instead.
  let namelessWithData = false;
  contactRows.forEach(row => {
    const contact = {
      _crmId: row.dataset.crmId || '',
      name: row.querySelector('.contact-name').value.trim(),
      role: row.querySelector('.contact-role').value.trim(),
      phone: row.querySelector('.contact-phone').value.trim(),
      phone2: row.querySelector('.contact-phone2').value.trim(),
      email: row.querySelector('.contact-email').value.trim(),
      isPrimary: row.querySelector('.contact-primary').checked
    };
    if (contact.name) {
      contacts.push(contact);
    } else if (contact.role || contact.phone || contact.phone2 || contact.email) {
      namelessWithData = true;
      row.querySelector('.contact-name')?.focus();
    }
    // A fully blank row is still dropped quietly — that's just an unused row.
  });
  if (namelessWithData) {
    window.showToast?.('Contact needs a name — add one or clear the row', 'error');
    return;
  }

  // Customer doc: never write the embedded contacts[] array for a CRM-managed
  // customer (its contacts live in the subcollection — writing embedded here is
  // exactly the desync that hid the POC). Legacy customers keep embedded.
  const customerData = {
    name,
    phone: document.getElementById('edit-customer-phone').value.trim() || null,
    email: document.getElementById('edit-customer-email').value.trim() || null,
    updatedAt: Timestamp.now()
  };
  // TASK-9588 — updateDoc merges, so every other customer field survives.
  const _teBoxSave = document.getElementById('edit-customer-ticket-emails-off');
  const _tePrior = _teBoxSave ? _teBoxSave.dataset.prior === '1' : null;
  if (_teBoxSave) customerData.ticketEmailsOff = _teBoxSave.checked === true;
  if (!crmManaged) customerData.contacts = contacts.map(({ _crmId, ...c }) => c);

  try {
    // For CRM-managed customers, persist contacts to the CRM-owned subcollection
    // FIRST (CRM owns that schema — dispatch must not reimplement it). If this
    // throws, surface the error and keep the modal open (don't lose edits).
    // SKIP the reconcile entirely when contacts never loaded (COD data-loss
    // guard) — reconciling against a list the user never actually saw would
    // delete the existing subcollection contacts. Customer name/phone/email
    // still save; a toast tells them to reopen to edit contacts.
    if (crmManaged && contactsLoaded) {
      if (!window.CRMModule?.saveContacts) throw new Error('CRM module not loaded');
      await window.CRMModule.saveContacts(customerId, contacts);
    } else if (crmManaged && !contactsLoaded) {
      window.showToast?.('Contacts didn’t load — details saved, but reopen to edit contacts', 'warning');
    }
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'customers', customerId), customerData);
    // TASK-9588 — customer id + old/new boolean only (no name / PII).
    if (_tePrior !== null && _tePrior !== customerData.ticketEmailsOff) {
      window.logAudit?.('crm', 'CUSTOMER_TICKET_EMAILS_TOGGLE', { customerId, from: _tePrior, to: customerData.ticketEmailsOff });
    }
    window.showToast?.('Customer updated', 'success');
    closeModal('edit-customer');

    // Refresh POC dropdown if customer is selected
    if (document.getElementById('job-customer').value === customerId) {
      populatePocDropdown(customerId);
    }
  } catch (error) {
    console.error('[Dispatch] Save customer error:', error);
    window.showToast?.('Error saving customer', 'error');
  }
}

async function deleteCustomer() {
  const customerId = document.getElementById('edit-customer-id').value;
  if (!customerId) return;

  const customer = getCustomerById(customerId);
  const name = customer?.name || 'this customer';
  const tenant = DispatchState.tenantId;

  // TASK-10030 delete guard — same rule as CRM (crm.js deleteCustomer): a
  // merged customer is kept for its billing history; one with jobs, price
  // books, contacts, sites or projects is set Inactive or merged, never
  // deleted. Fails closed when the check can't run.
  if (customer?.mergedIntoId) {
    window.showToast?.(`"${name}" was merged into ${customer.mergedIntoName || 'another customer'}. It's kept for its billing history.`, 'warning');
    return;
  }
  let held = null;
  try {
    if (typeof window.CRMModule?.customerDeleteHolds !== 'function') throw new Error('delete check unavailable');
    held = await window.CRMModule.customerDeleteHolds(customerId, customer?.name || '', tenant);
  } catch (e) {
    console.error('[Dispatch] TASK-10030: delete check failed', e);
    window.showToast?.(`Couldn't check what "${name}" still has, so it was not deleted. Try again.`, 'error');
    return;
  }
  if (held.length) {
    await window.showConfirm(`Can't delete "${name}"`, `This customer has ${held.join(', ')}. Deleting it would cut those off. Set it Inactive, or merge it into another customer from CRM, instead.`, { confirmText: 'OK', hideCancel: true, icon: 'block' });
    return;
  }

  // Count related data to show in confirmation
  let ticketCount = 0;
  let jobCount = 0;
  let contractCount = 0;
  try {
    const [ticketSnap, jobSnap, contractSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('customerId', '==', customerId))),
      getDocs(query(collection(db, 'tenants', tenant, 'jobs'), where('customerId', '==', customerId))),
      getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'))
    ]);
    ticketCount = ticketSnap.size;
    jobCount = jobSnap.size;
    contractCount = contractSnap.size;
  } catch (e) {
    console.warn('[Dispatch] Error counting related data:', e);
  }

  const impacts = [];
  if (ticketCount > 0) impacts.push(`${ticketCount} billing ticket${ticketCount !== 1 ? 's' : ''}`);
  if (jobCount > 0) impacts.push(`${jobCount} job${jobCount !== 1 ? 's' : ''}`);
  if (contractCount > 0) impacts.push(`${contractCount} contract${contractCount !== 1 ? 's' : ''} (price book${contractCount !== 1 ? 's' : ''})`);

  let msg = `Delete "${name}"?\n\n`;
  if (impacts.length > 0) {
    msg += `This customer is referenced by:\n• ${impacts.join('\n• ')}\n\nTickets and jobs will keep their data but lose the customer link. Contracts and their line items will be permanently deleted.\n\n`;
  }
  msg += 'This cannot be undone.';

  if (!confirm(msg)) return;

  try {
    // Delete subcollections first (contracts → lineItems, then contacts, sites)
    if (contractCount > 0) {
      const contractSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'));
      const batch = writeBatch(db);
      let opCount = 0;
      for (const cDoc of contractSnap.docs) {
        const liSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', cDoc.id, 'lineItems'));
        liSnap.docs.forEach(li => { batch.delete(li.ref); opCount++; });
        batch.delete(cDoc.ref);
        opCount++;
      }
      if (opCount > 0) await batch.commit();
    }

    // Delete contacts subcollection
    const contactsSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contacts'));
    if (contactsSnap.size > 0) {
      const batch2 = writeBatch(db);
      contactsSnap.docs.forEach(d => batch2.delete(d.ref));
      await batch2.commit();
    }

    // Delete sites subcollection
    const sitesSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'sites'));
    if (sitesSnap.size > 0) {
      const batch3 = writeBatch(db);
      sitesSnap.docs.forEach(d => batch3.delete(d.ref));
      await batch3.commit();
    }

    // Delete customer document
    await deleteDoc(doc(db, 'tenants', tenant, 'customers', customerId));

    window.showToast?.(`"${name}" deleted`, 'success');
    closeModal('edit-customer');
  } catch (error) {
    console.error('[Dispatch] Delete customer error:', error);
    window.showToast?.('Error deleting customer', 'error');
  }
}

// ============================================
// POC FUNCTIONS
// ============================================
// TASK-4579 — Smart Job Builder matches the customer by NAME (a two-way
// substring match), so it can land on a similarly named customer. Once the
// match runs onCustomerChange, that pick also brings the customer's price
// book, standing PO and contacts with it — so the one field this path can get
// wrong is called out until a person changes it. Pass a name to show the
// cue, null to remove it. textContent only: the name is customer data.
function _setJobCustomerAiCue(customerName) {
  document.getElementById('job-customer-ai-cue')?.remove();
  if (!customerName) return;
  const sel = document.getElementById('job-customer');
  const anchor = sel?.closest('.input-with-action') || sel;
  if (!anchor || !anchor.parentNode) return;
  const cue = document.createElement('div');
  cue.id = 'job-customer-ai-cue';
  cue.setAttribute('role', 'status');
  cue.className = 'dd-ai-cue'; // integ/2129 perf: the static look is .dd-ai-cue in styles.css (same values)
  const icon = document.createElement('span');
  icon.className = 'material-icons';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = 'auto_awesome';
  const txt = document.createElement('span');
  const strong = document.createElement('strong');
  strong.textContent = String(customerName);
  txt.append('Matched from your description: ', strong, ". Make sure it's the right customer.");
  cue.append(icon, txt);
  anchor.parentNode.insertBefore(cue, anchor.nextSibling);
}

function onCustomerChange(source = 'user') {
  // TASK-4579 — WHO set the customer: 'user' (the select's own onchange, and
  // every caller that passes nothing), 'ai_parse' (Smart Job Builder matched it
  // by name), 'duplicate' (Copy to New Job). Anything that is not a string
  // (an Event, if this is ever bound as a listener) counts as the user.
  const _custSource = typeof source === 'string' && source ? source : 'user';
  const customerId = document.getElementById('job-customer').value;
  p811JobFormSiteChanged(); // TASK-9887 — the potential / waiting-tickets banner follows the customer

  // TASK-398: breadcrumb — customer field changed from→to (IDs only). This is
  // the proven-incident instrumentation point: the field that landed a job on
  // the wrong customer. Fires on the <select> change event, not per-keystroke.
  // TASK-4579: `source` says whether a person or the machine picked it, so an
  // AI-matched customer is not recorded as the dispatcher's own choice.
  if ((customerId || null) !== (_breadcrumbCustomerId || null)) {
    window.ActionLog?.record('customer_change', { from: _breadcrumbCustomerId || null, to: customerId || null, source: _custSource });
    _breadcrumbCustomerId = customerId || null;
  }
  // TASK-4579 — the "matched from your description" cue belongs to the AI's
  // pick only; any other change of customer takes it away.
  if (_custSource !== 'ai_parse') _setJobCustomerAiCue(null);

  // TASK-9563 — driver PIN form: the Project picker lists the NEW customer's
  // active projects (server-fed; a PIN session cannot read projects). The
  // current pick is kept only if it is still one of them.
  if (window.AppState?.driverModePin === true) _driverRefreshProjectOptions();

  const pocSection = document.getElementById('poc-section');
  if (customerId) {
    pocSection.classList.remove('hidden');
  } else {
    pocSection.classList.add('hidden');
  }
  // Clear POC when customer changes — old contact doesn't belong to new customer
  const pocSelect = document.getElementById('job-poc-select');
  if (pocSelect) pocSelect.value = '';
  document.getElementById('poc-info-card')?.classList.add('hidden');
  ['job-poc-name', 'job-poc-role', 'job-poc-phone', 'job-poc-phone2', 'job-poc-email'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });
  populatePocDropdown(customerId);
  document.getElementById('job-builder-customer-hints')?.remove(); // TASK-4803 S3b — they were for the previous customer
  applyJobRequiredFieldWarnings();
  // TASK-9363 — a project carried by Copy stays only while it is this customer's.
  _recheckCarriedProjectCustomer();
  // Wolverine 2026-10-21 — a Location WE filled was the old customer's site;
  // drop it, then read this customer's price books (Project list) + sites.
  if (document.getElementById('job-location')?.dataset.autofilledLoc && _jobLocationIsOurs()) _applyJobLocation('', 'auto', null);
  // A New project opened from the previous customer's price book is that
  // customer's — close it rather than create it under this one.
  if (_jobFormBookStart && _jobFormBookStart.customerId !== customerId
      && document.getElementById('job-create-new-project')?.checked) {
    _resetJobNewProjectMode();
    _syncJobProjectLinkBox();
  }
  _jobFormBookStart = null;
  _loadJobFormCustomerSources(customerId, { autofill: true });
  // TASK-9606 — then re-filter the Project picker for the new customer (after
  // the carry check: it reads sel.value, and owns that case's one toast).
  _refilterJobProjectsForCustomer(customerId);

  // TASK-4782 — the PO suggestion list is scoped to the customer, so it has to
  // be rebuilt the moment the customer changes, and the hint re-evaluated: a PO
  // that matched an existing spelling for customer A means nothing for B.
  refreshJobPoSuggestions();
  onJobPoInput();

  // TASK-4660 — re-run the inline duplicate check now that the customer is known.
  // checkJobDuplicate only ever fired on focusout of #job-title / #job-well-name,
  // so the natural order (type the title, THEN pick the customer) matched nothing:
  // with #job-customer still empty the filter keeps only customer-less jobs, and
  // nothing re-ran it afterwards. Soft warning only — it never blocks, and the
  // save-time gate is unchanged. Safe on the edit path too: checkJobDuplicate
  // early-returns whenever #job-id is populated.
  checkJobDuplicate();

  // TASK-4579 (COD r2) — a standing PO this handler filled for the PREVIOUS
  // customer leaves with that customer. Only when the box still holds exactly
  // that fill (untouched; a typed, AI-stated or project PO never matches the
  // pair), so nothing a person or a project put there is cleared.
  {
    const poField = document.getElementById('job-po');
    const from = poField?.dataset?.autofilledPoFrom;
    if (from) {
      let pair = null;
      try { pair = JSON.parse(from); } catch (_) { pair = null; }
      if (!Array.isArray(pair) || pair[0] !== customerId) {
        if (Array.isArray(pair) && poField.value === pair[1] && poField.dataset.autofilledPo === pair[1]) {
          poField.value = '';
          delete poField.dataset.autofilledPo;
          onJobPoInput();
        }
        delete poField.dataset.autofilledPoFrom;
      }
    }
  }

  // Clear contract + line item picker state when customer changes
  DispatchState.pendingJobLineItemSelections = {};
  DispatchState.pendingJobSelectedContractId = null;
  const jobContractArea = document.getElementById('job-contract-area');
  if (jobContractArea) jobContractArea.innerHTML = '';
  { const _pbHint = document.getElementById('job-pricebook-hint'); if (_pbHint) _pbHint.innerHTML = ''; } // TASK-9446
  document.querySelectorAll('#modal-job .line-item-picker').forEach(el => el.remove());

  // Gate: only load pickers for clockbilling subscribers
  if (customerId && window.hasAddOnPackage?.('clockbilling')) {
    const _pbGen = _jobFormGen;
    loadCustomerLineItems(customerId).then(({ contracts, items, drafts }) => {
      // TASK-10031 (COD r1) — a slow read for the PREVIOUS customer (or form)
      // must not paint its price books / PO onto this one.
      if (_pbGen !== _jobFormGen || (document.getElementById('job-customer')?.value || '') !== customerId) return;
      // TASK-9446 — the Draft hint renders OUTSIDE the contracts.length branch:
      // 0 Active + 1 Draft is exactly the case that showed nothing useful.
      // Rendered first, so a fast customer switch is dropped by its own guard.
      _refreshJobPriceBookDraftHint(customerId);
      if (contracts.length > 0) {
        // TASK-9391 — show whatever is pending, not a hard-coded blank. It was
        // cleared synchronously above, so this is still null on a plain customer
        // pick; it is non-null only when a project New Job carried a price book
        // in the same tick (_applyProjectNewJobCarry), which must not render as
        // "Auto" while the save writes the carried book.
        renderContractPicker('job-contract-area', contracts, DispatchState.pendingJobSelectedContractId, true);
        // Auto-select if only 1 contract
        if (contracts.length === 1) {
          DispatchState.pendingJobSelectedContractId = contracts[0].id;
        }
        // Auto-fill PO from any contract's Permanent PO setting if the field is empty.
        // Mirrors the billing-ticket auto-fill so dispatch job creation doesn't force PMs
        // to retype a PO that's already locked in on the customer's contract.
        const poField = document.getElementById('job-po');
        if (poField && !poField.value.trim()) {
          const permPoContract = contracts.find(c => c.permanentPo && c.permanentPoNumber);
          if (permPoContract) {
            poField.value = permPoContract.permanentPoNumber;
            // Mark this as auto-filled, not hand-typed. A contract PO is a CUSTOMER-level
            // blanket; a project PO is the specific corridor. If a project is also in play
            // it must be able to win — and it can only tell the difference by this marker.
            // Without it, a job created from a project's "New Job" button silently bills to
            // the customer's blanket PO instead of the project's.
            poField.dataset.autofilledPo = permPoContract.permanentPoNumber;
            // TASK-4579 (COD r2) — and WHOSE blanket it is, so a customer
            // change can take back a PO that belonged to the previous one.
            poField.dataset.autofilledPoFrom = JSON.stringify([customerId, permPoContract.permanentPoNumber]);
            // TASK-4782 — a PO that arrives by AUTOFILL still has to clear
            // QuickBooks. A contract's blanket PO is set once and inherited by
            // every job under it, so an over-length one here is the worst case:
            // silently wrong on every future invoice. Warn on this path too.
            onJobPoInput();
            applyJobRequiredFieldWarnings();
          }
        }
        const contractItems = DispatchState.pendingJobSelectedContractId
          ? items.filter(i => i.contractId === DispatchState.pendingJobSelectedContractId)
          : items;
        if (contractItems.length > 0) {
          renderLineItemPickers('job-select-trucks', 'truck', contractItems);
          renderLineItemPickers('job-select-trailers', 'trailer', contractItems);
          renderLineItemPickers('job-select-assets', 'asset', contractItems);
          renderLineItemPickers('job-select-drivers', 'person', contractItems);
          renderLineItemPickers('job-select-helpers', 'person', contractItems);
        }
      } else if (jobContractArea && !(drafts || []).length) {
        // No active contracts — show warning banner. TASK-9446: when a Draft
        // book exists, the #job-pricebook-hint banner says so instead.
        // TASK-9344: was a hand-rolled customerId.replace(/'/g, "\\'"), which left
        // `"` (ends the attribute) and `\` unhandled. escJsAttr at the interpolation.
        jobContractArea.innerHTML = `
          <div class="no-contract-banner">
            <span class="material-icons">warning_amber</span>
            <div>
              <strong>No Price Book</strong> — rates will use defaults.
              <a href="#" onclick="event.preventDefault(); window.DispatchModule.showEditCustomer('${escJsAttr(customerId)}')">
                Add one in Edit Customer
              </a>
            </div>
          </div>`;
      }
    });
  }
}

// Required Fields — highlight empty fields based on customer requirements
const JOB_REQUIRED_FIELD_MAP = {
  poNumber: 'job-po',
  vendorNumber: 'job-vendor-number',
  afeNumber: 'job-afe',
  wellName: 'job-well-name',
  notes: 'job-notes',
};

function applyJobRequiredFieldWarnings() {
  // Clear all existing warnings first
  document.querySelectorAll('#modal-job .field-required-warn').forEach(el => el.classList.remove('field-required-warn'));
  document.querySelectorAll('#modal-job .field-required-hint').forEach(el => el.remove());

  const customerId = document.getElementById('job-customer')?.value;
  if (!customerId) return;
  const customer = DispatchState.customers.find(c => c.id === customerId);
  if (!customer?.requiredFields?.length) return;

  const customerName = customer.name || 'customer';
  for (const key of customer.requiredFields) {
    const elId = JOB_REQUIRED_FIELD_MAP[key];
    if (!elId) continue;
    const el = document.getElementById(elId);
    if (!el) continue;
    // TASK-4688 — a hidden section must never swallow a field the CUSTOMER
    // requires. Vendor Number / AFE are the only required-field targets that
    // live in a gate-able section; un-hide it rather than nagging invisibly.
    if (elId === 'job-vendor-number' || elId === 'job-afe') {
      document.getElementById('job-afe-vendor-row')?.classList.remove('hidden');
    }
    // Only highlight if empty
    if (!el.value?.trim()) {
      el.classList.add('field-required-warn');
      // Add hint below field if not already there
      if (!el.parentElement.querySelector('.field-required-hint')) {
        const hint = document.createElement('div');
        hint.className = 'field-required-hint';
        hint.innerHTML = `<span class="material-icons">info_outline</span> Required for ${escapeHtml(customerName)}`;
        el.parentElement.appendChild(hint);
      }
    }

    // Live update: remove warning when field gets filled
    if (!el.dataset.reqListener) {
      el.dataset.reqListener = '1';
      el.addEventListener('input', () => applyJobRequiredFieldWarnings());
    }
  }
}

async function populatePocDropdown(customerId) {
  const select = document.getElementById('job-poc-select');

  if (!customerId) {
    select.innerHTML = '<option value="">Select POC</option>';
    return;
  }

  const customer = getCustomerById(customerId);

  // CRM mode switch: crmManaged customers use CRM subcollection only,
  // non-CRM customers use legacy embedded contacts[] only
  let contactList = [];
  if (window.AppState?.driverModePin === true) {
    // TASK-9703 S2 — a PIN session can't read customers/contacts (TASK-9694
    // S6a): the lazy driverJobPoc.js fills the picker from the
    // driverCustomerContacts callable (and stores _mergedContacts for
    // onPocChange / restoreJobPocSelection). A failed chunk load leaves the
    // job's saved POC alone, same as a refused call.
    try {
      await (await __ddChunkWrap(import('./driverJobPoc.js?v=1'))).fillDriverPocSelect({ select, customerId, customer, httpsCallable, functions });
    } catch (err) {
      console.warn('[Dispatch] TASK-9703: driver POC list unavailable', err?.message || err);
      if (document.getElementById('job-customer')?.value === customerId) select.innerHTML = '<option value="">Contacts unavailable right now</option>';
    }
    return;
  } else if (customer?.crmManaged) {
    // CRM-managed: read from contacts subcollection (showInDispatch filter)
    try {
      const crmSnap = await getDocs(
        query(collection(db, `tenants/${DispatchState.tenantId}/customers/${customerId}/contacts`))
      );
      contactList = crmSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(c => c.showInDispatch !== false)
        .map(c => ({
          name: c.name,
          role: c.title || '',
          phone: c.phone || '',
          phone2: '',
          email: c.email || '',
          isPrimary: !!c.isPrimary,
          _crmId: c.id
        }));
    } catch (err) {
      console.warn('[Dispatch] Could not load CRM contacts:', err.message);
    }
  } else {
    // Legacy: use embedded contacts array
    contactList = [...(customer?.contacts || [])];
  }

  // Store resolved list on customer for onPocChange lookup
  if (customer) customer._mergedContacts = contactList;

  if (contactList.length === 0) {
    select.innerHTML = '<option value="">Add in Edit Customer</option>';
    return;
  }

  select.innerHTML = '<option value="">Select POC</option>';
  contactList.forEach((contact, idx) => {
    const option = document.createElement('option');
    option.value = idx;
    option.textContent = `${contact.name}${contact.role ? ` (${contact.role})` : ''}${contact.isPrimary ? ' ★' : ''}`;
    select.appendChild(option);
  });
}

// Re-select the POC a job already has. Matches the saved job.poc against the
// resolved contact list by name (phone breaks a tie between same-named contacts).
// A POC that is NOT in the customer's contacts still renders — it was typed on the
// job before the contact existed, and silently showing nothing is what caused the bug.
function restoreJobPocSelection(poc) {
  const select = document.getElementById('job-poc-select');
  const infoCard = document.getElementById('poc-info-card');
  const manualFields = document.getElementById('poc-manual-fields');
  if (!select || !infoCard || !poc?.name) return;

  const customer = getCustomerById(document.getElementById('job-customer').value);
  const contactList = customer?._mergedContacts || customer?.contacts || [];
  const norm = s => String(s || '').trim().toLowerCase();
  const digits = s => String(s || '').replace(/\D/g, '');

  let idx = contactList.findIndex(c => norm(c.name) === norm(poc.name) &&
    digits(c.phone) === digits(poc.phone));
  if (idx === -1) idx = contactList.findIndex(c => norm(c.name) === norm(poc.name));

  if (idx >= 0) {
    select.value = String(idx);
    onPocChange(); // renders the info card and refills the hidden save fields
    return;
  }

  // Saved on the job, not in the contact list — show it rather than nothing.
  if (manualFields) manualFields.style.display = 'none';
  infoCard.classList.remove('hidden');
  infoCard.innerHTML = `
    <div class="poc-card">
      <div class="poc-card-name">${escapeHtml(poc.name)}</div>
      ${poc.role ? `<div class="poc-card-role">${escapeHtml(poc.role)}</div>` : ''}
      <div class="poc-card-details">
        ${poc.phone ? `<span><span class="material-icons">phone</span> ${escapeHtml(poc.phone)}</span>` : ''}
        ${poc.phone2 ? `<span><span class="material-icons">phone</span> ${escapeHtml(poc.phone2)}</span>` : ''}
        ${poc.email ? `<span><span class="material-icons">email</span> ${escapeHtml(poc.email)}</span>` : ''}
      </div>
      <div class="poc-card-role">Saved on this job — not in ${escapeHtml(customer?.name || 'the customer')}'s contacts</div>
    </div>
  `;
}

function onPocChange() {
  const select = document.getElementById('job-poc-select');
  const value = select.value;
  const manualFields = document.getElementById('poc-manual-fields');
  const infoCard = document.getElementById('poc-info-card');

  if (value === '' || value === 'manual') {
    // Show manual fields, hide info card
    manualFields.style.display = '';
    infoCard.classList.add('hidden');

    if (value === 'manual') {
      // Clear fields for manual entry
      document.getElementById('job-poc-name').value = '';
      document.getElementById('job-poc-role').value = '';
      document.getElementById('job-poc-phone').value = '';
      document.getElementById('job-poc-phone2').value = '';
      document.getElementById('job-poc-email').value = '';
    }
  } else {
    // Selected a contact - hide manual fields, show info card
    const customerId = document.getElementById('job-customer').value;
    const customer = getCustomerById(customerId);
    if (!customer) return;

    const contactList = customer._mergedContacts || customer.contacts || [];
    const contact = contactList[parseInt(value)];
    if (!contact) return;

    // Populate hidden fields for saving
    document.getElementById('job-poc-name').value = contact.name || '';
    document.getElementById('job-poc-role').value = contact.role || '';
    document.getElementById('job-poc-phone').value = contact.phone || '';
    document.getElementById('job-poc-phone2').value = contact.phone2 || '';
    document.getElementById('job-poc-email').value = contact.email || '';

    // Show info card
    manualFields.style.display = 'none';
    infoCard.classList.remove('hidden');
    infoCard.innerHTML = `
      <div class="poc-card">
        <div class="poc-card-name">${escapeHtml(contact.name)}${contact.isPrimary ? ' <span class="primary-badge">Primary</span>' : ''}</div>
        ${contact.role ? `<div class="poc-card-role">${escapeHtml(contact.role)}</div>` : ''}
        <div class="poc-card-details">
          ${contact.phone ? `<span><span class="material-icons">phone</span> ${escapeHtml(contact.phone)}</span>` : ''}
          ${contact.phone2 ? `<span><span class="material-icons">phone</span> ${escapeHtml(contact.phone2)}</span>` : ''}
          ${contact.email ? `<span><span class="material-icons">email</span> ${escapeHtml(contact.email)}</span>` : ''}
        </div>
      </div>
    `;
  }
}

// ============================================
// ALL DAY TOGGLE
// ============================================
function toggleAllDay() {
  const allDay = document.getElementById('job-all-day').checked;
  document.getElementById('time-fields').style.display = allDay ? 'none' : '';
}

// ============================================
// AI JOB CREATION / DOT ASSISTANT
// ============================================

function renderWarnItem(w) {
  // TASK-962 (COD sibling b, defense-in-depth): w.message/w.icon/w.cfr are
  // LLM-authored (Gemini parse output) — untrusted text crossing into innerHTML.
  // Escape everything; these are plain-text strings by prompt contract.
  const severityClass = w.severity === 'red' ? 'dot-warn-red' : w.severity === 'yellow' ? 'dot-warn-yellow' : 'dot-warn-blue';
  const icon = w.icon || (w.severity === 'red' ? 'error' : w.severity === 'yellow' ? 'warning' : 'info');
  return `<div class="dot-warn-item ${severityClass}">
    <span class="material-icons">${escapeHtml(icon)}</span>
    <div class="dot-warn-text">
      <span>${escapeHtml(w.message)}</span>
      ${w.cfr ? `<span class="dot-warn-cfr">${escapeHtml(w.cfr)}</span>` : ''}
    </div>
  </div>`;
}

function showDotAssistantWarnings(warnings, permitNotes, permitEstimates) {
  // Remove any existing warnings
  document.getElementById('dot-assistant-warnings')?.remove();

  const container = document.createElement('div');
  container.id = 'dot-assistant-warnings';
  container.className = 'dot-assistant-warnings';

  let html = `<div class="dot-assistant-header">
    <span class="material-icons">verified_user</span> Job Builder — Compliance Check
    <span class="dot-source-label">DispatchDeck Compliance Records + 49 CFR</span>
  </div>`;

  if (warnings?.length) {
    warnings.forEach(w => { html += renderWarnItem(w); });
  }

  if (permitNotes) {
    // TASK-962 (COD sibling b): LLM-authored — escape before innerHTML.
    html += `<div class="dot-warn-item dot-warn-blue">
      <span class="material-icons">description</span>
      <div class="dot-warn-text"><span>${escapeHtml(permitNotes)}</span></div>
    </div>`;
  }

  // Permit estimates section
  if (permitEstimates?.length) {
    html += `<div class="dot-assistant-header" style="margin-top:12px">
      <span class="material-icons">receipt_long</span> Permit Estimates
      <span class="dot-source-label">Smart Estimate — verify with state DOT</span>
    </div>`;
    html += '<div class="dot-permit-grid">';
    permitEstimates.forEach(p => {
      // TASK-962 (COD sibling b): LLM-authored — escape before innerHTML.
      html += `<div class="dot-permit-card">
        <div class="dot-permit-state">${escapeHtml(p.state || '')}</div>
        <div class="dot-permit-cost">${escapeHtml(p.costRange || 'TBD')}</div>
        <div class="dot-permit-type">${escapeHtml(p.type || '')}</div>
        ${p.notes ? `<div class="dot-permit-notes">${escapeHtml(p.notes)}</div>` : ''}
      </div>`;
    });
    html += '</div>';
    html += `<div class="dot-permit-disclaimer">
      <span class="material-icons">info</span>
      <span>Planning estimates only. Actual fees vary by exact load specs and change frequently. Always verify with the issuing state DOT before quoting customers.</span>
    </div>`;
  }

  // Footer disclaimer about manual verification
  html += `<div class="dot-warn-footer">
    <span class="material-icons">priority_high</span>
    <span>Manual review required: real-time construction, current permits, local ordinances, and weather conditions are NOT checked.</span>
  </div>`;

  html += '<button type="button" class="btn btn-secondary btn-small dot-warn-dismiss" onclick="this.closest(\'.dot-assistant-warnings\').remove()"><span class="material-icons">close</span> Dismiss</button>';

  container.innerHTML = html;

  // Insert after the AI prompt section
  const aiSection = document.getElementById('ai-prompt-section');
  if (aiSection) {
    aiSection.insertAdjacentElement('afterend', container);
  } else {
    // Fallback — insert at top of modal body
    const modalBody = document.querySelector('#modal-job .modal-body');
    if (modalBody) modalBody.prepend(container);
  }
}

async function fetchTruckRouteCheck(parsed, idToken) {
  try {
    // Get all waypoints from the job form
    const waypointInputs = Array.from(document.querySelectorAll('.waypoint-url'))
      .map(el => el.value.trim())
      .filter(v => v);

    let originAddr, destAddr;

    if (waypointInputs.length >= 2) {
      // Multi-waypoint job: first = origin, last = destination
      originAddr = waypointInputs[0];
      destAddr = waypointInputs[waypointInputs.length - 1];
    } else if (waypointInputs.length === 1) {
      // Single waypoint = destination, use parsed origin or yard as fallback
      destAddr = waypointInputs[0];
      const yards = window.AppState?.settings?.yards || [];
      originAddr = parsed.originLocation || yards[0]?.address;
    } else if (parsed.location) {
      // No waypoints in form yet, use parsed location as destination
      destAddr = parsed.location;
      const yards = window.AppState?.settings?.yards || [];
      originAddr = parsed.originLocation || yards[0]?.address;
    }

    if (!originAddr || !destAddr) return;

    // Get truck weight info from selected trucks
    const selectedTruckIds = parsed.trucks || [];
    if (!selectedTruckIds.length) return;

    const truck = DispatchState.trucks.find(t => t.id === selectedTruckIds[0]);
    if (!truck) return;

    const grossWeightLbs = truck.gcwr || truck.gvwr || 0;
    if (!grossWeightLbs) return; // No weight data, skip

    const grossWeightKg = Math.round(grossWeightLbs * 0.453592);

    const response = await fetch('/api/getTruckRoute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({
        origin: originAddr,
        destination: destAddr,
        vehicle: {
          grossWeight: grossWeightKg,
          hazmat: !!parsed.jobFlags?.hazmat,
          axleCount: truck.axleCount || 3,
          trailerCount: parsed.trailers?.length || 0
        }
      })
    });

    const result = await response.json();
    if (!result.ok && !result.restrictions?.length) return;

    // Build route restriction warnings
    const routeWarnings = [];

    if (result.totalMiles) {
      routeWarnings.push({
        severity: 'blue',
        icon: 'route',
        message: `Truck route: ${result.totalMiles} mi, ~${result.totalHours} hrs`
      });
    }

    // Add any restrictions HERE found
    if (result.restrictions?.length) {
      result.restrictions.forEach(r => {
        routeWarnings.push({
          severity: r.severity || 'yellow',
          icon: r.severity === 'red' ? 'block' : 'warning',
          message: r.message
        });
      });
    }

    if (result.routeWarnings?.length) {
      result.routeWarnings.forEach(w => {
        routeWarnings.push({
          severity: w.severity || 'yellow',
          icon: 'alt_route',
          message: w.title || w.type
        });
      });
    }

    if (!routeWarnings.length) return;

    // Append to existing warnings or create new panel
    const existing = document.getElementById('dot-assistant-warnings');
    const routeHtml = routeWarnings.map(renderWarnItem).join('');
    const sectionHtml = `<div class="dot-assistant-header" style="margin-top:12px">
      <span class="material-icons">local_shipping</span> Truck Route Check
      <span class="dot-source-label">HERE Maps Truck Routing</span>
    </div>${routeHtml}`;

    if (existing) {
      // Insert before the footer (or before dismiss button)
      const footer = existing.querySelector('.dot-warn-footer') || existing.querySelector('.dot-warn-dismiss');
      if (footer) footer.insertAdjacentHTML('beforebegin', sectionHtml);
    } else {
      showDotAssistantWarnings(routeWarnings, null);
    }
  } catch (err) {
    // Silently fail — truck route check is advisory, not blocking
    console.warn('[DOT Assistant] Truck route check failed:', err.message);
  }
}

function toggleAiPrompt() {
  // In demo mode, require email gate first (3 question limit)
  if (isDemoMode()) {
    showAIEmailGate?.(() => {
      showAiPromptSection();
    });
    return;
  }

  // Check if tenant has AI package
  if (!window.hasAiPack?.()) {
    window.showAiUpgradeModal?.('job-parser');
    return;
  }

  showAiPromptSection();
}

function showAiPromptSection() {
  const section = document.getElementById('ai-prompt-section');
  const isHidden = section.classList.contains('hidden');
  section.classList.toggle('hidden');

  if (isHidden) {
    document.getElementById('ai-prompt-input').focus();
  } else {
    document.getElementById('dot-question-redirect')?.remove();
    document.getElementById('job-builder-lookup-redirect')?.remove();
    clearJobBuilderProposal(true); // TASK-4803 S2 — Cancel on the builder drops its proposal
  }
}

// TASK-9163 — hand a question typed into the Job Builder to Ask DispatchDeck,
// which already answers it from the company's records (askCarrierAssistant).
// carrierAssistantSendMessage takes NO argument — it reads #dd-assistant-input
// off the DOM — so the order is: open the panel, set the input, send. The panel
// toggles, so only open it when it is closed. Returns false when the assistant
// is not on the page (no access), so the caller can say where to go instead.
function handOffToAskDispatchDeck(question) {
  const panel = document.getElementById('dd-assistant-panel');
  if (!panel || typeof window.__toggleCarrierAssistant !== 'function') return false;
  if (!panel.classList.contains('is-open')) window.__toggleCarrierAssistant();
  const input = document.getElementById('dd-assistant-input');
  if (!input) return false;
  input.value = question;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  // If an answer is still in flight the send no-ops and the question waits in
  // the box for one tap — never lost.
  if (typeof window.__sendCarrierAssistantMessage === 'function') window.__sendCarrierAssistantMessage();
  return true;
}

// TASK-9163 — the Job Builder's answer to a lookup question: say it is a
// question, not a job, and offer to ask it in Ask DispatchDeck in one tap. The
// question text never goes into innerHTML — the button reads it from this
// closure.
function showJobBuilderLookupRedirect(description, opts = {}) {
  window.showToast?.('That\'s a question, not a job — nothing was filled in.', 'info');
  document.getElementById('dot-question-redirect')?.remove();
  document.getElementById('job-builder-lookup-redirect')?.remove();
  const aiSection = document.getElementById('ai-prompt-section');
  if (!aiSection) return;
  const canAsk = !opts.dot && !!document.getElementById('dd-assistant-panel') && typeof window.__toggleCarrierAssistant === 'function';
  const where = opts.dot
    ? 'For DOT and permit rules, use the <a href="#/dot-advisor" onclick="document.getElementById(\'job-builder-lookup-redirect\')?.remove();window.DispatchModule.closeModal?.(\'job\')" style="color:#3B82F6;text-decoration:underline;font-weight:600;">DOT Advisor</a> (sidebar).'
    : (canAsk ? 'Ask DispatchDeck can answer this from your records.' : 'Ask it in Ask DispatchDeck instead.');
  const box = document.createElement('div');
  box.id = 'job-builder-lookup-redirect';
  box.style.cssText = 'background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 16px;margin-top:8px;display:flex;align-items:flex-start;gap:10px;font-size:0.9rem;color:var(--text-primary);';
  box.innerHTML = `<span class="material-icons" style="color:#3B82F6;font-size:20px;flex-shrink:0;margin-top:1px;">help_outline</span><div><strong>That's a question, not a job.</strong><br>The Job Builder fills in a job from a description. ${where}${canAsk ? '<div style="margin-top:8px;"><button type="button" class="btn btn-primary btn-sm" style="min-height:44px;" data-jb-ask-dd>Ask DispatchDeck</button></div>' : ''}<span style="display:block;margin-top:6px;color:#475569;font-size:0.85rem;">To build a job, describe it: "Send Kyle in truck 101 to Border Plains tomorrow 6am"</span></div>`;
  box.querySelector('[data-jb-ask-dd]')?.addEventListener('click', () => {
    if (handOffToAskDispatchDeck(description)) box.remove();
    else window.showToast?.('Ask DispatchDeck isn\'t available on this page.', 'info');
  });
  aiSection.insertAdjacentElement('afterend', box);
}

// TASK-962 (S3): amber "Date assumed — verify" flag on the start-date field.
// When the AI parse returns no date, the form silently keeps its default start
// date (TASK-9407 Dispatch Settings: today / tomorrow / next business day;
// absent = tomorrow, the TASK-273 default) with no tell — flag it so the
// dispatcher verifies. Cleared:
// (1) on manual date change (onJobStartDateChange), (2) when a parse DOES
// return a date, (3+4) reset on every modal open (showAddJob/editJob) so it
// never persists across reopens. The default itself is kept.
function setDateAssumedFlag(show) {
  const flag = document.getElementById('job-date-assumed-flag');
  if (flag) flag.style.display = show ? 'flex' : 'none';
  const dateInput = document.getElementById('job-start-date');
  if (dateInput) dateInput.style.borderColor = show ? '#F59E0B' : '';
}

// TASK-9112 — the pre-save half: say it BEFORE they press Save, so the green
// toast is a confirmation and not a surprise.
//
// 🔴 Sits next to setDateAssumedFlag on purpose. That function owns
// #job-date-assumed-flag AND the input's borderColor, and fires from
// onJobStartDateChange — the same event that brings this hint up. This one
// touches ONLY #job-date-offscreen-hint. Do not merge them, do not let either
// reach for the other's element or the shared border, or one will erase the
// other and it will look like the hint "sometimes doesn't work".
//
// Silent by design when getVisibleDateRange() returns null (Projects/Search, a
// job form opened from Billing, driver mode): no window means no claim.
function refreshJobDateOffscreenHint() {
  // TASK-9859 — a date change can put work past a linked 811 ticket's expiry.
  if (document.getElementById('job-permit-chips')?.childElementCount) { try { populatePermitDropdown(); } catch (_) { /* advisory only */ } }
  const hint = document.getElementById('job-date-offscreen-hint');
  const textEl = document.getElementById('job-date-offscreen-hint-text');
  if (!hint || !textEl) return;

  const startVal = document.getElementById('job-start-date')?.value || '';
  // Only honour the end date when the job is actually multi-day. When the toggle
  // is off, saveJob collapses end to start anyway — but the field can still be
  // holding a stale later value mid-interaction, and reading it then would
  // suppress the hint for a job that is single-day and wholly off-board.
  const multiDay = document.getElementById('job-multi-day-toggle')?.checked;
  const endVal = multiDay ? (document.getElementById('job-end-date')?.value || '') : startVal;

  const range = getVisibleDateRange();
  if (!isRangeWhollyOffBoard(startVal, endVal, range)) {
    hint.style.display = 'none';
    return;
  }

  const when = endVal && endVal !== startVal
    ? `${formatBoardDateShort(startVal)} – ${formatBoardDateShort(endVal)}`
    : formatBoardDateShort(startVal);
  const board = `${formatBoardDateShort(range.from)} – ${formatBoardDateShort(range.to)}`;
  textEl.textContent = `${when} is outside the dates on your board (${board}). It will save — you just won't see it here until you go there.`;
  hint.style.display = 'flex';
}

// TASK-4803 S2 — the roster the builder matches names against, sent with the
// first build AND every correction (the server rebuilds the prompt each turn
// and never stores the roster). Moved out of parseWithAi unchanged.
function _jobBuilderRosterPayload() {
  return {
    people: DispatchState.people.filter(p => p.showInDispatch !== false).map(p => ({
      id: p.id, name: p.name,
      cdlClass: p.cdlClass || null,
      // TASK-1324: canonical fields are cdlEndorsements/cdlRestrictions (what hr.js writes
      // + checkDOTCompliance reads); fall back to bare legacy names ONLY when the canonical
      // one is empty (length-aware — an empty [] is truthy, so `||` alone would swallow it).
      endorsements: (p.cdlEndorsements?.length ? p.cdlEndorsements : p.endorsements) || null,
      restrictions: (p.cdlRestrictions?.length ? p.cdlRestrictions : p.restrictions) || null,
      medCardExpiry: p.medCardExpiryDate || null,
      dlExpiry: (window.PersonIdentity ? window.PersonIdentity.value(p, 'dlExpiryDate') : p.dlExpiryDate) || null // TASK-9359 — vault-first
    })),
    trucks: DispatchState.trucks.filter(t => t.showInDispatch !== false).map(t => ({
      // TASK-1324: nickname holds the human name on most fleets (name is null); without it
      // the model never sees "Hooty" and reports real trucks as not-in-roster.
      id: t.id, name: t.name, nickname: t.nickname || null, unit: t.unit,
      gvwr: t.gvwr || null, gcwr: t.gcwr || null,
      type: t.type || null
    })),
    trailers: DispatchState.trailers.filter(t => t.showInDispatch !== false).map(t => ({
      id: t.id, name: t.name, nickname: t.nickname || null, unit: t.unit,
      gvwr: t.gvwr || null, type: t.type || null
    })),
    assets: DispatchState.assets.filter(a => a.showInDispatch !== false).map(a => ({ id: a.id, name: a.name, partNumber: a.partNumber, consumable: !!a.consumable }))
  };
}

// TASK-4803 S2 — `accepted` is the dispatcher pressing "Use this" on a
// proposal: { result, description } from the conversation, filled with the
// SAME code below as a one-shot parse (no second fill path). Without it this
// is a new build; with a conversation in the reply it shows the proposal card
// and stops — nothing reaches the form until "Use this".
async function parseWithAi(accepted) {
  const _jbAccepted = accepted && typeof accepted === 'object' && accepted.result && typeof accepted.description === 'string' ? accepted : null;
  const _jbGen = _jobFormGen; // TASK-4803 S2 (COD r1) — the form this build belongs to, captured BEFORE the request
  const promptInput = document.getElementById('ai-prompt-input');
  const description = _jbAccepted ? _jbAccepted.description : promptInput.value.trim();

  if (!description || description.length < 5) {
    window.showToast?.('Please enter a job description', 'warning');
    return;
  }
  // TASK-4803 S2 — a new build replaces any proposal on screen (and ends its
  // server-side conversation); "Use this" keeps its own until the fill is done.
  if (!_jbAccepted) clearJobBuilderProposal(true);

  // Guardrail: a question is not a job. TASK-9163 — the rule lives in
  // shared/job-builder-intent.js (the server runs the same one). 'dot' is the
  // pre-TASK-9163 DOT check, unchanged; 'lookup' is a question about the
  // company's own records ("do you have a waypoint for border plains nd?").
  document.getElementById('job-builder-history-note')?.remove(); // TASK-4803: a note describes ONE fill, never the next
  document.getElementById('job-builder-crew-suggest')?.remove(); // TASK-4803 S3a: same — suggestions belong to one fill
  document.getElementById('job-builder-customer-hints')?.remove(); // TASK-4803 S3b: same
  document.getElementById('job-builder-unsupported-note')?.remove(); // TASK-9585 S6b: amber flags belong to one fill
  document.querySelectorAll('[data-jb-unsupported]').forEach(el => { el.style.boxShadow = el.dataset.jbUnsupported === '1' ? '' : el.dataset.jbUnsupported; delete el.dataset.jbUnsupported; });
  const inputClass = classifyJobBuilderInput(description);
  if (inputClass.kind === 'lookup') {
    showJobBuilderLookupRedirect(description);
    return;
  }
  document.getElementById('job-builder-lookup-redirect')?.remove();
  if (inputClass.kind === 'dot') {
    window.showToast?.('That sounds like a DOT regulation question — use the DOT Advisor for Q&A. The Job Builder is for describing jobs to auto-fill the form.', 'info');
    // Show a more helpful in-UI message
    const aiSection = document.getElementById('ai-prompt-section');
    if (aiSection) {
      const existing = document.getElementById('dot-question-redirect');
      existing?.remove();
      const redirect = document.createElement('div');
      redirect.id = 'dot-question-redirect';
      redirect.style.cssText = 'background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 16px;margin-top:8px;display:flex;align-items:flex-start;gap:10px;font-size:0.9rem;';
      redirect.innerHTML = `<span class="material-icons" style="color:#3B82F6;font-size:20px;flex-shrink:0;margin-top:1px;">policy</span><div><strong>That's a DOT regulation question.</strong><br>The Job Builder fills forms from job descriptions — for DOT Q&amp;A, use the <a href="#/dot-advisor" onclick="document.getElementById('dot-question-redirect')?.remove();window.DispatchModule.closeModal?.('job')" style="color:#3B82F6;text-decoration:underline;font-weight:600;">DOT Advisor</a> (sidebar).<br><span style="color:#475569;font-size:0.85rem;">Example job description: "Send Kyle in truck 101 from Williston to Odessa TX tomorrow 6am"</span></div>`;
      aiSection.insertAdjacentElement('afterend', redirect);
    }
    const parseBtn = document.getElementById('btn-parse-ai');
    return;
  }

  const parseBtn = document.getElementById('btn-parse-ai');
  const originalText = parseBtn.innerHTML;
  parseBtn.innerHTML = '<span class="material-icons spinning">sync</span> Checking...';
  parseBtn.disabled = true;

  // Progress feedback — DOT check can take 5-10s with live restriction data
  const progressTimer = setTimeout(() => {
    if (parseBtn.disabled) parseBtn.innerHTML = '<span class="material-icons spinning">sync</span> Checking DOT restrictions...';
  }, 3000);

  try {
    const user = auth.currentUser;
    if (!user) {
      window.showToast?.('Authentication required for Smart Tools', 'error');
      return;
    }
    const idToken = await user.getIdToken();

    // TASK-4803 S2 — "Use this" on a proposal: the reply is already here.
    let result;
    if (_jbAccepted) {
      result = _jbAccepted.result;
    } else {
      const response = await fetch('/api/parseJobDescription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          description: description,
          // TASK-962 (S1): dispatcher-local "today" anchor. The server runs in UTC,
          // so its own new Date() flips to tomorrow after ~7PM CT and evening builds
          // anchored "tomorrow"/"Monday" to the wrong day. Server strictly validates
          // the format and derives the weekday itself — send the date only.
          todayLocal: formatDateISOLocal(new Date()),
          // TASK-4803 S2 — ask for a conversation. The server opens one only for a
          // Dispatch-module (or owner/admin) login; anyone else gets the one-shot fill.
          conversational: true,
          roster: _jobBuilderRosterPayload()
        })
      });

      result = await response.json();
    }

    // Check for AI subscription error
    if (result.code === 'NO_AI_SUBSCRIPTION') {
      window.showAiUpgradeModal?.('job-parser');
      return;
    }

    // TASK-514: Gemini quota exhaustion — friendly toast instead of the generic
    // "Failed to parse description" error.
    if (result.reason === 'AI_UNAVAILABLE') {
      window.showToast?.('AI is temporarily unavailable — try again shortly.', 'error');
      return;
    }

    if (result.ok && result.data) {
      const parsed = result.data;

      // TASK-9163 — question intent. The server already resolved `intent`
      // through the shared rule (a lookup/DOT question never reaches Gemini; the
      // model's own "question" is honoured only for question-shaped text that is
      // not a build request). Re-resolve with the SAME rule so a missing or
      // garbled intent cannot turn a question into a job. Deliberately NOT gated
      // on rosterPopulated like the guard below: that gate gave a brand-new
      // tenant zero question detection.
      if (resolveJobParseIntent(parsed.intent, description) === 'question') {
        // A regulation question the stem pre-check missed ("90,000 lbs in North
        // Dakota, is this legal?") belongs in the DOT Advisor, not Ask
        // DispatchDeck (COD Edge Case) — same vocabulary as the guard below.
        const dotFlavored = /\b(dot|fmcsa|cfr|cdl|endorsement|med card|medical card|hours of service|hos|dvir|drug test|clearinghouse|dq file|permit|overweight|hazmat|placard|regulation|compliance|inspection|audit|violation|exemption|waiver|legal|lbs|pounds)\b/i.test(description || '');
        showJobBuilderLookupRedirect(description, { dot: dotFlavored });
        return;
      }

      // Post-parse guard: does this look like a JOB at all, or a Q&A question
      // that slipped past the pre-check?
      // TASK-1335 — this used to test "did anything get assigned," which is the
      // wrong proxy: creating an UNASSIGNED job (customer + pickup + dropoff now,
      // driver and truck later) is a normal dispatcher workflow, and the guard's
      // `return` fires BEFORE the form fill below — so a perfectly good parse
      // (customer matched, origin parsed) was discarded. It also ignored
      // parsed.assets even though the parser handles them.
      // The bar is now ONE structured job signal. title/notes are deliberately
      // EXCLUDED: the server prompt always asks for them, so an LLM emits them
      // for literally any input including a DOT question — counting them would
      // gut the Q&A backstop. A regulation question yields no customer, no
      // location, no date, no well, no PO, so it still redirects.
      const hasAssigned = (parsed.people?.length > 0) || (parsed.trucks?.length > 0)
        || (parsed.trailers?.length > 0) || (parsed.assets?.length > 0);
      // A bare `location` is the WEAK signal: the server prompt carries a
      // state-by-state permit/weight knowledge base, so a compliance question
      // that slips the pre-check ("90,000 lbs load in North Dakota, is this
      // legal") can plausibly come back with location:"North Dakota" and
      // nothing else — and the old guard correctly redirected that. So a
      // location-only parse counts as a job UNLESS the text reads like a
      // compliance question, in which case we still redirect (COD Domain +
      // Code Reviewer both landed on this same case).
      const hasStrongJobSignal = !!(parsed.customerName || parsed.date || parsed.wellName
        || parsed.poNumber || parsed.originLocation || parsed.stops?.length); // TASK-4803: a server-verified past stop is a job signal
      const _complianceFlavored = /\b(dot|fmcsa|cfr|cdl|endorsement|med card|medical card|hours of service|hos|dvir|drug test|clearinghouse|dq file|permit|overweight|hazmat|placard|regulation|compliance|inspection|audit|violation|exemption|waiver|legal|lbs|pounds)\b/i.test(description || '');
      const hasJobSignal = hasStrongJobSignal || (!!parsed.location && !_complianceFlavored);
      const rosterPopulated = DispatchState.people.length > 0 || DispatchState.trucks.length > 0;
      // TASK-4803 S2 — the server opened a conversation (Dispatch login, new
      // build). A parse with no job signal but a question for the dispatcher
      // ("which yard did you mean?") is exactly what the conversation is for:
      // it goes to the proposal card, where they can answer it.
      const _jbConv = !_jbAccepted && result.conversation && typeof result.conversation.sessionId === 'string' ? result.conversation : null;
      if (!hasJobSignal && !hasAssigned && rosterPopulated && !(_jbConv && parsed.followUp)) {
        window.showToast?.('That didn\'t parse as a job — no customer, location, or date found. Try the DOT Advisor for questions.', 'info');
        const aiSection = document.getElementById('ai-prompt-section');
        if (aiSection) {
          document.getElementById('dot-question-redirect')?.remove();
          const redirect = document.createElement('div');
          redirect.id = 'dot-question-redirect';
          redirect.style.cssText = 'background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 16px;margin-top:8px;display:flex;align-items:flex-start;gap:10px;font-size:0.9rem;';
          redirect.innerHTML = `<span class="material-icons" style="color:#3B82F6;font-size:20px;flex-shrink:0;margin-top:1px;">policy</span><div><strong>This doesn't look like a job description.</strong><br>No customer, location, or date could be found. For DOT/permit questions, use the <a href="#/dot-advisor" onclick="document.getElementById('dot-question-redirect')?.remove();window.DispatchModule.closeModal?.('job')" style="color:#3B82F6;text-decoration:underline;font-weight:600;">DOT Advisor</a> in the sidebar.<br><span style="color:#475569;font-size:0.85rem;">Example: "Send Kyle in truck 101 from Williston to Odessa TX tomorrow 6am"</span></div>`;
          aiSection.insertAdjacentElement('afterend', redirect);
        }
        // TASK-4803 — "which yard did you mean?" is exactly the parse that has
        // no job signal yet; the builder's question must not vanish with it.
        showJobBuilderHistoryNote(parsed);
        return;
      }

      // TASK-4803 S2 — PROPOSE → APPROVE. With a conversation, the parse is a
      // proposal: shown on a card the dispatcher can correct, and filled by
      // this same code only when they press "Use this" (parseWithAi re-entered
      // with the accepted reply). Nothing below runs, and nothing is saved.
      if (_jbConv) {
        showJobBuilderProposal(result, description, _jbGen);
        return;
      }

      // Populate form fields with parsed data
      if (parsed.title) document.getElementById('job-title').value = parsed.title;
      // TASK-1337 Layer 1 — the AI path created at most ONE waypoint (the
      // destination), so "from A to B" silently dropped the pickup: the schema
      // returns originLocation but nothing ever turned it into a row. It was
      // consumed transiently by the route check and then discarded, never
      // persisting to job.waypoints. Origin is appended FIRST because
      // fetchTruckRouteCheck reads first=origin/last=destination from the DOM
      // (it runs after this fill), and the saved array is the driver's route
      // order. With two rows it now takes the >=2 branch and uses the REAL
      // origin instead of falling back to the first yard's address.
      // Naming them also fixes the blank "Location name" box — this was the
      // only addWaypoint caller passing ''.
      // TASK-4803 — the rows now come from planJobBuilderWaypoints: stops the
      // builder found in past jobs (each coordinate verified server-side
      // against what the history tool returned) fill their leg, and the typed
      // origin/location only fill a leg history did not. With no history
      // stops this is the TASK-1337 order exactly: Pickup, then Drop off / Job site.
      const _jbPlan = planJobBuilderWaypoints(parsed);
      if (_jbPlan.length) {
        // Drop any blank row already sitting in the list (the modal can seed
        // one) so the parse doesn't leave a stray empty waypoint above the
        // filled ones.
        document.querySelectorAll('#job-waypoints-list .waypoint-row').forEach(row => {
          const n = row.querySelector('.waypoint-name')?.value?.trim();
          const u = row.querySelector('.waypoint-url')?.value?.trim();
          if (!n && !u) row.remove();
        });
        // addWaypoint lights the saved-location bookmark on a case-insensitive
        // NAME match, so a tenant with a saved location literally called
        // "Pickup" would get a false bookmark on an unsaved row. Fall back to a
        // blank name in that case (COD Blast + Independent).
        const _wpName = (label) => (DispatchState.savedLocations || [])
          .some(l => l.name?.toLowerCase() === String(label).toLowerCase()) ? '' : label;
        // COD (Code Reviewer) — re-running the builder (e.g. after answering
        // its "which yard?") must REPLACE its own previous rows, not stack a
        // second route under them. Only rows the builder added AND the
        // dispatcher has not touched since are removed; a hand-edited or
        // hand-added row is theirs and stays.
        document.querySelectorAll('#job-waypoints-list .waypoint-row[data-jb-fill]').forEach(row => {
          const n = row.querySelector('.waypoint-name')?.value ?? '';
          const u = row.querySelector('.waypoint-url')?.value ?? '';
          if (row.dataset.jbFill === JSON.stringify([n, u])) row.remove();
        });
        _jbPlan.forEach(w => {
          addWaypoint(w.generic ? _wpName(w.name) : w.name, w.url, '', w.lat, w.lng);
          const row = document.querySelector('#job-waypoints-list .waypoint-row:last-child');
          if (row) {
            row.dataset.jbFill = JSON.stringify([row.querySelector('.waypoint-name')?.value ?? '', row.querySelector('.waypoint-url')?.value ?? '']);
          }
        });
      }
      showJobBuilderHistoryNote(parsed); // TASK-4803
      if (parsed.wellName || parsed.wellNumber) document.getElementById('job-well-name').value = [parsed.wellName, parsed.wellNumber].filter(Boolean).join(' ');
      // TASK-4782 — a PO parsed out of a document by the AI is the LEAST
      // trustworthy way one can reach this field, so it gets the same check as a
      // typed one rather than less.
      if (parsed.poNumber) { document.getElementById('job-po').value = parsed.poNumber; delete document.getElementById('job-po').dataset.autofilledPo; onJobPoInput(); }
      if (parsed.notes) { document.getElementById('job-notes').value = parsed.notes; _syncJobNotesHeight(); } // TASK-4808

      // Set date if provided
      if (parsed.date) {
        document.getElementById('job-start-date').value = parsed.date;
        document.getElementById('job-end-date').value = parsed.date;
        document.getElementById('job-end-date').min = parsed.date;
        document.getElementById('job-start-date').max = '';
        setDateAssumedFlag(false); // TASK-962: AI supplied a date — no assumption
      } else {
        // TASK-962 (S3): no date in the parse — the form is silently keeping its
        // default start date (TASK-9407 setting). Flag it so the dispatcher
        // verifies before saving.
        setDateAssumedFlag(true);
      }
      // TASK-9112 — both branches. "Book the Hess load for the 24th" is exactly
      // the sentence that lands an AI-parsed job off the visible board, and the
      // assumed-tomorrow branch can be off-board too when the dispatcher is
      // looking at a different month.
      refreshJobDateOffscreenHint();

      // Set time if provided
      if (parsed.time) {
        document.getElementById('job-all-day').checked = false;
        document.getElementById('time-fields').style.display = '';
        document.getElementById('job-start-time').value = parsed.time;
      }

      // Try to match customer name
      // P0 2026-10-06 — customers only: a QBO vendor whose name contains the
      // parsed text ("Loenbro" vs a vendor "Loenbro Supply") must never win.
      let _jbCustomerId = ''; // TASK-4803 S3b — the customer THIS fill resolved
      if (parsed.customerName) {
        // TASK-4579 (COD Domain/Adversary) — an exact name wins before the
        // two-way substring match, so "ABC Paving" never loses to "ABC" just
        // because "ABC" sorts first. The substring match stays the fallback.
        const _jbPool = window.BillableCustomer.billableCustomers(DispatchState.customers || []);
        const _jbWant = parsed.customerName.toLowerCase().trim();
        const matchedCustomer = _jbPool.find(c => c.name && c.name.toLowerCase().trim() === _jbWant)
          || _jbPool.find(c => c.name && (
            c.name.toLowerCase().includes(parsed.customerName.toLowerCase()) ||
            parsed.customerName.toLowerCase().includes(c.name.toLowerCase())
          ));
        if (matchedCustomer) {
          document.getElementById('job-customer').value = matchedCustomer.id;
          // TASK-4579 — setting .value fires no change event, so the AI path
          // skipped everything a human pick does: POC list, price book + rate
          // pickers, standing PO, no-price-book warning, Project re-filter
          // (TASK-9606 did that one by hand here) and the breadcrumb. Run the
          // same handler, marked as the machine's pick, then flag the field.
          // A PO the description gave was set above and survives: the standing
          // PO only fills a blank box.
          onCustomerChange('ai_parse');
          _setJobCustomerAiCue(matchedCustomer.name);
          _jbCustomerId = matchedCustomer.id;
        }
      }

      // TASK-4660 — Smart Job Builder sets #job-title (and the well, and the
      // customer) with .value, which fires no focusout, so an AI-built job was
      // the ONE create path that skipped the inline duplicate warning entirely.
      // Runs here, after title + well + customer are all seeded, so the check
      // sees the finished form. Soft warning only; the save-time gate is
      // untouched and still has the last word.
      checkJobDuplicate();

      // Select crew/equipment/assets from AI response
      const selectAndRender = (containerId, sectionKey, ids) => {
        if (!ids?.length) return;
        const container = document.getElementById(containerId);
        if (!container) return;
        ids.forEach(id => {
          const item = container.querySelector(`input[value="${id}"]`);
          if (item && !item.checked) {
            item.checked = true;
            item.closest('.checkbox-item')?.classList.add('checked');
          }
        });
        // Update count
        const countEl = document.getElementById(`job-count-${sectionKey}`);
        const checked = container.querySelectorAll('input:checked').length;
        if (countEl) { countEl.textContent = checked; countEl.style.display = checked > 0 ? '' : 'none'; }
        // Open section if items were selected
        if (checked > 0) {
          const section = document.getElementById(`job-section-${sectionKey}`);
          if (section) section.classList.add('open');
        }
      };

      selectAndRender('job-select-drivers', 'drivers', parsed.people);
      selectAndRender('job-select-trucks', 'trucks', parsed.trucks);
      selectAndRender('job-select-trailers', 'trailers', parsed.trailers);
      const assetIds = parsed.assets?.map(a => typeof a === 'string' ? a : a.id) || [];
      selectAndRender('job-select-assets', 'assets', assetIds);
      // TASK-4803 S3a — AFTER the fill, so a person or unit the description
      // already put on the job is not offered again.
      showJobBuilderCrewSuggestions(parsed);
      // TASK-4803 S3b — usual PO / well / AFE, a likely repeat job, a PO in the
      // title. Async and best-effort: the fill above is already complete.
      showJobBuilderCustomerHints(_jbCustomerId, _jbPlan, parsed);
      // TASK-9585 S6b — fields the description does not support, amber. A
      // flag, not a block: every value above is still filled and Create works.
      showJobBuilderUnsupportedNote(parsed);

      // Set consumable quantities
      const qtyMap = parsed.assetQtys || {};
      // Also extract from parsed.assets array
      if (parsed.assets?.length) {
        parsed.assets.forEach(a => {
          if (typeof a === 'object' && a.id && a.qty > 1) qtyMap[a.id] = a.qty;
        });
      }
      Object.entries(qtyMap).forEach(([assetId, qty]) => {
        if (qty > 0) {
          DispatchState.pendingAssetQtys[assetId] = qty;
          const input = document.querySelector(`.asset-dispatch-qty[data-asset-id="${assetId}"]`);
          if (input) input.value = qty;
        }
      });

      // Set job flags if detected and trigger existing compliance matrix
      if (parsed.jobFlags) {
        if (parsed.jobFlags.hazmat) { const el = document.getElementById('job-hazmat'); if (el) el.checked = true; }
        if (parsed.jobFlags.liquidTank) { const el = document.getElementById('job-liquid-tank'); if (el) el.checked = true; }
        if (parsed.jobFlags.interstate) { const el = document.getElementById('job-interstate'); if (el) el.checked = true; }
        // Trigger the built-in compliance matrix check too
        if (typeof runJobComplianceCheck === 'function') runJobComplianceCheck();
      }

      // Show compliance warnings from DOT Assistant
      if (parsed.warnings?.length || parsed.permitEstimates?.length) {
        showDotAssistantWarnings(parsed.warnings || [], parsed.permitNotes, parsed.permitEstimates);
      }

      // Fire truck route check in background if we have a destination + truck
      fetchTruckRouteCheck(parsed, idToken);

      // TASK-4688 — the parse may have filled Vendor/AFE, a DOT flag or a
      // waypoint inside a section this tenant hides. Show what it wrote.
      revealPopulatedJobFormSections();

      // TASK-4803 S2 — accepted and filled: the conversation is over.
      if (_jbAccepted) clearJobBuilderProposal(true);
      // Hide AI section and show success
      toggleAiPrompt();
      promptInput.value = '';
      window.showToast?.(parsed.warnings?.length ? 'Job filled — check compliance warnings below' : 'Job details populated!', parsed.warnings?.length ? 'warning' : 'success');
    } else {
      throw new Error('Failed to parse description');
    }
  } catch (error) {
    console.error('[Dispatch] AI parse error:', error);
    window.showToast?.('Failed to parse description. Please fill in manually.', 'error');
  } finally {
    clearTimeout(progressTimer);
    parseBtn.innerHTML = originalText;
    parseBtn.disabled = false;
  }
}

// =============================================================================
// TASK-4803 S2 — the Smart Job Builder CONVERSATION (client side).
// The builder's first reply is a PROPOSAL card under the description box. The
// dispatcher can press "Use this" (parseWithAi re-entered with that reply →
// the ordinary form fill), type a correction and press "Revise" (the server
// replays the conversation it holds and returns a COMPLETE replacement
// proposal — never a second one stacked on the first), or "Start over".
// Nothing is filled or saved until "Use this"; the job is still only written
// when they press Create. The card belongs to ONE open form (_jobFormGen).
// The server holds the conversation and its caps (services/jobBuilderSession.js);
// this side only shows what is left.
// =============================================================================
let _jbProposal = null; // { sessionId, result, description, gen, busy }
// The card's markup is a LAZY module (dispatch.js is its only importer): it
// is only needed once a Dispatch login builds a job. A failed load is retried.
let _jbConversationModP = null;
function _jbConversationModule() {
  if (!_jbConversationModP) {
    _jbConversationModP = __ddChunkWrap(import('../utils/jobBuilderConversation.js?v=1')).catch((err) => { _jbConversationModP = null; throw err; });
  }
  return _jbConversationModP;
}

function _jbProposalResolve(kind, id) {
  const pools = { people: DispatchState.people, trucks: DispatchState.trucks, trailers: DispatchState.trailers, assets: DispatchState.assets };
  const item = (pools[kind] || []).find(x => x && x.id === id);
  if (!item) return null;
  if (kind === 'people' || kind === 'assets') return item.name || null;
  return (window.getUnitDisplay ? window.getUnitDisplay(item) : null) || item.unit || item.nickname || item.name || null;
}

async function _jbEndConversation(sessionId) {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const idToken = await user.getIdToken();
    await fetch('/api/parseJobDescription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({ sessionId, endSession: true })
    });
  } catch (_) { /* best-effort: the server expires an idle conversation on its own */ }
}

function clearJobBuilderProposal(endConversation) {
  const prev = _jbProposal;
  _jbProposal = null;
  document.getElementById('job-builder-proposal')?.remove();
  if (endConversation && prev && prev.sessionId) _jbEndConversation(prev.sessionId);
}

function _jbProposalError(msg) {
  const el = document.querySelector('#job-builder-proposal .jb-proposal-error');
  if (!el) return;
  el.textContent = msg || '';
  el.style.display = msg ? '' : 'none';
}

async function showJobBuilderProposal(result, description, formGen) {
  const section = document.getElementById('ai-prompt-section');
  const conv = result && result.conversation;
  if (!section || !conv || typeof conv.sessionId !== 'string') return;
  const gen = typeof formGen === 'number' ? formGen : _jobFormGen;
  if (gen !== _jobFormGen) { _jbEndConversation(conv.sessionId); return; } // the form closed while the builder was thinking
  let mod;
  try {
    mod = await _jbConversationModule();
  } catch (err) {
    // The card could not load (offline / stale chunk). Nothing is filled
    // without "Use this" (COD r2): end the conversation and say so; pressing
    // Dispatch again retries the load.
    console.error('[Dispatch] Job Builder card failed to load:', err);
    _jbEndConversation(conv.sessionId);
    if (gen === _jobFormGen) window.showToast?.('Could not show the proposal. Check your connection and press Dispatch again.', 'error');
    return;
  }
  if (gen !== _jobFormGen) { _jbEndConversation(conv.sessionId); return; }
  const prevSession = _jbProposal ? _jbProposal.sessionId : null;
  _jbProposal = { sessionId: conv.sessionId, result, description, gen: _jobFormGen, busy: false };
  if (prevSession && prevSession !== conv.sessionId) _jbEndConversation(prevSession);
  let box = document.getElementById('job-builder-proposal');
  if (!box) {
    box = document.createElement('div');
    box.id = 'job-builder-proposal';
    box.setAttribute('role', 'region');
    box.setAttribute('aria-label', 'Proposed job — not in the form yet');
    box.style.cssText = 'background:var(--bg-primary, #fff);border:1px solid #BFDBFE;border-left:4px solid #3B82F6;border-radius:8px;padding:12px 16px;margin-top:10px;font-size:0.9rem;color:var(--text-primary);';
    box.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-jb-act]');
      if (!btn || btn.disabled) return;
      const act = btn.dataset.jbAct;
      if (act === 'accept') acceptJobBuilderProposal();
      else if (act === 'revise') reviseJobBuilderProposal();
      else if (act === 'restart') {
        clearJobBuilderProposal(true);
        document.getElementById('ai-prompt-input')?.focus();
      }
    });
    box.addEventListener('keydown', (e) => {
      if (e.target && e.target.id === 'jb-correction-input' && e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        reviseJobBuilderProposal();
      }
    });
    section.appendChild(box);
  }
  box.innerHTML = mod.jobBuilderProposalCardHtml(result.data, conv, _jbProposalResolve);
  // COD r1 UX — bring the card into view (phones) and put focus on "Use this", not the
  // correction box: no keyboard pops up unless the dispatcher wants to correct.
  try { box.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
  box.querySelector('[data-jb-act="accept"]')?.focus();
}

function _jbProposalStale(p) {
  if (p.gen === _jobFormGen) return false;
  clearJobBuilderProposal(true);
  window.showToast?.('That proposal was for a form you closed. Describe the job again.', 'info');
  return true;
}

async function acceptJobBuilderProposal() {
  const p = _jbProposal;
  if (!p || p.busy || _jbProposalStale(p)) return;
  p.busy = true;
  try {
    await parseWithAi({ result: p.result, description: p.description });
  } finally {
    p.busy = false;
  }
}

async function reviseJobBuilderProposal() {
  const p = _jbProposal;
  if (!p || p.busy || _jbProposalStale(p)) return;
  const input = document.getElementById('jb-correction-input');
  const correction = (input?.value || '').trim();
  if (correction.length < 2) { _jbProposalError('Type what to change.'); return; }
  p.busy = true;
  _jbProposalError('');
  const box = document.getElementById('job-builder-proposal');
  const reviseBtn = box?.querySelector('[data-jb-act="revise"]');
  const btnHtml = reviseBtn ? reviseBtn.innerHTML : '';
  box?.querySelectorAll('[data-jb-act]').forEach(b => { b.disabled = true; });
  if (reviseBtn) reviseBtn.innerHTML = '<span class="material-icons spinning">sync</span> Revising...';
  try {
    const user = auth.currentUser;
    if (!user) { _jbProposalError('Authentication required for Smart Tools'); return; }
    const idToken = await user.getIdToken();
    const resp = await fetch('/api/parseJobDescription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
      body: JSON.stringify({
        sessionId: p.sessionId,
        correction,
        todayLocal: formatDateISOLocal(new Date()), // TASK-962 anchor, every turn
        roster: _jobBuilderRosterPayload()
      })
    });
    const res = await resp.json();
    if (_jbProposal !== p) return; // closed, restarted or replaced meanwhile
    if (res.code === 'NO_AI_SUBSCRIPTION') { window.showAiUpgradeModal?.('job-parser'); return; }
    if (res.reason === 'AI_UNAVAILABLE') { _jbProposalError('AI is temporarily unavailable — try again shortly.'); return; }
    if (res.ok && res.data) {
      // The revision REPLACES the proposal. A revision the server could not
      // keep (no conversation in the reply) is still shown, with no more
      // corrections: "Use this" or start over.
      const conv = res.conversation && typeof res.conversation.sessionId === 'string'
        ? res.conversation : { sessionId: p.sessionId, turn: 2, correctionsLeft: 0 };
      await showJobBuilderProposal({ ...res, conversation: conv }, p.description, p.gen);
      return;
    }
    if (res.code === 'JOB_BUILDER_CHAT_LIMIT' || res.code === 'JOB_BUILDER_CHAT_ENDED' || res.code === 'JOB_BUILDER_CHAT_NOT_ALLOWED') {
      // No more corrections on this one — keep the last proposal usable.
      await showJobBuilderProposal({ ...p.result, conversation: { ...p.result.conversation, correctionsLeft: 0 } }, p.description, p.gen);
    }
    _jbProposalError(res.code === 'JOB_BUILDER_CHAT_RATE' && typeof res.error === 'string' ? res.error : (await _jbConversationModule()).jobBuilderChatErrorText(res.code));
  } catch (err) {
    console.error('[Dispatch] Job Builder revise error:', err);
    if (_jbProposal === p) _jbProposalError(await _jbConversationModule().then(m => m.jobBuilderChatErrorText(null), () => 'Could not revise it just now.'));
  } finally {
    p.busy = false;
    if (_jbProposal === p) {
      const b2 = document.getElementById('job-builder-proposal');
      b2?.querySelectorAll('[data-jb-act]').forEach(b => { b.disabled = false; });
      const rb = b2?.querySelector('[data-jb-act="revise"]');
      if (rb && btnHtml) rb.innerHTML = btnHtml;
    }
  }
}

// TASK-9585 S6b — the server checks every field the builder proposes against
// the dispatcher's own description (proposalGuard.flagJobBuilderFields) and
// returns the unsupported ones in `parsed.unsupported`. They are FILLED like
// any other field; this only paints them amber with a short hint so they are
// checked before Create. Every string is escaped (values are model output).
const JOB_BUILDER_UNSUPPORTED_LABELS = {
  customerName: 'Customer', location: 'Location', originLocation: 'Pickup',
  date: 'Date', time: 'Start time', poNumber: 'PO', wellName: 'Well',
  wellNumber: 'Well number', notes: 'Notes', people: 'Crew', trucks: 'Truck',
  trailers: 'Trailer', assets: 'Equipment',
};
const JOB_BUILDER_UNSUPPORTED_TARGETS = {
  customerName: 'job-customer', location: 'job-waypoints-list', originLocation: 'job-waypoints-list',
  date: 'job-start-date', time: 'job-start-time', poNumber: 'job-po', wellName: 'job-well-name',
  wellNumber: 'job-well-name', notes: 'job-notes', people: 'job-section-drivers',
  trucks: 'job-section-trucks', trailers: 'job-section-trailers', assets: 'job-section-assets',
};
function jobBuilderUnsupportedHtml(unsupported) {
  const rows = (Array.isArray(unsupported) ? unsupported : [])
    .filter(u => u && typeof u.field === 'string' && JOB_BUILDER_UNSUPPORTED_LABELS[u.field]);
  if (!rows.length) return '';
  const hint = (reason) => reason === 'instruction_text' ? 'only in text that reads like an instruction — check it'
    : reason === 'not_in_roster' ? 'not on your roster — check it'
    : reason === 'ambiguous' ? 'more than one on your roster matches the text — check it'
    : 'not in the text — check it';
  const items = rows.map(u => `<li><strong>${escapeHtml(JOB_BUILDER_UNSUPPORTED_LABELS[u.field])}:</strong> ${escapeHtml(u.reason === 'not_in_roster' ? 'unknown' : String(u.value ?? '').slice(0, 120))} <span style="color:#92400E;">— ${escapeHtml(hint(u.reason))}</span></li>`).join('');
  return `<div style="display:flex;align-items:flex-start;gap:10px;"><span class="material-icons" style="color:#D97706;font-size:20px;flex-shrink:0;margin-top:1px;">error_outline</span><div><strong>Check these before you save.</strong><ul style="margin:4px 0 0;padding-left:18px;">${items}</ul></div></div>`;
}
let _jbUnsupportedObserver = null;
function showJobBuilderUnsupportedNote(parsed) {
  document.getElementById('job-builder-unsupported-note')?.remove();
  document.querySelectorAll('[data-jb-unsupported]').forEach(el => {
    el.style.boxShadow = el.dataset.jbUnsupported === '1' ? '' : el.dataset.jbUnsupported;
    delete el.dataset.jbUnsupported;
  });
  const unsupported = parsed && Array.isArray(parsed.unsupported) ? parsed.unsupported : [];
  const html = jobBuilderUnsupportedHtml(unsupported);
  if (!html) return;
  const anchor = document.getElementById('job-builder-history-note') || document.getElementById('ai-prompt-section');
  if (!anchor) return;
  const box = document.createElement('div');
  box.id = 'job-builder-unsupported-note';
  box.setAttribute('role', 'status');
  box.style.cssText = 'background:#FFFBEB;border:1px solid #FCD34D;border-left:4px solid #F59E0B;border-radius:8px;padding:12px 16px;margin-top:8px;font-size:0.9rem;color:#1F2937;';
  box.innerHTML = html;
  anchor.insertAdjacentElement('afterend', box);
  // Amber ring on each flagged field; editing the field clears its ring.
  unsupported.forEach(u => {
    const el = document.getElementById(JOB_BUILDER_UNSUPPORTED_TARGETS[u?.field] || '');
    if (!el || el.dataset.jbUnsupported) return;
    el.dataset.jbUnsupported = el.style.boxShadow || '1';
    el.style.boxShadow = '0 0 0 2px #F59E0B';
    const clear = () => {
      if (!el.dataset.jbUnsupported) return;
      el.style.boxShadow = el.dataset.jbUnsupported === '1' ? '' : el.dataset.jbUnsupported;
      delete el.dataset.jbUnsupported;
    };
    el.addEventListener('input', clear, { once: true });
    el.addEventListener('change', clear, { once: true });
  });
  // The note describes this fill only: gone when the job form closes. ONE
  // observer at a time — a re-parse replaces the previous one (COD r1 Blast).
  _jbUnsupportedObserver?.disconnect();
  _jbUnsupportedObserver = null;
  const modal = document.getElementById('modal-job');
  if (modal && typeof MutationObserver === 'function') {
    const obs = new MutationObserver(() => {
      if (!modal.classList.contains('hidden')) return;
      obs.disconnect();
      if (_jbUnsupportedObserver === obs) _jbUnsupportedObserver = null;
      if (document.getElementById('job-builder-unsupported-note') === box) showJobBuilderUnsupportedNote(null);
    });
    obs.observe(modal, { attributes: true, attributeFilter: ['class'] });
    _jbUnsupportedObserver = obs;
  }
}

// TASK-4803 — say where the builder's stops came from (and what it could not
// fill) right under the builder, so a history-filled stop is never mistaken for
// one the dispatcher typed. Markup is built by utils/jobBuilderHistory.js with
// every model/tool string escaped. Removed at the next parse and on close.
function showJobBuilderHistoryNote(parsed) {
  document.getElementById('job-builder-history-note')?.remove();
  const html = jobBuilderHistoryNoteHtml(parsed);
  if (!html) return;
  const aiSection = document.getElementById('ai-prompt-section');
  if (!aiSection) return;
  const box = document.createElement('div');
  box.id = 'job-builder-history-note';
  box.setAttribute('role', 'status');
  box.style.cssText = 'background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 16px;margin-top:8px;font-size:0.9rem;color:var(--text-primary);';
  box.innerHTML = html;
  aiSection.insertAdjacentElement('afterend', box);
}

// TASK-4803 S3a — "usually on jobs like this": the people, vehicles and
// trailers that ran this customer's matching past jobs (server tool
// findPastJobCrew), offered as tap-to-add buttons. PROPOSE → APPROVE: nothing
// is ticked until the dispatcher taps, and the job is still only written when
// they press Create. A tap goes through toggleJobCheckbox — the same path as a
// hand tick — so compliance, counts and line-item pickers all run as usual.
// Never shows availability: two jobs in a day is normal.
function _jbSuggestRow(section, id) {
  const container = document.getElementById(`job-select-${section}`);
  if (!container) return null;
  const input = Array.from(container.querySelectorAll('input[type="checkbox"]')).find(i => i.value === id);
  return input ? { input, row: input.closest('.checkbox-item') } : null;
}

function showJobBuilderCrewSuggestions(parsed) {
  document.getElementById('job-builder-crew-suggest')?.remove();
  const pools = { drivers: DispatchState.people, trucks: DispatchState.trucks, trailers: DispatchState.trailers };
  const html = jobBuilderCrewSuggestionsHtml(parsed, (section, id) => {
    const hit = _jbSuggestRow(section, id);
    if (!hit || !hit.row || hit.input.checked) return null;
    const item = (pools[section] || []).find(x => x.id === id);
    if (!item) return null;
    const label = window.getUnitDisplay ? (window.getUnitDisplay(item) || item.name) : (item.name || item.unit);
    return label ? { label } : null;
  });
  if (!html) return;
  const anchor = document.getElementById('job-builder-history-note') || document.getElementById('ai-prompt-section');
  if (!anchor) return;
  const box = document.createElement('div');
  box.id = 'job-builder-crew-suggest';
  box.setAttribute('role', 'region');
  box.setAttribute('aria-label', 'Suggested crew and equipment from past jobs');
  box.style.cssText = 'background:#F8FAFC;border:1px solid #CBD5E1;border-radius:8px;padding:12px 16px;margin-top:8px;font-size:0.9rem;color:var(--text-primary);';
  box.innerHTML = html;
  box.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-jb-suggest]');
    if (!btn || btn.disabled) return;
    const section = btn.dataset.jbSuggest;
    const hit = _jbSuggestRow(section, btn.dataset.jbId);
    if (!hit || !hit.row) {
      window.showToast?.('That one is no longer in the list — pick it by hand.', 'info');
      return;
    }
    if (!hit.input.checked) toggleJobCheckbox(hit.row, section);
    document.getElementById(`job-section-${section}`)?.classList.add('open');
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">check</span> Added';
  });
  anchor.insertAdjacentElement('afterend', box);
}

// TASK-4803 S3b — the resolved customer's usual PO / well / AFE, a job that
// looks like this one (same customer + same stops, recently or already on the
// board), and a PO-shaped code in the title while the PO box is blank. The
// server (jobBuilderCustomerHints callable — tenant from the auth claim,
// Dispatch module) reads the tenant's own jobs; no model sees any of it. The
// markup module is LAZY (utils/jobBuilderCustomerHints.js, only importer).
// PROPOSE → APPROVE: every item is a tap; nothing is filled until then, and
// "Copy from that job" is the existing duplicateJob path after a confirm.
// Best-effort: any failure shows nothing — the fill is already complete.
let _jbHintsSeq = 0; // COD r1 — two fills on one form: only the LATEST reply may render
async function showJobBuilderCustomerHints(customerId, plan, parsed) {
  document.getElementById('job-builder-customer-hints')?.remove();
  const gen = _jobFormGen;
  const seq = ++_jbHintsSeq;
  const val = (id) => document.getElementById(id)?.value?.trim() || '';
  let result = null;
  let mod;
  try {
    const modP = __ddChunkWrap(import('../utils/jobBuilderCustomerHints.js?v=10002950'));
    if (customerId) {
      const stops = (Array.isArray(plan) ? plan : []).map(w => ({
        name: w && !w.generic ? String(w.name || '') : '',
        lat: w && Number.isFinite(w.lat) ? w.lat : null,
        lng: w && Number.isFinite(w.lng) ? w.lng : null,
      }));
      const [m, res] = await Promise.all([modP, httpsCallable(functions, 'jobBuilderCustomerHints')({
        customerId,
        jobDate: val('job-start-date'),
        title: val('job-title'),
        poBlank: !val('job-po'),
        excludeJobId: val('job-id'), // never offer the job being edited as its own repeat
        stops,
      })]);
      mod = m;
      result = res && res.data;
    } else if (parsed && parsed.poInTitle) {
      // No customer resolved: only the server's labelled-PO check from the parse.
      mod = await modP;
      result = { poInTitle: parsed.poInTitle };
    } else {
      return;
    }
  } catch (err) {
    console.warn('[Dispatch] TASK-4803 S3b customer hints unavailable', err?.code || err?.message || err);
    return;
  }
  // The form moved on (closed, another job, customer changed) — say nothing.
  if (seq !== _jbHintsSeq || gen !== _jobFormGen || (customerId && val('job-customer') !== customerId)) return;
  // Still this form + this customer at TAP time (COD r1 Code Reviewer).
  const stillOurs = () => gen === _jobFormGen && seq === _jbHintsSeq && (!customerId || val('job-customer') === customerId);
  const hintPlan = mod.planJobBuilderCustomerHints(result, { po: val('job-po'), well: val('job-well-name'), afe: val('job-afe') });
  const html = mod.jobBuilderCustomerHintsHtml(hintPlan);
  if (!html) return;
  const anchor = document.getElementById('job-builder-crew-suggest')
    || document.getElementById('job-builder-history-note')
    || document.getElementById('ai-prompt-section');
  if (!anchor) return;
  const box = document.createElement('div');
  box.id = 'job-builder-customer-hints';
  box.setAttribute('role', 'region');
  box.setAttribute('aria-label', 'Suggestions from this customer\'s past jobs');
  box.style.cssText = 'background:#F8FAFC;border:1px solid #CBD5E1;border-radius:8px;padding:12px 16px;margin-top:8px;font-size:0.9rem;color:var(--text-primary);';
  box.innerHTML = html;
  box.addEventListener('click', async (e) => {
    if (!stillOurs()) { box.remove(); return; }
    const fillBtn = e.target.closest('[data-jbh-fill]');
    if (fillBtn && !fillBtn.disabled) {
      const field = fillBtn.dataset.jbhFill;
      const value = fillBtn.dataset.jbhValue || '';
      const elId = field === 'po' ? 'job-po' : field === 'well' ? 'job-well-name' : field === 'afe' ? 'job-afe' : '';
      const el = elId && document.getElementById(elId);
      if (!el || !value) return;
      el.value = value;
      if (field === 'po') { delete el.dataset.autofilledPo; onJobPoInput(); } // the dispatcher's pick, never "ours"
      if (field === 'afe') document.getElementById('job-afe-vendor-row')?.classList.remove('hidden');
      applyJobRequiredFieldWarnings();
      // One value per field: the other chips for it go quiet.
      box.querySelectorAll(`[data-jbh-fill="${field}"]`).forEach(b => { b.disabled = true; });
      fillBtn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">check</span> Filled';
      return;
    }
    const openBtn = e.target.closest('[data-jbh-open]');
    if (openBtn) { openJobDetailsById(openBtn.dataset.jbhOpen); return; }
    const copyBtn = e.target.closest('[data-jbh-copy]');
    if (!copyBtn || copyBtn.disabled) return;
    const jobId = copyBtn.dataset.jbhCopy;
    const msg = 'Copy from that job replaces this form with a copy of it. What the Job Builder filled in here is dropped. Continue?';
    const go = typeof window.showConfirm === 'function'
      ? await window.showConfirm('Copy from that job?', msg, { confirmText: 'Copy it', cancelText: 'Cancel', icon: 'content_copy' })
      : window.confirm(msg);
    if (!go || !stillOurs()) return;
    if (!_resolveJobAnywhere(jobId)) {
      const tid = window.AppState?.tenant;
      try {
        const got = tid && DispatchState.tenantId === tid ? await _fetchJobIntoExternalCache(tid, jobId) : 'switched';
        if (got === 'missing') { window.showToast?.('That job is no longer on file — it may have been deleted.', 'error'); return; }
        if (got !== 'ok' || !stillOurs()) return; // COD r2 — same form + customer after the fetch
      } catch (err) {
        console.warn('[Dispatch] TASK-4803 S3b copy load failed', jobId, err);
        window.showToast?.('That job could not be loaded. Check your connection and try again.', 'error');
        return;
      }
    }
    duplicateJob(jobId);
  });
  anchor.insertAdjacentElement('afterend', box);
}

// ============================================
// NAVIGATION
// ============================================
function navigate(delta) {
  if (DispatchState.roleMode === 'driver') {
    if (DispatchState.driverViewMode === 'calendar') {
      // Calendar mode — chevrons step month-by-month
      const m = new Date(DispatchState.driverCalendarMonth);
      m.setMonth(m.getMonth() + (delta > 0 ? 1 : -1));
      DispatchState.driverCalendarMonth = m;
    } else {
      // Day mode — chevrons step day-by-day
      DispatchState.currentDate.setDate(DispatchState.currentDate.getDate() + delta);
    }
  } else if (DispatchState.viewMode === 'month') {
    // Month view - navigate by month
    DispatchState.currentDate.setMonth(DispatchState.currentDate.getMonth() + delta);
  } else if (DispatchState.viewMode === 'list') {
    // List view - navigate by 5 days (matches daysToShow)
    DispatchState.currentDate.setDate(DispatchState.currentDate.getDate() + (delta * 5));
  } else {
    // Week view and Lanes view - navigate by week
    DispatchState.currentDate.setDate(DispatchState.currentDate.getDate() + (delta * 7));
    // TASK-583 — week navigation re-arms the scroll-to-today trigger, so
    // navving back to the current week re-lands on today (mobile). Renders
    // of past/future weeks consume the flag as a no-op (no .today block).
    if (DispatchState.viewMode === 'week') DispatchState._scrollWeekToToday = true;
  }
  updateDateDisplay();
  _renderDispatchBoardNow(); // user navigation (prev/next) — instant feedback
}

function navigateWeek(delta) {
  navigate(delta);
}

function goToToday() {
  DispatchState.currentDate = new Date();
  DispatchState._scrollWeekToToday = true; // TASK-583 — land on today's block (mobile week view)
  updateDateDisplay();
  _renderDispatchBoardNow(); // user navigation (Today) — instant feedback
}

function loadPreviousDays() {
  // Move 5 days back
  DispatchState.currentDate.setDate(DispatchState.currentDate.getDate() - 5);
  updateDateDisplay();
  _renderDispatchBoardNow(); // user navigation — instant feedback
}

function setListQuickNav(view) {
  const today = new Date();
  if (view === 'today') {
    DispatchState.currentDate = new Date(today);
  } else if (view === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    DispatchState.currentDate = tomorrow;
  } else if (view === 'week') {
    // Start from Sunday of current week
    const dayOfWeek = today.getDay(); // 0=Sun
    const sunday = new Date(today);
    sunday.setDate(sunday.getDate() - dayOfWeek);
    DispatchState.currentDate = sunday;
  }
  updateDateDisplay();
  _renderDispatchBoardNow(); // user quick-nav (Today/Tomorrow/Week) — instant feedback
}

function updateDateDisplay() {
  // Compute the per-view period string ONCE, then write it into the
  // #current-date span inside the centered date selector (TASK-1065).
  let dateDisplay;
  const current = new Date(DispatchState.currentDate);

  if (DispatchState.viewMode === 'search') {
    dateDisplay = 'Search Jobs';
  } else if (DispatchState.viewMode === 'month') {
    // Month view: "January 2026"
    dateDisplay = current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else if (DispatchState.viewMode === 'week' || DispatchState.viewMode === 'lanes') {
    // Week/Lanes view: show the range "Jan 20 - Jan 26".
    //
    // 🔴 TASK-9112 — LANES IS NOT WEEK-SNAPPED AND THIS HEADER USED TO CLAIM IT
    // WAS. renderLanesView (:6498) paints 7 days starting AT currentDate;
    // navigate() steps lanes by 7 days without snapping. This branch ran
    // getWeekStart() for BOTH views, so on any day but Sunday the Lanes header
    // named a range up to six days off the grid underneath it — caught live on
    // 2026-09-10: header "Sep 6 - Sep 12" above columns reading Sep 10 – Sep 16.
    //
    // Fixed here rather than filed away because this task's whole subject is the
    // app disagreeing with itself about which dates are on screen: the new
    // off-board hint names the real window, and leaving the header lying would
    // have put two contradictory date ranges on one screen and taught lanes
    // users to distrust the hint. Week is unchanged — it IS snapped.
    const start = DispatchState.viewMode === 'lanes' ? new Date(current) : getWeekStart(current);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    dateDisplay = `${startStr} - ${endStr}`;
  } else {
    // List view: show start date "Jan 22, 2026"
    dateDisplay = current.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const dateSpan = document.getElementById('current-date');
  if (dateSpan) dateSpan.textContent = dateDisplay;

  // Update Today button styling based on whether current date is today
  const todayBtn = document.getElementById('btn-today');
  if (todayBtn) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const current = new Date(DispatchState.currentDate);
    current.setHours(0, 0, 0, 0);
    const isToday = today.getTime() === current.getTime();
    todayBtn.classList.toggle('btn-pill-active', isToday);
  }
}

// ============================================
// WAYPOINT FUNCTIONS
// ============================================
// TASK-9541 — `stopId` is the stop's permanent id. Pass it ONLY when re-rendering
// a stop that already belongs to THIS job (editJob). Every other caller — Add
// Waypoint, the place pickers, the Smart Job Builder, Copy to New Job, project
// carry — is making a NEW stop and gets a fresh id. The id rides on the row as
// data-stop-id, so it follows the row through rename, coordinate change, pin
// retry and any DOM reorder, and collectWaypoints reads it back out.
// TASK-9540 — `categoryId` is the saved-place category the stop was picked as
// (useSavedLocation) or already carries (editJob). Stored on the stop so the
// map can badge it; it never changes the stop's status color.
function addWaypoint(name = '', url = '', savedLocId = '', lat = null, lng = null, stopId = '', categoryId = '') {
  const list = document.getElementById('job-waypoints-list');
  if (!list) return;
  const hasData = name || url;
  // Check if this waypoint is already a saved location
  const isSaved = savedLocId || (name && (DispatchState.savedLocations || []).some(l => l.name?.toLowerCase() === name.toLowerCase()));
  const row = document.createElement('div');
  row.className = 'waypoint-row';
  row.dataset.stopId = isValidStopId(stopId) ? stopId : mintStopId();
  if (savedLocId) row.dataset.savedLocId = savedLocId;
  if (isValidStopCategoryId(categoryId)) row.dataset.categoryId = categoryId;
  const isLink = url && url.startsWith('http') && !url.includes('google.com/maps') && !isAppleMapsUrl(url) && !url.includes('goo.gl');
  row.innerHTML = `
    <input type="text" class="waypoint-name" placeholder="Location name" value="${escapeHtml(name)}" oninput="window.DispatchModule.updateWaypointStatus(this)">
    <div class="waypoint-url-wrap">
      <input type="text" class="waypoint-url" placeholder="Paste map link or enter address" value="${escapeHtml(url)}" oninput="window.DispatchModule.updateWaypointStatus(this)">
      <a class="waypoint-open" target="_blank" rel="noopener noreferrer" aria-label="Open this destination in Maps" title="Open in Maps" aria-disabled="true" tabindex="-1"><span class="material-icons" aria-hidden="true">map</span></a>
    </div>
    <div class="waypoint-link-hint" style="display:${isLink ? 'flex' : 'none'};align-items:center;gap:4px;font-size:0.7rem;color:var(--brand-blue);grid-column:1/-1;padding:0 2px;">
      <span class="material-icons" style="font-size:12px;">link</span> Clickable link — drivers tap to open
    </div>
    <span class="material-icons waypoint-status ${hasData ? 'active' : ''}">check_circle</span>
    <button type="button" class="btn-icon waypoint-save-loc" onclick="window.DispatchModule.toggleSaveWaypoint(this)" title="${savedLocId ? 'Saved to your locations list — click to remove' : isSaved ? 'Already in your saved locations — edit or remove it from Saved Locations' : 'Click to save this destination to your locations list'}" style="padding:2px;flex-shrink:0;color:${isSaved ? '#3B82F6' : 'var(--text-muted)'};">
      <span class="material-icons" style="font-size:18px;">${isSaved ? 'bookmark' : 'bookmark_border'}</span>
    </button>
    <button type="button" class="btn-icon waypoint-remove" onclick="window.DispatchModule.removeWaypoint(this)" title="Remove destination" style="color:#ef4444;padding:2px;margin-left:2px;flex-shrink:0;">
      <span class="material-icons" style="font-size:18px;">close</span>
    </button>
  `;
  list.appendChild(row);
  const nameInput = row.querySelector('.waypoint-name');
  if (nameInput && !name) nameInput.focus();

  // Address autocomplete on waypoint URL/address input
  // TASK-9563 — not on the driver PIN form: autocomplete calls the
  // anonymous-OK placesAutocomplete endpoint; the server geocodes at save.
  const urlInputEl = row.querySelector('.waypoint-url');
  if (urlInputEl && window.attachAddressAutocomplete && window.AppState?.driverModePin !== true) window.attachAddressAutocomplete(urlInputEl);
  if (urlInputEl) urlInputEl.addEventListener('focus', _prewarmMapResolver);
  // TASK-10116 — the map icon follows the address box. An autocomplete pick
  // writes the box and fires `change`, not `input` (app.js
  // attachAddressAutocomplete), so it needs its own listener. (typeof guard:
  // the node harnesses lift addWaypoint out on its own.)
  if (urlInputEl && typeof _refreshWaypointOpen === 'function') {
    urlInputEl.addEventListener('change', () => _refreshWaypointOpen(urlInputEl));
    _refreshWaypointOpen(urlInputEl);
  }

  // Restore previously-geocoded coords to input dataset so they survive re-saves
  // without needing to re-geocode (avoids losing lat/lng for plain-address waypoints
  // when the user re-edits a job and saves before geocoding finishes).
  if (urlInputEl && lat != null && lng != null && !extractCoordsFromUrl(url).lat) {
    urlInputEl.dataset.lat = String(lat);
    urlInputEl.dataset.lng = String(lng);
  }

  // Auto-resolve short URLs or geocode place URLs loaded from Firestore.
  // Skip geocoding if we already have coords from the saved waypoint.
  if (url) {
    const urlInput = row.querySelector('.waypoint-url');
    const icon = row.querySelector('.waypoint-status');
    const haveCoords = (lat != null && lng != null) || extractCoordsFromUrl(url).lat;
    // 🔴 COD Code Reviewer round 2, DATA LOSS — the comment two lines up says
    // "skip if we already have coords from the saved waypoint", and the geocode
    // branch honours it. The short-link branch never did. That was harmless
    // while the resolve only wrote on success; it is NOT harmless now that a
    // failed resolve deletes the row's dataset coordinates. Reopen a job whose
    // stop has a share link AND a stored coordinate, be offline for four
    // seconds, save without touching anything — and the coordinate the job
    // already had is gone. `!haveCoords` is the difference between "the
    // dispatcher CHANGED this stop" (drop the old place's coordinates, they
    // describe somewhere else) and "we are re-rendering what is already saved"
    // (touch nothing). A stop that is still missing its coordinate DOES resolve
    // on reopen, which is how editing an old job heals it.
    if (isShortMapUrl(url) && !haveCoords) {
      if (urlInput) resolveShortMapUrl(url, urlInput, icon);
    } else if (needsGeocode(url) && !haveCoords) {
      if (urlInput) geocodeWaypoint(url, urlInput, icon);
    } else if (!haveCoords && icon) {
      // 🔴 TASK-9194 — THE ROW THAT NOBODY OWNED. A saved stop with no
      // coordinate that is neither a share link nor address-shaped (a plain
      // http link, a lat/lng that failed validation, a bare place name) hits
      // neither branch above, so nothing ran and nothing painted — and the row
      // rendered the same green check as a stop that is actually pinned. That
      // is the identical lie TASK-4805 fixed one level down. If we are not
      // going to look this stop up, the row has to say it has no pin.
      setWaypointStatusIcon(icon, 'warn', SHORT_URL_NO_COORDS_TITLE);
    }
  }

  updateWaypointMileage();
}

function updateWaypointStatus(input) {
  const row = input.closest('.waypoint-row');
  if (!row) return;
  const nameVal = row.querySelector('.waypoint-name')?.value.trim();
  const urlVal = row.querySelector('.waypoint-url')?.value.trim();
  const icon = row.querySelector('.waypoint-status');
  // TASK-9194 — the reason a row had no pin belongs to the URL that was in the
  // box when we asked. Edit the URL and the old excuse is void; every terminal
  // path below re-stamps it. Clearing it here is what stops a stop that now
  // fails to geocode from being persisted as "the map service timed out".
  if (input.classList.contains('waypoint-url')) _setWaypointPinFail(input, null);
  // TASK-10116 — repaint the row's map icon from what is in the box NOW.
  // typeof guard: the node harnesses lift this function out on its own.
  if (typeof _refreshWaypointOpen === 'function') _refreshWaypointOpen(row.querySelector('.waypoint-url'));
  // TASK-9540 — a changed link/address is a different place; its category
  // badge no longer describes it.
  if (input.classList.contains('waypoint-url') && row.dataset && row.dataset.categoryId) delete row.dataset.categoryId;
  // TASK-9563 (COD #3, Code Reviewer) — on the driver PIN form the geocode /
  // short-link resolvers return early (the server geocodes at save), and they
  // were where a CHANGED address dropped the old pin. Drop it here instead: a
  // stop whose link/address text changed must never save with the previous
  // place's coordinates. The server then reads the new link or geocodes it.
  if (input.classList.contains('waypoint-url') && globalThis.window?.AppState?.driverModePin === true) {
    _dropWaypointDatasetCoords(input);
  }

  // TASK-4805 — never re-green a row that is FLAGGED as having no coordinate.
  // Typing in the name field would otherwise paint `.active` back onto the
  // amber error_outline, which is the same lie in a different colour. An
  // EMPTIED row drops the warning with the content — an amber flag stuck on a
  // blank row is its own kind of nonsense.
  // TASK-9194 — `sync_problem` (the retry state) is the same kind of flag and
  // needs the same protection; without it, typing a stop NAME re-greens a row
  // whose link never resolved.
  if (icon) {
    if (!nameVal && !urlVal) setWaypointStatusIcon(icon, 'idle');
    else if (icon.textContent !== 'error_outline' && icon.textContent !== 'sync_problem') icon.classList.add('active');
  }

  // TASK-359 — clear .waypoint-incomplete highlight as soon as user types into
  // either input. Without this, the red stays even while the dispatcher fixes
  // the row and they'll think the form is broken.
  if (row.classList.contains('waypoint-incomplete') && (nameVal || urlVal)) {
    row.classList.remove('waypoint-incomplete');
  }

  // Show/hide clickable link hint
  const hint = row.querySelector('.waypoint-link-hint');
  if (hint) {
    const isLink = urlVal && urlVal.startsWith('http') && !urlVal.includes('google.com/maps') && !isAppleMapsUrl(urlVal) && !urlVal.includes('goo.gl');
    hint.style.display = isLink ? 'flex' : 'none';
  }

  // If a short Google Maps URL was pasted, resolve it to get coordinates
  if (urlVal && isShortMapUrl(urlVal) && input.classList.contains('waypoint-url')) {
    resolveShortMapUrl(urlVal, input, icon);
  }
  // If a place URL or plain address, geocode it to get coordinates
  else if (urlVal && needsGeocode(urlVal) && input.classList.contains('waypoint-url')) {
    geocodeWaypoint(urlVal, input, icon);
  }
  // 🔴 COD Code Reviewer — the THIRD case, which had no owner. Paste a share
  // link, then before it comes back replace it with a URL that already carries
  // a coordinate (or clear the box). Neither branch above fires, so nothing
  // bumps the row's sequence — and the old resolve, still believing it is
  // current, lands and overwrites the dispatcher's new URL with the OLD link's
  // expansion. Both of the branches above supersede as their first act; this one
  // has to as well, or "the newest edit wins" is only true two times out of
  // three. (Cheap: on a row with nothing pending it just claims a fresh number.)
  else if (input.classList.contains('waypoint-url')) {
    _supersedeGeocode(_geocodeKey(input));
    if (!urlVal) {
      _dropWaypointDatasetCoords(input);
      // COD round 3 — the `idle` reset at the top of this function needs BOTH
      // fields empty, so clearing only the URL on a row that still has a NAME
      // fell through it and left the hourglass (or the amber from the link just
      // deleted) frozen, with the superseded request no longer able to repaint.
      // A name-only row has always rendered as a plain check; keep that, and
      // strip the dressing that no longer describes anything.
      // A name-only row keeps the plain check it has always had; a row emptied
      // outright stays on the neutral grey the top of this function just set.
      if (icon) setWaypointStatusIcon(icon, nameVal ? 'ok' : 'idle');
    } else if (icon) {
      // COD round 2 — superseding without repainting left the row stuck on the
      // hourglass (or on the amber from the link it just replaced) with nothing
      // in flight to ever clear it. Whatever is in the box now needs no lookup:
      // either it already carries a coordinate, or it is a plain tap-to-open
      // link that never had one and never will. Both have always rendered as a
      // plain check; keep that, but repaint it so no stale state survives.
      setWaypointStatusIcon(icon, 'ok');
    }
  }

  updateWaypointMileage();
}

// TASK-1337(C) — current iOS Share yields `maps.apple/p/<id>` on the BARE
// domain (no `.com`). Every host check in this file tested the `.com` form, so
// the documented way to add a waypoint produced no coords, no geofence default,
// no mileage, and the wrong "clickable link" label. Matching `maps.apple` with
// an optional `.com` covers both. Substring matching is fine HERE — this is
// presentation only. It is NOT fine on the server: `resolveMapUrl`'s allowlists
// stay exact-match `Set.has`, because a substring check there once let
// `http://169.254.169.254/?maps.app.goo.gl` reach the cloud metadata endpoint.
function isAppleMapsUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  // Parse the HOST rather than substring the whole string: a plain
  // `.includes('maps.apple')` would also match `https://maps.apple.evil.com/x`
  // and `https://evil.com/?maps.apple`. Mislabeling those is only cosmetic here
  // (the server allowlist is what actually gates resolution) but there is no
  // reason to be sloppy about it.
  try {
    const host = new URL(raw).hostname.toLowerCase();
    // EXACTLY the two hosts the server's resolveMapUrl allowlist accepts —
    // subdomains were accepted here at first, which COD caught: the client
    // would label `beta.maps.apple.com` a map link and then the server resolve
    // would fail, which is a worse experience than not recognizing it. Keep
    // this list and the server Set in lockstep.
    return host === 'maps.apple' || host === 'maps.apple.com';
  } catch (_) {
    // Not a parseable absolute URL (a bare address, a relative string) — the
    // callers below already handle those as non-links.
    return false;
  }
}

function isShortMapUrl(url) {
  return /maps\.app\.goo\.gl|goo\.gl\/maps/i.test(url) || isAppleMapsUrl(url);
}

// TASK-1617 — the address string we would hand to the geocoder for this
// waypoint, or null if there is nothing address-shaped here. Single source of
// truth so the on-blur path and the on-save path always ask the SAME question
// and can never disagree about what a waypoint means.
//
// The `?q=`/`daddr=` branch is new and it matters: those long Google URLs are
// exactly the shape that used to yield a garbage coordinate. Now that
// extractCoordsFromUrl correctly refuses them, without this branch they would
// resolve to NO coordinate at all — trading a wrong pin for a missing pin
// instead of a right one. Pulling the address out of the param and geocoding
// it is what actually fixes those stops.
function geocodeQueryFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return null;
  // decodeURIComponent THROWS a URIError on malformed percent-encoding — a
  // pasted URL containing a bare `%` is enough. needsGeocode() runs from
  // updateWaypointStatus() on every keystroke, so an uncaught throw here would
  // take out the waypoint status + mileage readout mid-typing. Fall back to the
  // plus-normalized string rather than losing the address entirely.
  const clean = s => {
    const plus = String(s).replace(/\+/g, ' ');
    try { return decodeURIComponent(plus).trim(); } catch (_) { return plus.trim(); }
  };
  const usable = s => s && s.length > 5 && /[a-zA-Z]/.test(s);

  // Google Maps place URL: /maps/place/<name>
  const place = raw.match(/\/maps\/place\/([^/@?]+)/);
  if (place) { const q = clean(place[1]); if (usable(q)) return q; }

  // An address carried in a query param (Google or Apple). Only when the value
  // contains letters — a coordinate pair would have been caught upstream.
  // TASK-9497 — `query=` too: the connector's search links (/maps/search/?api=1&query=…).
  const param = raw.match(/[?&](?:q|query|daddr|destination|address|saddr|origin)=([^&#]+)/i);
  if (param) { const q = clean(param[1]); if (usable(q) && /[a-zA-Z]/.test(q)) return q; }

  // Plain typed text that is not a URL at all (the common case: a typed address)
  if (!/^https?:\/\//i.test(raw) && usable(raw) && /[\d\s,]/.test(raw)) return raw;

  return null;
}

// Check if URL/text needs geocoding (place name URL or plain address)
function needsGeocode(url) {
  if (!url) return false;
  // Already has a usable coordinate — nothing to do.
  if (extractCoordsFromUrl(url).lat != null) return false;
  return geocodeQueryFromUrl(url) !== null;
}

// Cache resolved URLs to avoid repeat calls
const _resolvedUrlCache = {};

// 10/8 — measured against prod: resolveMapUrl answers in ~0.7s warm and ~4.9s
// COLD, from a desktop. A dispatcher pastes one link every few minutes, so most
// pastes land on a cold instance, and a phone's round trip on top of 4.9s blows
// the 5s per-attempt budget (TITS 10/7-10/8: 6 of 8 failed short links are
// `pinFailReason: 'timeout'`). Wake the function when the dispatcher starts
// working on a stop, not when they paste: the request does no work at all,
// but the instance is
// warm by the time the real request arrives. OPTIONS, not POST: the function
// answers it with a bare 204 before any auth or work (and CORS allows it), so
// the warm-up leaves no 401 in the console. Throttled; never awaited; never
// throws.
let _mapResolverWarmAt = 0;
function _prewarmMapResolver() {
  try {
    if (globalThis.window?.AppState?.driverModePin === true) return;
    const now = Date.now();
    if (now - _mapResolverWarmAt < 4 * 60 * 1000) return;
    _mapResolverWarmAt = now;
    if (typeof fetch !== 'function') return;
    fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/resolveMapUrl', { method: 'OPTIONS' }).catch(() => {});
  } catch (_) { /* best-effort */ }
}

// ── TASK-4805 — THE ROW ICON HAS TO BE ABLE TO SAY "NO" ─────────────────────
// Every terminal state of resolveShortMapUrl used to write `check_circle`:
// resolved with coordinates, resolved WITHOUT coordinates, and outright
// failure. Three different facts, one green check. That is the whole reason
// 668 coordinate-less `maps.app.goo.gl` links piled up on live TITS without a
// single dispatcher noticing — the UI told them it had worked, 668 times.
//
// Three states, three appearances. `--status-warning` (#D97706) is the same
// amber the geofence readout already uses for "couldn't resolve that address",
// so this needs no new CSS and matches a pattern the dispatcher has seen.
//
// TASK-9194 adds a FOURTH: 'retry'. "We gave up waiting" is not "this link is
// not resolvable", and it must not look like it. Same amber (the stop still has
// no pin and that is still a warning) but a different glyph, a different
// sentence, and a tap that actually RETRIES instead of a tap that repeats the
// accusation. `onRetry` is optional — without it the row falls back to the
// explain-by-toast behaviour every other warn state has.
function setWaypointStatusIcon(statusIcon, state, title, onRetry) {
  if (!statusIcon) return;
  statusIcon.title = title || '';
  if (state === 'retry') {
    statusIcon.textContent = 'sync_problem';
    statusIcon.classList.remove('active');
    statusIcon.style.color = 'var(--status-warning)';
    statusIcon.style.cursor = 'pointer';
    // Returns the retry's promise so a caller (and a test) can await the
    // second attempt rather than guess at when it lands.
    statusIcon.onclick = () => {
      if (typeof onRetry === 'function') return onRetry();
      window.showToast?.(title || SHORT_URL_TIMEOUT_TITLE, 'warning');
    };
    return;
  }
  if (state === 'warn') {
    // We have no coordinate for this stop, and we are going to say so.
    statusIcon.textContent = 'error_outline';
    statusIcon.classList.remove('active');
    statusIcon.style.color = 'var(--status-warning)';
    // COD UX Lead — a dispatcher on a yard tablet has no hover, so a `title`
    // alone says nothing to half the people who hit this. Make the icon tappable
    // and give them the same sentence as a toast.
    statusIcon.style.cursor = 'pointer';
    statusIcon.onclick = () => window.showToast?.(title || SHORT_URL_NO_COORDS_TITLE, 'warning');
    return;
  }
  // Every non-warn state must actively CLEAR the warning dressing, not just
  // overwrite the glyph. COD Code Reviewer: geocoding an address after a failed
  // share link used to leave the amber colour and the stale "couldn't open that
  // map link" tooltip sitting on a row that had since resolved fine.
  statusIcon.style.color = '';
  statusIcon.style.cursor = '';
  statusIcon.onclick = null;
  statusIcon.textContent = state === 'pending' ? 'hourglass_empty' : 'check_circle';
  // 'idle' is the empty-row look: the grey check the row renders with.
  if (state === 'idle') statusIcon.classList.remove('active');
  else statusIcon.classList.add('active');
}

// ── TASK-9194 — THE BUDGET WAS THE BUG, AND THE MESSAGE MADE IT WORSE ───────
// This was ONE 4-second window covering THREE serial round trips inside
// resolveShortMapUrl: getIdToken(), the fetch to `resolveMapUrl`, and that
// function fetching Google and following the redirect. On a warm function over
// good signal it fits. On a cold start, or on the weak cell a dispatcher
// actually has in the oilfield, it does not — and every one of those gave up
// and painted "That map link didn't carry a location", which sends the user to
// re-copy a URL THAT WAS ALREADY CORRECT. Proven on live TITS: two
// `maps.app.goo.gl` links with the identical resolved shape, one pinned and one
// saved null. Same link shape, different outcome — it was never the link.
//
// Is 4s the right number for a mobile round trip on a bad connection? No. It is
// not even the right SHAPE of number: a budget shared serially across a token
// refresh and a network hop means whichever runs first can starve the other.
//
//  · SHORT_URL_TOKEN_TIMEOUT_MS gives `getIdToken()` its OWN window. It does not
//    depend on the pasted URL and is normally a memory read; it only touches the
//    network when the cached token has gone stale. It must not be able to spend
//    the budget the actual resolve needs.
//  · SHORT_URL_RESOLVE_TIMEOUT_MS is now PER ATTEMPT, not for the whole job.
//  · The retry is AUTOMATIC and there is exactly one. A cold start is the common
//    failure and asking twice fixes it; making the dispatcher click a button to
//    do what we could have done ourselves is just a slower version of blaming
//    them. More than one retry buys little and costs the save real seconds.
//  · SHORT_URL_RESOLVE_TOTAL_MS is a HARD wall-clock cap over both attempts, so
//    the retry can never turn a bounded wait into an unbounded one. Everything
//    downstream reasons about this number, not the per-attempt one.
const SHORT_URL_TOKEN_TIMEOUT_MS = 2500;
const SHORT_URL_RESOLVE_TIMEOUT_MS = 5000;
const SHORT_URL_RESOLVE_ATTEMPTS = 2;
// 🔴 TRIMMED AFTER COD — Architect, Skeptic, UX Lead and Code Reviewer all
// arrived at the same objection independently: the save WAITS on this, so
// every millisecond here is a millisecond a dispatcher stares at a spinner,
// and the save's true ceiling is this plus geocodeMissingWaypoints' own 6s.
// 8000 pushed that ceiling from ~12s to ~15s, which is too much to spend
// fixing a 4s problem. 7000 gives the FIRST attempt the full 5s it needed (the
// measured failure is attempt one not fitting) and lets the retry have
// whatever is left — the retry is the bonus, not the fix.
//
// ⚠️ NOT DONE HERE, and it is the better answer if this is still too slow:
// let the save complete IMMEDIATELY on the pinStatus mark and resolve in the
// background, writing the coordinate back afterwards. That is a post-save
// write-back with its own race against a second edit, and it is a bigger change
// than this defect justifies. Filed as the follow-up, not smuggled in.
const SHORT_URL_RESOLVE_TOTAL_MS = 7000;
const SHORT_URL_RETRY_BACKOFF_MS = 250;
// Don't start a second attempt that cannot finish — a retry with 400ms left is
// a guaranteed timeout dressed up as an effort.
const SHORT_URL_RETRY_MIN_REMAINING_MS = 1500;

// 🔴 THE INVARIANT THAT MUST NOT BREAK, and the reason it is spelled out here:
// the save's wait must OUTLAST the resolve's total budget. Two reasons, both
// about the dispatcher, and both inherited verbatim from TASK-4805:
//  · The save must never be the thing that gives up. Inside this window the
//    call has already settled one way or the other and written an honest icon,
//    so awaitPendingGeocodes never has to report timedOut for a short link.
//  · An abandoned lookup that outlives the save's wait parks its Promise in
//    _pendingGeocodes, and the NEXT save — a different job — inherits the wait
//    for a lookup that is never coming back.
// Raising the resolve budget therefore REQUIRED raising this one in lockstep.
// It was a hardcoded `6000` inside awaitPendingGeocodes, which is exactly how a
// pair of numbers that have to move together drift apart.
// SHORT_URL_RESOLVE_TOTAL_MS < PENDING_GEOCODE_WAIT_MS — asserted in
// tests/task9194-shortlink-timeout.unit.cjs.
const PENDING_GEOCODE_WAIT_MS = 7500;

// 🔴 COD Code Reviewer — the stale-coordinate trap TASK-1662 already documented
// for geocodeWaypoint, which this path had not inherited. A row that had an
// ADDRESS geocoded carries `dataset.lat/lng`. Replace that address with a share
// link and let the resolve fail, and collectWaypoints (:18893) falls back to
// those data attributes and persists the OLD place's coordinates for the NEW
// stop — then geocodeMissingWaypoints skips it because it "already looks
// plottable", so the warning never fires either. A wrong pin bills invented
// miles; a missing pin does not. Drop them on every terminal path.
function _dropWaypointDatasetCoords(urlInput) {
  if (!urlInput || !urlInput.dataset) return;
  delete urlInput.dataset.lat;
  delete urlInput.dataset.lng;
}

const GEOCODE_NO_RESULT_TITLE =
  "Couldn't find that address on the map — the destination will save without a location. Check the spelling, or paste a map link.";
const GEOCODE_FAILED_TITLE =
  "Couldn't look that address up — the destination will save without a location. Check your connection and try again.";
const SHORT_URL_NO_COORDS_TITLE =
  "That map link didn't carry a location. Open it in Maps and paste the full URL, or type the address.";
const SHORT_URL_FAILED_TITLE =
  "Couldn't open that map link — the destination will save without a location. Check your connection, or type the address.";
// 🔴 TASK-9194 — THREE OUTCOMES WERE WEARING ONE MESSAGE: timed out ·
// resolved-but-no-coordinates · genuinely bad link. Only the middle one is the
// link's fault. This copy must never imply the dispatcher pasted something
// wrong, because in the case it fires on, they did not.
const SHORT_URL_TIMEOUT_TITLE =
  "Couldn't reach the map service in time — your link looks fine, we just didn't get an answer. Tap to try again.";
const SHORT_URL_RETRYING_TITLE =
  "Still trying to reach the map service…";

// Expand a pasted Google/Apple share link into the full URL that carries the
// coordinates. TASK-4805: this was fire-and-forget and never registered in
// `_pendingGeocodes`, so `saveJob`'s `awaitPendingGeocodes()` — which exists for
// exactly this — never waited for it. Paste a link, hit Save, and the job
// persisted BEFORE the coordinate came back, to a DOM nobody would read again.
// It now registers through the same per-row supersede machinery geocodeWaypoint
// uses, so one row can only ever have one lookup in flight and typing cancels
// its own predecessor instead of stacking up fetches.
// TASK-9194 — why a stop has no pin, stamped on the row so collectWaypoints can
// persist the REASON and not just the absence. Pass null to clear it; a row that
// resolved must not carry a stale excuse from the attempt before.
function _setWaypointPinFail(urlInput, reason) {
  if (!urlInput || !urlInput.dataset) return;
  if (reason) urlInput.dataset.pinFail = reason;
  else delete urlInput.dataset.pinFail;
}

// A timeout has to be distinguishable from every other exception by the time it
// reaches the catch. Tagging the Error is the only thing that survives a
// Promise.race — the message alone is not something to branch on.
function _mapResolveTimeoutError() {
  const e = new Error('map link resolve timed out');
  e.mapResolveTimeout = true;
  return e;
}

// Race `p` against `ms` and abort `ctrl` if we give up.
//
// 🔴 The deadline is a REJECTING PROMISE, not only an AbortController signal —
// inherited from TASK-4805 and still the reason this exists. Firebase's
// `getIdToken()` can refresh over the network and knows nothing about our
// signal, so a stall there would sail past the abort and leave the caller's
// promise pending in `_pendingGeocodes` forever.
function _withDeadline(p, ms, ctrl) {
  let timer = null;
  let deadlineFired = false;
  const deadline = new Promise((_, rej) => {
    timer = setTimeout(() => {
      deadlineFired = true;
      // 🔴 REJECT FIRST, ABORT SECOND — the order is load-bearing and this file
      // had it backwards. `ctrl.abort()` makes fetch reject with its OWN
      // AbortError, and Promise.race reports whichever rejection lands first.
      // Aborting first hands the caller a generic AbortError, `.mapResolveTimeout`
      // is missing, and the row gets painted "that link didn't carry a location"
      // — which is the exact bug this task exists to kill, reintroduced one layer
      // down. Rejecting first makes the race deterministic.
      rej(_mapResolveTimeoutError());
      try { ctrl && ctrl.abort(); } catch (_) {}
    }, ms);
  });
  return Promise.race([Promise.resolve(p), deadline])
    .catch(err => {
      // Belt for the same hazard: if our deadline fired, the failure is ours no
      // matter which error object arrived. A tag that a different exception can
      // arrive in place of is not a guard.
      if (deadlineFired && !(err && err.mapResolveTimeout)) throw _mapResolveTimeoutError();
      throw err;
    })
    .finally(() => { if (timer) clearTimeout(timer); });
}

// ONE attempt at the resolveMapUrl round trip. Returns the expanded URL, or
// throws — a timeout carries `.mapResolveTimeout`, and anything the server has
// already decided about the LINK carries `.mapResolveFatal` so the caller does
// not waste a retry re-asking a question that is already answered.
async function _resolveMapUrlOnce(shortUrl, hardDeadlineAt) {
  const left = () => hardDeadlineAt - Date.now();
  if (left() <= 0) throw _mapResolveTimeoutError();

  // PHASE 1 — the ID token, on its own budget (see SHORT_URL_TOKEN_TIMEOUT_MS).
  // Maps endpoints require a Firebase ID token (safety-audit M3).
  const tokenMs = Math.max(1, Math.min(SHORT_URL_TOKEN_TIMEOUT_MS, left()));
  const idToken = await _withDeadline(Promise.resolve(auth.currentUser?.getIdToken()), tokenMs);
  if (!idToken) {
    // Not signed in is not a network problem and a second ask cannot fix it.
    const e = new Error('not authenticated');
    e.mapResolveFatal = true;
    throw e;
  }

  // PHASE 2 — the round trip. The fetch AND the body read share ONE attempt
  // clock and ONE controller.
  //
  // 🔴 COD Code Reviewer — the body read originally got `left()` (the whole
  // remaining TOTAL) and no controller at all. A response whose headers arrive
  // promptly and whose body then stalls — the classic half-open cell — would
  // sail past the per-attempt limit, eat the entire budget, and leave nothing
  // for the retry. `resp.json()` is a network read, not a local parse; it has to
  // be bounded and abortable exactly like the request that produced it.
  if (left() <= 0) throw _mapResolveTimeoutError();
  const ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
  const attemptMs = Math.max(1, Math.min(SHORT_URL_RESOLVE_TIMEOUT_MS, left()));
  const attemptEndsAt = Date.now() + attemptMs;
  const resp = await _withDeadline(fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/resolveMapUrl', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
    body: JSON.stringify({ url: shortUrl }),
    signal: ctrl ? ctrl.signal : undefined
  }), attemptMs, ctrl);

  if (!resp.ok) {
    const e = new Error(`resolve failed (${resp.status})`);
    // A 4xx means the server looked at this URL and said no — that is the link,
    // and it will say the same thing next time. A 5xx or a 429 is us or Google
    // having a moment, which is exactly what the retry is for.
    e.mapResolveFatal = resp.status >= 400 && resp.status < 500 && resp.status !== 429;
    throw e;
  }
  const bodyMs = Math.max(1, Math.min(attemptEndsAt - Date.now(), left()));
  const body = await _withDeadline(resp.json(), bodyMs, ctrl);
  return body && body.expanded;
}

// Steve/Mike (TITS) 2026-10-08 — a business shared from the Google Maps app
// (`maps.app.goo.gl/...?g_st=ic`) expands to `maps.google.com/?q=Elkan+Water+Inc,
// +12154+ND-23,+Watford+City,+ND` — a NAME AND ADDRESS, no coordinate. That was
// painted "That map link didn't carry a location" and saved unpinned (job 1176,
// "Elian hot water depot" / "Wasa water depot"). The address is right there:
// geocode it, exactly as a typed address would be. Returns true when a geocode
// was started (it owns the row from here), false when there is nothing to ask.
function _geocodeExpandedShareLink(expanded, urlInput, statusIcon) {
  if (!expanded || !urlInput) return false;
  const q = geocodeQueryFromUrl(expanded);
  if (!q) return false;
  geocodeWaypoint(expanded, urlInput, statusIcon);
  return true;
}

async function resolveShortMapUrl(shortUrl, urlInput, statusIcon) {
  // TASK-9563 — a PIN driver's stops are located SERVER-SIDE by driverSaveJob,
  // behind the validated driver session. Never call the anonymous-OK map
  // endpoints from the driver form; the save names any stop it could not pin.
  if (globalThis.window?.AppState?.driverModePin === true) return; // globalThis: also loads in the node harnesses
  const key = _geocodeKey(urlInput);

  if (_resolvedUrlCache[shortUrl]) {
    // A cached answer is for the text in the box right now, so it supersedes
    // anything still pending for this row (same reasoning as geocodeWaypoint).
    _supersedeGeocode(key);
    const expanded = _resolvedUrlCache[shortUrl];
    const coords = plottableCoord(extractCoordsFromUrl(expanded));
    _dropWaypointDatasetCoords(urlInput);
    if (coords) {
      if (urlInput) urlInput.value = expanded;
      if (typeof _refreshWaypointOpen === 'function') _refreshWaypointOpen(urlInput); // TASK-10116 — the box changed under the icon
      _setWaypointPinFail(urlInput, null);
      setWaypointStatusIcon(statusIcon, 'ok');
      updateWaypointMileage();
      return { status: GEOCODE_OUTCOME.RESOLVED, query: shortUrl, lat: coords.lat, lng: coords.lng };
    }
    if (_geocodeExpandedShareLink(expanded, urlInput, statusIcon)) {
      return { status: GEOCODE_OUTCOME.SUPERSEDED, query: shortUrl };
    }
    _setWaypointPinFail(urlInput, 'no-coords');
    setWaypointStatusIcon(statusIcon, 'warn', SHORT_URL_NO_COORDS_TITLE);
    updateWaypointMileage();
    return { status: GEOCODE_OUTCOME.NO_RESULT, query: shortUrl };
  }

  const seq = _supersedeGeocode(key);
  let settle;
  const pending = new Promise(resolve => {
    let settled = false;
    settle = (o) => { if (settled) return; settled = true; resolve(o); };
  });
  // Registered SYNCHRONOUSLY, before the first await, so a dispatcher who pastes
  // and immediately hits Save is already inside the save's wait window.
  _geocodeState.set(key, { timer: null, settle, seq, query: shortUrl });
  _pendingGeocodes.add(pending);
  pending.finally(() => _pendingGeocodes.delete(pending));

  setWaypointStatusIcon(statusIcon, 'pending');

  let outcome = { status: GEOCODE_OUTCOME.FAILED, query: shortUrl };
  try {
    // TASK-9194 — one automatic retry, and it retries ONLY the things a second
    // ask can fix. An expanded-but-coordinate-less answer and a 4xx are the
    // server telling us about the LINK; asking again just burns the
    // dispatcher's seconds to hear the same thing.
    const hardDeadlineAt = Date.now() + SHORT_URL_RESOLVE_TOTAL_MS;
    let expanded = null, lastErr = null;
    for (let attempt = 1; attempt <= SHORT_URL_RESOLVE_ATTEMPTS; attempt++) {
      try { expanded = await _resolveMapUrlOnce(shortUrl, hardDeadlineAt); lastErr = null; break; }
      catch (err) {
        lastErr = err;
        if (err && err.mapResolveFatal) break;
        if (attempt >= SHORT_URL_RESOLVE_ATTEMPTS) break;
        if (hardDeadlineAt - Date.now() < SHORT_URL_RETRY_MIN_REMAINING_MS) break;
        // Say we are still working. A silent gap between attempts is how a
        // dispatcher concludes the app has hung and starts re-pasting.
        if (_geocodeIsCurrent(key, seq)) setWaypointStatusIcon(statusIcon, 'pending', SHORT_URL_RETRYING_TITLE);
        await new Promise(r => setTimeout(r, SHORT_URL_RETRY_BACKOFF_MS));
      }
    }
    if (lastErr) throw lastErr;
    if (expanded) _resolvedUrlCache[shortUrl] = expanded;

    const coords = plottableCoord(extractCoordsFromUrl(expanded));
    if (!_geocodeIsCurrent(key, seq)) {
      // The row was edited while we were on the network. The answer is cached
      // (keyed by the link, so it stays useful) but it must not touch a DOM the
      // newer request now owns.
      outcome = { status: GEOCODE_OUTCOME.SUPERSEDED, query: shortUrl };
    } else if (coords) {
      // Replace the short URL with the expanded one that carries coordinates —
      // collectWaypoints reads them straight back out of the input value.
      if (urlInput) urlInput.value = expanded;
      if (typeof _refreshWaypointOpen === 'function') _refreshWaypointOpen(urlInput); // TASK-10116 — the box changed under the icon
      _dropWaypointDatasetCoords(urlInput);
      _setWaypointPinFail(urlInput, null);
      setWaypointStatusIcon(statusIcon, 'ok');
      updateWaypointMileage();
      outcome = { status: GEOCODE_OUTCOME.RESOLVED, query: shortUrl, lat: coords.lat, lng: coords.lng };
    } else {
      // Expanded, but Google gave us a link with no point in it (a `cid=` or a
      // plus-code). Keep the original URL — the driver can still tap it — but
      // stop pretending the stop is on the map.
      _dropWaypointDatasetCoords(urlInput);
      if (_geocodeExpandedShareLink(expanded, urlInput, statusIcon)) {
        // Hand-off: the geocode registered its own pending lookup (the save
        // drains in passes, so it waits for that one too).
        outcome = { status: GEOCODE_OUTCOME.SUPERSEDED, query: shortUrl };
      } else {
        _setWaypointPinFail(urlInput, 'no-coords');
        setWaypointStatusIcon(statusIcon, 'warn', SHORT_URL_NO_COORDS_TITLE);
        updateWaypointMileage();
        outcome = { status: GEOCODE_OUTCOME.NO_RESULT, query: shortUrl };
      }
    }
  } catch (err) {
    // 🔴 TASK-9194 — THE WHOLE POINT OF THIS BRANCH. "We ran out of time" and
    // "this link is not resolvable" arrive here as the same exception object
    // and used to leave as the same sentence. They are not the same fact and
    // the dispatcher cannot act on them the same way.
    const timedOut = !!(err && err.mapResolveTimeout);
    console.warn(`[Dispatch] Short URL resolve ${timedOut ? 'timed out' : 'failed'}:`, err);
    if (_geocodeIsCurrent(key, seq)) {
      // The URL the dispatcher typed STAYS IN THE BOX on every failure path —
      // nothing below writes urlInput.value. That is load-bearing: telling
      // someone to re-paste a link while quietly clearing it is the worst of
      // both worlds.
      _dropWaypointDatasetCoords(urlInput);
      _setWaypointPinFail(urlInput, timedOut ? 'timeout' : 'failed');
      if (timedOut) {
        setWaypointStatusIcon(statusIcon, 'retry', SHORT_URL_TIMEOUT_TITLE,
          () => resolveShortMapUrl(shortUrl, urlInput, statusIcon));
      } else {
        setWaypointStatusIcon(statusIcon, 'warn', SHORT_URL_FAILED_TITLE);
      }
      updateWaypointMileage();
    }
    outcome = {
      status: timedOut ? GEOCODE_OUTCOME.TIMED_OUT : GEOCODE_OUTCOME.FAILED,
      query: shortUrl,
      error: err?.message || String(err)
    };
  } finally {
    // TASK-9194 — the abort timer moved inside _withDeadline, which clears its
    // own in a `.finally`. There is nothing left to clear out here.
    const cur = _geocodeState.get(key);
    if (cur && cur.seq === seq) _geocodeState.delete(key);
    settle(outcome);
  }
  return pending;
}

// Geocode a place name URL or plain address to get coordinates
const _geocodeCache = {};

// ── TASK-1662 — THE DEBOUNCE IS PER WAYPOINT ROW, NOT PER MODULE ────────────
// This was ONE module-level `_geocodeDebounce` timer shared by every waypoint
// row on the form. Typing into the drop-off cleared the pick-up's pending timer
// and resolved the pick-up's Promise — so the pick-up's lookup NEVER RAN, and
// the awaiter could not tell that apart from "the geocoder answered and had
// nothing usable". Nothing threw, the save looked clean, and the stop was
// persisted with no coordinate. That is the mechanism behind "only 69% of
// waypoints carry lat/lng, and the missing end is usually the drop-off".
//
// Two things had to change, and BOTH matter:
//   1. Timer state is keyed on the row's own `.waypoint-url` <input>, so one
//      row's keystroke can only ever cancel that same row's pending lookup.
//   2. A canceled lookup reports SUPERSEDED, not an empty success. "We never
//      got to look" and "we looked and found nothing" are different facts and
//      the caller is now told which one it got.
//
// WeakMap keyed on the input element: the state dies with the DOM node, so
// closing and reopening the job modal drops it with no manual cleanup.
const _geocodeState = new WeakMap(); // urlInput -> { timer, settle, seq, query }
// WeakMap needs an object key. Defensive fallback for a call with no input
// element so that path behaves identically instead of throwing.
const _GEOCODE_NO_INPUT_KEY = Object.freeze({});
let _geocodeSeqCounter = 0;

// Explicit outcomes — see (2) above. Exposed on window.DispatchModule so a test
// can assert the distinction rather than infer it from a coordinate.
const GEOCODE_OUTCOME = {
  RESOLVED:   'resolved',    // coordinates found, validated, written to the row
  NO_RESULT:  'no-result',   // geocoder answered and had nothing plottable
  FAILED:     'failed',      // the call errored — the address may be perfectly good
  // TASK-9194 — split out of FAILED. "We ran out of time" is the one outcome
  // that says NOTHING about the link, and it was the one being reported as a
  // bad link. Nothing switches exhaustively on this enum; a reader that does
  // not know TIMED_OUT still sees a non-RESOLVED status and behaves correctly.
  TIMED_OUT:  'timed-out',   // we gave up waiting — the link may be perfectly good
  SUPERSEDED: 'superseded'   // canceled because THIS SAME row was edited again
};

const _pendingGeocodes = new Set();

function _geocodeKey(urlInput) {
  return (urlInput && typeof urlInput === 'object') ? urlInput : _GEOCODE_NO_INPUT_KEY;
}

// Cancel the not-yet-fired debounce for THIS row only and settle its Promise as
// SUPERSEDED, then claim a fresh sequence number for the row. The sequence is
// what lets a lookup that is ALREADY past its timer (awaiting the network) find
// out it has been overtaken, so a slow answer for a half-typed address cannot
// stomp the newer answer that landed first. Returns the caller's sequence.
function _supersedeGeocode(key) {
  const prev = _geocodeState.get(key);
  if (prev) {
    if (prev.timer) clearTimeout(prev.timer);
    if (prev.settle) prev.settle({ status: GEOCODE_OUTCOME.SUPERSEDED, query: prev.query || null });
  }
  const seq = ++_geocodeSeqCounter;
  _geocodeState.set(key, { timer: null, settle: null, seq, query: null });
  return seq;
}

// True while `seq` is still the newest request issued for this row.
function _geocodeIsCurrent(key, seq) {
  const st = _geocodeState.get(key);
  return !!st && st.seq === seq;
}

async function geocodeWaypoint(text, urlInput, statusIcon) {
  // TASK-9563 — PIN driver: the server geocodes at save (see resolveShortMapUrl).
  if (globalThis.window?.AppState?.driverModePin === true) return; // globalThis: also loads in the node harnesses
  // TASK-1617 — same extractor the save path uses, so the two can't disagree.
  const query = geocodeQueryFromUrl(text) || text;
  const key = _geocodeKey(urlInput);

  if (_geocodeCache[query]) {
    // A cache hit answers for the text that is in the box RIGHT NOW, so it
    // supersedes anything still pending for this row. Without this the older
    // pending lookup fires 800ms later and overwrites the cached answer with
    // the coordinates of a half-typed address.
    _supersedeGeocode(key);
    const cached = _geocodeCache[query];
    // TASK-1617 — validate the cached pair before it is allowed onto the input,
    // and gate on "the URL itself carries no coords" rather than "not http": an
    // http URL whose address lives in a ?q= param needs these data attributes
    // too, and under the old `!startsWith('http')` test it silently got none.
    const cc = plottableCoord(cached);
    if (cc && urlInput && extractCoordsFromUrl(text).lat == null) {
      urlInput.dataset.lat = String(cc.lat);
      urlInput.dataset.lng = String(cc.lng);
    }
    _setWaypointPinFail(urlInput, cc ? null : 'address-not-found');   // TASK-9194
    setWaypointStatusIcon(statusIcon, cc ? 'ok' : 'warn', cc ? '' : GEOCODE_NO_RESULT_TITLE);
    updateWaypointMileage();
    return;
  }

  // Supersede only THIS row's own pending lookup. Every other row's timer keeps
  // running untouched — that is the whole fix.
  const seq = _supersedeGeocode(key);

  let settle;
  const pending = new Promise(resolve => {
    // Idempotent: a lookup that was superseded mid-flight still reaches its own
    // `finally`, and must not resolve a second time with a contradictory answer.
    let settled = false;
    settle = (outcome) => {
      if (settled) return;
      settled = true;
      resolve(outcome);
    };
  });

  const timer = setTimeout(async () => {
    const own = _geocodeState.get(key);
    if (own && own.seq === seq) own.timer = null;

    setWaypointStatusIcon(statusIcon, 'pending');

    let outcome = { status: GEOCODE_OUTCOME.FAILED, query };
    try {
      const geocodeAddressFn = httpsCallable(functions, 'geocodeAddress');
      const resp = await geocodeAddressFn({ address: query });
      const { lat, lng, formattedAddress } = resp.data || {};
      // TASK-1617 — a geocode result is NOT trusted just because it came back.
      // Run it through the same gate every other coordinate passes, so the
      // path meant to fix bad coordinates cannot itself introduce one. A
      // rejected result caches as null: no coords written, no repeat call.
      const gc = plottableCoord({ lat, lng });
      _geocodeCache[query] = gc
        ? { lat: gc.lat, lng: gc.lng, formattedAddress }
        : { lat: null, lng: null, formattedAddress };

      // The await above is a real gap — the dispatcher may have retyped this row
      // while the call was in flight. Cache the answer either way (it is keyed
      // by address, so it is always useful) but never let a stale answer touch
      // the DOM the newer request now owns.
      if (!_geocodeIsCurrent(key, seq)) {
        outcome = { status: GEOCODE_OUTCOME.SUPERSEDED, query };
      } else if (gc) {
        // Store coords as data attributes for mileage calculation
        if (urlInput) {
          urlInput.dataset.lat = String(gc.lat);
          urlInput.dataset.lng = String(gc.lng);
        }
        _setWaypointPinFail(urlInput, null);   // TASK-9194
        setWaypointStatusIcon(statusIcon, 'ok');
        updateWaypointMileage();
        outcome = { status: GEOCODE_OUTCOME.RESOLVED, query, lat: gc.lat, lng: gc.lng };
      } else {
        // No usable point. Leave lat/lng absent — a missing pin is recoverable,
        // a wrong pin silently bills the customer for invented miles.
        if (urlInput) {
          delete urlInput.dataset.lat;
          delete urlInput.dataset.lng;
        }
        // TASK-4805 — this said `check_circle` too. The save-time backstop and
        // its toast already cover an address that would not geocode, but the ROW
        // still claimed success, and after a failed share link it would have left
        // the amber dressing sitting on a green check. One helper, one honest
        // state. (No staleness risk: the only thing that can retroactively fill
        // this stop is the save-time pass, and that save closes the modal.)
        _setWaypointPinFail(urlInput, 'address-not-found');   // TASK-9194
        setWaypointStatusIcon(statusIcon, 'warn', GEOCODE_NO_RESULT_TITLE);
        outcome = { status: GEOCODE_OUTCOME.NO_RESULT, query };
      }
    } catch (err) {
      console.warn('[Dispatch] Geocode failed:', err);
      // TASK-1662 (COD Code Reviewer + Independent Reviewer) — a transport
      // failure must not leave the PREVIOUS address's coordinates attached to
      // the text now in the box. They only got here because the row was edited,
      // so those coordinates describe a different place; collectWaypoints would
      // happily persist them, and the save-time backstop would skip the stop
      // because it already "looks plottable". Dropping them makes the failure
      // visible instead of wrong: geocodeMissingWaypoints retries the new
      // address at save (it deliberately never caches a transport failure), and
      // anything still unresolved is named to the dispatcher in the save toast.
      // Guarded on still-current so a superseded failure can't wipe the answer
      // the newer request already wrote.
      if (urlInput && _geocodeIsCurrent(key, seq)) {
        delete urlInput.dataset.lat;
        delete urlInput.dataset.lng;
        updateWaypointMileage();
      }
      if (_geocodeIsCurrent(key, seq)) {
        _setWaypointPinFail(urlInput, 'failed');   // TASK-9194
        setWaypointStatusIcon(statusIcon, 'warn', GEOCODE_FAILED_TITLE);
      }
      outcome = { status: GEOCODE_OUTCOME.FAILED, query, error: err?.message || String(err) };
    } finally {
      const cur = _geocodeState.get(key);
      if (cur && cur.seq === seq) _geocodeState.delete(key);
      settle(outcome);
    }
  }, 800); // 800ms debounce for typing

  _geocodeState.set(key, { timer, settle, seq, query });
  _pendingGeocodes.add(pending);
  pending.finally(() => _pendingGeocodes.delete(pending));
  return pending;
}

// Resolves once every in-flight geocode has finished, been superseded by a
// newer edit to the SAME row, or the hard 6s cap elapses — never blocks a save
// indefinitely.
//
// TASK-1662 — returns { outcomes, timedOut } instead of undefined. A caller can
// now tell "we looked and found nothing" (NO_RESULT) from "we never got to
// look" (SUPERSEDED / timedOut). Resolving with nothing at all is precisely how
// the shared-timer bug stayed invisible.
//
// Drains in passes: superseding a lookup registers a NEW pending Promise, and a
// keystroke can land while we are already awaiting, so a single snapshot of the
// set can miss real queued work.
async function awaitPendingGeocodes() {
  const outcomes = [];
  if (_pendingGeocodes.size === 0) return { outcomes, timedOut: false };
  // TASK-9194 — was a hardcoded 6000 that had to stay above the short-link
  // resolve's total budget and had no way of knowing what that budget was.
  const deadline = Date.now() + PENDING_GEOCODE_WAIT_MS;
  for (let pass = 0; pass < 8; pass++) {
    if (_pendingGeocodes.size === 0) return { outcomes, timedOut: false };
    const remaining = deadline - Date.now();
    if (remaining <= 0) return { outcomes, timedOut: true };
    const batch = Array.from(_pendingGeocodes);
    let capTimer = null;
    const cap = new Promise(r => { capTimer = setTimeout(() => r(null), remaining); });
    const settled = await Promise.race([Promise.all(batch), cap]);
    if (capTimer) clearTimeout(capTimer);
    if (settled === null) return { outcomes, timedOut: true };
    outcomes.push(...settled);
  }
  // 8 passes without draining means something is generating lookups faster than
  // they finish. Report it as unfinished rather than pretending we waited.
  return { outcomes, timedOut: _pendingGeocodes.size > 0 };
}

// ── TASK-1617 — geocode on save ─────────────────────────────────────────────
// The on-blur geocode above is best-effort and misses constantly: it shares ONE
// module-level debounce timer across every waypoint row, so typing into the
// drop-off cancels the pick-up's pending lookup and resolves its promise
// without ever doing the work. Nothing throws, the save looks clean, and the
// stop is persisted with no coordinate. That is the mechanism behind "only 69%
// of waypoints carry lat/lng, and the missing end is usually the drop-off."
//
// This pass runs at save time, after the DOM has settled, and fills whatever is
// still missing. It is the backstop, not a replacement — on-blur still gives
// the dispatcher live feedback and populates the mileage readout as they type.
//
// Deliberate choices:
//  · SERVER-SIDE via the existing `geocodeAddress` callable, not the Maps JS
//    Geocoder that is already on the page. The callable keeps the key server
//    side, is rate-limited, and already strips country/state-level junk hits.
//  · NEVER BLOCK THE SAVE. Bounded by GEOCODE_SAVE_TIMEOUT_MS. On timeout,
//    failure, offline or rate-limit the job saves anyway with the coordinate
//    absent, and the dispatcher is told which stops did not resolve. A save
//    that strands a dispatcher during a Google outage is a worse bug than a
//    missing pin.
//  · ONLY WHAT'S MISSING. A waypoint that already has a plottable coordinate is
//    skipped entirely, so re-saving an unchanged job costs zero calls and a
//    coordinate a human placed by hand is never overwritten.
//  · VALIDATE WHAT COMES BACK, and prefer nothing over a guess.
// 6s to match awaitPendingGeocodes' existing cap — COD flagged that a Cloud
// Function cold start inside the save lifecycle is felt by the dispatcher. This
// only elapses when a stop genuinely has no coordinate yet; the common re-save
// of an unchanged job never reaches the network at all.
const GEOCODE_SAVE_TIMEOUT_MS = 6000;
const GEOCODE_SAVE_MAX_CALLS = 10; // cost ceiling for one save

async function geocodeMissingWaypoints(waypoints) {
  // TASK-9194 — `unreachable` is split out of `unresolved` on purpose. Both
  // stops save without a pin, but only one of them is the dispatcher's link's
  // fault, and telling someone to re-copy a URL that was already correct is
  // what this whole task exists to stop.
  const result = { filled: 0, unresolved: [], unreachable: [], attempted: 0 };
  if (!Array.isArray(waypoints) || waypoints.length === 0) return result;

  // Which stops actually need a lookup, and what would we ask for?
  const todo = [];
  waypoints.forEach((wp, i) => {
    if (!wp) return;
    if (plottableCoord(wp)) return;                 // already good — never touch
    const q = geocodeQueryFromUrl(wp.url);
    if (!q) {
      // TASK-4805 — `maps.app.goo.gl/xY9` carries no address, so
      // geocodeQueryFromUrl returns null and this branch used to `return` in
      // silence. That silence IS the bug: the stop never reached
      // result.unresolved either, so the warning built for exactly this case
      // could not fire, and 668 coordinate-less short links accumulated on live
      // TITS with the dispatcher told nothing.
      //
      // By the time we get here the resolve has already had its shot — saveJob
      // awaits it now — so a short link still sitting in the box is a stop that
      // WILL persist with no coordinate. Name it. Do NOT hand it to the
      // geocoder: it is a link, not an address, and a lookup would burn quota
      // to return nothing.
      if (isShortMapUrl(wp.url)) {
        // TASK-9194 — same stop, two very different things to say about it.
        const label = wp.name || `Destination ${i + 1}`;
        if (wp.pinFailReason === 'timeout') result.unreachable.push(label);
        else result.unresolved.push(label);
      }
      return;
    }
    todo.push({ i, q });
  });
  if (todo.length === 0) return result;

  // Cache hits are free — apply them without any network call.
  const remaining = [];
  for (const t of todo) {
    const hit = _geocodeCache[t.q];
    if (hit !== undefined) {
      const c = plottableCoord(hit);
      if (c) { _clearWaypointPinMark(waypoints[t.i], c); result.filled++; }
      else result.unresolved.push(waypoints[t.i].name || `Destination ${t.i + 1}`);
      continue;
    }
    remaining.push(t);
  }

  // One network call per DISTINCT address, capped.
  const distinct = [...new Set(remaining.map(t => t.q))].slice(0, GEOCODE_SAVE_MAX_CALLS);
  if (distinct.length > 0) {
    result.attempted = distinct.length;
    const lookups = distinct.map(async q => {
      try {
        const fn = httpsCallable(functions, 'geocodeAddress');
        const resp = await fn({ address: q });
        const { lat, lng } = resp.data || {};
        // The callable returns lat:null when it could only match loosely (its
        // Places-autocomplete fallback) — that is exactly the low-confidence
        // case, and plottableCoord rejects it along with anything out of range.
        _geocodeCache[q] = plottableCoord({ lat, lng }) || { lat: null, lng: null };
      } catch (err) {
        // Do NOT cache a transport failure as "no result" — the address may be
        // perfectly good and the next save should try again.
        console.warn('[Dispatch] Save-time geocode failed for one stop:', err?.message || err);
      }
    });
    const cap = new Promise(r => setTimeout(r, GEOCODE_SAVE_TIMEOUT_MS));
    await Promise.race([Promise.allSettled(lookups), cap]);
  }

  // Apply whatever resolved inside the window. Anything still missing stays
  // missing and gets named in the toast.
  for (const t of remaining) {
    const c = plottableCoord(_geocodeCache[t.q]);
    if (c) { _clearWaypointPinMark(waypoints[t.i], c); result.filled++; }
    else result.unresolved.push(waypoints[t.i].name || `Destination ${t.i + 1}`);
  }
  return result;
}

// TASK-9194 — this pass MUTATES the same waypoint objects collectWaypoints just
// stamped, so filling a coordinate here without clearing the mark would persist
// a job whose stop has a perfectly good pin AND a badge saying it has none.
// The whole point of the marker is that it is trustworthy.
function _clearWaypointPinMark(wp, c) {
  if (!wp) return;
  wp.lat = c.lat;
  wp.lng = c.lng;
  delete wp.pinStatus;
  delete wp.pinFailReason;
}

// ── TASK-191 Phase 2 — per-job geofence config (job-edit modal) ──────────────
// Holds the resolved center coords for the geofence address field. When the
// admin types an address we geocode it (reusing the geocodeAddress Cloud Fn,
// same as waypoints) and stash {lat,lng,source}. Blank → fall back to the first
// waypoint at save time (source 'waypoint'). Cleared on form reset/open.
let _jobGeofenceCoords = null; // { lat, lng, source: 'manual'|'geocoded' } | null

function toggleGeofenceSection() {
  const cb = document.getElementById('job-geofence-enabled');
  const body = document.getElementById('job-geofence-body');
  if (body) body.classList.toggle('hidden', !cb?.checked);
  refreshGeofenceCenterLine();
}

// Geocode the geofence center address via the same Cloud Function used for
// waypoints. Updates the readout + stashes coords. Never throws.
// TASK-9648 — the "which point does it use" answer now lives in ONE place, the
// center line (refreshGeofenceCenterLine); the readout only reports a failure.
async function geocodeJobGeofence() {
  const input = document.getElementById('job-geofence-address');
  const statusEl = document.getElementById('job-geofence-geocode-status');
  const readout = document.getElementById('job-geofence-coords-readout');
  const addr = (input?.value || '').trim();
  if (!addr) {
    // Blank = no override. Only drop a TYPED center — a saved center from an
    // edited job stays until someone types a different one (unchanged rule).
    if (_jobGeofenceCoords?.typed) _jobGeofenceCoords = null;
    if (statusEl) { statusEl.textContent = ''; }
    if (readout) readout.textContent = '';
    refreshGeofenceCenterLine();
    return;
  }
  if (statusEl) { statusEl.textContent = 'hourglass_empty'; statusEl.style.color = 'var(--text-secondary)'; }
  try {
    const geocodeAddressFn = httpsCallable(functions, 'geocodeAddress');
    const resp = await geocodeAddressFn({ address: addr });
    const { lat, lng, formattedAddress } = resp.data || {};
    if (typeof lat === 'number' && typeof lng === 'number') {
      _jobGeofenceCoords = { lat, lng, source: 'geocoded', label: formattedAddress || addr, typed: true };
      if (statusEl) { statusEl.textContent = 'check_circle'; statusEl.style.color = 'var(--status-success)'; }
      if (readout) readout.textContent = '';
    } else {
      if (_jobGeofenceCoords?.typed) _jobGeofenceCoords = null;
      if (statusEl) { statusEl.textContent = 'error_outline'; statusEl.style.color = 'var(--status-warning)'; }
      if (readout) readout.textContent = "Couldn't find that address. Using the point shown above.";
    }
  } catch (err) {
    console.warn('[Dispatch] geofence geocode failed:', err);
    if (_jobGeofenceCoords?.typed) _jobGeofenceCoords = null;
    if (statusEl) { statusEl.textContent = 'error_outline'; statusEl.style.color = 'var(--status-warning)'; }
    if (readout) readout.textContent = "Couldn't look up that address. Using the point shown above.";
  }
  refreshGeofenceCenterLine();
}

// Build the job.geofence object for saveJob, or null when disabled. Resolves the
// center per the data model: typed/geocoded coords first, else the first waypoint
// with coords, else (TASK-9648) the job's own Location when it IS a GPS pair.
// Returns { enabled:false } if enabled but no center can be resolved — the form's
// center line says so in amber before Save, so it is no longer silent.
function collectJobGeofence(waypoints) {
  const cb = document.getElementById('job-geofence-enabled');
  if (!cb?.checked) return { enabled: false }; // explicit disable persists
  const radiusFeet = parseInt(document.getElementById('job-geofence-radius')?.value || '500', 10) || 500;
  const center = _geofenceCenterFor(waypoints);
  if (!center) {
    // Enabled but no center — can't build a fence. Persist disabled so the
    // toggle state round-trips without silently flagging on a centerless fence.
    return { enabled: false };
  }
  const uid = window.AppState?.user?.uid || null;
  return {
    enabled: true,
    lat: center.lat, lng: center.lng,
    radiusFeet,
    source: center.source,
    configuredByUid: uid,
    configuredAt: Timestamp.now(),
  };
}

// TASK-9648 — the ONE center resolver, shared by the save path and the form's
// center line so what the dispatcher reads is exactly what gets saved.
//   1. a typed/geocoded or previously SAVED center (_jobGeofenceCoords)
//   2. the first stop with a pin
//   3. the Location field, only when it holds a GPS pair (or a maps link with one)
// Returns { lat, lng, source, kind, label } or null.
function _geofenceCenterFor(waypoints) {
  if (_jobGeofenceCoords && typeof _jobGeofenceCoords.lat === 'number' && typeof _jobGeofenceCoords.lng === 'number') {
    const typed = _jobGeofenceCoords.typed === true;
    return {
      lat: _jobGeofenceCoords.lat, lng: _jobGeofenceCoords.lng,
      source: _jobGeofenceCoords.source || 'manual',
      kind: typed ? 'address' : 'saved',
      label: _jobGeofenceCoords.label || ''
    };
  }
  const first = (waypoints || []).find(w => w && typeof w.lat === 'number' && typeof w.lng === 'number');
  if (first) return { lat: first.lat, lng: first.lng, source: 'waypoint', kind: 'stop', label: first.name || '' };
  const loc = _jobLocationCoords(document.getElementById('job-location')?.value);
  if (loc) {
    const near = _nearestNamedPlace(loc.lat, loc.lng, waypoints);
    return { lat: loc.lat, lng: loc.lng, source: 'location', kind: 'location', label: near ? near.name : '' };
  }
  return null;
}

// Populate the geofence section from a job's saved config (editJob path).
function populateGeofenceSection(job) {
  const cb = document.getElementById('job-geofence-enabled');
  const body = document.getElementById('job-geofence-body');
  const addr = document.getElementById('job-geofence-address');
  const radius = document.getElementById('job-geofence-radius');
  const statusEl = document.getElementById('job-geofence-geocode-status');
  const readout = document.getElementById('job-geofence-coords-readout');
  _jobGeofenceCoords = null;
  const gf = job?.geofence;
  const enabled = gf?.enabled === true;
  if (cb) cb.checked = enabled;
  if (body) body.classList.toggle('hidden', !enabled);
  if (addr) addr.value = '';
  if (statusEl) statusEl.textContent = '';
  if (radius) radius.value = String(gf?.radiusFeet || 500);
  if (enabled && typeof gf?.lat === 'number' && typeof gf?.lng === 'number') {
    // Preserve the saved center so re-saving without re-typing keeps it.
    _jobGeofenceCoords = { lat: gf.lat, lng: gf.lng, source: gf.source === 'geocoded' ? 'geocoded' : 'manual' };
  }
  if (readout) readout.textContent = '';
  refreshGeofenceCenterLine();
}

// ── TASK-9648 — Site section helpers (Location place line, device location,
// Open in Maps, geofence center line). No new provider: coordinates are matched
// against the tenant's savedLocations and this form's own stops, which is all
// the app already knows about a place. The geocodeAddress callable does NOT
// reverse-geocode (probed 2026-10-08: a "47.63, -103.19" query returns no
// result), so a bare pin with no nearby saved place reads "GPS pin".
const _JOB_PLACE_MATCH_MILES = 0.25;

// A Location value that holds a GPS pair (bare "lat, lng" or a maps link with
// one) → { lat, lng }; anything else (an address, a legal description) → null.
function _jobLocationCoords(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  return plottableCoord(extractCoordsFromUrl(raw));
}

function _fmtLatLng(c) {
  return `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
}

// The closest NAMED place within a quarter mile: a tenant saved location, or one
// of this form's own stops. Returns { name, kind: 'saved'|'stop', miles } or null.
function _nearestNamedPlace(lat, lng, waypoints) {
  let best = null;
  const consider = (name, c, kind) => {
    const n = String(name || '').trim();
    if (!n || !c) return;
    const miles = haversineMiles(lat, lng, c.lat, c.lng);
    if (!(miles <= _JOB_PLACE_MATCH_MILES)) return;
    if (!best || miles < best.miles) best = { name: n, kind, miles };
  };
  for (const loc of (DispatchState.savedLocations || [])) {
    if (!loc) continue;
    consider(loc.name, plottableCoord(loc) || plottableCoord(extractCoordsFromUrl(loc.url || '')), 'saved');
  }
  let wps = waypoints;
  if (!Array.isArray(wps)) { try { wps = collectWaypoints(); } catch (_) { wps = []; } }
  for (const w of (wps || [])) {
    if (!w) continue;
    consider(w.name, plottableCoord(w), 'stop');
  }
  return best;
}

// What the Site summary and the place line say about the Location field.
// null when the field is not a GPS pair (its own text is already readable).
function _jobLocationPlaceInfo() {
  const c = _jobLocationCoords(document.getElementById('job-location')?.value);
  if (!c) return null;
  const near = _nearestNamedPlace(c.lat, c.lng);
  return { lat: c.lat, lng: c.lng, coordsText: _fmtLatLng(c), name: near ? near.name : '', kind: near ? near.kind : '' };
}

function _renderJobLocationPlace() {
  const el = document.getElementById('job-location-place');
  if (!el) return;
  const info = _jobLocationPlaceInfo();
  if (!info) { el.classList.add('hidden'); el.innerHTML = ''; return; }
  const title = info.name
    ? (info.kind === 'saved' ? 'Saved location: ' : 'Destination: ') + info.name
    : 'GPS pin';
  el.innerHTML = `<span class="material-icons" aria-hidden="true">place</span><span><strong>${escapeHtml(title)}</strong><span class="job-site-place-sub">${escapeHtml(info.coordsText)}</span></span>`;
  el.classList.remove('hidden');
}

// oninput/onchange of #job-location. The group summary, the place line and the
// geofence center line are all painted from _refreshJobFormGroups.
function refreshJobLocationPlace() {
  _refreshJobFormGroups();
}

function refreshGeofenceCenterLine() {
  const on = !!document.getElementById('job-geofence-enabled')?.checked;
  const radiusSel = document.getElementById('job-geofence-radius');
  const rLabel = String(radiusSel?.selectedOptions?.[0]?.textContent || '').trim();
  const chip = document.getElementById('job-geofence-radius-chip');
  if (chip) chip.textContent = on && rLabel ? '· ' + rLabel : '';
  const el = document.getElementById('job-geofence-center-line');
  if (!el) return;
  let wps = [];
  try { wps = collectWaypoints(); } catch (_) { wps = []; }
  const c = _geofenceCenterFor(wps);
  if (!c) {
    el.className = 'job-gf-center is-warn';
    el.innerHTML = '<span class="material-icons" aria-hidden="true">warning_amber</span><span>No center point yet. Add a destination with a map pin, put GPS coordinates in Location, or type an address below. The geofence will not save without one.</span>';
    return;
  }
  const label = c.label ? ' — ' + c.label : '';
  const head = c.kind === 'address' ? 'Center: ' + (c.label || 'the address below')
    : c.kind === 'saved' ? 'Center: the point saved with this job'
    : c.kind === 'stop' ? 'Center: first destination' + label
    : "Center: this job's Location" + label;
  el.className = 'job-gf-center';
  el.innerHTML = `<span class="material-icons" aria-hidden="true">place</span><span><strong>${escapeHtml(head)}</strong><span class="job-site-place-sub">${escapeHtml(_fmtLatLng(c))}</span></span>`;
}

function useMyLocationForJob() {
  const input = document.getElementById('job-location');
  const btn = document.getElementById('btn-job-location-here');
  if (!input) return;
  if (!navigator.geolocation) {
    window.showToast?.("This device can't share its location.", 'error');
    return;
  }
  const original = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="material-icons rotating" aria-hidden="true">sync</span> Locating…'; }
  const restore = () => { if (btn) { btn.disabled = false; btn.innerHTML = original; } };
  navigator.geolocation.getCurrentPosition(async (pos) => {
    restore();
    const c = plottableCoord({ lat: pos?.coords?.latitude, lng: pos?.coords?.longitude });
    if (!c) { window.showToast?.("Couldn't get a location fix. Try again or type it in.", 'error'); return; }
    const next = `${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}`;
    const prev = String(input.value || '').trim();
    if (prev && prev !== next && !(await _confirm811('Replace location', 'Replace the current Location with where this device is now?', 'Replace', false))) return;
    input.value = next;
    // Bubbles to the group's oninput, which repaints the summary + place line.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    window.showToast?.('Location set to where this device is now.', 'success');
  }, (err) => {
    restore();
    window.showToast?.(err && err.code === 1
      ? 'Location is blocked for this site. Allow it in the browser, or type the location in.'
      : "Couldn't get a location fix. Try again or type it in.", 'error');
  }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
}

function openJobLocationInMaps() {
  const raw = String(document.getElementById('job-location')?.value || '').trim();
  if (!raw) { window.showToast?.('Add a location first.', 'info'); return; }
  const c = _jobLocationCoords(raw);
  const url = c
    ? `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`
    : getGoogleMapsUrl(raw);
  window.open(url, '_blank', 'noopener');
}

// TASK-10116 — the ONE place a stop's text becomes a link someone can tap.
// Returns an http(s) URL or ''. Used by the stop row's map icon (Add / Edit /
// Copy Job, Smart Job Builder, driver add-job form) AND by every screen that
// renders a SAVED stop link (Job Details, the stops pop-up, the driver quick
// view, the board pills in jobboard.js via window.DispatchModule).
//   * Anything that parses as a URL must be https:/http: (new URL lowercases
//     the scheme and drops tab/newline the way a browser does, so
//     "JavaScript:", "java\tscript:" and "data:" all come back ''). A link is
//     returned as typed — short links stay short (COD Domain + Code).
//   * A coordinate in the text → a Google Maps pin for it.
//   * Other text of 3+ characters with a letter or digit → a Maps search.
// Input is the address text only — never the stop name or the row's dataset
// coordinates (those can still be the OLD pin until a re-geocode lands).
// Never throws.
function waypointOpenHref(text) {
  try {
    const raw = String(text == null ? '' : text).trim();
    if (!raw) return '';
    let parsed = null;
    try { parsed = new URL(raw); } catch (_) { parsed = null; }
    if (parsed) return (parsed.protocol === 'https:' || parsed.protocol === 'http:') ? raw : '';
    const c = _jobLocationCoords(raw);
    if (c) return `https://www.google.com/maps/search/?api=1&query=${c.lat},${c.lng}`;
    if (raw.length >= 3 && /[A-Za-z0-9]/.test(raw)) return getGoogleMapsUrl(raw);
    return '';
  } catch (_) {
    return '';
  }
}

// TASK-10116 — point the row's map icon at whatever is in its address box right
// now. href goes in with setAttribute (never innerHTML); no href = hidden
// (CSS keys off [href]) and out of the tab order.
function _refreshWaypointOpen(urlInput) {
  if (!urlInput || typeof urlInput.closest !== 'function') return;
  const wrap = urlInput.closest('.waypoint-url-wrap');
  const a = wrap ? wrap.querySelector('.waypoint-open') : null;
  if (!a) return;
  const href = waypointOpenHref(urlInput.value);
  if (href) {
    a.setAttribute('href', href);
    a.removeAttribute('aria-disabled');
    a.removeAttribute('tabindex');
  } else {
    a.removeAttribute('href');
    a.setAttribute('aria-disabled', 'true');
    a.setAttribute('tabindex', '-1');
  }
}

let _dirDebounce = null;
let _mileageSeq = 0; // TASK-10291 — a driving-distance answer applies only to the stops it was asked for
function _resetWaypointMileage() {
  clearTimeout(_dirDebounce);
  _mileageSeq++;
  const el = document.getElementById('waypoint-mileage-estimate');
  if (el) el.style.display = 'none';
}
function updateWaypointMileage() {
  // TASK-9648 — a stop edit can move the geofence center ("first stop") and the
  // name the Location pin resolves to; repaint the Site group with it.
  _refreshJobFormGroups();
  const container = document.getElementById('waypoint-mileage-estimate');
  const textEl = document.getElementById('waypoint-mileage-text');
  if (!container || !textEl) return;

  const rows = document.querySelectorAll('#job-waypoints-list .waypoint-row'); // TASK-10291 — this form's stops only
  const wps = [];
  rows.forEach(row => {
    const urlInput = row.querySelector('.waypoint-url');
    const url = urlInput?.value.trim() || '';
    const name = row.querySelector('.waypoint-name')?.value.trim() || '';
    if (url || name) {
      let coords = extractCoordsFromUrl(url);
      // Fall back to geocoded coords stored in data attributes
      if (!coords.lat && urlInput?.dataset.lat) {
        coords = { lat: parseFloat(urlInput.dataset.lat), lng: parseFloat(urlInput.dataset.lng) };
      }
      wps.push({ name, url, ...coords });
    }
  });

  // Quick haversine estimate first (instant)
  const hMiles = calcRouteMileage(wps);
  if (hMiles) {
    const estMin = Math.round(hMiles / 45 * 60);
    const hrs = Math.floor(estMin / 60);
    const mins = estMin % 60;
    const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
    textEl.textContent = `~${hMiles.toFixed(1)} mi · ~${timeStr} (${wps.length} destinations)`;
    container.style.display = 'flex';
  } else {
    _resetWaypointMileage();
    return;
  }

  // Then fetch real driving distance (debounced)
  // TASK-1615 — validate before the getDirections call.
  const coordWps = wps.map(w => { const c = plottableCoord(w); return c ? { ...w, ...c } : null; }).filter(Boolean);
  clearTimeout(_dirDebounce);
  const seq = ++_mileageSeq;
  if (coordWps.length < 2) return;
  _dirDebounce = setTimeout(async () => {
    try {
      // Maps endpoints now require a Firebase ID token (safety-audit M3).
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) return;
      const resp = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/getDirections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ waypoints: coordWps.map(w => ({ lat: w.lat, lng: w.lng })) })
      });
      const data = await resp.json();
      if (seq !== _mileageSeq) return; // TASK-10291 — the stops (or the job) changed while this was in flight
      if (data.totalMiles) {
        const secs = data.totalDurationSeconds || 0;
        const hrs = Math.floor(secs / 3600);
        const mins = Math.round((secs % 3600) / 60);
        const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
        textEl.textContent = `${data.totalMiles.toFixed(1)} mi · ${timeStr} driving (${wps.length} destinations)`;
        container.style.display = 'flex';
      }
    } catch { /* silently fall back to haversine estimate shown above */ }
  }, 800);
}

async function clearAllWaypoints() {
  const list = document.getElementById('job-waypoints-list');
  if (!list) return;
  if (list.children.length === 0) return;
  const confirmed = await window.showConfirm('Clear Destinations', 'Clear all destinations from this job?', { confirmText: 'Clear' });
  if (!confirmed) return;
  list.innerHTML = '';
  updateWaypointMileage(); // TASK-10291 — no stops, no mileage line
  window.showToast?.('Destinations cleared', 'success');
}

function removeWaypoint(btn) {
  const row = btn.closest('.waypoint-row');
  if (row) row.remove();
  updateWaypointMileage();
}

// Extract lat/lng from common Google Maps, Apple Maps, and coordinate URL formats
// TASK-1617 — every branch of extractCoordsFromUrl funnels its match through
// here. Two rules, and the second one is the whole ticket:
//   1. RANGE — the same bounds plottableCoord() enforces, applied at the PARSE
//      site so an impossible pair never even becomes a candidate.
//   2. DECIMALS on the ambiguous branches — a `?q=` / `daddr=` param almost
//      always carries a STREET ADDRESS, not a coordinate. `?q=5304+2nd+Ave+W`
//      parsed as lat=5304, lng=2 and that pair was written straight into the
//      job doc. Measured on live data: ALL 8 corrupt waypoints came from the
//      query-param branch, every one of them a house number paired with the
//      leading digit of the next address token (5304/105, 3979/144, 12788/61…).
//      Real coordinates in these params always carry a decimal point; house
//      numbers never do. Requiring the decimal is what separates them.
// A rejected match FALLS THROUGH to the next branch rather than returning null,
// so a URL carrying both a junk `?q=` and a good `@` still resolves correctly.
function coordPairOrNull(latStr, lngStr, requireDecimal) {
  const lat = parseFloat(latStr), lng = parseFloat(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (requireDecimal && !(/^-?\d+\.\d+$/.test(String(latStr)) && /^-?\d+\.\d+$/.test(String(lngStr)))) return null;
  if (lat > 90 || lat < -90) return null;
  if (lng > 180 || lng < -180) return null;
  if (Math.abs(lat) < MAP_NULL_ISLAND_EPS && Math.abs(lng) < MAP_NULL_ISLAND_EPS) return null;
  return { lat, lng };
}

function extractCoordsFromUrl(url) {
  if (!url) return { lat: null, lng: null };
  let c;
  // TASK-9497 (COD r2) — a Google place URL carries the PIN as !3d<lat>!4d<lng>
  // and the VIEWPORT centre as @lat,lng. Read the pin first (server twins too).
  let m = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (m && (c = coordPairOrNull(m[1], m[2], true))) return c;
  // Format: @47.1234,-101.5678 or /place/47.1234,-101.5678
  m = url.match(/@(-?\d+\.?\d*),\+?(-?\d+\.?\d*)/);
  if (m && (c = coordPairOrNull(m[1], m[2], true))) return c;
  // Format: /search/48.150093,+-103.588998 (expanded short URLs)
  m = url.match(/\/search\/([-\d.]+),\+?([-\d.]+)/);
  if (m && (c = coordPairOrNull(m[1], m[2], true))) return c;
  // Format: ?q=47.1234,-101.5678 or &ll=47.1234,-101.5678 (Google + Apple Maps)
  m = url.match(/[?&](?:q|ll|center|destination|origin|sll|daddr|saddr)=([-\d.]+)[,+]([-\d.]+)/);
  if (m && (c = coordPairOrNull(m[1], m[2], true))) return c;
  // Format: /dir/47.1234,-101.5678 — pattern already forces 3+ decimals.
  m = url.match(/\/dir\/.*?(-?\d{2,3}\.\d{3,}),\+?(-?\d{2,3}\.\d{3,})/);
  if (m && (c = coordPairOrNull(m[1], m[2], false))) return c;
  // Apple Maps: maps.apple(.com) with ll= or t= params containing coords.
  // TASK-1337(C) — a `maps.apple/p/<id>` share link carries NO coords at all,
  // so it correctly falls through to null here and gets resolved server-side.
  m = url.match(/maps\.apple(?:\.com)?.*[?&]ll=([-\d.]+),([-\d.]+)/);
  if (m && (c = coordPairOrNull(m[1], m[2], true))) return c;
  // Bare coordinates: "48.1234, -103.5678" — pattern already forces 3+ decimals.
  m = url.trim().match(/^(-?\d{1,3}\.\d{3,})[,\s]+(-?\d{1,3}\.\d{3,})$/);
  if (m && (c = coordPairOrNull(m[1], m[2], false))) return c;
  return { lat: null, lng: null };
}

async function toggleSaveWaypoint(btn) {
  const row = btn.closest('.waypoint-row');
  if (!row) return;
  const tenant = DispatchState.tenantId;
  if (!tenant) return;
  const name = row.querySelector('.waypoint-name')?.value.trim();
  const urlInput = row.querySelector('.waypoint-url');
  const url = urlInput?.value.trim() || '';
  const icon = btn.querySelector('.material-icons');

  if (!name) {
    window.showToast?.('Enter a location name first', 'error');
    return;
  }

  // If already saved, unsave it
  const savedId = row.dataset.savedLocId;
  if (savedId) {
    try {
      await deleteDoc(doc(db, 'tenants', tenant, 'savedLocations', savedId));
      delete row.dataset.savedLocId;
      icon.textContent = 'bookmark_border';
      btn.style.color = 'var(--text-muted)';
      btn.title = 'Click to save this destination to your locations list';
      window.showToast?.(`"${name}" removed from saved locations`, 'success');
    } catch (e) {
      console.error('[Dispatch] Unsave waypoint error:', e);
      window.showToast?.('Error removing location', 'error');
    }
    return;
  }

  // Check if already saved by name
  const existing = (DispatchState.savedLocations || []).find(l => l.name?.toLowerCase() === name.toLowerCase());
  // TASK-9631 F3 — a NAME match is not an id binding. This used to bind the row
  // to the matching saved location, so the next click on the same bookmark
  // deleted that shared record (gate code, contact and all) — from a job stop
  // that merely had the same name. Delete stays reachable only for a row bound
  // BY ID (picked from the saved list, or saved from this row above).
  if (existing) {
    icon.textContent = 'bookmark';
    btn.style.color = '#3B82F6';
    btn.title = 'Already in your saved locations — edit or remove it from Saved Locations';
    window.showToast?.(`"${name}" is already in your saved locations. Edit or remove it from Saved Locations.`, 'info');
    return;
  }

  // Save it
  try {
    let coords = extractCoordsFromUrl(url);
    if (!coords.lat && urlInput?.dataset.lat) {
      coords = { lat: parseFloat(urlInput.dataset.lat), lng: parseFloat(urlInput.dataset.lng) };
    }
    // 10/8 — a stop whose share link never resolved was bookmarked with a
    // null pin and a "saved" toast. Try once more; say so if it still fails.
    let _pin = plottableCoord(coords);
    let _pinNote = null;
    if (!_pin && url) {
      const found = await locateSavedLocationPin(url, '');
      _pin = found.coord;
      if (!_pin) _pinNote = found.timedOut ? SAVED_LOC_PIN_TIMEOUT_MSG : SAVED_LOC_NO_PIN_MSG;
    }
    // TASK-9421 — saved FROM a job, so it defaults to that job's customer.
    const _custId = _ppSelectedCustomerId();
    const docRef = await addDoc(collection(db, 'tenants', tenant, 'savedLocations'), {
      name,
      url: url || '',
      lat: _pin ? _pin.lat : null,
      lng: _pin ? _pin.lng : null,
      customerIds: _custId ? [_custId] : [],
      source: 'manual',
      createdAt: Timestamp.now()
    });
    row.dataset.savedLocId = docRef.id;
    icon.textContent = 'bookmark';
    btn.style.color = '#3B82F6';
    btn.title = 'Saved to your locations list — click to remove';
    if (_pinNote) window.showToast?.(`"${name}" saved — ${_pinNote}`, 'warning', null, 9000);
    else window.showToast?.(`"${name}" saved`, 'success');
  } catch (e) {
    console.error('[Dispatch] Save waypoint error:', e);
    window.showToast?.('Error saving location', 'error');
  }
}

// TASK-9464 — which stops on a just-saved job deserve a NEW row in
// tenants/{tid}/waypoints. That collection is a per-job-use log (the TASK-4783
// "PLACES FROM PAST JOBS" picker reads it), so a stop earns a row when it is
// NEW or CHANGED versus the job's pre-edit `waypoints[]` — never merely because
// the job was re-saved. Identity = name + url + lat/lng (coords compared at 6
// decimals, ~0.1 m, so a float round-trip through a data-attribute is not a
// "change"). Multiset, not set: a round trip A→B→A that already had both A's
// writes nothing, and one that gains a second A writes exactly one row.
// Pure: no DOM, no Firestore — tests/dispatch-waypoint-edit-dedupe.unit.cjs.
function waypointsNewOrChanged(nextWaypoints, priorWaypoints) {
  const coordKey = (v) => {
    if (v === null || v === undefined || v === '') return '';
    const n = Number(v);
    return Number.isFinite(n) && n !== 0 ? n.toFixed(6) : '';
  };
  const keyOf = (wp) => [
    String((wp && wp.name) || '').trim(),
    String((wp && wp.url) || '').trim(),
    coordKey(wp && wp.lat),
    coordKey(wp && wp.lng),
  ].join('\u0001');
  const remaining = new Map();
  for (const wp of (Array.isArray(priorWaypoints) ? priorWaypoints : [])) {
    if (!wp || (!wp.name && !wp.url)) continue;
    const k = keyOf(wp);
    remaining.set(k, (remaining.get(k) || 0) + 1);
  }
  const out = [];
  for (const wp of (Array.isArray(nextWaypoints) ? nextWaypoints : [])) {
    if (!wp) continue;
    const k = keyOf(wp);
    const left = remaining.get(k) || 0;
    if (left > 0) { remaining.set(k, left - 1); continue; }
    out.push(wp);
  }
  return out;
}

function collectWaypoints() {
  const rows = document.querySelectorAll('.waypoint-row');
  const waypoints = [];
  // TASK-9541 — ids already handed out on this form. A row with no id (a row
  // some future path built without addWaypoint) or one repeating an earlier
  // row's id gets a new one, written back onto the row so it stays put.
  const seenIds = new Set();
  rows.forEach(row => {
    const name = row.querySelector('.waypoint-name')?.value.trim() || '';
    const urlInput = row.querySelector('.waypoint-url');
    const url = urlInput?.value.trim() || '';
    if (name || url) {
      let coords = extractCoordsFromUrl(url);
      // Fall back to geocoded coords stored in data attributes
      if (coords.lat == null && urlInput?.dataset.lat) {
        coords = { lat: parseFloat(urlInput.dataset.lat), lng: parseFloat(urlInput.dataset.lng) };
      }
      // TASK-1617 — THE choke point. Every coordinate that reaches a job doc
      // passes through this line, so validating here means no write path in the
      // app can persist an unplottable point, whatever produced it. Rejected
      // pairs are stored as null (absent), never as the raw garbage: the map,
      // the mileage math and the per-mile billing quantity all handle a missing
      // coordinate correctly and none of them handle a wrong one.
      const vc = plottableCoord(coords);
      let stopId = row.dataset ? row.dataset.stopId : '';
      if (!isValidStopId(stopId) || seenIds.has(stopId)) {
        stopId = mintStopId();
        if (row.dataset) row.dataset.stopId = stopId;
      }
      seenIds.add(stopId);
      const wp = { id: stopId, name, url, lat: vc ? vc.lat : null, lng: vc ? vc.lng : null };
      // TASK-9540 — the category the stop was picked as (absent = none).
      const catId = row.dataset ? row.dataset.categoryId : '';
      if (isValidStopCategoryId(catId)) wp.categoryId = catId;
      // TASK-9497 — keep the pin's provenance while the coordinate is unchanged.
      if (vc && row.dataset && row.dataset.geo) {
        try {
          const g = JSON.parse(row.dataset.geo);
          if (g && Math.abs(g.lat - vc.lat) < 1e-6 && Math.abs(g.lng - vc.lng) < 1e-6) wp.geo = { src: g.src, conf: g.conf, at: g.at };
        } catch (_) { /* not ours — drop it */ }
      }
      // ── 🔴 TASK-9194 — A STOP THAT SAVES WITHOUT A PIN MUST SAY SO IN THE
      // DOCUMENT, not only in a toast that is gone in four seconds.
      //
      // THE DECISION, and why it is not "block the save": a dispatcher building
      // a job under time pressure, on the cell signal an oilfield actually has,
      // must not be locked out of saving because OUR map lookup timed out. That
      // is the TASK-428 shape — a tightening whose error lands on the one person
      // who cannot do anything about it. A job with a known-unpinned stop is far
      // more useful than no job. Nor is a silent background retry enough on its
      // own: it leaves nothing durable behind, so an operator who was not
      // looking at the screen at that moment never learns.
      //
      // So: SAVE IT, AND MARK IT. `pinStatus` rides with the waypoint, survives
      // reload, and is what the board badge and the route planner read. The
      // operator finds out by looking at the job — not from a driver on the road
      // with no navigation. `pinFailReason` carries WHICH of the three outcomes
      // it was, so the warning can stop calling a timeout a bad link.
      //
      // This is derived, never remembered: it is recomputed from the row on
      // every save, so a stop that later resolves simply stops carrying it.
      if (!vc) {
        wp.pinStatus = 'unpinned';
        const why = urlInput && urlInput.dataset ? urlInput.dataset.pinFail : '';
        if (why) wp.pinFailReason = why;
      }
      waypoints.push(wp);
    }
  });
  return waypoints;
}

// TASK-9540 — `purgeWaypoints` (a whole-collection client delete of the
// per-job-use `waypoints` log, exported with no UI caller) was removed. That
// log is the only record of where past jobs went; "deleting" a past place is a
// view concern (hide it), never a delete of the rows.

// ============================================
// TASK-4783 — PLACES FROM PAST JOBS (dedupe by COORDINATE, then scope by customer)
// ============================================
// `tenants/{tid}/waypoints` stores one row per JOB-USE, not one per place. On
// live TITS that is 2,541 rows for 415 real places — roughly 6x duplication —
// and the names are worthless as keys: "drop off" x588, "pick up" x306,
// "pickup" x302, "location" x141, "pu" x60. Filtering THAT by customer still
// leaves a dispatcher staring at 588 things called "drop off", which is why the
// dedupe has to come first and the axis has to be the coordinate.
//
// 🔴 THIS IS A VIEW CONCERN. Nothing here writes, merges or deletes a waypoint.
// The per-job rows are the only record of where a specific job actually went,
// and their `jobId` is what makes the customer derivable at all.
//
// TOLERANCE — 25 m, chosen against the live distribution, not by feel:
//   * These coordinates come from Google/Apple Maps PLACE links, not GPS traces,
//     so a repeat visit to the same yard writes the IDENTICAL pair. Exact match
//     alone already collapses 1,804 coordinate-bearing rows to 449 coordinates;
//     1,680 of those rows sit on a coordinate that is used more than once.
//   * The tolerance therefore only has to absorb "same place, slightly different
//     pin drop". Nearest-neighbour distances between the 449 distinct coordinates:
//     6 pairs at 1-5 m, 11 at 5-10 m, 55 at 10-25 m — then 45 more at 25-50 m.
//   * Above 25 m the merges start being WRONG. At 50 m the clustering joins
//     "west yard pick up" with "t&e williston drop off" — two different
//     companies' facilities. That is exactly the yard-and-its-gate case, and it
//     bites at 50 m. 25 m is still far wider than consumer pin-drop error.
//   Result on live TITS: 25 m -> 415 places (50 m would give 382, with 3 more
//   cross-facility merges bought for 33 fewer rows — a bad trade).
//
// Clustering is COMPLETE-LINKAGE greedy: a row joins the first cluster it is
// within tolerance of EVERY member of. So a cluster's diameter is <= 25 m, and
// it can never chain-drift across a yard the way single-link clustering does.
// (This started as seed-anchored — first cluster whose SEED is within tolerance.
// COD's Code Reviewer showed that still merges a yard and a gate 40 m apart when
// a third row lands at the midpoint and seeds first: 20 m to each, both join.
// Seed-anchoring bounds the diameter at 2x tolerance, which means "25 m" did not
// mean 25 m. Complete linkage is the guarantee the number claims to be.)
// Rows are processed newest-first with the doc id as tiebreak, so the result is
// deterministic regardless of the order Firestore hands them back.
//
// 🔴 COORDINATE-LESS ROWS ARE NEVER MERGED BY POSITION. 737 of the 2,541 rows
// (29%) have no coordinate — 267 distinct `maps.app.goo.gl` short links plus
// typed free text. A short link carries no coordinate; it has to be FOLLOWED.
// Treating them as all-equal would plant every one of them on top of each other.
// They are grouped by EXACT link/text identity only (an identical share link IS
// the same place — that is identity, not proximity) and shown in their own
// clearly-labelled section, so a dispatcher can still reuse them.
const PAST_PLACE_TOLERANCE_M = 25;
// Cap the scan the way findPastJobLocations already caps savedLocations. TITS is
// 2,541 rows today; a tenant 10x that must not pull 25,000 docs into a picker.
// Truncation is SURFACED in the UI, never silent.
const PAST_PLACE_SCAN_LIMIT = 3000;

// Labels that name the LEG, not the PLACE. A cluster whose every label is one of
// these has no human name at all, so the UI shows the customer + use count +
// coordinate instead of pretending "drop off" identifies somewhere.
const PAST_PLACE_GENERIC_LABELS = new Set([
  '', 'pick up', 'pickup', 'pick-up', 'pu', 'p/u', 'pick', 'pick up from',
  'drop off', 'dropoff', 'drop-off', 'drop', 'do', 'd/o', 'deliver', 'delivery', 'del',
  'location', 'loc', 'site', 'stop', 'load', 'unload', 'start', 'end', 'destination',
]);

// Trailing separators are noise from copy/paste ("Drop off -", "pick up –").
function _ppNormalizeLabel(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/^[\s\-–—.,:;_]+|[\s\-–—.,:;_]+$/g, '')
    .trim();
}

function _ppIsGenericLabel(name) {
  return PAST_PLACE_GENERIC_LABELS.has(_ppNormalizeLabel(name));
}

// Metres between two {lat,lng}. Built on the same haversine the route mileage
// uses, so there is one distance implementation in this file, not two.
function _ppMetersBetween(a, b) {
  return haversineMiles(a.lat, a.lng, b.lat, b.lng) * 1609.344;
}

// The coordinate for a stored row, or null.
// READ-TIME RECOVERY (no write): a row saved before the URL parser was hardened
// can carry a parseable coordinate in its url while lat/lng sit null. Re-parsing
// here recovers 9 live TITS rows without touching a single document. It runs
// through plottableCoord like every other coordinate in this file, so a garbage
// pair can never enter the index.
function _ppRowCoord(row) {
  const stored = plottableCoord(row);
  if (stored) return stored;
  if (!row || !row.url) return null;
  return plottableCoord(extractCoordsFromUrl(row.url));
}

// Identity key for a row that has NO coordinate. An identical share link is the
// same place; identical typed text is the same place. Nothing else is claimed.
function _ppIdentityKey(row) {
  const url = String((row && row.url) || '').trim();
  if (url) return 'url:' + url;
  return 'name:' + _ppNormalizeLabel(row && row.name);
}

// A url worth handing to addWaypoint. Prefer a real link the driver can open;
// skip short links when we already resolved a coordinate (addWaypoint would fire
// a needless short-url resolve), and synthesise a coordinate link as the floor.
function _ppPickUrl(rows, coord) {
  const direct = rows.find(r => r.url && !isShortMapUrl(r.url));
  if (direct) return direct.url;
  if (coord) return `https://www.google.com/maps?q=${coord.lat},${coord.lng}`;
  const any = rows.find(r => r.url);
  return any ? any.url : '';
}

// Pick the human name for a cluster: most-used specific label wins, ties go to
// the most recent. If every label is generic the cluster is flagged so the UI
// can be honest about it instead of showing a 588-way "Drop off".
function _ppPickName(rows) {
  const tally = new Map(); // normalized -> { display, count, lastMs }
  for (const r of rows) {
    const norm = _ppNormalizeLabel(r.name);
    if (!norm) continue;
    const cur = tally.get(norm) || { display: String(r.name || '').trim(), count: 0, lastMs: 0 };
    cur.count++;
    if ((r.createdAtMs || 0) >= cur.lastMs) { cur.lastMs = r.createdAtMs || 0; cur.display = String(r.name || '').trim(); }
    tally.set(norm, cur);
  }
  const entries = [...tally.entries()];
  const specific = entries.filter(([norm]) => !PAST_PLACE_GENERIC_LABELS.has(norm));
  const pool = specific.length ? specific : entries;
  if (!pool.length) return { name: 'Unnamed destination', genericOnly: true };
  pool.sort((a, b) => (b[1].count - a[1].count) || (b[1].lastMs - a[1].lastMs));
  return { name: pool[0][1].display || 'Unnamed destination', genericOnly: specific.length === 0 };
}

// PURE. rows: [{ id, name, url, lat, lng, jobId, createdAtMs }]
// customerByJobId: Map|object jobId -> customerId (may be partial; a row whose
// customer cannot be resolved is still indexed, just not customer-scopable).
function _ppBuildPlaceIndex(rows, options) {
  const opts = options || {};
  const tol = Number.isFinite(opts.toleranceMeters) ? opts.toleranceMeters : PAST_PLACE_TOLERANCE_M;
  // Duck-typed, not `instanceof Map`: a Map built in another realm (the vm the
  // unit test runs in) fails instanceof and would silently drop every customer.
  const custMap = (opts.customerByJobId && typeof opts.customerByJobId.get === 'function')
    ? opts.customerByJobId
    : new Map(Object.entries(opts.customerByJobId || {}));

  // Deterministic order: newest first, doc id as the tiebreak. The seed of every
  // cluster is therefore the most recent visit, and two runs over the same data
  // always produce the same clusters.
  const ordered = (rows || []).slice().sort((a, b) =>
    ((b.createdAtMs || 0) - (a.createdAtMs || 0)) || String(a.id).localeCompare(String(b.id)));

  const clusters = [];
  const linkGroups = new Map();
  let mappable = 0, recoveredFromUrl = 0;

  for (const row of ordered) {
    const coord = _ppRowCoord(row);
    if (coord) {
      mappable++;
      if (!plottableCoord(row)) recoveredFromUrl++;
      // COMPLETE LINKAGE, not seed-anchored: a row joins a cluster only if it is
      // within tolerance of EVERY point already in it. That makes the cluster's
      // diameter <= tolerance, full stop.
      //
      // 🔴 COD's Code Reviewer caught why seed-anchored was not enough. With a
      // seed and a 25 m radius, two stops 40 m apart still merge if a THIRD row
      // happens to sit at the midpoint and is the newest — 20 m to each, both
      // join, and the yard and its gate are one place after all. The diameter
      // bound was 2x tolerance, so "25 m" never actually meant 25 m. It does now.
      let hit = null;
      for (const c of clusters) {
        let fits = true;
        for (const p of c.pts) { if (_ppMetersBetween(p, coord) > tol) { fits = false; break; } }
        if (fits) { hit = c; break; }
      }
      if (!hit) { hit = { lat: coord.lat, lng: coord.lng, pts: [], rows: [] }; clusters.push(hit); }
      hit.pts.push(coord);
      hit.rows.push(row);
    } else {
      const key = _ppIdentityKey(row);
      if (!linkGroups.has(key)) linkGroups.set(key, { key, rows: [] });
      linkGroups.get(key).rows.push(row);
    }
  }

  const finish = (bucket, coord, key) => {
    const rowsIn = bucket.rows;
    const picked = _ppPickName(rowsIn);
    const customerIds = [];
    const jobIds = [];
    let lastUsedMs = 0;
    for (const r of rowsIn) {
      if (r.jobId && !jobIds.includes(r.jobId)) jobIds.push(r.jobId);
      const cid = r.jobId ? custMap.get(r.jobId) : null;
      if (cid && !customerIds.includes(cid)) customerIds.push(cid);
      if ((r.createdAtMs || 0) > lastUsedMs) lastUsedMs = r.createdAtMs || 0;
    }
    return {
      key,
      name: picked.name,
      genericOnly: picked.genericOnly,
      lat: coord ? coord.lat : null,
      lng: coord ? coord.lng : null,
      url: _ppPickUrl(rowsIn, coord),
      useCount: rowsIn.length,
      jobCount: jobIds.length,
      lastUsedMs,
      customerIds,
      jobIds,
    };
  };

  const byUse = (a, b) => (b.useCount - a.useCount) || (b.lastUsedMs - a.lastUsedMs) || a.name.localeCompare(b.name);
  const places = clusters.map((c, i) => finish(c, { lat: c.lat, lng: c.lng }, 'p' + i)).sort(byUse);
  const linkOnly = [...linkGroups.values()].map((g, i) => finish(g, null, 'l' + i)).sort(byUse);

  return {
    places,
    linkOnly,
    stats: {
      rows: ordered.length,
      mappable,
      unmappable: ordered.length - mappable,
      recoveredFromUrl,
      places: places.length,
      linkOnly: linkOnly.length,
      toleranceMeters: tol,
    },
  };
}

// Does this place belong to the given customer? A place used by several
// customers shows under each of them — that is real, not a bug.
function _ppMatchesCustomer(place, customerId) {
  if (!customerId) return true;
  return Array.isArray(place.customerIds) && place.customerIds.includes(customerId);
}

function _ppMatchesFilter(place, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (place.name || '').toLowerCase().includes(needle)
      || (place.url || '').toLowerCase().includes(needle);
}

// ---- loading (impure) -------------------------------------------------------
// COST, first time the picker is opened in a session, on live TITS:
//   * 2,541 waypoint reads (capped at PAST_PLACE_SCAN_LIMIT).
//   * The customer comes from `jobId -> job.customerId`, NO schema change. 267 of
//     the 539 referenced jobs are already in DispatchState.jobs (the 90-day admin
//     window) and cost nothing; the remaining ~272 are read individually,
//     pipelined 30 at a time. 40 of those jobs have been deleted and simply
//     resolve to no customer.
// So ~2,813 document reads once per session, then cached. It is deliberately
// LAZY — nothing loads until a dispatcher actually opens the picker — and the
// cache is dropped after a job save, because that save just wrote new waypoints.
function _ppInvalidateCache() {
  DispatchState._pastPlaceIndex = null;
  DispatchState._pastPlaceRows = null;
  DispatchState._pastPlaceCustomerByJob = null;
  DispatchState._pastPlaceTenantId = null;
  DispatchState._pastPlaceStatus = 'idle';
}

// 🔴 CROSS-TENANT LEAK GUARD (COD Code Reviewer). Firebase Auth is one session
// per ORIGIN and DispatchState survives a tenant switch in the same tab, so a
// cache with no tenant stamp would show the PREVIOUS tenant's yards to the next
// one. Every read of the cache goes through here.
function _ppCacheIsForCurrentTenant() {
  return !!DispatchState.tenantId && DispatchState._pastPlaceTenantId === DispatchState.tenantId;
}

// After a job save, the rows that were just written are already in hand — fold
// them into the cached index instead of throwing 2,541 documents away and
// re-reading them on the next picker open. A dispatcher who saves ten jobs and
// checks the picker each time would otherwise burn ~25,000 reads for data the
// session already had. Falls back to a plain invalidate when there is no cache
// to extend.
function _ppNoteSavedWaypoints(savedWaypoints, jobId, customerId) {
  try {
    if (!_ppCacheIsForCurrentTenant() || !Array.isArray(DispatchState._pastPlaceRows)) {
      _ppInvalidateCache();
      return;
    }
    const now = Date.now();
    for (const wp of (savedWaypoints || [])) {
      DispatchState._pastPlaceRows.push({
        // A synthetic id: these rows are never written back, and the id is only
        // ever used as a sort tiebreak.
        id: '_local_' + now + '_' + DispatchState._pastPlaceRows.length,
        name: wp.name || '', url: wp.url || '',
        lat: wp.lat, lng: wp.lng, jobId: jobId || null, createdAtMs: now,
      });
    }
    if (jobId && customerId) DispatchState._pastPlaceCustomerByJob.set(jobId, customerId);
    DispatchState._pastPlaceIndex = _ppBuildPlaceIndex(DispatchState._pastPlaceRows, {
      toleranceMeters: PAST_PLACE_TOLERANCE_M,
      customerByJobId: DispatchState._pastPlaceCustomerByJob,
    });
    DispatchState._pastPlaceIndex.stats.truncated = !!DispatchState._pastPlaceTruncated;
  } catch (e) {
    // Never let bookkeeping break a save. A dropped cache costs one re-read.
    console.warn('[Dispatch] TASK-4783 place-index update failed, invalidating instead', e);
    _ppInvalidateCache();
  }
}

async function _ppResolveCustomers(jobIds, tenantId) {
  const tid = tenantId || DispatchState.tenantId;
  const map = new Map();
  const missing = [];
  for (const jid of jobIds) {
    const job = (DispatchState.jobs || []).find(j => j.id === jid);
    if (job) { if (job.customerId) map.set(jid, job.customerId); }
    else missing.push(jid);
  }
  // Per-id getDoc rather than a `documentId() in [...]` query: documentId is not
  // re-exported from firebase-config.js and clock.js:960/7017 already set the
  // precedent of not adding it. Same read count either way (one read per job),
  // pipelined 30 at a time. A deleted job simply resolves to no customer.
  for (let i = 0; i < missing.length; i += 30) {
    const chunk = missing.slice(i, i + 30);
    const settled = await Promise.allSettled(chunk.map(jid =>
      getDoc(doc(db, 'tenants', tid, 'jobs', jid))
    ));
    settled.forEach((res, n) => {
      if (res.status !== 'fulfilled') {
        // A failed read costs customer scoping for that row, not the whole list.
        console.warn('[Dispatch] TASK-4783 job lookup failed', chunk[n], res.reason);
        return;
      }
      if (!res.value.exists()) return;
      const c = res.value.data().customerId;
      if (c) map.set(chunk[n], c);
    });
  }
  return map;
}

let _ppLoadPromise = null;

function _ppEnsureLoaded() {
  // Tenant switched under a cached index? Drop it before anything can read it.
  if (DispatchState._pastPlaceTenantId && !_ppCacheIsForCurrentTenant()) {
    _ppLoadPromise = null;
    _ppInvalidateCache();
  }
  if (DispatchState._pastPlaceStatus === 'ready' && DispatchState._pastPlaceIndex) return Promise.resolve(DispatchState._pastPlaceIndex);
  // Reopening the picker mid-load must join the SAME load, not start a second
  // 2,541-doc scan and not resolve early on a stale 'loading' status.
  if (_ppLoadPromise) return _ppLoadPromise;
  if (!DispatchState.tenantId) return Promise.resolve(null);
  DispatchState._pastPlaceStatus = 'loading';
  _ppLoadPromise = _ppLoad().finally(() => { _ppLoadPromise = null; });
  return _ppLoadPromise;
}

async function _ppLoad() {
  // Pin the tenant this load belongs to. If the session switches tenants while
  // the scan is in flight, the result is DISCARDED rather than published under
  // the new tenant.
  const loadTenantId = DispatchState.tenantId;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', loadTenantId, 'waypoints'),
      orderBy('createdAt', 'desc'),
      limit(PAST_PLACE_SCAN_LIMIT)
    ));
    const rows = snap.docs.map(d => {
      const w = d.data();
      return {
        id: d.id,
        name: w.name || '',
        url: w.url || '',
        lat: w.lat,
        lng: w.lng,
        jobId: w.jobId || null,
        createdAtMs: w.createdAt?.toMillis ? w.createdAt.toMillis() : 0,
      };
    });
    const jobIds = [...new Set(rows.map(r => r.jobId).filter(Boolean))];
    const customerByJobId = await _ppResolveCustomers(jobIds, loadTenantId);
    // The tenant may have changed while the scan and the job lookups ran.
    // Publishing now would show one tenant's places to another.
    if (DispatchState.tenantId !== loadTenantId) {
      console.warn('[Dispatch] TASK-4783 tenant changed mid-load — discarding the place index');
      return null;
    }
    const index = _ppBuildPlaceIndex(rows, { toleranceMeters: PAST_PLACE_TOLERANCE_M, customerByJobId });
    index.stats.truncated = snap.size >= PAST_PLACE_SCAN_LIMIT;
    DispatchState._pastPlaceIndex = index;
    DispatchState._pastPlaceRows = rows;
    DispatchState._pastPlaceCustomerByJob = customerByJobId;
    DispatchState._pastPlaceTruncated = index.stats.truncated;
    DispatchState._pastPlaceTenantId = loadTenantId;
    DispatchState._pastPlaceStatus = 'ready';
    console.log('[Dispatch] TASK-4783 place index:', index.stats);
    return index;
  } catch (e) {
    console.error('[Dispatch] TASK-4783 place index failed', e);
    _ppInvalidateCache();
    DispatchState._pastPlaceStatus = 'error';
    return null;
  }
}

// ---- rendering --------------------------------------------------------------
function _ppSelectedCustomerId() {
  const el = document.getElementById('job-customer');
  return (el && el.value) ? el.value : '';
}

function _ppCustomerName(id) {
  const c = (DispatchState.customers || []).find(x => x.id === id);
  return (c && c.name) || 'this customer';
}

function _ppLastUsedLabel(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function _ppSectionHeader(text) {
  return `<div style="padding:8px 16px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);background:var(--bg-secondary);">${escapeHtml(text)}</div>`;
}

// Rows are addressed by INDEX into the rendered list, never by interpolating a
// name or a url into an onclick — no tenant data reaches an attribute at all.
// (Correction, from COD's Adversary lane: dispatch.js's OWN escapeHtml at :7860
// DOES escape quotes, unlike the shared window.escapeHtml the house rule warns
// about. So interpolating would not have been injectable here. Indexing is still
// the right shape — a name containing a quote or a backslash would break the
// handler's parse even when it cannot escape it — but the reason is parsing, not
// XSS, and saying otherwise would mislead whoever reads this next.)
function _ppRenderRow(place, kind, idx, scopeCustomerId) {
  const isLink = kind === 'link';
  const bg = isLink ? '#FEF3C7' : '#EFF6FF';
  const fg = isLink ? '#B45309' : '#3B82F6';
  const icon = isLink ? 'link' : 'history';
  const last = _ppLastUsedLabel(place.lastUsedMs);
  const bits = [];
  // When the only label is "drop off", the row needs SOMETHING that tells two of
  // them apart. Naming the customer does that in a form a dispatcher reads at a
  // glance (COD's UX Lead: a raw lat/lng carries no operational meaning); the
  // coordinate stays as the last resort, and is dropped when it would just
  // repeat the customer the list is already scoped to.
  if (place.genericOnly) {
    const otherCust = (place.customerIds || []).filter(c => c !== scopeCustomerId);
    if (!scopeCustomerId && otherCust.length) bits.push(_ppCustomerName(otherCust[0]) + (otherCust.length > 1 ? ` +${otherCust.length - 1}` : ''));
  }
  bits.push(`${place.jobCount} job${place.jobCount === 1 ? '' : 's'}`);
  if (last) bits.push(`last ${last}`);
  if (place.genericOnly && place.lat != null) bits.push(`${place.lat.toFixed(5)}, ${place.lng.toFixed(5)}`);
  const sub = bits.join(' · ');
  // data-pp-* are the test hooks. Both values are generated here (a fixed word
  // and an integer) — no tenant string ever reaches an attribute.
  return `
    <div class="compliance-row-hover" data-pp-kind="${kind}" data-pp-idx="${idx}" style="padding:12px 16px;border-bottom:1px solid var(--border-light);cursor:pointer;display:flex;align-items:center;gap:10px;" onclick="window.DispatchModule.usePastPlace('${escJsAttr(kind)}', ${idx})">
      <div style="width:36px;height:36px;border-radius:8px;background:${bg};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
        <span class="material-icons" style="font-size:20px;color:${fg};">${icon}</span>
      </div>
      <div style="flex:1;min-width:0;">
        <div class="pp-row-name" style="font-weight:600;font-size:0.85rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(place.name)}</div>
        <div class="pp-row-sub" style="font-size:0.75rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(sub)}</div>
      </div>
      ${isLink ? `<span class="material-icons" style="font-size:16px;color:#B45309;flex-shrink:0;" title="Saved from a map share link — no coordinates, so it can't be mapped or matched to other destinations">help_outline</span>` : ''}
    </div>`;
}

// Returns the whole "from past jobs" block for the picker. Never throws — a
// failure here must not take the saved-locations list down with it.
function _ppRenderPastPlacesSection(filter) {
  const status = DispatchState._pastPlaceStatus;
  // A zero-height marker carrying the load state — the test hook that lets a
  // spec wait for "ready" instead of sleeping and hoping.
  const marker = st => `<div id="pp-past-places" data-pp-status="${st}"></div>`;
  if (status === 'loading' || status === 'idle') {
    return marker('loading') + _ppSectionHeader('From past jobs')
      + `<div style="padding:16px;font-size:0.8rem;color:var(--text-secondary);">Loading places from past jobs…</div>`;
  }
  if (status === 'error' || !DispatchState._pastPlaceIndex) {
    return marker('error') + _ppSectionHeader('From past jobs')
      + `<div style="padding:16px;font-size:0.8rem;color:var(--text-primary);">Couldn't load places from past jobs. Your saved locations and facilities above still work.</div>`;
  }

  const index = DispatchState._pastPlaceIndex;
  const custId = _ppSelectedCustomerId();
  // A widened scope belongs to the customer it was chosen for. Different
  // customer on the form => back to that customer's own places.
  if (DispatchState._pastPlaceScopeFor !== custId) {
    DispatchState._pastPlaceScope = 'customer';
    DispatchState._pastPlaceScopeFor = custId;
  }
  const scope = DispatchState._pastPlaceScope === 'all' || !custId ? 'all' : 'customer';
  const scopeCust = scope === 'customer' ? custId : '';

  const places = index.places.filter(p => _ppMatchesCustomer(p, scopeCust) && _ppMatchesFilter(p, filter));
  const links = index.linkOnly.filter(p => _ppMatchesCustomer(p, scopeCust) && _ppMatchesFilter(p, filter));
  DispatchState._pastPlaceVisible = { place: places, link: links };

  let html = marker('ready') + _ppSectionHeader('From past jobs');

  // Scope control. Only offered when a customer is actually selected on the job.
  if (custId) {
    const on  = 'background:var(--brand-blue);color:#fff;border:1px solid var(--brand-blue);';
    const off = 'background:transparent;color:var(--text-primary);border:1px solid var(--border-light);';
    html += `
      <div style="display:flex;gap:6px;align-items:center;padding:8px 16px;flex-wrap:wrap;">
        <button type="button" class="btn btn-sm" style="${scope === 'customer' ? on : off}font-size:0.72rem;padding:3px 10px;" onclick="window.DispatchModule.setPastPlaceScope('customer')">${escapeHtml(_ppCustomerName(custId))}</button>
        <button type="button" class="btn btn-sm" style="${scope === 'all' ? on : off}font-size:0.72rem;padding:3px 10px;" onclick="window.DispatchModule.setPastPlaceScope('all')">All customers</button>
      </div>`;
  }

  // The footer goes on BOTH branches. COD's Code Reviewer caught the empty
  // branch returning early without it: a truncated history that happens to have
  // nothing for THIS customer would read as "we have never been there", which
  // is a different claim from "we could not look that far back".
  const s = index.stats;
  const footer = `<div style="padding:10px 16px 14px;font-size:0.7rem;color:var(--text-secondary);border-top:1px solid var(--border-light);">`
    + `${s.rows} past destinations on ${s.places} place${s.places === 1 ? '' : 's'} (matched within ${s.toleranceMeters} m)`
    + (s.unmappable ? ` · ${s.unmappable} without coordinates` : '')
    + (s.truncated ? ` · showing your most recent ${PAST_PLACE_SCAN_LIMIT} destinations only` : '')
    + `</div>`;

  if (!places.length && !links.length) {
    html += `<div style="padding:16px;font-size:0.8rem;color:var(--text-secondary);">`
      + (filter
          ? `No past destinations match "${escapeHtml(filter)}".`
          : scope === 'customer'
            ? `No past destinations recorded for ${escapeHtml(_ppCustomerName(custId))} yet.`
            : `No past destinations recorded yet.`)
      + `</div>` + footer;
    return html;
  }

  html += places.map((p, i) => _ppRenderRow(p, 'place', i, scopeCust)).join('');

  if (links.length) {
    html += _ppSectionHeader(`No coordinates — map link only (${links.length})`);
    html += `<div style="padding:6px 16px 2px;font-size:0.72rem;color:var(--text-primary);">Saved from a Google Maps share link, which carries no coordinates. These still open in Maps for the driver, but they can't be mapped or matched against the destinations above.</div>`;
    html += links.map((p, i) => _ppRenderRow(p, 'link', i, scopeCust)).join('');
  }

  return html + footer;
}

function setPastPlaceScope(scope) {
  DispatchState._pastPlaceScope = scope === 'all' ? 'all' : 'customer';
  // Remember WHICH customer this choice was made for. Widening to "All
  // customers" on Loenbro must not leave the picker wide open when the
  // dispatcher then builds a job for someone else — that would silently show
  // one customer's yards under another, which is the exact leak the scoping
  // exists to prevent. Caught by the e2e, not by reading the code.
  DispatchState._pastPlaceScopeFor = _ppSelectedCustomerId();
  filterSavedLocations(document.getElementById('saved-loc-search')?.value || '');
}

function usePastPlace(kind, idx) {
  const bucket = (DispatchState._pastPlaceVisible || {})[kind] || [];
  const place = bucket[idx];
  if (!place) return;
  // savedLocId stays empty — this is NOT a savedLocations doc, and claiming one
  // would let the bookmark toggle try to delete a document that doesn't exist.
  addWaypoint(place.name, place.url || '', '', place.lat, place.lng);
  document.getElementById('dispatch-modal-overlay')?.classList.add('hidden');
  window.showToast?.(`Added "${place.name}"`, 'success');
}

// ============================================
// SAVED LOCATIONS
// ============================================
// ── TASK-10106 S4 — Saved Locations on the DRIVER Add/Edit Job form ─────────
// A PIN session cannot read `savedLocations` (rules: drvMember — they carry
// gate codes, contacts, notes). The driverSavedLocations callable returns the
// trimmed list (id, name, address, url, lat, lng, categoryId, customerIds)
// after checking the session + "drivers can add jobs" + the per-driver block
// list. Read-only here: no New / Manage / edit / delete, no past-jobs scan.
// Cached per SESSION (tenant + the PIN driver's person id — COD r1: a shared
// yard tablet must never hand the next driver the last one's list) for two
// minutes, so reopening the picker is instant, stays under the server's
// per-driver cap, and an office edit shows up soon.
const _DRIVER_PLACES_TTL_MS = 2 * 60 * 1000;
let _driverPlacesCache = null; // { key, at, list, truncated }
let _driverPlacesInflight = null; // { key, p }
let _driverPlacesSeq = 0; // COD r1: a late answer never repaints a newer picker
function _driverPlacesMode() {
  return window.AppState?.driverModePin === true;
}
function _driverPlacesKey() {
  const t = window.AppState?.tenant || '';
  const p = window.AppState?.driverPersonId || '';
  return t && p ? `${t}|${p}` : '';
}
function _driverPlacesCached(key) {
  const c = _driverPlacesCache;
  return key && c && c.key === key && Date.now() - c.at < _DRIVER_PLACES_TTL_MS ? c : null;
}
async function _driverLoadSavedLocations() {
  const key = _driverPlacesKey();
  if (!key) return null;
  const cached = _driverPlacesCached(key);
  if (cached) return cached.list;
  // Reopening the picker mid-load joins the same call (one lookup, not two).
  if (_driverPlacesInflight && _driverPlacesInflight.key === key) return _driverPlacesInflight.p;
  const p = (async () => {
    const res = await httpsCallable(functions, 'driverSavedLocations')({});
    if (_driverPlacesKey() !== key) return null; // switched company / driver mid-read (P0 1019)
    const list = Array.isArray(res?.data?.locations) ? res.data.locations : [];
    _driverPlacesCache = { key, at: Date.now(), list, truncated: res?.data?.truncated === true };
    return list;
  })();
  _driverPlacesInflight = { key, p };
  try { return await p; } finally { if (_driverPlacesInflight && _driverPlacesInflight.p === p) _driverPlacesInflight = null; }
}

function showSavedLocationsPicker() {
  DispatchState._placesCtx = null; // TASK-9540 — the picker is not the manager
  // TASK-10106 S4 — a PIN driver's picker paints from the cached trimmed list
  // (or empty + "Loading…") and fills in once driverSavedLocations answers.
  const driverPlaces = _driverPlacesMode();
  if (driverPlaces) {
    const cached = _driverPlacesCached(_driverPlacesKey());
    DispatchState.savedLocations = cached ? cached.list : [];
    DispatchState._driverPlacesTruncated = !!(cached && cached.truncated);
    DispatchState._driverPlacesStatus = cached ? 'ready' : 'loading';
  }
  const locations = DispatchState.savedLocations;
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'dispatch-modal-overlay';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }

  modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:500px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:85dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">
            <span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">bookmark</span> Saved Locations
          </h3>
          <div style="display:flex;gap:8px;align-items:center;">
            ${driverPlaces ? '' : `<button type="button" class="btn btn-sm" data-places-manage onclick="window.DispatchModule.openPlacesManager('job')" style="background:rgba(255,255,255,0.2);color:#fff;border:1px solid rgba(255,255,255,0.3);font-size:0.75rem;min-height:40px;" title="Categories, edit and delete — the same list as Maps">
              <span class="material-icons" style="font-size:16px;">tune</span> Manage
            </button>`}
            ${_canEditPlaces(false) ? `<button class="btn btn-sm" onclick="window.DispatchModule.showAddSavedLocation()" style="background:rgba(255,255,255,0.2);color:#fff;border:1px solid rgba(255,255,255,0.3);font-size:0.75rem;min-height:40px;">
              <span class="material-icons" style="font-size:16px;">add</span> New
            </button>` : ''}
            <button class="btn-icon" onclick="document.getElementById('dispatch-modal-overlay').classList.add('hidden')" style="color:#fff;">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </div>
      <div style="padding:12px 16px;border-bottom:1px solid var(--border-light);flex-shrink:0;">
        <input type="search" id="saved-loc-search" class="form-control" placeholder="${(locations || []).length > 8 ? `Search ${(locations || []).length} saved locations…` : 'Search locations…'}" aria-label="Search saved locations" autocomplete="off" oninput="window.DispatchModule.filterSavedLocations(this.value)" style="font-size:0.85rem;min-height:44px;">
      </div>
      <div id="saved-locations-list" style="overflow-y:auto;flex:1;">
        ${renderSavedLocationsList(locations)}
      </div>
    </div>
  `;

  // TASK-10013 — the search box sits above the scrolling list; put the cursor
  // in it so a long list is one keystroke from filtered.
  try { document.getElementById('saved-loc-search')?.focus({ preventScroll: true }); } catch (_) { /* focus is a nicety */ }

  // TASK-4783 — the past-job place list is LAZY: the 2,541-row waypoints scan
  // only happens once a dispatcher actually opens this picker, and only once per
  // session. The picker above has already painted, so a slow or failed load
  // degrades to a message inside that one section and never blocks the saved
  // locations and facilities that are already on screen.
  // TASK-10106 S4 — a PIN driver never runs the past-jobs scan (not part of
  // the driver picker); the trimmed list arrives from driverSavedLocations.
  if (driverPlaces) {
    // COD r2 — bump on EVERY open (a cached open too), so an older load can
    // never repaint a picker opened after it.
    const seq = ++_driverPlacesSeq;
    if (DispatchState._driverPlacesStatus === 'ready') return;
    const key = _driverPlacesKey();
    const current = () => seq === _driverPlacesSeq && _driverPlacesKey() === key;
    _driverLoadSavedLocations().then((list) => {
      if (!list || !current()) return;
      DispatchState.savedLocations = list;
      DispatchState._driverPlacesTruncated = !!_driverPlacesCached(key)?.truncated;
      DispatchState._driverPlacesStatus = 'ready';
    }).catch((e) => {
      if (!current()) return;
      console.warn('[Dispatch] TASK-10106 S4: saved locations unavailable for the driver form', e?.code || e?.message || e);
      DispatchState._driverPlacesStatus = 'error';
      // Our own refusal text (switch off / blocked / too many lookups) is
      // written for the driver; anything else gets the generic line.
      const code = String(e?.code || '').replace(/^functions\//, '');
      DispatchState._driverPlacesErrMsg = (code === 'permission-denied' || code === 'resource-exhausted') && e?.message ? String(e.message) : '';
    }).finally(() => {
      if (!current()) return;
      if (!document.getElementById('saved-locations-list')) return; // picker closed
      const search = document.getElementById('saved-loc-search');
      const n = (DispatchState.savedLocations || []).length;
      if (search) search.placeholder = n > 8 ? `Search ${n} saved locations…` : 'Search locations…';
      filterSavedLocations(search?.value || '');
    });
    return;
  }
  if (DispatchState._pastPlaceStatus !== 'ready') {
    _ppEnsureLoaded().then(() => {
      if (!document.getElementById('saved-locations-list')) return; // picker closed
      filterSavedLocations(document.getElementById('saved-loc-search')?.value || '');
    });
  }
}

// ── TASK-9421 — Saved Locations: customer tags + source ──────────────────────
// Shape on tenants/{tid}/savedLocations/{id} (additive; TASK-9175's map layer
// reuses it, do not invent a second one):
//   customerIds: string[]  customer DOC ids, never names. Missing/[] = untagged.
//   source:      'manual' | 'customer_text' | 'map_tool'. Missing = 'manual'.
// 🔴 The tag is a DISPLAY filter, not access control: every tenant member can
// READ every saved location (writes: owner / dispatch module, TASK-9540).
// Never describe it as privacy.
const SAVED_LOC_SOURCE_LABELS = { manual: 'Manual', customer_text: 'Customer text', map_tool: 'Map tool' };

function savedLocSource(loc) {
  const s = loc && typeof loc.source === 'string' ? loc.source.trim() : '';
  return s || 'manual';
}

function savedLocSourceLabel(src) {
  return SAVED_LOC_SOURCE_LABELS[src] || src;
}

// Tagged customer ids. With `knownIds` (a Set of live customer ids), a tag
// pointing at a deleted/merged customer is dropped — so a location whose only
// tag dangles reads as UNTAGGED and shows everywhere, instead of vanishing.
function savedLocCustomerIds(loc, knownIds) {
  const raw = loc && Array.isArray(loc.customerIds) ? loc.customerIds : [];
  const ids = [...new Set(raw.filter(x => typeof x === 'string' && x))];
  return knownIds ? ids.filter(id => knownIds.has(id)) : ids;
}

// The picker's filter. scope 'customer' with a customer on the form = that
// customer's locations PLUS untagged ones (nothing vanishes on day one, when
// no location carries a tag). No customer on the form = everything. Hidden
// sources apply in both scopes.
function filterSavedLocationsByScope(locations, opts) {
  const o = opts || {};
  const hidden = o.hiddenSources instanceof Set ? o.hiddenSources : new Set(o.hiddenSources || []);
  const custId = o.customerId || '';
  const wide = o.scope === 'all' || !custId;
  return (locations || []).filter(loc => {
    if (hidden.size && hidden.has(savedLocSource(loc))) return false;
    if (wide) return true;
    const ids = savedLocCustomerIds(loc, o.knownIds || null);
    return ids.length === 0 || ids.includes(custId);
  });
}

// TASK-10013 — how a saved location has been used, read off the past-job
// place index (TASK-4783: tenants/{tid}/waypoints, one row per job-use, already
// loaded when the picker opens). savedLocations docs carry no use stamp, so
// this is the only record. PURE. A pinned location matches the past places
// within PAST_PLACE_TOLERANCE_M of its pin; an unpinned one matches by its
// exact map link or its (non-generic) name. → { lastUsedMs, customerIds }.
function savedLocUsage(loc, index) {
  const out = { lastUsedMs: 0, customerIds: [] };
  if (!loc || !index) return out;
  const add = (p) => {
    if ((p.lastUsedMs || 0) > out.lastUsedMs) out.lastUsedMs = p.lastUsedMs || 0;
    for (const c of p.customerIds || []) if (!out.customerIds.includes(c)) out.customerIds.push(c);
  };
  const pin = plottableCoord(loc);
  const url = String(loc.url || '').trim();
  const nm = _ppNormalizeLabel(loc.name);
  const nameOk = nm && !PAST_PLACE_GENERIC_LABELS.has(nm);
  for (const p of index.places || []) {
    if (pin) { if (p.lat != null && p.lng != null && _ppMetersBetween(pin, p) <= PAST_PLACE_TOLERANCE_M) add(p); }
    else if ((url && p.url === url) || (nameOk && !p.genericOnly && _ppNormalizeLabel(p.name) === nm)) add(p);
  }
  if (!pin) {
    for (const p of index.linkOnly || []) {
      if ((url && p.url === url) || (nameOk && !p.genericOnly && _ppNormalizeLabel(p.name) === nm)) add(p);
    }
  }
  return out;
}

// TASK-10013 — PURE. The picker's order (Chris at Guy's 67 untagged saved
// locations: "you kind of need a better filter there"). With a customer on the
// form: that customer's locations first — tagged to them, or untagged but used
// on one of their jobs before. Then most recently used, then name.
// usage: Map id -> savedLocUsage(); missing = never used (the past-job index
// may not have loaded yet, which falls back to name order).
function sortSavedLocationsForPicker(list, opts) {
  const o = opts || {};
  const custId = o.customerId || '';
  const usage = o.usage && typeof o.usage.get === 'function' ? o.usage : new Map();
  const u = (loc) => usage.get(loc.id) || { lastUsedMs: 0, customerIds: [] };
  const rank = (loc) => {
    if (!custId) return 1;
    const tags = savedLocCustomerIds(loc, o.knownIds || null);
    if (tags.includes(custId)) return 0;
    if (tags.length === 0 && u(loc).customerIds.includes(custId)) return 0;
    return 1;
  };
  return (list || []).slice().sort((a, b) =>
    (rank(a) - rank(b))
    || ((u(b).lastUsedMs || 0) - (u(a).lastUsedMs || 0))
    || String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
}

// TASK-10013 — the usage map for the current tenant's index, or an empty map
// while it is loading / failed / belongs to another tenant.
// Cached per index OBJECT and per saved-location OBJECT: the picker re-renders on
// every keystroke, and savedLocUsage walks every past place. A job save rebuilds
// the index (new object) and a savedLocations snapshot makes new loc objects, so
// identity is exactly the invalidation we need.
let _savedLocUsageCache = { index: null, byId: new Map() };
function _savedLocUsageMap(locations) {
  const idx = DispatchState._pastPlaceStatus === 'ready' && DispatchState._pastPlaceTenantId === DispatchState.tenantId
    ? DispatchState._pastPlaceIndex : null;
  const m = new Map();
  if (!idx) return m;
  if (_savedLocUsageCache.index !== idx) _savedLocUsageCache = { index: idx, byId: new Map() };
  const cache = _savedLocUsageCache.byId;
  for (const loc of locations || []) {
    if (!loc || !loc.id) continue;
    const hit = cache.get(loc.id);
    if (hit && hit.loc === loc) { m.set(loc.id, hit.u); continue; }
    const u = savedLocUsage(loc, idx);
    cache.set(loc.id, { loc, u });
    m.set(loc.id, u);
  }
  return m;
}

function _savedLocKnownCustomerIds() {
  const list = DispatchState.customers || [];
  // Customers not loaded yet: don't treat every tag as dangling.
  return list.length ? new Set(list.map(c => c.id)) : null;
}

function setSavedLocScope(scope) {
  DispatchState._savedLocScope = scope === 'all' ? 'all' : 'customer';
  DispatchState._savedLocScopeFor = _ppSelectedCustomerId();
  filterSavedLocations(document.getElementById('saved-loc-search')?.value || '');
}

function toggleSavedLocSource(src) {
  const hidden = new Set(DispatchState._savedLocHiddenSources || []);
  if (hidden.has(src)) hidden.delete(src); else hidden.add(src);
  DispatchState._savedLocHiddenSources = [...hidden];
  filterSavedLocations(document.getElementById('saved-loc-search')?.value || '');
}

function _renderSavedLocRow(loc, knownIds, usage) {
  const custNames = savedLocCustomerIds(loc, knownIds).map(id => _ppCustomerName(id));
  // TASK-10013 — the recency the list is sorted by, shown so the order makes sense.
  const lastUsed = usage && usage.lastUsedMs ? _ppLastUsedLabel(usage.lastUsedMs) : '';
  const src = savedLocSource(loc);
  const srcBadge = src !== 'manual'
    ? `<span style="flex-shrink:0;font-size:0.62rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;padding:1px 6px;border-radius:9999px;background:#EFF6FF;color:#1D4ED8;border:1px solid #BFDBFE;">${escapeHtml(savedLocSourceLabel(src))}</span>`
    : '';
  // TASK-9540 — the place's category shape, the same mark the map draws.
  const cat = categoryForPlace(loc, _placeCats());
  const canEdit = _canEditPlaces(true);
  return `
      <div class="compliance-row-hover" data-saved-loc-id="${escapeHtml(loc.id)}" style="padding:12px 16px;border-bottom:1px solid var(--border-light);cursor:pointer;display:flex;align-items:center;gap:10px;" onclick="window.DispatchModule.useSavedLocation('${escJsAttr(loc.id)}')">
        <div style="width:36px;height:36px;border-radius:8px;background:#F1F5F9;display:flex;align-items:center;justify-content:center;flex-shrink:0;" title="${escapeHtml(cat.name)}" data-saved-loc-cat="${escapeHtml(cat.id)}">
          ${placeShapeSvg(cat.shape, cat.color, 24)}
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;align-items:center;gap:6px;">
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(loc.name)}</span>${srcBadge}
          </div>
          ${loc.address ? `<div style="font-size:0.75rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(loc.address)}</div>` : ''}
          ${loc.gateCode || loc.notes ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:1px;">${loc.gateCode ? 'Gate: ' + escapeHtml(loc.gateCode) : ''}${loc.gateCode && loc.notes ? ' · ' : ''}${loc.notes ? escapeHtml(loc.notes) : ''}</div>` : ''}
          ${custNames.length ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:1px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">For ${escapeHtml(custNames.join(', '))}</div>` : ''}
          ${lastUsed ? `<div data-saved-loc-last-used style="font-size:0.7rem;color:var(--text-secondary);margin-top:1px;">Last used ${escapeHtml(lastUsed)}</div>` : ''}
        </div>
        ${canEdit ? `<div style="display:flex;gap:2px;flex-shrink:0;" onclick="event.stopPropagation();">
          <button class="btn-icon" onclick="window.DispatchModule.editSavedLocation('${escJsAttr(loc.id)}')" title="Edit" aria-label="Edit ${escapeHtml(loc.name || 'place')}" style="min-width:40px;min-height:40px;">
            <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">edit</span>
          </button>
          <button class="btn-icon" data-saved-loc-delete onclick="window.DispatchModule.deleteSavedLocation('${escJsAttr(loc.id)}')" title="Delete" aria-label="Delete ${escapeHtml(loc.name || 'place')}" style="min-width:40px;min-height:40px;">
            <span class="material-icons" style="font-size:18px;color:#DC2626;">delete</span>
          </button>
        </div>` : ''}
      </div>
    `;
}

function renderSavedLocationsList(locations, filter = '') {
  let filtered = locations;
  if (filter) {
    const q = filter.toLowerCase();
    filtered = locations.filter(l =>
      (l.name || '').toLowerCase().includes(q) ||
      (l.address || '').toLowerCase().includes(q) ||
      (l.notes || '').toLowerCase().includes(q) ||
      (l.gateCode || '').toLowerCase().includes(q)
    );
  }

  // TASK-1142 — "Your Facilities": company yards/plants/shops/terminals from
  // roster settings, shown at top. Address-less facilities are included and
  // pickable (useYardAsWaypoint nudges — never blocks — on a missing address).
  // Yard shape is {slug, label, address?, type?} — type is optional/additive.
  const yards = (window.AppState?.settings?.yards || []);
  const yardTypeLabel = (y) => window.RosterModule?.getYardTypeLabel?.(y.type) || '';
  let filteredYards = yards;
  if (filter) {
    const q = filter.toLowerCase();
    filteredYards = yards.filter(y =>
      (y.label || '').toLowerCase().includes(q) ||
      (y.address || '').toLowerCase().includes(q) ||
      yardTypeLabel(y).toLowerCase().includes(q)
    );
  }

  // TASK-4783 — built once here so the "nothing at all" early-return below can
  // account for it. Never throws: a failure inside the place index must not take
  // the bookmarked locations and facilities down with it.
  // TASK-10106 S4 — the PIN driver picker: no past-jobs section, no Add.
  const driverPlaces = _driverPlacesMode();
  let pastHtml = '';
  if (!driverPlaces) {
    try { pastHtml = _ppRenderPastPlacesSection(filter); }
    catch (e) { console.error('[Dispatch] TASK-4783 past-places render failed', e); pastHtml = ''; }
  }
  if (driverPlaces && filtered.length === 0 && DispatchState._driverPlacesStatus !== 'ready') {
    const st = DispatchState._driverPlacesStatus === 'error' ? 'error' : 'loading';
    pastHtml = `<div id="drv-saved-loc-status" data-status="${st}" style="padding:16px;font-size:0.8rem;color:var(--text-primary);">`
      + (st === 'error'
          ? `Couldn't load saved locations. ${escapeHtml(DispatchState._driverPlacesErrMsg || 'Check your connection and try again.')}`
          : 'Loading saved locations…')
      + `</div>`;
  }

  // COD r1 — say so when the server capped the list (A to Z, first 1,000).
  if (driverPlaces && DispatchState._driverPlacesStatus === 'ready' && DispatchState._driverPlacesTruncated && !filter) {
    pastHtml = `<div id="drv-saved-loc-truncated" style="padding:12px 16px;font-size:0.8rem;color:var(--text-primary);">Showing the first ${(locations || []).length} saved locations (A to Z). Ask dispatch if the place you need isn't here.</div>`;
  }
  if (filtered.length === 0 && filteredYards.length === 0 && !pastHtml && driverPlaces) {
    return `
      <div style="padding:40px 20px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);">location_off</span>
        <div style="font-size:0.875rem;color:var(--text-secondary);margin-top:8px;">${filter ? 'No matching locations' : 'No saved locations yet. Ask dispatch to add the places you use.'}</div>
      </div>
    `;
  }
  if (filtered.length === 0 && filteredYards.length === 0 && !pastHtml) {
    return `
      <div style="padding:40px 20px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);">location_off</span>
        <div style="font-size:0.875rem;color:var(--text-secondary);margin-top:8px;">${filter ? 'No matching locations' : 'No saved locations yet'}</div>
        <button class="btn btn-primary btn-sm" style="margin-top:12px;" onclick="window.DispatchModule.showAddSavedLocation()">
          <span class="material-icons" style="font-size:16px;">add</span> Add Location
        </button>
      </div>
    `;
  }

  let html = '';

  // Your Facilities section (TASK-1142 — promoted from "Yards")
  if (filteredYards.length > 0) {
    html += `<div style="padding:8px 16px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);background:var(--bg-secondary);">Your Facilities</div>`;
    html += filteredYards.map(y => {
      const typeLabel = yardTypeLabel(y);
      return `
      <div class="compliance-row-hover" style="padding:12px 16px;border-bottom:1px solid var(--border-light);cursor:pointer;display:flex;align-items:center;gap:10px;" onclick="window.DispatchModule.useYardAsWaypoint('${escJsAttr(y.slug)}')">
        <div style="width:36px;height:36px;border-radius:8px;background:#ECFDF5;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
          <span class="material-icons" style="font-size:20px;color:#10B981;">warehouse</span>
        </div>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:flex;align-items:center;gap:6px;">
            <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(y.label)}</span>
            ${typeLabel ? `<span style="flex-shrink:0;font-size:0.62rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;padding:1px 6px;border-radius:9999px;background:#ECFDF5;color:#047857;border:1px solid #A7F3D0;">${escapeHtml(typeLabel)}</span>` : ''}
          </div>
          ${y.address
            ? `<div style="font-size:0.75rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(y.address)}</div>`
            : `<div style="font-size:0.75rem;color:var(--text-secondary);font-style:italic;">No address on file</div>`}
        </div>
      </div>
    `; }).join('');
  }

  // Saved locations section
  if (filtered.length > 0) {
    // TASK-9421 — customer scope + source chips. Same reset rule as "From
    // past jobs": a widened scope belongs to the customer it was chosen for.
    const custId = _ppSelectedCustomerId();
    if (DispatchState._savedLocScopeFor !== custId) {
      DispatchState._savedLocScope = 'customer';
      DispatchState._savedLocScopeFor = custId;
    }
    const scope = DispatchState._savedLocScope === 'all' || !custId ? 'all' : 'customer';
    const knownIds = _savedLocKnownCustomerIds();
    // Source chips only when the list actually mixes sources — one "Manual"
    // chip is noise. A hidden source only filters while its chip is on screen,
    // so a row can never be hidden by a control the dispatcher can't see.
    // Chips come from the CUSTOMER-SCOPED list (COD Edge Case r1): chips built
    // from every customer's rows would offer a source this customer doesn't
    // have, and switching off Manual would then empty this customer's view.
    const inScope = filterSavedLocationsByScope(filtered, { customerId: custId, scope, knownIds });
    const sources = [...new Set(inScope.map(savedLocSource))].sort((a, b) =>
      (a === 'manual' ? -1 : b === 'manual' ? 1 : a.localeCompare(b)));
    const showChips = sources.length > 1;
    const hidden = new Set(showChips ? (DispatchState._savedLocHiddenSources || []) : []);
    // TASK-10013 — this customer's first, then most recently used, then name.
    const usageMap = _savedLocUsageMap(inScope);
    const scoped = sortSavedLocationsForPicker(
      filterSavedLocationsByScope(inScope, { scope: 'all', hiddenSources: hidden }),
      { customerId: custId, usage: usageMap, knownIds });

    // TASK-4783 — label this section whenever it is not the only one on screen.
    if (filteredYards.length > 0 || pastHtml || custId || showChips) {
      html += `<div id="saved-loc-section" data-scope="${scope}" style="padding:8px 16px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);background:var(--bg-secondary);">Saved Locations</div>`;
    }
    if (custId || showChips) {
      const on  = 'background:var(--brand-blue);color:#fff;border:1px solid var(--brand-blue);';
      const off = 'background:transparent;color:var(--text-primary);border:1px solid var(--border-light);';
      html += `<div style="display:flex;gap:6px;align-items:center;padding:8px 16px;flex-wrap:wrap;">`;
      if (custId) {
        html += `<button type="button" class="btn btn-sm" data-saved-loc-scope="customer" style="${scope === 'customer' ? on : off}font-size:0.72rem;padding:3px 10px;min-height:32px;" onclick="window.DispatchModule.setSavedLocScope('customer')">${escapeHtml(_ppCustomerName(custId))}</button>`
          + `<button type="button" class="btn btn-sm" data-saved-loc-scope="all" style="${scope === 'all' ? on : off}font-size:0.72rem;padding:3px 10px;min-height:32px;" onclick="window.DispatchModule.setSavedLocScope('all')">All customers</button>`;
      }
      if (showChips) {
        if (custId) html += `<span style="width:1px;height:18px;background:var(--border-light);margin:0 2px;"></span>`;
        html += sources.map(src => `<button type="button" class="btn btn-sm" data-saved-loc-source="${escapeHtml(src)}" aria-pressed="${hidden.has(src) ? 'false' : 'true'}" style="${hidden.has(src) ? off : on}font-size:0.72rem;padding:3px 10px;min-height:32px;" onclick="window.DispatchModule.toggleSavedLocSource('${escJsAttr(src)}')">${escapeHtml(savedLocSourceLabel(src))}</button>`).join('');
      }
      html += `</div>`;
    }
    if (scoped.length) {
      html += scoped.map(loc => _renderSavedLocRow(loc, knownIds, usageMap.get(loc.id))).join('');
    } else {
      html += `<div style="padding:12px 16px;font-size:0.8rem;color:var(--text-secondary);">`
        + (inScope.length === 0
            ? `No saved locations for ${escapeHtml(_ppCustomerName(custId))}. Tap All customers to see the rest.`
            : `Every saved location here is from a source you've switched off.`)
        + `</div>`;
    }
  }

  // TASK-4783 — deduped places from past jobs, last so the dispatcher's own
  // bookmarks and facilities always stay at the top.
  html += pastHtml;

  return html;
}

function filterSavedLocations(q) {
  const list = document.getElementById('saved-locations-list');
  if (list) list.innerHTML = renderSavedLocationsList(DispatchState.savedLocations, q);
}

function useSavedLocation(locId) {
  const loc = DispatchState.savedLocations.find(l => l.id === locId);
  if (!loc) return;
  // Pass the stored pin. Without it a share-link location re-resolved on
  // every use and, on a cold map service, the stop saved unpinned even
  // though the saved location HAS a coordinate (Barracuda north, 10/8).
  const _pin = plottableCoord(loc);
  // TASK-9540 — the stop records its place's category AT PICK TIME, so the
  // badge survives the saved place being recategorised or deleted later.
  // A place with no category gives its stop none (no "Other" badge on stops).
  const _catId = loc.categoryId && _placeCats().some(c => c.id === loc.categoryId) ? loc.categoryId : '';
  addWaypoint(loc.name, loc.url || '', loc.id, _pin ? _pin.lat : null, _pin ? _pin.lng : null, '', _catId);
  document.getElementById('dispatch-modal-overlay')?.classList.add('hidden');
  window.showToast?.(`Added "${loc.name}"`, 'success');
}

function useYardAsWaypoint(slug) {
  const yard = (window.AppState?.settings?.yards || []).find(y => y.slug === slug);
  if (!yard) return;
  // Snapshot label/address into the job (via addWaypoint) — never a live ref.
  addWaypoint(yard.label, yard.address || '');
  document.getElementById('dispatch-modal-overlay')?.classList.add('hidden');
  if (yard.address) {
    window.showToast?.(`Added "${yard.label}"`, 'success');
  } else {
    // TASK-1142 — nudge (never block) when a facility has no address yet:
    // the waypoint still works as a named stop, but drivers get no directions.
    window.showToast?.(`Added "${yard.label}" — no address on this facility yet. Add one in Roster → Facilities so drivers get directions.`, 'info');
  }
}

// TASK-9540 — the place's category on the add/edit form.
function _savedLocCategoryFieldHtml(loc) {
  const cats = _placeCats();
  const cur = loc && typeof loc.categoryId === 'string' && cats.some(c => c.id === loc.categoryId) ? loc.categoryId : '';
  const shown = categoryForPlace({ categoryId: cur }, cats);
  return `<div>
          <label for="saved-loc-category" style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Category</label>
          <div style="display:flex;align-items:center;gap:10px;">
            <span id="saved-loc-category-swatch" style="display:inline-flex;">${placeShapeSvg(shown.shape, shown.color, 26)}</span>
            <select id="saved-loc-category" class="form-control" style="min-height:44px;flex:1;" onchange="window.DispatchModule.previewSavedLocCategory(this.value)">
              <option value=""${cur ? '' : ' selected'}>No category (shows as ${escapeHtml(cats.find(c => c.id === OTHER_CATEGORY_ID).name)})</option>
              ${cats.map(c => `<option value="${escapeHtml(c.id)}"${cur === c.id ? ' selected' : ''}>${escapeHtml(c.name)}</option>`).join('')}
            </select>
          </div>
          <p style="font-size:0.72rem;color:var(--text-secondary);margin:4px 0 0;">Sets the shape this place shows as on the map. Categories are shared by your whole team.</p>
        </div>`;
}

function previewSavedLocCategory(catId) {
  const sw = document.getElementById('saved-loc-category-swatch');
  if (!sw) return;
  const c = categoryForPlace({ categoryId: catId || '' }, _placeCats());
  sw.innerHTML = placeShapeSvg(c.shape, c.color, 26);
}

function showSavedLocationForm(loc = null) {
  const isEdit = !!loc;
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:460px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:90dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">${isEdit ? 'Edit' : 'Add'} Saved Location</h3>
          <button class="btn-icon" onclick="window.DispatchModule.placeFormBack()" style="color:#fff;min-width:44px;min-height:44px;" title="Back to list" aria-label="Back to list">
            <span class="material-icons">arrow_back</span>
          </button>
        </div>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;overflow-y:auto;flex:1;">
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Location Name *</label>
          <input type="text" id="saved-loc-name" class="form-control" value="${escapeHtml(loc?.name || '')}" placeholder="e.g. Copper Creek Ranch — Rig #4">
        </div>
        ${_savedLocCategoryFieldHtml(loc)}
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Address</label>
          <input type="text" id="saved-loc-address" class="form-control" value="${escapeHtml(loc?.address || '')}" placeholder="e.g. 43.2 miles north on Hwy 85">
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Google Maps Link</label>
          <input type="url" id="saved-loc-url" class="form-control" value="${escapeHtml(loc?.url || '')}" placeholder="Paste Google Maps link">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Gate Code</label>
            <input type="text" id="saved-loc-gate" class="form-control" value="${escapeHtml(loc?.gateCode || '')}" placeholder="e.g. 4411">
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Contact</label>
            <input type="text" id="saved-loc-contact" class="form-control" value="${escapeHtml(loc?.contact || '')}" placeholder="Site contact name">
          </div>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Notes</label>
          <input type="text" id="saved-loc-notes" class="form-control" value="${escapeHtml(loc?.notes || '')}" placeholder="Directions, instructions, etc.">
        </div>
        <div>
          <label for="saved-loc-customer-add" style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Customers</label>
          <div id="saved-loc-customers"></div>
          <p style="font-size:0.72rem;color:var(--text-secondary);margin:4px 0 0;">Picks which jobs list this location first. With no customer it shows on every job. Everyone on your team can still see every saved location.</p>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);display:flex;gap:12px;justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.DispatchModule.placeFormBack()">Cancel</button>
        <button class="btn btn-primary" id="saved-loc-save-btn" onclick="window.DispatchModule.saveSavedLocation('${escJsAttr(loc?.id || '')}')">
          <span class="material-icons" style="font-size:18px;">save</span> ${isEdit ? 'Update' : 'Save'}
        </button>
      </div>
    </div>
  `;
  // TASK-9421 — customer tags. Editing keeps what is there (a dangling id is
  // kept too — it reads as untagged and is dropped only by a save); a NEW
  // location opened from a job starts tagged with that job's customer.
  if (isEdit) {
    DispatchState._savedLocFormCustomerIds = savedLocCustomerIds(loc);
  } else {
    // TASK-9540 — opened from the MAP's manager there is no job: the hidden
    // job form may still hold the last job's customer, so don't inherit it.
    const fromMap = DispatchState._placesCtx && DispatchState._placesCtx.from === 'map';
    const cid = fromMap ? '' : _ppSelectedCustomerId();
    DispatchState._savedLocFormCustomerIds = cid ? [cid] : [];
  }
  _renderSavedLocFormCustomers();
  document.getElementById('saved-loc-name')?.focus();
  _prewarmMapResolver();
  const locAddr = document.getElementById('saved-loc-address');
  if (locAddr && window.attachAddressAutocomplete) window.attachAddressAutocomplete(locAddr);
}

function _renderSavedLocFormCustomers() {
  const box = document.getElementById('saved-loc-customers');
  if (!box) return;
  const known = _savedLocKnownCustomerIds();
  const ids = (DispatchState._savedLocFormCustomerIds || []).filter(id => !known || known.has(id));
  const chips = ids.map(id => `
    <span data-saved-loc-cust="${escapeHtml(id)}" style="display:inline-flex;align-items:center;gap:4px;padding:2px 4px 2px 10px;margin:0 6px 6px 0;border-radius:9999px;background:#EFF6FF;border:1px solid #BFDBFE;color:#1E40AF;font-size:0.78rem;font-weight:600;">
      ${escapeHtml(_ppCustomerName(id))}
      <button type="button" class="btn-icon" title="Remove" aria-label="Remove ${escapeHtml(_ppCustomerName(id))}" onclick="window.DispatchModule.removeSavedLocCustomer('${escJsAttr(id)}')" style="padding:0;min-width:24px;min-height:24px;color:#1E40AF;">
        <span class="material-icons" style="font-size:16px;">close</span>
      </button>
    </span>`).join('');
  // TASK-9548 slice 3 — grouped like every customer picker (vendors dropped).
  const options = window.PickerGroups.customerOptionsHtml(
    (DispatchState.customers || []).filter(c => c && !ids.includes(c.id)),
    { nameOf: c => c.name || 'Unnamed customer' });
  box.innerHTML = `
    <div>${chips || '<span style="font-size:0.78rem;color:var(--text-secondary);">Every customer</span>'}</div>
    <select id="saved-loc-customer-add" class="form-control" style="margin-top:4px;font-size:0.85rem;" onchange="window.DispatchModule.addSavedLocCustomer(this.value)">
      <option value="">+ Add a customer…</option>
      ${options}
    </select>`;
}

function addSavedLocCustomer(id) {
  if (!id) return;
  const cur = DispatchState._savedLocFormCustomerIds || [];
  if (!cur.includes(id)) DispatchState._savedLocFormCustomerIds = [...cur, id];
  _renderSavedLocFormCustomers();
}

function removeSavedLocCustomer(id) {
  DispatchState._savedLocFormCustomerIds = (DispatchState._savedLocFormCustomerIds || []).filter(x => x !== id);
  _renderSavedLocFormCustomers();
}

const SAVED_LOC_NO_PIN_MSG =
  "but we couldn't read a map pin from that link. Paste the full Google Maps link or type the address.";
const SAVED_LOC_PIN_TIMEOUT_MSG =
  "but the map service didn't answer, so it has no map pin yet. Open it and Update to retry.";

// Find a pin for a Saved Location being saved: the share link first (followed
// server-side — the browser can't follow a goo.gl redirect itself), then any
// address that link expands to, then the Address box. Never throws; returns
// { coord: {lat,lng}|null, timedOut }.
async function locateSavedLocationPin(url, address) {
  let timedOut = false;
  let geoQuery = null;
  try {
    if (url && isShortMapUrl(url)) {
      let expanded = _resolvedUrlCache[url] || null;
      if (!expanded) {
        const hardDeadlineAt = Date.now() + SHORT_URL_RESOLVE_TOTAL_MS;
        for (let attempt = 1; attempt <= SHORT_URL_RESOLVE_ATTEMPTS && !expanded; attempt++) {
          try { expanded = await _resolveMapUrlOnce(url, hardDeadlineAt); }
          catch (err) {
            if (err && err.mapResolveTimeout) timedOut = true;
            if ((err && err.mapResolveFatal) || hardDeadlineAt - Date.now() < SHORT_URL_RETRY_MIN_REMAINING_MS) break;
            await new Promise(r => setTimeout(r, SHORT_URL_RETRY_BACKOFF_MS));
          }
        }
        if (expanded) { _resolvedUrlCache[url] = expanded; timedOut = false; }
      }
      if (expanded) {
        const c = plottableCoord(extractCoordsFromUrl(expanded));
        if (c) return { coord: c, timedOut: false };
        geoQuery = geocodeQueryFromUrl(expanded);
      }
    } else if (url) {
      geoQuery = geocodeQueryFromUrl(url);
    }
    // COD (Code Reviewer) — the link's address first, THEN the typed Address
    // box; one failing must not skip the other.
    const typed = address ? (geocodeQueryFromUrl(address) || (address.length > 5 ? address : null)) : null;
    const queries = [geoQuery, typed].filter((q, i, a) => q && a.indexOf(q) === i);
    for (const q of queries) {
      try {
        const resp = await _withDeadline(httpsCallable(functions, 'geocodeAddress')({ address: q }), 6000);
        const c = plottableCoord({ lat: resp?.data?.lat, lng: resp?.data?.lng });
        if (c) return { coord: c, timedOut: false };
      } catch (gErr) {
        console.warn('[Dispatch] saved-location geocode failed:', gErr?.code || 'error');
      }
    }
  } catch (err) {
    if (err && err.mapResolveTimeout) timedOut = true;
    console.warn('[Dispatch] saved-location pin lookup failed:', err?.code || (err && err.mapResolveTimeout ? 'timeout' : 'error'));
  }
  return { coord: null, timedOut };
}

async function saveSavedLocationRecord(locId) {
  const name = document.getElementById('saved-loc-name')?.value?.trim();
  const address = document.getElementById('saved-loc-address')?.value?.trim() || '';
  const url = document.getElementById('saved-loc-url')?.value?.trim() || '';
  const gateCode = document.getElementById('saved-loc-gate')?.value?.trim() || '';
  const contact = document.getElementById('saved-loc-contact')?.value?.trim() || '';
  const notes = document.getElementById('saved-loc-notes')?.value?.trim() || '';

  if (!name) {
    window.showToast?.('Location name is required', 'error');
    return;
  }

  const btn = document.getElementById('saved-loc-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // TASK-9631 F2 — the stored pin wins unless the user changed the link. This
  // used to re-derive lat/lng from the URL on EVERY save, so a location whose
  // pin came from a geocode (bookmarked stop) or has no link at all lost its
  // coordinate the first time anyone edited its gate code.
  const prev = locId ? (DispatchState.savedLocations || []).find(l => l && l.id === locId) : null;
  let { lat, lng } = savedLocationCoordsForSave(prev, url, extractCoordsFromUrl(url || ''));
  // Steve/Mike (TITS) 2026-10-08 — this form never followed a share link, so
  // EVERY `maps.app.goo.gl` link saved here stored lat/lng null (HOFFMAN,
  // CORVETTE, MCGINNIS PIT, SC HAMILTON… all 10/8), and nothing said so.
  // Follow it now; fall back to the address the link (or the Address box)
  // carries. If none of that yields a pin the save still goes through — but
  // the dispatcher is TOLD, never left to find out from a driver.
  let _pinNote = null;
  if (lat == null && (url || address)) {
    if (btn) btn.innerHTML = '<span class="btn-spinner"></span> Finding location...';
    const found = await locateSavedLocationPin(url, address);
    if (found.coord) { lat = found.coord.lat; lng = found.coord.lng; }
    else if (url || address) _pinNote = found.timedOut ? SAVED_LOC_PIN_TIMEOUT_MSG : SAVED_LOC_NO_PIN_MSG;
  }

  // TASK-9421 — customer DOC ids only. A tag whose customer no longer exists
  // is dropped here (it already read as untagged in the picker).
  const _known = _savedLocKnownCustomerIds();
  const customerIds = [...new Set((DispatchState._savedLocFormCustomerIds || [])
    .filter(id => typeof id === 'string' && id && (!_known || _known.has(id))))];

  const data = { name, address, url, gateCode, contact, notes, lat, lng, customerIds, updatedAt: Timestamp.now() };
  // TASK-9540 — the category picked on the form. '' = no category (reads as
  // Other); an id no longer in the list is saved as no category, not as a
  // dangling id.
  const catSel = document.getElementById('saved-loc-category');
  if (catSel) {
    const cid = catSel.value || '';
    data.categoryId = cid && _placeCats().some(c => c.id === cid) ? cid : '';
  }

  try {
    const tenant = DispatchState.tenantId;
    if (locId) {
      // `source` is never rewritten on edit: it records where the location
      // CAME FROM, and editing it by hand doesn't change that.
      await updateDoc(doc(db, 'tenants', tenant, 'savedLocations', locId), data);
      window.showToast?.(_pinNote ? `Location updated — ${_pinNote}` : 'Location updated', _pinNote ? 'warning' : 'success', null, _pinNote ? 9000 : undefined);
    } else {
      data.source = 'manual'; // TASK-9421
      data.createdAt = Timestamp.now();
      await addDoc(collection(db, 'tenants', tenant, 'savedLocations'), data);
      window.showToast?.(_pinNote ? `Location saved — ${_pinNote}` : 'Location saved', _pinNote ? 'warning' : 'success', null, _pinNote ? 9000 : undefined);
    }
    DispatchState._placesDirty = true;
    _placesReturnToList();
  } catch (err) {
    console.error('[Dispatch] Save location error:', err);
    _placesWriteFailedToast(err, 'Error saving location');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save'; }
  }
}

// TASK-9540 — delete from EITHER surface (Add Job's picker or the Places
// manager) goes through here. showConfirm, never native confirm() (TASK-9540
// slice 0). Jobs keep their own copy of every stop, so deleting a saved place
// never touches a job; the confirm says so.
async function deleteSavedLocationRecord(locId) {
  const loc = (DispatchState.savedLocations || []).find(l => l && l.id === locId);
  if (!loc) return;
  if (!_canEditPlaces(true)) { window.showToast?.(PLACES_VIEW_ONLY_MSG, 'error', null, 8000); return; }
  const ok = await window.showConfirm(
    'Delete saved place?',
    `"${loc.name || 'Unnamed place'}" comes off the saved list and the map for everyone. Jobs that already use it keep their destination.`,
    { confirmText: 'Delete', cancelText: 'Keep it', danger: true, icon: 'delete' }
  );
  if (!ok) return;
  try {
    await deleteDoc(doc(db, 'tenants', DispatchState.tenantId, 'savedLocations', locId));
    // The snapshot listener will drop it too; do it now so the list the user
    // is looking at never shows a row that is already gone.
    DispatchState.savedLocations = (DispatchState.savedLocations || []).filter(l => l && l.id !== locId);
    DispatchState._placesDirty = true;
    window.showToast?.(`Deleted "${loc.name || 'place'}"`, 'success');
    _placesReturnToList();
  } catch (err) {
    console.error('[Dispatch] Delete location error:', err);
    _placesWriteFailedToast(err, 'Error deleting');
  }
}

// ============================================
// TASK-9540 — PLACES MANAGER + PLACE CATEGORIES
// ============================================
// ONE manager, opened from Add Job's saved-location picker ("Manage") and from
// the map key ("Manage" next to Saved waypoints). Both open this same render,
// in the same overlay, so they cannot drift apart. Two tabs: Places (pick each
// place's category, edit, delete, confirm name-based suggestions) and
// Categories (name + shape + color; add, edit, delete).
//
// Access mirrors firestore.rules placesWriter EXACTLY (module, never role):
// demo; not on a complianceOnly carrier; the OWNER by tenants.ownerId, or a
// member holding `dispatch` who is not readOnly — and for edit/delete not
// viewUploadOnly. The UI hides what it would refuse (house pattern: hide, not
// disable). The rules are the real gate; a denial lands on PLACES_VIEW_ONLY_MSG.
const PLACES_VIEW_ONLY_MSG = "Your login can see saved places but can't change them. Ask the owner to give you the Dispatch module.";

function _canEditPlaces(edit = true) {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo' || DispatchState.tenantId === 'demo') return true;
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (st.tenantData?.complianceOnly === true) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  if (edit && ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('dispatch');
}

function _placesWriteFailedToast(err, fallback) {
  if (err && err.code === 'permission-denied') window.showToast?.(PLACES_VIEW_ONLY_MSG, 'error', null, 8000);
  else window.showToast?.(fallback, 'error');
}

function _placeCats() {
  return mergePlaceCategories(DispatchState.waypointCategoryDocs || []);
}

function _placeCatBadge(cat, size = 16) {
  return `<span class="pm-cat-badge">${placeShapeSvg(cat.shape, cat.color, size)}<span>${escapeHtml(cat.name)}</span></span>`;
}

// Where the place form / a delete goes back to: the manager when it was opened
// from there, else the Add Job picker (unchanged behaviour).
function _placesReturnToList() {
  if (DispatchState._placesCtx) renderPlacesManager();
  else showSavedLocationsPicker();
}

// Snapshot hook (savedLocations + waypointCategories). Re-renders only what is
// open; never repaints the map from here (a teammate's edit must not redraw
// the map under a dispatcher) — the map repaints when the manager closes.
function _placesDataChanged() {
  if (!DispatchState._placesCtx) return;
  const overlay = document.getElementById('dispatch-modal-overlay');
  if (!overlay || overlay.classList.contains('hidden') || !overlay.querySelector('.places-mgr')) return;
  // Don't wipe a category form the user is typing into.
  if (overlay.querySelector('#pm-cat-form')) return;
  renderPlacesManager();
}

function _ensurePlacesCss() {
  if (document.getElementById('places-mgr-css')) return;
  const st = document.createElement('style');
  st.id = 'places-mgr-css';
  st.textContent = `
    .places-mgr { max-width: 560px; max-height: 88vh; max-height: 88dvh; margin: auto; display: flex; flex-direction: column; }
    .places-mgr .pm-head { padding: 14px 16px; background: linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%); color: #fff; display: flex; align-items: center; justify-content: space-between; gap: 8px; flex-shrink: 0; }
    .places-mgr .pm-head h3 { margin: 0; font-size: 1.1rem; font-weight: 700; display: flex; align-items: center; gap: 6px; }
    .places-mgr .pm-head .pm-icon-btn { color: #fff; }
    .places-mgr .pm-tabs { display: flex; border-bottom: 1px solid var(--border-light); flex-shrink: 0; background: var(--bg-card); }
    .places-mgr .pm-tab { flex: 1; min-height: 48px; background: none; border: none; border-bottom: 3px solid transparent; font-weight: 700; font-size: 0.9rem; color: var(--text-secondary); cursor: pointer; }
    .places-mgr .pm-tab[aria-selected="true"] { color: var(--text-primary); border-bottom-color: var(--brand-blue); }
    .places-mgr .pm-body { overflow-y: auto; flex: 1; -webkit-overflow-scrolling: touch; }
    .places-mgr .pm-section { padding: 8px 16px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-secondary); background: var(--bg-secondary); }
    .places-mgr .pm-row { display: flex; align-items: center; gap: 10px; padding: 10px 16px; border-bottom: 1px solid var(--border-light); flex-wrap: wrap; }
    .places-mgr .pm-row-main { flex: 1 1 180px; min-width: 0; }
    .places-mgr .pm-name { font-weight: 600; font-size: 0.92rem; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .places-mgr .pm-sub { font-size: 0.78rem; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .places-mgr .pm-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
    .places-mgr .pm-icon-btn { min-width: 48px; min-height: 48px; display: inline-flex; align-items: center; justify-content: center; background: none; border: none; border-radius: 10px; cursor: pointer; color: var(--text-secondary); }
    .places-mgr .pm-icon-btn:hover { background: var(--bg-secondary); }
    .places-mgr .pm-icon-btn.pm-danger { color: #DC2626; }
    .places-mgr .pm-btn { min-height: 48px; height: 48px; padding: 0 16px; font-weight: 600; box-sizing: border-box; } /* height, not min-height: the phone .btn rule pins min-height 40-44px !important */
    .places-mgr .pm-cat-select { min-height: 48px; font-size: 0.9rem; max-width: 190px; }
    .places-mgr .pm-cat-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 0.8rem; font-weight: 600; color: var(--text-primary); }
    .places-mgr .pm-search { padding: 10px 16px; border-bottom: 1px solid var(--border-light); }
    .places-mgr .pm-search input { min-height: 48px; font-size: 0.95rem; }
    .places-mgr .pm-note { padding: 10px 16px; font-size: 0.85rem; color: var(--text-secondary); }
    .places-mgr .pm-suggest { background: #FFFBEB; border-bottom: 1px solid #FDE68A; }
    .places-mgr .pm-form { padding: 16px; display: flex; flex-direction: column; gap: 14px; }
    .places-mgr .pm-form label.pm-lbl { font-size: 0.8rem; font-weight: 700; color: var(--text-secondary); display: block; margin-bottom: 6px; }
    .places-mgr .pm-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; }
    .places-mgr .pm-grid.pm-colors { grid-template-columns: repeat(7, 1fr); }
    .places-mgr .pm-choice { min-height: 48px; border: 2px solid var(--border-light); border-radius: 10px; background: #fff; display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 0; }
    .places-mgr .pm-choice[aria-pressed="true"] { border-color: var(--text-primary); box-shadow: 0 0 0 2px rgba(59,130,246,0.35); }
    .places-mgr .pm-color-dot { width: 26px; height: 26px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px rgba(15,23,42,0.25); }
    .places-mgr .pm-foot { padding: 12px 16px; border-top: 1px solid var(--border-light); display: flex; gap: 10px; justify-content: flex-end; flex-shrink: 0; flex-wrap: wrap; }
    .places-mgr .pm-error { color: #B91C1C; font-size: 0.85rem; font-weight: 600; }
    @media (max-width: 600px) {
      .places-mgr .pm-grid { grid-template-columns: repeat(3, 1fr); }
      .places-mgr .pm-grid.pm-colors { grid-template-columns: repeat(4, 1fr); }
      .places-mgr .pm-cat-select { max-width: none; flex: 1 1 100%; }
    }
    .pm-cat-badge { display: inline-flex; align-items: center; gap: 4px; }
  `;
  document.head.appendChild(st);
}

// Open the ONE manager. `from` = 'job' (Add Job picker) | 'map' (map key).
// Closing returns to where it was opened: the picker, or the map (repainted if
// anything changed).
function openPlacesManager(from = 'map', tab = 'places') {
  DispatchState._placesCtx = { from: from === 'job' ? 'job' : 'map', tab: tab === 'categories' ? 'categories' : 'places', q: '' };
  DispatchState._placesDirty = false;
  renderPlacesManager();
}

function closePlacesManager() {
  const ctx = DispatchState._placesCtx;
  DispatchState._placesCtx = null;
  const overlay = document.getElementById('dispatch-modal-overlay');
  if (ctx && ctx.from === 'job') { showSavedLocationsPicker(); return; }
  overlay?.classList.add('hidden');
  if (DispatchState._placesDirty) { DispatchState._placesDirty = false; repaintMapView(); }
}

function setPlacesManagerTab(tab) {
  if (!DispatchState._placesCtx) return;
  DispatchState._placesCtx.tab = tab === 'categories' ? 'categories' : 'places';
  DispatchState._placesCtx.editCat = null;
  renderPlacesManager();
}

function _placesOverlay() {
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'dispatch-modal-overlay';
    modal.className = 'modal';
    document.body.appendChild(modal);
  }
  return modal;
}

function _renderPlacesTab(cats, canEdit) {
  const ctx = DispatchState._placesCtx;
  const places = (DispatchState.savedLocations || []).slice()
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  const q = String(ctx.q || '').toLowerCase().trim();
  const shown = q ? places.filter(p => [p.name, p.address, p.notes].some(v => String(v || '').toLowerCase().includes(q))) : places;
  let html = `<div class="pm-search"><input type="search" id="pm-search" class="form-control" placeholder="Search saved places…" value="${escapeHtml(ctx.q || '')}" autocomplete="off" aria-label="Search saved places"></div>`;

  // Name-based suggestions — the user confirms each one; never a bulk write.
  const dismissed = new Set(DispatchState._placeSuggestDismissed || []);
  const sugg = canEdit ? placeCategorySuggestions(places, cats).filter(s => !dismissed.has(s.placeId)) : [];
  if (sugg.length && !q) {
    html += `<div class="pm-section">Suggested categories (${sugg.length})</div>
      <div class="pm-note pm-suggest">These places have no category. We guessed from the name — check each one.</div>`;
    html += sugg.slice(0, 12).map(s => {
      const c = cats.find(x => x.id === s.categoryId);
      return `<div class="pm-row pm-suggest" data-pm-suggest-row="${escapeHtml(s.placeId)}">
        <div class="pm-row-main"><div class="pm-name">${escapeHtml(s.name || 'Unnamed place')}</div><div class="pm-sub">Looks like: ${_placeCatBadge(c, 14)}</div></div>
        <div class="pm-actions">
          <button type="button" class="btn btn-primary btn-sm pm-btn" data-pm-suggest-use="${escapeHtml(s.placeId)}" data-pm-cat="${escapeHtml(s.categoryId)}">Use</button>
          <button type="button" class="btn btn-secondary btn-sm pm-btn" data-pm-suggest-skip="${escapeHtml(s.placeId)}">Skip</button>
        </div></div>`;
    }).join('');
    if (sugg.length > 12) html += `<div class="pm-note">${sugg.length - 12} more after these.</div>`;
  }

  html += `<div class="pm-section">Saved places (${places.length})</div>`;
  if (!places.length) {
    html += `<div class="pm-note">No saved places yet. Tap <strong>Add place</strong>, or bookmark a destination on any job.</div>`;
  } else if (!shown.length) {
    html += `<div class="pm-note">No saved place matches "${escapeHtml(ctx.q)}".</div>`;
  }
  html += shown.map(p => {
    const cat = categoryForPlace(p, cats);
    const hasCat = typeof p.categoryId === 'string' && p.categoryId && cats.some(c => c.id === p.categoryId);
    const pin = plottableCoord(p) || plottableCoord(extractCoordsFromUrl(p.url || ''));
    const sub = [p.address, !pin ? 'Not on the map (no pin)' : ''].filter(Boolean).join(' · ');
    const select = canEdit
      ? `<select class="form-control pm-cat-select" data-pm-place-cat="${escapeHtml(p.id)}" aria-label="Category for ${escapeHtml(p.name || 'place')}">
          <option value=""${hasCat ? '' : ' selected'}>No category (Other)</option>
          ${cats.filter(c => c.id !== OTHER_CATEGORY_ID).map(c => `<option value="${escapeHtml(c.id)}"${hasCat && p.categoryId === c.id ? ' selected' : ''}>${escapeHtml(c.name)}</option>`).join('')}
          <option value="${OTHER_CATEGORY_ID}"${hasCat && p.categoryId === OTHER_CATEGORY_ID ? ' selected' : ''}>${escapeHtml(cats.find(c => c.id === OTHER_CATEGORY_ID).name)}</option>
        </select>`
      : '';
    return `<div class="pm-row" data-pm-place-row="${escapeHtml(p.id)}">
      ${placeShapeSvg(cat.shape, cat.color, 26)}
      <div class="pm-row-main"><div class="pm-name">${escapeHtml(p.name || 'Unnamed place')}</div>
        <div class="pm-sub">${canEdit ? '' : _placeCatBadge(cat, 12) + (sub ? ' · ' : '')}${escapeHtml(sub)}</div></div>
      ${select}
      ${canEdit ? `<div class="pm-actions">
        <button type="button" class="pm-icon-btn" data-pm-place-edit="${escapeHtml(p.id)}" title="Edit" aria-label="Edit ${escapeHtml(p.name || 'place')}"><span class="material-icons">edit</span></button>
        <button type="button" class="pm-icon-btn pm-danger" data-pm-place-delete="${escapeHtml(p.id)}" title="Delete" aria-label="Delete ${escapeHtml(p.name || 'place')}"><span class="material-icons">delete</span></button>
      </div>` : ''}
    </div>`;
  }).join('');
  return html;
}

function _renderCategoriesTab(cats, canEdit) {
  const ctx = DispatchState._placesCtx;
  if (ctx.editCat) return _renderCategoryForm(cats, ctx.editCat);
  const counts = countPlacesByCategory(DispatchState.savedLocations || [], cats);
  let html = `<div class="pm-note">Categories are shared by your whole team. Each one draws its places on the map as its own shape and color.</div>`;
  html += cats.map(c => `<div class="pm-row" data-pm-cat-row="${escapeHtml(c.id)}">
      ${placeShapeSvg(c.shape, c.color, 28)}
      <div class="pm-row-main"><div class="pm-name">${escapeHtml(c.name)}</div>
        <div class="pm-sub">${counts[c.id] || 0} place${(counts[c.id] || 0) === 1 ? '' : 's'}${c.id === OTHER_CATEGORY_ID ? ' · places with no category show here' : ''}</div></div>
      ${canEdit ? `<div class="pm-actions">
        <button type="button" class="pm-icon-btn" data-pm-cat-edit="${escapeHtml(c.id)}" title="Edit" aria-label="Edit ${escapeHtml(c.name)}"><span class="material-icons">edit</span></button>
        ${c.id === OTHER_CATEGORY_ID ? '<span style="min-width:48px;"></span>' : `<button type="button" class="pm-icon-btn pm-danger" data-pm-cat-delete="${escapeHtml(c.id)}" title="Delete" aria-label="Delete ${escapeHtml(c.name)}"><span class="material-icons">delete</span></button>`}
      </div>` : ''}
    </div>`).join('');
  return html;
}

function _renderCategoryForm(cats, edit) {
  const draft = edit.draft;
  return `<div class="pm-form" id="pm-cat-form">
    <div>
      <label class="pm-lbl" for="pm-cat-name">Name</label>
      <input type="text" id="pm-cat-name" class="form-control" maxlength="${PLACE_CAT_NAME_MAX}" value="${escapeHtml(draft.name)}" placeholder="e.g. Frac pond" style="min-height:48px;font-size:1rem;">
    </div>
    <div>
      <span class="pm-lbl" id="pm-shape-lbl">Shape on the map</span>
      <div class="pm-grid" role="group" aria-labelledby="pm-shape-lbl">
        ${PLACE_SHAPE_IDS.map(sh => `<button type="button" class="pm-choice" data-pm-shape="${sh}" aria-pressed="${draft.shape === sh ? 'true' : 'false'}" aria-label="${escapeHtml(PLACE_SHAPES[sh].label)}" title="${escapeHtml(PLACE_SHAPES[sh].label)}">${placeShapeSvg(sh, draft.color, 28)}</button>`).join('')}
      </div>
    </div>
    <div>
      <span class="pm-lbl" id="pm-color-lbl">Color</span>
      <div class="pm-grid pm-colors" role="group" aria-labelledby="pm-color-lbl">
        ${PLACE_COLORS.map(c => `<button type="button" class="pm-choice" data-pm-color="${c.hex}" aria-pressed="${draft.color === c.hex ? 'true' : 'false'}" aria-label="${escapeHtml(c.label)}" title="${escapeHtml(c.label)}"><span class="pm-color-dot" style="background:${c.hex};"></span></button>`).join('')}
      </div>
    </div>
    <div style="display:flex;align-items:center;gap:10px;font-size:0.9rem;color:var(--text-primary);">Preview: ${placeShapeSvg(draft.shape, draft.color, 34)} <strong id="pm-cat-preview-name">${escapeHtml(draft.name || 'New category')}</strong></div>
    <div class="pm-error" id="pm-cat-error" role="alert"></div>
  </div>
  <div class="pm-foot">
    <button type="button" class="btn btn-secondary pm-btn" data-pm-cat-cancel>Cancel</button>
    <button type="button" class="btn btn-primary pm-btn" data-pm-cat-save>${edit.id ? 'Save category' : 'Add category'}</button>
  </div>`;
}

function renderPlacesManager() {
  const ctx = DispatchState._placesCtx;
  if (!ctx) return;
  _ensurePlacesCss();
  const overlay = _placesOverlay();
  const cats = _placeCats();
  const canEdit = _canEditPlaces(true);
  const canAdd = _canEditPlaces(false);
  const tab = ctx.tab;
  const body = tab === 'categories' ? _renderCategoriesTab(cats, canEdit) : _renderPlacesTab(cats, canEdit);
  const foot = ctx.editCat ? '' : (tab === 'categories'
    ? (canEdit ? `<div class="pm-foot"><button type="button" class="btn btn-primary pm-btn" data-pm-cat-new><span class="material-icons" style="font-size:18px;vertical-align:text-bottom;">add</span> New category</button></div>` : '')
    : (canAdd ? `<div class="pm-foot"><button type="button" class="btn btn-primary pm-btn" data-pm-place-new><span class="material-icons" style="font-size:18px;vertical-align:text-bottom;">add_location_alt</span> Add place</button></div>` : ''));
  overlay.onclick = (e) => { if (e.target === overlay) closePlacesManager(); };
  overlay.classList.remove('hidden');
  overlay.innerHTML = `
    <div class="modal-content places-mgr" role="dialog" aria-modal="true" aria-labelledby="pm-title">
      <div class="pm-head">
        <h3 id="pm-title"><span class="material-icons" style="font-size:20px;" aria-hidden="true">place</span> Manage places</h3>
        <button type="button" class="pm-icon-btn" data-pm-close aria-label="${ctx.from === 'job' ? 'Back to saved locations' : 'Close'}" title="${ctx.from === 'job' ? 'Back to saved locations' : 'Close'}"><span class="material-icons">${ctx.from === 'job' ? 'arrow_back' : 'close'}</span></button>
      </div>
      <div class="pm-tabs" role="tablist">
        <button type="button" class="pm-tab" role="tab" data-pm-tab="places" aria-selected="${tab === 'places' ? 'true' : 'false'}">Places</button>
        <button type="button" class="pm-tab" role="tab" data-pm-tab="categories" aria-selected="${tab === 'categories' ? 'true' : 'false'}">Categories</button>
      </div>
      ${canEdit ? '' : `<div class="pm-note" style="background:var(--bg-secondary);">${escapeHtml(canAdd ? 'Your login can add places but not change or delete them.' : PLACES_VIEW_ONLY_MSG)}</div>`}
      <div class="pm-body">${body}</div>
      ${foot}
    </div>`;
  const root = overlay.querySelector('.places-mgr');
  root.addEventListener('click', _onPlacesManagerClick);
  root.addEventListener('change', _onPlacesManagerChange);
  const search = root.querySelector('#pm-search');
  if (search) {
    search.addEventListener('input', () => {
      ctx.q = search.value;
      const pos = search.selectionStart;
      renderPlacesManager();
      const again = document.getElementById('pm-search');
      if (again) { again.focus(); try { again.setSelectionRange(pos, pos); } catch (_) { /* type=search */ } }
    });
  }
  const nameIn = root.querySelector('#pm-cat-name');
  if (nameIn) {
    nameIn.addEventListener('input', () => {
      ctx.editCat.draft.name = nameIn.value;
      const pv = document.getElementById('pm-cat-preview-name');
      if (pv) pv.textContent = nameIn.value || 'New category';
    });
    if (!ctx.editCat.focused) { ctx.editCat.focused = true; nameIn.focus(); }
  }
}

function _onPlacesManagerClick(e) {
  const ctx = DispatchState._placesCtx;
  if (!ctx) return;
  const t = (sel) => e.target.closest(sel);
  let el;
  if (t('[data-pm-close]')) { closePlacesManager(); return; }
  if ((el = t('[data-pm-tab]'))) { setPlacesManagerTab(el.dataset.pmTab); return; }
  if (t('[data-pm-place-new]')) { showSavedLocationForm(null); return; }
  if ((el = t('[data-pm-place-edit]'))) {
    const loc = (DispatchState.savedLocations || []).find(l => l.id === el.dataset.pmPlaceEdit);
    if (loc) { showSavedLocationForm(loc); }
    return;
  }
  if ((el = t('[data-pm-place-delete]'))) { deleteSavedLocationRecord(el.dataset.pmPlaceDelete); return; }
  if ((el = t('[data-pm-suggest-use]'))) { setSavedPlaceCategory(el.dataset.pmSuggestUse, el.dataset.pmCat); return; }
  if ((el = t('[data-pm-suggest-skip]'))) {
    DispatchState._placeSuggestDismissed = [...(DispatchState._placeSuggestDismissed || []), el.dataset.pmSuggestSkip];
    renderPlacesManager();
    return;
  }
  if (t('[data-pm-cat-new]')) {
    ctx.editCat = { id: null, draft: { name: '', shape: 'hexagon', color: PLACE_COLORS[0].hex } };
    renderPlacesManager();
    return;
  }
  if ((el = t('[data-pm-cat-edit]'))) {
    const c = _placeCats().find(x => x.id === el.dataset.pmCatEdit);
    if (c) { ctx.editCat = { id: c.id, draft: { name: c.name, shape: c.shape, color: c.color } }; renderPlacesManager(); }
    return;
  }
  if ((el = t('[data-pm-cat-delete]'))) { deletePlaceCategory(el.dataset.pmCatDelete); return; }
  if ((el = t('[data-pm-shape]')) && ctx.editCat) { ctx.editCat.draft.shape = el.dataset.pmShape; renderPlacesManager(); return; }
  if ((el = t('[data-pm-color]')) && ctx.editCat) { ctx.editCat.draft.color = el.dataset.pmColor; renderPlacesManager(); return; }
  if (t('[data-pm-cat-cancel]')) { ctx.editCat = null; renderPlacesManager(); return; }
  if (t('[data-pm-cat-save]')) { savePlaceCategory(); return; }
}

function _onPlacesManagerChange(e) {
  const sel = e.target.closest('[data-pm-place-cat]');
  if (sel) setSavedPlaceCategory(sel.dataset.pmPlaceCat, sel.value);
}

// One place, one write. Used by the per-row select AND by "Use" on a
// suggestion — so a suggestion is applied only by a tap on that row.
async function setSavedPlaceCategory(placeId, categoryId) {
  if (!_canEditPlaces(true)) { window.showToast?.(PLACES_VIEW_ONLY_MSG, 'error', null, 8000); return; }
  const loc = (DispatchState.savedLocations || []).find(l => l && l.id === placeId);
  if (!loc) return;
  const cid = categoryId && _placeCats().some(c => c.id === categoryId) ? categoryId : '';
  try {
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'savedLocations', placeId), { categoryId: cid, updatedAt: Timestamp.now() });
    loc.categoryId = cid;
    DispatchState._placesDirty = true;
    const c = _placeCats().find(x => x.id === (cid || OTHER_CATEGORY_ID));
    window.showToast?.(`"${loc.name || 'Place'}" is now ${c ? c.name : 'Other'}`, 'success');
    _rerenderPlacesManagerIfShown();
  } catch (err) {
    console.error('[Dispatch] set place category error:', err);
    _placesWriteFailedToast(err, 'Could not change the category');
    _rerenderPlacesManagerIfShown();
  }
}

// After an awaited write: repaint the manager ONLY if it is still what is on
// screen. If the user opened the place form (or closed the manager) while the
// write was in flight, painting over it would throw away what they typed.
function _rerenderPlacesManagerIfShown() {
  if (!DispatchState._placesCtx) return;
  const overlay = document.getElementById('dispatch-modal-overlay');
  if (!overlay || overlay.classList.contains('hidden') || !overlay.querySelector('.places-mgr')) return;
  renderPlacesManager();
}

async function savePlaceCategory() {
  const ctx = DispatchState._placesCtx;
  if (!ctx || !ctx.editCat) return;
  if (!_canEditPlaces(true)) { window.showToast?.(PLACES_VIEW_ONLY_MSG, 'error', null, 8000); return; }
  const nameIn = document.getElementById('pm-cat-name');
  if (nameIn) ctx.editCat.draft.name = nameIn.value;
  const v = validatePlaceCategory(ctx.editCat.draft);
  const errEl = document.getElementById('pm-cat-error');
  if (!v.ok) { if (errEl) errEl.textContent = v.error; return; }
  const cats = _placeCats();
  const dupe = cats.find(c => c.id !== ctx.editCat.id && c.name.toLowerCase() === v.value.name.toLowerCase());
  if (dupe) { if (errEl) errEl.textContent = `There is already a category called "${dupe.name}".`; return; }
  const id = ctx.editCat.id || doc(collection(db, 'tenants', DispatchState.tenantId, 'waypointCategories')).id;
  const prevDoc = (DispatchState.waypointCategoryDocs || []).find(d => d.id === id);
  const data = { ...v.value, hidden: false, updatedAt: Timestamp.now() };
  data.createdAt = prevDoc && prevDoc.createdAt ? prevDoc.createdAt : Timestamp.now();
  try {
    // setDoc, whole doc: a starter becomes an override doc the first time it
    // is edited; the rules check every key (placeCatOk).
    await setDoc(doc(db, 'tenants', DispatchState.tenantId, 'waypointCategories', id), data);
    ctx.editCat = null;
    DispatchState._placesDirty = true;
    window.showToast?.(`Saved "${v.value.name}"`, 'success');
    _rerenderPlacesManagerIfShown();
  } catch (err) {
    console.error('[Dispatch] save place category error:', err);
    _placesWriteFailedToast(err, 'Could not save the category');
  }
}

// A starter is "deleted" by an override doc with hidden:true (it lives in
// code); a custom category doc is deleted outright. Places in it are NOT
// rewritten — no bulk write — they simply read as Other from now on, and the
// confirm says so.
async function deletePlaceCategory(catId) {
  if (catId === OTHER_CATEGORY_ID) return;
  if (!_canEditPlaces(true)) { window.showToast?.(PLACES_VIEW_ONLY_MSG, 'error', null, 8000); return; }
  const cats = _placeCats();
  const cat = cats.find(c => c.id === catId);
  if (!cat) return;
  const n = countPlacesByCategory(DispatchState.savedLocations || [], cats)[catId] || 0;
  const ok = await window.showConfirm(
    'Delete category?',
    `"${cat.name}" goes away for your whole team.${n ? ` Its ${n} place${n === 1 ? '' : 's'} will show as Other until you pick a new category.` : ''}`,
    { confirmText: 'Delete', cancelText: 'Keep it', danger: true, icon: 'delete' }
  );
  if (!ok) return;
  try {
    const ref = doc(db, 'tenants', DispatchState.tenantId, 'waypointCategories', catId);
    if (isStarterCategoryId(catId)) {
      const prevDoc = (DispatchState.waypointCategoryDocs || []).find(d => d.id === catId);
      const hide = { name: cat.name, shape: cat.shape, color: cat.color, hidden: true, updatedAt: Timestamp.now() };
      if (prevDoc && prevDoc.createdAt) hide.createdAt = prevDoc.createdAt;
      await setDoc(ref, hide);
    } else {
      await deleteDoc(ref);
    }
    DispatchState._placesDirty = true;
    window.showToast?.(`Deleted "${cat.name}"`, 'success');
    _rerenderPlacesManagerIfShown();
  } catch (err) {
    console.error('[Dispatch] delete place category error:', err);
    _placesWriteFailedToast(err, 'Could not delete the category');
  }
}

// ── Map key: per-category on/off (per-viewer convenience, like the layer
// toggles; storage may throw and the map must render regardless).
function _mapSavedHiddenKey() {
  return 'dd_map_saved_hidden_' + (DispatchState.tenantId || window.AppState?.tenant || '');
}
function getMapSavedHiddenCats() {
  if (!Array.isArray(DispatchState.mapSavedHiddenCats)) {
    let v = [];
    try { const s = JSON.parse(localStorage.getItem(_mapSavedHiddenKey()) || '[]'); if (Array.isArray(s)) v = s.filter(x => typeof x === 'string'); } catch (_) { /* defaults */ }
    DispatchState.mapSavedHiddenCats = v;
  }
  return DispatchState.mapSavedHiddenCats;
}
// TASK-9631 S3 — opening a saved map sets the category toggles to its own,
// in memory only (COD r2): a refresh or tenant switch while a map is open
// comes back to the viewer's remembered toggles, and Close puts them back.
function _setMapSavedHiddenCats(ids, persist) {
  DispatchState.mapSavedHiddenCats = [...new Set((ids || []).filter(x => typeof x === 'string'))];
  if (persist) { try { localStorage.setItem(_mapSavedHiddenKey(), JSON.stringify(DispatchState.mapSavedHiddenCats)); } catch (_) { /* not persisted */ } }
}
function toggleMapSavedCategory(catId) {
  const cur = new Set(getMapSavedHiddenCats());
  if (cur.has(catId)) cur.delete(catId); else cur.add(catId);
  DispatchState.mapSavedHiddenCats = [...cur];
  // TASK-9631 S3 — while a saved map is open the toggles are that map's (saved
  // with it), not the viewer's remembered ones.
  if (!DispatchState.mapViewActive) { try { localStorage.setItem(_mapSavedHiddenKey(), JSON.stringify(DispatchState.mapSavedHiddenCats)); } catch (_) { /* not persisted */ } }
  repaintMapView();
}

// ============================================
// JOB PHOTO FUNCTIONS
// ============================================
// TASK-9397 — a job photo still converting isn't in pendingJobPhotos yet;
// saveJob refuses while this is busy (same shape as the permit prep lock).
const _jobPhotoPrep = createPhotoPrepTracker();

function handleJobPhoto(event) {
  return _jobPhotoPrep.track(_handleJobPhoto(event));
}

async function _handleJobPhoto(event) {
  const file = event.target.files[0];
  if (!file) return;

  // Reset input so same file can be re-selected
  event.target.value = '';

  // TASK-9546 — a PDF picked here is a permit, not a photo. Hand it to the
  // permit path (same cap, same size limit, same content check, same list that
  // drivers/billing/Permit Pete read) BEFORE the photo cap is consulted:
  // "Maximum N photos" would be the wrong refusal for it. Nobody uploads a PDF
  // of a photo (Chris, 2026-10-01), so this never steals a real photo. The
  // image pipeline below never sees it.
  if (_looksLikePdf(file)) {
    await _addPermitFiles([file], { source: 'photos' });
    return;
  }

  const totalPhotos = DispatchState.pendingJobPhotos.length + DispatchState.existingJobPhotos.length;
  if (totalPhotos >= STARTER_LIMITS.jobs.maxPhotos) {
    window.showToast?.(`Maximum ${STARTER_LIMITS.jobs.maxPhotos} photos allowed`, 'warning');
    return;
  }

  const validation = validateImageFile(file);
  if (!validation.valid) {
    window.showToast?.(validation.error, 'error');
    return;
  }

  // The job modal resets pendingJobPhotos to a NEW array on open; a photo
  // finishing after the dispatcher moved to another job must not land there.
  const pendingList = DispatchState.pendingJobPhotos;
  try {
    const id = `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    // TASK-9397 — HEIC used to go up RAW as `_full.jpg`/`_thumb.jpg` labelled
    // image/jpeg (broken everywhere but Safari). Convert when this browser
    // can't draw it, then the usual JPEG + thumbnail. Never raw.
    const drawable = await toDrawablePhoto(file);
    const processed = await processPhoto(drawable, {
      maxSizeKB: STARTER_LIMITS.jobs.maxPhotoSizeKB
    });
    const thumbnail = await generateThumbnail(drawable);
    if (DispatchState.pendingJobPhotos !== pendingList) return;

    const priorCount = DispatchState.existingJobPhotos.length + DispatchState.pendingJobPhotos.length;
    DispatchState.pendingJobPhotos.push({
      id,
      fullBlob: processed,
      thumbBlob: thumbnail,
      previewUrl: URL.createObjectURL(thumbnail)
    });

    const shared = _defaultPhotosVisibleOnFirstPhoto(priorCount);
    renderJobPhotoGrid();
    window.showToast?.(shared ? 'Photo added — drivers on this job will see it' : 'Photo added', 'success');
  } catch (error) {
    console.error('[Dispatch] Photo processing error:', error?.cause || error);
    window.showToast?.(photoErrorMessage(error, 'Failed to process photo'), 'error');
  }
}

// TASK-10224 (Willy 2026-10-29) — a note said "picture attached" and the
// driver saw none: Include photos in Driver's job view was off, which is also
// the box's default. (1) The office's FIRST photo on a job ticks the box,
// unless the dispatcher already chose it on this form; a job that already has
// photos keeps its setting, and saved jobs are never rewritten. (2) While the
// box is off and the notes mention an attachment, the editor says so. Word
// bounds: "pic" must not match "pickup".
const _NOTE_MENTIONS_ATTACHMENT_RE = /\b(attach\w*|pictures?|photos?|pics?)\b/i;
function _photosHiddenHintShows(notes, photosVisible, photoCount) {
  return !photosVisible && photoCount > 0 && _NOTE_MENTIONS_ATTACHMENT_RE.test(String(notes || ''));
}
function _syncPhotosHiddenHint() {
  const hint = document.getElementById('job-photos-hidden-hint');
  if (!hint) return;
  const n = (DispatchState.existingJobPhotos?.length || 0) + (DispatchState.pendingJobPhotos?.length || 0);
  const show = window.AppState?.driverModePin !== true &&
    _photosHiddenHintShows(document.getElementById('job-notes')?.value, document.getElementById('job-photos-visible')?.checked === true, n);
  hint.style.display = show ? '' : 'none';
}
function _defaultPhotosVisibleOnFirstPhoto(priorCount) {
  if (priorCount !== 0 || DispatchState.photosVisibleTouched === true || window.AppState?.driverModePin === true) return false;
  const box = document.getElementById('job-photos-visible');
  if (!box || box.checked) return false;
  box.checked = true;
  return true;
}
function onJobPhotosVisibleChange() {
  DispatchState.photosVisibleTouched = true;
  _syncPhotosHiddenHint();
}

function renderJobPhotoGrid() {
  _syncPhotosHiddenHint(); // TASK-10224
  const grid = document.getElementById('job-photo-grid');
  const countEl = document.getElementById('job-photo-count');
  if (!grid || !countEl) return;

  const total = DispatchState.existingJobPhotos.length + DispatchState.pendingJobPhotos.length;
  countEl.textContent = `${total}/${STARTER_LIMITS.jobs.maxPhotos} photos`;

  let html = '';

  // Existing photos
  // TASK-10106 S5 — a PIN driver sees the X only on a photo they added from the
  // driver board, and office photos only when the job shares them with drivers.
  const _drv = window.AppState?.driverModePin === true;
  const _drvSeesOffice = document.getElementById('job-photos-visible')?.checked === true;
  DispatchState.existingJobPhotos.forEach((photo, idx) => {
    const own = _drv && _driverOwnsJobFile(photo);
    if (_drv && !own && !_drvSeesOffice) return;
    html += `
      <div class="photo-grid-item">
        <img ${window.StorageFile.imgAttrs(photo, { urlKey: photo.thumbUrl ? 'thumbUrl' : undefined })} alt="Photo">
        ${(!_drv || own) ? `<button type="button" class="photo-remove-btn" onclick="window.DispatchModule.removeExistingJobPhoto(${idx})">
          <span class="material-icons">close</span>
        </button>` : ''}
      </div>
    `;
  });

  // Pending photos
  DispatchState.pendingJobPhotos.forEach((photo, idx) => {
    html += `
      <div class="photo-grid-item">
        <img src="${photo.previewUrl}" alt="New photo">
        <button type="button" class="photo-remove-btn" onclick="window.DispatchModule.removePendingJobPhoto(${idx})">
          <span class="material-icons">close</span>
        </button>
      </div>
    `;
  });

  grid.innerHTML = html;
}

function removeExistingJobPhoto(idx) {
  // TASK-10106 S5 — a PIN driver removes only their own upload; the save sends
  // it to driverSaveJob (files mode), which re-checks ownership + no ticket.
  if (window.AppState?.driverModePin === true) {
    const ph = DispatchState.existingJobPhotos[idx];
    if (!_driverOwnsJobFile(ph)) return;
    (DispatchState.driverFileRemovals = DispatchState.driverFileRemovals || []).push({ kind: 'photo', id: ph.id });
  }
  DispatchState.existingJobPhotos.splice(idx, 1);
  renderJobPhotoGrid();
}

function removePendingJobPhoto(idx) {
  const photo = DispatchState.pendingJobPhotos[idx];
  if (photo?.previewUrl) URL.revokeObjectURL(photo.previewUrl);
  DispatchState.pendingJobPhotos.splice(idx, 1);
  renderJobPhotoGrid();
}

// TASK-9958 — who is adding files in the job form, from the signed-in session
// (auth uid + the member's display name / personId). Never typed. null = no uid.
function _dispatchFileUploader() {
  const ma = window.AppState?.memberAccess || null;
  const u = window.AppState?.user || null;
  const cu = auth?.currentUser || null;
  let name = ma?.displayName || '';
  if (!name && ma?.personId) {
    const p = (DispatchState.people || []).find(x => x && x.id === ma.personId);
    if (p?.name) name = p.name;
  }
  if (!name) name = cu?.displayName || u?.displayName || cu?.email || u?.email || '';
  return buildTicketDocUploader({ uid: cu?.uid || u?.uid || '', name, personId: ma?.personId || '' });
}

// TASK-9958 — one auditLog FILE_ADDED row per file that is now ON the job.
// Called only after the write that attaches them commits. Fire-and-forget.
function _auditJobFilesAdded(jobId, target, rows) {
  try {
    const uid = auth?.currentUser?.uid || window.AppState?.user?.uid || '';
    const jobTitle = (DispatchState.jobs || []).find(j => j && j.id === jobId)?.title || undefined;
    for (const e of (rows || [])) {
      if (!e) continue;
      window.logAudit?.('job', 'FILE_ADDED', fileAddedAuditPayload({
        entry: e, target, via: e.uploadedVia || 'job_modal', jobId, jobTitle, uid,
      }));
    }
  } catch (_) { /* fire-and-forget, like JOB_MEDIA_SAVE */ }
}

async function uploadJobPhotos(tenantId, jobId) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const basePath = `tenants/${tenantId}/photos/jobs/${year}/${month}/${jobId}`;

  const photos = [];
  let failCount = 0;
  const _uploader = _dispatchFileUploader(); // TASK-9958

  for (const pending of DispatchState.pendingJobPhotos) {
    try {
      const fullPath = `${basePath}/${pending.id}_full.jpg`;
      const thumbPath = `${basePath}/${pending.id}_thumb.jpg`;

      const fullRef = ref(storage, fullPath);
      const thumbRef = ref(storage, thumbPath);

      const _meta = fileUploadCustomMetadata(_uploader?.uid, 'job_modal'); // TASK-9958
      await uploadBytes(fullRef, pending.fullBlob, { contentType: 'image/jpeg', customMetadata: _meta });
      await uploadBytes(thumbRef, pending.thumbBlob, { contentType: 'image/jpeg', customMetadata: _meta });

      const fullUrl = await getDownloadURL(fullRef);
      const thumbUrl = await getDownloadURL(thumbRef);

      photos.push(stampFileProvenance({
        id: pending.id,
        fullUrl,
        thumbUrl,
        fullPath,
        thumbPath,
        sizeBytes: pending.fullBlob.size,
        uploadedAt: new Date().toISOString()
      }, _uploader, 'job_modal'));
    } catch (err) {
      failCount++;
      console.error('[Dispatch] Photo upload failed:', pending.id, err);
      window.logError(err, { module: 'dispatch', operation: 'photo-upload', photoId: pending.id, jobId });
    }
  }

  if (failCount > 0) {
    window.showToast(`${failCount} photo(s) failed to upload`, 'error');
  }

  DispatchState._lastUploadFailCount = failCount;
  DispatchState.pendingJobPhotos = [];
  return photos;
}

// ============================================
// PERMIT FILE FUNCTIONS
// ============================================
function toggleAttachmentsSection() {
  const checked = document.getElementById('job-attachments-toggle')?.checked;
  document.getElementById('job-attachments-body')?.classList.toggle('hidden', !checked);
}

// TASK-9546 — the permit section no longer hides behind an "Attach Load Permit
// Files" checkbox; that is how a permit ended up in the photo picker. Kept as a
// function (exported, called at every form open) so there is one place that
// guarantees the body is showing.
function togglePermitsSection() {
  document.getElementById('job-permits-body')?.classList.remove('hidden');
}

// ============================================
// TASK-4649 — "Permit Required" checkbox (S3) + pre-tick heuristic (S5)
// ============================================
// Keyword signal ported from scripts/permit-signal.cjs — weights MEASURED
// against 794 TITS tickets (88% recall at score >= 2), not guessed. Only the
// TEXT signals apply here: trailer-type weights are ticket-level data that
// doesn't exist yet when a job is being created.
const PERMIT_SIGNAL_EXPLICIT = /\b(super ?load|oversize|over ?size|over ?width|over ?weight|wide load|pilot car|escort|permit|osow|over ?dimension)\b/i;
const PERMIT_SIGNAL_CARGO = /\b(track ?hoe|trackhoe|excavator|trencher|dozer|bulldozer|loader|backhoe|crane|roller|compactor|grader|scraper|skid ?steer|quad ?steer|man ?lift|boom ?lift|telehandler|sprayer|terra ?gator|blade|packer|side ?boom|winch tractor|tractor|rig ?mat)\b/i;
const PERMIT_SIGNAL_MOVE = /\b(equip(ment)? move|move (the )?equip|machinery move|heavy haul|lowboy)\b/i;

function scorePermitSignalText(text) {
  let score = 0;
  if (PERMIT_SIGNAL_EXPLICIT.test(text)) score += 3;
  if (PERMIT_SIGNAL_CARGO.test(text)) score += 2;
  if (PERMIT_SIGNAL_MOVE.test(text)) score += 2;
  return score;
}

// Tracks whether the current tick came from the heuristic vs a human, so the
// save can record which it was and the "auto-suggested" badge can't outlive a
// manual decision (UX: nobody records a compliance declaration they didn't make).
// Reset in resetJobFormPermitRequired(); set in the two functions below.
let _permitAutoState = { suggested: false, userTouched: false };

function _setPermitAutoBadge(visible) {
  document.getElementById('job-permit-required-auto')?.classList.toggle('hidden', !visible);
}

// Manual toggle — the human decided. Kill the auto badge either way.
function onPermitRequiredToggle() {
  _permitAutoState.userTouched = true;
  _permitAutoState.suggested = false;
  _setPermitAutoBadge(false);
}

// Runs on focusout of title / well name / notes. NEW jobs only — an existing
// job's permitRequired is a recorded decision, never overwritten by a heuristic.
// Suggest-only and reversible until save: ticks the box + shows the badge when
// the text scores >= 2, unticks its own suggestion if the text stops scoring,
// and never fights a checkbox the user has touched.
function maybeAutoSuggestPermitRequired() {
  if (!isPermitTrackingEnabled()) return;
  // #job-id holds the id when the form is in edit mode (editJob sets it, the
  // new-job reset clears it) — same marker saveJob keys create-vs-update on.
  if (document.getElementById('job-id')?.value) return;
  if (_permitAutoState.userTouched) return;
  const cb = document.getElementById('job-permit-required');
  if (!cb) return;
  const text = [
    document.getElementById('job-title')?.value,
    document.getElementById('job-well-name')?.value,
    document.getElementById('job-notes')?.value
  ].filter(Boolean).join(' ');
  const hit = scorePermitSignalText(text) >= 2;
  if (hit && !cb.checked) {
    cb.checked = true;
    _permitAutoState.suggested = true;
    _setPermitAutoBadge(true);
  } else if (!hit && cb.checked && _permitAutoState.suggested) {
    cb.checked = false;
    _permitAutoState.suggested = false;
    _setPermitAutoBadge(false);
  }
}

// Form reset (new job) — unticked, no badge, heuristic re-armed.
function resetJobFormPermitRequired() {
  _permitAutoState = { suggested: false, userTouched: false };
  const cb = document.getElementById('job-permit-required');
  if (cb) cb.checked = false;
  _setPermitAutoBadge(false);
}

// Edit-path load. userTouched=true disarms the heuristic entirely on edits.
// Badge persists only for a stored auto-suggested TRUE that no human has
// re-confirmed yet.
function loadJobPermitRequired(job) {
  _permitAutoState = { suggested: false, userTouched: true };
  const cb = document.getElementById('job-permit-required');
  if (!cb) return;
  cb.checked = job.permitRequired === true;
  _setPermitAutoBadge(job.permitRequired === true && job.permitRequiredSource === 'auto-suggested');
}

// ============================================================================
// TASK-9151 — PERMIT UPLOAD PATH
//
// The shape of the old path: pick ONE file per picker trip; hold every blob in
// DispatchState.pendingPermitFiles until Save; then, inside saveJob, run
//   for (const pending of …) { await uploadBytes(…) }
// one file at a time, on a truck's LTE, with nothing on screen. A dispatcher
// pressed Save and got a modal that did not move for minutes. If an upload
// threw, failCount++ and the loop carried on — then pendingPermitFiles was
// cleared UNCONDITIONALLY, so the blobs for the failures were destroyed. The
// user saw "3 permit(s) failed to upload", with no way to know WHICH three, and
// nothing left in the tray to retry. That is the data-loss bug in here.
//
// What changed:
//   • multi-select at the picker, with per-file validation and an add-time
//     progress line (image processing is the slow part of adding, not saving).
//   • a SIZE gate at add time. PDFs had none at all — storage.rules:660 caps a
//     permit object at 10 MB, so an 11 MB PDF was accepted into the tray and
//     then denied by the rule minutes later, at Save, after the wait.
//   • a bounded-concurrency pool at Save (PERMIT_UPLOAD_CONCURRENCY) driving
//     uploadBytesResumable, so there is a real byte-level progress bar.
//   • partial success is the DESIGNED outcome, not an error path: whatever
//     landed is written to the job, whatever failed STAYS in the tray marked
//     failed, and the modal refuses to close so Retry is one tap away.
// ============================================================================

// Why 3 and not 6, or 20.
//
// The binding constraint on a job site is UPLINK BANDWIDTH, which is shared —
// past a small number, parallel uploads do not finish sooner, they all finish
// later and at the same time, which makes a progress bar lie (everything sits
// at 40% and then jumps). What parallelism actually buys is hiding the fixed
// per-request cost (TLS + auth + the resumable-session round trip + the GCS
// commit, several hundred ms each on LTE) behind the next file's transfer, and
// 3 lanes is enough to keep the pipe full for that. 3 → 6 buys little on a
// bandwidth-bound uplink; it just deepens the queue.
//
// The other two reasons are the ones that make a bigger number actively worse:
//   • MEMORY. Peak in-flight bytes are concurrency × max file size, so 3 lanes
//     is 30 MB worst case NO MATTER how many permits are in the batch. Firing
//     20 at once is 200 MB — which is precisely the phone-tab crash this task
//     was filed about, reintroduced by the "fix".
//   • BLAST RADIUS. A dropped connection kills only what is in flight. 3 files
//     to retry, not 20.
const PERMIT_UPLOAD_CONCURRENCY = 3;

// storage.rules:660 — `request.resource.size < 10 * 1024 * 1024` on
// /tenants/{tid}/job-permits/{allPaths=**}. Checked here so an oversized file is
// refused at the picker with the file's name on screen, instead of being carried
// for the whole form and denied at Save.
const PERMIT_MAX_FILE_BYTES = 10 * 1024 * 1024;

// The only honest memory bound available without uploading at add-time (see the
// task report for why upload-on-add was rejected). Every pending blob is held
// until Save, so this caps what one form can be holding. 60 MB is well inside
// what a mid-range phone browser survives and still admits far more than the
// current maxPermits cap of 10 could ever reach — it exists so that RAISING the
// cap cannot silently reintroduce the crash.
const PERMIT_MAX_PENDING_BYTES = 60 * 1024 * 1024;

function _permitPendingBytes() {
  return (DispatchState.pendingPermitFiles || []).reduce(
    (n, p) => n + (p.blob?.size || 0) + (p.thumbBlob?.size || 0), 0);
}

function _fmtBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '0 KB';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// TASK-9546 — is this file a PDF by name/declared type? Cheap and synchronous;
// it decides which PATH a file takes. _readsAsPdf is the content check that
// decides whether it is ACCEPTED on that path.
function _looksLikePdf(file) {
  const nameLower = (file?.name || '').toLowerCase();
  return file?.type === 'application/pdf' || nameLower.endsWith('.pdf');
}

// TASK-9546 — '%PDF-' as bytes. The PDF spec lets a reader accept the header
// anywhere in the first 1024 bytes (some generators prepend junk), so scan that
// window instead of demanding offset 0. Content, not extension: a JPEG renamed
// .pdf used to be filed as a permit nobody could open. This is HEADER SCREENING,
// not validation — a truncated or corrupt PDF with a good header still passes.
const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46, 0x2d];

async function _readsAsPdf(file) {
  let bytes;
  try {
    const head = file.slice(0, 1024);
    const buf = typeof head.arrayBuffer === 'function'
      ? await head.arrayBuffer()
      : await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = () => reject(r.error || new Error('read failed'));
          r.readAsArrayBuffer(head);
        });
    bytes = new Uint8Array(buf);
  } catch (_) {
    return false;   // unreadable is not a PDF we can file
  }
  for (let i = 0; i + PDF_MAGIC.length <= bytes.length; i++) {
    let hit = true;
    for (let j = 0; j < PDF_MAGIC.length; j++) {
      if (bytes[i + j] !== PDF_MAGIC[j]) { hit = false; break; }
    }
    if (hit) return true;
  }
  return false;
}

async function handlePermitUpload(event) {
  // TASK-9151 — `multiple` is on the input now, so this is a BATCH. Snapshot the
  // FileList before clearing the input: it is live, and event.target.value = ''
  // empties it out from under a loop that reads it lazily.
  const picked = Array.from(event.target.files || []);
  event.target.value = '';
  if (picked.length === 0) return;
  return _addPermitFiles(picked, { source: 'permits' });
}

// TASK-9546 — the one path that files permits, whichever picker they came from.
// `source` only changes the success toast: a PDF that came in through Add Photo
// is told WHERE it went, because that user was looking at the photo grid.
async function _addPermitFiles(picked, { source = 'permits' } = {}) {
  if (!Array.isArray(picked) || picked.length === 0) return;

  // 🔴 TASK-9151 — REFUSE while a save or upload is in flight. uploadPermitFiles
  // snapshots its queue at the top, and saveJob decides whether it may close
  // BEFORE the submit lock is set — so a file picked during those seconds stays
  // 'pending', sits in nobody's queue, and the modal closes over it. Save is
  // disabled through all of that. This button was not.
  // 🔴 _permitPrepBusy is in here too, and it is not redundant: it rejects an
  // OVERLAPPING batch. Two pickers in flight share one flag, so a second batch of
  // fast files (a PDF needs no processing) would run to completion and clear the
  // flag in its own `finally` while the first batch's image is still inside
  // processPhoto — re-enabling Save under exactly the race this flag exists to
  // prevent. One batch at a time; the second is refused, not queued.
  if (_permitUploadBusy || _permitPrepBusy || window.__saveJobInFlight) {
    window.showToast?.(_permitPrepBusy && !_permitUploadBusy && !window.__saveJobInFlight
      ? 'Still getting the last one ready — add that one in a second.'
      : 'Still saving — add that one once the upload finishes.', 'warning');
    return;
  }

  // 🔴 And hold the door for the whole batch, not just this instant. Preparing an
  // image is `await processPhoto(...)` — seconds on a phone — and the row is only
  // pushed to pendingPermitFiles AFTER it resolves. A Save pressed during that
  // await snapshots its upload queue before the row exists, so the file misses
  // the upload and is then closed over. Blocking at entry alone does not cover
  // the await; this flag does, and _permitCloseIsBlocked reads it too.
  _permitPrepBusy = true;
  _paintPermitSaveButton();

  const maxPermits = STARTER_LIMITS.jobs.maxPermits;
  let accepted = 0;
  const rejected = [];

  try {
  for (let i = 0; i < picked.length; i++) {
    const file = picked[i];

    const totalPermits = DispatchState.pendingPermitFiles.length + DispatchState.existingPermitFiles.length;
    if (totalPermits >= maxPermits) {
      rejected.push(`${file.name} — over the ${maxPermits}-file limit`);
      continue;
    }

    const nameLower = file.name.toLowerCase();
    const isPdf = _looksLikePdf(file);
    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
    const isImage = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));

    if (!isPdf && !isImage) {
      rejected.push(`${file.name} — not an image or PDF`);
      continue;
    }

    // Only RAW-uploaded kinds need this check. A non-HEIC image goes through
    // processPhoto and comes out at maxPhotoSizeKB regardless of what went in,
    // so rejecting a big JPEG here would refuse a file we can actually handle.
    const uploadedRaw = isPdf || nameLower.endsWith('.heic') || nameLower.endsWith('.heif');
    if (uploadedRaw && file.size > PERMIT_MAX_FILE_BYTES) {
      rejected.push(`${file.name} — ${_fmtBytes(file.size)}, over the 10 MB limit`);
      continue;
    }

    const id = `permit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      if (isPdf) {
        // TASK-9546 — content check. Extension and declared type are what the
        // picker (and the user) said; the bytes are what we would be storing.
        if (!(await _readsAsPdf(file))) {
          rejected.push(`${file.name} — not a PDF`);
          continue;
        }
        DispatchState.pendingPermitFiles.push({
          id,
          blob: file,
          thumbBlob: null,
          previewUrl: null,
          name: file.name,
          mimeType: 'application/pdf',
          isPdf: true,
          uploadState: 'pending'
        });
      } else {
        const validation = validateImageFile(file);
        if (!validation.valid) {
          rejected.push(`${file.name} — ${validation.error}`);
          continue;
        }

        // TASK-9397 — HEIC/HEIF used to be uploaded RAW here (a permit only
        // Safari could open). Now it is converted when this browser can't
        // draw it (inside _permitPrepBusy, so Save waits), then processed
        // like any other image.
        // Processing is the expensive part of ADDING (canvas decode + re-encode
        // per image), so say so while it runs — on a phone a silent 20-file
        // batch here looks exactly like the frozen save this task is about.
        if (picked.length > 1) {
          showPermitProgress({
            label: `Preparing permits… ${i + 1} of ${picked.length}`,
            sub: file.name,
            pct: Math.round((i / picked.length) * 100)
          });
        }
        const drawable = await toDrawablePhoto(file);
        const processed = await processPhoto(drawable, {
          maxSizeKB: STARTER_LIMITS.jobs.maxPhotoSizeKB
        });
        const thumbnail = await generateThumbnail(drawable);

        DispatchState.pendingPermitFiles.push({
          id,
          blob: processed,
          thumbBlob: thumbnail,
          previewUrl: URL.createObjectURL(thumbnail),
          name: drawable.name || file.name,
          mimeType: 'image/jpeg',
          isPdf: false,
          uploadState: 'pending'
        });
      }

      // Memory ceiling, enforced AFTER the push so the check sees real sizes
      // (processPhoto's output size is not knowable in advance). The file that
      // crosses the line is rolled back out, not silently kept.
      if (_permitPendingBytes() > PERMIT_MAX_PENDING_BYTES) {
        const popped = DispatchState.pendingPermitFiles.pop();
        if (popped?.previewUrl) URL.revokeObjectURL(popped.previewUrl);
        rejected.push(`${file.name} — this form is already holding ${_fmtBytes(_permitPendingBytes())}. Save the job, reopen it, and add the rest.`);
        break;
      }

      accepted++;
    } catch (error) {
      console.error('[Dispatch] Permit file processing error:', file?.name, error?.cause || error);
      rejected.push(`${file.name} — ${error?.userMessage || 'could not be processed'}`);
    }
  }
  } finally {
    _permitPrepBusy = false;
  }

  renderPermitGrid();
  // 🔴 NOT a bare hide of the progress strip. Adding a file while the banner is
  // up would hide Retry and Discard while the submit lock keeps Save disabled —
  // no way out of the modal but the X. _syncPermitFailureBanner hides the strip
  // only when nothing is actually outstanding, and re-raises it when something is.
  _syncPermitFailureBanner();
  _paintPermitSaveButton();

  if (accepted > 0) {
    // TASK-9546 — from Add Photo, say where it went; that user was looking at
    // the photo grid and the file is not going to appear there.
    const msg = source === 'photos'
      ? (accepted === 1 ? 'Added to permit files' : `${accepted} added to permit files`)
      : (accepted === 1 ? 'Permit file added' : `${accepted} permit files added`);
    window.showToast?.(msg, 'success');
  }
  // Every refusal names the FILE. "Maximum 10 permit files allowed" told a
  // dispatcher who just picked 14 nothing about which 4 did not make it.
  if (rejected.length > 0) {
    const head = rejected.slice(0, 3).join(' · ');
    const tail = rejected.length > 3 ? ` (+${rejected.length - 3} more)` : '';
    window.showToast?.(`Not added: ${head}${tail}`, rejected.length === picked.length ? 'error' : 'warning', null, 8000);
  }
}

// TASK-9740 — the signed Bill of Lading / Disposal Ticket PDFs a driver filed
// from the phone on this job (job.documents[], server-written by
// onJobDocumentCreate). Read-only on purpose: there is no remove button because
// a signed document is a record — the file stays in Storage and the
// jobDocuments collection either way. TASK-9740: entries carry NO URL (job
// docs are broadcast to every driver session); a tap opens the PDF through
// the getJobDocumentPdf callable via window.JobBoardModule (office = dispatch
// or billing module, owner). Pitfall #1: window, not a ?v= cross-import.
function renderJobSignedDocs(job) {
  const box = document.getElementById('job-signed-docs');
  if (!box) return;
  const esc = (x) => window.escJsAttr(x);
  const docs = (job && Array.isArray(job.documents) ? job.documents : [])
    .filter(d => d && typeof d === 'object' && typeof d.id === 'string' && d.id)
    .slice()
    .sort((a, b) => Date.parse(b.createdAt || '') - Date.parse(a.createdAt || ''));
  if (!docs.length) { box.classList.add('hidden'); box.innerHTML = ''; return; }
  const when = (iso) => { const ms = Date.parse(iso || ''); return Number.isFinite(ms) ? new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''; };
  box.innerHTML = `
    <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:6px;">
      <span style="margin:0;font-weight:600;">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;color:var(--accent-teal);">assignment</span>
        Signed documents <span style="font-weight:500;color:var(--text-secondary);">(${docs.length})</span>
      </span>
    </div>
    <div style="display:flex;flex-direction:column;gap:6px;">
      ${docs.map(d => `
        <button type="button" onclick="event.preventDefault();window.JobBoardModule?.openJobDocumentPdf('${esc(job.id)}','${esc(d.id)}')"
           style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--border-light);border-radius:8px;background:var(--bg-elevated);color:var(--text-primary);text-align:left;width:100%;cursor:pointer;min-height:44px;font:inherit;">
          <span class="material-icons" style="color:#DC2626;">picture_as_pdf</span>
          <span style="min-width:0;flex:1;">
            <span style="display:block;font-weight:600;">${escapeHtml(d.name || 'Document')}</span>
            <span style="display:block;font-size:0.8rem;color:var(--text-secondary);">${escapeHtml([d.driverName ? (d.enteredByOffice ? d.driverName + ' (office)' : d.driverName) : '', when(d.createdAt), d.basis === 'clock' ? 'not on the crew — clocked in on the job' : ''].filter(Boolean).join(' · '))}</span>
            ${d.driverPo ? `<span title="The driver typed a PO that is not this job's PO. The job's PO was not changed." style="display:inline-flex;align-items:center;gap:4px;margin-top:4px;padding:2px 8px;border-radius:10px;background:#FEF3C7;border:1px solid #FDE68A;color:#92400E;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">warning</span>Driver-entered PO ${escapeHtml(d.driverPo)} — check it</span>` : ''}
            ${d.hazmat ? `<span style="display:inline-flex;align-items:center;gap:4px;margin-top:4px;margin-left:4px;padding:2px 8px;border-radius:10px;background:#FEE2E2;border:1px solid #FECACA;color:#991B1B;font-size:0.75rem;font-weight:600;">${d.hazmatDeclared ? 'Driver marked hazmat — job is not' : 'Hazmat'}</span>` : ''}
          </span>
          <span class="material-icons" style="color:var(--text-secondary);font-size:18px;">open_in_new</span>
        </button>`).join('')}
    </div>`;
  box.classList.remove('hidden');
}

function renderPermitGrid() {
  const grid = document.getElementById('job-permit-grid');
  const countEl = document.getElementById('job-permit-count');
  if (!grid || !countEl) return;

  const total = DispatchState.existingPermitFiles.length + DispatchState.pendingPermitFiles.length;
  countEl.textContent = `${total}/${STARTER_LIMITS.jobs.maxPermits} files`;

  let html = '';

  // TASK-9108 - a permit tile is now a VIEWER, not just a delete target. Four
  // grey PDF squares told you nothing about which permits were attached, and
  // the only way to read one was to save the job and go hunt for it elsewhere.
  // Tile click -> preview; the [x] stops propagation so removing a file never
  // opens it on the way out.
  const tileTitle = (file, verb) => escapeHtml(file.name ? `${verb} - ${file.name}` : verb);
  // TASK-9958 — "Added by X · via Job form · Oct 15, 8:34 PM" on the tile's
  // tooltip; '' for a row that records neither (old rows show as before).
  const provTitle = (file) => {
    const line = ticketDocUploadedLine(file, _fileAddedFmt);
    return line ? escapeHtml(` (${line})`) : '';
  };

  // Existing permit files
  // TASK-10106 S5 — a PIN driver gets the X only on a file they added from the
  // driver board (the server re-checks it, and refuses once there is a ticket).
  const _drv = window.AppState?.driverModePin === true;
  const removeBtn = (file, idx) => ((!_drv || _driverOwnsJobFile(file)) ? `
          <button type="button" class="photo-remove-btn" onclick="event.stopPropagation();window.DispatchModule.removeExistingPermit(${idx})">
            <span class="material-icons">close</span>
          </button>` : '');
  // TASK-9694 — a driver's job-screen Doc shares this grid (same array, same
  // file cap) but is tagged as a Doc, not a permit.
  // The Doc test needs the job's server-only driverDocs map (editJob keeps it).
  const _docJob = { driverDocs: DispatchState.existingPermitDocs || null };
  const docTag = (file) => (isJobDocEntry(file, _docJob) ? '<span style="position:absolute;left:4px;bottom:4px;padding:0 4px;border-radius:3px;font-size:0.65rem;font-weight:600;background:var(--brand-navy);color:#fff;">Doc</span>' : '');
  const docVerb = (file, verb) => (isJobDocEntry(file, _docJob) ? `Driver Doc, not a permit. ${verb}` : verb);
  DispatchState.existingPermitFiles.forEach((file, idx) => {
    if (file.mimeType === 'application/pdf' || file.isPdf) {
      html += `
        <div class="photo-grid-item permit-pdf-placeholder permit-tile-clickable" title="${tileTitle(file, docVerb(file, 'Click to open PDF'))}${provTitle(file)}"
             onclick="window.DispatchModule.previewExistingPermit(${idx})">
          <span class="material-icons" style="font-size:32px;">picture_as_pdf</span>${docTag(file)}${removeBtn(file, idx)}
        </div>
      `;
    } else {
      html += `
        <div class="photo-grid-item permit-tile-clickable" title="${tileTitle(file, docVerb(file, 'Click to view'))}${provTitle(file)}"
             onclick="window.DispatchModule.previewExistingPermit(${idx})">
          <img ${window.StorageFile.imgAttrs(file, { urlKey: file.thumbUrl ? 'thumbUrl' : undefined })} alt="${isJobDocEntry(file, _docJob) ? 'Document' : 'Permit'}">${docTag(file)}${removeBtn(file, idx)}
        </div>
      `;
    }
  });

  // Pending permit files
  DispatchState.pendingPermitFiles.forEach((file, idx) => {
    // TASK-9151 — per-tile upload state. Which file is uploading, which one
    // failed, and which succeeded has to be visible ON THE FILE. The old path
    // said "3 permit(s) failed to upload" and left the user with no way to tell
    // which three, because the tray had already been cleared.
    const stateOverlay = _permitTileStateOverlay(file);
    if (file.isPdf) {
      html += `
        <div class="photo-grid-item permit-pdf-placeholder permit-tile-clickable" title="${tileTitle(file, 'Click to open PDF')}"
             data-permit-id="${escapeHtml(file.id)}"
             onclick="window.DispatchModule.previewPendingPermit(${idx})">
          <span class="material-icons" style="font-size:32px;">picture_as_pdf</span>
          <button type="button" class="photo-remove-btn" onclick="event.stopPropagation();window.DispatchModule.removePendingPermit(${idx})">
            <span class="material-icons">close</span>
          </button>${stateOverlay}
        </div>
      `;
    } else {
      html += `
        <div class="photo-grid-item permit-tile-clickable" title="${tileTitle(file, 'Click to view')}"
             data-permit-id="${escapeHtml(file.id)}"
             onclick="window.DispatchModule.previewPendingPermit(${idx})">
          <img src="${file.previewUrl}" alt="New permit">
          <button type="button" class="photo-remove-btn" onclick="event.stopPropagation();window.DispatchModule.removePendingPermit(${idx})">
            <span class="material-icons">close</span>
          </button>${stateOverlay}
        </div>
      `;
    }
  });

  grid.innerHTML = html;
  _renderPermitProvenance();
}

// TASK-9958 — date + time for the provenance line ("Oct 15, 8:34 PM").
function _fileAddedFmt(v) {
  const dt = v?.toDate ? v.toDate() : new Date(v);
  if (!dt || isNaN(dt.getTime())) return '';
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// TASK-9958 — one quiet line per SAVED permit that records who/how it got here,
// under the tile grid. Old rows (no stamp) are not listed, so a job with only
// old permits looks exactly as before.
function _renderPermitProvenance() {
  const el = document.getElementById('job-permit-provenance');
  if (!el) return;
  const rows = (DispatchState.existingPermitFiles || [])
    .map(f => ({ f, line: ticketDocUploadedLine(f, _fileAddedFmt) }))
    .filter(r => r.line);
  if (!rows.length) { el.innerHTML = ''; el.classList.add('hidden'); return; }
  el.innerHTML = rows.map(({ f, line }) => `
    <div class="job-permit-provenance-row" style="font-size:0.75rem;line-height:1.35;color:var(--text-secondary);overflow-wrap:anywhere;">
      <span style="color:var(--text-primary);">${escapeHtml(f.name || 'Permit')}</span> · ${escapeHtml(line)}
    </div>`).join('');
  el.classList.remove('hidden');
}

// TASK-9108 - preview an ALREADY-SAVED permit from the Edit Job modal.
// Images go to the shared in-app lightbox (window.PhotoLightbox, never a
// cross-import - Pitfall #1); PDFs open in a new tab, since no lightbox
// renders one.
// fullUrl is the normal case, but permit rows are not guaranteed to carry it
// (the ticket-documents trap: some rows hold a path and no url, some the
// reverse) - so fall back to resolving fullPath/thumbPath through Storage. On
// that path the tab is claimed SYNCHRONOUSLY or the popup blocker eats it.
async function previewExistingPermit(idx) {
  const file = DispatchState.existingPermitFiles[idx];
  if (!file) return;
  const isPdf = file.mimeType === 'application/pdf' || file.isPdf;

  // TASK-9855 — open through the Storage SDK on the stored path (fullPath /
  // storagePath), else the path derived from the stored URL; never the token
  // URL itself and never getDownloadURL (it hands the same token back).
  // StorageFile.open claims the tab synchronously (popup blockers).
  const SF = window.StorageFile;
  const fr = SF.fileRef(file) || (file.thumbPath ? SF.fileRef(file, { urlKey: 'thumbUrl' }) : null);
  if (!fr) {
    window.showToast?.('This file has no stored link - re-upload it', 'error');
    return;
  }
  if (isPdf) { SF.open(fr); return; } // the whole ref: path + bucket + stored URL (transitional network fallback)
  try {
    const url = await SF.blobUrl(fr);
    window.PhotoLightbox?.show(url);
  } catch (err) {
    console.error('[Dispatch] Permit preview failed:', fr.path || '', err?.sfReason || err);
    window.showToast?.('Could not open that file', 'error');
  }
}

// TASK-9108 - preview a permit picked THIS session, still a local blob. Images
// already have a thumbnail objectURL; build a full-size one on demand (cached
// on the pending row) so the lightbox is not showing a blurry thumb, and revoke
// it in removePendingPermit alongside previewUrl.
function previewPendingPermit(idx) {
  const file = DispatchState.pendingPermitFiles[idx];
  if (!file?.blob) return;
  try {
    if (!file.fullPreviewUrl) file.fullPreviewUrl = URL.createObjectURL(file.blob);
    if (file.isPdf) window.open(file.fullPreviewUrl, '_blank', 'noopener');
    else window.PhotoLightbox?.show(file.fullPreviewUrl);
  } catch (err) {
    console.error('[Dispatch] Pending permit preview failed:', err);
    window.showToast?.('Could not open that file', 'error');
  }
}

function removeExistingPermit(idx) {
  // TASK-10106 S5 — same rule as removeExistingJobPhoto for a PIN driver.
  if (window.AppState?.driverModePin === true) {
    const f = DispatchState.existingPermitFiles[idx];
    if (!_driverOwnsJobFile(f)) return;
    (DispatchState.driverFileRemovals = DispatchState.driverFileRemovals || []).push({ kind: 'permit', id: f.id });
  }
  DispatchState.existingPermitFiles.splice(idx, 1);
  renderPermitGrid();
}

function removePendingPermit(idx) {
  const file = DispatchState.pendingPermitFiles[idx];
  if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
  // TASK-9108 - the full-size preview URL is created lazily on first click.
  if (file?.fullPreviewUrl) URL.revokeObjectURL(file.fullPreviewUrl);
  DispatchState.pendingPermitFiles.splice(idx, 1);
  renderPermitGrid();
  // TASK-9151 — the failure banner counts failed rows; removing the last one
  // must take the banner (and the closeModal block it implies) with it.
  _syncPermitFailureBanner();
  // Deleting the last failed tile IS a discard. The job is already written and
  // this form can never be submitted again, so leaving the modal open around a
  // permanently dead Save button is a trap with no exit but the X.
  if (DispatchState._permitModalSubmitLock && !hasFailedPermitUploads()) {
    closeModal('job');
  }
}

// ============================================================================
// TASK-9151 — bounded-concurrency upload pool + progress + partial-failure
// ============================================================================

/**
 * Run `worker` over `items` with at most `concurrency` in flight.
 *
 * THE WHOLE POINT is the failure semantics, so they are spelled out:
 *   • a rejected worker NEVER aborts the run. `Promise.all(items.map(worker))`
 *     rejects on the first failure and abandons the report for everything else —
 *     which on bad LTE is the normal case, not the exception. Here every item is
 *     settled and classified.
 *   • results are keyed by INPUT INDEX, so a caller can say which file failed.
 *   • it never throws. Callers get { ok, failed } and decide.
 *   • at most `concurrency` workers are ever in flight. That bound is what keeps
 *     peak memory at concurrency × filesize instead of batchsize × filesize.
 *
 * Pure (no DOM, no Firebase) so tests/permit-upload-pool.unit.cjs can extract it
 * from source and run it in node:vm — same pattern as
 * tests/dispatch-offboard-save.unit.cjs.
 */
async function runPermitUploadPool(items, worker, opts) {
  const options = opts || {};
  const list = Array.from(items || []);
  const bound = Math.max(1, Math.min(options.concurrency || PERMIT_UPLOAD_CONCURRENCY, list.length || 1));
  const results = new Array(list.length);
  let next = 0;
  let settled = 0;
  let inFlight = 0;
  let peakInFlight = 0;

  async function lane() {
    for (;;) {
      const i = next++;
      if (i >= list.length) return;
      inFlight++;
      if (inFlight > peakInFlight) peakInFlight = inFlight;
      try {
        const value = await worker(list[i], i);
        results[i] = { index: i, item: list[i], ok: true, value };
      } catch (err) {
        results[i] = { index: i, item: list[i], ok: false, error: err };
      }
      inFlight--;
      settled++;
      if (options.onSettled) {
        try { options.onSettled(settled, list.length, results[i]); } catch (_) { /* never let a progress callback kill the pool */ }
      }
    }
  }

  const lanes = [];
  for (let n = 0; n < bound; n++) lanes.push(lane());
  await Promise.all(lanes);

  return {
    results,
    ok: results.filter(r => r && r.ok),
    failed: results.filter(r => r && !r.ok),
    concurrency: bound,
    peakInFlight
  };
}

/**
 * uploadBytes with a progress callback. uploadBytes() reports NOTHING while it
 * runs — a resumable task is the only thing in the SDK that emits
 * bytesTransferred, which is what makes the difference between a bar that moves
 * and a modal that looks hung. Resumable also means the SDK retries transient
 * network errors itself instead of surfacing every LTE hiccup as a lost file.
 */
function _uploadPermitBlobWithProgress(storageRef, blob, metadata, onBytes) {
  return new Promise((resolve, reject) => {
    let task;
    try {
      task = uploadBytesResumable(storageRef, blob, metadata);
    } catch (err) { reject(err); return; }
    task.on('state_changed',
      (snap) => { try { onBytes?.(snap.bytesTransferred); } catch (_) {} },
      (err) => reject(err),
      () => {
        try { onBytes?.(task.snapshot?.totalBytes ?? blob?.size ?? 0); } catch (_) {}
        resolve(task.snapshot);
      }
    );
  });
}

// TASK-9585 S7 — the AI-read permit cost is a SUGGESTION, never a write.
// parsePermitCost (index.js) reads the permit and returns
// { suggestion: { amount, currency, sourceDoc, confidence, permitType } } and
// writes nothing to the job. The amount becomes money only on the office user's
// yes, saved by the server (decidePermitCost, TASK-10079):
// clock.js _findOrCreateTicketInner copies `permitCostConfirmed.amount` (and
// nothing else) onto a billable PERMIT ticket line. `permitParsedCost` is still
// written on the yes so the other readers (billing.js permit reconcile, the
// portal projection) see the same number they always did.
// 🔴 FAILS CLOSED: no confirm dialog → nothing is written. Unlike the crew
// warning (_dfConfirmSeating), a missing receipt here must not become money.
// Newest read per job wins: two quick saves can finish their reads out of
// order, and an older suggestion must never be asked about or saved over a
// newer one (COD r3 Code Reviewer).
const _permitCostOfferSeq = new Map();
async function _offerPermitCostSuggestion(jobId, imagePermits) {
  const tenantId = DispatchState.tenantId;
  if (!tenantId || !jobId || !Array.isArray(imagePermits) || imagePermits.length === 0) return;
  const seq = (_permitCostOfferSeq.get(jobId) || 0) + 1;
  _permitCostOfferSeq.set(jobId, seq);
  const superseded = () => _permitCostOfferSeq.get(jobId) !== seq;
  let res;
  try {
    window.showToast?.('Reading the permit cost...', 'info');
    const parsePermitCost = httpsCallable(functions, 'parsePermitCost');
    res = await parsePermitCost({
      tenantId,
      jobId,
      permitUrls: imagePermits.map(p => p.fullUrl)
    });
  } catch (e) {
    console.warn('[Dispatch] Permit AI parse call failed:', e);
    window.showToast?.('Could not read the permit cost. Add it on the ticket by hand.', 'warning');
    return;
  }
  if (superseded()) return;
  const s = res?.data?.suggestion || null;
  const amount = Math.round(Number(s?.amount) * 100) / 100;
  if (!s || !Number.isFinite(amount) || amount <= 0) {
    window.showToast?.('No cost found on the permit. Add it on the ticket by hand.', 'info');
    return;
  }
  const typeText = s.permitType ? ` (${String(s.permitType).slice(0, 80)})` : '';
  const partial = s.confidence === 'some_files_read' ? '\n\nSome of the permit files had no cost on them.' : '';
  if (typeof window.showConfirm !== 'function') return;
  // Named, because the dialog can open after the modal has closed (the read is
  // not awaited by the save) — the user must know WHICH job it is for.
  const _job = (DispatchState.jobs || []).find(j => j && j.id === jobId);
  const jobText = _job ? ` for "${String(_job.title || _job.name || '').slice(0, 80)}"` : '';
  // A thrown / rejected dialog is a no (both callers drop this promise).
  const yes = (await Promise.resolve().then(() => window.showConfirm(
    'Permit cost',
    `Permit cost read from the permit${jobText}: $${amount.toFixed(2)}${typeText}.${partial}\n\nUse it on this job's tickets?`,
    { confirmText: `Use $${amount.toFixed(2)}`, cancelText: 'Not now', icon: 'receipt_long' }
  )).catch(() => false)) === true;
  if (yes && superseded()) return;
  if (!yes) {
    window.showToast?.('Permit cost not used. Add it on the ticket by hand if it is billed.', 'info');
    return;
  }
  let outcome;
  try {
    outcome = await _savePermitCostConfirmation(jobId, {
      amount,
      sourceDoc: Array.isArray(s.sourceDoc) ? s.sourceDoc : [],
      permitType: s.permitType || null,
      readId: typeof s.readId === 'string' ? s.readId : null,   // TASK-10079: the server checks the amount against this read
      superseded,
    });
  } catch (e) {
    console.error('[Dispatch] Saving the confirmed permit cost failed:', e);
    window.logError?.(e, { module: 'dispatch', phase: 'permit-cost-confirm', jobId });
    window.showToast?.(_permitCostErrText(e, 'Could not save the permit cost.'), 'error');
    return;
  }
  if (outcome === 'no_user') return;
  if (outcome !== 'saved') {
    window.showToast?.(_PERMIT_WAIT_REFUSED[outcome] || 'The permit changed while it was being read. Permit cost not saved.', 'warning');
    return;
  }
  window.showToast?.(`Permit cost $${amount.toFixed(2)} saved on the job.`, 'success');
}

// TASK-10079 — every permit-cost answer goes through the server. Yes (job modal
// or the S7d list), Edit, Not billable and Put back all call the
// `decidePermitCost` callable (services/permitCostDecision.js), the ONLY writer
// of permitParsedCost / permitCostConfirmed / permitCostDeclined /
// permitCostReinstated — firestore.rules permitConfirmOk() refuses a client
// write of any of them. The server re-checks, in one transaction: the caller's
// Dispatch module (or owner), the job still open, no "not billable" on it, the
// permit files still on the job, and that the amount is what the AI read (or an
// edit WITH a reason); it writes the answer and its jobAuditLog row together.
// These helpers return the server's outcome word so the callers keep their
// messages: 'saved', 'no_user', 'superseded', 'permit_changed',
// 'amount_changed', 'already_ruled', 'job_closed', 'declined',
// 'reason_required', 'gone', 'not_declined'; they throw when the call fails
// (no access, offline, bad input) — the callers toast "Could not save".
// A refusal the server explains (no Dispatch module, view-only, suspended, bad
// input) is shown as said; anything else (offline, internal) gets the fallback.
function _permitCostErrText(e, fallback) {
  const c = String((e && e.code) || '');
  return /permission-denied|invalid-argument|failed-precondition/.test(c) && e.message ? String(e.message).slice(0, 200) : fallback;
}

async function _decidePermitCostCall(payload) {
  const tenantId = DispatchState.tenantId;
  if (!tenantId) throw new Error('permit cost: no company');
  if (!auth?.currentUser?.uid) return 'no_user';
  const call = httpsCallable(functions, 'decidePermitCost');
  const res = await call({ tenantId, ...payload });
  const out = res && res.data ? res.data.outcome : null;
  return typeof out === 'string' && out ? out : 'superseded';
}

// Yes / Edit. Returns an outcome word; throws on a failed call.
//   opts.readId          the parsePermitCost read this yes is for (job modal) —
//                        the server checks the amount against that stored read.
//   opts.sourceDoc       permit object paths the amount covers (S7d list; the
//                        job-modal yes uses the files the server read).
//   opts.superseded      () => true when a newer AI read for this job started (COD r5).
//   opts.expectParsedCost the permitParsedCost the user was SHOWN (S7d list).
//   opts.reason          why the amount differs from the AI read (Edit).
async function _savePermitCostConfirmation(jobId, opts) {
  const o = opts || {};
  const amount = Math.round(Number(o.amount) * 100) / 100;
  if (!DispatchState.tenantId || !jobId || !Number.isFinite(amount) || amount <= 0) throw new Error('permit cost confirm: bad input');
  if (!auth?.currentUser?.uid) return 'no_user';
  const superseded = typeof o.superseded === 'function' ? o.superseded : () => false;
  if (superseded()) return 'superseded';   // a newer read started while this one waited (COD r5)
  const payload = { action: 'confirm', jobId, amount };
  if (o.readId) {
    payload.readId = String(o.readId);
  } else {
    if (o.expectParsedCost === undefined) return 'no_read';  // job-modal yes, but the server stored no read
    payload.sourceDoc = Array.isArray(o.sourceDoc) ? o.sourceDoc.slice(0, 10).map(String) : [];
    payload.expectParsedCost = o.expectParsedCost;
  }
  if (o.reason) payload.reason = String(o.reason).slice(0, 300);
  return _decidePermitCostCall(payload);
}

// TASK-9585 S7d — "Not billable" from the waiting list. Writes NO money: the
// server stamps `permitCostDeclined` (who / when / which amount) + a
// PERMIT_COST_NOT_BILLABLE audit row; permitParsedCost stays as it was.
async function _savePermitCostNotBillable(jobId, expectParsedCost) {
  const shown = Math.round(Number(expectParsedCost) * 100) / 100;
  if (!DispatchState.tenantId || !jobId || !Number.isFinite(shown) || shown <= 0) throw new Error('permit not-billable: bad input');
  return _decidePermitCostCall({ action: 'not_billable', jobId, expectParsedCost: shown });
}

// TASK-9585 S7d reinstate — undo a "Not billable" (COD Domain: a mis-tap must
// not silently lose a permit cost). The server clears the decline, stamps
// `permitCostReinstated` (who / when / the decline it undid) + a
// PERMIT_COST_REINSTATED audit row, and refuses when the decline is not the one
// the user saw (same decliner, amount, time), someone already said yes, the cost
// moved, or the job is closed. No money: the job only goes back on the list.
async function _savePermitCostReinstate(jobId, seen) {
  const s = seen || {};
  const shown = Math.round(Number(s.amount) * 100) / 100;
  if (!DispatchState.tenantId || !jobId || !Number.isFinite(shown) || shown <= 0) throw new Error('permit reinstate: bad input');
  return _decidePermitCostCall({
    action: 'reinstate', jobId, expectParsedCost: shown,
    seen: {
      byUid: String(s.byUid || ''),
      declinedAmount: s.declinedAmount === undefined ? null : s.declinedAmount,
      atMs: s.atMs == null ? null : s.atMs,
    },
  });
}

// ============================================
// TASK-9585 S7d — PERMIT COSTS WAITING FOR A YES (board strip)
// ============================================
// Chris 2026-10-25: "b) One-time 'waiting for a yes' list". Jobs whose AI-read
// permit cost was written before S7 (no permitCostConfirmed) stop adding a
// PERMIT line once S7 ships. This strip sits on the Dispatch board — the
// screen the office opens every morning, right under the customer-requests
// strip — and lists them: job, customer, AI amount, the permit files, and
// Yes / Edit / Not billable. Yes and Edit save through the SAME write as the
// job-modal yes (_savePermitCostConfirmation), so the same rules fence applies.
// The list empties itself as jobs are ruled on (live listener) and the strip
// hides when it is empty; S7's job modal never writes an unconfirmed cost, so
// nothing new joins it.
// Gate: the Dispatch module or the tenant owner (the same check as the
// customer-request buttons, _customerRequestCanApprove — the jobs rule's
// writers). Never on a driver / link / demo board. Tenant-scoped by the
// collection path. Which jobs + which files: utils/permitCostWaiting.js.
let _permitWaitJobs = [];
const _permitWaitBusy = new Set();
let _permitWaitEditing = null;
let _permitWaitOpen = false;
let _permitDeclinedOpen = false;   // S7d reinstate: the "recently marked not billable" sub-list

function _subscribePermitCostsWaiting(tenantId) {
  _permitWaitJobs = [];
  _permitWaitEditing = null;
  _renderPermitWaitStrip();
  try {
    // Single-field range on the tenant's own jobs: no composite index needed.
    const q = query(collection(db, 'tenants', tenantId, 'jobs'), where('permitParsedCost', '>', 0));
    const unsub = onSnapshot(q, (snap) => {
      if (DispatchState.tenantId !== tenantId) return;
      _permitWaitJobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      _renderPermitWaitStrip();
    }, (err) => {
      if (err?.code !== 'permission-denied') console.warn('[Dispatch] permit-cost waiting listener:', err?.message);
      _permitWaitJobs = [];
      _renderPermitWaitStrip();
    });
    DispatchState.unsubscribers.push(unsub);
  } catch (err) {
    console.warn('[Dispatch] permit-cost waiting subscribe failed:', err?.message);
  }
}

function _renderPermitWaitStrip() {
  const el = document.getElementById('permit-costs-waiting-strip');
  if (!el) return;
  if (typeof permitWaitingRows !== 'function') { // integ/2120: lazy helpers not loaded yet — paint again when they are
    _loadPermitCostWaiting().then(() => _renderPermitWaitStrip()).catch((e) => console.warn('[Dispatch] permit-cost waiting helpers failed to load:', e?.message));
    return;
  }
  const S = window.AppState || {};
  const offBoard = S.driverMode || S.isPublicAccess || S.driverModePin;
  const canSee = !offBoard && _customerRequestCanApprove();
  const rows = canSee ? permitWaitingRows(_permitWaitJobs) : [];
  // S7d reinstate: recent "not billable" answers stay reachable under the list
  // (and keep the strip up after the last waiting row is answered), so a
  // mis-tap can be put back.
  const declined = canSee ? permitDeclinedRows(_permitWaitJobs) : [];
  if (!rows.length && !declined.length) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  const esc = escapeHtml;
  const money = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const total = permitWaitingTotal(rows);
  const head = rows.length
    ? `${rows.length} permit cost${rows.length === 1 ? '' : 's'} waiting for a yes · ${money(total)}`
    : 'No permit costs waiting for a yes';
  const declinedHtml = declined.length ? `
      <div style="${rows.length ? 'margin-top:10px;border-top:1px solid var(--border-light,#E2E8F0);padding-top:8px;' : 'margin-top:0;'}">
        <button type="button" aria-expanded="${_permitDeclinedOpen ? 'true' : 'false'}" onclick="window.DispatchModule.togglePermitDeclinedList()"
          style="background:none;border:0;padding:0;cursor:pointer;color:var(--brand-blue,#3B82F6);font-size:14px;min-height:40px;text-align:left;">
          ${_permitDeclinedOpen ? 'Hide' : 'Show'} recently marked not billable (${declined.length})
        </button>
        ${_permitDeclinedOpen ? `
        <div style="display:flex;flex-direction:column;gap:8px;margin-top:6px;">
          ${declined.map(d => {
            const busy = _permitWaitBusy.has(d.id);
            return `
          <div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border-light,#E2E8F0);border-radius:6px;background:var(--bg-elevated,#F8FAFC);">
            <div style="min-width:0;flex:1 1 220px;">
              <div style="font-weight:600;color:var(--text-primary);">${esc(d.title)}</div>
              <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">${esc(money(d.amount))} · not billable${d.byName ? ' · ' + esc(d.byName) : ''}${d.atMs ? ' · ' + esc(new Date(d.atMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })) : ''}</div>
            </div>
            <button class="btn btn-secondary" data-id="${esc(d.id)}" ${busy ? 'disabled' : ''} onclick="window.DispatchModule.reinstatePermitWait(this.dataset.id)">
              <span class="material-icons">undo</span> ${busy ? 'Saving…' : 'Put back on the list'}
            </button>
          </div>`;
          }).join('')}
        </div>` : ''}
      </div>` : '';
  el.style.display = '';
  if (!rows.length) {
    // COD r1 (UX / Skeptic): nothing waiting → no warning card, just a quiet
    // line with the undo link, so the board is not flagged for 30 days.
    el.innerHTML = `
    <div style="margin:0 0 12px 0;padding:4px 2px;">
      <span style="font-size:13px;color:var(--text-secondary);">${esc(head)}.</span>
      ${declinedHtml}
    </div>`;
    return;
  }
  el.innerHTML = `
    <div style="margin:0 0 12px 0;padding:12px;border:1px solid var(--border-light,#E2E8F0);border-left:4px solid var(--status-warning,#D97706);border-radius:8px;background:var(--bg-card,#fff);">
      ${rows.length ? `
      <button type="button" aria-expanded="${_permitWaitOpen ? 'true' : 'false'}" onclick="window.DispatchModule.togglePermitWaitList()"
        style="display:flex;align-items:center;gap:8px;width:100%;background:none;border:0;padding:0;cursor:pointer;color:var(--text-primary);font-weight:600;font-size:15px;text-align:left;min-height:44px;">
        <span class="material-icons" style="font-size:20px;color:var(--status-warning,#D97706);">receipt_long</span>
        <span style="flex:1 1 auto;min-width:0;">${esc(head)}</span>
        <span style="color:var(--brand-blue,#3B82F6);font-size:14px;white-space:nowrap;">${_permitWaitOpen ? 'Hide' : 'Review'}</span>
      </button>` : `
      <div style="display:flex;align-items:center;gap:8px;color:var(--text-primary);font-weight:600;font-size:15px;min-height:44px;">
        <span class="material-icons" style="font-size:20px;color:var(--status-warning,#D97706);">receipt_long</span>
        <span>${esc(head)}</span>
      </div>`}
      ${_permitWaitOpen && rows.length ? `
      <div style="font-size:13px;color:var(--text-secondary);margin:4px 0 10px 0;">
        The AI read these from the permits before anyone was asked to check them. They are not added to new tickets until someone says yes. Tickets already made are not changed.
      </div>
      <div style="display:flex;flex-direction:column;gap:10px;">
        ${rows.map(r => {
          const busy = _permitWaitBusy.has(r.id);
          const editing = _permitWaitEditing === r.id;
          const noFile = r.sourcePaths.length === 0;
          const cantYes = noFile || r.tooManyFiles;
          const files = r.files.length
            ? r.files.map(f => f.url
              ? `<a href="${esc(f.url)}" target="_blank" rel="noopener noreferrer" style="color:var(--brand-blue,#3B82F6);">${esc(f.name)}</a>`
              : esc(f.name)).join(', ')
            : 'No permit file on this job';
          const actions = editing ? `
              <label style="display:flex;align-items:center;gap:4px;font-size:14px;color:var(--text-primary);">$
                <input type="text" inputmode="decimal" data-permit-wait-input="${esc(r.id)}" value="${esc(r.amount.toFixed(2))}" aria-label="Permit cost for ${esc(r.title)}"
                  style="width:110px;min-height:40px;padding:6px 8px;border:1px solid var(--border-light,#E2E8F0);border-radius:6px;font-size:15px;">
              </label>
              <input type="text" maxlength="300" data-permit-wait-reason="${esc(r.id)}" placeholder="Why the change?" aria-label="Why the permit cost for ${esc(r.title)} is different"
                style="flex:1 1 160px;min-width:140px;min-height:40px;padding:6px 8px;border:1px solid var(--border-light,#E2E8F0);border-radius:6px;font-size:15px;">
              <button class="btn btn-primary" data-id="${esc(r.id)}" ${busy ? 'disabled' : ''} onclick="window.DispatchModule.savePermitWaitEdit(this.dataset.id)">${busy ? 'Saving…' : 'Save'}</button>
              <button class="btn btn-secondary" ${busy ? 'disabled' : ''} onclick="window.DispatchModule.editPermitWait(null)">Cancel</button>`
            : `
              <button class="btn btn-primary" data-id="${esc(r.id)}" ${busy || cantYes ? 'disabled' : ''} onclick="window.DispatchModule.confirmPermitWait(this.dataset.id)">
                <span class="material-icons">check</span> ${busy ? 'Saving…' : `Yes, bill ${esc(money(r.amount))}`}
              </button>
              <button class="btn btn-secondary" data-id="${esc(r.id)}" ${busy || cantYes ? 'disabled' : ''} onclick="window.DispatchModule.editPermitWait(this.dataset.id)">Edit</button>
              <button class="btn btn-secondary" data-id="${esc(r.id)}" ${busy ? 'disabled' : ''} onclick="window.DispatchModule.notBillablePermitWait(this.dataset.id)">Not billable</button>`;
          return `
          <div style="padding:10px;border:1px solid var(--border-light,#E2E8F0);border-radius:6px;background:var(--bg-elevated,#F8FAFC);">
            <div style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:8px;">
              <div style="min-width:0;flex:1 1 240px;">
                <div style="font-weight:600;color:var(--text-primary);">${esc(r.title)}</div>
                <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">
                  ${r.customer ? esc(r.customer) + ' · ' : ''}AI read <strong style="color:var(--text-primary);">${esc(money(r.amount))}</strong>${r.permitType ? ` · ${esc(r.permitType)}` : ''}
                </div>
                <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;overflow-wrap:anywhere;">Permit: ${files}</div>
                ${noFile ? '<div style="font-size:12px;color:var(--text-primary);margin-top:2px;">Add the permit to the job to bill it from here, or add the cost on the ticket by hand.</div>' : ''}
                ${r.tooManyFiles ? '<div style="font-size:12px;color:var(--text-primary);margin-top:2px;">This job has more than 10 permit pictures. Add the cost on the ticket by hand.</div>' : ''}
              </div>
              <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">${actions}</div>
            </div>
          </div>`;
        }).join('')}
      </div>` : ''}
      ${declinedHtml}
    </div>`;
  if (_permitWaitEditing) {
    const inp = el.querySelector(`[data-permit-wait-input="${CSS.escape(_permitWaitEditing)}"]`);
    if (inp && document.activeElement !== inp) { inp.focus(); inp.select?.(); }
  }
}

function togglePermitWaitList() {
  _permitWaitOpen = !_permitWaitOpen;
  if (!_permitWaitOpen) _permitWaitEditing = null;
  _renderPermitWaitStrip();
}

function togglePermitDeclinedList() {
  _permitDeclinedOpen = !_permitDeclinedOpen;
  _renderPermitWaitStrip();
}

function editPermitWait(jobId) {
  _permitWaitEditing = jobId || null;
  _renderPermitWaitStrip();
}

function _permitWaitRow(jobId) {
  if (typeof permitWaitingRows !== 'function') return null; // integ/2120: the strip has not painted yet
  return permitWaitingRows(_permitWaitJobs).find(r => r.id === jobId) || null;
}

const _PERMIT_WAIT_REFUSED = {
  permit_changed: 'The permit files on this job changed. Open the job and check the permit, then try again.',
  amount_changed: 'Someone changed this permit cost a moment ago. Check the new amount and try again.',
  already_ruled: 'Someone already answered for this permit cost.',
  superseded: 'A newer permit read is running for this job. Try again in a moment.',
  no_user: 'Sign in again to save this.',
  gone: 'This job is no longer there.',
  not_declined: 'Someone already changed this answer. Check the list again.',
  // TASK-10079 — refusals the server adds.
  job_closed: 'This job is finished or cancelled, so the permit cost was not saved. Add it on the ticket by hand if it is billed.',
  declined: 'This permit cost is marked not billable. Put it back on the list first.',
  reason_required: 'Say why the amount is different from the permit.',
  no_read: 'The permit read was not kept, so the cost was not saved. Save the job again to re-read it, or add the cost on the ticket by hand.',
};

// Yes (amount as read) and Edit (amount as typed) — both through the S7 write.
async function _confirmPermitWaitAt(jobId, amount, reason) {
  const r = _permitWaitRow(jobId);
  if (!r || r.tooManyFiles || !r.sourcePaths.length || _permitWaitBusy.has(jobId) || !_customerRequestCanApprove()) return;
  _permitWaitBusy.add(jobId);
  _renderPermitWaitStrip();
  try {
    const outcome = await _savePermitCostConfirmation(jobId, {
      amount,
      sourceDoc: r.sourcePaths,
      expectParsedCost: r.amount,
      reason: amount === r.amount ? '' : String(reason || ''),
    });
    if (outcome === 'saved') {
      if (_permitWaitEditing === jobId) _permitWaitEditing = null;
      window.showToast?.(`Permit cost $${amount.toFixed(2)} will be added to new tickets on "${r.title}".`, 'success');
    } else {
      window.showToast?.(_PERMIT_WAIT_REFUSED[outcome] || 'Permit cost not saved.', 'warning');
    }
  } catch (e) {
    console.error('[Dispatch] permit wait-list confirm failed:', e);
    window.logError?.(e, { module: 'dispatch', phase: 'permit-wait-confirm', jobId });
    window.showToast?.(_permitCostErrText(e, 'Could not save the permit cost.'), 'error');
  } finally {
    _permitWaitBusy.delete(jobId);
    _renderPermitWaitStrip();
  }
}

function confirmPermitWait(jobId) {
  const r = _permitWaitRow(jobId);
  if (!r) return;
  return _confirmPermitWaitAt(jobId, r.amount);
}

function savePermitWaitEdit(jobId) {
  const r = _permitWaitRow(jobId);
  if (!r) return;
  const inp = document.querySelector(`[data-permit-wait-input="${CSS.escape(jobId)}"]`);
  const amount = parsePermitAmount(inp ? inp.value : '');
  if (amount === null) {
    window.showToast?.('Type the permit cost in dollars, like 1755.02.', 'warning');
    inp?.focus();
    return;
  }
  // TASK-10079: a changed amount needs a reason (the server refuses without one).
  const why = document.querySelector(`[data-permit-wait-reason="${CSS.escape(jobId)}"]`);
  const reason = why ? String(why.value || '').trim() : '';
  if (amount !== r.amount && reason.length < 3) {
    window.showToast?.('Say why the amount is different from the permit.', 'warning');
    why?.focus();
    return;
  }
  return _confirmPermitWaitAt(jobId, amount, reason);
}

async function notBillablePermitWait(jobId) {
  const r = _permitWaitRow(jobId);
  if (!r || _permitWaitBusy.has(jobId) || !_customerRequestCanApprove()) return;
  const tenantAtAsk = DispatchState.tenantId;
  const ok = typeof window.showConfirm === 'function'
    ? (await Promise.resolve().then(() => window.showConfirm(
      'Not billable?',
      `The ${'$' + r.amount.toFixed(2)} permit cost on "${r.title}" will not be added to tickets. You can still add a permit line on a ticket by hand.`,
      { confirmText: 'Not billable', cancelText: 'Back', icon: 'money_off' }
    )).catch(() => false)) === true
    : false;
  if (!ok) return;
  // COD r1: the dialog awaited — the board may have switched company, lost
  // access, or the row may have changed. Refuse rather than decline a job in
  // another tenant that happens to share the id and amount.
  const again = _permitWaitRow(jobId);
  if (DispatchState.tenantId !== tenantAtAsk || !_customerRequestCanApprove() || !again || again.amount !== r.amount) {
    window.showToast?.('The list changed while you were answering. Nothing was saved.', 'warning');
    return;
  }
  _permitWaitBusy.add(jobId);
  _renderPermitWaitStrip();
  try {
    const outcome = await _savePermitCostNotBillable(jobId, r.amount);
    if (outcome === 'saved') window.showToast?.(`Marked not billable: "${r.title}".`, 'info');
    else window.showToast?.(_PERMIT_WAIT_REFUSED[outcome] || 'Not saved.', 'warning');
  } catch (e) {
    console.error('[Dispatch] permit wait-list not-billable failed:', e);
    window.logError?.(e, { module: 'dispatch', phase: 'permit-wait-not-billable', jobId });
    window.showToast?.(_permitCostErrText(e, 'Could not save that.'), 'error');
  } finally {
    _permitWaitBusy.delete(jobId);
    _renderPermitWaitStrip();
  }
}

// S7d reinstate — "Put back on the list". No dialog: it is the undo, and it
// moves no money (the job goes back to waiting; a Yes there is still needed).
async function reinstatePermitWait(jobId) {
  if (!jobId || _permitWaitBusy.has(jobId) || !_customerRequestCanApprove()) return;
  const S = window.AppState || {};
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return;
  const d = permitDeclinedRows(_permitWaitJobs).find(x => x.id === jobId);
  if (!d) return;
  _permitWaitBusy.add(jobId);
  _renderPermitWaitStrip();
  try {
    const outcome = await _savePermitCostReinstate(jobId, { amount: d.amount, byUid: d.byUid, declinedAmount: d.declinedAmount, atMs: d.atMs });
    if (outcome === 'saved') {
      _permitWaitOpen = true;
      window.showToast?.(`Back on the list: "${d.title}". It is not billed until someone says yes.`, 'info');
    } else {
      window.showToast?.(_PERMIT_WAIT_REFUSED[outcome] || 'Not saved.', 'warning');
    }
  } catch (e) {
    console.error('[Dispatch] permit wait-list reinstate failed:', e);
    window.logError?.(e, { module: 'dispatch', phase: 'permit-wait-reinstate', jobId });
    window.showToast?.(_permitCostErrText(e, 'Could not save that.'), 'error');
  } finally {
    _permitWaitBusy.delete(jobId);
    _renderPermitWaitStrip();
  }
}

function permitStorageBasePath(tenantId, jobId) {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  // 🔴 The REAL jobId is load-bearing in this path, not decoration:
  // scripts/permit-harvest.cjs:559 and scripts/backfill-task4574-permit-axles.cjs
  // recover the job by parsing job-permits/YYYY/MM/<jobId>/ back out of it —
  // that is how 160 filed permits joined to their rigs for Permit Pete. Any
  // future "upload before the job exists" change must keep this segment a real
  // job id or it breaks that pipeline silently.
  return `tenants/${tenantId}/job-permits/${year}/${month}/${jobId}`;
}

async function _uploadOnePermit(basePath, pending, onBytes) {
  const ext = pending.isPdf ? 'pdf' : 'jpg';
  const contentType = pending.isPdf ? 'application/pdf' : 'image/jpeg';
  const fullPath = `${basePath}/${pending.id}_full.${ext}`;
  const sizeBytes = pending.blob.size;

  // TASK-9958 — who/how, from the session. customMetadata.uploadedByUid is the
  // rule-pinned per-file record (storage.rules); the entry stamp is for display.
  const uploader = _dispatchFileUploader();
  const customMetadata = fileUploadCustomMetadata(uploader?.uid, 'job_modal');
  const fullRef = ref(storage, fullPath);
  await _uploadPermitBlobWithProgress(fullRef, pending.blob, { contentType, customMetadata }, onBytes);
  const fullUrl = await getDownloadURL(fullRef);

  let thumbUrl = null;
  let thumbPath = null;
  if (!pending.isPdf && pending.thumbBlob) {
    // Thumbnails are ~10 KB. The resumable protocol's extra session round trip
    // costs more than the transfer does, so these stay on plain uploadBytes and
    // are simply not counted in the progress total.
    thumbPath = `${basePath}/${pending.id}_thumb.jpg`;
    const thumbRef = ref(storage, thumbPath);
    await uploadBytes(thumbRef, pending.thumbBlob, { contentType: 'image/jpeg', customMetadata });
    thumbUrl = await getDownloadURL(thumbRef);
  }

  return stampFileProvenance({
    id: pending.id,
    fullUrl,
    thumbUrl,
    fullPath,
    thumbPath,
    name: pending.name,
    mimeType: pending.mimeType,
    isPdf: pending.isPdf,
    sizeBytes,
    uploadedAt: new Date().toISOString()
  }, uploader, 'job_modal');
}

/**
 * Upload the pending permit tray to Storage.
 *
 * Contract change from the old version, and it is the important one:
 * pendingPermitFiles is NO LONGER cleared unconditionally. Only rows that
 * actually landed are dropped (and their blobs released); a row that failed
 * stays in the tray with uploadState:'failed' so the tile can say so and Retry
 * has something to retry. The old code destroyed exactly the files the user
 * still needed.
 *
 * @param {string[]|null} onlyIds  retry: upload only these pending ids.
 */
async function uploadPermitFiles(tenantId, jobId, onlyIds) {
  const only = onlyIds ? new Set(onlyIds) : null;
  const queue = (DispatchState.pendingPermitFiles || []).filter(p =>
    p && p.blob && p.uploadState !== 'done' && (!only || only.has(p.id))
  );

  if (queue.length === 0) {
    DispatchState._lastUploadFailCount = 0;
    return [];
  }

  const basePath = permitStorageBasePath(tenantId, jobId);
  const totalBytes = queue.reduce((n, p) => n + (p.blob?.size || 0), 0);
  const sentByFile = new Map();
  let doneCount = 0;

  queue.forEach(p => { p.uploadState = 'queued'; p.uploadPct = 0; p.uploadError = null; });
  renderPermitGrid();
  setPermitSaveBusy(true);

  const paint = () => {
    let sent = 0;
    for (const v of sentByFile.values()) sent += v;
    const pct = totalBytes > 0 ? Math.min(99, Math.round((sent / totalBytes) * 100)) : 0;
    showPermitProgress({
      label: `Uploading permits… ${Math.min(doneCount + 1, queue.length)} of ${queue.length}`,
      sub: `${_fmtBytes(sent)} of ${_fmtBytes(totalBytes)} · ${PERMIT_UPLOAD_CONCURRENCY} at a time · keep this window open`,
      pct: doneCount >= queue.length ? 100 : pct
    });
  };
  paint();

  const { ok, failed } = await runPermitUploadPool(queue, async (pending) => {
    pending.uploadState = 'uploading';
    _paintPermitTile(pending);
    return await _uploadOnePermit(basePath, pending, (bytes) => {
      sentByFile.set(pending.id, bytes);
      pending.uploadPct = pending.blob?.size ? Math.min(100, Math.round((bytes / pending.blob.size) * 100)) : 0;
      _paintPermitTile(pending);
      paint();
    });
  }, {
    concurrency: PERMIT_UPLOAD_CONCURRENCY,
    onSettled: (n, total, res) => {
      doneCount = n;
      if (res?.ok) {
        // Count the whole file so a failed lane can't hold the bar back.
        sentByFile.set(res.item.id, res.item.blob?.size || 0);
        res.item.uploadState = 'done';
        res.item.uploadPct = 100;
      } else {
        res.item.uploadState = 'failed';
        res.item.uploadError = res.error?.code || res.error?.message || 'upload failed';
      }
      _paintPermitTile(res.item);
      paint();
    }
  });

  const permits = ok.map(r => r.value);

  // Release the blobs that landed — this is the only point at which the tray
  // stops holding a file. Failures KEEP their blob on purpose: it is the only
  // copy the retry has.
  for (const r of ok) {
    const p = r.item;
    if (p.previewUrl) { try { URL.revokeObjectURL(p.previewUrl); } catch (_) {} }
    if (p.fullPreviewUrl) { try { URL.revokeObjectURL(p.fullPreviewUrl); } catch (_) {} }
  }
  const doneIds = new Set(ok.map(r => r.item.id));
  DispatchState.pendingPermitFiles = (DispatchState.pendingPermitFiles || [])
    .filter(p => !doneIds.has(p.id));

  for (const r of failed) {
    console.error('[Dispatch] Permit upload failed:', r.item.id, r.error);
    window.logError(r.error, {
      module: 'dispatch', operation: 'permit-upload',
      permitId: r.item.id, jobId, fileName: r.item.name
    });
  }

  DispatchState._lastUploadFailCount = failed.length;
  DispatchState._permitRetryJobId = failed.length > 0 ? jobId : null;
  // Carried for the banner: the successful tiles leave the tray, so without this
  // a dispatcher who attached 20 and sees 1 failed tile has no way to know the
  // other 19 are safely on the job.
  DispatchState._permitUploadOkCount = (DispatchState._permitUploadOkCount || 0) + permits.length;

  renderPermitGrid();
  setPermitSaveBusy(false);

  // Names the files — "3 permit(s) failed to upload" was true and useless — and
  // hides itself when there is nothing left unresolved. One caller, one decision
  // point, so a pending attach can never be hidden behind a clean upload.
  _syncPermitFailureBanner();

  return permits;
}

/**
 * Hold uploaded-but-not-yet-attached permit rows, and remember which job they
 * belong to so Retry can find it. Cleared by the caller the moment the write
 * that attaches them commits — see the two call sites in saveJob.
 */
function _parkPermitsForAttach(jobId, permits) {
  if (!permits || permits.length === 0) return;
  DispatchState._permitPendingAttach = [...(DispatchState._permitPendingAttach || []), ...permits];
  DispatchState._permitRetryJobId = jobId;
}

/**
 * Re-upload just the permits that failed, and attach them to the job that was
 * already saved.
 *
 * TWO writes can fail here, not one, and the second one has no blob left to fall
 * back on: uploadPermitFiles releases a file the moment its bytes land, so
 * between "uploaded" and "attached" the ONLY handle on that file is
 * DispatchState._permitPendingAttach. It is filled before the attach write and
 * emptied only after it commits — a dropped response mid-attach leaves the rows
 * parked and the banner asking for one more tap, instead of leaving files in
 * Storage that no job points at.
 *
 * The attach runs in a TRANSACTION rather than arrayUnion + increment because it
 * has to be safe to repeat. increment() is not: a write that reached the server
 * and lost its response would be re-sent and double-count permitCount while
 * arrayUnion silently de-duplicated the array beside it. Reading the live array,
 * appending only ids it does not already hold, and setting permitCount to the
 * resulting length is idempotent AND cannot clobber a concurrent edit — which is
 * the array-overwrite bug the JOB_MEDIA_SAVE instrumentation exists to catch.
 */
async function retryFailedPermitUploads() {
  // 🔴 Serialised, and the hold spans the ATTACH as well as the upload. A second
  // retry started while the first was awaiting its transaction shared the park
  // buffer with it; whichever committed first cleared the whole buffer, and if the
  // other's attach then rejected its rows were in Storage, off the job, with their
  // blobs already released and nothing left pointing at them.
  // 🔴 Including the ORIGINAL save. The banner (and its Retry button) is raised by
  // uploadPermitFiles, which finishes before saveJob's own attaching write does —
  // so Retry is on screen and pressable while that write is still in flight. A
  // retry starting there flips the failed row to 'uploading', the save then sees
  // nothing outstanding and never sets the submit lock, and if the retry goes on
  // to fail, Save comes back enabled on a form whose job already exists: a
  // duplicate job, with no navigation and no second form involved.
  if (window.__saveJobInFlight || _permitPrepBusy) {
    _syncPermitFailureBanner('Hold on — the save is still finishing.');
    return;
  }
  if (_permitRetryInFlight) return;   // a second press of the same button
  _permitRetryInFlight = true;
  setPermitSaveBusy(true);
  let cleared = false;
  try {
    cleared = await _runPermitRetry();
  } finally {
    _permitRetryInFlight = false;
    setPermitSaveBusy(false);
  }
  // 🔴 The close happens HERE, after the hold is released, and never inside
  // _runPermitRetry. closeModal consults the very flag this wrapper holds, so a
  // close from in there is silently REFUSED — the retry succeeds, the banner goes
  // away, and the dispatcher is left in a modal whose Save is locked, with no clue
  // that the X is now the only way out.
  if (cleared) closeModal('job');
}

async function _runPermitRetry() {
  const jobId = DispatchState._permitRetryJobId;
  // Not just the FAILED rows. The "Add File / Photo" button is still live behind
  // the recovery banner, and a file added there is `pending`, never `failed` —
  // a failed-only retry uploaded everything except the file the dispatcher had
  // just picked, then closed the modal and threw it away.
  const retryIds = _unresolvedPermitRows().map(p => p.id);
  const alreadyUploaded = DispatchState._permitPendingAttach || [];
  // 🔴 Every id this run is responsible for, captured BEFORE the first await.
  // Shared state can be rewritten underneath a long attach (a view remount, a
  // different job opened), and "nothing is outstanding any more" then looks
  // exactly like success. It is not the same thing, and announcing "All permits
  // uploaded" over a row that was swept away is the one lie this whole task
  // exists to stop telling.
  const expectedIds = new Set([...retryIds, ...alreadyUploaded.map(p => p && p.id)]);

  if (!jobId || (retryIds.length === 0 && alreadyUploaded.length === 0)) {
    hidePermitProgress();
    return false;
  }

  try {
    const permits = retryIds.length > 0
      ? await uploadPermitFiles(DispatchState.tenantId, jobId, retryIds)
      : [];
    // 🔴 Via _parkPermitsForAttach, NOT a bare push — it also re-stamps
    // _permitRetryJobId. uploadPermitFiles nulls that id when nothing failed, so
    // a retry whose uploads all succeed and whose ATTACH then throws would leave
    // rows parked with no job id, and the next press of Retry would bail at the
    // guard below and hide the banner. Dead button, orphaned files.
    _parkPermitsForAttach(jobId, permits);

    const toAttach = DispatchState._permitPendingAttach || [];
    let _retryAdded = [];
    if (toAttach.length > 0) {
      const jobDocRef = doc(db, 'tenants', DispatchState.tenantId, 'jobs', jobId);
      const attached = await runTransaction(db, async (tx) => {
        const snap = await tx.get(jobDocRef);
        if (!snap.exists()) throw new Error('job-missing');
        const current = Array.isArray(snap.data().permits) ? snap.data().permits : [];
        const have = new Set(current.map(p => p && p.id));
        const added = toAttach.filter(p => p && !have.has(p.id));
        const merged = current.concat(added);
        tx.update(jobDocRef, {
          permits: merged,
          permitCount: merged.length,
          updatedAt: Timestamp.now()
        });
        _retryAdded = added; // TASK-9958 — the rows THIS (final) attempt added
        return merged;
      });
      // TASK-9958 (COD r1) — only the rows the committed transaction ADDED. An id
      // the job already held was skipped by the merge and is not a new file; the
      // last tx attempt's `added` is the one that committed.
      _auditJobFilesAdded(jobId, 'job_permits', _retryAdded);
      DispatchState._permitPendingAttach = [];
      try {
        window.logAudit?.('job', 'JOB_MEDIA_SAVE', {
          source: 'dispatch-permit-retry',
          jobId, mediaType: 'permit',
          count: toAttach.length,
          before: { permitCount: attached.length - toAttach.length },
          after: { permitCount: attached.length },
          redFlags: { uploadFailures: DispatchState._lastUploadFailCount || 0, retryPath: true }
        });
      } catch (_) { /* fire-and-forget */ }
      DispatchState.existingPermitFiles = attached;
      DispatchState.loadedPermitCount = attached.length;
      renderPermitGrid();
    }

    const stillFailed = _unresolvedPermitRows().length;
    if (stillFailed === 0) {
      // Account for every id, do not infer success from an empty tray.
      const onJob = new Set((DispatchState.existingPermitFiles || []).map(p => p && p.id));
      const unaccounted = [...expectedIds].filter(id => id && !onJob.has(id));
      if (unaccounted.length > 0) {
        console.error('[Dispatch] Permit retry: rows disappeared mid-run:', unaccounted);
        window.logError?.(new Error('permit-retry-unaccounted'), {
          module: 'dispatch', operation: 'permit-retry', jobId, unaccounted: unaccounted.length
        });
        window.showToast?.(`${unaccounted.length} permit${unaccounted.length === 1 ? '' : 's'} could not be accounted for — check the job and re-add ${unaccounted.length === 1 ? 'it' : 'them'}.`, 'error');
        return true;   // nothing left in here to act on; let the modal close
      }
      hidePermitProgress();
      // Safe to toast here and only here: the modal is about to close, so there is
      // no Retry button left for the toast to cover.
      window.showToast?.('All permits uploaded', 'success');
      return true;   // the WRAPPER closes — see retryFailedPermitUploads
    }
    // Not a toast — see _syncPermitFailureBanner. The banner is what the user is
    // already looking at, and its counts have just changed underneath them.
    _syncPermitFailureBanner('Still no luck on that last try.');
    return false;
  } catch (err) {
    console.error('[Dispatch] Permit retry failed:', err);
    window.logError?.(err, { module: 'dispatch', operation: 'permit-retry', jobId });
    _syncPermitFailureBanner(
      (DispatchState._permitPendingAttach || []).length > 0
        ? 'They uploaded, but attaching them to the job did not go through. Press Retry once more.'
        : 'That retry could not start — the files are still here.'
    );
    return false;
  }
}

/**
 * Give up on the failures and close. The job keeps whatever DID land — this
 * discards only the local blobs, never anything already written.
 */
async function discardFailedPermitUploads() {
  // 🔴 Never while something is in flight. This clears the park buffer, and the
  // rows in it belong to a retry that may still be mid-attach.
  if (_permitUploadBusy || _permitPrepBusy) {
    window.showToast?.('Still working on these — one moment.', 'info');
    return;
  }
  // 🔴 Same set closeModal gates on, or the two disagree and the modal becomes
  // un-closable: closeModal refuses because something is unresolved, hands off to
  // here, and here decides there is nothing to discard and returns. Dead X.
  const unresolved = _unresolvedPermitRows();
  const unattached = (DispatchState._permitPendingAttach || []).length;
  if (unresolved.length === 0 && unattached === 0) { hidePermitProgress(); closeModal('job'); return; }

  const neverUploaded = unresolved.length;
  const parts = [];
  if (neverUploaded > 0) {
    parts.push(`${neverUploaded} file${neverUploaded === 1 ? '' : 's'} never uploaded — discarding means re-picking ${neverUploaded === 1 ? 'it' : 'them'} from the phone later.`);
  }
  // The mixed case is the easy one to lie about. "Every permit that did upload is
  // already saved" is FALSE while rows are sitting in the attach buffer: those
  // are in Storage with no job pointing at them, and closing abandons them.
  if (unattached > 0) {
    parts.push(`${unattached} more made it up but never got attached to the job, and ${unattached === 1 ? 'it goes' : 'they go'} too. Retry would still catch ${unattached === 1 ? 'it' : 'them'}.`);
  } else {
    parts.push('The job itself, and every permit that did upload, is already saved.');
  }

  const confirmed = await window.showConfirm?.(
    'Discard these permits?',
    parts.join(' '),
    { confirmText: 'Discard', cancelText: 'Keep trying', danger: true }
  );
  if (!confirmed) return;

  for (const p of unresolved) {
    if (p.previewUrl) { try { URL.revokeObjectURL(p.previewUrl); } catch (_) {} }
    if (p.fullPreviewUrl) { try { URL.revokeObjectURL(p.fullPreviewUrl); } catch (_) {} }
  }
  const drop = new Set(unresolved.map(p => p.id));
  DispatchState.pendingPermitFiles = (DispatchState.pendingPermitFiles || []).filter(p => !drop.has(p.id));
  DispatchState._lastUploadFailCount = 0;
  DispatchState._permitRetryJobId = null;
  DispatchState._permitPendingAttach = [];
  renderPermitGrid();
  hidePermitProgress();
  _paintPermitSaveButton();
  closeModal('job');
}

// ── permit progress chrome ──────────────────────────────────────────────────

function showPermitProgress({ label, sub, pct, tone }) {
  const wrap = document.getElementById('job-permit-progress');
  if (!wrap) return;
  wrap.classList.remove('hidden');
  const l = document.getElementById('job-permit-progress-label');
  const s = document.getElementById('job-permit-progress-sub');
  const bar = document.getElementById('job-permit-progress-bar');
  const icon = document.getElementById('job-permit-progress-icon');
  const track = document.getElementById('job-permit-progress-track');
  // The wrapper is an aria-live region and `sub` re-renders on EVERY
  // bytesTransferred tick ("1.2 MB of 5.0 MB…"), which a screen reader would
  // read out over and over. Announce the label (one line per file) and the error
  // banner (which names the files) — never the byte counter.
  wrap.setAttribute('aria-live', tone === 'error' ? 'assertive' : 'polite');
  if (s) s.setAttribute('aria-hidden', tone === 'error' ? 'false' : 'true');
  if (l && l.textContent !== (label || '')) l.textContent = label || '';
  if (s && s.textContent !== (sub || '')) s.textContent = sub || '';
  if (bar) {
    bar.style.width = `${Math.max(0, Math.min(100, pct || 0))}%`;
    bar.style.background = tone === 'error' ? 'var(--status-error, #DC2626)' : 'var(--brand-blue)';
  }
  if (track) track.style.display = tone === 'error' ? 'none' : '';
  if (icon) {
    icon.textContent = tone === 'error' ? 'error_outline' : 'cloud_upload';
    icon.style.color = tone === 'error' ? 'var(--status-error, #DC2626)' : 'var(--brand-blue)';
  }
  document.getElementById('job-permit-progress-actions')?.classList.toggle('hidden', tone !== 'error');
}

function hidePermitProgress() {
  document.getElementById('job-permit-progress')?.classList.add('hidden');
  document.getElementById('job-permit-progress-actions')?.classList.add('hidden');
}

/**
 * The failure banner, and the single source of truth for "are there permits
 * still un-uploaded". saveJob reads hasFailedPermitUploads() to decide whether
 * the modal may close.
 */
function _syncPermitFailureBanner(note) {
  // The SAME set closeModal and discard gate on. Three definitions of "still
  // outstanding" in three functions is how a modal ends up refusing to close
  // over something it will not show you.
  const failed = _unresolvedPermitRows();
  const unattached = (DispatchState._permitPendingAttach || []).length;
  if (failed.length === 0 && unattached === 0) {
    DispatchState._lastUploadFailCount = 0;
    hidePermitProgress();
    _paintPermitSaveButton();
    return;
  }
  const okCount = DispatchState._permitUploadOkCount || 0;
  if (failed.length === 0) {
    // Uploaded, not yet attached. The bytes are in Storage and the blob is gone,
    // so this is NOT "nothing happened" — say what actually has to happen next.
    showPermitProgress({
      tone: 'error',
      label: `The job is saved. ${unattached} permit${unattached === 1 ? '' : 's'} uploaded but ${unattached === 1 ? 'is' : 'are'} not attached yet.`,
      sub: `Nothing is lost — press Retry to attach ${unattached === 1 ? 'it' : 'them'}.${note ? ` ${note}` : ''}`,
      pct: 100
    });
    _paintPermitSaveButton();
    return;
  }
  const names = failed.slice(0, 3).map(p => p.name).join(', ');
  const more = failed.length > 3 ? ` and ${failed.length - 3} more` : '';
  const okPart = okCount > 0 ? ` ${okCount} permit${okCount === 1 ? ' is' : 's are'} on it.` : '';
  // 🔴 EVERY word of the failure path lands HERE and not in a toast. Measured at
  // a 390px viewport: #toast-container is a full-width strip pinned at bottom:8px
  // with z-index 1000001, so an 8–12s toast sits directly ON TOP of the Retry
  // button this banner exists to offer. One surface, and one that does not time
  // out — a recovery action must never be covered by the message about it.
  showPermitProgress({
    tone: 'error',
    label: `The job is saved.${okPart} ${failed.length} didn't upload.`,
    sub: `${names}${more} — still here, nothing lost. Retry when you have signal.${note ? ` ${note}` : ''}`,
    pct: 100
  });
  _paintPermitSaveButton();
}

/**
 * The only two ways out of the failure banner (Retry-all-clear and Discard) both
 * close the modal, and the job under it was already saved — so a deferred
 * "Add Another Day" is still owed. saveJob read-and-cleared that request before
 * it returned early; this is where it is honoured.
 */


/**
 * "Is there permit work still unresolved" — the single gate saveJob reads before
 * it is allowed to close the modal. TWO states qualify, and the second one is the
 * easy one to forget: a file whose bytes are in Storage but which no job doc
 * points at yet. Its blob was released on upload, so closing over it strands it.
 */
function hasFailedPermitUploads() {
  return _unresolvedPermitRows().length > 0
    || (DispatchState._permitPendingAttach || []).length > 0;
}

/**
 * The tray rows that still need something to happen to them.
 *
 * The definition CHANGES the moment the form has written its job, and that is
 * deliberate. Before the lock, Save is still ahead and will upload everything in
 * the tray, so only a row that already FAILED is outstanding. After the lock,
 * Save is gone — so a file the dispatcher adds while staring at the recovery
 * banner has no route to Storage at all unless Retry takes it, and closing over
 * it drops it in silence. Post-lock, anything that is not `done` is outstanding.
 */
function _unresolvedPermitRows() {
  const rows = (DispatchState.pendingPermitFiles || []).filter(Boolean);
  return DispatchState._permitModalSubmitLock
    ? rows.filter(p => p.uploadState !== 'done')
    : rows.filter(p => p.uploadState === 'failed');
}

/** Called on every form open (new + edit) so nothing survives into the next job. */
function resetPermitUploadState() {
  // 🔴 NEVER rip this state out from under work that is still running. Navigating
  // to another view and back remounts dispatch and calls initDispatch, which calls
  // this — while an attach transaction may still be awaiting the network. Clearing
  // the park buffer there abandons rows whose bytes are already in Storage, and
  // hands the next retry a shared buffer to race on. The in-flight run settles its
  // own state in its finally; every other caller (showAddJob, editJob) runs with
  // nothing in flight, so this costs them nothing.
  if (_permitUploadBusy || _permitPrepBusy || _permitRetryInFlight) return;
  // 🔴 The TRAY is cleared in here, behind that guard, and not by the caller.
  // initDispatch used to clear it on the line after this call, so the guard above
  // refused and the very next statement threw the blobs away anyway — the guard
  // was decorative for the exact path it was written for.
  DispatchState.pendingPermitFiles = [];
  DispatchState._lastUploadFailCount = 0;
  DispatchState._permitRetryJobId = null;
  DispatchState._permitUploadOkCount = 0;
  DispatchState._permitPendingAddDayJobId = null;
  DispatchState._permitPendingAttach = [];
  DispatchState._permitModalSubmitLock = false;
  hidePermitProgress();
  // Force the busy state to zero rather than decrementing — this runs on form
  // OPEN, where there is nothing in flight and a stray decrement would be wrong.
  _permitBusyDepth = 0;
  _permitUploadBusy = false;
  _permitPrepBusy = false;
  _permitRetryInFlight = false;
  _paintPermitSaveButton();
}

let _permitUploadBusy = false;
// 🔴 REFERENCE-COUNTED, not a boolean. The retry holds the door across its whole
// run — upload AND the attach transaction — while uploadPermitFiles, nested
// inside it, takes and releases its own hold. A plain boolean let that inner
// release re-open everything while the transaction was still in flight, which is
// how a second retry got in and shared the park buffer with the first.
let _permitBusyDepth = 0;
// Set across the ADD path (picker → validate → processPhoto → push). Separate
// from _permitUploadBusy because it means a different thing to the user and
// because it is the flag that covers the awaits inside handlePermitUpload.
let _permitPrepBusy = false;
// One retry at a time. Two concurrent runs share DispatchState._permitPendingAttach,
// and whichever finishes first clears the WHOLE buffer — including the other's
// rows, whose blobs are already gone.
let _permitRetryInFlight = false;

function setPermitSaveBusy(busy) {
  _permitBusyDepth = Math.max(0, _permitBusyDepth + (busy ? 1 : -1));
  _permitUploadBusy = _permitBusyDepth > 0;
  _paintPermitSaveButton();
}

/**
 * The Save button has THREE permit-driven states, and the third one is a guard,
 * not chrome:
 *   uploading  — an upload is in flight outside a save (the retry path).
 *   blocked    — permits failed and the modal is still open. 🔴 On the create
 *                path `#job-id` is empty, so a second Save would CREATE A SECOND
 *                JOB. Retry and Discard are the only two ways out, and both close.
 *   normal     — restore whatever the button said before we touched it.
 */
function _paintPermitSaveButton(originalHtml) {
  // saveJob already owns this button for the length of a save (disabled +
  // "Saving...", restored in its finally). Touching it from here mid-save would
  // re-enable Save under a write that is still in flight — saveJob's own finally
  // calls back in here once the in-flight flag is down.
  if (window.__saveJobInFlight) return;
  const btn = document.getElementById('btn-save-job');
  if (!btn) return;
  if (originalHtml && !btn.dataset.permitPrevHtml) btn.dataset.permitPrevHtml = originalHtml;

  if (_permitUploadBusy || _permitPrepBusy) {
    if (!btn.dataset.permitPrevHtml) btn.dataset.permitPrevHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = _permitPrepBusy
      ? '<span class="material-icons">hourglass_top</span> Preparing…'
      : '<span class="material-icons">cloud_upload</span> Uploading…';
  } else if (DispatchState._permitModalSubmitLock) {
    if (!btn.dataset.permitPrevHtml) btn.dataset.permitPrevHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = hasFailedPermitUploads()
      ? '<span class="material-icons">cloud_off</span> Finish permits'
      : '<span class="material-icons">check</span> Job saved';
  } else {
    btn.disabled = false;
    if (btn.dataset.permitPrevHtml) {
      btn.innerHTML = btn.dataset.permitPrevHtml;
      delete btn.dataset.permitPrevHtml;
    }
  }
}

/**
 * Per-tile state badge. Kept as a string so renderPermitGrid can inline it, and
 * repainted in place by _paintPermitTile during an upload so a byte-progress
 * tick never re-renders (and therefore never re-creates) the whole grid.
 */
function _permitTileStateOverlay(file) {
  const st = file?.uploadState;
  if (!st || st === 'pending') return '';
  const base = 'position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;font-size:0.66rem;font-weight:700;border-radius:inherit;pointer-events:none;';
  if (st === 'failed') {
    return `<div class="permit-tile-state" style="${base}background:rgba(220,38,38,.88);color:#fff;">`
      + `<span class="material-icons" style="font-size:20px;">error_outline</span><span>Failed</span></div>`;
  }
  if (st === 'done') {
    return `<div class="permit-tile-state" style="${base}background:rgba(16,185,129,.85);color:#fff;">`
      + `<span class="material-icons" style="font-size:20px;">check_circle</span></div>`;
  }
  const pct = Math.max(0, Math.min(100, file.uploadPct || 0));
  const word = st === 'queued' ? 'Queued' : `${pct}%`;
  return `<div class="permit-tile-state" style="${base}background:rgba(15,23,42,.72);color:#fff;">`
    + `<span class="material-icons" style="font-size:18px;">cloud_upload</span><span>${escapeHtml(word)}</span></div>`;
}

function _paintPermitTile(file) {
  if (!file?.id) return;
  const tile = document.querySelector(`#job-permit-grid [data-permit-id="${CSS.escape(file.id)}"]`);
  if (!tile) return;
  tile.querySelector('.permit-tile-state')?.remove();
  const html = _permitTileStateOverlay(file);
  if (html) tile.insertAdjacentHTML('beforeend', html);
}

// ============================================
// WAYPOINTS MODAL
// ============================================
function showWaypointsModal(jobId) {
  const job = DispatchState.jobs.find(j => j.id === jobId);
  if (!job || !job.waypoints || job.waypoints.length === 0) return;

  const body = document.getElementById('waypoints-modal-body');
  body.innerHTML = job.waypoints.map((wp, i) => `
    <div class="waypoint-item" style="display:flex;align-items:center;gap:8px;padding:8px 0;border-bottom:1px solid var(--border-light);">
      <div style="flex:1;">
        <div style="font-weight:600;">${escapeHtml(wp.name || `Destination ${i + 1}`)}</div>
        ${wp.url ? (() => {
          // TASK-10116 — clickable only when the saved link is http(s).
          const _h = waypointOpenHref(wp.url);
          const _t = `${escapeHtml(String(wp.url).substring(0, 50))}${String(wp.url).length > 50 ? '...' : ''}`;
          return _h
            ? `<a href="${escapeHtml(_h)}" target="_blank" rel="noopener noreferrer" style="font-size:0.85rem;color:var(--brand-blue);word-break:break-all;">${_t}</a>`
            : `<span style="font-size:0.85rem;color:var(--text-secondary);word-break:break-all;">${_t}</span>`;
        })() : ''}
      </div>
      ${wp.url ? `<button class="btn btn-icon-secondary btn-sm" onclick="navigator.clipboard.writeText('${escJsAttr(wp.url)}'); window.showToast('Link copied','success');" title="Copy link"><span class="material-icons">content_copy</span></button>` : ''}
    </div>
  `).join('') + `
    <div style="margin-top:12px;">
      <button class="btn btn-secondary btn-sm" disabled title="Coming soon">
        <span class="material-icons">map</span> View on Map
      </button>
    </div>
  `;

  document.getElementById('modal-waypoints').classList.remove('hidden');
}

// ============================================
// OVERFLOW MENU ACTIONS
// ============================================

// Store current individual text data
let currentIndividualTextData = { jobId: null, jobDayId: null, recipients: [] };

function sendIndividualText(jobId, jobDayId) {
  window.Telemetry?.logUserAction('sms_modal_opened', { source: 'individual_text', jobId, jobDayId });
  const jobDay = DispatchState.jobDays.find(d => d.id === jobDayId);
  const job = DispatchState.jobs.find(j => j.id === jobId);
  if (!jobDay || !job) {
    // renders-audit #8 — from a SEARCH card this used to read like a bug
    // ("Job not found" for a job that is on screen). Texting a finished job's
    // crew staying unsupported is deliberate (audit product call — no blanket
    // widen); say what is actually going on instead.
    const offBoard = !!_resolveJobAnywhere(jobId);
    window.showToast?.(offBoard
      ? 'This job is off the live board (finished or older schedule) — texting its crew from here is disabled. Reopen the job to message the crew.'
      : 'Job not found', 'error');
    return;
  }
  // TASK-9750 — a tentative day texts nobody. Confirm it first.
  if (isTentativeJobDay(jobDay)) {
    window.showToast?.('This day is tentative, so the crew is not texted. Tap Confirm on the card first.', 'info');
    return;
  }

  // Reset full notes toggle on fresh open (not when regenerating from toggle)
  const indivModal = document.getElementById('modal-individual-text');
  if (indivModal?.classList.contains('hidden')) {
    const fn = document.getElementById('individual-text-full-notes');
    if (fn) fn.checked = false;
  }

  // Get crew members with phone numbers
  const recipients = [];
  const allCrewIds = [...(jobDay.drivers || []), ...(jobDay.helpers || [])];
  const uniqueIds = [...new Set(allCrewIds)];

  uniqueIds.forEach(id => {
    const person = DispatchState.people.find(p => p.id === id);
    if (person?.phone) {
      // TASK-856 (H3 follow-up): carry the custom-PIN signal through so the
      // individual-text flow never surfaces/texts the guessable computed PIN
      // to a driver who already set their own private PIN.
      // TASK-4873 — + the pending-setup-code signal (see sendPersonalizedMassText).
      recipients.push({ name: person.name, phone: person.phone, driverPinCustomSet: !!person.driverPinCustomSet, pinResetRequested: !!person.pinResetRequested });
    }
  });

  if (recipients.length === 0) {
    window.showToast?.('No crew with phone numbers assigned', 'warning');
    return;
  }

  // Store for sending
  currentIndividualTextData = { jobId, jobDayId, recipients };

  // Build message (use formatDateDisplay for timezone-safe display)
  const date = jobDay.date?.toDate?.() || new Date(jobDay.date);
  const dateStr = formatDateDisplay(date);

  // Get crew names
  const driverNames = (jobDay.drivers || []).map(id => {
    const s = String(id);
    const p = DispatchState.people.find(x => x.id === s || String(x.id) === s);
    return p?.name?.split(' ')[0] || '';
  }).filter(Boolean);

  const helperNames = (jobDay.helpers || []).map(id => {
    const s = String(id);
    const p = DispatchState.people.find(x => x.id === s || String(x.id) === s);
    return p?.name?.split(' ')[0] || '';
  }).filter(Boolean);

  // Get truck names
  const truckNames = (jobDay.trucks || []).map(id => {
    const s = String(id);
    const t = DispatchState.trucks.find(x => x.id === s || String(x.id) === s);
    return t ? (window.getUnitDisplay?.(t) || '') : '';
  }).filter(Boolean);

  // Get asset names
  const indivAssetNames = (jobDay.assets || []).map(id => {
    const s = String(id);
    const a = DispatchState.assets.find(x => x.id === s || String(x.id) === s);
    return a?.name || '';
  }).filter(Boolean);

  // Build message - plain text (no emojis to avoid carrier filtering)
  const lines = [];
  lines.push(job.customerName || job.title || 'Job');
  lines.push(`${dateStr} - ${_jobTypeLabelForJob(job) || 'Job'}`); // TASK-9390 — label, never a slug

  if (driverNames.length > 0 || helperNames.length > 0) {
    lines.push('');
    const crewList = [...driverNames, ...helperNames].join(', ');
    lines.push(`Crew: ${crewList}`);
  }

  if (truckNames.length > 0) {
    lines.push(`Truck: ${truckNames.join(', ')}`);
  }

  if (indivAssetNames.length > 0) {
    lines.push(`Equipment: ${indivAssetNames.join(', ')}`);
  }

  const indivFullNotes = document.getElementById('individual-text-full-notes')?.checked;
  if (job.notes) {
    lines.push('');
    if (indivFullNotes) {
      lines.push(`Notes: ${job.notes}`);
    } else {
      const flat = job.notes.replace(/\n/g, ' ').trim();
      lines.push(`Notes: ${flat.length > 80 ? flat.substring(0, 80) + '...' : flat}`);
    }
  }

  lines.push('');
  lines.push('Be safe! Do your pre-trip DVIR.');
  lines.push('');

  // TASK-9941 F S3 (Chris 10/18) — no "Your Code" line and no on-screen list of
  // name + phone codes any more. The text points at the private PIN.
  lines.push('Log in with your private PIN. No PIN yet? Ask dispatch for a setup code.');
  lines.push('(No-reply number)');

  const message = lines.join('\n');

  // Update modal
  document.getElementById('individual-text-content').value = message;
  document.getElementById('individual-text-char-count').textContent = `${message.length} chars`;

  const hint = document.getElementById('individual-text-hint');
  document.getElementById('individual-text-recipients').innerHTML = recipients.length === 1
    ? `Sending to: <strong>${escapeHtml(recipients[0].name)}</strong>`
    : `Sending to: <strong>${recipients.length} people</strong>`;
  if (hint) hint.style.display = 'none';

  // Show modal
  window.paintTenantSendNotices?.(document.getElementById('modal-individual-text')); // TASK-10027 muted-tenant UI hints
  document.getElementById('modal-individual-text').classList.remove('hidden');

  // Render SMS credit meter
  if (window.renderSmsCreditMeter) {
    window.renderSmsCreditMeter('individual-text-sms-meter', { variant: 'compact' });
  }

  // Focus textarea
  setTimeout(() => {
    document.getElementById('individual-text-content')?.focus();
  }, 100);
}

function onIndividualTextInput() {
  const textarea = document.getElementById('individual-text-content');
  const charCount = document.getElementById('individual-text-char-count');
  if (textarea && charCount) {
    charCount.textContent = `${textarea.value.length} chars`;
  }
}

function copyIndividualText() {
  const textarea = document.getElementById('individual-text-content');
  navigator.clipboard.writeText(textarea.value).then(() => {
    window.showToast?.('Copied to clipboard', 'success');
  });
}


// TASK-9381 — `createRestrictedAccessToken(jobId)` used to live here, minting a
// typeless `{tenantId, restrictedToJobId, …}` token whose only "restriction" was
// a client-side filter on the driver board; the member it minted could read the
// whole tenant. It had no callers and its one prod token was an expired test
// link, so it was deleted rather than hardened. grantAccessTokenMembership now
// REFUSES the shape and firestore.rules refuses minting it. A real per-job
// link needs a server-stamped, rules-enforced member restriction — not this key.

// TASK-9941 F S3 — calculateDriverPin removed from Dispatch: nothing here prints
// the name + phone code any more (mass text, Text Crew, the code list).

async function sendIndividualTextSMS() {
  if (_refuseCrewTextWhenPaused()) return; // TASK-10027 muted-tenant UI hints
  // Check SMS capability (testing phase: open to all via hasSmsAccess())
  if (!window.hasSmsAccess?.() && !isDemoMode()) {
    window.Telemetry?.logUserAction('sms_gate_blocked', { source: 'individual_text', reason: 'no_sms_access' });
    window.showSmsUpgradeModal?.('individual-text');
    return;
  }

  const baseMessage = document.getElementById('individual-text-content')?.value?.trim();
  if (!baseMessage) {
    window.showToast?.('No message to send', 'error');
    return;
  }

  const { recipients } = currentIndividualTextData;
  if (!recipients || recipients.length === 0) {
    window.showToast?.('No recipients found', 'error');
    return;
  }
  // TASK-9750 — re-checked at SEND: the day may have been made tentative while
  // this modal sat open (another dispatcher, another tab).
  if (isTentativeJobDay(DispatchState.jobDays.find(d => d.id === currentIndividualTextData.jobDayId))) {
    window.showToast?.('This day is now tentative, so the crew is not texted. Confirm it first.', 'warning');
    return;
  }

  const btn = document.getElementById('btn-send-individual-text');
  const originalText = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="loading-spinner-small"></span> Sending...';

  console.log('[sendIndividualTextSMS] Click handler entered', { recipients: recipients.length });
  window.Telemetry?.logUserAction('sms_send_initiated', { source: 'individual_text', recipientCount: recipients.length });

  try {
    const tenantId = DispatchState.tenantId;
    const sendMassTextFn = httpsCallable(functions, 'sendMassText');

    let sent = 0;
    let failed = 0;

    for (const recipient of recipients) {
      // TASK-9941 F S3 — nothing is personalized with a code any more. A draft
      // that still carries the old placeholder line has it removed, never filled.
      const personalizedMessage = baseMessage.replace('\nYour Code: [auto]', '');

      try {
        console.log(`[sendIndividualTextSMS] Sending to ${recipient.name}...`);
        // Client-side timeout so button never hangs more than 45s per recipient
        const result = await Promise.race([
          sendMassTextFn({
            tenantId,
            // TASK-1563 — individual "Text Crew" messages are dispatcher-typed
            // free text; same GSM-7 assert at the callable boundary as the
            // mass-text paths (sanitize + telemetry, never block).
            message: ensureGsm7BeforeSend(personalizedMessage, 'individual_text'),
            recipients: [{ phone: recipient.phone, name: recipient.name || '' }]
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Timed out after 45s')), 45000))
        ]);
        console.log(`[sendIndividualTextSMS] Result for ${recipient.name}:`, result?.data);

        if (result.data?.sent > 0 || result.data?.demo) {
          sent++;
        } else {
          failed++;
        }
      } catch (err) {
        console.error(`[Dispatch] SMS error for ${recipient.name}:`, err);
        failed++;
      }

      // Update progress
      btn.innerHTML = `<span class="loading-spinner-small"></span> Sending ${sent + failed}/${recipients.length}...`;
    }

    window.Telemetry?.logUserAction('sms_send_completed', { source: 'individual_text', sent, failed, total: recipients.length });

    if (sent > 0) {
      window.showToast?.(`SMS sent to ${sent} crew member(s)${failed > 0 ? `, ${failed} failed` : ''}`, 'success');
      closeModal('individual-text');
    } else if (isDemoMode()) {
      window.showToast?.('SMS simulated (demo mode)', 'success');
      closeModal('individual-text');
    } else {
      window.showToast?.('Failed to send SMS', 'error');
    }
  } catch (error) {
    console.error('[Dispatch] SMS error:', error);
    window.Telemetry?.logUserAction('sms_send_error', { source: 'individual_text', error: (error?.message || 'unknown').slice(0, 200) });
    const msg = error.message || 'Unknown error';
    if (msg.includes('SMS not enabled')) {
      window.showToast?.('SMS not enabled. Purchase an SMS Pack in Settings.', 'error');
    } else if (msg.includes('credit exhausted')) {
      window.showToast?.('SMS credits exhausted. Add more in Settings.', 'error');
    } else {
      window.showToast?.(`SMS error: ${msg}`, 'error');
    }
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalText;
  }
}

// TASK-10086-E follow-up — what a finished board Cancel / Restore day records:
// the general auditLog row (CANCEL_JOBDAY / RESTORE_JOBDAY, payload unchanged)
// AND a jobAuditLog row (JOB_DAY_CANCEL / JOB_DAY_RESTORE — jobs audit there),
// then the toast. The jobAuditLog write is fire-and-forget (_auditJobDayEvent
// never throws), so it never blocks or undoes the status write.
function _jobDayFlipDone(cancel, jobDayId, jd, more, txFlip) {
  window.logAudit?.('dispatch', cancel ? 'CANCEL_JOBDAY' : 'RESTORE_JOBDAY', { jobDayId, jobId: jd.jobId, date: jd.date?.toDate?.()?.toISOString?.()?.slice(0,10), ...more });
  // Only a real flip gets a jobAuditLog row (COD r1): the plain path re-writes an
  // already-cancelled day / restores an active one without asking. A ledger
  // transaction (txFlip) re-read the day itself and only succeeds on a real flip.
  // Date: noon-anchored like every jobDay date, so a bare 'YYYY-MM-DD' stays that day.
  if (txFlip || cancel !== (String(jd.status || '').toLowerCase() === 'cancelled')) {
    _auditJobDayEvent(cancel ? 'JOB_DAY_CANCEL' : 'JOB_DAY_RESTORE', { jobId: jd.jobId, jobDayId, date: _tsToYmdOrNull(jd.date), extra: { oldStatus: jd.status || null } });
  }
  window.showToast?.(cancel ? 'Job day cancelled' : 'Job day restored', 'success');
}

async function cancelJobDay(jobDayId) {
  const confirmed = await window.showConfirm('Cancel Job Day', 'Cancel this job day? This will mark it as cancelled.', { confirmText: 'Cancel Day', danger: true });
  if (!confirmed) return;
  try {
    const ref = doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      // Legacy/virtual jobDay — remove from local state
      DispatchState.jobDays = DispatchState.jobDays.filter(jd => jd.id !== jobDayId);
      _renderDispatchBoardNow(); // optimistic local-state removal — paint before toast
      window.showToast?.('Legacy job day removed', 'success');
      return;
    }
    const jdData = snap.data();

    // TASK-9336 — a LEDGER job settles its rentals against the job's record
    // of units out, in ONE transaction with the status write: the cancel, the
    // stock, the ledger and every inspection it closes land together or not at
    // all. So a retry finds the day already cancelled and moves nothing, and a
    // failure leaves nothing half-done. The day's own assetQtys decide nothing.
    // A read that throws lands in the catch below with nothing written.
    let _ledgerJob = false;
    // Only a day that carries equipment can have rentals to settle; skipping the
    // read otherwise keeps a plain cancel exactly as it was (and offline-able).
    if (jdData.jobId && Array.isArray(jdData.assets) && jdData.assets.length > 0) {
      const _js = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', jdData.jobId));
      _ledgerJob = _js.exists() && _jobHasRentalLedger(_js.data());
    }
    if (_ledgerJob) {
      const res = await _rentalLedgerLeaveTx(DispatchState.tenantId, jdData.jobId, jobDayId, 'cancel');
      if (res.outcome === 'gone') {
        window.showToast?.('That day no longer exists — somebody else removed it.', 'info');
        return;
      }
      if (res.outcome === 'already') {
        window.showToast?.('That day is already cancelled.', 'info');
        return;
      }
      _mirrorRentalAssets(res.changes);
      _jobDayFlipDone(true, jobDayId, jdData, {
        rentalGiveBack: res.changes.map(c => ({ assetId: c.assetId, qty: c.giveBack })), returnInspectionsCancelled: res.cancelledRis }, true);
      return;
    }

    // Pre-ledger job — reverse rental deploy transactions on cancel (unchanged,
    // except the return-inspection scope below).
    //
    // TASK-9336 — `returnInspections` used to be cancelled job-wide: every
    // pending inspection for this asset on this job, including one ANOTHER
    // live day owns. Now: an inspection stamped with this day is cancelled; one
    // stamped with another LIVE day is left alone; an unstamped one (every
    // inspection written before the stamp existed) is cancelled only when no
    // other live day of the job still returns this asset. That needs the job's
    // full schedule, read BEFORE any write so a failed read changes nothing.
    const _cancelRentalIds = (Array.isArray(jdData.assets) ? jdData.assets : []).filter(id => {
      const a = DispatchState.assets.find(x => x.id === id);
      return a && (a.quantityType === 'rental' || a.isRental);
    });
    let _cancelJobDays = [];
    if (jdData.jobId && _cancelRentalIds.some(id => (jdData.assetDirections?.[id] || 'deploy') === 'return')) {
      const _jdSnap = await getDocs(query(
        collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), where('jobId', '==', jdData.jobId)
      ));
      _cancelJobDays = _jdSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
    if (jdData.assets?.length > 0) {
      for (const assetId of jdData.assets) {
        const asset = DispatchState.assets.find(a => a.id === assetId);
        const isRental = asset && (asset.quantityType === 'rental' || asset.isRental);
        if (!isRental) continue;
        const dir = jdData.assetDirections?.[assetId] || 'deploy';
        const qty = jdData.assetQtys?.[assetId] || 1;
        // Reverse: deploy→undo (decrement), return→cancel pending inspection
        if (dir === 'deploy') {
          await processRentalTransaction(asset, 'return', qty, jdData.jobId, true);
        } else if (dir === 'return') {
          // Cancel the pending return inspections for this asset+job that
          // belong to THIS day (see _legacyRiBelongsToLeavingDay).
          const riSnap = await getDocs(query(
            collection(db, 'tenants', DispatchState.tenantId, 'returnInspections'),
            where('assetId', '==', assetId), where('jobId', '==', jdData.jobId), where('status', '==', 'pending')
          ));
          for (const riDoc of riSnap.docs) {
            if (!_legacyRiBelongsToLeavingDay(riDoc.data(), jobDayId, _cancelJobDays, assetId)) continue;
            await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'returnInspections', riDoc.id), {
              status: 'cancelled', updatedAt: Timestamp.now()
            });
          }
        }
      }
    }

    await updateDoc(ref, {
      status: 'cancelled',
      updatedAt: Timestamp.now()
    });
    _jobDayFlipDone(true, jobDayId, jdData);
  } catch (error) {
    console.error('[Dispatch] Cancel error:', error);
    window.showToast?.(_rentalWriteRefusedMsg(error, 'cancel that day') || 'Error cancelling job day', 'error');
  }
}

async function restoreJobDay(jobDayId) {
  try {
    const ref = doc(db, 'tenants', DispatchState.tenantId, 'jobDays', jobDayId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const data = snap.data();
    // TASK-9336 — a LEDGER job's restore undoes what its cancel did, in one
    // transaction with the status flip: units the cancel gave back (the job's
    // `parked`) go back out, and inspections that cancel closed re-open. The
    // pre-ledger restore only flipped the status, so a restored day carried
    // rentals the stock count said were back in the yard.
    if (data.jobId && Array.isArray(data.assets) && data.assets.length > 0) {
      const _js = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', data.jobId));
      if (_js.exists() && _jobHasRentalLedger(_js.data())) {
        const res = await _rentalLedgerRestoreTx(DispatchState.tenantId, data.jobId, jobDayId);
        if (res.outcome === 'gone') return;
        if (res.outcome === 'not-cancelled') {
          window.showToast?.('That day is not cancelled.', 'info');
          return;
        }
        _mirrorRentalAssets(res.changes);
        _jobDayFlipDone(false, jobDayId, data, {
          rentalRedeploy: res.changes.map(c => ({ assetId: c.assetId, qty: c.redeploy })) }, true);
        return;
      }
    }
    const hasAssignments = (data.drivers?.length > 0 || data.trucks?.length > 0);
    await updateDoc(ref, {
      status: hasAssignments ? 'assigned' : 'scheduled',
      updatedAt: Timestamp.now()
    });
    _jobDayFlipDone(false, jobDayId, data);
  } catch (error) {
    console.error('[Dispatch] Restore error:', error);
    window.showToast?.(_rentalWriteRefusedMsg(error, 'restore that day') || 'Error restoring job day', 'error');
  }
}

// ============================================
// TASK-9598 — a copied form says it is a copy
// ============================================
// Copy to New Job used to open titled "New Job" with the source's crew and
// trucks already ticked, so a copy read as a fresh form with a truck
// "preselected" (Steve, Blue Jay, 2026-10-02). These only LABEL the form and
// give one button to empty the pickers; what a copy carries is unchanged.
let _jobCopySource = null;

// PURE. How many crew/equipment ids a raw carry holds.
function _crewCarryCount(c) {
  if (!c) return 0;
  return ['drivers', 'helpers', 'trucks', 'trailers', 'assets']
    .reduce((n, k) => n + (Array.isArray(c[k]) ? c[k].filter(Boolean).length : 0), 0);
}

// PURE. The modal title for a copy of `job`.
function _copyJobModalTitle(job) {
  const num = formatJobNumber(job);
  if (num) return `Copy of ${num} — New Job`;
  const t = String(job?.title || '').trim();
  return t ? `Copy of “${t}” — New Job` : 'Copy of a job — New Job';
}

// Reads the customer list. What the banner says about a copy: the source's
// number, customer, and the date of the run the crew came from.
function _copySourceFromJob(job, seedDay) {
  const crewSrc = seedDay || job || {};
  const ms = seedDay ? _jobDayDateMs(seedDay) : null;
  const ymd = ms != null ? formatDateISOLocal(new Date(ms)) : _tsToYmdOrNull(job?.startDate);
  const cust = job?.customerId ? (DispatchState.customers || []).find(c => c && c.id === job.customerId) : null;
  const t = String(job?.title || '').trim();
  return {
    kind: 'copy',
    label: formatJobNumber(job) || (t ? `“${t}”` : 'the original job'),
    customer: String(cust?.name || '').trim(),
    ymd: ymd || '',
    carried: _crewCarryCount(crewSrc) > 0
  };
}

// The project currently picked on the form, by name ('' if none/unknown).
function _jobFormProjectName() {
  const id = document.getElementById('job-project-id')?.value || '';
  const all = window.ProjectsModule?.getState?.()?.projects || [];
  const p = id ? all.find(x => x && x.id === id) : null;
  return String(p?.name || '').trim();
}

// PURE. 'YYYY-MM-DD' → 'Oct 1' (year added when it is not this year).
function _copyBannerDate(ymd) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

// PURE. src → { text, canClear }. null src → null (no banner).
function _copySourceBannerModel(src) {
  if (!src) return null;
  // TASK-10006 — a return job says what it is and what to do next.
  if (src.returnOf) {
    const where = src.returnOf.project ? 'this project' : (src.label || 'the job');
    return {
      text: `Return job — every rental still out on ${where} is set to Return at the full count. Lower a number for a partial return, then pick the pickup date.`,
      canClear: src.ready !== false
    };
  }
  if (src.kind === 'cleared') {
    return { text: 'Crew and equipment cleared — pick who and what goes on this job.', canClear: false };
  }
  if (src.kind === 'project') {
    const proj = src.project ? `project ${src.project}` : 'this project';
    const from = src.from ? ` (${src.from})` : '';
    const text = src.kit
      ? `Crew and equipment copied from ${proj}${from} — equipment kit “${src.kit}” is ticked. Check before saving.`
      : `Crew and equipment copied from ${proj}${from} — check before saving.`;
    return { text, canClear: true };
  }
  const bits = [src.customer, _copyBannerDate(src.ymd)].filter(Boolean);
  const where = `${src.label}${bits.length ? ` (${bits.join(', ')})` : ''}`;
  // `ready: false` = the copy's prefill timer has not ticked the crew yet, so
  // there is nothing to clear and a click would be discarded (COD r1).
  return src.carried
    ? { text: `Crew and equipment copied from ${where} — check before saving.`, canClear: src.ready !== false }
    : { text: `Copied from ${where}. No crew or equipment was on it.`, canClear: false };
}

function _renderJobCopySourceBanner(src) {
  _jobCopySource = src || null;
  const el = document.getElementById('job-copy-source-banner');
  if (!el) return;
  const model = _copySourceBannerModel(_jobCopySource);
  if (!model) { el.innerHTML = ''; el.classList.add('hidden'); return; }
  // Fixed amber with dark text: readable in both themes without touching styles.css.
  el.innerHTML = `
    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 var(--spacing-md, 12px) 8px;padding:8px 12px;border:1px solid #F59E0B;border-left-width:4px;background:#FEF3C7;color:#78350F;border-radius:6px;font-size:0.85rem;line-height:1.4;">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;">content_copy</span>
      <span style="flex:1 1 200px;min-width:0;">${escapeHtml(model.text)}</span>
      ${model.canClear ? `<button type="button" id="btn-clear-copied-crew" class="btn btn-secondary btn-small" style="font-size:0.8rem;padding:4px 10px;white-space:nowrap;" onclick="window.DispatchModule.clearCopiedCrewAndEquipment()">Clear crew &amp; equipment</button>` : ''}
    </div>`;
  el.classList.remove('hidden');
}

// The banner's button. Empties every crew/equipment picker on a NEW job form and
// drops every carry that could re-tick them later (the late-roster reseed, a
// project kit still waiting on its snapshot, the copy's rental guard, template
// provenance) — the same crew state showAddJob resets for a plain New Job.
// Nothing else on the form moves: customer, PO, stops, permits, dates stay.
function clearCopiedCrewAndEquipment() {
  const modal = document.getElementById('modal-job');
  if (!modal || modal.classList.contains('hidden')) return;
  if (document.getElementById('job-id')?.value) return; // never on an edit
  if (_dupePrefillTimer) return; // the copy has not painted yet — nothing to clear (button not shown then)
  // COD r1 — saveJob's CREATE captures the crew/asset arrays before its awaits;
  // emptying the form mid-save would show "cleared" while the save writes them.
  if (window.__saveJobInFlight) {
    window.showToast?.('Saving — wait for it to finish before clearing crew and equipment.', 'warning');
    return;
  }
  _dupeCrewCarry = null;
  _resetCopyCarryGuard();
  _dupeDropAnnounced = '';
  _pendingProjectKit = null;
  DispatchState.jobCrewSeedIntent = null;
  DispatchState.jobCrewSeedPending = null;
  DispatchState.pendingAssetQtys = {};
  DispatchState.pendingAssetDirections = {};
  resetJobTemplateState();
  renderJobTemplateBar();
  populateAssignmentDropdowns('', '', '');
  populateJobCrewSections([], [], [], [], []);
  setTimeout(runJobComplianceCheck, 0);
  _renderJobCopySourceBanner({ kind: 'cleared' });
}

function duplicateJob(jobId, opts = {}) {
  // TASK-4535 — copying an OLD job for repeat work is the most legitimate
  // history flow there is; it was a silent dead click from search cards.
  const job = _resolveJobAnywhere(jobId);
  if (!job) {
    window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error');
    return;
  }
  // TASK-9703 — a PIN driver's copy (_driverCopyJob). The Job Board has no
  // dispatch jobDays stream: the day the driver opened the job from is the
  // crew source. And equipment never carries — assets are not a driver field
  // (driverSaveJob would refuse nothing, it simply never writes them).
  const _pinCopy = window.AppState?.driverModePin === true;
  const _dupDayPool = () => [
    ...DispatchState.jobDays,
    ...(DispatchState.searchJobDays || []),
    ...(_pinCopy ? (DispatchState.driverBoardJobDays || []) : []),
  ];

  // TASK-851 — close the job-details modal first (mirrors editJob at :9872), else
  // the pre-filled Add-Job form (modal-job) renders BEHIND the still-open details
  // modal and Duplicate "looks broken." closeModal is idempotent → harmless no-op
  // from the calendar-overflow entry points where job-details isn't open.
  closeModal('job-details');

  // Open add job modal and pre-fill with existing data.
  // showAddJob() increments _jobFormGen (TASK-388), so capture the generation
  // AFTER it runs — that's the form-identity this pre-fill belongs to.
  showAddJob();
  const myGen = _jobFormGen;

  // TASK-9598 — say it is a COPY, and of what, the moment the form opens. Steve
  // saw "New Job" with Blue Jay ticked and read it as a fresh form with a truck
  // preselected. Display only: the carry below is unchanged.
  // The Clear button appears only once the prefill below has ticked the crew
  // (COD r1): before that a Clear would be discarded and the timer re-tick it.
  const _retPre = (opts && opts.returnPrefill && !_pinCopy) ? opts.returnPrefill : null; // TASK-10006
  document.getElementById('modal-job-title').textContent = _retPre ? `Return job — ${job.title || 'Untitled Job'}` : _copyJobModalTitle(job);
  const copySrc = _copySourceFromJob(job, _pickLastJobDayByDate(_dupDayPool(), jobId));
  if (_retPre) copySrc.returnOf = { project: !!_retPre.projectId };
  _renderJobCopySourceBanner({ ...copySrc, ready: false });

  // Pre-fill after a short delay to let modal render.
  // TASK-388: token-guard against the form being navigated to a different job
  // (editJob/showAddJob) before this fires. If the dispatcher edits another job
  // in the meantime, _jobFormGen has advanced and we MUST NOT touch the form —
  // otherwise we'd flip the edited job's customer to this duplicate's customer.
  if (_dupePrefillTimer) clearTimeout(_dupePrefillTimer);
  _dupePrefillTimer = setTimeout(() => {
    _dupePrefillTimer = null;
    if (_jobFormGen !== myGen) return; // form moved on — abort all writes
    // TASK-9363 — customer FIRST, through the same onCustomerChange a human pick
    // runs (POC list, price-book picker, PO vocabulary), exactly as the project
    // New Job path does. It clears the contact and the pending price book; the
    // repeat-run carry below puts the SOURCE's back. The title goes in after,
    // so the soft duplicate check it triggers does not flag the job being copied.
    document.getElementById('job-customer').value = job.customerId || '';
    onCustomerChange('duplicate'); // TASK-4579 — the breadcrumb says Copy set it, not the dispatcher
    document.getElementById('job-title').value = job.title || '';
    _restoreInheritedPoMarker(job);
    document.getElementById('job-po-visible').checked = job.poVisible || false;
    document.getElementById('job-vendor-number').value = job.vendorNumber || '';
    document.getElementById('job-afe').value = job.afeNumber || ''; // TASK-9363 — Edit restores it; Copy dropped it
    document.getElementById('job-photos-visible').checked = job.photosVisible || false;
    document.getElementById('job-location').value = job.location || '';
    document.getElementById('job-well-name').value = [job.wellName, job.wellNumber].filter(Boolean).join(' ') || '';
    // TASK-9363 — NOTES START BLANK (Chris). Notes live on the job, and a repeat
    // run inheriting them is how drivers got the last run's yard time, trailers
    // and crew. The source's notes show read-only in the TASK-9391 "Previous
    // notes" panel under the box, with Use these — see _applyCopyRepeatRunCarry.
    if (job.customerId) {
      document.getElementById('poc-section').classList.remove('hidden');
    }
    // Re-add waypoints
    // TASK-359 belt-and-suspenders — filter legacy data where a saved waypoint
    // has both name+url empty (would re-create an empty row + spuriously fire
    // the save-side guard on every edit).
    if (job.waypoints && job.waypoints.length > 0) {
      // TASK-9363 — with the pin (lat/lng), as editJob restores it; dropping it
      // turned a pinned stop into an "unpinned" one on every copy.
      // TASK-9541 — deliberately NO stop id: the copy is a different job, so
      // each stop on it is a different stop and addWaypoint mints a fresh id.
      job.waypoints.forEach(wp => { if (wp.name || wp.url) addWaypoint(wp.name, wp.url, '', wp.lat, wp.lng); });
    }
    // TASK-910 — the dispatcher explicitly chose "Copy to New Job", so the intent
    // to create a SEPARATE job is already expressed. Pre-set the bypass so saveJob
    // does NOT fire the duplicate-blocking modal (whose primary "Add Day to
    // Existing Job" button would write to the ORIGINAL job's doc instead). Resets
    // to false on every showAddJob()/editJob(), so it can't leak to later creates.
    // TASK-9212 — PERMIT FILES. A repeat run of the same work needs the same
    // paperwork, and re-uploading it by hand is why Copy got skipped.
    //
    // 🔴 DEEP-CLONE, never alias `job.permits`. removeExistingPermit() SPLICES
    // DispatchState.existingPermitFiles in place, so handing it the source job's
    // own array would rip the row out of the ORIGINAL job's cached doc as well.
    // With its own array the copy holds its own list of POINTERS: both jobs name
    // the same Storage object, and nothing on this path ever deletes bytes —
    // removeExistingPermit is array-only, and deleting a job is a status flip.
    // (The one deleteObject for permits is deletePermit(), which belongs to the
    // 811 `permits` collection, a different thing entirely.) So deleting a
    // carried permit off the copy leaves the original job untouched, file and all.
    const copiedPermits = (job.permits || []).filter(Boolean).map(p => ({ ...p }));
    DispatchState.existingPermitFiles = copiedPermits;
    DispatchState.existingPermitDocs = null; // a copy has no server Doc map: a copied Doc is a permit there
    // 0, NOT copiedPermits.length. loadedPermitCount means "what was on disk when
    // this form opened", and nothing is on the new job yet — it has no id. The
    // create path in saveJob reads existingPermitFiles directly (see the
    // carried-permits branch there); this counter only feeds the removal audit's
    // before-value, which must say zero for a job that never had any.
    DispatchState.loadedPermitCount = 0;
    renderPermitGrid();
    togglePermitsSection(); // TASK-9546 — always open
    const dupAiParse = document.getElementById('permit-ai-parse');
    if (dupAiParse) dupAiParse.checked = job.permitSettings?.aiParseForBilling ?? true;
    const dupFastTrax = document.getElementById('permit-fasttrax');
    if (dupFastTrax) dupFastTrax.checked = job.permitSettings?.usedFastTrax ?? false;

    // TASK-9212 → TASK-9648 — the 811 tickets, ALL of them. They go into the
    // hidden #job-permit list directly, so a Copy clicked before the permits
    // snapshot keeps them (the old <select> dropped a value with no <option>).
    // populatePermitDropdown drops any that turn out to be deleted once the
    // collection has loaded, so the copy never claims a ticket that is gone.
    document.getElementById('job-permit').value = _jobPermitIdsOf(job).join(',');
    _jobProjDocsSet(job.projectDocs); // TASK-9650
    populatePermitDropdown();

    // TASK-9212 — the OS/OW "Permit Required" decision rides with the files.
    // Without this the copy carries permits AND writes permitRequired:false,
    // which files a "no permit needed" declaration on a job that visibly has one.
    loadJobPermitRequired(job);

    // TASK-9363 — what Edit restores and Copy used to drop: contact (+ CRM
    // re-select), job type (TASK-9390 code), hazmat / interstate / liquid tank,
    // splitBy, all-day + times, the last day's price book (TASK-9391's
    // _projCarriedBook check), the project while it is still this customer's,
    // and the source notes in the Previous notes panel. Dates never carry.
    _applyCopyRepeatRunCarry(job, _pickLastJobDayByDate([..._dupDayPool()], jobId), myGen);

    // TASK-9212 — CREW & EQUIPMENT, carried by default.
    //
    // Crew lives on jobDays, not on the job, so this seeds from the source job's
    // LAST day (see _pickLastJobDayByDate) — the crew that most recently ran the
    // work. `job` itself is the fallback for legacy GAS imports, which carry
    // drivers/trucks on the job doc and have no jobDays at all.
    //
    // 🔴 This CANNOT touch history. These values go into the Add-Job form only;
    // saveJob's create branch writes brand-new jobDay docs in its own batch and
    // never reads or updates the source job's days. Changing the crew here — or
    // clearing it — moves nothing on the original.
    const dupJobDays = _dupDayPool();
    const dupSeedDay = _pickLastJobDayByDate(dupJobDays, jobId);
    const dupCrew = dupSeedDay || job;

    // 🔴 HOLD THE RAW CARRY, not just the normalised result. Both normalisation
    // steps below need the roster, and the roster may not be here yet: click Copy
    // during a cold load and there is nothing to resolve names against and
    // nothing to check an asset's rental flags against. The prefill normalises
    // with whatever it has; _dupeCrewCarry is what lets
    // refreshJobModalCrewIfOpen re-derive from source once each collection lands,
    // instead of re-seeding the unfiltered list the prefill was forced to keep.
    // TASK-10106 S1a — a PIN copy carries STANDARD equipment only (+ its qty);
    // a rental / consumable / PPE item never rides into a driver's new job.
    const _pinStd = _pinCopy
      ? (dupCrew.assets || []).map(String).filter(id => _driverAssetIsStandard((DispatchState.assets || []).find(a => a && a.id === id)))
      : null;
    _dupeCrewCarry = {
      drivers: [...(dupCrew.drivers || [])],
      helpers: [...(dupCrew.helpers || [])],
      trucks: [...(dupCrew.trucks || [])],
      trailers: [...(dupCrew.trailers || [])],
      assets: _pinCopy ? _pinStd : [...(dupCrew.assets || [])],
      assetQtys: _pinCopy ? Object.fromEntries(_pinStd.filter(id => dupCrew.assetQtys?.[id] != null).map(id => [id, dupCrew.assetQtys[id]])) : { ...(dupCrew.assetQtys || {}) },
      assetDirections: _pinCopy ? {} : { ...(dupCrew.assetDirections || {}) },
      // TASK-9363 — the source job's rental ledger, so a quantity rental it
      // still holds out is not deployed a second time by the copy.
      sourceRental: _carriedRentalSource(job)
    };
    if (_retPre) {
      // TASK-10006 — a return job carries the crew and vehicles, and as
      // equipment ONLY the rentals the project still has out, set to Return
      // at that count. Nothing deploys and no consumable is used again.
      _dupeCrewCarry.assets = _retPre.rows.map(r => r.assetId);
      _dupeCrewCarry.assetQtys = Object.fromEntries(_retPre.rows.map(r => [r.assetId, r.pickable]));
      _dupeCrewCarry.assetDirections = Object.fromEntries(_retPre.rows.map(r => [r.assetId, 'return']));
      _dupeCrewCarry.sourceRental = null;
      _dupeCrewCarry.returnPrefill = true;
      // TASK-10006 S3 — the return job made from the save prompt has no crew.
      if (_retPre.noCrew) { _dupeCrewCarry.drivers = []; _dupeCrewCarry.helpers = []; _dupeCrewCarry.trucks = []; _dupeCrewCarry.trailers = []; }
      DispatchState.returnJobCtx = {
        sourceJobId: jobId, projectId: _retPre.projectId || null, customerId: job.customerId ?? null,
        pickable: Object.fromEntries(_retPre.rows.map(r => [r.assetId, r.pickable]))
      };
    }
    const dupNorm = _normalizeCarriedCrew(_dupeCrewCarry);

    // 🔴 SEED THE SIDE MAPS BEFORE RENDERING. populateJobMultiSelect reads
    // pendingAssetQtys / pendingAssetDirections while it builds each tile
    // (:12477, :12485), so setting them after the render paints "1 / Deploy" on a
    // form that saves "4 / Return" — the qty is a billed quantity and the
    // direction moves inventory, so the two disagreeing is a money bug, not a
    // cosmetic one.
    DispatchState.pendingAssetQtys = dupNorm.assetQtys;
    DispatchState.pendingAssetDirections = dupNorm.assetDirections;

    populateAssignmentDropdowns(dupNorm.trucks[0] || '', dupNorm.drivers[0] || '', dupNorm.trailers[0] || '');
    populateJobCrewSections(dupNorm.drivers, dupNorm.helpers, dupNorm.trucks, dupNorm.trailers, dupNorm.assets);

    // 🔴 THE COLD-LOAD CASE. Click Copy before the roster snapshots land and
    // populateJobMultiSelect renders "Loading…" with nothing checked — the
    // carried crew is gone, and the copy saves empty with no error. This is the
    // TASK-1672 mechanism editJob already uses: hold the INTENT plus which
    // dimensions were still pending, and refreshJobModalCrewIfOpen re-seeds each
    // one exactly once as its collection arrives. jobCrewSnapshot stays null —
    // that is the EDIT path's did-the-crew-change gate, and this form creates.
    DispatchState.jobCrewSeedIntent = {
      drivers: dupNorm.drivers, helpers: dupNorm.helpers,
      trucks: dupNorm.trucks, trailers: dupNorm.trailers, assets: dupNorm.assets
    };
    DispatchState.jobCrewSeedPending = {
      people: !DispatchState.dataLoaded.people,
      trucks: !DispatchState.dataLoaded.trucks,
      trailers: !DispatchState.dataLoaded.trailers,
      assets: !DispatchState.dataLoaded.assets
    };

    _announceDroppedCarriedAssets(dupNorm.dropped);
    _markCopyAutoCarried(dupNorm.assets); // TASK-9363 r3 — what the copy actually seeded
    _renderJobCopySourceBanner({ ...copySrc, ready: true }); // TASK-9598 — crew is ticked; Clear may act now

    // TASK-9212 — an 811 ticket is issued for a date and a staked dig footprint,
    // and nothing in the permits collection records either, so nothing here can
    // check it. The copy is for a different day by definition — say so once, at
    // the moment the ticket is carried, rather than let it ride silently.
    if (_jobPermitIdsOf(job).length && is811Enabled()) { // TASK-9994
      window.showToast?.(
        (_jobPermitIdsOf(job).length > 1 ? 'Copied the 811 tickets' : 'Copied the 811 ticket') + ' — confirm they still cover this date and location before anyone digs.',
        'warning', null, 9000
      );
    }

    // Carried crew is NOT pre-vetted crew. The copy is for a different date, so a
    // medical card or a hold that was fine on the source day may not be now —
    // re-run the check against what is actually selected, same as editJob does.
    setTimeout(runJobComplianceCheck, 200);

    // TASK-4688 — same reason as the AI parse: this prefill writes Vendor
    // Number and waypoints, both of which live in gate-able sections.
    // TASK-9212 — and now permits, which share the Attachments section gate.
    revealPopulatedJobFormSections();
    _dupeBypassConfirmed = true;
    if (_retPre) {
      // TASK-10006 — the pickup date is the dispatcher's call (Start Date is
      // required, so the form will not save without one). A pickup dated
      // after today moves no stock until that day (TASK-10015 / D1).
      const _sd = document.getElementById('job-start-date');
      const _ed = document.getElementById('job-end-date');
      if (_sd) _sd.value = '';
      if (_ed) _ed.value = '';
      // The pickups ARE the job: open Assets (and any collapsed group around
      // it) so the Return quantities are on screen, not behind a fold.
      const _sec = document.getElementById('job-section-assets');
      if (_sec) {
        _sec.classList.add('open');
        for (let p = _sec.parentElement; p && p.id !== 'modal-job'; p = p.parentElement) if (p.tagName === 'DETAILS') p.open = true;
      }
      _sd?.focus?.();
      // TASK-10006 S2 — the return job takes the company's "come back" type.
      _jobTypeApplyReturnType(myGen);
    }
    // TASK-9703 — the driver copy re-applies its trim + project picker here,
    // after everything above has painted.
    if (typeof opts.onPrefilled === 'function') {
      try { opts.onPrefilled(); } catch (e) { console.warn('[Dispatch] duplicateJob onPrefilled failed', e); }
    }
  }, 100);
}

// Simple date picker modal using window.showConfirm pattern
function showDatePickerModal(title, message, opts = {}) {
  return new Promise((resolve) => {
    // Create modal HTML
    const modalHtml = `
      <div id="date-picker-modal" class="modal hidden" style="z-index:10001;">
        <div class="modal-content" style="max-width:340px;">
          <div class="modal-header">
            <h3>${title}</h3>
          </div>
          <div class="modal-body" style="padding:var(--spacing-lg);">
            <p style="margin-bottom:var(--spacing-md);">${message}</p>
            <input type="date" id="date-picker-input" class="form-control" style="font-size:1rem;padding:12px;">
          </div>
          <div class="modal-footer" style="display:flex;gap:var(--spacing-sm);justify-content:flex-end;">
            <button type="button" class="btn btn-secondary" id="date-picker-cancel">Cancel</button>
            <button type="button" class="btn btn-primary" id="date-picker-confirm">${escapeHtml(opts.confirmText || 'Add Day')}</button>
          </div>
        </div>
      </div>
    `;

    // Insert modal
    const container = document.createElement('div');
    container.innerHTML = modalHtml;
    document.body.appendChild(container.firstElementChild);

    const modal = document.getElementById('date-picker-modal');
    modal.classList.remove('hidden');
    const input = document.getElementById('date-picker-input');
    const cancelBtn = document.getElementById('date-picker-cancel');
    const confirmBtn = document.getElementById('date-picker-confirm');

    // Set default to tomorrow — LOCAL date (TASK-9407 note: formatDateISO is
    // UTC and landed a day late after ~6pm CT, the TASK-273 bug).
    input.value = formatDateISOLocal(tomorrowLocal());
    if (opts.value && /^\d{4}-\d{2}-\d{2}$/.test(opts.value)) input.value = opts.value; // TASK-10288 D — Move Day opens on the day's own date

    // Event handlers
    const cleanup = () => modal.remove();

    cancelBtn.onclick = () => {
      cleanup();
      resolve(null);
    };

    confirmBtn.onclick = () => {
      const value = input.value;
      cleanup();
      resolve(value || null);
    };
  });
}

// ============================================
// SMART DUPLICATE WARNING
// ============================================
let _duplicateMatchJobId = null;

// TASK-273: per-form bypass flag, reset on every showAddJob() / editJob().
// Set true when dispatcher clicks "Create separate job anyway" in the pre-save
// dupe modal, which then short-circuits the next saveJob invocation. Module-
// scope (not window.*) per COD code-reviewer feedback.
let _dupeBypassConfirmed = false;

// TASK-4660: what the dispatcher was warned ABOUT when they clicked "Create
// separate job anyway", held until the create actually succeeds so the audit row
// can carry BOTH job ids (the matched one and the new one). Written next to the
// JOB_CREATE row in saveJob, then cleared. Deliberately NOT written at click
// time: the point of the receipt is "a duplicate job was created", and a bypass
// whose save then fails validation created nothing. Reset alongside
// _dupeBypassConfirmed on every form open so it can never leak across creates.
// NOTE: the TASK-910 "Copy to New Job" path sets _dupeBypassConfirmed WITHOUT a
// context — that is an explicit, already-expressed intent to create a separate
// job, not a bypass of the dupe modal, so it correctly logs nothing.
let _dupeBypassContext = null;

// TASK-4660: job.startDate is a Timestamp on healthy docs but NOT on all of them
// (see the TASK-4610 warn at ~L2901 — some tenants carry raw strings/Dates), and
// this value goes into an audit row where `undefined` would silently drop the
// entire write. Coerce anything to 'YYYY-MM-DD' or null, never undefined, never
// throw.
function _tsToYmdOrNull(v) {
  if (!v) return null;
  try {
    // A bare 'YYYY-MM-DD' parses as UTC MIDNIGHT, and formatDateISOLocal then
    // reads it back in local time — which in every US timezone lands on the
    // PREVIOUS day. Anchor date-only strings at noon local, the same rule
    // jobDay dates follow ([[lesson_jobday_date_anchors_to_noon_local]]).
    const d = v?.toDate?.() || (v instanceof Date ? v
      : new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? `${v}T12:00:00` : v));
    if (!d || isNaN(d.getTime())) return null;
    return formatDateISOLocal(d);
  } catch { return null; }
}

// TASK-388: form-generation token + duplicate pre-fill timeout handle.
// `duplicateJob` schedules a setTimeout to pre-fill the job form after render.
// If the dispatcher navigates the SAME form to a different job (editJob /
// showAddJob) before that timeout fires, the stale callback would overwrite
// #job-customer while #job-id points at the OTHER job — saveJob then writes the
// wrong customer onto the wrong job (confirmed prod incident: Loenbro→KS
// Industries). Incrementing the token on every form open invalidates any
// in-flight pre-fill; the callback aborts if the token moved. The handle lets
// us also clearTimeout as belt-and-suspenders.
let _jobFormGen = 0;
let _dupePrefillTimer = null;

// TASK-388: original customer of the job being edited, captured when editJob
// opens. saveJob uses it to confirm before silently moving a job to a different
// customer (defense-in-depth against ANY customer-crossing, not just the race).
let _editJobOriginalCustomerId = null;
// TASK-9289 — the project the job being edited was linked to at open.
let _editJobOriginalProjectId = null;

// TASK-9370 — the job open in the form was created from the driver board and its
// DOT flags are still UNKNOWN (null). While true the compliance check treats the
// flags as unknown (warns), and a Save leaves them null rather than silently
// writing the unticked checkboxes as false. Ticking any flag, or "None apply —
// confirm", ends it; the next Save clears needsDispatchReview.
let _jobFormDotUnconfirmed = false;
let _jobFormDotReviewPending = false;
function _renderJobDotReviewChip() {
  const chip = document.getElementById('job-dot-review-chip');
  if (chip) chip.style.display = _jobFormDotUnconfirmed ? 'flex' : 'none';
}
function onJobDotFlagChange() {
  _jobFormDotUnconfirmed = false;
  _renderJobDotReviewChip();
  runJobComplianceCheck();
}
function confirmJobDotFlags() {
  _jobFormDotUnconfirmed = false;
  _renderJobDotReviewChip();
  runJobComplianceCheck();
}

// TASK-4614: the jobDay the open Edit Job form is anchored to — the ONLY day its
// crew/equipment/rate edits may write to. Set by editJob() from the day-card the
// dispatcher clicked (falling back to the job's first day when there is no day
// context), cleared by showAddJob(). null means "no day in hand": saveJob then
// REFUSES the crew write with a toast instead of picking a day, because picking
// broadly is exactly the defect — one save on Aug 26 rewrote five earlier days
// that already carried tickets (Apollo, 2026-08-25 22:19).
let _editJobDayId = null;

// TASK-398: tracks the customer currently shown in the open job form so the
// breadcrumb buffer can record customer_change {from,to}. Set on form open
// (editJob / showAddJob), updated on every onCustomerChange.
let _breadcrumbCustomerId = null;

// ── TASK-9212 ───────────────────────────────────────────────────────────────
// Copy to New Job: the crew AS STORED on the job it was copied from, held raw.
//
// Why raw and not the normalised list. Normalising needs the roster, and Copy is
// clickable before the roster arrives. So the prefill normalises with whatever
// is loaded and keeps the source here; refreshJobModalCrewIfOpen re-derives from
// this the moment each collection lands. Keeping only the normalised result made
// the seed a one-shot: a cold-load copy froze the UNFILTERED list into
// jobCrewSeedIntent and the reseed handed it straight back — a unique rental then
// reached saveJob's CREATE inventory write and stole `deployedToJobId` off the
// job that still physically had the unit (COD Edge Case Hunter + Code Reviewer +
// Independent, round 2).
//
// null on every plain New Job and every edit — both form-open paths clear it.
let _dupeCrewCarry = null;

// One toast per set of dropped assets. The prefill and the reseed can both reach
// the announce (whichever one had the roster), and a dispatcher does not need to
// be told twice about the same unit.
let _dupeDropAnnounced = '';

// TASK-9363 — what _normalizeCarriedCrew needs to know about the job a carry
// came FROM: its rental ledger (TASK-9336) and a human label for the toast.
// PURE. Returned as a copy so a later snapshot of the source cannot move it.
function _carriedRentalSource(job) {
  if (!job || typeof job !== 'object') return null;
  const hasLedger = _jobHasRentalLedger(job);
  return {
    jobId: job.id || null, // TASK-9363 r2 — the save re-reads THIS doc inside the deploy transaction
    hasLedger,
    ledger: hasLedger ? { ...job.rentalLedger } : null,
    label: formatJobNumber(job) || String(job.title || '') || 'the job it came from'
  };
}

// TASK-9363 r2 — PURE. Does this ledger say the job still holds this rental?
// Only a ledger can say "out": `out` units deployed and not handed back, or
// `inspected` units sitting in a pending return inspection. NO ledger (every
// pre-TASK-9336 job, and every billing/project-created job — there is no
// backfill) is not evidence either way, so it answers false and the caller
// falls back to the yard-availability check.
function _sourceHoldsRentalOut(ledger, assetId) {
  if (!ledger || typeof ledger !== 'object' || Array.isArray(ledger)) return false;
  const e = _rentalLedgerEntry(ledger, assetId);
  return e.out > 0 || e.inspected > 0;
}

// TASK-9363 r2/r3 — PURE. The save-time verdict for ONE quantity rental, from
// the CURRENT source job doc and the CURRENT asset doc (both read inside a
// transaction). 'source-out' = the source's ledger now shows it out; 'short' =
// the yard cannot cover the quantity; null = deploy.
// mode 'carried' (the copy seeded it, nobody touched it) runs both tests, the
// source first. mode 'yard' (r3 — the dispatcher re-added it by hand after the
// toast said "add it if more are going") runs the yard test ONLY: the source
// holding its own mats is exactly why more are going.
function _carriedRentalDeployVerdict(srcJob, assetData, assetId, qty, mode) {
  if (mode !== 'yard' && srcJob && _jobHasRentalLedger(srcJob) && _sourceHoldsRentalOut(srcJob.rentalLedger, assetId)) return 'source-out';
  if (assetData && assetData.quantity != null) {
    const available = (Number(assetData.quantity) || 0) - (Number(assetData.deployedQty) || 0);
    if ((Number(qty) || 0) > available) return 'short';
  }
  return null;
}

// TASK-9363 r3 — which assets the copy ACTUALLY seeded into the form, after
// normalisation (set wherever a normalised carry is painted: duplicateJob, the
// project carry, and the late-roster reseed). A rental the toast dropped is
// NOT in here, so re-adding it by hand is never refused as "carried".
let _dupeAutoCarried = null;
// Assets a human has toggled since the form opened. Touching a carried row
// makes it the dispatcher's pick: it leaves _dupeAutoCarried and is checked
// against the yard only. Survives a reseed, so a late roster cannot re-arm it.
let _dupeCarryRetired = new Set();
function _markCopyAutoCarried(assetIds) {
  _dupeAutoCarried = new Set((assetIds || []).filter(id => !_dupeCarryRetired.has(id)));
}
function _retireCopyCarriedAsset(assetId) {
  if (!assetId) return;
  _dupeCarryRetired.add(assetId);
  if (_dupeAutoCarried) _dupeAutoCarried.delete(assetId);
}
function _resetCopyCarryGuard() {
  _dupeAutoCarried = null;
  _dupeCarryRetired = new Set();
}

// TASK-9363 r3 — PURE. The save-time guard for a NEW job created from a Copy:
// { sourceJobId, sourceLabel, modes: Map(assetId → 'carried' | 'yard') }.
//   'carried' = the copy seeded it and nobody touched it → source + yard check;
//   'yard'    = the dispatcher toggled it (e.g. re-added a dropped mat) →
//               yard check only, like the "add it if more are going" toast says.
// Any other selected asset (added by hand, never part of the carry) is not in
// the map and deploys exactly as a plain New Job would.
function _copyRentalGuardFor(selectedAssets, carry, autoCarried, retired) {
  const src = carry && carry.sourceRental;
  if (!src || !src.jobId) return null;
  const modes = new Map();
  (selectedAssets || []).forEach(id => {
    if (retired && retired.has(id)) modes.set(id, 'yard');
    else if (autoCarried && autoCarried.has(id)) modes.set(id, 'carried');
  });
  return modes.size ? { sourceJobId: src.jobId, sourceLabel: src.label || 'the job it came from', modes } : null;
}

// TASK-9363 r3 — decide deployability BEFORE any jobDay is written. One
// read-only transaction per guarded quantity rental reads the source job (for
// 'carried') and the asset as they are now; the verdict decides whether the
// rental goes into the day payload at all. Returns [{ assetId, verdict, name, qty }]
// for the refused ones. A failed read throws — saveJob has written nothing yet,
// so the save stops cleanly instead of guessing.
async function _precheckCarriedRentals(tenantId, guard, selectedAssets, qtyOf, dirOf) {
  const refused = [];
  if (!guard) return refused;
  for (const id of (selectedAssets || [])) {
    const mode = guard.modes.get(id);
    if (!mode) continue;
    const asset = (DispatchState.assets || []).find(a => a.id === id);
    if (!asset || !(asset.quantityType === 'rental' || asset.isRental) || asset.trackingMode !== 'quantity') continue;
    if (dirOf(id) !== 'deploy') continue;
    const qty = Math.max(0, Number(qtyOf(id)) || 0);
    const verdict = await runTransaction(db, async (tx) => {
      const srcSnap = mode === 'carried' ? await tx.get(doc(db, 'tenants', tenantId, 'jobs', String(guard.sourceJobId))) : null;
      const aSnap = await tx.get(doc(db, 'tenants', tenantId, 'assets', id));
      return _carriedRentalDeployVerdict(srcSnap && srcSnap.exists() ? srcSnap.data() : null,
        aSnap.exists() ? aSnap.data() : null, id, qty, mode);
    });
    if (verdict) refused.push({ assetId: id, verdict, name: asset.name || asset.partNumber || id, qty });
  }
  return refused;
}

// TASK-9363 r3 — the backstop's undo. Only reached when the deploy transaction
// refuses AFTER the days exist (the stock moved between the pre-check and the
// deploy). One atomic batch over every new day — all or none — and a failure is
// never swallowed: a blocking dialog offers Retry, because a day that lists a
// rental bills it (billing reads jd.assets). Declining leaves a sticky error
// naming the rental and is logged. Returns true when the days are clean.
// r4: `hooks.onPause` / `hooks.onResume` bracket the dialog so saveJob can
// pause its 25s hang watchdog while a human decides (the TASK-4567 mid-save
// confirm pattern) — otherwise the watchdog re-enables Save under the dialog.
async function _takeRefusedRentalOffDays(dayRefs, assetId, label, hooks) {
  if (!dayRefs || dayRefs.length === 0) return true;
  for (;;) {
    try {
      const b = writeBatch(db);
      dayRefs.forEach(r => b.update(r, { assets: arrayRemove(assetId) }));
      await b.commit();
      return true;
    } catch (e) {
      console.error('[Dispatch] TASK-9363 could not take a refused rental off the new days:', e);
      hooks?.onPause?.();
      let retry = false;
      try {
        retry = await window.showConfirm(
          'Rental not deployed',
          `${label} was not deployed, but it is still listed on this job's days and would be billed. Retry removing it?`,
          { confirmText: 'Retry', cancelText: 'Fix it on the board', icon: 'error', iconColor: '#DC2626' }
        );
      } finally {
        hooks?.onResume?.();
      }
      if (!retry) {
        window.logError?.(e, { module: 'dispatch', operation: 'TASK-9363-refused-rental-undo', assetId });
        // Infinity = sticky (showToast r4): stays until the dispatcher taps ✕.
        window.showToast?.(`${label} is still listed on this job's days but was NOT deployed. Remove it from the job before it is billed.`, 'error', null, Infinity);
        return false;
      }
    }
  }
}

function _announceDroppedCarriedAssets(dropped) {
  if (!dropped || dropped.length === 0) return;
  const key = dropped.slice().sort().join('|');
  if (key === _dupeDropAnnounced) return;
  _dupeDropAnnounced = key;
  window.showToast?.(
    `Not copied: ${dropped.join('; ')}. A rental stays on the job that has it.`,
    'info', null, 8000
  );
}

/**
 * Turn a raw carried crew into what the Add-Job form should hold.
 *
 * TWO transforms, both of which need collections that may still be loading —
 * which is exactly why this is a function called twice rather than a block run
 * once:
 *
 *   NAMES → IDS. CLAUDE.md pitfall #5: GAS-imported jobs and jobDays store name
 *   strings. populateJobMultiSelect checks `selected.includes(item.id)` and
 *   nothing else, so an unresolved name renders UNCHECKED and the copy then
 *   saves an EMPTY crew — it looks like Copy simply did not carry. A vehicle's
 *   display name lives in `nickname`/`unit`, not `name`
 *   ([[reference_vehicle_name_lives_in_nickname]]). A ref that resolves to
 *   nothing is passed through untouched: the picker drops what it cannot show,
 *   and inventing a miss here would delete a real assignment.
 *
 *   ASSET STATES A COPY CANNOT INHERIT. saveJob's CREATE path does real
 *   inventory work off these selections (phase 'assets-rental-update'), and it
 *   is a path editJob's identical populateJobCrewSections call never reaches:
 *     • a UNIQUE (non-quantity) rental writes `deployedToJobId: <new job>`. One
 *       physical unit cannot be on two jobs, so carrying it silently moves the
 *       pointer off the job that still has the thing in the field.
 *     • a carried direction of 'return' files a returnInspections row, or
 *       decrements deployedQty, for a brand-new job that never deployed
 *       anything — a second return for one deployment.
 *   Both describe the run that HAPPENED, not the one being planned. Dropped and
 *   announced, never dropped quietly. Everything else — people, vehicles,
 *   trailers, consumables, quantity rentals going out — carries.
 *
 * Side maps are rebuilt to match the surviving assets only, so a dropped rental
 * leaves no orphan qty/direction entry behind for the save to act on.
 */
function _normalizeCarriedCrew(raw) {
  const resolve = (refs, pool) => {
    const list = pool || [];
    const out = [];
    (refs || []).forEach(ref => {
      if (!ref) return;
      const hit = list.find(x => x.id === ref)
        || list.find(x => x.name === ref || x.nickname === ref || x.unit === ref);
      const id = hit ? hit.id : ref;
      if (!out.includes(id)) out.push(id);
    });
    return out;
  };

  const dropped = [];
  const assets = resolve(raw?.assets, DispatchState.assets).filter(id => {
    const asset = (DispatchState.assets || []).find(a => a.id === id);
    // Not in cache yet — KEEP it. The reseed re-runs this with the real row;
    // dropping on a cache miss would silently lose a legitimate asset.
    if (!asset) return true;
    if (!(asset.quantityType === 'rental' || asset.isRental)) return true;
    const dir = raw?.assetDirections?.[id] || 'deploy';
    // TASK-10006 — "Create return job": the carry IS the pickup list, built
    // from what the project still has out (_loadProjectRentalsOut). Kept.
    if (raw?.returnPrefill && dir === 'return') return true;
    if (dir === 'return' || asset.trackingMode !== 'quantity') {
      dropped.push(asset.name || asset.partNumber || id);
      return false;
    }
    // TASK-9363 — a QUANTITY rental the SOURCE job still holds out is not
    // deployed again by the copy: CREATE's rental lane (_rentalMoveLedgerTx's
    // deploy branch) increments deployedQty with no look at the source's
    // ledger, so the same mats would be counted out twice. Dropped only when a
    // ledger SHOWS it out (`out`, or `inspected` = a pending return inspection
    // is not a return). r2 (COD Independent): a source with NO ledger is the
    // common case — every pre-TASK-9336 job and every billing/project-created
    // job — so it falls through to the yard-availability check below instead of
    // dropping mats that are back in the yard. The save re-checks both against
    // the CURRENT docs inside the deploy transaction (_rentalMoveLedgerTx).
    // No sourceRental at all (job templates) = no source job = no check.
    if (raw && raw.sourceRental && raw.sourceRental.hasLedger
        && _sourceHoldsRentalOut(raw.sourceRental.ledger, id)) {
      dropped.push(`${asset.name || asset.partNumber || id} — still out on ${raw.sourceRental.label || 'the job it came from'}, add it if more are going`);
      return false;
    }
    // A QUANTITY rental going out again is legitimate — mats and tanks are
    // exactly what a repeat run needs — but only if the yard actually has them.
    // CREATE's rental lane does a bare `increment(deployedQty, qty)` with no
    // availability check (:assets-rental-update), so carrying a load the source
    // job is still holding deploys stock that does not exist. The check uses the
    // row's own numbers and refuses only when it can PROVE the shortfall; a row
    // with no `quantity` recorded is not evidence of a shortfall, so it carries.
    const want = raw?.assetQtys?.[id] || 1;
    const available = (asset.quantity || 0) - (asset.deployedQty || 0);
    if (asset.quantity != null && want > available) {
      dropped.push(`${asset.name || asset.partNumber || id} (${available} of ${want} available)`);
      return false;
    }
    return true;
  });

  const assetQtys = {};
  const assetDirections = {};
  assets.forEach(id => {
    const qty = raw?.assetQtys?.[id];
    if (qty && qty > 1) assetQtys[id] = qty;
    if (raw?.assetDirections?.[id] === 'return') assetDirections[id] = 'return';
  });

  return {
    drivers: resolve(raw?.drivers, DispatchState.people),
    helpers: resolve(raw?.helpers, DispatchState.people),
    trucks: resolve(raw?.trucks, DispatchState.trucks),
    trailers: resolve(raw?.trailers, DispatchState.trailers),
    assets, assetQtys, assetDirections, dropped
  };
}

// ============================================
// TASK-10006 S1 — "CREATE RETURN JOB": one tap brings rentals back
// ============================================
//
// Guy (10/21): "If I go into this job and this project, it should tell me,
// hey, you got stuff out. Do you want to do a return? … I shouldn't have to
// build it all. And it should add it up." A job (or project) with rentals out
// offers "Create return job": a Copy of the job with every rental the PROJECT
// still has out set to Return, quantity = what is still out across all of the
// project's jobs (installs + add-ons − pickups), editable for a partial return.
//
// HOW IT COUNTS — derived on read, nothing new is written. The count comes
// from the shared math (window.RentalLedgerMath.rlmProjectRentalsOut, the same
// file the board, the hourly pickup settle and the owner's recount use —
// TASK-10015), so the prefill, the project strip and the asset's out-count
// cannot disagree. A return job saved here is an ordinary removal job: its
// pickup comes off the asset total when it HAPPENS (TASK-10015 / D1 — a later
// day is held as "pickup planned" and moved by settleRentalPickups), and its
// own ledger says so (dayReturns / pendingPickups). The project count nets it.
// So delete / remove-day / cancel of the return job already put the units
// back through the existing paths — no other job's ledger is ever written by
// this feature (the spec's COD r1 server-settle concern does not arise).
//
// Not pickable twice: units already on a pickup booked for a later day, and
// units on a pending return inspection, are shown but left off the prefill.

let _createReturnJobBusy = false;

// tenant-local 'YYYY-MM-DD' of a job's start (orders the sources oldest first).
function _returnJobStartKey(job) {
  const v = job?.startDate?.toDate?.() || (job?.startDate != null ? new Date(job.startDate) : null);
  const ms = v ? v.getTime() : NaN;
  return Number.isFinite(ms) ? _rentalDateKey(ms) : '';
}

// Reads everything the count needs for ONE job's project (or the job alone
// when it has no project). Throws on a failed read — a count built from half
// the jobs would prefill the wrong number, so the caller says it could not.
async function _loadProjectRentalsOut(job) {
  const tid = DispatchState.tenantId;
  if (!job || !tid) return null;
  const projectId = job.projectId || null;
  let jobs;
  if (projectId) {
    const snap = await getDocs(query(collection(db, 'tenants', tid, 'jobs'), where('projectId', '==', projectId)));
    if (DispatchState.tenantId !== tid) return null; // switched companies mid-read
    // Board copies (live snapshot) win; cancelled jobs are left off.
    jobs = mergeProjectJobs(DispatchState.jobs, snap.docs.map(d => ({ id: d.id, ...d.data() })), projectId);
  } else {
    jobs = String(job.status || '').toLowerCase() === 'cancelled' ? [] : [job];
  }
  const chunk = (list) => { const out = []; for (let i = 0; i < list.length; i += 30) out.push(list.slice(i, i + 30)); return out; };
  const inQuery = async (coll, list, field = 'jobId') => {
    const snaps = await Promise.all(chunk(list).map(c => getDocs(query(collection(db, 'tenants', tid, coll), where(field, 'in', c)))));
    return snaps.flatMap(s => s.docs.map(d => ({ id: d.id, ...d.data() })));
  };
  // Return jobs made from these jobs (returnForJobId) count too, even when
  // they are not on the project — else a no-project job, or a return saved
  // without its project, would keep offering the same units forever.
  // COD r1: followed down the chain (a return made from a return), at most
  // 5 levels, each job once.
  if (jobs.length) {
    const have = new Set(jobs.map(j => j.id));
    let frontier = [...have];
    for (let depth = 0; frontier.length && depth < 5; depth++) {
      const rets = await inQuery('jobs', frontier, 'returnForJobId');
      if (DispatchState.tenantId !== tid) return null;
      frontier = [];
      for (const r of rets) {
        if (have.has(r.id)) continue;
        have.add(r.id);
        frontier.push(r.id); // COD r2: walk THROUGH a cancelled return to its live children
        if (String(r.status || '').toLowerCase() === 'cancelled') continue;
        jobs.push((DispatchState.jobs || []).find(j => j.id === r.id) || r);
      }
    }
  }
  const ids = jobs.map(j => j.id);
  const olderIds = jobs.filter(j => !_jobHasRentalLedger(j)).map(j => j.id);
  const [ris, txs] = await Promise.all([inQuery('returnInspections', ids), inQuery('assetTransactions', olderIds)]);
  if (DispatchState.tenantId !== tid) return null;
  let assets = DispatchState.assets;
  if (!(_dispatchDataIsFor(tid) && DispatchState.dataLoaded?.assets && Array.isArray(assets) && assets.length)) {
    // Opened from Projects before Dispatch loaded its roster: one read.
    const aSnap = await getDocs(collection(db, 'tenants', tid, 'assets'));
    if (DispatchState.tenantId !== tid) return null;
    assets = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  const dayDates = {};
  for (const d of (DispatchState.jobDays || [])) if (d && d.id && ids.includes(d.jobId)) dayDates[d.id] = _rentalDayKeyOf(d);
  const res = _rlm().rlmProjectRentalsOut({
    jobs: jobs.map(j => ({ id: j.id, title: j.title || j.name || '', live: true, ledger: _jobHasRentalLedger(j) ? j.rentalLedger : null, startKey: _returnJobStartKey(j) })),
    assets, ris, txs, dayDates, todayKey: _rentalTodayKey() // TASK-10006 S3 — overdue pickups
  });
  return { projectId, jobs, res };
}

// PURE. The plain-English lines for the "Out on this project" strip.
function _rentalsOutStripModel(res, isProject) {
  const rows = (res && res.rows) || [];
  const where = isProject ? 'on this project' : 'on this job';
  const onSite = rows.filter(r => r.onSite > 0);
  if (!onSite.length) return null;
  const lines = [];
  for (const r of onSite) {
    if (r.planned > 0) {
      const when = r.plannedOn.map(p => _copyBannerDate(p.dateKey)).filter(Boolean).join(', ');
      lines.push(`${r.planned} ${r.name} already on a pickup${when ? ` (${when})` : ''}`);
    }
    // TASK-10006 S3 — a pickup whose day passed and nobody picked up: still out.
    if (r.overdue > 0) lines.push(`Pickup overdue — still on site: ${r.overdue} ${r.name}`);
    if (r.inspecting > 0) lines.push(`${r.inspecting} ${r.name} back, waiting on return inspection`);
  }
  const older = (res.older || []).length;
  if (older) lines.push(`Includes ${older} older job${older === 1 ? '' : 's'} counted from stock records`);
  return {
    title: `Out ${where}: ` + onSite.map(r => `${r.onSite} ${r.name}`).join(' · '),
    lines,
    pickable: rows.reduce((n, r) => n + r.pickable, 0)
  };
}

function _rentalsOutStripHtml(model, buttonHtml) {
  if (!model) return '';
  return `
    <div class="rentals-out-strip" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 0 12px;padding:10px 12px;border:1px solid var(--border-light, #E2E8F0);border-left:4px solid var(--brand-blue, #3B82F6);border-radius:6px;background:var(--bg-secondary, #F8FAFC);color:var(--text-primary);min-width:0;">
      <span class="material-icons" aria-hidden="true" style="font-size:20px;color:var(--brand-blue, #3B82F6);">inventory_2</span>
      <div style="flex:1 1 220px;min-width:0;">
        <div style="font-weight:600;font-size:0.9rem;overflow-wrap:anywhere;">${escapeHtml(model.title)}</div>
        ${model.lines.map(l => `<div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;">${escapeHtml(l)}</div>`).join('')}
      </div>
      ${buttonHtml || ''}
    </div>`;
}

// Job Details: fill the strip + show the button once the count lands.
// Fire-and-forget; a late answer for a job no longer on screen is dropped.
async function _renderJobRentalsOut(jobId) {
  const host = document.getElementById('job-details-rentals-out');
  if (!host) return;
  host.dataset.jobId = jobId;
  const job = _resolveJobAnywhere(jobId);
  if (!job) return;
  try {
    const loaded = await _loadProjectRentalsOut(job);
    const now = document.getElementById('job-details-rentals-out');
    if (!loaded || !now || now.dataset.jobId !== jobId) return;
    const model = _rentalsOutStripModel(loaded.res, !!job.projectId);
    const rosterReady = _dispatchDataIsFor(DispatchState.tenantId) && !!DispatchState.dataLoaded?.assets;
    if (model && model.pickable > 0 && _returnJobCanCreate() && !rosterReady) model.lines.push('Open Dispatch to create a return job from here.');
    const btn = (model && model.pickable > 0 && _returnJobCanCreate() && rosterReady)
      ? `<button type="button" class="btn btn-primary btn-small" id="btn-create-return-job" style="white-space:nowrap;min-height:40px;" onclick="window.DispatchModule.createReturnJob('${escJsAttr(jobId)}')"><span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:-3px;">assignment_return</span> Create return job</button>`
      : '';
    now.innerHTML = _rentalsOutStripHtml(model, btn);
  } catch (e) {
    console.warn('[Dispatch] TASK-10006: rentals-out count failed', e?.code || e?.message || e);
  }
}

// Same gate as + Add Job (create access, not view & upload), office only.
function _returnJobCanCreate() {
  if (window.AppState?.driverModePin === true) return false;
  return _canCreateJobs() && _dispatchCanManageDays();
}

async function createReturnJob(jobId) {
  if (!_returnJobCanCreate()) {
    window.showToast?.('Your access can’t create jobs — ask an admin to add the Dispatch module.', 'error');
    return;
  }
  if (_createReturnJobBusy) return;
  _createReturnJobBusy = true;
  try {
    const job = _resolveJobAnywhere(jobId);
    if (!job) { window.showToast?.('That job could not be loaded. Try refreshing the page.', 'error'); return; }
    let loaded;
    try {
      loaded = await _loadProjectRentalsOut(job);
    } catch (e) {
      console.warn('[Dispatch] TASK-10006: createReturnJob count failed', e?.code || e?.message || e);
      window.showToast?.('Couldn’t add up what’s out on this project. Check your connection and try again.', 'error');
      return;
    }
    if (!loaded) return;
    const rows = loaded.res.rows.filter(r => r.pickable > 0);
    if (!rows.length) {
      const booked = loaded.res.rows.some(r => r.planned > 0 || r.inspecting > 0);
      window.showToast?.(booked
        ? 'Everything still out already has a pickup booked or is waiting on inspection.'
        : `Nothing is out ${job.projectId ? 'on this project' : 'on this job'} to bring back.`, 'info');
      return;
    }
    duplicateJob(jobId, { returnPrefill: { rows, projectId: loaded.projectId, older: loaded.res.older.length } });
  } finally {
    _createReturnJobBusy = false;
  }
}

// Projects page: the strip's numbers (no DOM) and its button. The copy base is
// the project's newest live job — customer, stops, PO and crew come from it.
async function projectRentalsOut(projectId) {
  if (!projectId) return null;
  const tid = window.AppState?.tenant;
  if (!tid) return null;
  if (!DispatchState.tenantId) DispatchState.tenantId = tid;
  if (DispatchState.tenantId !== tid) return null;
  const loaded = await _loadProjectRentalsOut({ id: '', projectId });
  if (!loaded) return null;
  const base = loaded.jobs.slice().sort((a, b) => _returnJobStartKey(b).localeCompare(_returnJobStartKey(a)))[0] || null;
  return { model: _rentalsOutStripModel(loaded.res, true), baseJobId: base ? base.id : null, canCreate: _returnJobCanCreate() };
}

async function createReturnJobForProject(projectId, baseJobId) {
  if (!baseJobId) return;
  const tid = window.AppState?.tenant;
  // The Add Job form needs Dispatch's roster. When Projects was opened first,
  // open the job instead; its details carry the same button.
  if (!(tid && _dispatchDataIsFor(tid) && DispatchState.dataLoaded?.assets && _resolveJobAnywhere(baseJobId))) {
    await openJobDetailsById(baseJobId);
    return;
  }
  _ensureDriverJobModalMounted();
  return createReturnJob(baseJobId);
}

// PURE. Quantities typed above what the project has out (warn, never block —
// the field wins, TASK-4810 S2; the extra is a pickup no job on it holds).
function _returnJobOverTyped(ctx, selectedAssets, directions, qtys, nameOf) {
  if (!ctx || !ctx.pickable) return [];
  const over = [];
  for (const id of (selectedAssets || [])) {
    if (!Object.prototype.hasOwnProperty.call(ctx.pickable, id) || (directions || {})[id] !== 'return') continue;
    const q = Number((qtys || {})[id]) || 1;
    if (q > ctx.pickable[id]) over.push(`${q} ${nameOf(id)} (${ctx.pickable[id]} out)`);
  }
  return over;
}

// ============================================
// TASK-10006 S3 — "Rentals are going out. Create the return job now?" and
// stock that moves only on a REAL pickup (Chris 2026-10-22).
// ============================================
//
// 1. PROMPT. Right after an office user saves a new job — or an edit that
//    newly sends a quantity-tracked rental OUT — one small question. Yes asks
//    for the pickup date (required, no default) and saves the return job at
//    once through S1's own path (createReturnJob's count → duplicateJob's
//    return prefill → saveJob): this job's items, its quantities set to
//    Return, no crew, tentative. "Not sure yet" does nothing — the "Out on
//    this project" strip and its Create return job button stay. Never for a
//    PIN driver, never for a job that only brings rentals back, never when
//    no quantity-tracked rental went out (so a company without rentals never
//    sees it).
// 2. PICKUP STATUS. A planned pickup moves stock only when it really happens
//    (server: first clock-in / a ticket on that day / "Mark picked up"). One
//    whose day has passed shows "Pickup overdue" on the board card and the
//    project strip, and the stock stays out. Shown only on a job that holds a
//    planned rental pickup (`rentalPickupsPending`) — nowhere else.

// Planned-pickup status of ONE job from the board's live copies, or null when
// the job holds no planned rental pickup (every job without rentals).
function _jobPickupStatus(job) {
  if (!job || !job.rentalPickupsPending || !_jobHasRentalLedger(job)) return null;
  const seen = new Set();
  const days = [...(DispatchState.jobDays || []), ...(DispatchState.searchJobDays || [])]
    .filter(d => d && d.jobId === job.id && !seen.has(d.id) && seen.add(d.id))
    .map(d => ({ ...d, dateKey: _rentalDayKeyOf(d) }));
  const st = _rlm().rlmJobPickupStatus(job.rentalLedger, days, _rentalTodayKey());
  return st.items.length ? st : null;
}

// Board card chip — only on a day of a job whose planned pickup is overdue.
function _pickupOverdueChipHtml(jobDay) {
  if (!jobDay || !jobDay.jobId) return '';
  const job = (DispatchState.jobs || []).find(j => j && j.id === jobDay.jobId);
  if (!job || !job.rentalPickupsPending) return '';
  let st = null;
  try { st = _jobPickupStatus(job); } catch (_) { return ''; }
  if (!st || !st.overdueDayIds[jobDay.id]) return '';
  return `<span class="day-flag-chip day-flag-pickup-overdue" data-day-flag="pickup-overdue" style="${_DAY_FLAG_CHIP_BASE}background:rgba(220,38,38,0.10);color:var(--text-primary);border:1px solid rgba(220,38,38,0.45);" title="Pickup overdue — the rentals are still on site and still counted out. They come back when the crew clocks in or a ticket is made on this job, or when the office marks it picked up."><span class="material-icons" style="font-size:14px;color:#DC2626;" aria-hidden="true">assignment_late</span>Pickup overdue</span>`;
}

// Job Details: the job's own planned pickup + "Mark picked up" (office).
function _renderJobPickupPanel(jobId) {
  const host = document.getElementById('job-details-rental-pickup');
  if (!host) return;
  let st = null;
  try { st = _jobPickupStatus(_resolveJobAnywhere(jobId)); } catch (_) { st = null; }
  let undo = [];
  try { undo = _returnJobCanCreate() ? _undoablePickups(jobId) : []; } catch (_) { undo = []; }
  const undoHtml = undo.map(u => `
    <div class="rental-pickup-undo" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 0 12px;padding:8px 12px;border:1px solid var(--border-light, #E2E8F0);border-radius:6px;background:var(--bg-secondary, #F8FAFC);color:var(--text-primary);min-width:0;">
      <span class="material-icons" aria-hidden="true" style="font-size:18px;color:var(--text-secondary);">task_alt</span>
      <div style="flex:1 1 200px;min-width:0;font-size:0.85rem;overflow-wrap:anywhere;">Marked picked up${u.dateKey ? ` ${escapeHtml(_copyBannerDate(u.dateKey))}` : ''}. Wrong job or day? You can undo it for 7 days.</div>
      <button type="button" class="btn btn-secondary btn-small" style="white-space:nowrap;min-height:40px;" onclick="window.DispatchModule.undoRentalPickup('${escJsAttr(jobId)}','${escJsAttr(u.settleId)}', this)"><span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:-3px;">undo</span> Undo</button>
    </div>`).join('');
  if (!st) { host.innerHTML = undoHtml; return; }
  const nameOf = (id) => { const a = (DispatchState.assets || []).find(x => x.id === id); return (a && (a.name || a.partNumber)) || 'item'; };
  const byAsset = {};
  for (const it of st.items) byAsset[it.assetId] = (byAsset[it.assetId] || 0) + it.qty;
  const what = Object.keys(byAsset).map(id => `${byAsset[id]} ${nameOf(id)}`).join(' · ');
  const when = st.earliestKey ? _copyBannerDate(st.earliestKey) : '';
  const title = st.overdue ? 'Pickup overdue — still on site' : `Pickup planned${when ? ` for ${when}` : ''}`;
  const sub = st.overdue
    ? `${what}${when ? ` (was due ${when})` : ''} — still counted out until it is picked up.`
    : `${what} — still counted out. They come back when the crew clocks in or a ticket is made on this job, or when you mark it picked up.`;
  const color = st.overdue ? '#DC2626' : 'var(--brand-blue, #3B82F6)';
  const btn = _returnJobCanCreate()
    ? `<button type="button" class="btn btn-secondary btn-small" id="btn-mark-picked-up" style="white-space:nowrap;min-height:40px;" onclick="window.DispatchModule.markRentalPickedUp('${escJsAttr(jobId)}')"><span class="material-icons" aria-hidden="true" style="font-size:16px;vertical-align:-3px;">task_alt</span> Mark picked up</button>`
    : '';
  host.innerHTML = `
    <div class="rental-pickup-panel" data-overdue="${st.overdue ? '1' : '0'}" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 0 12px;padding:10px 12px;border:1px solid var(--border-light, #E2E8F0);border-left:4px solid ${color};border-radius:6px;background:var(--bg-secondary, #F8FAFC);color:var(--text-primary);min-width:0;">
      <span class="material-icons" aria-hidden="true" style="font-size:20px;color:${color};">${st.overdue ? 'assignment_late' : 'event_available'}</span>
      <div style="flex:1 1 220px;min-width:0;">
        <div style="font-weight:600;font-size:0.9rem;overflow-wrap:anywhere;">${escapeHtml(title)}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary);overflow-wrap:anywhere;">${escapeHtml(sub)}</div>
      </div>
      ${btn}
    </div>${undoHtml}`;
}

// TASK-10006 S3b — office-marked pickups of this job still inside the 7-day
// undo window, one per settle (read off the days' server stamps).
function _undoablePickups(jobId) {
  const seen = new Set();
  const out = {};
  for (const d of [...(DispatchState.jobDays || []), ...(DispatchState.searchJobDays || [])]) {
    if (!d || d.jobId !== jobId || seen.has(d.id)) continue;
    seen.add(d.id);
    const p = d.rentalPickup;
    if (!p || p.via !== 'office' || typeof p.settleId !== 'string' || !p.settleId) continue;
    const ms = typeof p.at?.toMillis === 'function' ? p.at.toMillis() : (typeof p.at?.seconds === 'number' ? p.at.seconds * 1000 : NaN);
    if (!Number.isFinite(ms) || Date.now() - ms > 7 * 864e5) continue;
    if (!out[p.settleId]) out[p.settleId] = { settleId: p.settleId, dateKey: typeof p.dateKey === 'string' ? p.dateKey : '' };
  }
  return Object.values(out);
}

let _undoPickupBusy = false;
async function undoRentalPickup(jobId, settleId, btn) {
  if (!_returnJobCanCreate()) {
    window.showToast?.('Your access can’t change equipment counts — ask an admin.', 'error');
    return;
  }
  if (_undoPickupBusy || !jobId || !settleId) return;
  const ok = typeof window.showConfirm === 'function'
    ? (await window.showConfirm('Undo “Mark picked up”?', 'The rentals go back to counted out and the pickup is planned again.', { confirmText: 'Undo', cancelText: 'Keep it' })) === true
    : true;
  if (!ok) return;
  _undoPickupBusy = true;
  if (btn) { btn.disabled = true; btn.textContent = 'Undoing…'; }
  try {
    const res = await httpsCallable(functions, 'undoRentalPickup')({ jobId, settleId });
    const rebill = (res && res.data && Array.isArray(res.data.rebillJobs)) ? res.data.rebillJobs.length : 0;
    window.showToast?.(rebill
      ? 'Undone — the pickup is planned again. A draft rental ticket changed: run rental billing again on the install job before invoicing.'
      : 'Undone — the pickup is planned again and the rentals are counted out.', rebill ? 'warning' : 'success');
    setTimeout(() => { _renderJobRentalsOut(jobId); _renderJobPickupPanel(jobId); }, 1500);
  } catch (e) {
    console.warn('[Dispatch] TASK-10006 S3b: undoRentalPickup failed', e?.code || e?.message || e);
    // A refusal is a plain sentence from the server (with the yard-count pointer).
    const plain = /failed-precondition|not-found|permission-denied|invalid-argument/.test(String(e?.code || '')) && e?.message;
    window.showToast?.(plain || 'Couldn’t undo it. Check your connection and try again.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Undo'; }
  } finally {
    _undoPickupBusy = false;
  }
}

// TASK-10006 S3 — a pickup was booked on a day the server already marked
// picked up: ask the ONE server pickup step to settle it now (it moves stock,
// takes it off the source jobs and closes their billing). Office only; a
// failure is fine — the hourly backstop settles stamped days.
function _kickPickupSettle(jobId) {
  if (!jobId || window.AppState?.driverModePin === true) return;
  try {
    Promise.resolve(httpsCallable(functions, 'markRentalPickedUp')({ jobId, onlyStamped: true }))
      .catch(e => console.warn('[Dispatch] TASK-10006 S3: pickup settle request failed — the hourly run will settle it', e?.code || e?.message || e));
  } catch (e) { /* hourly backstop */ }
}

let _markPickedUpBusy = false;
async function markRentalPickedUp(jobId) {
  if (!_returnJobCanCreate()) {
    window.showToast?.('Your access can’t change equipment counts — ask an admin.', 'error');
    return;
  }
  if (_markPickedUpBusy || !jobId) return;
  const ok = typeof window.showConfirm === 'function'
    ? (await window.showConfirm('Mark picked up?', 'The rentals on this pickup come off the out-count now, logged as picked up today.', { confirmText: 'Mark picked up', cancelText: 'Not yet' })) === true
    : true;
  if (!ok) return;
  _markPickedUpBusy = true;
  const btn = document.getElementById('btn-mark-picked-up');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const res = await httpsCallable(functions, 'markRentalPickedUp')({ jobId });
    const r = (res && res.data) || {};
    const n = (r.settled || []).reduce((s, x) => s + (Number(x.moved) || 0), 0);
    window.showToast?.(n > 0 ? `Picked up — ${n} back in stock.` : 'Marked picked up.', 'success');
    const host = document.getElementById('job-details-rental-pickup');
    if (host) host.innerHTML = '';
    setTimeout(() => { _renderJobRentalsOut(jobId); _renderJobPickupPanel(jobId); }, 1500);
  } catch (e) {
    console.warn('[Dispatch] TASK-10006 S3: markRentalPickedUp failed', e?.code || e?.message || e);
    window.showToast?.(/permission/i.test(String(e?.code || '')) ? (e.message || 'Your login can’t change equipment counts.') : 'Couldn’t mark it picked up. Check your connection and try again.', 'error');
    if (btn) { btn.disabled = false; btn.textContent = 'Mark picked up'; }
  } finally {
    _markPickedUpBusy = false;
  }
}

// PURE. The quantity-tracked rentals a save sends OUT: { assetId: qty }.
function _rentalsGoingOut(assetIds, directions, qtys, assets) {
  const out = {};
  for (const id of (Array.isArray(assetIds) ? assetIds : [])) {
    const a = (assets || []).find(x => x && x.id === id);
    if (!a || !(a.quantityType === 'rental' || a.isRental) || a.trackingMode !== 'quantity') continue;
    if (((directions || {})[id] || 'deploy') !== 'deploy') continue;
    out[id] = Math.max(out[id] || 0, Math.max(1, Number((qtys || {})[id]) || 1));
  }
  return out;
}

function _closeReturnPrompt() {
  const el = document.getElementById('return-job-prompt');
  if (el) el.remove();
}

// The question itself. outQtys = what this save sent out ({ assetId: qty }).
function _offerReturnJobPrompt(jobId, outQtys) {
  if (window.AppState?.driverModePin === true || !_returnJobCanCreate()) return;
  if (!jobId || !outQtys || !Object.keys(outQtys).length) return;
  _closeReturnPrompt();
  const wrap = document.createElement('div');
  wrap.id = 'return-job-prompt';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.setAttribute('aria-labelledby', 'return-job-prompt-title');
  wrap.style.cssText = 'position:fixed;inset:0;z-index:10050;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,0.45);box-sizing:border-box;';
  wrap.innerHTML = `
    <div style="width:100%;max-width:400px;box-sizing:border-box;background:var(--bg-primary, #fff);color:var(--text-primary);border-radius:12px;padding:20px;box-shadow:0 12px 32px rgba(0,0,0,0.25);text-align:center;">
      <span class="material-icons" aria-hidden="true" style="font-size:36px;color:var(--brand-blue, #3B82F6);">assignment_return</span>
      <h3 id="return-job-prompt-title" style="margin:6px 0 4px;font-size:1.05rem;">Rentals are going out</h3>
      <div id="return-job-prompt-step1">
        <p style="margin:0 0 16px;color:var(--text-secondary);font-size:0.9rem;">Create the return job now?</p>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">
          <button type="button" class="btn btn-secondary" id="return-job-prompt-later" style="min-height:44px;flex:1 1 120px;">Not sure yet</button>
          <button type="button" class="btn btn-primary" id="return-job-prompt-yes" style="min-height:44px;flex:1 1 120px;">Yes</button>
        </div>
      </div>
      <div id="return-job-prompt-step2" style="display:none;text-align:left;">
        <label for="return-job-prompt-date" style="display:block;font-weight:600;font-size:0.9rem;margin:8px 0 4px;">Pickup date</label>
        <input type="date" id="return-job-prompt-date" required style="width:100%;box-sizing:border-box;min-height:44px;font-size:16px;padding:8px;border:1px solid var(--border-light, #CBD5E1);border-radius:6px;">
        <p style="margin:8px 0 16px;color:var(--text-secondary);font-size:0.8rem;">Saved tentative, with no crew. The stock stays out until the pickup really happens.</p>
        <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">
          <button type="button" class="btn btn-secondary" id="return-job-prompt-cancel" style="min-height:44px;flex:1 1 120px;">Cancel</button>
          <button type="button" class="btn btn-primary" id="return-job-prompt-create" style="min-height:44px;flex:1 1 120px;" disabled>Create return job</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(wrap);
  const $ = (id) => wrap.querySelector('#' + id);
  $('return-job-prompt-later').onclick = _closeReturnPrompt;
  $('return-job-prompt-cancel').onclick = _closeReturnPrompt;
  $('return-job-prompt-yes').onclick = () => {
    $('return-job-prompt-step1').style.display = 'none';
    $('return-job-prompt-step2').style.display = '';
    $('return-job-prompt-date').focus();
  };
  const dateEl = $('return-job-prompt-date');
  const sync = () => { $('return-job-prompt-create').disabled = !/^\d{4}-\d{2}-\d{2}$/.test(dateEl.value || ''); };
  dateEl.addEventListener('input', sync);
  dateEl.addEventListener('change', sync);
  $('return-job-prompt-create').onclick = async () => {
    const dateKey = dateEl.value || '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) { dateEl.focus(); return; }
    $('return-job-prompt-create').disabled = true;
    $('return-job-prompt-create').textContent = 'Creating…';
    _closeReturnPrompt();
    await _autoCreateReturnJob(jobId, dateKey, outQtys);
  };
}

// "Yes" + a date: S1's count, S1's return prefill (this job's items, at most
// what it sent out and what is still pickable), no crew, tentative, saved.
async function _autoCreateReturnJob(jobId, dateKey, outQtys) {
  if (!_returnJobCanCreate()) return;
  if (_createReturnJobBusy) return;
  _createReturnJobBusy = true;
  try {
    // The board's live copy of a job saved a moment ago may still be landing —
    // and the count reads its ledger, which the rental move writes AFTER the
    // job doc. Wait (≤ 8 s) until the copy shows what this save sent out.
    const _landed = (j) => !!j && _jobHasRentalLedger(j)
      && Object.keys(outQtys).every(id => (Number(_rentalLedgerEntry(j.rentalLedger, id).out) || 0) > 0);
    let job = _resolveJobAnywhere(jobId);
    for (let i = 0; !_landed(job) && i < 32; i++) { await new Promise(r => setTimeout(r, 250)); job = _resolveJobAnywhere(jobId); }
    const rosterReady = _dispatchDataIsFor(DispatchState.tenantId) && !!DispatchState.dataLoaded?.assets;
    if (!job || !rosterReady) { window.showToast?.('Couldn’t start the return job yet — open the job and use Create return job.', 'error'); return; }
    let loaded;
    try { loaded = await _loadProjectRentalsOut(job); } catch (e) {
      console.warn('[Dispatch] TASK-10006 S3: return prompt count failed', e?.code || e?.message || e);
      window.showToast?.('Couldn’t add up what’s out. Open the job and use Create return job.', 'error');
      return;
    }
    if (!loaded) return;
    const rows = _returnPromptRows(loaded.res.rows, outQtys);
    if (!rows.length) { window.showToast?.('Those rentals already have a pickup booked.', 'info'); return; }
    duplicateJob(jobId, {
      returnPrefill: { rows, projectId: loaded.projectId, older: loaded.res.older.length, noCrew: true },
      onPrefilled: () => {
        const sd = document.getElementById('job-start-date');
        const ed = document.getElementById('job-end-date');
        if (sd) { sd.value = dateKey; sd.dispatchEvent(new Event('change', { bubbles: true })); }
        if (ed) { ed.value = dateKey; ed.dispatchEvent(new Event('change', { bubbles: true })); }
        const tent = document.getElementById('job-tentative');
        if (tent) tent.checked = true;
        saveJob({ preventDefault() {} });
      }
    });
  } finally {
    _createReturnJobBusy = false;
  }
}

// PURE. S1's project rows narrowed to what THIS save sent out: each item at
// min(sent out, still pickable). Items another pickup already covers drop.
function _returnPromptRows(rows, outQtys) {
  const out = [];
  for (const r of (Array.isArray(rows) ? rows : [])) {
    if (!r || !Object.prototype.hasOwnProperty.call(outQtys || {}, r.assetId)) continue;
    const q = Math.min(Number(r.pickable) || 0, Number(outQtys[r.assetId]) || 0);
    if (q > 0) out.push({ ...r, pickable: q });
  }
  return out;
}

// ============================================
// TASK-9258 — JOB TEMPLATES (a saved, named equipment kit)
// ============================================
//
// WHAT THIS ADDS OVER `duplicateJob`, stated plainly because the honest answer
// decided the scope: **a name, a picker, and provenance.** TASK-9212 already
// taught Copy to New Job to carry drivers/trucks/trailers/assets/qtys off a
// source job's last day, and `_normalizeCarriedCrew` already does every hard
// part of turning a stored selection back into a safe form state. What Copy
// cannot do is the actual complaint — *"it turns into a nightmare tracking
// everything down that's required for jobs"* — because Copy needs you to FIND
// the past job that had the right kit, and then it drags that job's customer,
// title, location, waypoints, notes and 811 ticket along with it. A template is
// Copy's equipment half, saved under a name you can pick from a list, applied
// to the job you are already building. That is the whole feature. Everything
// else below is the provenance that keeps it honest on a money surface.
//
// 🔴 TWO THINGS A TEMPLATE DELIBERATELY DOES NOT STORE:
//
//  1. **NAMED PEOPLE.** Only slot COUNTS (`crewSlots: {drivers, helpers}`).
//     Chris's framing was *"assign whomever and they would have the list built
//     for them"* — the list is equipment; the people are today's decision. A
//     template that stamps names fights the dispatcher every morning and gets
//     abandoned. It also means a template can never seat a NOT-LEGAL driver:
//     TASK-9227's `_dfConfirmSeating` gate still runs at save over whoever the
//     dispatcher actually picked, and this feature neither widens nor bypasses
//     it. See `applyJobTemplate` for the one place that is re-asserted in code.
//  2. **RENTAL DIRECTION.** A stored `'return'` would file a second return, or
//     decrement `deployedQty`, for a brand-new job that never deployed anything.
//     `buildJobTemplateDoc` drops directions at SAVE and `applyJobTemplate`
//     never writes `pendingAssetDirections`, so there are two independent
//     reasons a template cannot preset a return. (`_normalizeCarriedCrew` is a
//     third — it drops any carried 'return' outright.)
//
// 🔴 THE MONEY RISK, AND HOW A TEMPLATED LINE IS KEPT FROM READING AS CONFIRMED.
// The danger is not that a template auto-bills — it cannot; the derived line
// lands on a DRAFT ticket and invoicing is a separate deliberate step with a
// human in it (Option A trust model, functions/src/index.js rental-slice
// header). The danger is narrower and sharper: a template makes every line look
// NORMAL. Six air movers on a job that always carries six air movers is exactly
// what a reviewer expects to see, so the template removed the thing there was to
// notice. The gap is INFORMATION, not enforcement. So:
//
//   • Every row a template seeds is marked UNCONFIRMED on the form — a dashed
//     "From template" chip sitting next to the quantity input, plus a count in
//     the bar above the section. The VALUE IS STILL THERE and still saves; this
//     is a mark, not a withheld number. (Withholding the number was explicitly
//     considered and withdrawn — it costs the entire speed win, which is the
//     point of the feature.)
//   • The mark clears the instant a human touches that row — ticking it, typing
//     a quantity, flipping a direction. Touching IS confirming.
//   • Whatever is still unconfirmed at Save is written through: onto the jobDay
//     as `templateUnconfirmedAssets`, and onto each `assetTransactions` row as
//     `templateConfirmed: false` alongside `source: 'template'` + `templateId`.
//     That is the one fact a biller cannot get today — this number came from a
//     template and nobody looked at it — and it makes "template-placed, never
//     confirmed" a query instead of archaeology.
//
// 🔴 THE TEMPLATE DOC IS MUTABLE, SO THE JOB CARRIES A SNAPSHOT. Storing only a
// `templateId` means editing "Water Extraction" in November silently rewrites
// what every August job APPEARS to have carried, and support pulls a 3-month-old
// job and sees today's lineup. `templateSnapshot` on the jobDay is the resolved
// contents AT APPLY TIME. This is unfixable retroactively, which is why it ships
// in v1 rather than as a follow-up.
//
// 🔴 PREREQUISITE — `tenants/{tid}/jobTemplates` NEEDS A firestore.rules STANZA.
// The tenant-level `match /{document=**}` catch-all in firestore.rules is
// commented out, so an un-stanzaed subcollection is default-DENIED, not open.
// The stanza is the `projectTemplates` one verbatim (same access profile: any
// member reads, dispatch writes, admin/dispatch deletes). Until it lands this
// whole surface renders NOTHING — see `subscribeToJobTemplates`, which gates the
// UI on a snapshot having actually arrived rather than letting a dispatcher
// stare at a picker that can never load.
//
// NOT DONE, ON PURPOSE:
//  • `job.type` is NOT repurposed. It is verified vestigial (5 cosmetic
//    consumers, only 'meeting'/'shop_day' matter), but the picker belongs in the
//    section it governs, and repurposing a field with live consumers to host a
//    control that has its own row buys nothing and risks those two strings.
//  • There is NO "create a job from a template" shortcut. `createProjectFromTemplate`
//    is the cautionary tale: it writes no jobDay, so its jobs render only through
//    a legacy fallback. A template here applies to the OPEN FORM, and saveJob's
//    create branch writes the job doc AND the jobDay batch exactly as it always
//    did — the hard invariant is preserved by having no second creation path at
//    all, not by a second copy of the batch that can drift.

// The "this came from a template and nobody has looked at it" mark. Literal hex,
// never a var(): an undefined custom property silently kills the whole property
// and this mark existing is the entire mitigation.
//
// COD's UX lane asked for amber. Declined, and this is the reason: TASK-9227's
// compliance flag is an amber pill sitting in the SAME picker, and "this driver
// is flagged NOT LEGAL" and "nobody has checked this quantity yet" are not the
// same severity — one hue for both teaches a dispatcher to read past the
// dangerous one. What the lane was actually right about is CONTRAST: the first
// version was mid-slate on near-white and read as disabled chrome, which is
// worse than useless on the one element that carries the whole mitigation. So:
// near-black slate on a tinted (not white) ground — 14:1, darker than the body
// text around it — while the DASHED border keeps carrying the "provisional"
// meaning without depending on colour at all.
const _JT_MARK_FG = '#1E293B';
const _JT_MARK_BG = '#E2E8F0';
const _JT_MARK_BORDER = '#475569';

/** The key a seeded row is tracked under. One shape for every picker section so
 *  the count in the bar and the chip on the row can never disagree. */
function jobTemplateKey(sectionKey, id) {
  return `${sectionKey}:${id}`;
}

/** Is this row still carrying an unconfirmed template value?
 *  Reads DispatchState, so it is a thin wrapper over the pure membership test —
 *  populateJobMultiSelect calls it per tile. */
function _jtIsUnconfirmed(sectionKey, id) {
  const list = DispatchState.templateUnconfirmed;
  return Array.isArray(list) && list.includes(jobTemplateKey(sectionKey, id));
}

/** A human touched this row — it is their number now, not the template's.
 *  Idempotent and total: called from three handlers, and a key that was never
 *  seeded is a no-op rather than an error. Returns true when something changed,
 *  so callers can skip a pointless re-render. */
function confirmTemplateRow(sectionKey, id) {
  const list = DispatchState.templateUnconfirmed;
  if (!Array.isArray(list) || list.length === 0) return false;
  const key = jobTemplateKey(sectionKey, id);
  const i = list.indexOf(key);
  if (i === -1) return false;
  list.splice(i, 1);
  renderJobTemplateNotice();
  return true;
}

/** Confirm a row AND strip its chip from the DOM in place.
 *
 *  In place, not by re-rendering the section: populateJobMultiSelect rebuilds
 *  `innerHTML` wholesale, which would destroy the Price-Book line-item pickers
 *  `toggleJobCheckbox` renders into those same rows and reset every search box
 *  and open/closed state. Removing one `<span>` is the smallest correct edit.
 *  `rowEl` is passed when the caller already has it; otherwise the row is found
 *  from its checkbox. CSS.escape because a doc id is caller-controlled text
 *  going into a selector. */
function confirmTemplateRowFromDom(sectionKey, id, rowEl) {
  if (!confirmTemplateRow(sectionKey, id)) return;
  let row = rowEl || null;
  if (!row) {
    try {
      const esc = (window.CSS && typeof window.CSS.escape === 'function')
        ? window.CSS.escape(String(id)) : String(id).replace(/["\\]/g, '\\$&');
      row = document.querySelector(`#job-select-${sectionKey} input[type="checkbox"][value="${esc}"]`)?.closest('.checkbox-item') || null;
    } catch (_) { row = null; }
  }
  row?.querySelector('.job-template-chip')?.remove();
}

/**
 * PURE. The document a "Save as template" writes.
 *
 * Takes the form's current selection and returns the stored shape. Three
 * invariants live here rather than at the call site, because this is the
 * function a test can hold:
 *   • `assetDirections` is NEVER carried. Not filtered — never built.
 *   • people are reduced to COUNTS. No person id can reach the doc.
 *   • a quantity is stored only when it is > 1, matching the jobDay convention
 *     (`assetQtys` there is also >1-only) so a template and a jobDay written
 *     from the same screen hold byte-identical maps.
 * Unknown/absent ids pass through untouched — resolution is APPLY's job, and
 * dropping here would delete a real kit item the moment a cache was cold.
 */
function buildJobTemplateDoc(input = {}) {
  const name = String(input.name || '').trim();
  const uniq = (arr) => Array.from(new Set((Array.isArray(arr) ? arr : []).filter(Boolean)));
  const assets = uniq(input.assets);
  const assetQtys = {};
  for (const id of assets) {
    const qty = Number(input.assetQtys?.[id]);
    if (Number.isFinite(qty) && qty > 1) assetQtys[id] = Math.floor(qty);
  }
  return {
    name,
    trucks: uniq(input.trucks),
    trailers: uniq(input.trailers),
    assets,
    assetQtys,
    crewSlots: {
      drivers: Math.max(0, Number(input.driverCount) || 0),
      helpers: Math.max(0, Number(input.helperCount) || 0),
    },
  };
}

/**
 * PURE. The provenance fields a template puts on every jobDay the save writes.
 *
 * Returns null when no template was applied, so the caller spreads nothing and
 * a hand-built job carries no template keys at all (Firestore rejects an
 * undefined field, and a `templateId: null` on every job in the tenant is noise
 * that makes the real ones harder to find).
 *
 * `templateIds` is an ARRAY, not a scalar, because a dispatcher can legitimately
 * stack a base kit and a job-specific add-on. "Which jobs came from this
 * template" stays a one-line query — `where('templateIds','array-contains',id)`
 * — on an automatic single-field index.
 *
 * `templateUnconfirmedAssets` is intersected with the assets ACTUALLY BEING
 * SAVED: a seeded row the dispatcher then un-ticked is not an unconfirmed
 * charge, it is a removed one, and leaving its id in the list would make the
 * "template-placed, never confirmed" report cry wolf.
 *
 * `templateAssetSource` maps each SAVED, template-seeded asset to the template
 * that placed it — a hand-picked asset is deliberately absent, so nothing here
 * claims an origin nobody gave it.
 */
function buildJobDayTemplateFields(applied, unconfirmed, selectedAssets) {
  const apps = Array.isArray(applied?.applications) ? applied.applications : [];
  if (apps.length === 0) return null;
  const selected = Array.isArray(selectedAssets) ? selectedAssets : [];
  const marks = Array.isArray(unconfirmed) ? unconfirmed : [];
  const seededBy = applied.seededBy || {};
  const assetSource = {};
  for (const id of selected) {
    const tplId = seededBy[jobTemplateKey('assets', id)];
    if (tplId) assetSource[id] = tplId;
  }
  // TWO keys, not five (COD round 2, Skeptic: five disjoint root fields is schema
  // sprawl that a billing-side reader will hardcode before it settles).
  // `templateIds` stays TOP-LEVEL because it is the one thing anyone queries —
  // `where('templateIds','array-contains',id)` answers "which jobs used this
  // kit" on an automatic single-field index, which a nested array would not.
  // Everything else is a record to read once you already have the doc.
  return {
    templateIds: Array.from(new Set(apps.map((a) => a.id))),
    templateMeta: {
      applications: apps.map((a) => ({
        id: a.id, name: a.name || '',
        appliedAt: a.appliedAt || null, appliedBy: a.appliedBy || '',
      })),
      // The resolved contents at APPLY time. The template doc is mutable; this
      // is not.
      //
      // 🔴 `snapshot.assetQtys` is WHAT THE TEMPLATE PROPOSED — not what the day
      // saved. For a row listed in `unconfirmedAssets` below the two are equal by
      // construction (nobody touched it). For a row a human retyped they SHOULD
      // differ, and that gap IS the audit trail: proposed 6, billed 2, changed by
      // a person. A reader that treats any difference as a discrepancy will cry
      // wolf on exactly the rows that were done right — check the unconfirmed
      // list first. (An earlier revision of this comment claimed the two could
      // never differ; that was wrong, and COD caught it.)
      snapshot: applied.snapshot || {},
      unconfirmedAssets: selected.filter((id) => marks.includes(jobTemplateKey('assets', id))),
      assetSource,
    },
  };
}

/**
 * PURE. Keep a STORED jobDay's template mark honest when its assets or their
 * quantities are edited afterwards.
 *
 * 🔴 WHY THIS EXISTS (COD round 6, Code Reviewer + Edge Case Hunter, same two
 * writers). `templateMeta.unconfirmedAssets` means "a template put this here and
 * no human has looked at it". The moment it is PERSISTED, any later writer that
 * changes `assets` / `assetQtys` can falsify it — and the two edit doors
 * (`saveAssignment`, `_planJobDayCrewUpdate`) did exactly that. Save six
 * unconfirmed movers, reopen, change the quantity to two, save: the day still
 * claimed "proposed six, unconfirmed" about a number a person had just typed.
 * Board Add Day then inherited that stale claim onto the next day. A mark that
 * goes stale on the most ordinary follow-up action is the feature failing at its
 * one job, so the reconcile lives here rather than in a comment.
 *
 * THREE THINGS CLEAR THE MARK, each of them a human acting on that row:
 *   • the asset is no longer on the day — it is not a charge at all now;
 *   • its quantity CHANGED — somebody typed that number;
 *   • its DIRECTION changed — somebody flipped Deploy/Return on it. The create
 *     form already treats that flip as confirmation (`toggleAssetDirection` →
 *     `confirmTemplateRowFromDom`), so omitting it here made the two halves of
 *     the same rule disagree: flip a templated rental on a stored day and the
 *     day went on saying nobody had checked a row someone had just acted on
 *     (COD round 7, Independent). It matters most on the sharpest asset class —
 *     a rental bills as a PERIOD.
 * An asset still present at the same quantity and direction keeps its mark:
 * re-saving a day without touching a row is not confirming it, which is the
 * whole distinction TASK-9253 drew on this surface.
 *
 * `snapshot` is NEVER touched. It is what the template PROPOSED, and history
 * does not change because someone edited the day afterwards — that gap is the
 * audit trail. `assetSource` is re-scoped to what the day still carries.
 *
 * Returns null when the day carries no template (nothing to reconcile) or when
 * nothing changed (so no pointless write).
 */
function reconcileStoredTemplateMeta(storedDay, nextAssets, nextQtys, nextDirections) {
  const meta = storedDay?.templateMeta;
  if (!Array.isArray(storedDay?.templateIds) || storedDay.templateIds.length === 0 || !meta) return null;
  const assets = Array.isArray(nextAssets) ? nextAssets : [];
  const prevQtys = storedDay.assetQtys || {};
  const qtys = nextQtys || {};
  // Only compared when the caller actually SUPPLIED a direction map. An omitted
  // map is "no information", not "everything is deploy" — reading it as the
  // latter would false-confirm every stored 'return' the moment some future
  // caller forgot the argument.
  const prevDirs = storedDay.assetDirections || {};
  const dirs = (nextDirections && typeof nextDirections === 'object') ? nextDirections : null;
  const wasUnconfirmed = Array.isArray(meta.unconfirmedAssets) ? meta.unconfirmedAssets : [];
  // `|| 1` / `|| 'deploy'` on BOTH sides: an absent entry means the default in
  // the stored map and the next one alike, so 1 → absent is NOT a change and
  // absent → 4 IS. Same rule for direction.
  const stillUnconfirmed = wasUnconfirmed.filter((id) =>
    assets.includes(id)
    && (prevQtys[id] || 1) === (qtys[id] || 1)
    && (!dirs || (prevDirs[id] || 'deploy') === (dirs[id] || 'deploy')));
  const srcSource = meta.assetSource || {};
  const assetSource = {};
  for (const id of assets) if (srcSource[id]) assetSource[id] = srcSource[id];
  const unchanged =
    stillUnconfirmed.length === wasUnconfirmed.length
    && Object.keys(assetSource).length === Object.keys(srcSource).length;
  if (unchanged) return null;
  return { ...meta, unconfirmedAssets: stillUnconfirmed, assetSource };
}

/**
 * PURE. Carry a stored jobDay's template provenance onto a day copied from it.
 *
 * The board Add-Day paths clone `assets` and `assetQtys` off the most recent
 * day. Those quantities are the template's, so the mark has to travel with them
 * or the copy reads as six movers somebody chose. Everything is FILTERED to the
 * assets the new day actually carries: a row dropped on the way over is not an
 * unconfirmed charge on a day that does not have it.
 *
 * `templateIds` / `applications` / `snapshot` are carried whole — they describe
 * which kits were applied and what they proposed, which does not change by
 * copying the day. Only the two per-asset maps are re-scoped.
 *
 * Returns null when the source day carries no template, so an ordinary day's
 * copy gains no keys at all.
 */
function inheritJobDayTemplateFields(sourceDay, assets) {
  const ids = sourceDay?.templateIds;
  const meta = sourceDay?.templateMeta;
  if (!Array.isArray(ids) || ids.length === 0 || !meta) return null;
  const carried = Array.isArray(assets) ? assets : [];
  const srcUnconfirmed = Array.isArray(meta.unconfirmedAssets) ? meta.unconfirmedAssets : [];
  const srcSource = meta.assetSource || {};
  const assetSource = {};
  for (const id of carried) if (srcSource[id]) assetSource[id] = srcSource[id];
  return {
    templateIds: ids.slice(),
    templateMeta: {
      applications: Array.isArray(meta.applications) ? meta.applications.slice() : [],
      snapshot: meta.snapshot || {},
      unconfirmedAssets: carried.filter((id) => srcUnconfirmed.includes(id)),
      assetSource,
      // This day was not the one the template was applied to. Saying so is
      // cheaper than a future reader inferring it wrongly from appliedAt.
      inheritedFromDayId: sourceDay.id || null,
    },
  };
}

/**
 * PURE. The stamp that rides on an `assetTransactions` row this save creates.
 *
 * Empty object unless A TEMPLATE ACTUALLY PLACED THIS ASSET, so the spread adds
 * nothing for every hand-built job AND for every asset the dispatcher picked by
 * hand on a job that also used a template. Stamping the latter was the original
 * version's bug: `source:'template'` on a row a human chose is a false
 * attribution on a money record, which is worse than no attribution at all.
 *
 * `templateConfirmed` is the point: `false` means a template put this quantity
 * here and no human ever touched the row. It is read off the live unconfirmed
 * list, which `seededBy` deliberately does not mirror — touching a row changes
 * whether it was confirmed, never who placed it.
 */
function jobTemplateAssetStamp(applied, unconfirmed, assetId) {
  const key = jobTemplateKey('assets', assetId);
  const tplId = applied?.seededBy?.[key];
  if (!tplId) return {};
  const app = (applied.applications || []).find((a) => a.id === tplId);
  const marks = Array.isArray(unconfirmed) ? unconfirmed : [];
  return {
    source: 'template',
    templateId: tplId,
    templateName: app?.name || '',
    templateConfirmed: !marks.includes(key),
  };
}

// The tenant's kits. A tiny, rarely-written collection, so a live snapshot costs
// nothing and keeps the picker fresh after a save without a reload.
//
// 🔴 THE READY FLAG IS THE POINT. It flips true only inside the success
// callback. Everything user-facing is gated on it, so when the rules stanza this
// feature needs is missing the dispatcher sees the form exactly as it is today —
// no picker, no button, no error text about a collection they have never heard
// of. The denial is logged once (not once per Firestore retry) and reported
// through window.logError, so it is loud to US and invisible to them. That is
// deliberate: a half-working control on a money surface is worse than no
// control.
let _jobTemplatesErrLogged = false;
function subscribeToJobTemplates(tenantId) {
  // 🔴 CLEAR BEFORE SUBSCRIBING, and re-check the tenant inside the callback
  // (COD round 4, Code Reviewer). Without the clear, a tenant switch left the
  // PREVIOUS tenant's kits in the picker — names and equipment ids from another
  // company — until the new snapshot or its denial arrived. Without the
  // in-callback re-check, a slow snapshot from the old subscription can still
  // land after the switch and re-publish them. Both are the same bug at
  // different moments, so both are closed.
  DispatchState.jobTemplates = [];
  DispatchState.jobTemplatesReady = false;
  _jobTemplatesErrLogged = false;
  try {
    const unsub = onSnapshot(
      collection(db, 'tenants', tenantId, 'jobTemplates'),
      (snap) => {
        if (DispatchState.tenantId !== tenantId) return; // switched out from under us
        // Sorted CLIENT-SIDE on purpose. An `orderBy('name')` server-side would
        // silently EXCLUDE any doc missing the field — a template that vanishes
        // from its own picker is the exact class of silent failure this file is
        // full of warnings about.
        DispatchState.jobTemplates = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        DispatchState.jobTemplatesReady = true;
        _jobTemplatesErrLogged = false;
        // Only repaint if the form is actually open.
        if (document.getElementById('job-template-bar')) renderJobTemplateBar();
        // TASK-9391 (COD r1) — a project's kit that was waiting on this snapshot.
        if (_pendingProjectKit) _flushPendingProjectKit();
      },
      (error) => {
        if (DispatchState.tenantId !== tenantId) return;
        DispatchState.jobTemplatesReady = false;
        DispatchState.jobTemplates = [];
        if (!_jobTemplatesErrLogged) {
          _jobTemplatesErrLogged = true;
          console.warn('[Dispatch] TASK-9258 jobTemplates subscription unavailable:', error?.code || error);
          window.logError?.(error, { module: 'dispatch', phase: 'jobtemplates-subscribe', code: error?.code || null });
        }
        if (document.getElementById('job-template-bar')) renderJobTemplateBar();
      }
    );
    DispatchState.unsubscribers.push(unsub);
  } catch (e) {
    console.warn('[Dispatch] TASK-9258 jobTemplates subscribe threw:', e);
  }
}

/** The picker + the two buttons. Rendered empty when templates are not readable
 *  (see the ready-flag note above) and empty in driver mode for the same
 *  reason — there is no job form there to template. */
function renderJobTemplateBar() {
  const bar = document.getElementById('job-template-bar');
  if (!bar) return;
  // 🔴 NEW JOBS ONLY in v1, and the gate is here rather than in applyJobTemplate
  // so there is nothing to reach. The provenance this feature turns on —
  // `templateId`/`templateSnapshot`/`templateUnconfirmedAssets` on the jobDay and
  // `templateConfirmed` on the ledger row — is written by saveJob's CREATE
  // branch. The edit branch writes crew through to existing jobDays on a
  // different code path with its own `newlyAdded` inventory gate, and seeding a
  // kit there would show the unconfirmed marks and then persist none of them: a
  // mitigation that renders and does not record is worse than no mitigation. An
  // edit still has Assign Crew and the pickers; it just has no template button.
  const editing = !!document.getElementById('job-id')?.value;
  if (editing || !DispatchState.jobTemplatesReady || window.AppState?.driverModePin === true) {
    bar.innerHTML = '';
    renderJobTemplateNotice();
    return;
  }
  const list = DispatchState.jobTemplates || [];
  const opts = list.map((t) => {
    const bits = [];
    const nT = (t.trucks || []).length, nR = (t.trailers || []).length, nA = (t.assets || []).length;
    if (nT) bits.push(`${nT} veh`);
    if (nR) bits.push(`${nR} trl`);
    if (nA) bits.push(`${nA} asset${nA === 1 ? '' : 's'}`);
    const summary = bits.length ? ` (${bits.join(', ')})` : '';
    return `<option value="${escapeHtml(t.id)}">${escapeHtml(String(t.name || 'Untitled'))}${escapeHtml(summary)}</option>`;
  }).join('');
  const pickerDisabled = list.length === 0;
  bar.innerHTML = `
    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-bottom:10px;">
      <select id="job-template-select" class="form-control form-control-sm"
              style="flex:1 1 180px;min-width:150px;font-size:0.85rem;" ${pickerDisabled ? 'disabled' : ''}
              aria-label="Job template">
        <option value="">${pickerDisabled ? 'No saved templates yet' : 'Apply a saved template…'}</option>
        ${opts}
      </select>
      <button type="button" class="btn btn-secondary" style="font-size:0.8rem;padding:6px 12px;"
              onclick="window.DispatchModule.applyJobTemplate()" ${pickerDisabled ? 'disabled' : ''}>Apply</button>
      <button type="button" class="btn btn-secondary" style="font-size:0.8rem;padding:6px 12px;"
              onclick="window.DispatchModule.toggleJobTemplateSave()">Save as template</button>
    </div>
    <div id="job-template-save-row" class="hidden" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-bottom:10px;">
      <input type="text" id="job-template-name" class="form-control form-control-sm" maxlength="60"
             placeholder="Name this kit — e.g. Water Extraction, Heavy Haul 104+104A"
             style="flex:1 1 220px;min-width:180px;font-size:0.85rem;">
      <button type="button" class="btn btn-primary" style="font-size:0.8rem;padding:6px 12px;"
              onclick="window.DispatchModule.saveJobAsTemplate()">Save</button>
      <button type="button" class="btn btn-secondary" style="font-size:0.8rem;padding:6px 12px;"
              onclick="window.DispatchModule.toggleJobTemplateSave(false)">Cancel</button>
      <span style="flex:1 1 100%;font-size:0.75rem;color:var(--text-secondary);">
        Saves the vehicles, trailers and assets ticked above, plus how many people the job takes — never who.
      </span>
    </div>`;
  renderJobTemplateNotice();
}

/** The running "N rows came from a template and nobody has checked them" line.
 *  Disappears on its own as rows are touched, so an untouched count is always a
 *  live number rather than a banner that is dismissed and forgotten. */
function renderJobTemplateNotice() {
  const el = document.getElementById('job-template-notice');
  if (!el) return;
  const applied = DispatchState.templateApplied;
  const apps = applied?.applications || [];
  if (apps.length === 0) { el.innerHTML = ''; return; }
  const n = (DispatchState.templateUnconfirmed || []).length;
  // Every kit applied to this form, named. With two stacked kits, naming only
  // the most recent would send the dispatcher looking for the wrong list.
  const label = apps.map((a) => `“${escapeHtml(String(a.name || 'template'))}”`).join(' + ');
  // Crew slots are summed across the applied kits — two kits that each want a
  // driver want two drivers.
  const drivers = apps.reduce((s, a) => s + (Number(a.crewSlots?.drivers) || 0), 0);
  const helpers = apps.reduce((s, a) => s + (Number(a.crewSlots?.helpers) || 0), 0);
  const crewBits = [];
  if (drivers) crewBits.push(`${drivers} driver${drivers === 1 ? '' : 's'}`);
  if (helpers) crewBits.push(`${helpers} helper${helpers === 1 ? '' : 's'}`);
  const crewLine = crewBits.length
    ? ` Normally runs with ${crewBits.join(' + ')} — pick who, yourself.`
    : '';
  const body = n > 0
    ? `<strong>${n}</strong> item${n === 1 ? '' : 's'} came from ${label} and ${n === 1 ? 'has' : 'have'} not been checked. Tap or retype anything you want to confirm; what you leave is saved as unconfirmed.`
    : `Everything from ${label} has been checked.`;
  el.innerHTML = `
    <div style="margin-bottom:10px;padding:8px 10px;border:1px dashed ${_JT_MARK_BORDER};background:${_JT_MARK_BG};color:${_JT_MARK_FG};border-radius:6px;font-size:0.78rem;line-height:1.45;">
      ${body}${escapeHtml(crewLine)}
    </div>`;
}

/** The per-row chip. Same dashed slate as the notice so the count and the rows
 *  it counts are visibly the same thing. */
function jobTemplateChipHtml(sectionKey, id) {
  if (!_jtIsUnconfirmed(sectionKey, id)) return '';
  const escAttrFn = window.escAttr || escapeHtml;
  const tip = escAttrFn('A template put this here. Tap the row or retype the quantity to confirm it — otherwise it saves marked unconfirmed.');
  return `<span class="job-template-chip" title="${tip}" style="display:inline-flex;align-items:center;gap:3px;margin-left:6px;padding:1px 7px;border:1px dashed ${_JT_MARK_BORDER};background:${_JT_MARK_BG};color:${_JT_MARK_FG};border-radius:10px;font-size:0.68rem;font-weight:700;white-space:nowrap;"><span class="material-icons" style="font-size:12px;">bookmark_border</span>From template</span>`;
}

function toggleJobTemplateSave(force) {
  const row = document.getElementById('job-template-save-row');
  if (!row) return;
  const show = (force === undefined) ? row.classList.contains('hidden') : !!force;
  row.classList.toggle('hidden', !show);
  if (show) document.getElementById('job-template-name')?.focus();
}

/** Save the form's CURRENT equipment selection under a name. */
async function saveJobAsTemplate() {
  if (!DispatchState.jobTemplatesReady) {
    window.showToast?.('Templates are not available on this account yet.', 'error');
    return;
  }
  const nameEl = document.getElementById('job-template-name');
  const name = String(nameEl?.value || '').trim();
  if (!name) {
    window.showToast?.('Give the template a name first.', 'warning');
    nameEl?.focus();
    return;
  }
  const trucks = getJobCrewSelected('trucks');
  const trailers = getJobCrewSelected('trailers');
  const assets = getJobCrewSelected('assets');
  if (trucks.length === 0 && trailers.length === 0 && assets.length === 0) {
    window.showToast?.('Tick the vehicles, trailers or assets this kit needs, then save it.', 'warning');
    return;
  }
  // People become COUNTS here and nowhere else. `job-select-helpers` is a hidden
  // section this form never populates (the one People picker is
  // `job-select-drivers`), so helpers reads 0 today — kept so the slot survives
  // if that section is ever un-hidden, exactly as TASK-9227 does.
  const docData = buildJobTemplateDoc({
    name, trucks, trailers, assets,
    assetQtys: DispatchState.pendingAssetQtys,
    driverCount: getJobCrewSelected('drivers').length,
    helperCount: getJobCrewSelected('helpers').length,
  });
  try {
    await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobTemplates'), {
      ...docData,
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.user?.email || 'unknown',
      useCount: 0,
      lastUsedAt: null,
    });
    if (nameEl) nameEl.value = '';
    toggleJobTemplateSave(false);
    window.showToast?.(`Saved “${name}” — pick it from the template list on any new job.`, 'success');
  } catch (err) {
    console.error('[Dispatch] TASK-9258 template save failed:', err);
    window.logError?.(err, { module: 'dispatch', phase: 'jobtemplate-save' });
    window.showToast?.('Could not save the template.', 'error');
  }
}

/**
 * PURE. Fold one Apply into the form's accumulated template state.
 *
 * Extracted from `applyJobTemplate` so the invariant below can be tested by
 * RUNNING it through real sequences rather than by matching source expressions
 * — COD round 3 was explicit that expression checks did not establish closure,
 * and it was right: two earlier versions of this merge passed a source check
 * and were still wrong.
 *
 * 🔴 ONE RULE, APPLIED TO ALL THREE MAPS: **whoever newly seeds a row owns it.**
 * `marks` is exactly the set of rows this Apply added that were NOT already on
 * the form, so for every key in `marks` the attribution (`seededBy`), the
 * snapshot quantity and the form quantity all take THIS template's values; every
 * row already on the form is left alone in all three. Getting here took three
 * rounds because the first two versions used different rules for different maps:
 *   • v1 replaced `templateUnconfirmed` wholesale — a second Apply silently
 *     cleared the first kit's untouched rows, saving them confirmed-by-nobody.
 *   • v2 made attribution and snapshot FIRST-writer-wins while the form quantity
 *     stayed last-writer-wins. Apply A (mover ×6) → UNTICK it → apply B (×10)
 *     and the row bills 10, the snapshot says 6, and the source names A. Three
 *     COD lanes reproduced it.
 * First-writer-wins was solving a problem `marks` already solves. Dropping it is
 * what makes the three maps agree by construction.
 *
 * WHAT `snapshot.assetQtys` MEANS, precisely, because v2's comment overclaimed:
 * it is WHAT THE TEMPLATE PROPOSED, not what was saved. For a row still in
 * `unconfirmedAssets` the two are necessarily equal — nobody touched it. For a
 * row a human retyped they SHOULD differ, and that difference is the audit
 * trail: proposed 6, billed 2, confirmed by a human. That is the invariant worth
 * asserting, and the only one that is true.
 */
function mergeTemplateApplication({ prev, prevUnconfirmed, template, norm, marks, appliedAt, appliedBy }) {
  const markList = Array.isArray(marks) ? marks : [];
  const mergeIds = (a, b) => Array.from(new Set([...(a || []), ...(b || [])]));
  // The ids THIS apply placed, per section — derived from `marks`, never from
  // the incoming kit, so every part of the record below describes the same set.
  const marked = (section) => markList
    .filter((k) => k.indexOf(`${section}:`) === 0)
    .map((k) => k.slice(section.length + 1));

  const seededBy = { ...(prev?.seededBy || {}) };
  const snapQtys = { ...(prev?.snapshot?.assetQtys || {}) };
  for (const key of markList) {
    seededBy[key] = template.id;
    if (key.indexOf('assets:') !== 0) continue;
    const id = key.slice('assets:'.length);
    const qty = norm?.assetQtys?.[id];
    // >1 only, and an explicit DELETE otherwise — mirroring exactly what the
    // caller does to pendingAssetQtys. An absent entry means 1 in both maps, so
    // a stale 6 left behind by an earlier kit would be a snapshot of a quantity
    // this Apply did not propose.
    if (qty && qty > 1) snapQtys[id] = qty;
    else delete snapQtys[id];
  }

  // Re-applying the SAME kit must not append a second application entry: it
  // would list the id twice and claim two applications where bumpJobTemplateUse
  // (which dedupes) credits one.
  const priorApps = prev?.applications || [];
  const alreadyApplied = priorApps.some((a) => a.id === template.id);
  return {
    applied: {
      applications: alreadyApplied ? priorApps : [
        ...priorApps,
        { id: template.id, name: template.name || '', appliedAt, appliedBy, crewSlots: template.crewSlots },
      ],
      seededBy,
      // The resolved contents, frozen. Not the template doc — that is mutable.
      //
      // 🔴 ONLY THE ROWS THIS APPLY PLACED, the same rule as the two maps above
      // (COD round 4 — Independent + Edge Case Hunter). Unioning the whole kit
      // put an ALREADY-TICKED asset into `snapshot.assets` while `snapQtys`
      // (correctly) held no entry for it, and an absent entry means 1 — so a
      // mover the dispatcher had hand-ticked at 4, on a kit that proposes 6,
      // recorded as "the template proposed 1". Wrong in a record whose only job
      // is to say what the template proposed. A row the template did not place
      // simply does not belong in its snapshot.
      snapshot: {
        trucks: mergeIds(prev?.snapshot?.trucks, marked('trucks')),
        trailers: mergeIds(prev?.snapshot?.trailers, marked('trailers')),
        assets: mergeIds(prev?.snapshot?.assets, marked('assets')),
        assetQtys: snapQtys,
      },
    },
    unconfirmed: Array.from(new Set([...(prevUnconfirmed || []), ...markList])),
  };
}

/** Re-render the job form's Price Book rate pickers over the CURRENT checkbox
 *  rows. Same five calls editJob makes after it fills the form, scoped to the
 *  already-loaded cache so this never blocks on a network round trip and never
 *  fires when the add-on is off. Silent no-op when there is no customer, no
 *  contract or no cached items — there is nothing to draw in any of those. */
function _rerenderJobLineItemPickers() {
  try {
    if (!window.hasAddOnPackage?.('clockbilling')) return;
    const customerId = document.getElementById('job-customer')?.value;
    if (!customerId) return;
    const cached = DispatchState.lineItemCache[customerId];
    if (!cached?.items?.length) return;
    const items = getContractScopedItems(customerId, true);
    if (!items?.length) return;
    for (const [id, kind] of [
      ['job-select-trucks', 'truck'], ['job-select-trailers', 'trailer'],
      ['job-select-assets', 'asset'], ['job-select-drivers', 'person'],
      ['job-select-helpers', 'person'],
    ]) renderLineItemPickers(id, kind, items);
  } catch (e) {
    console.warn('[Dispatch] TASK-9258 line-item picker re-render failed:', e);
  }
}

/** One toast per set of dropped template rows, same reason as the Copy path's. */
let _jtDropAnnounced = '';
function _announceDroppedTemplateAssets(dropped) {
  if (!dropped || dropped.length === 0) return;
  const key = dropped.slice().sort().join('|');
  if (key === _jtDropAnnounced) return;
  _jtDropAnnounced = key;
  window.showToast?.(
    `Not applied: ${dropped.join(', ')} — a rental unit stays on the job that has it. Add it back if this run needs it.`,
    'info', null, 8000
  );
}

/**
 * Apply the picked template to the OPEN FORM. Writes nothing to Firestore.
 *
 * 🔴 ZERO INVENTORY MOVES HERE, and that is structural rather than a promise:
 * stock deduction and rental deploy live inside saveJob's `assets-inventory-update`
 * / `assets-rental-update` phases, and this function touches only DOM state and
 * the two pending maps. Nothing on this path opens a batch, and the existing
 * `_JOB_INV_MAX` pre-flight still runs untouched at save.
 *
 * 🔴 IT MERGES, IT DOES NOT REPLACE. A row the dispatcher had already ticked was
 * their decision, so it stays ticked and is NOT marked unconfirmed — only rows
 * this apply newly added carry the mark. And PEOPLE ARE READ BACK OFF THE FORM
 * and passed through unchanged: a template carries no person ids, so applying
 * one must never disturb crew the dispatcher has already seated (which is also
 * why this cannot smuggle anything past TASK-9227's save-time gate — that gate
 * reads the same DOM containers and runs over whatever is in them at save).
 *
 * Normalisation is `_normalizeCarriedCrew`, reused verbatim rather than
 * re-derived: it already resolves legacy name-refs to ids, drops UNIQUE rentals
 * (one physical unit cannot be on two jobs), drops any 'return' direction, and
 * refuses a quantity rental the yard demonstrably cannot cover — naming the
 * shortfall. Those are this feature's hazards 5.2 and 5.3, already written and
 * already reviewed.
 */
function applyJobTemplate() {
  const sel = document.getElementById('job-template-select');
  const templateId = sel?.value || '';
  if (!templateId) {
    window.showToast?.('Pick a template first.', 'warning');
    return;
  }
  const tpl = (DispatchState.jobTemplates || []).find((t) => t.id === templateId);
  if (!tpl) {
    window.showToast?.('That template could not be loaded. Try reopening the form.', 'error');
    return;
  }
  // 🔴 REFUSE ON A COLD ROSTER, visibly. A template is a list of IDS. The
  // pickers render from the cached collections and drop any id they cannot show,
  // and `getJobCrewSelected` then reads the DOM — so applying before the
  // snapshots land would tick a handful of rows, silently lose the rest, and
  // save a kit that is missing pieces nobody was told about. Copy to New Job
  // solves this with the TASK-9212 re-seed channel because a copy fires from a
  // card click with no human waiting on it; Apply is a deliberate button press,
  // so a "try that again" the dispatcher can act on is both simpler and more
  // honest than a second seed-intent path that can drift from the first.
  if (!DispatchState.dataLoaded.trucks || !DispatchState.dataLoaded.trailers || !DispatchState.dataLoaded.assets) {
    window.showToast?.('Your vehicles and assets are still loading — apply the template again in a second.', 'warning', null, 6000);
    return;
  }

  const curDrivers = getJobCrewSelected('drivers');
  const curHelpers = getJobCrewSelected('helpers');
  const curTrucks = getJobCrewSelected('trucks');
  const curTrailers = getJobCrewSelected('trailers');
  const curAssets = getJobCrewSelected('assets');

  // assetDirections deliberately EMPTY — see the header block. Passing `{}` (not
  // the template's, which has none) keeps that true even if a doc written by
  // some future path ever carried one.
  const norm = _normalizeCarriedCrew({
    drivers: [], helpers: [],
    trucks: tpl.trucks || [],
    trailers: tpl.trailers || [],
    assets: tpl.assets || [],
    assetQtys: tpl.assetQtys || {},
    assetDirections: {},
  });

  const union = (cur, add) => Array.from(new Set([...cur, ...add]));
  const nextTrucks = union(curTrucks, norm.trucks);
  const nextTrailers = union(curTrailers, norm.trailers);
  const nextAssets = union(curAssets, norm.assets);

  // Only rows this apply ADDED are the template's to claim.
  const marks = [];
  for (const id of norm.trucks) if (!curTrucks.includes(id)) marks.push(jobTemplateKey('trucks', id));
  for (const id of norm.trailers) if (!curTrailers.includes(id)) marks.push(jobTemplateKey('trailers', id));
  for (const id of norm.assets) if (!curAssets.includes(id)) marks.push(jobTemplateKey('assets', id));

  // 🔴 SEED THE SIDE MAPS BEFORE RENDERING (TASK-9238 / TASK-9253). The tile
  // builder reads pendingAssetQtys while it builds, so a seed that lands after
  // the render paints `|| 1` over a template that says 6 — and the quantity on
  // screen is the quantity that moves stock. A dispatcher's own typed quantity
  // for an asset they had ALREADY ticked wins over the template's: merging means
  // the template fills gaps, it does not overwrite decisions.
  for (const id of norm.assets) {
    if (curAssets.includes(id)) continue;
    const qty = norm.assetQtys[id];
    if (qty && qty > 1) DispatchState.pendingAssetQtys[id] = qty;
    else delete DispatchState.pendingAssetQtys[id];
    // Never preset a return. Explicit delete rather than a trusted absence.
    delete DispatchState.pendingAssetDirections[id];
  }
  // TASK-10006 S2 — the TEMPLATE presets no direction; the job TYPE may (a
  // come-back type starts the rentals it adds as Return, before the render).
  _jobTypeRentalDirOnAdded(norm.assets.filter(id => !curAssets.includes(id)));

  // 🔴 ACCUMULATE, NEVER REPLACE (COD Edge Case Hunter + Code Reviewer, both
  // lanes, independently). A dispatcher can stack a base kit and a job-specific
  // add-on. The first version assigned `templateUnconfirmed = marks`, so the
  // SECOND Apply silently cleared the FIRST kit's untouched rows — they saved as
  // `templateConfirmed: true` having been confirmed by nobody, which is exactly
  // the failure this whole feature exists to prevent, caused by the feature.
  const prev = DispatchState.templateApplied;
  const crewSlots = {
    drivers: Number(tpl.crewSlots?.drivers) || 0,
    helpers: Number(tpl.crewSlots?.helpers) || 0,
  };
  const merged = mergeTemplateApplication({
    prev,
    prevUnconfirmed: DispatchState.templateUnconfirmed,
    template: { id: tpl.id, name: tpl.name || '', crewSlots },
    norm,
    marks,
    appliedAt: new Date().toISOString(),
    appliedBy: window.AppState?.user?.email || 'unknown',
  });
  DispatchState.templateApplied = merged.applied;
  DispatchState.templateUnconfirmed = merged.unconfirmed;

  populateJobCrewSections(curDrivers, curHelpers, nextTrucks, nextTrailers, nextAssets);
  // 🔴 REBUILDING THE CONTAINERS DESTROYS THE PRICE-BOOK RATE PICKERS (COD round
  // 4, Code Reviewer). populateJobCrewSections writes innerHTML wholesale, so
  // every rate dropdown `toggleJobCheckbox` had rendered into those rows is
  // gone — the OVERRIDES survive in pendingJobLineItemSelections, but they
  // become invisible, and the equipment this apply just seeded gets no picker at
  // all. That is a rate a dispatcher cannot set on a row a template chose, which
  // is the wrong half of this feature to leave silent. Re-rendered with
  // editJob's own helper rather than a second mechanism.
  _rerenderJobLineItemPickers();
  _announceDroppedTemplateAssets(norm.dropped);
  renderJobTemplateNotice();
  updateJobLoadCalcLive();
  // Same reason duplicateJob re-runs it: a kit can carry a unit under a safety
  // hold, and the check reads what is SELECTED, not what was selected before.
  setTimeout(runJobComplianceCheck, 50);

  const added = marks.length;
  window.showToast?.(
    added === 0
      ? `“${tpl.name || 'Template'}” added nothing new — everything it carries was already ticked.`
      : `“${tpl.name || 'Template'}” applied — ${added} item${added === 1 ? '' : 's'} added, marked unconfirmed until you check ${added === 1 ? 'it' : 'them'}.`,
    'success', null, 7000
  );
}

/** Bump the template's usage counters — at SAVE, never at apply. A template
 *  applied and then abandoned was not used, and counting it would defeat the one
 *  thing these fields are for: separating the three kits people actually run from
 *  the twelve someone made once. Fire-and-forget: a counter that fails must never
 *  fail a job save that has already committed. */
function bumpJobTemplateUse(applied) {
  const ids = Array.from(new Set((applied?.applications || []).map((a) => a.id).filter(Boolean)));
  if (ids.length === 0 || !DispatchState.tenantId) return;
  for (const id of ids) {
    updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobTemplates', id), {
      useCount: increment(1),
      lastUsedAt: Timestamp.now(),
    }).catch((e) => console.warn('[Dispatch] TASK-9258 template use-count bump failed:', e));
  }
}

/** Modal-scoped reset. Called by showAddJob and editJob for the same reason
 *  TASK-9238 added the pendingAssetQtys reset there: state that only the save
 *  path clears leaks into the next form — open a templated job, Cancel, start a
 *  new one, and the new job's jobDay would claim a template it never used. */
function resetJobTemplateState() {
  DispatchState.templateApplied = null;
  DispatchState.templateUnconfirmed = [];
  _jtDropAnnounced = '';
  const saveRow = document.getElementById('job-template-save-row');
  if (saveRow) saveRow.classList.add('hidden');
  const nameEl = document.getElementById('job-template-name');
  if (nameEl) nameEl.value = '';
  const sel = document.getElementById('job-template-select');
  if (sel) sel.value = '';
  renderJobTemplateNotice();
}

// TASK-273: Multi-day toggle handler. Shows/hides end-date wrapper + keeps end-date
// synced to start-date when collapsed (so saveJob writes 1 jobDay correctly).
function toggleMultiDay() {
  const toggle = document.getElementById('job-multi-day-toggle');
  const wrapper = document.getElementById('job-end-date-wrapper');
  const startEl = document.getElementById('job-start-date');
  const endEl = document.getElementById('job-end-date');
  if (!toggle || !wrapper || !startEl || !endEl) return;
  if (toggle.checked) {
    wrapper.style.display = '';
    // If end-date is blank or before start-date, seed it to start-date
    if (!endEl.value || endEl.value < startEl.value) {
      endEl.value = startEl.value;
    }
    endEl.min = startEl.value;
    startEl.max = endEl.value || ''; // TASK-10288 A — multi-day keeps start <= end
  } else {
    wrapper.style.display = 'none';
    // Silent sync: collapse end-date to start-date so saveJob writes 1 jobDay
    endEl.value = startEl.value;
    startEl.max = ''; // TASK-10288 A — a one-day job's start moves both ways
  }
  // TASK-9112 — toggling multi-day changes which days the job occupies, so it
  // can move a job on/off the visible board without either date field changing.
  refreshJobDateOffscreenHint();
}

// TASK-273: Start-date change handler. Keeps end-date.min in sync; when multi-day
// toggle is OFF, also sets end-date = start-date so saveJob doesn't write multiple
// jobDays from a stale end-date value.
function onJobStartDateChange(newStartVal) {
  // TASK-962 (S3): dispatcher touched the date — it's no longer an assumption.
  setDateAssumedFlag(false);
  const endEl = document.getElementById('job-end-date');
  const toggle = document.getElementById('job-multi-day-toggle');
  // TASK-9112 — the early `return` below is why this call is here and not at the
  // end: with no end-date element the function bails, and the off-board hint
  // would silently stop updating on every date change.
  if (!endEl) { refreshJobDateOffscreenHint(); return; }
  endEl.min = newStartVal || '';
  if (!toggle || !toggle.checked) {
    endEl.value = newStartVal;
  } else if (endEl.value && endEl.value < newStartVal) {
    // Multi-day on but end is now before start — clamp to start
    endEl.value = newStartVal;
  }
  refreshJobDateOffscreenHint(); // TASK-9112 — after end-date is settled, not before
}

function checkJobDuplicate() {
  const warning = document.getElementById('job-duplicate-warning');
  if (!warning) return;
  _duplicateMatchJobId = null;
  warning.style.display = 'none';
  _hideDupeProjectButton(); // TASK-1581 S3

  // Only check for NEW jobs (not editing)
  const jobId = document.getElementById('job-id').value;
  if (jobId) return;

  const title = (document.getElementById('job-title').value || '').trim().toLowerCase();
  const customerId = document.getElementById('job-customer').value;
  if (!title || title.length < 3) return;

  // TASK-795: well-aware dedupe. The #job-well-name input holds a combined
  // wellName+wellNumber string. Two jobs for the same customer at DIFFERENT
  // wells are legit, distinct oilfield work (Guy/Split Rock — data-verified),
  // so they must NOT trip the duplicate warning. If either side has no well,
  // fall back to title-only matching (no regression for non-oilfield work).
  const _normWell = s => (s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const newWell = _normWell(document.getElementById('job-well-name')?.value);

  // Find matching active jobs (same customer + similar title + same/unknown well)
  const matches = DispatchState.jobs.filter(j => {
    if (j.status === 'finished') return false;
    const jTitle = (j.title || '').toLowerCase();
    if (customerId && j.customerId !== customerId) return false;
    if (!customerId && j.customerId) return false;
    // Well-distinctness: if both jobs name a well and the wells differ, they
    // are different work — not a duplicate.
    const jWell = _normWell([j.wellName, j.wellNumber].filter(Boolean).join(' '));
    if (newWell && jWell && newWell !== jWell) return false;
    // Exact or near match
    return jTitle === title || jTitle.includes(title) || title.includes(jTitle);
  });

  if (matches.length === 0) return;

  // Find the most recent match
  const match = matches.sort((a, b) => {
    const da = a.updatedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
    const db2 = b.updatedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
    return db2 - da;
  })[0];

  _duplicateMatchJobId = match.id;

  // Find last scheduled date for this job
  const jobDays = DispatchState.jobDays
    .filter(d => d.jobId === match.id)
    .sort((a, b) => {
      const da = a.date?.toDate?.() || new Date(a.date);
      const db2 = b.date?.toDate?.() || new Date(b.date);
      return db2 - da;
    });
  const lastDay = jobDays[0];
  const lastDateStr = lastDay
    ? (lastDay.date?.toDate?.() || new Date(lastDay.date)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  const customer = getCustomerById(match.customerId);
  const detail = document.getElementById('job-duplicate-detail');
  // TASK-1579 — fold the matched job's PO into the existing meta parenthetical so the
  // dispatcher can judge the dup (the PO drives the invoice — job.poNumber inherits to
  // the ticket at clock-out). COD said NO to a 4th line (banner blindness), so this is
  // metadata on the SAME line, not a new warning. Only show "no PO set" when THIS
  // customer requires a PO (requiredFields) — a PO-agnostic customer with no PO adds
  // nothing. NOTE: adding a day does NOT carry the PO — the PO lives on the job doc;
  // the ticket picks it up at clock-out. The copy stays metadata, never implies the day
  // carries it. This is textContent (XSS-safe) — no HTML, so no escaping needed.
  const needsPO = Array.isArray(customer?.requiredFields) && customer.requiredFields.includes('poNumber');
  const po = jobPoLabel(match); // TASK-9811 S2
  const metaParts = [];
  if (lastDateStr) metaParts.push(`last: ${lastDateStr}`);
  if (po) metaParts.push(`PO: ${po}`);
  else if (needsPO) metaParts.push('no PO set');
  const metaStr = metaParts.length ? ` (${metaParts.join(' · ')})` : '';
  // TASK-1331 — the crew clause is the point: dispatchers dismissed this warning and
  // made a duplicate job on purpose, to keep the earlier days' crew. Days already carry
  // their own crew; the copy never said so.
  detail.textContent = `"${match.title}" for ${customer?.name || 'this customer'}${metaStr}. Add another day instead of creating a duplicate? Each day keeps its own crew — setting a new day's crew won't change the days already scheduled.`;
  // TASK-9289 S2 — inside the form's own project a repeated name is expected;
  // say so, and say what adding a day does to the project link.
  const _carry = _addDayProjectCarry(match, _formProjectContext());
  const _projLine = _addDayProjectCarryText(_carry, match.title);
  if (_projLine) detail.textContent = `"${match.title}" for ${customer?.name || 'this customer'}${metaStr}. ${_projLine}`;
  const _head = warning.querySelector?.('.smart-warning-text strong');
  if (_head) _head.textContent = _carry.kind === 'same' ? 'Already a job in this project' : 'Similar job exists';

  // TASK-1581 S3 — the matched job is in a project and this form has none:
  // offer that project as a third choice (suggest only, never auto-attach).
  const _sugg = match.projectId ? _dupeProjectSuggestion(match, _carry, {
    activeProjects: window.ProjectsModule?.getActiveProjects?.() || [],
    customerId,
    creatingProject: document.getElementById('job-create-new-project')?.checked === true,
    projectsHidden: window.AppState?.driverModePin === true || _jobFormProjectsHidden(),
  }) : null;
  if (_sugg) {
    const _ap = document.getElementById('btn-dupe-add-to-project');
    if (_ap) {
      _ap.textContent = `Add to project “${_sugg.name}”`;
      _ap.dataset.projectId = _sugg.projectId;
      _ap.style.display = '';
      detail.textContent += ` “${match.title}” is in project “${_sugg.name}”.`;
    }
  }

  warning.style.display = '';
}

// ============================================
// TASK-1581 S3 — "Add to project X" on the duplicate warning
// ============================================
// Pure. The project to offer, or null. Offered only when ALL hold:
//   - the matched job has a projectId and that project is ACTIVE (in the
//     active list the picker is built from — a closed project is never offered);
//   - the form has no project yet (carry 'none') and is not creating a new one;
//   - this login sees the Project section (not the PIN driver form, not a
//     login without the projects module — TASK-9651);
//   - the project fits the form's customer (TASK-9606 picker filter), so the
//     pick below can never reach a project the list itself would not show.
function _dupeProjectSuggestion(matchJob, carry, opts) {
  const o = opts || {};
  if (!matchJob || !matchJob.projectId) return null;
  if (o.projectsHidden || o.creatingProject) return null;
  if (carry && carry.kind && carry.kind !== 'none') return null;
  const p = (o.activeProjects || []).find(x => x && x.id === matchJob.projectId);
  if (!p) return null;
  if (!_projectFitsCustomer(p, o.customerId || '')) return null;
  return { projectId: p.id, name: String(p.name || '').trim() || 'Untitled project' };
}

function _hideDupeProjectButton() {
  const b = document.getElementById('btn-dupe-add-to-project');
  if (!b) return;
  b.style.display = 'none';
  b.textContent = '';
  delete b.dataset.projectId;
}

// The button's action: pick the project on the form exactly as the dispatcher
// would from the list (onJobProjectChange fills customer, PO, defaults, site).
// Refuses — and says so — if the option is not in the picker (project closed or
// moved to another customer since the warning painted). Nothing is saved here.
function addDupeMatchProject() {
  const btn = document.getElementById('btn-dupe-add-to-project');
  const pid = btn?.dataset?.projectId || '';
  const sel = document.getElementById('job-project-id');
  if (!pid || !sel || sel.disabled) return false;
  // COD r1 (Code): the button can be stale — the dispatcher may have picked a
  // project (or opened New project) after it painted. Only an EMPTY picker may
  // take the suggestion; otherwise it would replace their pick and its PO.
  if (sel.value || document.getElementById('job-create-new-project')?.checked === true) {
    _hideDupeProjectButton();
    return false;
  }
  if (!Array.from(sel.options || []).some(o => o.value === pid)) {
    window.showToast?.('That project is no longer in the Project list for this customer.', 'info');
    _hideDupeProjectButton();
    return false;
  }
  sel.value = pid;
  onJobProjectChange(sel);
  checkJobDuplicate(); // repaint: the form is now in the match's project ('same')
  window.showToast?.('Project picked on this job. Check the PO, then Save Job.', 'success');
  return true;
}

// TASK-1111 SLICE A — ONE builder for an "Add Day" jobDay so every add-day path
// carries the latest day's crew + selections + settings forward identically. Kills
// the 4-way copy-paste (was: addDayInstead / _dupeBlockAddDay / _quickAddDay copied
// crew, addDayToJob hardcoded EMPTY — the outlier). Crew is per-day, so "carry
// forward" = seed the new day from the most recent one; the user can still clear it.
// TASK-9233 — `formSeed` is OPTIONAL and only the two FORM-driven add-day paths
// (_dupeBlockAddDay, addDayInstead) pass it. 🔴 It must never default to reading
// the form: this is the single canonical builder for all four add-day paths, and
// the two BOARD paths (_quickAddDay, addDayToJob) run with #modal-job hidden but
// still populated from whatever was edited last — seeding those off the DOM
// would stamp one job's crew onto another.
//
// 🔴 And when a seed IS given it REPLACES; it does not fill in blanks. An empty
// crew array in an open form is a deliberate clear, not "untouched" — falling
// back to the matched job's latest day on blank is precisely the clone-the-old-
// day behaviour TASK-9233 exists to kill.
function buildInheritedJobDay(jobId, newDate, formSeed, daysOverride) {
  // TASK-10043 — daysOverride: the job's days from a FRESH read, for a caller
  // outside the board (Billing) whose DispatchState.jobDays may be empty/stale.
  const latestDay = (Array.isArray(daysOverride) ? daysOverride : DispatchState.jobDays)
    .filter(d => d.jobId === jobId)
    .sort((a, b) => {
      const da = a.date?.toDate?.() || new Date(a.date);
      const db2 = b.date?.toDate?.() || new Date(b.date);
      return db2 - da; // most recent first
    })[0];
  // One source for every crew dimension: the form when we were handed one,
  // otherwise the most recent day. Never a mix — a half-form/half-inherited day
  // is the shape that made Mike's 9/15 row unreadable after the fact.
  const src = formSeed || latestDay || {};
  const data = {
    jobId,
    date: Timestamp.fromDate(newDate),
    status: 'scheduled',
    drivers: src.drivers || [],
    helpers: src.helpers || [],
    trucks: src.trucks || [],
    trailers: src.trailers || [],
    assets: src.assets || [],
    createdAt: Timestamp.now(),
  };
  // TASK-9750 — a day added to a TENTATIVE run stays tentative: it carries the
  // same crew, and a confirmed copy would text them about work nobody has
  // confirmed. Half day is NOT carried (it describes one day, not the run).
  if (src.tentative === true) data.tentative = true;
  // Price book: the FORM wins when the dispatcher actually picked one, because
  // inherited billing settings sitting next to freshly-chosen crew is how a day
  // ends up billing against a contract nobody selected. Untouched pickers are
  // NOT a deliberate clear here (these live in DispatchState, not in checkboxes),
  // so an empty selection still inherits.
  const seedLineItems = formSeed?.lineItemSelections;
  if (seedLineItems && Object.keys(seedLineItems).length > 0) data.lineItemSelections = seedLineItems;
  else if (latestDay?.lineItemSelections && Object.keys(latestDay.lineItemSelections).length > 0) data.lineItemSelections = latestDay.lineItemSelections;
  if (formSeed?.selectedContractId) data.selectedContractId = formSeed.selectedContractId;
  else if (latestDay?.selectedContractId) data.selectedContractId = latestDay.selectedContractId;
  // These belong to the job being JOINED, not to the form — the new day is
  // landing on an existing job and must not contradict its ticket settings.
  // TASK-10190 — only when the new day bills the SAME book as that day: a day
  // on another book gets no stamp, and the Build Ticket resolves it at read
  // time (jobboard.js _jbEnsureJobDayFts) instead of showing the wrong pads.
  if (latestDay?.fieldTicketSettings && (data.selectedContractId || null) === (latestDay.selectedContractId || null)) {
    data.fieldTicketSettings = latestDay.fieldTicketSettings; // TASK-395 carry-forward
  }
  // Carry the asset DETAIL alongside `assets` — else rental quantities/directions
  // silently reset (COD Code Reviewer). jobDays store these (see date-sync :7463,
  // save :7607, editor :8737-8738).
  // TASK-9233 — read the detail from WHICHEVER source supplied `assets`, or the
  // form's equipment lands carrying the old day's quantities and directions.
  if (src.assetQtys && Object.keys(src.assetQtys).length > 0) data.assetQtys = src.assetQtys;
  if (src.assetDirections && Object.keys(src.assetDirections).length > 0) data.assetDirections = src.assetDirections;
  // TASK-9763 — "who's in what" carries forward like lineItemSelections (a
  // multi-day run keeps its pairings), pruned to the new day's own crew/units so
  // a form-seeded day never inherits a pairing for someone not on it.
  {
    // An explicit null on the source is a dispatcher's CLEAR — never revive the
    // older day's map over it; and a day that is 1 person / 1 truck carries no
    // pairing at all (COD Code Reviewer + Edge Case).
    const _CP = (typeof window !== 'undefined' && window.CrewPairing) || null;
    const _pairSrc = Object.prototype.hasOwnProperty.call(src, 'crewAssetAssignments')
      ? src.crewAssetAssignments : latestDay?.crewAssetAssignments;
    const _pairs = _CP && _pairSrc && _CP.applies(data) ? _CP.prune(_pairSrc, data) : null;
    if (_pairs) data.crewAssetAssignments = _pairs;
    // S3 — the "first on" order rides along the same way (explicit null on the
    // source = cleared, never revived from an older day).
    const _ordSrc = Object.prototype.hasOwnProperty.call(src, 'crewOrder') ? src.crewOrder : latestDay?.crewOrder;
    const _ord = _CP && _ordSrc ? _CP.orderPrune(_ordSrc, data) : null;
    if (_ord) data.crewOrder = _ord;
    // TASK-9797 item 1 — named slots ride along the same way, to the new day's trucks.
    const _sl = typeof _slotsOn === 'function' && _slotsOn('unitSlots' in src ? src.unitSlots : latestDay?.unitSlots, data.trucks);
    if (_sl) data.unitSlots = _sl;
  }
  // TASK-9258 — THE MARK FOLLOWS THE NUMBER. This path carries the quantities
  // faithfully, so a templated quantity would otherwise land here with nothing
  // saying nobody checked it. (Since TASK-9331 the date-sync range-extend lane
  // carries them too, and inherits the stamp the same way.)
  //
  // 🔴 GATED ON `formSeed`, which is the whole correctness argument. The other
  // two callers of this function are board-menu Add Day paths with no form open,
  // and `templateApplied` survives a CANCELLED form until the next
  // showAddJob/editJob — so an ungated stamp would attribute an abandoned
  // template to an unrelated job's new day. With the gate, the stamp only rides
  // the seed that actually came out of the form the template was applied to.
  if (formSeed) {
    const _tpl = buildJobDayTemplateFields(
      DispatchState.templateApplied, DispatchState.templateUnconfirmed, data.assets);
    if (_tpl) Object.assign(data, _tpl);
  } else {
    // 🔴 THE BOARD PATHS INHERIT IT FROM THE DAY THEY COPY (COD round 5, Code
    // Reviewer). `_quickAddDay` / `addDayToJob` carry `latestDay.assets` AND its
    // `assetQtys`, so without this the new day keeps six template-seeded movers
    // and loses the only record that nobody ever checked them — the number
    // survives and its mark does not, which is the precise failure this feature
    // exists to prevent. Inherited from the PERSISTED day rather than from
    // `DispatchState.templateApplied`, so there is no abandoned-modal risk here
    // and no reason to gate it: a stored jobDay is a fact, not live form state.
    const _inh = inheritJobDayTemplateFields(latestDay, data.assets);
    if (_inh) Object.assign(data, _inh);
  }
  // No form field for subcontractors, so this always belongs to the joined job.
  if (latestDay?.subcontractors && latestDay.subcontractors.length > 0) data.subcontractors = latestDay.subcontractors;
  return data;
}

// TASK-9233 — read the OPEN job form into an add-day seed.
//
// THE DEFECT THIS EXISTS FOR. `_dupeBlockAddDay` read exactly two inputs — the
// matched job id and the form's start date. Title, well, PO, notes, waypoints,
// drivers, helpers, trucks, trailers and assets were never read, so a dispatcher
// who built a whole job and then chose "Add Day to Existing Job" at the
// duplicate gate had every selection discarded: the new day was seeded from the
// MATCHED job's latest day instead, and `openNewDayCrewEditor`'s
// `closeModal('job')` then destroyed the form on the way out. Mike hit this on
// prod 2026-09-15T15:54:35Z (jobAuditLog JOB_DAY_ADD_VIA_DUPE_GATE, jobId
// vtymbsAf48BE2O5kkvQN) — the day that landed was a byte-for-byte clone of a
// 9/13 day he never chose, and the CREW_ASSIGN_UPDATE four seconds later shows
// before == after, i.e. he accepted a setup he had not picked.
//
// 🔴 Reads through `_jobCrewForCreate`, NOT `getJobCrewSelected` — that is the
// reader saveJob's CREATE branch uses, and the create branch is the write this
// dispatcher was one click away from running. Anything else repeats TASK-9212's
// lesson in reverse: that task landed because every earlier recovery had fixed
// the FORM and still persisted the wrong thing. Seed the WRITE from the same
// source the write itself would have read.
//
// Returns null when the job form is not actually on screen, so a caller with no
// form context can never seed from stale checkboxes left on a hidden #modal-job.
function _readJobFormDaySeed() {
  const modal = document.getElementById('modal-job');
  if (!modal || modal.classList.contains('hidden')) return null;
  if (!document.getElementById('job-select-drivers')) return null;
  const assets = _jobCrewForCreate('assets', 'assets');
  return {
    drivers: _jobCrewForCreate('people', 'drivers'),
    helpers: _jobCrewForCreate('people', 'helpers'),
    trucks: _jobCrewForCreate('trucks', 'trucks'),
    trailers: _jobCrewForCreate('trailers', 'trailers'),
    assets,
    // Same two derivations saveJob's create branch runs, so a day added through
    // the gate and a day created by Save are byte-identical for equipment.
    assetQtys: (() => { const q = {}; assets.forEach(id => { const v = DispatchState.pendingAssetQtys?.[id]; if (v && v > 1) q[id] = v; }); return q; })(),
    assetDirections: (() => { const d = {}; assets.forEach(id => { const v = DispatchState.pendingAssetDirections?.[id]; if (v === 'return') d[id] = 'return'; }); return d; })(),
    lineItemSelections: { ...(DispatchState.pendingJobLineItemSelections || {}) },
    selectedContractId: DispatchState.pendingJobSelectedContractId || null,
    // TASK-9750 — the form's Tentative tick rides the seed like the crew does.
    tentative: document.getElementById('job-day-flags-row')?.style.display !== 'none'
      && !!document.getElementById('job-tentative')?.checked,
  };
}

// TASK-9233 — the form's WHOLE date range as noon-local Dates.
//
// The form-driven add-day paths took only the start date, so a dispatcher who
// built a 9/15–9/17 job and chose Add Day got ONE day and lost the other two
// with no message at all. Pure (no DOM, no Firestore) so the tripwire can drive
// it directly.
//
// 🔴 Dates advance by CALENDAR day (`setDate(+1)`), never by +24h in ms: a
// ms-step across a DST boundary lands at 11:00 or 13:00 local and drifts
// further every day. Every date this returns is exactly noon local, which is the
// anchor jobDay.date requires — midnight-local renders nowhere, silently.
//
// Capped at _DUPE_ADD_DAY_MAX_DAYS. saveJob's create loop writes its range in a
// single writeBatch, but this path writes day-by-day with a job-range update per
// date, so a fat-fingered end year would be hundreds of sequential round-trips.
// Cap and SAY SO rather than grind or silently truncate.
const _DUPE_ADD_DAY_MAX_DAYS = 60;
function _dupeAddDayDateRange(startDateVal, endDateVal) {
  const parse = (v) => {
    if (!v) return null;
    const d = new Date(String(v) + 'T12:00:00');
    return Number.isFinite(d.getTime()) ? d : null;
  };
  const start = parse(startDateVal);
  if (!start) return { dates: [], truncated: false };
  let end = parse(endDateVal);
  // An end before the start is a half-typed form, not a request to write
  // backwards — treat it as a single day rather than refusing the whole add.
  if (!end || end < start) end = start;
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 12, 0, 0, 0);
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 12, 0, 0, 0);
  const dates = [];
  let truncated = false;
  while (cur <= last) {
    if (dates.length >= _DUPE_ADD_DAY_MAX_DAYS) { truncated = true; break; }
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return { dates, truncated };
}

// TASK-9233 — the ONE writer for both FORM-driven add-day paths (the duplicate
// gate and the focus-out "add day instead" warning). Fifth member of the family
// that already includes buildInheritedJobDay / claimAddDaySlot /
// _applyAddDayJobRange / openNewDayCrewEditor — those exist because this file
// kept growing four divergent copies of the same block, and the two form paths
// had drifted apart again.
//
// 🔴 CLAIMS EACH DATE INDEPENDENTLY. The old code called claimAddDaySlot once
// and SHORT-CIRCUITED on the first collision — `_dupeBlockAddDay` returned true
// and opened that day's editor — so a 9/15–9/17 range against a job that already
// owned 9/15 wrote nothing at all for 9/16 and 9/17 and closed the form on top
// of it. A date the job already holds is a SKIP, never an abort.
//
// 🔴 CALLS _applyAddDayJobRange FOR EVERY DATE, not just the first. A later date
// left outside job.endDate is deleted by the next saveJob date-sync, under a
// "Job updated." toast — delayed, silent loss.
//
// Writes nothing else: job-level fields (title, PO, notes, waypoints) have
// nowhere to go, and pushing them onto the matched job would rewrite an existing
// job's billing identity. The callers say so in the UI instead.
//
// Never throws — it reports. A throw here would unwind into a caller whose only
// tool is to close the form, which is the failure this task is about.
async function _addDaysFromJobForm(jobId, dates, formSeed, source) {
  const created = [];
  const skipped = [];
  let failed = 0;
  for (const newDate of dates) {
    const claim = claimAddDaySlot(jobId, newDate);
    if (!claim.key) {
      skipped.push({ date: newDate, existingDayId: claim.existingDayId || null });
      continue;
    }
    try {
      const newJobDayData = buildInheritedJobDay(jobId, newDate, formSeed);
      const newDayRef = await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), newJobDayData);
      // TASK-4567 — shared range write: widens startDate BACKWARD as well as
      // endDate forward, and clears this date's removal record.
      await _applyAddDayJobRange(jobId, newDate, source, newDayRef.id);
      created.push({ date: newDate, dayId: newDayRef.id, data: newJobDayData });
    } catch (err) {
      failed++;
      console.error('[Dispatch] form add-day failed for', formatDateISOLocal(newDate), err);
    } finally {
      releaseAddDaySlot(claim);
    }
  }
  return { created, skipped, failed };
}

// TASK-9233 — the toasts every form-driven add-day path owes the dispatcher.
// Split out so the two callers cannot drift on what they report, and so a
// partial result (some days written, some already scheduled, some failed) is
// never reported as a clean success.
function _reportFormAddDayResult(jobTitle, result, truncated) {
  const label = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const { created, skipped, failed } = result;
  if (created.length) {
    const span = created.length === 1
      ? label(created[0].date)
      : `${label(created[0].date)}–${label(created[created.length - 1].date)} (${created.length} days)`;
    window.showToast?.(`Day${created.length === 1 ? '' : 's'} added to "${jobTitle || 'job'}" for ${span}`, 'success');
  }
  if (skipped.length) {
    window.showToast?.(
      `${skipped.map(s => label(s.date)).join(', ')} already scheduled on this job — left unchanged.`,
      'info', null, 7000);
  }
  if (failed) {
    window.showToast?.(
      `${failed} day${failed === 1 ? '' : 's'} could not be added — check the board before you dispatch.`,
      'warning', null, 9000);
  }
  if (truncated) {
    window.showToast?.(
      `Only the first ${_DUPE_ADD_DAY_MAX_DAYS} days were added — add the rest from the job's menu.`,
      'warning', null, 9000);
  }
}

// TASK-1111 SLICE A — WARN (never block) when carried-forward crew has a DOT
// ELIGIBILITY problem (expired CDL / med card). Advisory only: FMCSA doesn't
// require a software hard-stop, and blocking would force dispatchers to route
// around Add Day mid-dispatch (the real hard-stop lives in saveAssignment).
// Scope: CDL/med-card eligibility only — checkDOTCompliance does NOT detect
// double-booking or HOS, so we don't imply it does. Post-add toast because the
// add-day paths have no inline warnings container.
function warnCarriedCrewDotIssues(jobDayData) {
  // TASK-9227 — the compliance flag rides the same advisory, and must be checked
  // BEFORE the drivers-empty bail below: `helpers` are included here (a day whose
  // only carried crew is a flagged helper would otherwise return before any check
  // ran) and checkDOTCompliance has no helper parameter at all. Own try/catch so
  // a flag-lookup failure cannot swallow the DOT advisory that follows.
  try {
    _dfNoticeEntries(
      _dfFlaggedEntries([...(jobDayData.drivers || []), ...(jobDayData.helpers || [])]),
      { surface: 'add-day-carry', jobId: jobDayData.jobId || null, action: 'FLAGGED_CREW_CARRIED' }
    );
  } catch (e) { console.warn('[Dispatch] TASK-9227 carried-crew flag check failed:', e); }
  try {
    if (!(jobDayData.drivers || []).length) return;
    const job = DispatchState.jobs.find(j => j.id === jobDayData.jobId);
    // Include severity 'error' — checkDOTCompliance tags EXPIRED CDL/med-card as
    // 'error' (not 'warning') when a truck is present, and we always carry trucks
    // forward, so a warning-only filter would miss the exact case we want to flag
    // (COD Code/Domain/Blast). We only TOAST (never block), so surfacing 'error'
    // as an advisory is correct.
    const warnings = (checkDOTCompliance(jobDayData.drivers || [], jobDayData.trucks || [], jobDayData.trailers || [], job) || [])
      .filter(w => w && (w.severity === 'error' || w.severity === 'warning'));
    if (!warnings.length) return;
    const plain = warnings.map(w => String(w.message || '').replace(/<[^>]+>/g, '').trim()).filter(Boolean).join(' · ');
    if (plain) window.showToast?.(`Crew carried forward — check eligibility: ${plain}`, 'warning');
  } catch (e) { console.warn('[Dispatch] carried-crew DOT check failed:', e); }
}

// TASK-1109 — guards a rapid double-add race: two Add-Day calls for the SAME
// job+date within snapshot-propagation latency would both pass the dedup guard
// (the first addDoc hasn't reached DispatchState.jobDays yet). Keyed jobId|date.
const _addDayInFlight = new Set();

// TASK-1331 — the TASK-1109 guards (same-date dedup + in-flight key) lived only in
// addDayToJob; the other three add-day paths could each write two jobDays for one date
// on a fast double-click. One guard for all four.
// Returns { key } — release it in a `finally` — or { key: null } when the caller must
// not add. `existingDayId` is set when the job is ALREADY scheduled that day, so the
// caller can open that day's crew editor instead of dead-ending the operator, which is
// exactly what sends them off to create a duplicate job.
function claimAddDaySlot(jobId, newDate) {
  // jobDay dates are noon-local Timestamps, so compare by LOCAL calendar date
  // (formatDateISOLocal), not raw ms, to dodge a tz off-by-one.
  const dateStr = formatDateISOLocal(newDate);
  const existing = DispatchState.jobDays.find(d => {
    if (d.jobId !== jobId) return false;
    const dd = d.date?.toDate?.() || new Date(d.date);
    return !isNaN(dd) && formatDateISOLocal(dd) === dateStr;
  });
  if (existing) return { key: null, existingDayId: existing.id, dateStr };
  const flightKey = `${jobId}|${dateStr}`;
  if (_addDayInFlight.has(flightKey)) return { key: null, existingDayId: null, dateStr };
  _addDayInFlight.add(flightKey);
  return { key: flightKey, existingDayId: null, dateStr };
}

function releaseAddDaySlot(claim) {
  if (claim?.key) _addDayInFlight.delete(claim.key);
}

// TASK-1331 — dispatchers deliberately created duplicate JOBS to protect crew history
// (12 records of one title in live data), because every add-day path wrote the new day
// silently: the carried-forward crew was never shown and there was no chance to change
// it in the flow. Days ARE independent already, so the fix is to end every add-day in
// the per-day crew editor for the new date. Shared by all four add-day paths.
async function openNewDayCrewEditor(jobId, jobDayId, jobDayData, opts) {
  if (!jobId || !jobDayId) return;
  // TASK-9943 — every caller but openExistingDayCrewEditor has just CREATED this
  // day, so the editor's Cancel offers to take it back off (cancelAssignment).
  const _d = jobDayData?.date?.toDate?.() || (jobDayData?.date ? new Date(jobDayData.date) : null);
  _justAddedDay = (opts && opts.existing) ? null : {
    jobId, jobDayId, at: Date.now(),
    dateLabel: (_d && Number.isFinite(_d.getTime())) ? formatDateDisplay(_d) : ''
  };
  // showAssignment resolves the day out of DispatchState.jobDays, so the local push is
  // required — without it the editor silently no-ops until the snapshot lands. The
  // snapshot rebuilds the array wholesale (:2942), so this can't leave a duplicate.
  if (jobDayData && !DispatchState.jobDays.some(d => d.id === jobDayId)) {
    DispatchState.jobDays.push({ id: jobDayId, ...jobDayData });
  }
  // showAssignment only closes 'job-details' — close the job form AND the dupe-block
  // overlay (inline z-index 10000) explicitly, or the editor opens stacked underneath
  // them and its save path inherits the job form's state.
  // closeModal('job') rather than a raw hide (integrated-diff COD): it also
  // removes the DOT-assistant warnings, bumps _jobFormGen to invalidate a
  // pending duplicate pre-fill, clears _dupePrefillTimer and resets
  // _editJobOriginalCustomerId — hiding the element leaves all of that alive
  // behind a closed form.
  closeModal('job');
  document.getElementById('job-dupe-block-modal')?.remove();
  await showAssignment(jobId, jobDayId, { dayScopeNote: true });
  // DOT advisory fires AFTER the editor is up — it is about the crew being edited right
  // now, and toasts (z-index 1100) sit above the assignment modal (1000), so it lands
  // on top rather than under the modal that opened a beat later.
  if (jobDayData) warnCarriedCrewDotIssues(jobDayData);
}

// TASK-1331 — shared "you're already on that date" branch: reopen the existing day's
// crew editor rather than toasting a dead end.
async function openExistingDayCrewEditor(jobId, claim) {
  const existingDay = DispatchState.jobDays.find(d => d.id === claim.existingDayId);
  const label = new Date(claim.dateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  window.showToast?.(`Already scheduled for ${label} — opening that day's crew`, 'info');
  await openNewDayCrewEditor(jobId, claim.existingDayId, existingDay, { existing: true });
}

// ============================================
// TASK-4567 — add-day date-range math (PURE, no Firestore)
// ============================================
// All four add-day paths carried the SAME copy-pasted block:
//   const currentEnd = job?.endDate?.toDate?.() || new Date(job?.endDate);
//   if (newDate > currentEnd) updateDoc(jobs/{id}, { endDate })
// i.e. endDate is pushed FORWARD and startDate is never pulled BACK. A day
// added before the job's startDate wrote clean and toasted success, but the
// job's range never widened to contain it — so the next Edit Job that touched
// the dates hit saveJob's "delete jobDays outside new range" batch and
// destroyed that day, its crew, trucks, assets, lineItemSelections and load
// counts, under a "Job updated." toast. clockEntries link by jobId only —
// no writer stamps jobDayId — so the time itself survives, but the record
// of WHICH day it was worked does not, and payroll already used that day.
//
// These helpers are PURE (Dates in, Dates out — no Timestamp, no db, no
// DispatchState) so tests/task4567-add-day-range.unit.cjs pins them offline.

// Firestore Timestamp | Date | date-string | null → a FINITE Date, or null.
// GAS-imported jobs carry missing/unparsable startDate/endDate, and the old
// `new Date(undefined)` produced an Invalid Date whose every comparison is
// false. That happened to be safe for the endDate arm; it is NOT something to
// keep relying on once a second (startDate) comparison exists. Reject explicitly.
function _finiteDateFrom(value) {
  if (value === null || value === undefined || value === '') return null;
  const d = typeof value?.toDate === 'function' ? value.toDate()
    : (value instanceof Date ? value : new Date(value));
  return (d instanceof Date && Number.isFinite(d.getTime())) ? d : null;
}

// Which job date fields must be written so `newDate` lands INSIDE the job's
// range. Returns Date values (the caller wraps them in Timestamp) and ONLY the
// keys that change — `{}` means the day is already inside the range, write
// nothing. A missing/unparsable side of the range is skipped, never widened to
// an Invalid Date.
function computeJobRangeForAddedDay(job, newDate) {
  const out = {};
  const target = _finiteDateFrom(newDate);
  if (!target) return out; // unparsable add-date → never touch the job doc
  const currentStart = _finiteDateFrom(job?.startDate);
  const currentEnd = _finiteDateFrom(job?.endDate);
  if (currentEnd && target > currentEnd) out.endDate = target;
  if (currentStart && target < currentStart) out.startDate = target;
  return out;
}

// TASK-4794 — which dates saveJob's date-sync may CREATE a jobDay on: only the
// ones the edit actually ADDED to the range, i.e. outside the OLD
// [oldStartStr..oldEndStr]. A date that was already inside the old range and
// has no jobDay is a hole somebody left (Add Day widens the range to a far
// date without filling between; weekends skipped; a day removed before
// TASK-4792 kept a record) — filling it invents a day nobody scheduled, with
// crew carried forward, on the board and textable. Measured 2026-10-15: 119
// jobs across 3 tenants carry such interior holes (TITS 38, show-account 76),
// so recording each gap at add-day time would not have covered the existing
// ones. All args are 'YYYY-MM-DD' keys (the same toISOString keying the
// date-sync uses), so string compare is date compare. A missing old bound
// falls back to the old behaviour (every date is creatable).
// `existingDates` = the date keys that already hold a jobDay. When NONE of
// them lands inside the new range the edit is a MOVE (or a shrink onto a
// hole, e.g. days on 10/6 + 10/10, range set to 10/8): a hole means nothing
// there, and skipping it would leave the job with no day at all — invisible
// on the board. So that case keeps the old fill-every-date behaviour.
// `keptDates` (optional) = dates OUTSIDE the new range whose jobDay stays
// anyway because a ticket lock refused its delete. That day survives the edit
// too, so the job is not dayless and holes are NOT filled — UNLESS the gap
// rule would then leave the NEW range with no day at all (COD
// cod-20261020T072023Z-73236 Independent: a move/shrink wholly onto holes
// inside the old range, with only an out-of-range locked day left, would point
// the job's range at dates with no jobDay). In that case the dispatcher put
// the job on exactly these dates, so every one is created, as before.
function dateSyncCreatableDates(newDates, oldStartStr, oldEndStr, existingDates, keptDates) {
  const _newSet = newDates instanceof Set ? newDates : new Set(newDates);
  const _existingSurvivor = [...(existingDates || [])].some(d => _newSet.has(d));
  const _keptSurvivor = !_existingSurvivor && [...(keptDates || [])].length > 0;
  const _hasSurvivor = _existingSurvivor || _keptSurvivor;
  const out = [];
  for (const d of _newSet) {
    if (_hasSurvivor && oldStartStr && oldEndStr && d >= oldStartStr && d <= oldEndStr) continue;
    out.push(d);
  }
  if (_keptSurvivor && out.length === 0) return [..._newSet];
  return out;
}

// The admin board's jobs/jobDays subscriptions are windowed to a lookback
// (default 90d, widened by "Load older", <= 0 = "all"). subscribeToData reads
// its cutoff from here, and so does the far-past add-day confirm — ONE source,
// so the warning threshold can never drift from the window it warns about.
// `0` is a live sentinel for "all": parse with Number.isFinite, never `|| 90`.
function computeAdminWindow(daysBackRaw, now) {
  const _now = (now instanceof Date && Number.isFinite(now.getTime())) ? now : new Date();
  const todayStartLocal = new Date(_now.getFullYear(), _now.getMonth(), _now.getDate());
  const raw = Number(daysBackRaw);
  const daysBack = Number.isFinite(raw) ? raw : 90;
  return {
    daysBack,
    all: daysBack <= 0,
    todayStartLocal,
    cutoff: new Date(todayStartLocal.getTime() - daysBack * 24 * 60 * 60 * 1000)
  };
}

// True when a date lands BEFORE the board's own lookback — the jobDay writes
// fine but renders NOWHERE until the dispatcher taps "Load older".
function isDateOutsideAdminWindow(date, daysBackRaw, now) {
  const target = _finiteDateFrom(date);
  if (!target) return false;
  const w = computeAdminWindow(daysBackRaw, now);
  if (w.all) return false; // "all" = no lower bound, nothing is out of window
  return target < w.cutoff;
}

// TASK-4567 step 3 — WARN, don't block. Runs before the jobDay write on all
// four add-day paths, so a cancel leaves nothing behind.
async function _confirmFarPastAddDay(newDate) {
  if (!isDateOutsideAdminWindow(newDate, DispatchState.adminWindowDaysBack)) return true;
  if (typeof window.showConfirm !== 'function') return true; // warning, not a gate
  const w = computeAdminWindow(DispatchState.adminWindowDaysBack);
  const label = newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  return await window.showConfirm(
    'Add a day that far back?',
    `${label} is further back than the ${w.daysBack} days this schedule is currently loading. The day will be created with the crew carried forward, but it won't appear on the board until you tap "Load older". Add it anyway?`,
    { confirmText: 'Add It Anyway' }
  );
}

// TASK-4567 steps 1 + 4 — the ONE job-range write every add-day path uses,
// plus the audit row for a day landing in the past. Replaces the four
// copy-pasted endDate-only blocks.
async function _applyAddDayJobRange(jobId, newDate, source, jobDayId, jobOverride) {
  // TASK-10043 — jobOverride: the job doc from a fresh read (Billing caller).
  const job = jobOverride || DispatchState.jobs.find(j => j.id === jobId);
  // TASK-10043 (COD) — every write here goes to the company this add started
  // in; after the await, a switched company gets no history rows from it.
  const _rangeTid = DispatchState.tenantId;
  const range = computeJobRangeForAddedDay(job, newDate);
  const update = {};
  // TASK-4792 — adding a day back CLEARS its removal record. Without this a
  // date removed once would stay permanently un-creatable by saveJob's
  // date-sync, and the dispatcher would have no way to undo their own removal
  // except by hand. Every add-day path funnels through here, so this is the one
  // place it has to live. arrayRemove on a field that does not exist is a no-op,
  // so it is safe on the ~every job that has never had a day removed.
  const _addedDayStr = (() => {
    const d = _finiteDateFrom(newDate);
    return d ? formatDateISO(d) : null;
  })();
  if (_addedDayStr) update.removedDates = arrayRemove(_addedDayStr);
  // TASK-4610 — route through the one coercion point. computeJobRangeForAddedDay
  // already hands back Dates, so this is a type ratchet, not a re-anchor: a Date
  // keeps its own time, only a date-only STRING gets snapped to noon local.
  const _rangeStartTs = coerceJobTimestamp(range.startDate);
  const _rangeEndTs = coerceJobTimestamp(range.endDate);
  if (_rangeStartTs) update.startDate = _rangeStartTs;
  if (_rangeEndTs) update.endDate = _rangeEndTs;
  // TASK-4792 — `update.removedDates` counts too. The old condition was
  // start/end only, so re-adding a day INSIDE the existing range (the common
  // case: the range never moves) would have skipped the write and left the
  // removal record standing against a day that now exists.
  if (update.startDate || update.endDate || update.removedDates) {
    update.updatedAt = Timestamp.now();
    await updateDoc(doc(db, 'tenants', _rangeTid, 'jobs', jobId), update);
  }

  // TASK-9943 — every add-day path funnels through here, so this is where the
  // add is RECORDED: who added which date to which job. Before this only a
  // back-dated add left a row, so "when was this day added, and by whom?" had
  // no answer (job #1165). Fire-and-forget: it never throws or delays the add.
  if (DispatchState.tenantId === _rangeTid) {
    const _t = _finiteDateFrom(newDate);
    const _ds = _t ? formatDateISO(_t) : null;
    _auditJobDayEvent('JOB_DAY_ADD', {
      jobId, jobDayId: jobDayId || null, source: source || 'dispatch', date: _ds,
      detail: `Added ${_ds || 'a day'} to the job`,
      extra: { jobStartDateWidened: !!range.startDate, jobEndDateWidened: !!range.endDate }
    });
  }

  // Step 4 — the back-dated add is what used to silently create the
  // delete-on-next-edit trap, so it leaves a row. Same shape as the dupe-gate
  // audit (JOB_DAY_ADD_VIA_DUPE_GATE), not a new one. Never throws.
  const target = _finiteDateFrom(newDate);
  const w = computeAdminWindow(DispatchState.adminWindowDaysBack);
  if (target && target < w.todayStartLocal) {
    try {
      if (DispatchState.tenantId !== _rangeTid) return range;
      await addDoc(collection(db, 'tenants', _rangeTid, 'jobAuditLog'), {
        action: 'JOB_DAY_ADD_BACKDATED',
        source: source || 'dispatch',
        timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        jobId,
        addedDate: formatDateISO(target),
        jobStartDateWidened: !!range.startDate,
        outsideBoardWindow: isDateOutsideAdminWindow(target, DispatchState.adminWindowDaysBack)
      });
    } catch (ae) { console.warn('[Dispatch] back-dated add-day audit log failed:', ae); }
  }
  return range;
}

// ── TASK-4793 slice B ──────────────────────────────────────────────────────
// saveJob's date-sync no longer deletes a jobDay from the browser outside the
// demo (firestore.rules allow a client jobDay delete in the demo only). Two
// calls to the removeJobDay callable, services/jobDaySync.js:
//   _dateSyncPrecheck  — BEFORE the job doc is written: would the new dates
//     push a ticketed or clocked-in day out of the job? Then nothing is saved
//     and the dispatcher is told which days and why. Same predicate as Remove
//     Day. Returns { ok } or { ok:false, message }.
//   _dateSyncServerDelete — AFTER the create batch: delete the confirmed
//     out-of-range days in one server transaction (re-checked; a day that is
//     now blocked is KEPT and named) + the JOB_DAY_DELETE row.
async function _dateSyncPrecheck(jobId, oldData, startDate, endDate) {
  const oldStart = oldData.startDate?.toDate?.() || null;
  const oldEnd = oldData.endDate?.toDate?.() || null;
  // Same keys and same "did the range change" test as the date-sync below; no
  // Timestamp range = no date-sync = nothing to check.
  if (!oldStart || !oldEnd) return { ok: true };
  const start = startDate.toISOString().slice(0, 10);
  const end = endDate.toISOString().slice(0, 10);
  if (start === oldStart.toISOString().slice(0, 10) && end === oldEnd.toISOString().slice(0, 10)) return { ok: true };
  let res;
  try {
    res = (await httpsCallable(functions, 'removeJobDay')({
      tenantId: DispatchState.tenantId, jobId, mode: 'check-range', start, end,
    }))?.data || null;
  } catch (error) {
    console.error('[saveJob] TASK-4793 B: date-change check failed — nothing saved', error);
    window.logError?.(error, { module: 'dispatch', phase: 'jobDays-datesync-precheck', jobId });
    const code = String(error?.code || '');
    if (/^functions\/(permission-denied|not-found|failed-precondition)$/.test(code) && error?.message) {
      return { ok: false, message: `${error.message} Nothing was saved.` };
    }
    return { ok: false, message: "Couldn't check the days this date change would drop — nothing was saved. Try again." };
  }
  if (!res || res.ok !== true || !Array.isArray(res.blocked)) {
    return { ok: false, message: "Couldn't check the days this date change would drop — nothing was saved. Try again." };
  }
  if (res.blocked.length === 0) return { ok: true };
  const b = res.blocked;
  // TASK-10288 B — an INVOICED day gets its own words. The server answers
  // ticketed-or-clocked; which ticketed day is invoiced comes from the SAME
  // client classification the date-sync and the drag use
  // (_classifyDayAgainstTickets → TASK-9645's invoiced predicate). A failed
  // ticket read only loses the better wording — the save is refused either way.
  if (b.some(x => x && x.code === 'ticketed')) {
    const found = await _fetchLiveTicketsForJob(jobId);
    if (found.ok) {
      const inv = b.map(x => ({ x, c: x.code === 'ticketed' ? _classifyDayAgainstTickets(found.tickets, x.date) : null }))
        .filter(r => r.c && r.c.invoiced);
      if (inv.length) {
        const first = inv[0];
        const others = b.length - 1;
        return {
          ok: false,
          blocked: b,
          invoiced: true,
          message: `Dates not changed: ${_invoicedDayMoveMessage(first.x.label, first.c.invoiceRef)}`
            + (others > 0 ? ` (${others} other day${others === 1 ? '' : 's'} would also fall outside the job.)` : '')
            + ' Nothing was saved.',
        };
      }
    }
  }
  const listed = b.slice(0, 3).map(x => `${x.label} (${x.why})`).join('; ');
  const more = b.length > 3 ? ` +${b.length - 3} more` : '';
  const one = b.length === 1;
  return {
    ok: false,
    blocked: b,
    message: `Dates not changed: ${listed}${more} would fall outside the job. `
      + `A day with a ticket or someone clocked in stays inside the job's dates — keep ${one ? 'it' : 'them'} in, `
      + `or clear the ticket in Billing / clock them out first. Nothing was saved.`,
  };
}

async function _dateSyncServerDelete(tenant, jobId, doomed) {
  const ids = doomed.map(([, jd]) => jd.id);
  let deletedN = 0;
  const kept = [];
  try {
    // The server takes at most 200 ids per call (one transaction each); a longer
    // job goes in chunks (COD r2). Each chunk re-checks against committed data.
    for (let i = 0; i < ids.length; i += 200) {
      const res = (await httpsCallable(functions, 'removeJobDay')({
        tenantId: tenant, jobId, mode: 'date-sync', jobDayIds: ids.slice(i, i + 200), source: 'dispatch-form-datesync',
      }))?.data || null;
      if (!res || res.ok !== true) throw new Error('unexpected reply');
      const deleted = Array.isArray(res.deleted) ? res.deleted : [];
      const gone = new Set(deleted.map(x => x.jobDayId));
      if (gone.size) DispatchState.jobDays = DispatchState.jobDays.filter(d => !gone.has(d.id));
      deletedN += deleted.length;
      if (Array.isArray(res.kept)) kept.push(...res.kept);
    }
    return { deleted: deletedN, kept, failed: false };
  } catch (error) {
    // The creates already landed, so the worst case is extra days on their own
    // dates. A lost reply may still have committed (its audit row with it), so
    // don't claim either way.
    console.error('[saveJob] TASK-4793 B: date-sync delete failed', error);
    window.logError?.(error, { module: 'dispatch', phase: 'jobDays-datesync-server-delete', jobId, count: ids.length });
    const refused = /^functions\/(failed-precondition|permission-denied|not-found|invalid-argument)$/.test(String(error?.code || '')) && error?.message;
    setTimeout(() => window.showToast?.(refused
      ? `Job saved, but the ${ids.length} day${ids.length === 1 ? '' : 's'} outside the new dates ${ids.length === 1 ? 'was' : 'were'} not removed: ${error.message}`
      : `Job saved, but we couldn't confirm the ${ids.length} day${ids.length === 1 ? '' : 's'} outside the new dates ${ids.length === 1 ? 'was' : 'were'} removed — check the board.`,
    'warning', null, 12000), 700);
    return { deleted: deletedN, kept, failed: true };
  }
}

// TASK-4567 — the confirm that stands in front of saveJob's "delete jobDays
// outside new range" batch. It must LIST what is about to be destroyed (which
// day, whose crew, how many clocked hours) rather than ask "are you sure": the
// entire failure mode is that the loss was invisible. Mirrors the clock/hours
// warning deleteJobFromDetails already shows before a job delete.
// Returns true = go ahead and delete, false = keep the days.
async function _confirmJobDayDeletions(tenant, jobId, doomed) {
  // Hours: query by jobId ONLY (indexed, no composite — the same safe shape
  // deleteJobFromDetails and rescheduleJobDay use; a composite query here would
  // THROW and fail open) and bucket client-side by the clock-in's LOCAL day,
  // which is how the board pairs time to a jobDay.
  const hoursByDate = {};
  const activeByDate = {};
  let hoursUnknown = false;
  try {
    const clockSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId))
    );
    clockSnap.forEach(d => {
      const x = d.data();
      const ci = x.clockIn?.toDate?.();
      if (!ci) return;
      const key = formatDateISOLocal(ci);
      if (x.clockOut == null && x.status !== 'completed') activeByDate[key] = (activeByDate[key] || 0) + 1;
      else hoursByDate[key] = (hoursByDate[key] || 0) + (Number(x.totalHours) || 0);
    });
  } catch (err) {
    // Don't quietly present the days as empty — say the hours weren't checked.
    console.warn('[saveJob] TASK-4567 clock lookup for out-of-range jobDays failed:', err);
    hoursUnknown = true;
  }

  const fmtDay = (dateStr) => new Date(dateStr + 'T12:00:00')
    .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const crewOf = (data) => {
    const names = [...(data.drivers || []), ...(data.helpers || [])]
      .map(id => getPersonNameById(id))
      .filter(Boolean);
    if (!names.length) return 'no crew';
    return names.length > 3 ? `${names.slice(0, 3).join(', ')} +${names.length - 3}` : names.join(', ');
  };

  const MAX_LISTED = 6;
  const listed = doomed.slice(0, MAX_LISTED).map(([dateStr, jdDoc]) => {
    const data = jdDoc.data();
    const active = activeByDate[dateStr] || 0;
    const hrs = hoursByDate[dateStr] || 0;
    const hoursBit = hoursUnknown ? "hours couldn't be checked"
      : active ? `${active} still clocked in`
      : hrs > 0 ? `${hrs.toFixed(1)}h clocked`
      : 'no hours';
    return `• ${fmtDay(dateStr)} — ${crewOf(data)} · ${hoursBit}`;
  });
  if (doomed.length > MAX_LISTED) listed.push(`• …and ${doomed.length - MAX_LISTED} more`);

  const doomedKeys = new Set(doomed.map(([d]) => d));
  const totalHours = Object.entries(hoursByDate)
    .filter(([k]) => doomedKeys.has(k))
    .reduce((sum, [, v]) => sum + v, 0);
  const totalActive = Object.entries(activeByDate)
    .filter(([k]) => doomedKeys.has(k))
    .reduce((sum, [, v]) => sum + v, 0);

  const head = doomed.length === 1
    ? 'The new dates no longer cover 1 scheduled day, so saving DELETES it:'
    : `The new dates no longer cover ${doomed.length} scheduled days, so saving DELETES them:`;
  const tailParts = ['Their crew, trucks, trailers, assets, line items and load counts go with them.'];
  if (totalActive > 0) tailParts.push(`${totalActive} ${totalActive === 1 ? 'person is' : 'people are'} still clocked in on ${totalActive === 1 ? 'one of these days' : 'these days'}.`);
  if (totalHours > 0) tailParts.push(`${totalHours.toFixed(1)}h of clocked time stays on the timesheet for payroll but detaches from the job.`);
  if (hoursUnknown) tailParts.push("Clock hours couldn't be checked — assume there may be time on them.");
  tailParts.push("This can't be undone.");

  const message = `${head}   ${listed.join('   ')}   ${tailParts.join(' ')}`;

  if (typeof window.showConfirm !== 'function') {
    // Fail CLOSED: if the dispatcher can't be asked, don't destroy the days.
    console.warn('[saveJob] TASK-4567 showConfirm unavailable — keeping out-of-range jobDays');
    return false;
  }
  return await window.showConfirm('Delete scheduled days?', message, {
    confirmText: 'Delete Those Days',
    cancelText: 'Keep Them',
    danger: true
  });
}

// ── TASK-4780 ──────────────────────────────────────────────────────────────
// THE ROW FOR A jobDay THAT WAS ACTUALLY DESTROYED.
//
// Until this, `jobAuditLog` recorded a date-sync jobDay delete ONLY when it was
// REFUSED — `JOB_DAY_DELETE_BLOCKED_TICKETED` (a ticketed day pulled out of the
// delete set) and `JOB_DAY_DELETE_DECLINED` (the dispatcher pressed "Keep
// Them"). The path where the delete SUCCEEDS wrote nothing at all: there was no
// `JOB_DAY_DELETE` action anywhere in the repo. So the ordinary "edit a job's
// dates" flow — the one a dispatcher runs several times a day, not an attack —
// took the day's drivers, helpers, trucks, trailers, assets, assetQtys,
// assetDirections and lineItemSelections with it and left NOTHING to look at
// when somebody later asks "where did the crew go?". Job Activity showed a
// JOB_UPDATE and no more.
//
// 🔴 WHY THIS LOGS AND DOES NOT REFUSE — the decision, not an omission.
// `_confirmJobDayDeletions` (just above) ALREADY stops the save, names the
// first six doomed days with their crew and clocked hours (and counts the rest
// — MAX_LISTED = 6, not "every day"; COD Code Reviewer corrected that claim),
// warns about anyone still clocked in, and makes the dispatcher press "Delete
// Those Days". A human has been asked and has said yes — which is also why the
// row below records ALL of them, capped only at 60: the log is the complete
// record the dialog is not. Adding a second, unskippable "this day carries
// crew" refusal would fire on nearly EVERY real shorten — a scheduled day
// almost always has a driver on it — and it would land mid-edit with the job
// doc's own startDate/endDate ALREADY committed ~200 lines above, so the
// dispatcher would be looking at a job whose range moved and whose days did
// not, with no escape hatch inside the job form. That is precisely the trapped
// state TASK-4654 reasoned its way OUT of. Consent was never the missing piece.
// The RECORD was. An audit row is strictly additive and cannot trap anybody.
// (A refusal on top of the confirm is a separate product call for Chris: it
// changes what a dispatcher is ALLOWED to do, not what we remember.)
//
// SHAPE: deliberately the `JOB_DELETE` row's shape (`action`/`source`/
// `timestamp`/`user`/`jobId`/`jobTitle`/`customerName`/`jobDaysDeleted`/
// `jobDaysSnapshot`), so `openJobActivityLog`'s reader renders it with no
// special-casing and one query over `jobAuditLog` still answers "what happened
// to this job". `deletedDays` is the addition: ids resolved to the NAMES a
// support call is actually about, captured here because after the commit the
// jobDay rows are gone and a terminated driver drops out of DispatchState.
//
// 🔴 NOT fire-and-forget behind a bare catch. Firestore is NOT initialised with
// `ignoreUndefinedProperties` (firebase-config.js:131), so ONE undefined field
// rejects the WHOLE row — that is how an audit writer once produced 0 of 7,824
// rows with nothing appearing to throw. So: every field is coerced, undefined
// keys are counted and nulled before the write, the count is checked against
// the array it describes, and a failure is logged AND toasted. By the time this
// runs the days are already gone; silence is the one unacceptable outcome.
const _JOBDAY_AUDIT_SNAPSHOT_CAP = 60;

// Strip `undefined` at EVERY depth, without destroying types. Both COD code
// reviewers landed on the same break independently: an undefined nested inside
// `jobDaysSnapshot` rejects the single addDoc, and the date-sync then succeeds
// with zero audit row. A shallow pass was not enough.
//
// 🔴 The reason this is not `JSON.parse(JSON.stringify(x))` or a naive walk:
// only a PLAIN object may be rebuilt. A Firestore Timestamp / GeoPoint /
// DocumentReference has its own prototype, and recursing into one turns it into
// an anonymous map — the snapshot would still write, and every date inside it
// would silently stop being a date. So descend only when the prototype is
// exactly Object.prototype, and pass everything else through by identity.
//
// The cycle guard is belt-and-braces: Firestore `.data()` cannot return a cycle
// and every other value here is a literal built two lines up, so a cycle should
// be impossible — but this runs immediately AFTER the days were destroyed, and
// an infinite recursion there would freeze the tab at the worst possible
// moment. (COD Skeptic raised it, 2026-09-08.)
//
// 🔴 It tracks the CURRENT PATH, not everything ever seen, and the difference is
// load-bearing: one `Timestamp.now()` assigned to both `createdAt` and
// `updatedAt` is the SAME object twice, which a seen-set calls a cycle and
// nulls out — a shared reference is a DAG, not a loop. The first draft did
// exactly that and the tripwire caught it; hence `delete` on the way back up.
function _stripUndefinedDeep(v, path) {
  const _path = path || new WeakSet();
  if (Array.isArray(v)) {
    if (_path.has(v)) return null;   // true cycle — record a hole, never hang
    _path.add(v);
    const out = v.filter(x => x !== undefined).map(x => _stripUndefinedDeep(x, _path));
    _path.delete(v);
    return out;
  }
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    if (_path.has(v)) return null;
    _path.add(v);
    const out = {};
    for (const k of Object.keys(v)) {
      if (v[k] === undefined) continue;
      out[k] = _stripUndefinedDeep(v[k], _path);
    }
    _path.delete(v);
    return out;
  }
  return v;
}

async function _auditJobDayDateSyncDeletes(ctx) {
  const tenant = ctx?.tenant;
  const jobId = ctx?.jobId;
  const doomed = Array.isArray(ctx?.doomed) ? ctx.doomed : [];
  if (!tenant || doomed.length === 0) return false;

  const nameOfPerson = (id) => String(getPersonNameById(id) || id || '').trim();
  // Vehicle/trailer display name lives in `unit`/`nickname`, NOT `name` — a
  // lookup that reads `.name` renders blank for most of the fleet.
  const nameOfUnit = (list, id) => {
    const m = (list || []).find(x => x && x.id === id);
    if (!m) return String(id || '');
    return String(window.getUnitDisplay?.(m) || m.unit || m.nickname || m.name || id || '').trim();
  };

  // 🔴 Coerce to an array before ANY spread or `.map()`. A GAS-imported jobDay
  // can hold a bare name STRING where the UI writes an array (CLAUDE.md pitfall
  // #5), and `[...'Bob']` yields characters while `[...3]` THROWS. A throw here
  // would escape BEFORE the write's own try/catch and lose the row outright —
  // right after the days were destroyed, which is the exact silent loss this
  // whole change exists to end. Both COD code-reviewer lanes caught it
  // independently, 2026-09-08.
  const arr = (v) => (Array.isArray(v) ? v
    : (v === undefined || v === null || v === '' ? [] : [v]));

  const buildPayload = () => {
  const rows = doomed.map(([dateStr, jdDoc]) => {
    const d = (jdDoc && typeof jdDoc.data === 'function' ? jdDoc.data() : null) || {};
    const lis = d.lineItemSelections;
    return {
      date: String(dateStr || ''),
      jobDayId: String(jdDoc?.id || ''),
      status: String(d.status || ''),
      crew: [...arr(d.drivers), ...arr(d.helpers)].map(nameOfPerson).filter(Boolean),
      trucks: arr(d.trucks).map(id => nameOfUnit(DispatchState.trucks, id)).filter(Boolean),
      trailers: arr(d.trailers).map(id => nameOfUnit(DispatchState.trailers, id)).filter(Boolean),
      assets: arr(d.assets).map(id => nameOfUnit(DispatchState.assets, id)).filter(Boolean),
      lineItemCount: (lis && typeof lis === 'object') ? Object.keys(lis).length : 0,
    };
  });

  const fmtShort = (s) => {
    const dt = new Date(s + 'T12:00:00');
    return isNaN(dt.getTime()) ? String(s || '') : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  // The one line a support call reads first: which days, and who/what was on
  // them. `detail` is what the Job Activity reader renders under the row.
  const bits = rows.slice(0, 3).map(r => {
    const who = r.crew.length
      ? r.crew.slice(0, 2).join(', ') + (r.crew.length > 2 ? ` +${r.crew.length - 2}` : '')
      : 'no crew';
    const gear = [...r.trucks, ...r.trailers].slice(0, 2).join(', ');
    return `${fmtShort(r.date)} — ${who}${gear ? ` · ${gear}` : ''}`;
  });
  // TASK-4792 — the lead clause is the CALLER's, because the two paths destroy
  // days for different reasons and a row that says "outside the new dates" about
  // a hand-picked single-day removal is a false forensic statement. Everything
  // else about the row — shape, caps, coercion, retry — is identical on purpose.
  const detail = `${ctx?.detailLead || `Deleted ${rows.length} scheduled day${rows.length === 1 ? '' : 's'} outside the new dates`}: `
    + `${bits.join('; ')}${rows.length > 3 ? ` +${rows.length - 3} more` : ''}.`;

  const cap = _JOBDAY_AUDIT_SNAPSHOT_CAP;
  const payload = {
    action: 'JOB_DAY_DELETE',
    source: ctx?.source || 'dispatch-form-datesync',
    timestamp: Timestamp.now(),
    // Must equal the caller's own token email (or 'unknown' for an anonymous
    // session) or firestore.rules `jobAuditActorIsSelf` refuses the row. Same
    // expression every other jobAuditLog writer uses — do not "improve" it.
    user: window.AppState?.user?.email || 'unknown',
    jobId: jobId || null,
    jobTitle: ctx?.jobTitle || null,
    customerName: ctx?.customerName || null,
    detail,
    newRange: { start: ctx?.newRange?.start || null, end: ctx?.newRange?.end || null },
    // Dates and ids for the WHOLE set, always — small, and they are what says
    // which days died even when the detail below is capped.
    deletedDates: rows.map(r => r.date),
    deletedJobDayIds: rows.map(r => r.jobDayId),
    jobDaysDeleted: rows.length,
    deletedDays: rows.slice(0, cap),
    // The raw docs, whole — names go stale, ids do not. Same field name and
    // same `{id, ...data}` element shape as the JOB_DELETE writers, so one
    // recovery tool works on both rows.
    jobDaysSnapshot: doomed.slice(0, cap).map(([, jd]) => ({
      id: String(jd?.id || ''),
      ...(jd && typeof jd.data === 'function' ? jd.data() : {}),
    })),
    snapshotTruncated: rows.length > cap,
  };

  // COUNT, don't write blind — an audit writer once produced 0 of 7,824 rows
  // while nothing appeared to throw.
  if (payload.jobDaysDeleted !== payload.deletedDates.length) {
    console.error('[saveJob] TASK-4780: deleted-day count disagrees with the dates it names', {
      jobDaysDeleted: payload.jobDaysDeleted, dates: payload.deletedDates.length
    });
  }
  return payload;
  };

  let clean;
  try {
    clean = _stripUndefinedDeep(buildPayload());
  } catch (be) {
    // Belt to the `arr()` braces above: even the BUILDER can throw on a jobDay
    // shaped in a way nobody anticipated, and a throw here would escape the
    // write's try/catch and lose the row entirely. So fall back to a row built
    // from nothing but the ids and dates the caller already handed us — no
    // field reads at all. Who / when / which days is still most of the answer.
    console.error('[saveJob] TASK-4780: audit payload build failed — falling back to a minimal row', be, { jobId });
    window.logError?.(be, { module: 'dispatch', phase: 'jobday-delete-audit-build', jobId });
    clean = {
      action: 'JOB_DAY_DELETE',
      source: ctx?.source || 'dispatch-form-datesync',
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'unknown',
      jobId: jobId || null,
      jobTitle: ctx?.jobTitle || null,
      customerName: ctx?.customerName || null,
      detail: `${ctx?.detailLead || `Deleted ${doomed.length} scheduled day${doomed.length === 1 ? '' : 's'} outside the new dates`}. `
        + 'The crew and equipment on them could not be read back off the deleted rows.',
      newRange: { start: ctx?.newRange?.start || null, end: ctx?.newRange?.end || null },
      deletedDates: doomed.map(([dt]) => String(dt || '')),
      deletedJobDayIds: doomed.map(([, jd]) => String(jd?.id || '')),
      jobDaysDeleted: doomed.length,
      deletedDays: [],
      jobDaysSnapshot: [],
      snapshotTruncated: false,
      detailBuildFailed: true,
    };
  }

  const write = async (body, label) => {
    const ref = await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), body);
    console.log(`[saveJob] TASK-4780: JOB_DAY_DELETE row written (${label})`, {
      auditId: ref.id, jobId, jobDaysDeleted: body.jobDaysDeleted, dates: body.deletedDates
    });
    return true;
  };

  try {
    return await write(clean, 'full');
  } catch (ae) {
    // The snapshot is the big, unvalidated half of this row and the only part
    // that can plausibly refuse (size, an exotic value, a rules edge). Losing
    // it is survivable; losing WHO/WHEN/WHICH-DAYS is not. So retry once
    // without it rather than let the whole record evaporate — the fallback row
    // still names the actor, the job, every deleted date and the crew that was
    // on each day. (Both COD code-reviewer lanes flagged the all-or-nothing
    // write, 2026-09-08.)
    console.error('[saveJob] TASK-4780: full JOB_DAY_DELETE row refused — retrying slim', ae, { jobId });
    const slim = { ...clean, jobDaysSnapshot: [], snapshotDropped: true, snapshotDropReason: String(ae?.code || ae?.message || 'unknown') };
    try {
      await write(slim, 'slim — snapshot dropped');
      window.logError?.(ae, {
        module: 'dispatch', phase: 'jobday-delete-audit-snapshot-dropped', jobId,
        jobDaysDeleted: clean.jobDaysDeleted
      });
      return true;
    } catch (ae2) {
      // Now the days are gone and there is no record. Say it out loud in all
      // three places — console, error telemetry, and the dispatcher's screen.
      // Never a bare catch: silence here is exactly the defect this closes.
      console.error('[saveJob] TASK-4780: JOB_DAY_DELETE audit write FAILED (slim retry too)', ae2, { jobId });
      window.logError?.(ae2, {
        module: 'dispatch', phase: 'jobday-delete-audit', jobId,
        jobDaysDeleted: clean.jobDaysDeleted, firstError: String(ae?.code || ae?.message || '')
      });
      setTimeout(() => window.showToast?.(
        `${clean.jobDaysDeleted} scheduled day${clean.jobDaysDeleted === 1 ? '' : 's'} deleted, but the history record didn't save — note the dates now if you may need to trace this later.`,
        'warning'), 1200);
      return false;
    }
  }
}

async function addDayInstead() {
  if (!_guardManageDays()) return;
  if (!_duplicateMatchJobId) return;
  const jobId = _duplicateMatchJobId;

  // Use the start date from the form as the new day
  const dateStr = document.getElementById('job-start-date').value;
  if (!dateStr) {
    window.showToast?.('Set a start date first', 'error');
    return;
  }

  // 🔴 TASK-9233 — this is the dupe gate's SIBLING and it had the same defect:
  // it read only `job-start-date`, seeded the new day from the MATCHED job's
  // latest day, and then let openNewDayCrewEditor's closeModal('job') discard
  // the form the dispatcher had just built. Same form, same loss — fixed in the
  // same pass, through the same shared helpers, so the two cannot drift again.
  // (The two BOARD paths, _quickAddDay and addDayToJob, are deliberately NOT
  // changed: they run with no job form open and must keep inheriting.)
  const formSeed = _readJobFormDaySeed();
  const _projCarry = _addDayProjectCarry(DispatchState.jobs.find(j => j.id === jobId), _formProjectContext()); // TASK-9289 S2
  const endDateStr = document.getElementById('job-end-date')?.value || '';
  const { dates, truncated } = _dupeAddDayDateRange(dateStr, endDateStr);
  if (!dates.length) {
    window.showToast?.('Set a start date first', 'error');
    return;
  }

  // TASK-4567 step 3 — a far-past day writes fine but renders nowhere until
  // "Load older". Ask BEFORE anything is claimed or written, against the
  // earliest date in the range.
  if (!await _confirmFarPastAddDay(dates[0])) return;

  const btn = document.getElementById('btn-add-day-instead');
  const btnOriginal = btn?.innerHTML;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Adding...'; }

  try {
    // TASK-1331 double-add guard + TASK-1111 crew-carry builder + TASK-4567
    // range write, all per date, inside the shared writer.
    const result = await _addDaysFromJobForm(jobId, dates, formSeed, 'dispatch-form-warning');
    const job = DispatchState.jobs.find(j => j.id === jobId);

    if (!result.created.length) {
      // Nothing written → the form stays open, exactly as in _dupeBlockAddDay.
      // Same deliberate divergence from TASK-1331: opening the already-scheduled
      // day's editor here would closeModal('job') and destroy a form the
      // dispatcher is still filling in.
      if (result.failed) {
        window.showToast?.('Could not add the day — nothing changed. Your job is still here.', 'error', null, 9000);
      } else if (result.skipped.length) {
        const label = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        window.showToast?.(
          `"${job?.title || 'That job'}" is already scheduled for ${result.skipped.map(s => label(s.date)).join(', ')} — nothing to add.`,
          'info', null, 9000);
      }
      return;
    }

    _reportFormAddDayResult(job?.title, result, truncated);
    await _carryProjectLinkAfterAddDay(jobId, _projCarry, 'dupe-warning-add-day');
    // TASK-1331 — closes the job modal and fires the carried-crew DOT advisory,
    // which now runs against the FORM's crew because that is what was written.
    await openNewDayCrewEditor(jobId, result.created[0].dayId, result.created[0].data);
  } catch (error) {
    console.error('[Dispatch] Add day instead error:', error);
    window.showToast?.('Error adding day', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
  }
}

// ============================================
// TASK-273: Pre-save dupe-detect blocking modal
// ============================================
// Shown by saveJob when a recent (<60min) similar-title job exists for the
// same customer. Three actions: Add Day to Existing Job (primary), Create
// Separate Job (sets _dupeBypassConfirmed + re-fires save), Cancel.
function showJobDuplicateBlockingModal(matchJob, startDateVal, createSeparateCallback) {
  // Remove any prior instance
  const existing = document.getElementById('job-dupe-block-modal');
  if (existing) existing.remove();

  const customer = getCustomerById(matchJob.customerId);
  const customerName = customer?.name || 'this customer';
  const matchTitle = matchJob.title || 'this job';
  // TASK-493: align timestamp source with the gate's filter (updatedAt || createdAt,
  // ~L6710) so a recently-edited older job isn't mislabeled between the two copies.
  const tsRef = matchJob.updatedAt || matchJob.createdAt;
  const ts = tsRef?.toDate?.()?.getTime?.() || (tsRef ? new Date(tsRef).getTime() : Date.now());
  const minsAgo = Math.max(1, Math.round((Date.now() - ts) / 60000));
  // The gate now also fires for recurring work (exact title match on an older open
  // job, no recency window). "X minutes ago" only makes sense for the rapid
  // double-submit case — branch the lead copy on whether the match is recent.
  const isRecentMatch = (Date.now() - ts) <= 60 * 60 * 1000;

  // Find last scheduled date for the matched job
  const jobDays = DispatchState.jobDays
    .filter(d => d.jobId === matchJob.id)
    .sort((a, b) => {
      const da = a.date?.toDate?.() || new Date(a.date);
      const db2 = b.date?.toDate?.() || new Date(b.date);
      return db2 - da;
    });
  const lastDay = jobDays[0];
  const lastDateStr = lastDay
    ? (lastDay.date?.toDate?.() || new Date(lastDay.date)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  // TASK-9233 — label the WHOLE range the form is about to add, not just its
  // first day. The old copy named one date while the button silently wrote one
  // day and dropped the rest, so the modal itself was part of the surprise.
  const _dupeEndVal = document.getElementById('job-end-date')?.value || '';
  const _dupeRange = _dupeAddDayDateRange(startDateVal, _dupeEndVal);
  const _fmtDay = (d, withWeekday) => d.toLocaleDateString('en-US',
    withWeekday ? { weekday: 'short', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' });
  const startLabel = _dupeRange.dates.length
    ? (_dupeRange.dates.length === 1
        ? _fmtDay(_dupeRange.dates[0], true)
        : `${_fmtDay(_dupeRange.dates[0], false)}–${_fmtDay(_dupeRange.dates[_dupeRange.dates.length - 1], false)} (${_dupeRange.dates.length} days)`)
    : '';

  // TASK-9233 — what the dispatcher has STAGED in the open form, so the modal can
  // promise it by name. This is the same seed the write uses, read the same way,
  // so the copy cannot claim something the write does not do.
  const _stagedSeed = _readJobFormDaySeed();
  const _stagedParts = [];
  if (_stagedSeed) {
    const n = (arr, one, many) => (arr || []).length ? `${arr.length} ${arr.length === 1 ? one : many}` : null;
    [n(_stagedSeed.drivers, 'driver', 'drivers'), n(_stagedSeed.helpers, 'helper', 'helpers'),
     n(_stagedSeed.trucks, 'truck', 'trucks'), n(_stagedSeed.trailers, 'trailer', 'trailers'),
     n(_stagedSeed.assets, 'equipment item', 'equipment items')]
      .filter(Boolean).forEach(s => _stagedParts.push(s));
  }

  const escapeHtml = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // TASK-1579 — mirror the inline focus-out warning: surface the matched job's PO so
  // this modal gives the SAME guidance about the same dup (COD note #1 — the two
  // surfaces must not diverge). PO drives the invoice; "no PO set" only when THIS
  // customer requires a PO. escapeHtml here because this surface IS HTML (the inline
  // warning is textContent).
  const needsPO = Array.isArray(customer?.requiredFields) && customer.requiredFields.includes('poNumber');
  const po = jobPoLabel(matchJob); // TASK-9811 S2
  const metaParts = [];
  if (lastDateStr) metaParts.push(`scheduled through ${escapeHtml(lastDateStr)}`);
  if (po) metaParts.push(`PO: ${escapeHtml(po)}`);
  else if (needsPO) metaParts.push('no PO set');
  const metaStr = metaParts.length ? ` (${metaParts.join(' · ')})` : '';

  // TASK-9289 S2 — read the form's project NOW (the form is still open).
  const _carry = _addDayProjectCarry(matchJob, _formProjectContext());
  const _carryText = _addDayProjectCarryText(_carry, matchTitle);
  const _sameProject = _carry.kind === 'same';
  const _projName = _carry.project?.name || 'this project';

  const modalHtml = `
    <div id="job-dupe-block-modal" class="modal" style="display:flex;align-items:center;justify-content:center;position:fixed;inset:0;background:rgba(15,23,42,0.55);z-index:10000;padding:16px;">
      <div class="modal-content" style="max-width:480px;width:100%;background:var(--surface,#fff);border-radius:12px;box-shadow:0 20px 50px rgba(0,0,0,0.25);padding:24px;">
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:8px;">
          <span class="material-icons" style="color:#F59E0B;font-size:28px;">warning</span>
          <h3 style="margin:0;font-size:18px;color:var(--text-primary);">${_sameProject ? `“${escapeHtml(matchTitle)}” is already in ${escapeHtml(_projName)}` : 'Did you mean to add a day?'}</h3>
        </div>
        <p style="margin:8px 0 16px;color:var(--text-primary);line-height:1.5;">
          ${isRecentMatch
            ? `You created <strong>"${escapeHtml(matchTitle)}"</strong> for <strong>${escapeHtml(customerName)}</strong> ${minsAgo} minute${minsAgo === 1 ? '' : 's'} ago${metaStr}.`
            : `A job named <strong>"${escapeHtml(matchTitle)}"</strong> already exists for <strong>${escapeHtml(customerName)}</strong>${metaStr}.`}
        </p>
        <p style="margin:0 0 20px;color:var(--text-secondary);font-size:14px;line-height:1.5;">
          Did you mean to add ${startLabel ? `<strong>${escapeHtml(startLabel)}</strong>` : 'another day'} to that job, or is this a separate job?
          <!-- TASK-1331: same crew clause as the focus-out warning — this modal is the
               other place the "a new day would rewrite my old crew" belief gets acted on. -->
          <br>Each day keeps its own crew, and the days already scheduled won't change.
          <!-- TASK-9233: the modal used to promise "you'll set this day's crew next"
               and say nothing about the form being discarded, while the day it wrote
               was a clone of the matched job's last day. Now it names what actually
               carries, what cannot, and the billing consequence — that last sentence
               is what stops the next dispatcher building a duplicate job at all.
               Worded as "bills separately by date", NOT "one ticket": ticket
               splitting has its own settings in clock.js and adding a day creates
               no ticket by itself. -->
          <br><br>${_stagedParts.length
            ? `The <strong>${escapeHtml(_stagedParts.join(', '))}</strong> you just assigned will be used for ${_dupeRange.dates.length > 1 ? 'these days' : 'this day'}.`
            : `${_dupeRange.dates.length > 1 ? 'These days' : 'This day'} will be added with no crew assigned — you can set it next.`}
          <br>This work bills separately by date under the same job.
        </p>
        <p style="margin:-8px 0 20px;color:var(--text-secondary);font-size:13px;line-height:1.5;">
          The title, PO, notes and destinations on this form belong to a job — they can't move onto
          <strong>"${escapeHtml(matchTitle)}"</strong> without rewriting it. Choose
          <em>${_sameProject ? 'new job in this project' : 'create a separate job'}</em> if you need those.
        </p>
        ${_carryText ? `<p id="job-dupe-block-project" style="margin:-8px 0 20px;color:var(--text-primary);font-size:13px;line-height:1.5;"><span class="material-icons" style="font-size:16px;vertical-align:-3px;color:#0369a1;">folder_special</span> ${escapeHtml(_carryText)}</p>` : ''}
        <div style="display:flex;flex-direction:column;gap:8px;">
          <!-- TASK-9289 S2 — inside the form's own project both answers are normal,
               so they get EQUAL weight; outside it Add Day stays primary (TASK-273). -->
          <button type="button" id="job-dupe-block-addday" class="btn ${_sameProject ? 'btn-secondary' : 'btn-primary'}" style="width:100%;min-height:44px;justify-content:center;">
            <span class="material-icons">add_circle</span> ${_sameProject ? `Add a day to “${escapeHtml(matchTitle)}”` : 'Add Day to Existing Job'}
          </button>
          <button type="button" id="job-dupe-block-separate" class="btn btn-secondary" style="width:100%;min-height:44px;justify-content:center;">
            ${_sameProject ? `<span class="material-icons">create_new_folder</span> New job in ${escapeHtml(_projName)}` : 'No, create a separate job'}
          </button>
          <button type="button" id="job-dupe-block-cancel" class="btn btn-text" style="width:100%;min-height:44px;justify-content:center;color:var(--text-secondary);">
            Cancel — let me edit this
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  const modal = document.getElementById('job-dupe-block-modal');

  const cleanup = () => { modal?.remove(); };

  document.getElementById('job-dupe-block-addday').onclick = async () => {
    const btn = document.getElementById('job-dupe-block-addday');
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Adding day...';
    try {
      // TASK-4567 — only tear down the dupe modal and the job form when the add
      // actually HAPPENED. _dupeBlockAddDay returns false when the dispatcher backs
      // out of the far-past warning; closing on that would throw away the job form
      // they just filled in as the price of reading a warning.
      //
      // 🔴 TASK-9233 — require an explicit `true`. `=== false` let TWO live
      // return paths through as if they had succeeded: `!_guardManageDays()`
      // returned `undefined`, so a dispatcher without manage-days permission lost
      // the form to a refusal; and the already-scheduled branch returned `true`
      // without writing anything. Anything that is not an explicit success keeps
      // the form open.
      const _added = await _dupeBlockAddDay(matchJob.id, startDateVal);
      if (_added !== true) {
        btn.disabled = false;
        btn.innerHTML = _sameProject
          ? `<span class="material-icons">add_circle</span> Add a day to “${escapeHtml(matchTitle)}”`
          : '<span class="material-icons">add_circle</span> Add Day to Existing Job';
        return;
      }
      cleanup();
      document.getElementById('modal-job').classList.add('hidden');
    } catch (err) {
      console.error('[Dispatch] dupe-block add-day failed:', err);
      window.showToast?.('Error adding day', 'error');
      btn.disabled = false;
      btn.innerHTML = _sameProject
        ? `<span class="material-icons">add_circle</span> Add a day to “${escapeHtml(matchTitle)}”`
        : '<span class="material-icons">add_circle</span> Add Day to Existing Job';
    }
  };

  document.getElementById('job-dupe-block-separate').onclick = () => {
    // TASK-4660 — the JOB_CREATE_DUPLICATE_BYPASS audit row USED TO BE WRITTEN
    // HERE, at click time. It has moved to saveJob's create branch, next to
    // JOB_CREATE. Two reasons, both learned from the live data:
    //   1. A click-time row lands even when the dispatcher then abandons or
    //      fails the save, so no duplicate job was ever created. That inflates
    //      the count (~65 rows on TITS) with events that produced nothing, which
    //      is why the count could not be trusted as a coaching signal.
    //   2. It could not name the job that got created — it did not exist yet.
    //      Attribution needs BOTH ids: the Tufto pair (8/24 + 8/26, identical
    //      titles, same PO 526 192 RMEB) is unprovable without the link, and
    //      jobAuditLog is `allow update: if false` so the row cannot be
    //      back-stamped later. It has to be written after the create.
    // Same action name, same collection, superset of the same fields — the
    // history viewer's 'Dup override' label (:~24323) and the existing rows keep
    // working. minsAgo is handed to the callback so the moved row keeps it.
    cleanup();
    createSeparateCallback(minsAgo);
  };

  document.getElementById('job-dupe-block-cancel').onclick = () => {
    cleanup();
    // Leave the job form open + bypass flag false so dispatcher can edit
    // title/customer/date and re-save (gate re-evaluates with new values).
  };
}

// TASK-273: Helper for showJobDuplicateBlockingModal "Add Day to Existing Job"
// path. Creates a jobDay on the matched jobId for the form's start-date,
// inherits crew/asset selections from the latest existing jobDay, and extends
// job.endDate if the new date is past current end. Mirrors addDayInstead's
// write logic but driven by the blocking modal instead of focus-out warning.
// 🔴 TASK-9233 — THE RETURN VALUE IS THE DATA-LOSS CONTRACT ON THIS PATH.
// The caller tears down the dupe modal AND #modal-job on anything that is not an
// explicit refusal, so every exit that writes NOTHING must return false or the
// dispatcher's whole build goes with it. Two exits were getting this wrong:
// `!_guardManageDays()` returned `undefined`, and the already-scheduled branch
// returned `true`. Both closed the form after writing nothing.
async function _dupeBlockAddDay(jobId, startDateVal) {
  // TASK-1408 (COD Code Reviewer ❌) — this writes jobDays WITHOUT passing through
  // the three named add-day entry points, so it needs its own guard or the trap
  // survives on this path.
  // 🔴 TASK-9233 — `return false`, not a bare `return`. A view-and-upload member
  // who tripped the gate lost the form to an `undefined` the caller read as
  // success. Same bug as TASK-4567's far-past return, on a second trigger, live.
  if (!_guardManageDays()) return false;
  if (!startDateVal) {
    window.showToast?.('Set a start date first', 'error');
    throw new Error('no-start-date');
  }

  // 🔴 Read the form BEFORE anything can close it. openNewDayCrewEditor calls
  // closeModal('job') and the caller hides #modal-job; after either runs there is
  // nothing left to read. This is the line that makes the dispatcher's staged
  // crew/trucks/trailers/equipment land on the new day instead of being replaced
  // by a clone of the matched job's last day.
  const formSeed = _readJobFormDaySeed();
  // TASK-9289 S2 — and the form's project, for the same reason.
  const _projCarry = _addDayProjectCarry(DispatchState.jobs.find(j => j.id === jobId), _formProjectContext());
  // The end date is read here rather than passed in: saveJob hands us only the
  // start, and the form is still open and authoritative at this point.
  const endDateVal = document.getElementById('job-end-date')?.value || '';
  const { dates, truncated } = _dupeAddDayDateRange(startDateVal, endDateVal);
  if (!dates.length) {
    window.showToast?.('Set a start date first', 'error');
    return false;
  }

  // TASK-4567 step 3 — far-past warning before anything is claimed or written,
  // asked against the EARLIEST date in the range (the one that decides whether
  // any of this renders on the board).
  // 🔴 TASK-4567 (COD Code Reviewer, caught at integration) — return FALSE, not
  // undefined. The caller cleans up the dupe modal AND hides #modal-job on any
  // normal return, so a dispatcher who backs out of the far-past warning would
  // lose the entire job form they had just filled in — no day added and the work
  // gone. A warning you can decline has to leave you where you were.
  if (!await _confirmFarPastAddDay(dates[0])) return false;

  // TASK-1331 — the double-add guard lives inside _addDaysFromJobForm's
  // per-date claim (this path once had none: a fast double-click on the blocking
  // modal's primary button wrote two jobDays for one date).
  const result = await _addDaysFromJobForm(jobId, dates, formSeed, 'dispatch-form-dupe-gate');
  const job = DispatchState.jobs.find(j => j.id === jobId);

  if (!result.created.length) {
    // 🔴 Nothing was written, so the form MUST survive. This is the branch where
    // a dispatcher would otherwise lose the build AND get no day.
    //
    // DELIBERATE DIVERGENCE FROM TASK-1331. That task made an already-scheduled
    // date open the existing day's crew editor rather than dead-end the
    // operator. On the BOARD paths that is still right and is unchanged. Here it
    // is not: opening that editor runs closeModal('job') and destroys a form the
    // dispatcher just filled in, to show them a day they did not ask to edit.
    // They are not dead-ended — the form is still up, so they can change the date
    // and re-save, or take "create a separate job".
    if (result.failed) {
      window.showToast?.('Could not add the day — nothing changed. Your job is still here.', 'error', null, 9000);
    } else if (result.skipped.length) {
      const label = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      window.showToast?.(
        `"${job?.title || 'That job'}" is already scheduled for ${result.skipped.map(s => label(s.date)).join(', ')} — nothing to add. Change the date, or create a separate job.`,
        'info', null, 9000);
    }
    return false;
  }

  // Audit AFTER the writes, so the row describes what actually landed rather
  // than what was attempted.
  try {
    await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
      action: 'JOB_DAY_ADD_VIA_DUPE_GATE',
      source: 'dispatch-form',
      timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'unknown',
      jobId: jobId,
      // Unchanged name and shape so the existing rows and the history viewer's
      // 'Day added' label keep working. It is now the first date CREATED, which
      // for a single-day add is identical to what this always wrote.
      addedDate: formatDateISOLocal(result.created[0].date),
      // TASK-9233 — the fields that make the next report diagnosable in minutes
      // instead of by reading prod jobDays. This was the one path that destroyed
      // work and logged nothing about what was lost. Every value coerced to
      // non-undefined: one `undefined` silently drops the whole write
      // ([[lesson_audit_writes_fail_silently_on_undefined]]).
      addedDates: result.created.map(c => formatDateISOLocal(c.date)),
      skippedDates: result.skipped.map(s => formatDateISOLocal(s.date)),
      requestedStart: startDateVal || null,
      requestedEnd: endDateVal || null,
      rangeTruncated: !!truncated,
      seededFromForm: !!formSeed,
      seedDrivers: formSeed ? (formSeed.drivers || []).length : 0,
      seedHelpers: formSeed ? (formSeed.helpers || []).length : 0,
      seedTrucks: formSeed ? (formSeed.trucks || []).length : 0,
      seedTrailers: formSeed ? (formSeed.trailers || []).length : 0,
      seedAssets: formSeed ? (formSeed.assets || []).length : 0,
      writeFailures: result.failed
    });
  } catch (ae) { console.warn('[Dispatch] dupe-gate add-day audit log failed:', ae); }

  _reportFormAddDayResult(job?.title, result, truncated);
  await _carryProjectLinkAfterAddDay(jobId, _projCarry, 'dupe-gate-add-day');
  // TASK-1331 — land in the new day's crew editor (also removes this blocking modal
  // and the job form, so the editor isn't opened underneath either of them).
  // TASK-9233 — the data handed here is now the FORM's crew, so
  // warnCarriedCrewDotIssues fires against what was actually written. Seeding the
  // day from the form without moving this would have shipped an expired-CDL
  // driver un-warned (§391).
  await openNewDayCrewEditor(jobId, result.created[0].dayId, result.created[0].data);
  return true;
}

// ============================================
// DRAG TO RESCHEDULE (Week + Month Views)
// ============================================
let _dragJobId = null;
let _dragJobDayId = null;
let _dragSourceDate = null;

function setupDragHandlers() {
  const content = document.getElementById('dispatch-content');
  if (!content) return;

  content.addEventListener('dragstart', (e) => {
    // Support both week (.job-card) and month (.month-job-indicator) draggables
    const card = e.target.closest('[draggable="true"][data-job-day-id]');
    if (!card) return;
    const jobDayId = card.dataset.jobDayId;
    if (jobDayId && jobDayId.startsWith('legacy-')) {
      e.preventDefault();
      window.showToast?.('Legacy job — use Add Day to schedule on a new date', 'info');
      return;
    }
    _dragJobId = card.dataset.jobId;
    _dragJobDayId = jobDayId;
    _dragSourceDate = card.dataset.date;
    card.classList.add('dragging');
    // Mark parent so sibling cards suppress hover transforms during drag
    const dayCol = card.closest('.week-day');
    if (dayCol) dayCol.classList.add('is-dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', _dragJobDayId);
    // Set explicit drag image to only show this one card (prevents ghost of siblings)
    try {
      const rect = card.getBoundingClientRect();
      e.dataTransfer.setDragImage(card, e.clientX - rect.left, e.clientY - rect.top);
    } catch (_) { /* fallback to browser default */ }
  });

  // TASK-10008 — tap empty space in a week-view day column → Add Job on that day.
  // (.week-day columns exist only in the week view, so no viewMode check.)
  content.addEventListener('click', (e) => {
    const dateStr = _weekEmptyTapDate(e.target, Date.now());
    if (!dateStr || !_calendarAddJobMode()) return;
    openAddJobWithDate(dateStr);
  });

  content.addEventListener('dragend', (e) => {
    _weekDragEndedAt = Date.now(); // TASK-10008 — a drop is never an empty-day tap
    const card = e.target.closest('[draggable="true"]');
    if (card) card.classList.remove('dragging');
    document.querySelectorAll('.week-day.drag-over, .month-day.drag-over').forEach(d => d.classList.remove('drag-over'));
    document.querySelectorAll('.week-day.is-dragging').forEach(d => d.classList.remove('is-dragging'));
    _dragJobId = null;
    _dragJobDayId = null;
    _dragSourceDate = null;
  });

  content.addEventListener('dragover', (e) => {
    // Support both week-day and month-day as drop targets
    const dayCol = e.target.closest('.week-day[data-date], .month-day[data-date]');
    if (!dayCol || !_dragJobDayId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    document.querySelectorAll('.week-day.drag-over, .month-day.drag-over').forEach(d => d.classList.remove('drag-over'));
    if (dayCol.dataset.date !== _dragSourceDate) {
      dayCol.classList.add('drag-over');
    }
  });

  content.addEventListener('dragleave', (e) => {
    const dayCol = e.target.closest('.week-day[data-date], .month-day[data-date]');
    if (dayCol && !dayCol.contains(e.relatedTarget)) {
      dayCol.classList.remove('drag-over');
    }
  });

  content.addEventListener('drop', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const dayCol = e.target.closest('.week-day[data-date], .month-day[data-date]');
    document.querySelectorAll('.week-day.drag-over, .month-day.drag-over').forEach(d => d.classList.remove('drag-over'));
    if (!dayCol || !_dragJobDayId) return;

    const newDateStr = dayCol.dataset.date;
    if (newDateStr === _dragSourceDate) return;

    // Capture and clear drag state immediately to prevent duplicate drops
    const jobDayId = _dragJobDayId;
    const jobId = _dragJobId;
    _dragJobDayId = null;
    _dragJobId = null;
    _dragSourceDate = null;

    await rescheduleJobDay(jobDayId, jobId, newDateStr);
  });
}

async function rescheduleJobDay(jobDayId, jobId, newDateStr) {
  const newDate = new Date(newDateStr + 'T12:00:00');
  // TASK-10288 fold 1 — one day per date per job. Checked BEFORE the 811 gate so
  // a move that will be refused never asks about a locate first. Reads the job's
  // days LIVE (the board array is windowed); a failed read refuses (fail closed,
  // same stance as the ticket guard). The live list also feeds the range recalc.
  const _dup = await _jobDayMoveDateClash(jobId, jobDayId, newDateStr);
  if (!_dup.ok) { window.showToast?.(_dup.message, 'error'); return; }
  if (!(await _p811GateDay(jobId, newDateStr, 'move-day'))) return; // TASK-9862 — expired-locate hard stop
  const tenant = DispatchState.tenantId;

  try {
    // Get the jobDay's current date to check for clock/billing data on that day.
    // TASK-10288 fold 3 — resolved anywhere: right-click → Move Day reaches this
    // from SEARCH-rendered cards too, whose days are not in DispatchState.jobDays.
    const jobDay = _resolveJobDayAnywhere(jobDayId);
    // TASK-4651 — without the source row there is no source DATE, and with no
    // source date BOTH checks below go inert: the clock comparison runs against
    // an Invalid Date (every compare false) and the ticket join has no day key
    // to match on. That is the same fail-open in a different costume, so refuse
    // rather than move a day we cannot identify. Cards render from
    // DispatchState.jobDays and virtual `legacy-` ids are already turned away at
    // dragstart, so this is the stale-state path, not a routine one.
    if (!jobDay) {
      console.warn('[Dispatch] TASK-4651: jobDay not in state — refusing the move', { jobDayId, jobId });
      window.showToast?.("Couldn't read that day — refresh and try again.", 'error');
      return;
    }
    const sourceDate = jobDay?.date?.toDate?.() || new Date(jobDay?.date);
    const dayStart = new Date(sourceDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(sourceDate);
    dayEnd.setHours(23, 59, 59, 999);

    // Clock entries: query by jobId only (no composite index needed), filter by
    // date client-side. clockIn IS a real Timestamp (500 of 500 sampled live),
    // so this half has always worked and is deliberately unchanged.
    const clockSnap = await getDocs(
      query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId))
    );
    const hasClockEntries = clockSnap.docs.some(d => {
      const clockIn = d.data().clockIn?.toDate?.();
      return clockIn && clockIn >= dayStart && clockIn <= dayEnd;
    });

    // TASK-4651 — the ticket half was a LIVE FAIL-OPEN. It read
    // `d.data().workDate?.toDate?.()`, but workDate is a 'YYYY-MM-DD' STRING on
    // 899 of 903 live tickets and a Timestamp on ZERO of them. `.toDate` is
    // undefined on a string, so `?.()` yielded undefined, `hasTickets` was
    // ALWAYS false, and this guard had never once fired since it shipped —
    // silently, nothing thrown, the check reading as "working" the whole time.
    // It also queried jobId only, going blind on the ~11% of tickets whose
    // originalJobId differs. Both are fixed by routing through the shared
    // matcher; see _checkTicketedDayRescheduleGuard for why the policy is a
    // refusal and not the crew guard's confirm.
    // TASK-10288 B — the ticket guard now runs on a clocked day too: an invoiced
    // day almost always has clock time, and its refusal must say "invoice", not
    // "clock entries". Same guard, same refusal; only the words change.
    const tGuard = await _checkTicketedDayRescheduleGuard(jobId, jobDay);

    if (hasClockEntries || (tGuard && tGuard.blocked)) {
      const job = DispatchState.jobs.find(j => j.id === jobId);
      // The job label is a nicety; the REFUSAL is not. The old code hid the
      // whole dialog behind `job && ...`, so a drag on a job that had fallen out
      // of the 90-day board array refused the move with no message at all —
      // indistinguishable from a broken drag.
      const jobLabel = job?.title ? `"${job.title}"` : 'this job';
      const targetLabel = newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      let why;
      if (tGuard && tGuard.blocked && tGuard.invoiced) {
        why = _invoicedDayMoveMessage(tGuard.label, tGuard.invoiceRef);
      } else if (hasClockEntries) {
        why = `Clock entries exist for ${jobLabel} on this day, so it can't be moved.`;
      } else if (tGuard.failedCheck) {
        // Not a refusal we can offer an alternative to — we do not know what is
        // there. Say exactly that instead of implying the day is ticketed.
        window.showToast?.(
          `Couldn't check whether ${tGuard.label} is already ticketed — the day was not moved. Check your connection and try again.`,
          'error'
        );
        console.warn('[Dispatch] TASK-4651: reschedule refused — ticket check could not run', { jobId, jobDayId });
        return;
      } else if (tGuard.billed) {
        why = `${tGuard.label} is already billed (${tGuard.detail}), so it can't be moved. The ticket keeps its own date and would be orphaned. Unarchive it in Billing if the date is wrong.`;
      } else {
        why = `${tGuard.label} already has a ${tGuard.detail}, so it can't be moved. The ticket does NOT move with the day and would be left on the old date.`;
      }
      console.warn('[Dispatch] TASK-4651: reschedule refused', {
        jobId, jobDayId,
        reason: tGuard?.invoiced ? 'invoiced-ticket' : hasClockEntries ? 'clock-entries' : (tGuard.billed ? 'billed-ticket' : 'open-ticket'),
        detail: tGuard?.detail || null
      });
      // Offer to add a day on the target date instead — the correct act, since
      // the ticketed/clocked work belongs to the ORIGINAL date.
      if (await window.showConfirm?.('Can\'t Move', `${why}\n\nAdd a new day on ${targetLabel} instead?`, { confirmText: 'Add Day' })) {
        await _quickAddDay(jobId, newDate);
      }
      return;
    }

    // Check if doc exists (legacy jobDays may be virtual)
    const jdRef = doc(db, 'tenants', tenant, 'jobDays', jobDayId);
    const jdSnap = await getDoc(jdRef);
    if (!jdSnap.exists()) {
      window.showToast?.('Legacy job day — use Add Day instead', 'error');
      return;
    }

    // TASK-4685 — the drag is the ONE path of the three where the gesture is
    // genuinely ambiguous AND we can still stop without leaving anything
    // half-written (nothing has been committed yet at this point, unlike the
    // saveJob shift where the job's date range is already on disk). Two readings
    // of "drag a struck-through card onto Monday": (a) put this job back to work
    // on Monday — by far the common one, and the one Chris hit; (b) I cancelled
    // the wrong day and I am moving the CANCELLATION. Reactivating silently
    // would quietly serve (a) and quietly betray (b), so ASK. One click, and the
    // dispatcher sees precisely what is about to happen.
    //
    // Status is read from jdSnap — the authoritative server doc — not from
    // DispatchState, which is the stale-state surface TASK-4651 already had to
    // refuse on. Placed AFTER the clock/ticket guards on purpose: a ticketed
    // cancelled day is still refused outright and never reaches this prompt.
    const _jdPrev = jdSnap.data();
    const _moveReset = _statusAfterJobDayMove(_jdPrev);
    if (_moveReset) {
      const _wasLabel = _jdPrev.status === 'finished' ? 'marked finished' : 'cancelled';
      const _toLabel = newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      const ok = await window.showConfirm?.(
        // COD UX Lead: a 'finished' day under a "Day Is Cancelled" header is a
        // lie about which state the dispatcher is undoing.
        _jdPrev.status === 'finished' ? 'Day Is Marked Finished' : 'Day Is Cancelled',
        `This day is ${_wasLabel}. Moving it to ${_toLabel} puts it back on the board as ${_moveReset} — a ${_wasLabel === 'cancelled' ? 'cancelled' : 'closed-out'} day is skipped by double-booking checks, so it can't just ride along to a new date.\n\nMove it and put it back on the board?`,
        { confirmText: 'Move & Reactivate' }
      );
      if (!ok) {
        console.warn('[Dispatch] TASK-4685: move of a terminal day declined by user', { jobId, jobDayId, status: _jdPrev.status });
        return;
      }
    }

    const _moveUpdate = {
      // NOON-anchored: newDate is `new Date(newDateStr + 'T12:00:00')` from the
      // top of this function. Midnight-local renders nowhere, silently.
      date: Timestamp.fromDate(newDate),
      updatedAt: Timestamp.now()
    };
    if (_moveReset) _moveUpdate.status = _moveReset;
    await updateDoc(jdRef, _moveUpdate);

    // Recalculate job startDate/endDate from actual jobDays to prevent
    // the legacy renderer from filling gaps with synthetic jobDays
    // TASK-10288 — the job's days come from the live read above (every day, not
    // the 90-day board window), so a day moved from Search updates the range too.
    const job = _resolveJobAnywhere(jobId);
    if (job || _dup.days) {
      const allJobDays = (_dup.days || DispatchState.jobDays).filter(jd => jd.id !== jobDayId && jd.jobId === jobId);
      const allDates = allJobDays.map(jd => (jd.date?.toDate?.() || new Date(jd.date)).getTime());
      allDates.push(newDate.getTime());
      const minDate = new Date(Math.min(...allDates));
      const maxDate = new Date(Math.max(...allDates));
      minDate.setHours(12, 0, 0, 0);
      maxDate.setHours(12, 0, 0, 0);
      // TASK-4610 — one coercion point, and a REFUSE-IF-IMPLAUSIBLE gate. A
      // jobDay carrying an unparsable date poisons Math.min/max into NaN; the
      // old Timestamp.fromDate(Invalid Date) threw, which was ugly but safe.
      // Writing a null startDate instead would be WORSE than the bug being
      // fixed — null is just as invisible to the range query as a string.
      // Leave the range alone and say so.
      const _minTs = coerceJobTimestamp(minDate);
      const _maxTs = coerceJobTimestamp(maxDate);
      if (_minTs && _maxTs) {
        await updateDoc(doc(db, 'tenants', tenant, 'jobs', jobId), {
          startDate: _minTs,
          endDate: _maxTs,
          updatedAt: Timestamp.now()
        });
      } else {
        console.warn('[Dispatch] TASK-4610 — job', jobId, 'has a jobDay with an unparsable date; left startDate/endDate untouched rather than writing null.', { allDates });
        window.showToast?.('Day moved, but the job date range could not be recalculated — one of its days has a bad date.', 'warning');
      }
    }

    window.logAudit?.('dispatch', 'RESCHEDULE_JOBDAY', {
      jobDayId, jobId,
      oldDate: sourceDate?.toISOString?.()?.slice(0,10),
      newDate: newDateStr,
      jobStartDateUpdated: true
    });

    // TASK-4685 — the forensic row lands LAST, after both consistency writes
    // (the day's date and the parent job's range). COD Code Reviewer ❌: the
    // first cut awaited this addDoc BETWEEN those two writes, so a stalled audit
    // write would hold the job range hostage while the day had already moved.
    // Audit is best-effort; the two writes that must agree are not.
    if (_moveReset) {
      await _announceJobDayStatusReset({
        jobId,
        jobDayId,
        prevStatus: _jdPrev.status,
        newStatus: _moveReset,
        // NOT formatDateISO — it is a bare `.toISOString()` and THROWS a
        // RangeError on an Invalid Date. Unparsable jobDay dates are real on
        // this collection (see the TASK-4610 note above), and a throw here would
        // land in the outer catch and paint "Couldn't move job" AFTER the move
        // had already committed.
        oldDateStr: Number.isFinite(sourceDate?.getTime?.()) ? formatDateISO(sourceDate) : null,
        newDateStr,
        source: 'dispatch-drag-reschedule',
        silentToast: true   // the confirm dialog already said it; the success toast repeats it
      });
    }
    // TASK-4685 — when a terminal day was reactivated, the success toast says so
    // rather than the bare "Moved to …". The dispatcher approved it in the
    // dialog; this is the receipt.
    const _movedLabel = newDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    window.showToast?.(
      _moveReset ? `Moved to ${_movedLabel} — back on the board as ${_moveReset}` : `Moved to ${_movedLabel}`,
      'success'
    );
  } catch (error) {
    console.error('[Dispatch] Reschedule error:', error);
    window.showToast?.(`Couldn't move job: ${error.message || 'unknown error'}`, 'error');
  }
}

// Quick add day helper (no date picker — date already known)
async function _quickAddDay(jobId, newDate) {
  // TASK-1408 (COD Code Reviewer ❌) — this writes jobDays WITHOUT passing through
  // the three named add-day entry points, so it needs its own guard or the trap
  // survives on this path.
  if (!_guardManageDays()) return;
  // TASK-4567 step 3 — far-past warning before anything is claimed or written.
  if (!await _confirmFarPastAddDay(newDate)) return;
  if (!(await _p811GateDay(jobId, newDate, 'add-day'))) return; // TASK-9862 — expired-locate hard stop
  // TASK-1331 — shared double-add guard (this path had none).
  const claim = claimAddDaySlot(jobId, newDate);
  if (!claim.key) {
    if (claim.existingDayId) await openExistingDayCrewEditor(jobId, claim);
    return;
  }

  try {
    // TASK-1111 — shared crew-carry builder (was inline copy-paste).
    const newJobDayData = buildInheritedJobDay(jobId, newDate);
    const newDayRef = await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), newJobDayData);

    // TASK-4567 — shared range write (widens startDate backward too).
    await _applyAddDayJobRange(jobId, newDate, 'dispatch-quick-add', newDayRef.id);

    window.showToast?.(`Day added: ${newDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, 'success');
    // TASK-1331 — the day carried a crew forward; show it and let them change it here.
    await openNewDayCrewEditor(jobId, newDayRef.id, newJobDayData);
  } catch (error) {
    console.error('[Dispatch] Quick add day error:', error);
    window.showToast?.('Error adding day', 'error');
  } finally {
    releaseAddDaySlot(claim);
  }
}

// TASK-10043 S1 — Billing's ONE way onto the board (window.JobOps, never an
// import — Pitfall #1). The sequencing lives in the lazy dispatchBillingDay.js
// chunk (perf budget); every step it runs is THIS file's own add-day machinery,
// handed in below — the same builder, claim, far-past confirm, 811 stop, range
// write and jobAuditLog rows as the board's add-day paths. Never throws.
let _billingDayMod = null;
function addJobDayFromBilling(opts) {
  if (!_billingDayMod) _billingDayMod = __ddChunkWrap(import('./dispatchBillingDay.js?v=1')).catch((e) => { _billingDayMod = null; throw e; });
  return _billingDayMod.then((m) => m.addJobDayFromBilling(opts, {
    DispatchState, db, doc, collection, addDoc, writeBatch, formatDateISOLocal,
    _dispatchCanManageDays, _fetchJobIntoExternalCache, _externalJobById, _externalJobDays, _jobDayDateMs,
    _confirmFarPastAddDay, _p811GateDay, claimAddDaySlot, releaseAddDaySlot, buildInheritedJobDay,
    _applyAddDayJobRange, _auditJobDayEvent,
  })).catch((e) => {
    console.error('[Dispatch] TASK-10043 billing add-day could not run:', e);
    return { ok: false, error: 'Could not put that day on the board — reload the page and try again.' };
  });
}

// ============================================
// JOB CONTEXT MENU (Right-click on job cards)
// ============================================
function showJobContextMenu(e, jobId, jobDayId) {
  e.preventDefault();
  e.stopPropagation();

  // Remove any existing context menu
  document.getElementById('job-context-menu')?.remove();

  // TASK-10288 fold 3 — resolved anywhere: the stale-tentative chip opens this
  // menu from SEARCH-rendered cards too, whose job/day are not in the board arrays.
  const job = _resolveJobAnywhere(jobId);
  if (!job) return;

  const jobDay = _resolveJobDayAnywhere(jobDayId);
  const isCancelled = jobDay?.status === 'cancelled';
  // TASK-10288 D — Move Day + the tentative toggle, so neither is three screens
  // deep. Same rules as the drag and the Assign modal: never a virtual legacy
  // day; "Mark Tentative" only when nobody is on the day
  // (_assignmentTentativeLocked — its crew may already have been texted).
  const _realDay = !!jobDay && !String(jobDayId || '').startsWith('legacy-');
  const _canManage = _realDay && !isCancelled && _dispatchCanManageDays();
  const _tentItem = !_canManage ? ''
    : isTentativeJobDay(jobDay)
      ? `<button class="context-menu-item" data-action="confirm-day"><span class="material-icons">event_available</span> Confirm Day</button>`
      : (!_assignmentTentativeLocked(jobDay)
        ? `<button class="context-menu-item" data-action="mark-tentative"><span class="material-icons">help_outline</span> Mark Tentative</button>`
        : '');

  const menu = document.createElement('div');
  menu.id = 'job-context-menu';
  menu.className = 'job-context-menu';
  menu.innerHTML = `
    ${_canManage ? `<button class="context-menu-item" data-action="move-day"><span class="material-icons">event</span> Move Day…</button>` : ''}
    ${_tentItem}
    <button class="context-menu-item" data-action="add-day"><span class="material-icons">add_circle</span> Add Another Day</button>
    <button class="context-menu-item" data-action="duplicate"><span class="material-icons">content_copy</span> Copy to New Job</button>
    ${_boardProjectForJob(job) ? `<button class="context-menu-item" data-action="project-new-job"><span class="material-icons">create_new_folder</span> New job in this project</button>
    <button class="context-menu-item" data-action="project-open"><span class="material-icons">folder_special</span> Open project</button>` : ''}
    <button class="context-menu-item" data-action="${isCancelled ? 'restore' : 'cancel'}"><span class="material-icons">${isCancelled ? 'undo' : 'cancel'}</span> ${isCancelled ? 'Restore Day' : 'Cancel Day'}</button>
  `;

  menu.style.position = 'fixed';
  menu.style.left = `${e.clientX}px`;
  menu.style.top = `${e.clientY}px`;
  menu.style.zIndex = '9999';

  menu.addEventListener('click', (ev) => {
    const item = ev.target.closest('.context-menu-item');
    if (!item) return;
    menu.remove();
    const action = item.dataset.action;
    if (action === 'add-day') addDayToJob(jobId);
    else if (action === 'move-day') _moveJobDayFromMenu(jobId, jobDayId);
    else if (action === 'confirm-day') confirmJobDay(jobDayId);
    else if (action === 'mark-tentative') markJobDayTentative(jobDayId);
    else if (action === 'duplicate') duplicateJob(jobId);
    else if (action === 'cancel') cancelJobDay(jobDayId);
    else if (action === 'restore') restoreJobDay(jobDayId);
    else if (action === 'project-new-job') newJobInProjectFromBoard(job.projectId);
    else if (action === 'project-open') openProjectFromBoard(job.projectId);
  });

  document.body.appendChild(menu);

  // Close on outside click
  const closeMenu = (ev) => {
    if (!menu.contains(ev.target)) {
      menu.remove();
      document.removeEventListener('click', closeMenu);
    }
  };
  setTimeout(() => document.addEventListener('click', closeMenu), 0);
}

// TASK-10288 D — right-click → Move Day…: pick a date, then the SAME move the
// drag does (rescheduleJobDay: 811 gate, clock / ticket / invoice refusals,
// crew comes along). No new write path.
async function _moveJobDayFromMenu(jobId, jobDayId) {
  if (!_guardManageDays()) return;
  // TASK-10288 fold 3 — a search-rendered card's day is in searchJobDays.
  const jd = _resolveJobDayAnywhere(jobDayId);
  if (!jd) { window.showToast?.("Couldn't read that day — refresh and try again.", 'error'); return; }
  const ms = _jobDayDateMs(jd);
  const fromStr = ms === null ? null : formatDateISO(new Date(ms));
  const dateStr = await showDatePickerModal('Move Day', 'Move this day to:', { confirmText: 'Move Day', value: fromStr });
  if (!dateStr || dateStr === fromStr) return;
  await rescheduleJobDay(jobDayId, jobId, dateStr);
}

// TASK-10288 fold 1 — PURE. Another day of the SAME job already dated dateStr
// ('YYYY-MM-DD'), or null. Every status counts, cancelled included: two rows on
// one date is the duplicate, whatever state one of them is in. Day dates are
// noon-anchored, so formatDateISO lands on their calendar day — the same key
// the ticket guard matches on (lesson_jobday_date_anchors_to_noon_local).
function _otherJobDayOnDate(days, jobDayId, dateStr) {
  if (!dateStr) return null;
  return (days || []).find(d => {
    if (!d || d.id === jobDayId) return false;
    const ms = _jobDayDateMs(d);
    return ms !== null && formatDateISO(new Date(ms)) === dateStr;
  }) || null;
}

// TASK-10288 fold 1 — PURE. The refusal words for a move onto a taken date.
function _jobDayDateClashMessage(clash, dateLabel) {
  const on = dateLabel || 'that date';
  return String(clash?.status || '').toLowerCase() === 'cancelled'
    ? `This job already has a day on ${on} (cancelled). Restore that day instead, or pick another date. Nothing was moved.`
    : `This job already has a day on ${on}. A job gets one day per date — pick another date. Nothing was moved.`;
}

// TASK-10288 fold 1 — read the job's days LIVE and refuse a move onto a date
// another of its days already has. { ok:true, days } | { ok:false, message }.
// Fail closed: a read that throws refuses, the move is not made. Never throws.
async function _jobDayMoveDateClash(jobId, jobDayId, newDateStr) {
  let days;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), where('jobId', '==', jobId)
    ));
    days = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.warn('[Dispatch] TASK-10288: job days read failed — move refused', { jobId, jobDayId, err });
    return { ok: false, message: "Couldn't check this job's other days — the day was not moved. Check your connection and try again." };
  }
  const clash = _otherJobDayOnDate(days, jobDayId, newDateStr);
  if (clash) {
    const label = new Date(newDateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return { ok: false, clash, message: _jobDayDateClashMessage(clash, label) };
  }
  return { ok: true, days };
}

// TASK-10288 fold 4 — PURE. Same refusal stance as the move guard
// (rescheduleJobDay): a day with clock time, a ticket, or an invoice on it is
// not called off from the Job details row. Invoiced first (its fix is in
// Billing), then clocked, then ticketed; a check that could not run refuses.
// tGuard is _checkTicketedDayRescheduleGuard's answer. Returns '' to allow.
function _dayCancelRefusal(label, hasClock, tGuard, clockFailed) {
  const day = label || 'That day';
  if (tGuard && tGuard.blocked && tGuard.invoiced) {
    const inv = tGuard.invoiceRef ? `invoice ${tGuard.invoiceRef}` : 'an invoice';
    return `${day} is on ${inv}, so it can't be cancelled here — cancelling the day won't change the invoice. If the day didn't happen, fix it in Billing first (someone with Billing can uninvoice it), then cancel the day.`;
  }
  if (hasClock) return `${day} has clock time on it, so it can't be cancelled here — the hours belong to that day. Fix the clock entries in Workforce first.`;
  if (clockFailed || (tGuard && tGuard.blocked && tGuard.failedCheck)) {
    return `Couldn't check whether ${day} already has clock time or a ticket — the day was not cancelled. Check your connection and try again.`;
  }
  if (tGuard && tGuard.blocked) {
    return `${day} already has a ${tGuard.detail || 'ticket'}, so it can't be cancelled here — the ticket would be left on a cancelled day. ${tGuard.billed ? 'Unarchive it in Billing' : 'Delete or move the ticket'} first.`;
  }
  return '';
}

// TASK-10288 fold 4 — the per-day Cancel Day guard: the move guard's two reads
// (clock entries on the day's date by jobId; _checkTicketedDayRescheduleGuard).
// { ok:true } | { ok:false, message }. Never throws.
async function _checkDayCancelGuard(jobId, jobDay) {
  const label = _formatJobDayLabel(jobDay) || '';
  const ms = _jobDayDateMs(jobDay);
  let hasClock = false, clockFailed = false;
  if (ms !== null) {
    const dayStart = new Date(ms); dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(ms); dayEnd.setHours(23, 59, 59, 999);
    try {
      const clockSnap = await getDocs(
        query(collection(db, 'tenants', DispatchState.tenantId, 'clockEntries'), where('jobId', '==', jobId))
      );
      hasClock = clockSnap.docs.some(d => {
        const ci = d.data().clockIn?.toDate?.();
        return ci && ci >= dayStart && ci <= dayEnd;
      });
    } catch (err) {
      console.warn('[Dispatch] TASK-10288: clock read failed — cancel refused', { jobId, err });
      clockFailed = true;
    }
  }
  const tGuard = await _checkTicketedDayRescheduleGuard(jobId, jobDay);
  const message = _dayCancelRefusal(label, hasClock, tGuard, clockFailed);
  return message ? { ok: false, message } : { ok: true };
}

// TASK-10288 D — right-click → Mark Tentative. The same write the Confirm
// chip's Undo makes (tentative:true, confirmedAt/By cleared), refused on a day
// with people on it (_assignmentTentativeLocked: they may already have been
// texted, and a tentative day texts nobody — not even that it moved or was
// cancelled). Same rule as the Assign modal's Tentative box.
async function markJobDayTentative(jobDayId) {
  if (!jobDayId || String(jobDayId).startsWith('legacy-')) return;
  if (!_guardManageDays()) return;
  const jd = _resolveJobDayAnywhere(jobDayId);
  if (!jd || isTentativeJobDay(jd)) return;
  if (_assignmentTentativeLocked(jd)) {
    window.showToast?.('This day has crew on it, so it stays confirmed — they may already have been told. Take the crew off first.', 'info');
    return;
  }
  await _undoConfirmJobDay(jobDayId, {
    audit: 'MARK_JOBDAY_TENTATIVE',
    toast: 'Marked tentative. Nobody gets texted until you confirm it.',
    errorToast: "Couldn't mark that day tentative. Nothing changed; try again.",
  });
}

async function addDayToJob(jobId) {
  if (!_guardManageDays()) return;
  // TASK-4535 — the WORST of the search dead-ends: for a finished/pruned job
  // this write used to SUCCEED with empty crew (carry-forward reads live
  // arrays), no dedup vs the real (search-only) days, no endDate update — then
  // rendered nowhere, and the success toast lied. Until add-day is taught the
  // full search-aware path (dedup + inherit + endDate against searchJobDays),
  // refuse OUT LOUD rather than half-write.
  if (!DispatchState.jobs.find(j => j.id === jobId)) {
    const known = (DispatchState.searchJobs || []).find(j => j.id === jobId);
    window.showToast?.(known
      ? 'This job is finished (off the live board) — reopen or duplicate it to schedule new days.'
      : 'That job could not be loaded. Try refreshing the page.', 'error');
    return;
  }
  // Show date picker modal
  const dateStr = await showDatePickerModal('Add Day to Job', 'Select the date to add:');
  if (!dateStr) return; // User cancelled

  const newDate = new Date(dateStr + 'T12:00:00');

  // TASK-9943 — a date more than a week past the job's last day: is this the
  // right job? (Steve added 10/19 to a job that last ran 10/02.)
  const _stale = await _askStaleJobBeforeAddDay(jobId, newDate);
  if (_stale === 'cancel') return;
  if (_stale === 'new') { _startNewJobFromStaleAddDay(jobId, dateStr); return; }

  // TASK-4567 step 3 — the picker has no `min` and happily accepts any past
  // date; warn (don't block) when it lands outside the board's own window.
  if (!await _confirmFarPastAddDay(newDate)) return;
  if (!(await _p811GateDay(jobId, newDate, 'add-day'))) return; // TASK-9862 — expired-locate hard stop

  // TASK-1331 — the TASK-1109 dedup + in-flight guards moved into claimAddDaySlot so
  // all four add-day paths share them. Behaviour change: a date the job already has now
  // OPENS that day's crew editor instead of only toasting "not adding a duplicate".
  const claim = claimAddDaySlot(jobId, newDate);
  if (!claim.key) {
    if (claim.existingDayId) await openExistingDayCrewEditor(jobId, claim);
    return;
  }

  try {
    // TASK-1111 — carry the latest day's crew forward like the other 3 add-day
    // paths (was the outlier that hardcoded EMPTY crew).
    const newJobDayData = buildInheritedJobDay(jobId, newDate);
    const newDayRef = await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays'), newJobDayData);

    // TASK-4567 — shared range write (widens startDate backward too).
    await _applyAddDayJobRange(jobId, newDate, 'dispatch-add-day', newDayRef.id);

    window.showToast?.(`Day added: ${newDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, 'success');
    // TASK-1331 — the day carried a crew forward; show it and let them change it here.
    await openNewDayCrewEditor(jobId, newDayRef.id, newJobDayData);
  } catch (error) {
    console.error('[Dispatch] Add day error:', error);
    window.showToast?.('Error adding day', 'error');
  } finally {
    releaseAddDaySlot(claim);
  }
}

// TASK-9898 S0 — the dispatch-side "correct meters" modal (showCorrectMeters /
// saveCorrectMeters / showCorrectMetersFromAssignment) was removed: nothing in the
// app opened it, and its save called the correctMeters onRequest endpoint through
// httpsCallable, which wraps the body in `data`, so every save failed with 400.
// Meter readings are logged from Maintenance (Update Meters, plausibility-checked).

// ============================================
// 811 PERMITS
// ============================================

// ── TASK-9648 — 811 tickets: more than one per job, a picker, a customer ──────
// DATA (COD-decided, 11 lanes, 2026-10-06):
//   jobs/{id}.permitIds: string[]   THE link, in the order the dispatcher added them.
//   jobs/{id}.permitId:  string|null MIRROR = permitIds[0] || null, written by EVERY
//                                    writer, because older readers (and other
//                                    tenants' cached bundles) still read it.
//   permits/{id}.customerId / customerName  optional; a FILTER, never a gate.
// Readers go through _jobPermitIdsOf, which reads a legacy job (no permitIds) as
// [permitId]. The link stays on the JOB: driver and billing readers key off it.
const MAX_JOB_PERMIT_IDS = 20;
let _811UploadForJobForm = false; // the 811 window was opened from "Upload new" in the job form
let _p811Selected = [];           // picker selection, in order
const _justUploadedPermitIds = new Set(); // uploaded this session; never pruned before the snapshot shows them
// TASK-9767 — the picker / 811 window opened by a Project's Docs tab (via
// window.DispatchModule). null = the job form, exactly as before.
let _p811PickCtx = null;  // { ids, customerId, subtitle, onApply, onUpload } (TASK-9859: renamed from _p811Ctx)
let _811UploadCb = null;  // show811Submission({ onUploaded }) — fired once after the upload lands
function _p811CustomerId() {
  return _p811PickCtx ? String(_p811PickCtx.customerId || '') : (document.getElementById('job-customer')?.value || '');
}

// TASK-9767 — what a job SHOWS: its own tickets, then its project's (read-only;
// never written onto the job, so Copy/billing/writers are unchanged — they keep
// using _jobPermitIdsOf).
function _jobShown811Ids(job) {
  const own = _jobPermitIdsOf(job);
  const proj = job && job.projectId ? (window.ProjectsModule?.getProjectPermitIds?.(job.projectId) || []) : [];
  return [...own, ...proj.filter(id => !own.includes(id))];
}

// TASK-9767 — job form: the project's tickets under the job's own, read-only.
function _jobFormProject811Html(ownIds) {
  const pid = document.getElementById('job-project-id')?.value || '';
  const ids = pid ? (window.ProjectsModule?.getProjectPermitIds?.(pid) || []).filter(id => !ownIds.includes(id)) : [];
  return ids.map(id => {
    const p = (DispatchState.permits || []).find(x => x.id === id);
    if (!p) return '';
    const name = p.name || p.pdfName || '811 ticket';
    const url = _permitSafeUrl(p);
    return `
        <div class="job-811-row job-811-row-project" data-permit-id="${escapeHtml(id)}">
          <span class="material-icons job-811-pdf" aria-hidden="true">picture_as_pdf</span>
          <span class="job-811-main">
            <span class="job-811-name">${escapeHtml(name)}</span>
            <span class="job-811-meta">From the project · change it in the project's Docs</span>
          </span>
          ${url ? `<a class="job-811-act" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" aria-label="View ${escapeHtml(name)}"><span class="material-icons" aria-hidden="true">open_in_new</span><span class="job-811-act-label"> View</span></a>` : ''}
        </div>`;
  }).join('');
}

// Pure. Every 811 ticket id a job links, legacy single-id jobs included.
function _jobPermitIdsOf(job) {
  if (!job) return [];
  if (Array.isArray(job.permitIds)) return _normPermitIds(job.permitIds);
  return job.permitId ? _normPermitIds([job.permitId]) : [];
}

// Pure. Strings only, no blanks, no repeats, order kept, capped.
function _normPermitIds(ids) {
  const out = [];
  for (const raw of (Array.isArray(ids) ? ids : [])) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || out.includes(id)) continue;
    out.push(id);
    if (out.length >= MAX_JOB_PERMIT_IDS) break;
  }
  return out;
}

// Pure. The two fields every job writer sets together — never one without the other.
function _jobPermitLinkFields(ids) {
  const permitIds = _normPermitIds(ids);
  return { permitIds, permitId: permitIds[0] || null };
}

// ── TASK-9859 — 811 ticket expiry + the customer-first job list (PURE) ──────
// DATA: permits/{id}.expiresOn: 'YYYY-MM-DD' | null — a DATE, not a timestamp.
// "Today" is the tenant-zone date key, so the badge flips at the tenant's
// midnight, not the viewer's or UTC's. Every function in this block is pure
// (tz/today passed in) so tests/task9859-811-ux.unit.cjs runs them in node:vm.
const P811_SOON_DAYS = 3;        // amber at <= 3 days (Chris 10/14)
const P811_RECENT_DAYS = 30;     // a job worked in the last 30 days is "recent"
const P811_VALID_DAYS_MAX = 365;

function _p811DayKey(v, tz) {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  let d = null;
  if (v && typeof v.toDate === 'function') d = v.toDate();
  else if (Object.prototype.toString.call(v) === '[object Date]') d = v;
  else if (v && typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
  else if (typeof v === 'number') d = new Date(v);
  if (!d || isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
    const g = (t) => (parts.find(p => p.type === t) || {}).value;
    return `${g('year')}-${g('month')}-${g('day')}`;
  } catch (_) { return ''; }
}

function _p811KeyMs(key) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof key === 'string' ? key : '');
  if (!m) return NaN;
  const ms = Date.UTC(+m[1], +m[2] - 1, +m[3]);
  // 2026-02-31 rolls to March in Date.UTC — refuse it instead.
  return new Date(ms).toISOString().slice(0, 10) === key ? ms : NaN;
}

function _p811ValidExpiresOn(v) {
  return Number.isFinite(_p811KeyMs(v)) ? v : '';
}

function _p811AddDays(key, n) {
  const ms = _p811KeyMs(key);
  if (!Number.isFinite(ms) || !Number.isFinite(n)) return '';
  return new Date(ms + Math.round(n) * 86400000).toISOString().slice(0, 10);
}

// The company setting "811 tickets are good for N days": a whole number
// 1..365, anything else = not set (no auto-fill).
function _p811ValidDays(v) {
  const s = typeof v === 'number' ? String(v) : String(v == null ? '' : v).trim();
  if (!/^\d{1,3}$/.test(s)) return null;
  const n = parseInt(s, 10);
  return n >= 1 && n <= P811_VALID_DAYS_MAX ? n : null;
}

// TASK-9863 alert settings (moved to Dispatch Settings by TASK-9862).
function _p811AlertDaysOf(v) { return (Number.isInteger(v) && v >= 1 && v <= 30) ? v : 5; }
// TASK-9887 — archived-potential retention: 0 = keep forever (default), 12, 24.
function _p811PotRetentionOf(v) { return v === 12 || v === 24 ? v : 0; }
function _p811AlertAdmin() {
  const ma = window.AppState?.memberAccess;
  return window.AppState?.isDemo === true || ma == null
    || (!(ma.status && ['revoked', 'inactive', 'pending'].includes(ma.status)) && (ma.role === 'owner' || ma.role === 'admin'));
}

function _p811PrefillExpiry(todayKey, validDays) {
  const n = _p811ValidDays(validDays);
  return n ? _p811AddDays(todayKey, n) : '';
}

// { state: 'none'|'ok'|'soon'|'expired', days, label }. Good THROUGH expiresOn:
// on that date it reads "Expires today", the day after "Expired".
function _p811Expiry(expiresOn, todayKey) {
  const exp = _p811ValidExpiresOn(expiresOn);
  const a = _p811KeyMs(todayKey);
  if (!exp || !Number.isFinite(a)) return { state: 'none', days: null, label: '' };
  const days = Math.round((_p811KeyMs(exp) - a) / 86400000);
  if (days < 0) return { state: 'expired', days, label: 'Expired' };
  if (days === 0) return { state: 'soon', days, label: 'Expires today' };
  return { state: days <= P811_SOON_DAYS ? 'soon' : 'ok', days, label: `Expires in ${days} day${days === 1 ? '' : 's'}` };
}

// "Oct 16" (+ ", 2025" when not this year). From a date KEY, so no zone shift.
function _p811ShortDate(key, todayKey) {
  const ms = _p811KeyMs(key);
  if (!Number.isFinite(ms)) return '';
  const sameYear = String(todayKey || '').slice(0, 4) === key.slice(0, 4);
  return new Date(ms).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

// BUG 2026-10-20 (TITS, Alyse): expiresOn is the LAST GOOD DAY (kept: Chris +
// Alyse 10/14, goodThroughFromPrinted). The ticket prints the moment it STOPS
// ("Expires 11/13/26 12:01 am" -> good through 11/12), so the stored day alone
// read as "a day early" and the office hand-changed it. Show both.
// The printed expiry the reader saw: { key, good, time } or null. `text` is the
// reader's expiresPrinted. With `storedKey`, null unless that stored day IS this
// printing (its printed day or its good-through day): a same-number renewal
// keeps the OLD reading, which must not be shown next to the new date.
function _p811PrintedExpiry(text, storedKey) {
  const L = window.Locates811;
  const raw = String(text || '').trim();
  if (!raw || !L || typeof L.printedDateKey !== 'function' || typeof L.goodThroughFromPrinted !== 'function') return null;
  const key = L.printedDateKey(raw);
  const good = L.goodThroughFromPrinted(raw);
  if (!key || !good) return null;
  if (storedKey !== undefined && storedKey !== key && storedKey !== good) return null;
  const mins = typeof L.printedMinutes === 'function' ? L.printedMinutes(raw) : -1;
  let time = '';
  if (mins >= 0) {
    const h = Math.floor(mins / 60), m = mins % 60;
    time = `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
  }
  return { key, good, time };
}
function _p811PrintedOf(permit) {
  const f = permit && permit.readFromPdf && permit.readFromPdf.fields;
  return f ? _p811PrintedExpiry(f.expiresPrinted, _p811ValidExpiresOn(permit.expiresOn) || '') : null;
}
// "ticket says expires Nov 13, 12:01 am"
function _p811PrintedPhrase(pr, todayKey) {
  return pr ? `ticket says expires ${_p811ShortDate(pr.key, todayKey)}${pr.time ? ', ' + pr.time : ''}` : '';
}
// "Good through Nov 12 (ticket says expires Nov 13, 12:01 am)" / "Good through Nov 12" / ''.
function _p811ThroughText(permit, todayKey) {
  const exp = _p811ValidExpiresOn(permit && permit.expiresOn);
  if (!exp) return '';
  const pr = _p811PrintedOf(permit);
  return `Good through ${_p811ShortDate(exp, todayKey)}${pr ? ` (${_p811PrintedPhrase(pr, todayKey)})` : ''}`;
}
// Helper text under a good-through date input, for the value typed in it.
// Says what the ticket printed and which day that makes the last full day; a
// typed printed day (when the ticket stops early that morning) is called out.
function _p811ExpHintText(pr, typedKey, todayKey) {
  if (!pr) return 'The last full day the ticket is good. If the ticket expires early in the morning (like 12:01 am), that is the day before.';
  const base = `Ticket says expires ${_p811ShortDate(pr.key, todayKey)}${pr.time ? ', ' + pr.time : ''}, so the last full day is ${_p811ShortDate(pr.good, todayKey)}.`;
  if (typedKey && typedKey === pr.key && pr.key !== pr.good) return `${base} ${_p811ShortDate(pr.key, todayKey)} is the day it stops, not a full day.`;
  return base;
}
function _p811ExpHintHtml(id, pr, typedKey, todayKey) {
  const warn = !!(pr && typedKey && typedKey === pr.key && pr.key !== pr.good);
  const goodBtn = warn && id.indexOf('edit-permit-exp-hint-') === 0
    ? ` <button type="button" class="btn btn-secondary btn-sm" data-p811-use-good="${escapeHtml(pr.good)}" onclick="var i=document.getElementById('${escJsAttr(id.replace('-hint-', '-'))}'); if(i){i.value=this.getAttribute('data-p811-use-good'); i.dispatchEvent(new Event('input',{bubbles:true}));}">Use ${escapeHtml(_p811ShortDate(pr.good, todayKey))}</button>` : '';
  return `<span id="${escapeHtml(id)}" class="p811-up-hint p811-exp-hint${warn ? ' is-warn' : ''}" data-p811-exp-hint="1" style="display:block;font-size:0.8rem;color:${warn ? '#92400E' : 'var(--text-secondary)'};${warn ? 'font-weight:600;' : ''}">${escapeHtml(_p811ExpHintText(pr, typedKey, todayKey))}${goodBtn}</span>`;
}
// Upload row: the reading of THIS file (no stored date to agree with yet).
function _p811RowPrinted(r) {
  return r && r.ai && r.ai.state === 'done' && r.ai.fields ? _p811PrintedExpiry(r.ai.fields.expiresPrinted) : null;
}
// Edit row: the ticket's own reading, unless a same-number renewal moved the
// date since (renewals[]) — then that PDF reading is the OLD ticket's.
function _p811EditPrinted(permit) {
  const f = permit && permit.readFromPdf && permit.readFromPdf.fields;
  if (!f) return null;
  if (_p811ValidExpiresOn(permit.expiresOn)) return _p811PrintedOf(permit);
  return Array.isArray(permit.renewals) && permit.renewals.length ? null : _p811PrintedExpiry(f.expiresPrinted);
}
function p811EditExpHint(permitId) {
  const permit = (DispatchState.permits || []).find(p => p && p.id === permitId);
  const h = document.getElementById('edit-permit-exp-hint-' + permitId);
  const input = document.getElementById('edit-permit-exp-' + permitId);
  if (!permit || !h || !input) return;
  h.outerHTML = _p811ExpHintHtml('edit-permit-exp-hint-' + permitId, _p811EditPrinted(permit), _p811ValidExpiresOn(input.value) || '', _p811Today());
}
// Site detail table: the printed expiry under the good-through date.
function _p811PrintedCellHtml(p, todayKey) {
  const pr = _p811PrintedOf(p);
  return pr ? `<span class="p811-exp-printed" style="display:block;font-size:0.75rem;font-weight:400;color:var(--text-secondary);">Ticket says expires ${escapeHtml(_p811ShortDate(pr.key, todayKey))}${pr.time ? ', ' + escapeHtml(pr.time) : ''}</span>` : '';
}

// Expired first (most recently expired on top), then expiring soon (soonest
// first), then the rest newest-upload first.
function _p811SortForList(permits, todayKey) {
  const sec = (p) => (p && p.createdAt && (p.createdAt.seconds || (typeof p.createdAt.toMillis === 'function' ? p.createdAt.toMillis() / 1000 : 0))) || 0;
  const info = new Map((permits || []).map(p => [p, _p811Expiry(p && p.expiresOn, todayKey)]));
  const rank = (e) => (e.state === 'expired' ? 0 : e.state === 'soon' ? 1 : 2);
  return [...(permits || [])].sort((a, b) => {
    const ea = info.get(a), eb = info.get(b);
    const r = rank(ea) - rank(eb);
    if (r) return r;
    if (ea.state === 'expired') return eb.days - ea.days;
    if (ea.state === 'soon') return ea.days - eb.days;
    return sec(b) - sec(a);
  });
}

function _p811NormName(s) { return String(s || '').trim().replace(/\s+/g, ' ').toLowerCase(); }

// Is this job the customer's? By id; a legacy job with only a NAME matches by
// exact (case/space-insensitive) name. Never a substring guess.
function _p811JobIsCustomers(job, customerId, customerName) {
  if (!job || !customerId) return false;
  if (job.customerId) return job.customerId === customerId;
  return !!customerName && _p811NormName(job.customer) === _p811NormName(customerName);
}

// jobId -> sorted unique tenant-zone day keys, from the loaded jobDays.
function _p811DayKeysByJob(jobDays, tz) {
  const m = new Map();
  for (const jd of (jobDays || [])) {
    if (!jd || !jd.jobId) continue;
    const k = _p811DayKey(jd.date, tz);
    if (!k) continue;
    if (!m.has(jd.jobId)) m.set(jd.jobId, new Set());
    m.get(jd.jobId).add(k);
  }
  const out = new Map();
  for (const [id, s] of m) out.set(id, [...s].sort());
  return out;
}

// { next, last } work date keys for a job: next = first day today or later,
// last = latest day before today. No jobDays loaded -> the job's own dates.
function _p811JobWhen(job, keysByJob, todayKey, tz) {
  const keys = (keysByJob && keysByJob.get(job && job.id)) || [];
  let next = '', last = '';
  for (const k of keys) {
    if (k >= todayKey) { if (!next) next = k; } else last = k;
  }
  if (!next && !last && job) {
    const s = _p811DayKey(job.startDate, tz), e = _p811DayKey(job.endDate, tz) || s;
    if (e && e >= todayKey) next = s && s >= todayKey ? s : todayKey;
    else if (e) last = e;
  }
  return { next, last };
}

// The job list for ONE customer: [{ job, when, label }] split into recent
// (upcoming soonest first, then most recently worked) and older (nothing
// upcoming and not worked in P811_RECENT_DAYS). `query` searches number,
// title, location and well name inside that customer — older jobs included.
function _p811JobChoices(jobs, keysByJob, o) {
  const opts = o || {};
  const today = opts.todayKey || '';
  if (!opts.customerId) return { recent: [], older: [], total: 0 };
  const q = _p811NormName(opts.query);
  const cutoff = _p811AddDays(today, -(opts.recentDays || P811_RECENT_DAYS));
  const fmt = typeof opts.formatNumber === 'function' ? opts.formatNumber : () => '';
  const rows = (jobs || []).filter(j => _p811JobIsCustomers(j, opts.customerId, opts.customerName)).map(job => {
    const when = _p811JobWhen(job, keysByJob, today, opts.tz);
    const num = fmt(job);
    const date = when.next ? 'next ' + _p811ShortDate(when.next, today) : (when.last ? 'last ' + _p811ShortDate(when.last, today) : 'no dates');
    const label = [num, job.title || 'Untitled job', date].filter(Boolean).join(' · ');
    const hay = _p811NormName([num, job.title, job.location, job.wellName].filter(Boolean).join(' '));
    return { job, when, label, hay };
  }).filter(r => !q || r.hay.includes(q));
  const upcoming = rows.filter(r => r.when.next).sort((a, b) => a.when.next.localeCompare(b.when.next));
  const past = rows.filter(r => !r.when.next && r.when.last).sort((a, b) => b.when.last.localeCompare(a.when.last));
  const none = rows.filter(r => !r.when.next && !r.when.last);
  const recentPast = past.filter(r => r.when.last >= cutoff);
  const olderPast = past.filter(r => r.when.last < cutoff);
  return { recent: upcoming.concat(recentPast), older: olderPast.concat(none), total: rows.length };
}

// Linked tickets that run out before work on the job: [{ permit, expiresOn, lateKeys }].
// Only days today or later count — a past day is history, not a warning.
function _p811ExpiryConflicts(permits, dayKeys, todayKey) {
  const out = [];
  const L = typeof window !== 'undefined' ? window.Locates811 : null;
  const tz = L ? _p811Tz() : '';
  const now = Date.now();
  for (const p of (permits || [])) {
    const exp = _p811ValidExpiresOn(p && p.expiresOn);
    if (!exp) continue;
    // TASK-9925 — the shared SITE-zone rule: last good company day, and today once run out.
    const last = L && typeof L.lastGoodDay === 'function' ? (L.lastGoodDay(p, tz) || exp) : exp;
    const ea = L && typeof L.expiresAt === 'function' ? L.expiresAt(p, tz) : null;
    const gone = !!ea && now >= ea.at;
    const late = [...new Set(dayKeys || [])].filter(k => k >= todayKey && (k > last || (gone && k === todayKey))).sort();
    if (late.length) out.push({ permit: p, expiresOn: exp, lateKeys: late });
  }
  return out;
}

// TASK-9859 (Chris + Alysse 10/14) — permits/{id}.pendingJobName: free text for
// a ticket that arrives BEFORE its job exists. Trimmed, spaces collapsed, <= 120.
const P811_PENDING_NAME_MAX = 120;
// TASK-9862 — the site a ticket carries (siteName, else the two old fields).
function _p811SiteOf(p) {
  const L = typeof window !== 'undefined' ? window.Locates811 : null;
  return L ? L.siteNameOf(p) : String((p && (p.siteName || p.pendingJobName || p.proposedProjectName)) || '').replace(/\s+/g, ' ').trim();
}
function _p811CleanPendingName(v) {
  return String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, P811_PENDING_NAME_MAX);
}

// Shared words between a ticket's pending name and a job title (token overlap,
// 2+ characters, case-insensitive). Higher = more likely the same site.
function _p811NameScore(a, b) {
  const toks = (x) => new Set(String(x || '').toLowerCase().split(/[^a-z0-9]+/).filter(t => t.length >= 2));
  const A = toks(a), B = toks(b);
  let n = 0;
  for (const t of A) if (B.has(t)) n++;
  return n;
}

// The job form's "Waiting for a job" group: the customer's tickets on NO job,
// best match to the job title first, then by name.
function _p811WaitingForJob(permits, isLinked, jobTitle) {
  const label = (p) => _p811SiteOf(p) || p.name || p.pdfName || ''; // TASK-9862 — the ONE site field
  return (permits || []).filter(p => !isLinked(p))
    .map(p => ({ p, score: _p811NameScore(label(p), jobTitle) }))
    .sort((x, y) => (y.score - x.score) || String(label(x.p)).localeCompare(String(label(y.p))))
    .map(x => x.p);
}

// TASK-9859 (Chris 10/14) — "Generate name":
//   {Customer} – {Project, else Job title + J-#, else Job/site name} – {811 ticket #}
// Empty parts skipped (no dangling dashes), <= 120 chars. Only ever runs on a click.
const P811_NAME_SEP = ' \u2013 ';
function _p811BuildName(parts) {
  const o = parts || {};
  const t = (v) => String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  const job = [t(o.jobTitle), t(o.jobNumber)].filter(Boolean).join(' ');
  const middle = t(o.projectName) || job || t(o.pendingJobName);
  const out = [t(o.customerName), middle, t(o.ticketNo)].filter(Boolean).join(P811_NAME_SEP);
  if (out.length <= 120) return out;
  // Too long: keep the ticket # whole and trim the middle, never leave a dash.
  const tail = t(o.ticketNo) ? P811_NAME_SEP + t(o.ticketNo) : '';
  return (out.slice(0, 120 - tail.length).replace(/[\s\u2013-]+$/, '') + tail).slice(0, 120);
}

// The 811 ticket number a file name LEADS with ("26138947_bridger.pdf"): 5+ digits.
function _p811TicketNoFromFile(fileName) {
  const m = /^\s*(\d{5,})(?!\d)/.exec(String(fileName || ''));
  return m ? m[1] : '';
}


// TASK-9859 (Chris 10/14 11:00) — an 811 ticket carries SUGGESTIONS; dispatch
// turns them into a job. The 811 login never creates jobs or projects.
//   permits/{id}.proposedProjectId    an existing project the uploader picked
//   permits/{id}.proposedProjectName  a name that matched no project (<= 120)
// Fuzzy name match (v1, no AI): lowercase, punctuation stripped, then the best
// of (a) edit-distance similarity with spaces removed ("Go Wola" ~ "Gowolla")
// and (b) word overlap. HOOK: an AI matcher can replace _p811Similarity later.
const P811_MATCH_MIN = 0.8;
function _p811NormForMatch(s) {
  return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9\s]+/g, '').replace(/\s+/g, ' ').trim();
}
function _p811Lev(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[b.length];
}
function _p811Similarity(a, b) {
  const A = _p811NormForMatch(a), B = _p811NormForMatch(b);
  if (!A || !B) return 0;
  // COD r3 (Domain): a locate is for ONE site. When both names carry numbers
  // (well, pad, section, ticket) they must be the same numbers, or it is a
  // different site no matter how close the letters are.
  const digits = (x) => (x.replace(/[^0-9 ]+/g, ' ').match(/[0-9]+/g) || []).join(' ');
  const dA = digits(A.replace(/([0-9]) ([0-9])/g, '$1$2')), dB = digits(B.replace(/([0-9]) ([0-9])/g, '$1$2'));
  if (dA && dB && dA !== dB) return Math.min(0.5, _p811SimilarityLetters(A, B));
  return _p811SimilarityLetters(A, B);
}
function _p811SimilarityLetters(A, B) {
  const a2 = A.replace(/ /g, ''), b2 = B.replace(/ /g, '');
  if (a2 === b2) return 1;
  const lev = 1 - _p811Lev(a2, b2) / Math.max(a2.length, b2.length);
  const ta = new Set(A.split(' ')), tb = new Set(B.split(' '));
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter++;
  const jac = inter / (ta.size + tb.size - inter);
  return Math.max(lev, jac);
}
// Best item whose name scores >= P811_MATCH_MIN, or null. Ties keep list order.
function _p811BestMatch(name, items, nameOf) {
  let best = null;
  for (const it of (items || [])) {
    const score = _p811Similarity(name, nameOf(it));
    if (score >= P811_MATCH_MIN && (!best || score > best.score)) best = { item: it, score };
  }
  return best;
}
// The project a typed name EXACTLY names (normalized), or null.
function _p811ExactProject(name, projects) {
  const n = _p811NormForMatch(name);
  if (!n) return null;
  return (projects || []).find(p => _p811NormForMatch(p && p.name) === n) || null;
}

// "26138947_bridger anomaly dig.pdf" -> "26138947 bridger anomaly dig"
function _p811NameFromFile(fileName) {
  return String(fileName || '').replace(/\.pdf$/i, '').replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
}

// The job form's linked tickets live in the hidden #job-permit as "id1,id2".
// Firestore auto-ids never contain a comma.
function _getJobPermitIds() {
  const raw = document.getElementById('job-permit')?.value || '';
  return _normPermitIds(raw.split(','));
}

function _setJobPermitIds(ids) {
  const el = document.getElementById('job-permit');
  if (el) el.value = _normPermitIds(ids).join(',');
  populatePermitDropdown();
}

function _permitDateStr(permit) {
  const d = permit?.createdAt?.toDate?.() || (permit?.createdAt?.seconds ? new Date(permit.createdAt.seconds * 1000) : null);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// http(s) only — a permit's URL is stored data and lands in an href.
function _permitSafeUrl(permit) {
  const url = String(permit?.pdfUrl || '');
  return /^https?:\/\//i.test(url) ? url : '';
}

// TASK-9855 — open a permit-library PDF through the Storage SDK on its stored
// `storagePath` (else the path inside pdfUrl), never the token URL. Returns the
// attribute string for an <a>; '' when the permit has nothing to open.
function _permitLinkAttrs(permit) {
  if (!permit || !(_permitSafeUrl(permit) || permit.storagePath)) return '';
  return window.StorageFile.linkAttrs(permit, { urlKey: 'pdfUrl', pathKey: permit.storagePath ? 'storagePath' : undefined });
}

// Jobs in the loaded window that link this ticket.
function _permitHolders(permitId) {
  return (DispatchState.jobs || []).filter(j => _jobPermitIdsOf(j).includes(permitId));
}

function _customerNameById(id) {
  if (!id) return '';
  const c = (DispatchState.customers || []).find(x => x.id === id);
  return c?.name || '';
}

// Rewrites one job's link through a transaction so the list and its mirror are
// computed from the LIVE doc, never from a possibly stale DispatchState copy.
async function _writeJobPermitIds(jobId, mutate) {
  const tenantId = DispatchState.tenantId;
  const jobRef = doc(db, 'tenants', tenantId, 'jobs', jobId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(jobRef);
    if (!snap.exists()) return;
    const wanted = mutate(_jobPermitIdsOf(snap.data()) || []);
    // COD 10/12 (Code Reviewer): the cap is refused, never silently applied —
    // _normPermitIds would drop the ticket being added and the caller would
    // report a link that never happened.
    const distinct = new Set((Array.isArray(wanted) ? wanted : []).map(r => (typeof r === 'string' ? r.trim() : '')).filter(Boolean));
    if (distinct.size > MAX_JOB_PERMIT_IDS) {
      throw Object.assign(new Error(`A job can hold up to ${MAX_JOB_PERMIT_IDS} 811 tickets.`), { code: 'permit-cap' });
    }
    const next = _normPermitIds(wanted);
    // TASK-9862 (COD): stamp each ticket being ADDED, so a renew / delete
    // transaction that read that ticket fails and retries instead of missing
    // this new link. Reads first, then writes (transaction rule).
    const before = _jobPermitIdsOf(snap.data()) || [];
    const added = next.filter(id => !before.includes(id));
    const addedSnaps = await Promise.all(added.map(id => tx.get(doc(db, 'tenants', tenantId, 'permits', id))));
    // COD r2: never ADD a ticket that is gone or already renewed (a renew /
    // delete that committed first would otherwise leave a dead link).
    for (const ps of addedSnaps) {
      if (!ps.exists()) throw Object.assign(new Error('That 811 ticket was deleted.'), { code: 'permit-gone' });
      const d = ps.data() || {};
      if (d.renewedById || d.status === 'renewed') throw Object.assign(new Error('That 811 ticket was renewed. Use the new ticket.'), { code: 'permit-renewed' });
    }
    tx.update(jobRef, _jobPermitLinkFields(next));
    for (const ps of addedSnaps) tx.update(ps.ref, { linkedAt: Timestamp.now() });
  });
}

// The job form's ticket list. Name kept (the permits snapshot and the copy path
// call it): it used to fill a <select>, it now paints the linked-ticket rows.
// Once the permits collection has loaded, an id with no permit doc is a DELETED
// ticket and is dropped — the honest answer, and what deletePermit would have
// done had the job been in its query. Before it loads, ids are kept as-is, so a
// Copy saved on a cold load still carries its tickets (TASK-9212's race).
function populatePermitDropdown() {
  const hidden = document.getElementById('job-permit');
  if (!hidden) return;
  let ids = _getJobPermitIds();
  if (DispatchState.permitsLoaded) {
    const known = new Set((DispatchState.permits || []).map(p => p.id));
    const kept = ids.filter(id => known.has(id) || _justUploadedPermitIds.has(id));
    if (kept.length !== ids.length) { ids = kept; hidden.value = ids.join(','); }
  }
  const list = document.getElementById('job-permit-chips');
  if (list) {
    // TASK-9859 — expiry badge per ticket, and a warning (never a block) when a
    // ticket runs out before a day this job is scheduled.
    const todayKey = _p811Today();
    const linked = ids.map(id => (DispatchState.permits || []).find(x => x.id === id)).filter(Boolean);
    const conflicts = new Map(_p811ExpiryConflicts(linked, _p811JobFormDayKeys(), todayKey).map(c => [c.permit.id, c]));
    list.innerHTML = ids.map(id => {
      const p = (DispatchState.permits || []).find(x => x.id === id);
      const name = p ? (p.name || p.pdfName || '811 ticket') : 'Loading ticket…';
      const meta = p ? [_permitDateStr(p) ? 'Uploaded ' + _permitDateStr(p) : '', p.customerName || ''].filter(Boolean).join(' · ') : '';
      const attrs = _permitLinkAttrs(p);
      const badge = p ? _p811BadgeHtml(p, todayKey) : '';
      const c = conflicts.get(id);
      const more = c && c.lateKeys.length > 1 ? ` (+${c.lateKeys.length - 1} more day${c.lateKeys.length > 2 ? 's' : ''})` : '';
      const warn = c ? `<span class="job-811-warn" role="note"><span class="material-icons" aria-hidden="true">warning</span><span>${escapeHtml(_p811ThroughText(c.permit, todayKey))}, but work is scheduled ${escapeHtml(_p811ShortDate(c.lateKeys[0], todayKey))}${escapeHtml(more)}. Renew it or add a new ticket.</span></span>` : '';
      return `
        <div class="job-811-row" data-permit-id="${escapeHtml(id)}">
          <span class="material-icons job-811-pdf" aria-hidden="true">picture_as_pdf</span>
          <span class="job-811-main">
            <span class="job-811-name">${escapeHtml(name)}</span>
            ${meta ? `<span class="job-811-meta">${escapeHtml(meta)}</span>` : ''}
            ${badge ? `<span class="job-811-meta">${badge}</span>` : ''}
            ${warn}
          </span>
          ${attrs ? `<a class="job-811-act" ${attrs} aria-label="View ${escapeHtml(name)}"><span class="material-icons" aria-hidden="true">open_in_new</span><span class="job-811-act-label"> View</span></a>` : ''}
          <button type="button" class="job-811-act" aria-label="Remove ${escapeHtml(name)} from this job" title="Remove from this job" onclick="window.DispatchModule.removeJobPermit('${escJsAttr(id)}')"><span class="material-icons" aria-hidden="true">close</span></button>
        </div>`;
    }).join('') + _jobFormProject811Html(ids); // TASK-9767
  }
  const startBtn = document.getElementById('btn-start-from-811'); // TASK-9859 — new jobs only
  // Visible (outside the collapsed Site group) on a NEW job, with 811 on and a
  // ticket waiting; never for a login that cannot write jobs.
  if (startBtn) startBtn.classList.toggle('hidden', !!document.getElementById('job-id')?.value || !is811Enabled()
    || window.isMember811Only?.() === true || !_p811WaitingTickets().length);
  const addBtn = document.getElementById('btn-add-811-ticket');
  if (addBtn) addBtn.innerHTML = `<span class="material-icons" aria-hidden="true">add</span> ${ids.length ? 'Add another 811 ticket' : 'Add 811 ticket'}`;
  // TASK-9859 — a problem ticket opens the Site group ONCE per job+ticket set,
  // so the warning is seen without stopping the dispatcher from closing it.
  _p811MaybeOpenSiteGroup(ids);
  _p811RenderJobAttach(); // TASK-9862 — "N locate tickets waiting for <site>"
  _refreshJobFormGroups(); // TASK-4695 — a ticket landing late must still show in the closed Site group
  _jobProjDocsRender(); // TASK-9650 — the project's "Use on jobs" docs follow the picker
}

// ── TASK-9862 — HARD STOP on expired locates ─────────────────────────────
// Saving a job / assigning crew / adding or moving a day dated AFTER a linked
// ticket's good-through date (the job's own tickets AND its project's) stops
// with a red dialog: Renew, Cancel, or "Dispatch anyway" with a REQUIRED
// reason that is written to jobAuditLog. Company time zone, date-only compare
// (window.Locates811.hardStops). Days before today are history and never stop.
let _p811StopResolve = null;
let _p811StopState = null;
let _p811PendingOverride = null; // a NEW job's override, logged once it has an id
function _p811TicketsFor(permitIds, projectId) {
  const own = _normPermitIds(permitIds);
  const proj = projectId ? (window.ProjectsModule?.getProjectPermitIds?.(projectId) || []) : [];
  const ids = [...own, ...proj.filter(id => !own.includes(id))];
  return ids.map(id => (DispatchState.permits || []).find(p => p && p.id === id)).filter(Boolean);
}
function _p811StopsFor(permitIds, projectId, dayKeys) {
  if (!is811Enabled() || !window.Locates811) return [];
  // TASK-9925 — the card's SITE-zone rule (clock), so the chip, the hard stop and the badge agree.
  return window.Locates811.hardStops(_p811TicketsFor(permitIds, projectId), dayKeys, _p811Today(), _p811Clock());
}
// A saved job's own + project tickets for one or more day keys.
function _p811StopsForJob(jobId, dayKeys) {
  const job = (DispatchState.jobs || []).find(j => j.id === jobId);
  if (!job) return [];
  return _p811StopsFor(_jobPermitIdsOf(job), job.projectId || '', dayKeys);
}
/**
 * Resolves true = go ahead (nothing expired, or overridden with a reason),
 * false = stop. ctx: { jobId, jobLabel, source }.
 */
function _p811HardStopGate(stops, ctx) {
  if (!stops || !stops.length) return Promise.resolve(true);
  const m = document.getElementById('modal-811-stop');
  if (!m) return Promise.resolve(true);
  if (_p811StopResolve) { try { _p811StopResolve(false); } catch (_) { /* replaced */ } }
  const todayKey = _p811Today();
  const L = window.Locates811;
  const first = stops[0];
  const allDays = [...new Set(stops.flatMap(s => s.lateKeys))].sort();
  const dayTxt = allDays.length === 1 ? _p811ShortDate(allDays[0], todayKey) : `${_p811ShortDate(allDays[0], todayKey)} (+${allDays.length - 1} more day${allDays.length > 2 ? 's' : ''})`;
  const anyExpired = stops.some(s => s.reason !== 'early');
  document.getElementById('p811-stop-title').textContent = anyExpired ? `Locate expired for ${dayTxt}` : `Locate not active yet on ${dayTxt}`;
  document.getElementById('p811-stop-body').innerHTML = stops.map(s => {
    const tn = s.permit.ticketNumber || _p811TicketNoFromFile(s.permit.name) || '';
    const site = L ? L.siteNameOf(s.permit) : '';
    const days = escapeHtml(s.lateKeys.map(k => _p811ShortDate(k, todayKey)).join(', '));
    const who = `Locate ticket <span class="p811-mono">#${escapeHtml(tn || '?')}</span>${site ? ` (${escapeHtml(site)})` : ''}`;
    return s.reason === 'early'
      ? `<p>${who} starts <b>${escapeHtml(_p811ShortDate(s.digStartOn, todayKey))}</b> (the work-to-begin date on the ticket). Work is set for ${days}. Move the day or override.</p>`
      : `<p>${who} expired <b>${escapeHtml(_p811ShortDate(s.expiresOn, todayKey))}</b> (good through that day${(() => { const pr = _p811PrintedOf(s.permit); return pr && pr.good === s.expiresOn ? '; ' + escapeHtml(_p811PrintedPhrase(pr, todayKey)) : ''; })()}). Work is set for ${days}. Renew it or override.</p>`;
  }).join('') + `<p style="margin:0;font-weight:700;color:#7F1D1D">No digging on those days without a valid ticket.</p>`;
  const why = document.getElementById('p811-stop-override');
  why.classList.add('hidden');
  const reason = document.getElementById('p811-stop-reason');
  reason.value = '';
  _p811StopState = { stops, ctx: ctx || {}, siteKey: L ? L.groupKey(first.permit.customerId, L.siteNameOf(first.permit)) : '' };
  _p811PaintStopFoot(false);
  m.classList.remove('hidden');
  return new Promise(res => { _p811StopResolve = res; });
}
function _p811PaintStopFoot(overriding) {
  const foot = document.getElementById('p811-stop-foot');
  if (!foot) return;
  const canRenew = _p811CanRenew() && _p811StopState && _p811StopState.siteKey;
  foot.innerHTML = overriding
    ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811StopChoose('cancel')">Cancel</button>
       <button type="button" class="btn btn-danger" onclick="window.DispatchModule.p811StopChoose('override')"><span class="material-icons">warning</span> Save anyway</button>`
    : `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811StopChoose('cancel')">Cancel</button>
       <button type="button" class="btn btn-danger-outline" onclick="window.DispatchModule.p811StopChoose('why')">Dispatch anyway…</button>
       ${canRenew ? '<button type="button" class="btn btn-primary" onclick="window.DispatchModule.p811StopChoose(\'renew\')"><span class="material-icons">autorenew</span> Renew ticket</button>' : ''}`;
}
async function p811StopChoose(what) {
  const res = _p811StopResolve;
  const st = _p811StopState;
  if (!res || !st) { closeModal('811-stop'); return; }
  if (what === 'why') {
    document.getElementById('p811-stop-override')?.classList.remove('hidden');
    _p811PaintStopFoot(true);
    setTimeout(() => document.getElementById('p811-stop-reason')?.focus(), 30);
    return;
  }
  if (what === 'override') {
    const reason = String(document.getElementById('p811-stop-reason')?.value || '').replace(/\s+/g, ' ').trim();
    if (reason.length < 5) { window.showToast?.('Say why (at least a few words). It is saved to the job history.', 'error'); document.getElementById('p811-stop-reason')?.focus(); return; }
    _p811StopResolve = null;
    closeModal('811-stop');
    const row = _p811OverrideRow(st, reason);
    if (st.ctx.jobId) _p811LogOverride(st.ctx.jobId, row);
    else _p811PendingOverride = row; // saveJob logs it once the new job has an id
    res(true);
    return;
  }
  _p811StopResolve = null;
  closeModal('811-stop');
  res(false);
  if (what === 'renew' && st.siteKey) p811OpenGroup(st.siteKey, true);
}
function _p811OverrideRow(st, reason) {
  return {
    action: 'LOCATE_EXPIRED_OVERRIDE', source: String(st.ctx.source || 'dispatch'),
    reason: reason.slice(0, 300),
    permitIds: st.stops.map(s => s.permit.id).slice(0, 20),
    ticketNumbers: st.stops.map(s => String(s.permit.ticketNumber || _p811TicketNoFromFile(s.permit.name) || '')).slice(0, 20),
    expiresOn: st.stops.map(s => s.expiresOn || null).slice(0, 20),
    stopReasons: st.stops.map(s => s.reason || 'expired').slice(0, 20), // 'expired' | 'early' (before dig start)
    dayKeys: [...new Set(st.stops.flatMap(s => s.lateKeys))].sort().slice(0, 60),
    jobTitle: String(st.ctx.jobLabel || '').slice(0, 120),
  };
}
function _p811LogOverride(jobId, row) {
  addDoc(collection(db, 'tenants', DispatchState.tenantId, 'jobAuditLog'), {
    ...row, jobId, timestamp: Timestamp.now(), user: window.AppState?.user?.email || 'unknown',
  }).catch(e => console.warn('[Dispatch] locate override not logged:', e && e.code));
}
function _p811FlushOverride(jobId) {
  if (!_p811PendingOverride || !jobId) { _p811PendingOverride = null; return; }
  const row = _p811PendingOverride;
  _p811PendingOverride = null;
  _p811LogOverride(jobId, row);
}
// Day keys between two keys (inclusive), capped.
function _p811RangeKeys(s, e) {
  const a = _p811ValidExpiresOn(s);
  if (!a) return [];
  const b = _p811ValidExpiresOn(e) || a;
  const out = [];
  for (let k = a, n = 0; k && k <= b && n < 366; k = _p811AddDays(k, 1), n++) out.push(k);
  return out;
}
// TASK-9862 — the hard stop for ONE day of a saved job (add / move a day).
async function _p811GateDay(jobId, dateOrKey, source) {
  const key = _p811DayKey(dateOrKey, _p811Tz());
  const stops = key ? _p811StopsForJob(jobId, [key]) : [];
  if (!stops.length) return true;
  const job = (DispatchState.jobs || []).find(j => j.id === jobId);
  return _p811HardStopGate(stops, { jobId, jobLabel: job ? job.title || '' : '', source });
}
// The board card / list card flag for one job DAY.
// Chris 10/27: on OFFICE cards it is one red icon in the card's indicator row
// (hover = title, tap = popover), not the full-width bar. opts.bar keeps the
// old bar for the driver-mode list card (driver screens unchanged).
function _p811CardStops(job, dayKey) {
  if (!job || !dayKey || !is811Enabled() || !window.Locates811) return [];
  const k = _p811DayKey(dayKey, _p811Tz());
  if (!k) return [];
  return _p811StopsFor(_jobPermitIdsOf(job), job.projectId || '', [k]);
}
// One plain-text line per blocking ticket: "Locate expired: #123 (Site) good through Oct 19".
function _p811CardStopLines(stops) {
  const todayKey = _p811Today();
  const L = window.Locates811;
  return stops.map(s => {
    const tn = s.permit.ticketNumber || _p811TicketNoFromFile(s.permit.name) || '?';
    const site = L ? L.siteNameOf(s.permit) : '';
    const who = `#${tn}${site ? ` (${site})` : ''}`;
    return s.reason === 'early'
      ? `Locate not active yet: ${who} starts ${_p811ShortDate(s.digStartOn, todayKey)}`
      : `Locate expired: ${who} good through ${_p811ShortDate(s.expiresOn, todayKey)}`;
  });
}
function _p811CardChipHtml(job, dayKey, opts) {
  const stops = _p811CardStops(job, dayKey);
  if (!stops.length) return '';
  const early = stops.every(s => s.reason === 'early');
  if (opts && opts.bar) {
    return `<div class="p811-card-flag" title="${early ? 'A linked locate ticket is not active until a later day' : 'A linked locate ticket expired before this day'}"><span class="material-icons" aria-hidden="true">block</span>${early ? 'LOCATE NOT ACTIVE YET' : 'LOCATE EXPIRED'}</div>`;
  }
  const tip = _p811CardStopLines(stops).join('\n') + '\nDon\'t dig without a valid ticket.';
  const label = early ? 'Locate not active yet' : 'Locate expired';
  return `<span class="assign-indicator p811-card-icon" role="button" tabindex="0" aria-label="${escapeHtml(label + '. ' + tip.replace(/\n/g, '. '))}" title="${escapeHtml(tip)}" onclick="event.stopPropagation(); window.DispatchModule.showP811CardPopover(event, '${escJsAttr(job.id)}', '${escJsAttr(String(dayKey))}')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}"><span class="material-icons" aria-hidden="true">block</span></span>`;
}
// Tap/click on the card icon: the same facts as the title, for touch screens.
function showP811CardPopover(event, jobId, dayKey) {
  ['dvir-missing-popover', 'jsa-missing-popover', 'assignment-popover', 'p811-card-popover'].forEach(id => document.getElementById(id)?.remove());
  const job = (DispatchState.jobs || []).find(j => j.id === jobId);
  const stops = _p811CardStops(job, dayKey);
  if (!stops.length) return;
  const early = stops.every(s => s.reason === 'early');
  const pop = document.createElement('div');
  pop.id = 'p811-card-popover';
  pop.setAttribute('role', 'dialog');
  pop.style.cssText = 'position:fixed;z-index:9999;background:#fff;border-radius:10px;box-shadow:0 8px 30px rgba(0,0,0,0.18),0 2px 8px rgba(0,0,0,0.08);padding:12px 16px;min-width:220px;max-width:320px;border-top:3px solid #DC2626;font-size:13px;';
  pop.innerHTML = `<div style="font-weight:800;color:#B91C1C;margin-bottom:6px;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:16px;" aria-hidden="true">block</span>${early ? 'Locate not active yet' : 'Locate expired'}</div>`
    + _p811CardStopLines(stops).map(t => `<div style="padding:3px 0;color:var(--text-primary);">${escapeHtml(t.replace(/^Locate (expired|not active yet): /, ''))}</div>`).join('')
    + `<div style="margin-top:6px;font-weight:700;color:#7F1D1D;">Don't dig without a valid ticket.</div>`;
  document.body.appendChild(pop);
  const anchor = event && event.target && event.target.closest ? event.target.closest('.p811-card-icon') : null;
  const rect = anchor ? anchor.getBoundingClientRect() : { top: 80, bottom: 80, left: 80, width: 0 };
  const pr = pop.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.left - pr.width / 2 + rect.width / 2;
  if (left < 8) left = 8;
  if (left + pr.width > window.innerWidth - 8) left = window.innerWidth - pr.width - 8;
  if (top + pr.height > window.innerHeight - 8) top = rect.top - pr.height - 6;
  pop.style.top = top + 'px';
  pop.style.left = left + 'px';
  setTimeout(() => {
    const dismiss = (e) => {
      if (!pop.contains(e.target)) { pop.remove(); document.removeEventListener('click', dismiss, true); }
    };
    document.addEventListener('click', dismiss, true);
  }, 10);
}

// ── TASK-9862 / TASK-10181 — Add Job: "N 811 tickets for <customer> aren't on a job yet"
// Shows as soon as a customer is picked (no title needed). One box: a matched
// potential (TASK-9887) or the strong site match (title >= 0.8) is the one-tap
// "Bring them in"; every other ticket is a single tap. Nothing is pre-ticked or
// attached without a click. Expired, renewed and already-held tickets are left out.
let _p811AttachDismissed = ''; // the customer id the nudge was closed for (this form)
let _p811NudgeOpen = false;
let _p811NudgeAll = false;
// Tickets of the form's customer that are on no job, not on this form, and can still be dug on.
function _p811NudgePool() {
  const L = window.Locates811;
  const custId = document.getElementById('job-customer')?.value || '';
  if (!L || !custId || !_p811ShowWarnings() || !is811Enabled() || window.isMember811Only?.() === true || is811OnlyMode() || !_p811CanStartJob()) return null; // TASK-10278: an advisory
  const todayKey = _p811Today();
  const onForm = new Set(_getJobPermitIds());
  const pool = (DispatchState.permits || []).filter(p => p && p.id && p.customerId === custId && !onForm.has(p.id)
    && !['renewed', 'expired'].includes(_p811StatusOf(p, todayKey)) && _permitHolders(p.id).length === 0);
  return { custId, pool };
}
// Title-vs-site score, the TASK-9862 rule (1 = same site key; >= 0.8 = strong).
function _p811SiteScore(L, title, site) {
  if (!title || !site) return 0;
  const tk = L.siteKey(title), gk = L.siteKey(site);
  return gk === tk ? 1 : Math.max(L.similarity(title, site), (gk.length >= 5 && tk.indexOf(gk) >= 0) ? 0.85 : 0);
}
function _p811WaitingGroupForForm() {
  const L = window.Locates811;
  const np = _p811NudgePool();
  const title = String(document.getElementById('job-title')?.value || '').trim();
  if (!np || title.length < 3) return null;
  let best = null;
  for (const g of L.groupTickets(np.pool)) {
    const score = _p811SiteScore(L, title, g.siteName);
    if (score >= 0.8 && (!best || score > best.score)) best = { g, score };
  }
  return best ? best.g : null;
}
// TASK-10181 — the box markup + ordering live in the lazy chunk views/p811JobNudge.js
// (never a static import: Pitfall #1 / perf budget). This side decides WHAT is
// offered and does every attach.
let _p811NudgeMod = null;
let _p811NudgeLoading = null;
let _p811NudgeSeq = 0;
function _p811NudgeH() {
  const L = window.Locates811;
  const todayKey = _p811Today();
  return { esc: escapeHtml, js: escJsAttr, date: (k) => _p811ShortDate(k, todayKey), statusOf: (p) => _p811StatusOf(p, todayKey),
    validExp: _p811ValidExpiresOn, ticketNo: _p811TicketNoFromFile, siteOf: (p) => (L ? L.siteNameOf(p) : ''), potKind: _p811PotKind,
    digLabel: (p) => _p811DigStartLabel(p, todayKey), utilLabel: _p811UtilLabel, siteScore: (t, s) => (L ? _p811SiteScore(L, t, s) : 0),
    projIdsOf: (id) => window.ProjectsModule?.getPermitProjectIds?.(id) || [] };
}
function _p811RenderJobAttach() {
  const box = document.getElementById('job-811-attach');
  if (!box) return;
  const seq = ++_p811NudgeSeq;
  const hide = () => { box.innerHTML = ''; box.classList.add('hidden'); };
  const pm = _p811PotentialForForm();
  const np = _p811NudgePool();
  // COD r1/r2: a potential offers only tickets no job holds (as p811JobUsePotential attaches), numbers included.
  const free = pm && pm.ticketIds ? (DispatchState.permits || []).filter(p => pm.ticketIds.includes(p.id) && !_permitHolders(p.id).length) : [];
  const potOk = free.length ? { ...pm, ticketIds: free.map(p => p.id), ticketNos: free.map(p => p.ticketNumber).filter(Boolean) } : null;
  let build;
  if (pm && pm.inUse) { // TASK-9887 — a potential in use keeps its own banner (Undo)
    const pot = _p811Pots().find(p => p.id === pm.id) || {};
    build = (m, h) => m.potBannerHtml({ inUse: true, pot, n: _p811FormPotentialIds.length }, h);
  } else if (!np && potOk && !document.getElementById('job-customer')?.value) { // no customer yet: the TASK-9887 offer
    build = (m, h) => m.potBannerHtml(potOk, h);
  } else {
    if (!np || _p811AttachDismissed === np.custId) { hide(); return; }
    const g = potOk ? null : _p811WaitingGroupForForm();
    if (!np.pool.length && !potOk) { hide(); return; }
    const model = { pool: np.pool, custName: _customerNameById(np.custId), potOk, g, summary: g ? _p811GroupSummary(g, _p811Today()) : null,
      open: _p811NudgeOpen, all: _p811NudgeAll, title: String(document.getElementById('job-title')?.value || '').trim(),
      projectId: document.getElementById('job-project-id')?.value || '', potIds: pm && pm.ticketIds };
    build = (m, h) => m.nudgeHtml(model, h);
  }
  const paint = (m) => {
    if (seq !== _p811NudgeSeq) return; // a newer render owns the box
    if (!m) { hide(); return; }
    box.innerHTML = build(m, _p811NudgeH());
    box.classList.remove('hidden');
  };
  if (_p811NudgeMod) { paint(_p811NudgeMod); return; }
  _p811NudgeLoading = _p811NudgeLoading || __ddChunkWrap(import('./p811JobNudge.js?v=10181001'))
    .then(m => (_p811NudgeMod = m))
    .catch(err => { _p811NudgeLoading = null; console.warn('[Dispatch] TASK-10181 p811JobNudge failed to load:', err?.message || err); return null; });
  _p811NudgeLoading.then(paint);
}
function p811JobFormSiteChanged() {
  clearTimeout(p811JobFormSiteChanged._t);
  p811JobFormSiteChanged._t = setTimeout(_p811RenderJobAttach, 250);
}
// Attach what fits under MAX_JOB_PERMIT_IDS and say what was left off (COD Edge 5:
// over the cap used to attach NOTHING).
function _p811AttachUpToCap(ids) {
  const cur = _getJobPermitIds();
  const add = [...new Set(ids || [])].filter(id => !cur.includes(id));
  const room = Math.max(0, MAX_JOB_PERMIT_IDS - cur.length);
  const take = add.slice(0, room);
  if (take.length) _setJobPermitIds([...cur, ...take]);
  const left = add.length - take.length;
  window.showToast?.(left ? `Attached ${take.length}. A job holds ${MAX_JOB_PERMIT_IDS} 811 tickets, so ${left} ${left === 1 ? 'was' : 'were'} left off.`
    : (take.length ? `Attached ${take.length}. Save the job to keep them.` : "That ticket can't go on this job now."), left || !take.length ? 'warning' : 'success', null, 9000);
  return take;
}
function p811JobAttachAll(key) {
  const g = _p811WaitingGroupForForm(); // re-derived: the customer / title / tickets may have moved
  if (g && g.key === key) _p811AttachUpToCap(g.ids);
  _p811RenderJobAttach();
}
// One ticket from the list, re-checked against the form's customer and the live ticket.
function p811JobAttachOne(id) {
  const np = _p811NudgePool();
  _p811AttachUpToCap(np && np.pool.some(p => p.id === id) ? [id] : []);
  _p811RenderJobAttach();
}
function p811JobAttachDismiss() { _p811AttachDismissed = document.getElementById('job-customer')?.value || ''; _p811RenderJobAttach(); }
function p811JobNudgeToggle() { _p811NudgeOpen = !_p811NudgeOpen; if (!_p811NudgeOpen) _p811NudgeAll = false; _p811RenderJobAttach(); }
function p811JobNudgeShowAll() { _p811NudgeAll = true; _p811RenderJobAttach(); }
function _p811ResetNudge() { _p811AttachDismissed = ''; _p811NudgeOpen = false; _p811NudgeAll = false; }


function removeJobPermit(permitId) {
  _setJobPermitIds(_getJobPermitIds().filter(id => id !== permitId));
}

/**
 * TASK-9212 → TASK-9648 — what saveJob writes for the 811 link.
 *
 * The hidden #job-permit holds the ids directly, so a Copy no longer loses its
 * ticket to a <select> that had no matching <option> yet: the ids are there from
 * the moment the copy fills the form. Once permits have loaded, an id with no
 * doc is a deleted ticket and is not written back.
 */
function _permitIdsForSave() {
  const ids = _getJobPermitIds();
  if (!DispatchState.permitsLoaded) return ids;
  const known = new Set((DispatchState.permits || []).map(p => p.id));
  return ids.filter(id => known.has(id) || _justUploadedPermitIds.has(id));
}

// ── The picker: "+ Add 811 ticket" ───────────────────────────────────────────
function open811Picker(opts) {
  // TASK-9767 — a Project passes { ids, customerId, subtitle, onApply, onUpload };
  // no opts (the job form's button) = the job form, unchanged.
  _p811PickCtx = (opts && typeof opts === 'object' && typeof opts.onApply === 'function') ? opts : null;
  _p811Selected = _p811PickCtx ? _normPermitIds(_p811PickCtx.ids) : _getJobPermitIds();
  const search = document.getElementById('p811-search');
  if (search) search.value = '';
  const custId = _p811CustomerId();
  const sub = document.getElementById('p811-subtitle');
  if (sub && _p811PickCtx?.subtitle) sub.textContent = String(_p811PickCtx.subtitle);
  else if (sub) sub.textContent = custId && _customerNameById(custId)
    ? 'Showing ' + _customerNameById(custId) + "'s tickets first."
    : 'Pick every ticket that covers this job.';
  _render811PickerList();
  document.getElementById('modal-811-picker')?.classList.remove('hidden');
  setTimeout(() => search?.focus(), 50);
}

function _p811Row(p) {
  const checked = _p811Selected.includes(p.id);
  const holders = _permitHolders(p.id);
  const linked = holders.length === 0 ? 'Not on the board yet'
    : 'Linked to ' + (holders[0].title || 'a job') + (holders.length > 1 ? ' +' + (holders.length - 1) + ' more' : '');
  const meta = [p.ticketNumber ? '#' + p.ticketNumber : '', _p811SiteOf(p) ? 'Site: ' + _p811SiteOf(p) : '', _permitDateStr(p) ? 'Uploaded ' + _permitDateStr(p) : '', linked].filter(Boolean).join(' · ');
  const badge = _p811BadgeHtml(p); // TASK-9859
  return `
    <label class="p811-row${checked ? ' is-on' : ''}">
      <input type="checkbox" value="${escapeHtml(p.id)}" ${checked ? 'checked' : ''} onchange="window.DispatchModule.toggle811PickerItem(this.value, this.checked)">
      <span class="material-icons p811-pdf" aria-hidden="true">picture_as_pdf</span>
      <span class="p811-main">
        <span class="p811-name">${escapeHtml(p.name || p.pdfName || '811 ticket')}</span>
        <span class="p811-meta">${escapeHtml(meta)}</span>
        ${badge ? `<span class="p811-meta">${badge}</span>` : ''}
      </span>
    </label>`;
}

function _render811PickerList() {
  const list = document.getElementById('p811-list');
  if (!list) return;
  const q = String(document.getElementById('p811-search')?.value || '').trim().toLowerCase();
  const custId = _p811CustomerId(); // TASK-9767
  const custName = _customerNameById(custId);
  const all = (DispatchState.permits || []).filter(p => !q
    || [p.name, p.pdfName, p.customerName, _p811SiteOf(p), p.ticketNumber].some(s => String(s || '').toLowerCase().includes(q)));
  const count = document.getElementById('p811-apply');
  if (count) count.textContent = _p811Selected.length ? 'Done · ' + _p811Selected.length + ' linked' : 'Done';
  if (!DispatchState.permitsLoaded) { list.innerHTML = '<p class="p811-empty">Loading 811 tickets…</p>'; return; }
  if (all.length === 0) {
    list.innerHTML = `<p class="p811-empty">${q ? 'No tickets match "' + escapeHtml(q) + '".' : 'No 811 tickets uploaded yet. Use Upload new ticket below.'}</p>`;
    return;
  }
  const group = (title, rows, open) => rows.length === 0 ? '' : `
    <details class="p811-group" ${open ? 'open' : ''}>
      <summary><span>${escapeHtml(title)}</span><span class="p811-count">${rows.length}</span><span class="material-icons" aria-hidden="true">expand_more</span></summary>
      ${rows.map(_p811Row).join('')}
    </details>`;
  if (!custId) {
    list.innerHTML = group('All 811 tickets', all, true);
    return;
  }
  // TASK-9859 (Chris + Alysse 10/14) — the customer's tickets on NO job yet go
  // first, best match to this job's title on top, so linking is one tap. A ticket
  // already checked on this form counts as linked.
  const jobTitle = String(document.getElementById('job-title')?.value || '');
  const waiting = _p811WaitingForJob(all.filter(p => p.customerId === custId),
    (p) => _permitHolders(p.id).length > 0 || _getJobPermitIds().includes(p.id), jobTitle);
  const waitingIds = new Set(waiting.map(p => p.id));
  const mine = all.filter(p => p.customerId === custId && !waitingIds.has(p.id));
  const none = all.filter(p => !p.customerId);
  const other = all.filter(p => p.customerId && p.customerId !== custId);
  // The job's customer first and open; the rest collapsed unless a search is on
  // or the customer has nothing yet (then "Not assigned" is where tickets are).
  list.innerHTML =
    group('Waiting for a job', waiting, true) +
    group((custName || 'This customer') + "'s tickets", mine, waiting.length === 0) +
    group('Not assigned to a customer', none, !!q || mine.length === 0) +
    group('Other customers', other, !!q);
}

function toggle811PickerItem(permitId, on) {
  const id = String(permitId || '');
  if (!id) return;
  if (on) {
    if (!_p811Selected.includes(id)) {
      if (_p811Selected.length >= MAX_JOB_PERMIT_IDS) {
        window.showToast?.(`A ${_p811PickCtx ? 'project' : 'job'} can hold up to ${MAX_JOB_PERMIT_IDS} 811 tickets.`, 'warning');
        _render811PickerList();
        return;
      }
      _p811Selected.push(id);
    }
  } else {
    _p811Selected = _p811Selected.filter(x => x !== id);
  }
  // Repaint only the row state + the count — a full re-render would collapse
  // the group the dispatcher just opened.
  document.querySelectorAll('#p811-list input[type="checkbox"]').forEach(cb => {
    cb.closest('.p811-row')?.classList.toggle('is-on', cb.checked);
  });
  const count = document.getElementById('p811-apply');
  if (count) count.textContent = _p811Selected.length ? 'Done · ' + _p811Selected.length + ' linked' : 'Done';
}

function apply811Picker() {
  const ctx = _p811PickCtx;
  _p811PickCtx = null;
  if (ctx) { // TASK-9767 — a Project's picker writes the project, not the job form
    const ids = _p811Selected.slice();
    closeModal('811-picker');
    Promise.resolve().then(() => ctx.onApply(ids)).catch(e => console.warn('[Dispatch] 811 picker apply failed:', e));
    return;
  }
  _setJobPermitIds(_p811Selected);
  closeModal('811-picker');
}

// "Upload new ticket" — the 811 window, opened over the job form with the job's
// customer pre-filled. The new ticket is added to this form when the upload lands
// (the job may not exist yet, so the window's own job link stays empty).
function upload811FromPicker() {
  closeModal('811-picker');
  const pctx = _p811PickCtx;
  _p811PickCtx = null;
  if (pctx && typeof pctx.onUpload === 'function') { pctx.onUpload(); return; } // TASK-9767
  // TASK-9859 — the ticket is FOR this job: customer and job come from the form,
  // so nobody picks them twice. A saved job (job-id set) is linked directly; a
  // new one gets the ticket on the form and saveJob writes the link.
  const title = String(document.getElementById('job-title')?.value || '').trim();
  const customerId = document.getElementById('job-customer')?.value || '';
  const custName = _customerNameById(customerId);
  show811Submission({
    fromJobForm: true,
    formGen: _jobFormGen, // COD r2 — results attach only to THIS form instance
    customerId,
    jobId: document.getElementById('job-id')?.value || '',
    jobLabel: [title || 'this job', custName ? `(${custName})` : ''].filter(Boolean).join(' '),
  });
}

// ── 811 project dropdown ────────────────────────────────────────────────────
// "Project (optional)" on the upload form and on each saved ticket's edit row.
// The link is projects/{id}.permitIds (TASK-9767); every write goes through
// window.ProjectsModule.setPermitProject (Pitfall #1 — never imported). Options
// read the LIVE projects snapshot, so a project made a minute ago in another tab
// is listed; refreshProjectSelects() repaints these on every snapshot.

// Pure. Active projects (plus keepIds — the ticket's own, even if completed),
// the customer's first (customerIds has it, or it is the primary), then the
// rest. Each group sorted by name. Nothing is hidden by the customer.
function _811ProjectOptionGroups(projects, customerId, keepIds, q) {
  const keep = Array.isArray(keepIds) ? keepIds : [];
  const needle = String(q || '').trim().toLowerCase();
  const seen = new Set();
  const list = (Array.isArray(projects) ? projects : []).filter(p => {
    if (!p || !p.id || seen.has(p.id) || p.deleting === true) return false;
    if (p.status !== 'active' && !keep.includes(p.id)) return false;
    seen.add(p.id);
    return !needle || keep.includes(p.id)
      || [p.name, p.primaryCustomerName].some(s => String(s || '').toLowerCase().includes(needle));
  });
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  const isMine = p => !!customerId && ((Array.isArray(p.customerIds) && p.customerIds.includes(customerId)) || p.primaryCustomerId === customerId);
  return { mine: list.filter(isMine).sort(byName), other: list.filter(p => !isMine(p)).sort(byName) };
}

// The field. key = 'new' (upload form) or a permit id (edit row).
function _811ProjectFieldHtml(key, currentIds, custSelectId) {
  if (window.isMember811Only?.() === true) return '';
  if (window.canAccessProjects?.() === false) return ''; // TASK-9651 — no field = no project change (_811ProjectIntent → null)
  const sid = key === 'new' ? 'permit-project' : `edit-permit-project-${key}`;
  const cur = Array.isArray(currentIds) ? currentIds : [];
  const all = window.ProjectsModule?.getState?.()?.projects || [];
  const nameOf = id => (all.find(p => p && p.id === id) || {}).name || 'another project';
  const more = cur.length > 1 ? `<p class="form-hint" style="margin:0;color:var(--text-secondary);">Also on ${escapeHtml(cur.slice(1).map(nameOf).join(', '))}. Changing the project here moves the ticket off all of them.</p>` : '';
  return `
      <div class="form-group p811-project-field" style="margin:0 0 12px;display:flex;flex-direction:column;gap:6px;">
        <label for="${escapeHtml(sid)}" style="margin:0;font-weight:600;">Project (optional)</label>
        <input type="search" id="${escapeHtml(sid)}-search" class="hidden" placeholder="Search projects..." aria-label="Search projects" style="width:100%;min-height:44px;" oninput="window.DispatchModule.fill811ProjectSelect('${escJsAttr(sid)}', true)">
        <select id="${escapeHtml(sid)}" data-p811-project="1" data-cust-src="${escapeHtml(custSelectId)}" data-keep="${escapeHtml(cur.join(','))}" data-original="${escapeHtml(cur[0] || '')}" style="width:100%;min-height:44px;"></select>
        ${more}
      </div>`;
}

// (Re)fills one project select, keeping what is picked. Without `force` it is
// skipped while focused (a native picker open on a phone collapses otherwise).
function fill811ProjectSelect(sid, force) {
  const sel = document.getElementById(sid);
  if (!sel || (!force && document.activeElement === sel)) return;
  const PM = window.ProjectsModule;
  const value = sel.dataset.filled ? sel.value : (sel.dataset.original || '');
  const keep = String(sel.dataset.keep || '').split(',').filter(Boolean);
  if (value && !keep.includes(value)) keep.push(value);
  const cust = document.getElementById(sel.dataset.custSrc || '')?.value || '';
  const search = document.getElementById(sid + '-search');
  const all = PM?.getState?.()?.projects || [];
  const { mine, other } = _811ProjectOptionGroups(all, cust, keep, search?.value);
  const opt = p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name || 'Untitled project')}`
    + `${p.status !== 'active' ? ` (${escapeHtml(p.status || 'not active')})` : ''}`
    + `${mine.includes(p) || !p.primaryCustomerName ? '' : ' — ' + escapeHtml(p.primaryCustomerName)}</option>`;
  const custName = cust ? (_customerNameById(cust) || 'This customer') : '';
  sel.innerHTML = `<option value="">${PM ? 'No project' : 'Loading projects…'}</option>`
    + (mine.length ? `<optgroup label="${escapeHtml(custName + "'s projects")}">${mine.map(opt).join('')}</optgroup>` : '')
    + (other.length ? (mine.length ? `<optgroup label="Other projects">${other.map(opt).join('')}</optgroup>` : other.map(opt).join('')) : '');
  sel.value = value;
  if (sel.value !== value) sel.value = '';
  sel.dataset.filled = '1';
  const canEdit = PM?.project811CanEdit?.() === true;
  sel.disabled = !canEdit;
  sel.title = canEdit || !PM ? '' : 'Only the owner or a dispatcher can change the project.';
  const n = all.filter(p => p && p.status === 'active').length;
  if (search) search.classList.toggle('hidden', !(canEdit && n > 8));
}

// refreshProjectSelects (every projects snapshot) → repaint the open window's selects.
// BUG 2026-10-20 (TITS): only the upload window (#modal-811-review) was
// repainted, so a ticket's Edit row (811 window library / Locates site sheet)
// drawn before the projects list arrived said "No project" for the rest of the
// session — "I could not go back in to save it". Every open 811 window now
// repaints; a select the user is IN repaints when they leave it, never under them.
const P811_PROJECT_MODALS = ['modal-811-review', 'modal-811-submission', 'modal-811-group'];
function _811RepaintWhenIdle(sel, paint) {
  if (document.activeElement !== sel) { paint(); return; }
  if (sel.dataset.p811PendingRepaint) return;
  sel.dataset.p811PendingRepaint = '1';
  sel.addEventListener('blur', () => { delete sel.dataset.p811PendingRepaint; if (sel.isConnected) paint(); }, { once: true });
}
// A saved ticket's Edit row drawn before the projects list arrived recorded its
// project as "none". While the user has not changed the select, re-read where
// the ticket really is, so the row shows it and "No project" can remove it.
// Upload rows (id ...-r_<rowKey>) are new tickets: nothing to re-read.
function _811ResyncEditOriginal(sel) {
  const pid = sel.id.startsWith('edit-permit-project-') ? sel.id.slice('edit-permit-project-'.length) : '';
  if (!pid || pid.startsWith('r_') || !window.ProjectsModule) return;
  if ((sel.value || '') !== (sel.dataset.original || '')) return; // the user picked something
  const cur = window.ProjectsModule.getPermitProjectIds?.(pid) || [];
  sel.dataset.keep = cur.join(',');
  sel.dataset.original = cur[0] || '';
  delete sel.dataset.filled;
}
// COD 10/21: an upload row whose pick was made (or kept) before its option was
// listed must SHOW that pick once the list arrives — never "No project" while
// the row still saves the project.
function _811ShowRowPick(sel) {
  const pfx = 'edit-permit-project-r_';
  if (!sel.id.startsWith(pfx)) return;
  const r = _p811Row811Key(sel.id.slice(pfx.length));
  if (!r || !r.projectId) return;
  const keep = String(sel.dataset.keep || '').split(',').filter(Boolean);
  if (!keep.includes(r.projectId)) { sel.dataset.keep = [...keep, r.projectId].join(','); fill811ProjectSelect(sel.id, true); }
  sel.value = r.projectId;
}
function _811RefreshProjectSelects() {
  // TASK-9887 — the Locates site sheet's project picker repaints too (a project
  // made or deleted in another tab); _p811RenderGroup(false) keeps ticks + pick.
  if (_p811GroupPanel === 'project') { try { _p811RefreshGroupIfOpen(); } catch (_) { /* advisory */ } }
  // TASK-9994 — the library's project folders come from this list: repaint the
  // tree when it lands (not over an open ticket Edit row).
  try {
    const lib = document.getElementById('modal-811-submission');
    if (lib && !lib.classList.contains('hidden') && !lib.querySelector('#permits-list .p811-edit')) renderPermitsList();
  } catch (_) { /* advisory: the folders repaint on the next render */ }
  for (const id of P811_PROJECT_MODALS) {
    const m = document.getElementById(id);
    if (!m || m.classList.contains('hidden')) continue;
    m.querySelectorAll('select[data-p811-project]').forEach(sel => _811RepaintWhenIdle(sel, () => { _811ResyncEditOriginal(sel); fill811ProjectSelect(sel.id, true); _811ShowRowPick(sel); }));
  }
}

// Mounts the field into `host` and wires the customer select to re-sort it.
function _811MountProjectField(host, key, currentIds, custSelectId) {
  if (!host) return;
  host.innerHTML = _811ProjectFieldHtml(key, currentIds, custSelectId);
  const sid = key === 'new' ? 'permit-project' : `edit-permit-project-${key}`;
  const cust = document.getElementById(custSelectId);
  if (cust) cust.onchange = () => fill811ProjectSelect(sid, true);
  fill811ProjectSelect(sid, true);
  if (!window.ProjectsModule) {
    Promise.resolve(window.ensureProjectsModule?.()).then(() => fill811ProjectSelect(sid, true)).catch(() => {});
  }
}

// Read the pick BEFORE any await (COD 10/14): a permits snapshot re-renders the
// list mid-save, and the edit row's select is gone by the time the save reads it.
function _811ProjectIntent(sid) {
  const sel = document.getElementById(sid);
  if (!sel || sel.disabled) return null;
  const next = sel.value || '';
  return next === (sel.dataset.original || '') ? null : { next };
}

// The save. null = nothing changed. Throws on a refused write (err.code
// 'permit-cap' = that project is full; nothing moved).
async function _811SaveProject(permitId, intent) {
  if (!intent) return null;
  const next = intent.next;
  const PM = window.ProjectsModule || await window.ensureProjectsModule?.();
  if (typeof PM?.setPermitProject !== 'function') throw Object.assign(new Error('Projects not loaded'), { code: 'no-projects' });
  const r = await PM.setPermitProject(permitId, next);
  if (r && r.failedUnlinks) {
    window.showToast?.(`The ticket is on the new project, but it couldn't be taken off ${r.failedUnlinks} old one${r.failedUnlinks > 1 ? 's' : ''}. Remove it from that project's Docs.`, 'warning', null, 8000);
  }
  if (r && r.failed) {
    window.showToast?.("The ticket is on the project, but it couldn't take the project's customer. Set the customer here.", 'warning', null, 8000);
  }
  return r;
}

function _811ProjectErrText(err) {
  return err && err.code === 'permit-cap'
    ? 'That project already holds 20 811 tickets, so the ticket was not added to it.'
    : "The ticket's project couldn't be changed. Try again, or link it from the project's Docs.";
}

// Meta-line label for a saved ticket ("· Project: X").
function _811ProjectLabel(permitId) {
  const ids = window.ProjectsModule?.getPermitProjectIds?.(permitId) || [];
  if (!ids.length) return '';
  const all = window.ProjectsModule?.getState?.()?.projects || [];
  const first = (all.find(p => p && p.id === ids[0]) || {}).name || 'a project';
  return ` · Project: ${escapeHtml(first)}${ids.length > 1 ? ` +${ids.length - 1} more` : ''}`;
}
// ── end 811 project dropdown ────────────────────────────────────────────────

function _fill811CustomerSelect(select, firstLabel, value, withNone) {
  if (!select) return;
  // A ticket belongs to the customer the work is for: billable customers only
  // (no vendors / subcontractors), keeping a stored value that is no longer one.
  // TASK-9548 slice 3 — the shared grouped builder (vendors dropped, a stored
  // value that is no longer billable kept), like every other customer picker.
  select.innerHTML = `<option value="">${escapeHtml(firstLabel)}</option>` +
    (withNone ? '<option value="__none">Not assigned to a customer</option>' : '') +
    window.PickerGroups.customerOptionsHtml(DispatchState.customers || [], {
      nameOf: c => c.name || '',
      keepId: [value],
    });
  select.value = value || '';
  if (select.value !== (value || '')) select.value = '';
}

// ── TASK-9859 — the 811 window's bulk uploader + shared view helpers ─────────
// Rows: one per picked PDF, each with its own name / customer / job / expiry.
// status: ready | invalid (never uploads) | queued | uploading | done | error.
// A row remembers what already went up (`uploaded`, `savedId`, `linked`) so a
// retry after a partial failure never uploads the same file or ticket twice.
const P811_MAX_ROWS = 50;
let _p811LibWaiting = false; // library "Waiting for a job" chip
function p811ToggleWaiting() { _p811LibWaiting = !_p811LibWaiting; renderPermitsList(); }
let _p811Rows = [];
let _p811RowSeq = 0;
let _p811Uploading = false;
let _p811Ctx = { fromJobForm: false, jobId: '', jobLabel: '', customerId: '', formGen: null };
const _p811EditState = {};
let _p811KeysCache = { src: null, tz: '', map: new Map() };

function _p811Tz() {
  return (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
}
function _p811Today() { return _p811DayKey(new Date(), _p811Tz()); }
// integ/2094 pre-ship (SAFETY) — the shared 811 rule's clock: this instant in the
// company zone, plus the ticket finder a RENEWAL needs (it is clear before its own
// start only while the ticket it renews is here, good and itself clear).
function _p811Clock() {
  return { now: Date.now(), tz: _p811Tz(), ticketOf: (id) => (DispatchState.permits || []).find(x => x && x.id === id) || null };
}

// jobDays is replaced (new array) on every snapshot, so identity is the cache key.
function _p811KeysByJobCached() {
  const src = DispatchState.jobDays || [];
  const tz = _p811Tz();
  if (_p811KeysCache.src !== src || _p811KeysCache.tz !== tz) _p811KeysCache = { src, tz, map: _p811DayKeysByJob(src, tz) };
  return _p811KeysCache.map;
}

function _p811BadgeHtml(permit, todayKey, noDate) {
  const t = todayKey || _p811Today();
  const e = _p811Expiry(permit && permit.expiresOn, t);
  if (e.state === 'none') return '';
  const icon = e.state === 'expired' ? 'error' : (e.state === 'soon' ? 'schedule' : 'event');
  const through = _p811ThroughText(permit, t);
  // BUG 2026-10-20 — the date itself shows under the badge (a title is never seen on a tablet).
  return `<span class="p811-exp p811-exp-${e.state}" title="${escapeHtml(through)}"><span class="material-icons" aria-hidden="true">${icon}</span>${escapeHtml(e.label)}</span>${noDate ? '' : `<span class="p811-exp-through" style="display:block;font-size:0.8rem;color:var(--text-secondary);">${escapeHtml(through)}</span>`}`;
}

// The day keys the open job form puts crews on: a saved job's loaded jobDays;
// a new job's start..end range (the days saveJob will create).
function _p811JobFormDayKeys() {
  const jobId = document.getElementById('job-id')?.value || '';
  if (jobId) {
    const days = (DispatchState.jobDays || []).filter(d => d && d.jobId === jobId);
    return _p811DayKeysByJob(days, _p811Tz()).get(jobId) || [];
  }
  const s = document.getElementById('job-start-date')?.value || '';
  const e = document.getElementById('job-end-date')?.value || s;
  if (!_p811ValidExpiresOn(s)) return [];
  const out = [];
  for (let k = s, n = 0; k && k <= (_p811ValidExpiresOn(e) || s) && n < 366; k = _p811AddDays(k, 1), n++) out.push(k);
  return out;
}

// { expired, late } counts for the open job form's linked tickets — feeds the
// Site group's summary line and its one-time auto-open.
function _p811MaybeOpenSiteGroup(ids) {
  if (!is811Enabled()) return; // TASK-9994
  const site = document.getElementById('job-acc-site');
  if (!site || !(ids || []).length) return;
  const w = _p811JobFormTicketWarnings(ids);
  const sig = (document.getElementById('job-id')?.value || 'new') + '|' + ids.join(',');
  if ((w.expired || w.late) && site.dataset.p811WarnOpened !== sig) { site.dataset.p811WarnOpened = sig; site.open = true; }
}

function _p811JobFormTicketWarnings(ids) {
  const todayKey = _p811Today();
  const linked = (ids || []).map(id => (DispatchState.permits || []).find(x => x.id === id)).filter(Boolean);
  const expired = linked.filter(p => _p811Expiry(p.expiresOn, todayKey).state === 'expired').length;
  const lateIds = new Set(_p811ExpiryConflicts(linked, _p811JobFormDayKeys(), todayKey).map(c => c.permit.id));
  const late = linked.filter(p => lateIds.has(p.id) && _p811Expiry(p.expiresOn, todayKey).state !== 'expired').length;
  return { expired, late };
}

// <option>s for one customer's jobs. No customer = "Pick a customer first".
// `keepId` (the ticket's current job) stays selectable even when it is not
// in the customer's list, so opening an edit row never silently drops a link.
// TASK-9994 (Chris 10/21: "every job from forever is going to be crazy") — the
// list is this customer's active / upcoming jobs (soonest first), then jobs
// that ended in the last 30 days (newest first). Older jobs only show for a
// typed search. `o.pot` adds "+ Add as potential job / project": the 811 side
// never puts a job on the board, it leaves a potential dispatch is offered.
const P811_POT_OPTS = '<optgroup label="Not on the board yet"><option value="__potjob">+ Add as potential job</option><option value="__potproj">+ Add as potential project</option></optgroup>';
function _p811JobOptionsHtml(customerId, selectedId, query, showOlder, keepId, o) {
  const pot = o && o.pot ? P811_POT_OPTS : '';
  if (!customerId) {
    const kept = keepId ? (DispatchState.jobs || []).find(j => j.id === keepId) : null;
    return '<option value="">Pick a customer first</option>' +
      (kept ? `<option value="${escapeHtml(kept.id)}" ${kept.id === selectedId ? 'selected' : ''}>${escapeHtml([formatJobNumber(kept), kept.title || 'Untitled job'].filter(Boolean).join(' · '))}</option>` : '') + pot;
  }
  const ch = _p811JobChoices(DispatchState.jobs || [], _p811KeysByJobCached(), {
    customerId, customerName: _customerNameById(customerId), todayKey: _p811Today(), tz: _p811Tz(),
    query, formatNumber: formatJobNumber,
  });
  const opt = (r) => `<option value="${escapeHtml(r.job.id)}" ${r.job.id === selectedId ? 'selected' : ''}>${escapeHtml(r.label)}</option>`;
  let html = '<option value="">No job (link it later)</option>';
  const listed = new Set(ch.recent.concat(ch.older).map(r => r.job.id));
  if (keepId && !listed.has(keepId)) {
    const kept = (DispatchState.jobs || []).find(j => j.id === keepId);
    if (kept) html += `<option value="${escapeHtml(kept.id)}" ${kept.id === selectedId ? 'selected' : ''}>${escapeHtml([formatJobNumber(kept), kept.title || 'Untitled job', 'linked now'].filter(Boolean).join(' · '))}</option>`;
  }
  if (!ch.total) return html + `<option value="" disabled>${query ? 'No jobs match that search' : 'No recent jobs for this customer'}</option>` + pot;
  const selInOlder = ch.older.some(r => r.job.id === selectedId);
  const active = ch.recent.filter(r => r.when.next);
  const ended = ch.recent.filter(r => !r.when.next);
  if (active.length) html += `<optgroup label="Active and upcoming">${active.map(opt).join('')}</optgroup>`;
  if (ended.length) html += `<optgroup label="Ended in the last ${P811_RECENT_DAYS} days">${ended.map(opt).join('')}</optgroup>`;
  if (!ch.recent.length && !query) html += '<option value="" disabled>No active or recent jobs. Search to find an older one.</option>';
  // Older jobs: only for a search (or the one already linked) — never the whole history.
  const olderShown = ch.older.filter(r => query || showOlder || r.job.id === selectedId);
  if (olderShown.length) html += `<optgroup label="Older">${olderShown.map(opt).join('')}</optgroup>`;
  else if (ch.older.length && !query && !selInOlder) html += `<option value="" disabled>${ch.older.length} older job${ch.older.length === 1 ? '' : 's'}: type to search</option>`;
  return html + pot;
}

function _p811Row811Key(key) { return _p811Rows.find(r => r.key === key); }

function _p811AddFiles(fileList) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (_p811Uploading) { window.showToast?.('Wait for the current upload to finish.', 'info'); return; }
  const files = Array.from(fileList || []);
  if (!files.length) return;
  const today = _p811Today();
  const validDays = window.AppState?.settings?.permit811ValidDays;
  const defCustomer = _p811Ctx.customerId || '';
  let dropped = 0;
  for (const file of files) {
    if (_p811Rows.length >= P811_MAX_ROWS) { dropped++; continue; }
    const isPdf = /\.pdf$/i.test(file.name || '') || file.type === 'application/pdf';
    const error = !isPdf ? 'Not a PDF. Only PDF files can be uploaded.'
      : (file.size > 10 * 1024 * 1024 ? 'Too large: 10MB maximum.'
      : (file.size === 0 ? 'This file is empty.' : ''));
    // TASK-9862 — ticket # and site come from the file name ("26139870_Bridger
    // Lostwood.pdf"), snapped to an existing site spelling; the PDF reader then
    // fills what the file name could not. The company "good for N days"
    // setting is only a FALLBACK, applied at save when nothing gave a date.
    const L = window.Locates811;
    const fileSite = L ? L.siteFromFile(file.name) : '';
    const renewOf = _p811Ctx.renewIds && _p811Ctx.renewIds.length ? _p811Ctx.renewIds[_p811Rows.filter(r => r.renewsId).length] || '' : '';
    const renewOld = renewOf ? (DispatchState.permits || []).find(p => p.id === renewOf) : null;
    _p811Rows.push({
      key: 'r' + (++_p811RowSeq), file, name: '', // TASK-9862 — saved as "ticket # · site" (else the file name)
      customerId: defCustomer || (renewOld && renewOld.customerId) || '', jobId: '', jobQuery: '', showOlder: false,
      siteName: (renewOld && L ? L.siteNameOf(renewOld) : '') || _p811SnapSite(fileSite, defCustomer),
      siteFrom: renewOld ? 'renew' : (fileSite ? 'file' : ''),
      ticketNumber: L ? L.ticketNoFromFile(file.name) : _p811TicketNoFromFile(file.name),
      digStartOn: '', expiresOn: '', validDaysFallback: _p811PrefillExpiry(today, validDays),
      renewsId: renewOf,
      ai: error ? null : { state: 'queued' }, aiFilled: {}, touched: {},
      status: error ? 'invalid' : 'ready', error, pct: 0,
      uploaded: null, savedId: '', linked: false, projectLinked: false,
    });
  }
  if (dropped) window.showToast?.(`Up to ${P811_MAX_ROWS} tickets at a time. ${dropped} file${dropped === 1 ? ' was' : 's were'} left out.`, 'warning', null, 7000);
  _p811OpenReview();
  _p811RenderRows();
  _p811PumpReads();
}

// ── TASK-9862 — read each PDF as it is added (<= 3 at a time) ──────────────
// Values come back as SUGGESTIONS: a field the user already typed in is never
// overwritten, every filled field is marked "Read from ticket — check it", and
// nothing is saved until the user presses Save. A failed read leaves the
// fields empty; the upload still works.
const P811_READ_CONCURRENCY = 3;
const P811_READ_MAX_BYTES = 7 * 1024 * 1024; // inline callable payload (base64) stays under 10 MB
let _p811ReadsInFlight = 0;
function _p811SnapSite(name, customerId) {
  const L = window.Locates811;
  const clean = L ? L.cleanText(name) : String(name || '').trim();
  if (!clean || !L) return clean;
  const hit = L.suggestSite(clean, [], customerId || '', _p811SiteCandidates(customerId));
  return hit ? hit.name : clean;
}
// Every known site name: other tickets' sites, active projects, this customer's jobs.
function _p811SiteCandidates(customerId) {
  const L = window.Locates811;
  const out = [];
  const seen = new Set();
  const add = (name, source, cust) => {
    const n = L ? L.cleanText(name) : String(name || '').trim();
    const k = L ? L.siteKey(n) : n.toLowerCase();
    if (!k || seen.has(k)) return;
    seen.add(k);
    out.push({ name: n, source, customerId: cust || '' });
  };
  for (const p of (DispatchState.permits || [])) add(L ? L.siteNameOf(p) : (p.siteName || ''), 'site', p.customerId);
  for (const p of _p811ActiveProjects()) add(p.name, 'project', p.primaryCustomerId);
  const custName = _customerNameById(customerId);
  for (const j of (DispatchState.jobs || [])) {
    if (customerId && !_p811JobIsCustomers(j, customerId, custName)) continue;
    add(j.title, 'job', j.customerId);
  }
  return out;
}
function _p811SiteDatalistHtml(id, customerId) {
  const opts = _p811SiteCandidates(customerId).slice(0, 300)
    .map(c => `<option value="${escapeHtml(c.name)}">${escapeHtml(c.source === 'site' ? 'Locate site' : c.source === 'project' ? 'Project' : 'Job')}</option>`).join('');
  return `<datalist id="${escapeHtml(id)}">${opts}</datalist>`;
}
function _p811PumpReads() {
  while (_p811ReadsInFlight < P811_READ_CONCURRENCY) {
    const r = _p811Rows.find(x => x.ai && x.ai.state === 'queued' && x.status !== 'invalid');
    if (!r) return;
    r.ai = { state: 'reading' };
    _p811ReadsInFlight++;
    _p811RenderRowRead(r);
    // TASK-10167 COD: a read that throws before it settles must not hold Save forever.
    _p811ReadRow(r).catch(() => { if (r.ai && r.ai.state === 'reading') { r.ai = { state: 'failed', message: "Couldn't read this ticket. Fill in the fields from the PDF." }; _p811RenderRowRead(r); } })
      .finally(() => { _p811ReadsInFlight--; _p811PumpReads(); _p811RenderActions(); });
  }
}
// TASK-10167 — the read itself lives in the lazy chunk views/p811Reads.js (not
// needed before first paint). A failed chunk load rejects here and
// _p811PumpReads marks the row failed (Save released for hand entry).
let _p811ReadsMod = null;
async function _p811ReadRow(r) {
  // A hung chunk fetch must not hold Save forever: reject after 15 s (row marked failed, Save back
  // for hand entry), but WAIT while window.ChunkReload is reloading or asking (same rule as app.js
  // _lazyImportWithRetry, TASK-10076) so its "updated" dialog never false-fails the read.
  const m = _p811ReadsMod || (_p811ReadsMod = await Promise.race([__ddChunkWrap(import('./p811Reads.js?v=10167002')), new Promise((_, no) => {
    const fire = () => (window.ChunkReload?.busy?.() ? setTimeout(fire, 1000) : no(new Error('811 reader chunk timed out')));
    setTimeout(fire, 15000);
  })]));
  return m.readRow(r, { maxBytes: P811_READ_MAX_BYTES, call: httpsCallable(functions, 'read811Ticket'), tenantId: DispatchState.tenantId, inRows: (x) => _p811Rows.includes(x),
    customers: () => DispatchState.customers, L: window.Locates811, renderRowRead: _p811RenderRowRead, renderStats: () => {}, applyRead: _p811ApplyRead });
}
// Fill what the user has not touched. Locked rows (uploading) are left alone.
function _p811ApplyRead(r) {
  if (_p811RowLocked(r)) return;
  const f = (r.ai && r.ai.fields) || {};
  const s = (r.ai && r.ai.suggest) || {};
  const put = (field, value) => {
    if (!value || r.touched[field]) return;
    if (r[field] && r[field] === value) { r.aiFilled[field] = value; return; }
    // The file name is the office's own naming: keep it unless the reader matched a known site.
    if (field === 'siteName' && r.siteFrom === 'file' && r.siteName && !['location', 'existing'].includes(s.siteSource)) return;
    if (field === 'siteName' && r.siteFrom === 'renew') return;
    if (field === 'ticketNumber' && r.ticketNumber && r.ticketNumber !== value) { r.aiFilled.ticketNumberMismatch = value; return; }
    r[field] = value;
    r.aiFilled[field] = value;
  };
  put('ticketNumber', f.ticketNumber);
  put('digStartOn', f.digStartOn);
  put('expiresOn', f.expiresOn);
  put('customerId', s.customerId);
  put('siteName', s.siteName);
  if (s.renews && s.renews.permitId && !r.renewsId && !r.touched.renewsId && _p811CanRenew()) {
    r.renewsId = s.renews.permitId;
    r.aiFilled.renewsId = s.renews.permitId;
  }
  if (r.status === 'error' && !r.savedId) { r.status = 'ready'; r.error = ''; }
  _p811RenderRowRead(r); // TASK-10168 (COD r1): sentence, mark and a reader-set "Replaces ticket" paint now; the full redraw may wait for a focused select
  _p811RenderRowsSoon();
}
// BUG 2026-10-20 (TITS, Alyse): a read lands 10-30 s after the drop and used to
// redraw EVERY row at once. A <select> the user was in (native picker open on a
// tablet / phone, dropdown open on a desktop) was destroyed under them, so the
// project they were picking was never recorded. Wait for that select to blur
// (once), then redraw. Text inputs keep today's caret-restoring redraw.
let _p811RenderDeferred = false;
function _p811RenderRowsSoon() {
  const box = document.getElementById('permit-upload-rows');
  const act = document.activeElement;
  if (box && act && act.tagName === 'SELECT' && box.contains(act)) {
    if (_p811RenderDeferred) return;
    _p811RenderDeferred = true;
    act.addEventListener('blur', () => {
      // The pick's change event fires before blur, so the row already holds it.
      // COD 10/21: re-check — the user may have gone straight into another row's select.
      setTimeout(() => { _p811RenderDeferred = false; _p811RenderRowsSoon(); }, 0);
    }, { once: true });
    return;
  }
  _p811RenderRows();
}
function _p811RenderRowRead(r) {
  if (_p811RevMod) _p811RevMod.paintRow(r, _p811RevH()); // TASK-10168: sentence, mark and cell outlines in place
}
function _p811CanRenew() {
  return !is811OnlyMode() && window.isMember811Only?.() !== true;
}

function p811DragOver(e) {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  document.getElementById('permit-upload-area')?.classList.add('is-drag');
}
function p811DragLeave() { document.getElementById('permit-upload-area')?.classList.remove('is-drag'); }
function p811Drop(e) {
  e.preventDefault();
  document.getElementById('permit-upload-area')?.classList.remove('is-drag');
  _p811AddFiles(e.dataTransfer && e.dataTransfer.files);
}

function _p811RowLocked(r) { return r.status === 'queued' || r.status === 'uploading' || r.status === 'done'; }
// TASK-10167 — rows whose ticket read is queued or running (a removed or invalid row never counts).
function _p811ReadingCount() { return _p811Rows.filter(r => r.status !== 'invalid' && r.ai && (r.ai.state === 'queued' || r.ai.state === 'reading')).length; }

// TASK-10168 — the review is one plain table (Ticket #, Customer, Site, Dig
// start, Good through + a status mark), drawn by the lazy chunk
// views/p811ReviewTable.js (never a static import: Pitfall #1 / perf budget).
// This side keeps the rows, every handler and the Save gate.
let _p811RevMod = null;
let _p811RevLoading = null;
function _p811LoadReview() {
  if (_p811RevMod) return Promise.resolve(_p811RevMod);
  // COD r2: bounded like _p811ReadRow — a hung fetch gives up after 15 s (Retry keeps the rows), but waits while window.ChunkReload is busy.
  _p811RevLoading = _p811RevLoading || Promise.race([__ddChunkWrap(import('./p811ReviewTable.js?v=10168002')), new Promise((_, no) => {
    const fire = () => (window.ChunkReload?.busy?.() ? setTimeout(fire, 1000) : no(new Error('811 review chunk timed out')));
    setTimeout(fire, 15000);
  })])
    .then(m => (_p811RevMod = m))
    .catch(err => { _p811RevLoading = null; console.warn('[Dispatch] TASK-10168 p811ReviewTable failed to load:', err?.message || err); return null; });
  return _p811RevLoading;
}
function _p811RevH() {
  const t = _p811Today();
  return { esc: escapeHtml, js: escJsAttr, today: t, date: (k) => _p811ShortDate(k, t), locked: _p811RowLocked, dupOf: _p811DupOf,
    printed: _p811RowPrinted, expHint: (r) => _p811ExpHintHtml('p811-exp-hint-' + r.key, _p811RowPrinted(r), r.expiresOn || '', t),
    sites: _p811SiteDatalistHtml, canRenew: _p811CanRenew(), only811: window.isMember811Only?.() === true, ctx: _p811Ctx,
    permits: DispatchState.permits || [], L: window.Locates811, tnOf: _p811TicketNoFromFile, jobLabel: _p811JobLabel };
}
function _p811JobLabel(id) {
  const j = id ? (DispatchState.jobs || []).find(x => x && x.id === id) : null;
  return j ? [formatJobNumber(j), j.title || 'Untitled job'].filter(Boolean).join(' · ') : '';
}

// TASK-9887 E2E — the same ticket uploaded twice saved two tickets and said
// nothing. A row whose ticket # is already on file (any ticket, renewed ones
// too) or on an EARLIER row of this batch is flagged, and Save skips it unless
// "Save a second copy anyway" is ticked.
function _p811TicketDigitsOf(p) {
  return String((p && (p.ticketNumber || _p811TicketNoFromFile(p.name) || _p811TicketNoFromFile(p.pdfName))) || '').replace(/\D+/g, '');
}
function _p811DupOf(r) {
  const tn = String((r && r.ticketNumber) || '').replace(/\D+/g, '');
  if (!r || r.savedId || tn.length < 5) return null;
  const onFile = (DispatchState.permits || []).find(p => p && p.id && _p811TicketDigitsOf(p) === tn);
  if (onFile) return { tn, where: `already on file${window.Locates811?.siteNameOf(onFile) ? ' for ' + window.Locates811.siteNameOf(onFile) : ''}` };
  const idx = _p811Rows.indexOf(r);
  const earlier = _p811Rows.slice(0, Math.max(0, idx)).find(x => x && x.status !== 'invalid' && String(x.ticketNumber || '').replace(/\D+/g, '') === tn);
  return earlier ? { tn, where: `also in this batch (${earlier.file?.name || 'another file'})` } : null;
}
function _p811RenderDupSlots() {
  if (!_p811RevMod) return;
  const h = _p811RevH();
  for (const r of _p811Rows) {
    const el = document.getElementById('p811-dup-' + r.key);
    if (el) el.innerHTML = r.status === 'invalid' ? '' : _p811RevMod.dupHtml(r, h);
    _p811RevMod.paintRow(r, h);
  }
}
function p811RowDupOk(key, on) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  r.dupOk = !!on;
  if (r.status === 'error') { r.status = 'ready'; r.error = ''; _p811RenderStatus(r); _p811RenderActions(); }
}

function _p811RenderStatus(r) {
  const el = document.getElementById('p811-status-' + r.key);
  if (el && _p811RevMod) { const h = _p811RevH(); el.innerHTML = _p811RevMod.statusHtml(r, h); _p811RevMod.paintRow(r, h); }
  const row = el?.closest('.p811-up-row');
  if (row) row.className = 'p811-up-row p811-rt-row is-' + r.status;
}

function _p811RenderActions() {
  const actions = document.getElementById('permit-upload-actions');
  const btn = document.getElementById('btn-save-permit');
  const clear = document.getElementById('btn-clear-permit-rows');
  const n = _p811Rows.filter(r => r.status === 'ready' || r.status === 'error').length;
  actions?.classList.toggle('hidden', _p811Rows.length === 0);
  const title = document.getElementById('p811-review-title'); // TASK-9862
  if (title) title.textContent = _p811Rows.length ? `Check ${_p811Rows.length} ticket${_p811Rows.length === 1 ? '' : 's'} before saving` : 'Add locate tickets';
  if (clear) clear.disabled = _p811Uploading;
  const reading = _p811ReadingCount();
  // TASK-10168 — "Reading 3 of 8" once at the top while any read runs; she keeps typing.
  const prog = document.getElementById('p811-review-progress');
  if (prog) {
    const all = _p811Rows.filter(r => r.status !== 'invalid' && r.ai).length;
    prog.innerHTML = reading ? `<span class="btn-spinner" aria-hidden="true"></span> Reading the tickets: ${all - reading} of ${all} done. You can check and type while it reads.` : '';
    prog.classList.toggle('hidden', !reading);
  }
  if (!btn) return;
  // TASK-10168 (COD r1): nothing saves before the table has drawn (a reader-set renewal must be on screen).
  const unseen = _p811Rows.length > 0 && !_p811RevMod;
  btn.disabled = _p811Uploading || n === 0 || reading > 0 || unseen;
  if (!_p811Uploading && reading) {
    btn.innerHTML = `<span class="btn-spinner"></span> Reading… ${reading} left`;
  } else if (_p811Uploading) {
    const left = _p811Rows.filter(r => r.status === 'queued' || r.status === 'uploading').length;
    btn.innerHTML = `<span class="btn-spinner"></span> Uploading… ${left} left`;
  } else {
    btn.innerHTML = `<span class="material-icons">save</span> ${n > 1 ? `Save ${n} tickets` : 'Save ticket'}`;
  }
}

function _p811RenderRowJob(r) {
  const sel = document.getElementById('p811-job-' + r.key);
  const q = document.getElementById('p811-jobq-' + r.key);
  if (q) q.classList.toggle('hidden', !r.customerId);
  if (!sel) return;
  // COD r2 (Edge Case): keepId = the chosen job, so a search that would hide it
  // never silently drops the link.
  // TASK-9994 — an 811-only login cannot link a job (rules), so it is offered
  // only the potential options; everyone else gets the customer's jobs too.
  const potOnly = window.isMember811Only?.() === true;
  sel.innerHTML = potOnly ? '<option value="">No job yet</option>' + P811_POT_OPTS
    : _p811JobOptionsHtml(r.customerId, r.jobId, r.jobQuery, r.showOlder, r.jobId, { pot: true });
  if (q && potOnly) q.classList.add('hidden');
  const want = r.potKind ? (r.potKind === 'project' ? '__potproj' : '__potjob') : (r.jobId || '');
  sel.value = want;
  if (sel.value !== want) { r.jobId = ''; r.potKind = ''; sel.value = ''; }
  sel.disabled = _p811RowLocked(r);
  _p811MoreSay(r); // TASK-10168: a customer change that drops the job never leaves a stale 'Job: …'
}
// TASK-10168 — a row's "More" line (job, project, renew) stays as the user left it.
function p811RowMoreOpen(key, open) {
  const r = _p811Row811Key(key);
  if (r) r.moreOpen = open === true;
}

function _p811RenderRows() {
  const box = document.getElementById('permit-upload-rows');
  if (!box) return;
  _p811RenderDeferred = false; // a full redraw supersedes a pending deferred one
  // TASK-9779 / TASK-9994 — the Job picker: an 811-only login cannot write jobs,
  // so it only offers "+ Add as potential job / project". From the job form the
  // job is already known, so no picker there.
  const showJob = !_p811Ctx.fromJobForm;
  // TASK-9862 — keep the caret where it was: a read landing re-renders rows.
  const act = document.activeElement;
  const keepId = act && box.contains(act) ? act.id : '';
  const keepSel = keepId && typeof act.selectionStart === 'number' ? [act.selectionStart, act.selectionEnd] : null;
  if (_p811Rows.length && !_p811RevMod) {
    // TASK-10168 — first draw: fetch the table chunk, then draw (the Save gate never waits on it).
    if (!box.querySelector('.p811-rt-loading')) box.innerHTML = '<p class="p811-rt-loading p811-empty"><span class="btn-spinner" aria-hidden="true"></span> Opening the tickets…</p>';
    _p811LoadReview().then(m => { if (m) _p811RenderRows(); else box.innerHTML = '<p class="p811-up-err">Could not open the ticket table. <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811RetryReview()">Try again</button></p>'; });
    _p811RenderActions();
    return;
  }
  box.innerHTML = _p811Rows.length ? _p811RevMod.tableHtml(_p811Rows, _p811RevH())
    : `<div class="permit-upload-area p811-drop" role="button" tabindex="0" onclick="document.getElementById('permit-file-input').click()"
        ondragover="window.DispatchModule.p811DragOver(event)" ondragleave="window.DispatchModule.p811DragLeave(event)" ondrop="window.DispatchModule.p811Drop(event)">
        <span class="material-icons" aria-hidden="true">upload_file</span><span>Pick or drop the ticket PDFs. PDF only, 10 MB max each.</span></div>`;
  for (const r of _p811Rows) {
    if (r.status === 'invalid') continue;
    const cs = document.getElementById('p811-cust-' + r.key);
    _fill811CustomerSelect(cs, 'No customer', r.customerId, false);
    if (cs && cs.value !== (r.customerId || '')) r.customerId = cs.value;
    if (showJob) _p811RenderRowJob(r);
    _p811MountRowProject(r);
  }
  if (keepId) {
    const el = document.getElementById(keepId);
    if (el) { try { el.focus({ preventScroll: true }); if (keepSel && el.setSelectionRange) el.setSelectionRange(keepSel[0], keepSel[1]); } catch (_) { /* date inputs */ } }
  }
  _p811RenderActions();
}

// TASK-9859 — a project by id from the projects module (if loaded). The 811
// Project dropdown itself is owned by the 811-project-dropdown lane; "Generate"
// reads whatever project the row / edit row has chosen.
function _p811ProjectById(id) {
  return id ? (window.ProjectsModule?.getState?.()?.projects || []).find(p => p && p.id === id) || null : null;
}

// What "Generate" knows for a row / an edit row.
function _p811NameParts({ customerId, projectId, jobId, pendingJobName, fileName }) {
  const job = jobId ? (DispatchState.jobs || []).find(j => j.id === jobId) : null;
  return {
    customerName: _customerNameById(customerId),
    projectName: _p811ProjectById(projectId)?.name || '',
    jobTitle: job ? (job.title || '') : '',
    jobNumber: job ? formatJobNumber(job) : '',
    pendingJobName,
    ticketNo: _p811TicketNoFromFile(fileName),
  };
}

function p811RowGenName(key) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  const jobId = _p811Ctx.fromJobForm ? _p811Ctx.jobId : r.jobId;
  const projectId = r.projectId || r.proposedProjectId || '';
  const parts = _p811NameParts({ customerId: r.customerId, projectId, jobId, pendingJobName: r.siteName, fileName: r.file?.name });
  if (_p811Ctx.fromJobForm && !jobId) parts.jobTitle = String(document.getElementById('job-title')?.value || '');
  const name = _p811BuildName(parts);
  if (!name) { window.showToast?.('Pick a customer, project or job first.', 'info'); return; }
  r.name = name;
  const el = document.getElementById('p811-name-' + r.key);
  if (el) el.value = name;
  if (r.status === 'error') { r.status = 'ready'; r.error = ''; _p811RenderStatus(r); _p811RenderActions(); }
}

function p811EditGenName(permitId) {
  const permit = (DispatchState.permits || []).find(p => p.id === permitId);
  const v = (id) => document.getElementById(id)?.value || '';
  const name = _p811BuildName(_p811NameParts({
    customerId: v(`edit-permit-customer-${permitId}`),
    projectId: v(`edit-permit-project-${permitId}`) || permit?.proposedProjectId || '',
    jobId: v(`edit-permit-job-${permitId}`) || _permitHolders(permitId)[0]?.id || '',
    pendingJobName: v(`edit-permit-site-${permitId}`),
    fileName: permit?.pdfName || '',
  }));
  if (!name) { window.showToast?.('Pick a customer, project or job first.', 'info'); return; }
  const el = document.getElementById(`edit-permit-name-${permitId}`);
  if (el) el.value = name;
}

// ── TASK-9859 — suggested project (typeahead) ──────────────────────────────
// Active projects, the chosen customer's first. Read-only here: the 811 window
// never creates or edits a project.
function _p811ActiveProjects() {
  const PM = window.ProjectsModule;
  const all = (PM?.getActiveProjects?.() || PM?.getState?.()?.projects || [])
    .filter(p => p && p.id && String(p.status || 'active').toLowerCase() === 'active');
  return all;
}
function _p811ProjectsForCustomer(customerId) {
  const all = _p811ActiveProjects();
  const isMine = (p) => !!customerId && ((Array.isArray(p.customerIds) && p.customerIds.includes(customerId)) || p.primaryCustomerId === customerId);
  return all.slice().sort((a, b) => (isMine(b) - isMine(a)) || String(a.name || '').localeCompare(String(b.name || '')));
}
function _p811ProjectDatalistHtml(id, customerId) {
  return `<datalist id="${escapeHtml(id)}">${_p811ProjectsForCustomer(customerId).map(p => `<option value="${escapeHtml(p.name || '')}"></option>`).join('')}</datalist>`;
}
// Typed text -> { proposedProjectId, proposedProjectName }.
function _p811ResolveProposed(text) {
  const clean = _p811CleanPendingName(text);
  if (!clean) return { proposedProjectId: '', proposedProjectName: '' };
  const hit = _p811ExactProject(clean, _p811ActiveProjects());
  return hit ? { proposedProjectId: hit.id, proposedProjectName: '' } : { proposedProjectId: '', proposedProjectName: clean };
}
const P811_KEPT_TEXT = 'Saved on this ticket. Dispatch will see it when they add the job and can create the project from it.';
function _p811ProposedHint(pp) {
  if (pp.proposedProjectId) return 'Existing project.';
  if (pp.proposedProjectName) return P811_KEPT_TEXT;
  return '';
}
function _p811ProposedLabel(permit) {
  if (permit.proposedProjectId) {
    const p = _p811ActiveProjects().find(x => x.id === permit.proposedProjectId) || _p811ProjectById(permit.proposedProjectId);
    return p ? (p.name || '') : '';
  }
  return permit.proposedProjectName || '';
}

// 811 project dropdown in a bulk row. Rendered directly (not via
// _811MountProjectField, which would replace the row's customer onchange).
function _p811RowProjectSid(r) { return 'edit-permit-project-r_' + r.key; }
function _p811MountRowProject(r) {
  const host = document.getElementById('p811-projhost-' + r.key);
  if (!host) return;
  host.innerHTML = _811ProjectFieldHtml('r_' + r.key, r.projectId ? [r.projectId] : [], 'p811-cust-' + r.key);
  const sid = _p811RowProjectSid(r);
  const sel = document.getElementById(sid);
  if (!sel) return;
  sel.dataset.original = ''; // a new ticket: any pick is a change
  if (r.projectId) { sel.dataset.keep = r.projectId; }
  fill811ProjectSelect(sid, true);
  // BUG 2026-10-20: this used to CLEAR r.projectId when the option was missing,
  // which is exactly the state for the first seconds after Dispatch opens (the
  // projects list streams in later — ~13 s on an iPad). The pick stays on the
  // row; data-keep re-adds its option as soon as the list arrives, and a project
  // deleted meanwhile is refused at save (setPermitProject 'not-found') and said.
  if (r.projectId) sel.value = r.projectId;
  if (_p811RowLocked(r)) sel.disabled = true;
  sel.onchange = () => p811RowProject(r.key, sel.value);
  if (!window.ProjectsModule) {
    Promise.resolve(window.ensureProjectsModule?.()).then(() => { fill811ProjectSelect(sid, true); if (r.projectId) sel.value = r.projectId; }).catch(() => {});
  }
}
// A picked project lends its MAIN customer when the row has none.
function p811RowProject(key, value) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  r.projectId = String(value || '');
  const p = _p811ProjectById(r.projectId);
  if (p && p.primaryCustomerId && !r.customerId) {
    r.customerId = p.primaryCustomerId;
    _fill811CustomerSelect(document.getElementById('p811-cust-' + r.key), 'No customer', r.customerId, false);
    _p811RenderRowJob(r);
  }
}

function p811RowProjectText(key, value) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  r.projectText = String(value || '');
  Object.assign(r, _p811ResolveProposed(r.projectText));
  const hint = document.getElementById('p811-pph-' + r.key);
  if (hint) hint.textContent = _p811ProposedHint(r);
  // An existing project brings its main customer when the ticket has none.
  const p = r.proposedProjectId ? _p811ActiveProjects().find(x => x.id === r.proposedProjectId) : null;
  if (p && p.primaryCustomerId && !r.customerId) {
    r.customerId = p.primaryCustomerId;
    _fill811CustomerSelect(document.getElementById('p811-cust-' + r.key), 'No customer', r.customerId, false);
    _p811RenderRowJob(r);
  }
}

// ── TASK-9859 — "Start from 811 ticket" (Add Job) and "Start job" (811 window)
function _p811WaitingTickets() {
  return (DispatchState.permits || []).filter(p => _permitHolders(p.id).length === 0 && !_getJobPermitIds().includes(p.id));
}
function open811StartPicker() {
  const list = document.getElementById('p811-start-list');
  if (!list) return;
  const custId = document.getElementById('job-customer')?.value || '';
  const rows = _p811WaitingTickets().slice().sort((a, b) =>
    ((b.customerId === custId) - (a.customerId === custId)) || String(_p811SiteOf(a) || a.name || '').localeCompare(String(_p811SiteOf(b) || b.name || '')));
  list.innerHTML = rows.length ? rows.map(p => {
    const proj = _p811ProposedLabel(p);
    const meta = [p.customerName || 'No customer', _p811SiteOf(p) ? 'Site: ' + _p811SiteOf(p) : '', proj ? (p.proposedProjectId ? 'Project: ' : 'Project to confirm: ') + proj : ''].filter(Boolean).join(' · ');
    return `<button type="button" class="p811-start-row" onclick="window.DispatchModule.p811StartFromTicket('${escJsAttr(p.id)}')">
        <span class="material-icons" aria-hidden="true" style="color:#B45309;">picture_as_pdf</span>
        <span class="p811-main"><span class="p811-name">${escapeHtml(p.name || p.pdfName || '811 ticket')}</span><span class="p811-meta">${escapeHtml(meta)}</span>${_p811BadgeHtml(p)}</span>
      </button>`;
  }).join('') : '<p class="p811-empty">No 811 tickets are waiting for a job.</p>';
  document.getElementById('modal-811-start')?.classList.remove('hidden');
}

function _p811ClearStartBanner() {
  const b = document.getElementById('job-811-start-banner');
  if (b) { b.innerHTML = ''; b.classList.add('hidden'); }
}

// Pre-fill a NEW job from a ticket's suggestions. Everything stays editable;
// nothing is created until the dispatcher saves (and a project only on
// "Create", through the job form's own new-project path).
// TASK-9994 (Chris 10/21: keep "Start a job here", but only for a login that
// can write jobs) — the owner, or an active member with the dispatch module
// that is not view-only / read-only. Mirrors firestore.rules dispOk (stricter:
// a view & upload only login is sent to a potential too). Everyone else who
// may touch 811 tickets gets "Add as potential job / project" (no job write).
function _p811CanStartJob() {
  if (!_p811CanRenew() || window.isPartner811ReadOnly?.() === true) return false;
  const st = window.AppState || {};
  const ma = st.memberAccess;
  if (st.isDemo === true || ma == null) return true;
  const ownerId = st.tenantData && st.tenantData.ownerId;
  if ((ownerId && st.user && st.user.uid === ownerId) || ma.role === 'owner') return true;
  if (ma.synthetic || (ma.status && ma.status !== 'active')) return false;
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false;
  return Array.isArray(ma.modules) ? ma.modules.includes('dispatch') : true; // legacy no-modules member = full access
}
// TASK-10181 (Chris 10/28: "the 'new project' shouldn't make the 811 user
// actually make a new project, it should add to a list that the system tries to
// match") — "Save as potential project", for every login that may write 811:
// the site's tickets go on a potential project (the open one for this customer +
// site, or a new one) and NOTHING else opens. The real project is made from
// Projects or the job form, where p811OfferPotential offers these tickets.
// A potential holds 20 tickets (rules potOk): the first 20 are saved and the
// rest are named, never silently dropped.
async function p811NewProjectFromGroup(key) {
  if (!_p811PotCanEdit()) return;
  const g = _p811FindGroup(key);
  if (!g) return;
  const todayKey = _p811Today();
  const all = g.permits.filter(p => !['renewed', 'expired'].includes(_p811StatusOf(p, todayKey))).map(p => p.id); // COD r1: never a dead ticket
  const ids = all.slice(0, 20);
  if (!ids.length) { window.showToast?.('Nothing to save.', 'info'); return; } // COD r2: all expired or renewed
  const leftOff = all.length - ids.length;
  const open = _p811Pots().find(p => _p811PotState(p) === 'open' && p.kind === 'project' && (p.customerId || '') === (g.customerId || '')
    && window.Locates811 && window.Locates811.siteKey(p.siteName || p.name || '') === window.Locates811.siteKey(g.siteName));
  const name = (open && open.name) || g.siteName;
  let placed = [];
  try {
    placed = await _p811PotPlaceTickets({ kind: 'project', name, site: g.siteName, customerId: g.customerId }, ids, open || null);
  } catch (e) {
    console.warn('[Dispatch] 811 potential project: write failed', e && e.code);
  }
  if (placed.length < ids.length) { window.showToast?.(placed.length ? `Saved ${placed.length} of ${ids.length} tickets on potential project "${name}". Try again for the rest.` : "Couldn't save the potential project. Try again.", 'error', null, 9000); return; }
  closeModal('811-group');
  window.showToast?.(`Saved as potential project "${name}" with ${placed.length} ticket${placed.length === 1 ? '' : 's'}. When the project is made, DispatchDeck offers them.`
    + (leftOff ? ` ${leftOff} more ${leftOff === 1 ? 'was' : 'were'} left off (20 per potential).` : ''), leftOff ? 'warning' : 'success', null, 9000);
}
async function p811StartFromTicket(permitId, moreIds) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (!_p811CanStartJob()) { p811NewPotentialForTickets([permitId].concat(moreIds || [])); return; } // TASK-9994
  const p = (DispatchState.permits || []).find(x => x.id === permitId);
  if (!p) return;
  // TASK-9862 — a site's other waiting tickets come along (Start a job from a site).
  const extra = (Array.isArray(moreIds) ? moreIds : []).filter(id => id && id !== permitId && (DispatchState.permits || []).some(x => x.id === id));
  const siteName = window.Locates811 ? window.Locates811.siteNameOf(p) : (p.pendingJobName || '');
  closeModal('811-start');
  if (window.isMember811Only?.() === true) return; // 811 logins never create jobs
  const m811 = document.getElementById('modal-811-submission');
  if (m811 && !m811.classList.contains('hidden')) closeModal('811-submission');
  const gen0 = _jobFormGen;
  try { await window.ensureProjectsModule?.(); } catch (_) { /* project match is optional */ }
  // COD r3 (Code Reviewer): a different job form opened during that await is not ours to fill.
  if (_jobFormGen !== gen0) { window.showToast?.('Another job form opened. Pick the ticket again.', 'info'); return; }
  const jobOpen = !document.getElementById('modal-job')?.classList.contains('hidden');
  if (!jobOpen || document.getElementById('job-id')?.value) showAddJob();

  const cs = document.getElementById('job-customer');
  if (cs && p.customerId && !cs.value) {
    cs.value = p.customerId;
    if (cs.value === p.customerId) onCustomerChange(); else cs.value = '';
  }
  const title = document.getElementById('job-title');
  if (title && !title.value && siteName) title.value = siteName;
  _setJobPermitIds([..._getJobPermitIds(), p.id, ...extra]);

  const custId = cs?.value || p.customerId || '';
  const lines = [];
  // Project: the ticket's pick, else the closest of this customer's projects.
  const projects = _p811ActiveProjects();
  const custProjects = projects.filter(x => !custId || (Array.isArray(x.customerIds) && x.customerIds.includes(custId)) || x.primaryCustomerId === custId);
  // TASK-9862 — an EXISTING project the 811 person explicitly picked is set
  // (and said so, with a change button); a name is only ever a "Did you mean"
  // that needs a click.
  if (p.proposedProjectId && projects.some(x => x.id === p.proposedProjectId)) {
    _p811StartSetProject(p.proposedProjectId);
    lines.push(`Project picked on the ticket: <strong>${escapeHtml(_p811ProposedLabel(p))}</strong>
      <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.p811StartClearProject()">Change</button>`);
  } else if (p.proposedProjectName || siteName) {
    const typedRaw = p.proposedProjectName || siteName;
    const best = _p811BestMatch(typedRaw, custProjects.length ? custProjects : projects, x => x.name);
    const typed = escapeHtml(typedRaw);
    const says = p.proposedProjectName ? 'Ticket says project' : 'Site';
    if (best || p.proposedProjectName) lines.push(best
      ? `${says} <strong>${typed}</strong>. Did you mean <strong>${escapeHtml(best.item.name || '')}</strong>?
         <button type="button" class="btn btn-primary btn-sm" onclick="window.DispatchModule.p811StartUseProject('${escJsAttr(best.item.id)}')">Use it</button>
         <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.p811StartCreateProject('${escJsAttr(permitId)}')">Create '${typed}'</button>`
      : `Ticket says project <strong>${typed}</strong> (not found).
         <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.p811StartCreateProject('${escJsAttr(permitId)}')">Create '${typed}'</button>`);
  }
  // Existing job: the ticket's job/site name close to one of this customer's jobs.
  if (siteName && custId) {
    const jobs = (DispatchState.jobs || []).filter(j => _p811JobIsCustomers(j, custId, _customerNameById(custId)));
    const best = _p811BestMatch(siteName, jobs, j => j.title || '');
    if (best) {
      const label = [best.item.title || 'Untitled job', formatJobNumber(best.item)].filter(Boolean).join(' ');
      // COD r3 (Domain): show WHERE, so a near-identical name at another site is caught by eye.
      const where = [best.item.location || best.item.wellName || '', (() => { const w = _p811JobWhen(best.item, _p811KeysByJobCached(), _p811Today(), _p811Tz()); return w.next ? 'next ' + _p811ShortDate(w.next, _p811Today()) : (w.last ? 'last ' + _p811ShortDate(w.last, _p811Today()) : ''); })()].filter(Boolean).join(' · ');
      lines.push(`Looks like an existing job: <strong>${escapeHtml(label)}</strong>${where ? ' (' + escapeHtml(where) + ')' : ''}. Check the ticket's site matches.
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.DispatchModule.p811StartAddToJob('${escJsAttr(best.item.id)}', '${escJsAttr(permitId)}')">Add to that job instead</button>`);
    }
  }
  const b = document.getElementById('job-811-start-banner');
  if (b) {
    b.innerHTML = `<div class="job-811-start-title"><span class="material-icons" aria-hidden="true">assignment</span> Started from ${extra.length ? (extra.length + 1) + ' locate tickets for ' + escapeHtml(siteName || p.name || '') : 'locate ticket ' + escapeHtml(p.ticketNumber || p.name || '')}</div>`
      + lines.map(l => `<div class="job-811-start-line">${l}</div>`).join('');
    b.classList.remove('hidden');
  }
}

function _p811StartSetProject(projectId) {
  try { refreshProjectSelects(); } catch (_) { /* options may already be there */ }
  const sel = document.getElementById('job-project-id');
  if (!sel) return false;
  sel.value = projectId;
  if (sel.value !== projectId) return false;
  onJobProjectChange(sel);
  return true;
}
function p811StartClearProject() { // TASK-9862 — "[change]" on a project picked on the ticket
  const sel = document.getElementById('job-project-id');
  if (!sel) return;
  sel.value = '';
  onJobProjectChange(sel);
  sel.focus();
}
function p811StartUseProject(projectId) {
  const ok = _p811StartSetProject(projectId);
  window.showToast?.(ok ? 'Project set. Save the job to keep it.' : "Couldn't select that project.", ok ? 'success' : 'error');
}
function p811StartCreateProject(permitId) {
  const p = (DispatchState.permits || []).find(x => x.id === permitId);
  setJobNewProjectMode(true);
  const n = document.getElementById('job-new-project-name');
  const nm = p ? (p.proposedProjectName || window.Locates811?.siteNameOf(p) || '') : '';
  if (n && nm) n.value = nm;
  n?.focus();
}
function p811StartAddToJob(jobId, permitId) {
  const job = (DispatchState.jobs || []).find(j => j.id === jobId);
  if (job && _jobPermitIdsOf(job).length >= MAX_JOB_PERMIT_IDS) {
    window.showToast?.(`That job already holds ${MAX_JOB_PERMIT_IDS} 811 tickets — nothing was added.`, 'error', null, 8000);
    return;
  }
  editJob(jobId);
  _setJobPermitIds([..._getJobPermitIds(), permitId]);
  window.showToast?.('Ticket added to this job. Save the job to keep it.', 'info', null, 7000);
}

function p811RemoveRow(key) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  _p811Rows = _p811Rows.filter(x => x !== r);
  _p811DropOrphanPdf(r); // TASK-9862 (COD) — bytes went up, the ticket doc never did
  _p811RenderRows();
}

// TASK-9862 (COD) — a row whose PDF reached Storage but whose ticket doc was
// never saved leaves an orphan file when the row is removed or cleared. Only a
// path this window uploaded into this tenant's permits folder is deleted.
function _p811DropOrphanPdf(r) {
  const sp = String((r && r.uploadedPath) || '');
  if (!sp || r.savedId || !sp.startsWith(`tenants/${DispatchState.tenantId}/permits/`)) return;
  r.uploadedPath = ''; r.uploaded = null;
  deleteObject(ref(storage, sp)).catch(e => console.warn('[Dispatch] orphan 811 PDF not deleted:', e && e.code));
}

function p811RowSet(key, field, value, rerender) {
  const r = _p811Row811Key(key);
  // TASK-9862 — the review row's fields. Typing in a field marks it TOUCHED:
  // a read landing later never overwrites it, and its "read" styling goes.
  if (!r || _p811RowLocked(r) || !['name', 'expiresOn', 'ticketNumber', 'siteName', 'digStartOn', 'renewsId'].includes(field)) return;
  r[field] = String(value || '');
  r.touched[field] = true;
  if (field === 'siteName') r.siteFrom = '';
  const el = document.getElementById(({ ticketNumber: 'p811-tn-', siteName: 'p811-site-', digStartOn: 'p811-dig-', expiresOn: 'p811-exp-', renewsId: 'p811-renew-' })[field] + r.key);
  if (el) el.classList.remove('p811-cell-read', 'p811-cell-err', 'p811-cell-doubt');
  if (_p811RevMod && ['digStartOn', 'expiresOn', 'ticketNumber'].includes(field)) _p811RevMod.paintRow(r, _p811RevH()); // TASK-10168: the row's sentence + mark follow the edit
  if (field === 'expiresOn') { const h = document.getElementById('p811-exp-hint-' + r.key); if (h) h.outerHTML = _p811ExpHintHtml('p811-exp-hint-' + r.key, _p811RowPrinted(r), r.expiresOn, _p811Today()); }
  if (r.status === 'error') { r.status = 'ready'; r.error = ''; _p811RenderStatus(r); _p811RenderActions(); }
  if (rerender || field === 'renewsId') _p811RenderRows();
  else if (field === 'ticketNumber') _p811RenderDupSlots(); // TASK-9887 duplicate flag follows the number
}

function p811RowCustomer(key, value) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  r.customerId = String(value || '');
  r.touched.customerId = true; // TASK-9862 — a later read never overrides it
  r.jobId = ''; r.jobQuery = ''; r.showOlder = false;
  const q = document.getElementById('p811-jobq-' + r.key);
  if (q) q.value = '';
  _p811RenderRowJob(r);
  if (document.getElementById(_p811RowProjectSid(r))) fill811ProjectSelect(_p811RowProjectSid(r), true); // 811 project dropdown: customer's first
}

function p811RowJobQuery(key, value) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  r.jobQuery = String(value || '');
  _p811RenderRowJob(r);
}

function p811RowJob(key, value) {
  const r = _p811Row811Key(key);
  if (!r || _p811RowLocked(r)) return;
  if (value === '__older') { r.showOlder = true; _p811RenderRowJob(r); return; }
  // TASK-9994 — "+ Add as potential job / project": saved after the ticket, never a job.
  if (value === '__potjob' || value === '__potproj') {
    r.potKind = value === '__potproj' ? 'project' : 'job';
    r.jobId = '';
    const h = document.getElementById('p811-pothint-' + r.key);
    if (h) { h.textContent = `Saved as a potential ${r.potKind} for dispatch to pick up. Nothing goes on the board.`; h.classList.remove('hidden'); }
    _p811MoreSay(r);
    return;
  }
  r.potKind = '';
  document.getElementById('p811-pothint-' + r.key)?.classList.add('hidden');
  r.jobId = String(value || '');
  _p811MoreSay(r);
  // Picking a job fills the ticket's customer from the job (a legacy name-only
  // match keeps the picked customer).
  const job = r.jobId ? (DispatchState.jobs || []).find(j => j.id === r.jobId) : null;
  if (job && job.customerId && job.customerId !== r.customerId) {
    r.customerId = job.customerId;
    const cs = document.getElementById('p811-cust-' + r.key);
    if (cs) _fill811CustomerSelect(cs, 'No customer', r.customerId, false);
    _p811RenderRowJob(r);
  }
}

// TASK-10168 — the "More" summary says whether the ticket is on a job (never hidden).
function _p811MoreSay(r) {
  const el = document.getElementById('p811-more-' + r.key);
  if (el) el.textContent = r.potKind ? `Potential ${r.potKind} (not on the board)` : (r.jobId ? 'Job: ' + (_p811JobLabel(r.jobId) || 'picked') : 'Not on a job yet');
}

// TASK-10168 — "Fill down", like Excel: the FIRST ticket's customer or site goes
// into every ticket below it (replaces the "for every ticket" boxes). Never the
// dates: those are read or typed per ticket. A renewal keeps its old ticket's site.
function p811FillDown(field) {
  if (!['customerId', 'siteName'].includes(field)) return;
  const live = _p811Rows.filter(r => r.status !== 'invalid');
  const v = String((live[0] && live[0][field]) || '');
  if (!v) { window.showToast?.(field === 'siteName' ? "Type the first ticket's site first." : "Pick the first ticket's customer first.", 'info'); return; }
  for (const r of live.slice(1)) {
    if (_p811RowLocked(r) || (field === 'siteName' && r.renewsId)) continue;
    if (field === 'customerId' && r.customerId !== v) { r.jobId = ''; r.jobQuery = ''; r.showOlder = false; r.potKind = ''; }
    r[field] = v; r.touched[field] = true; // a later read never overrides it
    if (field === 'siteName') r.siteFrom = '';
  }
  _p811RenderRows();
}

// ── TASK-9862 — reader audit, feedback loop, renew, transactional delete ────
// The reader's fields kept on the ticket for audit. Location/excavator text
// only — the reader never returns a caller name, phone or email.
function _p811ReadAuditFields(f) {
  const t = (v, n) => String(v == null ? '' : v).slice(0, n);
  return {
    ticketNumber: t(f.ticketNumber, 40), ticketType: t(f.ticketType, 60), relocateOf: t(f.relocateOf, 40),
    digStartOn: t(f.digStartOn, 10), digStartPrinted: t(f.digStartPrinted, 60),
    expiresOn: t(f.expiresOn, 10), expiresPrinted: t(f.expiresPrinted, 60),
    excavator: t(f.excavator, 120), workDoneFor: t(f.workDoneFor, 120), purpose: t(f.purpose, 160),
    address: t(f.address, 200), county: t(f.county, 60), state: t(f.state, 2), legal: t(f.legal, 400),
    place: t(f.place, 80), // TASK-9922 r3 — city / place / township of the dig site
    allMembersClear: f.allMembersClear === true,
    // TASK-9925 — "Waiting on utilities (N of M responded)" reads these.
    ...(Number.isInteger(f.membersCount) && f.membersCount > 0 && f.membersCount <= 40
      && Number.isInteger(f.membersClearCount) && f.membersClearCount >= 0 && f.membersClearCount <= f.membersCount
      ? { membersCount: f.membersCount, membersClearCount: f.membersClearCount } : {}),
  };
}

// Feedback loop (Chris 10/14 13:45): one row per field the reader FILLED —
// kept as read (accepted) or corrected. The next read for this company learns
// its naming from the corrections. Field values only, never PDF text.
const P811_FEEDBACK_FIELDS = ['ticketNumber', 'digStartOn', 'expiresOn', 'siteName', 'customerId'];
function _p811FeedbackRows(aiFilled, saved) {
  const rows = [];
  for (const field of P811_FEEDBACK_FIELDS) {
    const suggested = aiFilled && aiFilled[field];
    if (!suggested) continue;
    const final = String((saved && saved[field]) || '');
    rows.push({ field, suggested: String(suggested).slice(0, 120), final: final.slice(0, 120), accepted: String(suggested) === final });
  }
  return rows;
}
function _p811WriteFeedback(r, permitId, saved) {
  const uid = auth.currentUser?.uid;
  if (!uid || !r || !r.aiFilled) return;
  const rows = _p811FeedbackRows(r.aiFilled, saved);
  if (!rows.length) return;
  // Review fix: written server-side only (record811Feedback: capped, bounded).
  httpsCallable(functions, 'record811Feedback')({
    tenantId: DispatchState.tenantId, permitId,
    rows: rows.map(r => ({ field: r.field, suggested: r.suggested, final: r.final })),
  }).catch(e => console.warn('[Dispatch] 811 feedback not saved:', e && e.code));
}

// Every job (permitIds or the legacy permitId) and project holding a ticket,
// by QUERY (the loaded board window is a subset).
async function _p811HolderRefs(permitId) {
  const t = DispatchState.tenantId;
  const jobsCol = collection(db, 'tenants', t, 'jobs');
  const [byList, byLegacy, projs] = await Promise.all([
    getDocs(query(jobsCol, where('permitIds', 'array-contains', permitId))),
    getDocs(query(jobsCol, where('permitId', '==', permitId))),
    getDocs(query(collection(db, 'tenants', t, 'projects'), where('permitIds', 'array-contains', permitId))),
  ]);
  const jobIds = new Set();
  byList.forEach(d => jobIds.add(d.id));
  byLegacy.forEach(d => jobIds.add(d.id));
  const projectIds = new Set();
  projs.forEach(d => projectIds.add(d.id));
  return { jobIds: [...jobIds], projectIds: [...projectIds] };
}

function _p811RenewErrText(err) {
  const c = String((err && (err.code || err.message)) || '');
  if (/already-renewed/.test(c)) return 'the old ticket was already renewed';
  // TASK-9651 — the renew also moves the ticket on every project holding it,
  // and that project lookup needs Projects access.
  if (/permission/.test(c) && window.canAccessProjects?.() === false) return 'it may be on a project, and this login has no Projects access — ask someone with Projects';
  if (/permission/.test(c)) return 'this login cannot change jobs';
  if (/permit-cap/.test(c)) return 'a job or project is full';
  return 'try again';
}

// Renew: in ONE transaction, every job + project on oldId gets newId in the
// same place; the old ticket is marked renewed (kept for history) and points
// at the new one; the new one points back. A second renew of the same old
// ticket is refused (the transaction re-reads it).
// What the old ticket was before this renew. COD r1: a REPEAT of the same renew
// (already renewed by newId) keeps the value the first renew wrote — its status is
// 'renewed' by now, so reading it again would turn 'clear' into 'requested'.
function _p811StatusBeforeRenew(o) {
  if (o.status === 'renewed' || o.renewedById) return o.statusBeforeRenewed === 'clear' ? 'clear' : 'requested';
  return o.status === 'clear' ? 'clear' : 'requested';
}
async function _p811RenewMove(oldId, newId) {
  if (!oldId || !newId || oldId === newId) throw Object.assign(new Error('bad renew'), { code: 'invalid' });
  const t = DispatchState.tenantId;
  const L = window.Locates811;
  const oldRef = doc(db, 'tenants', t, 'permits', oldId);
  const newRef = doc(db, 'tenants', t, 'permits', newId);
  let moved = { jobs: [], projects: [] };
  await runTransaction(db, async (tx) => {
    const oldSnap = await tx.get(oldRef);
    const newSnap = await tx.get(newRef);
    if (!oldSnap.exists() || !newSnap.exists()) throw Object.assign(new Error('ticket gone'), { code: 'not-found' });
    const o = oldSnap.data() || {};
    if (o.renewedById && o.renewedById !== newId) throw Object.assign(new Error('already renewed'), { code: 'already-renewed' });
    const n0 = newSnap.data() || {};
    // COD (Code Reviewer): never overwrite a chain — the new ticket must not
    // already replace ANOTHER ticket, and must not itself be renewed.
    if (n0.renewsId && n0.renewsId !== oldId) throw Object.assign(new Error('new ticket already renews another'), { code: 'already-renewed' });
    if (n0.renewedById || n0.status === 'renewed') throw Object.assign(new Error('new ticket is renewed'), { code: 'already-renewed' });
    // Holders are queried AFTER the old ticket is read, so a concurrent move
    // that commits first either shows in this query or fails our commit.
    const h = await _p811HolderRefs(oldId);
    const jobSnaps = await Promise.all(h.jobIds.map(id => tx.get(doc(db, 'tenants', t, 'jobs', id))));
    const projSnaps = await Promise.all(h.projectIds.map(id => tx.get(doc(db, 'tenants', t, 'projects', id))));
    const plan = L.renewPlan(oldId, newId,
      jobSnaps.filter(s => s.exists()).map(s => ({ id: s.id, ...s.data() })),
      projSnaps.filter(s => s.exists()).map(s => ({ id: s.id, permitIds: (s.data() || {}).permitIds })));
    for (const j of plan.jobs) {
      if (j.permitIds.length > MAX_JOB_PERMIT_IDS) throw Object.assign(new Error('cap'), { code: 'permit-cap' });
      tx.update(doc(db, 'tenants', t, 'jobs', j.id), _jobPermitLinkFields(j.permitIds));
    }
    for (const p of plan.projects) tx.update(doc(db, 'tenants', t, 'projects', p.id), { permitIds: p.permitIds, updatedAt: Timestamp.now() });
    // integ/2094 pre-ship (SAFETY) — keep what the old ticket was: the renewal is
    // clear before its own dig start only while this one was clear and is still good.
    tx.update(oldRef, { status: 'renewed', renewedById: newId, renewedAt: Timestamp.now(), statusBeforeRenewed: _p811StatusBeforeRenew(o) });
    const n = newSnap.data() || {};
    const patch = { renewsId: oldId };
    if (!n.customerId && o.customerId) { patch.customerId = o.customerId; patch.customerName = o.customerName || null; }
    if (!n.siteName && L.siteNameOf(o)) patch.siteName = L.siteNameOf(o);
    tx.update(newRef, patch);
    moved = plan;
  });
  // Job history: one row per job that moved (best-effort, after the commit).
  for (const j of moved.jobs) {
    addDoc(collection(db, 'tenants', t, 'jobAuditLog'), {
      action: 'LOCATE_RENEWED', source: 'locates', timestamp: Timestamp.now(),
      user: window.AppState?.user?.email || 'unknown', jobId: j.id, oldPermitId: oldId, newPermitId: newId,
    }).catch(() => {});
  }
  return moved;
}

// Renew, same ticket number: only the good-through date moves. The old date is
// kept on the ticket (renewals[]) so the history shows both.
async function _p811RenewSameNumber(permitId, newExpiresOn) {
  const exp = _p811ValidExpiresOn(newExpiresOn);
  if (!exp) throw Object.assign(new Error('bad date'), { code: 'invalid' });
  const r = doc(db, 'tenants', DispatchState.tenantId, 'permits', permitId);
  await runTransaction(db, async (tx) => {
    const s = await tx.get(r);
    if (!s.exists()) throw Object.assign(new Error('gone'), { code: 'not-found' });
    const d = s.data() || {};
    const hist = Array.isArray(d.renewals) ? d.renewals.slice(-19) : [];
    hist.push({ at: Timestamp.now(), from: d.expiresOn || null, to: exp, by: window.AppState?.user?.email || null });
    tx.update(r, { expiresOn: exp, renewals: hist });
  });
}

// TASK-9925 (SAFETY) — the office marks a ticket clear once every utility
// member has marked or cleared (the one-call positive response), or takes that
// back. status 'clear' | 'requested' (rules p811Ok); every change is kept on
// the ticket (clearings[], last 20: at, to, by) so the old value is never lost.
async function p811SetUtilitiesClear(permitId, on) {
  if (window.isPartner811ReadOnly?.() === true) return;
  if (on && !(await window.showConfirm('Mark clear to dig?', 'Only when EVERY utility on the ticket has marked its lines or said it has none there (the one-call positive response).', { confirmText: 'Mark clear', cancelText: 'Cancel' }))) return;
  const r = doc(db, 'tenants', DispatchState.tenantId, 'permits', permitId);
  try {
    await runTransaction(db, async (tx) => {
      const s = await tx.get(r);
      if (!s.exists()) throw Object.assign(new Error('gone'), { code: 'not-found' });
      const d = s.data() || {};
      if (d.status === 'renewed' || (typeof d.renewedById === 'string' && d.renewedById)) throw Object.assign(new Error('renewed'), { code: 'failed-precondition' });
      const hist = Array.isArray(d.clearings) ? d.clearings.slice(-19) : [];
      hist.push({ at: Timestamp.now(), from: d.status || null, to: on ? 'clear' : 'requested', by: window.AppState?.user?.email || null, uid: auth.currentUser?.uid || null });
      tx.update(r, { status: on ? 'clear' : 'requested', clearings: hist });
    });
    window.showToast?.(on ? 'Marked clear to dig' : 'Marked waiting on utilities', 'success');
  } catch (e) {
    const c = String((e && e.code) || '');
    console.warn('[Dispatch] 811 clear change failed:', c);
    window.showToast?.(c === 'failed-precondition' ? 'This ticket was renewed. Mark the new ticket instead.'
      : (/permission-denied/.test(c) ? 'Not allowed to change 811 tickets on this login.' : 'Not saved. Try again.'), 'error');
  }
}

// Delete (COD): unlink every holder AND delete the doc in ONE transaction, so
// a ticket can never be gone while a job still points at it (or the reverse).
// The PDF is deleted from Storage only AFTER the doc is gone.
async function _p811DeleteTx(permitId) {
  const t = DispatchState.tenantId;
  const pRef = doc(db, 'tenants', t, 'permits', permitId);
  let storagePath = '';
  await runTransaction(db, async (tx) => {
    const ps = await tx.get(pRef);
    if (!ps.exists()) return;
    storagePath = String((ps.data() || {}).storagePath || '');
    const h = await _p811HolderRefs(permitId);
    const jobSnaps = await Promise.all(h.jobIds.map(id => tx.get(doc(db, 'tenants', t, 'jobs', id))));
    const projSnaps = await Promise.all(h.projectIds.map(id => tx.get(doc(db, 'tenants', t, 'projects', id))));
    for (const s of jobSnaps) {
      if (!s.exists()) continue;
      tx.update(s.ref, _jobPermitLinkFields(_jobPermitIdsOf(s.data()).filter(id => id !== permitId)));
    }
    for (const s of projSnaps) {
      if (!s.exists()) continue;
      const ids = (Array.isArray((s.data() || {}).permitIds) ? s.data().permitIds : []).filter(id => id !== permitId);
      tx.update(s.ref, { permitIds: ids, updatedAt: Timestamp.now() });
    }
    tx.delete(pRef);
  });
  if (storagePath && storagePath.startsWith(`tenants/${t}/permits/`)) {
    try { await deleteObject(ref(storage, storagePath)); }
    catch (e) { console.warn('[Dispatch] 811 PDF not deleted from storage:', e && e.code); }
  }
}

function _p811ErrorText(err) {
  if (err && err.code === 'p811/not-pdf') return 'This file is not a readable PDF.';
  const c = String((err && err.code) || '');
  if (/unauthorized|permission-denied/.test(c)) return 'Not allowed to upload 811 tickets on this login.';
  if (/quota|retry-limit|network|unavailable|canceled/.test(c)) return 'Upload did not finish (connection). Try again.';
  return 'Upload failed. Try again.';
}

// One row: file -> Storage (with progress), then the permit doc, then the job
// link. Each step is skipped on a retry if it already happened.
async function _p811UploadRow(r, tenantId) {
  r.status = 'uploading'; r.pct = 0; _p811RenderStatus(r);
  if (r.uploadedPath && !r.uploaded) {
    // COD r2 (Code Reviewer): the bytes went up last try; only the URL failed.
    r.uploaded = { storagePath: r.uploadedPath, pdfUrl: await getDownloadURL(ref(storage, r.uploadedPath)) };
  }
  if (!r.uploaded) {
    // %PDF- must appear in the first 1 KB (the PDF spec allows leading bytes).
    const head = new Uint8Array(await r.file.slice(0, 1024).arrayBuffer());
    if (!String.fromCharCode.apply(null, Array.from(head)).includes('%PDF-')) {
      throw Object.assign(new Error('not a pdf'), { code: 'p811/not-pdf' });
    }
    const safe = String(r.file.name || 'ticket.pdf').replace(/[^\w.\- ]+/g, '_').slice(-120);
    const storagePath = `tenants/${tenantId}/permits/${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${safe}`;
    const sref = ref(storage, storagePath);
    await _uploadPermitBlobWithProgress(sref, r.file, { contentType: 'application/pdf' }, (bytes) => {
      r.pct = r.file.size ? Math.min(99, Math.round(bytes * 100 / r.file.size)) : 0;
      _p811RenderStatus(r);
    });
    r.uploadedPath = storagePath;
    r.uploaded = { storagePath, pdfUrl: await getDownloadURL(sref) };
  }
  const jobId = _p811Ctx.fromJobForm ? _p811Ctx.jobId : r.jobId;
  if (!r.savedId) {
    let customerId = r.customerId || '';
    if (!customerId && jobId) customerId = (DispatchState.jobs || []).find(j => j.id === jobId)?.customerId || '';
    if (!customerId && r.projectId) customerId = _p811ProjectById(r.projectId)?.primaryCustomerId || ''; // 811 project dropdown
    const siteName = _p811CleanPendingName(r.siteName);
    // TASK-9862 — keep proposedProjectId ONLY when the site IS an existing
    // project (picked in the dropdown, or the typed site names one exactly).
    const exactProject = siteName ? _p811ExactProject(siteName, _p811ActiveProjects()) : null;
    const pickedProject = (_p811Ctx.fromJobForm || _p811Ctx.fromProject) ? null : (r.projectId || (exactProject && exactProject.id) || null);
    const ticketNumber = String(r.ticketNumber || '').replace(/[^0-9A-Za-z-]+/g, '').slice(0, 40);
    const readFields = r.ai && r.ai.state === 'done' ? r.ai.fields || {} : null;
    const permitDoc = {
      name: String(r.name || '').trim() || [ticketNumber, siteName].filter(Boolean).join(' · ') || _p811NameFromFile(r.file.name) || '811 ticket',
      pdfUrl: r.uploaded.pdfUrl,
      pdfName: r.file.name,
      storagePath: r.uploaded.storagePath,
      // TASK-9648 — optional; legacy tickets have neither field ("Not assigned").
      customerId: customerId || null,
      customerName: _customerNameById(customerId) || null,
      // TASK-9862 — first-class locate fields. The company "good for N days"
      // setting is only the fallback the row offered when no date was read.
      siteName: siteName || null,
      ticketNumber: ticketNumber || null,
      digStartOn: _p811ValidExpiresOn(r.digStartOn) || null,
      expiresOn: _p811ValidExpiresOn(r.expiresOn) || null, // TASK-9859
      proposedProjectId: pickedProject,
      createdAt: Timestamp.now(),
    };
    if (readFields) {
      // Audit: what the reader suggested (no caller name / phone / email is ever read).
      permitDoc.readFromPdf = {
        at: Timestamp.now(),
        model: String(r.ai.model || '').slice(0, 60),
        fields: _p811ReadAuditFields(readFields),
        suggested: { customerId: r.ai.suggest?.customerId || null, siteName: r.ai.suggest?.siteName || null, siteSource: r.ai.suggest?.siteSource || null },
      };
    }
    const permitRef = await addDoc(collection(db, 'tenants', tenantId, 'permits'), permitDoc);
    r.savedId = permitRef.id;
    r.savedCustomerId = customerId || ''; // TASK-9994 — a potential made from this row takes the ticket's customer
    _justUploadedPermitIds.add(permitRef.id); // COD 10/8 — survives a snapshot that has not caught up yet
    _p811WriteFeedback(r, permitRef.id, { ...permitDoc, customerId: customerId || '' }); // fire-and-forget
  }
  // TASK-9862 — "Renews an older ticket": every job + project link moves from
  // the old ticket to this one in ONE transaction; the old one stays as Renewed.
  if (r.renewsId && !r.renewed) {
    try { await _p811RenewMove(r.renewsId, r.savedId); r.renewed = true; }
    catch (renErr) {
      console.warn('[Dispatch] 811 renew move failed:', renErr && (renErr.code || renErr.message));
      return { linkNote: `Ticket saved, but the old ticket's jobs were not moved to it (${_p811RenewErrText(renErr)}). Open the site and press Renew again.` };
    }
  }
  // Optionally link to job — APPEND to its list (a job holds many tickets now).
  // The ticket itself is saved at this point; a refused link (an 811-only
  // member has no job write) must not read as "upload failed".
  let linkNote = '';
  if (jobId && !r.linked) {
    try {
      await _writeJobPermitIds(jobId, ids => [...ids, r.savedId]);
      r.linked = true;
    } catch (linkErr) {
      console.warn('[Dispatch] permit job link failed:', linkErr);
      linkNote = linkErr?.code === 'permit-cap'
        ? `"${r.name}" uploaded, but that job already holds ${MAX_JOB_PERMIT_IDS} tickets. Link it from another job.`
        : `"${r.name}" uploaded, but it couldn't be linked to that job. Add it from the job.`;
    }
  }
  // 811 project dropdown — the picked project gets it (projects module's writer).
  if (r.projectId && !r.projectLinked) {
    try { await _811SaveProject(r.savedId, { next: r.projectId }); r.projectLinked = true; }
    catch (projErr) {
      console.warn('[Dispatch] permit project link failed:', projErr && projErr.code);
      linkNote = linkNote || `"${r.name}" uploaded. ${_811ProjectErrText(projErr)}`;
    }
  }
  r.pct = 100;
  return { linkNote };
}

// Edit row (Uploaded Permits pencil): customer-first job list, like the uploader.
function _p811RenderEditJob(permitId, selectedId) {
  const sel = document.getElementById(`edit-permit-job-${permitId}`);
  if (!sel) return;
  const st = _p811EditState[permitId] || { showOlder: false, original: '' };
  const customerId = document.getElementById(`edit-permit-customer-${permitId}`)?.value || '';
  const q = document.getElementById(`edit-permit-jobq-${permitId}`)?.value || '';
  sel.innerHTML = _p811JobOptionsHtml(customerId, selectedId, q, st.showOlder, st.original, { pot: true }); // TASK-9994
  sel.value = selectedId || '';
  if (sel.value !== (selectedId || '')) sel.value = '';
}
function p811EditJobQuery(permitId) {
  const sel = document.getElementById(`edit-permit-job-${permitId}`);
  _p811RenderEditJob(permitId, sel?.value && !String(sel.value).startsWith('__') ? sel.value : '');
}

function p811EditCustomer(permitId) {
  const sel = document.getElementById(`edit-permit-job-${permitId}`);
  const cur = sel?.value || '';
  const customerId = document.getElementById(`edit-permit-customer-${permitId}`)?.value || '';
  const job = cur ? (DispatchState.jobs || []).find(j => j.id === cur) : null;
  // Keep the job when it belongs to the new customer; otherwise clear it.
  const keep = job && _p811JobIsCustomers(job, customerId, _customerNameById(customerId)) ? cur : '';
  _p811RenderEditJob(permitId, keep);
  if (document.getElementById(`edit-permit-project-${permitId}`)) fill811ProjectSelect(`edit-permit-project-${permitId}`, true);
}

function p811EditJob(permitId, value) {
  // TASK-9994 — "+ Add as potential job / project" opens the potential sheet
  // for this ticket; the ticket's job link is left as it was.
  if (value === '__potjob' || value === '__potproj') {
    const st = _p811EditState[permitId] || {};
    _p811RenderEditJob(permitId, st.original || '');
    p811NewPotentialForTickets([permitId], value === '__potproj' ? 'project' : 'job');
    return;
  }
  if (value === '__older') {
    if (_p811EditState[permitId]) _p811EditState[permitId].showOlder = true;
    _p811RenderEditJob(permitId, '');
    return;
  }
  const job = value ? (DispatchState.jobs || []).find(j => j.id === value) : null;
  const cs = document.getElementById(`edit-permit-customer-${permitId}`);
  if (job && job.customerId && cs && cs.value !== job.customerId) {
    _fill811CustomerSelect(cs, 'No customer', job.customerId, false);
    _p811RenderEditJob(permitId, value);
  }
}

function show811Submission(opts) {
  // TASK-9994 (Chris 10/21) — every 811 surface is gone while the company has
  // Locates turned off; every way in (toolbar, links, the 811-only screen,
  // project Docs) lands here.
  if (!is811Enabled()) { window.showToast?.('This is turned off for your company. An owner can turn it on in Settings.', 'info'); return; }
  const o = (opts && typeof opts === 'object') ? opts : {};
  // TASK-9859 — never wipe a batch that is still going up; just show it again.
  if (_p811Uploading) {
    document.getElementById('modal-811-review')?.classList.remove('hidden');
    return;
  }
  _811UploadForJobForm = o.fromJobForm === true;
  _p811Ctx = {
    fromJobForm: _811UploadForJobForm,
    jobId: _811UploadForJobForm ? String(o.jobId || '') : '',
    jobLabel: String(o.jobLabel || ''),
    customerId: String(o.customerId || ''),
    formGen: Number.isFinite(o.formGen) ? o.formGen : null,
    fromProject: typeof o.onUploaded === 'function', // TASK-9767 path
    // TASK-9862 — Renew: the new PDFs replace these tickets, in order.
    renewIds: Array.isArray(o.renewIds) ? o.renewIds.filter(id => typeof id === 'string' && id) : [],
  };
  _p811Rows = [];
  const input = document.getElementById('permit-file-input');
  if (input) input.value = '';
  // TASK-9805 — a compliance partner LOOKS: no upload form / Upload button, a one-line note instead.
  const readOnly811 = window.isPartner811ReadOnly?.() === true;
  document.getElementById('permit-upload-section')?.classList.toggle('hidden', readOnly811);
  document.getElementById('permit-readonly-note')?.classList.toggle('hidden', !readOnly811);
  // TASK-9994 — the 811 settings live in Dispatch Settings; the gear opens them
  // there. Not for a partner or an 811-only login (they cannot save them).
  document.getElementById('btn-p811-settings')?.classList.toggle('hidden', readOnly811 || window.isMember811Only?.() === true);
  { const ss = document.getElementById('p811-sort'); if (ss) ss.value = _p811CurrentSort(); }
  document.querySelector('#modal-811-submission .p811-upload-btn')?.classList.toggle('hidden', readOnly811);

  // TASK-9648 — customer: optional on upload, pre-filled from the job when
  // opened from one. The list FILTER defaults to that customer from a job,
  // "All" from the toolbar (TASK-9859: the window is a library, never gated).
  _fill811CustomerSelect(document.getElementById('permit-customer-filter'), 'All customers', o.customerId || '', true);
  { const pf = document.getElementById('permit-project-filter'); if (pf) pf.value = ''; } // TASK-10180

  // TASK-9767 — opened from a Project: each new ticket goes on that project.
  _811UploadCb = typeof o.onUploaded === 'function' ? o.onUploaded : null;
  const txt = _811UploadCb && o.hint ? String(o.hint)
    : (_811UploadForJobForm ? `New tickets go on ${_p811Ctx.jobLabel || 'the job you are editing'}.`
    : (_p811Ctx.renewIds.length ? `Pick the new ticket PDF${_p811Ctx.renewIds.length > 1 ? 's' : ''} from the one-call website. Each one replaces an old ticket and takes over its jobs.` : ''));
  for (const id of ['permit-job-form-hint', 'p811-review-hint']) {
    const hint = document.getElementById(id);
    if (hint) { hint.textContent = txt; hint.classList.toggle('hidden', !txt); }
  }

  _p811RenderRows();
  // TASK-9862 — from a job form, a project or a Renew the user came to ADD
  // tickets: open the review sheet straight away. The toolbar opens the page.
  if (_p811Ctx.fromJobForm || _p811Ctx.fromProject || _p811Ctx.renewIds.length) {
    _p811OpenReview();
    return;
  }
  renderPermitsList();
  document.getElementById('modal-811-submission').classList.remove('hidden');
}

function _p811OpenReview() {
  const m = document.getElementById('modal-811-review');
  if (!m) return;
  _p811LoadReview(); // TASK-10168 — the table chunk, while the first read runs
  m.classList.remove('hidden');
}

function p811CloseReview() {
  if (_p811Uploading) { closeModal('811-review'); return; }
  const unsaved = _p811Rows.some(r => r.status !== 'done' && r.status !== 'invalid');
  if (!unsaved) { clearPermitFile(); return; }
  _confirm811('Discard these tickets?', 'They have not been saved yet.', 'Discard').then(ok => { if (ok) clearPermitFile(); });
}

function handlePermitFile(e) {
  const input = e && e.target;
  _p811AddFiles(input && input.files);
  if (input) input.value = ''; // the same file can be picked again after a Remove
}

function clearPermitFile() {
  if (_p811Uploading) return;
  for (const r of _p811Rows) _p811DropOrphanPdf(r); // TASK-9862 (COD)
  _p811Rows = [];
  _p811RenderRows();
  closeModal('811-review');
}

async function savePermit() {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (_p811Uploading) return;
  // TASK-9859 — "Upload all" (the line above). One bad row never blocks the others; a second
  // click while a batch is going up does nothing (no duplicate tickets).
  const todo = _p811Rows.filter(r => r.status === 'ready' || r.status === 'error');
  if (!todo.length) {
    window.showToast?.(_p811Rows.length ? 'Fix or remove the tickets marked in red.' : 'Pick at least one PDF to upload.', 'error');
    return;
  }
  // TASK-10167 slice 2a — a row saved while its read is still running is locked
  // (_p811RowLocked), so the late dates were dropped and the ticket was stored
  // with NO dates, which the hard stops and the morning email skip. Wait (the
  // Save button says "Reading N tickets…" until every read is done or failed).
  if (_p811ReadingCount() || !_p811RevMod) { _p811RenderActions(); return; } // TASK-10168: and never before the review table has drawn
  const go = [];
  for (const r of todo) {
    r.name = String(r.name || '').trim();
    if (r.expiresOn && !_p811ValidExpiresOn(r.expiresOn)) { r.status = 'error'; r.error = 'The good-through date is not a valid date.'; continue; }
    if (r.digStartOn && !_p811ValidExpiresOn(r.digStartOn)) { r.status = 'error'; r.error = 'The dig start date is not a valid date.'; continue; }
    if (r.digStartOn && r.expiresOn && r.digStartOn > r.expiresOn) { r.status = 'error'; r.error = 'Dig start is after the good-through date. Check the ticket.'; continue; }
    // TASK-10168 (COD, all lanes) — an undated ticket is never judged by the hard stop
    // (l811HardStops) and the morning email skips it: it is not saved without both dates.
    const noDig = !_p811ValidExpiresOn(r.digStartOn), noExp = !_p811ValidExpiresOn(r.expiresOn);
    if (noDig || noExp) { r.status = 'error'; r.error = `Not saved: type the ${noDig && noExp ? 'dig start and good-through dates' : (noDig ? 'dig start date' : 'good-through date')} from the ticket.`; continue; }
    const dup = r.dupOk ? null : _p811DupOf(r); // TASK-9887 — never a silent second copy
    if (dup) { r.status = 'error'; r.error = `Not saved: ticket #${dup.tn} is ${dup.where}. Remove this row, or tick "Save a second copy anyway".`; continue; }
    r.status = 'queued'; r.error = '';
    go.push(r);
  }
  if (!go.length) { _p811RenderRows(); return; }
  // COD r2 (Edge Case): from the job form the ids land on the FORM, where
  // _normPermitIds would silently drop anything past the cap. Refuse first.
  if (_p811Ctx.fromJobForm) {
    const room = MAX_JOB_PERMIT_IDS - _getJobPermitIds().length;
    if (go.length > room) {
      for (const r of go) { r.status = 'ready'; }
      window.showToast?.(room > 0 ? `This job can take ${room} more 811 ticket${room === 1 ? '' : 's'}. Remove some rows first.` : `This job already holds ${MAX_JOB_PERMIT_IDS} 811 tickets.`, 'error', null, 8000);
      _p811RenderRows();
      return;
    }
  }

  _p811Uploading = true;
  _p811RenderRows();
  const tenantId = DispatchState.tenantId;
  let result;
  try {
    result = await runPermitUploadPool(go, (r) => _p811UploadRow(r, tenantId), {
      concurrency: PERMIT_UPLOAD_CONCURRENCY,
      onSettled: () => _p811RenderActions(),
    });
  } finally {
    _p811Uploading = false;
  }
  const notes = [];
  for (const res of (result?.results || [])) {
    if (!res) continue;
    const r = res.item;
    if (res.ok) {
      r.status = 'done';
      if (res.value?.linkNote) notes.push(res.value.linkNote);
    } else {
      r.status = 'error';
      r.error = _p811ErrorText(res.error);
      console.warn('[Dispatch] 811 upload failed:', r.file?.name, res.error);
    }
  }
  const done = go.filter(r => r.status === 'done');
  // TASK-9767 — opened from a Project's Docs tab: every uploaded ticket goes on
  // that project (its own writer, via the callback it passed in).
  let projectCbFailed = 0;
  if (_811UploadCb && done.length) {
    for (const r of done) {
      try { await _811UploadCb(r.savedId); }
      catch (projErr) { projectCbFailed++; console.warn('[Dispatch] permit project link failed:', projErr); }
    }
    if (projectCbFailed) notes.push(`${projectCbFailed} ticket${projectCbFailed === 1 ? '' : 's'} uploaded but not added to the project. Link from the project's Docs.`);
  }
  // TASK-9994 — rows marked "+ Add as potential job / project" become potentials
  // (never jobs): one per customer + site + kind, holding those tickets.
  { const potFail = await _p811SavePotentialsFromRows(done);
    if (potFail) notes.push(`${potFail} potential${potFail === 1 ? '' : 's'} not saved. Use "Add as potential" on the site.`); }
  // Opened from the job form's "Upload new ticket": the tickets go on THAT form.
  // COD r2 (Code Reviewer): only the SAME job-form instance the window was
  // opened from — a different job opened meanwhile must not receive them.
  const jobModalOpen = !document.getElementById('modal-job')?.classList.contains('hidden')
    && _p811Ctx.formGen !== null && _p811Ctx.formGen === _jobFormGen;
  if (_p811Ctx.fromJobForm && jobModalOpen && done.length) {
    _setJobPermitIds([..._getJobPermitIds(), ...done.map(r => r.savedId)]);
  }
  const failed = _p811Rows.filter(r => r.status === 'error' || r.status === 'invalid').length;
  _p811Rows = _p811Rows.filter(r => r.status !== 'done');
  _p811RenderRows();
  renderPermitsList();
  if (failed) {
    window.showToast?.(`${done.length} uploaded, ${failed} not uploaded. The ones left in the list say why.`, done.length ? 'warning' : 'error', null, 8000);
  } else if (notes.length) {
    window.showToast?.(notes[0] + (notes.length > 1 ? ` (+${notes.length - 1} more)` : ''), 'warning', null, 8000);
  } else {
    const renewed = done.filter(r => r.renewed).length;
    window.showToast?.((done.length === 1 ? 'Saved 1 locate ticket' : `Saved ${done.length} locate tickets`)
      + (renewed ? ` · renewed ${renewed}` : ''), 'success');
  }
  if (_p811Ctx.fromJobForm && jobModalOpen && done.length && !failed) {
    _811UploadForJobForm = false;
    closeModal('811-review');
  } else if (_811UploadCb && done.length && !failed && !projectCbFailed) {
    _811UploadCb = null;
    closeModal('811-review');
  } else if (!_p811Rows.length) {
    closeModal('811-review'); // TASK-9862 — all saved: back to the Locates page
  }
  _p811RefreshGroupIfOpen();
}

// TASK-9648 — every job that links a ticket, by QUERY (the loaded board window is
// a subset: a ticket can sit on a job from last spring), unioned with the loaded
// holders. Returns { ids:Set, complete:boolean } — complete=false means the query
// was refused, so the set may be missing jobs and a destructive caller must stop.
async function _queryPermitHolderIds(permitId) {
  const ids = new Set(_permitHolders(permitId).map(j => j.id));
  try {
    const jobsCol = collection(db, 'tenants', DispatchState.tenantId, 'jobs');
    const [byList, byLegacy] = await Promise.all([
      getDocs(query(jobsCol, where('permitIds', 'array-contains', permitId))),
      getDocs(query(jobsCol, where('permitId', '==', permitId)))
    ]);
    byList.forEach(d => ids.add(d.id));
    byLegacy.forEach(d => ids.add(d.id));
    return { ids, complete: true };
  } catch (qErr) {
    console.warn('[Dispatch] permit holder query failed:', qErr);
    return { ids, complete: false };
  }
}

async function _confirm811(title, message, confirmText, danger = true) {
  if (typeof window.showConfirm === 'function') {
    return !!(await window.showConfirm(title, message, { confirmText, danger }));
  }
  return confirm(message);
}

async function deletePermit(permitId) {
  // firestore.rules: delete is dispatch/admin only (an 811-only member cannot
  // unlink the jobs a ticket is on).
  if (is811OnlyMode()) { window.showToast?.('Ask a dispatcher to delete 811 tickets.', 'info'); return; }
  // COD 10/12 (Domain): the confirm says how many jobs lose the ticket — a
  // crew could be digging under one of them today. Query by jobId, not the
  // loaded board window (COD 10/8).
  const holders = await _queryPermitHolderIds(permitId);
  // TASK-9767 — and every PROJECT that links it, by query. No ProjectsModule or
  // a refused query = nothing deleted (an orphaned id would sit on a project).
  const PM = window.ProjectsModule || await window.ensureProjectsModule?.();
  const projHolders = typeof PM?.projectsHoldingPermit === 'function'
    ? await PM.projectsHoldingPermit(permitId) : { ids: new Set(), complete: false };
  if (!holders.complete || !projHolders.complete) {
    // TASK-9651 — without Projects the project check is refused by the rules
    // every time, so "try again" would never work: say who can do it.
    const msg = (holders.complete && window.canAccessProjects?.() === false)
      ? "This ticket may be on a project, and this login can't see Projects, so nothing was deleted. Ask someone with Projects access to delete it."
      : "Couldn't check which jobs use this ticket, so nothing was deleted. Try again.";
    window.showToast?.(msg, 'error', null, 9000);
    return;
  }
  const n = holders.ids.size;
  const onJobs = n === 0 ? 'It is not on any job.' : (n === 1 ? 'It comes off the 1 job it is on.' : `It comes off all ${n} jobs it is on.`);
  const np = projHolders.ids.size; // TASK-9767
  const onProjects = np === 0 ? '' : (np === 1 ? ' It comes off 1 project too.' : ` It comes off ${np} projects too.`);
  if (!(await _confirm811('Delete locate ticket', `Delete this ticket? ${onJobs}${onProjects} This cannot be undone.`, 'Delete'))) return;

  try {
    // TASK-9862 (COD) — every job + project unlink AND the doc delete commit
    // together (one transaction); the PDF leaves Storage only after that.
    await _p811DeleteTx(permitId);
    _justUploadedPermitIds.delete(permitId);

    // And from an open job form.
    if (_getJobPermitIds().includes(permitId)) removeJobPermit(permitId);

    window.showToast?.('Ticket deleted', 'success');
  } catch (err) {
    console.error('[Dispatch] Error deleting permit:', err);
    window.showToast?.('Failed to delete permit', 'error');
  }
}


async function renamePermit(permitId) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  // Show inline edit UI for this permit (name, customer, job link, expiry)
  const permit = DispatchState.permits.find(p => p.id === permitId);
  if (!permit) return;

  // TASK-9648 — find the row by its permit id. The list is FILTERED by customer
  // now, so an index into DispatchState.permits would open the wrong row.
  // TASK-9862 — the row is in the site detail OR the "needs a site name" list.
  const item = Array.from(document.querySelectorAll('#p811-group-body .permit-list-item, #permits-list .permit-list-item'))
    .find(el => el.dataset.permitId === permitId && el.offsetParent !== null)
    || Array.from(document.querySelectorAll('#permits-list .permit-list-item')).find(el => el.dataset.permitId === permitId);
  if (!item) return;
  item.classList.remove('p811-tick'); // the editor is a column, not the ticket grid

  // TASK-9212 — a ticket can be on more than one job (Copy to New Job carries
  // it; TASK-9648 lets a job hold several). A native <select> holds one value, so
  // it shows the first holder; the hint below names the rest, and `data-original`
  // is what lets savePermitEdit tell "the dispatcher REASSIGNED this" from "the
  // dispatcher renamed it and never touched the dropdown". Without that
  // distinction a rename-only Save unlinks every other holder and silently
  // strips the copy's ticket.
  const linkedJobs = _permitHolders(permit.id);
  const linkedJob = linkedJobs[0];
  const sharedHint = linkedJobs.length > 1 ? `
      <p class="form-hint" style="margin:0;color:var(--text-secondary);">Also on ${linkedJobs.length - 1} other job${linkedJobs.length > 2 ? 's' : ''}: ${escapeHtml(linkedJobs.slice(1).map(j => j.title).join(', '))}. Changing the job here moves the ticket off all of them.</p>` : '';
  // TASK-9859 — a ticket with no customer on a job that has one shows the job's
  // customer here, so Save fixes the "No customer" ticket.
  // COD r2 (Edge Case): only when every loaded job holding it agrees on ONE
  // customer — the backfill skips a split ticket, and so does this.
  const holderCustomers = [...new Set(linkedJobs.map(j => j.customerId).filter(Boolean))];
  const startCustomer = permit.customerId || (holderCustomers.length === 1 ? holderCustomers[0] : '');
  _p811EditState[permitId] = { showOlder: false, original: linkedJob?.id || '' };
  const pid = escapeHtml(permitId);

  item.innerHTML = `
    <div class="p811-edit" style="width:100%;display:flex;flex-direction:column;gap:8px;">
      <label for="edit-permit-name-${pid}" style="margin:0;font-weight:600;">Name</label>
      <div class="p811-name-row">
        <input type="text" id="edit-permit-name-${pid}" value="${escapeHtml(permit.name)}" placeholder="Ticket name">
        <button type="button" class="btn btn-secondary p811-gen" title="Fill the name from customer, project or job, and ticket number" onclick="window.DispatchModule.p811EditGenName('${escJsAttr(permitId)}')"><span class="material-icons" aria-hidden="true">auto_fix_high</span>Generate</button>
      </div>
      <label for="edit-permit-customer-${pid}" style="margin:0;font-weight:600;">Customer</label>
      <select id="edit-permit-customer-${pid}" onchange="window.DispatchModule.p811EditCustomer('${escJsAttr(permitId)}')"></select>
      <div id="edit-permit-project-host-${pid}"></div><!-- 811 project dropdown -->
      ${window.isMember811Only?.() === true ? `<button type="button" class="btn btn-secondary" style="min-height:44px;align-self:flex-start;" onclick="window.DispatchModule.p811NewPotentialForTickets(['${escJsAttr(permitId)}'])"><span class="material-icons" aria-hidden="true">lightbulb</span> Add as potential job / project</button>` : `<label for="edit-permit-job-${pid}" style="margin:0;font-weight:600;">Job</label>
      <input type="search" id="edit-permit-jobq-${pid}" placeholder="Search this customer's jobs" aria-label="Search this customer's jobs" style="min-height:44px;font-size:16px;" oninput="window.DispatchModule.p811EditJobQuery('${escJsAttr(permitId)}')">
      <select id="edit-permit-job-${pid}" data-original="${escapeHtml(linkedJob?.id || '')}" onchange="window.DispatchModule.p811EditJob('${escJsAttr(permitId)}', this.value)"></select>
      ${sharedHint}`}
      <!-- TASK-9994 — Site name is low priority (Chris 10/21): kept and saved (it
           groups tickets and feeds potential / connector matching), folded away. -->
      <details class="p811-rev-more"><summary>Site name${window.Locates811?.siteNameOf(permit) ? ': ' + escapeHtml(window.Locates811.siteNameOf(permit)) : ' (optional)'}</summary>
      <label for="edit-permit-site-${pid}" style="margin:0;font-weight:600;">Site name (the locate site)</label>
      <input type="text" id="edit-permit-site-${pid}" list="edit-permit-sl-${pid}" value="${escapeHtml(window.Locates811?.siteNameOf(permit) || '')}" maxlength="120" autocomplete="off" placeholder="Type or pick a site">
      ${_p811SiteDatalistHtml('edit-permit-sl-' + permitId, permit.customerId || '')}
      </details>
      <div class="p811-up-grid">
        <div><label for="edit-permit-tn-${pid}" style="margin:0;font-weight:600;">Ticket #</label>
        <input type="text" inputmode="numeric" id="edit-permit-tn-${pid}" class="p811-mono" value="${escapeHtml(permit.ticketNumber || _p811TicketNoFromFile(permit.name) || '')}" maxlength="40"></div>
        <div><label for="edit-permit-dig-${pid}" style="margin:0;font-weight:600;">Dig start</label>
        <input type="date" id="edit-permit-dig-${pid}" value="${escapeHtml(_p811ValidExpiresOn(permit.digStartOn))}"></div>
      </div>
      <label for="edit-permit-exp-${pid}" style="margin:0;font-weight:600;">Good through (last full day)</label>
      <input type="date" id="edit-permit-exp-${pid}" value="${escapeHtml(_p811ValidExpiresOn(permit.expiresOn))}" oninput="window.DispatchModule.p811EditExpHint('${escJsAttr(permitId)}')" onchange="window.DispatchModule.p811EditExpHint('${escJsAttr(permitId)}')">
      ${_p811ExpHintHtml('edit-permit-exp-hint-' + permitId, _p811EditPrinted(permit), _p811ValidExpiresOn(permit.expiresOn) || '', _p811Today())}
      <div style="display:flex;gap:8px;">
        <button class="btn btn-primary" style="min-height:44px;" onclick="window.DispatchModule.savePermitEdit('${escJsAttr(permitId)}')">
          <span class="material-icons" style="font-size:16px;">check</span> Save
        </button>
        <button class="btn btn-secondary" style="min-height:44px;" onclick="window.DispatchModule.renderPermitsList(); window.DispatchModule.p811RefreshGroup()">Cancel</button>
      </div>
    </div>
  `;
  _fill811CustomerSelect(document.getElementById(`edit-permit-customer-${permitId}`), 'No customer', startCustomer, false);
  _p811RenderEditJob(permitId, linkedJob?.id || '');
  // 811 project dropdown — rendered here, not via _811MountProjectField: that
  // helper assigns the customer select's onchange, which would replace
  // p811EditCustomer (it re-fills this select itself).
  { const host = document.getElementById(`edit-permit-project-host-${permitId}`);
    if (host) { host.innerHTML = _811ProjectFieldHtml(permitId, window.ProjectsModule?.getPermitProjectIds?.(permitId) || [], `edit-permit-customer-${permitId}`); fill811ProjectSelect(`edit-permit-project-${permitId}`, true); }
    // BUG 2026-10-20 — same as the upload row: start the projects list if it is not running.
    if (host && !window.ProjectsModule) {
      Promise.resolve(window.ensureProjectsModule?.()).then(() => {
        const sel = document.getElementById(`edit-permit-project-${permitId}`);
        if (!sel) return;
        _811ResyncEditOriginal(sel);
        fill811ProjectSelect(sel.id, true);
      }).catch(() => {});
    } }
}

async function savePermitEdit(permitId) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  const permit = DispatchState.permits.find(p => p.id === permitId);
  if (!permit) return;

  const newName = document.getElementById(`edit-permit-name-${permitId}`)?.value?.trim();
  const newJobId = document.getElementById(`edit-permit-job-${permitId}`)?.value || '';
  let newCustomerId = document.getElementById(`edit-permit-customer-${permitId}`)?.value || '';
  const expRaw = document.getElementById(`edit-permit-exp-${permitId}`)?.value || '';
  // TASK-9862 — ONE site field (old pendingJobName / proposedProjectName are
  // only read now), plus ticket # and dig start.
  const siteEl = document.getElementById(`edit-permit-site-${permitId}`);
  const newSite = siteEl ? _p811CleanPendingName(siteEl.value) : null;
  const tnEl = document.getElementById(`edit-permit-tn-${permitId}`);
  const newTicketNo = tnEl ? String(tnEl.value || '').replace(/[^0-9A-Za-z-]+/g, '').slice(0, 40) : null;
  const digRaw = document.getElementById(`edit-permit-dig-${permitId}`)?.value || '';
  const projIntent = _811ProjectIntent(`edit-permit-project-${permitId}`); // 811 project dropdown — before any await

  if (!newName) {
    window.showToast?.('Permit name is required', 'error');
    return;
  }
  if (expRaw && !_p811ValidExpiresOn(expRaw)) {
    window.showToast?.('The expiration date is not a valid date', 'error');
    return;
  }
  // TASK-9859 — a job picked with no customer set brings its customer along.
  if (newJobId && !newCustomerId) {
    newCustomerId = (DispatchState.jobs || []).find(j => j.id === newJobId)?.customerId || '';
  }

  try {
    const tenantId = DispatchState.tenantId;

    // Update permit name + customer (TASK-9648 — relink to a customer) + expiry (TASK-9859)
    const permitPatch = {};
    if (newName !== permit.name) permitPatch.name = newName;
    if ((newCustomerId || null) !== (permit.customerId || null)) {
      permitPatch.customerId = newCustomerId || null;
      permitPatch.customerName = _customerNameById(newCustomerId) || null;
    }
    if ((expRaw || null) !== (_p811ValidExpiresOn(permit.expiresOn) || null)) {
      permitPatch.expiresOn = expRaw || null;
    }
    if (digRaw && !_p811ValidExpiresOn(digRaw)) { window.showToast?.('The dig start date is not a valid date', 'error'); return; }
    if ((digRaw || null) !== (_p811ValidExpiresOn(permit.digStartOn) || null)) permitPatch.digStartOn = digRaw || null;
    // (A ticket # shown from the old name is saved as the real field on Save.)
    if (newTicketNo !== null && (newTicketNo || null) !== (permit.ticketNumber || null)) permitPatch.ticketNumber = newTicketNo || null;
    if (newSite !== null && (newSite || '') !== (window.Locates811?.siteNameOf(permit) || '')) {
      permitPatch.siteName = newSite || null;
      // The site is now the ONE field: clear the old ones so they never disagree.
      if (permit.pendingJobName) permitPatch.pendingJobName = null;
      if (permit.proposedProjectName) permitPatch.proposedProjectName = null;
      const exact = newSite ? _p811ExactProject(newSite, _p811ActiveProjects()) : null;
      if (!projIntent && exact) permitPatch.proposedProjectId = exact.id; // the site IS an existing project
    }
    if (projIntent && projIntent.next) { permitPatch.proposedProjectId = projIntent.next; }
    else if (projIntent && !projIntent.next && permit.proposedProjectId) { permitPatch.proposedProjectId = null; } // "No project" clears the suggestion too
    if (Object.keys(permitPatch).length) {
      await updateDoc(doc(db, 'tenants', tenantId, 'permits', permitId), permitPatch);
      Object.assign(permit, permitPatch);
    }

    // Update job link — only if the dispatcher actually CHANGED it.
    //
    // 🔴 TASK-9212, and the gate is the important half. A ticket can legitimately
    // sit on several jobs, and this form is reached from a pencil icon whose
    // usual job is a RENAME; its <select> can only hold one of the N holders, so
    // unlinking-all on EVERY save would strip the ticket off every job the
    // dropdown could not represent. Compare against what the dropdown was OPENED
    // with (`data-original`). Equal = every existing link left exactly as it was.
    // Different = a deliberate reassignment: every previous holder loses THIS id
    // from its list (its other tickets stay), and the new job gains it.
    const jobSelect = document.getElementById(`edit-permit-job-${permitId}`);
    const originalJobId = jobSelect?.dataset?.original || '';
    if (newJobId !== originalJobId) {
      // COD 10/8 — EVERY holder by query, not just the loaded window, or a
      // job from months back keeps the ticket while the hint says it moved.
      const holders = await _queryPermitHolderIds(permitId);
      if (!holders.complete) throw new Error("permit holder query refused");
      // COD 10/12 (Code Reviewer): the destination gains the ticket FIRST. If
      // that write is refused (20-ticket cap, no job write) nothing has been
      // unlinked yet, so the ticket stays exactly where it was.
      const alreadyLinked = holders.ids.has(newJobId);
      if (newJobId && !alreadyLinked) {
        await _writeJobPermitIds(newJobId, ids => [...ids, permitId]);
      }
      const oldLinkedJobs = [...holders.ids].filter(id => id !== newJobId).map(id => ({ id }));
      for (const oldJob of oldLinkedJobs) {
        await _writeJobPermitIds(oldJob.id, ids => ids.filter(id => id !== permitId));
      }
    }

    // 811 project dropdown — after name/customer/job, so a cleared customer can take the project's.
    try { await _811SaveProject(permitId, projIntent); }
    catch (projErr) {
      console.warn('[Dispatch] permit project change failed:', projErr && projErr.code);
      renderPermitsList();
      window.showToast?.(_811ProjectErrText(projErr), 'error', null, 8000);
      return;
    }

    renderPermitsList();
    renderPermitGrid();
    populatePermitDropdown();
    _p811RenderGroup(false); // TASK-9862 — the site detail it was edited from
    window.showToast?.('Ticket saved', 'success');
  } catch (err) {
    console.error('[Dispatch] Error updating permit:', err);
    window.showToast?.(err?.code === 'permit-cap' ? `That job already holds ${MAX_JOB_PERMIT_IDS} 811 tickets — nothing was moved.` : 'Failed to update permit', 'error');
  }
}

// ── TASK-9862 — the Locates page: tickets grouped by customer + site ───────
let _p811LibFilter = 'all'; // all | alert | nojob | renewed
let _p811GroupOpenKey = '';
function p811SetFilter(f, quiet) {
  _p811LibFilter = ['all', 'alert', 'nojob', 'renewed', 'potential'].includes(f) ? f : 'all';
  _p811LibWaiting = _p811LibFilter === 'nojob'; // the old chip's state (tests / connector parity)
  if (!quiet) renderPermitsList();
}
// Status shown to people: Dig starts … / Clear to dig / Expiring / Expired / Renewed (Chris 10/14: no "Requested").
// TASK-9920 (Alyse 10/16, SAFETY): a ticket before its legal dig start is
// 'waiting', never "Clear to dig" (Locates811.effectiveStatus, start time from
// the ticket PDF when read). The clock is read once per 30s, not per ticket.
let _p811NowMinCache = { at: -1, tz: '', v: -1 };
function _p811NowMinutes() {
  const L = window.Locates811;
  const tz = _p811Tz();
  const slot = Math.floor(Date.now() / 30000);
  if (_p811NowMinCache.at !== slot || _p811NowMinCache.tz !== tz) _p811NowMinCache = { at: slot, tz, v: L && typeof L.nowMinutes === 'function' ? L.nowMinutes(tz) : -1 };
  return _p811NowMinCache.v;
}
function _p811StatusOf(p, todayKey) {
  const L = window.Locates811;
  // No shared rules loaded: never claim clear on or before a dig start on the ticket.
  // TASK-9922 — judged at THIS instant in the dig SITE's zone (state + county off the PDF).
  const eff = L ? L.effectiveStatus(p, todayKey, _p811NowMinutes(), _p811Clock())
    : (p.renewedById ? 'renewed' : (_p811ValidExpiresOn(p.digStartOn) && _p811ValidExpiresOn(p.digStartOn) >= todayKey ? 'waiting' : (p.status === 'clear' ? 'clear' : 'utilities')));
  if (eff === 'renewed' || eff === 'expired' || eff === 'waiting') return eff;
  // TASK-9925 (SAFETY) — past the dig start but not every utility member has
  // marked / cleared: "Waiting on utilities", never "Clear to dig".
  if (eff !== 'clear') return 'utilities';
  const e = _p811Expiry(p.expiresOn, todayKey);
  return e.state === 'soon' ? 'soon' : 'clear';
}
function _p811MembersClear(p) {
  const L = window.Locates811;
  return !!(L && typeof L.membersOf === 'function' ? L.membersOf(p).clear : (p && p.status === 'clear'));
}
// TASK-9925 — "Waiting on utilities (2 of 5 responded)" (counts only when the reader saw them).
function _p811UtilLabel(p) {
  const L = window.Locates811;
  return (L && typeof L.utilitiesLabel === 'function' ? L.utilitiesLabel(p) : '') || 'Waiting on utilities';
}
// The Needs-attention filter: expired, expiring soon (also when the ticket is
// still waiting on utilities: that state hides the expiry on the badge).
function _p811IsAlert(p, todayKey) {
  const st = _p811StatusOf(p, todayKey);
  if (st === 'expired' || st === 'soon') return true;
  return st === 'utilities' && _p811Expiry(p.expiresOn, todayKey).state === 'soon';
}
// "Oct 21" / "today 7:00 AM" — when a waiting ticket may be dug. TASK-9922:
// the time is the ticket's (SITE time); its zone is named when it is not the
// company's ("Oct 21 7:00 AM MT").
function _p811DigStartLabel(p, todayKey) {
  const dig = _p811ValidExpiresOn(p && p.digStartOn);
  if (!dig) return '';
  const L = window.Locates811;
  const parts = L && typeof L.digStartLabelParts === 'function' ? L.digStartLabelParts(p, _p811Tz()) : null;
  const t = parts && parts.time ? ' ' + parts.time + (parts.zone ? ' ' + parts.zone : '') : '';
  return (dig === todayKey ? 'today' : _p811ShortDate(dig, todayKey)) + t;
}
// TASK-9922 r3 — the dig start / good-through are judged in the dig SITE's zone.
// When that zone is a guess (no state + county on file, a county split between
// two zones, or a location taken from the job / project) the card says so; the
// times then use the fail-safe rule (the later start, the earlier end).
function _p811ZoneGuessTag(p, st) {
  const L = window.Locates811;
  if (!L || typeof L.zoneGuessed !== 'function' || st === 'renewed' || st === 'expired') return '';
  if (!_p811ValidExpiresOn(p && p.digStartOn) && !_p811ValidExpiresOn(p && p.expiresOn)) return '';
  if (!L.zoneGuessed(p)) return '';
  const loc = typeof L.siteLocationOf === 'function' ? L.siteLocationOf(p) : { source: '' };
  const why = loc.source === 'derived' ? 'The site\'s state was taken from its job or project, not read off the ticket.'
    : (loc.state ? 'The site is near the time zone line, or its map point or county is not on file.' : 'No state or county is on file for this ticket.');
  return `<span class="p811-read-tag is-file" title="${escapeHtml(why + ' Dig start and good-through use the safer time.')}">Zone: guessed</span>`;
}
function _p811StatusBadge(p, todayKey) {
  const st = _p811StatusOf(p, todayKey);
  const exp = _p811ValidExpiresOn(p.expiresOn);
  if (st === 'renewed') return '<span class="p811-badge p811-b-ren"><span class="material-icons" aria-hidden="true">autorenew</span>Renewed</span>';
  if (st === 'expired') return `<span class="p811-badge p811-b-exp"><span class="material-icons" aria-hidden="true">error</span>Expired ${escapeHtml(_p811ShortDate(exp, todayKey))}</span>`;
  if (st === 'soon') return `<span class="p811-badge p811-b-soon"><span class="material-icons" aria-hidden="true">schedule</span>${escapeHtml(_p811Expiry(exp, todayKey).label)}</span>`;
  if (st === 'waiting') return `<span class="p811-badge p811-b-wait" title="Not clear to dig until the ticket's dig start"><span class="material-icons" aria-hidden="true">hourglass_top</span>Starts ${escapeHtml(_p811DigStartLabel(p, todayKey))}</span>`;
  // A ticket with no dates on file still reads "No date" (nothing to judge).
  if (st === 'utilities' && (exp || _p811ValidExpiresOn(p.digStartOn))) return `<span class="p811-badge p811-b-wait p811-b-util" title="Not every utility has marked or cleared yet. Mark it clear once they all have."><span class="material-icons" aria-hidden="true">hourglass_top</span>${escapeHtml(_p811UtilLabel(p))}</span>`;
  return exp ? '<span class="p811-badge p811-b-clear"><span class="material-icons" aria-hidden="true">check_circle</span>Clear to dig</span>'
    : '<span class="p811-badge p811-b-none">No date</span>';
}
// The groups on the page, filtered. Pure over its inputs (except the job
// holder lookup), so it can be reasoned about in one place.
function _p811LibGroups() {
  const L = window.Locates811;
  const todayKey = _p811Today();
  const cust = document.getElementById('permit-customer-filter')?.value || '';
  const qRaw = String(document.getElementById('permit-search')?.value || '').trim();
  const q = qRaw.toLowerCase();
  const byCust = (DispatchState.permits || []).filter(p => p && p.id && (!cust || (cust === '__none' ? !p.customerId : p.customerId === cust)));
  // TASK-10180 — Project filter: a SITE is kept whole when any of its tickets is on
  // the project (directly or through a job of it), so nothing on the card hides;
  // a site on two projects shows under either. '__none' = a ticket on no project.
  // COD r2: no projects loaded for this login = no Project filter (the select is hidden), even for one render.
  const proj = (window.ProjectsModule?.getState?.()?.projects || []).length ? (document.getElementById('permit-project-filter')?.value || '') : '';
  const all = proj ? _p811ProjectKeep(byCust, proj, todayKey) : byCust;
  const live = all.filter(p => _p811StatusOf(p, todayKey) !== 'renewed');
  const base = _p811LibFilter === 'renewed' ? all.filter(p => _p811StatusOf(p, todayKey) === 'renewed') : live;
  // TASK-9887 slice 5 — the Locates matcher (window.Locates811Search):
  // ticket # (part), site, customer, county/township, address, project, job #,
  // crew, dates, typos. The old substring match is the fallback if the mirror
  // is missing.
  const LS = window.Locates811Search;
  const parsed = q && LS && typeof LS.parseSearch === 'function' ? LS.parseSearch(qRaw, todayKey) : null;
  const scores = new Map();
  const extraCtx = parsed ? _p811SearchCtx() : null;
  const match = (p) => {
    if (!q) return true;
    if (!parsed) return [p.name, p.pdfName, p.ticketNumber, L ? L.siteNameOf(p) : '', p.customerName].some(x => String(x || '').toLowerCase().includes(q));
    const s = LS.matchTicket(p, parsed, { todayKey, soonDays: P811_SOON_DAYS, extra: _p811SearchExtra(p, extraCtx), statusOf: (t) => _p811StatusOf(t, todayKey) });
    if (s > 0) scores.set(p.id, s);
    return s > 0;
  };
  const filtered = base.filter(p => match(p)
    && (_p811LibFilter !== 'alert' || _p811IsAlert(p, todayKey))
    && (_p811LibFilter !== 'nojob' || _permitHolders(p.id).length === 0));
  // TASK-10180 COD r3 (SAFETY): a search / chip picks SITES; each card then
  // carries ALL of that site's tickets in this view, so a clear ticket's number
  // can never show its site "Clear to dig" while an expired sibling is left out.
  const whole = L && filtered.length < base.length ? new Map(L.groupTickets(base).map(g => [g.key, g])) : null;
  const groups = L ? L.groupTickets(filtered).map(g => (whole && whole.get(g.key)) || g) : [];
  const grouped = new Set(groups.flatMap(g => g.ids));
  const unnamed = filtered.filter(p => !grouped.has(p.id));
  const earliest = (g) => g.permits.map(p => _p811ValidExpiresOn(p.expiresOn)).filter(Boolean).sort()[0] || '9999-99-99';
  if (parsed) {
    // A search ranks what can still be dug first: expiring, then clear, then
    // no date, then expired; best match next.
    const bucket = (g) => {
      const sts = g.permits.map(p => _p811StatusOf(p, todayKey));
      if (sts.includes('soon')) return 0;
      if (sts.includes('clear') && g.permits.some(p => _p811ValidExpiresOn(p.expiresOn))) return 1;
      return sts.every(s => s === 'expired') ? 3 : 2;
    };
    const best = (g) => Math.max(0, ...g.permits.map(p => scores.get(p.id) || 0));
    groups.sort((a, b) => (bucket(a) - bucket(b)) || (best(b) - best(a)) || earliest(a).localeCompare(earliest(b)) || a.siteName.localeCompare(b.siteName));
    unnamed.sort((a, b) => (scores.get(b.id) || 0) - (scores.get(a.id) || 0));
  } // TASK-10180 — otherwise the lazy list chunk orders the flat list (Newest / Soonest / A-Z).
  // A ticket-number search that only hits RENEWED tickets says so (they live in history).
  const renewedHits = parsed && parsed.number && _p811LibFilter !== 'renewed'
    ? all.filter(p => _p811StatusOf(p, todayKey) === 'renewed' && LS.matchTicket(p, parsed, { todayKey, soonDays: P811_SOON_DAYS, extra: _p811SearchExtra(p, extraCtx), statusOf: (t) => _p811StatusOf(t, todayKey) }) > 0).length : 0;
  const counts = {
    all: live.length,
    alert: live.filter(p => _p811IsAlert(p, todayKey)).length,
    nojob: live.filter(p => _permitHolders(p.id).length === 0).length,
    renewed: all.length - live.length,
    potential: _p811PotCounts(), // TASK-9887 — open (suggesting) ones
  };
  return { groups, unnamed, counts, live, todayKey, byCust, hit: whole ? new Set(filtered.map(p => p.id)) : null, search: { q: qRaw, cust, proj, renewedHits, sorted: !!parsed, dated: !!(parsed && (parsed.dates.length || parsed.status)) } };
}
// TASK-10180 — the projects a ticket is on: directly, or through a job that holds it.
function _p811TicketProjIds(p) {
  const ids = new Set(window.ProjectsModule?.getPermitProjectIds?.(p.id) || []);
  for (const j of _permitHolders(p.id)) if (j && j.projectId) ids.add(j.projectId);
  return [...ids];
}
// Keep every ticket of a site that has one ticket on the project (renewed ones
// count only in, and only, the history view); an unnamed ticket is kept on its own.
function _p811ProjectKeep(tickets, proj, todayKey) {
  const L = window.Locates811;
  const hist = _p811LibFilter === 'renewed';
  // COD r1 (Edge 2): the history view matches its RENEWED tickets only, the normal view its live ones.
  const hit = (p) => ((_p811StatusOf(p, todayKey) === 'renewed') === hist) && (proj === '__none' ? !_p811TicketProjIds(p).length : _p811TicketProjIds(p).includes(proj));
  const keep = new Set();
  const groups = L ? L.groupTickets(tickets) : [];
  const grouped = new Set();
  for (const g of groups) { g.ids.forEach(id => grouped.add(id)); if (g.permits.some(hit)) g.ids.forEach(id => keep.add(id)); }
  for (const p of tickets) if (!grouped.has(p.id) && hit(p)) keep.add(p.id);
  return tickets.filter(p => keep.has(p.id));
}
// TASK-9887 — what a ticket is searchable by that is NOT on the ticket doc:
// customer name, the projects that hold it, the jobs it is on (number, title,
// customer) and the crew on those jobs' days. ctx is built once per render.
function _p811SearchCtx() {
  // Only the jobs that hold a ticket matter; people resolved through one map
  // (this runs on every keystroke).
  const held = new Set();
  for (const j of (DispatchState.jobs || [])) if (j && j.id && _jobPermitIdsOf(j).length) held.add(j.id);
  const nameOf = new Map();
  for (const pp of (DispatchState.people || [])) { if (pp && pp.name) { nameOf.set(String(pp.id), pp.name); nameOf.set(pp.name, pp.name); } }
  const crewByJob = new Map();
  for (const jd of (DispatchState.jobDays || [])) {
    if (!jd || !jd.jobId || !held.has(jd.jobId)) continue;
    const refs = [...(jd.drivers || []), ...(jd.helpers || [])];
    if (!refs.length) continue;
    let set = crewByJob.get(jd.jobId);
    if (!set) { set = new Set(); crewByJob.set(jd.jobId, set); }
    for (const r of refs) {
      const id = r && typeof r === 'object' ? (r.id || r.personId || r.name) : r;
      const name = nameOf.get(String(id)) || (r && typeof r === 'object' ? r.name : '');
      if (name) set.add(name);
    }
  }
  const projects = window.ProjectsModule?.getState?.()?.projects || [];
  return { crewByJob, projects };
}
function _p811SearchExtra(p, ctx) {
  if (!p || !ctx) return [];
  const out = [_customerNameById(p.customerId)];
  // TASK-9887 E2E — the OLD ticket # from the paperwork finds the ticket that
  // renewed it (the old one lives in history): every ancestor's number counts.
  let prevId = typeof p.renewsId === 'string' ? p.renewsId : '';
  for (let hop = 0; prevId && hop < 20; hop++) {
    const prev = (DispatchState.permits || []).find(x => x && x.id === prevId);
    if (!prev) break;
    out.push(prev.ticketNumber || _p811TicketNoFromFile(prev.name) || _p811TicketNoFromFile(prev.pdfName) || '');
    prevId = typeof prev.renewsId === 'string' ? prev.renewsId : '';
  }
  for (const pid of (window.ProjectsModule?.getPermitProjectIds?.(p.id) || [])) {
    const pr = ctx.projects.find(x => x && x.id === pid);
    if (pr) out.push(pr.name || '', pr.primaryCustomerName || '');
  }
  for (const j of _permitHolders(p.id)) {
    out.push(formatJobNumber(j) || '', j.jobNumber != null ? String(j.jobNumber) : '', j.title || '', j.customer || j.customerName || '');
    const crew = ctx.crewByJob.get(j.id);
    if (crew) out.push(...crew);
  }
  return out.filter(Boolean);
}
function _p811GroupSummary(g, todayKey) {
  const sts = g.permits.map(p => _p811StatusOf(p, todayKey));
  const expired = sts.filter(s => s === 'expired').length;
  const soon = g.permits.filter((p, i) => sts[i] !== 'expired' && _p811IsAlert(p, todayKey)).length; // TASK-9925: also one still waiting on utilities
  const waitP = g.permits.filter((p, i) => sts[i] === 'waiting'); // TASK-9920 — before the dig start
  const waiting = waitP.length;
  // COD 10/16 — the site is clear only when its LAST waiting ticket starts: the
  // latest date, then the latest time (an unknown time counts as latest).
  // TASK-9922 — compared as instants (each ticket's own site zone).
  const tz = _p811Tz();
  const startAt = (p) => { const d = window.Locates811?.digStartAt?.(p, tz); return d && Number.isFinite(d.at) ? d.at : Infinity; };
  const firstDig = waitP.slice().sort((a, b) => { const x = startAt(a), y = startAt(b); return x === y ? 0 : (x < y ? 1 : -1); })[0] || null;
  const jobs = new Map();
  let onJob = 0;
  for (const p of g.permits) {
    const h = _permitHolders(p.id);
    if (h.length) onJob++;
    for (const j of h) jobs.set(j.id, j);
  }
  const exps = g.permits.map(p => _p811ValidExpiresOn(p.expiresOn)).filter(Boolean).sort();
  // TASK-9925 — tickets past their dig start that not every utility has marked
  // or cleared (dated ones: an undated ticket says "No date"). Never "clear".
  const utilP = g.permits.filter((p, i) => sts[i] === 'utilities' && (_p811ValidExpiresOn(p.expiresOn) || _p811ValidExpiresOn(p.digStartOn)));
  const utilities = utilP.length;
  const undatedUtil = g.permits.some((p, i) => sts[i] === 'utilities') && !utilities;
  return { expired, soon, waiting, utilities, firstUtil: utilP[0] || null, firstDig, onJob, jobs: [...jobs.values()], earliest: exps[0] || '',
    state: expired ? 'expired' : (soon ? 'soon' : (waiting ? 'waiting' : (utilities ? 'utilities' : (undatedUtil ? 'none' : 'clear')))) };
}
// Another group of the SAME customer whose site name looks like this one's.
// A suggestion only: moving needs the click (Chris 10/14).
function _p811MergeTarget(g, groups) {
  const L = window.Locates811;
  if (!L) return null;
  let best = null;
  for (const o of groups) {
    if (o === g || o.customerId !== g.customerId || o.ids.length < g.ids.length) continue;
    if (o.ids.length === g.ids.length && o.key < g.key) continue; // one direction only
    const a = L.siteKey(g.siteName), b = L.siteKey(o.siteName);
    const close = L.similarity(g.siteName, o.siteName) >= 0.8 || (a.length >= 5 && b.indexOf(a) >= 0) || (b.length >= 5 && a.indexOf(b) >= 0);
    if (close && (!best || o.ids.length > best.ids.length)) best = o;
  }
  return best;
}
// Pure (unit-tested): "#26148500, #26148501, #26148502 +2 more". Numbers from
// ticketNumber, else the file name; repeats dropped; '' when none is known.
function _p811TicketNumbersText(tickets, max) {
  const n = max || 3;
  const seen = new Set();
  const nums = [];
  for (const p of tickets || []) {
    const tn = String((p && (p.ticketNumber || _p811TicketNoFromFile(p.name) || _p811TicketNoFromFile(p.pdfName))) || '').trim();
    if (tn && !seen.has(tn)) { seen.add(tn); nums.push(tn); }
  }
  if (!nums.length) return '';
  return nums.slice(0, n).map(x => '#' + x).join(', ') + (nums.length > n ? ` +${nums.length - n} more` : '');
}
function _p811TicketNumbersHtml(tickets) {
  const t = _p811TicketNumbersText(tickets, 3);
  return t ? `<div class="p811-tns" data-p811-tns="1" title="Ticket number${(tickets || []).length === 1 ? '' : 's'}">${escapeHtml(t)}</div>` : '';
}
// ── TASK-9994 (Chris 10/21) — tree, sort order, warnings setting ─────────────
// settings.show811Warnings (bool, absent = OFF for every tenant). Off hides the
// ADVISORY 811-page warnings: the red expired/expiring banner, "N expire soon",
// "not on the board yet" / "Not on a job yet" and the "Looks like … Move into"
// merge suggestion. Never hidden: the Expired badge, "Dig starts" and "Waiting on
// utilities" (a ticket that is not clear to dig never reads clear), the
// job-form late-work note and the expired-locate hard stop, the board chip,
// the driver line and the morning email (its own setting).
function _p811ShowWarnings() { return window.AppState?.settings?.show811Warnings === true; }
function _p811GroupProjIds(g, todayKey) {
  return [...new Set(g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed').flatMap(p => window.ProjectsModule?.getPermitProjectIds?.(p.id) || []))];
}
// Sort (TASK-10180): 'new' (default) = newest upload first; 'soon' = expired
// first, then the soonest date that matters to dispatch; 'az' = customer, then
// site, A-Z. The flat list is ordered in the lazy chunk views/p811LocatesList.js.
// Remembered per login on this device (localStorage; a private window keeps it
// for the session only).
const P811_SORTS = ['new', 'soon', 'az'];
let _p811SortSession = '';
function _p811SortStoreKey() { return 'dd_p811_sort_' + (_p811Uid() || 'anon'); }
function _p811CurrentSort() {
  if (P811_SORTS.includes(_p811SortSession)) return _p811SortSession;
  try { const v = localStorage.getItem(_p811SortStoreKey()); return P811_SORTS.includes(v) ? v : 'new'; } catch (_) { return 'new'; }
}
function p811SetSort(v) {
  const m = P811_SORTS.includes(v) ? v : 'new';
  try { localStorage.setItem(_p811SortStoreKey(), m); } catch (_) { /* private mode: this session only */ }
  _p811SortSession = m;
  renderPermitsList();
}
// TASK-10180 — the list cards, order, status line and Project options live in
// the lazy chunk views/p811LocatesList.js (never a static import: Pitfall #1 /
// perf budget). This side filters the tickets and owns every status rule.
let _p811ListMod = null;
let _p811ListLoading = null;
let _p811ListSeq = 0;
function _p811ListH(todayKey) {
  const projAll = window.ProjectsModule?.getState?.()?.projects || [];
  const jobProjName = (id) => { const j = (DispatchState.jobs || []).find(x => x && x.projectId === id && x.projectName); return j ? j.projectName : ''; };
  const canRenew = _p811CanRenew();
  return { todayKey, filter: _p811LibFilter, warn: _p811ShowWarnings(), esc: escapeHtml, js: escJsAttr,
    date: (k) => _p811ShortDate(k, todayKey), expiry: (k) => _p811Expiry(k, todayKey), validDate: _p811ValidExpiresOn,
    statusOf: (p) => _p811StatusOf(p, todayKey), summary: (g) => _p811GroupSummary(g, todayKey),
    digLabel: (p) => _p811DigStartLabel(p, todayKey), utilLabel: _p811UtilLabel, membersClear: _p811MembersClear, undatedLinked: (p) => _p811IsUndatedLinked(p, todayKey),
    custName: (g) => _customerNameById(g.customerId) || g.permits[0]?.customerName || 'No customer',
    projIds: (g) => _p811GroupProjIds(g, todayKey), projIdsOfTicket: _p811TicketProjIds,
    projName: (id) => (projAll.find(x => x && x.id === id) || {}).name || jobProjName(id) || '', // '' = not loaded for this login
    jobLabel: (j) => formatJobNumber(j) || j.title || 'a job', mergeTarget: _p811MergeTarget, ticketNumbersHtml: _p811TicketNumbersHtml,
    ticketNo: (p) => String(p.ticketNumber || _p811TicketNoFromFile(p.name) || _p811TicketNoFromFile(p.pdfName) || ''), // TASK-10278
    potForGroup: _p811PotForGroup, potKind: _p811PotKind, canRenew, canStartJob: _p811CanStartJob(), potCanEdit: _p811PotCanEdit(),
    canProject: canRenew && is811Enabled() && window.isPartner811ReadOnly?.() !== true };
}
function _p811LoadList() {
  if (_p811ListMod) return Promise.resolve(_p811ListMod);
  _p811ListLoading = _p811ListLoading || __ddChunkWrap(import('./p811LocatesList.js?v=10278001'))
    .then(m => (_p811ListMod = m))
    .catch(err => { _p811ListLoading = null; console.warn('[Dispatch] TASK-10180 p811LocatesList failed to load:', err?.message || err); return null; });
  return _p811ListLoading;
}
// The gear in the Locates window: Dispatch Settings, scrolled to its 811 section.
function p811OpenSettings() {
  openDispatchSettingsModal();
  const m = document.getElementById('dispatch-settings-modal');
  if (m) m.classList.add('p811-over');
  setTimeout(() => document.getElementById('dispatch-settings-811-section')?.scrollIntoView({ block: 'start' }), 50);
}

// TASK-9994 (coordinator 10/21: TITS's morning run reported undatedLinked: 2) —
// a live ticket on a job (or on a job's project) with no expiration date. Pure
// enough to unit-test with a holder lookup passed in.
function _p811IsUndatedLinked(p, todayKey, holdersOf, projectsOf) {
  if (!p || !p.id || _p811ValidExpiresOn(p.expiresOn)) return false;
  if (_p811StatusOf(p, todayKey) === 'renewed') return false;
  const jobs = (holdersOf || _permitHolders)(p.id) || [];
  const projs = (projectsOf || ((id) => window.ProjectsModule?.getPermitProjectIds?.(id) || []))(p.id) || [];
  return jobs.length > 0 || projs.length > 0;
}
function _p811UndatedHtml(live, todayKey) {
  const L = window.Locates811;
  const list = (live || []).filter(p => _p811IsUndatedLinked(p, todayKey));
  if (!list.length) return '';
  const names = [...new Set(list.map(p => (L ? L.siteNameOf(p) : '') || p.ticketNumber || p.name || 'a ticket'))];
  const shown = names.slice(0, 3).map(n => `<b>${escapeHtml(n)}</b>`).join(', ') + (names.length > 3 ? ` +${names.length - 3} more` : '');
  return `<span class="material-icons" aria-hidden="true">event_busy</span><div><b>No expiration date — add one.</b> ${list.length} ticket${list.length === 1 ? ' is' : 's are'} on a job with no expiration date, so the morning email can't warn about ${list.length === 1 ? 'it' : 'them'}: ${shown}. Open the site and add the good-through date.</div>`;
}
function _p811AlarmHtml(live, todayKey) {
  const exp = live.filter(p => _p811StatusOf(p, todayKey) === 'expired');
  const soon = live.filter(p => _p811StatusOf(p, todayKey) !== 'expired' && _p811IsAlert(p, todayKey));
  if (!exp.length && !soon.length) return '';
  const L = window.Locates811;
  // Is a SCHEDULED day (today or later) on any of them after its date?
  const keysByJob = _p811KeysByJobCached();
  let late = null;
  for (const p of exp.concat(soon)) {
    for (const j of _permitHolders(p.id)) {
      const last = (L && typeof L.lastGoodDay === 'function' && L.lastGoodDay(p, _p811Tz())) || p.expiresOn; // TASK-9925 site zone
      const hit = (keysByJob.get(j.id) || []).find(k => k >= todayKey && k > last);
      if (hit && (!late || hit < late.day)) late = { day: hit, job: j, p };
    }
  }
  const parts = [exp.length ? `${exp.length} expired` : '', soon.length ? `${soon.length} expire within ${P811_SOON_DAYS} days` : ''].filter(Boolean).join(' · ');
  const sub = late ? `${formatJobNumber(late.job) || late.job.title || 'A job'} is scheduled ${_p811ShortDate(late.day, todayKey)} on ${L ? L.siteNameOf(late.p) || 'a ticket' : 'a ticket'}, which is good through ${_p811ShortDate(late.p.expiresOn, todayKey)}.` : 'Renew them before the crew digs.';
  return `<span class="material-icons" aria-hidden="true">report</span>
    <div style="flex:1;min-width:200px"><div class="p811-alarm-big">${escapeHtml(parts)}</div><div class="p811-alarm-sub">${escapeHtml(sub)}</div></div>
    <button type="button" class="btn" onclick="window.DispatchModule.p811SetFilter('alert')">Show these ${exp.length + soon.length}</button>`;
}
// TASK-9887 — an empty result says what was searched, and where.
function _p811NoMatchHtml(search) {
  const s = search || {};
  const chip = { alert: 'Expired / expiring', nojob: 'Not on the board', renewed: 'Renewed (history)' }[_p811LibFilter] || '';
  const custName = s.cust === '__none' ? 'tickets with no customer' : (s.cust ? (_customerNameById(s.cust) || 'one customer') : '');
  const projName = s.proj === '__none' ? 'no project' : (s.proj ? (_p811ProjectById(s.proj)?.name || 'one project') : ''); // TASK-10180
  const scope = [chip ? `the "${chip}" tickets` : '', custName ? custName : '', projName].filter(Boolean).join(', ');
  const what = window.Locates811Search?.SEARCH_WHAT || 'ticket #, site and customer';
  let html = s.q
    ? `<p class="p811-empty" id="p811-nomatch">No tickets match "${escapeHtml(s.q)}"${scope ? ' in ' + escapeHtml(scope) : ''}. Searched ${escapeHtml(what)}${s.dated ? '. Tickets with no good-through date are not matched by a date search' : ' (try "good through next week", "expired" or "oct 20")'}.</p>`
    : `<p class="p811-empty" id="p811-nomatch">No tickets match${scope ? ' in ' + escapeHtml(scope) : ''}.</p>`;
  const acts = [];
  if (s.renewedHits) acts.push(`<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811SetFilter('renewed')">${s.renewedHits} renewed ticket${s.renewedHits === 1 ? '' : 's'} match. Show history</button>`);
  if (scope) acts.push('<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811SearchEverywhere()">Search all tickets</button>');
  if (acts.length) html += `<div class="p811-group-acts" style="margin-bottom:12px">${acts.join('')}</div>`;
  return html;
}
function p811SearchEverywhere() {
  const c = document.getElementById('permit-customer-filter');
  if (c) c.value = '';
  const pf = document.getElementById('permit-project-filter'); // TASK-10180
  if (pf) pf.value = '';
  p811SetFilter('all');
}
function renderPermitsList() {
  const box = document.getElementById('p811-groups');
  const container = document.getElementById('permits-list');
  if (!container) return;
  // TASK-10278 — the chips are advisory: off with the warnings, and a chip left picked resets.
  const warn = _p811ShowWarnings();
  if (!warn && (_p811LibFilter === 'alert' || _p811LibFilter === 'nojob')) p811SetFilter('all', 1);
  document.querySelector('.p811-chips')?.classList.toggle('hidden', !warn);
  const { groups, unnamed, counts, live, todayKey, search, byCust, hit } = _p811LibGroups();
  { // TASK-9994 — critical, not one of the switchable warnings.
    const el = document.getElementById('p811-undated');
    if (el) {
      const html = _p811LibFilter === 'renewed' ? '' : _p811UndatedHtml(live, todayKey);
      el.innerHTML = html;
      el.classList.toggle('hidden', !html);
    }
  }
  const alarm = document.getElementById('p811-alarm');
  if (alarm) {
    const html = _p811LibFilter === 'renewed' || !_p811ShowWarnings() ? '' : _p811AlarmHtml(live, todayKey); // TASK-9994
    alarm.innerHTML = html;
    alarm.classList.toggle('hidden', !html);
  }
  // TASK-10180 — three chips (All / Expired-expiring / Not on the board);
  // Renewed (history) and Potential moved into the "Show" select.
  document.querySelectorAll('[data-p811-filter]').forEach(b => {
    const f = b.dataset.p811Filter;
    b.setAttribute('aria-pressed', f === _p811LibFilter ? 'true' : 'false');
    b.textContent = `${{ all: 'All', alert: 'Expired / expiring', nojob: 'Not on the board' }[f]} ${counts[f] || 0}`;
  });
  const show = document.getElementById('p811-show');
  if (show) {
    show.value = _p811LibFilter === 'renewed' || _p811LibFilter === 'potential' ? _p811LibFilter : '';
    for (const o of show.options) {
      if (o.value === 'renewed') o.textContent = `Renewed (history) ${counts.renewed || 0}`;
      if (o.value === 'potential') { o.textContent = `Potential jobs / projects ${counts.potential || 0}`; o.hidden = o.disabled = window.isPartner811ReadOnly?.() === true; } // TASK-9887
    }
  }
  // TASK-9887 — the Potential view: potential jobs / projects, not tickets.
  if (_p811LibFilter === 'potential') {
    _p811ListSeq++; // TASK-10180 — a list paint still loading must not overwrite this view
    if (box) box.innerHTML = _p811PotListHtml(search.q, todayKey);
    if (alarm) alarm.classList.add('hidden');
    document.getElementById('p811-unnamed')?.classList.add('hidden');
    container.innerHTML = '';
    return;
  }
  // A search also finds potentials (archived ones too: "search still finds it").
  const potHits = search.q ? _p811PotMatchesForSearch(search.q, todayKey) : [];
  const potStrip = potHits.length ? `<h4 class="p811-pot-h"><span class="material-icons" aria-hidden="true">lightbulb</span> Potential jobs and projects matching "${escapeHtml(search.q)}"</h4>${potHits.slice(0, 5).map(p => _p811PotCardHtml(p, todayKey)).join('')}` : '';
  if (box) {
    // TASK-10180 — one flat list of site cards (no folders), in the chosen
    // order; a typed search keeps its relevance order. Painted by the lazy chunk.
    const empty = groups.length ? '' : (!(DispatchState.permits || []).length ? (window.isPartner811ReadOnly?.() === true ? '<p class="p811-empty">This company has no 811 tickets on file yet.</p>' : '<p class="p811-empty">No locate tickets yet. Upload the ticket PDFs from the one-call website.</p>')
      : (unnamed.length ? '' : _p811NoMatchHtml(search)));
    const seq = ++_p811ListSeq;
    const paint = (m) => {
      if (seq !== _p811ListSeq) return; // a newer render owns the list
      if (!m) { box.innerHTML = '<p class="p811-empty" role="alert">The ticket list did not load. Close Locates and open it again.</p>'; return; }
      const h = _p811ListH(todayKey);
      const pf = document.getElementById('permit-project-filter');
      if (pf) {
        // COD r1: no Projects loaded for this login (or none yet) = no Project filter,
        // never a "No project" that silently holds tickets on a project.
        const hasProj = (window.ProjectsModule?.getState?.()?.projects || []).length > 0;
        pf.classList.toggle('hidden', !hasProj);
        if (!hasProj) pf.value = '';
        const L = window.Locates811;
        const hist = _p811LibFilter === 'renewed';
        const pool = byCust.filter(p => (_p811StatusOf(p, todayKey) === 'renewed') === hist);
        const sites = L ? L.groupTickets(pool) : [];
        const inSite = new Set(sites.flatMap(g => g.ids));
        // Unnamed tickets count too (COD r1 Code): each one as its own "site".
        const html = m.projectOptionsHtml(sites.concat(pool.filter(p => !inSite.has(p.id)).map(p => ({ permits: [p] }))), search.proj, h);
        if (pf.dataset.opts !== html) { pf.innerHTML = html; pf.dataset.opts = html; pf.value = hasProj ? search.proj : ''; }
      }
      h.hit = hit;
      box.innerHTML = potStrip + (groups.length ? m.listHtml(groups, search.sorted, _p811CurrentSort(), h) : empty);
    };
    if (_p811ListMod) paint(_p811ListMod);
    else _p811LoadList().then(paint);
  }
  const wrap = document.getElementById('p811-unnamed');
  if (wrap) wrap.classList.toggle('hidden', !unnamed.length);
  const t = document.getElementById('p811-unnamed-title');
  if (t) t.textContent = `Needs a site name: ${unnamed.length} ticket${unnamed.length === 1 ? '' : 's'}`;
  container.innerHTML = _p811FlatRowsHtml(_p811SortForList(unnamed, todayKey));
}

// ── TASK-9862 — one site's detail: facts, its tickets, Renew, Attach ────────
let _p811GroupPanel = ''; // '' | 'renew' | 'attach'
let _p811GroupFocus = ''; // TASK-10278 — the ticket row the sheet was opened from (only it is ticked)
function _p811FindGroup(key) {
  const L = window.Locates811;
  if (!L || !key) return null;
  const all = (DispatchState.permits || []).filter(p => p && p.id);
  // The renewed history view groups renewed tickets too.
  return L.groupTickets(all).find(g => g.key === key) || null;
}
function _p811LiveIds(g, todayKey) {
  return g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed').map(p => p.id);
}
function p811OpenGroup(key, renew, focus) {
  _p811GroupOpenKey = String(key || '');
  _p811GroupFocus = String(focus || '');
  _p811GroupPanel = renew ? 'renew' : '';
  _p811RenderGroup(true);
  document.getElementById('modal-811-group')?.classList.remove('hidden');
}
function p811AttachGroupToJob(key, focus) {
  _p811GroupOpenKey = String(key || '');
  _p811GroupFocus = String(focus || '');
  _p811GroupPanel = 'attach';
  _p811RenderGroup(true);
  document.getElementById('modal-811-group')?.classList.remove('hidden');
}
// TASK-9887 slice 1 — Attach to project, same sheet + picker style as job.
// Projects load lazily; the picker repaints once they arrive.
function p811AttachGroupToProject(key, focus) {
  _p811GroupOpenKey = String(key || '');
  _p811GroupFocus = String(focus || '');
  _p811GroupPanel = 'project';
  _p811RenderGroup(true);
  document.getElementById('modal-811-group')?.classList.remove('hidden');
  if (!window.ProjectsModule) {
    Promise.resolve(window.ensureProjectsModule?.()).then(() => { if (_p811GroupPanel === 'project') _p811RenderGroup(false); }).catch(() => {});
  }
}
// TASK-9887 slice 2 — a summary tile was pressed.
function p811GroupTile(which) {
  const g = _p811FindGroup(_p811GroupOpenKey);
  if (!g) return;
  if (which === 'good') { _p811GroupPanel = 'renew'; _p811RenderGroup(true); return; }
  if (which === 'jobs') { _p811GroupPanel = 'attach'; _p811RenderGroup(true); return; }
  if (which === 'project') { p811AttachGroupToProject(_p811GroupOpenKey); return; }
  if (which === 'day') {
    const todayKey = _p811Today();
    const live = g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed');
    const jobs = [...new Map(live.flatMap(p => _permitHolders(p.id)).map(j => [j.id, j])).values()];
    const keysByJob = _p811KeysByJobCached();
    const next = jobs.flatMap(j => (keysByJob.get(j.id) || []).filter(k => k >= todayKey).map(k => ({ k, j }))).sort((a, b) => a.k.localeCompare(b.k))[0];
    if (next) {
      // Jump to that day on the board (user-initiated; goToBoardDate never runs on its own).
      closeModal('811-group');
      closeModal('811-submission');
      goToBoardDate(next.k); /* user-press: TASK-9887 tile */
      window.showToast?.(`${formatJobNumber(next.j) || next.j.title || 'The job'} · ${_p811ShortDate(next.k, todayKey)}`, 'info');
      return;
    }
    if (jobs.length) { closeModal('811-group'); closeModal('811-submission'); editJob(jobs[0].id); return; }
    p811StartFromGroup(_p811GroupOpenKey); // TASK-9994: a potential for a login without Dispatch
  }
}
async function p811GroupProjectGo() {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; }
  if (_p811GroupBusy) return;
  const ids = _p811GroupSelected();
  const projectId = document.getElementById('p811-attach-project')?.value || '';
  if (!ids.length) { window.showToast?.('Tick the tickets to attach.', 'info'); return; }
  if (!projectId) { window.showToast?.('Pick a project.', 'info'); return; }
  const PM = window.ProjectsModule || await window.ensureProjectsModule?.();
  if (PM?.project811CanEdit?.() !== true) { window.showToast?.('Only the owner or a dispatcher can change the project.', 'info'); return; }
  _p811GroupBusy = true;
  let failed = 0, cap = false, done = 0;
  try {
    // The 811 window's ONE project path: setPermitProject per ticket (moves it
    // off any other project in one transaction; cap refused, never truncated).
    for (const id of ids) {
      try { await _811SaveProject(id, { next: projectId }); done++; }
      catch (e) { failed++; if (e && e.code === 'permit-cap') cap = true; console.warn('[Dispatch] 811 project attach failed', id, e && e.code); }
    }
  } finally { _p811GroupBusy = false; }
  const name = ((PM?.getState?.()?.projects || []).find(p => p && p.id === projectId) || {}).name || 'the project';
  if (failed) window.showToast?.(`${done} attached to ${name}, ${failed} not.${cap ? ' That project already holds 20 811 tickets.' : ' Try again.'}`, 'warning', null, 8000);
  else window.showToast?.(`Attached ${done} to ${name}`, 'success');
  _p811GroupPanel = '';
  _p811RenderGroup(true);
  renderPermitsList();
}
// Fills the project select of the 'project' panel (customer's projects first).
function _p811ProjectPickHtml(customerId, keepIds) {
  const PM = window.ProjectsModule;
  if (!PM) return '<option value="">Loading projects…</option>';
  const { mine, other } = _811ProjectOptionGroups(PM.getState?.()?.projects || [], customerId, keepIds, '');
  const opt = p => `<option value="${escapeHtml(p.id)}" ${keepIds[0] === p.id ? 'selected' : ''}>${escapeHtml(p.name || 'Untitled project')}${p.status !== 'active' ? ` (${escapeHtml(p.status || 'not active')})` : ''}${mine.includes(p) || !p.primaryCustomerName ? '' : ' — ' + escapeHtml(p.primaryCustomerName)}</option>`;
  const custName = customerId ? (_customerNameById(customerId) || 'This customer') : '';
  if (!mine.length && !other.length) return '<option value="">No open projects yet</option>';
  return '<option value="">Pick a project</option>'
    + (mine.length ? `<optgroup label="${escapeHtml(custName + "'s projects")}">${mine.map(opt).join('')}</optgroup>` : '')
    + (other.length ? (mine.length ? `<optgroup label="Other projects">${other.map(opt).join('')}</optgroup>` : other.map(opt).join('')) : '');
}
function _p811RefreshGroupIfOpen() {
  const m = document.getElementById('modal-811-group');
  if (!m || m.classList.contains('hidden') || m.querySelector('.p811-edit')) return;
  _p811RenderGroup(false);
}
function _p811RenderGroup(fresh) {
  const body = document.getElementById('p811-group-body');
  const foot = document.getElementById('p811-group-foot');
  const title = document.getElementById('p811-group-title');
  if (!body || !foot) return;
  const g = _p811FindGroup(_p811GroupOpenKey);
  const todayKey = _p811Today();
  if (!g) { body.innerHTML = '<p class="p811-empty">This site has no tickets now.</p>'; foot.innerHTML = '<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeModal(\'811-group\')">Close</button>'; return; }
  // Keep ticked boxes across a snapshot repaint.
  const prevTicked = fresh ? null : new Set(Array.from(body.querySelectorAll('input.p811-gsel:checked')).map(c => c.value));
  // TASK-9887 (COD) — and the picked project: a repaint never changes where Attach writes.
  const prevProject = fresh ? null : (document.getElementById('p811-attach-project')?.value ?? null);
  const custName = _customerNameById(g.customerId) || g.permits[0]?.customerName || 'No customer';
  const live = g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed');
  const s = _p811GroupSummary({ ...g, permits: live, ids: live.map(p => p.id) }, todayKey);
  const keysByJob = _p811KeysByJobCached();
  const nextDay = s.jobs.flatMap(j => (keysByJob.get(j.id) || []).filter(k => k >= todayKey)).sort()[0] || '';
  const projIds = [...new Set(live.flatMap(p => window.ProjectsModule?.getPermitProjectIds?.(p.id) || []))];
  const projName = projIds.length ? ((window.ProjectsModule?.getState?.()?.projects || []).find(x => x.id === projIds[0])?.name || 'A project') + (projIds.length > 1 ? ` +${projIds.length - 1}` : '') : 'None';
  const unprojected = live.filter(p => !(window.ProjectsModule?.getPermitProjectIds?.(p.id) || []).length).map(p => p.id);
  // TASK-9920 (Alyse 10/16) — the header says what the name IS (the locate
  // site), carries the project beside it, and the name can be changed here.
  if (title && (fresh || !title.querySelector('.p811-edit'))) {
    const canRename = _p811CanRenameSite();
    title.innerHTML = `<span class="p811-hdr-kicker">Locate site</span> <span class="p811-hdr-name">${escapeHtml(g.siteName)}</span>`
      + (canRename ? ` <button type="button" class="btn-icon p811-rename-btn" data-p811-rename="1" aria-label="Rename this site" title="Rename this site" onclick="window.DispatchModule.p811RenameSiteStart()"><span class="material-icons" aria-hidden="true">edit</span></button>` : '')
      + (projIds.length ? ` <span class="p811-proj-chip" title="Project"><span class="material-icons" aria-hidden="true">folder</span>${escapeHtml(projName)}</span>` : '')
      + ` <span class="p811-count">${escapeHtml(custName)} · ${live.length} ticket${live.length === 1 ? '' : 's'}</span>`;
  }
  const red = (cond) => (cond ? ' is-red' : '');
  // TASK-9887 slice 2 — every tile is a control for a dispatcher. For a
  // login that can't dispatch (811-only / partner) they stay read-only and
  // say where the value comes from.
  const canAct = _p811CanRenew() && window.isPartner811ReadOnly?.() !== true;
  const tile = (which, cls, label, value, hint, icon, roNote) => canAct
    ? `<button type="button" class="p811-fact is-action${cls}" data-p811-tile="${which}" onclick="window.DispatchModule.p811GroupTile('${which}')" aria-label="${escapeHtml(label + ': ' + value + '. ' + hint)}"><div class="k">${escapeHtml(label)}</div><div class="v">${escapeHtml(value)}</div><div class="h"><span class="material-icons" aria-hidden="true">${icon}</span>${escapeHtml(hint)}</div></button>`
    : `<div class="p811-fact${cls}" data-p811-tile="${which}"><div class="k">${escapeHtml(label)}</div><div class="v">${escapeHtml(value)}</div>${roNote ? `<div class="h is-ro">${escapeHtml(roNote)}</div>` : ''}</div>`;
  const goodVal = s.earliest ? _p811ShortDate(s.earliest, todayKey) + (s.state === 'waiting' ? ' · dig starts ' + _p811DigStartLabel(s.firstDig, todayKey) : s.state === 'utilities' ? ' · ' + (s.utilities === 1 ? _p811UtilLabel(s.firstUtil) : s.utilities + ' waiting on utilities') : (s.state !== 'clear' ? ' · ' + _p811Expiry(s.earliest, todayKey).label : '')) : 'No date';
  const jobsVal = s.jobs.length ? s.jobs.slice(0, 3).map(j => formatJobNumber(j) || j.title || 'a job').join(', ') + ` (${s.onJob} ticket${s.onJob === 1 ? '' : 's'})` : 'Not on the board yet';
  const dayHint = nextDay ? 'Go to that day' : (s.jobs.length ? 'Add a day on the job' : 'Start a job');
  const facts = `
    <div class="p811-facts">
      ${tile('good', red(s.state === 'expired' || s.state === 'soon'), 'Earliest good through', goodVal, 'From the ticket · Renew / extend', 'autorenew', 'From the ticket')}
      ${tile('jobs', '', 'On the board', jobsVal, s.jobs.length ? 'Attach to another job' : 'Attach to a job', 'link', '')}
      ${tile('day', red(nextDay && s.earliest && nextDay > s.earliest), 'Next work day', nextDay ? _p811ShortDate(nextDay, todayKey) : 'None scheduled', dayHint, nextDay ? 'event' : 'add_task', 'From the job board')}
      ${tile('project', '', 'Project', projName, projIds.length ? 'Change project' : 'Attach to a project', 'folder_open', '')}
    </div>`;
  const defaultTick = (p) => {
    if (prevTicked) return prevTicked.has(p.id);
    if (_p811GroupFocus && g.ids.includes(_p811GroupFocus)) return p.id === _p811GroupFocus; // TASK-10278
    if (_p811GroupPanel === 'attach') return _permitHolders(p.id).length === 0 && _p811StatusOf(p, todayKey) !== 'expired';
    // TASK-9887 — project: the tickets on no project yet (all of them when every one is).
    if (_p811GroupPanel === 'project') return unprojected.length ? unprojected.includes(p.id) : _p811StatusOf(p, todayKey) !== 'renewed';
    return _p811IsAlert(p, todayKey);
  };
  const rows = g.permits.slice().sort((a, b) => String(a.expiresOn || '9').localeCompare(String(b.expiresOn || '9'))).map(p => {
    const st = _p811StatusOf(p, todayKey);
    const holders = _permitHolders(p.id);
    const url = _permitSafeUrl(p);
    const tn = p.ticketNumber || _p811TicketNoFromFile(p.name) || '—';
    const pidJ = escJsAttr(p.id);
    const renewedTo = p.renewedById ? (DispatchState.permits || []).find(x => x.id === p.renewedById) : null;
    return `
      <div class="permit-list-item p811-tick" data-permit-id="${escapeHtml(p.id)}">
        ${st === 'renewed' ? '<span></span>' : `<input type="checkbox" class="p811-gsel" value="${escapeHtml(p.id)}" aria-label="Select ticket ${escapeHtml(tn)}" ${defaultTick(p) ? 'checked' : ''} onchange="window.DispatchModule.p811GroupSelChanged()">`}
        <span class="p811-mono">${escapeHtml(tn)}</span>
        <span>${_p811StatusBadge(p, todayKey)}${_p811ZoneGuessTag(p, st)}${renewedTo ? `<span class="p811-read-tag is-file">→ #${escapeHtml(renewedTo.ticketNumber || _p811TicketNoFromFile(renewedTo.name) || '')}</span>` : ''}</span>
        <span class="p811-hm">${escapeHtml(_p811ShortDate(_p811ValidExpiresOn(p.digStartOn), todayKey) || '—')}</span>
        <span class="p811-hm"><b>${escapeHtml(_p811ShortDate(_p811ValidExpiresOn(p.expiresOn), todayKey) || '—')}</b>${_p811PrintedCellHtml(p, todayKey)}</span>
        <span class="p811-hm">${holders.length ? escapeHtml(holders.slice(0, 2).map(j => formatJobNumber(j) || j.title || 'a job').join(', ') + (holders.length > 2 ? ` +${holders.length - 2}` : '')) : 'Not yet'}</span>
        <details class="p811-tickmenu" ontoggle="window.DispatchModule.p811PlaceMenu(this)">
          <summary class="btn-icon" aria-label="More for ticket ${escapeHtml(tn)}"><span class="material-icons">more_vert</span></summary>
          <div class="p811-tickmenu-list">
            ${url ? `<a class="btn btn-secondary" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer"><span class="material-icons">open_in_new</span> View PDF</a>` : ''}
            <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.renamePermit('${pidJ}')"><span class="material-icons">edit</span> Edit</button>
            ${st === 'renewed' || window.isPartner811ReadOnly?.() === true ? '' : (p.status === 'clear'
              ? `<button type="button" class="btn btn-secondary" data-p811-unclear="1" onclick="window.DispatchModule.p811SetUtilitiesClear('${pidJ}', false)"><span class="material-icons">undo</span> Not clear yet</button>`
              : (_p811MembersClear(p) ? '' : `<button type="button" class="btn btn-primary" data-p811-markclear="1" onclick="window.DispatchModule.p811SetUtilitiesClear('${pidJ}', true)"><span class="material-icons">check_circle</span> Utilities all marked</button>`))}
            ${is811OnlyMode() ? '' : `<button type="button" class="btn btn-danger-outline" onclick="window.DispatchModule.deletePermit('${pidJ}')"><span class="material-icons">delete</span> Delete</button>`}
          </div>
        </details>
      </div>`;
  }).join('');
  const table = `
    <div class="p811-ticks">
      <div class="p811-tick head"><span></span><span>Ticket #</span><span>Status</span><span class="p811-hm">Dig start</span><span class="p811-hm">Good through</span><span class="p811-hm">On the board</span><span></span></div>
      ${rows}
    </div>`;
  const canDispatch = _p811CanRenew();
  let panel = '';
  if (_p811GroupPanel === 'renew' && canDispatch) {
    panel = `
      <div class="p811-renew" id="p811-renew-panel">
        <h5><span class="material-icons" aria-hidden="true">autorenew</span> Renew <span id="p811-sel-count"></span></h5>
        <div style="margin-bottom:6px;color:var(--text-primary)">Renew on the one-call website first, then pick one:</div>
        <label class="p811-opt"><input type="radio" name="p811-renew-kind" value="new" checked> <span><b>New ticket number</b> — upload the new ticket PDFs. Each old ticket stays in history as Renewed and its jobs move to the new one.</span></label>
        <label class="p811-opt"><input type="radio" name="p811-renew-kind" value="same"> <span><b>Same ticket #, new good-through date</b><br><input type="date" id="p811-renew-date" aria-label="New good-through date" onfocus="document.querySelector('input[name=p811-renew-kind][value=same]').checked=true"></span></label>
      </div>`;
  } else if (_p811GroupPanel === 'attach' && canDispatch) {
    panel = `
      <div class="p811-renew" id="p811-attach-panel">
        <h5><span class="material-icons" aria-hidden="true">link</span> Attach <span id="p811-sel-count"></span> to a job</h5>
        <select id="p811-attach-job" aria-label="Job" style="width:100%;min-height:48px;font-size:16px">${_p811JobOptionsHtml(g.customerId, '', '', true, '')}</select>
        <p class="p811-up-saved">Drivers on that job see these tickets. You can also start a new job from this site.</p>
      </div>`;
  } else if (_p811GroupPanel === 'project' && canDispatch) {
    // TASK-10278 COD r1 (Code) — opened from a ticket row: that ticket's own project, never a sibling's ('' = Pick a project).
    const fpj = _p811GroupFocus && g.ids.includes(_p811GroupFocus) ? ((window.ProjectsModule?.getPermitProjectIds?.(_p811GroupFocus) || [])[0] || '') : null;
    const others = projIds.length ? ` Each ticket moves off any other project it is on.` : '';
    panel = `
      <div class="p811-renew" id="p811-project-panel">
        <h5><span class="material-icons" aria-hidden="true">folder_open</span> Attach <span id="p811-sel-count"></span> to a project</h5>
        <select id="p811-attach-project" aria-label="Project" style="width:100%;min-height:48px;font-size:16px">${_p811ProjectPickHtml(g.customerId, prevProject !== null ? [prevProject, ...projIds.filter(id => id !== prevProject)] : (fpj !== null ? [fpj, ...projIds.filter(id => id !== fpj)] : projIds))}</select>
        <p class="p811-up-saved">Every job in that project shows these tickets.${escapeHtml(others)}</p>
      </div>`;
  }
  // TASK-9887 slice 6 — directions / waypoints: shown when a ticket (or the site's
  // potential) carries them; a placeholder until they are captured. No map yet.
  const sitePot = _p811PotForGroup({ ...g, permits: live, ids: live.map(p => p.id) });
  const dirSrc = live.find(p => typeof p.directions === 'string' && p.directions.trim()) || (sitePot && typeof sitePot.directions === 'string' && sitePot.directions.trim() ? sitePot : null);
  const wpN = Math.max(0, ...live.map(p => Array.isArray(p.waypoints) ? p.waypoints.length : 0), sitePot && Array.isArray(sitePot.waypoints) ? sitePot.waypoints.length : 0);
  const dirs = `<div class="p811-dirs" id="p811-dirs"><div class="k"><span class="material-icons" aria-hidden="true">directions</span> Directions and destinations</div><div class="v">${dirSrc ? escapeHtml(String(dirSrc.directions).slice(0, 2000)) : 'Coming soon: the ticket’s own directions and destinations here, and on the project map.'}${wpN ? ` · ${wpN} destination${wpN === 1 ? '' : 's'}` : ''}</div></div>`;
  const potBtn = _p811PotCanEdit() ? (sitePot
    ? `<button type="button" class="btn btn-secondary" data-p811-pot-btn="edit" onclick="window.DispatchModule.p811OpenPotential('${escJsAttr(sitePot.id)}')"><span class="material-icons">lightbulb</span> Edit potential</button>`
    : `<button type="button" class="btn btn-secondary" data-p811-pot-btn="new" onclick="window.DispatchModule.p811NewPotential('${escJsAttr(g.key)}')"><span class="material-icons">lightbulb</span> Add as potential job / project…</button>`) : '';
  body.innerHTML = facts + dirs + table + panel;
  const close = '<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeModal(\'811-group\')">Close</button>';
  const k = escJsAttr(g.key);
  const kf = _p811GroupFocus && g.ids.includes(_p811GroupFocus) ? `${k}', '${escJsAttr(_p811GroupFocus)}` : k; // TASK-10278: the footer keeps the row's ticket picked
  if (_p811GroupPanel === 'renew' && canDispatch) {
    foot.innerHTML = `${close}<button type="button" class="btn btn-primary" id="p811-renew-go" onclick="window.DispatchModule.p811GroupRenewGo()"><span class="material-icons">autorenew</span> Renew</button>`;
  } else if (_p811GroupPanel === 'attach' && canDispatch) {
    foot.innerHTML = `${close}${_p811CanStartJob() ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811StartFromGroup('${k}')"><span class="material-icons">add_task</span> Start a new job</button>` : ''}<button type="button" class="btn btn-primary" id="p811-attach-go" onclick="window.DispatchModule.p811GroupAttachGo()"><span class="material-icons">link</span> Attach</button>`;
  } else if (_p811GroupPanel === 'project' && canDispatch) {
    foot.innerHTML = `${close}${_p811PotCanEdit() ? `<button type="button" class="btn btn-secondary" data-p811-newproj="1" onclick="window.DispatchModule.p811NewProjectFromGroup('${k}')"><span class="material-icons">lightbulb</span> Save as potential project</button>` : ''}<button type="button" class="btn btn-primary" id="p811-project-go" onclick="window.DispatchModule.p811GroupProjectGo()"><span class="material-icons">folder_open</span> Attach</button>`;
  } else {
    foot.innerHTML = (canDispatch ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811AttachGroupToJob('${kf}')"><span class="material-icons">link</span> Attach to job…</button>
      <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811AttachGroupToProject('${kf}')"><span class="material-icons">folder_open</span> Attach to project…</button>
      ${_p811CanStartJob() ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811StartFromGroup('${k}')"><span class="material-icons">add_task</span> Start a job</button>` : ''}
      <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811OpenGroup('${k}', true${kf === k ? '' : `, '${escJsAttr(_p811GroupFocus)}'`})"><span class="material-icons">autorenew</span> Renew…</button>` : '') + potBtn + close;
  }
  p811GroupSelChanged();
}
// TASK-9887 slice 3 — a ticket row's ⋮ menu was absolutely placed inside
// .p811-ticks (overflow:hidden) and the scrolling sheet body, so it opened
// under the "Attach" panel / off the bottom of the sheet (Chris's laptop,
// 10/15). Now it is placed in VIEWPORT coordinates (position:fixed escapes
// both clips), below the ⋮ when it fits, else flipped above, and clamped to
// stay fully on screen. Scroll / resize / outside click / Escape close it.
let _p811MenuWired = false;
function _p811CloseMenus(except) {
  document.querySelectorAll('details.p811-tickmenu[open]').forEach(d => { if (d !== except) d.open = false; });
}
function p811PlaceMenu(det) {
  const list = det && det.querySelector('.p811-tickmenu-list');
  const sum = det && det.querySelector('summary');
  if (!list || !sum) return;
  if (!det.open) { list.removeAttribute('style'); list.removeAttribute('data-flip'); return; }
  _p811CloseMenus(det);
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const vh = window.innerHeight;
  const pad = 8, gap = 4;
  Object.assign(list.style, { position: 'fixed', top: '0px', left: '0px', right: 'auto', zIndex: '1200', maxHeight: (vh - pad * 2) + 'px', overflowY: 'auto' });
  const r = sum.getBoundingClientRect();
  const w = list.offsetWidth, h = list.offsetHeight;
  const below = vh - r.bottom - pad, above = r.top - pad;
  let top = (h + gap <= below || below >= above) ? r.bottom + gap : r.top - gap - h;
  top = Math.max(pad, Math.min(top, vh - h - pad));
  const left = Math.max(pad, Math.min(r.right - w, vw - w - pad));
  list.style.top = Math.round(top) + 'px';
  list.style.left = Math.round(left) + 'px';
  list.dataset.flip = top < r.top ? 'up' : 'down';
  if (!_p811MenuWired) {
    _p811MenuWired = true;
    document.addEventListener('scroll', (e) => {
      if (e.target && e.target.closest && e.target.closest('.p811-tickmenu-list')) return;
      _p811CloseMenus(null);
    }, true);
    window.addEventListener('resize', () => _p811CloseMenus(null));
    document.addEventListener('click', (e) => {
      if (!(e.target && e.target.closest && e.target.closest('details.p811-tickmenu'))) _p811CloseMenus(null);
    }, true);
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = document.querySelector('details.p811-tickmenu[open]');
      if (open) { e.stopPropagation(); open.open = false; open.querySelector('summary')?.focus(); }
    }, true);
  }
}
function _p811GroupSelected() {
  return Array.from(document.querySelectorAll('#p811-group-body input.p811-gsel:checked')).map(c => c.value);
}
function p811GroupSelChanged() {
  const n = _p811GroupSelected().length;
  const c = document.getElementById('p811-sel-count');
  if (c) c.textContent = `${n} selected ticket${n === 1 ? '' : 's'}`;
  for (const id of ['p811-renew-go', 'p811-attach-go', 'p811-project-go']) {
    const b = document.getElementById(id);
    if (b) { b.disabled = n === 0; b.lastChild.textContent = ` ${id === 'p811-renew-go' ? 'Renew' : 'Attach'} ${n || ''}`.trimEnd(); }
  }
}
let _p811GroupBusy = false;
async function p811GroupRenewGo() {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (_p811GroupBusy) return;
  const ids = _p811GroupSelected();
  if (!ids.length) { window.showToast?.('Tick the tickets to renew.', 'info'); return; }
  const kind = document.querySelector('input[name=p811-renew-kind]:checked')?.value || 'new';
  const g = _p811FindGroup(_p811GroupOpenKey);
  if (kind === 'new') {
    closeModal('811-group');
    show811Submission({ renewIds: ids, customerId: g?.customerId || '' });
    document.getElementById('permit-file-input')?.click();
    return;
  }
  const date = document.getElementById('p811-renew-date')?.value || '';
  if (!_p811ValidExpiresOn(date)) { window.showToast?.('Pick the new good-through date from the ticket.', 'error'); return; }
  if (date < _p811Today()) { window.showToast?.('That date is already past. Check the ticket.', 'error'); return; }
  _p811GroupBusy = true;
  let failed = 0;
  try {
    for (const id of ids) {
      try { await _p811RenewSameNumber(id, date); } catch (e) { failed++; console.warn('[Dispatch] same-number renew failed', id, e && e.code); }
    }
  } finally { _p811GroupBusy = false; }
  window.showToast?.(failed ? `${ids.length - failed} renewed, ${failed} not saved. Try again.` : `Renewed ${ids.length} · good through ${_p811ShortDate(date, _p811Today())}`, failed ? 'warning' : 'success');
  _p811GroupPanel = '';
  _p811RenderGroup(true);
  renderPermitsList();
}
async function p811GroupAttachGo() {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (_p811GroupBusy) return;
  const ids = _p811GroupSelected();
  const jobId = document.getElementById('p811-attach-job')?.value || '';
  if (!ids.length) { window.showToast?.('Tick the tickets to attach.', 'info'); return; }
  if (!jobId || jobId === '__older') { window.showToast?.('Pick a job.', 'info'); return; }
  _p811GroupBusy = true;
  try {
    await _writeJobPermitIds(jobId, cur => [...cur, ...ids.filter(id => !cur.includes(id))]);
    const job = (DispatchState.jobs || []).find(j => j.id === jobId);
    window.showToast?.(`Attached ${ids.length} to ${formatJobNumber(job) || job?.title || 'the job'}`, 'success');
    _p811GroupPanel = '';
    _p811RenderGroup(true);
    renderPermitsList();
  } catch (e) {
    window.showToast?.(e && e.code === 'permit-cap' ? `That job can hold up to ${MAX_JOB_PERMIT_IDS} 811 tickets. Nothing was attached.` : "Couldn't attach them. Try again.", 'error', null, 8000);
  } finally { _p811GroupBusy = false; }
}
// "Move into <site>": every ticket of one group takes the other group's site
// name. Only on the click (Chris 10/14); never automatic.
async function p811MergeGroup(fromKey, toKey) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  const from = _p811FindGroup(fromKey), to = _p811FindGroup(toKey);
  if (!from || !to || from.customerId !== to.customerId) return;
  if (!(await _confirm811('Move tickets', `Move ${from.ids.length} ticket${from.ids.length === 1 ? '' : 's'} from "${from.siteName}" into "${to.siteName}"?`, 'Move', false))) return;
  let failed = 0;
  for (const p of from.permits) {
    const patch = { siteName: to.siteName };
    if (p.pendingJobName) patch.pendingJobName = null;
    if (p.proposedProjectName) patch.proposedProjectName = null;
    try { await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'permits', p.id), patch); Object.assign(p, patch); }
    catch (e) { failed++; }
  }
  window.showToast?.(failed ? `${from.ids.length - failed} moved, ${failed} not. Try again.` : `Moved ${from.ids.length} into ${to.siteName}`, failed ? 'warning' : 'success');
  renderPermitsList();
}
// TASK-9920 (Alyse 10/16: "I can't edit this part at the top once it is
// entered") — rename the SITE from the site sheet's header. The ⋮ Edit on a
// ticket renames ONE ticket (which splits it off a multi-ticket site); this
// writes the same siteName field to every ticket of the site, through the same
// permit update path and rules as ⋮ Edit and "Move into" (no new rule).
// Hidden for a partner (read-only) and a view-and-upload-only login: the rules
// refuse their permit updates anyway (isViewUploadOnly), the pencil would only fail.
function _p811CanRenameSite() {
  return window.isPartner811ReadOnly?.() !== true && window.AppState?.memberAccess?.viewUploadOnly !== true;
}
function p811RenameSiteStart() {
  if (!_p811CanRenameSite()) { window.showToast?.("This login can't rename 811 sites.", 'info'); return; }
  const g = _p811FindGroup(_p811GroupOpenKey);
  const title = document.getElementById('p811-group-title');
  if (!g || !title) return;
  title.innerHTML = `<span class="p811-edit p811-rename">
      <label for="p811-rename-input" class="p811-hdr-kicker">Locate site name</label>
      <input type="text" id="p811-rename-input" maxlength="${window.Locates811?.SITE_MAX || 120}" autocomplete="off" value="${escapeHtml(g.siteName)}"
        onkeydown="if(event.key==='Enter'){event.preventDefault();window.DispatchModule.p811RenameSiteSave();}else if(event.key==='Escape'){event.preventDefault();event.stopPropagation();window.DispatchModule.p811RenameSiteCancel();}">
      <button type="button" class="btn btn-primary" id="p811-rename-save" onclick="window.DispatchModule.p811RenameSiteSave()">Save</button>
      <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811RenameSiteCancel()">Cancel</button>
    </span>`;
  const inp = document.getElementById('p811-rename-input');
  if (inp) { inp.focus(); inp.select(); }
}
function p811RenameSiteCancel() {
  const title = document.getElementById('p811-group-title');
  if (title) title.innerHTML = '';
  _p811RenderGroup(true);
}
let _p811RenameBusy = false;
async function p811RenameSiteSave() {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (_p811RenameBusy) return;
  const L = window.Locates811;
  let g = _p811FindGroup(_p811GroupOpenKey);
  const inp = document.getElementById('p811-rename-input');
  if (!g || !inp || !L) return;
  const next = _p811CleanPendingName(inp.value).slice(0, L.SITE_MAX || 120);
  if (!next) { window.showToast?.('Type a site name.', 'error'); inp.focus(); return; }
  if (next === g.siteName) { p811RenameSiteCancel(); return; }
  const newKey = L.groupKey(g.customerId, next);
  // Same name as another site of this customer = those tickets join it. Say so first.
  const other = newKey !== g.key ? _p811FindGroup(newKey) : null;
  if (other && !(await _confirm811('Join another site', `"${other.siteName}" is already a site for this customer with ${other.ids.length} ticket${other.ids.length === 1 ? '' : 's'}. Move these ${g.ids.length} ticket${g.ids.length === 1 ? '' : 's'} into it?`, 'Move', false))) return;
  // COD 10/16 — re-read the site after the dialog: a ticket added meanwhile moves too.
  g = _p811FindGroup(g.key);
  if (!g) { window.showToast?.('This site has no tickets now.', 'info'); return; }
  _p811RenameBusy = true;
  const btn = document.getElementById('p811-rename-save');
  if (btn) btn.disabled = true;
  // COD 10/16 — ONE batch: all tickets move to the new name or none do (a
  // half-done loop would split the site). A site is far under the 500 cap.
  const patches = g.permits.map(p => {
    const patch = { siteName: next };
    if (p.pendingJobName) patch.pendingJobName = null;
    if (p.proposedProjectName) patch.proposedProjectName = null;
    return [p, patch];
  });
  let ok = false;
  try {
    const batch = writeBatch(db);
    for (const [p, patch] of patches) batch.update(doc(db, 'tenants', DispatchState.tenantId, 'permits', p.id), patch);
    await batch.commit();
    for (const [p, patch] of patches) Object.assign(p, patch);
    ok = true;
  } catch (e) {
    console.warn('[811] site rename failed:', e && e.code);
  } finally { _p811RenameBusy = false; }
  if (!ok) { window.showToast?.("Couldn't rename the site. Nothing was changed. Try again.", 'error', null, 8000); if (btn) btn.disabled = false; return; }
  _p811GroupOpenKey = newKey; // the sheet follows the site to its new name
  window.showToast?.(`Site renamed to ${next}`, 'success');
  renderPermitsList();
  const title = document.getElementById('p811-group-title');
  if (title) title.innerHTML = '';
  _p811RenderGroup(true);
}
// "Start a job" from a site: a NEW job with the customer, the site as the
// title, and every ticket of the site not already on a job (renewed left out).
async function p811StartFromGroup(key) {
  if (window.isPartner811ReadOnly?.() === true) { window.showToast?.("Only this company's own staff can change 811 tickets.", 'info'); return; } // TASK-9805
  if (!_p811CanStartJob()) { p811NewPotential(key); return; } // TASK-9994 — no job write without Dispatch
  const g = _p811FindGroup(key);
  if (!g) return;
  const todayKey = _p811Today();
  const ids = g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed' && _permitHolders(p.id).length === 0).map(p => p.id);
  if (!ids.length) { window.showToast?.('Every ticket of this site is already on a job.', 'info'); return; }
  closeModal('811-group');
  await p811StartFromTicket(ids[0], ids.slice(1));
}

// ── TASK-9887 slice 4 — potential jobs / projects ───────────────────────────
// An 811 person often knows the work before dispatch does, and some of it
// never happens. A potential (tenants/{tid}/potentials) holds customer, name,
// site, ticket links and notes. It is NEVER on the board. A dispatcher making a
// job / project like it gets a suggestion, and ONE confirm pulls its tickets
// in (never silently). Lifecycle + match rules: window.Locates811Search
// (potentialState / potentialMatch). Rules: '811-permits' or dispatch, no delete.
//
// TASK-9887 slice 6 (SKELETON ONLY) — directions + waypoints. Reserved shape,
// on a locate ticket (permits/{id}) AND on a potential:
//   directions  string <= 2000   the ticket's own "how to get there" text
//   waypoints   [{ lat, lng, label }] <= 25, in driving order
// Nothing writes them yet; the site sheet shows them when present (placeholder
// otherwise). The project map does not read them yet.
let _p811PotEditId = '';      // '' = a new potential
let _p811PotPrefillIds = [];
let _p811FormPotentialId = ''; // job form: the potential the dispatcher chose to use
let _p811FormPotentialIds = []; // ... and the ticket ids that brought in
let _p811PotDismissed = '';
function _p811PotentialsChanged() {
  const m = document.getElementById('modal-811-submission');
  if (m && !m.classList.contains('hidden') && !m.querySelector('#permits-list .p811-edit')) renderPermitsList();
  _p811RefreshGroupIfOpen();
  if (document.getElementById('modal-job') && !document.getElementById('modal-job').classList.contains('hidden')) _p811RenderJobAttach();
}
function _p811Uid() { return auth.currentUser?.uid || window.AppState?.user?.uid || null; }
function _p811PotCanEdit() { return window.isPartner811ReadOnly?.() !== true && !!_p811Uid(); }
function _p811Pots() {
  const tz = _p811Tz();
  // TASK-9887 — company retention (settings.potentialRetentionMonths, 0 = keep
  // forever): one that stopped suggesting longer ago than that drops out of view.
  const LS = window.Locates811Search;
  const months = _p811PotRetentionOf(window.AppState?.settings?.potentialRetentionMonths);
  const todayKey = _p811Today();
  return (DispatchState.potentials || []).filter(p => p && p.id).map(p => Object.assign({}, p, { _createdKey: _p811DayKey(p.createdAt, tz), _statusKey: _p811DayKey(p.statusAt, tz) }))
    .filter(p => !months || !LS?.potentialPastRetention || !LS.potentialPastRetention(p, DispatchState.permits || [], todayKey, p._createdKey, p._statusKey, months));
}
function _p811PotState(pot) {
  const LS = window.Locates811Search;
  const st = LS && pot ? LS.potentialState(pot, DispatchState.permits || [], _p811Today(), pot._createdKey) : 'open';
  // TASK-9994 (TITS 10/21) — every ticket already on a real job: the work
  // happened without "Use it", so it stops suggesting. Derived here, never
  // written by just looking: the list offers a one-tap Close that records it
  // the same way "Use it" does (status 'used' + usedId), with an audit row.
  if (st === 'open' && pot) { const c = _p811PotJobCoverage(pot); if (c.total && c.on === c.total) return 'onjob'; }
  return st;
}
// A job that holds a ticket for real: loaded, not cancelled, not a draft.
function _p811RealHolders(permitId) {
  return _permitHolders(permitId).filter(j => j && j.status !== 'cancelled' && j.status !== 'draft' && !j.deletedAt);
}
// Pure (unit-tested): how many of a potential's CURRENT tickets are on a job.
// holdersOf(id) -> [job]. Jobs are listed in the order first met, no repeats.
function _p811PotCoverageOf(tickets, holdersOf) {
  const jobs = new Map();
  let on = 0;
  for (const t of tickets || []) {
    const hs = holdersOf(t.id) || [];
    if (hs.length) on++;
    for (const j of hs) if (j && j.id && !jobs.has(j.id)) jobs.set(j.id, j);
  }
  return { total: (tickets || []).length, on, jobs: [...jobs.values()] };
}
function _p811PotJobCoverage(pot) {
  const todayKey = _p811Today();
  const ts = _p811PotTickets(pot).filter(t => _p811StatusOf(t, todayKey) !== 'renewed');
  return _p811PotCoverageOf(ts, _p811RealHolders);
}
function _p811PotJobsLabel(jobs) {
  const names = jobs.slice(0, 2).map(j => formatJobNumber(j) || j.title || 'a job');
  return names.join(', ') + (jobs.length > 2 ? ` +${jobs.length - 2}` : '');
}
// One tap: record it as used by the job its tickets are on (the "Use it" record).
async function p811PotentialCloseOnJob(id) {
  if (!_p811PotCanEdit() || !_p811CanStartJob()) return; // COD r1: rules allow 'used' only with Dispatch write access
  const pot = _p811Pots().find(p => p.id === id);
  if (!pot || _p811PotState(pot) !== 'onjob') return;
  const c = _p811PotJobCoverage(pot);
  const job = c.jobs[0];
  if (!job) return;
  const ok = await p811PotentialMarkUsed(id, 'job', job.id);
  if (!ok) { window.showToast?.("Couldn't close it. Try again.", 'error'); return; }
  window.logAudit?.('dispatch', 'POTENTIAL_CLOSED_ON_JOB', { potentialId: id, jobId: job.id, jobIds: c.jobs.map(j => j.id), tickets: c.total, statusFrom: pot.status || 'open', statusTo: 'used' });
  window.showToast?.(`Closed: on ${_p811PotJobsLabel([job])}`, 'success');
}
function _p811PotTickets(pot) {
  const LS = window.Locates811Search;
  return LS && pot ? LS.potentialTickets(pot, DispatchState.permits || []) : [];
}
// The last day it still suggests (last ticket expiry + 30).
function _p811PotUntil(pot) {
  const exps = _p811PotTickets(pot).map(p => _p811ValidExpiresOn(p.expiresOn)).filter(Boolean).sort();
  const last = exps[exps.length - 1] || pot._createdKey || '';
  return last ? _p811AddDays(last, window.Locates811Search?.POT_GRACE_DAYS || 30) : '';
}
function _p811PotKind(p) { return p && p.kind === 'project' ? 'Potential project' : 'Potential job'; }
// The open potential on a site: one of its tickets, or the same site (+ customer).
function _p811PotForGroup(g) {
  const L = window.Locates811;
  if (!g) return null;
  const ids = new Set(g.ids);
  const key = L ? L.siteKey(g.siteName) : '';
  return _p811Pots().find(p => _p811PotState(p) === 'open' && (
    _p811PotTickets(p).some(t => ids.has(t.id))
    || (!!key && !!L && (L.siteKey(p.siteName || '') === key || L.siteKey(p.name || '') === key) && (p.customerId || '') === (g.customerId || '')))) || null; // COD: same customer for a name-only match
}
// Search a potential by its words (dates do not apply to it).
function _p811PotScore(pot, parsed, todayKey) {
  const LS = window.Locates811Search;
  if (!LS || !parsed || !parsed.words.length) return 0;
  const ts = _p811PotTickets(pot);
  return LS.matchTicket(
    { name: pot.name, siteName: pot.siteName, customerName: pot.customerName || _customerNameById(pot.customerId) },
    Object.assign({}, parsed, { dates: [], status: '', empty: false }),
    { todayKey, extra: [pot.notes || '', _p811PotKind(pot), ...ts.map(t => t.ticketNumber || t.name || ''), ...ts.map(t => window.Locates811?.siteNameOf(t) || '')] });
}
function _p811PotUsedLabel(pot) {
  if (pot.usedKind === 'project') return ((window.ProjectsModule?.getState?.()?.projects || []).find(x => x && x.id === pot.usedId) || {}).name || 'a project';
  const j = (DispatchState.jobs || []).find(x => x && x.id === pot.usedId);
  return j ? (formatJobNumber(j) || j.title || 'a job') : 'a job';
}
function _p811PotCardHtml(pot, todayKey) {
  const st = _p811PotState(pot);
  const ts = _p811PotTickets(pot);
  const id = escJsAttr(pot.id);
  const cust = pot.customerName || _customerNameById(pot.customerId) || 'No customer';
  const until = st === 'open' ? _p811PotUntil(pot) : '';
  const cov = st === 'open' || st === 'onjob' ? _p811PotJobCoverage(pot) : null; // TASK-9994
  const part = st === 'open' && cov && cov.on ? ` <span class="p811-badge p811-b-none" data-p811-pot-part="1">${cov.on} of ${cov.total} on ${escapeHtml(_p811PotJobsLabel(cov.jobs))}</span>` : '';
  const badge = st === 'onjob' ? `<span class="p811-badge p811-b-clear" data-p811-pot-onjob="1"><span class="material-icons" aria-hidden="true">check_circle</span>Already on ${escapeHtml(_p811PotJobsLabel(cov.jobs))}</span>`
    : st === 'open' ? `<span class="p811-badge p811-b-pot"><span class="material-icons" aria-hidden="true">lightbulb</span>Suggested to dispatch${until ? ' until ' + escapeHtml(_p811ShortDate(until, todayKey)) : ''}</span>${part}`
    : st === 'wont' ? '<span class="p811-badge p811-b-none">Won\'t happen</span>'
    : st === 'used' ? `<span class="p811-badge p811-b-clear"><span class="material-icons" aria-hidden="true">check_circle</span>Used: ${escapeHtml(_p811PotUsedLabel(pot))}</span>`
    : '<span class="p811-badge p811-b-none">Archived</span>';
  const tix = ts.length ? ts.slice(0, 3).map(t => escapeHtml(t.ticketNumber || _p811TicketNoFromFile(t.name) || 'ticket')).join(', ') + (ts.length > 3 ? ` +${ts.length - 3}` : '') : 'No tickets yet';
  const can = _p811PotCanEdit();
  // TASK-9994 COD r1 — Close writes status 'used', which rules allow only to a
  // login with Dispatch write access (dispOk); others never see a Close that fails.
  const canClose = can && _p811CanStartJob();
  return `
    <div class="p811-group p811-pot is-${st}" data-pot-id="${escapeHtml(pot.id)}">
      <div class="p811-group-stripe"></div>
      <div class="p811-group-body">
        <div class="p811-group-left">
          <div class="p811-group-cust">${escapeHtml(_p811PotKind(pot))} · ${escapeHtml(cust)}</div>
          <p class="p811-group-title">${escapeHtml(pot.name || 'Untitled')}</p>
          <div class="p811-group-meta">${badge}${pot.siteName && pot.siteName !== pot.name ? ` · Site <b>${escapeHtml(pot.siteName)}</b>` : ''} · ${ts.length} ticket${ts.length === 1 ? '' : 's'}: ${tix}</div>
          ${pot.notes ? `<div class="p811-pot-notes">${escapeHtml(String(pot.notes).slice(0, 200))}${String(pot.notes).length > 200 ? '…' : ''}</div>` : ''}
        </div>
        <div class="p811-group-acts">
          ${can ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811OpenPotential('${id}')"><span class="material-icons" aria-hidden="true">edit</span>Edit</button>` : ''}
          ${canClose && st === 'onjob' ? `<button type="button" class="btn btn-primary" data-p811-pot-close="1" onclick="window.DispatchModule.p811PotentialCloseOnJob('${id}')"><span class="material-icons" aria-hidden="true">check</span>Close</button>` : ''}
          ${can && st === 'open' ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811PotentialStatus('${id}', 'wont')"><span class="material-icons" aria-hidden="true">do_not_disturb_on</span>Won't happen</button>` : ''}
          ${can && st === 'wont' ? `<button type="button" class="btn btn-secondary" onclick="window.DispatchModule.p811PotentialStatus('${id}', 'open')">Reopen</button>` : ''}
        </div>
      </div>
    </div>`;
}
// The Potential view (chip) and the "matching potentials" strip of a search.
function _p811PotListHtml(qRaw, todayKey) {
  const LS = window.Locates811Search;
  const parsed = qRaw && LS ? LS.parseSearch(qRaw, todayKey) : null;
  const rank = { onjob: 0, open: 0, wont: 2, used: 2, archived: 1 };
  const all = _p811Pots().map(p => ({ p, st: _p811PotState(p), s: parsed ? _p811PotScore(p, parsed, todayKey) : 1 }))
    .filter(r => !parsed || !parsed.words.length || r.s > 0)
    .sort((a, b) => (rank[a.st] - rank[b.st]) || (b.s - a.s) || String(a.p.name || '').localeCompare(String(b.p.name || '')));
  const add = _p811PotCanEdit() ? '<div class="p811-group-acts" style="margin:0 0 12px"><button type="button" class="btn btn-primary" id="btn-p811-new-potential" onclick="window.DispatchModule.p811NewPotential()"><span class="material-icons" aria-hidden="true">add</span>New potential job or project</button></div>' : '';
  const intro = '<p class="p811-jobhint">A potential is work you know is coming before dispatch does. It is not on the board. When dispatch makes a job or project like it, DispatchDeck suggests it and one tap pulls its tickets in. It stops suggesting 30 days after its last ticket expires, and stays here as Archived.</p>';
  if (!all.length) return intro + add + `<p class="p811-empty">${qRaw ? `No potential jobs or projects match "${escapeHtml(qRaw)}". Searched name, site, customer, notes and ticket #.` : 'No potential jobs or projects yet.'}</p>`;
  return intro + add + all.map(r => _p811PotCardHtml(r.p, todayKey)).join('');
}
function _p811PotCounts() { return _p811Pots().filter(p => _p811PotState(p) === 'open').length; }
function _p811PotMatchesForSearch(qRaw, todayKey) {
  const LS = window.Locates811Search;
  const parsed = qRaw && LS ? LS.parseSearch(qRaw, todayKey) : null;
  if (!parsed || !parsed.words.length) return [];
  return _p811Pots().map(p => ({ p, s: _p811PotScore(p, parsed, todayKey) })).filter(r => r.s > 0).sort((a, b) => b.s - a.s).map(r => r.p);
}

// ── the potential sheet ──────────────────────────────────────────────────────
// kind: 'job' (default) | 'project'. TASK-9994: this sheet is what "Add as
// potential job / project" opens from a site card, the site sheet and a ticket.
function p811NewPotential(groupKey, kind) {
  const g = groupKey ? _p811FindGroup(groupKey) : null;
  const todayKey = _p811Today();
  _p811PotEditId = '';
  _p811PotPrefillIds = g ? g.permits.filter(p => _p811StatusOf(p, todayKey) !== 'renewed').map(p => p.id) : [];
  _p811PotFill({ kind: kind === 'project' ? 'project' : 'job', name: g ? g.siteName : '', siteName: g ? g.siteName : '', customerId: g ? (g.customerId || '') : '', notes: '', status: 'open' });
}
// TASK-9994 — the same sheet for chosen tickets (a ticket row / its Edit row).
function p811NewPotentialForTickets(ids, kind) {
  const all = (DispatchState.permits || []).filter(p => p && (ids || []).includes(p.id));
  if (!all.length) return;
  const site = window.Locates811 ? window.Locates811.siteNameOf(all[0]) : (all[0].siteName || '');
  _p811PotEditId = '';
  _p811PotPrefillIds = all.map(p => p.id);
  _p811PotFill({ kind: kind === 'project' ? 'project' : 'job', name: site || '', siteName: site || '', customerId: all[0].customerId || '', notes: '', status: 'open' });
}
// TASK-9994 — after an upload: rows marked "+ Add as potential job / project"
// become potentials. One per customer + site + kind (an open one for that
// same customer + site + kind gains the tickets instead of a second copy).
// Returns how many could not be saved. Never writes a job or a job day.
async function _p811SavePotentialsFromRows(rows) {
  const L = window.Locates811;
  const want = (rows || []).filter(r => r && r.potKind && r.savedId && !r.potSaved);
  if (!want.length) return 0;
  const groups = new Map();
  for (const r of want) {
    const site = (L ? L.cleanText(r.siteName || '') : String(r.siteName || '').trim()).slice(0, 120);
    const cust = r.savedCustomerId || r.customerId || '';
    const key = [r.potKind, cust, L ? L.siteKey(site) : site.toLowerCase()].join('|');
    if (!groups.has(key)) groups.set(key, { kind: r.potKind, customerId: cust, site, rows: [] });
    groups.get(key).rows.push(r);
  }
  let failed = 0;
  for (const g of groups.values()) {
    const ids = g.rows.map(r => r.savedId);
    const name = g.site || ('Ticket #' + (g.rows[0].ticketNumber || _p811TicketNoFromFile(g.rows[0].file?.name) || '')).trim();
    const open = _p811Pots().find(p => _p811PotState(p) === 'open' && (p.kind || 'job') === g.kind && (p.customerId || '') === g.customerId
      && !!g.site && L && L.siteKey(p.siteName || p.name || '') === L.siteKey(g.site));
    let saved = [];
    try {
      saved = await _p811PotPlaceTickets({ kind: g.kind, name, site: g.site, customerId: g.customerId }, ids, open);
    } catch (e) {
      console.warn('[Dispatch] potential from upload failed', e && e.code);
    }
    // COD r1 (Edge Case / Code): only the rows that really landed are marked.
    const got = new Set(saved);
    g.rows.forEach(r => { if (got.has(r.savedId)) r.potSaved = true; });
    if (g.rows.some(r => !r.potSaved)) failed++;
  }
  return failed;
}
// TASK-9994 COD r1 — put ticket ids on potentials, never more than 20 on one
// (rules potOk) and never dropping any: the open twin gains what fits (in a
// transaction on the live doc, so two uploads at once cannot overwrite each
// other); the rest goes on new potentials of 20. A twin this login cannot
// update (a view & upload only member may only create) gets a new potential
// instead. Returns the ids now on a potential.
async function _p811PotPlaceTickets(base, ids, twin) {
  const tid = DispatchState.tenantId;
  const uid = _p811Uid();
  const placed = [];
  let rest = [...new Set(ids || [])];
  if (twin && rest.length) {
    try {
      const added = await runTransaction(db, async (tx) => {
        const ref = doc(db, 'tenants', tid, 'potentials', twin.id);
        const snap = await tx.get(ref);
        const cur = snap.exists() ? (snap.data().permitIds || []) : null;
        if (!cur || (snap.data().status && snap.data().status !== 'open')) return [];
        const fit = rest.filter(x => !cur.includes(x)).slice(0, Math.max(0, 20 - cur.length));
        if (fit.length) tx.update(ref, { permitIds: cur.concat(fit), updatedAt: Timestamp.now(), updatedBy: uid });
        return rest.filter(x => cur.includes(x)).concat(fit);
      });
      placed.push(...added);
      rest = rest.filter(x => !added.includes(x));
    } catch (e) {
      console.warn('[Dispatch] potential merge refused, making a new one', e && e.code);
    }
  }
  for (let i = 0; rest.length; i++) {
    const chunk = rest.slice(0, 20);
    const now = Timestamp.now();
    // COD r1 (Code): a later chunk failing never hides what already landed.
    try {
    const data = {
      kind: base.kind === 'project' ? 'project' : 'job', name: (String(base.name || 'Potential') + (i || placed.length ? ` (${i + 2})` : '')).slice(0, 120),
      siteName: base.site || null, customerId: base.customerId || null,
      customerName: base.customerId ? (_customerNameById(base.customerId) || null) : null, permitIds: chunk, notes: '',
      status: 'open', createdAt: now, createdBy: uid, updatedAt: now, updatedBy: uid,
    };
    const ref = await addDoc(collection(db, 'tenants', tid, 'potentials'), data);
    // Seen by Add Job / New Project before the snapshot lands (the snapshot replaces the list).
    if (!(DispatchState.potentials || []).some(p => p.id === ref.id)) DispatchState.potentials = (DispatchState.potentials || []).concat([{ id: ref.id, ...data }]);
    placed.push(...chunk);
    } catch (e) {
      console.warn('[Dispatch] potential create failed', e && e.code);
      break;
    }
    rest = rest.slice(20);
  }
  return placed;
}
function p811OpenPotential(id) {
  const pot = _p811Pots().find(p => p.id === id);
  if (!pot) { window.showToast?.('That potential is gone.', 'info'); return; }
  _p811PotEditId = id;
  _p811PotPrefillIds = _p811PotTickets(pot).map(t => t.id);
  _p811PotFill(pot);
}
function _p811PotFill(pot) {
  const m = document.getElementById('modal-811-potential');
  if (!m) return;
  // COD: a sheet opened over another one must not keep its ticks.
  const tickBox = document.getElementById('p811-pot-tickets');
  if (tickBox) delete tickBox.dataset.filled;
  document.getElementById('p811-pot-title').textContent = _p811PotEditId ? 'Edit potential' : 'New potential job or project';
  for (const r of m.querySelectorAll('input[name=p811-pot-kind]')) r.checked = r.value === (pot.kind === 'project' ? 'project' : 'job');
  document.getElementById('p811-pot-name').value = pot.name || '';
  document.getElementById('p811-pot-site').value = pot.siteName || '';
  document.getElementById('p811-pot-notes').value = pot.notes || '';
  _fill811CustomerSelect(document.getElementById('p811-pot-customer'), 'No customer yet', pot.customerId || '', false);
  _p811PotRenderTickets();
  const st = _p811PotEditId ? _p811PotState(pot) : 'new';
  const wont = document.getElementById('p811-pot-wont');
  if (wont) wont.classList.toggle('hidden', st !== 'open' && st !== 'onjob');
  m.classList.remove('hidden');
  setTimeout(() => document.getElementById('p811-pot-name')?.focus(), 50);
}
// Live tickets of the picked customer (all when none), the linked ones always.
function _p811PotRenderTickets() {
  const box = document.getElementById('p811-pot-tickets');
  if (!box) return;
  const ticked = new Set(box.dataset.filled ? Array.from(box.querySelectorAll('input:checked')).map(c => c.value) : _p811PotPrefillIds);
  const cust = document.getElementById('p811-pot-customer')?.value || '';
  const todayKey = _p811Today();
  const L = window.Locates811;
  const rows = (DispatchState.permits || []).filter(p => p && p.id && _p811StatusOf(p, todayKey) !== 'renewed' && (ticked.has(p.id) || !cust || p.customerId === cust))
    .sort((a, b) => (ticked.has(b.id) - ticked.has(a.id)) || String(L ? L.siteNameOf(a) : '').localeCompare(String(L ? L.siteNameOf(b) : '')));
  box.innerHTML = rows.length ? rows.map(p => `
    <label class="p811-pot-tick"><input type="checkbox" value="${escapeHtml(p.id)}" ${ticked.has(p.id) ? 'checked' : ''}>
      <span class="p811-mono">${escapeHtml(p.ticketNumber || _p811TicketNoFromFile(p.name) || '—')}</span>
      <span class="p811-pot-tick-site">${escapeHtml((L ? L.siteNameOf(p) : '') || p.name || '')}</span>
      ${_p811StatusBadge(p, todayKey)}</label>`).join('')
    : '<p class="p811-empty">No tickets for this customer yet. You can save it now and link tickets later.</p>';
  box.dataset.filled = '1';
}
function p811PotCustomerChanged() { _p811PotRenderTickets(); }
function p811ClosePotential() {
  const box = document.getElementById('p811-pot-tickets');
  if (box) delete box.dataset.filled;
  document.getElementById('modal-811-potential')?.classList.add('hidden');
}
let _p811PotBusy = false;
async function p811SavePotential() {
  if (_p811PotBusy || !_p811PotCanEdit()) return;
  const name = (window.Locates811 ? window.Locates811.cleanText(document.getElementById('p811-pot-name')?.value || '') : String(document.getElementById('p811-pot-name')?.value || '').trim()).slice(0, 120);
  if (!name) { window.showToast?.('Give it a name, like the lease or project.', 'info'); document.getElementById('p811-pot-name')?.focus(); return; }
  const kind = document.querySelector('input[name=p811-pot-kind]:checked')?.value === 'project' ? 'project' : 'job';
  const customerId = document.getElementById('p811-pot-customer')?.value || '';
  const site = (window.Locates811 ? window.Locates811.cleanText(document.getElementById('p811-pot-site')?.value || '') : '').slice(0, 120);
  const notes = String(document.getElementById('p811-pot-notes')?.value || '').trim().slice(0, 1000);
  const permitIds = Array.from(document.querySelectorAll('#p811-pot-tickets input:checked')).map(c => c.value).slice(0, 21);
  if (permitIds.length > 20) { window.showToast?.('A potential can hold up to 20 tickets.', 'warning'); return; }
  const uid = _p811Uid();
  const now = Timestamp.now();
  const data = { kind, name, siteName: site || null, customerId: customerId || null, customerName: customerId ? (_customerNameById(customerId) || null) : null,
    permitIds, notes, updatedAt: now, updatedBy: uid };
  _p811PotBusy = true;
  try {
    if (_p811PotEditId) {
      await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'potentials', _p811PotEditId), data);
    } else {
      await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'potentials'), { ...data, status: 'open', createdAt: now, createdBy: uid });
    }
    window.showToast?.(_p811PotEditId ? 'Potential saved' : `${kind === 'project' ? 'Potential project' : 'Potential job'} saved. Dispatch will see it as a suggestion.`, 'success');
    p811ClosePotential();
    renderPermitsList();
    _p811RefreshGroupIfOpen();
  } catch (e) {
    console.warn('[Dispatch] potential save failed', e && e.code);
    window.showToast?.(e && e.code === 'permission-denied' ? 'You need the 811 or Dispatch module to save potentials.' : "Couldn't save it. Try again.", 'error', null, 8000);
  } finally { _p811PotBusy = false; }
}
async function p811PotentialStatus(id, status) {
  if (!_p811PotCanEdit() || !['open', 'wont'].includes(status)) return;
  const pot = _p811Pots().find(p => p.id === id);
  if (!pot) return;
  if (status === 'wont' && !(await _confirm811("Won't happen?", `"${pot.name}" stops being suggested to dispatch. It stays in the Potential list as history.`, "Won't happen", false))) return;
  try {
    const uid = _p811Uid();
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'potentials', id), { status, statusAt: Timestamp.now(), statusBy: uid, updatedAt: Timestamp.now(), updatedBy: uid });
    window.showToast?.(status === 'wont' ? 'Marked won\'t happen' : 'Reopened', 'success');
    if (_p811PotEditId === id) p811ClosePotential();
  } catch (e) {
    window.showToast?.("Couldn't change it. Try again.", 'error');
  }
}
function p811PotentialWontFromSheet() { if (_p811PotEditId) p811PotentialStatus(_p811PotEditId, 'wont'); }

// ── dispatch side: the suggestion ────────────────────────────────────────────
// For projects.js (window.DispatchModule, Pitfall #1): the open potential a
// new job / project most likely is, with its CURRENT ticket ids.
function p811PotentialFor(kind, customerId, name) {
  const LS = window.Locates811Search;
  if (!LS || !is811Enabled()) return null;
  // TASK-9994 — never suggest one whose tickets are all on a job already.
  const pot = LS.potentialMatch(_p811Pots().filter(p => _p811PotState(p) !== 'onjob'), DispatchState.permits || [], { kind, customerId: customerId || '', name: String(name || '') }, _p811Today());
  return pot ? _p811PotOfferOf(pot, customerId) : null;
}
function _p811PotOfferOf(pot, customerId) {
  const todayKey = _p811Today();
  // COD (Domain + Adversary): only tickets that can still be dug on and that
  // belong to THIS customer (or none) are offered; the rest are counted, never
  // attached. The customer name comes from the id, not the stored copy.
  const all = _p811PotTickets(pot).filter(t => _p811StatusOf(t, todayKey) !== 'renewed');
  // COD round 2 (Domain + Code Reviewer): the customer that decides is the
  // job's, else the potential's. A ticket for another customer is never
  // offered; a ticket with NO customer only when the potential names that same
  // customer (the 811 person put it there for this work). No customer anywhere
  // = only customer-less tickets.
  const eff = customerId || pot.customerId || '';
  const ok = all.filter(t => _p811StatusOf(t, todayKey) !== 'expired'
    && (t.customerId ? t.customerId === eff : (!eff || pot.customerId === eff)));
  return { id: pot.id, kind: pot.kind, name: pot.name, siteName: pot.siteName || '', customerId: pot.customerId || '', notes: pot.notes || '',
    customerName: _customerNameById(pot.customerId) || pot.customerName || '',
    ticketIds: ok.map(t => t.id), leftOff: all.length - ok.length,
    // Shown before the one tap, so a lookalike name is caught by eye.
    ticketNos: ok.map(t => t.ticketNumber || _p811TicketNoFromFile(t.name) || '').filter(Boolean),
    sites: [...new Set(ok.map(t => window.Locates811?.siteNameOf(t) || '').filter(Boolean))] };
}
// Marks it used by a real job / project. Called only after that save succeeded.
async function p811PotentialMarkUsed(potId, kind, refId) {
  if (!potId || !refId) return false;
  try {
    const uid = _p811Uid();
    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'potentials', potId), {
      status: 'used', usedKind: kind === 'project' ? 'project' : 'job', usedId: refId, statusAt: Timestamp.now(), statusBy: uid, updatedAt: Timestamp.now(), updatedBy: uid,
    });
    return true;
  } catch (e) { console.warn('[Dispatch] potential mark-used failed', e && e.code); return false; }
}
// Job form: the potential this NEW job looks like (or the one already in use).
function _p811PotentialForForm() {
  if (document.getElementById('job-id')?.value || window.isMember811Only?.() === true || is811OnlyMode()) return null;
  if (_p811FormPotentialId) return (DispatchState.potentials || []).find(p => p.id === _p811FormPotentialId) ? { id: _p811FormPotentialId, inUse: true } : null;
  const title = String(document.getElementById('job-title')?.value || '').trim();
  if (title.length < 3) return null;
  const m = p811PotentialFor('job', document.getElementById('job-customer')?.value || '', title);
  return m && m.id !== _p811PotDismissed ? m : null;
}
function p811JobUsePotential(id) {
  const pot = (DispatchState.potentials || []).find(p => p.id === id);
  if (!pot) return;
  const formCust = document.getElementById('job-customer')?.value || '';
  if (formCust && pot.customerId && formCust !== pot.customerId) { window.showToast?.("That potential is for a different customer. Nothing was attached.", 'warning'); _p811RenderJobAttach(); return; }
  // valid tickets of this customer only; TASK-10181 COD r1: never one another job holds, and over the cap attach what fits
  const add = _p811AttachUpToCap(_p811PotOfferOf(pot, formCust || pot.customerId || '').ticketIds.filter(x => !_permitHolders(x).length));
  // COD r2: nothing attached = the potential is NOT in use (Save must not mark it used).
  if (!add.length) { _p811RenderJobAttach(); return; }
  const cs = document.getElementById('job-customer');
  if (cs && !cs.value && pot.customerId && [...cs.options].some(o => o.value === pot.customerId)) { cs.value = pot.customerId; cs.dispatchEvent(new Event('change', { bubbles: true })); }
  _p811FormPotentialId = id;
  _p811FormPotentialIds = add;
  _p811RenderJobAttach();
}
function p811JobUndoPotential() {
  if (_p811FormPotentialIds.length) _setJobPermitIds(_getJobPermitIds().filter(x => !_p811FormPotentialIds.includes(x)));
  _p811PotDismissed = _p811FormPotentialId;
  _p811FormPotentialId = '';
  _p811FormPotentialIds = [];
  _p811RenderJobAttach();
}
function p811JobDismissPotential(id) { _p811PotDismissed = id; _p811RenderJobAttach(); }
function _p811ResetFormPotential() { _p811FormPotentialId = ''; _p811FormPotentialIds = []; _p811PotDismissed = ''; _p811ResetNudge(); _p811RenderJobAttach(); }
// saveJob reads this BEFORE its first await (the form can be reset meanwhile).
// COD: only when the job's customer still fits the potential (it may have been
// changed after "Use it") — a mismatch saves the job and leaves the potential.
function _p811FormPotentialForSave(jobId) {
  if (jobId || !_p811FormPotentialId) return '';
  const pot = (DispatchState.potentials || []).find(p => p.id === _p811FormPotentialId);
  const cust = document.getElementById('job-customer')?.value || '';
  return pot && (!pot.customerId || !cust || pot.customerId === cust) ? _p811FormPotentialId : '';
}

// The flat ticket rows (the "Needs a site name" list).
function _p811FlatRowsHtml(rows) {
  const todayKey = _p811Today();
  return rows.map(permit => {
    const dateStr = _permitDateStr(permit);
    // TASK-9212 — was a `find`, which named an ARBITRARY first match. A ticket can
    // sit on several jobs (Copy carries it; a job can hold many), so say how many.
    const linkedJobs = _permitHolders(permit.id);
    const linkedLabel = linkedJobs.length === 0 ? ''
      : linkedJobs.length === 1 ? ` · Linked to: ${escapeHtml(linkedJobs[0].title)}`
      : ` · Linked to: ${escapeHtml(linkedJobs[0].title)} +${linkedJobs.length - 1} more`;
    const custLabel = permit.customerName ? ` · ${escapeHtml(permit.customerName)}` : '';
    const attrs = _permitLinkAttrs(permit);
    const exp = _p811Expiry(permit.expiresOn, todayKey);
    const badge = _p811BadgeHtml(permit, todayKey, true); // the date is in the meta line below
    const through = exp.state !== 'none' ? ` · ${escapeHtml(_p811ThroughText(permit, todayKey))}` : '';
    const waiting = linkedJobs.length === 0 && _p811ShowWarnings() ? '<span class="p811-wait">Not on a job yet</span>' : ''; // TASK-9994
    // TASK-9994 — Start job only for a login that can write jobs; otherwise a potential.
    const canStart = linkedJobs.length === 0 && _p811CanStartJob();
    const canPot = linkedJobs.length === 0 && !canStart && _p811PotCanEdit();
    return `
      <div class="permit-list-item${exp.state === 'expired' ? ' is-expired' : ''}" data-permit-id="${escapeHtml(permit.id)}">
        <div class="permit-list-info">
          <a ${attrs} class="permit-list-name">
            <span class="material-icons" style="font-size:18px;color:#B45309;">picture_as_pdf</span>
            ${escapeHtml(permit.name)}
          </a>
          <div class="permit-list-meta">
            ${badge}${waiting}${escapeHtml(dateStr)}${custLabel}${through}${linkedLabel}${_811ProjectLabel(permit.id)}
          </div>
        </div>
        ${canStart ? `<button class="btn btn-secondary p811-startjob" onclick="window.DispatchModule.p811StartFromTicket('${escJsAttr(permit.id)}')" title="Start a job from this ticket" aria-label="Start a job from this ticket"><span class="material-icons" aria-hidden="true">add_task</span> Start job</button>` : ''}
        ${canPot ? `<button class="btn btn-secondary p811-startjob" data-p811-pot-ticket="1" onclick="window.DispatchModule.p811NewPotentialForTickets(['${escJsAttr(permit.id)}'])" title="Add as a potential job or project for dispatch" aria-label="Add as a potential job or project"><span class="material-icons" aria-hidden="true">lightbulb</span> Add as potential</button>` : ''}
        ${window.isPartner811ReadOnly?.() === true ? '' : `<button class="btn-icon" onclick="window.DispatchModule.renamePermit('${escJsAttr(permit.id)}')" title="Edit ticket (site, ticket #, dates, customer, job)" aria-label="Edit ticket" style="margin-right:2px;">
          <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">edit</span>
        </button>`}
        ${is811OnlyMode() ? '' : `<button class="btn-icon btn-danger-icon" onclick="window.DispatchModule.deletePermit('${escJsAttr(permit.id)}')" title="Delete ticket" aria-label="Delete ticket">
          <span class="material-icons">delete</span>
        </button>`}
      </div>
    `;
  }).join('');
}


/**
 * TASK-9212 — the carried crew, for a Save that beat the roster snapshot.
 *
 * Same race, worse outcome. populateJobMultiSelect renders "Loading…" with
 * nothing checked while a collection is in flight, so `getJobCrewSelected`
 * honestly reports an empty selection — and a copy saved in that window creates
 * its jobDays with NO crew and no error. refreshJobModalCrewIfOpen would have
 * repaired the form, but only if the snapshot won the race.
 *
 * So for a COPY, and only for a copy (`_dupeCrewCarry`), a dimension that is
 * still flagged pending falls back to the seed intent instead of the empty DOM.
 * Per-dimension, matching the reseed's own rule: a dimension that has loaded is
 * read from the DOM, so anything the dispatcher changed wins. `people` covers
 * drivers and helpers together, as it does in jobCrewSeedPending.
 */
function _jobCrewForCreate(dimension, domKey) {
  const dom = getJobCrewSelected(domKey);
  if (!_dupeCrewCarry) return dom;
  const intent = DispatchState.jobCrewSeedIntent;
  const pending = DispatchState.jobCrewSeedPending;
  if (!intent || !pending || !pending[dimension]) return dom;

  // 🔴 ASSETS ARE NOT LIKE THE OTHER DIMENSIONS, and getting this wrong reopened
  // the exact bug round 2 closed (COD Code Reviewer + Independent, round 5 — the
  // Code Reviewer reproduced it against this task's own test harness).
  //
  // People, vehicles and trailers are inert: writing a carried id into a jobDay
  // assigns somebody, and nothing else moves. An ASSET id goes on to drive real
  // inventory — CREATE's rental lane writes `deployedToJobId` and increments
  // `deployedQty` off this very array. And the carried asset list is only
  // TRUSTWORTHY once the asset cache has loaded, because _normalizeCarriedCrew
  // deliberately KEEPS an asset it cannot find (a cache miss is not a decision).
  // So `intent.assets` during the pending window is the UNFILTERED list, and
  // handing it to the save steals a unique rental's pointer off the job that
  // still physically has the unit.
  //
  // Two answers, and the second is the one that also closes the async half of
  // the finding (this array is captured before several awaits and consumed after
  // them, so a snapshot landing in that window cannot be allowed to matter):
  if (dimension === 'assets') {
    // Cache is here now — re-derive from the RAW carry rather than trust the
    // frozen intent, so the rental/return/availability drops actually apply.
    if (DispatchState.dataLoaded.assets) return _normalizeCarriedCrew(_dupeCrewCarry).assets;
    // Cache is still absent: there is no way to tell a unique rental from a
    // pallet of mats. Carry NOTHING rather than carry something that writes
    // inventory unvetted, and say so — whatever is captured here is what the
    // inventory lane acts on minutes later, so an empty array is the only value
    // that stays safe no matter when the snapshot lands.
    if ((_dupeCrewCarry.assets || []).length > 0) {
      window.showToast?.(
        'Equipment was not copied — inventory had not loaded yet. Add it to the job and save again.',
        'warning', null, 9000
      );
    }
    return [];
  }
  return intent[domKey] || dom;
}

// ============================================
// MISSING DVIR POPOVER
// ============================================
function showMissingDvirPopover(event, timeClass, missingTrucks) {
  // Remove any existing popovers
  ['dvir-missing-popover', 'jsa-missing-popover', 'assignment-popover', 'p811-card-popover'].forEach(id => {
    document.getElementById(id)?.remove();
  });

  const label = timeClass === 'past' ? 'Missing DVIR' : 'No DVIR yet today';
  const color = timeClass === 'past' ? '#ea580c' : '#d97706';

  const popover = document.createElement('div');
  popover.id = 'dvir-missing-popover';
  popover.style.cssText = `
    position: fixed; z-index: 9999; background: #fff; border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
    padding: 12px 16px; min-width: 200px; max-width: 300px;
    border-top: 3px solid ${color}; font-size: 13px;
  `;

  let html = `<div style="font-weight:700;color:${color};margin-bottom:6px;display:flex;align-items:center;gap:4px;">
    <span class="material-icons" style="font-size:16px;">warning</span> ${label}
  </div>`;
  html += `<div style="color:var(--text-primary);">`;
  missingTrucks.forEach(name => {
    html += `<div style="padding:3px 0;display:flex;align-items:center;gap:6px;">
      <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">local_shipping</span>
      <span>${escapeHtml(name)}</span>
    </div>`;
  });
  html += `</div>`;
  popover.innerHTML = html;

  document.body.appendChild(popover);

  // Position near the clicked element
  const rect = event.target.closest('.assign-indicator').getBoundingClientRect();
  const popRect = popover.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.left - popRect.width / 2 + rect.width / 2;

  // Keep within viewport
  if (left < 8) left = 8;
  if (left + popRect.width > window.innerWidth - 8) left = window.innerWidth - popRect.width - 8;
  if (top + popRect.height > window.innerHeight - 8) top = rect.top - popRect.height - 6;

  popover.style.top = top + 'px';
  popover.style.left = left + 'px';

  // Dismiss on click outside
  setTimeout(() => {
    const dismiss = (e) => {
      if (!popover.contains(e.target)) {
        popover.remove();
        document.removeEventListener('click', dismiss, true);
      }
    };
    document.addEventListener('click', dismiss, true);
  }, 10);
}

// ============================================
// TASK-9811 S1 — EXTRA POs ON A JOB (UI + rules in jobExtraPos.js, lazy)
// ============================================
// The job keeps ONE main PO (poNumber, what tickets inherit); the others live in
// extraPoNumbers. State for the open form — null/untouched means saveJob writes
// nothing, so every path that never opens extras leaves the field as stored.
let _jobExtraPos = null; // { jobId, before[], now[], adding, msg }
let _jobExtraPosMod = null;
function _loadJobExtraPos() {
  if (!_jobExtraPosMod) _jobExtraPosMod = __ddChunkWrap(import('./jobExtraPos.js?v=9811003')).catch((e) => { _jobExtraPosMod = null; throw e; });
  return _jobExtraPosMod;
}
const _jobExtraPosCtx = () => ({ state: _jobExtraPos, host: document.getElementById('job-extra-pos'), mainPo: () => (document.getElementById('job-po') || {}).value || '', fetchTickets: _fetchLiveTicketsForJob });
function _openJobExtraPos(job) {
  const before = (job && Array.isArray(job.extraPoNumbers)) ? job.extraPoNumbers.filter((p) => typeof p === 'string') : [];
  const st = _jobExtraPos = { jobId: (job && job.id) || null, before, now: before.slice(), adding: false, msg: '' };
  const host = document.getElementById('job-extra-pos');
  if (host) host.textContent = '';
  if (before.length) _loadJobExtraPos().then((m) => { if (_jobExtraPos === st) m.renderEditor(_jobExtraPosCtx()); }).catch(() => {});
}
function addJobExtraPo() {
  if (!_jobExtraPos) _openJobExtraPos(null);
  _jobExtraPos.adding = true;
  _loadJobExtraPos().then((m) => m.renderEditor(_jobExtraPosCtx())).catch(() => window.showToast?.("Couldn't open the PO list. Try again.", 'error'));
}
// saveJob → { ok, write, list, message }. Untouched, or the module can't load
// (then nothing could have been changed through it) → write nothing.
async function _jobExtraPosForSave(jobId, mainPo) {
  const st = _jobExtraPos;
  if (!st || (st.jobId || null) !== (jobId || null) || (!st.before.length && !st.now.length)) return { ok: true };
  try { return (await _loadJobExtraPos()).checkSave(_jobExtraPosCtx(), mainPo); } catch (_) { return { ok: true }; }
}

// ============================================
// TASK-9645 — INVOICED / PAID ON THE JOB MODAL + EDIT JOB LOCK
// ============================================
// Billing state lives ONLY on tickets (jobs/jobDays carry none of it), so every
// answer here is derived from tickets matched by jobId OR originalJobId.

// Mirror of billing.js isTicketInvoiced (window.BillingDedup when loaded; this
// copy is the fallback so the LOCK never fails open because billing.js is
// absent). NOT a cross-import (Pitfall #1).
function _ticketCountsAsInvoiced(t) {
  const bd = (typeof window !== 'undefined') ? window.BillingDedup : null;
  if (bd && typeof bd.isTicketInvoiced === 'function') return bd.isTicketInvoiced(t);
  t = t || {};
  return t.invoiced === true || !!t.invoiceNumber || !!t.invoicedAt;
}

// PURE. The Edit Job lock decision. Locked when ANY live ticket of this job
// (jobId OR originalJobId; `deleted` never counts) is invoiced — billed
// elsewhere included (it carries an EXT- invoiceNumber). Archived-but-not-
// invoiced does NOT lock: it is most of history (732/903 TITS tickets) and
// would freeze nearly every job. Returns { locked, days[] } with days sorted
// 'YYYY-MM-DD' keys of the invoiced tickets (workDate, else originalWorkDate).
function _jobInvoiceLockFromTickets(tickets, jobId, isInvoiced) {
  const inv = typeof isInvoiced === 'function' ? isInvoiced : _ticketCountsAsInvoiced;
  const list = Array.isArray(tickets) ? tickets : [];
  const days = new Set();
  let locked = false;
  for (const t of list) {
    if (!t || (t.status || '') === 'deleted') continue;
    if (!(t.jobId === jobId || (t.originalJobId && t.originalJobId === jobId))) continue;
    if (!inv(t)) continue;
    locked = true;
    const d = t.workDate || t.originalWorkDate;
    if (typeof d === 'string' && d) days.add(d);
  }
  return { locked, days: Array.from(days).sort() };
}

// PURE. Which of the three locked fields (title / customer / PO) differ from
// what the modal opened with. Clock/hours are NOT here and never will be — those
// corrections stay in Workforce (COD fold 7).
function _jobLockedFieldsChanged(orig, now, normalizePo) {
  const np = typeof normalizePo === 'function' ? normalizePo : (v => String(v == null ? '' : v).trim());
  if (!orig) return ['title', 'customer', 'po']; // unknown baseline — check, never assume clean
  const out = [];
  if (String(now.title || '').trim() !== String(orig.title || '').trim()) out.push('title');
  if ((now.customerId || null) !== (orig.customerId || null)) out.push('customer');
  if (np(now.poNumber) !== np(orig.poNumber)) out.push('po');
  return out;
}

// 'YYYY-MM-DD' → 'Oct 12' (local, no TZ shift — built from parts).
function _fmtLockDay(dayStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayStr || ''));
  if (!m) return String(dayStr || 'A day');
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// The banner copy (COD fold 5 — names the way out). PURE.
function _jobInvoiceLockMessage(lock) {
  const days = (lock && lock.days) || [];
  const first = days.length ? _fmtLockDay(days[0]) : 'A day of this job';
  const more = days.length > 1 ? ` and ${days.length - 1} other day${days.length - 1 === 1 ? '' : 's'} are` : ' is';
  return `Locked: ${first}${more} invoiced. Someone with Billing can uninvoice it to change the title, customer or PO.`;
}

let _editJobLockBaseline = null; // { jobId, title, customerId, poNumber } at editJob open

// Paint (or clear) the lock on the shared job form. `lock` null/unlocked clears.
// Title + PO read-only, customer select + quick-add disabled; every other field
// stays editable (Copy to New Job, Add Day, Archive, reminders are separate).
function _applyEditJobInvoiceLock(lock) {
  const on = !!(lock && lock.locked);
  const title = document.getElementById('job-title');
  const po = document.getElementById('job-po');
  const cust = document.getElementById('job-customer');
  if (title) title.readOnly = on;
  // TASK-10106 S5 (COD r1) — a PIN driver's PO stays read-only when the job
  // already has one, whatever the invoice lock says.
  if (po) po.readOnly = on || (window.AppState?.driverModePin === true && DispatchState.driverPoLocked === true);
  if (cust) cust.disabled = on;
  document.querySelectorAll('button[onclick*="showQuickAddCustomer"]').forEach(b => {
    if (b.closest('#form-job')) b.disabled = on;
  });
  let banner = document.getElementById('job-invoice-lock-banner');
  if (!on) { if (banner) banner.remove(); return; }
  if (!banner) {
    const anchor = document.getElementById('job-customer-request-box');
    if (!anchor || !anchor.parentNode) return;
    banner = document.createElement('div');
    banner.id = 'job-invoice-lock-banner';
    banner.setAttribute('role', 'status');
    banner.style.cssText = 'display:flex;align-items:flex-start;gap:8px;margin:0 0 12px;padding:10px 12px;border-radius:8px;background:#FEF3C7;border:1px solid #FDE68A;color:#78350F;font-size:0.85rem;line-height:1.4;';
    anchor.parentNode.insertBefore(banner, anchor);
  }
  banner.innerHTML = `<span class="material-icons" style="font-size:18px;color:#92400E;flex-shrink:0;">lock</span><span>${escapeHtml(_jobInvoiceLockMessage(lock))}</span>`;
}

// editJob → instant answer from the live board stream, then the authoritative
// two-query read (the board stream is 90-day windowed; old invoiced days live
// outside it). `gen` drops a late answer for a form that has moved on.
function _refreshEditJobInvoiceLock(jobId, gen) {
  try {
    const fromBoard = _jobInvoiceLockFromTickets(DispatchState.tickets || [], jobId);
    if (fromBoard.locked) _applyEditJobInvoiceLock(fromBoard);
  } catch (e) { /* the live read below decides */ }
  _fetchLiveTicketsForJob(jobId).then(found => {
    if (gen !== _jobFormGen || document.getElementById('job-id')?.value !== jobId) return;
    if (!found.ok) return; // saveJob re-checks and fails closed on a locked-field change
    _applyEditJobInvoiceLock(_jobInvoiceLockFromTickets(found.tickets, jobId));
  }).catch(() => { /* saveJob re-checks */ });
}

// PURE-ish view helper. One Schedule row's billing chip. Tap opens the shared
// ticket popover (no hover-only tooltip). Returns '' for a day with no tickets.
function _renderJobDayBillingChip(jobId, dayStr, tickets) {
  const bd = window.BillingDedup;
  if (!bd || typeof bd.classifyDayBillingState !== 'function' || !dayStr) return '';
  const covering = (tickets || []).filter(t => t && (t.status || '') !== 'deleted'
    && (t.jobId === jobId || (t.originalJobId && t.originalJobId === jobId))
    && !t.archived && _ticketCoversDay(t, dayStr) && !t.sealedReturnEstimate && !_isSealedReturnEstimate(t));   // TASK-10107
  const st = bd.classifyDayBillingState(covering);
  if (!st.kind) return '';
  const isExt = typeof bd.isBilledExternally === 'function' ? bd.isBilledExternally : () => false;
  const MAP = {
    paid:     { color: '#15803D', bg: '#DCFCE7', fg: '#14532D', icon: 'check_circle', label: 'Paid' },
    invoiced: { color: '#22C55E', bg: '#DCFCE7', fg: '#14532D', icon: 'lock', label: 'Invoiced' },
    ready:    { color: '#F59E0B', bg: '#FEF3C7', fg: '#78350F', icon: 'schedule', label: 'Ready' },
    draft:    { color: '#EF4444', bg: '#FEE2E2', fg: '#7F1D1D', icon: 'edit_note', label: 'Draft' },
    archived: { color: '#94A3B8', bg: '#F1F5F9', fg: '#334155', icon: 'inventory_2', label: 'Archived' }
  };
  const m = MAP[st.kind];
  if (!m) return '';
  let text = m.label;
  if (st.kind === 'paid' && st.paidAtMs) text += ' ' + new Date(st.paidAtMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if ((st.kind === 'invoiced' || st.kind === 'paid') && st.billedElsewhere) text += ' (billed elsewhere)';
  if ((st.kind === 'invoiced' || st.kind === 'paid') && st.refs.length) text += ' · ' + st.refs.slice(0, 2).join(', ') + (st.refs.length > 2 ? '…' : '');
  if ((st.kind === 'draft' || st.kind === 'ready') && st.invoicedCount > 0) text += ` · ${st.invoicedCount} of ${st.count} invoiced`;
  const popTitle = st.kind === 'invoiced' && st.billedElsewhere ? 'Invoiced (billed elsewhere)' : m.label;
  const ticketsJson = encodeURIComponent(JSON.stringify(covering.map(t => {
    const inv = bd.isTicketInvoiced(t);
    const ext = inv && isExt(t);
    return { id: t.id, status: t.status, invoiced: inv, archived: bd.isTicketArchivedStatus(t), customer: t.customerName || t.customer || '',
      paid: inv && t.paid === true, ext, ref: inv ? String((ext ? (t.externalInvoiceRef || t.invoiceNumber) : t.invoiceNumber) || '') : '' };
  })));
  return `<button type="button" class="assign-indicator job-day-billing-chip" aria-label="${escapeHtml('Billing: ' + text)}" style="opacity:1;display:inline-flex;align-items:center;gap:4px;max-width:100%;padding:3px 8px;border-radius:12px;border:1px solid ${m.color};background:${m.bg};color:${m.fg};font-size:0.72rem;font-weight:600;line-height:1.2;cursor:pointer;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" onclick="event.stopPropagation(); window.DispatchModule.showTicketStatusPopover(event, '${escJsAttr(m.color)}', '${escJsAttr(popTitle)}', '${escJsAttr(ticketsJson)}', '${escJsAttr(st.kind)}')"><span class="material-icons" style="font-size:14px;color:${m.fg};">${m.icon}</span><span style="overflow:hidden;text-overflow:ellipsis;">${escapeHtml(text)}</span></button>`;
}

// Job details: paint chips from the board stream now, then from the live
// two-query read (old days sit outside the 90-day stream).
function _refreshJobDetailsBillingChips(jobId) {
  const paint = (tickets) => {
    document.querySelectorAll('#job-details-content [data-billing-chip-job]').forEach(el => {
      if (el.getAttribute('data-billing-chip-job') !== jobId) return;
      el.innerHTML = _renderJobDayBillingChip(jobId, el.getAttribute('data-billing-chip-day'), tickets);
    });
  };
  try { paint(DispatchState.tickets || []); } catch (e) { /* live read below */ }
  _fetchLiveTicketsForJob(jobId).then(found => {
    if (found && found.ok) paint(found.tickets);
  }).catch(() => { /* board-stream chips stay */ });
}

// "View invoice" — opens the ticket INSIDE Billing via the same hand-off the
// Clock receipt button uses (TASK-1364: the ticket modals only exist while the
// Billing view is mounted). Never the public ?invoice= URL. Re-gated here
// because an onclick string is replayable.
function openTicketInBillingFromDispatch(ticketId) {
  if (!(typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('billing', true) === true)) {
    window.showToast?.('Billing access is required to open invoices.', 'warning');
    return;
  }
  if (!ticketId || typeof window.navigateToView !== 'function') {
    window.showToast?.('Could not open that ticket.', 'error');
    return;
  }
  document.getElementById('ticket-status-popover')?.remove();
  closeModal('job-details');
  window._clockTicketDeepLink = { ticketId, at: Date.now(), returnTo: null };
  window.navigateToView('billing');
}

// ============================================
// TICKET STATUS POPOVER (Billing dot)
// ============================================
function showTicketStatusPopover(event, color, title, ticketsJson, kind) {
  // Remove any existing popovers
  ['dvir-missing-popover', 'jsa-missing-popover', 'assignment-popover', 'ticket-status-popover', 'p811-card-popover'].forEach(id => {
    document.getElementById(id)?.remove();
  });

  let tickets = [];
  try { tickets = JSON.parse(decodeURIComponent(ticketsJson)); } catch (e) { /* ignore */ }

  // TASK-9645 B — `kind` (paid / invoiced) picks the same glyph the badge shows.
  const icon = kind === 'paid' ? 'check_circle' : kind === 'invoiced' ? 'lock'
    : color === '#22C55E' ? 'check_circle' : color === '#EF4444' ? 'edit_note' : color === '#94A3B8' ? 'inventory_2' : 'schedule';
  // TASK-9645 C — "View invoice" ONLY for a Billing holder, and it opens the
  // ticket INSIDE Billing (openTicketInBillingFromDispatch) — never the public
  // ?invoice= link. Everyone else sees status text only, no link, no amounts.
  const _canBill = typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('billing', true) === true;

  const popover = document.createElement('div');
  popover.id = 'ticket-status-popover';
  popover.style.cssText = `
    position: fixed; z-index: 9999; background: #fff; border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
    padding: 12px 16px; min-width: 200px; max-width: 300px;
    border-top: 3px solid ${color}; font-size: 13px;
  `;

  let html = `<div style="font-weight:700;color:${color};margin-bottom:6px;display:flex;align-items:center;gap:4px;">
    <span class="material-icons" style="font-size:16px;">${icon}</span> ${escapeHtml(title)}
  </div>`;

  if (tickets.length > 0) {
    html += `<div style="color:var(--text-secondary);font-size:12px;margin-bottom:4px;">${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}</div>`;
    html += `<div style="color:var(--text-primary);">`;
    tickets.forEach(t => {
      // TASK-9344 — the fallback is the raw stored `status`, which the ticket
      // create rule does not constrain for a driver-PIN session. Escaped below.
      const statusLabel = t.paid ? 'Paid' : t.invoiced ? (t.ext ? 'Invoiced (billed elsewhere)' : 'Invoiced') : t.archived ? 'Archived' : t.status === 'draft' ? 'Draft' : t.status === 'ready' ? 'Ready' : t.status;
      const statusColor = t.paid ? '#15803D' : t.invoiced ? '#22C55E' : t.archived ? '#94A3B8' : t.status === 'draft' ? '#EF4444' : '#F59E0B';
      html += `<div style="padding:3px 0;display:flex;align-items:center;gap:6px;">
        <span style="width:6px;height:6px;border-radius:50%;background:${statusColor};flex-shrink:0;"></span>
        <span>${escapeHtml(t.customer || 'Ticket')}</span>
        <span style="margin-left:auto;font-size:11px;color:var(--text-secondary);">${escapeHtml(statusLabel)}</span>
      </div>`;
      // TASK-9645 B/C — the invoice reference (number or outside ref), then the
      // Billing-only link. A ref is a reference, not money; amounts never show.
      if (t.invoiced && t.ref) {
        html += `<div style="padding:0 0 3px 12px;font-size:11px;color:var(--text-secondary);">${t.ext ? 'Billed elsewhere · ' : ''}${escapeHtml(t.ref)}</div>`;
      }
      if (_canBill && t.invoiced && t.id) {
        html += `<div style="padding:0 0 4px 12px;"><button type="button" class="btn btn-secondary btn-small" style="font-size:11px;padding:4px 8px;" onclick="event.stopPropagation(); window.DispatchModule.openTicketInBillingFromDispatch('${escJsAttr(t.id)}')"><span class="material-icons" style="font-size:14px;">receipt_long</span> ${t.ext ? 'Open ticket' : 'View invoice'}</button></div>`;
      }
    });
    html += `</div>`;
  }

  popover.innerHTML = html;
  document.body.appendChild(popover);

  // Position near the clicked element
  const target = event.target.closest('.assign-indicator') || event.target;
  const rect = target.getBoundingClientRect();
  const popRect = popover.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.left - popRect.width / 2 + rect.width / 2;

  if (left < 8) left = 8;
  if (left + popRect.width > window.innerWidth - 8) left = window.innerWidth - popRect.width - 8;
  if (top + popRect.height > window.innerHeight - 8) top = rect.top - popRect.height - 6;

  popover.style.top = top + 'px';
  popover.style.left = left + 'px';

  setTimeout(() => {
    const dismiss = (e) => {
      if (!popover.contains(e.target)) {
        popover.remove();
        document.removeEventListener('click', dismiss, true);
      }
    };
    document.addEventListener('click', dismiss, true);
  }, 10);
}

// ============================================
// ASSIGNMENT POPOVER (People / Trucks)
// ============================================
// TASK-10050 — the ONE name resolver for a day's crew + equipment display. The
// 🚛/👤 popover and the List view "show on every job" lines both read it, so
// the two can never disagree. Vehicles: getUnitDisplay (nickname/unit) → unit →
// name, else the raw ref; people: legacy dual lookup (id or name), else the ref.
function _assignmentDisplayNames(jobDay) {
  const keep = (ref) => ref !== null && ref !== undefined && ref !== '';
  const fleet = (refs, list) => (refs || []).filter(keep).map(ref => {
    const t = _resolveFleetRef(ref, list);
    return t ? (window.getUnitDisplay?.(t) || t.unit || t.name || ref) : ref;
  });
  const people = (refs) => (refs || []).filter(keep).map(ref => {
    const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
    return p ? p.name : ref;
  });
  return {
    trucks: fleet(jobDay?.trucks, DispatchState.trucks),
    trailers: fleet(jobDay?.trailers, DispatchState.trailers),
    drivers: people(jobDay?.drivers),
    helpers: people(jobDay?.helpers),
  };
}

function showAssignmentPopover(event, jobDayId, type) {
  // Remove any existing popover
  ['assignment-popover', 'dvir-missing-popover', 'jsa-missing-popover', 'p811-card-popover'].forEach(id => {
    document.getElementById(id)?.remove();
  });

  const jobDay = _resolveJobDayAnywhere(jobDayId); // TASK-4535 — search cards too
  if (!jobDay) return;

  const popover = document.createElement('div');
  popover.id = 'assignment-popover';
  const color = type === 'trucks' ? '#3B82F6' : '#10b981';
  popover.style.cssText = `
    position: fixed; z-index: 9999; background: #fff; border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
    padding: 12px 16px; min-width: 180px; max-width: 300px;
    border-top: 3px solid ${color}; font-size: 13px;
  `;

  let html = '';
  const names = _assignmentDisplayNames(jobDay); // TASK-10050 — shared with the List view toggles

  if (type === 'trucks') {
    const { trucks, trailers } = names;
    html += `<div style="font-weight:700;color:${color};margin-bottom:6px;display:flex;align-items:center;gap:4px;">
      <span class="material-icons" style="font-size:16px;">local_shipping</span> Vehicles
    </div>`;
    if (trucks.length) {
      trucks.forEach(name => {
        html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
          <span style="font-size:14px;">🚛</span> <span>${escapeHtml(name)}</span>
        </div>`;
      });
    }
    if (trailers.length) {
      html += `<div style="margin-top:6px;font-weight:600;font-size:12px;color:var(--text-secondary);">Trailers</div>`;
      trailers.forEach(name => {
        html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
          <span style="font-size:14px;">🔗</span> <span>${escapeHtml(name)}</span>
        </div>`;
      });
    }
    if (!trucks.length && !trailers.length) html += '<div style="color:var(--text-secondary);">None assigned</div>';
  } else {
    const { drivers, helpers } = names;
    html += `<div style="font-weight:700;color:${color};margin-bottom:6px;display:flex;align-items:center;gap:4px;">
      <span class="material-icons" style="font-size:16px;">group</span> Crew
    </div>`;
    if (drivers.length) {
      drivers.forEach(name => {
        html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
          <span style="font-size:14px;">👤</span> <span>${escapeHtml(name)}</span>
        </div>`;
      });
    }
    if (helpers.length) {
      html += `<div style="margin-top:6px;font-weight:600;font-size:12px;color:var(--text-secondary);">Helpers</div>`;
      helpers.forEach(name => {
        html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
          <span style="font-size:14px;">🧑‍🔧</span> <span>${escapeHtml(name)}</span>
        </div>`;
      });
    }
    if (!drivers.length && !helpers.length) html += '<div style="color:var(--text-secondary);">None assigned</div>';
  }

  popover.innerHTML = html;
  document.body.appendChild(popover);

  // Position near the clicked element
  const rect = event.target.closest('.assign-indicator').getBoundingClientRect();
  const popRect = popover.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.left - popRect.width / 2 + rect.width / 2;
  if (left < 8) left = 8;
  if (left + popRect.width > window.innerWidth - 8) left = window.innerWidth - popRect.width - 8;
  if (top + popRect.height > window.innerHeight - 8) top = rect.top - popRect.height - 6;
  popover.style.top = top + 'px';
  popover.style.left = left + 'px';

  // Dismiss on click outside
  setTimeout(() => {
    const dismiss = (e) => {
      if (!popover.contains(e.target)) {
        popover.remove();
        document.removeEventListener('click', dismiss, true);
      }
    };
    document.addEventListener('click', dismiss, true);
  }, 10);
}

// ============================================
// MISSING JSA POPOVER (founder only)
// ============================================
function showMissingJsaPopover(event, jobDayId, timeClass) {
  // Remove any existing popover
  ['jsa-missing-popover', 'dvir-missing-popover', 'assignment-popover', 'p811-card-popover'].forEach(id => {
    document.getElementById(id)?.remove();
  });

  const jobDay = DispatchState.jobDays.find(jd => jd.id === jobDayId);
  if (!jobDay) return;
  const job = DispatchState.jobs.find(j => j.id === jobDay.jobId);

  const label = timeClass === 'past' ? 'Missing JSA' : 'No JSA yet today';
  const color = timeClass === 'past' ? '#ea580c' : '#d97706';

  // Resolve names — include drivers, helpers, trucks, trailers (same as assignment popovers)
  const drivers = (jobDay.drivers || []).map(ref => {
    const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
    return p ? p.name : ref;
  });
  const helpers = (jobDay.helpers || []).map(ref => {
    const p = DispatchState.people.find(pp => pp.id === ref || pp.name === ref);
    return p ? p.name : ref;
  });
  const trucks = (jobDay.trucks || []).map(ref => {
    const t = _resolveFleetRef(ref, DispatchState.trucks);
    return t ? (window.getUnitDisplay?.(t) || t.unit || t.name) : ref;
  });
  const trailers = (jobDay.trailers || []).map(ref => {
    const t = _resolveFleetRef(ref, DispatchState.trailers);
    return t ? (window.getUnitDisplay?.(t) || t.unit || t.name) : ref;
  });

  const popover = document.createElement('div');
  popover.id = 'jsa-missing-popover';
  popover.style.cssText = `
    position: fixed; z-index: 9999; background: #fff; border-radius: 10px;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08);
    padding: 12px 16px; min-width: 220px; max-width: 300px;
    border-top: 3px solid ${color}; font-size: 13px;
  `;

  let html = `<div style="font-weight:700;color:${color};margin-bottom:6px;display:flex;align-items:center;gap:4px;">
    <span class="material-icons" style="font-size:16px;">health_and_safety</span> ${label}
  </div>`;

  if (job) {
    html += `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(job.title || 'Untitled')}</div>`;
  }

  // Crew section
  if (drivers.length || helpers.length) {
    html += `<div style="font-weight:600;font-size:12px;color:var(--text-secondary);margin-top:6px;">Crew</div>`;
    html += `<div style="color:var(--text-primary);margin-top:2px;">`;
    drivers.forEach(name => {
      html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
        <span style="font-size:14px;">👤</span> <span>${escapeHtml(name)}</span>
      </div>`;
    });
    helpers.forEach(name => {
      html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
        <span style="font-size:14px;">🧑‍🔧</span> <span>${escapeHtml(name)}</span>
      </div>`;
    });
    html += '</div>';
  }

  // Vehicles section
  if (trucks.length || trailers.length) {
    html += `<div style="font-weight:600;font-size:12px;color:var(--text-secondary);margin-top:6px;">Vehicles</div>`;
    html += `<div style="color:var(--text-primary);margin-top:2px;">`;
    trucks.forEach(name => {
      html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
        <span style="font-size:14px;">🚛</span> <span>${escapeHtml(name)}</span>
      </div>`;
    });
    trailers.forEach(name => {
      html += `<div style="padding:2px 0;display:flex;align-items:center;gap:6px;">
        <span style="font-size:14px;">🔗</span> <span>${escapeHtml(name)}</span>
      </div>`;
    });
    html += '</div>';
  }

  if (!drivers.length && !helpers.length && !trucks.length && !trailers.length) {
    html += '<div style="color:var(--text-secondary);margin-top:4px;">No crew or vehicles assigned</div>';
  }

  html += `<div style="display:flex;gap:6px;margin-top:10px;">
    <button class="btn btn-sm" style="flex:1;background:${color};color:#fff;border:none;border-radius:6px;padding:6px;font-size:12px;cursor:pointer;" onclick="document.getElementById('jsa-missing-popover')?.remove(); window.JSAModule?.showNewJSA?.('${escJsAttr(job?.id || '')}', '${escJsAttr(jobDayId)}');">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;">add</span> Submit JSA
    </button>
    <button class="btn btn-sm" style="flex:1;background:#fff;color:var(--text-secondary);border:1px solid #CBD5E1;border-radius:6px;padding:6px;font-size:12px;cursor:pointer;" onclick="window.DispatchModule.dismissJsa('${escJsAttr(jobDayId)}')">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;">do_not_disturb</span> Dismiss
    </button>
  </div>`;

  popover.innerHTML = html;
  document.body.appendChild(popover);

  // Position near the clicked element
  const anchor = event.target.closest('.assign-indicator') || event.target.closest('.missing-jsa-line') || event.target;
  const rect = anchor.getBoundingClientRect();
  const popRect = popover.getBoundingClientRect();
  let top = rect.bottom + 6;
  let left = rect.left - popRect.width / 2 + rect.width / 2;
  if (left < 8) left = 8;
  if (left + popRect.width > window.innerWidth - 8) left = window.innerWidth - popRect.width - 8;
  if (top + popRect.height > window.innerHeight - 8) top = rect.top - popRect.height - 6;
  popover.style.top = top + 'px';
  popover.style.left = left + 'px';

  // Dismiss on click outside
  setTimeout(() => {
    const dismiss = (e) => {
      if (!popover.contains(e.target)) {
        popover.remove();
        document.removeEventListener('click', dismiss, true);
      }
    };
    document.addEventListener('click', dismiss, true);
  }, 10);
}

// ============================================
// DISMISS JSA (without filing a report)
// ============================================
async function dismissJsa(jobDayId) {
  // Remove popover immediately
  document.getElementById('jsa-missing-popover')?.remove();

  const tenantId = window.AppState?.tenant;
  if (!tenantId || !jobDayId) return;

  try {
    // Write a minimal dismissed doc to jsaReports — clears the indicator
    await addDoc(collection(db, 'tenants', tenantId, 'jsaReports'), {
      jobDayId,
      dismissed: true,
      createdAt: Timestamp.now(),
      dismissedBy: auth.currentUser?.email || 'unknown'
    });

    // Update local lookup immediately so UI refreshes without waiting for snapshot
    DispatchState.jsaJobDayIds[jobDayId] = true;
    _renderDispatchBoardNow(); // optimistic local-state update — paint immediately

    window.showToast?.('JSA dismissed', 'success');
  } catch (err) {
    console.error('[Dispatch] Failed to dismiss JSA:', err);
    window.showToast?.('Failed to dismiss JSA', 'error');
  }
}

// ============================================
// DRIVER TIME OFF (PIN Mode)
// ============================================
function openDriverTimeOff() {
  // Legacy driver-PIN call site. Delegates to openDriverTimeOffForPerson with
  // identity pulled from AppState.driverPersonId/Name. TASK-348 refactor.
  return openDriverTimeOffForPerson(
    window.AppState?.driverPersonId,
    window.AppState?.driverPersonName || ''
  );
}

function openDriverTimeOffForPerson(personId, personName) {
  if (personId) DispatchState._personalActionPersonId = personId;
  if (personName) DispatchState._personalActionPersonName = personName;
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const driverName = personName || window.AppState?.driverPersonName || '';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'driver-timeoff-modal';

  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:480px;">
      <div class="eticket-form-header">
        <div class="eticket-form-header-title">
          <span class="material-icons">event_busy</span>
          Request Time Off
        </div>
        <button class="modal-close" onclick="window.DispatchModule.closeDriverTimeOff()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;overflow-y:auto;">
        <div class="eticket-form-context" style="margin-bottom:16px;">
          <div class="eticket-form-context-row">
            <span class="material-icons">person</span>
            <span><strong>${escapeHtml(driverName)}</strong></span>
          </div>
        </div>
        <div class="eticket-form-field">
          <label class="eticket-form-label">Type</label>
          <select class="form-control" id="driver-timeoff-type">
            <!-- TASK-9838: Unpaid only until the person is known to be on a plan;
                 _applyDriverTimeOffTypeOptions adds the rest once loaded. -->
            <option value="unpaid">Unpaid</option>
          </select>
          <div id="driver-timeoff-type-hint" style="display:none;margin-top:6px;font-size:0.8rem;color:var(--text-secondary);"></div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="eticket-form-field">
            <label class="eticket-form-label">Start Date</label>
            <input type="date" class="form-control" id="driver-timeoff-start" value="${tomorrow}" required onchange="var _e=document.getElementById('driver-timeoff-end');if(_e&&this.value&&_e.value<this.value)_e.value=this.value;window.DispatchModule.onTimeOffDateChange()">
          </div>
          <div class="eticket-form-field">
            <label class="eticket-form-label">End Date</label>
            <input type="date" class="form-control" id="driver-timeoff-end" value="${tomorrow}" required onchange="window.DispatchModule.onTimeOffDateChange()">
          </div>
        </div>
        <div class="eticket-form-field">
          <label class="eticket-form-label">Hours</label>
          <div style="display:flex;align-items:center;gap:8px;">
            <input type="number" class="form-control" id="driver-timeoff-hours" min="1" max="2000" value="8" style="width:100px;">
            <span id="driver-timeoff-day-hint" style="font-size:0.8rem;color:var(--text-secondary);">1 day = 8 hrs</span>
          </div>
        </div>
        <div class="eticket-form-field">
          <label class="eticket-form-label">Reason (optional)</label>
          <textarea rows="3" class="form-control" id="driver-timeoff-reason" placeholder="Optional notes..."></textarea>
        </div>
        <!-- Chris 2026-07-13: balance + request history sit BELOW the form —
             the request fields are the modal's job; history is reference. -->
        <div id="driver-timeoff-balance" style="margin-bottom:16px;padding:12px;border-radius:8px;background:var(--bg-elevated);display:none;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">account_balance</span>
            <strong style="font-size:0.85rem;">Your Time-Off Balance</strong>
          </div>
          <div id="driver-timeoff-balance-content" style="font-size:0.85rem;color:var(--text-secondary);"></div>
        </div>
        <div id="driver-timeoff-requests" style="margin-bottom:16px;padding:12px;border-radius:8px;background:var(--bg-elevated);display:none;">
          <div style="display:flex;align-items:center;gap:6px;margin-bottom:8px;">
            <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">history</span>
            <strong style="font-size:0.85rem;">Your Requests</strong>
          </div>
          <div id="driver-timeoff-unpaid-taken" style="display:none;margin-bottom:8px;font-size:0.85rem;font-weight:600;color:var(--text-primary);"></div>
          <div id="driver-timeoff-requests-content" style="font-size:0.85rem;color:var(--text-secondary);"></div>
        </div>
      </div>
      <div class="eticket-form-actions">
        <button class="btn btn-secondary" onclick="window.DispatchModule.closeDriverTimeOff()">Cancel</button>
        <button class="btn btn-primary" id="btn-submit-driver-timeoff" onclick="window.DispatchModule.submitDriverTimeOff()">
          <span class="material-icons">send</span> Submit Request
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // TASK-9838 — fail closed: Unpaid only until the plan check below says otherwise.
  DispatchState._driverTimeOffHasPlan = false;
  DispatchState._driverTimeOffPlanUnknown = false;

  // Auto-load time-off balance for this driver
  loadDriverTimeOffBalance();
}

// >>> TASK-9838 no time-off plan = Unpaid only — BEGIN
// The driver modal's type list. With a plan (the person's Time-Off Level is in
// the company plan) every type shows as before; without one it is Unpaid only
// (Chris 10/13 — no Bereavement either: that belongs in the plan). The check is
// window.TimeOffBalance.hasPlan, the same rule the office form uses.
const DRIVER_TIMEOFF_ALL_TYPES = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick Leave' },
  { value: 'personal', label: 'Personal Day' },
  { value: 'bereavement', label: 'Bereavement' },
  { value: 'other', label: 'Other' },
];
function _driverTimeOffHasPlan(person, policy) {
  const TOB = window.TimeOffBalance;
  if (TOB && typeof TOB.hasPlan === 'function') return TOB.hasPlan(person, policy);
  const levelId = person && person.hrTimeOffLevel;
  return !!levelId && !!policy && Array.isArray(policy.levels) && policy.levels.some(l => l && l.id === levelId);
}
// Rebuild #driver-timeoff-type. `keepType` (an edited request's own type) stays
// selectable even when no longer offered, so saving an edit never silently
// rewrites the type.
function _applyDriverTimeOffTypeOptions(keepType) {
  const sel = document.getElementById('driver-timeoff-type');
  if (!sel) return;
  const hasPlan = !!DispatchState._driverTimeOffHasPlan;
  const options = (hasPlan ? DRIVER_TIMEOFF_ALL_TYPES : DRIVER_TIMEOFF_ALL_TYPES.filter(o => o.value === 'unpaid')).slice();
  const wanted = sel.value || keepType || 'unpaid';
  if (keepType && !options.some(o => o.value === keepType)) {
    const known = DRIVER_TIMEOFF_ALL_TYPES.find(o => o.value === keepType);
    options.push({ value: keepType, label: `${known ? known.label : keepType} (no longer offered)` });
  }
  sel.innerHTML = options.map(o => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`).join('');
  sel.value = options.some(o => o.value === wanted) ? wanted : 'unpaid';
  const hint = document.getElementById('driver-timeoff-type-hint');
  if (hint) {
    hint.textContent = hasPlan ? ''
      : DispatchState._driverTimeOffPlanUnknown
        ? "Couldn't load your time-off plan, so only Unpaid shows. Close and reopen this window to try again."
        : 'No time-off plan on your profile, so requests are Unpaid.';
    hint.style.display = hasPlan ? 'none' : '';
  }
}
// "Unpaid time off taken in 2026: N hrs" — approved Unpaid days already past,
// this year, per day (window.TimeOffBalance.unpaidTakenHours: excluded days and
// per-day types honoured, same hours-per-day as the HR tab). A count, never a
// balance: no "available", "owed" or "remaining" wording.
function _renderDriverUnpaidTaken(person, approvedSnap) {
  const el = document.getElementById('driver-timeoff-unpaid-taken');
  const TOB = window.TimeOffBalance;
  if (!el) return;
  if (!TOB || typeof TOB.unpaidTakenHours !== 'function' || !approvedSnap) { el.style.display = 'none'; return; }
  const personId = DispatchState._personalActionPersonId || window.AppState?.driverPersonId;
  const requests = (approvedSnap.docs || []).map(d => ({ id: d.id, ...d.data() }));
  const r = TOB.unpaidTakenHours({ ...(person || {}), id: personId }, requests, {
    personId,
    tz: window.AppState?.settings?.timezone || window.AppState?.tenantData?.timezone || 'America/Chicago',
  });
  el.textContent = `Unpaid time off taken in ${r.year}: ${r.hours} hr${r.hours === 1 ? '' : 's'}`;
  el.style.display = '';
}
// <<< TASK-9838 no time-off plan = Unpaid only — END

function onTimeOffDateChange() {
  const start = document.getElementById('driver-timeoff-start')?.value;
  const end = document.getElementById('driver-timeoff-end')?.value;
  const hint = document.getElementById('driver-timeoff-day-hint');
  if (start && end && hint) {
    const days = Math.max(1, Math.round((new Date(end) - new Date(start)) / 86400000) + 1);
    hint.textContent = `${days} day${days !== 1 ? 's' : ''} = ${days * 8} hrs`;
    const hoursEl = document.getElementById('driver-timeoff-hours');
    if (hoursEl) hoursEl.value = days * 8;
  }
}

// TASK-320: Loads both the time-off balance card AND the "Your Requests" list
// (last 5 requests, all statuses) in a single Promise.all to keep the modal
// open snappy. Renamed legacy entry point kept as wrapper for the existing
// call site.
//
// COLLECTION NOTE (corrected via TASK-320): every writer (hr.js approve/deny,
// driver submit at submitDriverTimeOff above) writes to `timeOff`. The legacy
// version of this loader queried `timeOffRequests`, which silently returned
// zero docs, so the balance card never actually rendered. Fixed in-place
// since we're already touching this function.
async function loadDriverTimeOffBalance() {
  return _loadDriverTimeOffPanels();
}

async function _loadDriverTimeOffPanels() {
  const tenant = window.AppState?.tenant;
  // TASK-348 — prefer SPA personal-action personId when set; fall back to PIN.
  const personId = DispatchState._personalActionPersonId
    || window.AppState?.driverPersonId;
  if (!tenant || !personId) return;
  // TASK-4720 follow-up — anything waiting on the phone tries to send now.
  try { if (window.TimeOffOutbox) flushDriverTimeOffOutbox(); } catch (_) { /* best effort */ }
  // TASK-9838 COD r1 — out-of-order guard (see _driverTimeOffLoadGen).
  const _gen = ++DispatchState._driverTimeOffLoadGen;
  const _modalEl = document.getElementById('driver-timeoff-modal');

  try {
    // Load person, policy, approved requests (for balance math), and recent
    // requests (for the "Your Requests" list) in parallel.
    // TASK-1121 fix (Chris repro, PIN driver CH6711) — THREE stacked breaks:
    // (1) the policy read pointed at `hr/timeOffPolicy` — a NONEXISTENT doc in a
    //     rule-less collection (default deny) — the real doc is
    //     `hrTimeOffPolicy/config` (hr.js/timeoff.js agree);
    // (2) orderBy('createdAt') has NO composite index with personId — the
    //     EXISTING index is (personId, startDate DESC), and startDate is the
    //     right semantic anyway (future accepted first, then history);
    // (3) one rejected read killed the whole Promise.all, taking the requests
    //     list down with the balance — each read now fails independently.
    const _safe = (pr) => pr.catch((e) => { console.warn('[Dispatch] Time off panel read failed:', e?.message); return null; });
    const [personSnap, policySnap, approvedSnap, recentSnap] = await Promise.all([
      _safe(getDoc(doc(db, 'tenants', tenant, 'people', personId))),
      _safe(getDoc(doc(db, 'tenants', tenant, 'hrTimeOffPolicy', 'config'))),
      _safe(getDocs(query(collection(db, 'tenants', tenant, 'timeOff'),
        where('personId', '==', personId),
        where('status', '==', 'approved')))),
      _safe(getDocs(query(collection(db, 'tenants', tenant, 'timeOff'),
        where('personId', '==', personId),
        orderBy('startDate', 'desc'),
        limit(50))))
    ]);

    if (_gen !== DispatchState._driverTimeOffLoadGen || !_modalEl || document.getElementById('driver-timeoff-modal') !== _modalEl) return; // TASK-9838 stale load
    const person = (personSnap && personSnap.exists()) ? personSnap.data() : null;
    const policy = (policySnap && policySnap.exists()) ? policySnap.data() : null;

    // TASK-9838 — the type list follows the plan; a failed person/policy read
    // leaves it Unpaid only (fail closed). An edit in progress keeps its type.
    DispatchState._driverTimeOffHasPlan = _driverTimeOffHasPlan(person, policy);
    DispatchState._driverTimeOffPlanUnknown = !personSnap || !policySnap;
    const _editing = DispatchState._editTimeOffRequestId
      ? (DispatchState._driverTimeOffRequests?.[DispatchState._editTimeOffRequestId]?.type || null) : null;
    _applyDriverTimeOffTypeOptions(_editing);
    _renderDriverUnpaidTaken(person, approvedSnap);
    if (person && policy && approvedSnap) _renderDriverTimeOffBalance(person, policy, approvedSnap);
    if (recentSnap) { _renderDriverTimeOffRequests(recentSnap); } else {
      const _c = document.getElementById('driver-timeoff-requests');
      const _cc = document.getElementById('driver-timeoff-requests-content');
      if (_c && _cc) {
        _cc.innerHTML = _driverOutboxRowsHtml().html + `<div style="color:var(--text-secondary);font-style:italic;">Couldn't load your requests — check your connection and reopen this window.</div>`;
        _c.style.display = '';
      }
    }
  } catch (err) {
    // Count/message only — request docs can carry personal detail in .reason.
    console.warn('[Dispatch] Time off panels load error:', err.message);
    // TASK-1121 — surface the failure instead of a silently-missing panel.
    const _c = document.getElementById('driver-timeoff-requests');
    const _cc = document.getElementById('driver-timeoff-requests-content');
    if (_c && _cc) {
      _cc.innerHTML = `<div style="color:var(--text-secondary);font-style:italic;">Couldn't load your requests — check your connection and reopen this window.</div>`;
      _c.style.display = '';
    }
  }
}

// >>> TASK-9839 driver time-off balance (shared calculator) — BEGIN
// The math is window.TimeOffBalance.computeBalances — the SAME function the HR
// tab runs — so the driver sees the office's number: accrual methods, per-day
// types, excluded days, milestone grants and negatives included.
function _renderDriverTimeOffBalance(person, policy, approvedSnap, clockHours) {
  const container = document.getElementById('driver-timeoff-balance');
  const content = document.getElementById('driver-timeoff-balance-content');
  const TOB = window.TimeOffBalance;
  if (!container || !content || !TOB || !person || !policy) return;
  const personId = DispatchState._personalActionPersonId || window.AppState?.driverPersonId;
  const tenant = window.AppState?.tenant;
  const requests = (approvedSnap?.docs || []).map(d => ({ id: d.id, ...d.data() }));
  const opts = {
    personId,
    tz: window.AppState?.settings?.timezone || window.AppState?.tenantData?.timezone || 'America/Chicago',
    payPeriodType: window.AppState?.settings?.payPeriodType || 'biweekly',
    clockHours: typeof clockHours === 'number' ? clockHours : 0,
  };
  const bal = TOB.computeBalances({ ...person, id: personId }, policy, requests, opts);
  if (bal.status !== 'ok') return; // no plan = no balance (TASK-9838)
  if (bal.needsClockHours && typeof clockHours !== 'number' && tenant && personId) {
    // Per-hours-worked accrual needs this person's own completed hours this year.
    const yearStart = new Date(bal.year, 0, 1);
    getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'), where('personId', '==', personId), where('status', '==', 'completed')))
      .then(snap => {
        let hrs = 0;
        snap.docs.forEach(d => { const c = d.data(); const t = c.clockIn?.toDate?.() || new Date(c.clockIn); if (t >= yearStart) hrs += (c.totalHours || c.hours || 0); });
        _renderDriverTimeOffBalance(person, policy, approvedSnap, hrs);
      })
      .catch(e => console.warn('[Dispatch] Time off clock hours read failed:', e?.message));
    return;
  }
  const rows = bal.rows.filter(r => r.earned > 0 || (r.used > 0 && r.id !== 'unpaid'));
  if (!rows.length) return;
  const fmt = (n) => Math.round(n * 100) / 100;
  let html = rows.map(r => {
    const pct = r.earned > 0 ? (r.remaining / r.earned) * 100 : 0;
    const color = r.remaining < 0 ? '#dc2626' : pct > 50 ? '#16a34a' : pct > 20 ? '#d97706' : '#dc2626';
    return `<div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;">
      <span>${escapeHtml(r.label)}</span>
      <span style="font-weight:600;color:${color};">${fmt(r.remaining)} hrs <span style="font-weight:400;color:var(--text-secondary);">/ ${fmt(r.earned)} hrs</span></span>
    </div>`;
  }).join('');
  const next = bal.grants?.upcoming?.[0];
  if (next) {
    const d = new Date(next.on + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    html += `<div style="font-size:0.8rem;color:var(--text-secondary);padding-top:4px;">Next: +${next.days} day${next.days === 1 ? '' : 's'} ${escapeHtml(next.categoryLabel)} on ${d}</div>`;
  }
  content.innerHTML = html;
  container.style.display = '';
}
// <<< TASK-9839 driver time-off balance (shared calculator) — END

// TASK-320 Slice 1: "Your Requests" list (last ~5, most recent first, all statuses).
// Driver sees the lifecycle of their own asks. No edit/cancel buttons in v1 — read-only.
function _renderDriverTimeOffRequests(snap) {
  const container = document.getElementById('driver-timeoff-requests');
  const content = document.getElementById('driver-timeoff-requests-content');
  if (!container || !content) return;

  // TASK-1121 — split client-side: ALL pending first (a driver must always see
  // every unresolved ask), then the last 10 settled (approved/denied/cancelled)
  // so a denial is visible, not silence. Both groups stay createdAt-desc.
  const _all = snap?.docs || [];
  const _pending = _all.filter(d => ((d.data()?.status || 'pending').toLowerCase() === 'pending'));
  const _settled = _all.filter(d => ((d.data()?.status || 'pending').toLowerCase() !== 'pending')).slice(0, 10);
  const docs = [..._pending, ..._settled];
  // TASK-4720 follow-up — requests / withdraws made with no signal live in the
  // phone's outbox (public/src/timeoff-outbox.js) until they reach the server.
  const _ob = _driverOutboxRowsHtml();
  if (!docs.length) {
    content.innerHTML = _ob.html + (_ob.count ? '' : `<div style="color:var(--text-secondary);font-style:italic;">No requests yet — submit your first below.</div>`);
    container.style.display = '';
    return;
  }

  const typeLabels = {
    vacation: 'Vacation',
    sick: 'Sick Leave',
    personal: 'Personal Day',
    unpaid: 'Unpaid',
    bereavement: 'Bereavement',
    other: 'Other'
  };

  const fmtShort = (d) => {
    if (!d || isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // TASK-598 — stash the raw docs so editDriverTimeOff(id) can prefill the modal
  // without re-querying Firestore (and without HTML-encoding dates/reason into
  // onclick attrs). Reset each render; keyed by doc id.
  DispatchState._driverTimeOffRequests = {};
  Object.assign(DispatchState._driverTimeOffRequests, _ob.byQid);

  const rows = docs.map(d => {
    const r = d.data() || {};
    DispatchState._driverTimeOffRequests[d.id] = r;
    const _qw = _ob.withdraws[d.id] || null; // a withdraw of THIS request waiting on the phone
    const start = r.startDate?.toDate?.() || (r.startDate ? new Date(r.startDate) : null);
    const end = r.endDate?.toDate?.() || (r.endDate ? new Date(r.endDate) : null);

    let dateLabel = '';
    if (start && end) {
      const sameDay = fmtShort(start) === fmtShort(end);
      dateLabel = sameDay ? fmtShort(start) : `${fmtShort(start)} – ${fmtShort(end)}`;
    } else if (start) {
      dateLabel = fmtShort(start);
    } else {
      dateLabel = '—';
    }

    const typeLabel = typeLabels[r.type] || (r.type ? r.type.charAt(0).toUpperCase() + r.type.slice(1) : 'Other');
    const hours = Number(r.hours) || 0;
    const statusRaw = (r.status || 'pending').toLowerCase();
    const statusKey = ['pending', 'approved', 'denied', 'cancelled'].includes(statusRaw) ? statusRaw : 'pending';
    const statusLabel = statusKey.toUpperCase();

    // TASK-598 — Edit + Cancel only while still pending. Settled rows
    // (approved/denied/cancelled) stay read-only. 44px tap targets for mobile.
    const actions = _qw
      ? `<div style="margin-top:8px;">
          ${_qw.state === 'refused' ? `<div style="font-size:0.8rem;color:#dc2626;margin-bottom:6px;">Withdraw not sent: ${escapeHtml(String(_qw.error || ''))}</div>` : ''}
          <button class="btn btn-secondary btn-sm" style="min-height:44px;width:100%;" onclick="window.DispatchModule.discardDriverTimeOffDraft('${escJsAttr(_qw.qid)}')">
            <span class="material-icons" style="font-size:18px;">undo</span> Keep this request (cancel the withdraw)
          </button>
        </div>`
      : statusKey === 'pending'
      ? `<div class="driver-timeoff-request-actions" style="display:flex;gap:8px;margin-top:8px;">
          <button class="btn btn-secondary btn-sm" style="min-height:44px;flex:1;" onclick="window.DispatchModule.editDriverTimeOff('${escJsAttr(d.id)}')">
            <span class="material-icons" style="font-size:18px;">edit</span> Edit
          </button>
          <button class="btn btn-secondary btn-sm" style="min-height:44px;flex:1;" onclick="window.DispatchModule.cancelDriverTimeOff('${escJsAttr(d.id)}')">
            <span class="material-icons" style="font-size:18px;">close</span> Withdraw
          </button>
        </div>`
      : '';

    // TASK-1136 — a denied row shows the admin's deny reason (reviewNote,
    // written by adminManageTimeOff) so a denial is an explanation, not
    // silence. Free text → escaped. Legacy denied rows without one unchanged.
    const denyReason = (statusKey === 'denied' && r.reviewNote)
      ? `<div class="driver-timeoff-request-reason" style="margin-top:6px;font-size:0.8rem;color:#dc2626;">Reason: ${escapeHtml(String(r.reviewNote))}</div>`
      : '';

    return `<div class="driver-timeoff-request-row">
      <div style="flex:1;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <div>
            <div class="driver-timeoff-request-dates">${escapeHtml(dateLabel)}</div>
            <div class="driver-timeoff-request-meta">${escapeHtml(typeLabel)} · ${hours} hr${hours === 1 ? '' : 's'}</div>
          </div>
          ${_qw ? _driverOutboxPill(_qw.state === 'refused' ? 'WITHDRAW NOT SENT' : 'WITHDRAW WAITING TO SEND', _qw.state) : `<span class="driver-timeoff-status-pill ${statusKey}">${statusLabel}</span>`}
        </div>
        ${denyReason}
        ${actions}
      </div>
    </div>`;
  }).join('');

  content.innerHTML = _ob.html + rows;
  container.style.display = '';
}

// ---------------------------------------------------------------------------
// TASK-4720 follow-up — OFFLINE OUTBOX (dispatcher 2026-10-22: no signal is
// normal in the oilfield). A request or withdraw made offline is kept on the
// phone (window.TimeOffOutbox, public/src/timeoff-outbox.js), shown here as
// "waiting to send", and sent when the connection returns. A refusal is shown
// and the draft is kept to fix or discard. Nothing leaves the outbox silently.
// ---------------------------------------------------------------------------
function _driverTimeOffScope() {
  const OB = window.TimeOffOutbox;
  if (!OB) return null;
  return OB.scopeOf(window.AppState?.tenant,
    DispatchState._personalActionPersonId || window.AppState?.driverPersonId || window.AppState?.memberAccess?.personId || null);
}

function _driverOutboxPill(label, state) {
  const c = state === 'refused' ? 'color:#dc2626;background:#fef2f2;' : 'color:#1d4ed8;background:#eff6ff;';
  return `<span class="driver-timeoff-status-pill" style="${c}">${escapeHtml(label)}</span>`;
}

function _driverOutboxRowsHtml() {
  const out = { html: '', count: 0, byQid: {}, withdraws: {} };
  const OB = window.TimeOffOutbox; const scope = _driverTimeOffScope();
  if (!OB || !scope) return out;
  const items = OB.list(scope);
  const fmt = (iso) => { const d = iso ? new Date(String(iso).slice(0, 10) + 'T12:00:00') : null; return d && !isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''; };
  const rows = [];
  for (const it of items) {
    const p = it.payload || {};
    if (it.kind === 'withdraw') { if (p.requestId) out.withdraws[p.requestId] = it; continue; }
    // Drafts are editable like a pending request (editDriverTimeOff reads this map).
    out.byQid[it.qid] = { ...p, status: 'pending', startDate: p.startDate ? String(p.startDate).slice(0, 10) + 'T12:00:00' : null, endDate: p.endDate ? String(p.endDate).slice(0, 10) + 'T12:00:00' : null, _draft: it };
    const a = fmt(p.startDate); const b = fmt(p.endDate);
    const label = a && b && a !== b ? `${a} – ${b}` : (a || '—');
    const hrs = Number(p.hours) || 0;
    const refused = it.state === 'refused';
    rows.push(`<div class="driver-timeoff-request-row" data-outbox="${escapeHtml(it.qid)}">
      <div style="flex:1;">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
          <div>
            <div class="driver-timeoff-request-dates">${escapeHtml(label)}</div>
            <div class="driver-timeoff-request-meta">${escapeHtml(String(p.type || 'unpaid'))} · ${hrs} hr${hrs === 1 ? '' : 's'}${it.kind === 'update' ? ' · change' : ''}</div>
          </div>
          ${_driverOutboxPill(refused ? 'NOT SENT' : 'WAITING TO SEND', it.state)}
        </div>
        <div style="margin-top:6px;font-size:0.8rem;color:${refused ? '#dc2626' : 'var(--text-secondary)'};">${refused ? escapeHtml(String(it.error || '')) : 'Saved on this phone. It sends by itself when you have signal.'}</div>
        <div class="driver-timeoff-request-actions" style="display:flex;gap:8px;margin-top:8px;">
          <button class="btn btn-secondary btn-sm" style="min-height:44px;flex:1;" onclick="window.DispatchModule.editDriverTimeOff('${escJsAttr(it.qid)}')">
            <span class="material-icons" style="font-size:18px;">edit</span> Edit
          </button>
          <button class="btn btn-secondary btn-sm" style="min-height:44px;flex:1;" onclick="window.DispatchModule.discardDriverTimeOffDraft('${escJsAttr(it.qid)}')">
            <span class="material-icons" style="font-size:18px;">delete</span> Discard
          </button>
        </div>
      </div>
    </div>`);
  }
  out.count = rows.length;
  out.html = rows.join('');
  return out;
}

// The one sender. A queued withdraw of a request that is already gone has done
// its job (the server says not-found), so it counts as sent.
async function _driverTimeOffSend(payload, item) {
  try {
    return await httpsCallable(functions, 'saveTimeOffRequest')(payload);
  } catch (e) {
    if (item && item.kind === 'withdraw' && String(e?.code || '').replace(/^functions\//, '') === 'not-found') return { data: { status: 'withdrawn' } };
    throw e;
  }
}

async function flushDriverTimeOffOutbox() {
  const OB = window.TimeOffOutbox; const scope = _driverTimeOffScope();
  if (!OB || !scope) return null;
  if (!OB.list(scope).some(x => x.state === 'waiting')) return null;
  let r;
  try { r = await OB.flush(scope, _driverTimeOffSend); } catch (e) { console.warn('[Dispatch] time-off outbox flush failed:', e?.message); return null; }
  for (const x of r.sent) {
    window.showToast?.(x.kind === 'withdraw' ? 'Your withdraw went through.' : 'Your saved time-off request was sent.', 'success');
  }
  for (const x of r.refused) {
    window.showToast?.(`${x.kind === 'withdraw' ? 'Withdraw' : 'Time-off request'} not sent: ${x.error}`, 'error', null, 12000);
  }
  if ((r.sent.length || r.refused.length) && document.getElementById('driver-timeoff-modal')) _loadDriverTimeOffPanels();
  return r;
}

function discardDriverTimeOffDraft(qid) {
  const OB = window.TimeOffOutbox; const scope = _driverTimeOffScope();
  if (!OB || !scope) return;
  const it = OB.list(scope).find(x => x.qid === qid);
  if (!it) { _loadDriverTimeOffPanels(); return; }
  const msg = it.kind === 'withdraw' ? 'Keep this request and cancel the withdraw that has not been sent?' : 'Discard this request? It has not been sent to the office.';
  if (!window.confirm(msg)) return;
  OB.remove(scope, qid);
  if (DispatchState._editTimeOffRequestId === qid) DispatchState._editTimeOffRequestId = null;
  _loadDriverTimeOffPanels();
}

// Send whatever is waiting when the connection comes back, and keep trying
// once a minute while anything waits (navigator.onLine can say "online" on a
// dead tower).
if (typeof window !== 'undefined' && !window.__ddTimeOffOutboxWired) {
  window.__ddTimeOffOutboxWired = true;
  window.addEventListener('online', () => { flushDriverTimeOffOutbox(); });
  setInterval(() => { flushDriverTimeOffOutbox(); }, 60000);
}

function closeDriverTimeOff() {
  const modal = document.getElementById('driver-timeoff-modal');
  if (modal) modal.remove();
  // TASK-348 — clear personal-action identity stash on cancel/close.
  DispatchState._personalActionPersonId = null;
  DispatchState._personalActionPersonName = null;
  // TASK-598 — clear edit-mode flag so the next open is a fresh create.
  DispatchState._editTimeOffRequestId = null;
  DispatchState._driverTimeOffHasPlan = false; // TASK-9838
  DispatchState._driverTimeOffPlanUnknown = false;
  DispatchState._driverTimeOffLoadGen++; // drop any in-flight load for this modal
}

async function submitDriverTimeOff() {
  // TASK-348 — prefer SPA personal-action personId, fall back to PIN identity.
  const personId = DispatchState._personalActionPersonId
    || window.AppState?.driverPersonId;
  const personName = DispatchState._personalActionPersonName
    || window.AppState?.driverPersonName
    || 'Unknown';
  const tenantId = window.AppState?.tenant;
  const source = DispatchState._personalActionPersonId ? 'spa_personal_action' : 'driver_pin';

  if (!personId || !tenantId) {
    window.showToast?.('Session error — please re-enter your code', 'error');
    return;
  }

  const type = document.getElementById('driver-timeoff-type')?.value || 'unpaid';
  const startDate = document.getElementById('driver-timeoff-start')?.value;
  const endDate = document.getElementById('driver-timeoff-end')?.value;
  const hours = parseInt(document.getElementById('driver-timeoff-hours')?.value) || 8;
  const reason = document.getElementById('driver-timeoff-reason')?.value?.trim() || '';

  if (!startDate || !endDate) { window.showToast?.('Please select start and end dates', 'error'); return; }
  if (new Date(endDate) < new Date(startDate)) { window.showToast?.('End date cannot be before start date', 'error'); return; }

  // TASK-598 — edit mode: if _editTimeOffRequestId is set, we UPDATE the existing
  // pending request instead of creating a new one. The update keeps status/personId/
  // createdAt intact and stamps editedAt — matching the firestore.rules self-edit
  // field fence (allowed keys: type/startDate/endDate/hours/reason/status/editedAt).
  const editId = DispatchState._editTimeOffRequestId;

  const btn = document.getElementById('btn-submit-driver-timeoff');
  const restoreLabel = editId
    ? '<span class="material-icons">save</span> Save Changes'
    : '<span class="material-icons">send</span> Submit Request';
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="btn-spinner"></span> ${editId ? 'Saving...' : 'Submitting...'}`; }

  // TASK-4720 — saveTimeOffRequest is the only write path (driver PIN session
  // or the SPA member's own person, resolved server-side). It refuses a
  // request that overlaps one of theirs that is pending or approved:
  // "You already have time off requested for <dates>. Withdraw that request first."
  // With no signal the request goes to the phone's outbox instead (see
  // flushDriverTimeOffOutbox); editing an unsent draft (q_ id) re-sends it.
  const OB = window.TimeOffOutbox;
  const scope = _driverTimeOffScope();
  const payload = { tenantId, startDate, endDate, type, hours, reason };
  const draft = (editId && String(editId).startsWith('q_') && OB && scope) ? (OB.list(scope).find(x => x.qid === editId) || null) : null;
  let kind; let data;
  if (draft) {
    kind = draft.kind === 'update' ? 'update' : 'create';
    data = kind === 'update'
      ? { ...payload, action: 'update', requestId: draft.payload.requestId }
      : { ...payload, action: 'create', personId, source, clientRequestId: draft.payload.clientRequestId };
  } else if (editId) {
    kind = 'update';
    data = { ...payload, action: 'update', requestId: editId };
  } else {
    kind = 'create';
    data = { ...payload, action: 'create', personId, source, clientRequestId: 'c_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10) };
  }
  try {
    await _driverTimeOffSend(data, { kind });
    if (draft) OB.remove(scope, draft.qid);
    DispatchState._editTimeOffRequestId = null;
    window.showToast?.(kind === 'update' ? 'Time off request updated!' : 'Time off request submitted!', 'success');
    closeDriverTimeOff();
  } catch (err) {
    if (OB && OB.isOfflineError(err)) {
      const saved = scope ? OB.enqueue(scope, kind, data, draft ? { qid: draft.qid } : undefined) : null;
      if (saved) {
        DispatchState._editTimeOffRequestId = null;
        window.showToast?.("No signal. Your request is saved on this phone and sends by itself when you're back in service.", 'info', null, 10000);
        closeDriverTimeOff();
        return;
      }
      window.showToast?.('No signal, and this phone could not save the request. Try again when you have signal.', 'error', null, 12000);
      if (btn) { btn.disabled = false; btn.innerHTML = restoreLabel; }
      return;
    }
    console.error('[Dispatch] Time off submission error:', err);
    window.showToast?.(err?.message || 'Failed to save request', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = restoreLabel; }
  }
}

// TASK-598 — Prefill the SAME modal to edit a still-pending request. Retitles the
// header → "Edit Request" + the submit button → "Save Changes", and sets the
// edit-mode flag so submitDriverTimeOff() routes to updateDoc. Only the editable
// fields (type/start/end/hours/reason) are prefilled; status/personId/createdAt are
// left untouched server-side.
function editDriverTimeOff(id) {
  const r = DispatchState._driverTimeOffRequests?.[id];
  if (!r) { window.showToast?.('Could not load that request — refresh and try again.', 'error'); return; }
  if ((r.status || 'pending').toLowerCase() !== 'pending') {
    window.showToast?.('Only pending requests can be edited.', 'error');
    return;
  }

  DispatchState._editTimeOffRequestId = id;

  const toInput = (v) => {
    const d = v?.toDate?.() || (v ? new Date(v) : null);
    if (!d || isNaN(d.getTime())) return '';
    // Local YYYY-MM-DD (the doc stores noon-local, so date-part is stable).
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dy = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${dy}`;
  };

  const typeEl = document.getElementById('driver-timeoff-type');
  const startEl = document.getElementById('driver-timeoff-start');
  const endEl = document.getElementById('driver-timeoff-end');
  const hoursEl = document.getElementById('driver-timeoff-hours');
  const reasonEl = document.getElementById('driver-timeoff-reason');
  _applyDriverTimeOffTypeOptions(r.type || 'unpaid'); // TASK-9838 — keep the request's own type
  if (typeEl) typeEl.value = r.type || 'unpaid';
  if (startEl) startEl.value = toInput(r.startDate);
  if (endEl) endEl.value = toInput(r.endDate);
  if (hoursEl) hoursEl.value = Number(r.hours) || 8;
  if (reasonEl) reasonEl.value = r.reason || '';

  // Retitle header + submit button.
  const header = document.querySelector('#driver-timeoff-modal .eticket-form-header-title');
  if (header) header.innerHTML = '<span class="material-icons">edit</span> Edit Request';
  const btn = document.getElementById('btn-submit-driver-timeoff');
  if (btn) btn.innerHTML = '<span class="material-icons">save</span> Save Changes';

  // Scroll the form into view (the request list sits above the form fields).
  startEl?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
}

// TASK-598 / TASK-4720 — WITHDRAW a still-pending request. Chris 2026-10-22:
// the requester can withdraw (delete) and resubmit. It was a status update to
// 'cancelled'; it is now a delete through saveTimeOffRequest (requester only,
// pending only), with a PTO_WITHDRAW audit row server-side.
async function cancelDriverTimeOff(id) {
  if (String(id || '').startsWith('q_')) { discardDriverTimeOffDraft(id); return; } // an unsent draft
  const tenantId = window.AppState?.tenant;
  const r = DispatchState._driverTimeOffRequests?.[id];
  if (!tenantId || !r) { window.showToast?.('Could not load that request — refresh and try again.', 'error'); return; }
  if ((r.status || 'pending').toLowerCase() !== 'pending') {
    window.showToast?.('Only pending requests can be cancelled.', 'error');
    return;
  }

  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm(
        'Withdraw Request?',
        'This deletes your pending time-off request. You can submit a new one anytime.',
        { confirmText: 'Withdraw', cancelText: 'Keep It', danger: true }
      )
    : window.confirm('Withdraw this pending time-off request?');
  if (!ok) return;

  const _wd = { tenantId, action: 'withdraw', requestId: id };
  try {
    await _driverTimeOffSend(_wd, { kind: 'withdraw' });
    window.showToast?.('Request withdrawn.', 'success');
    // If we were mid-edit on this same doc, clear edit mode.
    if (DispatchState._editTimeOffRequestId === id) DispatchState._editTimeOffRequestId = null;
    _loadDriverTimeOffPanels();
  } catch (err) {
    const OB = window.TimeOffOutbox; const scope = _driverTimeOffScope();
    if (OB && OB.isOfflineError(err)) {
      const prior = scope ? OB.withdrawFor(scope, id) : null;
      if (scope && OB.enqueue(scope, 'withdraw', _wd, prior ? { qid: prior.qid } : undefined)) {
        window.showToast?.("No signal. The withdraw is saved on this phone and sends by itself when you're back in service.", 'info', null, 10000);
        if (DispatchState._editTimeOffRequestId === id) DispatchState._editTimeOffRequestId = null;
        _loadDriverTimeOffPanels();
        return;
      }
      window.showToast?.('No signal, and this phone could not save the withdraw. Try again when you have signal.', 'error', null, 12000);
      return;
    }
    console.error('[Dispatch] Time off withdraw error:', err);
    window.showToast?.(err?.message || 'Failed to withdraw', 'error');
  }
}

// ============================================
// EXPOSE TO WINDOW
// ============================================
// ============================================
// DRIVER CLOCK BUTTON STATE UPDATE
// ============================================
// ============================================
// DRIVER TIMESHEET (reuses pubLoadTimesheet from app.js)
// ============================================
let _timesheetExpanded = false;

function initDriverTimesheet() {
  const section = document.getElementById('driver-timesheet-section');
  if (!section || !window.AppState?.driverModePin || !window.driverHasClockAccess?.()) return;

  // Set up shared timesheet context so pubLoadTimesheet + pubReEditTicket can work
  // (TASK-363 retired the standalone clock-in page; this shim is now the only
  // creator of the context, used by the driver-PIN dispatch timesheet view.)
  if (!window._timesheetCtx) {
    window._timesheetCtx = {
      tenantId: DispatchState.tenantId,
      people: DispatchState.people || [],
      customers: DispatchState.customers || [],
      jobs: DispatchState.jobs || [],
      trucks: DispatchState.trucks || [],
      trailers: DispatchState.trailers || [],
      assets: DispatchState.assets || [],
      activeEntries: (DispatchState._clockEntries || []).filter(e => e.status === 'active'),
      todaysJobDays: DispatchState.jobDays || [],
      clockOutEntry: null,
      clockOutEquipment: [],
      clockOutDowntime: [],
      clockOutStandby: [],
      clockOutPotholes: [],
      clockOutPhotos: []
    };
  }

  // Don't show section here — it gets shown when Timesheet tab is clicked
  // Just load the data so it's ready

  // Auto-load timesheet data
  const personId = window.AppState?.driverPersonId;
  if (personId && typeof window.pubLoadTimesheet === 'function') {
    window.pubLoadTimesheet(personId);
    // Show summary and days immediately
    const summary = document.getElementById('pub-ts-summary');
    const days = document.getElementById('pub-ts-days');
    const chev = document.getElementById('ts-chevron');
    if (summary) summary.style.display = 'block';
    if (days) days.style.display = 'block';
    if (chev) chev.textContent = 'expand_less';
    _timesheetExpanded = true;
  }
}

function toggleTimesheet() {
  const summary = document.getElementById('pub-ts-summary');
  const days = document.getElementById('pub-ts-days');
  const chev = document.getElementById('ts-chevron');
  if (!summary || !days) return;

  _timesheetExpanded = !_timesheetExpanded;

  if (_timesheetExpanded) {
    summary.style.display = 'block';
    days.style.display = 'block';
    if (chev) chev.textContent = 'expand_less';
    // Load timesheet data
    const personId = window.AppState?.driverPersonId;
    if (personId && typeof window.pubLoadTimesheet === 'function') {
      // Ensure shared timesheet context exists (TASK-363 — see initDriverTimesheet)
      if (!window._timesheetCtx) {
        window._timesheetCtx = {
          tenantId: DispatchState.tenantId,
          people: DispatchState.people || [],
          customers: DispatchState.customers || [],
          jobs: DispatchState.jobs || [],
          trucks: DispatchState.trucks || [],
          trailers: DispatchState.trailers || [],
          assets: DispatchState.assets || [],
          activeEntries: (DispatchState._clockEntries || []).filter(e => e.status === 'active'),
          todaysJobDays: DispatchState.jobDays || [],
          clockOutEntry: null,
          clockOutEquipment: [],
          clockOutDowntime: [],
          clockOutStandby: [],
          clockOutPotholes: [],
          clockOutPhotos: []
        };
      }
      window.pubLoadTimesheet(personId);
    }
  } else {
    summary.style.display = 'none';
    days.style.display = 'none';
    if (chev) chev.textContent = 'expand_more';
  }
}

function handleDriverClockButton() {
  const driverId = window.AppState?.driverPersonId;
  const myEntry = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
  console.log('[Dispatch] Clock button clicked:', { hasClockedIn: !!myEntry, entryId: myEntry?.id });
  if (myEntry) {
    openDriverClockOut();
  } else {
    openDriverClockIn();
  }
}

// TASK-9553 — the clock mode of one person: their own override, else the
// company's. Without app.js's resolver it is the company setting, exactly as before.
function _dspClockMode(personId) {
  const cm = window.ClockMode;
  if (cm && typeof cm.effective === 'function') return cm.effective(personId || null);
  return window.AppState?.settings?.clockOutMode || 'simple';
}

function updateDriverClockButton() {
  // Snapshot-update path. Must match renderDriverBoard's three-mode logic
  // exactly (v1.15.359) — Codex flagged that this fn was stomping renders.
  const clockBtn = document.getElementById('btn-driver-clockin-dispatch');
  if (!clockBtn || !window.AppState?.driverModePin || !window.driverHasClockAccess?.()) return;

  const driverId = window.AppState?.driverPersonId;
  const myEntry = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
  const label = document.getElementById('btn-driver-clockin-dispatch-label');
  const icon = clockBtn.querySelector('.material-icons');
  const mode = _dspClockMode(driverId); // TASK-9553 per-person clock mode
  const isTicket = mode === 'ticket' || mode === 'ticketonly';
  const isTicketOnly = mode === 'ticketonly';

  clockBtn.style.display = '';
  if (myEntry) {
    clockBtn.style.background = isTicket ? 'linear-gradient(135deg,#3B82F6,#06B6D4)' : '#DC2626';
    if (label) label.textContent = isTicket ? 'Build Ticket' : 'Clock Out';
    if (icon) icon.textContent = isTicket ? 'receipt_long' : 'logout';
  } else {
    clockBtn.style.background = isTicketOnly
      ? 'linear-gradient(135deg,#3B82F6,#06B6D4)'
      : 'linear-gradient(135deg,#166534,#059669)';
    if (label) label.textContent = isTicketOnly ? 'Build Ticket' : 'Clock In';
    if (icon) icon.textContent = isTicketOnly ? 'receipt_long' : 'login';
  }
}

// ============================================
// TASK-347 — Driver bottom-nav state helpers (Slices 1 + 2 + 3)
// ============================================

// Slice 2 — sync the Clock tab in the new bottom-nav with the same
// three-mode logic as updateDriverClockButton. Called from the same
// post-render code paths so both the legacy .driver-action-bar button AND
// the new tab stay in lockstep with _clockEntries snapshot updates.
function updateDriverBottomNavClock() {
  const navItem = document.getElementById('driver-btmnav-clock');
  if (!navItem || !window.AppState?.driverModePin) return;
  // Always visible (so drivers without active clock can still walk-up
  // start a shift), but only meaningful when the carrier has clock access.
  if (!window.driverHasClockAccess?.()) {
    navItem.style.display = 'none';
    return;
  }
  navItem.style.display = '';

  const driverId = window.AppState?.driverPersonId;
  const myEntry = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
  const labelEl = document.getElementById('driver-btmnav-clock-label');
  const iconEl = document.getElementById('driver-btmnav-clock-icon');
  const mode = _dspClockMode(driverId); // TASK-9553 per-person clock mode
  const isTicket = mode === 'ticket' || mode === 'ticketonly';
  const isTicketOnly = mode === 'ticketonly';

  if (myEntry) {
    if (labelEl) labelEl.textContent = isTicket ? 'Build Ticket' : 'Clock Out';
    if (iconEl) iconEl.textContent = isTicket ? 'receipt_long' : 'logout';
    navItem.classList.add('active');
  } else {
    if (labelEl) labelEl.textContent = isTicketOnly ? 'Build Ticket' : 'Clock In';
    if (iconEl) iconEl.textContent = isTicketOnly ? 'receipt_long' : 'login';
    navItem.classList.remove('active');
  }
}

// Slice 3 — open the Actions sheet. Tiles are built fresh each open so
// addon flags loaded after the initial render reflect correctly.
function openDriverActionsSheet() {
  const sheet = document.getElementById('driver-actions-sheet');
  if (!sheet) return;
  const tilesEl = document.getElementById('driver-actions-tiles');
  if (tilesEl) tilesEl.innerHTML = _renderDriverActionTiles();

  sheet.classList.remove('hidden');
  // TASK-997 — lock the background scroll container behind the sheet. In driver
  // mode the scroller is #main-content (see `body.driver-mode-pin #main-content`
  // in styles.css), NOT body/#app — so we add `body.driver-actions-open`, which
  // mirrors the existing `body.modal-open #main-content { overflow:hidden }` rule.
  // We can't reuse the `modal-open` class directly: the global initModalScrollLock
  // MutationObserver (app.js) only keeps it while a `.modal:not(.hidden)` is open
  // and would immediately strip it off this non-.modal sheet. Capture scrollTop so
  // we can restore it on close (defensive against an iOS position jump).
  const scroller = document.getElementById('main-content');
  sheet._savedScrollTop = scroller ? scroller.scrollTop : 0;
  document.body.classList.add('driver-actions-open');
  document.documentElement.classList.add('driver-actions-open');
  // ESC to close
  if (!sheet._escHandler) {
    sheet._escHandler = (e) => {
      if (e.key === 'Escape') closeDriverActionsSheet();
    };
    document.addEventListener('keydown', sheet._escHandler);
  }
}

function closeDriverActionsSheet() {
  const sheet = document.getElementById('driver-actions-sheet');
  if (!sheet) return;
  sheet.classList.add('hidden');
  // TASK-997 — release the background scroll lock and restore scroll position.
  document.body.classList.remove('driver-actions-open');
  document.documentElement.classList.remove('driver-actions-open');
  const scroller = document.getElementById('main-content');
  if (scroller && typeof sheet._savedScrollTop === 'number') {
    scroller.scrollTop = sheet._savedScrollTop;
  }
  if (sheet._escHandler) {
    document.removeEventListener('keydown', sheet._escHandler);
    sheet._escHandler = null;
  }
}

// Build the dynamic tile list. Conditional on addon + supervisor gates.
// Single quotes ONLY inside any HTML comment in this template literal.
function _renderDriverActionTiles() {
  const tiles = [];
  const settings = window.AppState?.settings || {};

  // Time Off — exact same gate as the legacy #btn-driver-timeoff in
  // renderDriverBoard (settings.driverTimeOff.enabled + VIP + driverAddons.hr).
  // Code Reviewer COD catch — mismatch with legacy gate would have hidden
  // Time Off on TITS/VIP carriers that don't carry the hr addon.
  // TASK-865 — TITS hardcode generalized to settings.driverTimeOff.enabled.
  // TASK-1194 — gate extracted to _driverHasTimeOffTile() so the Help-sheet
  // FAQ can share the EXACT same predicate (never describe a hidden tile).
  const hasTimeOff = _driverHasTimeOffTile();
  if (hasTimeOff) {
    tiles.push({
      id: 'tile-timeoff',
      icon: 'event_busy',
      label: 'Time Off',
      onclick: 'window.DispatchModule.closeDriverActionsSheet();window.DispatchModule.openDriverTimeOff();'
    });
  }

  // Vehicles — driver-PIN drivers always have access; mirror dispatch.js:2970 gate.
  tiles.push({
    id: 'tile-vehicles',
    icon: 'local_shipping',
    label: 'Vehicles',
    onclick: 'window.DispatchModule.closeDriverActionsSheet();window.DispatchModule.openDriverVehicleList();'
  });

  // TASK-587 — Maintenance entry for PIN drivers carrying the per-driver
  // allowMaintenance grant (router allow-list app.js:6802). Previously only
  // reachable via the generic #mobile-nav's Maintenance tab, which the
  // persistent driver chrome now hides on every PIN view — without this tile
  // those drivers would lose the module entirely.
  // TASK-774 Gap B — also show for grant-less drivers who have >= 1 maintenance
  // task assigned to them (driverCanSeeMaintenance = grant OR assigned), so an
  // assigned driver can reach their scoped to-do list without the full grant.
  if (window.driverCanSeeMaintenance ? window.driverCanSeeMaintenance() : window.AppState?.driverAllowMaintenance) {
    tiles.push({
      id: 'tile-maintenance',
      icon: 'build',
      label: 'Maintenance',
      onclick: "window.DispatchModule.closeDriverActionsSheet();window.navigateToView&&window.navigateToView('maintenance');"
    });
  }

  // Team — driver-facing contact directory (TASK-459). Tenant opt-in toggle,
  // default OFF; only people with showOnDriverCard===true appear (per-person
  // opt-in, default OFF). Chris 2026-06-04 ask from driver "Steve".
  if (settings.driverContactDirectory?.enabled === true) {
    tiles.push({
      id: 'tile-team',
      icon: 'groups',
      label: 'Team',
      // Directory render logic lives in roster.js (its domain) — don't bloat
      // dispatch.js further (COD Architect, TASK-459).
      onclick: 'window.DispatchModule.closeDriverActionsSheet();window.RosterModule.openDriverContactDirectory();'
    });
  }

  // Doc Page — driver-facing company documents (TASK-483). Tenant opt-in toggle
  // (settings.driverDocs.enabled, default OFF; flipped from HR → Company Documents
  // → "Driver View"). Shows ONLY publiclyShared documents, in-app (no public link).
  // Panel render logic lives in hr.js (its domain) — don't bloat dispatch.js.
  if (settings.driverDocs?.enabled === true) {
    tiles.push({
      id: 'tile-docs',
      icon: 'library_books',
      label: 'Doc Page',
      onclick: 'window.DispatchModule.closeDriverActionsSheet();window.HRModule.openDriverDocs();'
    });
  }

  // My Documents (TASK-1017) — driver self-service view of THEIR OWN credential
  // docs (training certs, CDL, med card) per 49 CFR 391.51. DISTINCT from the
  // company-wide "Doc Page" tile above (openDriverDocs / publiclyShared docs) —
  // this one is self-scoped: the getDriverOwnDocBytes callable binds personId
  // from the driver session server-side and streams bytes (no stored URLs).
  // Always available to a PIN driver (their own DQ file is theirs by right);
  // render logic lives in hr.js (its domain) — do not bloat dispatch.js.
  tiles.push({
    id: 'tile-mydocs',
    icon: 'folder_shared',
    label: 'My Documents',
    onclick: 'window.DispatchModule.closeDriverActionsSheet();window.HRModule.openDriverMyDocuments();'
  });

  // Switch Job — only when currently clocked in (mirror dispatch.js:3014-3016 gate).
  if (window.driverHasClockAccess?.()) {
    const driverId = window.AppState?.driverPersonId;
    const myEntry = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
    if (myEntry) {
      tiles.push({
        id: 'tile-switchjob',
        icon: 'swap_horiz',
        label: 'Switch Job',
        onclick: 'window.DispatchModule.closeDriverActionsSheet();window.DispatchModule.openDriverSwitchJob();'
      });
    }
  }

  // Timesheet (TASK-562) — view-only, pay-period-stepped (◀/▶), self-scoped,
  // ~12-months-back "My Timesheet". Reuses the EXISTING clock.js modal
  // (window.ClockModule.showMyTimesheet — self-resolves the viewer's personId
  // internally via _myTimesheetSelfPersonId, never takes an arg). Gated by clock
  // access (same as Switch Job) but shown regardless of currently-clocked-in
  // state so drivers can see their historical timesheets. View-only for P1 — no
  // edit/delete (strictly additive; PIN drivers had zero timesheet access before).
  if (window.driverHasClockAccess?.()) {
    tiles.push({
      id: 'tile-timesheet',
      icon: 'schedule',
      label: 'My Hours',
      onclick: 'window.DispatchModule.closeDriverActionsSheet();window.ClockModule.showMyTimesheet&&window.ClockModule.showMyTimesheet();'
    });
  }

  // Record Expense — same gate as dispatch.js:2981 (driverExpenses.enabled).
  if (settings.driverExpenses?.enabled === true) {
    tiles.push({
      id: 'tile-record-expense',
      icon: 'attach_money',
      label: 'Record Expense',
      onclick: 'window.DispatchModule.closeDriverActionsSheet();window.DispatchModule.openDriverRecordExpense();'
    });
  }

  // Per Chris 2026-05-22 direction: driver-PIN portal stays drivers-only.
  // Supervisors who need employee-notes get a real SPA member login (TASK-319
  // multi-user staff system) + HR Actions "+ Add Employee Note" button.
  // Supervisor-note tile + writer flow removed in TASK-347 follow-up.

  // Help + Sign Out — always present. Restored 2026-05-28 after v1.15.914 swap
  // hid the standard #mobile-more-menu (which carried Help + Sign Out) behind
  // .driver-mode-pin .jobboard-actions{display:none}. Without these tiles a
  // PIN driver has no way to log out.
  // TASK-1194 — the old onclick set window.location.hash='#/help', but PIN
  // driver sessions have no #/help route handler, so the tile silently did
  // NOTHING (dead button). Now opens the self-contained driver Help sheet.
  tiles.push({
    id: 'tile-help',
    icon: 'help',
    label: 'Help',
    onclick: 'window.DispatchModule.closeDriverActionsSheet();window.DispatchModule.openDriverHelpSheet();'
  });
  tiles.push({
    id: 'tile-signout',
    icon: 'logout',
    label: 'Sign Out',
    danger: true,
    onclick: 'window.DispatchModule.closeDriverActionsSheet();window.handleLogout&&window.handleLogout();'
  });

  // TASK-9373 — per-tenant hide + reorder (settings.driverApp, reader in
  // app.js). Gates above still decide what is ELIGIBLE; this can only hide
  // more. NEVER_HIDE (Vehicles, My Documents, Help, Sign Out) survives any
  // config and Sign Out is pinned last by the reader. Default layout returns
  // the array unchanged — byte-identical to the pre-task sheet.
  const tileLayout = (window.driverAppLayout && window.driverAppApplyOrder) ? window.driverAppLayout() : null;
  const shownTiles = tileLayout ? window.driverAppApplyOrder('actions', tiles, tileLayout) : tiles;
  return shownTiles.map(t => {
    const safeOnclick = t.onclick.replace(/"/g, '&quot;');
    const dangerStyle = t.danger ? ' style="color:#DC2626;"' : '';
    const iconStyle = t.danger ? ' style="color:#DC2626;"' : '';
    return '<button type="button" class="driver-actions-tile" id="' + t.id + '" onclick="' + safeOnclick + '"' + dangerStyle + '>'
      + '<span class="material-icons"' + iconStyle + '>' + t.icon + '</span>'
      + '<span>' + t.label + '</span>'
      + '</button>';
  }).join('');
}

// TASK-1194 — single source of truth for the driver Time Off gate. Shared by
// the Actions-sheet tile above AND the Help-sheet FAQ below so the FAQ can
// never describe a tile the driver doesn't actually have (COD Code Reviewer:
// the tile is conditionally gated — the help copy must use the SAME predicate).
function _driverHasTimeOffTile() {
  const addons = window.AppState?.driverAddons || {};
  // TASK-9373 — the tenant's Driver App layout can hide this tile; fold it into
  // the SAME predicate so the Help FAQ never describes a hidden tile.
  if (window.driverAppIsHidden && window.driverAppIsHidden('actions', 'tile-timeoff')) return false;
  return !!(window.AppState?.driverModePin && (
    (window.AppState?.settings?.driverTimeOff?.enabled === true) ||
    (typeof window.isVipAccount === 'function' && window.isVipAccount()) ||
    (addons.hr === true)
  ));
}

// ============================================
// DRIVER HELP SHEET (TASK-1194)
// ============================================
// The Actions-sheet Help tile used to route to '#/help' — a route that does
// not exist in PIN-driver sessions, so the tile was a dead button. This sheet
// replaces it with a self-contained, driver-level help surface rendered
// entirely from data the PIN session already holds in memory (no new Firestore
// query, no callable, no Gemini):
//
//   1. Contact Dispatcher — first ACTIVE person matching /dispatch/i via the
//      standard role pattern (p.crewType || p.role || p.roles?.[0]). People
//      resolve via the _resolveDriverFleet() fallback pattern below because
//      DispatchState.people is EMPTY on the PIN jobboard — jobboard.js
//      publishes full people docs to window.AppState.fleetPeople (TASK-459
//      bridge). Privacy: eligibility matches EXACTLY what the driver Team
//      directory already exposes (tenant settings.driverContactDirectory
//      .enabled AND per-person showOnDriverCard===true AND active/not
//      terminated — roster.js _renderTeamDirectoryList precedent). This
//      surface must NOT widen driver-visible contact exposure: the roster
//      opt-in checkbox explicitly tells admins exposure requires the Team
//      directory toggle, so both gates apply here too. No eligible dispatcher
//      with contact info -> the whole section is hidden (never a dead link).
//   2. Training videos (Academy) — links from window.DriverAcademy (utils/driverAcademy.js).
//   3. Driver FAQ — 6 static items (native <details> accordion, no JS state).
//      Answers reference the REAL PIN-driver flows (bottom-nav Clock tile,
//      Inspections tile, Job Board home). The Clock and Time Off answers are
//      conditional on the same gates that show those surfaces
//      (driverHasClockAccess / _driverHasTimeOffTile) so the FAQ never
//      describes a button the driver lacks. Emergency answer is 911-FIRST,
//      dispatcher second (COD Domain: safety-messaging order is
//      non-negotiable for a fleet product).
//
// XSS (COD Adversary mitigation, BINDING): every interpolated dispatcher
// field (name/phone/email) goes through escapeHtml() in BOTH text nodes and
// href attributes (dispatch.js escapeHtml precedent); tel:/sms: hrefs are
// additionally stripped to [\d+] before escaping. All FAQ copy is hardcoded.
function _resolveDriverHelpPeople() {
  // Mirrors _resolveDriverFleet(): dispatcher/admin sessions have
  // DispatchState populated by subscribeToData; PIN drivers land on /jobboard
  // and never run it, so DispatchState.people stays empty -> fall back to the
  // shared store jobboard publishes (window.AppState.fleetPeople).
  let people = _dispatchDataIsFor(window.AppState?.tenant) ? (DispatchState.people || []) : []; // P0 1019
  if (people.length === 0) {
    people = _fleetBridge('fleetPeople');
  }
  return people;
}

function openDriverHelpSheet() {
  document.getElementById('driver-help-sheet-modal')?.remove();

  // ── Contact Dispatcher (conditional — see privacy gates in header) ──
  const dirEnabled = window.AppState?.settings?.driverContactDirectory?.enabled === true;
  const roleOf = (p) => String(p.crewType || p.role || (p.roles && p.roles[0]) || '');
  const dispatcher = !dirEnabled ? null : (_resolveDriverHelpPeople().find(p =>
    p && p.active !== false && p.terminated !== true && p.showOnDriverCard === true &&
    /dispatch/i.test(roleOf(p)) && (p.phone || p.email)) || null);

  let contactHTML = '';
  if (dispatcher) {
    const name = escapeHtml(dispatcher.name || 'Dispatcher');
    // tel:/sms: hrefs: strip to digits/+ THEN escape (escapeHtml on hrefs is
    // the binding Adversary mitigation; the strip is extra defense).
    const telDigits = String(dispatcher.phone || '').replace(/[^\d+]/g, '');
    const phoneDisplay = escapeHtml(window.formatPhoneDisplay
      ? window.formatPhoneDisplay(dispatcher.phone) : (dispatcher.phone || ''));
    const actionBtn = (href, icon, label) => `
      <a href="${href}" style="flex:1;display:flex;align-items:center;justify-content:center;gap:6px;min-height:44px;padding:10px 8px;border-radius:10px;background:var(--brand-blue,#3B82F6);color:#fff;font-weight:600;font-size:0.85rem;text-decoration:none;">
        <span class="material-icons" style="font-size:18px;">${icon}</span>${label}
      </a>`;
    const actions = [];
    if (telDigits) {
      actions.push(actionBtn('tel:' + escapeHtml(telDigits), 'call', 'Call'));
      actions.push(actionBtn('sms:' + escapeHtml(telDigits), 'sms', 'Text'));
    }
    if (dispatcher.email) {
      actions.push(actionBtn('mailto:' + escapeHtml(dispatcher.email), 'mail', 'Email'));
    }
    contactHTML = `
      <div id="driver-help-contact" style="margin:12px 0 4px;padding:14px;border:1px solid var(--border-color,#e2e8f0);border-radius:12px;background:var(--bg-secondary,#F8FAFC);">
        <div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary,#475569);margin-bottom:6px;">Contact Dispatcher</div>
        <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary,#0F172A);">${name}</div>
        ${phoneDisplay ? `<div style="font-size:0.85rem;color:var(--text-secondary,#475569);margin-top:2px;">${phoneDisplay}</div>` : ''}
        <div style="display:flex;gap:8px;margin-top:10px;">${actions.join('')}</div>
      </div>`;
  }

  // ── Driver FAQ — 6 static items, answers match the real PIN flows ──
  const hasClock = !!(window.driverHasClockAccess && window.driverHasClockAccess());
  const hasTimeOff = _driverHasTimeOffTile();
  const faq = [
    {
      q: 'How do I clock in?',
      a: hasClock
        ? 'Tap the <strong>Clock In</strong> button in the bottom bar. If asked, pick the customer or job you\'re working, then confirm. When your shift is over, the same button becomes <strong>Clock Out</strong>.'
        : 'Your company doesn\'t use the in-app time clock. Follow your company\'s normal timekeeping process, or ask your dispatcher if you\'re not sure.'
    },
    {
      q: 'How do I submit a pre-trip?',
      a: 'Tap <strong>Inspections</strong> in the bottom bar, then <strong>New Inspection</strong>. Pick your vehicle, walk through the checklist, note any defects, and submit.'
    },
    {
      q: 'Where do I see my schedule?',
      a: 'Your <strong>Job Board</strong> home screen lists the jobs assigned to you. Tap any job card to see the customer, crew, trucks, directions, and notes.'
    },
    {
      q: 'How do I request time off?',
      a: hasTimeOff
        ? 'Tap <strong>Actions</strong> in the bottom bar, then <strong>Time Off</strong>. Pick your dates, add a note if you want, and submit — your dispatcher will review the request.'
        : 'Time-off requests are handled by your office. Contact your dispatcher to request time off.'
    },
    {
      q: 'I forgot my PIN or Driver Code. What do I do?',
      a: 'On the sign-in screen tap <strong>Driver Code</strong>, then <strong>Forgot your PIN?</strong> and enter your mobile number — a new setup code can be texted to you. Or ask your office to text you a new setup code.'
    },
    {
      q: 'Who do I call in an emergency?',
      a: '<strong>Call 911 first</strong> for any accident, injury, fire, or immediate danger. Once everyone is safe, call your dispatcher to report what happened.'
    },
  ];
  // ── Training videos (Academy) — Chris 2026-10-26: an office can tell a new
  // hire "watch the Academy videos under Help". The list lives ONCE in
  // utils/driverAcademy.js (window.DriverAcademy — no cross-import, Pitfall #1);
  // hardcoded copy + same-origin static URLs, nothing user-supplied. If the
  // global is somehow missing the section is simply left out.
  const academy = window.DriverAcademy;
  const academyHTML = (academy && typeof academy.linksHtml === 'function') ? `
      <div id="driver-help-academy" style="margin:12px 0 4px;padding:10px 14px;border:1px solid var(--border-color,#e2e8f0);border-radius:12px;background:var(--bg-secondary,#F8FAFC);">
        <div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary,#475569);margin-bottom:2px;">${academy.label || 'Training videos (Academy)'}</div>
        ${academy.linksHtml()}
      </div>` : '';

  const faqHTML = faq.map(item => `
    <details class="driver-help-faq-item" style="border-bottom:1px solid var(--border-color,#e2e8f0);">
      <summary style="display:flex;align-items:center;justify-content:space-between;gap:8px;min-height:48px;padding:12px 4px;cursor:pointer;font-weight:600;font-size:0.92rem;color:var(--text-primary,#0F172A);list-style:none;-webkit-tap-highlight-color:transparent;">
        ${item.q}
        <span class="material-icons" style="font-size:20px;color:var(--text-secondary,#475569);flex-shrink:0;">expand_more</span>
      </summary>
      <div style="padding:0 4px 14px;font-size:0.88rem;line-height:1.5;color:var(--text-secondary,#475569);">${item.a}</div>
    </details>`).join('');

  // Mobile modal pattern (Pitfall #2): dvh with vh fallback; flex column with
  // min-height:0 on the scroll body (flexbox-scroll lesson). Injected modals
  // must use class="modal" (driver-mode lesson) — same pattern as
  // openDriverVehicleList / the Team directory.
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'driver-help-sheet-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;max-height:85vh;max-height:85dvh;display:flex;flex-direction:column;overflow:hidden;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">help</span>Help</h3>
        <button class="btn-icon" aria-label="Close help" onclick="document.getElementById('driver-help-sheet-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:0 16px 16px;">
        ${contactHTML}
        ${academyHTML}
        <div style="padding:14px 4px 4px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary,#475569);">Driver FAQ</div>
        ${faqHTML}
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

// ============================================
// DRIVER CLOCK IN / OUT (dispatch driver board)
// ============================================
function openDriverClockIn() {
  if (!window.AppState?.driverModePin || !window.driverHasClockAccess?.()) return;

  const driverId = window.AppState?.driverPersonId;
  const existing = (DispatchState._clockEntries || []).find(e => e.personId === driverId);
  if (existing) {
    window.showToast?.('Already clocked in', 'error');
    return;
  }

  const settings = window.AppState?.settings || {};
  const _myClockMode = _dspClockMode(driverId); // TASK-9553 per-person clock mode
  const isTicketMode = _myClockMode === 'ticket' || _myClockMode === 'ticketonly';
  const showCustomer = isTicketMode || settings.clockInAskCustomer === true;

  // TASK-1175 — internalTimeOnly (Shop/Office/yard) customers pinned first so
  // yard time stays one tap; TASK-9548 — under a labeled "Internal — not billed"
  // optgroup, then "Customers" A–Z, via the ONE shared builder (window.PickerGroups).
  const customers = (DispatchState.customers || [])
    .filter(c => c.name && !window.PickerGroups.isInactiveCustomer(c)); // TASK-10030 — case-blind ('Inactive' from CRM was let through)

  const customerOptions = window.PickerGroups.customerOptionsHtml(customers, { nameOf: c => c.name || '' });
  const customerHint = window.PickerGroups.customerHintHtml(customers, { nameOf: c => c.name || '', color: '#475569' });

  // Explicit inline styles ensure select/label visibility on all devices (no form-control/form-label CSS exists)
  const labelStyle = 'display:block;font-size:14px;font-weight:600;color:#334155;margin-bottom:6px;';
  const selectStyle = 'display:block;width:100%;padding:12px;font-size:16px;border:1px solid #CBD5E1;border-radius:10px;background:#F8FAFC;color:#0F172A;-webkit-appearance:menulist;appearance:menulist;min-height:48px;';
  const inputStyle = 'display:block;width:100%;padding:12px;font-size:16px;border:1px solid #CBD5E1;border-radius:10px;background:#F8FAFC;color:#0F172A;min-height:48px;';

  const _modeForTitle = _myClockMode;
  const _isTicketOnlyForTitle = _modeForTitle === 'ticketonly';
  const _modalTitle = _isTicketOnlyForTitle ? 'Build Ticket' : 'Clock In';
  const _modalIcon = _isTicketOnlyForTitle ? 'receipt_long' : 'login';
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-clockin-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">${_modalIcon}</span>${_modalTitle}</h3>
        <button class="btn-icon" onclick="document.getElementById('dispatch-clockin-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        ${showCustomer ? `<div style="margin-bottom:14px;">
          <label style="${labelStyle}">Customer ${isTicketMode ? '<span style="color:#ef4444;">*</span>' : '(optional)'}</label>
          <select id="dispatch-clockin-customer" style="${selectStyle}" onchange="window.DispatchModule.onClockInCustomerChange()">
            <option value="">Select customer...</option>
            ${customerOptions}
          </select>
          ${customerHint}
        </div>
        <div style="margin-bottom:14px;">
          <label style="${labelStyle}">Job (optional)</label>
          <select id="dispatch-clockin-job" style="${selectStyle}">
            <option value="">No specific job</option>
          </select>
        </div>` : ''}
        <div style="margin-bottom:18px;">
          <label style="${labelStyle}">Notes (optional)</label>
          <input type="text" id="dispatch-clockin-notes" style="${inputStyle}" placeholder="e.g. shop work, yard cleanup..." />
        </div>
        <button id="dispatch-clockin-submit" class="btn btn-primary" style="width:100%;padding:14px;font-size:1rem;font-weight:600;background:${_isTicketOnlyForTitle ? '#3B82F6' : '#166534'};border:none;border-radius:10px;color:#fff;" onclick="window.DispatchModule.submitDriverClockIn()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">${_modalIcon}</span>${_modalTitle}
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function onClockInCustomerChange() {
  const customerId = document.getElementById('dispatch-clockin-customer')?.value;
  const jobSelect = document.getElementById('dispatch-clockin-job');
  if (!jobSelect) return;

  if (!customerId || customerId === '__shop__') {
    jobSelect.innerHTML = '<option value="">No specific job</option>';
    return;
  }

  // Only show active jobs that are current (endDate >= today or no endDate)
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const driverId = window.AppState?.driverPersonId;
  const customerJobs = (DispatchState.jobs || [])
    .filter(j => {
      if (j.customerId !== customerId || !(j.title || j.name)) return false;
      if (j.status === 'finished') return false;
      const endRaw = j.endDate || j.startDate;
      if (!endRaw) return true; // no date = show it
      const endMs = endRaw?.toDate ? endRaw.toDate().getTime() : new Date(endRaw).getTime();
      if (isNaN(endMs)) return true; // unparseable = show it
      const isAssigned = driverId && (j.assignedDrivers || []).includes(driverId);
      return endMs >= todayMs || isAssigned;
    })
    .sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));

  jobSelect.innerHTML = '<option value="">No specific job</option>' +
    customerJobs.map(j => `<option value="${j.id}">${escapeHtml(j.title || j.name)}</option>`).join('');
}

async function submitDriverClockIn() {
  const tenantId = DispatchState.tenantId;
  const driverId = window.AppState?.driverPersonId;
  const driverName = window.AppState?.driverPersonName || '';
  const customerId = document.getElementById('dispatch-clockin-customer')?.value || '';
  const jobId = document.getElementById('dispatch-clockin-job')?.value || '';
  const notes = document.getElementById('dispatch-clockin-notes')?.value?.trim() || '';

  const settings = window.AppState?.settings || {};
  const _myClockMode = _dspClockMode(driverId); // TASK-9553 per-person clock mode
  const isTicketMode = _myClockMode === 'ticket' || _myClockMode === 'ticketonly';
  if (isTicketMode && !customerId) { window.showToast?.('Select a customer', 'error'); return; }

  // Guard: if no job is picked but the customer has active jobs in the picker, make the
  // driver confirm. This stops the "I clocked in, but my clock got a ghost ticket because
  // I didn't pick a job" pattern. Log to clockAuditLog when they continue anyway so tech
  // support can trace it.
  const isShopForWarn = customerId === '__shop__';
  let availableJobIdsForAudit = [];
  if (!isShopForWarn && !jobId && customerId) {
    const jobSelect = document.getElementById('dispatch-clockin-job');
    const availableJobs = jobSelect
      ? Array.from(jobSelect.options).map(o => ({ id: o.value, label: o.textContent })).filter(o => o.id)
      : [];
    if (availableJobs.length) {
      const customerNameForWarn = (DispatchState.customers.find(c => c.id === customerId)?.name) || 'this customer';
      const jobList = availableJobs.slice(0, 6).map(j => `• ${j.label}`).join('\n');
      const more = availableJobs.length > 6 ? `\n…and ${availableJobs.length - 6} more` : '';
      const msg = `No job selected.\n\n${customerNameForWarn} has ${availableJobs.length} active job${availableJobs.length === 1 ? '' : 's'} available:\n\n${jobList}${more}\n\nClock in without a job anyway?`;
      // Use native confirm — the PIN flow is already outside the normal modal framework.
      const keepGoing = window.confirm(msg);
      if (!keepGoing) {
        return; // Let them go back and pick a job; don't disable the submit button.
      }
      availableJobIdsForAudit = availableJobs.map(j => j.id);
    }
  }

  const btn = document.getElementById('dispatch-clockin-submit');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="loading-spinner-small"></span> Clocking In...'; }
  const _resetBtn = () => { if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">login</span>Clock In'; } };

  // clock-double-tap — one open shift per person, checked BEFORE the ticket is
  // touched so a refused tap leaves nothing behind.
  const _csg = window.ClockShiftGuard;
  const _csgEnd = _csg ? _csg.begin(driverId) : () => {};
  if (!_csgEnd) return; // this tab's own second tap — the first is still saving
  const _newEntryRef = doc(collection(db, 'tenants', tenantId, 'clockEntries'));
  let _csgClaim = null;
  if (_csg) {
    _csgClaim = await _csg.guardClockIn(_dispatchCsgDeps(), { tenantId, personId: driverId, personName: driverName, self: true, entryId: _newEntryRef.id, resolveStale: true, surface: 'dispatch-pin' });
    if (!_csgClaim.ok) {
      _csgEnd();
      window.showToast?.(_csgClaim.message, 'warning', null, 9000);
      document.getElementById('dispatch-clockin-modal')?.remove();
      return;
    }
  }

  const isShop = customerId === '__shop__';
  const customer = isShop ? null : DispatchState.customers.find(c => c.id === customerId);
  const job = jobId ? DispatchState.jobs.find(j => j.id === jobId) : null;
  const clockInTime = Timestamp.now();

  try {
    let ticketId = null;
    let ticketLineId = null;
    let role = 'NON_CDL';

    // Determine role from person data — exhaustive 3-step fallback per
    // project convention (`p?.crewType || p?.role || p?.roles?.[0]`).
    const person = (DispatchState.people || []).find(p => p.id === driverId);
    if (person) {
      const ct = (person.crewType || person.role || person.roles?.[0] || '').toLowerCase();
      if (ct.includes('cdl') || ct.includes('driver')) role = 'CDL';
      else if (ct) role = person.crewType || person.role || person.roles?.[0] || 'NON_CDL';
    }

    // Create ticket if ticket building is enabled. Delegate to the shared
    // ClockModule.findOrCreateTicket helper (4-tier dedup, internal-time
    // gate, lock keyed by customer+job+date). Skip for the literal __shop__
    // sentinel; pass skipInternalTime=true when the resolved customer doc is
    // flagged internalTimeOnly so the helper short-circuits without a
    // second Firestore read.
    // TASK-1221 — dispatch remote clock-in captures NO per-operator equipment, so
    // a split job here would resolve an op: key (empty assetIds) and mint an
    // op:-keyed ticket — but the driver's later asset-aware clock-out (jobboard,
    // truck picked) resolves the veh: key and forms a DIFFERENT rig ticket,
    // orphaning this one. So for split jobs we DEFER ticket creation to clock-out
    // (jobboard submitClockOut atomically creates the ticket + EMPLOYEE line by
    // the rig key). Combined jobs keep eager clock-in ticket creation, unchanged.
    const _isSplitJob = !!(job?.splitBy && job.splitBy !== 'combined');
    // TASK-9553 — this driver's own clock mode decides (COD: was company-only).
    if (!isShop && window.shouldBuildTickets?.(driverId || null) && !_isSplitJob) {
      try {
        const skipInternalTime = customer?.internalTimeOnly === true;
        const result = await window.ClockModule.findOrCreateTicket(
          customerId,
          customer?.name || '',
          jobId,
          job?.title || job?.name || '',
          clockInTime.toDate(),
          tenantId,
          { source: 'dispatch_pin', skipInternalTime },
          { splitBy: job?.splitBy || 'combined', assetIds: [], personId: driverId }
        );
        if (result && result.ticketId && !result.skippedInternalTime) {
          ticketId = result.ticketId;
          // Create EMPLOYEE ticket line on the resolved ticket
          const lineRef = await addDoc(collection(db, 'tenants', tenantId, 'tickets', ticketId, 'ticketLines'), {
            type: 'EMPLOYEE',
            clockEntryId: null, // linked below
            personId: driverId,
            personName: driverName,
            role,
            fromTime: clockInTime,
            toTime: null,
            hours: 0,
            rate: 0,
            lineAmount: 0,
            notes: notes || null,
            createdAt: Timestamp.now()
          });
          ticketLineId = lineRef.id;
        }
      } catch (ticketErr) {
        console.error('[Dispatch] Ticket creation error (non-blocking):', ticketErr);
        // Don't block clock-in if ticket creation fails
      }
    }

    // Create clock entry (with ticketId if we created one)
    // clock-double-tap — under the id the open-shift lock was claimed for.
    const clockEntryRef = _newEntryRef;
    const _entryData = {
      personId: driverId,
      personName: driverName,
      clockIn: clockInTime,
      clockOut: null,
      status: 'active',
      jobId: jobId || null,
      jobTitle: job?.title || job?.name || '',
      customerId: isShop ? null : (customerId || null),
      customerName: isShop ? 'Shop' : (customer?.name || ''),
      ticketId: ticketId || null,
      ticketLineId: ticketLineId || null,
      role,
      source: 'dispatch_pin',
      notes: notes || null,
      createdAt: Timestamp.now()
    };
    try {
      // TASK-9713 — late/offline or far-off-clock clock-ins land marked for review.
      await (window.ClockShiftGuard?.writeClockIn ? window.ClockShiftGuard.writeClockIn(setDoc, clockEntryRef, _entryData) : setDoc(clockEntryRef, _entryData));
    } catch (_entryErr) {
      try { await _csgClaim?.release?.(); } catch (_) { /* best effort */ }
      if (_csg && _csg.isDuplicateRefusal(_entryErr)) {
        // Another device clocked this person in a moment ago (rules backstop).
        // The zero-hour EMPLOYEE line written above stays unlinked; a driver
        // session cannot delete a line with no clockEntryId (rules), the office
        // can — it is logged here so it can be found.
        const _open = await _csg.openShifts(_dispatchCsgDeps(), tenantId, driverId);
        const _msg = (_open && _open.length) ? _csg.alreadyInMessage(_open[0], { self: true })
          : (!_csgClaim?.locked ? _csg.pendingMessage({ self: true }) : null);
        if (_msg) {
          console.warn('[ClockShiftGuard] clock-in refused (dispatch PIN); unlinked line:', ticketId, ticketLineId);
          window.showToast?.(_msg, 'warning', null, 9000);
          document.getElementById('dispatch-clockin-modal')?.remove();
          _entryErr._userMessageShown = true;
        }
      }
      throw _entryErr;
    }

    // Link ticket line to clock entry + bump employee count
    if (ticketId && ticketLineId) {
      await updateDoc(doc(db, 'tenants', tenantId, 'tickets', ticketId, 'ticketLines', ticketLineId), {
        clockEntryId: clockEntryRef.id
      });
      await updateDoc(doc(db, 'tenants', tenantId, 'tickets', ticketId), {
        employeeCount: increment(1),
        updatedAt: Timestamp.now()
      });
    }

    // Audit: dispatch clock-in
    try {
      await addDoc(collection(db, 'tenants', tenantId, 'clockAuditLog'), {
        action: 'CLOCK_IN', source: 'dispatch-pin', timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        clockEntryId: clockEntryRef.id, personId: driverId, personName: driverName,
        jobId: jobId || null, jobTitle: job?.title || job?.name || null,
        customerId: customerId || null, ticketId, ticketLineId, role,
        // When jobId is null but jobs were actually available, record that the driver
        // was warned and chose to continue — plus which jobs they bypassed. Lets tech
        // support reconstruct "why is there a ghost ticket?" after the fact.
        noJobBypassed: !isShop && !jobId && availableJobIdsForAudit.length > 0,
        availableJobIdsAtClockIn: availableJobIdsForAudit,
      });
    } catch (ae) { console.warn('[Dispatch] Audit log failed:', ae); }

    const now = new Date();
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const _modeAfterClockIn = _myClockMode;
    // 1017 (Chris: Ticket-Only KEEPS shop/yard hours) — Shop / internal time
    // builds no ticket, so never pop the close-out form at clock-in: finishing
    // it there would end the shift seconds after it started. EXACTLY the ticket
    // skip above (Shop sentinel or the internalTimeOnly flag) — COD r1: a name
    // guess would skip the form while a ticket was still minted.
    const _internalPunch = isShop || customer?.internalTimeOnly === true;
    if (_modeAfterClockIn === 'ticketonly' && !_internalPunch) {
      // ticketonly: don't say "Clocked in" — say "Building ticket" and auto-open
      // the Build Ticket form so the driver lands directly on the ticket UI
      // instead of having to tap the button again.
      window.showToast?.(`Building ticket — ${isShop ? 'Shop' : (customer?.name || '')}`, 'success');
      document.getElementById('dispatch-clockin-modal')?.remove();
      // Defer to next tick so the modal close completes first.
      setTimeout(() => openDriverClockOut(), 200);
    } else {
      window.showToast?.(`Clocked in at ${timeStr} — ${isShop ? 'Shop' : (customer?.name || '')}`, 'success');
      document.getElementById('dispatch-clockin-modal')?.remove();
    }
  } catch (err) {
    if (err && err._userMessageShown) return;
    try { await _csgClaim?.release?.(); } catch (_) { /* best effort — never holds an OPEN shift's lock */ }
    console.error('[Dispatch] Clock in error:', err);
    window.showToast?.('Error clocking in. Please try again.', 'error');
    _resetBtn();
  } finally {
    _csgEnd();
  }
}

// clock-double-tap — the Firestore calls window.ClockShiftGuard makes, bound to
// this module's own firebase imports (the guard imports nothing itself).
function _dispatchCsgDeps() {
  // stuck-shift-morning — updateDoc/Timestamp/addDoc close a stale open shift.
  return { db, doc, collection, query, where, getDocs, getDoc, runTransaction, serverTimestamp, auth, updateDoc, addDoc, Timestamp };
}

async function openDriverClockOut() {
  const driverId = window.AppState?.driverPersonId;
  // clock-double-tap — with two open shifts, never whichever one the snapshot
  // listed first: show both (start + job) and let the driver pick. Cancel =
  // nothing closed.
  const _csg = window.ClockShiftGuard;
  const _mine = (DispatchState._clockEntries || []).filter(e => e && e.personId === driverId && (!_csg || _csg.isOpen(e)));
  let entry = _mine[0] || null;
  if (!entry) { window.showToast?.('No active clock entry found', 'error'); return; }
  if (_mine.length > 1 && _csg) {
    entry = await _csg.pickShiftToClose(_mine, { self: true });
    if (!entry) return;
  }

  // If ticket mode is enabled, open Build Ticket in-place
  const clockOutMode = _dspClockMode(entry.personId || driverId); // TASK-9553 per-person clock mode
  if (clockOutMode === 'ticketonly' || clockOutMode === 'ticket') {
    // Hydrate JobBoardState with dispatch data so Build Ticket form works
    if (window.JobBoardModule) {
      const jbs = window.JobBoardModule._state || {};
      // Copy dispatch data into jobboard state if available
      if (typeof window.JobBoardModule._hydrateState === 'function') {
        window.JobBoardModule._hydrateState({
          tenantId: DispatchState.tenantId,
          trucks: DispatchState.trucks || [],
          trailers: DispatchState.trailers || [],
          people: DispatchState.people || [],
          customers: DispatchState.customers || [],
          clockEntries: DispatchState._clockEntries || [],
          jobs: DispatchState.jobs || [],
          jobDays: DispatchState.jobDays || []
        });
      }
      // The live driver subscription above already decided (one shift, or the
      // driver picked) — the board must not ask again.
      window.JobBoardModule.clockOutFromBoard(entry.id, entry.jobId || '', '', { shiftChosen: true });
      return;
    }
    // Fallback: navigate to jobboard (if JobBoardModule not loaded)
    window._pendingBuildTicket = { entryId: entry.id, jobId: entry.jobId };
    window.navigateToView('jobboard');
    return;
  }

  const clockIn = entry.clockIn?.toDate?.() || new Date(entry.clockIn);
  const elapsed = Math.round((Date.now() - clockIn.getTime()) / 60000);
  const hrs = Math.floor(elapsed / 60);
  const mins = elapsed % 60;

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-clockout-modal';
  modal.innerHTML = `
    <div class="modal-content modal-small" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">logout</span>Clock Out</h3>
        <button class="btn-icon" onclick="document.getElementById('dispatch-clockout-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="text-align:center;margin-bottom:16px;background:var(--bg-secondary,#F1F5F9);border-radius:10px;padding:16px;">
          <div style="font-size:0.85rem;color:var(--text-secondary,#475569);">Clocked in at</div>
          <div style="font-size:1.2rem;font-weight:700;color:var(--text-primary,#0F172A);">${clockIn.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
          <div style="font-size:0.85rem;color:var(--text-secondary,#475569);margin-top:4px;">${escapeHtml(entry.customerName || 'No customer')}${entry.jobTitle ? ' — ' + escapeHtml(entry.jobTitle) : ''}</div>
          <div style="font-size:1.5rem;font-weight:700;color:var(--brand-blue,#3B82F6);margin-top:8px;">${hrs}h ${mins}m</div>
        </div>
        <textarea id="dispatch-clockout-notes" rows="2" placeholder="${window.AppState?.settings?.requireShiftNotes === true ? 'REQUIRED — What did you do today?' : 'Shift notes (optional)…'}" style="width:100%;padding:10px;font-size:0.9rem;border:1px solid #e2e8f0;border-radius:10px;resize:vertical;background:#f8fafc;color:#0f172a;margin-bottom:12px;"></textarea>
        <button class="btn btn-primary" style="width:100%;padding:12px;font-size:1rem;font-weight:600;background:#DC2626;border:none;border-radius:10px;color:#fff;" onclick="window.DispatchModule.submitDriverClockOut('${escJsAttr(entry.id)}')">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">logout</span>Clock Out
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function submitDriverClockOut(entryId) {
  // TASK-619 (Lacey) — require a shift note before clock-out when the tenant opts
  // in. This simple-mode dispatch clock-out modal otherwise captures no note, so
  // gate BEFORE any write (no half-complete) and never trap: blank → toast + focus.
  const dcNotes = document.getElementById('dispatch-clockout-notes')?.value?.trim() || '';
  if (window.AppState?.settings?.requireShiftNotes === true && !dcNotes) {
    window.showToast?.('Add a shift note to clock out — what did you do today?', 'error');
    document.getElementById('dispatch-clockout-notes')?.focus();
    return;
  }
  try {
    const entrySnap = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'clockEntries', entryId));
    const entryData = entrySnap.data();
    const clockInTime = entryData?.clockIn?.toDate?.() || new Date(entryData?.clockIn);
    const clockOutTime = new Date();
    const totalHours = Math.max(0, parseFloat(((clockOutTime - clockInTime) / 3600000).toFixed(2)));

    await updateDoc(doc(db, 'tenants', DispatchState.tenantId, 'clockEntries', entryId), {
      clockOut: Timestamp.fromDate(clockOutTime),
      totalHours,
      status: 'completed',
      ...(dcNotes ? { clockOutNotes: dcNotes, notes: dcNotes } : {}),
      updatedAt: Timestamp.now()
    });

    // Audit: dispatch clock-out
    try {
      await addDoc(collection(db, 'tenants', DispatchState.tenantId, 'clockAuditLog'), {
        action: 'CLOCK_OUT', source: 'dispatch-pin', timestamp: Timestamp.now(),
        user: window.AppState?.user?.email || 'unknown',
        clockEntryId: entryId, personId: entryData?.personId, personName: entryData?.personName,
        jobId: entryData?.jobId, totalHours,
        ticketId: entryData?.ticketId, ticketLineId: entryData?.ticketLineId
      });
    } catch (ae) { console.warn('[Dispatch] Audit log failed:', ae); }

    window.showToast?.('Clocked out', 'success');
    document.getElementById('dispatch-clockout-modal')?.remove();
  } catch (err) {
    console.error('[Dispatch] Clock out error:', err);
    window.showToast?.('Error clocking out', 'error');
  }
}

// ============================================
// DRIVER RECORD EXPENSE (PIN mode)
// ============================================
// Driver-PIN landing renders dispatch.js (not jobboard.js), so JobBoardState
// isn't subscribed. This wrapper builds the ctx the modal expects from
// DispatchState (trucks + jobs + clockEntries already in memory) and
// pre-fetches the tenant's costAllocations so the dropdown is populated
// even though the JobBoard view never mounted. See Bug A fix 2026-05-13.
async function openDriverRecordExpense() {
  // Legacy driver-PIN call site. TASK-348 refactor delegates to
  // openDriverRecordExpenseForPerson with PIN identity.
  return openDriverRecordExpenseForPerson(
    window.AppState?.driverPersonId,
    window.AppState?.driverPersonName || ''
  );
}

async function openDriverRecordExpenseForPerson(personId, personName) {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) {
    window.showToast?.('Tenant not loaded — try refreshing', 'error');
    return;
  }
  if (!window.JobBoardModule?.openExpenseModal) {
    window.showToast?.('Expense module not loaded — try refreshing', 'error');
    return;
  }

  const driverId = personId || window.AppState?.driverPersonId;
  const driverName = personName || window.AppState?.driverPersonName || '';

  // TASK-1489 — hard identity guard. The cold-route hydration below runs
  // `where('personId', '==', driverId)`, and Firestore THROWS
  // "Unsupported field value: undefined" on an undefined comparand — it does not
  // return empty. Both of today's callers happen to pre-check identity
  // (app.js:openPersonalExpense toasts on a null personId; the driver-PIN sheet
  // always has driverPersonId), so this is currently unreachable — but personless
  // members make "a signed-in user with no personId" a normal population, so
  // close it here rather than relying on every future caller. Mirrors the guard
  // openSwitchJobForPerson already has.
  if (!driverId) {
    window.showToast?.('Identity not resolved — this action needs a linked roster profile.', 'error');
    return;
  }

  // TASK-1440 — COLD-ROUTE HYDRATION.
  // This modal is reachable from the GLOBAL Personal Actions sheet (app.js), but
  // every field below came from DispatchState, which is populated ONLY by
  // subscribeToData() -> initDispatch(). Opened from any view other than Dispatch,
  // that state is stone cold: jobs is [], so jobboard's `_showJobPicker =
  // _expJobs.length > 0` is false and the Job row is OMITTED entirely — the field
  // just isn't there. Same cold state also left the Vehicle dropdown
  // placeholder-only and the expense unlinked from the person's active clock
  // entry (COD Codex + Independent).
  // Mirrors the getDocs pre-fetch this function ALREADY does for costAllocations,
  // rather than calling subscribeToData() — that would drag in unrelated listeners
  // and render side effects (Codex).
  let _ctxJobs = DispatchState.jobs || [];
  let _ctxJobDays = DispatchState.jobDays || [];
  let _ctxPeople = DispatchState.people || [];
  let _ctxTrucks = DispatchState.trucks || [];
  let _ctxClock = DispatchState._clockEntries || [];
  // Any missing piece triggers it, not just empty jobs — trucks and the clock
  // entry are just as cold on this route.
  if (!_ctxJobs.length || !_ctxPeople.length || !_ctxTrucks.length || !_ctxClock.length) {
    // Window the jobs read the SAME way subscribeToData does (TASK-424). An
    // unbounded getDocs over 700+ job docs on a driver's phone is exactly what
    // TASK-424 removed.
    // TASK-4567 (COD Blast) — this was a FOURTH undisclosed copy of the cutoff
    // parse. It now shares computeAdminWindow with subscribeToData and the
    // far-past add-day confirm, so a change to the window arithmetic cannot skip
    // this path silently.
    //
    // The ONE deliberate divergence, now explicit instead of hidden in a second
    // copy: when the window is "all" (daysBack <= 0) the live listeners simply
    // drop the filter, but this is a one-shot getDocs on a driver's phone and an
    // unbounded read over 700+ job docs is exactly what TASK-424 removed — so
    // "all" is bounded to 10 years here.
    const _win = computeAdminWindow(DispatchState.adminWindowDaysBack, new Date());
    const _cutDays = _win.all ? 3650 : _win.daysBack;
    const _cut = Timestamp.fromDate(new Date(_win.todayStartLocal.getTime() - _cutDays * 86400000));
    try {
      // TWO job queries, merged. A startDate-only filter silently drops every
      // multi-day job whose window straddles the cutoff — the endDate arm is why
      // subscribeToData runs both.
      const [recentSnap, activeSnap, peopleSnap, trucksSnap, clockSnap] = await Promise.all([
        getDocs(query(collection(db, 'tenants', tenantId, 'jobs'), where('startDate', '>=', _cut))),
        getDocs(query(collection(db, 'tenants', tenantId, 'jobs'), where('endDate', '>=', _cut))),
        _ctxPeople.length ? null : getDocs(collection(db, 'tenants', tenantId, 'people')),
        _ctxTrucks.length ? null : getDocs(collection(db, 'tenants', tenantId, 'trucks')),
        _ctxClock.length ? null : getDocs(query(
          collection(db, 'tenants', tenantId, 'clockEntries'),
          where('personId', '==', driverId), where('status', '==', 'active'))),
      ]);
      const _byId = new Map();
      // startDate arm: as-is. endDate arm: DROP finished jobs client-side, exactly
      // as the warm path does (:2904-2910) — without this, an old finished
      // multi-day job whose endDate lands inside the window would be offered in
      // the expense Job picker (COD Code Reviewer ❌).
      // TASK-4610 — same normalizer the warm path uses, so the expense Job
      // picker's date window (_expenseVisibleJobs) never compares a raw string.
      recentSnap && recentSnap.docs.forEach(d => _byId.set(d.id, normalizeJobDoc(d.id, d.data())));
      activeSnap && activeSnap.docs.forEach(d => {
        const data = d.data();
        if (data.status === 'finished') return;
        if (!_byId.has(d.id)) _byId.set(d.id, normalizeJobDoc(d.id, data));
      });
      if (_byId.size) _ctxJobs = [..._byId.values()];
      if (peopleSnap) _ctxPeople = peopleSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (trucksSnap) _ctxTrucks = trucksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (clockSnap) _ctxClock = clockSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn('[Dispatch] TASK-1440 cold-fetch failed:', e?.message);
    }

    // jobDays ONLY for a RESTRICTED person. _expenseVisibleJobs returns early for
    // everyone else, so an unrestricted user should pay zero extra reads.
    // Restriction is a property of the PERSON, which is why people is fetched
    // above first — without it a restricted person silently resolves to
    // unrestricted (the hole TASK-1338's COD caught).
    const _self = _ctxPeople.find(pp => pp && pp.id === driverId);
    // COD Code Reviewer ❌ — if the people fetch failed or came back empty we
    // CANNOT resolve restriction, and _expenseVisibleJobs would then treat this
    // person as unrestricted and offer the whole fleet. That is precisely the
    // 'silently resolves to unrestricted' hole TASK-1338 closed, re-opened via a
    // failed read. No person doc => no picker.
    if (driverId && !_self) {
      console.warn('[Dispatch] TASK-1440 could not resolve person for restriction — failing closed');
      _ctxJobs = [];
      _ctxJobDays = [];
    }
    const _restricted = !!driverId
      && (window.AppState?.settings?.personalizedSchedule === true || _self?.showAllJobs === false);
    if (_restricted && !_ctxJobDays.length) {
      try {
        const jdSnap = await getDocs(query(
          collection(db, 'tenants', tenantId, 'jobDays'), where('date', '>=', _cut)));
        _ctxJobDays = jdSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        // FAIL CLOSED. A restricted person whose jobDays read fails must get NO
        // picker — never the whole fleet. Empty jobDays makes _expenseVisibleJobs
        // resolve to zero visible jobs, which hides the row.
        console.warn('[Dispatch] TASK-1440 jobDays fetch failed — failing closed', e?.message);
        _ctxJobDays = [];
      }
    }
  }

  const activeEntry = _ctxClock.find(e => e.personId === driverId && e.status === 'active');

  // Pre-fetch cost allocations (driver may have just landed and not have any
  // background subscription running for this collection).
  let costAllocations = [];
  // TASK-4585 — a FAILED fetch also yields [], and downstream an empty list now
  // waives the cost-allocation requirement. Say which one this is.
  let costAllocationsSettled = false;
  try {
    const allocSnap = await getDocs(collection(db, 'tenants', tenantId, 'costAllocations'));
    costAllocations = allocSnap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0) || String(a.label || '').localeCompare(String(b.label || '')));
    costAllocationsSettled = true;
  } catch (e) {
    console.warn('[Dispatch] openDriverRecordExpense: cost allocations fetch failed', e?.message);
  }

  const driverExpenseSettings = window.AppState?.settings?.driverExpenses || {};

  window.JobBoardModule.openExpenseModal(
    activeEntry?.id || null,
    activeEntry?.jobId || null,
    null,
    {
      tenantId,
      clockEntries: _ctxClock,
      trucks: _ctxTrucks,
      jobs: _ctxJobs,
      jobDays: _ctxJobDays,
      // TASK-1338 — people is REQUIRED for expense job-visibility scoping.
      people: _ctxPeople,
      costAllocations,
      costAllocationsSettled,
      driverExpenseSettings,
      driverId,
      driverName,
    }
  );
}

// ============================================
// DRIVER VEHICLE INFO (PIN mode)
// ============================================

function openDriverSwitchJob() {
  // Legacy driver-PIN call site. TASK-348 refactor delegates to
  // openSwitchJobForPerson with PIN identity.
  return openSwitchJobForPerson(
    window.AppState?.driverPersonId,
    window.AppState?.driverPersonName || ''
  );
}

// fix-1999-followups (TASK-9576 follow-up) — Switch Ticket must not depend on
// the Dispatch view having been opened. This modal is reachable from the GLOBAL
// Personal Actions sheet (app.js switchPersonalTicket) on any view, but it used to
// read the open shift, customers and jobs from DispatchState — live only while
// Dispatch is mounted (leaving it unsubscribes and FREEZES the arrays; a session
// that never opened it has nothing). A clocked-in owner tapping it from HR got
// "You are not clocked in" and an empty customer list. Same class as TASK-9576's
// clock-out fix (jobboard.js clockOutFromBoardForPerson, 6491aaeb8), same remedy:
//   - the open shift is read from the DATABASE by personId on every open (the
//     self-scoped query the clockEntries read rule allows — TASK-452); Dispatch's
//     live array is only a fallback when that read fails;
//   - the tenant comes from the SESSION (AppState.tenant), never a view's state;
//   - when Dispatch is not live for this tenant, ONE load fills what the modal
//     needs (customers, windowed jobs, and for the close-out's equipment the
//     entry's own job days + fleet) with getDocs — no listeners, no render, and
//     nothing written into DispatchState (a frozen Dispatch array is never
//     overwritten; the context lives in DispatchState._switchJobCtx for this
//     modal only and is cleared on close / submit).
let _switchJobOpening = false;

function _switchJobDispatchLive(tenantId) {
  return !!tenantId && DispatchState.tenantId === tenantId && (DispatchState.unsubscribers || []).length > 0;
}

// The X button drops only this modal's context — the _personalAction* identity
// stash is shared with the Personal Actions clock flows, and the X never cleared
// it before (COD Blast-Radius). A completed switch clears both, as it always did.
function closeSwitchJobModal(clearIdentity) {
  document.getElementById('dispatch-switch-job-modal')?.remove();
  DispatchState._switchJobCtx = null;
  if (clearIdentity === true) {
    DispatchState._personalActionPersonId = null;
    DispatchState._personalActionPersonName = null;
  }
}

// The signed-in identity this session acts as (SPA member or driver PIN). A
// session that changed person while the modal loaded must not get this shift.
function _switchJobSessionPersonId() {
  return window.AppState?.memberAccess?.personId || window.AppState?.driverPersonId || null;
}

// Returns { entry, customers, jobs, jobDays, trucks, trailers, equipmentUnknown }
// or { error } when the open shift could not be read at all.
async function _loadSwitchJobContext(tenantId, driverId) {
  const settle = async (label, fn) => {
    try { return await fn(); } catch (e) {
      console.warn(`[Dispatch] Switch Ticket: ${label} read failed:`, e?.message);
      return null;
    }
  };
  const live = _switchJobDispatchLive(tenantId);
  const col = (name) => collection(db, 'tenants', tenantId, name);
  const docsOf = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Same window the live listeners use (TASK-424 / TASK-4567), "all" bounded to
  // 10 years for a one-shot read on a phone — the TASK-1440 rule.
  const _win = computeAdminWindow(DispatchState.adminWindowDaysBack, new Date());
  const _cutDays = _win.all ? 3650 : _win.daysBack;
  const cut = Timestamp.fromDate(new Date(_win.todayStartLocal.getTime() - _cutDays * 86400000));

  const [clockSnap, custSnap, recentSnap, activeSnap] = await Promise.all([
    settle('open shift', () => getDocs(query(col('clockEntries'),
      where('personId', '==', driverId), where('status', '==', 'active')))),
    live ? null : settle('customers', () => _dispatchCustomerSource().getAll(tenantId, { prefer: _dispatchCustomerPrefer() })), // TASK-9694 S6a
    live ? null : settle('jobs (startDate window)', () => getDocs(query(col('jobs'), where('startDate', '>=', cut)))),
    live ? null : settle('jobs (endDate window)', () => getDocs(query(col('jobs'), where('endDate', '>=', cut)))),
  ]);

  let mine;
  if (clockSnap) {
    mine = docsOf(clockSnap);
  } else if (live) {
    mine = (DispatchState._clockEntries || []).filter(e => e && e.personId === driverId && e.status === 'active');
  } else {
    return { error: 'shift-read' };
  }
  // Two open shifts for one person is a data problem: act on the NEWEST, the one
  // actually running (app.js togglePersonalClock makes the same call).
  const _ms = (e) => (e.clockIn?.toMillis ? e.clockIn.toMillis() : (e.clockIn ? new Date(e.clockIn).getTime() : 0)) || 0;
  if (mine.length > 1) console.warn('[Dispatch] Switch Ticket: more than one active entry for this person:', mine.map(e => e.id));
  const _sorted = mine.slice().sort((a, b) => _ms(b) - _ms(a));
  const entry = _sorted[0] || null;
  // TASK-9579 — the older open shift(s) keep adding hours; the modal says so
  // (the switch closes only the newest). Never closed from here.
  const otherOpen = _sorted.slice(1);

  let customers; let jobs;
  if (live) {
    customers = DispatchState.customers || [];
    jobs = DispatchState.jobs || [];
  } else {
    customers = Array.isArray(custSnap) ? custSnap : []; // TASK-9694 S6a — getAll returns a list
    const byId = new Map();
    // startDate arm as-is; endDate arm drops finished jobs, as the warm path does.
    if (recentSnap) recentSnap.docs.forEach(d => byId.set(d.id, normalizeJobDoc(d.id, d.data())));
    if (activeSnap) activeSnap.docs.forEach(d => {
      const data = d.data();
      if (data.status === 'finished') return;
      if (!byId.has(d.id)) byId.set(d.id, normalizeJobDoc(d.id, data));
    });
    jobs = [...byId.values()];
  }

  // Equipment the switch closes out on the current job (TASK-9556). Tie ON uses
  // entry.assetIds only; OFF needs the entry's job day + the fleet to resolve it.
  let jobDays = live ? (DispatchState.jobDays || []) : [];
  let trucks = live ? (DispatchState.trucks || []) : [];
  let trailers = live ? (DispatchState.trailers || []) : [];
  let equipmentUnknown = false;
  // Tie counts only in a ticket clock-out mode (Simple = OFF) — same rule as
  // jobboard.js / clock.js equipmentTieEnabled; keep in sync.
  const tieOn = window.AppState?.settings?.equipmentTie?.enabled === true
    && ['ticket', 'ticketonly'].includes(_dspClockMode(entry?.personId || null)); // TASK-9553
  // v1.15.2000 COD 🔴 (money): only trust the live window when it holds the
  // shift's EXACT day — a job with a newer day in the window but the shift's
  // day outside it would otherwise close zero trucks with no warning.
  const _ciKey = entry ? _switchDayKey(entry.clockIn) : null;
  const _liveHasShiftDay = live && _ciKey != null && jobDays.some(jd => jd && jd.jobId === entry.jobId && _switchDayKey(jd.date) === _ciKey);
  if (entry && entry.jobId && !tieOn && !_liveHasShiftDay) {
    const [daysSnap, trucksSnap, trailersSnap] = await Promise.all([
      settle('entry job days', () => getDocs(query(col('jobDays'), where('jobId', '==', entry.jobId)))),
      (live && trucks.length) ? null : settle('trucks', () => getDocs(col('trucks'))),
      (live && trailers.length) ? null : settle('trailers', () => getDocs(col('trailers'))),
    ]);
    if (daysSnap) jobDays = docsOf(daysSnap);
    if (trucksSnap) trucks = docsOf(trucksSnap);
    if (trailersSnap) trailers = docsOf(trailersSnap);
    if (!daysSnap || (!(live && DispatchState.trucks?.length) && !trucksSnap) || (!(live && DispatchState.trailers?.length) && !trailersSnap)) {
      equipmentUnknown = true;
    }
  }
  // TASK-9579 — date miss: the job's days are all loaded (live window held the
  // shift's day, or the by-jobId read ran) and none is on the clock-in tenant
  // date, so the segment this switch closes gets NO truck
  // (_switchJobEquipmentForEntry → []). Say so in the modal — the Clock sheet
  // and the board's close-out say the same thing. Tie ON closes entry.assetIds
  // whatever the day, so it is not a date miss.
  let equipmentDayMiss = null;
  if (entry && entry.jobId && !tieOn && !equipmentUnknown && _ciKey != null && entry.customerId !== '__shop__') {
    const _cust = (customers || []).find(c => c && c.id === entry.customerId);
    const _internal = !!_cust && (_cust.internalTimeOnly === true || (_cust.internalTimeOnly === undefined && /^(shop|office|internal|yard)$/i.test(_cust.name || '')));
    const _hasDay = jobDays.some(jd => jd && jd.jobId === entry.jobId && _switchDayKey(jd.date) === _ciKey);
    if (!_internal && !_hasDay) equipmentDayMiss = { label: _switchDayLabel(entry.clockIn) };
  }
  return { entry, otherOpen, customers, jobs, jobDays, trucks, trailers, equipmentUnknown, equipmentDayMiss };
}

// TASK-9579 — "Thu, Oct 1" in the tenant zone, for the modal's warnings.
function _switchDayLabel(v, withTime) {
  const d = v?.toDate?.() || (v instanceof Date ? v : (v != null ? new Date(v) : null));
  if (!d || isNaN(d.getTime())) return '';
  const tz = (window.AppState?.settings || window.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  const opts = withTime
    ? { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { weekday: 'short', month: 'short', day: 'numeric' };
  try { return d.toLocaleString('en-US', { ...opts, timeZone: tz }); } catch (_) { return d.toLocaleString('en-US', opts); }
}

async function openSwitchJobForPerson(personId, personName) {
  const driverId = personId || window.AppState?.driverPersonId;
  if (!driverId) { window.showToast?.('Identity not resolved.', 'error'); return; }
  const tenantId = window.AppState?.tenant || DispatchState.tenantId || null;
  if (!tenantId) { window.showToast?.('Session not ready. Try again in a moment.', 'error'); return; }
  if (_switchJobOpening) return; // a double tap must not stack two loads / two modals
  _switchJobOpening = true;
  let ctx;
  try {
    ctx = await _loadSwitchJobContext(tenantId, driverId);
  } finally {
    _switchJobOpening = false;
  }
  // The reads are async: a session that moved to another tenant or person
  // meanwhile must not get this one's shift.
  if ((window.AppState?.tenant || tenantId) !== tenantId) return;
  const _sessPid = _switchJobSessionPersonId();
  if (_sessPid && _sessPid !== driverId) return;
  if (ctx.error) {
    window.showToast?.('Couldn\'t check your shift. Check your signal and try again.', 'error');
    return;
  }
  const entry = ctx.entry;
  if (!entry) {
    window.showToast?.('You are not clocked in. Clock in first to switch tickets.', 'error');
    return;
  }
  // Stash for submitSwitchJob to pick up.
  DispatchState._personalActionPersonId = driverId;
  DispatchState._personalActionPersonName = personName || '';
  DispatchState._switchJobCtx = { tenantId, personId: driverId, ...ctx };
  document.getElementById('dispatch-switch-job-modal')?.remove();

  const escH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  // TASK-1175 — internalTimeOnly (Shop/Office) pinned first; TASK-9548 — grouped
  // + labeled by the shared builder (window.PickerGroups) in the select below.
  const customers = (ctx.customers || []).filter(c => c.name && !window.PickerGroups.isInactiveCustomer(c)); // TASK-10030 — case-blind

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-switch-job-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">swap_horiz</span>Switch Job</h3>
        <button class="btn-icon" onclick="window.DispatchModule.closeSwitchJobModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="background:var(--bg-secondary,#F1F5F9);border-radius:10px;padding:12px 16px;margin-bottom:16px;">
          <div style="font-size:0.75rem;color:var(--text-secondary);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">Current Job</div>
          <div style="font-weight:700;margin-top:4px;">${escH(entry.customerName || 'No customer')}${entry.jobTitle ? ' — ' + escH(entry.jobTitle) : ''}</div>
        </div>
        ${ctx.equipmentUnknown ? `<div style="font-size:0.8rem;color:#92400e;background:rgba(245,158,11,0.12);border-radius:8px;padding:8px 10px;margin-bottom:12px;">Couldn't load this job's equipment. After you switch, check the trucks on the closed ticket.</div>` : ''}
        ${ctx.equipmentDayMiss ? `<div id="switch-job-daymiss" role="alert" style="font-size:0.8rem;color:#78350f;background:rgba(245,158,11,0.12);border:1px solid #f59e0b;border-radius:8px;padding:8px 10px;margin-bottom:12px;line-height:1.4;"><strong>No truck found for this shift's day (${escH(ctx.equipmentDayMiss.label)}).</strong> No job day for this job was found on that date, so the ticket you're closing gets no truck. After you switch, check the equipment on that ticket.</div>` : ''}
        ${(ctx.otherOpen || []).length ? `<div id="switch-job-multi-open" role="alert" style="font-size:0.8rem;color:#78350f;background:rgba(245,158,11,0.12);border:1px solid #f59e0b;border-radius:8px;padding:8px 10px;margin-bottom:12px;line-height:1.4;"><strong>You have ${ctx.otherOpen.length === 1 ? 'another open shift' : ctx.otherOpen.length + ' other open shifts'}</strong> (started ${escH(ctx.otherOpen.map(e => _switchDayLabel(e.clockIn, true)).join(', '))}). This switch closes only the newest one. The other keeps adding hours until it's closed — ask the office to close it.</div>` : ''}
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">New Customer</label>
          <select id="switch-job-customer" class="form-control" onchange="window.DispatchModule.onSwitchJobCustomerChange()">
            <option value="">Select customer...</option>
            ${window.PickerGroups.customerOptionsHtml(customers, { nameOf: c => c.name || '' })}
          </select>
          ${window.PickerGroups.customerHintHtml(customers, { nameOf: c => c.name || '' })}
        </div>
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">New Job</label>
          <select id="switch-job-job" class="form-control">
            <option value="">No specific job</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Shift note for the current job${window.AppState?.settings?.requireShiftNotes === true ? '' : ' <span style="font-weight:400;color:var(--text-secondary);">(optional)</span>'}</label>
          <textarea id="switch-job-notes" class="form-control" rows="2" placeholder="What did you do on this job?" style="resize:vertical;"></textarea>
        </div>
        <div id="switch-job-error" style="display:none;font-size:0.8rem;color:var(--danger,#dc2626);background:rgba(220,38,38,0.08);border-radius:8px;padding:8px 10px;margin-bottom:12px;"></div>
        <button id="switch-job-submit" class="btn btn-primary" style="width:100%;padding:12px;font-size:1rem;font-weight:600;border-radius:10px;" onclick="window.DispatchModule.submitSwitchJob()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">swap_horiz</span>Switch
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function onSwitchJobCustomerChange() {
  const custId = document.getElementById('switch-job-customer')?.value;
  const jobSelect = document.getElementById('switch-job-job');
  if (!jobSelect) return;
  if (!custId || custId === '__shop__') { jobSelect.innerHTML = '<option value="">No specific job</option>'; return; }
  const ctx = DispatchState._switchJobCtx;
  const pool = ctx ? (ctx.jobs || []) : (DispatchState.jobs || []);
  const jobs = pool.filter(j => j.customerId === custId && (j.title || j.name)).sort((a, b) => (a.title || a.name || '').localeCompare(b.title || b.name || ''));
  jobSelect.innerHTML = '<option value="">No specific job</option>' + jobs.map(j => `<option value="${String(j.id).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')}">${(j.title || j.name || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')}</option>`).join('');
}

// TASK-9556 — the equipment the switch closes out on job A, resolved from the
// modal's context (Dispatch's live state when it is mounted, else the one-shot
// load above; the job board's own state is not loaded on the SPA Personal
// Actions route). Same rule as the board's switch (jobboard.js jbRunSwitchJob /
// _jbLegacyEquipmentIdsForEntry): equipmentTie ON → exactly the entry's assetIds;
// OFF → that job day's trucks + trailers.
// Returns null when the job day isn't in memory, so the board's resolver decides.
// Tenant-local day serial for a clock-in / jobDay date (shared by the switch
// resolver and the hydrate window check). Same rule as jobboard.js
// _jbPickJobDayForEntry — keep in sync.
function _switchDayKey(v) {
  const _W = (typeof window !== 'undefined' && window) || {};
  const _tz = (_W.AppState?.settings || _W.AppState?.tenantData?.settings || {}).timezone || 'America/Chicago';
  if (typeof v === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
    if (m) return Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000);
  }
  const d = v?.toDate?.() || (v instanceof Date ? v : (v != null ? new Date(v) : null));
  if (!d || isNaN(d.getTime())) return null;
  if (_W.ClockDay && typeof _W.ClockDay.localDaySerial === 'function') {
    const k = _W.ClockDay.localDaySerial(d, _tz);
    if (k != null) return k;
  }
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86400000);
}
function _switchJobEquipmentForEntry(entry, ctx) {
  if (!entry) return [];
  // Simple clock-out mode = tie OFF (jobboard.js equipmentTieEnabled — keep in sync).
  if (window.AppState?.settings?.equipmentTie?.enabled === true
    && ['ticket', 'ticketonly'].includes(_dspClockMode(entry.personId || null))) { // TASK-9553
    return Array.isArray(entry.assetIds) ? entry.assetIds.slice() : [];
  }
  if (!entry.jobId) return [];
  const src = ctx || { jobDays: DispatchState.jobDays, trucks: DispatchState.trucks, trailers: DispatchState.trailers };
  const days = (src.jobDays || []).filter(jd => jd && jd.jobId === entry.jobId);
  if (!days.length) return null;
  // v1.15.2000 COD 🔴 (money) — the day on the clock-in TENANT date, or nothing.
  // The closest-day and first-day fallbacks billed another day's truck on the
  // closed segment. Same rule as jobboard.js _jbPickJobDayForEntry — keep in sync.
  const _dayKey = _switchDayKey;
  const ciKey = _dayKey(entry.clockIn);
  const sameDay = ciKey == null ? [] : days.filter(jd => _dayKey(jd.date) === ciKey);
  const jobDay = (entry.jobDayId && sameDay.find(jd => jd.id === entry.jobDayId)) || sameDay[0] || null;
  // No day on that date. The one-shot ctx read is the job's every day, so that
  // is final: close nothing. Dispatch's live array is only a window — return
  // null so the board's resolver (same rule) decides.
  if (!jobDay) return ctx ? [] : null;
  const ids = [];
  const push = (ref, list) => {
    if (ref == null) return;
    const asset = _resolveFleetRef(ref, list);
    const id = asset?.id || String(ref);
    if (id && !ids.includes(id)) ids.push(id);
  };
  (jobDay.trucks || []).forEach(r => push(r, src.trucks));
  (jobDay.trailers || []).forEach(r => push(r, src.trailers));
  return ids;
}

async function submitSwitchJob() {
  // TASK-348 — prefer SPA personal-action personId, fall back to PIN identity.
  const driverId = DispatchState._personalActionPersonId
    || window.AppState?.driverPersonId;
  const ctx = DispatchState._switchJobCtx;
  const errEl = document.getElementById('switch-job-error');
  const showErr = (msg) => { if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; } else window.showToast?.(msg, 'error'); };
  if (errEl) errEl.style.display = 'none';
  // The shift the modal was opened on (read from the database at open).
  const entry = (ctx && ctx.personId === driverId) ? ctx.entry : null;
  if (!entry) { showErr('Your shift is no longer loaded — close this and tap Switch Ticket again.'); return; }
  const tenantId = ctx.tenantId;
  const _sessPid = _switchJobSessionPersonId();
  if ((window.AppState?.tenant || tenantId) !== tenantId || (_sessPid && _sessPid !== ctx.personId)) { showErr('Your session changed — close this and try again.'); return; }

  const newCustId = document.getElementById('switch-job-customer')?.value;
  if (!newCustId) { window.showToast?.('Select a customer', 'error'); return; }

  // TASK-9556 — a switch CLOSES the current shift (it is a clock-out of job A),
  // so it runs through the same atomic server op as the board's "Done here —
  // switch" button: A is completed at the switch with its labor line closed,
  // and a NEW shift starts on job B. The old version re-pointed this same entry
  // at job B and opened a line there without ever closing A's line — ticket A
  // kept an open 0-hour line and the close-out billed the whole day to job B.
  // Online-only, like the board's switch: it is one atomic money mutation.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    showErr('You’re offline — reconnect to switch jobs.');
    return;
  }
  const notes = document.getElementById('switch-job-notes')?.value?.trim() || '';
  if (window.AppState?.settings?.requireShiftNotes === true && !notes) {
    showErr('Add a shift note before switching — what did you do on this job?');
    document.getElementById('switch-job-notes')?.focus();
    return;
  }
  if (!window.JobBoardModule?.jbRunSwitchJob) {
    showErr('Switch Job is still loading. Try again in a moment.');
    return;
  }

  const btn = document.getElementById('switch-job-submit');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Switching...'; }

  const isShop = newCustId === '__shop__';
  const newJobId = isShop ? '' : (document.getElementById('switch-job-job')?.value || '');

  try {
    const updated = await window.JobBoardModule.jbRunSwitchJob(entry, {
      tenantId,
      newCustomerId: newCustId,
      newJobId,
      notes,
      newAssetIds: [], // this sheet picks no equipment for job B
      equipmentAssetIds: _switchJobEquipmentForEntry(entry, ctx),
      jobs: ctx.jobs || [], // TASK-9712 — the job board's state is cold here; fences come from these
    });
    const destLabel = updated.newJobTitle || updated.newCustomerName || (isShop ? 'Shop' : 'the new job');
    window.showToast?.(`Switched — ${updated.totalHours != null ? updated.totalHours + ' hrs closed out, ' : ''}now on ${destLabel}.`, 'success');
    // TASK-348 — clear personal-action identity stash (and this modal's context).
    closeSwitchJobModal(true);
  } catch (err) {
    console.error('[Dispatch] Switch job error:', err);
    // TASK-1160 — a timeout does not mean it failed: the server may still finish.
    if (window.JobBoardModule?.jbIsGatewayTimeout?.(err)) {
      showErr('That took too long — the switch may still have gone through. Wait a few seconds, then check your shift before retrying.');
    } else {
      showErr((err && err.message) ? err.message : 'Could not switch jobs. Try again.');
    }
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">swap_horiz</span>Switch'; }
  }
}

function switchDriverTab(tab) {
  const jobsView = document.getElementById('driver-view-jobs');
  const tsView = document.getElementById('driver-view-timesheet');
  const jobsTab = document.getElementById('driver-tab-jobs');
  const tsTab = document.getElementById('driver-tab-timesheet');
  const tsSection = document.getElementById('driver-timesheet-section');

  if (tab === 'timesheet') {
    if (jobsView) jobsView.style.display = 'none';
    if (tsView) tsView.style.display = '';
    if (jobsTab) { jobsTab.style.background = 'transparent'; jobsTab.style.color = 'var(--text-secondary,#64748B)'; }
    if (tsTab) { tsTab.style.background = '#fff'; tsTab.style.color = 'var(--text-primary,#0F172A)'; }
    // Move timesheet section into timesheet view
    if (tsSection && tsView) {
      tsSection.style.display = '';
      tsView.appendChild(tsSection);
    }
  } else {
    if (jobsView) jobsView.style.display = '';
    if (tsView) tsView.style.display = 'none';
    if (jobsTab) { jobsTab.style.background = '#fff'; jobsTab.style.color = 'var(--text-primary,#0F172A)'; }
    if (tsTab) { tsTab.style.background = 'transparent'; tsTab.style.color = 'var(--text-secondary,#64748B)'; }
  }
}

// TASK-379 — resolve the fleet for the driver vehicle modal. Dispatcher/admin
// sessions have DispatchState populated by subscribeToData; PIN drivers land on
// /jobboard and never run it, so DispatchState.trucks stays empty. In that case
// fall back to the shared store jobboard publishes (window.AppState.fleet*).
// Single renderer, single source — no duplicated modal HTML, no circular dep.
// P0 1019 — the jobboard fleet bridge counts only when it is stamped with the
// CURRENT tenant (jobboard.js sets AppState.fleetTenantId beside every publish).
function _fleetBridge(key) {
  const as = window.AppState || {};
  if (!as.tenant || as.fleetTenantId !== as.tenant) return [];
  return as[key] || [];
}
function _resolveDriverFleet() {
  // P0 1019 — DispatchState survives a company switch; only trust it when it
  // belongs to the tenant this session is in now.
  const own = _dispatchDataIsFor(window.AppState?.tenant);
  let trucks = own ? (DispatchState.trucks || []) : [];
  let trailers = own ? (DispatchState.trailers || []) : [];
  if (trucks.length === 0 && trailers.length === 0) {
    trucks = _fleetBridge('fleetTrucks');
    trailers = _fleetBridge('fleetTrailers');
  }
  return { trucks, trailers };
}

// Driver Vehicles list — the list showed one generic blue truck icon for
// every unit, so a driver scanning for "the white Pete with the headache rack"
// had nothing to go on but a nickname. Roster already stores a photo on the
// truck/trailer doc (`photoUrl`, same field the Roster cards render), so reuse
// it as the row thumbnail and fall back to the icon when there is no photo.
// Fixed 44px box either way so rows stay aligned in a mixed list.
function _driverFleetThumb(v, icon, color, escH, getUnit) {
  if (v && v.photoUrl) {
    return `<img src="${escH(v.photoUrl)}" alt="${escH(getUnit(v))}" loading="lazy" onerror="this.style.display='none';this.nextElementSibling.style.display='flex';" style="width:44px;height:44px;flex:0 0 auto;border-radius:8px;object-fit:cover;background:#0F172A;">`
      + `<span class="material-icons" style="display:none;width:44px;height:44px;flex:0 0 auto;align-items:center;justify-content:center;font-size:24px;color:${color};">${icon}</span>`;
  }
  return `<span class="material-icons" style="width:44px;flex:0 0 auto;text-align:center;font-size:24px;color:${color};">${icon}</span>`;
}

function openDriverVehicleList() {
  const { trucks, trailers } = _resolveDriverFleet();
  if (trucks.length === 0 && trailers.length === 0) {
    window.showToast?.('No vehicles found', 'info');
    return;
  }

  const getUnit = (v) => window.getUnitDisplay ? window.getUnitDisplay(v) : (v.unit || v.name || 'Unknown');
  const escH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  const truckCards = trucks.filter(t => t.active !== false).map(t => `
    <div class="vehicle-list-card" onclick="window.DispatchModule.showDriverVehicleInfo('truck','${escJsAttr(t.id)}')" style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid var(--border-color,#e2e8f0);cursor:pointer;transition:background 0.15s;">
      ${_driverFleetThumb(t, 'local_shipping', 'var(--brand-blue,#3B82F6)', escH, getUnit)}
      <div style="flex:1;min-width:0;">
        <div style="font-weight:600;font-size:0.95rem;">${escH(getUnit(t))}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary,#475569);">${escH(t.type || 'Vehicle')}${t.make ? ' — ' + escH(t.make) : ''}${t.model ? ' ' + escH(t.model) : ''}</div>
      </div>
      <span class="material-icons" style="font-size:18px;color:var(--text-muted,#94A3B8);">chevron_right</span>
    </div>
  `).join('');

  const trailerCards = trailers.filter(t => t.active !== false).map(t => `
    <div class="vehicle-list-card" onclick="window.DispatchModule.showDriverVehicleInfo('trailer','${escJsAttr(t.id)}')" style="display:flex;align-items:center;gap:12px;padding:14px 16px;border-bottom:1px solid var(--border-color,#e2e8f0);cursor:pointer;transition:background 0.15s;">
      ${_driverFleetThumb(t, 'rv_hookup', 'var(--accent-teal,#06B6D4)', escH, getUnit)}
      <div style="flex:1;min-width:0;">
        <div style="font-weight:600;font-size:0.95rem;">${escH(getUnit(t))}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary,#475569);">${escH(t.type || 'Trailer')}</div>
      </div>
      <span class="material-icons" style="font-size:18px;color:var(--text-muted,#94A3B8);">chevron_right</span>
    </div>
  `).join('');

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-vehicle-list-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;max-height:80dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">local_shipping</span>Vehicles</h3>
        <button class="btn-icon" onclick="document.getElementById('dispatch-vehicle-list-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:0;">
        ${truckCards ? `<div style="padding:10px 16px 4px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);">Trucks</div>${truckCards}` : ''}
        ${trailerCards ? `<div style="padding:10px 16px 4px;font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);">Trailers</div>${trailerCards}` : ''}
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function showDriverVehicleInfo(type, id) {
  // TASK-379 — same fleet resolution as openDriverVehicleList so PIN drivers
  // (DispatchState empty) resolve the vehicle from the shared store.
  const { trucks, trailers } = _resolveDriverFleet();
  const item = type === 'trailer'
    ? trailers.find(t => t.id === id)
    : trucks.find(t => t.id === id);
  if (!item) return;

  const escH = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const getUnit = (v) => window.getUnitDisplay ? window.getUnitDisplay(v) : (v.unit || v.name || 'Unknown');
  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = d.toDate ? d.toDate() : new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const row = (label, val) => val ? `<div style="display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid var(--border-color,#e2e8f0);"><span style="color:var(--text-secondary);font-size:0.85rem;">${label}</span><span style="font-weight:600;font-size:0.85rem;">${escH(String(val))}</span></div>` : '';
  // TASK-9855 — SDK read on the doc's path; driver sessions go through the
  // job-day-scoped 'driverVehicle' door (storage.rules deny them truck-docs /
  // trailer-docs).
  const _vDoor = { door: 'driverVehicle', doorArgs: { vtype: type === 'trailer' ? 'trailer' : 'truck', vid: String(id) } };
  const docLinks = (docs) => {
    if (!docs?.length) return '';
    return docs.map(d => `<a ${window.StorageFile.linkAttrs(d, _vDoor)} style="display:flex;align-items:center;gap:6px;padding:8px 0;border-bottom:1px solid var(--border-color,#e2e8f0);color:var(--brand-blue);font-size:0.85rem;font-weight:500;text-decoration:none;">
      <span class="material-icons" style="font-size:16px;">${(d.name || '').toLowerCase().endsWith('.pdf') ? 'picture_as_pdf' : 'image'}</span>
      ${escH(d.name || 'Document')}
      <span class="material-icons" style="font-size:14px;margin-left:auto;">open_in_new</span>
    </a>`).join('');
  };
  const colorDisplay = (c) => {
    if (!c) return null;
    // If it's a hex code, show a swatch instead of the raw code
    if (c.startsWith('#')) return null;
    return c;
  };

  let html = `
    <div style="padding:16px;">
      <div style="text-align:center;margin-bottom:16px;">
        ${item.photoUrl
          ? `<img src="${escH(item.photoUrl)}" alt="${escH(getUnit(item))}" loading="lazy" style="width:100%;max-width:280px;aspect-ratio:4/3;object-fit:cover;border-radius:10px;background:#0F172A;display:block;margin:0 auto;">`
          : `<span class="material-icons" style="font-size:40px;color:${type === 'trailer' ? 'var(--accent-teal)' : 'var(--brand-blue)'};">${type === 'trailer' ? 'rv_hookup' : 'local_shipping'}</span>`}
        <h3 style="margin:8px 0 0;font-size:1.2rem;">${escH(getUnit(item))}</h3>
        <p style="margin:4px 0 0;color:var(--text-secondary);font-size:0.85rem;">${escH(item.type || (type === 'trailer' ? 'Trailer' : 'Vehicle'))}</p>
      </div>
  `;

  // Registration & DOT info
  html += '<div style="margin-bottom:16px;">';
  html += row('VIN', item.vin);
  html += row('License Plate', item.licensePlate || item.plate);
  html += row('Make', item.make);
  html += row('Model', item.model);
  html += row('Year', item.year);
  html += row('Color', colorDisplay(item.color));
  html += '</div>';

  // Registration
  if (item.registrationExpiry || item.registrationNoExpiry === true || item.registrationState || (item.registrationDocs || []).length > 0) {
    html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);margin-bottom:6px;">Registration</div><div style="margin-bottom:16px;">`;
    html += row('State', item.registrationState);
    html += row('Expires', item.registrationNoExpiry === true ? 'Doesn’t expire' : fmtDate(item.registrationExpiry)); // TASK-9814 follow-up
    html += docLinks(item.registrationDocs);
    html += '</div>';
  }

  // Insurance
  if (item.insuranceExpiry || item.insuranceProvider || (item.insuranceDocs || []).length > 0) {
    html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);margin-bottom:6px;">Insurance</div><div style="margin-bottom:16px;">`;
    html += row('Provider', item.insuranceProvider);
    html += row('Policy #', item.insurancePolicyNumber || item.policyNumber);
    html += row('Expires', fmtDate(item.insuranceExpiry));
    html += docLinks(item.insuranceDocs);
    html += '</div>';
  }

  // Inspection
  if (item.lastInspectionDate || item.annualInspectionDate || (item.inspectionDocs || []).length > 0) {
    html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);margin-bottom:6px;">Inspection</div><div style="margin-bottom:16px;">`;
    html += row('Annual Inspection', fmtDate(item.annualInspectionDate || item.lastInspectionDate));
    html += row('Next Due', fmtDate(item.nextInspectionDate));
    html += docLinks(item.inspectionDocs);
    html += '</div>';
  }

  // Custom documents flagged for the job board (annual permits etc.) — parity
  // with the other two truck cards (dispatch.js:4848, jobboard.js:1885). The
  // showOnJobBoard flag is the explicit opt-in set in Roster. TASK-4615.
  const boardDocs = (item.documents || []).filter(d => d && d.showOnJobBoard);
  boardDocs.forEach(d => {
    html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);margin-bottom:6px;">${escH(d.label || d.type || 'Document')}</div><div style="margin-bottom:16px;">`;
    html += row('Expires', d.expiresAt ? fmtDate(d.expiresAt) : null);
    html += docLinks([d]);
    html += '</div>';
  });

  // Weight ratings
  if (item.gvwr || item.gcwr) {
    html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-secondary);margin-bottom:6px;">Weight Ratings</div><div style="margin-bottom:16px;">`;
    html += row('GVWR', item.gvwr ? item.gvwr.toLocaleString() + ' lbs' : null);
    html += row('GCWR', item.gcwr ? item.gcwr.toLocaleString() + ' lbs' : null);
    html += '</div>';
  }

  // Driver-PIN inventory action — opens the slim count + purchase modal (TASK-197)
  if (item.inventoryEnabled && type !== 'trailer') {
    // Stash the resolved truck on window so jobboard's showDriverInventory can use it
    // without needing access to module-scoped DispatchState.
    window._dispatchInventoryTruck = item;
    html += `
      <div style="margin-top:8px;padding-top:16px;border-top:1px solid var(--border-color,#e2e8f0);">
        <button class="btn btn-primary" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;" onclick="document.getElementById('dispatch-vehicle-detail-modal')?.remove(); window.JobBoardModule?.showDriverInventory?.(window._dispatchInventoryTruck)">
          <span class="material-icons">inventory_2</span> Take Inventory
        </button>
      </div>
    `;
  }

  html += '</div>';

  // Remove vehicle list modal, show detail
  document.getElementById('dispatch-vehicle-list-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-vehicle-detail-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;max-height:80dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">${type === 'trailer' ? 'rv_hookup' : 'local_shipping'}</span>${escH(getUnit(item))}</h3>
        <button class="btn-icon" onclick="document.getElementById('dispatch-vehicle-detail-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      ${html}
    </div>
  `;
  document.body.appendChild(modal);
}

// ============================================
// CITATION / VEHICLE EXAM REPORT UPLOAD
// ============================================

let _citationPhotos = [];

function openCitationUpload() {
  _citationPhotos = [];
  const driverName = window.AppState?.driverPersonName || '';
  const driverId = window.AppState?.driverPersonId || '';
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Try to find driver's assigned truck from today's jobs
  let autoVehicleId = '';
  let autoVehicleUnit = '';
  const todayKey = todayStr;
  for (const jd of DispatchState.jobDays) {
    const jdDate = jd.date || '';
    if (jdDate !== todayKey) continue;
    const trucks = jd.trucks || [];
    if (trucks.length > 0) {
      const truckRef = trucks[0];
      const truck = _resolveFleetRef(truckRef, _resolveDriverFleet().trucks);
      if (truck) {
        autoVehicleId = truck.id;
        autoVehicleUnit = window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name || '');
      }
      break;
    }
  }

  // Build vehicle options. TASK-587 — fall back to the AppState.fleetTrucks
  // bridge (published by jobboard's trucks subscription, TASK-379 pattern):
  // PIN drivers land on /jobboard and never run dispatch's subscribeToData,
  // so DispatchState.trucks is empty for them on BOTH the Job Board and the
  // new persistent off-board nav — the dropdown rendered empty.
  const _citTrucks = _resolveDriverFleet().trucks; // P0 1019 — current tenant only
  const vehicleOptions = _citTrucks.map(t => {
    const unit = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.unit || t.name || t.id);
    return `<option value="${t.id}" ${t.id === autoVehicleId ? 'selected' : ''}>${escapeHtml(unit)}</option>`;
  }).join('');

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'dispatch-citation-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:500px;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header" style="background:linear-gradient(135deg,#DC2626,#991B1B);color:#fff;">
        <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">gavel</span> Vehicle Exam Report
        </h3>
        <button class="btn-icon" onclick="document.getElementById('dispatch-citation-modal')?.remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 16px;">Got pulled over for an inspection? Snap a photo of the report and fill in the details below.</p>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Report Number</label>
          <input type="text" id="citation-report-number" class="form-control" placeholder="From the inspection form">
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;">
          <div class="form-group" style="margin-bottom:0;">
            <label class="form-label">Inspection Date</label>
            <input type="date" id="citation-date" class="form-control" value="${todayStr}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label class="form-label">Level</label>
            <select id="citation-level" class="form-control">
              <option value="">Select...</option>
              <option value="I">Level I — Full</option>
              <option value="II">Level II — Walk-Around</option>
              <option value="III">Level III — Driver Only</option>
              <option value="IV">Level IV — Special</option>
              <option value="V">Level V — Vehicle Only</option>
            </select>
          </div>
        </div>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Location</label>
          <input type="text" id="citation-location" class="form-control" placeholder="City, State or highway">
        </div>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Vehicle</label>
          <select id="citation-vehicle" class="form-control">
            <option value="">Select vehicle...</option>
            ${vehicleOptions}
          </select>
        </div>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Violations / Description</label>
          <textarea id="citation-description" class="form-control" rows="3" placeholder="Describe violations found (the photo is the source of truth)"></textarea>
        </div>

        <!-- OOS Checkboxes -->
        <div style="display:flex;gap:16px;margin-bottom:16px;padding:12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:10px;">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.85rem;font-weight:600;color:#991B1B;">
            <input type="checkbox" id="citation-vehicle-oos"> Vehicle Out of Service
          </label>
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.85rem;font-weight:600;color:#991B1B;">
            <input type="checkbox" id="citation-driver-oos"> Driver Out of Service
          </label>
        </div>

        <!-- Photo Upload -->
        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Report Photos</label>
          <label style="display:flex;align-items:center;justify-content:center;gap:8px;padding:14px;background:var(--bg-secondary,#F1F5F9);border:2px dashed var(--border-light,#CBD5E1);border-radius:10px;cursor:pointer;font-size:0.85rem;font-weight:600;color:var(--brand-blue,#3B82F6);">
            <span class="material-icons">photo_camera</span> Take Photo or Choose File
            <input type="file" id="citation-photo-input" accept="image/*,.heic,.heif,application/pdf" multiple style="display:none;" onchange="window.DispatchModule.onCitationPhotos(this)">
          </label>
          <div id="citation-photo-previews" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:8px;"></div>
        </div>

        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Notes</label>
          <input type="text" id="citation-notes" class="form-control" placeholder="Optional notes">
        </div>

        <button id="citation-submit-btn" class="btn btn-primary" style="width:100%;padding:12px;font-size:1rem;font-weight:600;background:linear-gradient(135deg,#DC2626,#991B1B);border:none;border-radius:10px;color:#fff;" onclick="window.DispatchModule.submitCitation()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">upload_file</span> Submit Report
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function onCitationPhotos(input) {
  const files = Array.from(input.files || []);
  const previewDiv = document.getElementById('citation-photo-previews');
  if (!previewDiv) return;

  files.forEach(file => {
    _citationPhotos.push(file);
    if (file.type.startsWith('image/')) {
      // TASK-1383 — guarded shared reader; preview is best-effort (the file
      // is already in _citationPhotos above, so it still uploads on submit).
      window.FileReadUtil.readAsDataURL(file).then((dataUrl) => {
        const img = document.createElement('img');
        img.src = dataUrl;
        img.style.cssText = 'width:60px;height:60px;object-fit:cover;border-radius:8px;border:1px solid var(--border-light);';
        previewDiv.appendChild(img);
      }).catch(() => { /* thumbnail only — the photo still submits */ });
    } else {
      const badge = document.createElement('div');
      badge.style.cssText = 'width:60px;height:60px;display:flex;align-items:center;justify-content:center;background:#F1F5F9;border-radius:8px;border:1px solid var(--border-light);';
      badge.innerHTML = '<span class="material-icons" style="color:var(--brand-blue);">picture_as_pdf</span>';
      previewDiv.appendChild(badge);
    }
  });
}

/**
 * TASK-9397 COD — plain-English note for photos that did not attach to a saved
 * exam report. `skipped` = [{ name, reason }]. null when nothing was skipped.
 */
function citationSkippedPhotosMessage(skipped) {
  if (!Array.isArray(skipped) || skipped.length === 0) return null;
  const n = skipped.length;
  const lines = skipped.map(sk => `${sk.name || 'photo'}: ${sk.reason}`).join(' · ');
  return `Report saved, but ${n} photo${n > 1 ? 's' : ''} didn't attach — ${lines}`;
}

/**
 * TASK-9397 COD — convert + upload an exam report's photos AFTER the report
 * exists. Never throws: each photo that can't be converted or uploaded is
 * skipped and returned as { name, reason }. Whatever did upload is written
 * to the report's photoUrls.
 */
async function _attachCitationPhotos(tenant, citationId, files) {
  const skipped = [];
  if (!files || files.length === 0) return skipped;
  const photoUrls = [];
  const uploadedNames = [];
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');

  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const name = String(f?.name || `photo ${i + 1}`);
    let file = f;
    try {
      if (await detectHeic(f)) {
        const drawable = await toDrawablePhoto(f);
        const jpeg = await processPhoto(drawable, { maxDimension: 2400, quality: 0.85, maxSizeKB: 3000 });
        const jpgName = name.replace(/\.[^.]*$/, '') + '.jpg';
        file = new File([jpeg], jpgName, { type: 'image/jpeg' });
      }
    } catch (err) {
      console.warn('[Dispatch] Citation photo conversion failed:', name, err?.cause || err);
      skipped.push({ name, reason: photoErrorMessage(err, "Couldn't read this photo. Pick a JPG or PNG instead.") });
      continue;
    }
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `tenants/${tenant}/photos/citations/${year}/${month}/${citationId}_${i}.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      photoUrls.push({ id: `${citationId}_${i}`, url, path, uploadedAt: new Date().toISOString() });
      uploadedNames.push(name);
    } catch (err) {
      console.warn('[Dispatch] Citation photo upload failed:', name, err);
      skipped.push({ name, reason: 'Upload failed (check your signal).' });
    }
  }

  if (photoUrls.length > 0) {
    try {
      await updateDoc(doc(db, 'tenants', tenant, 'citations', citationId), { photoUrls });
    } catch (err) {
      console.error('[Dispatch] Citation photoUrls update failed:', err);
      for (const nm of uploadedNames) skipped.push({ name: nm, reason: "Uploaded but couldn't be linked to the report." });
    }
  }
  return skipped;
}

async function submitCitation() {
  const reportNumber = document.getElementById('citation-report-number')?.value?.trim() || '';
  const inspectionDate = document.getElementById('citation-date')?.value || '';
  const level = document.getElementById('citation-level')?.value || '';
  const location = document.getElementById('citation-location')?.value?.trim() || '';
  const vehicleId = document.getElementById('citation-vehicle')?.value || '';
  const description = document.getElementById('citation-description')?.value?.trim() || '';
  const vehicleOOS = document.getElementById('citation-vehicle-oos')?.checked || false;
  const driverOOS = document.getElementById('citation-driver-oos')?.checked || false;
  const notes = document.getElementById('citation-notes')?.value?.trim() || '';

  if (!inspectionDate) {
    window.showToast?.('Inspection date is required', 'error');
    return;
  }

  const btn = document.getElementById('citation-submit-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Submitting...';
  }

  try {
    // TASK-587 — DispatchState.tenantId is only set by initDispatch; PIN
    // drivers (canonical /jobboard landing) never run it, so the write went
    // to tenants/undefined and failed. Fall back to the session tenant.
    const tenant = window.AppState?.tenant || DispatchState.tenantId; // P0 1019 — the session's tenant wins over board state
    if (!tenant) throw new Error('No tenant in session');
    const driverId = window.AppState?.driverPersonId || '';
    const driverName = window.AppState?.driverPersonName || '';

    // Get vehicle unit for denormalization (fleet-bridge fallback — see
    // openCitationUpload vehicle options, TASK-587).
    let vehicleUnit = '';
    if (vehicleId) {
      const _subTrucks = _resolveDriverFleet().trucks; // P0 1019 — current tenant only
      const truck = _subTrucks.find(t => t.id === vehicleId);
      vehicleUnit = truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : (truck.unit || truck.name || '')) : '';
    }

    // Calculate repair deadline (15 days from inspection)
    const inspDate = new Date(inspectionDate + 'T00:00:00');
    const deadline = new Date(inspDate.getTime() + 15 * 24 * 60 * 60 * 1000);
    const repairDeadline = `${deadline.getFullYear()}-${String(deadline.getMonth() + 1).padStart(2, '0')}-${String(deadline.getDate()).padStart(2, '0')}`;

    // Create citation document
    const citationData = {
      reportNumber,
      inspectionDate,
      inspectionLevel: level,
      location,
      vehicleId,
      vehicleUnit,
      driverId,
      driverName,
      description,
      vehicleOOS,
      driverOOS,
      photoUrls: [],
      repairDeadline,
      repairStatus: 'pending',
      repairCompletedAt: null,
      certificationReturned: false,
      notes,
      createdAt: Timestamp.now(),
      createdBy: driverName || 'Driver',
      updatedAt: Timestamp.now()
    };

    // TASK-9397 COD — the report is a compliance record: it saves FIRST, and
    // no photo can stop it. Photos are best-effort after that — one that
    // can't be converted (bad/unsupported HEIC) or uploaded is skipped, and
    // the driver is told which one and why. A raw HEIC is still never stored
    // (unviewable off Safari); non-HEIC files upload untouched, as before.
    const citRef = await addDoc(collection(db, 'tenants', tenant, 'citations'), citationData);
    const photos = _citationPhotos.slice();
    _citationPhotos = [];
    const skipped = await _attachCitationPhotos(tenant, citRef.id, photos);

    document.getElementById('dispatch-citation-modal')?.remove();
    const skippedMsg = citationSkippedPhotosMessage(skipped);
    // Sticky (Infinity) — the driver must see which photo is missing, not
    // lose it to a timer; they close it with the ✕.
    if (skippedMsg) window.showToast?.(skippedMsg, 'warning', null, Infinity);
    else window.showToast?.('Exam report submitted', 'success');
  } catch (err) {
    // Only reached when the report itself did not save (photos never throw).
    console.error('[Dispatch] Citation submit error:', err?.cause || err);
    window.showToast?.('Error submitting report — nothing was saved. Try again.', 'error');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:20px;">upload_file</span> Submit Report';
    }
  }
}

// ============================================
// DOT COMPLIANCE FLAGGING ENGINE
// ============================================

/**
 * Parse CDL endorsements from mixed formats (string "H, N, T" or array ["H","N"])
 * Returns a Set of single uppercase letters.
 */
function parseEndorsements(val) {
  if (!val) return new Set();
  if (Array.isArray(val)) return new Set(val.map(e => (typeof e === 'string' ? e : e.id || '').trim().toUpperCase()).filter(Boolean));
  return new Set(val.split(/[,\s]+/).map(s => s.trim().toUpperCase()).filter(Boolean));
}

/**
 * TASK-9430 — CDL restriction codes as an UPPERCASE array, whatever the stored shape.
 * Since TASK-9300 (5) the roster, carrier Compliance and partner portal all SAVE
 * `cdlRestrictions` as the typed string ("L, Z"); older records and imports carry an
 * array. checkDOTCompliance read it as an array only, so a string silently dropped
 * the K (intrastate-only) and L (no air brakes) warnings. Tokens split on anything
 * that is not a letter/digit, so "K - intrastate only" still yields K; a run of
 * letters is never split into codes ("NONE" must not read as N, O, N, E).
 */
function parseCdlRestrictions(val) {
  if (!val) return [];
  const parts = Array.isArray(val) ? val : String(val).split(/[^A-Za-z0-9]+/);
  return parts.map(r => (typeof r === 'string' ? r : (r && (r.code || r.id)) || '').trim().toUpperCase()).filter(Boolean);
}

/**
 * CDL class hierarchy: A > B > C > D (Regular). Returns true if held >= required.
 */
function cdlClassMeetsRequirement(held, required) {
  const rank = { 'A': 4, 'B': 3, 'C': 2, 'D': 1, 'REGULAR': 1, 'NONE': 0, '': 0 };
  const heldRank = rank[(held || '').toUpperCase()] || 0;
  const reqRank = rank[(required || '').toUpperCase()] || 0;
  return heldRank >= reqRank;
}

/**
 * Map truck type slug to DOT vehicle category.
 */
function getVehicleCategory(truckType) {
  const t = (truckType || '').toLowerCase().replace(/-/g, '_');
  const tractorTypes = ['semi', 'vac', 'vac_truck', 'winch_truck', 'bed_truck', 'water_truck'];
  if (tractorTypes.includes(t)) return 'Truck-Tractor';
  return 'Straight Truck';
}

/**
 * Determine GCWR weight bracket from a truck's weight fields.
 */
function getWeightBracket(truck) {
  const weight = truck.gcwr || truck.gvwr || 0;
  if (weight <= 0) return null; // Missing data
  if (weight <= 10000) return 'under10k';
  if (weight <= 26000) return '10k-26k';
  return 'over26k';
}

/**
 * Core compliance matrix lookup. Given scenario inputs, returns requirements.
 * Implements the decision tree from John Bruch's License & Endorsements Matrix.
 */
// TASK-9370 — which DOT flags on a job are UNKNOWN (null) rather than false.
// A driver-created job is born with all three null + needsDispatchReview; legacy
// jobs with the key simply absent keep today's "absent = false" meaning.
function lookupComplianceUnknownFlags(job) {
  if (!job) return [];
  const labels = { interstate: 'Interstate', hazmat: 'Hazmat', liquidTank: 'Liquid Tank' };
  return Object.keys(labels).filter(k => job[k] === null).map(k => labels[k]);
}

function lookupComplianceRequirements({ weightBracket, vehicleCategory, pullingTrailer, trailerOver10k, hazmat, liquidTank, interstate }) {
  let requiredClass = 'D';
  let requiredMedCard = false;
  let requiredDQFile = false;
  const requiredEndorsements = new Set();

  // Hazmat always requires H endorsement + med card + DQ file
  if (hazmat) {
    requiredEndorsements.add('H');
    requiredMedCard = true;
    requiredDQFile = true;
  }

  // Liquid tank >1000 gal requires N endorsement
  if (liquidTank) {
    requiredEndorsements.add('N');
  }

  if (weightBracket === 'under10k') {
    // Under 10,001 lbs — Regular/Class D unless hazmat
    requiredClass = hazmat ? 'C' : 'D';
    // No med card or DQ file unless hazmat (already set above)
  } else if (weightBracket === '10k-26k') {
    // 10,001 - 26,000 lbs
    if (liquidTank && hazmat) {
      requiredClass = 'B';
    } else if (liquidTank) {
      requiredClass = pullingTrailer ? 'B' : 'D';
    } else if (hazmat) {
      requiredClass = 'C';
    } else {
      requiredClass = 'D';
    }
    // Interstate requires med card + DQ file for any CMV >10k
    if (interstate) {
      requiredMedCard = true;
      requiredDQFile = true;
    }
  } else if (weightBracket === 'over26k') {
    // Over 26,001 lbs — always CDL required
    requiredMedCard = true;
    requiredDQFile = true;
    if (pullingTrailer && trailerOver10k) {
      requiredClass = 'A';
    } else {
      requiredClass = 'B';
    }
  }

  return { requiredClass, requiredMedCard, requiredDQFile, requiredEndorsements };
}

/**
 * Run compliance check for all selected driver/truck pairs.
 * Returns array of warning objects. Warnings are consolidated by truck:
 * one entry per (truck, requirement) listing all affected drivers, rather
 * than one per (driver, truck) pair — keeps the assignment modal readable
 * when a crew has many drivers without complete CDL data on file.
 */
function checkDOTCompliance(driverIds, truckIds, trailerIds, job, assetIds) {
  const warnings = [];
  const people = DispatchState.people || [];
  const trucks = DispatchState.trucks || [];
  const trailers = DispatchState.trailers || [];
  const assets = DispatchState.assets || [];

  const _escH = (s) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const today = new Date().toISOString().split('T')[0];
  const fmtDrivers = (arr) => {
    if (arr.length === 0) return '';
    if (arr.length === 1) return arr[0];
    if (arr.length === 2) return `${arr[0]} and ${arr[1]}`;
    if (arr.length <= 4) return `${arr.slice(0, -1).join(', ')}, and ${arr[arr.length - 1]}`;
    return `${arr.slice(0, 3).join(', ')}, and ${arr.length - 3} more`;
  };

  const selectedDrivers = driverIds.map(id => people.find(p => p.id === id)).filter(Boolean);
  const selectedTrucks = truckIds.map(id => trucks.find(t => t.id === id)).filter(Boolean);
  const selectedTrailers = trailerIds.map(id => trailers.find(t => t.id === id)).filter(Boolean);
  const selectedAssets = (assetIds || []).map(id => assets.find(a => a.id === id)).filter(Boolean);

  // TASK-9198 — resolve every open critical hold to the ONE fleet doc it points
  // at, ONCE, before the per-item loops. Keyed by fleet doc id, so the truck,
  // trailer and asset lanes below all read the same index and a hold can only
  // land on one item. See resolveSafetyHoldTarget for why assetType is ignored
  // and why doc-id refs are exhausted before unit strings.
  const safetyHoldByFleetId = new Map();
  for (const hold of (DispatchState.safetyHolds || [])) {
    const target = resolveSafetyHoldTarget(hold, { truck: trucks, trailer: trailers, asset: assets });
    if (target && !safetyHoldByFleetId.has(target.item.id)) {
      safetyHoldByFleetId.set(target.item.id, hold);
    }
  }
  // TASK-462's push-time escape, kept verbatim: any dispatcher, mechanic or
  // driver PIN session can write a maintenance doc, so `description` is
  // untrusted and a crafted <img onerror> would otherwise run in the assigner's
  // session. severity:'safety' is also verbatim — it is what bypasses the tenant
  // hideComplianceWarnings opt-out AND what keeps this out of any error-severity
  // hard-stop. These warnings BLOCK NOTHING: renderComplianceWarnings paints a
  // dismissible inline banner in the assignment/job modal and the hard-stop path
  // (_hsCollectExpiredForAssignment) never reads them, so the dispatcher always
  // keeps the operational call.
  const pushSafetyHold = (item, label, idField, subject) => {
    const hold = safetyHoldByFleetId.get(item.id);
    if (!hold) return;
    const holdDesc = _escH(truncateText(hold.description || hold.title || 'Critical defect', 60));
    warnings.push({
      severity: 'safety', icon: 'block',
      message: `<strong>Safety Hold</strong> — ${label} has an open critical DVIR safety hold: ${holdDesc}. ${subject || 'Vehicle'} should not be dispatched until cleared.`,
      [idField]: item.id
    });
  };

  const pullingTrailer = selectedTrailers.length > 0;
  const trailerOver10k = selectedTrailers.some(t => (t.gvwr || 0) > 10001);
  // TASK-9370 (COD Domain) — a driver-created job carries null here until a
  // dispatcher confirms the flags. null is UNKNOWN, not "no": say so once, up
  // front, so nobody reads a clean CDL check as a clean bill.
  const _dotUnknown = lookupComplianceUnknownFlags(job);
  if (_dotUnknown.length && (selectedDrivers.length || selectedTrucks.length)) {
    warnings.push({
      severity: 'warning', icon: 'help',
      message: `<strong>DOT flags not confirmed</strong> — this job was created from the driver board and nobody has set ${_dotUnknown.join(', ')} yet. The CDL, endorsement and med-card checks below cannot account for them. Open Edit Job and confirm.`
    });
  }
  const hazmat = job?.hazmat || false;
  const liquidTank = job?.liquidTank || false;
  const interstate = job?.interstate || false;

  // Pre-compute per-driver derived fields so we can group across trucks.
  const driverInfo = selectedDrivers.map(driver => {
    const heldClass = (driver.cdlClass || '').toUpperCase();
    return {
      driver,
      name: _escH(driver.name || 'Unknown'),
      heldClass,
      heldEndorsements: parseEndorsements(driver.cdlEndorsements),
      cdlRestrictions: parseCdlRestrictions(driver.cdlRestrictions), // TASK-9430 — string or array
      hasCdlData: heldClass && heldClass !== 'NONE' && heldClass !== '',
      hasMedCard: !!driver.medCardExpiryDate
    };
  });

  // ── PER-TRUCK GROUPED WARNINGS ──────────────────────────────────────
  for (const truck of selectedTrucks) {
    const truckLabel = _escH(truck.unit || truck.nickname || truck.id);
    const weightBracket = getWeightBracket(truck);

    // TASK-462 — open critical DVIR safety hold on this vehicle. Surface a soft,
    // non-blocking advisory at the assignment moment (the real FMCSA §396.9 guard;
    // calendar visibility was never the safety net). severity:'safety' so it (a) is
    // NOT caught by any error-severity hard-stop and (b) bypasses the tenant
    // hideComplianceWarnings opt-out (a critical defect ≠ a CDL nag — see
    // renderComplianceWarnings). TASK-9198 — the inline matcher that used to live
    // here compared holds against SELECTED TRUCKS ONLY, so the guard did not exist
    // for trailers or assets; it now reads the shared safetyHoldByFleetId index
    // built above, which covers all three fleets with the same ref precedence.
    pushSafetyHold(truck, truckLabel, 'truckId');

    if (!weightBracket) {
      warnings.push({
        severity: 'info', icon: 'info',
        message: `<strong>Missing weight data</strong> — ${truckLabel} has no GCWR/GVWR entered. DOT compliance check incomplete.`,
        truckId: truck.id
      });
      continue;
    }

    const vehicleCategory = getVehicleCategory(truck.type);
    const reqs = lookupComplianceRequirements({ weightBracket, vehicleCategory, pullingTrailer, trailerOver10k, hazmat, liquidTank, interstate });
    const truckWeightStr = `${(truck.gcwr || truck.gvwr || 0).toLocaleString()} lbs${pullingTrailer && trailerOver10k ? ' + trailer >10k' : ''}`;

    // Buckets per truck (only emit if non-empty)
    const noCdl = [];
    const classMismatch = []; // entries: {name, heldClass}
    const missingEndorsement = {}; // { 'H': [names], 'N': [names], 'X': [names] }
    const noMedCard = [];
    const expiredMedCard = []; // entries: {name, date}

    for (const info of driverInfo) {
      if (!info.hasCdlData && reqs.requiredClass !== 'D') {
        noCdl.push(info.name);
        continue; // skip class check when no CDL data at all
      }
      if (!cdlClassMeetsRequirement(info.heldClass, reqs.requiredClass)) {
        classMismatch.push({ name: info.name, heldClass: info.heldClass || 'D' });
      }
      for (const reqE of reqs.requiredEndorsements) {
        const hasIt = info.heldEndorsements.has(reqE)
          || (reqE === 'H' && info.heldEndorsements.has('X'))
          || (reqE === 'N' && info.heldEndorsements.has('X'));
        if (!hasIt) {
          (missingEndorsement[reqE] = missingEndorsement[reqE] || []).push(info.name);
        }
      }
      if (reqs.requiredMedCard) {
        if (!info.hasMedCard) {
          noMedCard.push(info.name);
        } else if (info.driver.medCardExpiryDate < today) {
          expiredMedCard.push({ name: info.name, date: info.driver.medCardExpiryDate });
        }
      }
    }

    if (noCdl.length) {
      warnings.push({
        severity: 'error', icon: 'gpp_bad',
        message: `<strong>No CDL on file</strong> — ${truckLabel} (${truckWeightStr}) requires Class ${reqs.requiredClass}. ${noCdl.length} ${noCdl.length === 1 ? 'driver has' : 'drivers have'} no CDL info: ${fmtDrivers(noCdl)}.`,
        truckId: truck.id
      });
    }
    if (classMismatch.length) {
      const classBreakdown = {};
      classMismatch.forEach(e => { (classBreakdown[e.heldClass] = classBreakdown[e.heldClass] || []).push(e.name); });
      const breakdownStr = Object.entries(classBreakdown)
        .map(([cls, names]) => `${fmtDrivers(names)} (Class ${escapeHtml(cls)})`)
        .join('; ');
      warnings.push({
        severity: 'error', icon: 'gpp_bad',
        message: `<strong>CDL class mismatch</strong> — ${truckLabel} (${truckWeightStr}) requires Class ${reqs.requiredClass}. ${classMismatch.length} ${classMismatch.length === 1 ? 'driver holds' : 'drivers hold'} a lower class: ${breakdownStr}.`,
        truckId: truck.id
      });
    }
    const endorsementNames = { H: 'Hazmat (H)', N: 'Tank (N)', X: 'Hazmat+Tank (X)' };
    for (const [reqE, names] of Object.entries(missingEndorsement)) {
      warnings.push({
        severity: 'error', icon: 'gpp_bad',
        message: `<strong>Missing ${endorsementNames[reqE] || reqE} endorsement</strong> — required for ${truckLabel}. ${names.length} ${names.length === 1 ? 'driver lacks' : 'drivers lack'} it: ${fmtDrivers(names)}.`,
        truckId: truck.id
      });
    }
    if (noMedCard.length) {
      warnings.push({
        severity: 'warning', icon: 'gpp_maybe',
        message: `<strong>No med card on file</strong> — required for ${truckLabel}. ${noMedCard.length} ${noMedCard.length === 1 ? 'driver has' : 'drivers have'} no medical card expiry: ${fmtDrivers(noMedCard)}.`,
        truckId: truck.id
      });
    }
    if (expiredMedCard.length) {
      const breakdown = expiredMedCard.map(e => `${e.name} (exp ${escapeHtml(e.date)})`);
      warnings.push({
        severity: 'error', icon: 'gpp_bad',
        message: `<strong>Med card expired</strong> — required for ${truckLabel}. ${expiredMedCard.length} ${expiredMedCard.length === 1 ? 'driver has' : 'drivers have'} an expired med card: ${fmtDrivers(breakdown)}.`,
        truckId: truck.id
      });
    }
  }

  // ── PER-TRAILER / PER-ASSET SAFETY HOLDS ────────────────────────────────
  // TASK-9198 — the §396.9 guard TASK-462 built existed for trucks only. A
  // trailer under an open critical DVIR hold (live TITS: trailer 109, spider
  // valve stuck open dumping air) warned NOBODY at assignment. Holds are the
  // ONLY check that runs here: a trailer/asset carries no CDL, med card or
  // endorsement of its own, so nothing else from the truck loop applies.
  for (const trailer of selectedTrailers) {
    pushSafetyHold(trailer, `Trailer ${_escH(trailer.unit || trailer.nickname || trailer.id)}`, 'trailerId', 'Trailer');
  }
  for (const asset of selectedAssets) {
    // Assets on this codebase carry NO unit and NO nickname — only `name`
    // (verified on live TITS: 4/4). Hence the name-first order here.
    pushSafetyHold(asset, `Asset ${_escH(asset.name || asset.unit || asset.nickname || asset.id)}`, 'assetId', 'Equipment');
  }

  // ── PER-DRIVER WARNINGS (independent of which truck they're paired with) ──
  for (const info of driverInfo) {
    // Expired CDL — one per driver
    if (info.heldClass && info.heldClass !== 'D' && info.driver.cdlExpiryDate && info.driver.cdlExpiryDate < today) {
      warnings.push({
        severity: 'error', icon: 'gpp_bad',
        message: `<strong>CDL expired</strong> — ${info.name}'s CDL expired ${escapeHtml(info.driver.cdlExpiryDate)}.`,
        driverId: info.driver.id
      });
    }
    // K restriction — once per driver if any interstate work
    if (interstate && info.cdlRestrictions.includes('K')) {
      warnings.push({
        severity: 'error', icon: 'block',
        message: `<strong>K restriction</strong> — ${info.name} has an intrastate-only restriction and cannot be dispatched on interstate jobs.`,
        driverId: info.driver.id
      });
    }
    // L restriction — once per driver if any truck is selected
    if (selectedTrucks.length > 0 && info.cdlRestrictions.includes('L')) {
      warnings.push({
        severity: 'info', icon: 'info',
        message: `<strong>L restriction</strong> — ${info.name} cannot operate air-brake vehicles. Verify selected truck brake types.`,
        driverId: info.driver.id
      });
    }
  }

  // ── INTERSTATE REMINDER — group by truck so one message per heavy truck ──
  if (!interstate) {
    for (const truck of selectedTrucks) {
      const weightBracket = getWeightBracket(truck);
      if (weightBracket !== '10k-26k' && weightBracket !== 'over26k') continue;
      const missing = driverInfo.filter(i => !i.hasMedCard).map(i => i.name);
      if (missing.length === 0) continue;
      const truckLabel = _escH(truck.unit || truck.nickname || truck.id);
      warnings.push({
        severity: 'info', icon: 'info',
        message: `<strong>Interstate reminder</strong> — ${truckLabel} is over 10,001 lbs. If this job crosses state lines, a med card and DQ file are required. ${missing.length} ${missing.length === 1 ? 'driver has' : 'drivers have'} no med card on file: ${fmtDrivers(missing)}.`,
        truckId: truck.id
      });
    }
  }

  // ── NO TRUCKS SELECTED — still flag expired CDLs ──
  if (selectedTrucks.length === 0) {
    for (const info of driverInfo) {
      if (info.driver.cdlExpiryDate && info.driver.cdlExpiryDate < today) {
        warnings.push({
          severity: 'warning', icon: 'gpp_maybe',
          message: `<strong>CDL expired</strong> — ${info.name}'s CDL expired ${escapeHtml(info.driver.cdlExpiryDate)}.`,
          driverId: info.driver.id, truckId: null
        });
      }
    }
  }

  return warnings;
}

/**
 * Render compliance warnings into a container element.
 */
function renderComplianceWarnings(warnings, containerId) {
  let container = document.getElementById(containerId);
  if (!container) return;
  // Tenant setting: hide DOT compliance warnings entirely (Split Rock 2026-05-13).
  // TASK-462: a critical DVIR safety hold (§396.9 liability) is categorically
  // different from a CDL/medcard nag — it must still show even when the tenant
  // opted out of compliance warnings. Keep only severity:'safety' in that case.
  if (window.AppState?.settings?.hideComplianceWarnings === true) {
    // TASK-9844 — crew-skill lines are their own opt-in (Roster → Skills), not a DOT nag.
    warnings = (warnings || []).filter(w => w.severity === 'safety' || w.kind === 'skill');
  }
  if (!warnings || warnings.length === 0) {
    container.innerHTML = '';
    return;
  }

  const severityClass = { safety: 'smart-warning--duplicate', error: 'smart-warning--duplicate', warning: 'smart-warning--similar', info: 'smart-warning--format' };

  container.innerHTML = warnings.map((w, idx) => `
    <div class="smart-warning ${severityClass[w.severity] || 'smart-warning--similar'}" style="margin-bottom:4px;" data-warn-severity="${w.severity || ''}" data-warn-idx="${idx}">
      <span class="material-icons smart-warning-icon">${w.icon}</span>
      <span class="smart-warning-text">${w.message}</span>
      <button class="smart-warning-dismiss" onclick="window.DispatchModule.dismissComplianceWarning(this, '${escJsAttr(containerId)}')" title="Dismiss">&times;</button>
    </div>
  `).join('');
}

// TASK-258 v1.15.535 — write COMPLIANCE_WARNING_DISMISSED audit on dismiss so
// we still have a forensic record when the hard-stop toggle is OFF (per scope:
// "When OFF: dismissible warning + audit log entry on dismissal so we still
// have forensic record"). Strips HTML tags from the warning text for the
// payload to keep audit rows readable.
function dismissComplianceWarning(btn, containerId) {
  try {
    const card = btn?.closest('.smart-warning');
    if (card) {
      const rawText = card.querySelector('.smart-warning-text')?.innerHTML || '';
      const plain = rawText.replace(/<[^>]+>/g, '').trim().slice(0, 240);
      const severity = card.getAttribute('data-warn-severity') || '';
      window.logAudit?.('compliance', 'COMPLIANCE_WARNING_DISMISSED', {
        source: 'dispatcher',
        containerId,
        severity,
        message: plain,
        hardStopEnabled: _hsIsHardStopEnabled(),
        at: new Date().toISOString()
      });
      card.remove();
    }
  } catch (err) {
    console.warn('[Dispatch] dismissComplianceWarning failed:', err);
    btn?.closest('.smart-warning')?.remove();
  }
}

// TASK-9844 — crew skills warning. The rule is window.CrewSkills (generated
// from functions/src/shared/crewSkills.js; Roster → Skills keeps the list and
// what each job type / vehicle type needs). One compact line per skill someone
// on the day lacks or has expired. ADVISORY ONLY: kind:'skill' warnings never
// reach the hard-stop path (_hsCollectExpiredForAssignment reads its own data),
// and they still show when the tenant hides DOT warnings — the company turned
// skills on deliberately. Nothing shows while the feature is off.
function _crewSkillWarnings(driverIds, helperIds, truckIds, jobType, asOfKey) {
  const CS = window.CrewSkills;
  if (!CS) return [];
  const cfg = CS.config(window.AppState?.settings || {});
  if (!CS.isOn(cfg)) return [];
  const people = DispatchState.people || [];
  const trucks = DispatchState.trucks || [];
  // Pitfall #5 — legacy days can hold a NAME instead of an id.
  const person = (ref) => people.find(p => p.id === ref) || people.find(p => p.name === ref) || null;
  const drivers = (driverIds || []).map(person).filter(Boolean);
  const helpers = (helperIds || []).map(person).filter(Boolean);
  if (!drivers.length && !helpers.length) return [];
  // Pitfall #5 dual lookup for units too (legacy days can hold a unit string).
  const truckOf = (ref) => trucks.find(t => t.id === ref) || trucks.find(t => ref && (t.unit === ref || t.nickname === ref || t.name === ref)) || null;
  const vehicles = (truckIds || []).map(truckOf).filter(Boolean).map(t => {
    const typeLabel = (t.type && window.getTypeLabel ? window.getTypeLabel(t.type, 'truckTypes') : '') || t.type || 'Vehicle';
    return { type: t.type || '', label: `${typeLabel} ${t.unit || t.nickname || ''}`.trim() };
  });
  const jt = jobType || 'job';
  // TASK-9911 M3 — a job / vehicle type can also need an HR certification
  // ('cert:<itemId>'). The training list is app.js's canonical
  // settings.safetyTrainings reader; whether a person holds one is HR's own
  // rule (hr.js computeRequiredTrainingState — position scope, exemption,
  // expiry date / recurrence). No HR rule loaded → the warning says the cert
  // could not be checked (never a silent pass).
  const certs = typeof CS.certList === 'function' ? CS.certList(window._stGetCurrent?.() || []) : [];
  const hrState = window.HRModule?.computeRequiredTrainingState;
  const certItems = new Map((window._stGetCurrent?.() || []).filter(i => i && i.itemId != null).map(i => [String(i.itemId), i]));
  const certState = typeof hrState === 'function'
    ? (p, itemId) => { const item = certItems.get(String(itemId)); return item ? hrState(p, item) : null; }
    : undefined;
  const issues = CS.checkCrew({
    config: cfg, jobType: jt, jobTypeLabel: _jobTypeLabel(jt),
    vehicles, drivers, helpers, asOf: asOfKey || formatDateISOLocal(new Date()),
    certs, certState,
  });
  const esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const join = (a) => (a.length <= 2 ? a.join(' and ') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
  return issues.map(i => {
    const why = i.neededFor.length ? ` Needed for ${esc(join(i.neededFor))}.` : '';
    if (i.unchecked) {
      return {
        severity: 'warning', icon: 'badge', kind: 'skill',
        message: `<strong>Could not check certification: ${esc(i.label)}</strong> — check the cards in HR before dispatching.${why}`,
      };
    }
    const parts = [];
    if (i.missing.length) parts.push(`${join(i.missing.map(esc))} ${i.missing.length === 1 ? 'does not have it' : 'do not have it'}`);
    if (i.expired.length) parts.push(`${join(i.expired.map(e => `${esc(e.name)}${e.expiresOn ? ` (expired ${esc(e.expiresOn)})` : ''}`))} ${i.expired.length === 1 ? 'has it expired' : 'have it expired'}`);
    return {
      severity: 'warning', icon: i.cert ? 'badge' : 'workspace_premium', kind: 'skill',
      message: `<strong>${i.cert ? 'Missing certification' : 'Missing skill'}: ${esc(i.label)}</strong> — ${parts.join('; ')}.${why}`,
    };
  });
}

function _assignmentDayKey(jobDayId) {
  const jd = (DispatchState.jobDays || []).find(d => d.id === jobDayId);
  const dt = jd?.date?.toDate ? jd.date.toDate() : (jd?.date ? new Date(jd.date) : null);
  return dt && !isNaN(dt.getTime()) ? formatDateISOLocal(dt) : null;
}

/**
 * Gather selected IDs and run compliance check for assignment modal.
 */
function runAssignmentComplianceCheck() {
  const driverContainer = document.getElementById('select-drivers');
  const truckContainer = document.getElementById('select-trucks');
  const trailerContainer = document.getElementById('select-trailers');
  const assetContainer = document.getElementById('select-assets'); // TASK-9198
  if (!driverContainer || !truckContainer) return;

  const driverIds = [...driverContainer.querySelectorAll('input:checked')].map(i => i.value);
  const truckIds = [...truckContainer.querySelectorAll('input:checked')].map(i => i.value);
  const trailerIds = [...(trailerContainer?.querySelectorAll('input:checked') || [])].map(i => i.value);
  const assetIds = [...(assetContainer?.querySelectorAll('input:checked') || [])].map(i => i.value);

  const jobId = document.getElementById('assignment-job-id')?.value;
  const job = DispatchState.jobs.find(j => j.id === jobId);

  const warnings = checkDOTCompliance(driverIds, truckIds, trailerIds, job, assetIds);
  // TASK-9844 — crew skills (advisory; empty unless the company turned it on).
  const helperIds = [...(document.getElementById('select-helpers')?.querySelectorAll('input:checked') || [])].map(i => i.value);
  const dayKey = _assignmentDayKey(document.getElementById('assignment-job-day-id')?.value);
  warnings.push(..._crewSkillWarnings(driverIds, helperIds, truckIds, job?.type, dayKey));
  renderComplianceWarnings(warnings, 'compliance-warnings-assignment');
}

/**
 * Gather selected IDs and run compliance check for job modal.
 */
function runJobComplianceCheck() {
  const driverContainer = document.getElementById('job-select-drivers');
  const truckContainer = document.getElementById('job-select-trucks');
  const trailerContainer = document.getElementById('job-select-trailers');
  const assetContainer = document.getElementById('job-select-assets'); // TASK-9198
  if (!driverContainer || !truckContainer) return;

  const driverIds = [...driverContainer.querySelectorAll('input:checked')].map(i => i.value);
  const truckIds = [...truckContainer.querySelectorAll('input:checked')].map(i => i.value);
  const trailerIds = [...(trailerContainer?.querySelectorAll('input:checked') || [])].map(i => i.value);
  const assetIds = [...(assetContainer?.querySelectorAll('input:checked') || [])].map(i => i.value);

  const job = _jobFormDotUnconfirmed
    ? { interstate: null, hazmat: null, liquidTank: null } // TASK-9370 — unknown, not false
    : {
      interstate: document.getElementById('job-interstate')?.checked || false,
      hazmat: document.getElementById('job-hazmat')?.checked || false,
      liquidTank: document.getElementById('job-liquid-tank')?.checked || false
    };

  const warnings = checkDOTCompliance(driverIds, truckIds, trailerIds, job, assetIds);
  // TASK-9844 — crew skills against the job type picked in the form, as of
  // the start date (advisory; empty unless the company turned it on).
  const helperIds = [...(document.getElementById('job-select-helpers')?.querySelectorAll('input:checked') || [])].map(i => i.value);
  const startKey = (document.getElementById('job-start-date')?.value || '').slice(0, 10) || null;
  warnings.push(..._crewSkillWarnings(driverIds, helperIds, truckIds, document.getElementById('job-type')?.value, startKey));
  renderComplianceWarnings(warnings, 'compliance-warnings-job');
}

// TASK-361 (2026-05-26, dispatch.js cleanup 2026-05-28) — Driver "My Recent
// JSAs" relocated to Inspections > JSA tab (jsa.js renderMyJsaSection).
// renderDriverMyJsas / emailDriverJsa / sendDriverJsaEmail /
// downloadDriverJsaPdf removed here. window.JSAModule + jsa.js own the
// canonical implementation.

// ============================================
// DISPATCH SETTINGS MODAL (decentralized from main Settings 2026-05-19)
// ============================================
// Owns 6 dispatch-domain toggles (5 previously in main Settings → Dispatch Features:
//   enable811, enableLoadCalculator, enableRoutePlanner, personalizedSchedule,
//   hideComplianceWarnings — plus placardCustomerFirst added by TASK-1385,
//   generalised to placardTokens + the six hide* form-section keys by TASK-4688).
// Schema unchanged — reads/writes the same tenant.settings.* keys main Settings used.
function _ensureDispatchSettingsModalDOM() {
  if (document.getElementById('dispatch-settings-modal')) return;
  const wrap = document.createElement('div');
  wrap.id = 'dispatch-settings-modal';
  wrap.className = 'modal hidden';
  wrap.setAttribute('onclick', 'if(event.target===this)window.DispatchModule.closeDispatchSettingsModal()');
  wrap.innerHTML = `
    <div class="modal-content" style="max-width:560px;display:flex;flex-direction:column;max-height:95vh;max-height:95dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h2><span class="material-icons" style="color:var(--brand-blue);">settings</span> Dispatch Settings</h2>
        <button class="modal-close" onclick="window.DispatchModule.closeDispatchSettingsModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-lg);flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Dispatch board feature toggles. These persist to your account-level settings and apply company-wide.
        </p>

        <!-- TASK-4688 — composable job card title. Replaces the TASK-1385
             "customer first" checkbox; that boolean is still the back-compat
             floor and is not written unless this control is actually changed. -->
        <div class="form-group">
          <label style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Job card title</label>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">
            Tick what shows on every job card and put it in the order you read it. Applies to the Week, Month, List and Search views. Empty fields drop out on their own — a job with no PO just shows the rest. With Job number ticked, the small Month view chips show just the number.
          </p>
          <div id="dispatch-settings-placard-tokens"></div>
          <div style="margin-top:8px;padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);">
            <span style="font-size:0.72rem;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:var(--text-secondary);">Preview</span>
            <div id="dispatch-settings-placard-preview" style="margin-top:4px;font-size:0.9rem;font-weight:600;color:var(--text-primary);word-break:break-word;"></div>
          </div>
        </div>

        <!-- TASK-9597 — Crew text: what the Notify Crew text contains. Same tick +
             Up/Down control as the job card title. Not saved unless touched. -->
        <div class="form-group" id="dispatch-settings-crew-text-group">
          <label style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Crew text</label>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">
            Tick what the Notify Crew text contains and put it in the order you want. Empty fields drop out on their own. Site contact is off unless you turn it on.
          </p>
          <div id="dispatch-settings-crew-text-tokens"></div>
          <div id="dispatch-settings-crew-text-warn" style="margin-top:6px;font-size:0.8rem;color:var(--warning, #b45309);"></div>
          <div style="margin-top:8px;padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);">
            <span style="font-size:0.72rem;font-weight:700;letter-spacing:0.04em;text-transform:uppercase;color:var(--text-secondary);">Preview (tomorrow's schedule)</span>
            <pre id="dispatch-settings-crew-text-preview" style="margin:4px 0 0;font-size:0.8rem;color:var(--text-primary);white-space:pre-wrap;word-break:break-word;font-family:inherit;max-height:180px;overflow:auto;"></pre>
            <div id="dispatch-settings-crew-text-count" style="margin-top:4px;font-size:0.75rem;color:var(--text-secondary);"></div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" style="margin-top:6px;" onclick="window.DispatchModule.resetCrewTextTokens()">Use the standard layout</button>
        </div>

        <!-- TASK-9994 (Chris 10/21: "give 811 permits its own setting … put any
             setting belonging to 811 there") — every 811 setting in one section.
             Stored keys unchanged; the Locates window's gear scrolls here. -->
        <section id="dispatch-settings-811-section" aria-labelledby="dispatch-settings-811-h" style="border-top:1px solid var(--border-light);border-bottom:1px solid var(--border-light);padding:var(--spacing-lg) 0 var(--spacing-sm);margin:var(--spacing-lg) 0;">
        <h3 id="dispatch-settings-811-h" style="display:flex;align-items:center;gap:8px;font-size:1.05rem;font-weight:700;margin:0 0 12px;color:var(--text-primary);"><span class="material-icons" style="color:var(--brand-blue);" aria-hidden="true">assignment</span> 811 Locates</h3>
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-enable-811" onchange="document.getElementById('dispatch-settings-811-more')?.classList.toggle('hidden', !this.checked)">
            <label for="dispatch-settings-enable-811" style="cursor:pointer;margin:0;">Enable Locates (811)</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Show Locates (811) in Dispatch and the locate tickets in the job form. Turn this off if your company doesn't handle 811 locate tickets.
          </p>
        </div>
        <!-- TASK-9994 — the rest of the 811 settings only while 811 is on (Chris 10/21: everything 811 disappears when off). -->
        <div id="dispatch-settings-811-more" class="${is811Enabled() ? '' : 'hidden'}">

        <div class="form-group">
          <label for="dispatch-settings-811-valid-days" style="margin:0 0 4px;">811 tickets are good for</label>
          <div style="display:flex;align-items:center;gap:var(--spacing-md);">
            <input type="number" id="dispatch-settings-811-valid-days" min="1" max="365" step="1" inputmode="numeric" placeholder="Not set" style="width:120px;min-height:44px;font-size:16px;">
            <span>days</span>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Only a fallback: DispatchDeck reads the good-through date off each ticket. When a ticket's date can't be read, the upload offers today plus this many days for you to check. Leave blank to never offer it.
          </p>
        </div>

        <!-- TASK-9863 (moved here by TASK-9862, Chris 10/14) — 811 locate expiry morning
             alert: settings.permit811ExpiryAlertEnabled (absent = ON) and
             settings.permit811ExpiryAlertDays (1..30, absent = 5), read by the
             permit811ExpiryAlert cron. Owner/admin only (same gate as before). -->
        <div class="form-group" id="dispatch-settings-811-alert-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);min-height:44px;">
            <input type="checkbox" id="dispatch-settings-811-alert-enabled" checked style="width:22px;height:22px;">
            <label for="dispatch-settings-811-alert-enabled" style="cursor:pointer;margin:0;font-weight:600;">811 locate alerts</label>
          </div>
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-top:8px;flex-wrap:wrap;">
            <label for="dispatch-settings-811-alert-days" style="margin:0;">Warn when a ticket expires within</label>
            <input type="number" id="dispatch-settings-811-alert-days" min="1" max="30" step="1" inputmode="numeric" value="5" style="width:80px;min-height:44px;font-size:16px;">
            <span>days</span>
          </div>
          <p id="dispatch-settings-811-alert-hint" style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">A morning email (about 6:30) to people tagged Dispatch Admin: expired locate tickets that still have a crew scheduled, then tickets about to expire. No email on days with nothing to report.</p>
        </div>

        <!-- TASK-9887 — settings.potentialRetentionMonths (0 = keep forever, the
             default; 12 | 24). Owner/admin only, same gate as the alert. -->
        <div class="form-group" id="dispatch-settings-pot-retention-group">
          <label for="dispatch-settings-pot-retention" style="margin:0 0 4px;">Keep archived potential jobs and projects</label>
          <select id="dispatch-settings-pot-retention" style="max-width:260px;min-height:44px;font-size:16px;">
            <option value="0">Forever (default)</option>
            <option value="12">1 year after archived</option>
            <option value="24">2 years after archived</option>
          </select>
          <p id="dispatch-settings-pot-retention-hint" style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">A potential stops being suggested 30 days after its last 811 ticket expires, or when someone marks it won't happen or uses it. It stays searchable in Locates for this long after that, then drops out of the list.</p>
        </div>

        <!-- TASK-9994 — settings.show811Warnings (absent = OFF for every tenant).
             Written only when it moves, audited old->new. Rules: t9337DispatchKeys. -->
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);min-height:44px;">
            <input type="checkbox" id="dispatch-settings-811-show-warnings" style="width:22px;height:22px;">
            <label for="dispatch-settings-811-show-warnings" style="cursor:pointer;margin:0;font-weight:600;">Show 811 warnings</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Adds reminders to the Locates page: the red "expired / expiring" banner, "expires soon", "not on the board yet", "looks like the same site" suggestions and the All / Expired-expiring / Not on the board filter buttons, plus the Add Job box "811 tickets for this customer aren't on a job yet". Default off. Expired tickets, "Dig starts" and "Waiting on utilities" always show, and a job still can't be saved onto an expired ticket without saying so.
          </p>
        </div>

        <!-- TASK-10283 — settings.show811DriverLines (absent = ON). Written only when it moves, audited old->new. Rules: t9337DispatchKeys. -->
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);min-height:44px;">
            <input type="checkbox" id="dispatch-settings-811-driver-lines" style="width:22px;height:22px;">
            <label for="dispatch-settings-811-driver-lines" style="cursor:pointer;margin:0;font-weight:600;">Show 811 lines to drivers</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Drivers see a red "Locate expired. Don't dig" line on the job card and a "Don't dig" note on the job's day when a locate is expired or not active yet. Default on. Turned off, drivers get neither line (the ticket and its dates still show), so turn it off only if your drivers don't dig. The board chip, the stop when dispatching onto an expired ticket, and the morning email stay.
          </p>
        </div>
        </div>
        </section>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-enable-permit-tracking">
            <label for="dispatch-settings-enable-permit-tracking" style="cursor:pointer;margin:0;">Track road permit requirements</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Adds a "Permit Required" checkbox to the job form for oversize/overweight road permits, and flags tickets in AR/AP when a permit-required job reaches billing with no permit attached. The flag never blocks invoicing. Default off.
          </p>
        </div>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-enable-load-calculator">
            <label for="dispatch-settings-enable-load-calculator" style="cursor:pointer;margin:0;">Enable Load Calculator</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Show load calculations on jobs when assets have a Unit Weight and trucks have a Payload Capacity set in the Roster. Calculates number of loads per truck automatically.
          </p>
        </div>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-enable-route-planner">
            <label for="dispatch-settings-enable-route-planner" style="cursor:pointer;margin:0;">Enable Route Planner</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Show a route planner card on jobs with 2+ destinations. Chains the destinations into a single multi-destination Google Maps route with distance estimates.
          </p>
        </div>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-personalized-schedule">
            <label for="dispatch-settings-personalized-schedule" style="cursor:pointer;margin:0;">Personalized Schedule (drivers see only their jobs)</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            When on, drivers logging in via PIN or driver link see only the jobs they're assigned to — not every job in the company — and Notify Crew opens with "Personalized Messages" on, so each driver is texted only their own jobs (untick it for a one-off full-schedule text). Useful for subcontracting carriers who don't want drivers seeing each other's customers or rates. Default off.
          </p>
        </div>

        <!-- TASK-9373 (COD UX) — the Driver App page lives in AR/AP Settings
             (Chris's placement); this row is the pointer from where the other
             driver toggles live. Opening it respects the billing-module gate. -->
        <div class="form-group" style="display:flex;align-items:center;gap:10px;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);min-height:44px;">
          <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">smartphone</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">Driver App layout</div>
            <div id="dispatch-settings-driver-app-hint" style="font-size:0.78rem;color:var(--text-secondary);">Tabs, Actions tiles and job-detail sections drivers see — configured in AR/AP Settings.</div>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" id="dispatch-settings-driver-app-btn" onclick="window.DispatchModule.openDriverAppFromDispatch()">Configure in AR/AP</button>
        </div>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-driver-job-edit">
            <label for="dispatch-settings-driver-job-edit" style="cursor:pointer;margin:0;">Drivers can add and edit jobs</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            When on, PIN drivers get "Add Job" on their Job Board and "Edit" on every job: customer, point of contact (they see its name, phone and email), project, type, title, dates, location, destinations, notes (new jobs; after that they're the office's), crew and their own equipment, plus Add Another Day, Copy to New Job, and Delete on a job they added with no ticket or clock time. They can ask for rentals, fill in a PO when the job has none, and add photos and load permits. Changing a PO, stock, price book, job notes and removing files stay with dispatch; drivers add Updates on the job screen. Every driver save is recorded in History (Job Activity, ⋮ menu). Default off; turn it off for one person in Roster.
          </p>
        </div>

        <!-- TASK-9202 — text the crew member when they are added to / removed
             from a job. Default Off for every existing tenant, and "Off" is
             genuinely off: the Cloud Function allowlists the two enabled values
             server-side, so an absent key sends nothing. -->
        <div class="form-group">
          <label for="dispatch-settings-crew-job-alerts" style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Text crew about their assignments</label>
          <select id="dispatch-settings-crew-job-alerts" style="width:100%;">
            <option value="off">Off</option>
            <option value="assignment">Assignment only — added to or removed from a job</option>
            <option value="assignment_changes">Assignment + changes — also when a job they are on changes</option>
          </select>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Sends a short text to anyone — driver or helper — added to or removed from a job. With <strong>Assignment + changes</strong>, it also texts the crew on a job when its customer, location, destinations, start/end time or day changes, or the day is cancelled — notes, PO and billing edits never text anyone. It names the customer and the date and nothing else, with no link: DispatchDeck stays the one place the details live. Texts are <strong>batched</strong>, so shuffling the board for a few minutes produces one message per person, not one per change. Past days are never texted about. Uses your SMS credits: one credit per message.
          </p>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Only people who have <strong>SMS notifications turned on</strong> on their Roster card will get these — same rule as every other one-to-one text DispatchDeck sends.
          </p>
        </div>

        <!-- TASK-9516 — "by <name>" on the Dispatch Admin job-change text.
             Absent = off; written only when it moves (never stamped by an
             unrelated Save). The server resolves the name, never the client. -->
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-alert-show-editor">
            <label for="dispatch-settings-alert-show-editor" style="cursor:pointer;margin:0;">Include editor name in dispatch text alerts</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Adds who made the change to the job texts your dispatch admins get, like "Dispatch Updated by Dana S.: …". The name comes from the person's Roster card or login, and covers office saves, deletes and edits drivers make from their Job Board. Changes made by DispatchDeck itself (auto-close, bulk moves, drag on the board) go out without a name. Default off.
          </p>
        </div>

        <!-- TASK-9763 S2 — settings.driverTruckSwap. Absent = off; written only
             when it moves, audited old->new. Only matters with "Track
             equipment per operator" on (the clock-in equipment rows). -->
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-driver-truck-swap">
            <label for="dispatch-settings-driver-truck-swap" style="cursor:pointer;margin:0;">Drivers can pick a different truck at clock-in</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            For when the yard puts someone in another rig. At clock-in the truck you assigned them is still checked, and they can choose any other active truck from your roster instead. Billing Review shows "Ran #22, planned #19" when they differ, and a truck that wasn't on the job is flagged on the ticket for you to confirm. Needs "Track equipment by operator" turned on in Billing settings. Default off.
          </p>
        </div>

        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-hide-compliance-warnings">
            <label for="dispatch-settings-hide-compliance-warnings" style="cursor:pointer;margin:0;">Hide DOT compliance warnings</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            When on, the assignment and job modals will not show CDL class / endorsement / medical card warnings. Use this if your drivers' compliance data isn't in DispatchDeck yet and you don't want the warnings until it is. Default off — most carriers benefit from the safety check.
          </p>
        </div>

        <!-- TASK-9763 S3 — company crew seniority ("first on, last off"). Its OWN
             Save + audit row (renderSeniorityEditor, lazy views/assignPairing.js);
             the shared Save button below never touches it. -->
        <div class="form-group" style="border-top:1px solid var(--border-light);padding-top:var(--spacing-lg);margin-top:var(--spacing-lg);">
          <label style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Crew seniority</label>
          <div id="dispatch-settings-seniority"><p style="font-size:0.8rem;color:var(--text-secondary);margin:0;"><span class="loading-spinner-small"></span> Loading…</p></div>
        </div>

        <!-- TASK-9407 — default start date on a new job. Absent = tomorrow
             (the TASK-273 default), so nobody's form changes until they pick. -->
        <div class="form-group" style="border-top:1px solid var(--border-light);padding-top:var(--spacing-lg);margin-top:var(--spacing-lg);">
          <label for="dispatch-settings-default-start-date" style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Default start date for new jobs</label>
          <select id="dispatch-settings-default-start-date" style="width:100%;">
            <option value="today">Today</option>
            <option value="tomorrow">Tomorrow</option>
            <option value="nextBusinessDay">Next business day (Mon–Fri)</option>
          </select>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            The date a new job starts on when you open Add Job. Next business day skips Saturday and Sunday, so on a Friday it picks Monday. It does not know holidays. You can still change the date on each job.
          </p>
        </div>

        <!-- TASK-10126 — settings.jobTitleFromProject (absent = off). Written only
             when it moves, audited old->new. Read by _fillJobTitleFromProject. -->
        <div class="form-group">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="dispatch-settings-title-from-project">
            <label for="dispatch-settings-title-from-project" style="cursor:pointer;margin:0;">Fill the job title from the project name</label>
          </div>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            On a new job, picking a project puts its name in an empty Job Title. A title you typed is never changed. Pick a different project and the title follows only if it is still the first project's name. Works on driver Add Job too. Default off.
          </p>
        </div>

        <!-- TASK-4688 — Add Job form section visibility. hide* polarity: absent
             = false = shown, so nobody's form changes without them asking. -->
        <div class="form-group" style="border-top:1px solid var(--border-light);padding-top:var(--spacing-lg);margin-top:var(--spacing-lg);">
          <label style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Add Job form — hide sections you don't use</label>
          <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 8px;">
            Everything is shown by default. Hiding a section only shortens the form. A job that already has something in a hidden section still shows that section when you open it, and nothing is ever dropped on save.
          </p>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:0 var(--spacing-lg);">
            <label for="dispatch-settings-hide-afe-vendor" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-afe-vendor" style="margin:0;flex-shrink:0;">
              <span>Vendor Number &amp; AFE</span>
            </label>
            <label for="dispatch-settings-hide-dot-flags" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-dot-flags" style="margin:0;flex-shrink:0;">
              <span>DOT classification chips <span style="display:block;font-size:0.76rem;color:var(--text-secondary);font-weight:400;">Interstate / Hazmat / Liquid Tank. Hide these and new jobs are created unclassified, so the hazmat and interstate driver-credential check will not run for them. Permit Required is not affected.</span></span>
            </label>
            <label for="dispatch-settings-hide-ticketing-mode" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-ticketing-mode" style="margin:0;flex-shrink:0;">
              <span>Ticketing (combined / split)</span>
            </label>
            <label for="dispatch-settings-hide-waypoints" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-waypoints" style="margin:0;flex-shrink:0;">
              <span>Destinations</span>
            </label>
            <label for="dispatch-settings-hide-geofence" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-geofence" style="margin:0;flex-shrink:0;">
              <span>Geofence</span>
            </label>
            <label for="dispatch-settings-hide-attachments" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;cursor:pointer;margin:0;font-size:0.88rem;color:var(--text-primary);">
              <input type="checkbox" id="dispatch-settings-hide-attachments" style="margin:0;flex-shrink:0;">
              <span>Attachments &amp; Load Permits</span>
            </label>
          </div>
        </div>

        <!-- TASK-9366 — Auto-close stale jobs. The cron
             (functions/src/scheduled/autoCloseJobs.js) has run daily since
             TASK-1083 but was OPT-IN with no way to opt in: its own warning
             said "re-enable via the settings toggle" and no toggle existed.
             Dispatch-MODULE holders + the owner (hidden below for anyone else —
             the firestore tenantModuleSettingsUpdate / t9337DispatchKeys arm on
             the tenant doc is the real boundary; module access, not roles). It has
             its OWN Save on purpose: this governs which jobs drop off the
             billing date-picker, so it never rides the shared Save button
             above and is always audited old→new (the workweek-flip rule). -->
        <div class="form-group" id="dispatch-settings-autoclose-section" style="border-top:1px solid var(--border-light);padding-top:var(--spacing-lg);margin-top:var(--spacing-lg);">
          <label style="display:block;font-weight:600;margin:0 0 4px;color:var(--text-primary);">Auto-close finished jobs</label>
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap;">
            <input type="checkbox" id="dispatch-settings-autoclose-enabled">
            <label for="dispatch-settings-autoclose-enabled" style="cursor:pointer;margin:0;">Mark old jobs Finished automatically after</label>
            <input type="number" id="dispatch-settings-autoclose-days" min="1" max="365" step="1" value="30" style="width:72px;padding:6px 8px;" aria-label="Days after a job ends">
            <span>days</span>
          </div>
          <p id="dispatch-settings-autoclose-copy" style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;">
            Jobs that ended more than <strong id="dispatch-settings-autoclose-n">30</strong> days ago, with nobody clocked in and nothing unbilled, are marked Finished automatically. Only jobs ending after you turn this on are affected.
          </p>
          <p id="dispatch-settings-autoclose-status" style="font-size:0.8rem;color:var(--text-secondary);margin-top:4px;"></p>
          <div style="margin-top:8px;">
            <button type="button" class="btn btn-secondary btn-sm" id="btn-save-autoclose" onclick="window.DispatchModule.saveJobAutoClose()">Save auto-close</button>
          </div>
        </div>
      </div>
      <div class="modal-footer" style="flex-shrink:0;">
        <span id="dispatch-settings-viewonly-note" role="note" style="display:none;flex:1 1 auto;min-width:0;align-self:center;font-size:0.85rem;color:#92400E;background:#FEF3C7;border:1px solid #FDE68A;border-radius:6px;padding:6px 10px;line-height:1.35;">Your login is view &amp; upload only — ask the owner or an admin to change this.</span>
        <button type="button" class="btn btn-secondary" onclick="window.DispatchModule.closeDispatchSettingsModal()">Cancel</button>
        <button type="button" class="btn btn-primary" id="btn-save-dispatch-settings" onclick="window.DispatchModule.saveDispatchSettings()">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(wrap);
}

// TASK-4688 — the job-card-title control. COD R5 (UX Lead + Code Reviewer,
// independently) killed the drag row Chris described: drag-to-reorder is hostile
// on the tablets this is used on. Tick-box + Up/Down tap buttons instead, every
// target 44px. _placardTokensDirty gates the WRITE — a tenant who opens Dispatch
// Settings for some other toggle and hits Save never gains a placardTokens key,
// so their board keeps the exact TASK-1385 rendering.
let _placardDraftTokens = null;
let _placardTokensDirty = false;

// The inline min-height above is enough everywhere an author rule is not
// !important, but styles.css:9710 sets label{margin-bottom:6px!important} and
// :9739 forces the checkbox to 22px inside @media (max-width:768px) — an
// !important stylesheet rule beats an inline declaration, so the guarantee has
// to carry !important too. Keyed on (pointer: coarse), NOT a width: a cab
// tablet at 1024 needs the 44px and a narrow mouse window does not.
// Injected here rather than in styles.css because this lane owns dispatch.js
// only; the selector is a class this function is the sole emitter of.
//
// NO BACKTICKS ANYWHERE INSIDE THE TEMPLATE BELOW, comments included — TASK-4745
// had one close the literal early, node --check still passed, and the whole
// injected sheet silently became a fragment.
function _ensurePlacardTokenTapStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('dispatch-placard-token-tap-styles')) return;
  const el = document.createElement('style');
  el.id = 'dispatch-placard-token-tap-styles';
  el.textContent = `
@media (pointer: coarse) {
  .placard-token-tap { min-height: 44px !important; margin: 0 !important; }
}`;
  document.head.appendChild(el);
}

function _renderPlacardTokenEditor() {
  const host = document.getElementById('dispatch-settings-placard-tokens');
  if (!host) return;
  _ensurePlacardTokenTapStyles();
  const sel = _placardDraftTokens || [];
  // escapeHtml is NOT attribute-safe (it leaves quotes alone) — aria-label is an
  // attribute, so it goes through escAttr. [[lesson_escapehtml_is_not_attribute_safe]]
  const escA = window.escAttr || escapeHtml;
  const rows = [];
  sel.forEach((key, i) => {
    const def = PLACARD_TOKEN_DEFS.find(d => d.key === key);
    if (!def) return;
    // The tick box is 18x18 and cannot be enlarged without breaking the native
    // control, so the LABEL carries the target: it wraps box + name and claims
    // the full row height. It deliberately does NOT wrap the Up/Down buttons —
    // a label around those would make every reorder tap also untick the token.
    rows.push(`
      <div style="display:flex;align-items:center;gap:var(--spacing-md);padding:2px var(--spacing-md);border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);margin-bottom:4px;">
        <label class="placard-token-tap" style="display:flex;align-items:center;gap:var(--spacing-md);flex:1;min-height:44px;margin:0;cursor:pointer;">
          <input type="checkbox" checked style="margin:0;flex-shrink:0;" aria-label="Remove ${escA(def.label)} from the job card title"
                 onchange="window.DispatchModule.togglePlacardToken('${escJsAttr(def.key)}')">
          <span style="flex:1;font-size:0.88rem;font-weight:600;color:var(--text-primary);">${escapeHtml(def.label)}</span>
        </label>
        <span style="font-size:0.72rem;color:var(--text-secondary);min-width:16px;text-align:right;">${i + 1}</span>
        <button type="button" title="Move up" aria-label="Move ${escA(def.label)} up" ${i === 0 ? 'disabled' : ''}
                onclick="window.DispatchModule.movePlacardToken('${escJsAttr(def.key)}',-1)"
                style="min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;background:none;border:none;border-radius:var(--radius-md);cursor:pointer;color:${i === 0 ? 'var(--border-medium)' : 'var(--brand-blue)'};">
          <span class="material-icons" style="font-size:20px;">arrow_upward</span></button>
        <button type="button" title="Move down" aria-label="Move ${escA(def.label)} down" ${i === sel.length - 1 ? 'disabled' : ''}
                onclick="window.DispatchModule.movePlacardToken('${escJsAttr(def.key)}',1)"
                style="min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;background:none;border:none;border-radius:var(--radius-md);cursor:pointer;color:${i === sel.length - 1 ? 'var(--border-medium)' : 'var(--brand-blue)'};">
          <span class="material-icons" style="font-size:20px;">arrow_downward</span></button>
      </div>`);
  });
  PLACARD_TOKEN_DEFS.filter(d => !sel.includes(d.key)).forEach(def => {
    rows.push(`
      <label class="placard-token-tap" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;padding:0 var(--spacing-md);border:1px dashed var(--border-light);border-radius:var(--radius-md);margin-bottom:4px;cursor:pointer;">
        <input type="checkbox" style="margin:0;flex-shrink:0;"
               onchange="window.DispatchModule.togglePlacardToken('${escJsAttr(def.key)}')">
        <span style="flex:1;font-size:0.88rem;color:var(--text-secondary);">${escapeHtml(def.label)}</span>
      </label>`);
  });
  host.innerHTML = rows.join('');
  _renderPlacardPreview();
}

// Preview off a REAL job — the one that fills the most tokens, so the dispatcher
// sees their own data, not lorem ipsum. Falls back to a sample on an empty board.
function _pickPlacardPreviewJob() {
  let best = null, bestScore = 0;
  for (const j of (DispatchState.jobs || [])) {
    const score = PLACARD_TOKEN_KEYS.reduce((c, k) => c + (getPlacardTokenValue(j, k) ? 1 : 0), 0);
    if (score > bestScore) { bestScore = score; best = j; }
  }
  return best || { title: 'Mat Install', type: 'Mat Install', customer: 'Sample Customer',
                   wellName: 'Storhaug Pad', location: 'NW/4 Sec 12', poNumber: 'PO-12345', jobNumber: 1054 };
}

function _renderPlacardPreview() {
  const el = document.getElementById('dispatch-settings-placard-preview');
  if (!el) return;
  const job = _pickPlacardPreviewJob();
  const parts = (_placardDraftTokens || [])
    .map(t => String(getPlacardTokenValue(job, t) || '').trim()).filter(Boolean);
  // textContent, not innerHTML — this is live job data (title, well name,
  // customer) and it must never execute. escapeHtml is not attribute-safe and
  // there is no reason to build markup here at all.
  el.textContent = parts.length ? parts.join(' · ') : (job.title || 'Untitled');
  el.style.color = parts.length ? 'var(--text-primary)' : 'var(--text-secondary)';
}

// TASK-9278 — the ONLY place jobNumber is seeded. A tenant with no saved order
// sees the badge today (prepended by getPlacardTitleHtml's legacy path), so the
// draft must lead with it too, or the first tick of any other box would save an
// order without it and silently drop the number off every card. A tenant with a
// saved order gets exactly what they saved: if jobNumber is absent there, they
// unticked it. Never seed getPlacardTokens()' fallback instead (see PLACARD_TOKEN_DEFS).
function _seedPlacardDraftTokens() {
  const saved = getPlacardTokens().slice();
  return _hasExplicitPlacardTokens() ? saved : ['jobNumber', ...saved];
}

function togglePlacardToken(key) {
  if (!PLACARD_TOKEN_KEYS.includes(key)) return;
  _placardDraftTokens = _placardDraftTokens || [];
  const i = _placardDraftTokens.indexOf(key);
  if (i === -1) _placardDraftTokens.push(key); else _placardDraftTokens.splice(i, 1);
  _placardTokensDirty = true;
  _renderPlacardTokenEditor();
}

function movePlacardToken(key, delta) {
  const arr = _placardDraftTokens || [];
  const i = arr.indexOf(key);
  const j = i + delta;
  if (i === -1 || j < 0 || j >= arr.length) return;
  arr.splice(j, 0, arr.splice(i, 1)[0]);
  _placardTokensDirty = true;
  _renderPlacardTokenEditor();
}

// TASK-9597 — Crew text editor. Same shape as the placard editor; draft is local
// until Save, and _crewTextDirty gates the WRITE so a tenant who never touches it
// never gains a crewTextTokens key (absent = the text Notify Crew always sent).
let _crewTextDraftTokens = null;
let _crewTextDirty = false;

function _renderCrewTextEditor() {
  const host = document.getElementById('dispatch-settings-crew-text-tokens');
  if (!host) return;
  _ensurePlacardTokenTapStyles();
  const sel = _crewTextDraftTokens || [];
  const escA = window.escAttr || escapeHtml;
  const rows = [];
  sel.forEach((key, i) => {
    const def = CREW_TEXT_TOKEN_DEFS.find(d => d.key === key);
    if (!def) return;
    rows.push(`
      <div style="display:flex;align-items:center;gap:var(--spacing-md);padding:2px var(--spacing-md);border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);margin-bottom:4px;">
        <label class="placard-token-tap" style="display:flex;align-items:center;gap:var(--spacing-md);flex:1;min-height:44px;margin:0;cursor:pointer;">
          <input type="checkbox" checked style="margin:0;flex-shrink:0;" aria-label="Remove ${escA(def.label)} from the crew text"
                 onchange="window.DispatchModule.toggleCrewTextToken('${escJsAttr(def.key)}')">
          <span style="flex:1;font-size:0.88rem;font-weight:600;color:var(--text-primary);">${escapeHtml(def.label)}</span>
        </label>
        <span style="font-size:0.72rem;color:var(--text-secondary);min-width:16px;text-align:right;">${i + 1}</span>
        <button type="button" title="Move up" aria-label="Move ${escA(def.label)} up" ${i === 0 ? 'disabled' : ''}
                onclick="window.DispatchModule.moveCrewTextToken('${escJsAttr(def.key)}',-1)"
                style="min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;background:none;border:none;border-radius:var(--radius-md);cursor:pointer;color:${i === 0 ? 'var(--border-medium)' : 'var(--brand-blue)'};">
          <span class="material-icons" style="font-size:20px;">arrow_upward</span></button>
        <button type="button" title="Move down" aria-label="Move ${escA(def.label)} down" ${i === sel.length - 1 ? 'disabled' : ''}
                onclick="window.DispatchModule.moveCrewTextToken('${escJsAttr(def.key)}',1)"
                style="min-width:44px;min-height:44px;display:inline-flex;align-items:center;justify-content:center;background:none;border:none;border-radius:var(--radius-md);cursor:pointer;color:${i === sel.length - 1 ? 'var(--border-medium)' : 'var(--brand-blue)'};">
          <span class="material-icons" style="font-size:20px;">arrow_downward</span></button>
      </div>`);
  });
  CREW_TEXT_TOKEN_DEFS.filter(d => !sel.includes(d.key)).forEach(def => {
    rows.push(`
      <label class="placard-token-tap" style="display:flex;align-items:center;gap:var(--spacing-md);min-height:44px;padding:0 var(--spacing-md);border:1px dashed var(--border-light);border-radius:var(--radius-md);margin-bottom:4px;cursor:pointer;">
        <input type="checkbox" style="margin:0;flex-shrink:0;"
               onchange="window.DispatchModule.toggleCrewTextToken('${escJsAttr(def.key)}')">
        <span style="flex:1;font-size:0.88rem;color:var(--text-secondary);">${escapeHtml(def.label)}</span>
      </label>`);
  });
  host.innerHTML = rows.join('');
  _renderCrewTextPreview();
}

// SMS segments for a GSM-7 string: 160 for one, 153 each once it concatenates.
// A heads-up only (never a cap) and NOT the billed-credit count.
// GSM-7 extension characters ([ ] { } ^ backslash | ~ euro) cost two septets each.
function crewTextSegments(text) {
  const t = String(text || '');
  let len = t.length;
  for (const ch of t) if ('[]{}^\\|~\u20AC'.includes(ch)) len++;
  return len <= 0 ? 0 : (len <= 160 ? 1 : Math.ceil(len / 153));
}

function _renderCrewTextPreview() {
  const pre = document.getElementById('dispatch-settings-crew-text-preview');
  if (!pre) return;
  const draft = _crewTextDraftTokens || [];
  const warn = document.getElementById('dispatch-settings-crew-text-warn');
  if (warn) {
    const miss = ['time', 'crew'].filter(k => !draft.includes(k)).map(k => k === 'time' ? 'start time' : 'crew');
    warn.textContent = miss.length ? 'Heads up: drivers need the ' + miss.join(' and ') + ' in this text.' : '';
  }
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);
  let message = '';
  try {
    const days = [{ date: tomorrow, entries: getCrewTextJobsForDate(formatDateISO(tomorrow)) /* TASK-9750 */ }];
    // Same builder + same GSM-7 choke point the real text goes through.
    message = toGsm7(buildCrewTextBody(days, draft.length ? draft : null, { fullNotes: false }, _crewTextResolvers()).message);
  } catch (e) {
    console.warn('[Dispatch] crew text preview failed', e);
    message = '(preview unavailable)';
  }
  pre.textContent = message;
  const cnt = document.getElementById('dispatch-settings-crew-text-count');
  if (cnt) {
    const n = crewTextSegments(message);
    cnt.textContent = message.length + ' characters, about ' + n + ' text segment' + (n === 1 ? '' : 's') + ' for tomorrow';
  }
}

function toggleCrewTextToken(key) {
  if (!CREW_TEXT_TOKEN_KEYS.includes(key)) return;
  _crewTextDraftTokens = _crewTextDraftTokens || [];
  const i = _crewTextDraftTokens.indexOf(key);
  if (i === -1) {
    // Picking one of an exclusive pair swaps the other out (stops/allStops, notes/notesFull).
    CREW_TEXT_EXCLUSIVE.forEach(pair => {
      if (pair.includes(key)) pair.filter(o => o !== key).forEach(o => {
        const j = _crewTextDraftTokens.indexOf(o);
        if (j !== -1) _crewTextDraftTokens.splice(j, 1);
      });
    });
    _crewTextDraftTokens.push(key);
  } else {
    _crewTextDraftTokens.splice(i, 1);
  }
  _crewTextDirty = true;
  _renderCrewTextEditor();
}

function moveCrewTextToken(key, delta) {
  const arr = _crewTextDraftTokens || [];
  const i = arr.indexOf(key);
  const j = i + delta;
  if (i === -1 || j < 0 || j >= arr.length) return;
  arr.splice(j, 0, arr.splice(i, 1)[0]);
  _crewTextDirty = true;
  _renderCrewTextEditor();
}

function resetCrewTextTokens() {
  _crewTextDraftTokens = CREW_TEXT_DEFAULT_TOKENS.slice();
  _crewTextDirty = true;
  _renderCrewTextEditor();
}

// "Edit default layout" inside Notify Crew: close it (its unsent edit is already
// in the TASK-9532 draft) and open Dispatch Settings.
function editCrewTextLayout() {
  closeModal('mass-text');
  openDispatchSettingsModal();
  setTimeout(() => {
    document.getElementById('dispatch-settings-crew-text-group')?.scrollIntoView?.({ block: 'start' });
  }, 50);
}

// TASK-9373 (COD UX) — Dispatch Settings → Driver App pointer. The page is
// gated by the `billing` module (it lives in AR/AP); checkModuleAccess is the
// same gate the AR/AP view uses. A viewer without it sees a hint instead of a
// button that would open a page whose saves the rules then refuse.
function _dispatchDriverAppAccess() {
  if (typeof window.checkModuleAccess !== 'function' || typeof window.BillingModule?.openDriverAppSettings !== 'function') return false;
  return window.checkModuleAccess('billing', true);
}
function _dispatchCanOpenDriverApp() {
  return _dispatchDriverAppAccess() === true;
}
// COD round 2 (Independent): while the member doc is still loading,
// checkModuleAccess returns 'loading'. Show "checking" and re-check every
// 500 ms (max 20 tries, only while the modal is open) instead of settling on
// "needs access" for a billing holder.
function _syncDispatchDriverAppRow(attempt = 0) {
  const btn = document.getElementById('dispatch-settings-driver-app-btn');
  const hint = document.getElementById('dispatch-settings-driver-app-hint');
  const access = _dispatchDriverAppAccess();
  const ok = access === true;
  const loading = access === 'loading';
  if (btn) btn.style.display = ok ? '' : 'none';
  if (hint) hint.textContent = ok
    ? 'Tabs, Actions tiles and job-detail sections drivers see — configured in AR/AP Settings.'
    : loading
      ? 'Tabs, Actions tiles and job-detail sections drivers see — checking your AR/AP access…'
      : 'Tabs, Actions tiles and job-detail sections drivers see are set in AR/AP Settings — needs AR/AP module access.';
  if (loading && attempt < 20) {
    setTimeout(() => {
      const m = document.getElementById('dispatch-settings-modal');
      if (m && !m.classList.contains('hidden')) _syncDispatchDriverAppRow(attempt + 1);
    }, 500);
  }
}
function openDriverAppFromDispatch() {
  if (!_dispatchCanOpenDriverApp()) {
    window.showToast?.('The Driver App layout is in AR/AP Settings — needs AR/AP module access', 'error');
    _syncDispatchDriverAppRow();
    return;
  }
  closeDispatchSettingsModal();
  window.BillingModule.openDriverAppSettings();
}

// TASK-9894 — a "View & upload only" login (members.viewUploadOnly) can't save
// Dispatch settings: the tenant update rule is isTenantAdmin (owner/admin role)
// or tenantModuleSettingsUpdate, and t9337ModuleWrite refuses viewUploadOnly.
// Wolverine 2026-10-15: turning on 811 Locates came back as a raw permission
// error. Mirror of those arms, viewUploadOnly only; the server still decides.
// Pure so the unit test executes it.
const DISPATCH_SETTINGS_VIEW_UPLOAD_ONLY_MSG = 'Your login is view & upload only — ask the owner or an admin to change this.';
// TASK-9527 (5) — a read-only ?access= link member (members.readOnly) is refused
// by t9337ModuleWrite too, so Save is off for it the same way (isTenantAdmin
// still lets an owner/admin role through, as for viewUploadOnly).
const DISPATCH_SETTINGS_READ_ONLY_MSG = 'View-only link: settings are locked.';
function _dispatchSettingsViewOnlyGate(memberAccess, isDemo) {
  if (isDemo === true) return true;
  const m = memberAccess;
  if (!m || (m.viewUploadOnly !== true && m.readOnly !== true)) return true;
  return ['owner', 'admin'].includes(m.role);
}
function _dispatchSettingsCanSave() {
  const S = window.AppState || {};
  return _dispatchSettingsViewOnlyGate(S.memberAccess, S.isDemo === true || S.tenant === 'demo');
}
function _dispatchSettingsRefusalMsg() {
  return window.AppState?.memberAccess?.readOnly === true ? DISPATCH_SETTINGS_READ_ONLY_MSG : DISPATCH_SETTINGS_VIEW_UPLOAD_ONLY_MSG;
}

function openDispatchSettingsModal() {
  _ensureDispatchSettingsModalDOM();
  const m = document.getElementById('dispatch-settings-modal');
  if (!m) return;
  {
    const canSave = _dispatchSettingsCanSave(); // TASK-9894
    const saveBtn = document.getElementById('btn-save-dispatch-settings');
    if (saveBtn) { saveBtn.disabled = !canSave; saveBtn.style.opacity = canSave ? '' : '0.5'; saveBtn.style.cursor = canSave ? '' : 'not-allowed'; }
    const note = document.getElementById('dispatch-settings-viewonly-note');
    if (note) { note.style.display = canSave ? 'none' : ''; note.textContent = _dispatchSettingsRefusalMsg(); }
  }
  const s = window.AppState?.settings || {};
  const setChk = (id, val) => { const el = document.getElementById(id); if (el) el.checked = val === true; };
  // TASK-4688 — seed the draft from getPlacardTokens(), which already folds the
  // legacy placardCustomerFirst boolean in, then mark it clean.
  _placardDraftTokens = _seedPlacardDraftTokens();
  _placardTokensDirty = false;
  _renderPlacardTokenEditor();
  // TASK-9597 — Crew text draft: the saved layout, or today's standard one.
  _crewTextDraftTokens = (getCrewTextTokens() || CREW_TEXT_DEFAULT_TOKENS).slice();
  _crewTextDirty = false;
  _renderCrewTextEditor();
  renderSeniorityEditor(); // TASK-9763 S3 — own Save, lazy chunk
  _syncDispatchDriverAppRow();
  setChk('dispatch-settings-hide-afe-vendor', s.hideAfeVendor);
  setChk('dispatch-settings-hide-dot-flags', s.hideDotFlags);
  setChk('dispatch-settings-hide-ticketing-mode', s.hideTicketingMode);
  setChk('dispatch-settings-hide-waypoints', s.hideWaypoints);
  setChk('dispatch-settings-hide-geofence', s.hideGeofence);
  setChk('dispatch-settings-hide-attachments', s.hideAttachments);
  setChk('dispatch-settings-enable-811', s.enable811);
  setChk('dispatch-settings-811-show-warnings', s.show811Warnings); // TASK-9994
  setChk('dispatch-settings-811-driver-lines', s.show811DriverLines !== false); // TASK-10283 — absent = on
  document.getElementById('dispatch-settings-811-more')?.classList.toggle('hidden', !is811Enabled()); // TASK-9994
  { const el = document.getElementById('dispatch-settings-811-valid-days'); if (el) el.value = _p811ValidDays(s.permit811ValidDays) || ''; } // TASK-9859
  { // TASK-9863 alert, moved here (TASK-9862). Owner/admin only, absent = on / 5 days.
    const on = document.getElementById('dispatch-settings-811-alert-enabled');
    const days = document.getElementById('dispatch-settings-811-alert-days');
    const hint = document.getElementById('dispatch-settings-811-alert-hint');
    if (on && days) {
      const admin = _p811AlertAdmin();
      on.checked = s.permit811ExpiryAlertEnabled !== false;
      days.value = String(_p811AlertDaysOf(s.permit811ExpiryAlertDays));
      on.disabled = !admin; days.disabled = !admin;
      if (hint) hint.textContent = 'A morning email (about 6:30) to people tagged Dispatch Admin: expired locate tickets that still have a crew scheduled, then tickets about to expire. No email on days with nothing to report.' + (admin ? '' : ' Only an owner or admin can change this.');
    }
  }
  { // TASK-9887 — potential retention (owner/admin; absent = keep forever).
    const el = document.getElementById('dispatch-settings-pot-retention');
    if (el) { el.value = String(_p811PotRetentionOf(s.potentialRetentionMonths)); el.disabled = !_p811AlertAdmin(); }
  }
  setChk('dispatch-settings-enable-permit-tracking', s.enablePermitTracking);
  setChk('dispatch-settings-enable-load-calculator', s.enableLoadCalculator);
  setChk('dispatch-settings-enable-route-planner', s.enableRoutePlanner);
  setChk('dispatch-settings-personalized-schedule', s.personalizedSchedule);
  setChk('dispatch-settings-hide-compliance-warnings', s.hideComplianceWarnings);
  setChk('dispatch-settings-driver-job-edit', s.driverJobEdit?.enabled === true); // TASK-9370
  setChk('dispatch-settings-alert-show-editor', s.dispatchAlertShowEditor === true); // TASK-9516
  setChk('dispatch-settings-driver-truck-swap', s.driverTruckSwap === true); // TASK-9763 S2
  setChk('dispatch-settings-title-from-project', s.jobTitleFromProject === true); // TASK-10126
  // TASK-9202 — seed from the stored value, fail-closed to 'off'. An unknown or
  // absent value renders Off, which is exactly what the server does with it.
  const _crewSel = document.getElementById('dispatch-settings-crew-job-alerts');
  if (_crewSel) {
    _crewSel.value = ['assignment', 'assignment_changes'].includes(s.crewJobAlerts) ? s.crewJobAlerts : 'off';
  }
  // TASK-9407 — absent/unknown renders Tomorrow, which is what showAddJob does with it.
  const _startSel = document.getElementById('dispatch-settings-default-start-date');
  if (_startSel) _startSel.value = normalizeDefaultJobStartDate(s.defaultJobStartDate);
  _seedJobAutoCloseSection(s.jobAutoClose);
  m.classList.remove('hidden');
}

function closeDispatchSettingsModal() {
  const m = document.getElementById('dispatch-settings-modal');
  if (m) m.classList.add('hidden');
}

// ── TASK-9366 — Auto-close toggle ─────────────────────────────────────────────
// Client mirror of the cron's resolveConfig() defaults (autoCloseJobs.js):
// `enabled` is the switch; `days` falls back to 30 when missing or <= 0.
const JOB_AUTOCLOSE_DEFAULT_DAYS = 30;

function _jobAutoCloseDays(cfg) {
  const n = Number(cfg && cfg.days);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : JOB_AUTOCLOSE_DEFAULT_DAYS;
}

// TASK-9366 sweep (Chris 2026-09-24: "we don't use roles. we use module access.
// we need to hardline that rule.") — MODULE gate, no role check. The first cut
// gated on hrEffectiveAdmin (owner/admin ROLE); this mirrors the rules arm the
// write actually passes through, tenantModuleSettingsUpdate → t9337DispatchKeys,
// where `jobAutoClose` is a `dispatch`-module key.
//   - no member doc (memberAccess == null) → tenant owner / legacy single-login
//     → allowed. Owners have no members doc (TASK-319); the self-created owner
//     member doc has no `modules` field either, so it lands on the next arm.
//   - member doc with NO `modules` field → legacy full access (TASK-319) → allowed
//   - revoked / inactive / pending → refused
//   - viewUploadOnly → refused (t9337ModuleWrite denies the write)
//   - otherwise → holds `dispatch`. An admin ROLE without the module is refused
//     — that is the hard line, not an oversight.
function _jobAutoCloseCanEdit() {
  const ma = window.AppState?.memberAccess;
  if (ma == null) return true;
  if (['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  if (ma.viewUploadOnly === true || ma.readOnly === true) return false; // + TASK-9527 (5) read-only link (t9337ModuleWrite)
  if (!Array.isArray(ma.modules)) return true;
  return ma.modules.includes('dispatch');
}

function _seedJobAutoCloseSection(cfg) {
  const section = document.getElementById('dispatch-settings-autoclose-section');
  if (!section) return;
  // Dispatch-module holders only — anyone else never sees the control, so they
  // never hit the rules denial the write would raise.
  section.style.display = _jobAutoCloseCanEdit() ? '' : 'none';
  const c = cfg || {};
  const chk = document.getElementById('dispatch-settings-autoclose-enabled');
  const daysEl = document.getElementById('dispatch-settings-autoclose-days');
  if (chk) chk.checked = c.enabled === true;
  if (daysEl) {
    daysEl.value = String(_jobAutoCloseDays(c));
    daysEl.oninput = () => {
      const n = document.getElementById('dispatch-settings-autoclose-n');
      if (n) n.textContent = String(_jobAutoCloseDays({ days: daysEl.value }));
    };
  }
  const nEl = document.getElementById('dispatch-settings-autoclose-n');
  if (nEl) nEl.textContent = String(_jobAutoCloseDays(c));
  _renderJobAutoCloseStatus(c);
}

function _renderJobAutoCloseStatus(cfg) {
  const el = document.getElementById('dispatch-settings-autoclose-status');
  if (!el) return;
  const c = cfg || {};
  const toDate = (v) => (v && typeof v.toDate === 'function') ? v.toDate() : (v ? new Date(v) : null);
  if (c.enabled === true) {
    const at = toDate(c.enabledAt);
    el.textContent = at && !isNaN(at)
      ? `On since ${at.toLocaleDateString()} — only jobs ending after that date are eligible.`
      : 'On — the nightly run stamps the start date on its next pass.';
  } else {
    el.textContent = 'Off — nothing is closed automatically.';
  }
}

// Pure payload builder — the ENTIRE value written to settings.jobAutoClose.
// It is a whole-map write (field path 'settings.jobAutoClose'), so every key
// the cron reads must be present here; nothing is spread from existing settings.
//   ON  → { enabled:true,  enabledAt, days }
//   OFF → { enabled:false, disabledAt, disabledReason:'owner toggled off' }
// `enabledAt` is the cron's GO-FORWARD boundary (TASK-4647): turning ON stamps
// server time; re-saving while already ON (e.g. changing days) keeps the
// existing stamp so the boundary never silently moves forward.
function buildJobAutoClosePayload(nextEnabled, daysInput, prev) {
  const p = prev || {};
  if (nextEnabled) {
    const n = Number(daysInput);
    if (!Number.isFinite(n) || n <= 0 || Math.round(n) !== n) {
      throw new Error('Days must be a whole number of 1 or more');
    }
    const keepStamp = p.enabled === true && p.enabledAt && typeof p.enabledAt.toDate === 'function';
    return { enabled: true, enabledAt: keepStamp ? p.enabledAt : serverTimestamp(), days: n };
  }
  return { enabled: false, disabledAt: serverTimestamp(), disabledReason: 'owner toggled off' };
}

async function saveJobAutoClose() {
  const tid = window.AppState?.tenant;
  if (!tid) { window.showToast?.('Not logged in', 'error'); return; }
  if (!_jobAutoCloseCanEdit()) {
    window.showToast?.('Auto-close needs the Dispatch module — ask an admin for access', 'error');
    return;
  }
  const chk = document.getElementById('dispatch-settings-autoclose-enabled');
  const daysEl = document.getElementById('dispatch-settings-autoclose-days');
  const btn = document.getElementById('btn-save-autoclose');
  const nextEnabled = !!(chk && chk.checked);
  const btnOriginal = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
  try {
    // Read the CURRENT value fresh so the audit from→to and the no-op check are
    // against Firestore truth, not a possibly-stale AppState copy.
    const tenantRef = doc(db, 'tenants', tid);
    const snap = await getDoc(tenantRef);
    const prev = (snap.exists() && snap.data()?.settings?.jobAutoClose) || {};
    const prevEnabled = prev.enabled === true;
    const prevDays = _jobAutoCloseDays(prev);

    let payload;
    try {
      payload = buildJobAutoClosePayload(nextEnabled, daysEl ? daysEl.value : JOB_AUTOCLOSE_DEFAULT_DAYS, prev);
    } catch (e) {
      window.showToast?.(e.message, 'error');
      return;
    }
    if (prevEnabled === nextEnabled && (!nextEnabled || prevDays === payload.days)) {
      window.showToast?.('No change to auto-close', 'info');
      return;
    }

    // Field-path write of the ONE key this control owns. Never a spread of
    // existing settings (app.js TASK-9255 block explains the lost-write and
    // permission-denied traps that spread caused).
    await updateDoc(tenantRef, { 'settings.jobAutoClose': payload });

    // Refresh from the server so AppState carries the real Timestamp, not the
    // serverTimestamp() sentinel.
    const after = await getDoc(tenantRef);
    const stored = (after.exists() && after.data()?.settings?.jobAutoClose) || payload;
    if (!window.AppState.settings) window.AppState.settings = {};
    window.AppState.settings.jobAutoClose = stored;

    const iso = (v) => (v && typeof v.toDate === 'function') ? v.toDate().toISOString() : null;
    window.logAudit?.('settings', 'SAVE_JOB_AUTOCLOSE', {
      from: { enabled: prevEnabled, days: prevDays, enabledAt: iso(prev.enabledAt) },
      to: { enabled: nextEnabled, days: nextEnabled ? payload.days : null, enabledAt: iso(stored.enabledAt) },
    });
    _renderJobAutoCloseStatus(stored);
    window.showToast?.(nextEnabled ? `Auto-close on — jobs ending from today close after ${payload.days} days` : 'Auto-close off', 'success');
  } catch (e) {
    console.error('[Dispatch Settings] auto-close save failed', e);
    window.showToast?.('Save failed: ' + (e?.message || 'unknown'), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = btnOriginal; }
  }
}

async function saveDispatchSettings() {
  if (!_dispatchSettingsCanSave()) { // TASK-9894
    window.showToast?.(_dispatchSettingsRefusalMsg(), 'error');
    return;
  }
  const getChk = (id) => document.getElementById(id)?.checked ?? false;
  const enable811 = getChk('dispatch-settings-enable-811');
  const enablePermitTracking = getChk('dispatch-settings-enable-permit-tracking');
  const enableLoadCalculator = getChk('dispatch-settings-enable-load-calculator');
  const enableRoutePlanner = getChk('dispatch-settings-enable-route-planner');
  const personalizedSchedule = getChk('dispatch-settings-personalized-schedule');
  const hideComplianceWarnings = getChk('dispatch-settings-hide-compliance-warnings');

  // TASK-9202 — this one governs OUTBOUND COMMS and spends SMS credits, so it
  // is written through the same allowlist the server uses (never trust the
  // select's raw value) and audited with its OLD and NEW values, not just its
  // key name. A shared Save button silently flipping a money/comms-governing
  // field with no old→new row is the exact shape of the workweek flip that
  // mis-cut two payrolls.
  const _crewRaw = document.getElementById('dispatch-settings-crew-job-alerts')?.value;
  const crewJobAlerts = ['assignment', 'assignment_changes'].includes(_crewRaw) ? _crewRaw : 'off';
  const prevCrewJobAlerts = ['assignment', 'assignment_changes'].includes(window.AppState?.settings?.crewJobAlerts)
    ? window.AppState.settings.crewJobAlerts
    : 'off';

  // TASK-9370 — "Drivers can add and edit jobs". A security-governing switch on
  // a shared Save button, so it is audited old->new by name, not just by key
  // ([[lesson_workweek_silently_flipped_sun_sat]]).
  // TASK-9710 — written by FIELD PATH (settings.driverJobEdit.enabled) and only
  // when it moved: the map also holds blockedPersonIds (the Roster per-driver
  // opt-out), and a whole-map write from a stale modal would wipe every one.
  const driverJobEdit = getChk('dispatch-settings-driver-job-edit');
  const prevDriverJobEdit = window.AppState?.settings?.driverJobEdit?.enabled === true;

  // TASK-9407 — allowlisted like crewJobAlerts, and written ONLY when it moved:
  // absent already means 'tomorrow', so saving the modal for any other reason
  // never stamps the key (and never depends on the rules allowlist for it).
  const defaultJobStartDate = normalizeDefaultJobStartDate(document.getElementById('dispatch-settings-default-start-date')?.value);
  const prevDefaultJobStartDate = normalizeDefaultJobStartDate(window.AppState?.settings?.defaultJobStartDate);

  // TASK-9516 — "Include editor name in dispatch text alerts". Same rule as
  // defaultJobStartDate: written ONLY when it moved, so saving this modal for
  // any other reason never puts the key on a tenant (Chris: default off, and
  // never on for trippin-trucking). Audited old->new below.
  const showEditor = getChk('dispatch-settings-alert-show-editor');
  const prevShowEditor = window.AppState?.settings?.dispatchAlertShowEditor === true;

  // TASK-9763 S2 — "Drivers can pick a different truck at clock-in". Changes
  // what the server accepts on a clock entry, so: written ONLY when it moved,
  // audited old->new by name below.
  const truckSwap = getChk('dispatch-settings-driver-truck-swap');
  const prevTruckSwap = window.AppState?.settings?.driverTruckSwap === true;

  // TASK-10126 — "Fill the job title from the project name". Written ONLY when
  // it moved (absent = off), audited old->new by name below.
  const titleFromProject = getChk('dispatch-settings-title-from-project');
  const prevTitleFromProject = window.AppState?.settings?.jobTitleFromProject === true;
  // (TASK-9370's switch above: a nested { enabled } map; its writes run through
  // the driverSaveJob callable, never through loosened rules. The HTML comment
  // that said so was dropped from the modal markup — it shipped to every user.)

  // TASK-9859 — settings.permit811ValidDays (1..365, null = not set). Written ONLY
  // when it moved; a typo is refused, never saved as 'not set'.
  const _p811DaysRaw = String(document.getElementById('dispatch-settings-811-valid-days')?.value || '').trim();
  const permit811ValidDays = _p811ValidDays(_p811DaysRaw);
  if (_p811DaysRaw && permit811ValidDays === null) {
    window.showToast?.('811 tickets are good for: enter a whole number of days from 1 to 365, or leave it blank', 'error');
    return;
  }
  const prevPermit811ValidDays = _p811ValidDays(window.AppState?.settings?.permit811ValidDays);
  // TASK-9863 alert (moved here by TASK-9862): read only when the box is enabled
  // (owner/admin); null = untouched. Named keys, written only when they moved.
  const _alOnEl = document.getElementById('dispatch-settings-811-alert-enabled');
  const _alDaysEl = document.getElementById('dispatch-settings-811-alert-days');
  const p811AlertOn = (_alOnEl && !_alOnEl.disabled) ? _alOnEl.checked === true : null;
  let p811AlertDays = null;
  if (_alDaysEl && !_alDaysEl.disabled) {
    const n = Number(String(_alDaysEl.value || '').trim());
    if (!Number.isInteger(n) || n < 1 || n > 30) { window.showToast?.('811 alert days must be a whole number from 1 to 30', 'error'); return; }
    p811AlertDays = n;
  }
  // TASK-9887 — potential retention: read only when enabled (owner/admin).
  const _potRetEl = document.getElementById('dispatch-settings-pot-retention');
  const potRetention = (_potRetEl && !_potRetEl.disabled) ? _p811PotRetentionOf(Number(_potRetEl.value)) : null;
  const prevPotRetention = _p811PotRetentionOf(window.AppState?.settings?.potentialRetentionMonths);
  const prevP811AlertOn = window.AppState?.settings?.permit811ExpiryAlertEnabled !== false;
  const prevP811AlertDays = _p811AlertDaysOf(window.AppState?.settings?.permit811ExpiryAlertDays);

  // TASK-9994 — "Show 811 warnings" (absent = off). Written only when it moved.
  const show811Warnings = getChk('dispatch-settings-811-show-warnings');
  const prevShow811Warnings = window.AppState?.settings?.show811Warnings === true;
  // TASK-10283 — "Show 811 lines to drivers" (absent = on). Written only when it moved.
  const prevShow811DriverLines = window.AppState?.settings?.show811DriverLines !== false;
  const _dlEl = document.getElementById('dispatch-settings-811-driver-lines'); // no box = no change (getChk would read a missing box as off)
  const show811DriverLines = _dlEl ? _dlEl.checked === true : prevShow811DriverLines;
  const prev811 = window.AppState?.settings?.enable811 === true;
  const enable811Changed = prev811 !== enable811;
  // TASK-4649 — same template-re-render problem as 811: the Permit Required
  // checkbox in the job form is gated at template build time.
  const prevPermitTracking = window.AppState?.settings?.enablePermitTracking === true;
  const permitTrackingChanged = prevPermitTracking !== enablePermitTracking;

  // TASK-4688 — a job card with no fields is not a job card. Refuse rather
  // than saving an empty array (which getPlacardTokens would silently ignore,
  // leaving the dispatcher sure they saved something they did not).
  if (_placardTokensDirty && !(_placardDraftTokens || []).length) {
    window.showToast?.('Pick at least one field for the job card title', 'error');
    return;
  }

  // TASK-9597 — same refusal as the card title: an empty crew text is not a text.
  if (_crewTextDirty && !(sanitizeCrewTextTokens(_crewTextDraftTokens) || []).length) {
    window.showToast?.('Pick at least one field for the crew text', 'error');
    return;
  }
  const prevCrewTextTokens = getCrewTextTokens();

  const patch = {
    enable811,
    enablePermitTracking,
    enableLoadCalculator,
    enableRoutePlanner,
    personalizedSchedule,
    hideComplianceWarnings,
    crewJobAlerts, // TASK-9202
    // TASK-4688 — Add Job form section visibility. Always written: these are
    // plain checkboxes seeded from the stored value, so writing false where the
    // key was absent is a no-op in meaning (absent = false = shown).
    hideAfeVendor: getChk('dispatch-settings-hide-afe-vendor'),
    hideDotFlags: getChk('dispatch-settings-hide-dot-flags'),
    hideTicketingMode: getChk('dispatch-settings-hide-ticketing-mode'),
    hideWaypoints: getChk('dispatch-settings-hide-waypoints'),
    hideGeofence: getChk('dispatch-settings-hide-geofence'),
    hideAttachments: getChk('dispatch-settings-hide-attachments'),
  };

  // TASK-4688 — placardTokens is written ONLY if the dispatcher actually
  // touched the control. Saving this modal for any other reason must not
  // migrate a tenant off the TASK-1385 rendering (or its ' — ' separator).
  if (_placardTokensDirty && (_placardDraftTokens || []).length) {
    patch.placardTokens = _placardDraftTokens.slice();
    // Keep the legacy boolean truthful for anything still reading it.
    // TASK-9278 — the seeded draft now leads with jobNumber, so "[0] is
    // customer" would write false for every customer-first tenant who saves.
    // The boolean means "the NAME leads with the customer": skip the number.
    patch.placardCustomerFirst = _placardDraftTokens.filter(k => k !== 'jobNumber')[0] === 'customer';
  }
  if (driverJobEdit !== prevDriverJobEdit) patch['driverJobEdit.enabled'] = driverJobEdit; // TASK-9370/9710 — field path, key in t9337DispatchKeys
  if (defaultJobStartDate !== prevDefaultJobStartDate) patch.defaultJobStartDate = defaultJobStartDate; // TASK-9407
  if (showEditor !== prevShowEditor) patch.dispatchAlertShowEditor = showEditor; // TASK-9516
  if (truckSwap !== prevTruckSwap) patch.driverTruckSwap = truckSwap; // TASK-9763 S2
  if (titleFromProject !== prevTitleFromProject) patch.jobTitleFromProject = titleFromProject; // TASK-10126
  if (permit811ValidDays !== prevPermit811ValidDays) patch.permit811ValidDays = permit811ValidDays; // TASK-9859
  if (show811Warnings !== prevShow811Warnings) patch.show811Warnings = show811Warnings; // TASK-9994
  if (show811DriverLines !== prevShow811DriverLines) patch.show811DriverLines = show811DriverLines; // TASK-10283
  if (p811AlertOn !== null && p811AlertOn !== prevP811AlertOn) patch.permit811ExpiryAlertEnabled = p811AlertOn; // TASK-9863
  if (p811AlertDays !== null && p811AlertDays !== prevP811AlertDays) patch.permit811ExpiryAlertDays = p811AlertDays; // TASK-9863
  if (potRetention !== null && potRetention !== prevPotRetention) patch.potentialRetentionMonths = potRetention; // TASK-9887
  // TASK-9597 — written ONLY when touched AND different from what is stored
  // (an untouched tenant never gains the key). Whitelisted + capped on the way out.
  const nextCrewTextTokens = _crewTextDirty ? sanitizeCrewTextTokens(_crewTextDraftTokens) : null;
  const crewTextChanged = !!nextCrewTextTokens
    && nextCrewTextTokens.join(',') !== (prevCrewTextTokens || []).join(',');
  if (crewTextChanged) patch.crewTextTokens = nextCrewTextTokens;

  // Prefer canonical persist helper (matches Inspection/HR sub-modal save pattern).
  // Falls back to direct updateDoc if helper not yet on window (e.g., during load-order race).
  let ok = false;
  try {
    if (typeof window._settingsPersist === 'function') {
      ok = await window._settingsPersist(patch);
    } else {
      const tid = window.AppState?.tenant;
      if (!tid) {
        window.showToast?.('Not logged in', 'error');
        return;
      }
      const tenantRef = doc(db, 'tenants', tid);
      const out = {};
      Object.keys(patch).forEach(k => { out['settings.' + k] = patch[k]; });
      await updateDoc(tenantRef, out);
      if (!window.AppState.settings) window.AppState.settings = {};
      Object.assign(window.AppState.settings, patch);
      ok = true;
    }
  } catch (e) {
    console.error('[Dispatch Settings] save failed', e);
    window.showToast?.('Save failed: ' + (e?.message || 'unknown'), 'error');
    return;
  }

  if (!ok) return;

  // TASK-9710 — the persist helper mirrors the patch into AppState.settings with
  // Object.assign, which turns the field-path key into a literal
  // 'driverJobEdit.enabled' property. Fold it back into the nested map.
  if ('driverJobEdit.enabled' in patch && window.AppState?.settings) {
    const _s = window.AppState.settings;
    delete _s['driverJobEdit.enabled'];
    _s.driverJobEdit = { ...(_s.driverJobEdit && typeof _s.driverJobEdit === 'object' ? _s.driverJobEdit : {}), enabled: driverJobEdit };
  }

  // TASK-9202 — `keys` alone says WHICH keys moved, never their values. For the
  // one key here that spends money and texts real phones, log old→new by name.
  // Deliberately NOT a refactor of the whole audit call — one key, two fields.
  window.logAudit?.('settings', 'SAVE_DISPATCH_SETTINGS', {
    keys: Object.keys(patch),
    ...(prevCrewJobAlerts !== crewJobAlerts
      ? { crewJobAlertsFrom: prevCrewJobAlerts, crewJobAlertsTo: crewJobAlerts }
      : {}),
    ...(prevDriverJobEdit !== driverJobEdit
      ? { driverJobEditFrom: prevDriverJobEdit, driverJobEditTo: driverJobEdit } // TASK-9370
      : {}),
    ...(prevDefaultJobStartDate !== defaultJobStartDate
      ? { defaultJobStartDateFrom: prevDefaultJobStartDate, defaultJobStartDateTo: defaultJobStartDate } // TASK-9407
      : {}),
    ...(permit811ValidDays !== prevPermit811ValidDays
      ? { permit811ValidDaysFrom: prevPermit811ValidDays, permit811ValidDaysTo: permit811ValidDays } // TASK-9859
      : {}),
    ...('show811Warnings' in patch ? { show811WarningsFrom: prevShow811Warnings, show811WarningsTo: patch.show811Warnings } : {}), // TASK-9994
    ...('show811DriverLines' in patch ? { show811DriverLinesFrom: prevShow811DriverLines, show811DriverLinesTo: patch.show811DriverLines } : {}), // TASK-10283
    ...('permit811ExpiryAlertEnabled' in patch ? { permit811ExpiryAlertEnabledFrom: prevP811AlertOn, permit811ExpiryAlertEnabledTo: patch.permit811ExpiryAlertEnabled } : {}), // TASK-9863
    ...('permit811ExpiryAlertDays' in patch ? { permit811ExpiryAlertDaysFrom: prevP811AlertDays, permit811ExpiryAlertDaysTo: patch.permit811ExpiryAlertDays } : {}), // TASK-9863
    ...('potentialRetentionMonths' in patch ? { potentialRetentionMonthsFrom: prevPotRetention, potentialRetentionMonthsTo: patch.potentialRetentionMonths } : {}), // TASK-9887
    ...(prevShowEditor !== showEditor
      ? { dispatchAlertShowEditorFrom: prevShowEditor, dispatchAlertShowEditorTo: showEditor } // TASK-9516
      : {}),
    ...(prevTruckSwap !== truckSwap
      ? { driverTruckSwapFrom: prevTruckSwap, driverTruckSwapTo: truckSwap } // TASK-9763 S2
      : {}),
    ...('jobTitleFromProject' in patch ? { jobTitleFromProjectFrom: prevTitleFromProject, jobTitleFromProjectTo: patch.jobTitleFromProject } : {}), // TASK-10126
    // TASK-9597 — it changes what every crew member receives, so log old -> new by name.
    ...(crewTextChanged
      ? { crewTextTokensFrom: prevCrewTextTokens ? prevCrewTextTokens.join(',') : '(standard)', crewTextTokensTo: nextCrewTextTokens.join(',') }
      : {}),
  });
  window.showToast?.('Dispatch settings saved', 'success');
  closeDispatchSettingsModal();

  // Placard title format changed — repaint the calendar so it applies immediately
  renderDispatchBoard();
  // TASK-9994 — the Locates window may be open under this one (its gear).
  { const m811 = document.getElementById('modal-811-submission'); if (m811 && !m811.classList.contains('hidden')) renderPermitsList(); }

  // 811 toggle changes the header layout (811 button visibility) and the job
  // form permit dropdown — reload so templates re-render. Mirrors the legacy
  // main-Settings reload guard that lived at app.js L12881-12883.
  if (enable811Changed || permitTrackingChanged) {
    setTimeout(() => location.reload(), 300);
  }
}


// ============================================
// TASK-412: Job rename — ONE canonical path (window.JobOps)
// ============================================
// A job's title is denormalized onto clockEntries (jobTitle) + tickets
// (jobName/jobTitle). Three rename surfaces used to propagate differently, leaving
// the job doc, tickets, and clock entries showing different names (live TITS
// split-brain 2026-06-01). These are the single source of truth for renames.
// Clock HOURS are never at risk — entries link by stable jobId; the name is a label.

// Chunked batch updates — a single writeBatch dies past Firestore's 500-op limit;
// a long-running job easily exceeds that across historical clock entries + tickets.
async function _jobOpsChunkedUpdate(docs, dataFor) {
  const CHUNK = 450;
  for (let i = 0; i < docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + CHUNK)) batch.update(d.ref, dataFor(d));
    await batch.commit();
  }
}

// Propagate a new title to ALL clock entries + draft/ready tickets for a job.
// NEVER touches invoiced/sent/archived tickets (frozen financial records).
// TASK-10182 — the TICKET half runs only for a biller (window.canEditMoney):
// firestore.rules pins ticket jobName / jobTitle to billers, and the server
// trigger onJobTitleTicketLabels relabels the open tickets after every job
// title change anyway. A biller keeps the browser pass for the same-title
// re-save that heals drift (renameJob), which changes no job doc and so fires
// no trigger. Best-effort: the job doc and clock entries are already saved.
async function propagateJobTitle(tenant, jobId, newTitle) {
  if (!tenant || !jobId || !newTitle) return;
  const clockSnap = await getDocs(
    query(collection(db, 'tenants', tenant, 'clockEntries'), where('jobId', '==', jobId)));
  await _jobOpsChunkedUpdate(clockSnap.docs, () => ({ jobTitle: newTitle }));
  if (!(typeof window.canEditMoney === 'function' && window.canEditMoney() === true)) return;
  try {
    await _propagateJobTitleToTickets(tenant, jobId, newTitle);
  } catch (e) {
    console.warn('[JobOps] ticket label pass skipped (server relabels open tickets):', e?.code || e?.message);
  }
}

async function _propagateJobTitleToTickets(tenant, jobId, newTitle) {
  const ticketSnap = await getDocs(
    query(collection(db, 'tenants', tenant, 'tickets'),
      where('jobId', '==', jobId), where('status', 'in', ['draft', 'ready'])));
  // NEVER rename an invoiced ticket — markAsInvoiced sets `invoiced:true` as a FLAG
  // and leaves status draft/ready (billing.js:6904), so the status filter alone
  // isn't enough to protect frozen financial records. (COD Code Reviewer #1.)
  // TASK-10182 (COD r2) — same scope + resend flag as the server relabel
  // (functions jobTitleTicketLabels relabelUpdate): skip split tickets, skip
  // already-correct labels, and mark an emailed ticket dirty. Whichever writer
  // lands first, the resend flag is never lost.
  const renamableTickets = ticketSnap.docs.filter(d => {
    const t = d.data();
    return t.invoiced !== true && t.invoiceNumber == null && t.invoicedAt == null && !t.splitFrom
      && !(t.jobName === newTitle && t.jobTitle === newTitle);
  });
  await _jobOpsChunkedUpdate(renamableTickets, (d) => {
    const t = d.data();
    return { jobName: newTitle, jobTitle: newTitle, ...((t.ticketSendLastAt || t.ticketEmailSentAt) ? { ticketEmailDirtyAt: Timestamp.now() } : {}) };
  });
}

// Full rename: update the job doc + name everywhere + audit. Used by the two
// billing rename surfaces. saveJob (dispatch form) does its own richer full-job
// save + audit and calls propagateJobTitle directly. Returns the updated job (so
// callers refresh local state without a reload), or null on no-op/invalid.
async function renameJob(tenant, jobId, newTitle, opts = {}) {
  if (!tenant || !jobId) return null; // tenant guard — don't trust a stray jobId
  newTitle = (newTitle || '').trim();
  if (!newTitle) return null;
  const jobRef = doc(db, 'tenants', tenant, 'jobs', jobId);
  const snap = await getDoc(jobRef);
  if (!snap.exists()) return null;
  const old = snap.data();
  const oldTitle = old.title || old.name || null;
  if (oldTitle === newTitle) {
    // Same title — no job-doc change or audit needed, but STILL re-propagate so an
    // explicit re-save heals any drift left by a prior partial rename (COD: the
    // no-op-skip otherwise locks a split-brain job out of self-repair).
    await propagateJobTitle(tenant, jobId, newTitle);
    return { id: jobId, ...old };
  }
  await updateDoc(jobRef, { title: newTitle, name: newTitle, updatedAt: Timestamp.now() });
  await propagateJobTitle(tenant, jobId, newTitle);
  try {
    await addDoc(collection(db, 'tenants', tenant, 'jobAuditLog'), {
      action: 'JOB_UPDATE', source: opts.source || 'unknown', timestamp: Timestamp.now(),
      user: opts.actor || window.AppState?.user?.email || 'unknown',
      jobId, jobTitle: newTitle, oldTitle, titleChanged: true,
    });
  } catch (ae) { console.warn('[JobOps] rename audit failed:', ae); }
  return { id: jobId, ...old, title: newTitle, name: newTitle };
}

// TASK-4781 — allocateJobNumber / allocateJobNumberSafe / formatJobNumber ride
// this SAME global, which is how billing.js already reaches renameJob. A `window.*`
// handoff, NEVER a cross-import: `import` with a `?v=` buster would give billing.js
// its own second copy of this module and therefore a SECOND allocator instance
// (Pitfall #1) — the precise failure mode a shared counter exists to prevent.
//
// The other SIX creation paths (app.js onboarding wizard, projects.js template,
// billing.js x3, and the server twin in functions/) must call through here.
window.JobOps = { renameJob, propagateJobTitle, allocateJobNumber, allocateJobNumberSafe, formatJobNumber, addJobDayFromBilling }; // TASK-10043 S1

// ============================================
// EXPORT / PRINT JOB SCHEDULE (TASK-516)
// Pattern mirrors roster.js showPrintListModal/printRosterList: build an HTML
// string -> window.open -> .print() for PDF; CSV via a LOCAL downloadCsv helper
// (NOT cross-imported from billing.js — Pitfall #1). Crew/trucks/equipment live
// on jobDays which may not all be in memory for old ranges, so the export QUERIES
// jobDays for the selected range, tenant-scoped (never collectionGroup).
// ============================================

// CSV-injection guard: a leading = + - @ can be interpreted as a formula by
// Excel/Sheets. Prefix with a single quote to neutralize, and always wrap.
function _csvCell(val) {
  let s = (val == null ? '' : String(val));
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  return '"' + s.replace(/"/g, '""') + '"';
}

// Local CSV download helper (no cross-module import). Mirrors the BOM + anchor
// pattern used elsewhere so Excel opens UTF-8 cleanly.
function _downloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(_csvCell).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Compute the current view's default From/To range (local YYYY-MM-DD strings).
function _defaultExportRange() {
  const cur = DispatchState.currentDate || new Date();
  let from, to;
  if (DispatchState.viewMode === 'month') {
    const y = cur.getFullYear(), m = cur.getMonth();
    from = new Date(y, m, 1);
    to = new Date(y, m + 1, 0);
  } else {
    // week (and any non-month view) — Sunday..Saturday of currentDate's week
    from = getWeekStart(cur);
    to = new Date(from);
    to.setDate(from.getDate() + 6);
  }
  return { from: formatDateISOLocal(from), to: formatDateISOLocal(to) };
}

function showExportJobsModal() {
  // Defense-in-depth: dispatcher-only (button is CSS-hidden in driver mode too).
  if (DispatchState.roleMode === 'driver') return;
  const { from, to } = _defaultExportRange();
  let modal = document.getElementById('modal-export-jobs');
  if (!modal) {
    modal = document.createElement('div');
    modal.className = 'modal hidden';
    modal.id = 'modal-export-jobs';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-small" style="padding:var(--spacing-xl);max-width:440px;">
      <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--brand-blue);">file_download</span>
        <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Export / Print Jobs</h3>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:var(--spacing-lg);line-height:1.5;font-size:0.85rem;text-align:center;">Pick a date range and a format. Defaults to the range you're viewing.</p>
      <div style="display:flex;gap:var(--spacing-md);margin-bottom:var(--spacing-md);">
        <label style="flex:1;display:flex;flex-direction:column;gap:4px;font-size:0.8rem;color:var(--text-secondary);">From
          <input type="date" id="export-jobs-from" value="${from}" style="padding:8px;border:1px solid var(--border-light);border-radius:8px;font-size:0.9rem;">
        </label>
        <label style="flex:1;display:flex;flex-direction:column;gap:4px;font-size:0.8rem;color:var(--text-secondary);">To
          <input type="date" id="export-jobs-to" value="${to}" style="padding:8px;border:1px solid var(--border-light);border-radius:8px;font-size:0.9rem;">
        </label>
      </div>
      <div style="display:flex;flex-direction:column;gap:10px;margin-bottom:var(--spacing-lg);">
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;padding:8px 12px;border-radius:8px;border:1px solid var(--border-light);">
          <input type="radio" name="export-jobs-format" value="csv" checked> <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">table_view</span> CSV (open in Excel)
        </label>
        <label style="display:flex;align-items:center;gap:10px;cursor:pointer;padding:8px 12px;border-radius:8px;border:1px solid var(--border-light);">
          <input type="radio" name="export-jobs-format" value="pdf"> <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">print</span> PDF / Print
        </label>
      </div>
      <label style="display:flex;align-items:flex-start;gap:10px;cursor:pointer;padding:8px 12px;border-radius:8px;border:1px solid var(--border-light);margin-bottom:var(--spacing-lg);">
        <input type="checkbox" id="export-jobs-bench" style="margin-top:2px;">
        <span style="font-size:0.85rem;color:var(--text-secondary);"><strong style="color:var(--text-primary);">Unassigned resources ("Bench")</strong><br>List drivers, helpers &amp; equipment NOT assigned to any job in this range — who's available.</span>
      </label>
      <div id="export-jobs-status" style="min-height:18px;font-size:0.8rem;color:var(--text-secondary);text-align:center;margin-bottom:var(--spacing-sm);"></div>
      <div style="display:flex;gap:var(--spacing-md);justify-content:center;">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-export-jobs').classList.add('hidden')" style="flex:1;max-width:140px;">Cancel</button>
        <button class="btn btn-primary" id="export-jobs-go" onclick="window.DispatchModule.exportJobs()" style="flex:1;max-width:140px;"><span class="material-icons" style="font-size:16px;">file_download</span> Export</button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
}

// perf (appColdLoadGzip, perf/app-coldload-slim2) — the Export Jobs (CSV) body
// lives in the LAZY views/dispatchExportJobs.js. Same name + same contract
// (async, no args) for window.DispatchModule.exportJobs (the export modal's
// Export button); deps are handed over, never imported (pitfall #1). A failed
// load toasts and is retried on the next tap. The driver-mode guard is still the
// body's first line, unchanged.
// A second tap while the FIRST load is in flight is dropped: before the move the
// body disabled Export synchronously on the first tap, so a double tap never
// exported twice; the load gap must not open that window.
// Editing dispatchExportJobs.js: bump its ?v= here AND dispatch.js's ?v= in app.js.
let _exportJobsLoad = null;
let _exportJobsLoading = false;
async function exportJobs() {
  if (_exportJobsLoading) return;
  let m;
  try {
    if (!_exportJobsLoad) _exportJobsLoading = true;
    m = await (_exportJobsLoad ||= __ddChunkWrap(import('./dispatchExportJobs.js?v=1')).then((mod) => {
      mod._bind({
        DispatchState, Timestamp, _downloadCsv, _resolveFleetRef, collection, db, escapeHtml,
        formatDateDisplay, getCustomerById, getDocs, isDispatchScheduleReady, jobPoList, orderBy,
        query, where
      });
      return mod;
    }));
  } catch (e) {
    _exportJobsLoad = null;
    console.error('[Dispatch] export module failed to load:', e);
    if (window.showToast) window.showToast('Could not start the export. Check your connection and try again.', 'error');
    return;
  } finally {
    _exportJobsLoading = false;
  }
  return m.exportJobs();
}

// ============================================
// JOB ACTIVITY LOG (TASK-961) — in-app "who did what to which jobs"
// Reads tenants/{tid}/jobAuditLog (the canonical job forensic record — jobs
// audit here, NOT the general auditLog). Read-only, dispatcher-gated. Last 14
// days by default; the query is single-field (timestamp range+order) so it needs
// no composite index.
// ============================================
const _JOB_ACT_LABEL = {
  JOB_CREATE: 'Created', JOB_UPDATE: 'Edited', JOB_DELETE: 'Deleted',
  JOB_RESTORE: 'Restored', JOB_ARCHIVE: 'Archived',
  CREW_ASSIGN_UPDATE: 'Crew changed', JOB_MEDIA_SAVE: 'Photos/permits',
  JOB_DAY_ADD_VIA_DUPE_GATE: 'Day added', JOB_CREATE_DUPLICATE_BYPASS: 'Dup override',
  // TASK-4780 — the date-sync trio. Without labels these fall through to
  // `String(act).replace('JOB_','')` and render as DAY_DELETE_BLOCKED_TICKETED.
  JOB_DAY_DELETE: 'Days deleted',
  JOB_DAY_DELETE_BLOCKED_TICKETED: 'Days kept (ticketed)',
  JOB_DAY_DELETE_DECLINED: 'Days kept',
  // TASK-9943 — add-day and the remove-day attempts that did not remove.
  JOB_DAY_ADD: 'Day added',
  JOB_DAY_REMOVE_REFUSED: 'Remove day refused',
  JOB_DAY_REMOVE_CANCELLED: 'Remove day cancelled',
  // TASK-10086-E follow-up — Cancel / Restore day (board, Claude, Billing review).
  JOB_DAY_CANCEL: 'Day cancelled', JOB_DAY_RESTORE: 'Day restored',
  // TASK-9585 S7 / S7d — permit cost yes and "not billable".
  PERMIT_COST_CONFIRMED: 'Permit cost confirmed',
  PERMIT_COST_NOT_BILLABLE: 'Permit cost not billable',
  PERMIT_COST_REINSTATED: 'Permit cost put back (waiting for a yes)',
};
const _JOB_ACT_CLASS = {
  JOB_CREATE: 'create', JOB_UPDATE: 'update', JOB_DELETE: 'delete',
  JOB_RESTORE: 'restore', JOB_ARCHIVE: 'update',
  // Only the one that actually destroyed something gets the red chip; the two
  // "kept" rows are the refusals and must not read as a loss.
  JOB_DAY_DELETE: 'delete',
};

// TASK-1615 — names the stops the map refused to plot. Reached from the
// "N with a bad location" chip in the map header. Read-only: it points the
// dispatcher at the job so a human fixes the address, and deliberately does
// NOT offer to repair the coordinate, because whatever wrote these is still
// out there and would just re-break the row.
function showBadLocationJobs() {
  const bad = DispatchState._badLocationJobs || [];
  if (!bad.length) return;
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) { modal = document.createElement('div'); modal.id = 'dispatch-modal-overlay'; modal.className = 'modal'; document.body.appendChild(modal); }
  modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
  modal.classList.remove('hidden');
  const rows = bad.map(b => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;">
        <div style="min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${escapeHtml(b.title)}</div>
          ${b.stops.map(sp => `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;">
            ${escapeHtml(sp.name)} — <span style="font-family:'Courier New',Courier,monospace;color:var(--action-danger);">${escapeHtml(String(sp.lat))}, ${escapeHtml(String(sp.lng))}</span>
          </div>`).join('')}
          ${b.stillMapped ? '<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Other destinations on this job are still on the map.</div>' : '<div style="font-size:0.72rem;color:var(--action-danger);margin-top:4px;">This job is not on the map.</div>'}
        </div>
        <button type="button" class="btn btn-sm btn-secondary" style="flex-shrink:0;" data-bad-loc-job="${escapeHtml(b.jobId)}" data-bad-loc-jobday="${escapeHtml(b.jobDayId)}">Open job</button>
      </div>
    </div>`).join('');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:88dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:var(--action-danger);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">
            <span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">wrong_location</span> Left off the map
          </h3>
          <button class="btn-icon" onclick="document.getElementById('dispatch-modal-overlay').classList.add('hidden')" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="font-size:0.72rem;opacity:0.9;margin-top:3px;">These destinations store a coordinate outside the real range, so plotting them would put a pin somewhere wrong. Re-enter the address on the job to fix it.</div>
      </div>
      <div style="overflow-y:auto;flex:1;padding:12px 16px;background:var(--bg-page);" id="bad-location-list">${rows}</div>
    </div>`;
  // Delegated — one listener for the whole list, re-bound each time the modal
  // is rebuilt (innerHTML above discards the previous nodes and their handlers).
  document.getElementById('bad-location-list')?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bad-loc-job]');
    if (!btn) return;
    modal.classList.add('hidden');
    window.DispatchModule.showJobDetails(btn.dataset.badLocJob, btn.dataset.badLocJobday);
  });
}

// TASK-4786 — names the stops that have NO coordinate at all, reached from the
// "N stops not plotted" chip. This is the honest half of one-pin-per-stop: the
// map can now draw every stop it HAS, which makes the ones it doesn't have
// conspicuous rather than invisible. 771 of 2,541 TITS waypoints are in this
// bucket as of 2026-09-08, most of them unresolved maps.app.goo.gl short links
// (TASK-4805 fixed the save path; the existing rows are still blank).
//
// Read-only on purpose, same as showBadLocationJobs: it points at the job so a
// human re-enters the address. It does NOT geocode from here — a silently
// wrong pin on a demo map is worse than a stop that says it is missing.
function showUnlocatedStops() {
  const rowsData = DispatchState._unlocatedStopJobs || [];
  if (!rowsData.length) return;
  // escapeHtml() is NOT attribute-safe (it skips quotes) — anything landing in
  // a data-* attribute goes through window.escAttr.
  const escA = window.escAttr || escapeHtml;
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) { modal = document.createElement('div'); modal.id = 'dispatch-modal-overlay'; modal.className = 'modal'; document.body.appendChild(modal); }
  modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
  modal.classList.remove('hidden');
  const totalStops = rowsData.reduce((n, r) => n + r.stops.length, 0);
  const rows = rowsData.map(b => `
    <div style="padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;background:var(--bg-card);">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;">
        <div style="min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.9rem;">${escapeHtml(b.title)}</div>
          ${b.stops.map(sp => `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:3px;">
            <strong style="color:var(--text-primary);">Destination ${sp.stopNumber}:</strong> ${escapeHtml(sp.name)}${sp.url ? ' <span style="color:var(--text-secondary);">(map link never resolved to a coordinate)</span>' : ''}
            ${sp.suggestion && sp.id ? `<div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-top:4px;color:var(--text-primary);">
              <span><span class="material-icons" style="font-size:14px;vertical-align:text-bottom;color:var(--status-warning);">help_outline</span> Maybe: ${escapeHtml(sp.suggestion.label)} <span style="color:var(--text-secondary);">(${escapeHtml(sp.suggestion.why)})</span></span>
              <button type="button" class="btn btn-sm btn-primary" data-unloc-use="${escA(sp.id)}" data-unloc-usejob="${escA(b.jobId)}">Use this location</button>
            </div>` : ''}
          </div>`).join('')}
          ${b.stillMapped
            ? '<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">This job’s other destinations ARE on the map — the route you see is incomplete.</div>'
            : '<div style="font-size:0.72rem;color:var(--text-primary);margin-top:4px;">This job is not on the map at all.</div>'}
        </div>
        <button type="button" class="btn btn-sm btn-secondary" style="flex-shrink:0;" data-unloc-job="${escA(b.jobId)}" data-unloc-jobday="${escA(b.jobDayId)}">Open job</button>
      </div>
    </div>`).join('');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:88dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:linear-gradient(135deg,#D97706 0%,#B45309 100%);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">
            <span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">location_off</span> ${totalStops} destination${totalStops !== 1 ? 's' : ''} not plotted
          </h3>
          <button class="btn-icon" onclick="document.getElementById('dispatch-modal-overlay').classList.add('hidden')" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="font-size:0.72rem;opacity:0.95;margin-top:3px;">These destinations have never been geocoded, so there is no coordinate to pin. Open the job and re-enter the address — or, where we found a likely match, check it and use it.</div>
      </div>
      <div style="overflow-y:auto;flex:1;padding:12px 16px;background:var(--bg-page);" id="unlocated-stop-list">${rows}</div>
    </div>`;
  // Delegated — one listener for the whole list, re-bound each time the modal is
  // rebuilt (innerHTML above discards the previous nodes and their handlers).
  document.getElementById('unlocated-stop-list')?.addEventListener('click', async (e) => {
    // TASK-9497 — confirm the server's suggestion for one stop. Only WHICH stop
    // goes up; the server applies the coordinate it stored itself.
    const use = e.target.closest('[data-unloc-use]');
    if (use) {
      if (use.disabled) return;
      use.disabled = true;
      use.textContent = 'Placing…';
      try {
        const fn = httpsCallable(functions, 'applyStopLocationSuggestion');
        await fn({ tenantId: DispatchState.tenantId, jobId: use.dataset.unlocUsejob, stopId: use.dataset.unlocUse });
        use.textContent = 'Placed on the map';
        window.showToast?.('Destination placed on the map.', 'success');
      } catch (err) {
        use.disabled = false;
        use.textContent = 'Use this location';
        window.showToast?.(err?.message || 'Could not place that destination. Open the job and enter the address.', 'error');
      }
      return;
    }
    const btn = e.target.closest('[data-unloc-job]');
    if (!btn) return;
    modal.classList.add('hidden');
    window.DispatchModule.showJobDetails(btn.dataset.unlocJob, btn.dataset.unlocJobday);
  });
}

async function openJobActivityLog() {
  // Defense-in-depth: dispatcher-only (the button is CSS-hidden in driver mode
  // too), mirroring showExportJobsModal. jobAuditLog read is gated on the
  // `dispatch` module in firestore.rules — matching the /jobs create gate,
  // because job history is dispatch-owned operational data, NOT an admin crown
  // jewel. So anyone who can work the board can see who changed what.
  if (DispatchState.roleMode === 'driver') return;
  let modal = document.getElementById('dispatch-modal-overlay');
  if (!modal) { modal = document.createElement('div'); modal.id = 'dispatch-modal-overlay'; modal.className = 'modal'; document.body.appendChild(modal); }
  modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;max-height:88dvh;display:flex;flex-direction:column;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, var(--brand-blue) 0%, #2563EB 100%);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">
            <span class="material-icons" style="font-size:20px;vertical-align:text-bottom;">history</span> Job Activity
          </h3>
          <button class="btn-icon" onclick="document.getElementById('dispatch-modal-overlay').classList.add('hidden')" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div style="font-size:0.72rem;opacity:0.85;margin-top:3px;">Who created, edited, or deleted jobs — last 14 days</div>
      </div>
      <div style="padding:12px 16px;border-bottom:1px solid var(--border-light);flex-shrink:0;">
        <input type="text" id="job-activity-search" class="form-control" placeholder="Search job, customer, person…" oninput="window.DispatchModule.filterJobActivity(this.value)" style="font-size:0.85rem;">
      </div>
      <div id="job-activity-list" style="overflow-y:auto;flex:1;padding:8px 12px;">
        <div class="loading-indicator" style="padding:32px;text-align:center;"><div class="loading-spinner"></div><p>Loading history…</p></div>
      </div>
    </div>`;
  try {
    const tenantId = DispatchState.tenantId;
    const since = new Date(Date.now() - 14 * 86400000);
    const snap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'jobAuditLog'),
      where('timestamp', '>=', since), orderBy('timestamp', 'desc'), limit(300)
    ));
    DispatchState._jobActivity = _collapseJobActivity(snap.docs.map(d => {
      const x = d.data();
      const ts = x.timestamp?.toDate ? x.timestamp.toDate() : new Date(x.timestamp);
      // TASK-4735 slice 2a — 🔴 THE PROVENANCE GATE, and it is load-bearing.
      // `jobAuditLog` create validates only the `user` field (jobAuditActorIsSelf,
      // rules:760); every OTHER field on the row is unvalidated, `serverAuthored`
      // included. So a member CAN write a row signed with their OWN email — which
      // passes the pin — carrying `serverAuthored: true` and
      // `deletedByEmail: 'coworker@…'`. If the reader preferred those fields
      // unconditionally it would re-open, through the reader, exactly the forgery
      // the rules half closed. (COD Code Reviewer caught this in the first draft.)
      //   `user === 'system'` is the ONE marker a client cannot produce: the pin
      // requires `user` to equal the caller's token email (never the 6 characters
      // 'system' — a token email is a real address), or their uid, or the anon
      // literals 'driver-pin'/'unknown'. So it, and only it, gates the breadcrumb
      // fields and the collapse below.
      const serverAuthored = x.user === 'system';
      const breadcrumbWho = serverAuthored ? (x.deletedByEmail || x.deletedByName || '') : '';
      return {
        ms: ts.getTime(),
        // A server row names the person from its breadcrumb; without one it says
        // 'system', which is the honest answer for a delete that bypassed the
        // product. A client row keeps naming whoever signed it.
        who: String(breadcrumbWho || x.user || x.actor || 'system').split('@')[0],
        act: x.action || x.type || '',
        item: x.jobTitle || x.title || '',
        cust: x.customerName || '',
        detail: x.detail || x.summary || '',
        sched: x.startDate ? (x.startDate.toDate ? x.startDate.toDate().toISOString().slice(0, 10) : String(x.startDate).slice(0, 10)) : '',
        _jobId: x.jobId || '',
        _server: serverAuthored,
        // The FULL identity, for the collapse to compare on. `who` is the
        // display form and has had the domain cut off it, so comparing `who`
        // would treat chris@trippintrucking.com and chris@dispatchdeck.app as
        // the same person — and would treat a member literally called
        // system@example.com as the unattributed 'system' placeholder. Both
        // flagged by COD (Code Reviewer + AuthZ Sentinel, 2026-09-08). Empty
        // when the row names nobody, which is what stops an unattributed row
        // from ever pairing.
        _ident: String(breadcrumbWho || x.user || x.actor || '').trim().toLowerCase(),
      };
    }));
    renderJobActivityList('');
  } catch (err) {
    console.error('[Dispatch] Job activity load error:', err);
    const el = document.getElementById('job-activity-list');
    if (el) el.innerHTML = '<div style="padding:32px;text-align:center;color:var(--text-secondary);">Couldn\'t load history — check your connection and try again.</div>';
  }
}

// TASK-4735 slice 2a — collapse rows that describe ONE event.
//
// Slice 1's onDelete trigger writes a JOB_DELETE row server-side and the two
// client writers still write their own (deliberately, and temporarily — the
// trigger had to be proven live BEFORE the client writers come out, so there is
// never a window with no trail). To a dispatcher reading Job Activity that
// looks like the job was deleted twice, which is exactly the kind of thing that
// makes people stop believing a trail. Three COD lanes flagged it on slice 1.
//
// Non-lossy by construction: matched rows are MERGED, not dropped. The kept row
// takes the first non-empty value of every display field, so whichever row
// carried the job title supplies it. Nothing a reader could have seen disappears.
//
// Scoped tight so it cannot hide two genuinely separate events:
//   • same action AND same non-empty jobId — never collapses across jobs, and
//     never collapses rows that carry no jobId at all;
//   • within 2 minutes — a client row and its trigger row land seconds apart;
//   • one row must be server-authored and the other not, so two client rows are
//     never folded together;
//   • 🔴 both rows must ALREADY NAME THE SAME PERSON, compared on the FULL
//     identity (`_ident`), never on the display name. See below.
//   • at most ONE client row merges into ONE server row, so two real deletes of
//     a restored job minutes apart still render as two.
// After slice 2b removes the client writers this becomes a no-op rather than
// something that has to be unwound.
//
// 🔴 WHY THE MERGE NEVER LENDS A NAME (COD Code Reviewer, 2026-09-08 — a real
// hole in the first draft). The client audit row is written BEFORE the delete so
// the snapshot survives it, which means a delete that FAILS still leaves an
// orphan client row naming the person who tried. The first draft filled a
// 'system' server row's name from the paired client row — so an orphan attempt
// by A, followed within 2 minutes by a RAW delete of that same job by B, would
// have rendered as a single row reading "A deleted this job." A reader-side
// convenience must never manufacture attribution the data does not contain. So
// the merge now REQUIRES the two rows to agree on the person, and when they do
// not it shows both and lets the dispatcher see the discrepancy.
const _JOB_ACT_MERGE_MS = 120000;
function _collapseJobActivity(rows) {
  const out = [];
  for (const r of rows) {
    let merged = false;
    if (r._jobId) {
      for (let i = out.length - 1; i >= 0; i--) {
        const p = out[i];
        if (p.ms - r.ms > _JOB_ACT_MERGE_MS) break; // rows arrive newest-first
        if (p._merged) continue;                    // bucket already closed
        if (p.act !== r.act || p._jobId !== r._jobId) continue;
        if (p._server === r._server) continue;      // two client rows are two events
        // Must already agree on WHO, compared on the FULL identity and only when
        // there IS one. The bare literal 'system' is the "named nobody"
        // placeholder, not an identity, so it never pairs — that is what stops a
        // nameless server row being folded into somebody's orphaned delete
        // attempt. A member whose address really is system@example.com is a
        // different string and is unaffected.
        if (!p._ident || p._ident === 'system' || p._ident !== r._ident) continue;
        // Fill the kept row's gaps from the merged one — display fields only.
        // `who` is deliberately NOT among them: they already match, and lending
        // a name across rows is how a reader invents attribution.
        if (!p.item && r.item) p.item = r.item;
        if (!p.cust && r.cust) p.cust = r.cust;
        if (!p.detail && r.detail) p.detail = r.detail;
        if (!p.sched && r.sched) p.sched = r.sched;
        p._merged = true;   // closes the bucket: exactly one pairing, no third row
        merged = true;
        break;
      }
    }
    if (!merged) out.push(r);
  }
  return out;
}

function renderJobActivityList(term) {
  const el = document.getElementById('job-activity-list');
  if (!el) return;
  const t = (term || '').toLowerCase();
  const rows = (DispatchState._jobActivity || []).filter(r =>
    !t || (r.who + ' ' + r.act + ' ' + r.item + ' ' + r.cust + ' ' + r.detail).toLowerCase().includes(t)
  );
  if (!rows.length) { el.innerHTML = '<div style="padding:32px;text-align:center;color:var(--text-secondary);font-size:0.9rem;">No matching activity.</div>'; return; }
  const esc = (s) => (s == null ? '' : String(s)).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const fmtDay = (ms) => new Date(ms).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const fmtTime = (ms) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const BG = { create: '#EAF7EE', update: '#FDF3E3', delete: '#FCECEC', restore: '#E7EEFE' };
  const FG = { create: '#15803D', update: '#B45309', delete: '#B91C1C', restore: '#1D4ED8' };
  let html = '', curDay = '';
  for (const r of rows) {
    const dayKey = new Date(r.ms).toDateString();
    if (dayKey !== curDay) { curDay = dayKey; html += `<div style="font-size:0.7rem;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);padding:14px 4px 6px;">${esc(fmtDay(r.ms))}</div>`; }
    const cls = _JOB_ACT_CLASS[r.act] || '';
    const label = _JOB_ACT_LABEL[r.act] || String(r.act).replace('JOB_', '');
    html += `<div style="display:flex;gap:10px;padding:9px 6px;border-bottom:1px solid var(--border-light);">
      <div style="font-size:0.78rem;color:var(--text-secondary);min-width:60px;font-variant-numeric:tabular-nums;">${esc(fmtTime(r.ms))}</div>
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:7px;flex-wrap:wrap;">
          <span style="font-weight:700;font-size:0.85rem;text-transform:capitalize;">${esc(r.who)}</span>
          <span style="font-size:0.62rem;font-weight:700;text-transform:uppercase;letter-spacing:0.03em;padding:2px 6px;border-radius:5px;background:${BG[cls] || '#EEF1F6'};color:${FG[cls] || '#475569'};">${esc(label)}</span>
        </div>
        ${r.item ? `<div style="font-size:0.85rem;margin-top:2px;word-break:break-word;">${esc(r.item)}${r.cust ? ` <span style="color:var(--text-secondary);">· ${esc(r.cust)}</span>` : ''}</div>` : (r.cust ? `<div style="font-size:0.85rem;margin-top:2px;color:var(--text-secondary);">${esc(r.cust)}</div>` : '')}
        ${r.sched ? `<span style="display:inline-block;font-size:0.7rem;color:var(--brand-blue);font-weight:600;margin-top:3px;">scheduled ${esc(r.sched)}</span>` : ''}
        ${r.detail ? `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;word-break:break-word;">${esc(r.detail)}</div>` : ''}
      </div>
    </div>`;
  }
  el.innerHTML = html;
}

function filterJobActivity(term) { renderJobActivityList(term || ''); }

// Picking a project prefills the job's PO from the project's standing PO — but only
// into an empty PO field. If the dispatcher already typed a PO, that one wins and we
// say nothing; a job's own PO is deliberate and must never be silently replaced.
// The hint line makes the inherited value visible BEFORE save so it can be overridden.
// Opening an existing job for edit: put its PO in the box, and re-establish our claim
// on it IF it demonstrably came from the job's project. Without this, a job that
// inherited Project A's PO looks hand-typed on reopen, so reassigning it to Project B
// would keep A's number — a stale PO on a live billing surface.
function _restoreInheritedPoMarker(job) {
  const poInput = document.getElementById('job-po');
  if (!poInput) return;
  const po = String(job?.poNumber || '');
  poInput.value = po;
  delete poInput.dataset.autofilledPo;
  delete poInput.dataset.autofilledPoFrom; // TASK-4579 — a stored PO is the job's, not a customer's blanket
  // TASK-4782 — opening an existing job must evaluate its STORED PO, not just a
  // freshly typed one. This is where an over-length PO already in the record
  // surfaces: the dispatcher sees the QuickBooks warning on the job that has the
  // problem, instead of finding out when the invoice syncs without a PO.
  refreshJobPoSuggestions();
  onJobPoInput();
  if (!po.trim() || !job?.projectId) return;
  const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p.id === job.projectId);
  if (proj && String(proj.poNumber || '').trim() === po.trim()) {
    poInput.dataset.autofilledPo = po.trim();
  }
}

// ============================================
// PO NUMBER — entry hygiene for a money field (TASK-4782)
// ============================================
// A PO number is how a customer's AP department finds this invoice. A malformed
// one is an invoice that sits unpaid while everybody assumes it was received.
//
// MEASURED across all 88 tenants 2026-09-08 (scripts/po-vocabulary-audit.cjs,
// read-only): 826 PO-bearing documents, 159 distinct exact values, 140 distinct
// group keys — so 15 groups hold more than one spelling of the SAME PO. 18
// distinct values (32 documents) are longer than QuickBooks will accept.
//
// TWO RULES GOVERN EVERYTHING BELOW.
//   1. SUGGEST, NEVER REWRITE. The only transform applied to a stored value is
//      trim (normalizePoForSave). Everything else — stripping a "PO #" prefix,
//      changing case, splitting two POs out of one field, blanking "UNKNOWN" —
//      is a judgement call that belongs to a human, because each of them changes
//      what the customer's AP department is being asked to match.
//   2. WARN, NEVER BLOCK. Chris's standing rule is that missing or awkward
//      paperwork never stops the truck.

// QuickBooks Online caps Invoice.PONumber at 15 characters (Intuit's Invoice
// entity reference — this is THEIR limit, not one we invented). A longer PO
// cannot ride the sync, so the dispatcher has to see that while the customer is
// still on the phone rather than after the invoice is short a PO.
const QBO_PO_MAX_CHARS = 15;

// The COMPARISON key. It exists to answer one question: "is what you just typed
// another spelling of a PO this customer already uses?" It is never stored, never
// suggested, and never written anywhere — poGroupKey('ND25TU1-U1') is 'nd25tu1u1'
// and 'nd25tu1u1' must never reach Firestore or QuickBooks.
//
// It is deliberately EXACT-after-folding, not fuzzy. Live data is full of real
// POs one character apart (ND25TU0-U1 vs ND25TU1-U1, 25-3245 vs 25-3295), and a
// fuzzy match between two live corridors is a bill pointed at the wrong
// authorization.
//
// IT FOLDS CASE AND WHITESPACE ONLY. PUNCTUATION STAYS SIGNIFICANT, and that is
// deliberate: `smartAssistant.js:18` already recorded the finding that PO-12345
// and PO12345 are genuinely different POs, and a key that strips punctuation
// would offer to swap one for the other on a money field.
//
// MEASURED COST of keeping punctuation (2026-09-08, all 88 tenants): folding
// punctuation too would group 15 of the live spelling-collision groups; folding
// only case + whitespace groups 13. The two it gives up are "326.060 JWCG" vs
// "326 060 JWCG" and a pair of "no po required per eric" placeholder phrases.
// Missing two is the cheaper error — a missed suggestion costs a dispatcher one
// keystroke, a wrong one costs an invoice.
function poGroupKey(v) {
  return String(v == null ? '' : v)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s  -​﻿]+/g, '');
}

// The ONLY transform applied to a PO on its way into storage.
//
// Trim, and stop. 18 live documents carry a stray leading/trailing space purely
// because saveJob stored `input.value` raw, and that whitespace is the sole
// reason ' ND25TU0-U1' and 'ND25TU0-U1' are two values instead of one. Fixing
// that at the point of entry is lossless and visible — the dispatcher already
// sees the trimmed text in the box.
//
// Unicode spaces (NBSP et al) count as whitespace because they are invisible; a
// PO that differs from another only by a character nobody can see is the exact
// failure this is here to stop. INTERNAL whitespace is left alone: '26-134  --
// NOT 26-99' keeps its double space, because collapsing it is editing somebody's
// note, not tidying a field.
function normalizePoForSave(raw) {
  return String(raw == null ? '' : raw).replace(/^[\s  -​﻿]+|[\s  -​﻿]+$/g, '');
}

// What, if anything, to tell the dispatcher about the PO they just typed.
// Returns findings only — it never mutates the value and never sets a blocking
// flag. `existingPos` is the PO vocabulary already in use for THIS customer.
function poEntryIssues(value, existingPos) {
  const trimmed = normalizePoForSave(value);
  const out = { value: trimmed, len: trimmed.length, tooLong: false, existingSpelling: null };
  if (!trimmed) return out;

  out.tooLong = trimmed.length > QBO_PO_MAX_CHARS;

  // "You already use this PO, spelled differently." Only fires on an exact
  // group-key match, and never when the typed value is already exactly right —
  // there would be nothing to offer.
  const key = poGroupKey(trimmed);
  if (key) {
    for (const candidate of (existingPos || [])) {
      const c = normalizePoForSave(candidate);
      if (!c || c === trimmed) continue;
      if (poGroupKey(c) === key) { out.existingSpelling = c; break; }
    }
  }
  return out;
}

// The PO vocabulary already in use for the job form's selected customer.
//
// HONEST LIMIT: DispatchState.jobs is the WINDOWED admin stream (TASK-424) unless
// the dispatcher has hit "Load older", so this is "POs used recently for this
// customer", not the customer's whole history. That is the right corpus anyway —
// the PO codes carry the year inside them (ND25… rolls to ND26… every January),
// so an expired corridor is not something to suggest. The copy says "recent" so
// nobody reads it as complete.
function getCustomerPoVocabulary() {
  const customerId = document.getElementById('job-customer')?.value || '';
  // COD Code Reviewer — with no customer chosen this used to fall through the
  // filter and offer EVERY customer's POs. A PO belongs to one customer's AP
  // department; suggesting another customer's is both noise and a way to type
  // the wrong one. No customer, no vocabulary.
  if (!customerId) return [];
  const editingJobId = document.getElementById('job-id')?.value || '';
  const seen = new Map(); // group key -> most recent exact spelling
  for (const j of (DispatchState.jobs || [])) {
    if (j.id === editingJobId) continue;
    if ((j.customerId || '') !== customerId) continue;
    for (const po of jobPoList(j)) { // TASK-9811 S2 — extras are this customer's POs too
      const key = poGroupKey(po);
      if (key && !seen.has(key)) seen.set(key, po);
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

function refreshJobPoSuggestions() {
  const list = document.getElementById('job-po-datalist');
  if (!list) return;
  // A PO lands inside value="" — escapeHtml does NOT escape quotes, so this must
  // go through window.escAttr ([[lesson_escapehtml_is_not_attribute_safe]]).
  const escA = window.escAttr || escapeHtml;
  list.innerHTML = getCustomerPoVocabulary()
    .map(po => `<option value="${escA(po)}"></option>`)
    .join('');
}

// Render the hint under the PO box. Reuses the existing `.smart-warning` styles
// so it sits in the same visual family as the rest of the form's guidance —
// nothing new in styles.css.
function onJobPoInput() {
  const input = document.getElementById('job-po');
  const host = document.getElementById('job-po-hint');
  if (!input || !host) return;

  const issues = poEntryIssues(input.value, getCustomerPoVocabulary());
  const rows = [];
  // A PO is other users' text going into an attribute — escapeHtml skips quotes,
  // so the attribute path uses window.escAttr.
  const escA = window.escAttr || escapeHtml;

  if (issues.existingSpelling) {
    // The dispatcher clicks "Use it" or ignores this. We never swap the value
    // for them — that is the difference between an offer and a rewrite.
    rows.push(`
      <div class="smart-warning smart-warning--similar">
        <span class="material-icons smart-warning-icon">merge_type</span>
        <span class="smart-warning-text">This customer already uses <strong>${escapeHtml(issues.existingSpelling)}</strong>. Same PO, different spelling — matching it keeps the billing together.</span>
        <button type="button" class="smart-warning-action" data-po="${escA(issues.existingSpelling)}" onclick="window.DispatchModule.useExistingPoSpelling(this.dataset.po)">Use it</button>
      </div>`);
  }

  if (issues.tooLong) {
    // WARN, NOT BLOCK, and say what actually happens. The dispatcher can save
    // this exactly as typed; what they cannot do is get it into QuickBooks.
    rows.push(`
      <div class="smart-warning smart-warning--duplicate">
        <span class="material-icons smart-warning-icon">warning</span>
        <span class="smart-warning-text"><strong>${issues.len} characters — QuickBooks accepts ${QBO_PO_MAX_CHARS}.</strong> This saves fine, but it will not reach the invoice in QuickBooks. If part of this is a note, put the note in Notes below and leave just the PO here.</span>
      </div>`);
  }

  host.innerHTML = rows.join('');
}

function useExistingPoSpelling(po) {
  const input = document.getElementById('job-po');
  if (!input) return;
  input.value = po;
  // A hand-picked value is the dispatcher's, not ours — drop the autofill claim
  // so applyProjectPo() cannot later overwrite it (see the comment there).
  delete input.dataset.autofilledPo;
  onJobPoInput();
  applyJobRequiredFieldWarnings();
}

// ============================================
// TASK-9391 Slice 2 — Projects → New Job honours the project's "New job defaults"
// ============================================
// projects.js (resolveProjectNewJobPrefill) does the reading: it finds the
// project's latest job (last live jobDay by date) and hands it here on
// window.__pendingProjectLink as `{ ..., defaults: {carry, jobTemplateId},
// source: {job, day} }`. This side only turns that into form state, through the
// same machinery Edit and Copy already use — nothing here writes to Firestore,
// and the save is the ordinary saveJob CREATE branch (job doc + noon-anchored
// jobDay batch). So the source job's history cannot be touched from here.
//
// TASK-9392 builds on this: a customer-request draft opens the form through
// openNewJobWithProjectPrefill() with the same link shape plus its own facts.

// Pure: link → what to put in the form. null = no source job, which means
// "today's behaviour" (customer, PO, project location and nothing else).
// Customer-bound groups (contact, price book, AFE/vendor/PO fallback) carry only
// when the source job belongs to the project's customer — a contact or price
// book is the customer's, and a project can hold jobs for more than one.
function _buildProjectNewJobCarry(link) {
  const src = link && link.source;
  const job = src && src.job;
  if (!job || !job.id) return null;
  const carry = (link.defaults && link.defaults.carry) || {};
  const on = (k) => carry[k] === true;
  const day = (src.day && src.day.jobId === job.id) ? src.day : null;
  const sameCustomer = !!link.customerId && job.customerId === link.customerId;
  // Legacy GAS jobs have no jobDays and keep crew on the job doc (same fallback
  // duplicateJob uses).
  const crewSrc = day || job;
  const arr = (v) => Array.isArray(v) ? v.filter(Boolean).map(String) : [];
  const waypoints = (Array.isArray(job.waypoints) ? job.waypoints : [])
    .filter(wp => wp && (wp.name || wp.url))
    .map(wp => ({ name: wp.name || '', url: wp.url || '', lat: wp.lat ?? null, lng: wp.lng ?? null }));
  const wellName = [job.wellName, job.wellNumber].filter(Boolean).join(' ');
  const lis = (day && day.lineItemSelections && typeof day.lineItemSelections === 'object') ? day.lineItemSelections : {};
  const notes = String(job.notes || '');
  const dayMs = day ? _jobDayDateMs(day) : null;

  return {
    sourceJobId: job.id,
    sourceLabel: formatJobNumber(job) || String(job.title || 'the last job'),
    sourceDayMs: dayMs,
    customerId: link.customerId || null,
    refs: sameCustomer ? {
      poNumber: String(job.poNumber || ''),
      poVisible: job.poVisible === true,
      afeNumber: String(job.afeNumber || ''),
      vendorNumber: String(job.vendorNumber || '')
    } : null,
    location: (on('location') && (waypoints.length || job.location || wellName)) ? {
      location: String(job.location || ''), wellName, waypoints
    } : null,
    jobType: on('jobType') ? {
      type: job.type || 'job',
      splitBy: job.splitBy === 'vehicle' ? 'vehicle' : 'combined',
      interstate: job.interstate === true,
      hazmat: job.hazmat === true,
      liquidTank: job.liquidTank === true
    } : null,
    contact: (on('contact') && sameCustomer && job.poc && job.poc.name) ? {
      name: job.poc.name || '', role: job.poc.role || '', phone: job.poc.phone || '',
      phone2: job.poc.phone2 || '', email: job.poc.email || ''
    } : null,
    rates: (on('rates') && sameCustomer && day && (day.selectedContractId || Object.keys(lis).length)) ? {
      contractId: day.selectedContractId || null,
      lineItemSelections: { ...lis }
    } : null,
    times: on('times') ? {
      allDay: job.allDay !== false,
      defaultStart: job.defaultStart || '',
      defaultEnd: job.defaultEnd || ''
    } : null,
    crew: on('crew') ? { drivers: arr(crewSrc.drivers), helpers: arr(crewSrc.helpers) } : null,
    equipment: on('equipment') ? {
      trucks: arr(crewSrc.trucks), trailers: arr(crewSrc.trailers), assets: arr(crewSrc.assets),
      assetQtys: { ...(crewSrc.assetQtys || {}) }, assetDirections: { ...(crewSrc.assetDirections || {}) },
      sourceRental: _carriedRentalSource(job) // TASK-9363 — no second deploy of rentals still out
    } : null,
    // Notes carried → into the box. Notes NOT carried → the grey "Previous notes"
    // panel with Use these, so the old instructions never reach a driver unread.
    notes: on('notes') ? notes : null,
    prevNotes: (!on('notes') && notes.trim()) ? notes : null
  };
}

// ============================================
// TASK-4808 — the job Notes box grows as you type
// ============================================
// GROW-ONLY, on purpose. The textbook `height='auto'; height=scrollHeight` two-
// step stomps a manual drag: drag the box to 400px, type one letter, and it
// snaps back to fit the text. So the box only ever gets TALLER on its own; it
// keeps `resize: vertical` (the TASK-4804 grip) because dragging is the only way
// to make it smaller. Capped by CSS (`.modal textarea.textarea-grow`, 40dvh) so
// a long note scrolls inside the box instead of pushing Create off the screen.
//
// PURE. The inline height to set, or null to leave the box alone.
// `borderY` is added because the app is `box-sizing: border-box` and
// scrollHeight excludes borders — without it the box stops 2px short and shows
// a scrollbar on every line.
function _jobNotesGrowTarget(scrollH, clientH, borderY, maxPx) {
  const sh = Number(scrollH) || 0;
  const ch = Number(clientH) || 0;
  if (sh <= 0 || ch <= 0) return null;   // not rendered — any number here is a lie
  if (sh <= ch) return null;             // text fits (or the user dragged it taller): never shrink
  let h = sh + (Number(borderY) || 0);
  if (Number.isFinite(maxPx) && maxPx > 0) h = Math.min(h, maxPx);
  return Math.ceil(h);
}

// DOM half. `reset: true` clears the inline height first — required after
// form.reset() / on every new form, or the box opens at the PREVIOUS job's size.
// A hidden textarea (modal not shown yet, the editJob path) has clientHeight 0,
// so sizing is retried on the next frames until it is actually laid out.
// COD r1 — a height this function did not write means the dispatcher dragged the
// grip, taller OR shorter. From then on the box is theirs until the form resets:
// auto-grow stops and long text scrolls inside it, rather than a drag-shorter
// being undone by the next keystroke.
function _syncJobNotesHeight(opts) {
  const ta = document.getElementById('job-notes');
  if (!ta) return;
  _syncPhotosHiddenHint(); // TASK-10224 — every notes change passes through here
  if (opts && opts.reset) { ta.style.height = ''; delete ta.dataset.autoH; }
  const attempt = (tries) => {
    if (!ta.isConnected) return;
    if (ta.style.height && ta.style.height !== ta.dataset.autoH) return; // user-sized
    if (ta.clientHeight === 0) {
      if (tries < 30 && typeof requestAnimationFrame === 'function') requestAnimationFrame(() => attempt(tries + 1));
      return;
    }
    const cs = getComputedStyle(ta);
    const borderY = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const maxPx = parseFloat(cs.maxHeight); // 'none' → NaN → uncapped here, CSS still caps
    const h = _jobNotesGrowTarget(ta.scrollHeight, ta.clientHeight, borderY, maxPx);
    if (h !== null) { ta.style.height = `${h}px`; ta.dataset.autoH = ta.style.height; }
  };
  attempt(0);
}

// The read-only "Previous notes (from #N, date)" panel under #job-notes, with a
// "Use these" button that copies them into the box for editing. Built for the
// project path here; TASK-9363's Copy path is meant to reuse it. Pass null to
// clear — showAddJob and editJob both do, because the form node is reused and a
// panel left from the last job would show somebody else's instructions.
let _jobPrevNotesText = '';
function _renderJobPrevNotesBox(info) {
  const box = document.getElementById('job-prev-notes-box');
  if (!box) return;
  const text = info && typeof info.text === 'string' ? info.text : '';
  _jobPrevNotesText = text;
  if (!text.trim()) { box.style.display = 'none'; box.innerHTML = ''; return; }
  let when = '';
  if (Number.isFinite(info.dateMs)) {
    try { when = new Date(info.dateMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { when = ''; }
  }
  const from = [info.from, when].filter(Boolean).join(', ');
  box.style.display = 'block';
  box.innerHTML = `
    <div style="margin-top:6px;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;background:#F1F5F9;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:4px;">
        <span style="font-size:0.75rem;font-weight:600;color:#334155;">Previous notes${from ? ` (from ${escapeHtml(from)})` : ''}</span>
        <button type="button" class="btn btn-secondary btn-sm" style="font-size:0.75rem;padding:4px 10px;min-height:32px;"
                onclick="window.DispatchModule.useJobPrevNotes()">Use these</button>
      </div>
      <div style="white-space:pre-wrap;font-size:0.8rem;color:#334155;max-height:140px;overflow-y:auto;">${escapeHtml(text)}</div>
    </div>`;
}

function useJobPrevNotes() {
  const ta = document.getElementById('job-notes');
  if (!ta || !_jobPrevNotesText) return;
  const cur = ta.value.trim();
  ta.value = cur ? `${ta.value.replace(/\s+$/, '')}\n${_jobPrevNotesText}` : _jobPrevNotesText;
  _syncJobNotesHeight(); // TASK-4808
  ta.focus();
}

// DOM half. Runs inside showAddJob's __pendingProjectLink branch, after the
// customer, the project and the project PO are in. `gen` is the form generation
// that owns this prefill: every async step re-checks it, the TASK-388 /
// TASK-4877 rule, so a slow contact or price-book read can never write into a
// form the dispatcher has since pointed at another job.
function _applyProjectNewJobCarry(plan, gen, opts) {
  if (!plan) return;
  const byId = (id) => document.getElementById(id);

  // PO / AFE / vendor — the billing references travel with the PO, which always
  // carries. The project's standing PO (applyProjectPo, already run) wins; the
  // source job's PO only fills a box the project left empty.
  if (plan.refs) {
    const poInput = byId('job-po');
    if (poInput && !poInput.value.trim() && plan.refs.poNumber) {
      poInput.value = plan.refs.poNumber;
      delete poInput.dataset.autofilledPo;
      const pv = byId('job-po-visible');
      if (pv) pv.checked = plan.refs.poVisible;
      onJobPoInput();
    }
    const afe = byId('job-afe');
    if (afe && plan.refs.afeNumber && !afe.value) afe.value = plan.refs.afeNumber;
    const ven = byId('job-vendor-number');
    if (ven && plan.refs.vendorNumber && !ven.value) ven.value = plan.refs.vendorNumber;
  }

  if (plan.location) {
    if (byId('job-location')) byId('job-location').value = plan.location.location;
    if (byId('job-well-name')) byId('job-well-name').value = plan.location.wellName;
    // TASK-9541 — no stop id passed: a stop carried from the project's last job
    // is a NEW stop on the new job, so it gets a fresh id.
    plan.location.waypoints.forEach(wp => addWaypoint(wp.name, wp.url, '', wp.lat, wp.lng));
  }

  if (plan.jobType) {
    const t = byId('job-type');
    if (t) { _renderJobTypeSelect(plan.jobType.type || 'job'); onJobTypeChange(); } // TASK-9390 — a legacy/archived type is kept, not reset to 'job'
    setJobSplitBy(plan.jobType.splitBy);
    if (byId('job-interstate')) byId('job-interstate').checked = plan.jobType.interstate;
    if (byId('job-hazmat')) byId('job-hazmat').checked = plan.jobType.hazmat;
    if (byId('job-liquid-tank')) byId('job-liquid-tank').checked = plan.jobType.liquidTank;
  }

  if (plan.times) {
    const ad = byId('job-all-day');
    if (ad) ad.checked = plan.times.allDay;
    const tf = byId('time-fields');
    if (tf) tf.style.display = plan.times.allDay ? 'none' : '';
    if (plan.times.defaultStart && byId('job-start-time')) byId('job-start-time').value = plan.times.defaultStart;
    if (plan.times.defaultEnd && byId('job-end-time')) byId('job-end-time').value = plan.times.defaultEnd;
  }

  if (plan.notes !== null && byId('job-notes')) { byId('job-notes').value = plan.notes; _syncJobNotesHeight(); } // TASK-4808
  _renderJobPrevNotesBox(plan.prevNotes
    ? { text: plan.prevNotes, from: plan.sourceLabel, dateMs: plan.sourceDayMs }
    : null);

  // Contact — same two steps editJob takes: the hidden save fields NOW (so a
  // quick Save still carries it), then re-select it in the dropdown once the
  // customer's contact list has resolved (CRM contacts are an async read).
  if (plan.contact) {
    byId('job-poc-name').value = plan.contact.name;
    byId('job-poc-role').value = plan.contact.role;
    byId('job-poc-phone').value = plan.contact.phone;
    byId('job-poc-phone2').value = plan.contact.phone2;
    byId('job-poc-email').value = plan.contact.email;
    const cid = plan.customerId;
    const restore = () => {
      if (gen !== _jobFormGen) return; // this form belongs to another job now
      if (byId('job-customer')?.value !== cid) return; // customer changed → onCustomerChange cleared it on purpose
      if ((byId('job-poc-name')?.value || '') !== plan.contact.name) return; // dispatcher changed it
      restoreJobPocSelection(plan.contact);
    };
    populatePocDropdown(cid).then(restore);
    // onCustomerChange started its own read of the same list; whichever lands
    // second rewrites the <select>. One late re-pass keeps the dropdown showing
    // the carried contact either way (the save reads the hidden fields).
    setTimeout(restore, 900);
  }

  // Price book — pending state now; onCustomerChange's own async render picks up
  // the pending contract. Then verify against the customer's ACTIVE price books:
  // a book deactivated since the last run must not be carried onto a new job.
  //
  // 🔴 COD round 1 (Codex ❌ + Independent ⚠️, both reproduced): the old guard
  // was "pending contract still equals the carried one, else the dispatcher
  // changed it". But onCustomerChange's own .then ALSO writes that field — it
  // auto-selects the sole Active book — and loadCustomerLineItems serves a
  // 10-minute cache, so it lands in the next microtask, before this check. The
  // check then read the auto-select as a human choice and returned, leaving the
  // DEAD book's lineItemSelections on the form with no warning; saveJob writes
  // them onto every jobDay and billing treats them as hand-picked rates.
  // So the "a human chose" signal is now explicit: onContractPick (the only
  // human path) clears _projCarriedBook. Nothing else can cancel this check,
  // and it decides the same way whichever callback resolves first.
  if (plan.rates) {
    DispatchState.pendingJobSelectedContractId = plan.rates.contractId;
    DispatchState.pendingJobLineItemSelections = { ...plan.rates.lineItemSelections };
    const cid = plan.customerId;
    const carried = { gen, customerId: cid, contractId: plan.rates.contractId };
    _projCarriedBook = carried;
    loadCustomerLineItems(cid).then(({ contracts, items }) => {
      if (gen !== _jobFormGen || byId('job-customer')?.value !== cid) return;
      if (_projCarriedBook !== carried) return; // the dispatcher picked a book — theirs wins
      _projCarriedBook = null;
      const res = _resolveCarriedPriceBook(carried.contractId, DispatchState.pendingJobLineItemSelections, contracts, items);
      DispatchState.pendingJobSelectedContractId = res.contractId;
      DispatchState.pendingJobLineItemSelections = res.lineItemSelections;
      if (res.inactive) {
        window.showToast?.('The last job’s price book is no longer active — its rates were not copied. Pick a price book for this job.', 'warning', null, 7000);
      }
      if (window.hasAddOnPackage?.('clockbilling') && contracts.length > 0) {
        renderContractPicker('job-contract-area', contracts, DispatchState.pendingJobSelectedContractId, true);
      }
      _rerenderJobLineItemPickers();
    });
  }
  // Crew / equipment — the TASK-9212 carry machinery verbatim: raw carry held in
  // _dupeCrewCarry (so a cold roster re-derives instead of re-seeding an
  // unfiltered list, and _jobCrewForCreate refuses unvetted assets), side maps
  // seeded BEFORE the tiles render, seed intent for late snapshots, and dropped
  // rentals announced. Carried crew is not pre-vetted crew → compliance re-runs.
  if (plan.crew || plan.equipment) {
    _dupeCrewCarry = {
      drivers: plan.crew ? [...plan.crew.drivers] : [],
      helpers: plan.crew ? [...plan.crew.helpers] : [],
      trucks: plan.equipment ? [...plan.equipment.trucks] : [],
      trailers: plan.equipment ? [...plan.equipment.trailers] : [],
      assets: plan.equipment ? [...plan.equipment.assets] : [],
      assetQtys: plan.equipment ? { ...plan.equipment.assetQtys } : {},
      assetDirections: plan.equipment ? { ...plan.equipment.assetDirections } : {},
      sourceRental: plan.equipment ? (plan.equipment.sourceRental || null) : null // TASK-9363
    };
    const norm = _normalizeCarriedCrew(_dupeCrewCarry);
    DispatchState.pendingAssetQtys = norm.assetQtys;
    DispatchState.pendingAssetDirections = norm.assetDirections;
    populateAssignmentDropdowns(norm.trucks[0] || '', norm.drivers[0] || '', norm.trailers[0] || '');
    populateJobCrewSections(norm.drivers, norm.helpers, norm.trucks, norm.trailers, norm.assets);
    DispatchState.jobCrewSeedIntent = {
      drivers: norm.drivers, helpers: norm.helpers,
      trucks: norm.trucks, trailers: norm.trailers, assets: norm.assets
    };
    DispatchState.jobCrewSeedPending = {
      people: !DispatchState.dataLoaded.people,
      trucks: !DispatchState.dataLoaded.trucks,
      trailers: !DispatchState.dataLoaded.trailers,
      assets: !DispatchState.dataLoaded.assets
    };
    _announceDroppedCarriedAssets(norm.dropped);
    _markCopyAutoCarried(norm.assets); // TASK-9363 r3
    setTimeout(runJobComplianceCheck, 200);
    // TASK-9598 — the project door ticks crew/equipment too; say where it came
    // from. Copy (announce:false) already painted its own J-#### banner.
    if (!(opts && opts.announce === false) && _crewCarryCount(_dupeCrewCarry) > 0) {
      _renderJobCopySourceBanner({ kind: 'project', project: _jobFormProjectName(), from: plan.sourceLabel || '' });
    }
  }

  // Deferred, not inline: showAddJob runs applyJobFormSectionVisibility(null)
  // AFTER this prefill, and that would re-hide a gated section (waypoints, AFE)
  // that now holds carried data. Same ordering duplicateJob gets from its timer.
  setTimeout(() => { if (gen === _jobFormGen) revealPopulatedJobFormSections(); }, 0);
  if (opts && opts.announce === false) return; // TASK-9363 — Copy to New Job reuses this without the project toast
  window.showToast?.(
    `Started from ${plan.sourceLabel}, this project’s latest job. Dates are never copied — check them before you save.`,
    'info', null, 6000
  );
}

// ============================================
// TASK-9363 — Copy to New Job is the repeat-run flow
// ============================================
// Copy reuses the TASK-9391 project carry (one machine, two doors): the same
// plan builder and the same DOM applier, with the groups duplicateJob does NOT
// already handle itself switched on. Location/waypoints, crew/equipment,
// permits and the 811 ticket stay in duplicateJob (TASK-9212). Notes are OFF,
// so the source's notes land in the read-only Previous notes panel.
const _COPY_CARRY_FLAGS = Object.freeze({
  location: false, jobType: true, contact: true, rates: true, times: true,
  crew: false, equipment: false, notes: false
});

// PURE. The carry plan for a copy of `job`, seeded from its LAST day.
function _buildCopyRepeatRunPlan(job, seedDay) {
  if (!job || !job.id) return null;
  return _buildProjectNewJobCarry({
    customerId: job.customerId || null,
    defaults: { carry: { ..._COPY_CARRY_FLAGS } },
    source: { job, day: seedDay || null }
  });
}

function _applyCopyRepeatRunCarry(job, seedDay, gen) {
  const plan = _buildCopyRepeatRunPlan(job, seedDay);
  if (plan) _applyProjectNewJobCarry(plan, gen, { announce: false });
  _applyCopiedProject(job);
}

// PURE. Is `customerId` one of this project's customers? Projects keep them in
// customerIds[] + primaryCustomerId — NOT customerId, which projects never set.
function _projectHasCustomer(project, customerId) {
  if (!project || !customerId) return false;
  if (project.primaryCustomerId === customerId) return true;
  return Array.isArray(project.customerIds) && project.customerIds.includes(customerId);
}

// TASK-9289 COD r1 — saveJob's only way to change a job's projectId. Delegates
// to ProjectsModule.changeJobProject (reached through window — Pitfall #1) and
// never throws. Returns the gate's result, or { ok:false, reason:'unavailable' }
// with no projectId (caller keeps the stored one).
//
// r5 (COD r4) — TWO timings, and the toasts follow the timing:
//   POST-save (default): the job itself is already saved, so every failure is
//     said here as "Job saved, but the project did not change".
//   PRE-save (`extra.beforeSave: true`, from _saveJobProjectBeforeUpdate): NOTHING
//     is saved yet and the caller will say "Nothing was saved"; this helper stays
//     quiet so the user is never told "Job saved" about a save that bailed.
// `beforeSave` is this helper's own flag and is not passed to the gate.
async function _saveJobChangeProject(jobId, toProjectId, expectedPreviousProjectId, source, extra = {}) {
  const { beforeSave = false, ...gateOpts } = extra || {};
  const fn = window.ProjectsModule?.changeJobProject;
  if (typeof fn !== 'function') {
    if (!beforeSave) window.showToast?.('Job saved, but its project was NOT changed — Projects is not loaded. Change it from the project page.', 'error', null, 9000);
    return { ok: false, reason: 'unavailable' };
  }
  try {
    const res = await fn(jobId, toProjectId, { expectedPreviousProjectId, source, ...gateOpts });
    if (!beforeSave && res && res.ok === false && res.reason === 'declined') {
      window.showToast?.('Job saved — its project was left as it was.', 'info', null, 7000);
    }
    return res || { ok: false, reason: 'error' };
  } catch (err) {
    console.warn('[Dispatch] project change failed:', err);
    if (!beforeSave) window.showToast?.('Job saved, but its project could not be changed.', 'error', null, 8000);
    return { ok: false, reason: 'error' };
  }
}

// TASK-9289 r5 — the PO fields the gate's transaction wrote (projects.js
// changeJobProject `poApplied`), carried into the job update that follows it so
// that update does not write the form's blank PO over the inherited one.
function _applyGatePoToJobData(jobData, poApplied) {
  if (!jobData || !poApplied || typeof poApplied !== 'object') return;
  if ('poNumber' in poApplied) jobData.poNumber = poApplied.poNumber;
  if ('poVisible' in poApplied) jobData.poVisible = poApplied.poVisible;
}

// TASK-10013 — "Project created, but its address couldn't be mapped" while
// the job's own stop of the same name WAS on the map (Guy, "Albert North Pad").
// A pad or lease name is not something a geocoder can find; the stop already
// carries the pin. PURE: the pin of the first stop on this job — then the
// first saved location — whose name (or saved address) is the project's
// location, ignoring case, spacing and punctuation. null when none match or
// the match has no usable coordinate.
// COD r1 (Code / Edge / Domain): a GENERIC label ("drop off", "yard", "site")
// never matches — it names the leg, not a place (PAST_PLACE_GENERIC_LABELS). And
// an AMBIGUOUS match gives no pin: two matching stops (or saved locations) more
// than PAST_PLACE_TOLERANCE_M apart → null, so the text is geocoded as before.
// Saved locations tagged only to OTHER customers are skipped (customerId given).
function _newProjectPinFromStops(location, waypoints, savedLocations, customerId) {
  const norm = (v) => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const key = norm(location);
  if (!key || PAST_PLACE_GENERIC_LABELS.has(_ppNormalizeLabel(location)) || PAST_PLACE_GENERIC_LABELS.has(key)) return null;
  const one = (pins) => {
    if (!pins.length) return undefined;
    // COD r2 — every PAIR, not each-vs-first: order must not decide ambiguity.
    for (let i = 0; i < pins.length; i++) for (let j = i + 1; j < pins.length; j++) {
      if (_ppMetersBetween(pins[i], pins[j]) > PAST_PLACE_TOLERANCE_M) return null; // ambiguous
    }
    return pins[0];
  };
  const stopPins = [];
  for (const wp of waypoints || []) {
    if (wp && norm(wp.name) === key) { const c = plottableCoord(wp); if (c) stopPins.push(c); }
  }
  const fromStops = one(stopPins);
  if (fromStops !== undefined) return fromStops;
  const savedPins = [];
  for (const loc of savedLocations || []) {
    if (!loc || (norm(loc.name) !== key && norm(loc.address) !== key)) continue;
    const tags = savedLocCustomerIds(loc);
    if (customerId && tags.length && !tags.includes(customerId)) continue;
    const c = plottableCoord(loc); if (c) savedPins.push(c);
  }
  const fromSaved = one(savedPins);
  return fromSaved === undefined ? null : fromSaved;
}

// TASK-10013 — the createProject fields for that pin (none when no match, so
// createProject geocodes the text exactly as before).
function _newProjectPinFields(location, jobData) {
  const pin = _newProjectPinFromStops(location, jobData && jobData.waypoints, DispatchState.savedLocations, (jobData && jobData.customerId) || '');
  return pin ? { locationLat: pin.lat, locationLng: pin.lng } : {};
}

// TASK-9289 r5 — the inline "Create a new project" fields on the job form, as a
// createProject payload for the EDIT path. `jobData` supplies the (new) customer.
function _readInlineNewProjectForEdit(jobData) {
  const npStartVal = document.getElementById('job-new-project-start')?.value;
  const npEndVal = document.getElementById('job-new-project-end')?.value;
  const custId = jobData.customerId || null;
  const npLocation = document.getElementById('job-new-project-location')?.value?.trim() || '';
  return {
    name: document.getElementById('job-new-project-name')?.value?.trim() || '',
    customerIds: custId ? [custId] : [],
    primaryCustomerId: custId,
    primaryCustomerName: jobData.customer || '',
    startDate: npStartVal ? Timestamp.fromDate(new Date(npStartVal + 'T12:00:00')) : Timestamp.now(),
    endDate: npEndVal ? Timestamp.fromDate(new Date(npEndVal + 'T12:00:00')) : null,
    location: npLocation,
    ..._newProjectPinFields(npLocation, jobData), // TASK-10013
    // TASK-4782 (COD Blast-Radius) — a project's standing PO inherits onto every
    // job in the corridor, so it goes through the SAME normalizer as the job form.
    poNumber: normalizePoForSave(document.getElementById('job-new-project-po')?.value),
    description: document.getElementById('job-new-project-description')?.value?.trim() || '',
    tags: [],
    estimatedRevenue: null,
    onSiteContacts: []
  };
}

// TASK-9289 r5 (COD r4) — the project half of an EDIT save that has to land
// BEFORE the job update, as ONE all-or-nothing step. Two callers' worth of cases:
//   • the customer changes on a job that is in a project (rules pair them) —
//     one gated move carrying the new customer (keep / switch / unlink);
//   • "Create a new project instead" — ask the invoiced-ticket question ONCE
//     (before anything is written), create the project, then ONE gated move
//     from the old project straight into it (customer carried if it changed).
//     If that move does not go through, the new project is rolled back.
// On success, the PO the move inherited is folded into jobData (the caller's
// update writes jobData next). Never throws.
// Returns { ok:true, projectId } | { ok:false, level, message } — on ok:false
// NOTHING about the job was written and `message` says so.
async function _saveJobProjectBeforeUpdate({ jobId, jobData, storedProjectId, custChanging, target, expected, newProject, adoptCustomer = '' }) {
  const PM = window.ProjectsModule;
  const NOTHING = 'Nothing was saved';
  const AS_WAS = `${NOTHING} — the job, its customer and its project are as they were.`;
  const alsoSet = custChanging ? { customerId: jobData.customerId || null, customer: jobData.customer || '' } : null;
  let toProjectId = target || null;
  let billedConfirmed;
  let createdId = null;

  if (newProject) {
    if (typeof PM?.createProject !== 'function' || typeof PM?.confirmBilledJobMove !== 'function') {
      return { ok: false, level: 'error', message: `${NOTHING} — Projects is not loaded, so the new project could not be created. Untick “Create a new project” to save the job without it.` };
    }
    let pre;
    try {
      pre = await PM.confirmBilledJobMove(jobId, { title: jobData.title, fromProjectId: storedProjectId || null, toName: `“${newProject.name || 'the new project'}”` });
    } catch (e) { pre = { ok: false, reason: 'unverified' }; }
    if (!pre || pre.ok !== true) {
      return pre?.reason === 'declined'
        ? { ok: false, level: 'info', message: AS_WAS }
        : { ok: false, level: 'error', message: `${NOTHING} — could not check this job’s invoiced tickets. Try again.` };
    }
    billedConfirmed = pre.billedCount || 0;
    try {
      createdId = await PM.createProject(newProject);
    } catch (e) {
      console.warn('[Dispatch] createProject threw on edit:', e);
      createdId = null;
    }
    // createProject toasts its own failure reason on every null-return path.
    if (!createdId) return { ok: false, level: 'error', message: `${NOTHING} — the new project could not be created.` };
    toProjectId = createdId;
  }

  const gateOpts = { beforeSave: true, pendingPo: jobData.poNumber || '' };
  if (alsoSet) gateOpts.alsoSet = alsoSet;
  if (billedConfirmed !== undefined) gateOpts.billedConfirmed = billedConfirmed;
  // TASK-9599 — already asked in saveJob; `adoptCustomer` is the customer id agreed to.
  if (typeof adoptCustomer === 'string' && adoptCustomer && !newProject) { gateOpts.adoptCustomer = true; gateOpts.adoptFor = adoptCustomer; }
  const res = await _saveJobChangeProject(jobId, toProjectId, expected, newProject ? 'job-edit-new-project' : 'job-edit-customer', gateOpts);

  if (!res || res.ok !== true) {
    let tail = '';
    if (createdId) {
      let gone = false;
      try { gone = (await PM.discardUnusedProject?.(createdId)) === true; } catch (_) { gone = false; }
      if (!gone) tail = ` The new project “${newProject.name || 'Untitled'}” was created but has no jobs — delete it from Projects.`;
    }
    if (res?.reason === 'declined') return { ok: false, level: 'info', message: AS_WAS + tail };
    if (res?.reason === 'unavailable') return { ok: false, level: 'error', message: `${NOTHING} — Projects is not loaded.${tail}` };
    return {
      ok: false, level: 'error',
      message: `${NOTHING} — ${custChanging ? 'this job’s customer and its project have to change together, and ' : ''}the project change did not go through.${tail}`
    };
  }
  _applyGatePoToJobData(jobData, res.poApplied);
  return { ok: true, projectId: res.projectId !== undefined ? res.projectId : toProjectId };
}

// TASK-9289 B2 — mirror of projects.js linkCustomerConflict (no cross-import,
// Pitfall #1). r3: which is itself the exact mirror of firestore.rules
// jobProjectAdmits, which now REFUSES the write server-side — so this form
// check has to agree with it or the dispatcher gets a bare permission error:
// customerIds must contain the job's customer; a customer-less job fits only a
// customer-less project.
function _projectCustomerConflict(project, customerId) {
  if (!project) return false;
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  const cust = typeof customerId === 'string' && customerId ? customerId : null;
  return cust ? !ids.includes(cust) : ids.length > 0;
}

// TASK-9599 — mirror of projects.js projectAdoptsCustomer: the one conflict the
// job form may resolve in the same save. The project names NO customer yet and
// this job has one, so the project can adopt it. Never for a project that
// already names anyone.
function _projectAdoptsCustomer(project, customerId) {
  if (!project || project.deleting === true) return false;
  const ids = Array.isArray(project.customerIds) ? project.customerIds : [];
  if (!(ids.length === 0 && typeof customerId === 'string' && customerId !== '')) return false;
  return !project.primaryCustomerId || project.primaryCustomerId === customerId;
}

// TASK-9599 — saveJob's refusal text, honest about which conflict it is. It
// used to call a project with no customer "for another customer".
function _projectCustomerConflictMessage(project, customerId, customerName) {
  const pn = `“${project?.name || 'That project'}”`;
  const ids = Array.isArray(project?.customerIds) ? project.customerIds : [];
  if (!ids.length && !project?.primaryCustomerId) {
    return `Not saved — ${pn} has no customer yet. Save again and choose “Add customer & save” to add ${customerName || 'this customer'} to it, or pick another project.`;
  }
  if (!customerId) {
    return `${pn} is for ${project?.primaryCustomerName || 'a customer'}, and this job has no customer. Pick the job's customer, or another project.`;
  }
  return `${pn} is for ${project?.primaryCustomerName || 'another customer'}, not this job's customer. Pick another project, or add this customer to the project in Edit Project.`;
}

// ============================================
// TASK-9289 S1 — projects on the board
// ============================================
// Lacey (TITS billing, 2026-09-21): "The option to have a project is in DD but it
// doesn't show up on the calendar; it only shows jobs." The only project surface
// near the board was a strip inside an already-linked job's detail modal. Every
// linked job card now names its project, and the name opens it.
function _boardProjectForJob(job) {
  const pid = job && job.projectId;
  if (!pid) return null;
  const all = window.ProjectsModule?.getState?.()?.projects
    || window.ProjectsModule?.getActiveProjects?.() || [];
  return all.find(p => p && p.id === pid) || null;
}

function _isBoardDriverSession() {
  return !!(window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin);
}

// Plain HTML string; callers render it inside the card's click target, so the
// chip stops propagation. dispatcher-only: never on a driver board.
// TASK-9569 — the reminder bell on a job card. window.Reminders owns the module
// gate (dispatch), the per-login hide and the live state; never imported
// (Pitfall #1). Driver-PIN / link sessions get '' from it. '' when absent.
function _reminderBell(type, id, label, opts) {
  try { return window.Reminders?.bellHtml ? window.Reminders.bellHtml(type, id, label, opts) : ''; } catch (_) { return ''; }
}

function _projectChipHtml(job) {
  if (_isBoardDriverSession()) return '';
  const p = _boardProjectForJob(job);
  if (!p) return '';
  const name = p.name || 'Project';
  const _escA = window.escAttr || escapeHtml;
  return `<button type="button" class="dd-project-chip dispatcher-only" title="Open project ${_escA(name)}" ` +
    `onclick="event.stopPropagation(); window.DispatchModule.openProjectFromBoard('${escJsAttr(p.id)}')" ` +
    `style="display:inline-flex;align-items:center;gap:3px;max-width:100%;margin:2px 0;padding:1px 6px;border:1px solid rgba(3,105,161,0.35);border-radius:10px;background:rgba(3,105,161,0.08);color:#0369a1;font-size:0.7rem;font-weight:600;line-height:1.5;cursor:pointer;">` +
    `<span class="material-icons" style="font-size:12px;" aria-hidden="true">folder_special</span>` +
    `<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(name)}</span></button>`;
}

function _projectNewJobMenuItemHtml(job) {
  if (_isBoardDriverSession() || !_boardProjectForJob(job)) return '';
  return `<button class="overflow-item" onclick="window.DispatchModule.newJobInProjectFromBoard('${escJsAttr(job.projectId)}')">` +
    `<span class="material-icons">create_new_folder</span> New job in this project</button>`;
}

// Switch the board to the Projects view and open this project — the same state
// the segmented Projects button sets, then the project's detail.
async function openProjectFromBoard(projectId) {
  if (!projectId || _isBoardDriverSession()) return;
  if (window.canAccessProjects?.() === false) { // TASK-9651
    window.showToast?.('This login does not have Projects. Ask an admin to turn on Projects for you.', 'warning');
    return;
  }
  const PM = window.ProjectsModule;
  if (typeof PM?.openProjectDetail !== 'function') {
    window.showToast?.('Projects are still loading — try again in a moment.', 'info');
    return;
  }
  document.getElementById('job-context-menu')?.remove();
  document.querySelectorAll('.view-btn').forEach(b => b.classList.toggle('active', b.dataset.view === 'projects'));
  DispatchState.viewMode = 'projects';
  DispatchState.listOffset = 0;
  try { syncDispatchSearchBtn(); updateDateDisplay(); syncLayerFilterChips(); } catch (_) { /* chrome only */ }
  await PM.openProjectDetail(projectId);
}

// New Job straight into a project, from a card — the Projects view's own New
// Job path (resolveProjectNewJobPrefill → the box ticked, customer/PO/location
// and the project's New job defaults carried), so there is one behaviour.
async function newJobInProjectFromBoard(projectId) {
  if (!projectId || _isBoardDriverSession()) return;
  if (window.canAccessProjects?.() === false) { // TASK-9651
    window.showToast?.('This login does not have Projects. Ask an admin to turn on Projects for you.', 'warning');
    return;
  }
  if (typeof window.ProjectsModule?.scheduleJobFromProject !== 'function') {
    window.showToast?.('Projects are still loading — try again in a moment.', 'info');
    return;
  }
  await window.ProjectsModule.scheduleJobFromProject(projectId);
}

// ============================================
// TASK-9289 S2 — a repeated name inside a project
// ============================================
// The project the open job form will link on save — the same read saveJob uses
// (_jobFormProjectIdForSave), so the dupe surfaces cannot disagree with the
// save. "Create a new project instead" has no project yet: null.
function _formProjectContext() {
  if (document.getElementById('job-create-new-project')?.checked) return null;
  const projectId = _jobFormProjectIdForSave();
  if (!projectId) return null;
  const project = (window.ProjectsModule?.getState?.()?.projects || []).find(p => p && p.id === projectId) || null;
  return {
    projectId,
    project,
    customerId: document.getElementById('job-customer')?.value || ''
  };
}

// Pure. What adding a day to matchJob does to the form's project link:
//   none     — the form has no project; nothing to say.
//   same     — matchJob is already in the form's project: a repeated name is
//              the expected case, both choices are equal.
//   link     — matchJob is in NO project: the day would land outside the
//              project, so the link is carried (money-gated at link time).
//   other    — matchJob is in a DIFFERENT project: never re-homed from here.
//   customer — the form's project does not name this customer: never linked.
function _addDayProjectCarry(matchJob, ctx) {
  if (!ctx || !ctx.projectId || !matchJob) return { kind: 'none' };
  const project = ctx.project || null;
  if (matchJob.projectId === ctx.projectId) return { kind: 'same', projectId: ctx.projectId, project };
  if (matchJob.projectId) return { kind: 'other', projectId: ctx.projectId, project, otherProjectId: matchJob.projectId };
  if (_projectCustomerConflict(project, matchJob.customerId || ctx.customerId)) return { kind: 'customer', projectId: ctx.projectId, project };
  return { kind: 'link', projectId: ctx.projectId, project };
}

function _addDayProjectCarryText(carry, matchTitle) {
  if (!carry || carry.kind === 'none') return '';
  const pn = carry.project?.name ? `“${carry.project.name}”` : 'this project';
  const jt = `“${matchTitle || 'that job'}”`;
  switch (carry.kind) {
    case 'same':
      return `${jt} is already a job in ${pn}. Add a day to it, or save this as a new job in ${pn} — both are fine.`;
    case 'link':
      return `${jt} isn't in ${pn} yet. Adding a day also puts it in ${pn} — unless it already has invoiced tickets, which are never moved from here.`;
    case 'other': {
      const other = _boardProjectForJob({ projectId: carry.otherProjectId });
      return `${jt} belongs to ${other?.name ? `“${other.name}”` : 'another project'}. A day added to it stays there, not in ${pn} — save a new job to keep this work in ${pn}.`;
    }
    case 'customer':
      // TASK-9599 — say so when the project names nobody (the add-day carry
      // never adds a customer to a project; that is the job form's question).
      if (!(Array.isArray(carry.project?.customerIds) && carry.project.customerIds.length)) {
        return `${pn} has no customer yet, so adding a day to ${jt} won't put it in ${pn}.`;
      }
      return `${pn} doesn't include this customer, so adding a day to ${jt} won't put it in ${pn}.`;
    default:
      return '';
  }
}

// After a form add-day wrote its days: carry the form's project onto the matched
// job ONLY in the 'link' case, and never move billed tickets (billedPolicy
// 'refuse') — that is a money event the dispatcher did not ask for here.
async function _carryProjectLinkAfterAddDay(jobId, carry, source) {
  if (!carry || carry.kind !== 'link' || !jobId) return;
  if (typeof window.ProjectsModule?.linkJobToProject !== 'function') {
    window.showToast?.('Day added, but the job was not put in the project — Projects is not loaded. Link it from the project.', 'warning', null, 8000);
    return;
  }
  try {
    const res = await window.ProjectsModule.linkJobToProject(jobId, carry.projectId, { billedPolicy: 'refuse', expectedPreviousProjectId: null, source });
    if (res && res.ok === false && res.reason === 'billed') {
      const pn = carry.project?.name ? `“${carry.project.name}”` : 'the project';
      window.showToast?.(
        `Day added, but the job was NOT put in ${pn}: it has ${res.billedCount} invoiced ticket${res.billedCount === 1 ? '' : 's'}, and moving them would change ${pn}'s billed totals. Link it from the project if that's intended.`,
        'warning', null, 10000);
    }
  } catch (err) {
    console.warn('[Dispatch] TASK-9289 add-day project carry failed:', err);
    window.showToast?.('Day added, but linking the job to the project failed — link it from the project.', 'warning', null, 8000);
  }
}

// Smart Assistant candidates for #job-title (see the SA.register call).
function _jobTitleSaCandidates() {
  const customerId = document.getElementById('job-customer')?.value || '';
  if (!customerId) return [];
  const ctx = _formProjectContext();
  return DispatchState.jobs
    .filter(j => j && j.customerId === customerId
      && j.status !== 'finished' && j.status !== 'cancelled'
      && !(ctx && ctx.projectId && j.projectId === ctx.projectId))
    .map(j => ({ value: j.title || j.name || '', id: j.id }));
}

// TASK-9289 S3 — pure; notes are compared exactly as saved (no trimming: a
// whitespace-only edit is still an edit), and each side is capped.
const JOB_NOTES_AUDIT_CAP = 4000;
function _jobNotesAuditDiff(oldNotes, newNotes) {
  const o = typeof oldNotes === 'string' ? oldNotes : '';
  const n = typeof newNotes === 'string' ? newNotes : '';
  if (o === n) return { changed: false };
  const cap = (s) => s.length > JOB_NOTES_AUDIT_CAP ? s.slice(0, JOB_NOTES_AUDIT_CAP) + '…[truncated]' : s;
  return { changed: true, oldNotes: cap(o), newNotes: cap(n) };
}

// The project a copy carried, while it is still carried. Cleared on every form
// open (showAddJob / editJob) and whenever the dispatcher moves off it.
let _dupeCarriedProjectId = null;

function _applyCopiedProject(job) {
  _dupeCarriedProjectId = null;
  const pid = job && job.projectId;
  if (!pid) return;
  const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === pid);
  if (!_projectHasCustomer(proj, job.customerId)) return; // archived, gone, or not this customer's
  const sel = document.getElementById('job-project-id');
  if (!sel) return;
  sel.value = pid;
  if (sel.value !== pid) return; // no <option> for it — nothing to claim
  const cb = document.getElementById('job-add-to-project');
  if (cb) cb.checked = true;
  _dupeCarriedProjectId = pid;
}

// Run on every customer change and at save. Only ever undoes OUR carry: once
// the dispatcher sets "No project" or picks another project, the choice is theirs.
function _recheckCarriedProjectCustomer() {
  const pid = _dupeCarriedProjectId;
  if (!pid) return;
  const sel = document.getElementById('job-project-id');
  const cb = document.getElementById('job-add-to-project');
  if (!sel || !cb || !cb.checked || sel.value !== pid) { _dupeCarriedProjectId = null; return; }
  const customerId = document.getElementById('job-customer')?.value || '';
  const proj = (window.ProjectsModule?.getActiveProjects?.() || []).find(p => p && p.id === pid);
  if (_projectHasCustomer(proj, customerId)) return;
  _dupeCarriedProjectId = null;
  sel.value = '';
  cb.checked = false;
  _releaseProjectJobDefaultClaims(); // TASK-4696 — values it filled stay, as the job's own
  _showUnlinkedProjectPoHint(); // TASK-4695 — the PO it filled stays; say so
  window.showToast?.(
    `Took this job out of ${proj?.name ? '“' + proj.name + '”' : 'the copied project'} — that project isn’t for this customer.`,
    'info', null, 6000
  );
}

// saveJob's projectId. An EDIT keeps today's read (the select), untouched. A
// NEW job links a project only when the "Add to project" box is ticked —
// saveJob used to write the select's value even with the box unticked.
// TASK-9289 B1 — an EDIT now honours the box too. Unticking "Add to project" on
// an edit used to leave the job linked (the select still held the project), and
// ticking-then-unticking on an unlinked job linked it anyway.
// TASK-4695 — there is no visible box now: #job-add-to-project is a hidden
// mirror of the Project picker (_syncJobProjectLinkBox), so "No project" on the
// picker is what unlinks.
function _jobFormProjectIdForSave() {
  // TASK-9651 — no Projects access: the picker is hidden, so an edit keeps the
  // job's stored link untouched and a new job (incl. a copy) is never linked.
  if (_jobFormProjectsHidden()) {
    return document.getElementById('job-id')?.value ? (_editJobOriginalProjectId || null) : null;
  }
  const sel = document.getElementById('job-project-id');
  const val = sel?.value || null;
  return document.getElementById('job-add-to-project')?.checked ? val : null;
}

// ── TASK-9650 BEGIN — "Use on jobs" project documents on the job form ──────
// After Project, the form offers the project's "Use on jobs" library docs
// (projects.js getUseOnJobsDocs; owner / 'projects' module can read them) as
// toggle chips. Saved as REFS jobs/{id}.projectDocs: [{projectId, docId, name,
// mimeType}] — never a copied file; the file opens by ids (storage.rules lets a
// crew session read it while the library doc is still "Use on jobs"). Only the
// refs for the job's CURRENT project are saved, so changing the project drops
// the old project's docs. A login that cannot read the library still sees and
// keeps (or removes) what the job already carries.
let _jobProjDocsSel = [];
let _jobProjDocsOrig = []; // what the job carried when the form opened (stays pickable)
let _jobProjDocsAvail = [];
let _jobProjDocsSeq = 0;
const _JOB_PROJ_DOC_ID = /^[A-Za-z0-9_-]{1,128}$/;
// Pure. Valid, de-duplicated, capped refs.
function _normJobProjDocs(refs) {
  const out = [];
  for (const r of (Array.isArray(refs) ? refs : [])) {
    if (!r || !_JOB_PROJ_DOC_ID.test(String(r.projectId || '')) || !_JOB_PROJ_DOC_ID.test(String(r.docId || ''))) continue;
    if (out.some(x => x.projectId === r.projectId && x.docId === r.docId)) continue;
    out.push({ projectId: r.projectId, docId: r.docId, name: String(r.name || 'Document').slice(0, 120), mimeType: String(r.mimeType || '').slice(0, 80) });
    if (out.length >= 20) break;
  }
  return out;
}
// Pure. What saveJob writes for the project the job is saved under.
function _jobProjDocsForSave(sel, projectId) {
  return projectId ? _normJobProjDocs(sel).filter(r => r.projectId === projectId) : [];
}
function _jobProjDocsSet(refs) {
  _jobProjDocsSel = _normJobProjDocs(refs);
  _jobProjDocsOrig = _jobProjDocsSel.slice();
  _jobProjDocsAvail = [];
}
async function _jobProjDocsRender() {
  const box = document.getElementById('job-project-docs');
  if (!box) return;
  const seq = ++_jobProjDocsSeq;
  const pid = document.getElementById('job-project-id')?.value || '';
  let avail = null;
  if (pid) { try { avail = await (window.ProjectsModule?.getUseOnJobsDocs?.(pid) || false); } catch (_) { avail = false; } }
  if (seq !== _jobProjDocsSeq) return;
  const mine = _jobProjDocsSel.filter(r => r.projectId === pid);
  const list = _normJobProjDocs([...mine, ..._jobProjDocsOrig.filter(r => r.projectId === pid), ...(avail || [])]).sort((a, b) => a.name.localeCompare(b.name));
  _jobProjDocsAvail = list;
  if (!pid || !list.length) { box.innerHTML = ''; box.classList.add('hidden'); return; }
  box.classList.remove('hidden');
  box.innerHTML = `
    <div style="font-weight:600;font-size:0.85rem;color:#0369a1;margin:10px 0 6px;">Project documents for this job</div>
    <div style="display:flex;flex-wrap:wrap;gap:6px;">${list.map((r, i) => {
      const on = mine.some(x => x.docId === r.docId);
      return `<span style="display:inline-flex;align-items:center;border:1px solid ${on ? '#16A34A' : 'var(--border-light)'};border-radius:999px;background:${on ? '#DCFCE7' : 'var(--bg-card,#fff)'};max-width:100%;">
        <button type="button" class="job-projdoc-chip" aria-pressed="${on}" onclick="window.DispatchModule.toggleJobProjectDoc(${i})"
          style="display:inline-flex;align-items:center;gap:4px;min-height:44px;padding:0 6px 0 12px;border:0;background:none;font:inherit;font-size:0.85rem;color:var(--text-primary);cursor:pointer;min-width:0;">
          <span class="material-icons" aria-hidden="true" style="font-size:18px;color:${on ? '#16A34A' : 'var(--text-secondary)'};">${on ? 'check_circle' : 'add_circle_outline'}</span>
          <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:220px;">${escapeHtml(r.name)}</span>
        </button>
        <button type="button" aria-label="View ${escapeHtml(r.name)}" title="View" onclick="window.DispatchModule.viewJobProjectDoc(${i})"
          style="min-width:44px;min-height:44px;border:0;border-left:1px solid var(--border-light);background:none;color:var(--brand-blue);cursor:pointer;"><span class="material-icons" aria-hidden="true" style="font-size:18px;">open_in_new</span></button>
      </span>`;
    }).join('')}</div>
    <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Crews on this job can open the ones you pick.${avail === null ? ' The project library needs Projects access to add more.' : avail === false ? ' Could not load the project library, so only the documents already on this job show.' : ''}</div>`;
}
function toggleJobProjectDoc(i) {
  const r = _jobProjDocsAvail[Number(i)];
  if (!r) return;
  const has = _jobProjDocsSel.some(x => x.projectId === r.projectId && x.docId === r.docId);
  _jobProjDocsSel = has ? _jobProjDocsSel.filter(x => !(x.projectId === r.projectId && x.docId === r.docId))
    : _normJobProjDocs([..._jobProjDocsSel, r]);
  _jobProjDocsRender();
}
function viewJobProjectDoc(i) {
  const r = _jobProjDocsAvail[Number(i)];
  if (!r) return;
  if (window.ProjectsModule?.openProjectDocRef) window.ProjectsModule.openProjectDocRef(r);
  else window.showToast?.('Could not open that file', 'error');
}
// ── TASK-9650 END ───────────────────────────────────────────────────────────

// Pure: what a carried price book becomes once the customer's ACTIVE books are
// known. Independent of whatever onCustomerChange's auto-select has written in
// the meantime — the input is the CARRIED id, never the current pending value.
//   inactive → no rates carried (sole active book pre-selected, as a plain
//              customer pick would), and the caller warns;
//   active   → the carried book, keeping only rates whose line item still exists.
function _resolveCarriedPriceBook(carriedContractId, selections, contracts, items) {
  const list = Array.isArray(contracts) ? contracts : [];
  const inactive = !!carriedContractId && !list.some(c => c && c.id === carriedContractId);
  if (inactive) {
    return { contractId: list.length === 1 ? list[0].id : null, lineItemSelections: {}, inactive: true };
  }
  const known = new Set((items || []).map(i => i && i.id));
  const kept = {};
  for (const [k, v] of Object.entries(selections || {})) {
    if (v && known.has(v.lineItemId)) kept[k] = v;
  }
  return { contractId: carriedContractId || (list.length === 1 ? list[0].id : null), lineItemSelections: kept, inactive: false };
}

// The carried price book awaiting its active-book check (see
// _applyProjectNewJobCarry). Cleared by a human pick in onContractPick.
let _projCarriedBook = null;

// The project's equipment kit (TASK-9258 job template), applied through the
// normal Apply button's code so its "unconfirmed" marks and provenance hold.
//
// COD round 1: this used to try once at 150ms and silently give up when the
// jobTemplates snapshot (or the roster) had not landed yet. The intent is now
// HELD in _pendingProjectKit and retried when the templates snapshot arrives
// (subscribeToJobTemplates) and when roster collections land
// (refreshJobModalCrewIfOpen). It is dropped only when applied, when the form
// moves on (generation), or when a LOADED template list says the kit is gone.
let _pendingProjectKit = null;
function _applyProjectJobKit(templateId, gen) {
  if (!templateId) return;
  _pendingProjectKit = { templateId, gen };
  setTimeout(_flushPendingProjectKit, 150);
}

function _flushPendingProjectKit() {
  const p = _pendingProjectKit;
  if (!p) return;
  if (p.gen !== _jobFormGen) { _pendingProjectKit = null; return; }
  const modal = document.getElementById('modal-job');
  if (!modal || modal.classList.contains('hidden')) return; // not painted yet — a later trigger retries
  if (!DispatchState.jobTemplatesReady) return; // snapshot not here yet — keep the intent
  const tpl = (DispatchState.jobTemplates || []).find(t => t.id === p.templateId);
  if (!tpl) {
    _pendingProjectKit = null;
    window.showToast?.('This project’s equipment kit no longer exists — pick another in Edit Project.', 'warning', null, 7000);
    return;
  }
  // applyJobTemplate refuses on a cold roster; wait for the roster instead of
  // letting it toast "try again" at a dispatcher who never pressed anything.
  if (!DispatchState.dataLoaded.trucks || !DispatchState.dataLoaded.trailers || !DispatchState.dataLoaded.assets) return;
  const sel = document.getElementById('job-template-select');
  if (!sel) return;
  sel.value = p.templateId;
  if (sel.value !== p.templateId) return; // option not rendered yet — retry on the next trigger
  _pendingProjectKit = null;
  applyJobTemplate();
  // TASK-9598 — a project kit ticks equipment nobody picked on this form.
  if (DispatchState.templateApplied) {
    const prev = _jobCopySource && _jobCopySource.kind === 'project' ? _jobCopySource : null;
    _renderJobCopySourceBanner({
      kind: 'project', project: _jobFormProjectName(),
      from: prev ? prev.from : '', kit: String(tpl.name || '')
    });
  }
}
// Seam for TASK-9392 (customer-request drafts) and any other caller that has a
// resolved project link in hand: open New Job prefilled exactly like the
// project's New Job button does.
function openNewJobWithProjectPrefill(link) {
  if (!link || !link.projectId) return;
  window.__pendingProjectLink = link;
  showAddJob();
}

function applyProjectPo(selectEl) {
  const hint = document.getElementById('job-project-po-hint');
  const poInput = document.getElementById('job-po');
  const poVisible = document.getElementById('job-po-visible');
  if (hint) { hint.style.display = 'none'; hint.textContent = ''; }
  if (!selectEl || !poInput) return;

  const projectPo = String(selectEl.selectedOptions?.[0]?.dataset?.po || '').trim();
  const existing = String(poInput.value || '').trim();

  // `autofilledPo` records the value WE put in the box. It is what separates
  // "the dispatcher typed this PO on purpose" from "we auto-filled it a moment ago".
  // Without it, switching Project A -> Project B leaves PO A sitting in a non-empty
  // field, we skip the fill, and the job silently bills to the wrong corridor.
  const ours = String(poInput.dataset.autofilledPo || '').trim();
  const isStillOurs = ours && existing === ours;

  // The dispatcher edited the field by hand — that value wins, always. Drop our claim.
  if (existing && !isStillOurs) {
    delete poInput.dataset.autofilledPo;
    if (projectPo && existing !== projectPo && hint) {
      hint.textContent = `Heads up: this project's standing PO is ${projectPo}, but this job is set to ${existing}. Keeping ${existing}.`;
      hint.style.display = 'block';
    }
    return;
  }

  // Field is empty, or still holds exactly what we last auto-filled — safe to replace.
  if (!projectPo) {
    // COD Code Reviewer — clearing the box must clear its warning too, or a
    // "20 characters, QuickBooks accepts 15" hint outlives the value it was
    // about and reads as a warning on an empty field.
    if (isStillOurs) { poInput.value = ''; delete poInput.dataset.autofilledPo; onJobPoInput(); }
    return;
  }

  poInput.value = projectPo;
  poInput.dataset.autofilledPo = projectPo;
  // TASK-4782 — a project's standing PO lands on every job in the corridor, so
  // if it cannot reach QuickBooks the dispatcher needs to know here, once,
  // rather than on each invoice that quietly ships without a PO.
  onJobPoInput();
  // A standing project PO exists to reach the invoice and the driver's job card;
  // leaving poVisible unchecked would hide it on the driver quickview (see :4389).
  if (poVisible) poVisible.checked = true;
  if (hint) {
    hint.textContent = isStillOurs
      ? `PO switched to ${projectPo} for this project. Change it below if this job bills differently.`
      : `PO ${projectPo} filled in from this project. Change it below if this job bills differently.`;
    hint.style.display = 'block';
  }
}

// ============================================
// TASK-4696 — Project defaults: Well name / AFE # / Vendor # / Job type
// ============================================
// projects.js owns the data (`project.jobDefaults`, normalizeProjectJobDefaults —
// read through window.ProjectsModule, never imported: Pitfall #1). This side only
// PRE-FILLS the job form, with the exact applyProjectPo claim rule: a dataset
// marker records the value WE put in each box, so
//   • a value the dispatcher typed (or that Edit loaded from the job) is never
//     replaced — the hint says the project differs and which one is kept;
//   • switching Project A → B replaces A's value only while it is still ours;
//   • unlinking leaves values as they are (they are the job's now).
// Nothing here writes Firestore. The ordinary saveJob carries the boxes, and a
// ticket copies the job's fields at clock-out, so a default changed later can
// never reach a ticket that already exists — invoiced or not.
// Compliance flags (hazmat / interstate / liquidTank) and the title are not in
// the set by design (TASKBOARD TASK-4696 blockers).
const _PROJECT_JD_FIELDS = [
  { key: 'wellName', el: 'job-well-name', label: 'Well name' },
  { key: 'afeNumber', el: 'job-afe', label: 'AFE #' },
  { key: 'vendorNumber', el: 'job-vendor-number', label: 'Vendor #' },
  { key: 'type', el: 'job-type', label: 'Job type' },
];

function _projectJobDefaultsFor(projectId) {
  if (!projectId) return {};
  const PM = window.ProjectsModule;
  const all = PM?.getState?.()?.projects || PM?.getActiveProjects?.() || [];
  const p = (all || []).find(x => x && x.id === projectId);
  if (!p || typeof PM?.normalizeProjectJobDefaults !== 'function') return {};
  return PM.normalizeProjectJobDefaults(p.jobDefaults);
}

// PURE. defaults: normalized map. current: { key: { value, claim } }.
// opts.isNew: a new job (Job type is only ever pre-filled on a new job — an
// edited job's type is the job's). opts.force: the fresh Projects → New Job form,
// where the boxes were filled a moment ago from the project's LAST job (TASK-9391
// carry), not by a person: the project's Job type wins there; its Well name /
// AFE # / Vendor # do not (see forceable below).
// Returns { key: { action: 'fill'|'clear'|'keep'|'none', value, differs } }.
function _planProjectJobDefaults(defaults, current, opts = {}) {
  const d = (defaults && typeof defaults === 'object') ? defaults : {};
  const plan = {};
  for (const f of _PROJECT_JD_FIELDS) {
    const cur = (current && current[f.key]) || {};
    const blank = f.key === 'type' ? 'job' : '';
    let value = String(cur.value == null ? '' : cur.value).trim();
    if (value === blank) value = '';
    const claim = String(cur.claim || '').trim();
    const ours = !!claim && value === claim;
    const want = typeof d[f.key] === 'string' ? d[f.key].trim() : '';
    if (f.key === 'type' && !opts.isNew && !ours) { plan[f.key] = { action: 'none' }; continue; }
    // COD r1-r3 (Code Reviewer / Edge) — a value a PERSON touched is theirs, even
    // edited away and back onto our value, and even a plain "Job" type (which
    // otherwise reads as blank). Only real input events set userPicked: the type
    // select's inline onchange and the input listener applyProjectJobDefaults
    // attaches to the text boxes; code setting .value fires neither.
    if (cur.userPicked) {
      plan[f.key] = { action: 'keep', value: value || (f.key === 'type' ? 'job' : ''), differs: !!want && want !== value ? want : '' };
      continue;
    }
    // COD r2/r3 (Independent + Domain) — force never replaces the last job's well,
    // AFE or vendor #: on a multi-well lease the carried value is the likelier
    // right one and operators match invoices on all three. The hint names the
    // project's value instead. Only Job type is forced.
    const forceable = opts.force && want && f.key === 'type';
    if (value && !ours && !forceable) {
      plan[f.key] = { action: 'keep', value, differs: !!want && want !== value ? want : '' };
      continue;
    }
    // COD r1 (Domain) — force replacing a carried value from the last job is said
    // out loud: on a multi-well project the last job's well may be the right one.
    if (want) plan[f.key] = { action: (value === want && ours) ? 'none' : 'fill', value: want, replaced: (value && !ours && value !== want) ? value : '' };
    else plan[f.key] = ours ? { action: 'clear' } : { action: 'none' };
  }
  return plan;
}

function _resetProjectJobDefaultClaims(opts = {}) {
  for (const f of _PROJECT_JD_FIELDS) {
    const el = document.getElementById(f.el);
    if (!el || !el.dataset) continue;
    delete el.dataset.autofilledJd;
    if (!opts.keepUserPicks) delete el.dataset.jdUserPicked;
  }
  const h = document.getElementById('job-project-defaults-hint');
  if (h) { h.style.display = 'none'; h.textContent = ''; }
}

// Unlink: the values stay on the form and are the job's own from here on, so a
// later pick of another project can never swap them (COD r1, Code Reviewer).
function _releaseProjectJobDefaultClaims() {
  _resetProjectJobDefaultClaims({ keepUserPicks: true });
}

function applyProjectJobDefaults(projectId, opts = {}) {
  const hint = document.getElementById('job-project-defaults-hint');
  if (hint) { hint.style.display = 'none'; hint.textContent = ''; }
  const current = {};
  for (const f of _PROJECT_JD_FIELDS) {
    const el = document.getElementById(f.el);
    current[f.key] = el ? { value: el.value, claim: el.dataset?.autofilledJd || '', userPicked: el.dataset?.jdUserPicked === '1' } : { value: '', claim: '' };
  }
  const isNew = !String(document.getElementById('job-id')?.value || '').trim();
  const plan = _planProjectJobDefaults(_projectJobDefaultsFor(projectId), current, { isNew, force: !!opts.force });
  const filled = [], kept = [], replaced = [];
  for (const f of _PROJECT_JD_FIELDS) {
    const el = document.getElementById(f.el);
    const step = plan[f.key];
    if (!el || !step) continue;
    if (step.action === 'fill') {
      if (f.key === 'type') { _renderJobTypeSelect(step.value); onJobTypeChange(); } // keeps an archived/legacy type as its own option
      else {
        el.value = step.value;
        if (!el.dataset.jdListen && typeof el.addEventListener === 'function') {
          el.dataset.jdListen = '1';
          el.addEventListener('input', () => { el.dataset.jdUserPicked = '1'; }); // a person typing; .value = never fires it
        }
      }
      el.dataset.autofilledJd = step.value;
      delete el.dataset.jdUserPicked;
      filled.push(f.label);
      if (step.replaced) replaced.push(`${f.label} (the last job had ${step.replaced})`);
    } else if (step.action === 'clear') {
      if (f.key === 'type') { _renderJobTypeSelect('job'); onJobTypeChange(); }
      else el.value = '';
      delete el.dataset.autofilledJd;
    } else if (step.action === 'keep') {
      delete el.dataset.autofilledJd; // the dispatcher's value — drop any stale claim
      if (step.differs) kept.push(`${f.label} is ${step.differs}, this job keeps ${step.value}`);
    } else if (step.action === 'none' && plan[f.key].value) {
      filled.push(f.label); // still ours from an earlier pick of the same project
    }
  }
  if (hint && (filled.length || kept.length)) {
    const parts = [];
    if (filled.length) parts.push(`Inherited from project: ${filled.join(', ')}. Change it below if this job is different.`);
    if (replaced.length) parts.push(`Replaced from the project: ${replaced.join('; ')}.`);
    if (kept.length) parts.push(`This project's ${kept.join('; ')}.`);
    hint.textContent = parts.join(' ');
    hint.style.display = 'block';
  }
  return plan;
}

// ============================================
// TASK-9260 — CUSTOMER-PORTAL REQUESTS ON THE BOARD
// ============================================
// A customer's portal request, once the intake has every fact it needs (job
// name, date, PO), lands here as a card ABOVE the board — where dispatchers
// already are during morning load-out, not buried in project change orders
// (architecture doc §12.3). "Approve & assign" calls approveCustomerRequest,
// which re-checks everything server-side (claim-bound tenant, approver gate) and
// runs the existing executeJobRequest: job + jobDay, status unassigned. Then the
// assignment editor opens on the new day so the crew is one more tap.
//
// This view never writes the draft itself — firestore.rules deny all client
// writes to customerRequestDrafts. The buttons are a convenience; the callable
// is the gate. A viewer who cannot approve sees the card and a note saying
// why, rather than a button that errors.

let _customerRequestDrafts = [];
let _customerRequestBusy = new Set();
let _customerRequestStaleTimer = null;

// TASK-9392 follow-up — MODULE access, never a role (Chris 2026-09-24). Mirrors
// the server gate (_criRequireApprover → _claimBoundTenantAuthorize) exactly, so
// nobody is shown a button the callable refuses: the tenant OWNER via
// tenants/{tid}.ownerId, OR a member whose `modules` array includes 'dispatch'.
// members.role is not read; a member with no modules array gets no buttons (the
// server has no legacy no-modules arm either). Demo has no member doc → none.
// Deliberately NOT checkModuleAccess('dispatch'): its PIN / legacy / default-
// allow arms would admit viewers the server refuses.
function _customerRequestCanApprove() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed') return false;
  return Array.isArray(m.modules) && m.modules.includes('dispatch');
}

function _subscribeCustomerRequests(tenantId) {
  _customerRequestDrafts = [];
  _onCustomerRequestsChanged();
  try {
    const q = query(
      collection(db, 'tenants', tenantId, 'customerRequestDrafts'),
      // 'approving' too (COD round 2): a crashed approve would otherwise hide
      // the request forever. The server re-claims a claim older than 2 min and
      // is idempotent per draft, so Retry can never make a second job.
      where('status', 'in', ['ready', 'approving'])
    );
    const unsub = onSnapshot(q, (snap) => {
      if (DispatchState.tenantId !== tenantId) return;
      _customerRequestDrafts = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.readyAt?.toMillis?.() || 0) - (b.readyAt?.toMillis?.() || 0));
      _onCustomerRequestsChanged();
    }, (err) => {
      // A member without dispatch access is DENIED by rules — that is the
      // correct answer, not an error to show. Hide the strip and stay quiet.
      if (err?.code !== 'permission-denied') console.warn('[Dispatch] customer requests listener:', err?.message);
      _customerRequestDrafts = [];
      _onCustomerRequestsChanged();
    });
    DispatchState.unsubscribers.push(unsub);
  } catch (err) {
    console.warn('[Dispatch] customer requests subscribe failed:', err?.message);
  }
}

function _fmtCustomerRequestAge(ts) {
  const ms = ts?.toMillis?.();
  if (!ms) return '';
  const mins = Math.max(0, Math.round((Date.now() - ms) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

function _renderCustomerRequestStrip() {
  _paintCustomerRequestBadges(); // TASK-9392 — cheap, and survives a header re-render
  const el = document.getElementById('customer-requests-strip');
  if (!el) return;
  const list = _customerRequestDrafts;
  if (!list.length) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  const canApprove = _customerRequestCanApprove();
  // COD round 3: staleness is time-based, so re-render when the oldest
  // in-flight claim crosses 2 minutes (no snapshot will fire for that).
  clearTimeout(_customerRequestStaleTimer);
  const pendingClaims = list.filter(d => d.status === 'approving' && d.approvingAt?.toMillis);
  if (pendingClaims.length) {
    // Only FUTURE deadlines: an already-stale claim must not stop the timer
    // for a fresh one (COD round 4).
    const future = pendingClaims.map(d => d.approvingAt.toMillis() + 120000 - Date.now()).filter(ms => ms > 0);
    if (future.length) _customerRequestStaleTimer = setTimeout(_renderCustomerRequestStrip, Math.min(...future) + 500);
  }
  const esc = escapeHtml;
  el.style.display = '';
  el.innerHTML = `
    <div style="margin:0 0 12px 0;padding:12px;border:1px solid var(--border-light,#E2E8F0);border-left:4px solid var(--brand-blue,#3B82F6);border-radius:8px;background:var(--bg-card,#fff);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;color:var(--text-primary);font-weight:600;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue,#3B82F6);">inbox</span>
        Customer requests waiting to be added to dispatch (${list.length})
      </div>
      <div style="display:flex;flex-direction:column;gap:10px;">
        ${list.map(d => {
          const f = d.fields || {};
          const approving = d.status === 'approving';
          const staleMs = approving ? (Date.now() - (d.approvingAt?.toMillis?.() || 0)) : 0;
          const busy = _customerRequestBusy.has(d.id) || (approving && staleMs < 120000);
          const po = f.poNumber ? `PO ${esc(f.poNumber)}` : (f.poDeclined ? 'No PO (customer said none)' : 'No PO');
          const items = Array.isArray(d.requestedItems) && d.requestedItems.length
            ? d.requestedItems.map(i => `${esc(i.quantity)}x ${esc(i.name)}`).join(', ') : '';
          // TASK-9769 — a texted request: who / which number, no project, and a
          // customer the office must pick when the number is on two customers.
          const isText = d.channel === 'sms';
          const via = isText
            ? `by text${d.fromLast4 ? ` from the number ending ${esc(String(d.fromLast4).replace(/[^0-9]/g, '').slice(-4))}` : ''}`
            : 'via the customer portal';
          const noCustomer = isText
            ? (Array.isArray(d.customerCandidates) && d.customerCandidates.length
              ? ` · <strong>Pick the customer:</strong> ${d.customerCandidates.map(c => esc(c?.name || '')).filter(Boolean).join(' or ')}`
              : ' · <strong>No customer matched</strong>')
            : ' · <strong>No customer on this project</strong>';
          const lastPo = isText && !f.poNumber && d.history?.lastPo?.poNumber
            ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">Last PO on file for this customer: ${esc(d.history.lastPo.poNumber)} (not filled in — check it applies)</div>` : '';
          const pending = isText && d.extraction?.state === 'pending'
            ? '<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">Reading the text…</div>' : '';
          const noText = isText && d.optOutNoted
            ? '<div style="font-size:12px;color:#B91C1C;margin-top:2px;font-weight:600;">The customer asked not to be texted. Call them to confirm.</div>' : '';
          return `
          <div style="padding:10px;border:1px solid var(--border-light,#E2E8F0);border-radius:6px;background:var(--bg-elevated,#F8FAFC);">
            <div style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:8px;">
              <div style="min-width:0;flex:1 1 240px;">
                <div style="font-weight:600;color:var(--text-primary);">${esc(f.jobName || 'Request')}</div>
                <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">
                  ${esc(d.resolved?.whenStr || (isText ? 'No day yet' : ''))} · ${po}${d.customerName ? ` · ${esc(d.customerName)}` : noCustomer}${d.projectName ? ` · ${esc(d.projectName)}` : ''}
                </div>
                <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">
                  Asked by ${esc(d.requesterName || 'customer')} ${via} · ${esc(_fmtCustomerRequestAge(d.readyAt))}
                </div>
                ${pending}${noText}${lastPo}
                ${items ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">Items: ${items}</div>` : ''}
                <details style="margin-top:6px;">
                  <summary style="cursor:pointer;font-size:12px;color:var(--brand-blue,#3B82F6);">What the customer wrote</summary>
                  <div style="white-space:pre-wrap;font-size:13px;color:var(--text-primary);margin-top:4px;">${esc(d.verbatimText || '')}</div>
                </details>
              </div>
              <div style="display:flex;gap:6px;align-items:flex-start;flex-wrap:wrap;">
                ${canApprove ? (approving ? `
                  <button class="btn btn-primary" data-id="${esc(d.id)}" ${busy ? 'disabled' : ''}
                    onclick="window.DispatchModule.approveCustomerRequest(this.dataset.id)">
                    <span class="material-icons">check</span> ${busy ? 'Working…' : 'Retry approval'}
                  </button>` : `
                  <button class="btn btn-primary" data-id="${esc(d.id)}" ${busy ? 'disabled' : ''}
                    title="Opens the job form filled in from this request and the project's New job defaults"
                    onclick="window.DispatchModule.addCustomerRequestToDispatch(this.dataset.id)">
                    <span class="material-icons">playlist_add</span> ${busy ? 'Working…' : 'Add to dispatch'}
                  </button>
                  <button class="btn btn-secondary" data-id="${esc(d.id)}" ${busy ? 'disabled' : ''}
                    onclick="window.DispatchModule.declineCustomerRequest(this.dataset.id)">Decline</button>`)
                : `<span style="font-size:12px;color:var(--text-secondary);max-width:260px;">${esc(CR_APPROVER_NOTE)}</span>`}
              </div>
            </div>
          </div>`;
        }).join('')}
      </div>
    </div>`;
}

// TASK-9769 S2b — the approve worked, but the server's texting gate held the
// customer's confirmation text back. Say so plainly; it is not an error. The
// line comes from the server (customerTextIntake.CONFIRM_NOT_APPROVED_NOTE).
function _customerRequestConfirmNote(r) {
  const note = r && typeof r.confirmationNote === 'string' ? r.confirmationNote.slice(0, 160) : '';
  if (note) window.showToast?.(note, 'info', null, 8000);
}

async function approveCustomerRequest(draftId) {
  if (!draftId || _customerRequestBusy.has(draftId)) return;
  _customerRequestBusy.add(draftId);
  _renderCustomerRequestStrip();
  try {
    const fn = httpsCallable(functions, 'approveCustomerRequest');
    const res = await fn({ draftId });
    const r = res?.data || {};
    if (!r.ok) {
      window.showToast?.(r.message || 'Could not create this job.', 'error');
      return;
    }
    window.showToast?.(r.alreadyApproved ? 'Already approved — opening the job.' : 'Job created from the customer request. Assign the crew next.', r.alreadyApproved ? 'info' : 'success');
    _customerRequestConfirmNote(r);
    // Jump the board to the job's day, then open the assignment editor once the
    // new job + jobDay arrive through the live listeners (up to ~6s).
    if (r.startAtMs) {
      DispatchState.currentDate = new Date(r.startAtMs);
      _renderDispatchBoardNow();
    }
    const started = Date.now();
    const tryOpen = () => {
      if (_resolveJobAnywhere(r.jobId) && _resolveJobDayAnywhere(r.jobDayId)) {
        showAssignment(r.jobId, r.jobDayId);
      } else if (Date.now() - started < 6000) {
        setTimeout(tryOpen, 300);
      }
    };
    tryOpen();
  } catch (err) {
    console.error('[Dispatch] approveCustomerRequest failed:', err);
    window.showToast?.(err?.message || 'Could not approve this request.', 'error');
  } finally {
    _customerRequestBusy.delete(draftId);
    _renderCustomerRequestStrip();
  }
}

async function declineCustomerRequest(draftId) {
  if (!draftId || _customerRequestBusy.has(draftId)) return;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Decline this request?', 'The customer will see it marked Declined on their portal. Nothing is added to the board.', { confirmText: 'Decline', icon: 'block' })
    : window.confirm('Decline this request? The customer will see it marked Declined.');
  if (!ok) return;
  _customerRequestBusy.add(draftId);
  _renderCustomerRequestStrip();
  try {
    const fn = httpsCallable(functions, 'declineCustomerRequest');
    await fn({ draftId });
    window.showToast?.('Request declined.', 'info');
  } catch (err) {
    console.error('[Dispatch] declineCustomerRequest failed:', err);
    window.showToast?.(err?.message || 'Could not decline this request.', 'error');
  } finally {
    _customerRequestBusy.delete(draftId);
    _renderCustomerRequestStrip();
  }
}

// ============================================
// TASK-9392 — a customer request becomes a ready-to-dispatch job FORM
// ============================================
// The customer supplies the WHAT (job name, date, PO, their own words); the
// project supplies the WHO and WHICH EQUIPMENT (its TASK-9391 New job defaults).
// "Add to dispatch" opens the ordinary New Job form with both filled in; the
// dispatcher's Save is the ordinary saveJob create (job + noon-anchored jobDays,
// under the ordinary rules), then linkCustomerRequestJob marks the draft
// approved against that job.
//
// WHY THE DRAFT IS NOT A JOB. A draft lives only in customerRequestDrafts until
// a dispatcher saves. No trigger listens on that collection, and no board,
// driver view or notification reads it, so a waiting request cannot reach a
// driver or fire onJobCreate. Flagging a `jobs` doc as draft instead would mean
// teaching every jobs/jobDays reader and every onCreate broadcaster to skip it
// — one missed reader puts customer text on a driver's phone.
//
// ONE JOB PER REQUEST. The form creates `jobs/cr_{draftId}` (the id the server
// approve path already uses) inside a create-only transaction, so a double tap,
// a second dispatcher or the old one-tap approve can never make a second job.
// If the save lands but the link call fails, Add to dispatch finds the saved
// job and just finishes the link.

const CR_APPROVER_NOTE = 'Adding a customer request to dispatch needs the Dispatch module on your login (or the account owner). You can see it here so you know it is coming.';
const CR_DRAFT_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

let _jobFormCustomerRequest = null; // { ...facts, gen } — the form this request owns
let _customerRequestOpenBusy = false;

function _customerRequestIntakeOn() {
  const st = window.AppState?.settings || {};
  // TASK-9769 — texted requests count too when that lane is on.
  return st.customerRequestIntake?.enabled === true || st.customerTextIntake?.enabled === true;
}

// Pure: pending drafts → { total, byProject }. Nothing counts when the tenant has
// intake off. Only 'ready' and 'approving' are pending (the listener's own set).
function _customerRequestCounts(drafts, intakeOn) {
  const out = { total: 0, byProject: {} };
  if (!intakeOn || !Array.isArray(drafts)) return out;
  for (const d of drafts) {
    if (!d || (d.status !== 'ready' && d.status !== 'approving')) continue;
    out.total++;
    const pid = typeof d.projectId === 'string' && d.projectId ? d.projectId : null;
    if (pid) out.byProject[pid] = (out.byProject[pid] || 0) + 1;
  }
  return out;
}

function getCustomerRequestCounts() {
  return _customerRequestCounts(_customerRequestDrafts, _customerRequestIntakeOn());
}

function getCustomerRequestDraftsForProject(projectId) {
  if (!projectId || !_customerRequestIntakeOn()) return [];
  return _customerRequestDrafts.filter(d => d.projectId === projectId);
}

function _paintCustomerRequestBadges() {
  const badge = document.getElementById('dispatch-projects-cr-badge');
  if (!badge) return;
  const cr = getCustomerRequestCounts().total;
  // TASK-9652 S2 — plus customer documents waiting for review (projects.js
  // sums customerUploadsPending off its projects listener; window handle only).
  let up = 0;
  try { up = Number(window.ProjectsModule?.getPendingCustomerUploadCount?.()) || 0; } catch (_e) { up = 0; }
  const n = cr + up;
  if (!n) { badge.style.display = 'none'; badge.textContent = ''; badge.removeAttribute('title'); return; }
  badge.textContent = n > 99 ? '99+' : String(n);
  const parts = [];
  if (cr) parts.push(`${cr} customer request${cr === 1 ? '' : 's'} waiting to be added to dispatch`);
  if (up) parts.push(`${up} customer document${up === 1 ? '' : 's'} waiting for review`);
  const label = parts.join(' · ');
  badge.title = label;
  badge.setAttribute('aria-label', label);
  badge.style.cssText = 'display:inline-block;margin-left:4px;min-width:18px;padding:0 5px;border-radius:9px;background:#DC2626;color:#fff;font-size:0.7rem;font-weight:700;line-height:18px;text-align:center;';
}

// Snapshot landed (or the listener was reset / denied): strip, badge, and the
// Projects view (reached through window.ProjectsModule — never an import).
function _onCustomerRequestsChanged() {
  _renderCustomerRequestStrip();
  try { window.ProjectsModule?.onCustomerRequestsChanged?.(); } catch (err) {
    console.warn('[Dispatch] projects customer-request refresh failed:', err?.message || err);
  }
}

// Pure: a draft → what the form gets, plus the provenance stamped on the job and
// its days. Every string is length-capped; nothing here is rendered raw.
function _customerRequestFormFacts(draft, approverUid) {
  if (!draft || typeof draft.id !== 'string' || !CR_DRAFT_ID_RE.test(draft.id)) return null;
  const f = draft.fields || {};
  const r = draft.resolved || {};
  const str = (v, max) => String(v ?? '').slice(0, max);
  const startMs = typeof r.startAt?.toMillis === 'function' ? r.startAt.toMillis()
    : (Number.isFinite(r.startAtMs) ? r.startAtMs : null);
  let dateYmd = null;
  let startTime = null;
  if (Number.isFinite(startMs)) {
    const d = new Date(startMs);
    const p = (n) => String(n).padStart(2, '0');
    dateYmd = `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    if (r.allDay === false) startTime = `${p(d.getHours())}:${p(d.getMinutes())}`;
  }
  const requesterName = str(draft.requesterName, 120).trim() || null;
  const verbatimText = str(draft.verbatimText, 4000);
  const poNumber = str(f.poNumber, 60).trim();
  return {
    draftId: draft.id,
    jobId: `cr_${draft.id}`,
    channel: draft.channel === 'sms' ? 'sms' : 'portal',
    title: str(f.jobName, 100).trim(),
    dateYmd,
    allDay: r.allDay !== false,
    startTime,
    whenStr: str(r.whenStr, 80),
    poNumber,
    poDeclined: !poNumber && f.poDeclined === true,
    customerId: typeof draft.customerId === 'string' && draft.customerId ? draft.customerId : null,
    requesterName,
    verbatimText,
    items: (Array.isArray(draft.requestedItems) ? draft.requestedItems : []).slice(0, 50)
      .map(i => ({ name: str(i?.name, 120), quantity: str(i?.quantity, 20) }))
      .filter(i => i.name),
    jobStamp: {
      source: 'customer_request',
      customerRequest: {
        channel: draft.channel === 'sms' ? 'sms' : 'portal',
        draftId: draft.id,
        requesterName,
        verbatimText,
        approvedByUid: approverUid || null
      }
    }
  };
}

// The banner at the top of the job form. Customer text is untrusted: escaped.
function _renderJobCustomerRequestBox(cr) {
  const box = document.getElementById('job-customer-request-box');
  if (!box) return;
  if (!cr) { box.style.display = 'none'; box.innerHTML = ''; return; }
  const esc = escapeHtml;
  const items = cr.items && cr.items.length
    ? `<div style="font-size:0.8rem;color:#334155;margin-top:4px;">Items: ${cr.items.map(i => `${esc(i.quantity)}x ${esc(i.name)}`).join(', ')}</div>` : '';
  const po = cr.poNumber ? '' : (cr.poDeclined ? '<div style="font-size:0.8rem;color:#334155;margin-top:4px;">The customer said there is no PO.</div>' : '');
  box.style.display = 'block';
  box.innerHTML = `
    <div style="margin-bottom:var(--spacing-md, 16px);padding:10px 12px;border:1px solid #BFDBFE;border-left:4px solid var(--brand-blue, #3B82F6);border-radius:8px;background:#EFF6FF;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:600;font-size:0.875rem;color:#1E3A8A;">
        <span class="material-icons" style="font-size:18px;">inbox</span>
        From a customer request${cr.requesterName ? ` — asked by ${esc(cr.requesterName)}` : ''}
      </div>
      <div style="font-size:0.8rem;color:#334155;margin-top:4px;">
        ${cr.channel === 'sms'
          ? 'Texted in by the customer. What we could read (job name, date, PO) is filled in — check it against their text, then Save to put it on the board. Nothing goes to the crew before you save.'
          : 'Their job name, date and PO are filled in. Crew, equipment and rates come from this project’s New job defaults. Check it all, then Save to put it on the board — nothing goes to the crew before you save.'}
      </div>
      ${items}${po}
      <details style="margin-top:6px;">
        <summary style="cursor:pointer;font-size:0.8rem;color:#1D4ED8;">What the customer wrote</summary>
        <div style="white-space:pre-wrap;font-size:0.8rem;color:#0F172A;margin-top:4px;max-height:160px;overflow-y:auto;">${esc(cr.verbatimText)}</div>
      </details>
    </div>`;
}

// DOM half, run LAST inside showAddJob's project-link branch. Owns the form for
// `gen`; a later open/edit/close bumps _jobFormGen and orphans it.
function _applyCustomerRequestFacts(cr, gen) {
  if (!cr || gen !== _jobFormGen) return;
  _jobFormCustomerRequest = { ...cr, gen };
  const byId = (id) => document.getElementById(id);
  const title = byId('job-title');
  if (title && cr.title) title.value = cr.title;
  if (cr.dateYmd) {
    const s = byId('job-start-date');
    const e = byId('job-end-date');
    if (s) s.value = cr.dateYmd;
    if (e) { e.value = cr.dateYmd; e.min = cr.dateYmd; }
    refreshJobDateOffscreenHint();
  }
  if (!cr.allDay && cr.startTime) {
    const ad = byId('job-all-day');
    if (ad) ad.checked = false;
    const tf = byId('time-fields');
    if (tf) tf.style.display = '';
    const st = byId('job-start-time');
    if (st) st.value = cr.startTime;
  }
  // The customer's PO is the one for THIS job; it wins over the project's
  // standing PO, and the project hint says so.
  if (cr.poNumber) {
    const po = byId('job-po');
    if (po) {
      const was = String(po.value || '').trim();
      po.value = cr.poNumber;
      delete po.dataset.autofilledPo;
      const pv = byId('job-po-visible');
      if (pv) pv.checked = true;
      onJobPoInput();
      const hint = byId('job-project-po-hint');
      if (hint && was && was !== cr.poNumber) {
        hint.textContent = `Using the customer’s PO ${cr.poNumber} from their request (this project’s standing PO is ${was}).`;
        hint.style.display = 'block';
      }
    }
  }
  _renderJobCustomerRequestBox(cr);
  const mt = byId('modal-job-title');
  if (mt) mt.textContent = 'New Job — customer request';
}

function _activeJobFormCustomerRequest() {
  const cr = _jobFormCustomerRequest;
  if (!cr || cr.gen !== _jobFormGen) return null;
  if (String(document.getElementById('job-id')?.value || '') !== '') return null;
  return cr;
}

// COD (TASK-9392 merge round): the customer-request job and EVERY one of its
// jobDays are written in ONE transaction — all land or none do, so a jobDay
// failure can never leave cr_{draftId} on file with no day (which the link would
// then refuse forever). Inside the same transaction:
//   • the DRAFT is re-read: a request declined, approved or pulled back since the
//     form opened refuses the save ('cr-draft-not-ready'), nothing written;
//   • the job must not exist yet ('cr-job-exists'): create-only, so a second
//     dispatcher, a double tap or the old one-tap approve can't make two jobs.
// A transactional set on a missing doc is a rules CREATE, so the ordinary jobs /
// jobDays create rules (createdBy pinned to the caller, empty ledger) apply.
// NOTE the draft check is client-side: rules don't read the draft. A member who
// bypasses it only creates a job they could create anyway; the server link
// (linkDecision) is what refuses to mark a declined request approved.
const CR_MAX_ATOMIC_DAYS = 400; // one transaction = 500 writes; job + days + headroom
function _crDraftNotReadyMessage(status) {
  if (status === 'declined') return 'This request was declined while you were filling in the job, so nothing was saved.';
  if (status === 'approved') return 'This request was already added to dispatch by someone else, so nothing was saved.';
  return 'This request is no longer waiting to be added, so nothing was saved.';
}
async function _commitCustomerRequestJobAtomically(cr, jobRef, jobData, dayWrites) {
  const days = Array.isArray(dayWrites) ? dayWrites : [];
  if (!days.length) throw Object.assign(new Error('A customer-request job needs at least one day.'), { code: 'cr-no-days' });
  if (days.length > CR_MAX_ATOMIC_DAYS) {
    throw Object.assign(new Error(`That is ${days.length} days — too many to save in one step. Shorten the date range.`), { code: 'cr-too-many-days' });
  }
  const draftRef = doc(db, 'tenants', DispatchState.tenantId, 'customerRequestDrafts', cr.draftId);
  await runTransaction(db, async (tx) => {
    const d = await tx.get(draftRef);
    const status = d.exists() ? d.data().status : null;
    if (status !== 'ready' && status !== 'approving') {
      throw Object.assign(new Error(_crDraftNotReadyMessage(status)), { code: 'cr-draft-not-ready' });
    }
    const j = await tx.get(jobRef);
    if (j.exists()) {
      throw Object.assign(new Error('This customer request already has a job.'), { code: 'cr-job-exists' });
    }
    tx.set(jobRef, jobData);
    for (const [ref, data] of days) tx.set(ref, data);
  });
  return jobRef;
}

// TASK-9392 COD r2 — the Edit-path orphaned-job repair. Pure: one row per date
// in [startDate, endDate], stepping a day at a time exactly like the loop it
// replaces (same fields, same dates), so behaviour is unchanged except that the
// writes are now atomic.
const ORPHAN_REPAIR_MAX_DAYS = 450; // one writeBatch holds 500 writes
function _orphanJobDayRows(jobId, startDate, endDate, fields) {
  const rows = [];
  if (!jobId || !startDate || !endDate) return rows;
  const dayMs = 24 * 60 * 60 * 1000;
  let cur = new Date(startDate);
  const end = new Date(endDate);
  while (cur <= end && rows.length <= ORPHAN_REPAIR_MAX_DAYS) {
    rows.push({ jobId, date: Timestamp.fromDate(new Date(cur)), ...fields, createdAt: Timestamp.now() });
    cur = new Date(cur.getTime() + dayMs);
  }
  return rows;
}

// All missing days in ONE batch: a failure on any day (including the second)
// commits nothing, so the job is never left with a partial schedule that a retry
// would then mistake for "already has days".
async function _createMissingJobDaysAtomically(rows) {
  if (!Array.isArray(rows) || !rows.length) return 0;
  if (rows.length > ORPHAN_REPAIR_MAX_DAYS) {
    throw new Error(`This job spans more than ${ORPHAN_REPAIR_MAX_DAYS} days — too many to add in one step. Shorten the date range and save again.`);
  }
  const batch = writeBatch(db);
  for (const row of rows) batch.set(doc(collection(db, 'tenants', DispatchState.tenantId, 'jobDays')), row);
  await batch.commit();
  return rows.length;
}

// The job is on file but has no day. editJob resolves jobs from the live board
// window (or search results), so a job outside the window is handed over through
// searchJobs first — the same hand-off the search view uses.
function _openCustomerRequestJobForRepair(jobId, jobDoc) {
  if (!_resolveJobAnywhere(jobId)) {
    DispatchState.searchJobs = [...(DispatchState.searchJobs || []).filter(j => j.id !== jobId), { id: jobId, ...(jobDoc || {}) }];
  }
  window.showToast?.('This request’s job is saved but has no day on the board yet. Check the date and press Save to put it on the board, then tap Add to dispatch again.', 'warning', null, 10000);
  editJob(jobId);
}

async function _linkCustomerRequestAfterSave(draftId, jobId) {
  try {
    const fn = httpsCallable(functions, 'linkCustomerRequestJob');
    const res = await fn({ draftId, jobId });
    window.showToast?.(res?.data?.alreadyApproved
      ? 'This customer request was already marked as added.'
      : 'Customer request added to dispatch.', 'success');
    _customerRequestConfirmNote(res?.data);
    return true;
  } catch (err) {
    console.warn('[Dispatch] linkCustomerRequestJob failed:', err);
    window.showToast?.(`The job is saved, but the customer request still shows as waiting (${err?.message || 'error'}). Tap Add to dispatch on it again to finish.`, 'warning', null, 9000);
    return false;
  }
}

async function addCustomerRequestToDispatch(draftId) {
  if (!draftId || _customerRequestOpenBusy || _customerRequestBusy.has(draftId)) return;
  const draft = _customerRequestDrafts.find(d => d.id === draftId);
  if (!draft) { window.showToast?.('That request is no longer waiting.', 'info'); return; }
  if (!_customerRequestCanApprove()) { window.showToast?.(CR_APPROVER_NOTE, 'info', null, 7000); return; }
  const cr = _customerRequestFormFacts(draft, auth.currentUser?.uid || null);
  if (!cr) { window.showToast?.('This request could not be read.', 'error'); return; }
  _customerRequestOpenBusy = true;
  _customerRequestBusy.add(draftId);
  _onCustomerRequestsChanged();
  try {
    // Saved before but never linked (link call failed, or another dispatcher
    // got there first)? Finish the link instead of opening a second form.
    const existing = await getDoc(doc(db, 'tenants', DispatchState.tenantId, 'jobs', cr.jobId));
    if (existing.exists()) {
      // COD (merge round): a job with NO day (legacy, or a partial write from
      // before the atomic save) would make the link refuse forever. Open it in
      // the EDIT form instead — its save runs the orphaned-job repair (saveJob's
      // edit path creates the missing jobDays), then Add to dispatch links it.
      const days = await getDocs(query(
        collection(db, 'tenants', DispatchState.tenantId, 'jobDays'),
        where('jobId', '==', cr.jobId), limit(1)));
      if (days.empty) {
        _openCustomerRequestJobForRepair(cr.jobId, existing.data());
        return;
      }
      await _linkCustomerRequestAfterSave(draftId, cr.jobId);
      return;
    }
    let link = null;
    if (draft.projectId && typeof window.ProjectsModule?.resolveProjectNewJobPrefill === 'function') {
      link = await window.ProjectsModule.resolveProjectNewJobPrefill(draft.projectId);
    }
    if (!link) {
      if (draft.projectId) window.showToast?.('Could not load this request’s project — its New job defaults were not applied.', 'warning', null, 6000);
      link = { projectId: draft.projectId || '', customerId: cr.customerId, location: '', poNumber: '', defaults: null, source: null };
    }
    if (!link.customerId && cr.customerId) link.customerId = cr.customerId;
    link.customerRequest = cr;
    window.__pendingProjectLink = link;
    showAddJob();
  } catch (err) {
    console.error('[Dispatch] addCustomerRequestToDispatch failed:', err);
    window.showToast?.(err?.message || 'Could not open this request.', 'error');
  } finally {
    _customerRequestOpenBusy = false;
    _customerRequestBusy.delete(draftId);
    _onCustomerRequestsChanged();
  }
}

// ============================================
// TASK-9587 — A CLAUDE JOB REQUEST, REVIEWED AND SAVED IN THE JOB BUILDER
// ============================================
// Claude's create_job that needs a human (crew on it, starts soon, names not
// exact, a similar job on the board) comes back with a link:
// #/dispatch/claude-job/<proposalId> (a TASK-9658 record link: app.js parks it
// as AppState.pendingRecordFocus, across a sign-in). It opens HERE as a pre-filled New Job, so
// the dispatcher fixes it in the form they know instead of approving a
// summary. What Claude ASSUMED (a customer or crew name that was not exact) is
// marked amber and has to be confirmed or changed before Save.
//
// The request stays in tenants/{tid}/mcpProposals (server-only). This view
// never reads it: the mcpJobDraft callable does, on the signed-in session's
// tenant, with Approve's rules (only the person whose Claude asked, 24 h,
// token live, Dispatch module). The job is written by the normal Save, under
// firestore.rules, as jobs/mcpd_<proposalId> in one create-only transaction
// with its days, so one request can never become two jobs; the callable then
// marks the request done. Nothing is saved by opening the link.

let _jobFormClaudeDraft = null;     // { ...facts, gen, confirmed: Set }
let _claudeJobOpenBusy = false;
const CLAUDE_JOB_READY_WAIT_MS = 15000;

function _claudeJobCall(action, payload) {
  return httpsCallable(functions, 'mcpJobDraft')({ action, ...payload }).then(r => r.data || {});
}

// The parked focus is consumed ONCE (takePendingFocus), whatever happens next.
function _consumeClaudeJobLink() {
  const focus = window.RecordLinks?.takePendingFocus?.('dispatch') || null;
  if (focus && focus.type === 'locate') { _p811OpenLocateLink(focus.id); return; } // TASK-9862
  const id = claudeJobIdFromFocus(focus);
  if (id) openClaudeJobDraft(id);
}

// TASK-9671 S1 — #/dispatch?dd_open=job&job=<id> / dispatch_board&date=<day>
// (app.js parks it as AppState.pendingFocus, window.OpenLinks). takeHint
// consumes it once (stale / other company = null); a driver, link or PIN
// session drops it. Access re-check + open: the LAZY openLinkDrain.js.
function _drainDispatchOpenHint() {
  const OL = window.OpenLinks;
  if (!OL || window.AppState?.pendingFocus?.view !== 'dispatch') return;
  const hint = OL.takeHint('dispatch');
  if (!hint) return;
  if (window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin) {
    window.showToast?.('Links from Claude open from the office login.', 'info'); // COD r1 Domain — never silent
    return;
  }
  const tenant = window.AppState?.tenant || null;
  // COD r1 Code Reviewer — THIS mount's board element: a remount (or leaving
  // Dispatch) while the import / data wait / job fetch is pending opens nothing.
  const host = document.getElementById('dispatch-content');
  __ddChunkWrap(import('./openLinkDrain.js?v=1'))
    // The board moves only for a link the user TAPPED ("the board for tomorrow"), never on its own.
    .then((m) => m.openDispatchHint(hint, { tenant, host, openJob: openJobDetailsById, goToDate: goToBoardDate, waitReady: _waitForClaudeJobData })) /* user-press: TASK-9671 link */
    .catch((e) => { console.warn('[Dispatch] open link failed', e?.message); window.showToast?.('Could not open that from the link. Find it on the Dispatch board.', 'error'); });
}

// TASK-9862 — #/dispatch/locate/<ticketId> (the connector's openUrl): open the
// Locates page on that ticket's site. The permits snapshot decides access.
function _p811OpenLocateLink(permitId) {
  if (!is811Enabled()) { window.showToast?.('Locates are turned off for this company.', 'info'); return; }
  const until = Date.now() + 15000;
  const tryOpen = () => {
    if (!DispatchState.permitsLoaded) { if (Date.now() < until) setTimeout(tryOpen, 300); else window.showToast?.('Locates did not load. Open Locates from Dispatch.', 'warning'); return; }
    const p = (DispatchState.permits || []).find(x => x && x.id === permitId);
    if (!p) { window.showToast?.('That locate ticket was not found.', 'warning'); return; }
    show811Submission();
    const L = window.Locates811;
    const key = L ? L.groupKey(p.customerId, L.siteNameOf(p)) : '';
    if (key) p811OpenGroup(key);
    else renamePermit(permitId);
  };
  tryOpen();
}

// Roster + customers must be on the board before the form can tick anyone.
function _claudeJobDataReady() {
  const L = DispatchState.dataLoaded || {};
  return !!(L.people && L.trucks && L.trailers && Array.isArray(DispatchState.customers) && DispatchState.customers.length);
}
function _waitForClaudeJobData() {
  return new Promise((resolve) => {
    if (_claudeJobDataReady()) { resolve(true); return; }
    const until = Date.now() + CLAUDE_JOB_READY_WAIT_MS;
    const t = setInterval(() => {
      if (_claudeJobDataReady()) { clearInterval(t); resolve(true); }
      else if (Date.now() > until) { clearInterval(t); resolve(false); }
    }, 250);
  });
}

async function openClaudeJobDraft(proposalId) {
  if (!proposalId || _claudeJobOpenBusy) return;
  // Same module gate as the server (owner via ownerId, or the Dispatch module).
  if (!_customerRequestCanApprove()) {
    window.showToast?.('Opening a job request from Claude needs the Dispatch module on your login (or the account owner).', 'info', null, 7000);
    return;
  }
  _claudeJobOpenBusy = true;
  try {
    window.showToast?.('Opening the job Claude asked for…', 'info');
    const reply = await _claudeJobCall('open', { proposalId });
    if (reply.state === 'done') {
      const jn = reply.jobNumber != null && reply.jobNumber !== '' ? `J-${reply.jobNumber}` : 'This job';
      window.showToast?.(`${jn} from that Claude request is already on the board.`, 'info', null, 7000);
      if (reply.jobId && typeof openJobDetailsById === 'function') { try { openJobDetailsById(reply.jobId); } catch (_) { /* toast already said it */ } }
      return;
    }
    const facts = claudeDraftFormFacts(reply);
    if (!facts) { window.showToast?.('That job request could not be read.', 'error'); return; }
    const ready = await _waitForClaudeJobData();
    showAddJob(facts.startDate || null);
    _applyClaudeDraftFacts(facts, _jobFormGen, ready);
  } catch (err) {
    console.warn('[Dispatch] TASK-9587 open Claude job request failed:', err?.code || '', err?.message || err);
    window.showToast?.(err?.message || 'Could not open that job request.', 'error', null, 8000);
  } finally {
    _claudeJobOpenBusy = false;
  }
}

// What the form holds right now, for the "still assumed" check.
function _claudeDraftFormState() {
  return {
    customerId: document.getElementById('job-customer')?.value || '',
    selected: {
      drivers: getJobCrewSelected('drivers'), helpers: getJobCrewSelected('helpers'),
      trucks: getJobCrewSelected('trucks'), trailers: getJobCrewSelected('trailers'),
    },
  };
}

function _activeJobFormClaudeDraft() {
  const cd = _jobFormClaudeDraft;
  if (!cd || cd.gen !== _jobFormGen) return null;
  if (String(document.getElementById('job-id')?.value || '') !== '') return null;
  return cd;
}

function _claudeDraftUnconfirmedNow(cd) {
  return cd ? claudeDraftUnconfirmed(cd.assumed, cd.confirmed, _claudeDraftFormState()) : [];
}

// Amber outline on the customer box and on each crew row still assumed.
function _paintClaudeDraftMarks(unconfirmed) {
  document.querySelectorAll('.cd-assumed').forEach(el => { el.classList.remove('cd-assumed'); el.style.outline = ''; el.style.outlineOffset = ''; });
  for (const a of unconfirmed || []) {
    const el = a.field === 'customer' ? document.getElementById('job-customer') : (_jbSuggestRow(a.field, a.id)?.row || null);
    if (!el) continue;
    el.classList.add('cd-assumed');
    el.style.outline = '2px solid #F59E0B';
    el.style.outlineOffset = '2px';
  }
}

function _renderJobClaudeDraftBox(cd) {
  const box = document.getElementById('job-claude-draft-box');
  if (!box) return;
  if (!cd) {
    box.style.display = 'none'; box.innerHTML = '';
    _paintClaudeDraftMarks([]);
    return;
  }
  const open = _claudeDraftUnconfirmedNow(cd);
  box.style.display = 'block';
  box.innerHTML = claudeDraftBannerHtml(cd, open);
  _paintClaudeDraftMarks(open);
}

// One delegated listener, bound once: "Looks right" on an assumed value, and a
// re-paint whenever the customer or a crew tick changes.
function _bindClaudeDraftBox() {
  const box = document.getElementById('job-claude-draft-box');
  if (!box || box.dataset.cdBound) return;
  box.dataset.cdBound = '1';
  box.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-cd-confirm]');
    const cd = _activeJobFormClaudeDraft();
    if (!btn || !cd) return;
    cd.confirmed.add(btn.dataset.cdConfirm);
    _renderJobClaudeDraftBox(cd);
  });
  const form = document.getElementById('form-job');
  if (form && !form.dataset.cdBound) {
    form.dataset.cdBound = '1';
    const repaint = () => { const cd = _activeJobFormClaudeDraft(); if (cd) setTimeout(() => _renderJobClaudeDraftBox(cd), 20); };
    form.addEventListener('change', repaint);
    form.addEventListener('click', (e) => { if (e.target.closest('.checkbox-item')) repaint(); });
  }
}

// DOM half: fill the New Job form for `gen`. A later open/edit/close bumps
// _jobFormGen and orphans it. Every value goes in with .value (never markup).
function _applyClaudeDraftFacts(facts, gen, dataReady) {
  if (!facts || gen !== _jobFormGen) return;
  const byId = (id) => document.getElementById(id);
  const missed = [];
  if (byId('job-title')) byId('job-title').value = facts.title;
  if (facts.startDate) {
    const s = byId('job-start-date'); const e = byId('job-end-date');
    if (s) s.value = facts.startDate;
    if (e) { e.value = facts.endDate || facts.startDate; e.min = facts.startDate; }
    const tog = byId('job-multi-day-toggle');
    if (tog && facts.multiDay) { tog.checked = true; toggleMultiDay(); if (e) e.value = facts.endDate; }
    setDateAssumedFlag(false);
    refreshJobDateOffscreenHint();
  }
  if (facts.customerId) {
    const sel = byId('job-customer');
    if (sel) {
      sel.value = facts.customerId;
      if (sel.value === facts.customerId) onCustomerChange();
      else { sel.value = ''; missed.push(`customer ${facts.customerName || ''}`.trim()); }
    }
  }
  if (byId('job-location')) byId('job-location').value = facts.location;
  if (byId('job-well-name')) byId('job-well-name').value = facts.wellName;
  // TASK-9737 — the request's start time: All day off + the time (the same
  // fields the form saves as allDay / defaultStart).
  if (facts.startTime && byId('job-start-time')) {
    const ad = byId('job-all-day');
    if (ad) ad.checked = false;
    const tf = byId('time-fields');
    if (tf) tf.style.display = '';
    byId('job-start-time').value = facts.startTime;
    // No end time was given: leave it empty (saved as none), never the form's 18:00 default (diff COD).
    if (byId('job-end-time')) byId('job-end-time').value = '';
  }
  // TASK-9750 — Claude asked for a tentative job: pre-tick it (the person saving can untick).
  if (facts.tentative === true && byId('job-tentative')) byId('job-tentative').checked = true;
  if (facts.notes && byId('job-notes')) { byId('job-notes').value = facts.notes; _syncJobNotesHeight(); }
  if (facts.poNumber && byId('job-po')) {
    const po = byId('job-po');
    po.value = facts.poNumber; delete po.dataset.autofilledPo; onJobPoInput();
  }
  if (facts.waypoints.length) {
    document.querySelectorAll('#job-waypoints-list .waypoint-row').forEach(row => {
      if (!row.querySelector('.waypoint-name')?.value?.trim() && !row.querySelector('.waypoint-url')?.value?.trim()) row.remove();
    });
    facts.waypoints.forEach(w => addWaypoint(w.name, w.url, '', w.lat, w.lng));
  }
  for (const section of ['drivers', 'helpers', 'trucks', 'trailers']) {
    for (const id of facts.crew[section]) {
      const hit = _jbSuggestRow(section, id);
      if (!hit || !hit.row) {
        const label = facts.names?.[id] || (section === 'trucks' ? 'a vehicle' : section === 'trailers' ? 'a trailer' : 'a crew member');
        missed.push(label);
        // COD Domain — a person or unit Claude put on the job that is not on
        // this roster must be acknowledged, never silently dropped at Save.
        facts.assumed.push({ field: 'missing', id: `${section}-${id}`, said: '', used: label });
        continue;
      }
      if (!hit.input.checked) toggleJobCheckbox(hit.row, section);
      document.getElementById(`job-section-${section}`)?.classList.remove('hidden');
      document.getElementById(`job-section-${section}`)?.classList.add('open');
    }
  }
  checkJobDuplicate();
  revealPopulatedJobFormSections();
  _jobFormClaudeDraft = { ...facts, gen, confirmed: new Set() };
  _bindClaudeDraftBox();
  _renderJobClaudeDraftBox(_jobFormClaudeDraft);
  const mt = byId('modal-job-title');
  if (mt) mt.textContent = 'New Job — from Claude';
  if (missed.length) {
    window.showToast?.(`Could not fill in: ${missed.slice(0, 4).join(', ')}${missed.length > 4 ? '…' : ''}. ${dataReady ? 'Pick them by hand.' : 'The roster was still loading — pick them by hand.'}`, 'warning', null, 9000);
  }
}

// TASK-9624 — fill a NEW job from a work order / rate con / BOL. The fill
// itself lives in the lazy views/docIntake.js (fillJobForm): empty fields
// only, every value via .value, nothing written — the normal Save writes the
// job. These are the form helpers it calls (module-local here).
function openJobFromDocument() {
  if (String(document.getElementById('job-id')?.value || '') !== '') { window.showToast?.('Filling from a document is for a new job. Open New Job first.', 'info'); return; }
  window.DocIntake?.open({ surface: 'job_builder', jobForm: { toggleMultiDay, onCustomerChange, onJobPoInput, setDateAssumedFlag, refreshJobDateOffscreenHint, syncNotes: _syncJobNotesHeight, checkJobDuplicate, revealPopulatedJobFormSections } });
}

// Save gate: every assumed value confirmed or changed. Returns true to block.
function _claudeDraftBlocksSave() {
  const cd = _activeJobFormClaudeDraft();
  if (!cd) return false;
  const open = _claudeDraftUnconfirmedNow(cd);
  if (!open.length) return false;
  _renderJobClaudeDraftBox(cd);
  document.getElementById('job-claude-draft-box')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  window.showToast?.(`Check the ${open.length} value${open.length === 1 ? '' : 's'} Claude guessed (in amber) — tap Looks right or change it — then Save.`, 'warning', null, 8000);
  return true;
}

// The job + every jobDay in ONE transaction, create-only on jobs/mcpd_<id>.
async function _commitClaudeDraftJobAtomically(jobRef, jobData, dayWrites) {
  const days = Array.isArray(dayWrites) ? dayWrites : [];
  if (!days.length) throw Object.assign(new Error('A job needs at least one day.'), { code: 'cd-no-days' });
  if (days.length > CR_MAX_ATOMIC_DAYS) {
    throw Object.assign(new Error(`That is ${days.length} days — too many to save in one step. Shorten the date range.`), { code: 'cd-too-many-days' });
  }
  await runTransaction(db, async (tx) => {
    const j = await tx.get(jobRef);
    if (j.exists()) throw Object.assign(new Error('This Claude request is already on the board.'), { code: 'cd-job-exists' });
    tx.set(jobRef, jobData);
    for (const [ref, data] of days) tx.set(ref, data);
  });
  return jobRef;
}

async function _finishClaudeDraftAfterSave(proposalId, jobId) {
  try {
    await _claudeJobCall('finish', { proposalId, jobId });
    return true;
  } catch (err) {
    console.warn('[Dispatch] TASK-9587 finish failed:', err?.message || err);
    window.showToast?.('The job is saved. Claude’s request may still show as waiting — opening its link again will mark it done.', 'warning', null, 9000);
    return false;
  }
}

function _releaseClaudeDraft(proposalId, claimId) {
  _claudeJobCall('release', { proposalId, claimId }).catch(err => console.warn('[Dispatch] TASK-9587 release failed:', err?.message || err));
}

window.DispatchModule = {
  rentalReqConfirm, rentalReqDecline, // TASK-10106 S1b
  openPinnedAsset,          // TASK-10007 — strip chip → that asset in Roster
  renderPinnedAssetStrip,   // TASK-10007
  // P0 1019 — app.js calls this at every logout / company switch (beside ClockMode.reset).
  resetTenantData: resetDispatchTenantData,
  // TASK-9260 — customer-portal request approve / decline (server-gated)
  approveCustomerRequest,
  declineCustomerRequest,
  // TASK-9585 S7d — permit costs waiting for a yes
  togglePermitWaitList,
  editPermitWait,
  confirmPermitWait,
  savePermitWaitEdit,
  notBillablePermitWait,
  togglePermitDeclinedList,   // S7d reinstate
  reinstatePermitWait,        // S7d reinstate
  // TASK-9392 — Add to dispatch (prefilled form) + the badge/project seam
  addCustomerRequestToDispatch,
  // TASK-9587 — a Claude job request opened in the Job Builder
  openClaudeJobDraft,
  // TASK-9624 — fill a new job from a work order / rate con / BOL
  openJobFromDocument,
  getCustomerRequestCounts,
  repaintProjectsBadge: _paintCustomerRequestBadges, // TASK-9652 S2 — projects.js calls this per projects snapshot
  getCustomerRequestDraftsForProject,
  customerRequestCanApprove: _customerRequestCanApprove,
  customerRequestApproverNote: CR_APPROVER_NOTE,
  refreshProjectSelects,
  // Prefill the job PO from a picked project's standing PO (fill-if-blank only)
  applyProjectPo,
  applyProjectJobDefaults, // TASK-4696
  // TASK-9391 — "Previous notes" panel button + the project-prefill seam (TASK-9392)
  useJobPrevNotes,
  syncJobNotesHeight: () => _syncJobNotesHeight(), // TASK-4808 — #job-notes oninput
  onJobPhotosVisibleChange, // TASK-10224
  openNewJobWithProjectPrefill,
  // TASK-9289 S1 — board → project
  openProjectFromBoard,
  newJobInProjectFromBoard,
  // TASK-4782 — PO entry hygiene on a money field: suggest the customer's own
  // spelling, warn at QuickBooks' 15-char limit. Never blocks, never rewrites.
  onJobPoInput,
  useExistingPoSpelling,
  refreshJobPoSuggestions,
  addJobExtraPo, // TASK-9811 S1
  // TASK-1615 — "N with a bad location" chip in the dispatch map header
  showBadLocationJobs,
  showUnlocatedStops,
  // TASK-9175 — map key / layers / project filter
  toggleMapLayer,
  toggleMapKey,
  filterMapProjects,
  setMapProject,
  clearMapProject,
  showUnplottedSavedLocations,
  showLastDispatchedIssues,
  // TASK-4800 — Equipment on site layer: list panel + its filters
  showEquipmentList,
  showEquipmentUnplotted,
  setEquipmentListFilter,
  // TASK-4800 S3 — Roster › Assets › On site reads the same derivation, and
  // hands off to the map with the layer on.
  planEquipmentOnSite,
  openEquipmentMap,
  equipmentAgeColors: Object.freeze({ ...MAP_EQUIPMENT_AGE_COLORS }),
  equipmentAgeDays: Object.freeze({ fresh: MAP_EQUIPMENT_FRESH_DAYS, stale: MAP_EQUIPMENT_STALE_DAYS }),
  // Read-only view of what each layer drew — the map draws to a canvas, so this
  // is how a test (or support) counts pins per layer.
  getMapLayerMarkers: () => ({
    stops: DispatchState.mapMarkers.slice(),
    saved: _savedLocMarkers.slice(),
    lastDispatched: _lastDispatchedMarkers.slice(),
    fleet: _samsaraMarkers.slice(),
    project: _projectPinMarkers.slice(),
    equipment: _equipmentMarkers.slice(),
    estimated: _estimatedMarkers.slice(),   // TASK-4800 S4
  }),
  // TASK-961 — in-app job activity log (History button)
  openJobActivityLog,
  filterJobActivity,
  // TASK-347 Slice 3+4 — driver bottom-nav + Actions sheet HTML helpers.
  // Exported 2026-05-28 so jobboard.js can render the same surface for PIN
  // drivers on /jobboard (canonical driver view per TASK-355). Previously
  // these only fired inside dispatch.js renderDriverBoard, so jobboard
  // landings had to use the redundant top-bar colored action buttons.
  getDriverBottomNavHTML,
  getDriverActionsSheetHTML,
  // Driver-mode view-mode toggle + calendar
  toggleDriverViewMode,
  jumpDriverDayFromCalendar,
  driverJumpToToday,
  // Layer filters (Jobs / Days Off / Maintenance) for Week + Month views
  toggleLayerFilter,
  syncLayerFilterChips,
  // TASK-424 #5 (C2): widen the windowed admin jobs/jobDays live stream.
  loadOlderDispatch,
  // TASK-9174 — user-initiated, bounded road-route warm for jobs already on the map.
  warmVisibleJobRoutes,
  promptSetLoadsExpected,
  // Export / Print job schedule (TASK-516)
  showExportJobsModal,
  exportJobs,

  // TASK-191 Phase 2 — per-job geofence config (job-edit modal)
  toggleGeofenceSection,
  geocodeJobGeofence,

  // Job modals
  showAddJob,
  openAddJobWithDate,
  startJsaForJob,      // TASK-10011
  reopenJobDetails,    // TASK-10011 — jsa.js returns here after a JSA started from the office
  saveJob,
  showJobDetails,
  openJobDetailsById, // Projects → job click (any date, Dispatch not mounted)
  showSafetyHoldDetails,
  openMaintenanceForVehicle,
  showTruckInfo,
  renderTruckInfo,
  showTrailerInfo,
  renderTrailerInfo,
  showAssignment,
  saveAssignment,
  asgSetUnitPair, // TASK-9763 — "Who's in what" per-unit pick
  asgAddUnitPair, // TASK-9763 S4 — add a second person to a unit (team pairing)
  asgSetUnitSeat, // TASK-10193 S2 — "May drive" on a team truck's 2nd+ person
  asgMoveCrew, asgResetCrewOrder, // TASK-9763 S3 — the day's "first on" order
  senMove, senSort, senSave, senClear, // TASK-9763 S3 — Dispatch Settings → Crew seniority
  dismissComplianceWarning, // TASK-258 v1.15.535 — audit log on warning dismiss
  editJob,
  setJobSplitBy, // TASK-1221 — per-job ticketing mode toggle
  archiveJob,
  deleteJobFromDetails,
  openRecentlyDeleted, // TASK-10010 — Dispatch ⋮ → Recently deleted (owner)
  restoreDeletedJob,   // TASK-10010 — the Restore on the delete toast
  removeJobDay, // TASK-4792 — take a single day off a multi-day job
  cancelAssignment, // TASK-9943 — crew editor Cancel offers to undo a just-added day
  openEditJobRemoveDay, // TASK-9943 — Edit Job → Remove Day list
  closeModal,
  duplicateCurrentJob,
  addDayToCurrentJob,
  deleteCurrentJob,

  // Navigation
  navigate,
  navigateWeek,
  goToToday,
  // TASK-9112 — the single source of truth for "what dates is the board showing".
  // Exported for the same reason isDispatchScheduleReady is: support can answer
  // "why didn't my job show up?" from the console instead of guessing. Read-only.
  getVisibleDateRange,
  // Called from the end-date input's inline onchange in the job modal markup.
  refreshJobDateOffscreenHint,
  // The save toast's "Go to <date>" button. NOTHING calls this automatically —
  // the board only moves when the dispatcher asks it to.
  goToBoardDate,
  loadPreviousDays,
  setListQuickNav,

  // Customer modals
  showQuickAddCustomer,
  saveQuickCustomer,
  showEditCustomer,
  saveEditCustomer,
  deleteCustomer,
  addContactRow,
  removeContactRow,
  showQuickContractForm,
  createQuickContract,

  // POC
  onCustomerChange,
  onPocChange,
  toggleAllDay,
  // TASK-273: multi-day toggle + start-date sync
  toggleMultiDay,
  onJobStartDateChange,

  // Crew selection
  selectAllCrew,
  onJobTypeChange,
  fillProjectReturnsForType, // TASK-10006 S2
  showManageJobTypes, // TASK-9390
  closeManageJobTypes,

  // AI Features
  toggleAiPrompt,
  parseWithAi,

  // Day Details & Quickview
  showDayDetails,
  showQuickview,

  // Driver Board
  showDriverQuickview,
  openDirections,
  showDriverTruckDetails,
  refreshDriverBoard,
  openDriverTimeOff,
  // TASK-348 — SPA personal-action wrapper (explicit personId/personName)
  openDriverTimeOffForPerson,
  closeDriverTimeOff,
  submitDriverTimeOff,
  editDriverTimeOff,
  cancelDriverTimeOff,
  discardDriverTimeOffDraft, // TASK-4720 offline outbox
  flushDriverTimeOffOutbox,
  onTimeOffDateChange,

  // Driver JSA Phase 2 removed 2026-05-28 — TASK-361 relocation cleanup.
  // jsa.js + window.JSAModule own the driver-facing "My Recent JSAs"
  // section in Inspections > JSA tab.

  // Driver Timesheet
  toggleTimesheet,

  // Driver Clock In/Out
  handleDriverClockButton,
  // TASK-347 — driver bottom-nav (Phase 1)
  openDriverActionsSheet,
  closeDriverActionsSheet,
  // TASK-1194 — driver Help sheet (replaces the dead '#/help' hash route)
  openDriverHelpSheet,
  openDriverClockIn,
  onClockInCustomerChange,
  submitDriverClockIn,
  openDriverClockOut,
  submitDriverClockOut,
  switchDriverTab,
  openDriverSwitchJob,
  // TASK-348 — SPA personal-action wrapper (explicit personId/personName)
  openSwitchJobForPerson,
  closeSwitchJobModal,
  onSwitchJobCustomerChange,
  submitSwitchJob,
  openDriverVehicleList,
  showDriverVehicleInfo,
  openDriverRecordExpense,
  // TASK-348 — SPA personal-action wrapper (explicit personId/personName)
  openDriverRecordExpenseForPerson,

  // Citations / Exam Reports
  openCitationUpload,
  onCitationPhotos,
  submitCitation,

  // Mass Text
  // Exposed so support can answer "was the board loaded when they hit send?"
  // from the console instead of guessing. Read-only predicate, no setter.
  isDispatchScheduleReady,
  showMassText,
  toggleMassTextConfirmAsk,   // TASK-9842
  onMassTextRecentToggle,     // TASK-9842
  setMassTextDay,
  copyMassText,
  sendMassText,
  onMassTextInput,
  discardMassTextDraft,   // TASK-9532 — the restored-draft banner's button
  copyDriverLink,
  regenerateDriverLink,
  updateMassTextContent,
  togglePersonalizedMode,
  toggleFullNotes,

  // Individual Text
  sendIndividualText,
  copyIndividualText,
  sendIndividualTextSMS,
  onIndividualTextInput,

  // Waypoints
  addWaypoint,
  removeWaypoint,
  clearAllWaypoints,
  updateWaypointStatus,
  waypointOpenHref, // TASK-10116 — jobboard.js _jbWaypointLinks reaches it here (Pitfall #1)
  toggleSaveWaypoint,
  calcRouteMileage,
  // TASK-1662 — exposed so a test can drive the two-waypoint interleaving and
  // assert the OUTCOME (resolved / no-result / superseded), which is the whole
  // point of the fix. Nothing in the app calls these from the window object.
  awaitPendingGeocodes,
  GEOCODE_OUTCOME,
  // TASK-9194 — same reasoning: a test (and the board badge) must be able to
  // ask "does this job have a stop with no pin" without re-deriving it.
  unpinnedStopCount,

  // TASK-1616 — map date range controls (wired from the map's range bar).
  setMapRangePreset,
  toggleMapCustomRange,
  applyMapCustomRange,

  // Saved Locations
  showSavedLocationsPicker,
  showAddSavedLocation: () => showSavedLocationForm(null),
  editSavedLocation: (id) => {
    const loc = DispatchState.savedLocations.find(l => l.id === id);
    if (loc) showSavedLocationForm(loc);
  },
  useSavedLocation,
  useYardAsWaypoint,
  filterSavedLocations,
  // TASK-9421 — customer scope, source chips, customer tags on the form.
  setSavedLocScope,
  toggleSavedLocSource,
  addSavedLocCustomer,
  removeSavedLocCustomer,
  // TASK-9446 — Draft Price Book hint + cross-module cache invalidation.
  invalidateCustomerPriceBooks,
  openDraftPriceBook,

  // TASK-4783 — places from past jobs (coordinate dedupe + customer scope).
  usePastPlace,
  setPastPlaceScope,
  // Exposed for tests/dogfooding: rebuild the index from raw rows without a fetch.
  _ppBuildPlaceIndex,
  _ppInvalidateCache,
  saveSavedLocation: (id) => saveSavedLocationRecord(id || null),
  deleteSavedLocation: (id) => deleteSavedLocationRecord(id),
  // TASK-9540 — the ONE places manager (Add Job picker + map key) + categories.
  openPlacesManager,
  closePlacesManager,
  placeFormBack: () => _placesReturnToList(),
  previewSavedLocCategory,
  setSavedPlaceCategory,
  toggleMapSavedCategory,

  // Job Photos
  handleJobPhoto,
  removeExistingJobPhoto,
  removePendingJobPhoto,

  // Permits
  toggleAttachmentsSection,
  togglePermitsSection,
  // TASK-4649 — Permit Required checkbox handlers
  onPermitRequiredToggle,
  removeJobPermit,     // TASK-9648 — 811 ticket rows in the job form
  open811Picker,
  toggle811PickerItem,
  apply811Picker,
  upload811FromPicker,
  render811PickerList: _render811PickerList,
  // TASK-9767 — projects.js renders a project's tickets from the live snapshot
  getPermitsSnapshot: () => ({ loaded: DispatchState.permitsLoaded === true, permits: DispatchState.permits || [] }),
  refreshJobLocationPlace, // TASK-9648 — Site section
  refreshGeofenceCenterLine,
  useMyLocationForJob,
  openJobLocationInMaps,
  maybeAutoSuggestPermitRequired,
  handlePermitUpload,
  removeExistingPermit,
  removePendingPermit,
  previewExistingPermit,   // TASK-9108 — tile click → lightbox / new tab
  previewPendingPermit,
  retryFailedPermitUploads,   // TASK-9151 — partial-failure recovery (footer banner)
  discardFailedPermitUploads, // TASK-9151

  // Assignment
  updateSectionCount,
  toggleCheckbox,
  setAssetQty,
  toggleAssetDirection,
  updateJobLoadCalcLive,
  recordLoadPickup,
  undoLoadPickup,
  filterMultiSelect,
  toggleJobCheckbox,
  onLineItemPick,
  onContractPick,
  onRatePanelPick,
  onInlineLineItemPick,

  // Waypoints Modal
  showWaypointsModal,

  // Overflow Menu Actions
  sendIndividualText,
  cancelJobDay,
  confirmJobDay, // TASK-9750
  markJobDayTentative, // TASK-10288 D
  jobDayActionFromDetails, // TASK-10288 F
  isTentativeJobDay, // TASK-9750 — pure, for tests
  getCrewTextJobsForDate, // TASK-9750
  restoreJobDay,
  duplicateJob,
  createReturnJob, // TASK-10006 — Job Details "Create return job"
  createReturnJobForProject, // TASK-10006 — Projects strip button
  markRentalPickedUp, // TASK-10006 S3 — Job Details "Mark picked up"
  undoRentalPickup, // TASK-10006 S3b — its 7-day Undo
  projectRentalsOut, // TASK-10006 — Projects "Out on this project" strip
  clearCopiedCrewAndEquipment, // TASK-9598 — the copy banner's button
  copyToNewJobFromEdit, // TASK-1111 — dirty-guarded Copy-to-New-Job from the edit form
  addDayToJob,

  // TASK-9258 — Job Templates (saved equipment kit). Apply seeds the OPEN FORM
  // and writes nothing; the money mitigation is the unconfirmed mark that rides
  // from the picker onto the jobDay and the assetTransactions row.
  applyJobTemplate,
  saveJobAsTemplate,
  toggleJobTemplateSave,
  // Exported (pure, no DOM, no Firestore) so tests/task9258-job-templates.unit.cjs
  // asserts the SHIPPED functions rather than a copy.
  buildJobTemplateDoc,
  buildJobDayTemplateFields,
  jobTemplateAssetStamp,
  jobTemplateKey,
  mergeTemplateApplication,
  inheritJobDayTemplateFields,
  reconcileStoredTemplateMeta,

  // Smart Duplicate Warning
  checkJobDuplicate,
  addDayInstead,
  addDupeMatchProject, // TASK-1581 S3

  // TASK-4567 — pure date-range math behind Add Day. Exported (no Firestore, no
  // DOM) so tests/task4567-add-day-range.unit.cjs asserts the SHIPPED functions.
  computeJobRangeForAddedDay,
  computeAdminWindow,
  isDateOutsideAdminWindow,

  // Context Menu & Drag
  showJobContextMenu,

  // Lanes View
  toggleLanesPeople,
  toggleLanesTrucks,
  toggleLanesTrailers,
  toggleLanesAssets,
  openLaneCell,
  openLaneWeek,
  openLaneJob,

  // 811 Permits
  show811Submission,
  handlePermitFile,
  clearPermitFile,
  savePermit,
  deletePermit,
  renamePermit,
  p811SetUtilitiesClear, // TASK-9925
  savePermitEdit,
  // TASK-9859 — bulk uploader + edit-row job list
  p811DragOver,
  p811DragLeave,
  p811Drop,
  p811RemoveRow,
  p811RowSet,
  // TASK-9862 — Locates page, review sheet, site detail, renew, attach, hard stop
  p811SetFilter,
  p811RowMoreOpen, p811NewPotentialForTickets, p811EditJobQuery, p811NewProjectFromGroup, // TASK-9994 piece 1
  p811SetSort, p811OpenSettings, // TASK-9994 sort + 811 settings gear (TASK-10180: the tree is gone)
  p811PotentialCloseOnJob, // TASK-9994 — potentials already on a job
  p811CloseReview,
  p811FillDown,
  p811RetryReview: () => _p811RenderRows(),
  p811OpenGroup,
  p811AttachGroupToJob,
  p811AttachGroupToProject, p811GroupTile, p811GroupProjectGo, p811PlaceMenu, p811SearchEverywhere, // TASK-9887
  p811RowDupOk, // TASK-9887 duplicate upload
  p811NewPotential, p811OpenPotential, p811PotCustomerChanged, p811ClosePotential, p811SavePotential, p811PotentialStatus, p811PotentialWontFromSheet, // TASK-9887 slice 4
  p811PotentialFor, p811PotentialMarkUsed, p811JobUsePotential, p811JobUndoPotential, p811JobDismissPotential, // TASK-9887 — dispatch suggestion (projects.js reads the first two)
  p811GroupSelChanged,
  p811GroupRenewGo,
  p811GroupAttachGo,
  p811MergeGroup,
  p811RenameSiteStart, // TASK-9920
  p811RenameSiteSave,
  p811RenameSiteCancel,
  p811StartFromGroup,
  p811StartClearProject,
  p811RefreshGroup: () => _p811RenderGroup(false),
  p811JobFormSiteChanged,
  p811JobAttachAll,
  p811JobAttachDismiss,
  p811JobAttachOne, p811JobNudgeToggle, p811JobNudgeShowAll, // TASK-10181
  p811StopChoose,
  p811RowCustomer,
  p811RowJobQuery,
  p811RowJob,
  p811EditCustomer,
  p811EditJob,
  p811RowGenName,
  p811RowProjectText,
  // TASK-9859 — the matcher projects.js uses for "811 tickets waiting" (pure).
  p811Similarity: _p811Similarity,
  P811_MATCH_MIN,
  p811RowProject,

  p811EditExpHint, // BUG 2026-10-20 — printed expiry next to good-through
  open811StartPicker,
  p811StartFromTicket,
  p811StartUseProject,
  p811StartCreateProject,
  p811StartAddToJob,
  p811ToggleWaiting,
  p811EditGenName,
  renderPermitsList,
  fill811ProjectSelect, // 811 project dropdown

  // Popovers
  showMissingDvirPopover,
  showP811CardPopover, // Chris 10/27 locate icon on office board cards
  showMissingJsaPopover,
  showTicketStatusPopover,
  openTicketInBillingFromDispatch, // TASK-9645 C
  showAssignmentPopover,
  toggleListDetail, // TASK-10050 List view "show on every job" header toggles
  dismissJsa,
  openJsaSignatures, // TASK-794
  showJsaListForJobDay, // TASK-9371

  // Search
  onSearchInput,
  clearSearch,
  setSearchStatus,
  loadMoreSearchResults,

  // DOT Compliance
  runJobComplianceCheck,
  onJobDotFlagChange,   // TASK-9370
  confirmJobDotFlags,   // TASK-9370
  runAssignmentComplianceCheck,

  // Dispatch Settings (decentralized from main Settings 2026-05-19)
  applyJobFormSectionVisibility,
  revealPopulatedJobFormSections,
  // TASK-4695 — Add Job modal: Project picker + collapsed groups
  onJobProjectChange,
  onJobSiteChange, // Wolverine 2026-10-21
  toggleJobProjectDoc, viewJobProjectDoc, // TASK-9650
  setJobNewProjectMode,
  refreshJobFormGroups: _refreshJobFormGroups,
  togglePlacardToken,
  movePlacardToken,
  toggleCrewTextToken,   // TASK-9597
  moveCrewTextToken,
  resetCrewTextTokens,
  editCrewTextLayout,
  openDispatchSettingsModal,
  openDriverAppFromDispatch, // TASK-9373
  closeDispatchSettingsModal,
  saveDispatchSettings,
  saveJobAutoClose, // TASK-9366 — own Save, never on the shared button
  // TASK-9370 — driver Job Board entry points into the (trimmed) Add/Edit Job modal
  driverJobEditEnabled,
  driverOpenAddJob,
  driverOpenEditJob,

  // TASK-355 2026-05-22: My Today (Path B) killed per unanimous COD verdict.
  // Job Board is now member-aware (jobboard.js getActiveJobBoardPersonId), so
  // SPA-signed-in members get the same personId-scoped daily view there.
};

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
